/**
 * Generated by @openapi-codegen
 *
 * @version 2.0.0
 */
import * as reactQuery from "@tanstack/react-query";
import { type Apiv2Context, useApiv2Context, queryKeyFn } from "./apiv2Context";
import { deepMerge } from "./apiv2Utils";
import type * as Fetcher from "./apiv2Fetcher";
import { apiv2Fetch } from "./apiv2Fetcher";
import type * as Schemas from "./apiv2Schemas";

type QueryFnOptions = {
  signal?: AbortController["signal"];
};

export type GetCandidatesQueryParams = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Find relevant candidates using candidate details and attached document analysis.
   *
   * @minLength 1
   * @maxLength 256
   */
  q?: string;
  /**
   * Search candidates by name (forename or surname)
   *
   * @maxLength 256
   */
  search?: string;
  /**
   * The field to sort by. Prefix with '-' for descending. Availability priority requires an availability period and ranks free time, counting a full available day as one and a half-day as half.
   */
  sort?:
    | (
        | "relevanceScore"
        | "-relevanceScore"
        | "forename"
        | "-forename"
        | "surname"
        | "-surname"
        | "mobile"
        | "-mobile"
        | "email"
        | "-email"
        | "address.city"
        | "-address.city"
        | "address.postcode"
        | "-address.postcode"
        | "address.county"
        | "-address.county"
        | "address.borough"
        | "-address.borough"
        | "address.addressLine1"
        | "-address.addressLine1"
        | "address.addressLine2"
        | "-address.addressLine2"
        | "address.addressLine3"
        | "-address.addressLine3"
        | "subject"
        | "-subject"
        | "statusChangeDate"
        | "-statusChangeDate"
        | "lastContactedDate"
        | "-lastContactedDate"
        | "daysSinceLastContactDisplay"
        | "-daysSinceLastContactDisplay"
        | "status"
        | "-status"
        | "occupation"
        | "-occupation"
        | "dob"
        | "-dob"
        | "trnNumberCheckedDate"
        | "-trnNumberCheckedDate"
        | "criminalRecord.dbsLastChecked"
        | "-criminalRecord.dbsLastChecked"
        | "criminalRecord.updateServiceCheckDate"
        | "-criminalRecord.updateServiceCheckDate"
        | "rtwVerification"
        | "-rtwVerification"
        | "criminalRecord.section128Checked"
        | "-criminalRecord.section128Checked"
        | "criminalRecord.prohibitionCheck"
        | "-criminalRecord.prohibitionCheck"
        | "createdAt"
        | "-createdAt"
        | "updatedAt"
        | "-updatedAt"
        | "_internalId"
        | "-_internalId"
        | "trnNumber"
        | "-trnNumber"
        | "desiredLocation"
        | "-desiredLocation"
        | "telephone"
        | "-telephone"
        | "previousNames"
        | "-previousNames"
        | "otherNames"
        | "-otherNames"
        | "location"
        | "-location"
      )
    | ("availabilityPriority" | "-availabilityPriority");
  /**
   * The fields to include in the response in dot notation format
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "forename"
    | "surname"
    | "previousNames"
    | "email"
    | "telephone"
    | "mobile"
    | "occupation"
    | "subject"
    | "address.addressLine1"
    | "address.addressLine2"
    | "address.addressLine3"
    | "address.city"
    | "address.county"
    | "address.borough"
    | "address.postcode"
    | "address.country"
    | "address.lat"
    | "address.lng"
    | "qualifications"
    | "employment"
    | "references"
    | "referenceCheckConsent"
    | "referenceCheckConsentName"
    | "referenceCheckConsentDate"
    | "dob"
    | "sex"
    | "status"
    | "onboardingStatus"
    | "onboardingSent"
    | "nextOfKin.nextOfKinName"
    | "nextOfKin.nextOfKinRelationship"
    | "nextOfKin.nextOfKinTelephone"
    | "nextOfKin.nextOfKinEmail"
    | "nextOfKin.nextOfKinAddress.addressLine1"
    | "nextOfKin.nextOfKinAddress.addressLine2"
    | "nextOfKin.nextOfKinAddress.addressLine3"
    | "nextOfKin.nextOfKinAddress.city"
    | "nextOfKin.nextOfKinAddress.county"
    | "nextOfKin.nextOfKinAddress.borough"
    | "nextOfKin.nextOfKinAddress.postcode"
    | "nextOfKin.nextOfKinAddress.country"
    | "nextOfKin.nextOfKinAddress.lat"
    | "nextOfKin.nextOfKinAddress.lng"
    | "criminalRecord.dbsHeld"
    | "criminalRecord.dbsNumber"
    | "criminalRecord.dbsType"
    | "criminalRecord.dbsLastChecked"
    | "criminalRecord.dbsUpdateServiceNumber"
    | "criminalRecord.prohibitionCheck"
    | "criminalRecord.overseasCheck"
    | "criminalRecord.overseasCheckNote"
    | "criminalRecord.convictedOffence"
    | "criminalRecord.convictedOffenceDescription"
    | "criminalRecord.convictions"
    | "criminalRecord.convictionsDescription"
    | "criminalRecord.registeredToUpdateService"
    | "criminalRecord.consentUpdateServiceChecks"
    | "criminalRecord.note"
    | "criminalRecord.updateServiceCheckDate"
    | "criminalRecord.abroad"
    | "criminalRecord.overseasDeclarationExtended"
    | "criminalRecord.educationalDisciplinaryProceedings"
    | "criminalRecord.dbsComments"
    | "criminalRecord.dbsDeclarationRead"
    | "criminalRecord.overseasOffence"
    | "criminalRecord.overseasOffenceDescription"
    | "criminalRecord.currentCriminalOffence"
    | "criminalRecord.currentCriminalOffenceDescription"
    | "criminalRecord.dbsIssuedByBusiness"
    | "criminalRecord.dbsIssuedByCurrentEmployer"
    | "criminalRecord.section128Required"
    | "criminalRecord.section128Checked"
    | "criminalRecordDeclaration.questions"
    | "criminalRecordDeclaration.answers"
    | "criminalRecordDeclaration.submittedAt"
    | "criminalRecordDeclaration.submittedIp"
    | "criminalRecordDeclaration.configurationUpdatedAt"
    | "criminalRecordCheckConsent"
    | "criminalRecordCheckConsentName"
    | "criminalRecordCheckConsentDate"
    | "safeInEducation.barred"
    | "safeInEducation.barredCheckDate"
    | "safeInEducation.waiver"
    | "safeInEducation.ordersYou"
    | "safeInEducation.ordersChild"
    | "safeInEducation.previousSafeguardingAllegations"
    | "safeInEducation.cautioned"
    | "safeInEducation.violentOffence"
    | "safeInEducation.anotherCountry"
    | "safeInEducation.readKCSIE"
    | "safeInEducation.readChildcareAct"
    | "safeInEducation.prohibitedFromTeaching"
    | "safeInEducation.knownPolice"
    | "safeInEducation.prohibitedFromManagement"
    | "safeInEducation.criminalOffencesLaw"
    | "safeInEducation.safeInEducationDetails"
    | "safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
    | "safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
    | "safeInEducation.safeguardingQuestionaire.signOfAbuse"
    | "safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
    | "safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
    | "safeInEducation.safeguardingQuestionaire.fgmDefinition"
    | "safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
    | "safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
    | "safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
    | "safeInEducation.safeguardingQuestionaire.studentConfides"
    | "socialMedia.facebook"
    | "socialMedia.instagram"
    | "socialMedia.twitter"
    | "socialMedia.linkedIn"
    | "socialMedia.tiktok"
    | "socialMedia.others"
    | "socialMedia.socialMediaChecked"
    | "socialMedia.googleCheckedDate"
    | "safeInEducationCheckConsent"
    | "safeInEducationCheckConsentName"
    | "safeInEducationCheckConsentDate"
    | "healthRecord.physicalAbilityCondition"
    | "healthRecord.confused"
    | "healthRecord.hearing"
    | "healthRecord.eyesight"
    | "healthRecord.depression"
    | "healthRecord.severePain"
    | "healthRecord.excessiveDrowsiness"
    | "healthRecord.epilepsy"
    | "healthRecord.heart"
    | "healthRecord.asthma"
    | "healthRecord.mentalHealth"
    | "healthRecord.expectantMother"
    | "healthRecord.allergies"
    | "healthRecord.medication"
    | "healthRecord.healthIssues"
    | "healthRecord.healthIssuesDescription"
    | "healthRecord.disabilityNeeds"
    | "healthRecord.contagiousDiseases"
    | "healthRecord.affectAbility"
    | "healthRecord.notificationAgree"
    | "healthRecord.sasOptIn"
    | "healthRecord.signature"
    | "healthRecord.drawnSignature"
    | "healthRecord.signatureDate"
    | "healthRecord.vaccinationMeasles"
    | "healthRecord.vaccinationMumps"
    | "healthRecord.vaccinationRubella"
    | "healthRecord.vaccinationVaricella"
    | "healthRecord.vaccinationTuberculosis"
    | "healthRecord.vaccinationHepatitisB"
    | "healthRecord.vaccinationHepatitisC"
    | "healthRecord.vaccinationHIV"
    | "healthRecord.vaccinationCovid"
    | "healthRecord.vaccinationCovid1"
    | "healthRecord.vaccinationCovid2"
    | "healthRecord.vaccinationCovidBooster"
    | "healthRecord.vaccinationCovidInfluenza"
    | "healthRecord.fitToWorkOverride"
    | "healthDeclaration.questions"
    | "healthDeclaration.answers"
    | "healthDeclaration.submittedAt"
    | "healthDeclaration.submittedIp"
    | "healthDeclaration.configurationUpdatedAt"
    | "healthRecordCheckConsent"
    | "healthRecordCheckConsentName"
    | "healthRecordCheckConsentDate"
    | "bankDetails.bankAccountName"
    | "bankDetails.bankName"
    | "bankDetails.accountNumber"
    | "bankDetails.sortCode"
    | "bankDetails.niNumber"
    | "bankDetails.jobOp1"
    | "bankDetails.jobOp2"
    | "bankDetails.jobOp3"
    | "bankDetails.studentLoan.studentLoan"
    | "bankDetails.studentLoan.plan1"
    | "bankDetails.studentLoan.plan2"
    | "bankDetails.studentLoan.plan4"
    | "bankDetails.studentLoan.postGradLoan"
    | "declarationCheckConsent"
    | "declarationCheckConsentName"
    | "declarationCheckConsentDate"
    | "contractForServiceConsent"
    | "contractForServiceName"
    | "contractForServiceDate"
    | "confirmed"
    | "createdBy"
    | "customerId"
    | "migrationId"
    | "migrationApproval"
    | "assignee"
    | "assignee.id"
    | "assignee.email"
    | "assignee.firstName"
    | "assignee.lastName"
    | "assignee.username"
    | "assignee.lea"
    | "assignee.clients"
    | "assignee.position"
    | "assignee.permissions"
    | "assignee.activationCode"
    | "assignee.customer"
    | "assignee.disabled"
    | "assignee.commissionThreshold"
    | "assignee.branches"
    | "assignee.displayOnboarding"
    | "assignee._customId"
    | "assignee.signature"
    | "assignee.phone"
    | "assignee.defaultCandidateWatcher"
    | "assignee.defaultTaskWorkspace"
    | "assignee.teamAdmin"
    | "assignee.contact"
    | "assignee.contact.id"
    | "assignee.contact.forename"
    | "assignee.contact.surname"
    | "assignee.contact.email"
    | "assignee.contact.telephone"
    | "assignee.contact.mobile"
    | "assignee.contact.extension"
    | "assignee.createdAt"
    | "assignee.evertime.id"
    | "assignee.timesheetPortal.id"
    | "assignee.timesheetPortal.code"
    | "currentBooking"
    | "profilePhoto"
    | "payType"
    | "payInterval"
    | "payRate"
    | "customId"
    | "_internalId"
    | "statusChangeDate"
    | "optOut"
    | "trnNumber"
    | "trnNumberCheckedDate"
    | "trnNumberNotes"
    | "employmentStartDate"
    | "ewcConfirmation"
    | "homeOfficeShareCode"
    | "archived"
    | "umbrella"
    | "payeUmbrella"
    | "umbrellaCompanyName"
    | "umbrellaCompanyCode"
    | "transport"
    | "importantNotes"
    | "candidateLastUpdated"
    | "source"
    | "sourceOther"
    | "onlineCheckConsent"
    | "title"
    | "pronoun"
    | "updatedAt"
    | "lastContactedDate"
    | "highlight"
    | "teacherPayScale"
    | "p45"
    | "roleType"
    | "rtwVerification"
    | "rtwVerificationNote"
    | "rtwVerificationStatus"
    | "desiredLocation"
    | "complianceUser"
    | "complianceUser.id"
    | "complianceUser.email"
    | "complianceUser.firstName"
    | "complianceUser.lastName"
    | "complianceUser.username"
    | "complianceUser.lea"
    | "complianceUser.clients"
    | "complianceUser.position"
    | "complianceUser.permissions"
    | "complianceUser.activationCode"
    | "complianceUser.customer"
    | "complianceUser.disabled"
    | "complianceUser.commissionThreshold"
    | "complianceUser.branches"
    | "complianceUser.displayOnboarding"
    | "complianceUser._customId"
    | "complianceUser.signature"
    | "complianceUser.phone"
    | "complianceUser.defaultCandidateWatcher"
    | "complianceUser.defaultTaskWorkspace"
    | "complianceUser.teamAdmin"
    | "complianceUser.contact"
    | "complianceUser.contact.id"
    | "complianceUser.contact.forename"
    | "complianceUser.contact.surname"
    | "complianceUser.contact.email"
    | "complianceUser.contact.telephone"
    | "complianceUser.contact.mobile"
    | "complianceUser.contact.extension"
    | "complianceUser.createdAt"
    | "complianceUser.evertime.id"
    | "complianceUser.timesheetPortal.id"
    | "complianceUser.timesheetPortal.code"
    | "watchers"
    | "tags"
    | "documents"
    | "documentsCount"
    | "desiredLocationMultiple"
    | "availabilityDays"
    | "availabilityLastChecked"
    | "subjectToNotice"
    | "subsidiarySubject"
    | "employmentPreference"
    | "otherNames"
    | "ltdCompany"
    | "employmentVerified"
    | "candidateDBSVerification"
    | "candidateDBSVerificationNote"
    | "candidateDBSVerificationStatus"
    | "candidateDBSCheckVerification"
    | "candidateDBSCheckVerificationNote"
    | "candidateDBSCheckVerificationStatus"
    | "candidateDBSUpdateServiceVerificationNote"
    | "candidateDBSUpdateServiceVerificationStatus"
    | "candidateTRNVerificationNote"
    | "candidateTRNVerificationStatus"
    | "candidateHealthSafetyVerification"
    | "candidateHealthSafetyVerificationNote"
    | "candidateHealthSafetyVerificationStatus"
    | "onboardingSession"
    | "onboardingSession.id"
    | "onboardingSession.url"
    | "onboardingSession.healthDeclarationConfig.questions"
    | "onboardingSession.healthDeclarationConfig.updatedAt"
    | "onboardingSession.criminalRecordConfig.questions"
    | "onboardingSession.criminalRecordConfig.updatedAt"
    | "contact"
    | "contact.id"
    | "contact.forename"
    | "contact.surname"
    | "contact.email"
    | "contact.telephone"
    | "contact.mobile"
    | "contact.extension"
    | "optOut48Hours"
    | "recAuditConsent"
    | "recAuditConsentDate"
    | "recAuditConsentMethod"
    | "createdAt"
    | "candidateMobileAppStatus"
    | "evertime.id"
    | "evertime.niNumberMatch"
    | "relevanceScore"
    | "matchReasons"
    | "distance"
    | "timesheetPortal.id"
    | "timesheetPortal.code"
    | "mobileAppLinkInvite.isUsed"
    | "mobileAppLinkInvite.createdAt"
    | "mobileAppLinkInvite.usedBy"
    | "mobileAppLinkInvite.usedAt"
    | "mobileAppLinkInvite.unlinkedBy"
    | "mobileAppLinkInvite.unlinkedAt"
    | "mobileAppLinkInvite.deletedBy"
    | "mobileAppLinkInvite.deletedAt"
    | "mobileAppLinkInvite.status"
  )[];
  /**
   * Related resources to return as objects instead of IDs. Supported values are documented for this endpoint.
   *
   * @maxItems 100
   */
  expand?: (
    | "assignee"
    | "contact"
    | "customer"
    | "umbrella"
    | "payeUmbrella"
    | "complianceUser"
    | "onboardingSession"
    | "watchers"
    | "tags"
    | "documents"
  )[];
  /**
   * Filter candidates based on various criteria
   */
  filter?: {
    /**
     * Inclusive calendar dates, up to 31 days. Both boundaries are required when selecting availability days, filtering availability statuses or sorting by availability priority.
     */
    availabilityPeriod?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Match candidates with any selected status during the availability period. Requires an availability period. Empty selects all statuses.
     *
     * @maxItems 5
     */
    availabilityStatuses?: (
      | "available"
      | "unavailable"
      | "booked"
      | "awaiting"
      | "notRecorded"
    )[];
    /**
     * Filter candidates by their IDs. Maximum 50.
     *
     * @maxItems 50
     */
    ids?: string[];
    /**
     * Filter candidates by their status
     *
     * @maxItems 25
     */
    status?: (
      | "Pending"
      | "Approved"
      | "Rejected"
      | "Anonymised"
      | "Archived"
      | "Enquired"
      | "Hold"
      | "Future"
      | "DNC"
      | "Pre"
    )[];
    /**
     * Filter candidates by their onboarding status
     *
     * @maxItems 25
     */
    onboardingStatus?: ("Not Started" | "In Progress" | "Completed")[];
    /**
     * Filter candidates by if they have an active onboarding session
     */
    activeOnboardingSession?: boolean;
    /**
     * Filter candidates by if they have been sent onboarding materials
     */
    onboardingSent?: "true" | "false";
    /**
     * Filter candidates by if they require section 128
     */
    section128Required?: "true" | "false";
    /**
     * Filter candidates by confirmed status
     */
    confirmed?: "true" | "false";
    /**
     * Filter candidates by opt out status
     */
    optOut?: "true" | "false";
    /**
     * Filter candidates by opt out 48 hours status
     */
    optOut48Hours?: "true" | "false";
    /**
     * Filter candidates by fit to work override status
     */
    fitToWorkOverride?: "true" | "false";
    /**
     * Filter candidates by whether they have health conditions
     */
    affectAbility?: "true" | "false";
    /**
     * Filter candidates by EWC confirmation status
     */
    ewcConfirmation?: "true" | "false";
    /**
     * Filter candidates by whether they hold a DBS certificate
     */
    dbsHeld?: "true" | "false";
    /**
     * Filter candidates by DBS update service registration
     */
    dbsRegisteredToUpdateService?: "true" | "false";
    /**
     * Filter candidates by their first name
     *
     * @maxLength 500
     */
    firstName?: string;
    /**
     * Filter candidates by their last name
     *
     * @maxLength 500
     */
    lastName?: string;
    /**
     * Filter candidates by their email address
     *
     * @maxLength 500
     */
    email?: string;
    /**
     * Filter candidates by their mobile number
     *
     * @maxLength 500
     */
    mobile?: string;
    /**
     * Address fields used to filter candidates. Multiple supplied fields are combined.
     */
    address?: {
      /**
       * Filter candidates by their city
       *
       * @maxLength 500
       */
      city?: string;
      /**
       * Filter candidates by their postcode
       *
       * @maxLength 20
       */
      postcode?: string;
      /**
       * Filter candidates by their county
       *
       * @maxLength 500
       */
      county?: string;
      /**
       * Filter candidates by their borough
       *
       * @maxLength 500
       */
      borough?: string;
      /**
       * Filter candidates by address line 1
       *
       * @maxLength 500
       */
      addressLine1?: string;
      /**
       * Filter candidates by address line 2
       *
       * @maxLength 500
       */
      addressLine2?: string;
      /**
       * Filter candidates by address line 3
       *
       * @maxLength 500
       */
      addressLine3?: string;
    };
    /**
     * Filter candidates by their occupation
     *
     * @maxItems 25
     */
    occupation?: string[];
    /**
     * Filter candidates by their specialism
     *
     * @maxLength 500
     */
    specialism?: string;
    /**
     * Filter candidates by their company-defined secondary identifier.
     *
     * @maxLength 500
     */
    _internalId?: string;
    /**
     * Filter candidates by their compliance user
     *
     * @maxItems 25
     */
    complianceUser?: string[];
    /**
     * Filter candidates by their assignee
     *
     * @maxItems 25
     */
    assignee?: string[];
    /**
     * Filter candidates by their watcher
     *
     * @maxItems 25
     */
    watcher?: string[];
    /**
     * Filter candidates by their team
     *
     * @maxItems 25
     */
    team?: string[];
    /**
     * Filter candidates by their associated contact ID
     *
     * @pattern ^[0-9a-f]{24}$
     */
    contactId?: string;
    /**
     * Filter candidates by whether they have an active booking
     */
    currentBooking?: "true" | "false";
    /**
     * Filter candidates with an active booking overlapping an absolute or relative date range.
     */
    bookedInRange?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates without an active booking overlapping an absolute or relative date range. Combine with `bookedInRange` to find candidates booked in one period but not another.
     */
    notBookedInRange?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by their tags
     *
     * @maxItems 25
     */
    tags?: string[];
    /**
     * Filter candidates by location from coordinates
     */
    location?: {
      lat?: number | null;
      lng?: number | null;
      radius?: number | null;
    };
    /**
     * Filter candidates by last status changed
     */
    statusChangeDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by last contact
     */
    lastContactedDateFilter?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by employment preference
     *
     * @maxItems 25
     */
    employmentPreference?: (
      | "Day To Day"
      | "Long Term"
      | "Fixed Term"
      | "Perm"
    )[];
    /**
     * Filter candidates by their chosen mode of transport
     *
     * @maxItems 25
     */
    transport?: (
      | "Drives Own Vehicle"
      | "Use Public Transport"
      | "Access to Another Vehicle"
      | "Bicycle"
      | "No Access"
      | "Other"
    )[];
    /**
     * Filter candidates by their TRN number
     *
     * @maxLength 500
     */
    trnNumber?: string;
    /**
     * Filter candidates by their desired location
     *
     * @maxLength 500
     */
    desiredLocation?: string;
    /**
     * Filter candidates by whether they are subject to a notice period
     *
     * @maxItems 25
     */
    subjectToNotice?: ("Unsure" | "Yes" | "No")[];
    /**
     * Filter candidates by title
     *
     * @maxItems 25
     */
    title?: (
      | "Mr"
      | "Mrs"
      | "Ms"
      | "Miss"
      | "Dr"
      | "Mx"
      | "Lady"
      | "Lord"
      | "Other"
    )[];
    /**
     * Filter candidates by sex
     *
     * @maxItems 25
     */
    sex?: ("Male" | "Female" | "Other" | "Unknown")[];
    /**
     * Filter candidates by pronoun
     *
     * @maxItems 25
     */
    pronoun?: (
      | "She/Her/Hers"
      | "He/Him/His"
      | "They/Them/Theirs"
      | "Ze/Hir/Hirs"
      | "Ze/Zir/Zirs"
      | "Other"
      | "Prefer not to say"
    )[];
    /**
     * Filter candidates by role type
     *
     * @maxItems 25
     */
    roleType?: (
      | "Senior Leadership Positions"
      | "Teaching Roles"
      | "School Support Positions"
    )[];
    /**
     * Filter candidates by pay type
     *
     * @maxItems 25
     */
    payType?: ("PAYE" | "Umbrella" | "Limited Company" | "Not Applicable")[];
    /**
     * Filter candidates by payment interval
     *
     * @maxItems 25
     */
    payInterval?: (
      | "Monthly"
      | "Weekly"
      | "Weekly Pay in Arrears"
      | "Bi-Weekly"
    )[];
    /**
     * Filter candidates by DBS check type
     *
     * @maxItems 25
     */
    dbsType?: (
      | "Basic"
      | "Standard"
      | "Enhanced"
      | "Enhanced with Barred List"
      | "Child workforce"
      | "Child and adult workforce"
      | "Adult workforce"
    )[];
    /**
     * Filter candidates by source
     */
    source?: (
      | (
          | "Company Website"
          | "iCan Teach UK Website"
          | "Apply to Education"
          | "CV Library - CV Search"
          | "CV Library Advert"
          | "CV Library - Watchdog"
          | "Guardian- CV Search"
          | "Guardian Advert"
          | "Indeed - CV Search"
          | "Indeed Advert"
          | "Reed - CV Search"
          | "Reed Advert"
          | "Reed \u2013 Watchdog"
          | "SENploy - Advert"
          | "Total Jobs"
          | "Other Job Board"
          | "Google"
          | "LinkedIn"
          | "Linkedin Recruiter"
          | "LinkedIn Advert"
          | "Referral - School"
          | "Referral - Candidate"
          | "UK University"
          | "Irish University"
          | "Canadian University"
          | "Marketing Campaign"
          | "Woo"
          | "Job Show"
          | "Recruitment Fair"
          | "Career Fair"
          | "Volcanic"
          | "Broadbean"
          | "Headhunt"
          | "Referral"
          | "Direct"
          | "Other"
        )
      | (
          | "Company Website"
          | "Direct Call"
          | "CV Library Watchdog"
          | "CV Library Advert"
          | "CV Library CV Search"
          | "Reed Watchdog"
          | "Reed Advert"
          | "Reed CV Search"
          | "Indeed Watchdog"
          | "Indeed Advert"
          | "Indeed CV Search"
          | "Job Centre"
          | "Returning Candidate"
          | "Referral Candidate"
          | "Referral Client"
          | "Training Provider"
          | "Recruitment Fair"
          | "University/College"
        )
    )[];
    /**
     * Filter candidates by their telephone number
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * Filter candidates by their previous names
     *
     * @maxLength 500
     */
    previousNames?: string;
    /**
     * Filter candidates by their other names
     *
     * @maxLength 500
     */
    otherNames?: string;
    /**
     * Filter candidates by date of birth
     */
    dob?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by TRN number checked date
     */
    trnNumberCheckedDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by DBS last checked date
     */
    dbsLastChecked?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by DBS update service check date
     */
    dbsUpdateServiceCheckDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by RTW verification date
     */
    rtwVerificationDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by Section 128 checked date
     */
    section128CheckedDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by prohibition check date
     */
    prohibitionCheckDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by created date
     */
    createdAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by last updated date
     */
    updatedAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by mobile app status
     *
     * @maxItems 25
     */
    candidateMobileAppStatus?: (
      | "Unlinked"
      | "Scheduled Unlink"
      | "Linked"
      | "Invited"
      | "Not Invited"
    )[];
    /**
     * Filter candidates by REC audit consent status
     */
    recAuditConsent?: "true" | "false";
    /**
     * Filter candidates by migration
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Filter candidates by migration approval
     *
     * @maxItems 3
     */
    migrationApproval?: ("pending" | "approved" | "rejected")[];
  };
};

export type GetCandidatesError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetCandidatesResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the candidate
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The forename of the candidate
     *
     * @minLength 1
     * @maxLength 256
     */
    forename?: string;
    /**
     * The surname of the candidate
     *
     * @minLength 1
     * @maxLength 256
     */
    surname?: string;
    /**
     * Any previous names of the candidate
     *
     * @maxLength 256
     */
    previousNames?: string;
    /**
     * The email of the candidate. This will be used for communication.
     *
     * @format email
     */
    email?: string;
    /**
     * The telephone number of the candidate
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * The mobile number of the candidate
     *
     * @maxLength 32
     */
    mobile?: string;
    /**
     * The occupation of the candidate
     */
    occupation?:
      | "Teacher"
      | "Cover Supervisor"
      | "Teaching Assistant"
      | "School Support Staff"
      | "Primary Teacher"
      | "Secondary Teacher"
      | "Head Teacher"
      | "Senior Leader"
      | "Tutor"
      | "Mentor"
      | "Learning Support Assistant"
      | "Finance Officer"
      | "HR Administrator"
      | "HR Manager"
      | "Office Manager"
      | "Administrator"
      | "Personal Assistant"
      | "Pastoral Manager"
      | "Receptionist"
      | "Instructor"
      | "Caretaker"
      | "Nursery Assistant"
      | "Nursery Nurse"
      | "Deputy Nursery Manager"
      | "Lunchtime Supervisor"
      | "Classroom Graduate"
      | "Primary Cover Supervisor"
      | "HLTA"
      | "Nurture Teacher"
      | "Nurture Mentor"
      | "SENDCo"
      | "Registered Manager"
      | "Regional Manager"
      | "Education Director"
      | "Residential Director"
      | "Deputy Manager"
      | "Residential Support Worker"
      | "Sports Coach"
      | "Executive Headteacher"
      | "General Practitioner"
      | "Advance Nurse Practitioner (ANP)"
      | "Emergency Nurse Practitioner (ENP)"
      | "Advanced Clinical Practitioner (ACP)"
      | "Emergency Clinical Practitioner (ECP)"
      | "Consultant Psychiatrist"
      | "Psychiatrist \u2013 SPR"
      | "IT Technician"
      | "Art Technician"
      | "DT Technician"
      | "Science Technician"
      | "Healthcare Assistant/ Residential Support Worker"
      | "Healthcare Assistant"
      | "Senior Healthcare Assistant"
      | "Team Leader"
      | "Registered General Nurse"
      | "Registered Mental Nurse"
      | "Registered Nurse Learning Disabilities"
      | "Overseas Trained Teacher";
    /**
     * The subject the candidate specializes in
     *
     * @maxLength 256
     */
    subject?: string;
    /**
     * The address of the resource
     */
    address?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The qualifications of the candidate
     *
     * @maxItems 32
     */
    qualifications?: {
      /**
       * The type of the qualification
       *
       * @maxLength 256
       */
      type?: string;
      /**
       * The place where the qualification was obtained
       *
       * @maxLength 256
       */
      place?: string;
      /**
       * The name of the qualification
       *
       * @maxLength 256
       */
      qualificationName?: string;
      /**
       * The date the qualification was obtained
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      date?: string;
      /**
       * The unique identifier of the qualification
       *
       * @maxLength 256
       */
      qualificationId?: string;
      /**
       * The order index of the qualification
       *
       * @minimum 0
       */
      orderIndex?: number;
      /**
       * The expiry date of the qualification
       *
       * @maxLength 256
       */
      qualificationExpiry?: string;
      /**
       * Whether to exclude this qualification from vetting
       */
      excludeFromVetting?: boolean;
      /**
       * The date the qualification was created
       *
       * @format date-time
       */
      createdAt?: string | null;
    }[];
    /**
     * The employment history of the candidate
     *
     * @maxItems 32
     */
    employment?: {
      /**
       * The name of the employer
       *
       * @maxLength 256
       */
      employerName?: string;
      /**
       * The date the candidate started employment
       *
       * @maxLength 256
       */
      dateStarted?: string;
      /**
       * The date the candidate ended employment
       *
       * @maxLength 256
       */
      dateEnded?: string;
      /**
       * The job title of the candidate
       *
       * @maxLength 256
       */
      jobTitle?: string;
      /**
       * The reason the candidate left this employment
       *
       * @maxLength 1024
       */
      reasonForLeaving?: string;
      /**
       * The duties of the candidate in this role. Consultant only, visible on Edun CV
       *
       * @maxLength 1024
       */
      duties?: string;
      /**
       * The salary of the candidate in this role
       *
       * @maxLength 256
       */
      salary?: string;
      /**
       * The address of the candidate's reference for this employment
       */
      referenceAddress?: {
        /**
         * The first line of the address
         *
         * @maxLength 256
         */
        addressLine1?: string;
        /**
         * The second line of the address
         *
         * @maxLength 256
         */
        addressLine2?: string;
        /**
         * The third line of the address
         *
         * @maxLength 256
         */
        addressLine3?: string;
        /**
         * The city of the address
         *
         * @maxLength 256
         */
        city?: string;
        /**
         * The county of the address
         *
         * @maxLength 256
         */
        county?: string;
        /**
         * The borough of the address
         *
         * @maxLength 256
         */
        borough?: string;
        /**
         * The postcode of the address
         *
         * @maxLength 10
         */
        postcode?: string;
        /**
         * The country of the address
         *
         * @maxLength 256
         */
        country?: string;
        /**
         * The latitude of the address
         *
         * @minimum -90
         * @maximum 90
         */
        lat?: number;
        /**
         * The longitude of the address
         *
         * @minimum -180
         * @maximum 180
         */
        lng?: number;
      };
      /**
       * The unique identifier for this employment record
       *
       * @maxLength 256
       */
      employmentId?: string;
      /**
       * The order index of this employment record
       *
       * @minimum 0
       */
      orderIndex?: number;
    }[];
    /**
     * The references for the candidate
     *
     * @maxItems 32
     */
    references?: {
      /**
       * The title of the reference
       *
       * @maxLength 16
       */
      referenceTitle?: string;
      /**
       * The forename of the reference
       *
       * @maxLength 256
       */
      referenceForename?: string;
      /**
       * The surname of the reference
       *
       * @maxLength 256
       */
      referenceSurname?: string;
      /**
       * The company of the reference
       *
       * @maxLength 256
       */
      referenceCompany?: string;
      /**
       * The position of the reference
       *
       * @maxLength 256
       */
      referencePosition?: string;
      /**
       * The email of the reference
       *
       * @format email
       */
      referenceEmail?: string;
      /**
       * The telephone number of the reference
       *
       * @maxLength 32
       */
      referenceTelephone?: string;
      /**
       * The type of the reference
       */
      referenceType?:
        | "Education"
        | "Professional"
        | "Character"
        | "Factual"
        | "Academic"
        | "Volunteer";
      /**
       * The duration of the reference
       *
       * @maxLength 128
       */
      referenceDuration?: string;
      /**
       * The ID of the reference
       *
       * @maxLength 256
       */
      referenceId?: string;
      /**
       * This field only applies if the reference immediate consent question is enabled
       * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
       */
      referenceApply?: boolean;
      /**
       * Any additional notes about the reference
       *
       * @maxLength 1024
       */
      referenceNote?: string;
      /**
       * The start date of the reference
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      dateFrom?: string;
      /**
       * The end date of the reference
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      dateTo?: string;
    }[];
    /**
     * Whether the candidate has consented to a reference check
     */
    referenceCheckConsent?: boolean;
    /**
     * The name of the person who provided consent for the reference check
     *
     * @maxLength 256
     */
    referenceCheckConsentName?: string;
    /**
     * The date when consent for the reference check was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    referenceCheckConsentDate?: string;
    /**
     * The date of birth of the candidate
     *
     * @format date-time
     */
    dob?: string | null;
    /**
     * The sex of the candidate
     */
    sex?: "Male" | "Female" | "Other" | "Unknown";
    /**
     * The status of the candidate
     */
    status?:
      | "Pending"
      | "Approved"
      | "Rejected"
      | "Anonymised"
      | "Archived"
      | "Enquired"
      | "Hold"
      | "Future"
      | "DNC"
      | "Pre";
    /**
     * The current status of the onboarding process for the candidate
     */
    onboardingStatus?: "Not Started" | "In Progress" | "Completed";
    /**
     * Whether the onboarding information has been sent to the candidate
     */
    onboardingSent?: boolean;
    nextOfKin?: {
      /**
       * The name of the next of kin
       *
       * @maxLength 256
       */
      nextOfKinName?: string;
      /**
       * The relationship of the next of kin to the candidate
       *
       * @maxLength 256
       */
      nextOfKinRelationship?: string;
      /**
       * The telephone number of the next of kin
       *
       * @maxLength 32
       */
      nextOfKinTelephone?: string;
      /**
       * The email address of the next of kin
       *
       * @format email
       */
      nextOfKinEmail?: string;
      nextOfKinAddress?: {
        /**
         * The first line of the address
         *
         * @maxLength 256
         */
        addressLine1?: string;
        /**
         * The second line of the address
         *
         * @maxLength 256
         */
        addressLine2?: string;
        /**
         * The third line of the address
         *
         * @maxLength 256
         */
        addressLine3?: string;
        /**
         * The city of the address
         *
         * @maxLength 256
         */
        city?: string;
        /**
         * The county of the address
         *
         * @maxLength 256
         */
        county?: string;
        /**
         * The borough of the address
         *
         * @maxLength 256
         */
        borough?: string;
        /**
         * The postcode of the address
         *
         * @maxLength 10
         */
        postcode?: string;
        /**
         * The country of the address
         *
         * @maxLength 256
         */
        country?: string;
        /**
         * The latitude of the address
         *
         * @minimum -90
         * @maximum 90
         */
        lat?: number;
        /**
         * The longitude of the address
         *
         * @minimum -180
         * @maximum 180
         */
        lng?: number;
      };
    };
    criminalRecord?: {
      /**
       * Whether the candidate has a DBS certificate
       */
      dbsHeld?: boolean;
      /**
       * The unique identifier for the DBS certificate
       *
       * @maxLength 256
       */
      dbsNumber?: string;
      /**
       * The type of DBS check
       */
      dbsType?:
        | "Basic"
        | "Standard"
        | "Enhanced"
        | "Enhanced with Barred List"
        | "Child workforce"
        | "Child and adult workforce"
        | "Adult workforce";
      /**
       * The date when the DBS check was last performed
       *
       * @maxLength 256
       */
      dbsLastChecked?: string;
      /**
       * The DBS update service number for the candidate
       *
       * @maxLength 256
       */
      dbsUpdateServiceNumber?: string;
      /**
       * The result of the prohibition check
       *
       * @maxLength 256
       */
      prohibitionCheck?: string;
      /**
       * The result of the overseas check
       *
       * @maxLength 256
       */
      overseasCheck?: string;
      /**
       * Any additional notes regarding the overseas check
       *
       * @maxLength 256
       */
      overseasCheckNote?: string;
      /**
       * Whether the candidate has a convicted offence
       */
      convictedOffence?: boolean;
      /**
       * The description of the convicted offence
       *
       * @maxLength 1024
       */
      convictedOffenceDescription?: string;
      /**
       * Whether the candidate has any convictions
       */
      convictions?: boolean;
      /**
       * The description of the convictions
       *
       * @maxLength 1024
       */
      convictionsDescription?: string;
      /**
       * Whether the candidate is registered to the DBS update service
       */
      registeredToUpdateService?: boolean;
      /**
       * Whether the candidate consents to update service checks
       */
      consentUpdateServiceChecks?: boolean;
      /**
       * Any additional notes regarding the candidate's criminal record
       *
       * @maxLength 1024
       */
      note?: string;
      /**
       * The date when the update service check was performed
       *
       * @maxLength 64
       */
      updateServiceCheckDate?: string;
      /**
       * Whether the candidate has lived abroad
       */
      abroad?: boolean;
      /**
       * Any additional notes regarding the overseas declaration
       *
       * @maxLength 1024
       */
      overseasDeclarationExtended?: string;
      /**
       * Whether the candidate has been subject to educational disciplinary proceedings
       */
      educationalDisciplinaryProceedings?: boolean;
      /**
       * Any comments regarding the DBS check
       *
       * @maxLength 1024
       */
      dbsComments?: string;
      /**
       * Whether the candidate has read the DBS declaration
       */
      dbsDeclarationRead?: boolean;
      /**
       * Whether the candidate has any overseas offences
       */
      overseasOffence?: boolean;
      /**
       * The description of the overseas offence
       *
       * @maxLength 1024
       */
      overseasOffenceDescription?: string;
      /**
       * Whether the candidate has a current criminal offence
       */
      currentCriminalOffence?: boolean;
      /**
       * The description of the current criminal offence
       *
       * @maxLength 1024
       */
      currentCriminalOffenceDescription?: string;
      /**
       * Whether the DBS was issued by a business
       */
      dbsIssuedByBusiness?: boolean;
      /**
       * Whether the DBS was issued by the current employer
       */
      dbsIssuedByCurrentEmployer?: boolean;
      /**
       * Whether a Section 128 check is required for the candidate.
       */
      section128Required?: boolean;
      /**
       * The date when the Section 128 check was completed.
       */
      section128Checked?: string;
    };
    /**
     * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
     */
    criminalRecordDeclaration?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * The answers recorded for the point-in-time question set.
       *
       * @maxItems 100
       */
      answers?: {
        /**
         * The identifier of the question being answered.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        questionId?: string;
        /**
         * The stable key of the question being answered.
         *
         * @minLength 1
         * @maxLength 128
         */
        questionKey?: string;
        /**
         * The submitted answer to the question.
         */
        value?: string | string | boolean | number | (void | null);
      }[];
      /**
       * When the question set was submitted.
       *
       * @format date-time
       */
      submittedAt?: string;
      /**
       * The network address recorded when the response was submitted.
       *
       * @maxLength 64
       */
      submittedIp?: string;
      /**
       * When the question configuration captured by this response was last updated.
       *
       * @format date-time
       */
      configurationUpdatedAt?: string;
    };
    /**
     * Whether the candidate confirmed their criminal record declaration.
     */
    criminalRecordCheckConsent?: boolean;
    /**
     * The name used to confirm the criminal record declaration.
     *
     * @maxLength 256
     */
    criminalRecordCheckConsentName?: string;
    /**
     * The date the criminal record declaration was confirmed.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    criminalRecordCheckConsentDate?: string;
    safeInEducation?: {
      /**
       * Whether the candidate is barred from working in education
       */
      barred?: boolean;
      /**
       * The date when the barred status was checked
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      barredCheckDate?: string;
      /**
       * Whether the candidate has a waiver for the barred status
       */
      waiver?: boolean;
      /**
       * Any orders related to the candidate's barred status
       */
      ordersYou?: boolean;
      /**
       * Any orders related to the candidate's barred status for children
       */
      ordersChild?: boolean;
      /**
       * Whether the candidate has any previous safeguarding allegations
       */
      previousSafeguardingAllegations?: boolean;
      /**
       * Whether the candidate has been cautioned
       */
      cautioned?: boolean;
      /**
       * Whether the candidate has a violent offence
       */
      violentOffence?: boolean;
      /**
       * Whether the candidate has lived or worked in another country
       */
      anotherCountry?: boolean;
      /**
       * Whether the candidate has read the Keeping Children Safe in Education document
       */
      readKCSIE?: boolean;
      /**
       * Whether the candidate has read the Disqualification under the Childcare Act guidance
       */
      readChildcareAct?: boolean;
      /**
       * Whether the candidate is prohibited from teaching
       */
      prohibitedFromTeaching?: boolean;
      /**
       * Whether the candidate is known to the police
       */
      knownPolice?: boolean;
      /**
       * Whether the candidate is prohibited from management roles
       */
      prohibitedFromManagement?: boolean;
      /**
       * Whether the candidate has any criminal offences under law
       */
      criminalOffencesLaw?: boolean;
      /**
       * Any additional details regarding the candidate's safe in education status
       */
      safeInEducationDetails?: string;
      safeguardingQuestionaire?: {
        /**
         * What is the primary purpose of safeguarding in a school environment?
         */
        primaryPurposeSafeguarding?:
          | "enhance-academic-performance"
          | "ensure-safe-learning-environment"
          | "promote-healthy-relationships"
          | "facilitate-extracurricular-activities";
        /**
         * Who is typically responsible for coordinating safeguarding efforts within a school?
         */
        typicallyResponsibleCoordinatingSafeguarding?:
          | "janitorial-staff"
          | "parents-association"
          | "school-nurse"
          | "designated-safeguarding-lead";
        /**
         * Which of the following is considered a potential sign of child abuse or neglect?
         */
        signOfAbuse?:
          | "student-excelling"
          | "frequent-extracurricular"
          | "unexplained-injuries"
          | "not-listening";
        /**
         * How should a teacher respond if a student discloses information about potential abuse or harm?
         */
        studentDisclosesInformation?:
          | "ignore-disclosure"
          | "report-disclosure"
          | "share-information"
          | "advise-independent";
        /**
         * What is the purpose of a school's Child Protection Policy?
         */
        schoolsChildProtectionPolicy?:
          | "enforcing-strict-disciplinary-measures"
          | "identifying-troublemakers"
          | "outlining-procedures-for-protecting-students-from-harm"
          | "establishing-academic-achievement-standards";
        /**
         * What does the term 'FGM' stand for in the context of safeguarding?
         */
        fgmDefinition?:
          | "female-general-medicine"
          | "forced-genetic-modification"
          | "female-genital-mutilation"
          | "foundation-gender-equality";
        /**
         * Which of the following is a key principle of online safety for students?
         */
        keyPrincipleOnlineSafetyStudents?:
          | "unlimited-screen-time"
          | "anon-online-interaction"
          | "responsible-digital-citizen"
          | "restrict-edu-sites";
        /**
         * When planning a school trip, what should teachers consider regarding safeguarding?
         */
        teachersConsiderRegardingSafeguarding?:
          | "minimise-supervision"
          | "appropriate-staff-ratios"
          | "exclude-medical-conditions"
          | "allow-roaming";
        /**
         * What is the purpose of a criminal background check for teachers?
         */
        criminalBackgroundCheckTeachers?:
          | "evaluating-teaching-proficiency"
          | "verifying-immigration-status"
          | "assessing-financial-stability"
          | "identifying-criminal-history";
        /**
         * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
         */
        studentConfides?:
          | "assure-secret-safe"
          | "encourage-share-friends"
          | "explain-no-guarantee"
          | "disclose-to-others";
      };
    };
    socialMedia?: {
      /**
       * The Facebook profile URL of the candidate
       *
       * @maxLength 256
       */
      facebook?: string;
      /**
       * The Instagram profile URL of the candidate
       *
       * @maxLength 256
       */
      instagram?: string;
      /**
       * The Twitter profile URL of the candidate
       *
       * @maxLength 256
       */
      twitter?: string;
      /**
       * The LinkedIn profile URL of the candidate
       *
       * @maxLength 256
       */
      linkedIn?: string;
      /**
       * The TikTok profile URL of the candidate
       *
       * @maxLength 256
       */
      tiktok?: string;
      /**
       * Any other social media profile URLs of the candidate
       *
       * @maxLength 256
       */
      others?: string;
      /**
       * Whether the social media profiles have been checked
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      socialMediaChecked?: string;
      /**
       * The date the candidate's Google search check was completed
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      googleCheckedDate?: string;
    };
    /**
     * Whether the candidate has consented to a safe in education check
     */
    safeInEducationCheckConsent?: boolean;
    /**
     * The name of the person who provided consent for the safe in education check
     *
     * @maxLength 256
     */
    safeInEducationCheckConsentName?: string;
    /**
     * The date when consent for the safe in education check was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    safeInEducationCheckConsentDate?: string;
    /**
     * The health record of the individual
     */
    healthRecord?: {
      /**
       * Whether the individual has a physical ability condition
       */
      physicalAbilityCondition?: boolean;
      /**
       * Whether the individual is confused
       */
      confused?: boolean;
      /**
       * Whether the individual has hearing issues
       */
      hearing?: boolean;
      /**
       * Whether the individual has eyesight issues
       */
      eyesight?: boolean;
      /**
       * Whether the individual has depression
       */
      depression?: boolean;
      /**
       * Whether the individual has severe pain
       */
      severePain?: boolean;
      /**
       * Whether the individual has excessive drowsiness
       */
      excessiveDrowsiness?: boolean;
      /**
       * Whether the individual has epilepsy
       */
      epilepsy?: boolean;
      /**
       * Whether the individual has heart issues
       */
      heart?: boolean;
      /**
       * Whether the individual has asthma
       */
      asthma?: boolean;
      /**
       * Whether the individual has mental health issues
       */
      mentalHealth?: boolean;
      /**
       * Whether the individual is an expectant mother
       */
      expectantMother?: boolean;
      /**
       * Whether the individual has allergies
       */
      allergies?: boolean;
      /**
       * Whether the individual is taking medication
       */
      medication?: boolean;
      /**
       * Whether the individual has any health issues
       */
      healthIssues?: boolean;
      /**
       * A description of the individual's health issues
       *
       * @maxLength 2048
       */
      healthIssuesDescription?: string;
      /**
       * Any disability needs of the individual
       *
       * @maxLength 2048
       */
      disabilityNeeds?: string;
      /**
       * Whether the individual has any contagious diseases
       */
      contagiousDiseases?: boolean;
      /**
       * Whether the individual's health affects their ability
       */
      affectAbility?: boolean;
      /**
       * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
       */
      notificationAgree?: boolean;
      /**
       * Whether the individual has opted into the SAS Wellbeing App
       */
      sasOptIn?: boolean;
      /**
       * The individual's signature
       *
       * @maxLength 256
       */
      signature?: string;
      /**
       * The individual's drawn signature in base64url format
       *
       * @maxLength 4096
       */
      drawnSignature?: string;
      /**
       * The date the individual signed the form
       *
       * @format date-time
       */
      signatureDate?: string | null;
      /**
       * Whether the individual has been vaccinated against measles
       */
      vaccinationMeasles?: boolean;
      /**
       * Whether the individual has been vaccinated against mumps
       */
      vaccinationMumps?: boolean;
      /**
       * Whether the individual has been vaccinated against rubella
       */
      vaccinationRubella?: boolean;
      /**
       * Whether the individual has been vaccinated against varicella
       */
      vaccinationVaricella?: boolean;
      /**
       * Whether the individual has been vaccinated against tuberculosis
       */
      vaccinationTuberculosis?: boolean;
      /**
       * Whether the individual has been vaccinated against hepatitis B
       */
      vaccinationHepatitisB?: boolean;
      /**
       * Whether the individual has been vaccinated against hepatitis C
       */
      vaccinationHepatitisC?: boolean;
      /**
       * Whether the individual has been vaccinated against HIV
       */
      vaccinationHIV?: boolean;
      /**
       * Whether the individual has been vaccinated against COVID-19
       */
      vaccinationCovid?: boolean;
      /**
       * Whether the individual has received the 1st dose of the COVID-19 vaccine
       */
      vaccinationCovid1?: boolean;
      /**
       * Whether the individual has received the 2nd dose of the COVID-19 vaccine
       */
      vaccinationCovid2?: boolean;
      /**
       * Whether the individual has received the COVID-19 booster vaccine
       */
      vaccinationCovidBooster?: boolean;
      /**
       * Whether the individual has received the COVID-19 influenza vaccine
       */
      vaccinationCovidInfluenza?: boolean;
      /**
       * Whether the individual has an override for fit to work requirements
       */
      fitToWorkOverride?: boolean;
    };
    /**
     * The candidate's latest submitted health declaration, including the exact questions presented.
     */
    healthDeclaration?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * The answers recorded for the point-in-time question set.
       *
       * @maxItems 100
       */
      answers?: {
        /**
         * The identifier of the question being answered.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        questionId?: string;
        /**
         * The stable key of the question being answered.
         *
         * @minLength 1
         * @maxLength 128
         */
        questionKey?: string;
        /**
         * The submitted answer to the question.
         */
        value?: string | string | boolean | number | (void | null);
      }[];
      /**
       * When the question set was submitted.
       *
       * @format date-time
       */
      submittedAt?: string;
      /**
       * The network address recorded when the response was submitted.
       *
       * @maxLength 64
       */
      submittedIp?: string;
      /**
       * When the question configuration captured by this response was last updated.
       *
       * @format date-time
       */
      configurationUpdatedAt?: string;
    };
    /**
     * Whether the candidate confirmed their health declaration.
     */
    healthRecordCheckConsent?: boolean;
    /**
     * The name used to confirm the health declaration.
     *
     * @maxLength 256
     */
    healthRecordCheckConsentName?: string;
    /**
     * The date the health declaration was confirmed.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    healthRecordCheckConsentDate?: string;
    /**
     * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
     */
    bankDetails?: {
      /**
       * The name on the bank account
       */
      bankAccountName?: string;
      /**
       * The name of the bank
       */
      bankName?: string;
      /**
       * The account number
       */
      accountNumber?: string;
      /**
       * The sort code
       */
      sortCode?: string;
      /**
       * The National Insurance number
       */
      niNumber?: string;
      /**
       * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
       */
      jobOp1?: boolean;
      /**
       * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
       */
      jobOp2?: boolean;
      /**
       * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
       */
      jobOp3?: boolean;
      studentLoan?: {
        /**
         * Boolean flag for if candidate has student loan
         */
        studentLoan?: boolean;
        /**
         * Boolean flag for if candidate is on plan 1
         */
        plan1?: boolean;
        /**
         * Boolean flag for if candidate is on plan 2
         */
        plan2?: boolean;
        /**
         * Boolean flag for if candidate is on plan 4
         */
        plan4?: boolean;
        /**
         * Boolean flag for if candidate has postgrad loan
         */
        postGradLoan?: boolean;
      };
    };
    /**
     * Whether the candidate has confirmed their details are correct.
     */
    declarationCheckConsent?: boolean;
    /**
     * The name of the person who confirmed their details are correct.
     *
     * @maxLength 256
     */
    declarationCheckConsentName?: string;
    /**
     * The date when consent for the declaration check was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    declarationCheckConsentDate?: string;
    /**
     * Whether the candidate has consented to a contract for service
     */
    contractForServiceConsent?: boolean;
    /**
     * The name of the person who provided consent for the contract for service
     *
     * @maxLength 256
     */
    contractForServiceName?: string;
    /**
     * The date when consent for the contract for service was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    contractForServiceDate?: string;
    /**
     * The candidate has completed their onboarding form.
     */
    confirmed?: boolean;
    /**
     * The ID of the user who created the candidate record.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    createdBy?: string;
    /**
     * The ID of the customer associated with the candidate.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customerId?: string;
    /**
     * The migration that created this candidate
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Whether this migrated candidate has been approved
     */
    migrationApproval?: "pending" | "approved" | "rejected";
    /**
     * The user assigned to the candidate. If expanded, a user object else the user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The ID of the current booking for the candidate.
     */
    currentBooking?: string[];
    /**
     * The URL of the candidate's profile photo.
     *
     * @maxLength 256
     */
    profilePhoto?: string;
    /**
     * The pay type for the candidate.
     */
    payType?: "PAYE" | "Umbrella" | "Limited Company" | "Not Applicable";
    /**
     * The pay interval for the candidate.
     */
    payInterval?:
      | "Monthly"
      | "Weekly"
      | "Weekly Pay in Arrears"
      | "Bi-Weekly"
      | any
      | null;
    /**
     * The pay rate for the candidate.
     */
    payRate?: string;
    /**
     * A custom ID for the candidate.
     *
     * @maxLength 256
     */
    customId?: string;
    /**
     * A company-defined identifier used to match the candidate with external records.
     *
     * @maxLength 256
     */
    _internalId?: string;
    /**
     * The date when the candidate's status was last changed.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    statusChangeDate?: string;
    /**
     * Whether the candidate has opted out of communications.
     */
    optOut?: boolean;
    /**
     * The TRN number for the candidate.
     *
     * @maxLength 256
     */
    trnNumber?: string;
    /**
     * The date when the TRN number was last checked.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    trnNumberCheckedDate?: string;
    /**
     * Any notes related to the TRN number.
     *
     * @maxLength 1024
     */
    trnNumberNotes?: string;
    /**
     * The date when the candidate's employment is set to start.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    employmentStartDate?: string;
    /**
     * Whether the candidate has confirmed their Early Years Workforce status.
     */
    ewcConfirmation?: boolean;
    /**
     * The share code for the candidate's Home Office application.
     *
     * @maxLength 256
     */
    homeOfficeShareCode?: string;
    /**
     * Whether the candidate has been archived.
     */
    archived?: boolean;
    /**
     * Whether the candidate is using an umbrella company.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    umbrella?: string;
    /**
     * ID of the PAYE umbrella company.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    payeUmbrella?: string;
    /**
     * The name of the umbrella company the candidate is using.
     *
     * @maxLength 256
     */
    umbrellaCompanyName?: string;
    /**
     * The code of the umbrella company the candidate is using.
     *
     * @maxLength 256
     */
    umbrellaCompanyCode?: string;
    /**
     * The transport method for the candidate.
     */
    transport?:
      | "Drives Own Vehicle"
      | "Use Public Transport"
      | "Access to Another Vehicle"
      | "Bicycle"
      | "No Access"
      | "Other";
    /**
     * Any important notes related to the candidate.
     *
     * @maxLength 1024
     */
    importantNotes?: string;
    /**
     * The date when the candidate was last updated.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    candidateLastUpdated?: string;
    /**
     * The source from which the candidate was obtained.
     */
    source?:
      | (
          | "Company Website"
          | "iCan Teach UK Website"
          | "Apply to Education"
          | "CV Library - CV Search"
          | "CV Library Advert"
          | "CV Library - Watchdog"
          | "Guardian- CV Search"
          | "Guardian Advert"
          | "Indeed - CV Search"
          | "Indeed Advert"
          | "Reed - CV Search"
          | "Reed Advert"
          | "Reed \u2013 Watchdog"
          | "SENploy - Advert"
          | "Total Jobs"
          | "Other Job Board"
          | "Google"
          | "LinkedIn"
          | "Linkedin Recruiter"
          | "LinkedIn Advert"
          | "Referral - School"
          | "Referral - Candidate"
          | "UK University"
          | "Irish University"
          | "Canadian University"
          | "Marketing Campaign"
          | "Woo"
          | "Job Show"
          | "Recruitment Fair"
          | "Career Fair"
          | "Volcanic"
          | "Broadbean"
          | "Headhunt"
          | "Referral"
          | "Direct"
          | "Other"
        )
      | (
          | "Company Website"
          | "Direct Call"
          | "CV Library Watchdog"
          | "CV Library Advert"
          | "CV Library CV Search"
          | "Reed Watchdog"
          | "Reed Advert"
          | "Reed CV Search"
          | "Indeed Watchdog"
          | "Indeed Advert"
          | "Indeed CV Search"
          | "Job Centre"
          | "Returning Candidate"
          | "Referral Candidate"
          | "Referral Client"
          | "Training Provider"
          | "Recruitment Fair"
          | "University/College"
        );
    /**
     * Any other source from which the candidate was obtained.
     *
     * @maxLength 256
     */
    sourceOther?: string;
    /**
     * Whether the candidate has given consent for online checks.
     */
    onlineCheckConsent?: boolean;
    /**
     * The title of the candidate.
     */
    title?:
      | "Mr"
      | "Mrs"
      | "Ms"
      | "Miss"
      | "Dr"
      | "Mx"
      | "Lady"
      | "Lord"
      | "Other";
    /**
     * The pronoun used by the candidate.
     */
    pronoun?:
      | "She/Her/Hers"
      | "He/Him/His"
      | "They/Them/Theirs"
      | "Ze/Hir/Hirs"
      | "Ze/Zir/Zirs"
      | "Other"
      | "Prefer not to say";
    /**
     * The date when the candidate was last updated.
     *
     * @format date-time
     */
    updatedAt?: string;
    /**
     * The date when the candidate was last contacted.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    lastContactedDate?: string;
    /**
     * A short, prominent note shown with the candidate to communicate important context.
     */
    highlight?: string;
    /**
     * The pay scale for the candidate's teaching position.
     *
     * @maxLength 256
     */
    teacherPayScale?: string;
    /**
     * Whether the candidate has a P45.
     */
    p45?: boolean;
    /**
     * The type of role the candidate is applying for.
     */
    roleType?:
      | "Senior Leadership Positions"
      | "Teaching Roles"
      | "School Support Positions";
    /**
     * The Right to Work verification status for the candidate.
     *
     * @maxLength 256
     */
    rtwVerification?: string;
    /**
     * Any notes related to the Right to Work verification status for the candidate.
     *
     * @maxLength 1024
     */
    rtwVerificationNote?: string;
    /**
     * The Right to Work verification status for the candidate.
     *
     * @maxLength 256
     */
    rtwVerificationStatus?: string;
    /**
     * The location where the candidate desires to work.
     *
     * @maxLength 256
     */
    desiredLocation?: string;
    /**
     * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
     */
    complianceUser?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
     */
    watchers?:
      | string[]
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        }[];
    /**
     * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
     *
     * @maxItems 5
     */
    documents?: {
      /**
       * The unique identifier of the document
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The name of the document
       *
       * @maxLength 512
       */
      name?: string;
      /**
       * The file reference used to retrieve the document
       *
       * @maxLength 2048
       */
      reference?: string;
      /**
       * The file's media type
       *
       * @maxLength 256
       */
      fileType?: string;
      /**
       * The area of the platform the document belongs to
       *
       * @maxLength 256
       */
      type?: string;
      /**
       * The configured category of the document
       *
       * @maxLength 256
       */
      docType?: string;
      /**
       * The document's expiry date, when applicable
       *
       * @maxLength 64
       */
      expiry?: string;
      /**
       * The document's issued date, when applicable
       *
       * @maxLength 64
       */
      issued?: string;
      /**
       * Whether the document has been archived
       */
      documentArchived?: boolean;
      /**
       * The date the document was uploaded
       *
       * @format date-time
       */
      createdAt?: string;
    }[];
    /**
     * The total number of documents attached to the candidate.
     *
     * @minimum 0
     */
    documentsCount?: number;
    /**
     * The locations where the candidate desires to work.
     */
    desiredLocationMultiple?: string[];
    /**
     * Daily availability for the requested period. Returned by the availability view only.
     *
     * @maxItems 31
     */
    availabilityDays?: {
      /**
       * Calendar date in YYYY-MM-DD format.
       *
       * @maxLength 10
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      date?: string;
      /**
       * Availability or booking state for this date.
       */
      status?:
        | "available"
        | "unavailable"
        | "booked"
        | "halfDay"
        | "awaiting"
        | "unknown";
      /**
       * Recorded availability, or null when unrecorded.
       */
      available?: boolean | null;
      /**
       * Morning or afternoon availability.
       */
      partial?: "AM" | "PM" | any | null;
      /**
       * Additional availability information.
       *
       * @maxLength 4096
       */
      note?: string | null;
      /**
       * Active bookings on this date.
       *
       * @maxItems 100
       */
      bookings?: {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @maxLength 512
         */
        clientName?: string;
        /**
         * @maxLength 512
         */
        role?: string;
        /**
         * @maxLength 32
         */
        timeDesignation?: string;
      }[];
    }[];
    /**
     * The date when the candidate's availability was last checked.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    availabilityLastChecked?: string;
    /**
     * Whether the candidate is subject to notice period.
     */
    subjectToNotice?: "Unsure" | "Yes" | "No";
    /**
     * The subsidiary subjects the candidate is qualified to teach.
     */
    subsidiarySubject?: string[];
    /**
     * The candidate's preferred employment type.
     */
    employmentPreference?: (
      | "Day To Day"
      | "Long Term"
      | "Fixed Term"
      | "Perm"
    )[];
    /**
     * Any other names the candidate is known by.
     *
     * @maxLength 256
     */
    otherNames?: string;
    /**
     * The name of the candidate's limited company, if applicable.
     *
     * @maxLength 256
     */
    ltdCompany?: string;
    /**
     * Whether the candidate's employment has been verified.
     */
    employmentVerified?: boolean;
    /**
     * The DBS verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSVerification?: string;
    /**
     * Any notes related to the DBS verification status for the candidate.
     *
     * @maxLength 1024
     */
    candidateDBSVerificationNote?: string;
    /**
     * The DBS verification status for the candidate.
     */
    candidateDBSVerificationStatus?: "Cleared" | "Alerts";
    /**
     * The DBS check verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSCheckVerification?: string;
    /**
     * Any notes related to the DBS check verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSCheckVerificationNote?: string;
    /**
     * The DBS check verification status for the candidate.
     */
    candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
    /**
     * Any notes related to the DBS update service verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSUpdateServiceVerificationNote?: string;
    /**
     * The DBS update service verification status for the candidate.
     */
    candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
    /**
     * Any notes related to the TRN verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateTRNVerificationNote?: string;
    /**
     * The TRN verification status for the candidate.
     */
    candidateTRNVerificationStatus?:
      | "Completed Induction"
      | "Not Yet Completed Induction"
      | "Induction Exempt"
      | "ALERTS - Failed Induction"
      | "Induction Not Required";
    /**
     * The health and safety verification status for the candidate.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    candidateHealthSafetyVerification?: string;
    /**
     * Any notes related to the health and safety verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateHealthSafetyVerificationNote?: string;
    /**
     * The health and safety verification status for the candidate.
     */
    candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
    /**
     * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
     */
    onboardingSession?:
      | string
      | {
          /**
           * Unique identifier for the onboarding session
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The URL for the onboarding session
           *
           * @format uri
           */
          url?: string;
          /**
           * The frozen health declaration configuration for this onboarding session.
           */
          healthDeclarationConfig?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * When this question configuration was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
          };
          /**
           * The frozen Criminal Record & Security Checks configuration for this onboarding session.
           */
          criminalRecordConfig?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * When this question configuration was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
          };
        };
    /**
     * The contact record for this candidate. If expanded, a contact object else the contact record ID.
     */
    contact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
        };
    /**
     * Whether the candidate has opted out of 48 hour communications.
     */
    optOut48Hours?: boolean;
    /**
     * Whether the candidate has consented to REC audit checks.
     */
    recAuditConsent?: boolean | null;
    /**
     * The date the candidate's REC audit consent was recorded.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    recAuditConsentDate?: string;
    /**
     * The method through which REC audit consent was captured.
     *
     * @maxLength 256
     */
    recAuditConsentMethod?: string;
    /**
     * The date when the candidate record was created.
     *
     * @format date-time
     */
    createdAt?: string;
    /**
     * The mobile app link status for the candidate.
     */
    candidateMobileAppStatus?:
      | "Unlinked"
      | "Scheduled Unlink"
      | "Linked"
      | "Invited"
      | "Not Invited";
    /**
     * Evertime details for this candidate
     */
    evertime?: {
      /**
       * The Evertime CandidateId used for this candidate when exporting payment runs
       *
       * @maxLength 256
       */
      id?: string;
      /**
       * Whether the matched Evertime candidate's NI number also matches the one held in Edun
       */
      niNumberMatch?: boolean;
    };
    /**
     * Search relevance relative to the strongest result, where 100 is the strongest match.
     *
     * @minimum 0
     * @maximum 100
     */
    relevanceScore?: number;
    /**
     * Candidate fields or attached document excerpts that explain why the candidate matched.
     *
     * @maxItems 5
     */
    matchReasons?: {
      /**
       * Whether the matching information came from the candidate record or an attached document.
       */
      source?: "candidate" | "document";
      /**
       * The candidate or document information that matched the search.
       *
       * @maxLength 256
       */
      field?: string;
      /**
       * A short excerpt showing why the result matched.
       *
       * @maxLength 1000
       */
      text?: string;
      /**
       * The exact terms within the content that matched the search, including synonym matches.
       *
       * @maxItems 25
       */
      matchedTerms?: string[];
      /**
       * The name of the attached document when the match came from a document.
       *
       * @maxLength 512
       */
      documentName?: string | null;
      /**
       * The unique identifier of the attached document when the match came from a document.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      documentId?: string | null;
    }[];
    /**
     * The candidate's distance in miles from the requested location. Present only when location filtering is used.
     *
     * @minimum 0
     */
    distance?: number;
    /**
     * Timesheet Portal details for this candidate
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal contractor GUID used when exporting payment runs
       *
       * @format uuid
       */
      id?: string;
      /**
       * The Timesheet Portal contractor code
       *
       * @maxLength 256
       */
      code?: string;
    };
    /**
     * Invite to candidate to link their mobile app.
     */
    mobileAppLinkInvite?: {
      /**
       * Boolean status of whether the link invite is being used
       *
       * @default false
       */
      isUsed?: boolean;
      /**
       * Creation date of the invite
       *
       * @format date-time
       * @default 2026-10-30T13:39:09.027Z
       */
      createdAt?: string;
      /**
       * ID of candidate using this link
       *
       * @pattern ^[0-9a-f]{24}$
       */
      usedBy?: string;
      /**
       * Date of when link used
       *
       * @format date-time
       */
      usedAt?: string;
      /**
       * If unlinked, ID of user that unlinked this candidate's link
       *
       * @pattern ^[0-9a-f]{24}$
       */
      unlinkedBy?: string;
      /**
       * If unlinked, date of when this link was unlinked
       *
       * @format date-time
       */
      unlinkedAt?: string;
      /**
       * If deleted, ID of user that deleted this candidate's link
       *
       * @pattern ^[0-9a-f]{24}$
       */
      deletedBy?: string;
      /**
       * If deleted, date of when this link was deleted
       *
       * @format date-time
       */
      deletedAt?: string;
      /**
       * Current status of this mobile app link
       */
      status?:
        | "Sent"
        | "Linked"
        | "Unlinked"
        | "ScheduledUnlink"
        | "Not Sent"
        | "Rescinded";
    };
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
  /**
   * Availability totals before availability-status filtering and pagination. Present only when an availability period is supplied.
   */
  metadata?: {
    /**
     * Matching candidate totals before availability status filtering and pagination. A candidate can count in several statuses.
     */
    summary: {
      /**
       * @minimum 0
       */
      total: number;
      /**
       * @minimum 0
       */
      available: number;
      /**
       * @minimum 0
       */
      unavailable: number;
      /**
       * @minimum 0
       */
      booked: number;
      /**
       * @minimum 0
       */
      awaiting: number;
      /**
       * @minimum 0
       */
      notRecorded: number;
    };
  };
  /**
   * How the AI search query was interpreted. Present only when AI search is used.
   */
  aiSearch?: {
    /**
     * The remaining candidate and document search text after structured filters were extracted.
     *
     * @maxLength 256
     */
    query: string;
    /**
     * Terms omitted from relevance scoring because they describe candidate context already implied by the search.
     *
     * @maxItems 10
     */
    ignoredTerms: string[];
    /**
     * Structured candidate filters interpreted from the search query.
     */
    filters: {
      /**
       * Candidate statuses interpreted from the search query.
       *
       * @maxItems 10
       */
      statuses: (
        | "Pending"
        | "Approved"
        | "Rejected"
        | "Anonymised"
        | "Archived"
        | "Enquired"
        | "Hold"
        | "Future"
        | "DNC"
        | "Pre"
      )[];
      /**
       * Candidate location criteria interpreted from the search query.
       */
      location?: {
        /**
         * The location interpreted from the search query.
         *
         * @minLength 1
         * @maxLength 256
         */
        name: string;
        /**
         * The maximum distance from the interpreted location, when specified or implied.
         */
        distance?: {
          /**
           * The distance used to limit location-based search results.
           *
           * @minimum 0
           * @exclusiveMinimum true
           * @maximum 500
           */
          value: number;
          /**
           * The unit used for the search distance.
           */
          unit: "miles" | "kilometres";
        };
      };
    };
  };
};

export type GetCandidatesVariables = {
  queryParams?: GetCandidatesQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetCandidates = (
  variables: GetCandidatesVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetCandidatesResponse,
    GetCandidatesError,
    undefined,
    {},
    GetCandidatesQueryParams,
    {}
  >({ url: "/candidates", method: "get", ...variables, signal });

export function getCandidatesQuery(variables: GetCandidatesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetCandidatesResponse>;
};

export function getCandidatesQuery(
  variables: GetCandidatesVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetCandidatesResponse>)
    | reactQuery.SkipToken;
};

export function getCandidatesQuery(
  variables: GetCandidatesVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/candidates",
      operationId: "getCandidates",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetCandidates(variables, signal),
  };
}

export const useSuspenseGetCandidates = <TData = GetCandidatesResponse,>(
  variables: GetCandidatesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCandidatesResponse,
      GetCandidatesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetCandidatesResponse,
    GetCandidatesError,
    TData
  >({
    ...getCandidatesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetCandidates = <TData = GetCandidatesResponse,>(
  variables: GetCandidatesVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCandidatesResponse,
      GetCandidatesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetCandidatesResponse, GetCandidatesError, TData>({
    ...getCandidatesQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateCandidateError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 501;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not implemented
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateCandidateResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the candidate
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The forename of the candidate
     *
     * @minLength 1
     * @maxLength 256
     */
    forename?: string;
    /**
     * The surname of the candidate
     *
     * @minLength 1
     * @maxLength 256
     */
    surname?: string;
    /**
     * Any previous names of the candidate
     *
     * @maxLength 256
     */
    previousNames?: string;
    /**
     * The email of the candidate. This will be used for communication.
     *
     * @format email
     */
    email?: string;
    /**
     * The telephone number of the candidate
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * The mobile number of the candidate
     *
     * @maxLength 32
     */
    mobile?: string;
    /**
     * The occupation of the candidate
     */
    occupation?:
      | "Teacher"
      | "Cover Supervisor"
      | "Teaching Assistant"
      | "School Support Staff"
      | "Primary Teacher"
      | "Secondary Teacher"
      | "Head Teacher"
      | "Senior Leader"
      | "Tutor"
      | "Mentor"
      | "Learning Support Assistant"
      | "Finance Officer"
      | "HR Administrator"
      | "HR Manager"
      | "Office Manager"
      | "Administrator"
      | "Personal Assistant"
      | "Pastoral Manager"
      | "Receptionist"
      | "Instructor"
      | "Caretaker"
      | "Nursery Assistant"
      | "Nursery Nurse"
      | "Deputy Nursery Manager"
      | "Lunchtime Supervisor"
      | "Classroom Graduate"
      | "Primary Cover Supervisor"
      | "HLTA"
      | "Nurture Teacher"
      | "Nurture Mentor"
      | "SENDCo"
      | "Registered Manager"
      | "Regional Manager"
      | "Education Director"
      | "Residential Director"
      | "Deputy Manager"
      | "Residential Support Worker"
      | "Sports Coach"
      | "Executive Headteacher"
      | "General Practitioner"
      | "Advance Nurse Practitioner (ANP)"
      | "Emergency Nurse Practitioner (ENP)"
      | "Advanced Clinical Practitioner (ACP)"
      | "Emergency Clinical Practitioner (ECP)"
      | "Consultant Psychiatrist"
      | "Psychiatrist \u2013 SPR"
      | "IT Technician"
      | "Art Technician"
      | "DT Technician"
      | "Science Technician"
      | "Healthcare Assistant/ Residential Support Worker"
      | "Healthcare Assistant"
      | "Senior Healthcare Assistant"
      | "Team Leader"
      | "Registered General Nurse"
      | "Registered Mental Nurse"
      | "Registered Nurse Learning Disabilities"
      | "Overseas Trained Teacher";
    /**
     * The subject the candidate specializes in
     *
     * @maxLength 256
     */
    subject?: string;
    /**
     * The address of the resource
     */
    address?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The qualifications of the candidate
     *
     * @maxItems 32
     */
    qualifications?: {
      /**
       * The type of the qualification
       *
       * @maxLength 256
       */
      type?: string;
      /**
       * The place where the qualification was obtained
       *
       * @maxLength 256
       */
      place?: string;
      /**
       * The name of the qualification
       *
       * @maxLength 256
       */
      qualificationName?: string;
      /**
       * The date the qualification was obtained
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      date?: string;
      /**
       * The unique identifier of the qualification
       *
       * @maxLength 256
       */
      qualificationId?: string;
      /**
       * The order index of the qualification
       *
       * @minimum 0
       */
      orderIndex?: number;
      /**
       * The expiry date of the qualification
       *
       * @maxLength 256
       */
      qualificationExpiry?: string;
      /**
       * Whether to exclude this qualification from vetting
       */
      excludeFromVetting?: boolean;
      /**
       * The date the qualification was created
       *
       * @format date-time
       */
      createdAt?: string | null;
    }[];
    /**
     * The employment history of the candidate
     *
     * @maxItems 32
     */
    employment?: {
      /**
       * The name of the employer
       *
       * @maxLength 256
       */
      employerName?: string;
      /**
       * The date the candidate started employment
       *
       * @maxLength 256
       */
      dateStarted?: string;
      /**
       * The date the candidate ended employment
       *
       * @maxLength 256
       */
      dateEnded?: string;
      /**
       * The job title of the candidate
       *
       * @maxLength 256
       */
      jobTitle?: string;
      /**
       * The reason the candidate left this employment
       *
       * @maxLength 1024
       */
      reasonForLeaving?: string;
      /**
       * The duties of the candidate in this role. Consultant only, visible on Edun CV
       *
       * @maxLength 1024
       */
      duties?: string;
      /**
       * The salary of the candidate in this role
       *
       * @maxLength 256
       */
      salary?: string;
      /**
       * The address of the candidate's reference for this employment
       */
      referenceAddress?: {
        /**
         * The first line of the address
         *
         * @maxLength 256
         */
        addressLine1?: string;
        /**
         * The second line of the address
         *
         * @maxLength 256
         */
        addressLine2?: string;
        /**
         * The third line of the address
         *
         * @maxLength 256
         */
        addressLine3?: string;
        /**
         * The city of the address
         *
         * @maxLength 256
         */
        city?: string;
        /**
         * The county of the address
         *
         * @maxLength 256
         */
        county?: string;
        /**
         * The borough of the address
         *
         * @maxLength 256
         */
        borough?: string;
        /**
         * The postcode of the address
         *
         * @maxLength 10
         */
        postcode?: string;
        /**
         * The country of the address
         *
         * @maxLength 256
         */
        country?: string;
        /**
         * The latitude of the address
         *
         * @minimum -90
         * @maximum 90
         */
        lat?: number;
        /**
         * The longitude of the address
         *
         * @minimum -180
         * @maximum 180
         */
        lng?: number;
      };
      /**
       * The unique identifier for this employment record
       *
       * @maxLength 256
       */
      employmentId?: string;
      /**
       * The order index of this employment record
       *
       * @minimum 0
       */
      orderIndex?: number;
    }[];
    /**
     * The references for the candidate
     *
     * @maxItems 32
     */
    references?: {
      /**
       * The title of the reference
       *
       * @maxLength 16
       */
      referenceTitle?: string;
      /**
       * The forename of the reference
       *
       * @maxLength 256
       */
      referenceForename?: string;
      /**
       * The surname of the reference
       *
       * @maxLength 256
       */
      referenceSurname?: string;
      /**
       * The company of the reference
       *
       * @maxLength 256
       */
      referenceCompany?: string;
      /**
       * The position of the reference
       *
       * @maxLength 256
       */
      referencePosition?: string;
      /**
       * The email of the reference
       *
       * @format email
       */
      referenceEmail?: string;
      /**
       * The telephone number of the reference
       *
       * @maxLength 32
       */
      referenceTelephone?: string;
      /**
       * The type of the reference
       */
      referenceType?:
        | "Education"
        | "Professional"
        | "Character"
        | "Factual"
        | "Academic"
        | "Volunteer";
      /**
       * The duration of the reference
       *
       * @maxLength 128
       */
      referenceDuration?: string;
      /**
       * The ID of the reference
       *
       * @maxLength 256
       */
      referenceId?: string;
      /**
       * This field only applies if the reference immediate consent question is enabled
       * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
       */
      referenceApply?: boolean;
      /**
       * Any additional notes about the reference
       *
       * @maxLength 1024
       */
      referenceNote?: string;
      /**
       * The start date of the reference
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      dateFrom?: string;
      /**
       * The end date of the reference
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      dateTo?: string;
    }[];
    /**
     * Whether the candidate has consented to a reference check
     */
    referenceCheckConsent?: boolean;
    /**
     * The name of the person who provided consent for the reference check
     *
     * @maxLength 256
     */
    referenceCheckConsentName?: string;
    /**
     * The date when consent for the reference check was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    referenceCheckConsentDate?: string;
    /**
     * The date of birth of the candidate
     *
     * @format date-time
     */
    dob?: string | null;
    /**
     * The sex of the candidate
     */
    sex?: "Male" | "Female" | "Other" | "Unknown";
    /**
     * The status of the candidate
     */
    status?:
      | "Pending"
      | "Approved"
      | "Rejected"
      | "Anonymised"
      | "Archived"
      | "Enquired"
      | "Hold"
      | "Future"
      | "DNC"
      | "Pre";
    /**
     * The current status of the onboarding process for the candidate
     */
    onboardingStatus?: "Not Started" | "In Progress" | "Completed";
    /**
     * Whether the onboarding information has been sent to the candidate
     */
    onboardingSent?: boolean;
    nextOfKin?: {
      /**
       * The name of the next of kin
       *
       * @maxLength 256
       */
      nextOfKinName?: string;
      /**
       * The relationship of the next of kin to the candidate
       *
       * @maxLength 256
       */
      nextOfKinRelationship?: string;
      /**
       * The telephone number of the next of kin
       *
       * @maxLength 32
       */
      nextOfKinTelephone?: string;
      /**
       * The email address of the next of kin
       *
       * @format email
       */
      nextOfKinEmail?: string;
      nextOfKinAddress?: {
        /**
         * The first line of the address
         *
         * @maxLength 256
         */
        addressLine1?: string;
        /**
         * The second line of the address
         *
         * @maxLength 256
         */
        addressLine2?: string;
        /**
         * The third line of the address
         *
         * @maxLength 256
         */
        addressLine3?: string;
        /**
         * The city of the address
         *
         * @maxLength 256
         */
        city?: string;
        /**
         * The county of the address
         *
         * @maxLength 256
         */
        county?: string;
        /**
         * The borough of the address
         *
         * @maxLength 256
         */
        borough?: string;
        /**
         * The postcode of the address
         *
         * @maxLength 10
         */
        postcode?: string;
        /**
         * The country of the address
         *
         * @maxLength 256
         */
        country?: string;
        /**
         * The latitude of the address
         *
         * @minimum -90
         * @maximum 90
         */
        lat?: number;
        /**
         * The longitude of the address
         *
         * @minimum -180
         * @maximum 180
         */
        lng?: number;
      };
    };
    criminalRecord?: {
      /**
       * Whether the candidate has a DBS certificate
       */
      dbsHeld?: boolean;
      /**
       * The unique identifier for the DBS certificate
       *
       * @maxLength 256
       */
      dbsNumber?: string;
      /**
       * The type of DBS check
       */
      dbsType?:
        | "Basic"
        | "Standard"
        | "Enhanced"
        | "Enhanced with Barred List"
        | "Child workforce"
        | "Child and adult workforce"
        | "Adult workforce";
      /**
       * The date when the DBS check was last performed
       *
       * @maxLength 256
       */
      dbsLastChecked?: string;
      /**
       * The DBS update service number for the candidate
       *
       * @maxLength 256
       */
      dbsUpdateServiceNumber?: string;
      /**
       * The result of the prohibition check
       *
       * @maxLength 256
       */
      prohibitionCheck?: string;
      /**
       * The result of the overseas check
       *
       * @maxLength 256
       */
      overseasCheck?: string;
      /**
       * Any additional notes regarding the overseas check
       *
       * @maxLength 256
       */
      overseasCheckNote?: string;
      /**
       * Whether the candidate has a convicted offence
       */
      convictedOffence?: boolean;
      /**
       * The description of the convicted offence
       *
       * @maxLength 1024
       */
      convictedOffenceDescription?: string;
      /**
       * Whether the candidate has any convictions
       */
      convictions?: boolean;
      /**
       * The description of the convictions
       *
       * @maxLength 1024
       */
      convictionsDescription?: string;
      /**
       * Whether the candidate is registered to the DBS update service
       */
      registeredToUpdateService?: boolean;
      /**
       * Whether the candidate consents to update service checks
       */
      consentUpdateServiceChecks?: boolean;
      /**
       * Any additional notes regarding the candidate's criminal record
       *
       * @maxLength 1024
       */
      note?: string;
      /**
       * The date when the update service check was performed
       *
       * @maxLength 64
       */
      updateServiceCheckDate?: string;
      /**
       * Whether the candidate has lived abroad
       */
      abroad?: boolean;
      /**
       * Any additional notes regarding the overseas declaration
       *
       * @maxLength 1024
       */
      overseasDeclarationExtended?: string;
      /**
       * Whether the candidate has been subject to educational disciplinary proceedings
       */
      educationalDisciplinaryProceedings?: boolean;
      /**
       * Any comments regarding the DBS check
       *
       * @maxLength 1024
       */
      dbsComments?: string;
      /**
       * Whether the candidate has read the DBS declaration
       */
      dbsDeclarationRead?: boolean;
      /**
       * Whether the candidate has any overseas offences
       */
      overseasOffence?: boolean;
      /**
       * The description of the overseas offence
       *
       * @maxLength 1024
       */
      overseasOffenceDescription?: string;
      /**
       * Whether the candidate has a current criminal offence
       */
      currentCriminalOffence?: boolean;
      /**
       * The description of the current criminal offence
       *
       * @maxLength 1024
       */
      currentCriminalOffenceDescription?: string;
      /**
       * Whether the DBS was issued by a business
       */
      dbsIssuedByBusiness?: boolean;
      /**
       * Whether the DBS was issued by the current employer
       */
      dbsIssuedByCurrentEmployer?: boolean;
      /**
       * Whether a Section 128 check is required for the candidate.
       */
      section128Required?: boolean;
      /**
       * The date when the Section 128 check was completed.
       */
      section128Checked?: string;
    };
    /**
     * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
     */
    criminalRecordDeclaration?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * The answers recorded for the point-in-time question set.
       *
       * @maxItems 100
       */
      answers?: {
        /**
         * The identifier of the question being answered.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        questionId?: string;
        /**
         * The stable key of the question being answered.
         *
         * @minLength 1
         * @maxLength 128
         */
        questionKey?: string;
        /**
         * The submitted answer to the question.
         */
        value?: string | string | boolean | number | (void | null);
      }[];
      /**
       * When the question set was submitted.
       *
       * @format date-time
       */
      submittedAt?: string;
      /**
       * The network address recorded when the response was submitted.
       *
       * @maxLength 64
       */
      submittedIp?: string;
      /**
       * When the question configuration captured by this response was last updated.
       *
       * @format date-time
       */
      configurationUpdatedAt?: string;
    };
    /**
     * Whether the candidate confirmed their criminal record declaration.
     */
    criminalRecordCheckConsent?: boolean;
    /**
     * The name used to confirm the criminal record declaration.
     *
     * @maxLength 256
     */
    criminalRecordCheckConsentName?: string;
    /**
     * The date the criminal record declaration was confirmed.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    criminalRecordCheckConsentDate?: string;
    safeInEducation?: {
      /**
       * Whether the candidate is barred from working in education
       */
      barred?: boolean;
      /**
       * The date when the barred status was checked
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      barredCheckDate?: string;
      /**
       * Whether the candidate has a waiver for the barred status
       */
      waiver?: boolean;
      /**
       * Any orders related to the candidate's barred status
       */
      ordersYou?: boolean;
      /**
       * Any orders related to the candidate's barred status for children
       */
      ordersChild?: boolean;
      /**
       * Whether the candidate has any previous safeguarding allegations
       */
      previousSafeguardingAllegations?: boolean;
      /**
       * Whether the candidate has been cautioned
       */
      cautioned?: boolean;
      /**
       * Whether the candidate has a violent offence
       */
      violentOffence?: boolean;
      /**
       * Whether the candidate has lived or worked in another country
       */
      anotherCountry?: boolean;
      /**
       * Whether the candidate has read the Keeping Children Safe in Education document
       */
      readKCSIE?: boolean;
      /**
       * Whether the candidate has read the Disqualification under the Childcare Act guidance
       */
      readChildcareAct?: boolean;
      /**
       * Whether the candidate is prohibited from teaching
       */
      prohibitedFromTeaching?: boolean;
      /**
       * Whether the candidate is known to the police
       */
      knownPolice?: boolean;
      /**
       * Whether the candidate is prohibited from management roles
       */
      prohibitedFromManagement?: boolean;
      /**
       * Whether the candidate has any criminal offences under law
       */
      criminalOffencesLaw?: boolean;
      /**
       * Any additional details regarding the candidate's safe in education status
       */
      safeInEducationDetails?: string;
      safeguardingQuestionaire?: {
        /**
         * What is the primary purpose of safeguarding in a school environment?
         */
        primaryPurposeSafeguarding?:
          | "enhance-academic-performance"
          | "ensure-safe-learning-environment"
          | "promote-healthy-relationships"
          | "facilitate-extracurricular-activities";
        /**
         * Who is typically responsible for coordinating safeguarding efforts within a school?
         */
        typicallyResponsibleCoordinatingSafeguarding?:
          | "janitorial-staff"
          | "parents-association"
          | "school-nurse"
          | "designated-safeguarding-lead";
        /**
         * Which of the following is considered a potential sign of child abuse or neglect?
         */
        signOfAbuse?:
          | "student-excelling"
          | "frequent-extracurricular"
          | "unexplained-injuries"
          | "not-listening";
        /**
         * How should a teacher respond if a student discloses information about potential abuse or harm?
         */
        studentDisclosesInformation?:
          | "ignore-disclosure"
          | "report-disclosure"
          | "share-information"
          | "advise-independent";
        /**
         * What is the purpose of a school's Child Protection Policy?
         */
        schoolsChildProtectionPolicy?:
          | "enforcing-strict-disciplinary-measures"
          | "identifying-troublemakers"
          | "outlining-procedures-for-protecting-students-from-harm"
          | "establishing-academic-achievement-standards";
        /**
         * What does the term 'FGM' stand for in the context of safeguarding?
         */
        fgmDefinition?:
          | "female-general-medicine"
          | "forced-genetic-modification"
          | "female-genital-mutilation"
          | "foundation-gender-equality";
        /**
         * Which of the following is a key principle of online safety for students?
         */
        keyPrincipleOnlineSafetyStudents?:
          | "unlimited-screen-time"
          | "anon-online-interaction"
          | "responsible-digital-citizen"
          | "restrict-edu-sites";
        /**
         * When planning a school trip, what should teachers consider regarding safeguarding?
         */
        teachersConsiderRegardingSafeguarding?:
          | "minimise-supervision"
          | "appropriate-staff-ratios"
          | "exclude-medical-conditions"
          | "allow-roaming";
        /**
         * What is the purpose of a criminal background check for teachers?
         */
        criminalBackgroundCheckTeachers?:
          | "evaluating-teaching-proficiency"
          | "verifying-immigration-status"
          | "assessing-financial-stability"
          | "identifying-criminal-history";
        /**
         * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
         */
        studentConfides?:
          | "assure-secret-safe"
          | "encourage-share-friends"
          | "explain-no-guarantee"
          | "disclose-to-others";
      };
    };
    socialMedia?: {
      /**
       * The Facebook profile URL of the candidate
       *
       * @maxLength 256
       */
      facebook?: string;
      /**
       * The Instagram profile URL of the candidate
       *
       * @maxLength 256
       */
      instagram?: string;
      /**
       * The Twitter profile URL of the candidate
       *
       * @maxLength 256
       */
      twitter?: string;
      /**
       * The LinkedIn profile URL of the candidate
       *
       * @maxLength 256
       */
      linkedIn?: string;
      /**
       * The TikTok profile URL of the candidate
       *
       * @maxLength 256
       */
      tiktok?: string;
      /**
       * Any other social media profile URLs of the candidate
       *
       * @maxLength 256
       */
      others?: string;
      /**
       * Whether the social media profiles have been checked
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      socialMediaChecked?: string;
      /**
       * The date the candidate's Google search check was completed
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      googleCheckedDate?: string;
    };
    /**
     * Whether the candidate has consented to a safe in education check
     */
    safeInEducationCheckConsent?: boolean;
    /**
     * The name of the person who provided consent for the safe in education check
     *
     * @maxLength 256
     */
    safeInEducationCheckConsentName?: string;
    /**
     * The date when consent for the safe in education check was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    safeInEducationCheckConsentDate?: string;
    /**
     * The health record of the individual
     */
    healthRecord?: {
      /**
       * Whether the individual has a physical ability condition
       */
      physicalAbilityCondition?: boolean;
      /**
       * Whether the individual is confused
       */
      confused?: boolean;
      /**
       * Whether the individual has hearing issues
       */
      hearing?: boolean;
      /**
       * Whether the individual has eyesight issues
       */
      eyesight?: boolean;
      /**
       * Whether the individual has depression
       */
      depression?: boolean;
      /**
       * Whether the individual has severe pain
       */
      severePain?: boolean;
      /**
       * Whether the individual has excessive drowsiness
       */
      excessiveDrowsiness?: boolean;
      /**
       * Whether the individual has epilepsy
       */
      epilepsy?: boolean;
      /**
       * Whether the individual has heart issues
       */
      heart?: boolean;
      /**
       * Whether the individual has asthma
       */
      asthma?: boolean;
      /**
       * Whether the individual has mental health issues
       */
      mentalHealth?: boolean;
      /**
       * Whether the individual is an expectant mother
       */
      expectantMother?: boolean;
      /**
       * Whether the individual has allergies
       */
      allergies?: boolean;
      /**
       * Whether the individual is taking medication
       */
      medication?: boolean;
      /**
       * Whether the individual has any health issues
       */
      healthIssues?: boolean;
      /**
       * A description of the individual's health issues
       *
       * @maxLength 2048
       */
      healthIssuesDescription?: string;
      /**
       * Any disability needs of the individual
       *
       * @maxLength 2048
       */
      disabilityNeeds?: string;
      /**
       * Whether the individual has any contagious diseases
       */
      contagiousDiseases?: boolean;
      /**
       * Whether the individual's health affects their ability
       */
      affectAbility?: boolean;
      /**
       * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
       */
      notificationAgree?: boolean;
      /**
       * Whether the individual has opted into the SAS Wellbeing App
       */
      sasOptIn?: boolean;
      /**
       * The individual's signature
       *
       * @maxLength 256
       */
      signature?: string;
      /**
       * The individual's drawn signature in base64url format
       *
       * @maxLength 4096
       */
      drawnSignature?: string;
      /**
       * The date the individual signed the form
       *
       * @format date-time
       */
      signatureDate?: string | null;
      /**
       * Whether the individual has been vaccinated against measles
       */
      vaccinationMeasles?: boolean;
      /**
       * Whether the individual has been vaccinated against mumps
       */
      vaccinationMumps?: boolean;
      /**
       * Whether the individual has been vaccinated against rubella
       */
      vaccinationRubella?: boolean;
      /**
       * Whether the individual has been vaccinated against varicella
       */
      vaccinationVaricella?: boolean;
      /**
       * Whether the individual has been vaccinated against tuberculosis
       */
      vaccinationTuberculosis?: boolean;
      /**
       * Whether the individual has been vaccinated against hepatitis B
       */
      vaccinationHepatitisB?: boolean;
      /**
       * Whether the individual has been vaccinated against hepatitis C
       */
      vaccinationHepatitisC?: boolean;
      /**
       * Whether the individual has been vaccinated against HIV
       */
      vaccinationHIV?: boolean;
      /**
       * Whether the individual has been vaccinated against COVID-19
       */
      vaccinationCovid?: boolean;
      /**
       * Whether the individual has received the 1st dose of the COVID-19 vaccine
       */
      vaccinationCovid1?: boolean;
      /**
       * Whether the individual has received the 2nd dose of the COVID-19 vaccine
       */
      vaccinationCovid2?: boolean;
      /**
       * Whether the individual has received the COVID-19 booster vaccine
       */
      vaccinationCovidBooster?: boolean;
      /**
       * Whether the individual has received the COVID-19 influenza vaccine
       */
      vaccinationCovidInfluenza?: boolean;
      /**
       * Whether the individual has an override for fit to work requirements
       */
      fitToWorkOverride?: boolean;
    };
    /**
     * The candidate's latest submitted health declaration, including the exact questions presented.
     */
    healthDeclaration?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * The answers recorded for the point-in-time question set.
       *
       * @maxItems 100
       */
      answers?: {
        /**
         * The identifier of the question being answered.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        questionId?: string;
        /**
         * The stable key of the question being answered.
         *
         * @minLength 1
         * @maxLength 128
         */
        questionKey?: string;
        /**
         * The submitted answer to the question.
         */
        value?: string | string | boolean | number | (void | null);
      }[];
      /**
       * When the question set was submitted.
       *
       * @format date-time
       */
      submittedAt?: string;
      /**
       * The network address recorded when the response was submitted.
       *
       * @maxLength 64
       */
      submittedIp?: string;
      /**
       * When the question configuration captured by this response was last updated.
       *
       * @format date-time
       */
      configurationUpdatedAt?: string;
    };
    /**
     * Whether the candidate confirmed their health declaration.
     */
    healthRecordCheckConsent?: boolean;
    /**
     * The name used to confirm the health declaration.
     *
     * @maxLength 256
     */
    healthRecordCheckConsentName?: string;
    /**
     * The date the health declaration was confirmed.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    healthRecordCheckConsentDate?: string;
    /**
     * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
     */
    bankDetails?: {
      /**
       * The name on the bank account
       */
      bankAccountName?: string;
      /**
       * The name of the bank
       */
      bankName?: string;
      /**
       * The account number
       */
      accountNumber?: string;
      /**
       * The sort code
       */
      sortCode?: string;
      /**
       * The National Insurance number
       */
      niNumber?: string;
      /**
       * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
       */
      jobOp1?: boolean;
      /**
       * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
       */
      jobOp2?: boolean;
      /**
       * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
       */
      jobOp3?: boolean;
      studentLoan?: {
        /**
         * Boolean flag for if candidate has student loan
         */
        studentLoan?: boolean;
        /**
         * Boolean flag for if candidate is on plan 1
         */
        plan1?: boolean;
        /**
         * Boolean flag for if candidate is on plan 2
         */
        plan2?: boolean;
        /**
         * Boolean flag for if candidate is on plan 4
         */
        plan4?: boolean;
        /**
         * Boolean flag for if candidate has postgrad loan
         */
        postGradLoan?: boolean;
      };
    };
    /**
     * Whether the candidate has confirmed their details are correct.
     */
    declarationCheckConsent?: boolean;
    /**
     * The name of the person who confirmed their details are correct.
     *
     * @maxLength 256
     */
    declarationCheckConsentName?: string;
    /**
     * The date when consent for the declaration check was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    declarationCheckConsentDate?: string;
    /**
     * Whether the candidate has consented to a contract for service
     */
    contractForServiceConsent?: boolean;
    /**
     * The name of the person who provided consent for the contract for service
     *
     * @maxLength 256
     */
    contractForServiceName?: string;
    /**
     * The date when consent for the contract for service was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    contractForServiceDate?: string;
    /**
     * The candidate has completed their onboarding form.
     */
    confirmed?: boolean;
    /**
     * The ID of the user who created the candidate record.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    createdBy?: string;
    /**
     * The ID of the customer associated with the candidate.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customerId?: string;
    /**
     * The migration that created this candidate
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Whether this migrated candidate has been approved
     */
    migrationApproval?: "pending" | "approved" | "rejected";
    /**
     * The user assigned to the candidate. If expanded, a user object else the user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The ID of the current booking for the candidate.
     */
    currentBooking?: string[];
    /**
     * The URL of the candidate's profile photo.
     *
     * @maxLength 256
     */
    profilePhoto?: string;
    /**
     * The pay type for the candidate.
     */
    payType?: "PAYE" | "Umbrella" | "Limited Company" | "Not Applicable";
    /**
     * The pay interval for the candidate.
     */
    payInterval?:
      | "Monthly"
      | "Weekly"
      | "Weekly Pay in Arrears"
      | "Bi-Weekly"
      | any
      | null;
    /**
     * The pay rate for the candidate.
     */
    payRate?: string;
    /**
     * A custom ID for the candidate.
     *
     * @maxLength 256
     */
    customId?: string;
    /**
     * A company-defined identifier used to match the candidate with external records.
     *
     * @maxLength 256
     */
    _internalId?: string;
    /**
     * The date when the candidate's status was last changed.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    statusChangeDate?: string;
    /**
     * Whether the candidate has opted out of communications.
     */
    optOut?: boolean;
    /**
     * The TRN number for the candidate.
     *
     * @maxLength 256
     */
    trnNumber?: string;
    /**
     * The date when the TRN number was last checked.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    trnNumberCheckedDate?: string;
    /**
     * Any notes related to the TRN number.
     *
     * @maxLength 1024
     */
    trnNumberNotes?: string;
    /**
     * The date when the candidate's employment is set to start.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    employmentStartDate?: string;
    /**
     * Whether the candidate has confirmed their Early Years Workforce status.
     */
    ewcConfirmation?: boolean;
    /**
     * The share code for the candidate's Home Office application.
     *
     * @maxLength 256
     */
    homeOfficeShareCode?: string;
    /**
     * Whether the candidate has been archived.
     */
    archived?: boolean;
    /**
     * Whether the candidate is using an umbrella company.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    umbrella?: string;
    /**
     * ID of the PAYE umbrella company.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    payeUmbrella?: string;
    /**
     * The name of the umbrella company the candidate is using.
     *
     * @maxLength 256
     */
    umbrellaCompanyName?: string;
    /**
     * The code of the umbrella company the candidate is using.
     *
     * @maxLength 256
     */
    umbrellaCompanyCode?: string;
    /**
     * The transport method for the candidate.
     */
    transport?:
      | "Drives Own Vehicle"
      | "Use Public Transport"
      | "Access to Another Vehicle"
      | "Bicycle"
      | "No Access"
      | "Other";
    /**
     * Any important notes related to the candidate.
     *
     * @maxLength 1024
     */
    importantNotes?: string;
    /**
     * The date when the candidate was last updated.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    candidateLastUpdated?: string;
    /**
     * The source from which the candidate was obtained.
     */
    source?:
      | (
          | "Company Website"
          | "iCan Teach UK Website"
          | "Apply to Education"
          | "CV Library - CV Search"
          | "CV Library Advert"
          | "CV Library - Watchdog"
          | "Guardian- CV Search"
          | "Guardian Advert"
          | "Indeed - CV Search"
          | "Indeed Advert"
          | "Reed - CV Search"
          | "Reed Advert"
          | "Reed \u2013 Watchdog"
          | "SENploy - Advert"
          | "Total Jobs"
          | "Other Job Board"
          | "Google"
          | "LinkedIn"
          | "Linkedin Recruiter"
          | "LinkedIn Advert"
          | "Referral - School"
          | "Referral - Candidate"
          | "UK University"
          | "Irish University"
          | "Canadian University"
          | "Marketing Campaign"
          | "Woo"
          | "Job Show"
          | "Recruitment Fair"
          | "Career Fair"
          | "Volcanic"
          | "Broadbean"
          | "Headhunt"
          | "Referral"
          | "Direct"
          | "Other"
        )
      | (
          | "Company Website"
          | "Direct Call"
          | "CV Library Watchdog"
          | "CV Library Advert"
          | "CV Library CV Search"
          | "Reed Watchdog"
          | "Reed Advert"
          | "Reed CV Search"
          | "Indeed Watchdog"
          | "Indeed Advert"
          | "Indeed CV Search"
          | "Job Centre"
          | "Returning Candidate"
          | "Referral Candidate"
          | "Referral Client"
          | "Training Provider"
          | "Recruitment Fair"
          | "University/College"
        );
    /**
     * Any other source from which the candidate was obtained.
     *
     * @maxLength 256
     */
    sourceOther?: string;
    /**
     * Whether the candidate has given consent for online checks.
     */
    onlineCheckConsent?: boolean;
    /**
     * The title of the candidate.
     */
    title?:
      | "Mr"
      | "Mrs"
      | "Ms"
      | "Miss"
      | "Dr"
      | "Mx"
      | "Lady"
      | "Lord"
      | "Other";
    /**
     * The pronoun used by the candidate.
     */
    pronoun?:
      | "She/Her/Hers"
      | "He/Him/His"
      | "They/Them/Theirs"
      | "Ze/Hir/Hirs"
      | "Ze/Zir/Zirs"
      | "Other"
      | "Prefer not to say";
    /**
     * The date when the candidate was last updated.
     *
     * @format date-time
     */
    updatedAt?: string;
    /**
     * The date when the candidate was last contacted.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    lastContactedDate?: string;
    /**
     * A short, prominent note shown with the candidate to communicate important context.
     */
    highlight?: string;
    /**
     * The pay scale for the candidate's teaching position.
     *
     * @maxLength 256
     */
    teacherPayScale?: string;
    /**
     * Whether the candidate has a P45.
     */
    p45?: boolean;
    /**
     * The type of role the candidate is applying for.
     */
    roleType?:
      | "Senior Leadership Positions"
      | "Teaching Roles"
      | "School Support Positions";
    /**
     * The Right to Work verification status for the candidate.
     *
     * @maxLength 256
     */
    rtwVerification?: string;
    /**
     * Any notes related to the Right to Work verification status for the candidate.
     *
     * @maxLength 1024
     */
    rtwVerificationNote?: string;
    /**
     * The Right to Work verification status for the candidate.
     *
     * @maxLength 256
     */
    rtwVerificationStatus?: string;
    /**
     * The location where the candidate desires to work.
     *
     * @maxLength 256
     */
    desiredLocation?: string;
    /**
     * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
     */
    complianceUser?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
     */
    watchers?:
      | string[]
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        }[];
    /**
     * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
     *
     * @maxItems 5
     */
    documents?: {
      /**
       * The unique identifier of the document
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The name of the document
       *
       * @maxLength 512
       */
      name?: string;
      /**
       * The file reference used to retrieve the document
       *
       * @maxLength 2048
       */
      reference?: string;
      /**
       * The file's media type
       *
       * @maxLength 256
       */
      fileType?: string;
      /**
       * The area of the platform the document belongs to
       *
       * @maxLength 256
       */
      type?: string;
      /**
       * The configured category of the document
       *
       * @maxLength 256
       */
      docType?: string;
      /**
       * The document's expiry date, when applicable
       *
       * @maxLength 64
       */
      expiry?: string;
      /**
       * The document's issued date, when applicable
       *
       * @maxLength 64
       */
      issued?: string;
      /**
       * Whether the document has been archived
       */
      documentArchived?: boolean;
      /**
       * The date the document was uploaded
       *
       * @format date-time
       */
      createdAt?: string;
    }[];
    /**
     * The total number of documents attached to the candidate.
     *
     * @minimum 0
     */
    documentsCount?: number;
    /**
     * The locations where the candidate desires to work.
     */
    desiredLocationMultiple?: string[];
    /**
     * Daily availability for the requested period. Returned by the availability view only.
     *
     * @maxItems 31
     */
    availabilityDays?: {
      /**
       * Calendar date in YYYY-MM-DD format.
       *
       * @maxLength 10
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      date?: string;
      /**
       * Availability or booking state for this date.
       */
      status?:
        | "available"
        | "unavailable"
        | "booked"
        | "halfDay"
        | "awaiting"
        | "unknown";
      /**
       * Recorded availability, or null when unrecorded.
       */
      available?: boolean | null;
      /**
       * Morning or afternoon availability.
       */
      partial?: "AM" | "PM" | any | null;
      /**
       * Additional availability information.
       *
       * @maxLength 4096
       */
      note?: string | null;
      /**
       * Active bookings on this date.
       *
       * @maxItems 100
       */
      bookings?: {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @maxLength 512
         */
        clientName?: string;
        /**
         * @maxLength 512
         */
        role?: string;
        /**
         * @maxLength 32
         */
        timeDesignation?: string;
      }[];
    }[];
    /**
     * The date when the candidate's availability was last checked.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    availabilityLastChecked?: string;
    /**
     * Whether the candidate is subject to notice period.
     */
    subjectToNotice?: "Unsure" | "Yes" | "No";
    /**
     * The subsidiary subjects the candidate is qualified to teach.
     */
    subsidiarySubject?: string[];
    /**
     * The candidate's preferred employment type.
     */
    employmentPreference?: (
      | "Day To Day"
      | "Long Term"
      | "Fixed Term"
      | "Perm"
    )[];
    /**
     * Any other names the candidate is known by.
     *
     * @maxLength 256
     */
    otherNames?: string;
    /**
     * The name of the candidate's limited company, if applicable.
     *
     * @maxLength 256
     */
    ltdCompany?: string;
    /**
     * Whether the candidate's employment has been verified.
     */
    employmentVerified?: boolean;
    /**
     * The DBS verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSVerification?: string;
    /**
     * Any notes related to the DBS verification status for the candidate.
     *
     * @maxLength 1024
     */
    candidateDBSVerificationNote?: string;
    /**
     * The DBS verification status for the candidate.
     */
    candidateDBSVerificationStatus?: "Cleared" | "Alerts";
    /**
     * The DBS check verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSCheckVerification?: string;
    /**
     * Any notes related to the DBS check verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSCheckVerificationNote?: string;
    /**
     * The DBS check verification status for the candidate.
     */
    candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
    /**
     * Any notes related to the DBS update service verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSUpdateServiceVerificationNote?: string;
    /**
     * The DBS update service verification status for the candidate.
     */
    candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
    /**
     * Any notes related to the TRN verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateTRNVerificationNote?: string;
    /**
     * The TRN verification status for the candidate.
     */
    candidateTRNVerificationStatus?:
      | "Completed Induction"
      | "Not Yet Completed Induction"
      | "Induction Exempt"
      | "ALERTS - Failed Induction"
      | "Induction Not Required";
    /**
     * The health and safety verification status for the candidate.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    candidateHealthSafetyVerification?: string;
    /**
     * Any notes related to the health and safety verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateHealthSafetyVerificationNote?: string;
    /**
     * The health and safety verification status for the candidate.
     */
    candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
    /**
     * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
     */
    onboardingSession?:
      | string
      | {
          /**
           * Unique identifier for the onboarding session
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The URL for the onboarding session
           *
           * @format uri
           */
          url?: string;
          /**
           * The frozen health declaration configuration for this onboarding session.
           */
          healthDeclarationConfig?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * When this question configuration was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
          };
          /**
           * The frozen Criminal Record & Security Checks configuration for this onboarding session.
           */
          criminalRecordConfig?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * When this question configuration was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
          };
        };
    /**
     * The contact record for this candidate. If expanded, a contact object else the contact record ID.
     */
    contact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
        };
    /**
     * Whether the candidate has opted out of 48 hour communications.
     */
    optOut48Hours?: boolean;
    /**
     * Whether the candidate has consented to REC audit checks.
     */
    recAuditConsent?: boolean | null;
    /**
     * The date the candidate's REC audit consent was recorded.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    recAuditConsentDate?: string;
    /**
     * The method through which REC audit consent was captured.
     *
     * @maxLength 256
     */
    recAuditConsentMethod?: string;
    /**
     * The date when the candidate record was created.
     *
     * @format date-time
     */
    createdAt?: string;
    /**
     * The mobile app link status for the candidate.
     */
    candidateMobileAppStatus?:
      | "Unlinked"
      | "Scheduled Unlink"
      | "Linked"
      | "Invited"
      | "Not Invited";
    /**
     * Evertime details for this candidate
     */
    evertime?: {
      /**
       * The Evertime CandidateId used for this candidate when exporting payment runs
       *
       * @maxLength 256
       */
      id?: string;
      /**
       * Whether the matched Evertime candidate's NI number also matches the one held in Edun
       */
      niNumberMatch?: boolean;
    };
    /**
     * Search relevance relative to the strongest result, where 100 is the strongest match.
     *
     * @minimum 0
     * @maximum 100
     */
    relevanceScore?: number;
    /**
     * Candidate fields or attached document excerpts that explain why the candidate matched.
     *
     * @maxItems 5
     */
    matchReasons?: {
      /**
       * Whether the matching information came from the candidate record or an attached document.
       */
      source?: "candidate" | "document";
      /**
       * The candidate or document information that matched the search.
       *
       * @maxLength 256
       */
      field?: string;
      /**
       * A short excerpt showing why the result matched.
       *
       * @maxLength 1000
       */
      text?: string;
      /**
       * The exact terms within the content that matched the search, including synonym matches.
       *
       * @maxItems 25
       */
      matchedTerms?: string[];
      /**
       * The name of the attached document when the match came from a document.
       *
       * @maxLength 512
       */
      documentName?: string | null;
      /**
       * The unique identifier of the attached document when the match came from a document.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      documentId?: string | null;
    }[];
    /**
     * The candidate's distance in miles from the requested location. Present only when location filtering is used.
     *
     * @minimum 0
     */
    distance?: number;
    /**
     * Timesheet Portal details for this candidate
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal contractor GUID used when exporting payment runs
       *
       * @format uuid
       */
      id?: string;
      /**
       * The Timesheet Portal contractor code
       *
       * @maxLength 256
       */
      code?: string;
    };
    /**
     * Invite to candidate to link their mobile app.
     */
    mobileAppLinkInvite?: {
      /**
       * Boolean status of whether the link invite is being used
       *
       * @default false
       */
      isUsed?: boolean;
      /**
       * Creation date of the invite
       *
       * @format date-time
       * @default 2026-10-30T13:39:09.027Z
       */
      createdAt?: string;
      /**
       * ID of candidate using this link
       *
       * @pattern ^[0-9a-f]{24}$
       */
      usedBy?: string;
      /**
       * Date of when link used
       *
       * @format date-time
       */
      usedAt?: string;
      /**
       * If unlinked, ID of user that unlinked this candidate's link
       *
       * @pattern ^[0-9a-f]{24}$
       */
      unlinkedBy?: string;
      /**
       * If unlinked, date of when this link was unlinked
       *
       * @format date-time
       */
      unlinkedAt?: string;
      /**
       * If deleted, ID of user that deleted this candidate's link
       *
       * @pattern ^[0-9a-f]{24}$
       */
      deletedBy?: string;
      /**
       * If deleted, date of when this link was deleted
       *
       * @format date-time
       */
      deletedAt?: string;
      /**
       * Current status of this mobile app link
       */
      status?:
        | "Sent"
        | "Linked"
        | "Unlinked"
        | "ScheduledUnlink"
        | "Not Sent"
        | "Rescinded";
    };
  };
};

export type CreateCandidateRequestBody = {
  /**
   * The forename of the candidate
   *
   * @minLength 1
   * @maxLength 256
   */
  forename: string;
  /**
   * The surname of the candidate
   *
   * @minLength 1
   * @maxLength 256
   */
  surname: string;
  /**
   * Any previous names of the candidate
   *
   * @maxLength 256
   */
  previousNames?: string;
  /**
   * The email of the candidate. This will be used for communication.
   *
   * @format email
   */
  email: string;
  /**
   * The telephone number of the candidate
   *
   * @maxLength 32
   */
  telephone?: string;
  /**
   * The mobile number of the candidate
   *
   * @maxLength 32
   */
  mobile?: string;
  /**
   * The occupation of the candidate
   */
  occupation:
    | "Teacher"
    | "Cover Supervisor"
    | "Teaching Assistant"
    | "School Support Staff"
    | "Primary Teacher"
    | "Secondary Teacher"
    | "Head Teacher"
    | "Senior Leader"
    | "Tutor"
    | "Mentor"
    | "Learning Support Assistant"
    | "Finance Officer"
    | "HR Administrator"
    | "HR Manager"
    | "Office Manager"
    | "Administrator"
    | "Personal Assistant"
    | "Pastoral Manager"
    | "Receptionist"
    | "Instructor"
    | "Caretaker"
    | "Nursery Assistant"
    | "Nursery Nurse"
    | "Deputy Nursery Manager"
    | "Lunchtime Supervisor"
    | "Classroom Graduate"
    | "Primary Cover Supervisor"
    | "HLTA"
    | "Nurture Teacher"
    | "Nurture Mentor"
    | "SENDCo"
    | "Registered Manager"
    | "Regional Manager"
    | "Education Director"
    | "Residential Director"
    | "Deputy Manager"
    | "Residential Support Worker"
    | "Sports Coach"
    | "Executive Headteacher"
    | "General Practitioner"
    | "Advance Nurse Practitioner (ANP)"
    | "Emergency Nurse Practitioner (ENP)"
    | "Advanced Clinical Practitioner (ACP)"
    | "Emergency Clinical Practitioner (ECP)"
    | "Consultant Psychiatrist"
    | "Psychiatrist \u2013 SPR"
    | "IT Technician"
    | "Art Technician"
    | "DT Technician"
    | "Science Technician"
    | "Healthcare Assistant/ Residential Support Worker"
    | "Healthcare Assistant"
    | "Senior Healthcare Assistant"
    | "Team Leader"
    | "Registered General Nurse"
    | "Registered Mental Nurse"
    | "Registered Nurse Learning Disabilities"
    | "Overseas Trained Teacher";
  /**
   * The subject the candidate specializes in
   *
   * @maxLength 256
   */
  subject?: string;
  /**
   * The address of the resource
   */
  address: {
    /**
     * The first line of the address
     *
     * @maxLength 256
     */
    addressLine1?: string;
    /**
     * The second line of the address
     *
     * @maxLength 256
     */
    addressLine2?: string;
    /**
     * The third line of the address
     *
     * @maxLength 256
     */
    addressLine3?: string;
    /**
     * The city of the address
     *
     * @maxLength 256
     */
    city?: string;
    /**
     * The county of the address
     *
     * @maxLength 256
     */
    county?: string;
    /**
     * The borough of the address
     *
     * @maxLength 256
     */
    borough?: string;
    /**
     * The postcode of the address
     *
     * @maxLength 10
     */
    postcode?: string;
    /**
     * The country of the address
     *
     * @maxLength 256
     */
    country?: string;
    /**
     * The latitude of the address
     *
     * @minimum -90
     * @maximum 90
     */
    lat?: number;
    /**
     * The longitude of the address
     *
     * @minimum -180
     * @maximum 180
     */
    lng?: number;
  };
  /**
   * The qualifications of the candidate
   *
   * @maxItems 32
   */
  qualifications: {
    /**
     * The type of the qualification
     *
     * @maxLength 256
     */
    type?: string;
    /**
     * The place where the qualification was obtained
     *
     * @maxLength 256
     */
    place?: string;
    /**
     * The name of the qualification
     *
     * @maxLength 256
     */
    qualificationName?: string;
    /**
     * The date the qualification was obtained
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    date?: string;
    /**
     * The unique identifier of the qualification
     *
     * @maxLength 256
     */
    qualificationId?: string;
    /**
     * The order index of the qualification
     *
     * @minimum 0
     */
    orderIndex?: number;
    /**
     * The expiry date of the qualification
     *
     * @maxLength 256
     */
    qualificationExpiry?: string;
    /**
     * Whether to exclude this qualification from vetting
     */
    excludeFromVetting?: boolean;
    /**
     * The date the qualification was created
     *
     * @format date-time
     */
    createdAt: string | null;
  }[];
  /**
   * The employment history of the candidate
   *
   * @maxItems 32
   */
  employment: {
    /**
     * The name of the employer
     *
     * @maxLength 256
     */
    employerName: string;
    /**
     * The date the candidate started employment
     *
     * @maxLength 256
     */
    dateStarted: string;
    /**
     * The date the candidate ended employment
     *
     * @maxLength 256
     */
    dateEnded?: string;
    /**
     * The job title of the candidate
     *
     * @maxLength 256
     */
    jobTitle?: string;
    /**
     * The reason the candidate left this employment
     *
     * @maxLength 1024
     */
    reasonForLeaving?: string;
    /**
     * The duties of the candidate in this role. Consultant only, visible on Edun CV
     *
     * @maxLength 1024
     */
    duties?: string;
    /**
     * The salary of the candidate in this role
     *
     * @maxLength 256
     */
    salary?: string;
    /**
     * The address of the candidate's reference for this employment
     */
    referenceAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The unique identifier for this employment record
     *
     * @maxLength 256
     */
    employmentId?: string;
    /**
     * The order index of this employment record
     *
     * @minimum 0
     */
    orderIndex?: number;
  }[];
  /**
   * The references for the candidate
   *
   * @maxItems 32
   */
  references: {
    /**
     * The title of the reference
     *
     * @maxLength 16
     */
    referenceTitle?: string;
    /**
     * The forename of the reference
     *
     * @maxLength 256
     */
    referenceForename: string;
    /**
     * The surname of the reference
     *
     * @maxLength 256
     */
    referenceSurname: string;
    /**
     * The company of the reference
     *
     * @maxLength 256
     */
    referenceCompany?: string;
    /**
     * The position of the reference
     *
     * @maxLength 256
     */
    referencePosition?: string;
    /**
     * The email of the reference
     *
     * @format email
     */
    referenceEmail: string;
    /**
     * The telephone number of the reference
     *
     * @maxLength 32
     */
    referenceTelephone: string;
    /**
     * The type of the reference
     */
    referenceType:
      | "Education"
      | "Professional"
      | "Character"
      | "Factual"
      | "Academic"
      | "Volunteer";
    /**
     * The duration of the reference
     *
     * @maxLength 128
     */
    referenceDuration: string;
    /**
     * The ID of the reference
     *
     * @maxLength 256
     */
    referenceId?: string;
    /**
     * This field only applies if the reference immediate consent question is enabled
     * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
     */
    referenceApply?: boolean;
    /**
     * Any additional notes about the reference
     *
     * @maxLength 1024
     */
    referenceNote?: string;
    /**
     * The start date of the reference
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    dateFrom?: string;
    /**
     * The end date of the reference
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    dateTo?: string;
  }[];
  /**
   * Whether the candidate has consented to a reference check
   */
  referenceCheckConsent?: boolean;
  /**
   * The name of the person who provided consent for the reference check
   *
   * @maxLength 256
   */
  referenceCheckConsentName?: string;
  /**
   * The date when consent for the reference check was provided
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  referenceCheckConsentDate?: string;
  /**
   * The date of birth of the candidate
   *
   * @format date-time
   */
  dob: string | null;
  /**
   * The sex of the candidate
   */
  sex: "Male" | "Female" | "Other" | "Unknown";
  /**
   * The status of the candidate
   */
  status:
    | "Pending"
    | "Approved"
    | "Rejected"
    | "Anonymised"
    | "Archived"
    | "Enquired"
    | "Hold"
    | "Future"
    | "DNC"
    | "Pre";
  /**
   * The current status of the onboarding process for the candidate
   */
  onboardingStatus: "Not Started" | "In Progress" | "Completed";
  /**
   * Whether the onboarding information has been sent to the candidate
   */
  onboardingSent?: boolean;
  /**
   * The next of kin information for the candidate
   */
  nextOfKin?: {
    /**
     * The name of the next of kin
     *
     * @maxLength 256
     */
    nextOfKinName?: string;
    /**
     * The relationship of the next of kin to the candidate
     *
     * @maxLength 256
     */
    nextOfKinRelationship?: string;
    /**
     * The telephone number of the next of kin
     *
     * @maxLength 32
     */
    nextOfKinTelephone?: string;
    /**
     * The email address of the next of kin
     *
     * @format email
     */
    nextOfKinEmail?: string;
    /**
     * The address of the resource
     */
    nextOfKinAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
  };
  /**
   * The criminal record information for the candidate
   */
  criminalRecord?: {
    /**
     * Whether the candidate has a DBS certificate
     */
    dbsHeld?: boolean;
    /**
     * The unique identifier for the DBS certificate
     *
     * @maxLength 256
     */
    dbsNumber?: string;
    /**
     * The type of DBS check
     */
    dbsType?:
      | "Basic"
      | "Standard"
      | "Enhanced"
      | "Enhanced with Barred List"
      | "Child workforce"
      | "Child and adult workforce"
      | "Adult workforce";
    /**
     * The date when the DBS check was last performed
     *
     * @maxLength 256
     */
    dbsLastChecked?: string;
    /**
     * The DBS update service number for the candidate
     *
     * @maxLength 256
     */
    dbsUpdateServiceNumber?: string;
    /**
     * The result of the prohibition check
     *
     * @maxLength 256
     */
    prohibitionCheck?: string;
    /**
     * The result of the overseas check
     *
     * @maxLength 256
     */
    overseasCheck?: string;
    /**
     * Any additional notes regarding the overseas check
     *
     * @maxLength 256
     */
    overseasCheckNote?: string;
    /**
     * Whether the candidate has a convicted offence
     */
    convictedOffence?: boolean;
    /**
     * The description of the convicted offence
     *
     * @maxLength 1024
     */
    convictedOffenceDescription?: string;
    /**
     * Whether the candidate has any convictions
     */
    convictions?: boolean;
    /**
     * The description of the convictions
     *
     * @maxLength 1024
     */
    convictionsDescription?: string;
    /**
     * Whether the candidate is registered to the DBS update service
     */
    registeredToUpdateService?: boolean;
    /**
     * Whether the candidate consents to update service checks
     */
    consentUpdateServiceChecks?: boolean;
    /**
     * Any additional notes regarding the candidate's criminal record
     *
     * @maxLength 1024
     */
    note?: string;
    /**
     * The date when the update service check was performed
     *
     * @maxLength 64
     */
    updateServiceCheckDate?: string;
    /**
     * Whether the candidate has lived abroad
     */
    abroad?: boolean;
    /**
     * Any additional notes regarding the overseas declaration
     *
     * @maxLength 1024
     */
    overseasDeclarationExtended?: string;
    /**
     * Whether the candidate has been subject to educational disciplinary proceedings
     */
    educationalDisciplinaryProceedings?: boolean;
    /**
     * Any comments regarding the DBS check
     *
     * @maxLength 1024
     */
    dbsComments?: string;
    /**
     * Whether the candidate has read the DBS declaration
     */
    dbsDeclarationRead?: boolean;
    /**
     * Whether the candidate has any overseas offences
     */
    overseasOffence?: boolean;
    /**
     * The description of the overseas offence
     *
     * @maxLength 1024
     */
    overseasOffenceDescription?: string;
    /**
     * Whether the candidate has a current criminal offence
     */
    currentCriminalOffence?: boolean;
    /**
     * The description of the current criminal offence
     *
     * @maxLength 1024
     */
    currentCriminalOffenceDescription?: string;
    /**
     * Whether the DBS was issued by a business
     */
    dbsIssuedByBusiness?: boolean;
    /**
     * Whether the DBS was issued by the current employer
     */
    dbsIssuedByCurrentEmployer?: boolean;
    /**
     * Whether a Section 128 check is required for the candidate.
     */
    section128Required?: boolean;
    /**
     * The date when the Section 128 check was completed.
     */
    section128Checked?: string;
  };
  /**
   * The safe in education information for the candidate
   */
  safeInEducation?: {
    /**
     * Whether the candidate is barred from working in education
     */
    barred?: boolean;
    /**
     * The date when the barred status was checked
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    barredCheckDate?: string;
    /**
     * Whether the candidate has a waiver for the barred status
     */
    waiver?: boolean;
    /**
     * Any orders related to the candidate's barred status
     */
    ordersYou?: boolean;
    /**
     * Any orders related to the candidate's barred status for children
     */
    ordersChild?: boolean;
    /**
     * Whether the candidate has any previous safeguarding allegations
     */
    previousSafeguardingAllegations?: boolean;
    /**
     * Whether the candidate has been cautioned
     */
    cautioned?: boolean;
    /**
     * Whether the candidate has a violent offence
     */
    violentOffence?: boolean;
    /**
     * Whether the candidate has lived or worked in another country
     */
    anotherCountry?: boolean;
    /**
     * Whether the candidate has read the Keeping Children Safe in Education document
     */
    readKCSIE?: boolean;
    /**
     * Whether the candidate has read the Disqualification under the Childcare Act guidance
     */
    readChildcareAct?: boolean;
    /**
     * Whether the candidate is prohibited from teaching
     */
    prohibitedFromTeaching?: boolean;
    /**
     * Whether the candidate is known to the police
     */
    knownPolice?: boolean;
    /**
     * Whether the candidate is prohibited from management roles
     */
    prohibitedFromManagement?: boolean;
    /**
     * Whether the candidate has any criminal offences under law
     */
    criminalOffencesLaw?: boolean;
    /**
     * Any additional details regarding the candidate's safe in education status
     */
    safeInEducationDetails?: string;
    /**
     * The safeguarding questionnaire responses for the candidate
     */
    safeguardingQuestionaire?: {
      /**
       * What is the primary purpose of safeguarding in a school environment?
       */
      primaryPurposeSafeguarding:
        | "enhance-academic-performance"
        | "ensure-safe-learning-environment"
        | "promote-healthy-relationships"
        | "facilitate-extracurricular-activities";
      /**
       * Who is typically responsible for coordinating safeguarding efforts within a school?
       */
      typicallyResponsibleCoordinatingSafeguarding:
        | "janitorial-staff"
        | "parents-association"
        | "school-nurse"
        | "designated-safeguarding-lead";
      /**
       * Which of the following is considered a potential sign of child abuse or neglect?
       */
      signOfAbuse:
        | "student-excelling"
        | "frequent-extracurricular"
        | "unexplained-injuries"
        | "not-listening";
      /**
       * How should a teacher respond if a student discloses information about potential abuse or harm?
       */
      studentDisclosesInformation:
        | "ignore-disclosure"
        | "report-disclosure"
        | "share-information"
        | "advise-independent";
      /**
       * What is the purpose of a school's Child Protection Policy?
       */
      schoolsChildProtectionPolicy:
        | "enforcing-strict-disciplinary-measures"
        | "identifying-troublemakers"
        | "outlining-procedures-for-protecting-students-from-harm"
        | "establishing-academic-achievement-standards";
      /**
       * What does the term 'FGM' stand for in the context of safeguarding?
       */
      fgmDefinition:
        | "female-general-medicine"
        | "forced-genetic-modification"
        | "female-genital-mutilation"
        | "foundation-gender-equality";
      /**
       * Which of the following is a key principle of online safety for students?
       */
      keyPrincipleOnlineSafetyStudents:
        | "unlimited-screen-time"
        | "anon-online-interaction"
        | "responsible-digital-citizen"
        | "restrict-edu-sites";
      /**
       * When planning a school trip, what should teachers consider regarding safeguarding?
       */
      teachersConsiderRegardingSafeguarding:
        | "minimise-supervision"
        | "appropriate-staff-ratios"
        | "exclude-medical-conditions"
        | "allow-roaming";
      /**
       * What is the purpose of a criminal background check for teachers?
       */
      criminalBackgroundCheckTeachers:
        | "evaluating-teaching-proficiency"
        | "verifying-immigration-status"
        | "assessing-financial-stability"
        | "identifying-criminal-history";
      /**
       * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
       */
      studentConfides:
        | "assure-secret-safe"
        | "encourage-share-friends"
        | "explain-no-guarantee"
        | "disclose-to-others";
    };
  };
  /**
   * The social media information for the candidate
   */
  socialMedia?: {
    /**
     * The Facebook profile URL of the candidate
     *
     * @maxLength 256
     */
    facebook?: string;
    /**
     * The Instagram profile URL of the candidate
     *
     * @maxLength 256
     */
    instagram?: string;
    /**
     * The Twitter profile URL of the candidate
     *
     * @maxLength 256
     */
    twitter?: string;
    /**
     * The LinkedIn profile URL of the candidate
     *
     * @maxLength 256
     */
    linkedIn?: string;
    /**
     * The TikTok profile URL of the candidate
     *
     * @maxLength 256
     */
    tiktok?: string;
    /**
     * Any other social media profile URLs of the candidate
     *
     * @maxLength 256
     */
    others?: string;
    /**
     * Whether the social media profiles have been checked
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    socialMediaChecked?: string;
    /**
     * The date the candidate's Google search check was completed
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    googleCheckedDate?: string;
  };
  /**
   * Whether the candidate has consented to a safe in education check
   */
  safeInEducationCheckConsent: boolean;
  /**
   * The name of the person who provided consent for the safe in education check
   *
   * @maxLength 256
   */
  safeInEducationCheckConsentName: string;
  /**
   * The date when consent for the safe in education check was provided
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  safeInEducationCheckConsentDate: string;
  /**
   * The health record of the individual
   */
  healthRecord: {
    /**
     * Whether the individual has a physical ability condition
     */
    physicalAbilityCondition?: boolean;
    /**
     * Whether the individual is confused
     */
    confused?: boolean;
    /**
     * Whether the individual has hearing issues
     */
    hearing?: boolean;
    /**
     * Whether the individual has eyesight issues
     */
    eyesight?: boolean;
    /**
     * Whether the individual has depression
     */
    depression?: boolean;
    /**
     * Whether the individual has severe pain
     */
    severePain?: boolean;
    /**
     * Whether the individual has excessive drowsiness
     */
    excessiveDrowsiness?: boolean;
    /**
     * Whether the individual has epilepsy
     */
    epilepsy?: boolean;
    /**
     * Whether the individual has heart issues
     */
    heart?: boolean;
    /**
     * Whether the individual has asthma
     */
    asthma?: boolean;
    /**
     * Whether the individual has mental health issues
     */
    mentalHealth?: boolean;
    /**
     * Whether the individual is an expectant mother
     */
    expectantMother?: boolean;
    /**
     * Whether the individual has allergies
     */
    allergies?: boolean;
    /**
     * Whether the individual is taking medication
     */
    medication?: boolean;
    /**
     * Whether the individual has any health issues
     */
    healthIssues?: boolean;
    /**
     * A description of the individual's health issues
     *
     * @maxLength 2048
     */
    healthIssuesDescription?: string;
    /**
     * Any disability needs of the individual
     *
     * @maxLength 2048
     */
    disabilityNeeds?: string;
    /**
     * Whether the individual has any contagious diseases
     */
    contagiousDiseases?: boolean;
    /**
     * Whether the individual's health affects their ability
     */
    affectAbility?: boolean;
    /**
     * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
     */
    notificationAgree?: boolean;
    /**
     * Whether the individual has opted into the SAS Wellbeing App
     */
    sasOptIn?: boolean;
    /**
     * The individual's signature
     *
     * @maxLength 256
     */
    signature?: string;
    /**
     * The individual's drawn signature in base64url format
     *
     * @maxLength 4096
     */
    drawnSignature?: string;
    /**
     * The date the individual signed the form
     *
     * @format date-time
     */
    signatureDate?: string | null;
    /**
     * Whether the individual has been vaccinated against measles
     */
    vaccinationMeasles?: boolean;
    /**
     * Whether the individual has been vaccinated against mumps
     */
    vaccinationMumps?: boolean;
    /**
     * Whether the individual has been vaccinated against rubella
     */
    vaccinationRubella?: boolean;
    /**
     * Whether the individual has been vaccinated against varicella
     */
    vaccinationVaricella?: boolean;
    /**
     * Whether the individual has been vaccinated against tuberculosis
     */
    vaccinationTuberculosis?: boolean;
    /**
     * Whether the individual has been vaccinated against hepatitis B
     */
    vaccinationHepatitisB?: boolean;
    /**
     * Whether the individual has been vaccinated against hepatitis C
     */
    vaccinationHepatitisC?: boolean;
    /**
     * Whether the individual has been vaccinated against HIV
     */
    vaccinationHIV?: boolean;
    /**
     * Whether the individual has been vaccinated against COVID-19
     */
    vaccinationCovid?: boolean;
    /**
     * Whether the individual has received the 1st dose of the COVID-19 vaccine
     */
    vaccinationCovid1?: boolean;
    /**
     * Whether the individual has received the 2nd dose of the COVID-19 vaccine
     */
    vaccinationCovid2?: boolean;
    /**
     * Whether the individual has received the COVID-19 booster vaccine
     */
    vaccinationCovidBooster?: boolean;
    /**
     * Whether the individual has received the COVID-19 influenza vaccine
     */
    vaccinationCovidInfluenza?: boolean;
    /**
     * Whether the individual has an override for fit to work requirements
     */
    fitToWorkOverride?: boolean;
  };
  /**
   * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
   */
  bankDetails: {
    /**
     * The name on the bank account
     */
    bankAccountName?: string;
    /**
     * The name of the bank
     */
    bankName?: string;
    /**
     * The account number
     */
    accountNumber?: string;
    /**
     * The sort code
     */
    sortCode?: string;
    /**
     * The National Insurance number
     */
    niNumber?: string;
    /**
     * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
     */
    jobOp1?: boolean;
    /**
     * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
     */
    jobOp2?: boolean;
    /**
     * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
     */
    jobOp3?: boolean;
    /**
     * Details about the candidate's student loan status
     */
    studentLoan?: {
      /**
       * Boolean flag for if candidate has student loan
       */
      studentLoan: boolean;
      /**
       * Boolean flag for if candidate is on plan 1
       */
      plan1: boolean;
      /**
       * Boolean flag for if candidate is on plan 2
       */
      plan2: boolean;
      /**
       * Boolean flag for if candidate is on plan 4
       */
      plan4: boolean;
      /**
       * Boolean flag for if candidate has postgrad loan
       */
      postGradLoan: boolean;
    };
  };
  /**
   * Whether the candidate has confirmed their details are correct.
   */
  declarationCheckConsent: boolean;
  /**
   * The name of the person who confirmed their details are correct.
   *
   * @maxLength 256
   */
  declarationCheckConsentName?: string;
  /**
   * The date when consent for the declaration check was provided
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  declarationCheckConsentDate?: string;
  /**
   * Whether the candidate has consented to a contract for service
   */
  contractForServiceConsent: boolean;
  /**
   * The name of the person who provided consent for the contract for service
   *
   * @maxLength 256
   */
  contractForServiceName?: string;
  /**
   * The date when consent for the contract for service was provided
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  contractForServiceDate?: string;
  /**
   * The candidate has completed their onboarding form.
   */
  confirmed: boolean;
  /**
   * The ID of the customer associated with the candidate.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  customerId: string;
  /**
   * The user assigned to the candidate. If expanded, a user object else the user ID.
   */
  assignee?:
    | string
    | {
        /**
         * The unique identifier for the user
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The email address of the user
         *
         * @format email
         */
        email: string;
        /**
         * The first name of the user
         *
         * @maxLength 128
         */
        firstName: string;
        /**
         * The last name of the user
         *
         * @maxLength 128
         */
        lastName: string;
        /**
         * The username of the user
         *
         * @maxLength 128
         */
        username: string;
        /**
         * The local education authority of the user
         *
         * @maxLength 128
         */
        lea?: string;
        /**
         * Array of client IDs associated with the user
         */
        clients: string[];
        /**
         * The position or job title of the user
         *
         * @maxLength 128
         */
        position: string;
        /**
         * Array of permissions assigned to the user
         */
        permissions: number[];
        /**
         * The activation code for the user
         *
         * @maxLength 256
         */
        activationCode?: string;
        /**
         * The unique identifier of the company the user belongs to.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customer: string;
        /**
         * Indicates whether the user account is disabled
         */
        disabled: boolean;
        /**
         * Array of commission thresholds for the user
         */
        commissionThreshold: {
          min: number | string;
          commission: number | string;
          type?: "Fixed" | "Variable";
          weekly?: boolean;
        }[];
        /**
         * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
         */
        branches:
          | string[]
          | {
              /**
               * The unique identifier for the branch
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The name of the branch
               *
               * @maxLength 256
               */
              name: string;
              /**
               * The code of the branch
               *
               * @maxLength 32
               */
              code?: string;
              /**
               * The contact email of the branch
               *
               * @maxLength 256
               * @format email
               */
              contactEmail?: string;
              /**
               * The contact phone number of the branch
               *
               * @maxLength 32
               */
              contactTelephone?: string;
              /**
               * The contact website of the branch
               *
               * @maxLength 512
               */
              contactWebsite?: string;
              /**
               * The accent color for the customer that the branch belongs to
               *
               * @maxLength 7
               */
              customerAccentColor?: string;
              /**
               * The address of the branch
               */
              address?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
              /**
               * Indicates whether the branch is archived
               */
              archived: boolean;
              /**
               * The number of users assigned to the branch. Only returned when explicitly selected.
               *
               * @minimum 0
               */
              userCount?: number;
            }[];
        /**
         * Indicates whether to display the onboarding process for the user
         */
        displayOnboarding: boolean;
        /**
         * A custom identifier for the user
         *
         * @maxLength 256
         */
        _customId?: string;
        /**
         * The signature of the user in base64 format
         *
         * @maxLength 2048
         */
        signature?: string | null;
        /**
         * The phone number of the user
         *
         * @maxLength 32
         */
        phone?: string;
        /**
         * Indicates whether the user is the default candidate watcher
         */
        defaultCandidateWatcher: boolean;
        /**
         * The user's default task workspace
         *
         * @pattern ^[0-9a-f]{24}$
         */
        defaultTaskWorkspace?: string | null;
        /**
         * Indicates whether the user has team admin privileges
         */
        teamAdmin: boolean;
        /**
         * The contact record for this user. If expanded, a contact object else the contact record ID.
         */
        contact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        /**
         * The date and time the user account was created
         */
        createdAt?: string;
        /**
         * Evertime details for this user
         */
        evertime?: {
          /**
           * The Evertime ConsultantId used for this user when exporting payment runs
           *
           * @maxLength 256
           */
          id?: string;
        };
        /**
         * Timesheet Portal details for this user
         */
        timesheetPortal?: {
          /**
           * The Timesheet Portal administrator GUID used when exporting payment runs
           *
           * @format uuid
           */
          id?: string;
          /**
           * The Timesheet Portal administrator code
           *
           * @maxLength 256
           */
          code?: string;
        };
      };
  /**
   * The ID of the current booking for the candidate.
   */
  currentBooking?: string[];
  /**
   * The URL of the candidate's profile photo.
   *
   * @maxLength 256
   */
  profilePhoto?: string;
  /**
   * The pay type for the candidate.
   */
  payType: "PAYE" | "Umbrella" | "Limited Company" | "Not Applicable";
  /**
   * The pay interval for the candidate.
   */
  payInterval:
    | "Monthly"
    | "Weekly"
    | "Weekly Pay in Arrears"
    | "Bi-Weekly"
    | any
    | null;
  /**
   * The pay rate for the candidate.
   */
  payRate?: string;
  /**
   * A custom ID for the candidate.
   *
   * @maxLength 256
   */
  customId?: string;
  /**
   * A company-defined identifier used to match the candidate with external records.
   *
   * @maxLength 256
   */
  _internalId?: string;
  /**
   * The date when the candidate's status was last changed.
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  statusChangeDate?: string;
  /**
   * Whether the candidate has opted out of communications.
   */
  optOut?: boolean;
  /**
   * The TRN number for the candidate.
   *
   * @maxLength 256
   */
  trnNumber?: string;
  /**
   * The date when the TRN number was last checked.
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  trnNumberCheckedDate?: string;
  /**
   * Any notes related to the TRN number.
   *
   * @maxLength 1024
   */
  trnNumberNotes?: string;
  /**
   * The date when the candidate's employment is set to start.
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  employmentStartDate?: string;
  /**
   * Whether the candidate has confirmed their Early Years Workforce status.
   */
  ewcConfirmation?: boolean;
  /**
   * The share code for the candidate's Home Office application.
   *
   * @maxLength 256
   */
  homeOfficeShareCode?: string;
  /**
   * Whether the candidate has been archived.
   */
  archived?: boolean;
  /**
   * Whether the candidate is using an umbrella company.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  umbrella?: string;
  /**
   * ID of the PAYE umbrella company.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  payeUmbrella?: string;
  /**
   * The name of the umbrella company the candidate is using.
   *
   * @maxLength 256
   */
  umbrellaCompanyName?: string;
  /**
   * The code of the umbrella company the candidate is using.
   *
   * @maxLength 256
   */
  umbrellaCompanyCode?: string;
  /**
   * The transport method for the candidate.
   */
  transport?:
    | "Drives Own Vehicle"
    | "Use Public Transport"
    | "Access to Another Vehicle"
    | "Bicycle"
    | "No Access"
    | "Other";
  /**
   * Any important notes related to the candidate.
   *
   * @maxLength 1024
   */
  importantNotes?: string;
  /**
   * The date when the candidate was last updated.
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  candidateLastUpdated?: string;
  /**
   * The source from which the candidate was obtained.
   */
  source?:
    | (
        | "Company Website"
        | "iCan Teach UK Website"
        | "Apply to Education"
        | "CV Library - CV Search"
        | "CV Library Advert"
        | "CV Library - Watchdog"
        | "Guardian- CV Search"
        | "Guardian Advert"
        | "Indeed - CV Search"
        | "Indeed Advert"
        | "Reed - CV Search"
        | "Reed Advert"
        | "Reed \u2013 Watchdog"
        | "SENploy - Advert"
        | "Total Jobs"
        | "Other Job Board"
        | "Google"
        | "LinkedIn"
        | "Linkedin Recruiter"
        | "LinkedIn Advert"
        | "Referral - School"
        | "Referral - Candidate"
        | "UK University"
        | "Irish University"
        | "Canadian University"
        | "Marketing Campaign"
        | "Woo"
        | "Job Show"
        | "Recruitment Fair"
        | "Career Fair"
        | "Volcanic"
        | "Broadbean"
        | "Headhunt"
        | "Referral"
        | "Direct"
        | "Other"
      )
    | (
        | "Company Website"
        | "Direct Call"
        | "CV Library Watchdog"
        | "CV Library Advert"
        | "CV Library CV Search"
        | "Reed Watchdog"
        | "Reed Advert"
        | "Reed CV Search"
        | "Indeed Watchdog"
        | "Indeed Advert"
        | "Indeed CV Search"
        | "Job Centre"
        | "Returning Candidate"
        | "Referral Candidate"
        | "Referral Client"
        | "Training Provider"
        | "Recruitment Fair"
        | "University/College"
      );
  /**
   * Any other source from which the candidate was obtained.
   *
   * @maxLength 256
   */
  sourceOther?: string;
  /**
   * Whether the candidate has given consent for online checks.
   */
  onlineCheckConsent?: boolean;
  /**
   * The title of the candidate.
   */
  title?:
    | "Mr"
    | "Mrs"
    | "Ms"
    | "Miss"
    | "Dr"
    | "Mx"
    | "Lady"
    | "Lord"
    | "Other";
  /**
   * The pronoun used by the candidate.
   */
  pronoun?:
    | "She/Her/Hers"
    | "He/Him/His"
    | "They/Them/Theirs"
    | "Ze/Hir/Hirs"
    | "Ze/Zir/Zirs"
    | "Other"
    | "Prefer not to say";
  /**
   * The date when the candidate was last updated.
   *
   * @format date-time
   */
  updatedAt?: string;
  /**
   * The date when the candidate was last contacted.
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  lastContactedDate?: string;
  /**
   * A short, prominent note shown with the candidate to communicate important context.
   */
  highlight?: string;
  /**
   * The pay scale for the candidate's teaching position.
   *
   * @maxLength 256
   */
  teacherPayScale?: string;
  /**
   * Whether the candidate has a P45.
   */
  p45?: boolean;
  /**
   * The type of role the candidate is applying for.
   */
  roleType?:
    | "Senior Leadership Positions"
    | "Teaching Roles"
    | "School Support Positions";
  /**
   * The Right to Work verification status for the candidate.
   *
   * @maxLength 256
   */
  rtwVerification?: string;
  /**
   * Any notes related to the Right to Work verification status for the candidate.
   *
   * @maxLength 1024
   */
  rtwVerificationNote?: string;
  /**
   * The Right to Work verification status for the candidate.
   *
   * @maxLength 256
   */
  rtwVerificationStatus?: string;
  /**
   * The location where the candidate desires to work.
   *
   * @maxLength 256
   */
  desiredLocation?: string;
  /**
   * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
   */
  complianceUser?:
    | string
    | {
        /**
         * The unique identifier for the user
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The email address of the user
         *
         * @format email
         */
        email: string;
        /**
         * The first name of the user
         *
         * @maxLength 128
         */
        firstName: string;
        /**
         * The last name of the user
         *
         * @maxLength 128
         */
        lastName: string;
        /**
         * The username of the user
         *
         * @maxLength 128
         */
        username: string;
        /**
         * The local education authority of the user
         *
         * @maxLength 128
         */
        lea?: string;
        /**
         * Array of client IDs associated with the user
         */
        clients: string[];
        /**
         * The position or job title of the user
         *
         * @maxLength 128
         */
        position: string;
        /**
         * Array of permissions assigned to the user
         */
        permissions: number[];
        /**
         * The activation code for the user
         *
         * @maxLength 256
         */
        activationCode?: string;
        /**
         * The unique identifier of the company the user belongs to.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customer: string;
        /**
         * Indicates whether the user account is disabled
         */
        disabled: boolean;
        /**
         * Array of commission thresholds for the user
         */
        commissionThreshold: {
          min: number | string;
          commission: number | string;
          type?: "Fixed" | "Variable";
          weekly?: boolean;
        }[];
        /**
         * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
         */
        branches:
          | string[]
          | {
              /**
               * The unique identifier for the branch
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The name of the branch
               *
               * @maxLength 256
               */
              name: string;
              /**
               * The code of the branch
               *
               * @maxLength 32
               */
              code?: string;
              /**
               * The contact email of the branch
               *
               * @maxLength 256
               * @format email
               */
              contactEmail?: string;
              /**
               * The contact phone number of the branch
               *
               * @maxLength 32
               */
              contactTelephone?: string;
              /**
               * The contact website of the branch
               *
               * @maxLength 512
               */
              contactWebsite?: string;
              /**
               * The accent color for the customer that the branch belongs to
               *
               * @maxLength 7
               */
              customerAccentColor?: string;
              /**
               * The address of the branch
               */
              address?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
              /**
               * Indicates whether the branch is archived
               */
              archived: boolean;
              /**
               * The number of users assigned to the branch. Only returned when explicitly selected.
               *
               * @minimum 0
               */
              userCount?: number;
            }[];
        /**
         * Indicates whether to display the onboarding process for the user
         */
        displayOnboarding: boolean;
        /**
         * A custom identifier for the user
         *
         * @maxLength 256
         */
        _customId?: string;
        /**
         * The signature of the user in base64 format
         *
         * @maxLength 2048
         */
        signature?: string | null;
        /**
         * The phone number of the user
         *
         * @maxLength 32
         */
        phone?: string;
        /**
         * Indicates whether the user is the default candidate watcher
         */
        defaultCandidateWatcher: boolean;
        /**
         * The user's default task workspace
         *
         * @pattern ^[0-9a-f]{24}$
         */
        defaultTaskWorkspace?: string | null;
        /**
         * Indicates whether the user has team admin privileges
         */
        teamAdmin: boolean;
        /**
         * The contact record for this user. If expanded, a contact object else the contact record ID.
         */
        contact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        /**
         * The date and time the user account was created
         */
        createdAt?: string;
        /**
         * Evertime details for this user
         */
        evertime?: {
          /**
           * The Evertime ConsultantId used for this user when exporting payment runs
           *
           * @maxLength 256
           */
          id?: string;
        };
        /**
         * Timesheet Portal details for this user
         */
        timesheetPortal?: {
          /**
           * The Timesheet Portal administrator GUID used when exporting payment runs
           *
           * @format uuid
           */
          id?: string;
          /**
           * The Timesheet Portal administrator code
           *
           * @maxLength 256
           */
          code?: string;
        };
      };
  /**
   * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
   */
  watchers?:
    | string[]
    | {
        /**
         * The unique identifier for the user
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The email address of the user
         *
         * @format email
         */
        email: string;
        /**
         * The first name of the user
         *
         * @maxLength 128
         */
        firstName: string;
        /**
         * The last name of the user
         *
         * @maxLength 128
         */
        lastName: string;
        /**
         * The username of the user
         *
         * @maxLength 128
         */
        username: string;
        /**
         * The local education authority of the user
         *
         * @maxLength 128
         */
        lea?: string;
        /**
         * Array of client IDs associated with the user
         */
        clients: string[];
        /**
         * The position or job title of the user
         *
         * @maxLength 128
         */
        position: string;
        /**
         * Array of permissions assigned to the user
         */
        permissions: number[];
        /**
         * The activation code for the user
         *
         * @maxLength 256
         */
        activationCode?: string;
        /**
         * The unique identifier of the company the user belongs to.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customer: string;
        /**
         * Indicates whether the user account is disabled
         */
        disabled: boolean;
        /**
         * Array of commission thresholds for the user
         */
        commissionThreshold: {
          min: number | string;
          commission: number | string;
          type?: "Fixed" | "Variable";
          weekly?: boolean;
        }[];
        /**
         * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
         */
        branches:
          | string[]
          | {
              /**
               * The unique identifier for the branch
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The name of the branch
               *
               * @maxLength 256
               */
              name: string;
              /**
               * The code of the branch
               *
               * @maxLength 32
               */
              code?: string;
              /**
               * The contact email of the branch
               *
               * @maxLength 256
               * @format email
               */
              contactEmail?: string;
              /**
               * The contact phone number of the branch
               *
               * @maxLength 32
               */
              contactTelephone?: string;
              /**
               * The contact website of the branch
               *
               * @maxLength 512
               */
              contactWebsite?: string;
              /**
               * The accent color for the customer that the branch belongs to
               *
               * @maxLength 7
               */
              customerAccentColor?: string;
              /**
               * The address of the branch
               */
              address?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
              /**
               * Indicates whether the branch is archived
               */
              archived: boolean;
              /**
               * The number of users assigned to the branch. Only returned when explicitly selected.
               *
               * @minimum 0
               */
              userCount?: number;
            }[];
        /**
         * Indicates whether to display the onboarding process for the user
         */
        displayOnboarding: boolean;
        /**
         * A custom identifier for the user
         *
         * @maxLength 256
         */
        _customId?: string;
        /**
         * The signature of the user in base64 format
         *
         * @maxLength 2048
         */
        signature?: string | null;
        /**
         * The phone number of the user
         *
         * @maxLength 32
         */
        phone?: string;
        /**
         * Indicates whether the user is the default candidate watcher
         */
        defaultCandidateWatcher: boolean;
        /**
         * The user's default task workspace
         *
         * @pattern ^[0-9a-f]{24}$
         */
        defaultTaskWorkspace?: string | null;
        /**
         * Indicates whether the user has team admin privileges
         */
        teamAdmin: boolean;
        /**
         * The contact record for this user. If expanded, a contact object else the contact record ID.
         */
        contact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        /**
         * The date and time the user account was created
         */
        createdAt?: string;
        /**
         * Evertime details for this user
         */
        evertime?: {
          /**
           * The Evertime ConsultantId used for this user when exporting payment runs
           *
           * @maxLength 256
           */
          id?: string;
        };
        /**
         * Timesheet Portal details for this user
         */
        timesheetPortal?: {
          /**
           * The Timesheet Portal administrator GUID used when exporting payment runs
           *
           * @format uuid
           */
          id?: string;
          /**
           * The Timesheet Portal administrator code
           *
           * @maxLength 256
           */
          code?: string;
        };
      }[];
  /**
   * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
   */
  tags?:
    | string[]
    | {
        /**
         * The unique identifier of the tag
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The label displayed wherever this tag is applied.
         *
         * @minLength 1
         * @maxLength 128
         */
        name: string;
        /**
         * The tag display colour as a hexadecimal value, for example #2563EB.
         *
         * @maxLength 32
         */
        color?: string | null;
      }[];
  /**
   * The locations where the candidate desires to work.
   */
  desiredLocationMultiple?: string[];
  /**
   * The date when the candidate's availability was last checked.
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  availabilityLastChecked?: string;
  /**
   * Whether the candidate is subject to notice period.
   */
  subjectToNotice: "Unsure" | "Yes" | "No";
  /**
   * The subsidiary subjects the candidate is qualified to teach.
   */
  subsidiarySubject?: string[];
  /**
   * The candidate's preferred employment type.
   */
  employmentPreference?: ("Day To Day" | "Long Term" | "Fixed Term" | "Perm")[];
  /**
   * Any other names the candidate is known by.
   *
   * @maxLength 256
   */
  otherNames?: string;
  /**
   * The name of the candidate's limited company, if applicable.
   *
   * @maxLength 256
   */
  ltdCompany?: string;
  /**
   * Whether the candidate's employment has been verified.
   */
  employmentVerified?: boolean;
  /**
   * The DBS verification status for the candidate.
   *
   * @maxLength 150
   */
  candidateDBSVerification?: string;
  /**
   * Any notes related to the DBS verification status for the candidate.
   *
   * @maxLength 1024
   */
  candidateDBSVerificationNote?: string;
  /**
   * The DBS verification status for the candidate.
   */
  candidateDBSVerificationStatus?: "Cleared" | "Alerts";
  /**
   * The DBS check verification status for the candidate.
   *
   * @maxLength 150
   */
  candidateDBSCheckVerification?: string;
  /**
   * Any notes related to the DBS check verification status for the candidate.
   *
   * @maxLength 150
   */
  candidateDBSCheckVerificationNote?: string;
  /**
   * The DBS check verification status for the candidate.
   */
  candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
  /**
   * Any notes related to the DBS update service verification status for the candidate.
   *
   * @maxLength 150
   */
  candidateDBSUpdateServiceVerificationNote?: string;
  /**
   * The DBS update service verification status for the candidate.
   */
  candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
  /**
   * Any notes related to the TRN verification status for the candidate.
   *
   * @maxLength 150
   */
  candidateTRNVerificationNote?: string;
  /**
   * The TRN verification status for the candidate.
   */
  candidateTRNVerificationStatus?:
    | "Completed Induction"
    | "Not Yet Completed Induction"
    | "Induction Exempt"
    | "ALERTS - Failed Induction"
    | "Induction Not Required";
  /**
   * The health and safety verification status for the candidate.
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  candidateHealthSafetyVerification?: string;
  /**
   * Any notes related to the health and safety verification status for the candidate.
   *
   * @maxLength 150
   */
  candidateHealthSafetyVerificationNote?: string;
  /**
   * The health and safety verification status for the candidate.
   */
  candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
  /**
   * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
   */
  onboardingSession:
    | string
    | {
        /**
         * Unique identifier for the onboarding session
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The URL for the onboarding session
         *
         * @format uri
         */
        url: string;
        /**
         * The frozen health declaration configuration for this onboarding session.
         */
        healthDeclarationConfig?: {
          /**
           * The ordered questions included in the question set.
           *
           * @maxItems 100
           */
          questions: {
            /**
             * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * A stable key used to associate the question with current and historical answers.
             *
             * @minLength 1
             * @maxLength 128
             */
            key: string;
            /**
             * The question shown to the person completing the form.
             *
             * @minLength 1
             * @maxLength 500
             */
            label: string;
            /**
             * Optional supporting text displayed with the question.
             *
             * @maxLength 2000
             */
            description?: string;
            /**
             * The type of answer collected for the question.
             */
            type:
              | "checkbox"
              | "date"
              | "daterange"
              | "dropdown"
              | "number"
              | "signature"
              | "text"
              | "textarea";
            /**
             * Whether an answer is required while the question is visible.
             */
            required: boolean;
            /**
             * Whether the question is included in new forms. Historical responses retain disabled questions.
             */
            enabled: boolean;
            /**
             * The display order of the question.
             *
             * @minimum 0
             * @maximum 1000
             */
            order: number;
            /**
             * Optional rules that determine when the question is shown.
             */
            condition?: {
              /**
               * Whether any rule or every rule must match before the question is shown.
               */
              mode: "any" | "all";
              /**
               * The comparisons used to determine question visibility.
               *
               * @minItems 1
               * @maxItems 20
               */
              rules: {
                /**
                 * The stable key of the question that controls visibility.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey: string;
                /**
                 * How the controlling answer is compared with the value.
                 */
                operator: "equals" | "notEquals";
                /**
                 * The answer value used to determine visibility.
                 */
                value: string | boolean | number | (void | null);
              }[];
            };
            /**
             * The available answers for a dropdown question.
             *
             * @minItems 1
             * @maxItems 100
             */
            options?: {
              /**
               * The value stored when this option is selected.
               *
               * @minLength 1
               * @maxLength 200
               */
              value: string;
              /**
               * The option displayed to the person answering the question.
               *
               * @minLength 1
               * @maxLength 200
               */
              label: string;
            }[];
            /**
             * Whether this question belongs to the standard template.
             */
            ["default"]?: boolean;
            /**
             * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
             */
            audience?: "candidate" | "staff";
            /**
             * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
             */
            fixedRole?: "fullName" | "signature";
          }[];
          /**
           * When this question configuration was last updated.
           *
           * @format date-time
           */
          updatedAt?: string;
        };
        /**
         * The frozen Criminal Record & Security Checks configuration for this onboarding session.
         */
        criminalRecordConfig?: {
          /**
           * The ordered questions included in the question set.
           *
           * @maxItems 100
           */
          questions: {
            /**
             * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * A stable key used to associate the question with current and historical answers.
             *
             * @minLength 1
             * @maxLength 128
             */
            key: string;
            /**
             * The question shown to the person completing the form.
             *
             * @minLength 1
             * @maxLength 500
             */
            label: string;
            /**
             * Optional supporting text displayed with the question.
             *
             * @maxLength 2000
             */
            description?: string;
            /**
             * The type of answer collected for the question.
             */
            type:
              | "checkbox"
              | "date"
              | "daterange"
              | "dropdown"
              | "number"
              | "signature"
              | "text"
              | "textarea";
            /**
             * Whether an answer is required while the question is visible.
             */
            required: boolean;
            /**
             * Whether the question is included in new forms. Historical responses retain disabled questions.
             */
            enabled: boolean;
            /**
             * The display order of the question.
             *
             * @minimum 0
             * @maximum 1000
             */
            order: number;
            /**
             * Optional rules that determine when the question is shown.
             */
            condition?: {
              /**
               * Whether any rule or every rule must match before the question is shown.
               */
              mode: "any" | "all";
              /**
               * The comparisons used to determine question visibility.
               *
               * @minItems 1
               * @maxItems 20
               */
              rules: {
                /**
                 * The stable key of the question that controls visibility.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey: string;
                /**
                 * How the controlling answer is compared with the value.
                 */
                operator: "equals" | "notEquals";
                /**
                 * The answer value used to determine visibility.
                 */
                value: string | boolean | number | (void | null);
              }[];
            };
            /**
             * The available answers for a dropdown question.
             *
             * @minItems 1
             * @maxItems 100
             */
            options?: {
              /**
               * The value stored when this option is selected.
               *
               * @minLength 1
               * @maxLength 200
               */
              value: string;
              /**
               * The option displayed to the person answering the question.
               *
               * @minLength 1
               * @maxLength 200
               */
              label: string;
            }[];
            /**
             * Whether this question belongs to the standard template.
             */
            ["default"]?: boolean;
            /**
             * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
             */
            audience?: "candidate" | "staff";
            /**
             * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
             */
            fixedRole?: "fullName" | "signature";
          }[];
          /**
           * When this question configuration was last updated.
           *
           * @format date-time
           */
          updatedAt?: string;
        };
      };
  /**
   * The contact record for this candidate. If expanded, a contact object else the contact record ID.
   */
  contact?:
    | string
    | {
        /**
         * The unique identifier of the contact
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The forename of the contact
         *
         * @maxLength 128
         */
        forename: string;
        /**
         * The surname of the contact
         *
         * @maxLength 128
         */
        surname?: string;
        /**
         * The email address of the contact
         *
         * @format email
         */
        email: string;
        /**
         * The telephone number of the contact
         *
         * @maxLength 32
         */
        telephone?: string;
        /**
         * The mobile number of the contact
         *
         * @maxLength 32
         */
        mobile?: string;
        /**
         * The extension number of the contact
         *
         * @maxLength 8
         */
        extension?: string;
      };
  /**
   * Whether the candidate has opted out of 48 hour communications.
   */
  optOut48Hours?: boolean;
  /**
   * Whether the candidate has consented to REC audit checks.
   */
  recAuditConsent: boolean | null;
  /**
   * The date the candidate's REC audit consent was recorded.
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  recAuditConsentDate?: string;
  /**
   * The method through which REC audit consent was captured.
   *
   * @maxLength 256
   */
  recAuditConsentMethod?: string;
  /**
   * The date when the candidate record was created.
   *
   * @format date-time
   */
  createdAt?: string;
  /**
   * The mobile app link status for the candidate.
   */
  candidateMobileAppStatus?:
    | "Unlinked"
    | "Scheduled Unlink"
    | "Linked"
    | "Invited"
    | "Not Invited";
  /**
   * Evertime details for this candidate
   */
  evertime?: {
    /**
     * The Evertime CandidateId used for this candidate when exporting payment runs
     *
     * @maxLength 256
     */
    id?: string;
    /**
     * Whether the matched Evertime candidate's NI number also matches the one held in Edun
     */
    niNumberMatch?: boolean;
  };
  /**
   * Timesheet Portal details for this candidate
   */
  timesheetPortal?: {
    /**
     * The Timesheet Portal contractor GUID used when exporting payment runs
     *
     * @format uuid
     */
    id?: string;
    /**
     * The Timesheet Portal contractor code
     *
     * @maxLength 256
     */
    code?: string;
  };
  /**
   * Invite to candidate to link their mobile app.
   */
  mobileAppLinkInvite?: {
    /**
     * Boolean status of whether the link invite is being used
     *
     * @default false
     */
    isUsed?: boolean;
    /**
     * Creation date of the invite
     *
     * @format date-time
     * @default 2026-10-30T13:39:09.195Z
     */
    createdAt?: string;
    /**
     * ID of candidate using this link
     *
     * @pattern ^[0-9a-f]{24}$
     */
    usedBy?: string;
    /**
     * Date of when link used
     *
     * @format date-time
     */
    usedAt?: string;
    /**
     * If unlinked, ID of user that unlinked this candidate's link
     *
     * @pattern ^[0-9a-f]{24}$
     */
    unlinkedBy?: string;
    /**
     * If unlinked, date of when this link was unlinked
     *
     * @format date-time
     */
    unlinkedAt?: string;
    /**
     * If deleted, ID of user that deleted this candidate's link
     *
     * @pattern ^[0-9a-f]{24}$
     */
    deletedBy?: string;
    /**
     * If deleted, date of when this link was deleted
     *
     * @format date-time
     */
    deletedAt?: string;
    /**
     * Current status of this mobile app link
     */
    status?:
      | "Sent"
      | "Linked"
      | "Unlinked"
      | "ScheduledUnlink"
      | "Not Sent"
      | "Rescinded";
  };
};

export type CreateCandidateVariables = {
  body: CreateCandidateRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateCandidate = (
  variables: CreateCandidateVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateCandidateResponse,
    CreateCandidateError,
    CreateCandidateRequestBody,
    {},
    {},
    {}
  >({ url: "/candidates", method: "post", ...variables, signal });

export const useCreateCandidate = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateCandidateResponse,
      CreateCandidateError,
      CreateCandidateVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateCandidateResponse,
    CreateCandidateError,
    CreateCandidateVariables
  >({
    mutationFn: (variables: CreateCandidateVariables) =>
      fetchCreateCandidate(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetCandidatePathParams = {
  /**
   * The unique identifier for the candidate.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
};

export type GetCandidateQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "forename"
    | "surname"
    | "previousNames"
    | "email"
    | "telephone"
    | "mobile"
    | "occupation"
    | "subject"
    | "address.addressLine1"
    | "address.addressLine2"
    | "address.addressLine3"
    | "address.city"
    | "address.county"
    | "address.borough"
    | "address.postcode"
    | "address.country"
    | "address.lat"
    | "address.lng"
    | "address"
    | "qualifications"
    | "employment"
    | "references"
    | "referenceCheckConsent"
    | "referenceCheckConsentName"
    | "referenceCheckConsentDate"
    | "dob"
    | "sex"
    | "status"
    | "onboardingStatus"
    | "onboardingSent"
    | "nextOfKin.nextOfKinName"
    | "nextOfKin.nextOfKinRelationship"
    | "nextOfKin.nextOfKinTelephone"
    | "nextOfKin.nextOfKinEmail"
    | "nextOfKin.nextOfKinAddress.addressLine1"
    | "nextOfKin.nextOfKinAddress.addressLine2"
    | "nextOfKin.nextOfKinAddress.addressLine3"
    | "nextOfKin.nextOfKinAddress.city"
    | "nextOfKin.nextOfKinAddress.county"
    | "nextOfKin.nextOfKinAddress.borough"
    | "nextOfKin.nextOfKinAddress.postcode"
    | "nextOfKin.nextOfKinAddress.country"
    | "nextOfKin.nextOfKinAddress.lat"
    | "nextOfKin.nextOfKinAddress.lng"
    | "nextOfKin.nextOfKinAddress"
    | "nextOfKin"
    | "criminalRecord.dbsHeld"
    | "criminalRecord.dbsNumber"
    | "criminalRecord.dbsType"
    | "criminalRecord.dbsLastChecked"
    | "criminalRecord.dbsUpdateServiceNumber"
    | "criminalRecord.prohibitionCheck"
    | "criminalRecord.overseasCheck"
    | "criminalRecord.overseasCheckNote"
    | "criminalRecord.convictedOffence"
    | "criminalRecord.convictedOffenceDescription"
    | "criminalRecord.convictions"
    | "criminalRecord.convictionsDescription"
    | "criminalRecord.registeredToUpdateService"
    | "criminalRecord.consentUpdateServiceChecks"
    | "criminalRecord.note"
    | "criminalRecord.updateServiceCheckDate"
    | "criminalRecord.abroad"
    | "criminalRecord.overseasDeclarationExtended"
    | "criminalRecord.educationalDisciplinaryProceedings"
    | "criminalRecord.dbsComments"
    | "criminalRecord.dbsDeclarationRead"
    | "criminalRecord.overseasOffence"
    | "criminalRecord.overseasOffenceDescription"
    | "criminalRecord.currentCriminalOffence"
    | "criminalRecord.currentCriminalOffenceDescription"
    | "criminalRecord.dbsIssuedByBusiness"
    | "criminalRecord.dbsIssuedByCurrentEmployer"
    | "criminalRecord.section128Required"
    | "criminalRecord.section128Checked"
    | "criminalRecord"
    | "criminalRecordDeclaration.questions"
    | "criminalRecordDeclaration.answers"
    | "criminalRecordDeclaration.submittedAt"
    | "criminalRecordDeclaration.submittedIp"
    | "criminalRecordDeclaration.configurationUpdatedAt"
    | "criminalRecordDeclaration"
    | "criminalRecordCheckConsent"
    | "criminalRecordCheckConsentName"
    | "criminalRecordCheckConsentDate"
    | "safeInEducation.barred"
    | "safeInEducation.barredCheckDate"
    | "safeInEducation.waiver"
    | "safeInEducation.ordersYou"
    | "safeInEducation.ordersChild"
    | "safeInEducation.previousSafeguardingAllegations"
    | "safeInEducation.cautioned"
    | "safeInEducation.violentOffence"
    | "safeInEducation.anotherCountry"
    | "safeInEducation.readKCSIE"
    | "safeInEducation.readChildcareAct"
    | "safeInEducation.prohibitedFromTeaching"
    | "safeInEducation.knownPolice"
    | "safeInEducation.prohibitedFromManagement"
    | "safeInEducation.criminalOffencesLaw"
    | "safeInEducation.safeInEducationDetails"
    | "safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
    | "safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
    | "safeInEducation.safeguardingQuestionaire.signOfAbuse"
    | "safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
    | "safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
    | "safeInEducation.safeguardingQuestionaire.fgmDefinition"
    | "safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
    | "safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
    | "safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
    | "safeInEducation.safeguardingQuestionaire.studentConfides"
    | "safeInEducation.safeguardingQuestionaire"
    | "safeInEducation"
    | "socialMedia.facebook"
    | "socialMedia.instagram"
    | "socialMedia.twitter"
    | "socialMedia.linkedIn"
    | "socialMedia.tiktok"
    | "socialMedia.others"
    | "socialMedia.socialMediaChecked"
    | "socialMedia.googleCheckedDate"
    | "socialMedia"
    | "safeInEducationCheckConsent"
    | "safeInEducationCheckConsentName"
    | "safeInEducationCheckConsentDate"
    | "healthRecord.physicalAbilityCondition"
    | "healthRecord.confused"
    | "healthRecord.hearing"
    | "healthRecord.eyesight"
    | "healthRecord.depression"
    | "healthRecord.severePain"
    | "healthRecord.excessiveDrowsiness"
    | "healthRecord.epilepsy"
    | "healthRecord.heart"
    | "healthRecord.asthma"
    | "healthRecord.mentalHealth"
    | "healthRecord.expectantMother"
    | "healthRecord.allergies"
    | "healthRecord.medication"
    | "healthRecord.healthIssues"
    | "healthRecord.healthIssuesDescription"
    | "healthRecord.disabilityNeeds"
    | "healthRecord.contagiousDiseases"
    | "healthRecord.affectAbility"
    | "healthRecord.notificationAgree"
    | "healthRecord.sasOptIn"
    | "healthRecord.signature"
    | "healthRecord.drawnSignature"
    | "healthRecord.signatureDate"
    | "healthRecord.vaccinationMeasles"
    | "healthRecord.vaccinationMumps"
    | "healthRecord.vaccinationRubella"
    | "healthRecord.vaccinationVaricella"
    | "healthRecord.vaccinationTuberculosis"
    | "healthRecord.vaccinationHepatitisB"
    | "healthRecord.vaccinationHepatitisC"
    | "healthRecord.vaccinationHIV"
    | "healthRecord.vaccinationCovid"
    | "healthRecord.vaccinationCovid1"
    | "healthRecord.vaccinationCovid2"
    | "healthRecord.vaccinationCovidBooster"
    | "healthRecord.vaccinationCovidInfluenza"
    | "healthRecord.fitToWorkOverride"
    | "healthRecord"
    | "healthDeclaration.questions"
    | "healthDeclaration.answers"
    | "healthDeclaration.submittedAt"
    | "healthDeclaration.submittedIp"
    | "healthDeclaration.configurationUpdatedAt"
    | "healthDeclaration"
    | "healthRecordCheckConsent"
    | "healthRecordCheckConsentName"
    | "healthRecordCheckConsentDate"
    | "bankDetails.bankAccountName"
    | "bankDetails.bankName"
    | "bankDetails.accountNumber"
    | "bankDetails.sortCode"
    | "bankDetails.niNumber"
    | "bankDetails.jobOp1"
    | "bankDetails.jobOp2"
    | "bankDetails.jobOp3"
    | "bankDetails.studentLoan.studentLoan"
    | "bankDetails.studentLoan.plan1"
    | "bankDetails.studentLoan.plan2"
    | "bankDetails.studentLoan.plan4"
    | "bankDetails.studentLoan.postGradLoan"
    | "bankDetails.studentLoan"
    | "bankDetails"
    | "declarationCheckConsent"
    | "declarationCheckConsentName"
    | "declarationCheckConsentDate"
    | "contractForServiceConsent"
    | "contractForServiceName"
    | "contractForServiceDate"
    | "confirmed"
    | "createdBy"
    | "customerId"
    | "migrationId"
    | "migrationApproval"
    | "assignee"
    | "assignee.id"
    | "assignee.email"
    | "assignee.firstName"
    | "assignee.lastName"
    | "assignee.username"
    | "assignee.lea"
    | "assignee.clients"
    | "assignee.position"
    | "assignee.permissions"
    | "assignee.activationCode"
    | "assignee.customer"
    | "assignee.disabled"
    | "assignee.commissionThreshold"
    | "assignee.branches"
    | "assignee.displayOnboarding"
    | "assignee._customId"
    | "assignee.signature"
    | "assignee.phone"
    | "assignee.defaultCandidateWatcher"
    | "assignee.defaultTaskWorkspace"
    | "assignee.teamAdmin"
    | "assignee.contact"
    | "assignee.contact.id"
    | "assignee.contact.forename"
    | "assignee.contact.surname"
    | "assignee.contact.email"
    | "assignee.contact.telephone"
    | "assignee.contact.mobile"
    | "assignee.contact.extension"
    | "assignee.createdAt"
    | "assignee.evertime.id"
    | "assignee.evertime"
    | "assignee.timesheetPortal.id"
    | "assignee.timesheetPortal.code"
    | "assignee.timesheetPortal"
    | "currentBooking"
    | "profilePhoto"
    | "payType"
    | "payInterval"
    | "payRate"
    | "customId"
    | "_internalId"
    | "statusChangeDate"
    | "optOut"
    | "trnNumber"
    | "trnNumberCheckedDate"
    | "trnNumberNotes"
    | "employmentStartDate"
    | "ewcConfirmation"
    | "homeOfficeShareCode"
    | "archived"
    | "umbrella"
    | "payeUmbrella"
    | "umbrellaCompanyName"
    | "umbrellaCompanyCode"
    | "transport"
    | "importantNotes"
    | "candidateLastUpdated"
    | "source"
    | "sourceOther"
    | "onlineCheckConsent"
    | "title"
    | "pronoun"
    | "updatedAt"
    | "lastContactedDate"
    | "highlight"
    | "teacherPayScale"
    | "p45"
    | "roleType"
    | "rtwVerification"
    | "rtwVerificationNote"
    | "rtwVerificationStatus"
    | "desiredLocation"
    | "complianceUser"
    | "complianceUser.id"
    | "complianceUser.email"
    | "complianceUser.firstName"
    | "complianceUser.lastName"
    | "complianceUser.username"
    | "complianceUser.lea"
    | "complianceUser.clients"
    | "complianceUser.position"
    | "complianceUser.permissions"
    | "complianceUser.activationCode"
    | "complianceUser.customer"
    | "complianceUser.disabled"
    | "complianceUser.commissionThreshold"
    | "complianceUser.branches"
    | "complianceUser.displayOnboarding"
    | "complianceUser._customId"
    | "complianceUser.signature"
    | "complianceUser.phone"
    | "complianceUser.defaultCandidateWatcher"
    | "complianceUser.defaultTaskWorkspace"
    | "complianceUser.teamAdmin"
    | "complianceUser.contact"
    | "complianceUser.contact.id"
    | "complianceUser.contact.forename"
    | "complianceUser.contact.surname"
    | "complianceUser.contact.email"
    | "complianceUser.contact.telephone"
    | "complianceUser.contact.mobile"
    | "complianceUser.contact.extension"
    | "complianceUser.createdAt"
    | "complianceUser.evertime.id"
    | "complianceUser.evertime"
    | "complianceUser.timesheetPortal.id"
    | "complianceUser.timesheetPortal.code"
    | "complianceUser.timesheetPortal"
    | "watchers"
    | "tags"
    | "documents"
    | "documentsCount"
    | "desiredLocationMultiple"
    | "availabilityDays"
    | "availabilityLastChecked"
    | "subjectToNotice"
    | "subsidiarySubject"
    | "employmentPreference"
    | "otherNames"
    | "ltdCompany"
    | "employmentVerified"
    | "candidateDBSVerification"
    | "candidateDBSVerificationNote"
    | "candidateDBSVerificationStatus"
    | "candidateDBSCheckVerification"
    | "candidateDBSCheckVerificationNote"
    | "candidateDBSCheckVerificationStatus"
    | "candidateDBSUpdateServiceVerificationNote"
    | "candidateDBSUpdateServiceVerificationStatus"
    | "candidateTRNVerificationNote"
    | "candidateTRNVerificationStatus"
    | "candidateHealthSafetyVerification"
    | "candidateHealthSafetyVerificationNote"
    | "candidateHealthSafetyVerificationStatus"
    | "onboardingSession"
    | "onboardingSession.id"
    | "onboardingSession.url"
    | "onboardingSession.healthDeclarationConfig.questions"
    | "onboardingSession.healthDeclarationConfig.updatedAt"
    | "onboardingSession.healthDeclarationConfig"
    | "onboardingSession.criminalRecordConfig.questions"
    | "onboardingSession.criminalRecordConfig.updatedAt"
    | "onboardingSession.criminalRecordConfig"
    | "contact"
    | "contact.id"
    | "contact.forename"
    | "contact.surname"
    | "contact.email"
    | "contact.telephone"
    | "contact.mobile"
    | "contact.extension"
    | "optOut48Hours"
    | "recAuditConsent"
    | "recAuditConsentDate"
    | "recAuditConsentMethod"
    | "createdAt"
    | "candidateMobileAppStatus"
    | "evertime.id"
    | "evertime.niNumberMatch"
    | "evertime"
    | "relevanceScore"
    | "matchReasons"
    | "distance"
    | "timesheetPortal.id"
    | "timesheetPortal.code"
    | "timesheetPortal"
    | "mobileAppLinkInvite.isUsed"
    | "mobileAppLinkInvite.createdAt"
    | "mobileAppLinkInvite.usedBy"
    | "mobileAppLinkInvite.usedAt"
    | "mobileAppLinkInvite.unlinkedBy"
    | "mobileAppLinkInvite.unlinkedAt"
    | "mobileAppLinkInvite.deletedBy"
    | "mobileAppLinkInvite.deletedAt"
    | "mobileAppLinkInvite.status"
    | "mobileAppLinkInvite"
    | ""
  )[];
  /**
   * Additional fields to expand in the response
   */
  expand?: ("assignee" | "onboardingSession" | "contact" | "documents")[];
};

export type GetCandidateError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetCandidateResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the candidate
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The forename of the candidate
     *
     * @minLength 1
     * @maxLength 256
     */
    forename?: string;
    /**
     * The surname of the candidate
     *
     * @minLength 1
     * @maxLength 256
     */
    surname?: string;
    /**
     * Any previous names of the candidate
     *
     * @maxLength 256
     */
    previousNames?: string;
    /**
     * The email of the candidate. This will be used for communication.
     *
     * @format email
     */
    email?: string;
    /**
     * The telephone number of the candidate
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * The mobile number of the candidate
     *
     * @maxLength 32
     */
    mobile?: string;
    /**
     * The occupation of the candidate
     */
    occupation?:
      | "Teacher"
      | "Cover Supervisor"
      | "Teaching Assistant"
      | "School Support Staff"
      | "Primary Teacher"
      | "Secondary Teacher"
      | "Head Teacher"
      | "Senior Leader"
      | "Tutor"
      | "Mentor"
      | "Learning Support Assistant"
      | "Finance Officer"
      | "HR Administrator"
      | "HR Manager"
      | "Office Manager"
      | "Administrator"
      | "Personal Assistant"
      | "Pastoral Manager"
      | "Receptionist"
      | "Instructor"
      | "Caretaker"
      | "Nursery Assistant"
      | "Nursery Nurse"
      | "Deputy Nursery Manager"
      | "Lunchtime Supervisor"
      | "Classroom Graduate"
      | "Primary Cover Supervisor"
      | "HLTA"
      | "Nurture Teacher"
      | "Nurture Mentor"
      | "SENDCo"
      | "Registered Manager"
      | "Regional Manager"
      | "Education Director"
      | "Residential Director"
      | "Deputy Manager"
      | "Residential Support Worker"
      | "Sports Coach"
      | "Executive Headteacher"
      | "General Practitioner"
      | "Advance Nurse Practitioner (ANP)"
      | "Emergency Nurse Practitioner (ENP)"
      | "Advanced Clinical Practitioner (ACP)"
      | "Emergency Clinical Practitioner (ECP)"
      | "Consultant Psychiatrist"
      | "Psychiatrist \u2013 SPR"
      | "IT Technician"
      | "Art Technician"
      | "DT Technician"
      | "Science Technician"
      | "Healthcare Assistant/ Residential Support Worker"
      | "Healthcare Assistant"
      | "Senior Healthcare Assistant"
      | "Team Leader"
      | "Registered General Nurse"
      | "Registered Mental Nurse"
      | "Registered Nurse Learning Disabilities"
      | "Overseas Trained Teacher";
    /**
     * The subject the candidate specializes in
     *
     * @maxLength 256
     */
    subject?: string;
    /**
     * The address of the resource
     */
    address?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The qualifications of the candidate
     *
     * @maxItems 32
     */
    qualifications?: {
      /**
       * The type of the qualification
       *
       * @maxLength 256
       */
      type?: string;
      /**
       * The place where the qualification was obtained
       *
       * @maxLength 256
       */
      place?: string;
      /**
       * The name of the qualification
       *
       * @maxLength 256
       */
      qualificationName?: string;
      /**
       * The date the qualification was obtained
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      date?: string;
      /**
       * The unique identifier of the qualification
       *
       * @maxLength 256
       */
      qualificationId?: string;
      /**
       * The order index of the qualification
       *
       * @minimum 0
       */
      orderIndex?: number;
      /**
       * The expiry date of the qualification
       *
       * @maxLength 256
       */
      qualificationExpiry?: string;
      /**
       * Whether to exclude this qualification from vetting
       */
      excludeFromVetting?: boolean;
      /**
       * The date the qualification was created
       *
       * @format date-time
       */
      createdAt?: string | null;
    }[];
    /**
     * The employment history of the candidate
     *
     * @maxItems 32
     */
    employment?: {
      /**
       * The name of the employer
       *
       * @maxLength 256
       */
      employerName?: string;
      /**
       * The date the candidate started employment
       *
       * @maxLength 256
       */
      dateStarted?: string;
      /**
       * The date the candidate ended employment
       *
       * @maxLength 256
       */
      dateEnded?: string;
      /**
       * The job title of the candidate
       *
       * @maxLength 256
       */
      jobTitle?: string;
      /**
       * The reason the candidate left this employment
       *
       * @maxLength 1024
       */
      reasonForLeaving?: string;
      /**
       * The duties of the candidate in this role. Consultant only, visible on Edun CV
       *
       * @maxLength 1024
       */
      duties?: string;
      /**
       * The salary of the candidate in this role
       *
       * @maxLength 256
       */
      salary?: string;
      /**
       * The address of the candidate's reference for this employment
       */
      referenceAddress?: {
        /**
         * The first line of the address
         *
         * @maxLength 256
         */
        addressLine1?: string;
        /**
         * The second line of the address
         *
         * @maxLength 256
         */
        addressLine2?: string;
        /**
         * The third line of the address
         *
         * @maxLength 256
         */
        addressLine3?: string;
        /**
         * The city of the address
         *
         * @maxLength 256
         */
        city?: string;
        /**
         * The county of the address
         *
         * @maxLength 256
         */
        county?: string;
        /**
         * The borough of the address
         *
         * @maxLength 256
         */
        borough?: string;
        /**
         * The postcode of the address
         *
         * @maxLength 10
         */
        postcode?: string;
        /**
         * The country of the address
         *
         * @maxLength 256
         */
        country?: string;
        /**
         * The latitude of the address
         *
         * @minimum -90
         * @maximum 90
         */
        lat?: number;
        /**
         * The longitude of the address
         *
         * @minimum -180
         * @maximum 180
         */
        lng?: number;
      };
      /**
       * The unique identifier for this employment record
       *
       * @maxLength 256
       */
      employmentId?: string;
      /**
       * The order index of this employment record
       *
       * @minimum 0
       */
      orderIndex?: number;
    }[];
    /**
     * The references for the candidate
     *
     * @maxItems 32
     */
    references?: {
      /**
       * The title of the reference
       *
       * @maxLength 16
       */
      referenceTitle?: string;
      /**
       * The forename of the reference
       *
       * @maxLength 256
       */
      referenceForename?: string;
      /**
       * The surname of the reference
       *
       * @maxLength 256
       */
      referenceSurname?: string;
      /**
       * The company of the reference
       *
       * @maxLength 256
       */
      referenceCompany?: string;
      /**
       * The position of the reference
       *
       * @maxLength 256
       */
      referencePosition?: string;
      /**
       * The email of the reference
       *
       * @format email
       */
      referenceEmail?: string;
      /**
       * The telephone number of the reference
       *
       * @maxLength 32
       */
      referenceTelephone?: string;
      /**
       * The type of the reference
       */
      referenceType?:
        | "Education"
        | "Professional"
        | "Character"
        | "Factual"
        | "Academic"
        | "Volunteer";
      /**
       * The duration of the reference
       *
       * @maxLength 128
       */
      referenceDuration?: string;
      /**
       * The ID of the reference
       *
       * @maxLength 256
       */
      referenceId?: string;
      /**
       * This field only applies if the reference immediate consent question is enabled
       * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
       */
      referenceApply?: boolean;
      /**
       * Any additional notes about the reference
       *
       * @maxLength 1024
       */
      referenceNote?: string;
      /**
       * The start date of the reference
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      dateFrom?: string;
      /**
       * The end date of the reference
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      dateTo?: string;
    }[];
    /**
     * Whether the candidate has consented to a reference check
     */
    referenceCheckConsent?: boolean;
    /**
     * The name of the person who provided consent for the reference check
     *
     * @maxLength 256
     */
    referenceCheckConsentName?: string;
    /**
     * The date when consent for the reference check was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    referenceCheckConsentDate?: string;
    /**
     * The date of birth of the candidate
     *
     * @format date-time
     */
    dob?: string | null;
    /**
     * The sex of the candidate
     */
    sex?: "Male" | "Female" | "Other" | "Unknown";
    /**
     * The status of the candidate
     */
    status?:
      | "Pending"
      | "Approved"
      | "Rejected"
      | "Anonymised"
      | "Archived"
      | "Enquired"
      | "Hold"
      | "Future"
      | "DNC"
      | "Pre";
    /**
     * The current status of the onboarding process for the candidate
     */
    onboardingStatus?: "Not Started" | "In Progress" | "Completed";
    /**
     * Whether the onboarding information has been sent to the candidate
     */
    onboardingSent?: boolean;
    nextOfKin?: {
      /**
       * The name of the next of kin
       *
       * @maxLength 256
       */
      nextOfKinName?: string;
      /**
       * The relationship of the next of kin to the candidate
       *
       * @maxLength 256
       */
      nextOfKinRelationship?: string;
      /**
       * The telephone number of the next of kin
       *
       * @maxLength 32
       */
      nextOfKinTelephone?: string;
      /**
       * The email address of the next of kin
       *
       * @format email
       */
      nextOfKinEmail?: string;
      nextOfKinAddress?: {
        /**
         * The first line of the address
         *
         * @maxLength 256
         */
        addressLine1?: string;
        /**
         * The second line of the address
         *
         * @maxLength 256
         */
        addressLine2?: string;
        /**
         * The third line of the address
         *
         * @maxLength 256
         */
        addressLine3?: string;
        /**
         * The city of the address
         *
         * @maxLength 256
         */
        city?: string;
        /**
         * The county of the address
         *
         * @maxLength 256
         */
        county?: string;
        /**
         * The borough of the address
         *
         * @maxLength 256
         */
        borough?: string;
        /**
         * The postcode of the address
         *
         * @maxLength 10
         */
        postcode?: string;
        /**
         * The country of the address
         *
         * @maxLength 256
         */
        country?: string;
        /**
         * The latitude of the address
         *
         * @minimum -90
         * @maximum 90
         */
        lat?: number;
        /**
         * The longitude of the address
         *
         * @minimum -180
         * @maximum 180
         */
        lng?: number;
      };
    };
    criminalRecord?: {
      /**
       * Whether the candidate has a DBS certificate
       */
      dbsHeld?: boolean;
      /**
       * The unique identifier for the DBS certificate
       *
       * @maxLength 256
       */
      dbsNumber?: string;
      /**
       * The type of DBS check
       */
      dbsType?:
        | "Basic"
        | "Standard"
        | "Enhanced"
        | "Enhanced with Barred List"
        | "Child workforce"
        | "Child and adult workforce"
        | "Adult workforce";
      /**
       * The date when the DBS check was last performed
       *
       * @maxLength 256
       */
      dbsLastChecked?: string;
      /**
       * The DBS update service number for the candidate
       *
       * @maxLength 256
       */
      dbsUpdateServiceNumber?: string;
      /**
       * The result of the prohibition check
       *
       * @maxLength 256
       */
      prohibitionCheck?: string;
      /**
       * The result of the overseas check
       *
       * @maxLength 256
       */
      overseasCheck?: string;
      /**
       * Any additional notes regarding the overseas check
       *
       * @maxLength 256
       */
      overseasCheckNote?: string;
      /**
       * Whether the candidate has a convicted offence
       */
      convictedOffence?: boolean;
      /**
       * The description of the convicted offence
       *
       * @maxLength 1024
       */
      convictedOffenceDescription?: string;
      /**
       * Whether the candidate has any convictions
       */
      convictions?: boolean;
      /**
       * The description of the convictions
       *
       * @maxLength 1024
       */
      convictionsDescription?: string;
      /**
       * Whether the candidate is registered to the DBS update service
       */
      registeredToUpdateService?: boolean;
      /**
       * Whether the candidate consents to update service checks
       */
      consentUpdateServiceChecks?: boolean;
      /**
       * Any additional notes regarding the candidate's criminal record
       *
       * @maxLength 1024
       */
      note?: string;
      /**
       * The date when the update service check was performed
       *
       * @maxLength 64
       */
      updateServiceCheckDate?: string;
      /**
       * Whether the candidate has lived abroad
       */
      abroad?: boolean;
      /**
       * Any additional notes regarding the overseas declaration
       *
       * @maxLength 1024
       */
      overseasDeclarationExtended?: string;
      /**
       * Whether the candidate has been subject to educational disciplinary proceedings
       */
      educationalDisciplinaryProceedings?: boolean;
      /**
       * Any comments regarding the DBS check
       *
       * @maxLength 1024
       */
      dbsComments?: string;
      /**
       * Whether the candidate has read the DBS declaration
       */
      dbsDeclarationRead?: boolean;
      /**
       * Whether the candidate has any overseas offences
       */
      overseasOffence?: boolean;
      /**
       * The description of the overseas offence
       *
       * @maxLength 1024
       */
      overseasOffenceDescription?: string;
      /**
       * Whether the candidate has a current criminal offence
       */
      currentCriminalOffence?: boolean;
      /**
       * The description of the current criminal offence
       *
       * @maxLength 1024
       */
      currentCriminalOffenceDescription?: string;
      /**
       * Whether the DBS was issued by a business
       */
      dbsIssuedByBusiness?: boolean;
      /**
       * Whether the DBS was issued by the current employer
       */
      dbsIssuedByCurrentEmployer?: boolean;
      /**
       * Whether a Section 128 check is required for the candidate.
       */
      section128Required?: boolean;
      /**
       * The date when the Section 128 check was completed.
       */
      section128Checked?: string;
    };
    /**
     * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
     */
    criminalRecordDeclaration?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * The answers recorded for the point-in-time question set.
       *
       * @maxItems 100
       */
      answers?: {
        /**
         * The identifier of the question being answered.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        questionId?: string;
        /**
         * The stable key of the question being answered.
         *
         * @minLength 1
         * @maxLength 128
         */
        questionKey?: string;
        /**
         * The submitted answer to the question.
         */
        value?: string | string | boolean | number | (void | null);
      }[];
      /**
       * When the question set was submitted.
       *
       * @format date-time
       */
      submittedAt?: string;
      /**
       * The network address recorded when the response was submitted.
       *
       * @maxLength 64
       */
      submittedIp?: string;
      /**
       * When the question configuration captured by this response was last updated.
       *
       * @format date-time
       */
      configurationUpdatedAt?: string;
    };
    /**
     * Whether the candidate confirmed their criminal record declaration.
     */
    criminalRecordCheckConsent?: boolean;
    /**
     * The name used to confirm the criminal record declaration.
     *
     * @maxLength 256
     */
    criminalRecordCheckConsentName?: string;
    /**
     * The date the criminal record declaration was confirmed.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    criminalRecordCheckConsentDate?: string;
    safeInEducation?: {
      /**
       * Whether the candidate is barred from working in education
       */
      barred?: boolean;
      /**
       * The date when the barred status was checked
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      barredCheckDate?: string;
      /**
       * Whether the candidate has a waiver for the barred status
       */
      waiver?: boolean;
      /**
       * Any orders related to the candidate's barred status
       */
      ordersYou?: boolean;
      /**
       * Any orders related to the candidate's barred status for children
       */
      ordersChild?: boolean;
      /**
       * Whether the candidate has any previous safeguarding allegations
       */
      previousSafeguardingAllegations?: boolean;
      /**
       * Whether the candidate has been cautioned
       */
      cautioned?: boolean;
      /**
       * Whether the candidate has a violent offence
       */
      violentOffence?: boolean;
      /**
       * Whether the candidate has lived or worked in another country
       */
      anotherCountry?: boolean;
      /**
       * Whether the candidate has read the Keeping Children Safe in Education document
       */
      readKCSIE?: boolean;
      /**
       * Whether the candidate has read the Disqualification under the Childcare Act guidance
       */
      readChildcareAct?: boolean;
      /**
       * Whether the candidate is prohibited from teaching
       */
      prohibitedFromTeaching?: boolean;
      /**
       * Whether the candidate is known to the police
       */
      knownPolice?: boolean;
      /**
       * Whether the candidate is prohibited from management roles
       */
      prohibitedFromManagement?: boolean;
      /**
       * Whether the candidate has any criminal offences under law
       */
      criminalOffencesLaw?: boolean;
      /**
       * Any additional details regarding the candidate's safe in education status
       */
      safeInEducationDetails?: string;
      safeguardingQuestionaire?: {
        /**
         * What is the primary purpose of safeguarding in a school environment?
         */
        primaryPurposeSafeguarding?:
          | "enhance-academic-performance"
          | "ensure-safe-learning-environment"
          | "promote-healthy-relationships"
          | "facilitate-extracurricular-activities";
        /**
         * Who is typically responsible for coordinating safeguarding efforts within a school?
         */
        typicallyResponsibleCoordinatingSafeguarding?:
          | "janitorial-staff"
          | "parents-association"
          | "school-nurse"
          | "designated-safeguarding-lead";
        /**
         * Which of the following is considered a potential sign of child abuse or neglect?
         */
        signOfAbuse?:
          | "student-excelling"
          | "frequent-extracurricular"
          | "unexplained-injuries"
          | "not-listening";
        /**
         * How should a teacher respond if a student discloses information about potential abuse or harm?
         */
        studentDisclosesInformation?:
          | "ignore-disclosure"
          | "report-disclosure"
          | "share-information"
          | "advise-independent";
        /**
         * What is the purpose of a school's Child Protection Policy?
         */
        schoolsChildProtectionPolicy?:
          | "enforcing-strict-disciplinary-measures"
          | "identifying-troublemakers"
          | "outlining-procedures-for-protecting-students-from-harm"
          | "establishing-academic-achievement-standards";
        /**
         * What does the term 'FGM' stand for in the context of safeguarding?
         */
        fgmDefinition?:
          | "female-general-medicine"
          | "forced-genetic-modification"
          | "female-genital-mutilation"
          | "foundation-gender-equality";
        /**
         * Which of the following is a key principle of online safety for students?
         */
        keyPrincipleOnlineSafetyStudents?:
          | "unlimited-screen-time"
          | "anon-online-interaction"
          | "responsible-digital-citizen"
          | "restrict-edu-sites";
        /**
         * When planning a school trip, what should teachers consider regarding safeguarding?
         */
        teachersConsiderRegardingSafeguarding?:
          | "minimise-supervision"
          | "appropriate-staff-ratios"
          | "exclude-medical-conditions"
          | "allow-roaming";
        /**
         * What is the purpose of a criminal background check for teachers?
         */
        criminalBackgroundCheckTeachers?:
          | "evaluating-teaching-proficiency"
          | "verifying-immigration-status"
          | "assessing-financial-stability"
          | "identifying-criminal-history";
        /**
         * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
         */
        studentConfides?:
          | "assure-secret-safe"
          | "encourage-share-friends"
          | "explain-no-guarantee"
          | "disclose-to-others";
      };
    };
    socialMedia?: {
      /**
       * The Facebook profile URL of the candidate
       *
       * @maxLength 256
       */
      facebook?: string;
      /**
       * The Instagram profile URL of the candidate
       *
       * @maxLength 256
       */
      instagram?: string;
      /**
       * The Twitter profile URL of the candidate
       *
       * @maxLength 256
       */
      twitter?: string;
      /**
       * The LinkedIn profile URL of the candidate
       *
       * @maxLength 256
       */
      linkedIn?: string;
      /**
       * The TikTok profile URL of the candidate
       *
       * @maxLength 256
       */
      tiktok?: string;
      /**
       * Any other social media profile URLs of the candidate
       *
       * @maxLength 256
       */
      others?: string;
      /**
       * Whether the social media profiles have been checked
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      socialMediaChecked?: string;
      /**
       * The date the candidate's Google search check was completed
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      googleCheckedDate?: string;
    };
    /**
     * Whether the candidate has consented to a safe in education check
     */
    safeInEducationCheckConsent?: boolean;
    /**
     * The name of the person who provided consent for the safe in education check
     *
     * @maxLength 256
     */
    safeInEducationCheckConsentName?: string;
    /**
     * The date when consent for the safe in education check was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    safeInEducationCheckConsentDate?: string;
    /**
     * The health record of the individual
     */
    healthRecord?: {
      /**
       * Whether the individual has a physical ability condition
       */
      physicalAbilityCondition?: boolean;
      /**
       * Whether the individual is confused
       */
      confused?: boolean;
      /**
       * Whether the individual has hearing issues
       */
      hearing?: boolean;
      /**
       * Whether the individual has eyesight issues
       */
      eyesight?: boolean;
      /**
       * Whether the individual has depression
       */
      depression?: boolean;
      /**
       * Whether the individual has severe pain
       */
      severePain?: boolean;
      /**
       * Whether the individual has excessive drowsiness
       */
      excessiveDrowsiness?: boolean;
      /**
       * Whether the individual has epilepsy
       */
      epilepsy?: boolean;
      /**
       * Whether the individual has heart issues
       */
      heart?: boolean;
      /**
       * Whether the individual has asthma
       */
      asthma?: boolean;
      /**
       * Whether the individual has mental health issues
       */
      mentalHealth?: boolean;
      /**
       * Whether the individual is an expectant mother
       */
      expectantMother?: boolean;
      /**
       * Whether the individual has allergies
       */
      allergies?: boolean;
      /**
       * Whether the individual is taking medication
       */
      medication?: boolean;
      /**
       * Whether the individual has any health issues
       */
      healthIssues?: boolean;
      /**
       * A description of the individual's health issues
       *
       * @maxLength 2048
       */
      healthIssuesDescription?: string;
      /**
       * Any disability needs of the individual
       *
       * @maxLength 2048
       */
      disabilityNeeds?: string;
      /**
       * Whether the individual has any contagious diseases
       */
      contagiousDiseases?: boolean;
      /**
       * Whether the individual's health affects their ability
       */
      affectAbility?: boolean;
      /**
       * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
       */
      notificationAgree?: boolean;
      /**
       * Whether the individual has opted into the SAS Wellbeing App
       */
      sasOptIn?: boolean;
      /**
       * The individual's signature
       *
       * @maxLength 256
       */
      signature?: string;
      /**
       * The individual's drawn signature in base64url format
       *
       * @maxLength 4096
       */
      drawnSignature?: string;
      /**
       * The date the individual signed the form
       *
       * @format date-time
       */
      signatureDate?: string | null;
      /**
       * Whether the individual has been vaccinated against measles
       */
      vaccinationMeasles?: boolean;
      /**
       * Whether the individual has been vaccinated against mumps
       */
      vaccinationMumps?: boolean;
      /**
       * Whether the individual has been vaccinated against rubella
       */
      vaccinationRubella?: boolean;
      /**
       * Whether the individual has been vaccinated against varicella
       */
      vaccinationVaricella?: boolean;
      /**
       * Whether the individual has been vaccinated against tuberculosis
       */
      vaccinationTuberculosis?: boolean;
      /**
       * Whether the individual has been vaccinated against hepatitis B
       */
      vaccinationHepatitisB?: boolean;
      /**
       * Whether the individual has been vaccinated against hepatitis C
       */
      vaccinationHepatitisC?: boolean;
      /**
       * Whether the individual has been vaccinated against HIV
       */
      vaccinationHIV?: boolean;
      /**
       * Whether the individual has been vaccinated against COVID-19
       */
      vaccinationCovid?: boolean;
      /**
       * Whether the individual has received the 1st dose of the COVID-19 vaccine
       */
      vaccinationCovid1?: boolean;
      /**
       * Whether the individual has received the 2nd dose of the COVID-19 vaccine
       */
      vaccinationCovid2?: boolean;
      /**
       * Whether the individual has received the COVID-19 booster vaccine
       */
      vaccinationCovidBooster?: boolean;
      /**
       * Whether the individual has received the COVID-19 influenza vaccine
       */
      vaccinationCovidInfluenza?: boolean;
      /**
       * Whether the individual has an override for fit to work requirements
       */
      fitToWorkOverride?: boolean;
    };
    /**
     * The candidate's latest submitted health declaration, including the exact questions presented.
     */
    healthDeclaration?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * The answers recorded for the point-in-time question set.
       *
       * @maxItems 100
       */
      answers?: {
        /**
         * The identifier of the question being answered.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        questionId?: string;
        /**
         * The stable key of the question being answered.
         *
         * @minLength 1
         * @maxLength 128
         */
        questionKey?: string;
        /**
         * The submitted answer to the question.
         */
        value?: string | string | boolean | number | (void | null);
      }[];
      /**
       * When the question set was submitted.
       *
       * @format date-time
       */
      submittedAt?: string;
      /**
       * The network address recorded when the response was submitted.
       *
       * @maxLength 64
       */
      submittedIp?: string;
      /**
       * When the question configuration captured by this response was last updated.
       *
       * @format date-time
       */
      configurationUpdatedAt?: string;
    };
    /**
     * Whether the candidate confirmed their health declaration.
     */
    healthRecordCheckConsent?: boolean;
    /**
     * The name used to confirm the health declaration.
     *
     * @maxLength 256
     */
    healthRecordCheckConsentName?: string;
    /**
     * The date the health declaration was confirmed.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    healthRecordCheckConsentDate?: string;
    /**
     * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
     */
    bankDetails?: {
      /**
       * The name on the bank account
       */
      bankAccountName?: string;
      /**
       * The name of the bank
       */
      bankName?: string;
      /**
       * The account number
       */
      accountNumber?: string;
      /**
       * The sort code
       */
      sortCode?: string;
      /**
       * The National Insurance number
       */
      niNumber?: string;
      /**
       * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
       */
      jobOp1?: boolean;
      /**
       * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
       */
      jobOp2?: boolean;
      /**
       * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
       */
      jobOp3?: boolean;
      studentLoan?: {
        /**
         * Boolean flag for if candidate has student loan
         */
        studentLoan?: boolean;
        /**
         * Boolean flag for if candidate is on plan 1
         */
        plan1?: boolean;
        /**
         * Boolean flag for if candidate is on plan 2
         */
        plan2?: boolean;
        /**
         * Boolean flag for if candidate is on plan 4
         */
        plan4?: boolean;
        /**
         * Boolean flag for if candidate has postgrad loan
         */
        postGradLoan?: boolean;
      };
    };
    /**
     * Whether the candidate has confirmed their details are correct.
     */
    declarationCheckConsent?: boolean;
    /**
     * The name of the person who confirmed their details are correct.
     *
     * @maxLength 256
     */
    declarationCheckConsentName?: string;
    /**
     * The date when consent for the declaration check was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    declarationCheckConsentDate?: string;
    /**
     * Whether the candidate has consented to a contract for service
     */
    contractForServiceConsent?: boolean;
    /**
     * The name of the person who provided consent for the contract for service
     *
     * @maxLength 256
     */
    contractForServiceName?: string;
    /**
     * The date when consent for the contract for service was provided
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    contractForServiceDate?: string;
    /**
     * The candidate has completed their onboarding form.
     */
    confirmed?: boolean;
    /**
     * The ID of the user who created the candidate record.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    createdBy?: string;
    /**
     * The ID of the customer associated with the candidate.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customerId?: string;
    /**
     * The migration that created this candidate
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Whether this migrated candidate has been approved
     */
    migrationApproval?: "pending" | "approved" | "rejected";
    /**
     * The user assigned to the candidate. If expanded, a user object else the user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The ID of the current booking for the candidate.
     */
    currentBooking?: string[];
    /**
     * The URL of the candidate's profile photo.
     *
     * @maxLength 256
     */
    profilePhoto?: string;
    /**
     * The pay type for the candidate.
     */
    payType?: "PAYE" | "Umbrella" | "Limited Company" | "Not Applicable";
    /**
     * The pay interval for the candidate.
     */
    payInterval?:
      | "Monthly"
      | "Weekly"
      | "Weekly Pay in Arrears"
      | "Bi-Weekly"
      | any
      | null;
    /**
     * The pay rate for the candidate.
     */
    payRate?: string;
    /**
     * A custom ID for the candidate.
     *
     * @maxLength 256
     */
    customId?: string;
    /**
     * A company-defined identifier used to match the candidate with external records.
     *
     * @maxLength 256
     */
    _internalId?: string;
    /**
     * The date when the candidate's status was last changed.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    statusChangeDate?: string;
    /**
     * Whether the candidate has opted out of communications.
     */
    optOut?: boolean;
    /**
     * The TRN number for the candidate.
     *
     * @maxLength 256
     */
    trnNumber?: string;
    /**
     * The date when the TRN number was last checked.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    trnNumberCheckedDate?: string;
    /**
     * Any notes related to the TRN number.
     *
     * @maxLength 1024
     */
    trnNumberNotes?: string;
    /**
     * The date when the candidate's employment is set to start.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    employmentStartDate?: string;
    /**
     * Whether the candidate has confirmed their Early Years Workforce status.
     */
    ewcConfirmation?: boolean;
    /**
     * The share code for the candidate's Home Office application.
     *
     * @maxLength 256
     */
    homeOfficeShareCode?: string;
    /**
     * Whether the candidate has been archived.
     */
    archived?: boolean;
    /**
     * Whether the candidate is using an umbrella company.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    umbrella?: string;
    /**
     * ID of the PAYE umbrella company.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    payeUmbrella?: string;
    /**
     * The name of the umbrella company the candidate is using.
     *
     * @maxLength 256
     */
    umbrellaCompanyName?: string;
    /**
     * The code of the umbrella company the candidate is using.
     *
     * @maxLength 256
     */
    umbrellaCompanyCode?: string;
    /**
     * The transport method for the candidate.
     */
    transport?:
      | "Drives Own Vehicle"
      | "Use Public Transport"
      | "Access to Another Vehicle"
      | "Bicycle"
      | "No Access"
      | "Other";
    /**
     * Any important notes related to the candidate.
     *
     * @maxLength 1024
     */
    importantNotes?: string;
    /**
     * The date when the candidate was last updated.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    candidateLastUpdated?: string;
    /**
     * The source from which the candidate was obtained.
     */
    source?:
      | (
          | "Company Website"
          | "iCan Teach UK Website"
          | "Apply to Education"
          | "CV Library - CV Search"
          | "CV Library Advert"
          | "CV Library - Watchdog"
          | "Guardian- CV Search"
          | "Guardian Advert"
          | "Indeed - CV Search"
          | "Indeed Advert"
          | "Reed - CV Search"
          | "Reed Advert"
          | "Reed \u2013 Watchdog"
          | "SENploy - Advert"
          | "Total Jobs"
          | "Other Job Board"
          | "Google"
          | "LinkedIn"
          | "Linkedin Recruiter"
          | "LinkedIn Advert"
          | "Referral - School"
          | "Referral - Candidate"
          | "UK University"
          | "Irish University"
          | "Canadian University"
          | "Marketing Campaign"
          | "Woo"
          | "Job Show"
          | "Recruitment Fair"
          | "Career Fair"
          | "Volcanic"
          | "Broadbean"
          | "Headhunt"
          | "Referral"
          | "Direct"
          | "Other"
        )
      | (
          | "Company Website"
          | "Direct Call"
          | "CV Library Watchdog"
          | "CV Library Advert"
          | "CV Library CV Search"
          | "Reed Watchdog"
          | "Reed Advert"
          | "Reed CV Search"
          | "Indeed Watchdog"
          | "Indeed Advert"
          | "Indeed CV Search"
          | "Job Centre"
          | "Returning Candidate"
          | "Referral Candidate"
          | "Referral Client"
          | "Training Provider"
          | "Recruitment Fair"
          | "University/College"
        );
    /**
     * Any other source from which the candidate was obtained.
     *
     * @maxLength 256
     */
    sourceOther?: string;
    /**
     * Whether the candidate has given consent for online checks.
     */
    onlineCheckConsent?: boolean;
    /**
     * The title of the candidate.
     */
    title?:
      | "Mr"
      | "Mrs"
      | "Ms"
      | "Miss"
      | "Dr"
      | "Mx"
      | "Lady"
      | "Lord"
      | "Other";
    /**
     * The pronoun used by the candidate.
     */
    pronoun?:
      | "She/Her/Hers"
      | "He/Him/His"
      | "They/Them/Theirs"
      | "Ze/Hir/Hirs"
      | "Ze/Zir/Zirs"
      | "Other"
      | "Prefer not to say";
    /**
     * The date when the candidate was last updated.
     *
     * @format date-time
     */
    updatedAt?: string;
    /**
     * The date when the candidate was last contacted.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    lastContactedDate?: string;
    /**
     * A short, prominent note shown with the candidate to communicate important context.
     */
    highlight?: string;
    /**
     * The pay scale for the candidate's teaching position.
     *
     * @maxLength 256
     */
    teacherPayScale?: string;
    /**
     * Whether the candidate has a P45.
     */
    p45?: boolean;
    /**
     * The type of role the candidate is applying for.
     */
    roleType?:
      | "Senior Leadership Positions"
      | "Teaching Roles"
      | "School Support Positions";
    /**
     * The Right to Work verification status for the candidate.
     *
     * @maxLength 256
     */
    rtwVerification?: string;
    /**
     * Any notes related to the Right to Work verification status for the candidate.
     *
     * @maxLength 1024
     */
    rtwVerificationNote?: string;
    /**
     * The Right to Work verification status for the candidate.
     *
     * @maxLength 256
     */
    rtwVerificationStatus?: string;
    /**
     * The location where the candidate desires to work.
     *
     * @maxLength 256
     */
    desiredLocation?: string;
    /**
     * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
     */
    complianceUser?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
     */
    watchers?:
      | string[]
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        }[];
    /**
     * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
     *
     * @maxItems 5
     */
    documents?: {
      /**
       * The unique identifier of the document
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The name of the document
       *
       * @maxLength 512
       */
      name?: string;
      /**
       * The file reference used to retrieve the document
       *
       * @maxLength 2048
       */
      reference?: string;
      /**
       * The file's media type
       *
       * @maxLength 256
       */
      fileType?: string;
      /**
       * The area of the platform the document belongs to
       *
       * @maxLength 256
       */
      type?: string;
      /**
       * The configured category of the document
       *
       * @maxLength 256
       */
      docType?: string;
      /**
       * The document's expiry date, when applicable
       *
       * @maxLength 64
       */
      expiry?: string;
      /**
       * The document's issued date, when applicable
       *
       * @maxLength 64
       */
      issued?: string;
      /**
       * Whether the document has been archived
       */
      documentArchived?: boolean;
      /**
       * The date the document was uploaded
       *
       * @format date-time
       */
      createdAt?: string;
    }[];
    /**
     * The total number of documents attached to the candidate.
     *
     * @minimum 0
     */
    documentsCount?: number;
    /**
     * The locations where the candidate desires to work.
     */
    desiredLocationMultiple?: string[];
    /**
     * Daily availability for the requested period. Returned by the availability view only.
     *
     * @maxItems 31
     */
    availabilityDays?: {
      /**
       * Calendar date in YYYY-MM-DD format.
       *
       * @maxLength 10
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      date?: string;
      /**
       * Availability or booking state for this date.
       */
      status?:
        | "available"
        | "unavailable"
        | "booked"
        | "halfDay"
        | "awaiting"
        | "unknown";
      /**
       * Recorded availability, or null when unrecorded.
       */
      available?: boolean | null;
      /**
       * Morning or afternoon availability.
       */
      partial?: "AM" | "PM" | any | null;
      /**
       * Additional availability information.
       *
       * @maxLength 4096
       */
      note?: string | null;
      /**
       * Active bookings on this date.
       *
       * @maxItems 100
       */
      bookings?: {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @maxLength 512
         */
        clientName?: string;
        /**
         * @maxLength 512
         */
        role?: string;
        /**
         * @maxLength 32
         */
        timeDesignation?: string;
      }[];
    }[];
    /**
     * The date when the candidate's availability was last checked.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    availabilityLastChecked?: string;
    /**
     * Whether the candidate is subject to notice period.
     */
    subjectToNotice?: "Unsure" | "Yes" | "No";
    /**
     * The subsidiary subjects the candidate is qualified to teach.
     */
    subsidiarySubject?: string[];
    /**
     * The candidate's preferred employment type.
     */
    employmentPreference?: (
      | "Day To Day"
      | "Long Term"
      | "Fixed Term"
      | "Perm"
    )[];
    /**
     * Any other names the candidate is known by.
     *
     * @maxLength 256
     */
    otherNames?: string;
    /**
     * The name of the candidate's limited company, if applicable.
     *
     * @maxLength 256
     */
    ltdCompany?: string;
    /**
     * Whether the candidate's employment has been verified.
     */
    employmentVerified?: boolean;
    /**
     * The DBS verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSVerification?: string;
    /**
     * Any notes related to the DBS verification status for the candidate.
     *
     * @maxLength 1024
     */
    candidateDBSVerificationNote?: string;
    /**
     * The DBS verification status for the candidate.
     */
    candidateDBSVerificationStatus?: "Cleared" | "Alerts";
    /**
     * The DBS check verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSCheckVerification?: string;
    /**
     * Any notes related to the DBS check verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSCheckVerificationNote?: string;
    /**
     * The DBS check verification status for the candidate.
     */
    candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
    /**
     * Any notes related to the DBS update service verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateDBSUpdateServiceVerificationNote?: string;
    /**
     * The DBS update service verification status for the candidate.
     */
    candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
    /**
     * Any notes related to the TRN verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateTRNVerificationNote?: string;
    /**
     * The TRN verification status for the candidate.
     */
    candidateTRNVerificationStatus?:
      | "Completed Induction"
      | "Not Yet Completed Induction"
      | "Induction Exempt"
      | "ALERTS - Failed Induction"
      | "Induction Not Required";
    /**
     * The health and safety verification status for the candidate.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    candidateHealthSafetyVerification?: string;
    /**
     * Any notes related to the health and safety verification status for the candidate.
     *
     * @maxLength 150
     */
    candidateHealthSafetyVerificationNote?: string;
    /**
     * The health and safety verification status for the candidate.
     */
    candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
    /**
     * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
     */
    onboardingSession?:
      | string
      | {
          /**
           * Unique identifier for the onboarding session
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The URL for the onboarding session
           *
           * @format uri
           */
          url?: string;
          /**
           * The frozen health declaration configuration for this onboarding session.
           */
          healthDeclarationConfig?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * When this question configuration was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
          };
          /**
           * The frozen Criminal Record & Security Checks configuration for this onboarding session.
           */
          criminalRecordConfig?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * When this question configuration was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
          };
        };
    /**
     * The contact record for this candidate. If expanded, a contact object else the contact record ID.
     */
    contact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
        };
    /**
     * Whether the candidate has opted out of 48 hour communications.
     */
    optOut48Hours?: boolean;
    /**
     * Whether the candidate has consented to REC audit checks.
     */
    recAuditConsent?: boolean | null;
    /**
     * The date the candidate's REC audit consent was recorded.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    recAuditConsentDate?: string;
    /**
     * The method through which REC audit consent was captured.
     *
     * @maxLength 256
     */
    recAuditConsentMethod?: string;
    /**
     * The date when the candidate record was created.
     *
     * @format date-time
     */
    createdAt?: string;
    /**
     * The mobile app link status for the candidate.
     */
    candidateMobileAppStatus?:
      | "Unlinked"
      | "Scheduled Unlink"
      | "Linked"
      | "Invited"
      | "Not Invited";
    /**
     * Evertime details for this candidate
     */
    evertime?: {
      /**
       * The Evertime CandidateId used for this candidate when exporting payment runs
       *
       * @maxLength 256
       */
      id?: string;
      /**
       * Whether the matched Evertime candidate's NI number also matches the one held in Edun
       */
      niNumberMatch?: boolean;
    };
    /**
     * Search relevance relative to the strongest result, where 100 is the strongest match.
     *
     * @minimum 0
     * @maximum 100
     */
    relevanceScore?: number;
    /**
     * Candidate fields or attached document excerpts that explain why the candidate matched.
     *
     * @maxItems 5
     */
    matchReasons?: {
      /**
       * Whether the matching information came from the candidate record or an attached document.
       */
      source?: "candidate" | "document";
      /**
       * The candidate or document information that matched the search.
       *
       * @maxLength 256
       */
      field?: string;
      /**
       * A short excerpt showing why the result matched.
       *
       * @maxLength 1000
       */
      text?: string;
      /**
       * The exact terms within the content that matched the search, including synonym matches.
       *
       * @maxItems 25
       */
      matchedTerms?: string[];
      /**
       * The name of the attached document when the match came from a document.
       *
       * @maxLength 512
       */
      documentName?: string | null;
      /**
       * The unique identifier of the attached document when the match came from a document.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      documentId?: string | null;
    }[];
    /**
     * The candidate's distance in miles from the requested location. Present only when location filtering is used.
     *
     * @minimum 0
     */
    distance?: number;
    /**
     * Timesheet Portal details for this candidate
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal contractor GUID used when exporting payment runs
       *
       * @format uuid
       */
      id?: string;
      /**
       * The Timesheet Portal contractor code
       *
       * @maxLength 256
       */
      code?: string;
    };
    /**
     * Invite to candidate to link their mobile app.
     */
    mobileAppLinkInvite?: {
      /**
       * Boolean status of whether the link invite is being used
       *
       * @default false
       */
      isUsed?: boolean;
      /**
       * Creation date of the invite
       *
       * @format date-time
       * @default 2026-10-30T13:39:09.027Z
       */
      createdAt?: string;
      /**
       * ID of candidate using this link
       *
       * @pattern ^[0-9a-f]{24}$
       */
      usedBy?: string;
      /**
       * Date of when link used
       *
       * @format date-time
       */
      usedAt?: string;
      /**
       * If unlinked, ID of user that unlinked this candidate's link
       *
       * @pattern ^[0-9a-f]{24}$
       */
      unlinkedBy?: string;
      /**
       * If unlinked, date of when this link was unlinked
       *
       * @format date-time
       */
      unlinkedAt?: string;
      /**
       * If deleted, ID of user that deleted this candidate's link
       *
       * @pattern ^[0-9a-f]{24}$
       */
      deletedBy?: string;
      /**
       * If deleted, date of when this link was deleted
       *
       * @format date-time
       */
      deletedAt?: string;
      /**
       * Current status of this mobile app link
       */
      status?:
        | "Sent"
        | "Linked"
        | "Unlinked"
        | "ScheduledUnlink"
        | "Not Sent"
        | "Rescinded";
    };
  };
};

export type GetCandidateVariables = {
  pathParams: GetCandidatePathParams;
  queryParams?: GetCandidateQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetCandidate = (
  variables: GetCandidateVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetCandidateResponse,
    GetCandidateError,
    undefined,
    {},
    GetCandidateQueryParams,
    GetCandidatePathParams
  >({ url: "/candidates/{candidateId}", method: "get", ...variables, signal });

export function getCandidateQuery(variables: GetCandidateVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetCandidateResponse>;
};

export function getCandidateQuery(
  variables: GetCandidateVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetCandidateResponse>)
    | reactQuery.SkipToken;
};

export function getCandidateQuery(
  variables: GetCandidateVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/candidates/{candidateId}",
      operationId: "getCandidate",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetCandidate(variables, signal),
  };
}

export const useSuspenseGetCandidate = <TData = GetCandidateResponse,>(
  variables: GetCandidateVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetCandidateResponse, GetCandidateError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetCandidateResponse,
    GetCandidateError,
    TData
  >({
    ...getCandidateQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetCandidate = <TData = GetCandidateResponse,>(
  variables: GetCandidateVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetCandidateResponse, GetCandidateError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetCandidateResponse, GetCandidateError, TData>({
    ...getCandidateQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type ExportCandidatesError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type ExportCandidatesResponse = {
  /**
   * @maxLength 256
   */
  message: string;
};

export type ExportCandidatesRequestBody = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Find relevant candidates using candidate details and attached document analysis.
   *
   * @minLength 1
   * @maxLength 256
   */
  q?: string;
  /**
   * Search candidates by name (forename or surname)
   *
   * @maxLength 256
   */
  search?: string;
  /**
   * The field to sort by. Prefix with '-' for descending. Availability priority requires an availability period and ranks free time, counting a full available day as one and a half-day as half.
   */
  sort?:
    | (
        | "relevanceScore"
        | "-relevanceScore"
        | "forename"
        | "-forename"
        | "surname"
        | "-surname"
        | "mobile"
        | "-mobile"
        | "email"
        | "-email"
        | "address.city"
        | "-address.city"
        | "address.postcode"
        | "-address.postcode"
        | "address.county"
        | "-address.county"
        | "address.borough"
        | "-address.borough"
        | "address.addressLine1"
        | "-address.addressLine1"
        | "address.addressLine2"
        | "-address.addressLine2"
        | "address.addressLine3"
        | "-address.addressLine3"
        | "subject"
        | "-subject"
        | "statusChangeDate"
        | "-statusChangeDate"
        | "lastContactedDate"
        | "-lastContactedDate"
        | "daysSinceLastContactDisplay"
        | "-daysSinceLastContactDisplay"
        | "status"
        | "-status"
        | "occupation"
        | "-occupation"
        | "dob"
        | "-dob"
        | "trnNumberCheckedDate"
        | "-trnNumberCheckedDate"
        | "criminalRecord.dbsLastChecked"
        | "-criminalRecord.dbsLastChecked"
        | "criminalRecord.updateServiceCheckDate"
        | "-criminalRecord.updateServiceCheckDate"
        | "rtwVerification"
        | "-rtwVerification"
        | "criminalRecord.section128Checked"
        | "-criminalRecord.section128Checked"
        | "criminalRecord.prohibitionCheck"
        | "-criminalRecord.prohibitionCheck"
        | "createdAt"
        | "-createdAt"
        | "updatedAt"
        | "-updatedAt"
        | "_internalId"
        | "-_internalId"
        | "trnNumber"
        | "-trnNumber"
        | "desiredLocation"
        | "-desiredLocation"
        | "telephone"
        | "-telephone"
        | "previousNames"
        | "-previousNames"
        | "otherNames"
        | "-otherNames"
        | "location"
        | "-location"
      )
    | ("availabilityPriority" | "-availabilityPriority");
  /**
   * @minItems 1
   * @maxItems 16
   */
  select: (
    | "id"
    | "forename"
    | "surname"
    | "previousNames"
    | "email"
    | "telephone"
    | "mobile"
    | "occupation"
    | "subject"
    | "address.addressLine1"
    | "address.addressLine2"
    | "address.addressLine3"
    | "address.city"
    | "address.county"
    | "address.borough"
    | "address.postcode"
    | "address.country"
    | "address.lat"
    | "address.lng"
    | "qualifications"
    | "employment"
    | "references"
    | "referenceCheckConsent"
    | "referenceCheckConsentName"
    | "referenceCheckConsentDate"
    | "dob"
    | "sex"
    | "status"
    | "onboardingStatus"
    | "onboardingSent"
    | "nextOfKin.nextOfKinName"
    | "nextOfKin.nextOfKinRelationship"
    | "nextOfKin.nextOfKinTelephone"
    | "nextOfKin.nextOfKinEmail"
    | "nextOfKin.nextOfKinAddress.addressLine1"
    | "nextOfKin.nextOfKinAddress.addressLine2"
    | "nextOfKin.nextOfKinAddress.addressLine3"
    | "nextOfKin.nextOfKinAddress.city"
    | "nextOfKin.nextOfKinAddress.county"
    | "nextOfKin.nextOfKinAddress.borough"
    | "nextOfKin.nextOfKinAddress.postcode"
    | "nextOfKin.nextOfKinAddress.country"
    | "nextOfKin.nextOfKinAddress.lat"
    | "nextOfKin.nextOfKinAddress.lng"
    | "criminalRecord.dbsHeld"
    | "criminalRecord.dbsNumber"
    | "criminalRecord.dbsType"
    | "criminalRecord.dbsLastChecked"
    | "criminalRecord.dbsUpdateServiceNumber"
    | "criminalRecord.prohibitionCheck"
    | "criminalRecord.overseasCheck"
    | "criminalRecord.overseasCheckNote"
    | "criminalRecord.convictedOffence"
    | "criminalRecord.convictedOffenceDescription"
    | "criminalRecord.convictions"
    | "criminalRecord.convictionsDescription"
    | "criminalRecord.registeredToUpdateService"
    | "criminalRecord.consentUpdateServiceChecks"
    | "criminalRecord.note"
    | "criminalRecord.updateServiceCheckDate"
    | "criminalRecord.abroad"
    | "criminalRecord.overseasDeclarationExtended"
    | "criminalRecord.educationalDisciplinaryProceedings"
    | "criminalRecord.dbsComments"
    | "criminalRecord.dbsDeclarationRead"
    | "criminalRecord.overseasOffence"
    | "criminalRecord.overseasOffenceDescription"
    | "criminalRecord.currentCriminalOffence"
    | "criminalRecord.currentCriminalOffenceDescription"
    | "criminalRecord.dbsIssuedByBusiness"
    | "criminalRecord.dbsIssuedByCurrentEmployer"
    | "criminalRecord.section128Required"
    | "criminalRecord.section128Checked"
    | "criminalRecordDeclaration.questions"
    | "criminalRecordDeclaration.answers"
    | "criminalRecordDeclaration.submittedAt"
    | "criminalRecordDeclaration.submittedIp"
    | "criminalRecordDeclaration.configurationUpdatedAt"
    | "criminalRecordCheckConsent"
    | "criminalRecordCheckConsentName"
    | "criminalRecordCheckConsentDate"
    | "safeInEducation.barred"
    | "safeInEducation.barredCheckDate"
    | "safeInEducation.waiver"
    | "safeInEducation.ordersYou"
    | "safeInEducation.ordersChild"
    | "safeInEducation.previousSafeguardingAllegations"
    | "safeInEducation.cautioned"
    | "safeInEducation.violentOffence"
    | "safeInEducation.anotherCountry"
    | "safeInEducation.readKCSIE"
    | "safeInEducation.readChildcareAct"
    | "safeInEducation.prohibitedFromTeaching"
    | "safeInEducation.knownPolice"
    | "safeInEducation.prohibitedFromManagement"
    | "safeInEducation.criminalOffencesLaw"
    | "safeInEducation.safeInEducationDetails"
    | "safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
    | "safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
    | "safeInEducation.safeguardingQuestionaire.signOfAbuse"
    | "safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
    | "safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
    | "safeInEducation.safeguardingQuestionaire.fgmDefinition"
    | "safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
    | "safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
    | "safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
    | "safeInEducation.safeguardingQuestionaire.studentConfides"
    | "socialMedia.facebook"
    | "socialMedia.instagram"
    | "socialMedia.twitter"
    | "socialMedia.linkedIn"
    | "socialMedia.tiktok"
    | "socialMedia.others"
    | "socialMedia.socialMediaChecked"
    | "socialMedia.googleCheckedDate"
    | "safeInEducationCheckConsent"
    | "safeInEducationCheckConsentName"
    | "safeInEducationCheckConsentDate"
    | "healthRecord.physicalAbilityCondition"
    | "healthRecord.confused"
    | "healthRecord.hearing"
    | "healthRecord.eyesight"
    | "healthRecord.depression"
    | "healthRecord.severePain"
    | "healthRecord.excessiveDrowsiness"
    | "healthRecord.epilepsy"
    | "healthRecord.heart"
    | "healthRecord.asthma"
    | "healthRecord.mentalHealth"
    | "healthRecord.expectantMother"
    | "healthRecord.allergies"
    | "healthRecord.medication"
    | "healthRecord.healthIssues"
    | "healthRecord.healthIssuesDescription"
    | "healthRecord.disabilityNeeds"
    | "healthRecord.contagiousDiseases"
    | "healthRecord.affectAbility"
    | "healthRecord.notificationAgree"
    | "healthRecord.sasOptIn"
    | "healthRecord.signature"
    | "healthRecord.drawnSignature"
    | "healthRecord.signatureDate"
    | "healthRecord.vaccinationMeasles"
    | "healthRecord.vaccinationMumps"
    | "healthRecord.vaccinationRubella"
    | "healthRecord.vaccinationVaricella"
    | "healthRecord.vaccinationTuberculosis"
    | "healthRecord.vaccinationHepatitisB"
    | "healthRecord.vaccinationHepatitisC"
    | "healthRecord.vaccinationHIV"
    | "healthRecord.vaccinationCovid"
    | "healthRecord.vaccinationCovid1"
    | "healthRecord.vaccinationCovid2"
    | "healthRecord.vaccinationCovidBooster"
    | "healthRecord.vaccinationCovidInfluenza"
    | "healthRecord.fitToWorkOverride"
    | "healthDeclaration.questions"
    | "healthDeclaration.answers"
    | "healthDeclaration.submittedAt"
    | "healthDeclaration.submittedIp"
    | "healthDeclaration.configurationUpdatedAt"
    | "healthRecordCheckConsent"
    | "healthRecordCheckConsentName"
    | "healthRecordCheckConsentDate"
    | "bankDetails.bankAccountName"
    | "bankDetails.bankName"
    | "bankDetails.accountNumber"
    | "bankDetails.sortCode"
    | "bankDetails.niNumber"
    | "bankDetails.jobOp1"
    | "bankDetails.jobOp2"
    | "bankDetails.jobOp3"
    | "bankDetails.studentLoan.studentLoan"
    | "bankDetails.studentLoan.plan1"
    | "bankDetails.studentLoan.plan2"
    | "bankDetails.studentLoan.plan4"
    | "bankDetails.studentLoan.postGradLoan"
    | "declarationCheckConsent"
    | "declarationCheckConsentName"
    | "declarationCheckConsentDate"
    | "contractForServiceConsent"
    | "contractForServiceName"
    | "contractForServiceDate"
    | "confirmed"
    | "createdBy"
    | "customerId"
    | "migrationId"
    | "migrationApproval"
    | "assignee"
    | "assignee.id"
    | "assignee.email"
    | "assignee.firstName"
    | "assignee.lastName"
    | "assignee.username"
    | "assignee.lea"
    | "assignee.clients"
    | "assignee.position"
    | "assignee.permissions"
    | "assignee.activationCode"
    | "assignee.customer"
    | "assignee.disabled"
    | "assignee.commissionThreshold"
    | "assignee.branches"
    | "assignee.displayOnboarding"
    | "assignee._customId"
    | "assignee.signature"
    | "assignee.phone"
    | "assignee.defaultCandidateWatcher"
    | "assignee.defaultTaskWorkspace"
    | "assignee.teamAdmin"
    | "assignee.contact"
    | "assignee.contact.id"
    | "assignee.contact.forename"
    | "assignee.contact.surname"
    | "assignee.contact.email"
    | "assignee.contact.telephone"
    | "assignee.contact.mobile"
    | "assignee.contact.extension"
    | "assignee.createdAt"
    | "assignee.evertime.id"
    | "assignee.timesheetPortal.id"
    | "assignee.timesheetPortal.code"
    | "currentBooking"
    | "profilePhoto"
    | "payType"
    | "payInterval"
    | "payRate"
    | "customId"
    | "_internalId"
    | "statusChangeDate"
    | "optOut"
    | "trnNumber"
    | "trnNumberCheckedDate"
    | "trnNumberNotes"
    | "employmentStartDate"
    | "ewcConfirmation"
    | "homeOfficeShareCode"
    | "archived"
    | "umbrella"
    | "payeUmbrella"
    | "umbrellaCompanyName"
    | "umbrellaCompanyCode"
    | "transport"
    | "importantNotes"
    | "candidateLastUpdated"
    | "source"
    | "sourceOther"
    | "onlineCheckConsent"
    | "title"
    | "pronoun"
    | "updatedAt"
    | "lastContactedDate"
    | "highlight"
    | "teacherPayScale"
    | "p45"
    | "roleType"
    | "rtwVerification"
    | "rtwVerificationNote"
    | "rtwVerificationStatus"
    | "desiredLocation"
    | "complianceUser"
    | "complianceUser.id"
    | "complianceUser.email"
    | "complianceUser.firstName"
    | "complianceUser.lastName"
    | "complianceUser.username"
    | "complianceUser.lea"
    | "complianceUser.clients"
    | "complianceUser.position"
    | "complianceUser.permissions"
    | "complianceUser.activationCode"
    | "complianceUser.customer"
    | "complianceUser.disabled"
    | "complianceUser.commissionThreshold"
    | "complianceUser.branches"
    | "complianceUser.displayOnboarding"
    | "complianceUser._customId"
    | "complianceUser.signature"
    | "complianceUser.phone"
    | "complianceUser.defaultCandidateWatcher"
    | "complianceUser.defaultTaskWorkspace"
    | "complianceUser.teamAdmin"
    | "complianceUser.contact"
    | "complianceUser.contact.id"
    | "complianceUser.contact.forename"
    | "complianceUser.contact.surname"
    | "complianceUser.contact.email"
    | "complianceUser.contact.telephone"
    | "complianceUser.contact.mobile"
    | "complianceUser.contact.extension"
    | "complianceUser.createdAt"
    | "complianceUser.evertime.id"
    | "complianceUser.timesheetPortal.id"
    | "complianceUser.timesheetPortal.code"
    | "watchers"
    | "tags"
    | "documents"
    | "documentsCount"
    | "desiredLocationMultiple"
    | "availabilityDays"
    | "availabilityLastChecked"
    | "subjectToNotice"
    | "subsidiarySubject"
    | "employmentPreference"
    | "otherNames"
    | "ltdCompany"
    | "employmentVerified"
    | "candidateDBSVerification"
    | "candidateDBSVerificationNote"
    | "candidateDBSVerificationStatus"
    | "candidateDBSCheckVerification"
    | "candidateDBSCheckVerificationNote"
    | "candidateDBSCheckVerificationStatus"
    | "candidateDBSUpdateServiceVerificationNote"
    | "candidateDBSUpdateServiceVerificationStatus"
    | "candidateTRNVerificationNote"
    | "candidateTRNVerificationStatus"
    | "candidateHealthSafetyVerification"
    | "candidateHealthSafetyVerificationNote"
    | "candidateHealthSafetyVerificationStatus"
    | "onboardingSession"
    | "onboardingSession.id"
    | "onboardingSession.url"
    | "onboardingSession.healthDeclarationConfig.questions"
    | "onboardingSession.healthDeclarationConfig.updatedAt"
    | "onboardingSession.criminalRecordConfig.questions"
    | "onboardingSession.criminalRecordConfig.updatedAt"
    | "contact"
    | "contact.id"
    | "contact.forename"
    | "contact.surname"
    | "contact.email"
    | "contact.telephone"
    | "contact.mobile"
    | "contact.extension"
    | "optOut48Hours"
    | "recAuditConsent"
    | "recAuditConsentDate"
    | "recAuditConsentMethod"
    | "createdAt"
    | "candidateMobileAppStatus"
    | "evertime.id"
    | "evertime.niNumberMatch"
    | "relevanceScore"
    | "matchReasons"
    | "distance"
    | "timesheetPortal.id"
    | "timesheetPortal.code"
    | "mobileAppLinkInvite.isUsed"
    | "mobileAppLinkInvite.createdAt"
    | "mobileAppLinkInvite.usedBy"
    | "mobileAppLinkInvite.usedAt"
    | "mobileAppLinkInvite.unlinkedBy"
    | "mobileAppLinkInvite.unlinkedAt"
    | "mobileAppLinkInvite.deletedBy"
    | "mobileAppLinkInvite.deletedAt"
    | "mobileAppLinkInvite.status"
  )[];
  /**
   * Related resources to return as objects instead of IDs. Supported values are documented for this endpoint.
   *
   * @maxItems 100
   */
  expand?: (
    | "assignee"
    | "contact"
    | "customer"
    | "umbrella"
    | "payeUmbrella"
    | "complianceUser"
    | "onboardingSession"
    | "watchers"
    | "tags"
    | "documents"
  )[];
  /**
   * Filter candidates based on various criteria
   */
  filter?: {
    /**
     * Inclusive calendar dates, up to 31 days. Both boundaries are required when selecting availability days, filtering availability statuses or sorting by availability priority.
     */
    availabilityPeriod?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Match candidates with any selected status during the availability period. Requires an availability period. Empty selects all statuses.
     *
     * @maxItems 5
     */
    availabilityStatuses?: (
      | "available"
      | "unavailable"
      | "booked"
      | "awaiting"
      | "notRecorded"
    )[];
    /**
     * Filter candidates by their IDs. Maximum 50.
     *
     * @maxItems 50
     */
    ids?: string[];
    /**
     * Filter candidates by their status
     *
     * @maxItems 25
     */
    status?: (
      | "Pending"
      | "Approved"
      | "Rejected"
      | "Anonymised"
      | "Archived"
      | "Enquired"
      | "Hold"
      | "Future"
      | "DNC"
      | "Pre"
    )[];
    /**
     * Filter candidates by their onboarding status
     *
     * @maxItems 25
     */
    onboardingStatus?: ("Not Started" | "In Progress" | "Completed")[];
    /**
     * Filter candidates by if they have an active onboarding session
     */
    activeOnboardingSession?: boolean;
    /**
     * Filter candidates by if they have been sent onboarding materials
     */
    onboardingSent?: "true" | "false";
    /**
     * Filter candidates by if they require section 128
     */
    section128Required?: "true" | "false";
    /**
     * Filter candidates by confirmed status
     */
    confirmed?: "true" | "false";
    /**
     * Filter candidates by opt out status
     */
    optOut?: "true" | "false";
    /**
     * Filter candidates by opt out 48 hours status
     */
    optOut48Hours?: "true" | "false";
    /**
     * Filter candidates by fit to work override status
     */
    fitToWorkOverride?: "true" | "false";
    /**
     * Filter candidates by whether they have health conditions
     */
    affectAbility?: "true" | "false";
    /**
     * Filter candidates by EWC confirmation status
     */
    ewcConfirmation?: "true" | "false";
    /**
     * Filter candidates by whether they hold a DBS certificate
     */
    dbsHeld?: "true" | "false";
    /**
     * Filter candidates by DBS update service registration
     */
    dbsRegisteredToUpdateService?: "true" | "false";
    /**
     * Filter candidates by their first name
     *
     * @maxLength 500
     */
    firstName?: string;
    /**
     * Filter candidates by their last name
     *
     * @maxLength 500
     */
    lastName?: string;
    /**
     * Filter candidates by their email address
     *
     * @maxLength 500
     */
    email?: string;
    /**
     * Filter candidates by their mobile number
     *
     * @maxLength 500
     */
    mobile?: string;
    /**
     * Address fields used to filter candidates. Multiple supplied fields are combined.
     */
    address?: {
      /**
       * Filter candidates by their city
       *
       * @maxLength 500
       */
      city?: string;
      /**
       * Filter candidates by their postcode
       *
       * @maxLength 20
       */
      postcode?: string;
      /**
       * Filter candidates by their county
       *
       * @maxLength 500
       */
      county?: string;
      /**
       * Filter candidates by their borough
       *
       * @maxLength 500
       */
      borough?: string;
      /**
       * Filter candidates by address line 1
       *
       * @maxLength 500
       */
      addressLine1?: string;
      /**
       * Filter candidates by address line 2
       *
       * @maxLength 500
       */
      addressLine2?: string;
      /**
       * Filter candidates by address line 3
       *
       * @maxLength 500
       */
      addressLine3?: string;
    };
    /**
     * Filter candidates by their occupation
     *
     * @maxItems 25
     */
    occupation?: string[];
    /**
     * Filter candidates by their specialism
     *
     * @maxLength 500
     */
    specialism?: string;
    /**
     * Filter candidates by their company-defined secondary identifier.
     *
     * @maxLength 500
     */
    _internalId?: string;
    /**
     * Filter candidates by their compliance user
     *
     * @maxItems 25
     */
    complianceUser?: string[];
    /**
     * Filter candidates by their assignee
     *
     * @maxItems 25
     */
    assignee?: string[];
    /**
     * Filter candidates by their watcher
     *
     * @maxItems 25
     */
    watcher?: string[];
    /**
     * Filter candidates by their team
     *
     * @maxItems 25
     */
    team?: string[];
    /**
     * Filter candidates by their associated contact ID
     *
     * @pattern ^[0-9a-f]{24}$
     */
    contactId?: string;
    /**
     * Filter candidates by whether they have an active booking
     */
    currentBooking?: "true" | "false";
    /**
     * Filter candidates with an active booking overlapping an absolute or relative date range.
     */
    bookedInRange?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates without an active booking overlapping an absolute or relative date range. Combine with `bookedInRange` to find candidates booked in one period but not another.
     */
    notBookedInRange?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by their tags
     *
     * @maxItems 25
     */
    tags?: string[];
    /**
     * Filter candidates by location from coordinates
     */
    location?: {
      lat?: number | null;
      lng?: number | null;
      radius?: number | null;
    };
    /**
     * Filter candidates by last status changed
     */
    statusChangeDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by last contact
     */
    lastContactedDateFilter?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by employment preference
     *
     * @maxItems 25
     */
    employmentPreference?: (
      | "Day To Day"
      | "Long Term"
      | "Fixed Term"
      | "Perm"
    )[];
    /**
     * Filter candidates by their chosen mode of transport
     *
     * @maxItems 25
     */
    transport?: (
      | "Drives Own Vehicle"
      | "Use Public Transport"
      | "Access to Another Vehicle"
      | "Bicycle"
      | "No Access"
      | "Other"
    )[];
    /**
     * Filter candidates by their TRN number
     *
     * @maxLength 500
     */
    trnNumber?: string;
    /**
     * Filter candidates by their desired location
     *
     * @maxLength 500
     */
    desiredLocation?: string;
    /**
     * Filter candidates by whether they are subject to a notice period
     *
     * @maxItems 25
     */
    subjectToNotice?: ("Unsure" | "Yes" | "No")[];
    /**
     * Filter candidates by title
     *
     * @maxItems 25
     */
    title?: (
      | "Mr"
      | "Mrs"
      | "Ms"
      | "Miss"
      | "Dr"
      | "Mx"
      | "Lady"
      | "Lord"
      | "Other"
    )[];
    /**
     * Filter candidates by sex
     *
     * @maxItems 25
     */
    sex?: ("Male" | "Female" | "Other" | "Unknown")[];
    /**
     * Filter candidates by pronoun
     *
     * @maxItems 25
     */
    pronoun?: (
      | "She/Her/Hers"
      | "He/Him/His"
      | "They/Them/Theirs"
      | "Ze/Hir/Hirs"
      | "Ze/Zir/Zirs"
      | "Other"
      | "Prefer not to say"
    )[];
    /**
     * Filter candidates by role type
     *
     * @maxItems 25
     */
    roleType?: (
      | "Senior Leadership Positions"
      | "Teaching Roles"
      | "School Support Positions"
    )[];
    /**
     * Filter candidates by pay type
     *
     * @maxItems 25
     */
    payType?: ("PAYE" | "Umbrella" | "Limited Company" | "Not Applicable")[];
    /**
     * Filter candidates by payment interval
     *
     * @maxItems 25
     */
    payInterval?: (
      | "Monthly"
      | "Weekly"
      | "Weekly Pay in Arrears"
      | "Bi-Weekly"
    )[];
    /**
     * Filter candidates by DBS check type
     *
     * @maxItems 25
     */
    dbsType?: (
      | "Basic"
      | "Standard"
      | "Enhanced"
      | "Enhanced with Barred List"
      | "Child workforce"
      | "Child and adult workforce"
      | "Adult workforce"
    )[];
    /**
     * Filter candidates by source
     */
    source?: (
      | (
          | "Company Website"
          | "iCan Teach UK Website"
          | "Apply to Education"
          | "CV Library - CV Search"
          | "CV Library Advert"
          | "CV Library - Watchdog"
          | "Guardian- CV Search"
          | "Guardian Advert"
          | "Indeed - CV Search"
          | "Indeed Advert"
          | "Reed - CV Search"
          | "Reed Advert"
          | "Reed \u2013 Watchdog"
          | "SENploy - Advert"
          | "Total Jobs"
          | "Other Job Board"
          | "Google"
          | "LinkedIn"
          | "Linkedin Recruiter"
          | "LinkedIn Advert"
          | "Referral - School"
          | "Referral - Candidate"
          | "UK University"
          | "Irish University"
          | "Canadian University"
          | "Marketing Campaign"
          | "Woo"
          | "Job Show"
          | "Recruitment Fair"
          | "Career Fair"
          | "Volcanic"
          | "Broadbean"
          | "Headhunt"
          | "Referral"
          | "Direct"
          | "Other"
        )
      | (
          | "Company Website"
          | "Direct Call"
          | "CV Library Watchdog"
          | "CV Library Advert"
          | "CV Library CV Search"
          | "Reed Watchdog"
          | "Reed Advert"
          | "Reed CV Search"
          | "Indeed Watchdog"
          | "Indeed Advert"
          | "Indeed CV Search"
          | "Job Centre"
          | "Returning Candidate"
          | "Referral Candidate"
          | "Referral Client"
          | "Training Provider"
          | "Recruitment Fair"
          | "University/College"
        )
    )[];
    /**
     * Filter candidates by their telephone number
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * Filter candidates by their previous names
     *
     * @maxLength 500
     */
    previousNames?: string;
    /**
     * Filter candidates by their other names
     *
     * @maxLength 500
     */
    otherNames?: string;
    /**
     * Filter candidates by date of birth
     */
    dob?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by TRN number checked date
     */
    trnNumberCheckedDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by DBS last checked date
     */
    dbsLastChecked?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by DBS update service check date
     */
    dbsUpdateServiceCheckDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by RTW verification date
     */
    rtwVerificationDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by Section 128 checked date
     */
    section128CheckedDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by prohibition check date
     */
    prohibitionCheckDate?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by created date
     */
    createdAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by last updated date
     */
    updatedAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter candidates by mobile app status
     *
     * @maxItems 25
     */
    candidateMobileAppStatus?: (
      | "Unlinked"
      | "Scheduled Unlink"
      | "Linked"
      | "Invited"
      | "Not Invited"
    )[];
    /**
     * Filter candidates by REC audit consent status
     */
    recAuditConsent?: "true" | "false";
    /**
     * Filter candidates by migration
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Filter candidates by migration approval
     *
     * @maxItems 3
     */
    migrationApproval?: ("pending" | "approved" | "rejected")[];
  };
  /**
   * File format for the candidate export. Selected availability is included as one column per date in the requested period.
   *
   * @default csv
   */
  format?: "csv" | "xlsx" | "pdf";
  /**
   * Optional email address that will receive the exported file.
   *
   * @maxLength 320
   * @format email
   */
  email?: string;
};

export type ExportCandidatesVariables = {
  body: ExportCandidatesRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchExportCandidates = (
  variables: ExportCandidatesVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    ExportCandidatesResponse,
    ExportCandidatesError,
    ExportCandidatesRequestBody,
    {},
    {},
    {}
  >({ url: "/candidates/exports", method: "post", ...variables, signal });

export const useExportCandidates = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      ExportCandidatesResponse,
      ExportCandidatesError,
      ExportCandidatesVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    ExportCandidatesResponse,
    ExportCandidatesError,
    ExportCandidatesVariables
  >({
    mutationFn: (variables: ExportCandidatesVariables) =>
      fetchExportCandidates(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetCandidateDocumentsPathParams = {
  /**
   * The unique identifier for the candidate.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
};

export type GetCandidateDocumentsQueryParams = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * The document fields to include in the response in dot notation format
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "name"
    | "reference"
    | "fileType"
    | "type"
    | "docType"
    | "expiry"
    | "issued"
    | "documentArchived"
    | "createdAt"
    | ""
  )[];
  /**
   * The field used to order documents
   */
  sort?: "createdAt" | "-createdAt" | "name" | "-name";
};

export type GetCandidateDocumentsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetCandidateDocumentsResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the document
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the document
     *
     * @maxLength 512
     */
    name?: string;
    /**
     * The file reference used to retrieve the document
     *
     * @maxLength 2048
     */
    reference?: string;
    /**
     * The file's media type
     *
     * @maxLength 256
     */
    fileType?: string;
    /**
     * The area of the platform the document belongs to
     *
     * @maxLength 256
     */
    type?: string;
    /**
     * The configured category of the document
     *
     * @maxLength 256
     */
    docType?: string;
    /**
     * The document's expiry date, when applicable
     *
     * @maxLength 64
     */
    expiry?: string;
    /**
     * The document's issued date, when applicable
     *
     * @maxLength 64
     */
    issued?: string;
    /**
     * Whether the document has been archived
     */
    documentArchived?: boolean;
    /**
     * The date the document was uploaded
     *
     * @format date-time
     */
    createdAt?: string;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetCandidateDocumentsVariables = {
  pathParams: GetCandidateDocumentsPathParams;
  queryParams?: GetCandidateDocumentsQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetCandidateDocuments = (
  variables: GetCandidateDocumentsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetCandidateDocumentsResponse,
    GetCandidateDocumentsError,
    undefined,
    {},
    GetCandidateDocumentsQueryParams,
    GetCandidateDocumentsPathParams
  >({
    url: "/candidates/{candidateId}/documents",
    method: "get",
    ...variables,
    signal,
  });

export function getCandidateDocumentsQuery(
  variables: GetCandidateDocumentsVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetCandidateDocumentsResponse>;
};

export function getCandidateDocumentsQuery(
  variables: GetCandidateDocumentsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetCandidateDocumentsResponse>)
    | reactQuery.SkipToken;
};

export function getCandidateDocumentsQuery(
  variables: GetCandidateDocumentsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/candidates/{candidateId}/documents",
      operationId: "getCandidateDocuments",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetCandidateDocuments(variables, signal),
  };
}

export const useSuspenseGetCandidateDocuments = <
  TData = GetCandidateDocumentsResponse,
>(
  variables: GetCandidateDocumentsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCandidateDocumentsResponse,
      GetCandidateDocumentsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetCandidateDocumentsResponse,
    GetCandidateDocumentsError,
    TData
  >({
    ...getCandidateDocumentsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetCandidateDocuments = <
  TData = GetCandidateDocumentsResponse,
>(
  variables: GetCandidateDocumentsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCandidateDocumentsResponse,
      GetCandidateDocumentsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetCandidateDocumentsResponse,
    GetCandidateDocumentsError,
    TData
  >({
    ...getCandidateDocumentsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetCandidateDocumentContentPathParams = {
  /**
   * The unique identifier for the candidate.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
  /**
   * The unique identifier for the candidate document.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  documentId: string;
};

export type GetCandidateDocumentContentError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetCandidateDocumentContentVariables = {
  pathParams: GetCandidateDocumentContentPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetCandidateDocumentContent = (
  variables: GetCandidateDocumentContentVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    Blob,
    GetCandidateDocumentContentError,
    undefined,
    {},
    {},
    GetCandidateDocumentContentPathParams
  >({
    url: "/candidates/{candidateId}/documents/{documentId}/content",
    method: "get",
    ...variables,
    signal,
  });

export function getCandidateDocumentContentQuery(
  variables: GetCandidateDocumentContentVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Blob>;
};

export function getCandidateDocumentContentQuery(
  variables: GetCandidateDocumentContentVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: ((options: QueryFnOptions) => Promise<Blob>) | reactQuery.SkipToken;
};

export function getCandidateDocumentContentQuery(
  variables: GetCandidateDocumentContentVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/candidates/{candidateId}/documents/{documentId}/content",
      operationId: "getCandidateDocumentContent",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetCandidateDocumentContent(variables, signal),
  };
}

export const useSuspenseGetCandidateDocumentContent = <TData = Blob,>(
  variables: GetCandidateDocumentContentVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Blob, GetCandidateDocumentContentError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    Blob,
    GetCandidateDocumentContentError,
    TData
  >({
    ...getCandidateDocumentContentQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetCandidateDocumentContent = <TData = Blob,>(
  variables: GetCandidateDocumentContentVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<Blob, GetCandidateDocumentContentError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<Blob, GetCandidateDocumentContentError, TData>({
    ...getCandidateDocumentContentQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type ExportCandidateCvPathParams = {
  /**
   * The unique identifier for the candidate.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
};

export type ExportCandidateCvError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type ExportCandidateCvRequestBody = {
  /**
   * The file format to generate for the candidate CV.
   */
  exportType: "pdf" | "docx";
  /**
   * Candidate sections to include in the generated CV. Exporting candidate information requires the relevant CV export permission.
   */
  fields: (
    | "highlightPlacements"
    | "highlightQualifications"
    | "dbs"
    | "rtwVerification"
    | "trnNumber"
    | "profilePhoto"
    | "forename"
    | "surname"
    | "occupation"
    | "dob"
    | "sex"
    | "subject"
    | "summary"
    | "experience"
    | "employment"
    | "qualifications"
    | "qualificationExpiry"
  )[];
};

export type ExportCandidateCvVariables = {
  body: ExportCandidateCvRequestBody;
  pathParams: ExportCandidateCvPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchExportCandidateCv = (
  variables: ExportCandidateCvVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    ExportCandidateCvError,
    ExportCandidateCvRequestBody,
    {},
    {},
    ExportCandidateCvPathParams
  >({
    url: "/candidates/{candidateId}/cv-exports",
    method: "post",
    ...variables,
    signal,
  });

export const useExportCandidateCv = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      ExportCandidateCvError,
      ExportCandidateCvVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    ExportCandidateCvError,
    ExportCandidateCvVariables
  >({
    mutationFn: (variables: ExportCandidateCvVariables) =>
      fetchExportCandidateCv(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetCandidateHealthDeclarationPathParams = {
  /**
   * The candidate whose health declaration is requested.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
};

export type GetCandidateHealthDeclarationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetCandidateHealthDeclarationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The ordered questions included in the question set.
     *
     * @maxItems 100
     */
    questions?: {
      /**
       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * A stable key used to associate the question with current and historical answers.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label?: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type?:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required?: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled?: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order?: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode?: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules?: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator?: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value?: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value?: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label?: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
      /**
       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
       */
      audience?: "candidate" | "staff";
      /**
       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
       */
      fixedRole?: "fullName" | "signature";
    }[];
    /**
     * The answers recorded for the point-in-time question set.
     *
     * @maxItems 100
     */
    answers?: {
      /**
       * The identifier of the question being answered.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      questionId?: string;
      /**
       * The stable key of the question being answered.
       *
       * @minLength 1
       * @maxLength 128
       */
      questionKey?: string;
      /**
       * The submitted answer to the question.
       */
      value?: string | string | boolean | number | (void | null);
    }[];
    /**
     * When the question set was submitted.
     *
     * @format date-time
     */
    submittedAt?: string;
    /**
     * The network address recorded when the response was submitted.
     *
     * @maxLength 64
     */
    submittedIp?: string;
    /**
     * When the question configuration captured by this response was last updated.
     *
     * @format date-time
     */
    configurationUpdatedAt?: string;
    /**
     * When the question configuration currently used for new forms was last updated.
     *
     * @format date-time
     */
    currentConfigurationUpdatedAt?: string;
    /**
     * Whether the questions currently used for new forms differ from those captured by this response.
     */
    questionsHaveChanged?: boolean;
    /**
     * Whether this response was reconstructed from an existing record that predates stored question snapshots.
     */
    isLegacyFallback?: boolean;
    /**
     * Whether the candidate has completed the health declaration.
     */
    isComplete?: boolean;
    /**
     * Whether answers to the standard health questions require attention. Custom affirmative answers do not automatically trigger this indicator.
     */
    hasRiskAnswers?: boolean;
  };
};

export type GetCandidateHealthDeclarationVariables = {
  pathParams: GetCandidateHealthDeclarationPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetCandidateHealthDeclaration = (
  variables: GetCandidateHealthDeclarationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetCandidateHealthDeclarationResponse,
    GetCandidateHealthDeclarationError,
    undefined,
    {},
    {},
    GetCandidateHealthDeclarationPathParams
  >({
    url: "/candidates/{candidateId}/health-declaration",
    method: "get",
    ...variables,
    signal,
  });

export function getCandidateHealthDeclarationQuery(
  variables: GetCandidateHealthDeclarationVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (
    options: QueryFnOptions,
  ) => Promise<GetCandidateHealthDeclarationResponse>;
};

export function getCandidateHealthDeclarationQuery(
  variables: GetCandidateHealthDeclarationVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((
        options: QueryFnOptions,
      ) => Promise<GetCandidateHealthDeclarationResponse>)
    | reactQuery.SkipToken;
};

export function getCandidateHealthDeclarationQuery(
  variables: GetCandidateHealthDeclarationVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/candidates/{candidateId}/health-declaration",
      operationId: "getCandidateHealthDeclaration",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetCandidateHealthDeclaration(variables, signal),
  };
}

export const useSuspenseGetCandidateHealthDeclaration = <
  TData = GetCandidateHealthDeclarationResponse,
>(
  variables: GetCandidateHealthDeclarationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCandidateHealthDeclarationResponse,
      GetCandidateHealthDeclarationError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetCandidateHealthDeclarationResponse,
    GetCandidateHealthDeclarationError,
    TData
  >({
    ...getCandidateHealthDeclarationQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetCandidateHealthDeclaration = <
  TData = GetCandidateHealthDeclarationResponse,
>(
  variables: GetCandidateHealthDeclarationVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCandidateHealthDeclarationResponse,
      GetCandidateHealthDeclarationError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetCandidateHealthDeclarationResponse,
    GetCandidateHealthDeclarationError,
    TData
  >({
    ...getCandidateHealthDeclarationQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateCandidateHealthDeclarationPathParams = {
  /**
   * The candidate whose health declaration is requested.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
};

export type UpdateCandidateHealthDeclarationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateCandidateHealthDeclarationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The ordered questions included in the question set.
     *
     * @maxItems 100
     */
    questions?: {
      /**
       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * A stable key used to associate the question with current and historical answers.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label?: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type?:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required?: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled?: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order?: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode?: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules?: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator?: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value?: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value?: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label?: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
      /**
       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
       */
      audience?: "candidate" | "staff";
      /**
       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
       */
      fixedRole?: "fullName" | "signature";
    }[];
    /**
     * The answers recorded for the point-in-time question set.
     *
     * @maxItems 100
     */
    answers?: {
      /**
       * The identifier of the question being answered.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      questionId?: string;
      /**
       * The stable key of the question being answered.
       *
       * @minLength 1
       * @maxLength 128
       */
      questionKey?: string;
      /**
       * The submitted answer to the question.
       */
      value?: string | string | boolean | number | (void | null);
    }[];
    /**
     * When the question set was submitted.
     *
     * @format date-time
     */
    submittedAt?: string;
    /**
     * The network address recorded when the response was submitted.
     *
     * @maxLength 64
     */
    submittedIp?: string;
    /**
     * When the question configuration captured by this response was last updated.
     *
     * @format date-time
     */
    configurationUpdatedAt?: string;
    /**
     * When the question configuration currently used for new forms was last updated.
     *
     * @format date-time
     */
    currentConfigurationUpdatedAt?: string;
    /**
     * Whether the questions currently used for new forms differ from those captured by this response.
     */
    questionsHaveChanged?: boolean;
    /**
     * Whether this response was reconstructed from an existing record that predates stored question snapshots.
     */
    isLegacyFallback?: boolean;
    /**
     * Whether the candidate has completed the health declaration.
     */
    isComplete?: boolean;
    /**
     * Whether answers to the standard health questions require attention. Custom affirmative answers do not automatically trigger this indicator.
     */
    hasRiskAnswers?: boolean;
  };
};

export type UpdateCandidateHealthDeclarationRequestBody = {
  /**
   * The answers recorded for the point-in-time question set.
   *
   * @maxItems 100
   */
  answers: {
    /**
     * The identifier of the question being answered.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    questionId: string;
    /**
     * The stable key of the question being answered.
     *
     * @minLength 1
     * @maxLength 128
     */
    questionKey: string;
    /**
     * The submitted answer to the question.
     */
    value: string | string | boolean | number | (void | null);
  }[];
  /**
   * The onboarding session whose frozen declaration configuration was answered.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  onboardingSessionId?: string;
};

export type UpdateCandidateHealthDeclarationVariables = {
  body: UpdateCandidateHealthDeclarationRequestBody;
  pathParams: UpdateCandidateHealthDeclarationPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateCandidateHealthDeclaration = (
  variables: UpdateCandidateHealthDeclarationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateCandidateHealthDeclarationResponse,
    UpdateCandidateHealthDeclarationError,
    UpdateCandidateHealthDeclarationRequestBody,
    {},
    {},
    UpdateCandidateHealthDeclarationPathParams
  >({
    url: "/candidates/{candidateId}/health-declaration",
    method: "put",
    ...variables,
    signal,
  });

export const useUpdateCandidateHealthDeclaration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateCandidateHealthDeclarationResponse,
      UpdateCandidateHealthDeclarationError,
      UpdateCandidateHealthDeclarationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateCandidateHealthDeclarationResponse,
    UpdateCandidateHealthDeclarationError,
    UpdateCandidateHealthDeclarationVariables
  >({
    mutationFn: (variables: UpdateCandidateHealthDeclarationVariables) =>
      fetchUpdateCandidateHealthDeclaration(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type PostCandidateHealthDeclarationExportPathParams = {
  /**
   * The candidate whose health declaration is requested.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
};

export type PostCandidateHealthDeclarationExportError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type PostCandidateHealthDeclarationExportVariables = {
  pathParams: PostCandidateHealthDeclarationExportPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchPostCandidateHealthDeclarationExport = (
  variables: PostCandidateHealthDeclarationExportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    PostCandidateHealthDeclarationExportError,
    undefined,
    {},
    {},
    PostCandidateHealthDeclarationExportPathParams
  >({
    url: "/candidates/{candidateId}/health-declaration/exports",
    method: "post",
    ...variables,
    signal,
  });

export const usePostCandidateHealthDeclarationExport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      PostCandidateHealthDeclarationExportError,
      PostCandidateHealthDeclarationExportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    PostCandidateHealthDeclarationExportError,
    PostCandidateHealthDeclarationExportVariables
  >({
    mutationFn: (variables: PostCandidateHealthDeclarationExportVariables) =>
      fetchPostCandidateHealthDeclarationExport(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type GetCandidateCriminalRecordDeclarationPathParams = {
  /**
   * The candidate whose criminal record declaration is requested.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
};

export type GetCandidateCriminalRecordDeclarationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetCandidateCriminalRecordDeclarationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The ordered questions included in the question set.
     *
     * @maxItems 100
     */
    questions?: {
      /**
       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * A stable key used to associate the question with current and historical answers.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label?: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type?:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required?: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled?: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order?: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode?: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules?: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator?: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value?: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value?: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label?: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
      /**
       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
       */
      audience?: "candidate" | "staff";
      /**
       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
       */
      fixedRole?: "fullName" | "signature";
    }[];
    /**
     * The answers recorded for the point-in-time question set.
     *
     * @maxItems 100
     */
    answers?: {
      /**
       * The identifier of the question being answered.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      questionId?: string;
      /**
       * The stable key of the question being answered.
       *
       * @minLength 1
       * @maxLength 128
       */
      questionKey?: string;
      /**
       * The submitted answer to the question.
       */
      value?: string | string | boolean | number | (void | null);
    }[];
    /**
     * When the question set was submitted.
     *
     * @format date-time
     */
    submittedAt?: string;
    /**
     * The network address recorded when the response was submitted.
     *
     * @maxLength 64
     */
    submittedIp?: string;
    /**
     * When the question configuration captured by this response was last updated.
     *
     * @format date-time
     */
    configurationUpdatedAt?: string;
    /**
     * When the question configuration currently used for new forms was last updated.
     *
     * @format date-time
     */
    currentConfigurationUpdatedAt?: string;
    /**
     * Whether the questions currently used for new forms differ from those captured by this response.
     */
    questionsHaveChanged?: boolean;
    /**
     * Whether this response was reconstructed from an existing record that predates stored question snapshots.
     */
    isLegacyFallback?: boolean;
    /**
     * Whether the candidate has completed the criminal record declaration.
     */
    isComplete?: boolean;
    /**
     * Whether the candidate's declaration contains answers that require attention.
     */
    hasRiskAnswers?: boolean;
  };
};

export type GetCandidateCriminalRecordDeclarationVariables = {
  pathParams: GetCandidateCriminalRecordDeclarationPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetCandidateCriminalRecordDeclaration = (
  variables: GetCandidateCriminalRecordDeclarationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetCandidateCriminalRecordDeclarationResponse,
    GetCandidateCriminalRecordDeclarationError,
    undefined,
    {},
    {},
    GetCandidateCriminalRecordDeclarationPathParams
  >({
    url: "/candidates/{candidateId}/criminal-record-declaration",
    method: "get",
    ...variables,
    signal,
  });

export function getCandidateCriminalRecordDeclarationQuery(
  variables: GetCandidateCriminalRecordDeclarationVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (
    options: QueryFnOptions,
  ) => Promise<GetCandidateCriminalRecordDeclarationResponse>;
};

export function getCandidateCriminalRecordDeclarationQuery(
  variables:
    | GetCandidateCriminalRecordDeclarationVariables
    | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((
        options: QueryFnOptions,
      ) => Promise<GetCandidateCriminalRecordDeclarationResponse>)
    | reactQuery.SkipToken;
};

export function getCandidateCriminalRecordDeclarationQuery(
  variables:
    | GetCandidateCriminalRecordDeclarationVariables
    | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/candidates/{candidateId}/criminal-record-declaration",
      operationId: "getCandidateCriminalRecordDeclaration",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetCandidateCriminalRecordDeclaration(variables, signal),
  };
}

export const useSuspenseGetCandidateCriminalRecordDeclaration = <
  TData = GetCandidateCriminalRecordDeclarationResponse,
>(
  variables: GetCandidateCriminalRecordDeclarationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCandidateCriminalRecordDeclarationResponse,
      GetCandidateCriminalRecordDeclarationError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetCandidateCriminalRecordDeclarationResponse,
    GetCandidateCriminalRecordDeclarationError,
    TData
  >({
    ...getCandidateCriminalRecordDeclarationQuery(
      deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export const useGetCandidateCriminalRecordDeclaration = <
  TData = GetCandidateCriminalRecordDeclarationResponse,
>(
  variables:
    | GetCandidateCriminalRecordDeclarationVariables
    | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCandidateCriminalRecordDeclarationResponse,
      GetCandidateCriminalRecordDeclarationError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetCandidateCriminalRecordDeclarationResponse,
    GetCandidateCriminalRecordDeclarationError,
    TData
  >({
    ...getCandidateCriminalRecordDeclarationQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateCandidateCriminalRecordDeclarationPathParams = {
  /**
   * The candidate whose criminal record declaration is requested.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
};

export type UpdateCandidateCriminalRecordDeclarationError =
  Fetcher.ErrorWrapper<
    | {
        status: 400;
        payload: {
          /**
           * A human-readable summary of the error.
           *
           * @example Bad Request
           */
          message?: string;
          /**
           * The ISO 8601 date and time when the error was produced.
           *
           * @example 2024-01-01T00:00:00.000Z
           */
          timestamp?: string;
        };
      }
    | {
        status: 401;
        payload: {
          /**
           * A human-readable summary of the error.
           *
           * @example Unauthorized
           */
          message?: string;
          /**
           * The ISO 8601 date and time when the error was produced.
           *
           * @example 2024-01-01T00:00:00.000Z
           */
          timestamp?: string;
        };
      }
    | {
        status: 403;
        payload: {
          /**
           * A human-readable summary of the error.
           *
           * @example Forbidden
           */
          message?: string;
          /**
           * The ISO 8601 date and time when the error was produced.
           *
           * @example 2024-01-01T00:00:00.000Z
           */
          timestamp?: string;
        };
      }
    | {
        status: 404;
        payload: {
          /**
           * A human-readable summary of the error.
           *
           * @example Not Found
           */
          message?: string;
          /**
           * The ISO 8601 date and time when the error was produced.
           *
           * @example 2024-01-01T00:00:00.000Z
           */
          timestamp?: string;
        };
      }
    | {
        status: 500;
        payload: {
          /**
           * A human-readable summary of the error.
           *
           * @example Internal Server Error
           */
          message?: string;
          /**
           * The ISO 8601 date and time when the error was produced.
           *
           * @example 2024-01-01T00:00:00.000Z
           */
          timestamp?: string;
        };
      }
  >;

export type UpdateCandidateCriminalRecordDeclarationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The ordered questions included in the question set.
     *
     * @maxItems 100
     */
    questions?: {
      /**
       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * A stable key used to associate the question with current and historical answers.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label?: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type?:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required?: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled?: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order?: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode?: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules?: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator?: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value?: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value?: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label?: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
      /**
       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
       */
      audience?: "candidate" | "staff";
      /**
       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
       */
      fixedRole?: "fullName" | "signature";
    }[];
    /**
     * The answers recorded for the point-in-time question set.
     *
     * @maxItems 100
     */
    answers?: {
      /**
       * The identifier of the question being answered.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      questionId?: string;
      /**
       * The stable key of the question being answered.
       *
       * @minLength 1
       * @maxLength 128
       */
      questionKey?: string;
      /**
       * The submitted answer to the question.
       */
      value?: string | string | boolean | number | (void | null);
    }[];
    /**
     * When the question set was submitted.
     *
     * @format date-time
     */
    submittedAt?: string;
    /**
     * The network address recorded when the response was submitted.
     *
     * @maxLength 64
     */
    submittedIp?: string;
    /**
     * When the question configuration captured by this response was last updated.
     *
     * @format date-time
     */
    configurationUpdatedAt?: string;
    /**
     * When the question configuration currently used for new forms was last updated.
     *
     * @format date-time
     */
    currentConfigurationUpdatedAt?: string;
    /**
     * Whether the questions currently used for new forms differ from those captured by this response.
     */
    questionsHaveChanged?: boolean;
    /**
     * Whether this response was reconstructed from an existing record that predates stored question snapshots.
     */
    isLegacyFallback?: boolean;
    /**
     * Whether the candidate has completed the criminal record declaration.
     */
    isComplete?: boolean;
    /**
     * Whether the candidate's declaration contains answers that require attention.
     */
    hasRiskAnswers?: boolean;
  };
};

export type UpdateCandidateCriminalRecordDeclarationRequestBody = {
  /**
   * The answers recorded for the point-in-time question set.
   *
   * @maxItems 100
   */
  answers: {
    /**
     * The identifier of the question being answered.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    questionId: string;
    /**
     * The stable key of the question being answered.
     *
     * @minLength 1
     * @maxLength 128
     */
    questionKey: string;
    /**
     * The submitted answer to the question.
     */
    value: string | string | boolean | number | (void | null);
  }[];
  /**
   * The onboarding session whose frozen criminal record configuration was answered.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  onboardingSessionId?: string;
};

export type UpdateCandidateCriminalRecordDeclarationVariables = {
  body: UpdateCandidateCriminalRecordDeclarationRequestBody;
  pathParams: UpdateCandidateCriminalRecordDeclarationPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateCandidateCriminalRecordDeclaration = (
  variables: UpdateCandidateCriminalRecordDeclarationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateCandidateCriminalRecordDeclarationResponse,
    UpdateCandidateCriminalRecordDeclarationError,
    UpdateCandidateCriminalRecordDeclarationRequestBody,
    {},
    {},
    UpdateCandidateCriminalRecordDeclarationPathParams
  >({
    url: "/candidates/{candidateId}/criminal-record-declaration",
    method: "put",
    ...variables,
    signal,
  });

export const useUpdateCandidateCriminalRecordDeclaration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateCandidateCriminalRecordDeclarationResponse,
      UpdateCandidateCriminalRecordDeclarationError,
      UpdateCandidateCriminalRecordDeclarationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateCandidateCriminalRecordDeclarationResponse,
    UpdateCandidateCriminalRecordDeclarationError,
    UpdateCandidateCriminalRecordDeclarationVariables
  >({
    mutationFn: (
      variables: UpdateCandidateCriminalRecordDeclarationVariables,
    ) =>
      fetchUpdateCandidateCriminalRecordDeclaration(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type PostCandidateCriminalRecordDeclarationExportPathParams = {
  /**
   * The candidate whose criminal record declaration is requested.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
};

export type PostCandidateCriminalRecordDeclarationExportError =
  Fetcher.ErrorWrapper<
    | {
        status: 400;
        payload: {
          /**
           * A human-readable summary of the error.
           *
           * @example Bad Request
           */
          message?: string;
          /**
           * The ISO 8601 date and time when the error was produced.
           *
           * @example 2024-01-01T00:00:00.000Z
           */
          timestamp?: string;
        };
      }
    | {
        status: 401;
        payload: {
          /**
           * A human-readable summary of the error.
           *
           * @example Unauthorized
           */
          message?: string;
          /**
           * The ISO 8601 date and time when the error was produced.
           *
           * @example 2024-01-01T00:00:00.000Z
           */
          timestamp?: string;
        };
      }
    | {
        status: 403;
        payload: {
          /**
           * A human-readable summary of the error.
           *
           * @example Forbidden
           */
          message?: string;
          /**
           * The ISO 8601 date and time when the error was produced.
           *
           * @example 2024-01-01T00:00:00.000Z
           */
          timestamp?: string;
        };
      }
    | {
        status: 404;
        payload: {
          /**
           * A human-readable summary of the error.
           *
           * @example Not Found
           */
          message?: string;
          /**
           * The ISO 8601 date and time when the error was produced.
           *
           * @example 2024-01-01T00:00:00.000Z
           */
          timestamp?: string;
        };
      }
    | {
        status: 500;
        payload: {
          /**
           * A human-readable summary of the error.
           *
           * @example Internal Server Error
           */
          message?: string;
          /**
           * The ISO 8601 date and time when the error was produced.
           *
           * @example 2024-01-01T00:00:00.000Z
           */
          timestamp?: string;
        };
      }
  >;

export type PostCandidateCriminalRecordDeclarationExportVariables = {
  pathParams: PostCandidateCriminalRecordDeclarationExportPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchPostCandidateCriminalRecordDeclarationExport = (
  variables: PostCandidateCriminalRecordDeclarationExportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    PostCandidateCriminalRecordDeclarationExportError,
    undefined,
    {},
    {},
    PostCandidateCriminalRecordDeclarationExportPathParams
  >({
    url: "/candidates/{candidateId}/criminal-record-declaration/exports",
    method: "post",
    ...variables,
    signal,
  });

export const usePostCandidateCriminalRecordDeclarationExport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      PostCandidateCriminalRecordDeclarationExportError,
      PostCandidateCriminalRecordDeclarationExportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    PostCandidateCriminalRecordDeclarationExportError,
    PostCandidateCriminalRecordDeclarationExportVariables
  >({
    mutationFn: (
      variables: PostCandidateCriminalRecordDeclarationExportVariables,
    ) =>
      fetchPostCandidateCriminalRecordDeclarationExport(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type CallContactError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 503;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Failed to call contact
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CallContactResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    redirectUrl?: string;
  };
};

export type CallContactRequestBody =
  | {
      /**
       * Whether to call the resource's telephone or mobile number.
       */
      contactFieldToUse: "telephone" | "mobile";
      /**
       * The configured calling provider to use. The user must have access to that integration.
       */
      provider: "cloudcall" | "dialpad" | "devyce";
      /**
       * Identifies the resource as a candidate.
       */
      entityType: "candidate";
      /**
       * The unique identifier of the resource
       *
       * @pattern ^[0-9a-f]{24}$
       */
      entityId: string;
    }
  | {
      /**
       * Whether to call the resource's telephone or mobile number.
       */
      contactFieldToUse: "telephone" | "mobile";
      /**
       * The configured calling provider to use. The user must have access to that integration.
       */
      provider: "cloudcall" | "dialpad" | "devyce";
      /**
       * Identifies the resource as a client.
       */
      entityType: "client";
      /**
       * The unique identifier of the resource
       *
       * @pattern ^[0-9a-f]{24}$
       */
      entityId: string;
    }
  | {
      /**
       * Whether to call the resource's telephone or mobile number.
       */
      contactFieldToUse: "telephone" | "mobile";
      /**
       * The configured calling provider to use. The user must have access to that integration.
       */
      provider: "cloudcall" | "dialpad" | "devyce";
      /**
       * Identifies the resource as a client contact.
       */
      entityType: "clientcontact";
      /**
       * The unique identifier of the resource
       *
       * @pattern ^[0-9a-f]{24}$
       */
      entityId: string;
      /**
       * The unique identifier of the resource
       *
       * @pattern ^[0-9a-f]{24}$
       */
      clientId: string;
    };

export type CallContactVariables = {
  body?: CallContactRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCallContact = (
  variables: CallContactVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CallContactResponse,
    CallContactError,
    CallContactRequestBody,
    {},
    {},
    {}
  >({ url: "/contacts/call", method: "post", ...variables, signal });

export const useCallContact = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CallContactResponse,
      CallContactError,
      CallContactVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CallContactResponse,
    CallContactError,
    CallContactVariables
  >({
    mutationFn: (variables: CallContactVariables) =>
      fetchCallContact(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetClientsQueryParams = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Find relevant clients using their details and location.
   *
   * @minLength 1
   * @maxLength 256
   */
  q?: string;
  /**
   * Search clients by name
   *
   * @maxLength 256
   */
  search?: string;
  /**
   * The field to sort results by. Prefix with - for descending order
   */
  sort?:
    | "name"
    | "-name"
    | "createdAt"
    | "-createdAt"
    | "assignee"
    | "-assignee"
    | "telephone"
    | "-telephone"
    | "email"
    | "-email"
    | "address.postcode"
    | "-address.postcode"
    | "address.county"
    | "-address.county"
    | "lea"
    | "-lea"
    | "type"
    | "-type"
    | "multiAcademyTrust"
    | "-multiAcademyTrust"
    | "address.city"
    | "-address.city"
    | "borough"
    | "-borough"
    | "specialism"
    | "-specialism"
    | "internalId"
    | "-internalId"
    | "lastContactedDate"
    | "-lastContactedDate"
    | "distance"
    | "-distance";
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "name"
    | "address.addressLine1"
    | "address.addressLine2"
    | "address.addressLine3"
    | "address.city"
    | "address.county"
    | "address.borough"
    | "address.postcode"
    | "address.country"
    | "address.lat"
    | "address.lng"
    | "address"
    | "telephone"
    | "email"
    | "contacts"
    | "borough"
    | "lea"
    | "type"
    | "multiAcademyTrust"
    | "color"
    | "customId"
    | "internalId"
    | "assignee"
    | "assignee.id"
    | "assignee.email"
    | "assignee.firstName"
    | "assignee.lastName"
    | "assignee.username"
    | "assignee.lea"
    | "assignee.clients"
    | "assignee.position"
    | "assignee.permissions"
    | "assignee.activationCode"
    | "assignee.customer"
    | "assignee.disabled"
    | "assignee.commissionThreshold"
    | "assignee.branches"
    | "assignee.displayOnboarding"
    | "assignee._customId"
    | "assignee.signature"
    | "assignee.phone"
    | "assignee.defaultCandidateWatcher"
    | "assignee.defaultTaskWorkspace"
    | "assignee.teamAdmin"
    | "assignee.contact"
    | "assignee.contact.id"
    | "assignee.contact.forename"
    | "assignee.contact.surname"
    | "assignee.contact.email"
    | "assignee.contact.telephone"
    | "assignee.contact.mobile"
    | "assignee.contact.extension"
    | "assignee.createdAt"
    | "assignee.evertime.id"
    | "assignee.evertime"
    | "assignee.timesheetPortal.id"
    | "assignee.timesheetPortal.code"
    | "assignee.timesheetPortal"
    | "tags"
    | "tobSentAt"
    | "website"
    | "archived"
    | "specialism"
    | "currentBooking"
    | "bookingCount"
    | "importantNotes"
    | "roles"
    | "startTime"
    | "endTime"
    | "additionalInstructions"
    | "additionalInstructionsClient"
    | "highlight"
    | "area"
    | "governance"
    | "mathq"
    | "ofstedRating"
    | "dateOfstedReportChecked"
    | "boarding"
    | "licenceToIssueSponsorship"
    | "agencySpendTotal"
    | "vatExempt"
    | "splitInvoices"
    | "switchBoard"
    | "ccsClient"
    | "ccsClientUrn"
    | "ccsClientOfstedUrn"
    | "ccsClientLotNumber"
    | "buyerReference"
    | "ccsClientInvoiceDate"
    | "keyStage"
    | "contact"
    | "contact.id"
    | "contact.forename"
    | "contact.surname"
    | "contact.email"
    | "contact.telephone"
    | "contact.mobile"
    | "contact.extension"
    | "lastContactedDate"
    | "relevanceScore"
    | "matchReasons"
    | "distance"
    | "migrationId"
    | "migrationApproval"
    | "evertime.id"
    | "evertime.contacts"
    | "evertime.locations"
    | "evertime.nameMatch"
    | "evertime"
    | "timesheetPortal.id"
    | "timesheetPortal.code"
    | "timesheetPortal"
    | ""
  )[];
  /**
   * Related fields to include as objects in the response.
   *
   * @maxItems 25
   */
  expand?: ("assignee" | "tags")[];
  /**
   * Additional fields to include in the response
   */
  include?: "bookings"[];
  /**
   * Filter clients based on various criteria
   */
  filter?: {
    /**
     * Filter clients by their IDs. Maximum 50.
     *
     * @maxItems 50
     */
    ids?: string[];
    /**
     * Filter clients by name.
     *
     * @maxLength 256
     */
    name?: string;
    /**
     * Filter clients by telephone number.
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * Filter clients by email address.
     *
     * @maxLength 256
     */
    email?: string;
    /**
     * Address fields used to filter clients. Multiple supplied fields are combined.
     */
    address?: {
      /**
       * Filter clients by postcode.
       *
       * @maxLength 32
       */
      postcode?: string;
      /**
       * Filter clients by county.
       *
       * @maxLength 128
       */
      county?: string;
      /**
       * Filter clients by city.
       *
       * @maxLength 128
       */
      city?: string;
    };
    /**
     * Filter clients by local education authority.
     *
     * @maxLength 128
     */
    lea?: string;
    /**
     * Filter clients by type.
     *
     * @maxLength 128
     */
    type?: string;
    /**
     * Filter clients by multi academy trust.
     *
     * @maxLength 256
     */
    multiAcademyTrust?: string;
    /**
     * Filter clients by borough.
     *
     * @maxLength 128
     */
    borough?: string;
    /**
     * Filter clients by specialism.
     *
     * @maxItems 25
     */
    specialism?: string[];
    /**
     * Filter clients by their company-defined identifier.
     *
     * @maxLength 256
     */
    internalId?: string;
    /**
     * Filter clients by whether they are a GCA client.
     */
    ccsClient?: "true" | "false";
    /**
     * Filter clients by GCA client URN.
     *
     * @maxLength 256
     */
    ccsClientUrn?: string;
    /**
     * Filter clients by assigned users.
     *
     * @maxItems 25
     */
    assignee?: string[];
    /**
     * Filter clients by last contact date.
     */
    lastContactedDateFilter?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter clients with an active booking overlapping an absolute or relative date range.
     */
    bookedInRange?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter clients without an active booking overlapping an absolute or relative date range.
     */
    notBookedInRange?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter clients by tags they have.
     *
     * @maxItems 25
     */
    tags?: string[];
    /**
     * Find clients with coordinates relative to a location. A positive radius limits results to that distance in miles; omit it or use zero for no distance limit.
     */
    location?: {
      /**
       * @minimum -90
       * @maximum 90
       */
      lat: number | null;
      /**
       * @minimum -180
       * @maximum 180
       */
      lng: number | null;
      /**
       * @minimum 0
       * @maximum 500
       */
      radius?: number | null;
    };
    /**
     * Filter clients by an associated contact.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    ["contacts.contactId"]?: string;
    /**
     * Filter clients by migration.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Filter clients by migration approval.
     *
     * @maxItems 3
     */
    migrationApproval?: ("pending" | "approved" | "rejected")[];
  };
};

export type GetClientsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetClientsResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the client
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the client
     *
     * @maxLength 256
     */
    name?: string;
    /**
     * The address of the client
     */
    address?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The telephone number of the client
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * The email address of the client
     *
     * @format email
     */
    email?: string;
    /**
     * Array of client contacts
     */
    contacts?: {
      /**
       * The unique identifier of the contact
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The forename of the contact
       *
       * @maxLength 128
       */
      forename?: string;
      /**
       * The surname of the contact
       *
       * @maxLength 128
       */
      surname?: string;
      /**
       * The email address of the contact
       *
       * @format email
       */
      email?: string;
      /**
       * The telephone number of the contact
       *
       * @maxLength 32
       */
      telephone?: string;
      /**
       * The mobile number of the contact
       *
       * @maxLength 32
       */
      mobile?: string;
      /**
       * The extension number of the contact
       *
       * @maxLength 8
       */
      extension?: string;
      /**
       * The role of the contact
       */
      role?:
        | "Head Teacher"
        | "Deputy Head"
        | "Cover Manager"
        | "HR Manager"
        | "Finance Manager"
        | "Head of Maths"
        | "Head of Science"
        | "Head of English"
        | "Assistant Headteacher"
        | "Headteacher's PA"
        | "SENCO"
        | "Other"
        | "Business Manager"
        | "Business Administrator"
        | "Invoice Contact"
        | "Academy Recruitment Contact"
        | "Nursery Manager"
        | "CEO"
        | "COO"
        | "Executive Headteacher"
        | "Inclusion Manager"
        | "EYFS Lead"
        | "Admin Team"
        | "Head of Humanities"
        | "Head of History"
        | "Head of Geography"
        | "Head of Business Studies"
        | "Head of PE"
        | "Head of KS1"
        | "Head of KS2"
        | "Head of KS3"
        | "Head of KS4"
        | "Virtual School Headteacher"
        | "Deputy Virtual School Headteacher"
        | "Post-16 Team Leader"
        | "SEN Team"
        | "Inclusion Team"
        | "UASC Team"
        | "Caseworker"
        | "Designated Teacher"
        | "Attendance Officer"
        | "Coordinator"
        | "Children's Commissioner"
        | "Timesheet Contact";
      /**
       * The name of the role if the role is 'Other'
       *
       * @maxLength 128
       */
      roleOther?: string;
      /**
       * The unique identifier of the contact
       *
       * @format uuid
       */
      contactId?: string;
      /**
       * Timesheet Portal details for this contact
       */
      timesheetPortal?: {
        /**
         * The Timesheet Portal approver GUID linked to this client contact
         *
         * @format uuid
         */
        id?: string;
        /**
         * The Timesheet Portal approver code
         *
         * @maxLength 256
         */
        code?: string;
      };
      /**
       * Whether the contact has opted out of communications
       */
      optOut?: boolean;
      /**
       * Any additional notes about the contact
       *
       * @maxLength 175
       */
      note?: string;
      /**
       * Whether the contact is a decision maker
       */
      decisionMaker?: boolean;
      /**
       * The status of the contact
       */
      status?: "Active" | "Passive" | "Inactive";
      /**
       * The Client associated with the contact.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      clientId?: string;
      /**
       * The name of the Client associated with the contact.
       *
       * @maxLength 256
       */
      clientName?: string;
      /**
       * The address of the Client associated with the contact.
       */
      clientAddress?: {
        /**
         * The first line of the address
         *
         * @maxLength 256
         */
        addressLine1?: string;
        /**
         * The second line of the address
         *
         * @maxLength 256
         */
        addressLine2?: string;
        /**
         * The third line of the address
         *
         * @maxLength 256
         */
        addressLine3?: string;
        /**
         * The city of the address
         *
         * @maxLength 256
         */
        city?: string;
        /**
         * The county of the address
         *
         * @maxLength 256
         */
        county?: string;
        /**
         * The borough of the address
         *
         * @maxLength 256
         */
        borough?: string;
        /**
         * The postcode of the address
         *
         * @maxLength 10
         */
        postcode?: string;
        /**
         * The country of the address
         *
         * @maxLength 256
         */
        country?: string;
        /**
         * The latitude of the address
         *
         * @minimum -90
         * @maximum 90
         */
        lat?: number;
        /**
         * The longitude of the address
         *
         * @minimum -180
         * @maximum 180
         */
        lng?: number;
      };
      /**
       * The local education authority of the associated Client.
       *
       * @maxLength 128
       */
      clientLea?: string;
      /**
       * The sector of the associated Client.
       *
       * @maxLength 128
       */
      clientType?: string;
      /**
       * The multi-academy trust of the associated Client.
       *
       * @maxLength 256
       */
      clientMultiAcademyTrust?: string;
      /**
       * The borough of the associated Client.
       *
       * @maxLength 128
       */
      clientBorough?: string;
      /**
       * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
       */
      clientAssignee?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * The specialism of the associated Client.
       *
       * @maxLength 256
       */
      clientSpecialism?: string;
      /**
       * When the associated Client was last updated.
       *
       * @format date-time
       */
      clientUpdatedAt?: string;
      /**
       * The associated Client's distance in miles from the requested location.
       *
       * @minimum 0
       */
      distance?: number;
      /**
       * Search relevance relative to the strongest result, where 100 is the strongest match.
       *
       * @minimum 0
       * @maximum 100
       */
      relevanceScore?: number;
      /**
       * Contact and Client fields that explain why the contact matched.
       *
       * @maxItems 5
       */
      matchReasons?: {
        /**
         * The contact or Client information that matched the search.
         *
         * @maxLength 256
         */
        field?: string;
        /**
         * A short excerpt showing why the contact matched.
         *
         * @maxLength 1000
         */
        text?: string;
        /**
         * The exact terms within the content that matched the search.
         *
         * @maxItems 25
         */
        matchedTerms?: string[];
      }[];
    }[];
    /**
     * The borough of the client
     *
     * @maxLength 128
     */
    borough?: string;
    /**
     * The local education authority of the client
     *
     * @maxLength 128
     */
    lea?: string;
    /**
     * The type of the client
     *
     * @maxLength 128
     */
    type?: string;
    /**
     * The multi academy trust that the client belongs to
     *
     * @maxLength 256
     */
    multiAcademyTrust?: string;
    /**
     * The color (HEX format prefixed with #) associated with the client
     *
     * @pattern ^#[0-9a-fA-F]{6}$
     */
    color?: string;
    /**
     * A custom ID for the client.
     *
     * @maxLength 256
     */
    customId?: string;
    /**
     * A company-defined identifier used to match the client with external records.
     *
     * @maxLength 256
     */
    internalId?: string;
    /**
     * The user assigned to the client. If expanded, a user object else the user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * Tags associated with this client. If expanded, an array of tag objects; otherwise, an array of tag IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * The date when the TOB was sent
     *
     * @format date-time
     */
    tobSentAt?: string;
    /**
     * The website of the client
     *
     * @format uri
     */
    website?: string;
    /**
     * Whether the client is archived
     */
    archived?: boolean;
    /**
     * The specialism of the client
     *
     * @maxLength 256
     */
    specialism?: string;
    /**
     * The current booking IDs associated with the client
     */
    currentBooking?: string[];
    /**
     * The number of active bookings associated with the client, constrained by the requested booking date range when present.
     *
     * @minimum 0
     */
    bookingCount?: number;
    /**
     * Any important notes related to the client
     *
     * @maxLength 1024
     */
    importantNotes?: string;
    /**
     * The booking roles associated with the client
     */
    roles?: {
      /**
       * The occupation of the individual in the booking role
       *
       * @maxLength 256
       */
      occupation?: string;
      /**
       * The pay rate for the booking role
       *
       * @maxLength 256
       */
      payRate?: string;
      /**
       * The charge rate for the booking role
       *
       * @maxLength 256
       */
      chargeRate?: string;
      /**
       * The type of pay for the booking role
       */
      payType?: "hourly" | "daily" | any | null;
      /**
       * The length of the day for the booking role
       */
      dayLength?: "full" | "half" | any | null;
      /**
       * The AM/PM designation for the day length
       */
      dayLengthAmPm?: "AM" | "PM" | any | null;
    }[];
    /**
     * The start time for the client
     */
    startTime?: string;
    /**
     * The end time for the client
     */
    endTime?: string;
    /**
     * Any additional instructions for the client
     *
     * @maxLength 1024
     */
    additionalInstructions?: string;
    /**
     * Any additional instructions specific to the client
     */
    additionalInstructionsClient?: string;
    /**
     * Tag highlight colour (HEX format prefixed with #)
     *
     * @pattern ^#[0-9a-fA-F]{6}$
     */
    highlight?: string;
    /**
     * The area of the client
     */
    area?: string;
    /**
     * The governance type of the client
     */
    governance?:
      | "Academy"
      | "Childrens Centre"
      | "College"
      | "Independant"
      | "LEA"
      | "University";
    /**
     * The MAT HQ details of the client
     */
    mathq?: "Unsure" | "Yes" | "No";
    /**
     * The Ofsted rating of the client
     */
    ofstedRating?:
      | "Outstanding"
      | "Good"
      | "Requires Improvement"
      | "Inadequate"
      | "NA - Independent"
      | "NA - Academy";
    /**
     * The date when the Ofsted report was checked
     *
     * @format date-time
     */
    dateOfstedReportChecked?: string | null;
    /**
     * The boarding details of the client
     */
    boarding?: "Unsure" | "Yes" | "No" | any | null;
    /**
     * The licence to issue sponsorship for the client
     */
    licenceToIssueSponsorship?: "Unsure" | "Yes" | "No" | any | null;
    /**
     * The total agency spend for the client
     */
    agencySpendTotal?: number;
    /**
     * Whether the client is VAT exempt
     */
    vatExempt?: boolean;
    /**
     * Whether to split invoices for the client
     */
    splitInvoices?: boolean;
    /**
     * The switch board details for the client
     */
    switchBoard?: string;
    /**
     * Whether the client is a GCA client
     */
    ccsClient?: boolean;
    /**
     * The GCA client URN
     */
    ccsClientUrn?: string;
    /**
     * The GCA client Ofsted URN
     */
    ccsClientOfstedUrn?: string;
    /**
     * The GCA client lot number
     */
    ccsClientLotNumber?: string;
    /**
     * The buyer reference supplied by a GCA client, when available.
     *
     * @maxLength 255
     */
    buyerReference?: string | null;
    /**
     * The GCA client invoice date
     *
     * @format date-time
     */
    ccsClientInvoiceDate?: string;
    /**
     * The key stage for the client
     */
    keyStage?: string;
    /**
     * The contact record for this client. If expanded, a contact object else the contact record ID.
     */
    contact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
        };
    /**
     * The date when the client was last contacted.
     *
     * @format date-time
     */
    lastContactedDate?: string;
    /**
     * Search relevance relative to the strongest result, where 100 is the strongest match.
     *
     * @minimum 0
     * @maximum 100
     */
    relevanceScore?: number;
    /**
     * Client fields that explain why the client matched.
     *
     * @maxItems 5
     */
    matchReasons?: {
      /**
       * The client information that matched the search.
       *
       * @maxLength 256
       */
      field?: string;
      /**
       * A short excerpt showing why the client matched.
       *
       * @maxLength 1000
       */
      text?: string;
      /**
       * The exact terms within the content that matched the search.
       *
       * @maxItems 25
       */
      matchedTerms?: string[];
    }[];
    /**
     * The Client's distance in miles from the requested location. Present only when location filtering is used.
     *
     * @minimum 0
     */
    distance?: number;
    /**
     * The migration that created this client
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Whether this migrated client has been approved
     */
    migrationApproval?: "pending" | "approved" | "rejected";
    /**
     * Evertime details for this client
     */
    evertime?: {
      /**
       * The Evertime ClientId used for this client when exporting payment runs
       *
       * @maxLength 256
       */
      id?: string;
      /**
       * The Evertime contacts to create from Edun (clientContactId set) or reference (clientContactId unset) when exporting payment runs
       */
      contacts?: {
        /**
         * The Evertime ClientContactId
         *
         * @maxLength 256
         */
        evertimeId?: string;
        /**
         * The Edun client contact this Evertime contact is created from; absent when the Evertime contact is only referenced (never created or overwritten)
         *
         * @maxLength 256
         */
        clientContactId?: string;
      }[];
      /**
       * The Evertime locations to create from the client's address (create set) or reference (create unset) when exporting payment runs
       */
      locations?: {
        /**
         * The Evertime LocationId
         *
         * @maxLength 256
         */
        evertimeId?: string;
        /**
         * True when this location is created from the client's address; false when the Evertime location is only referenced (never created or overwritten)
         */
        create?: boolean;
      }[];
      /**
       * Whether the matched Evertime client's name also matches the one held in Edun
       */
      nameMatch?: boolean;
    };
    /**
     * Timesheet Portal details for this client
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal client GUID used when exporting payment runs
       *
       * @format uuid
       */
      id?: string;
      /**
       * The Timesheet Portal client code
       *
       * @maxLength 256
       */
      code?: string;
    };
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
  /**
   * How the AI search query was interpreted. Present only when AI search is used.
   */
  aiSearch?: {
    /**
     * The remaining Client search text after structured filters were extracted.
     *
     * @maxLength 256
     */
    query: string;
    /**
     * Terms omitted from relevance scoring because they describe Client context.
     *
     * @maxItems 10
     */
    ignoredTerms: string[];
    filters: {
      /**
       * Client location criteria interpreted from the query.
       */
      location?: {
        /**
         * The location interpreted from the search query.
         *
         * @minLength 1
         * @maxLength 256
         */
        name: string;
        /**
         * The maximum distance from the interpreted location.
         */
        distance?: {
          /**
           * The distance used to limit location-based search results.
           *
           * @minimum 0
           * @exclusiveMinimum true
           * @maximum 500
           */
          value: number;
          /**
           * The unit used for the search distance.
           */
          unit: "miles" | "kilometres";
        };
      };
    };
  };
};

export type GetClientsVariables = {
  queryParams?: GetClientsQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetClients = (
  variables: GetClientsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetClientsResponse,
    GetClientsError,
    undefined,
    {},
    GetClientsQueryParams,
    {}
  >({ url: "/clients", method: "get", ...variables, signal });

export function getClientsQuery(variables: GetClientsVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetClientsResponse>;
};

export function getClientsQuery(
  variables: GetClientsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetClientsResponse>)
    | reactQuery.SkipToken;
};

export function getClientsQuery(
  variables: GetClientsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/clients",
      operationId: "getClients",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetClients(variables, signal),
  };
}

export const useSuspenseGetClients = <TData = GetClientsResponse,>(
  variables: GetClientsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetClientsResponse, GetClientsError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetClientsResponse,
    GetClientsError,
    TData
  >({
    ...getClientsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetClients = <TData = GetClientsResponse,>(
  variables: GetClientsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetClientsResponse, GetClientsError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetClientsResponse, GetClientsError, TData>({
    ...getClientsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type BulkUpdateClientsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type BulkUpdateClientsResponse = {
  /**
   * @minimum 0
   */
  matchedCount: number;
  /**
   * @minimum 0
   */
  modifiedCount: number;
  /**
   * @minimum 0
   */
  tagAssignmentsCreated: number;
};

export type BulkUpdateClientsRequestBody = {
  /**
   * The Clients to update. Maximum 500.
   *
   * @minItems 1
   * @maxItems 500
   */
  clientIds: string[];
  /**
   * The active user to assign to every selected Client.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  assigneeId?: string;
  /**
   * Existing tags to add to every selected Client.
   *
   * @maxItems 25
   */
  tagIds?: string[];
  /**
   * Whether every selected Client is marked as a GCA Client.
   */
  ccsClient?: boolean;
};

export type BulkUpdateClientsVariables = {
  body: BulkUpdateClientsRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchBulkUpdateClients = (
  variables: BulkUpdateClientsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    BulkUpdateClientsResponse,
    BulkUpdateClientsError,
    BulkUpdateClientsRequestBody,
    {},
    {},
    {}
  >({ url: "/clients", method: "patch", ...variables, signal });

export const useBulkUpdateClients = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      BulkUpdateClientsResponse,
      BulkUpdateClientsError,
      BulkUpdateClientsVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    BulkUpdateClientsResponse,
    BulkUpdateClientsError,
    BulkUpdateClientsVariables
  >({
    mutationFn: (variables: BulkUpdateClientsVariables) =>
      fetchBulkUpdateClients(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CreateClientError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 501;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not implemented
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateClientResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the client
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the client
     *
     * @maxLength 256
     */
    name?: string;
    /**
     * The address of the client
     */
    address?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The telephone number of the client
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * The email address of the client
     *
     * @format email
     */
    email?: string;
    /**
     * Array of client contacts
     */
    contacts?: {
      /**
       * The unique identifier of the contact
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The forename of the contact
       *
       * @maxLength 128
       */
      forename?: string;
      /**
       * The surname of the contact
       *
       * @maxLength 128
       */
      surname?: string;
      /**
       * The email address of the contact
       *
       * @format email
       */
      email?: string;
      /**
       * The telephone number of the contact
       *
       * @maxLength 32
       */
      telephone?: string;
      /**
       * The mobile number of the contact
       *
       * @maxLength 32
       */
      mobile?: string;
      /**
       * The extension number of the contact
       *
       * @maxLength 8
       */
      extension?: string;
      /**
       * The role of the contact
       */
      role?:
        | "Head Teacher"
        | "Deputy Head"
        | "Cover Manager"
        | "HR Manager"
        | "Finance Manager"
        | "Head of Maths"
        | "Head of Science"
        | "Head of English"
        | "Assistant Headteacher"
        | "Headteacher's PA"
        | "SENCO"
        | "Other"
        | "Business Manager"
        | "Business Administrator"
        | "Invoice Contact"
        | "Academy Recruitment Contact"
        | "Nursery Manager"
        | "CEO"
        | "COO"
        | "Executive Headteacher"
        | "Inclusion Manager"
        | "EYFS Lead"
        | "Admin Team"
        | "Head of Humanities"
        | "Head of History"
        | "Head of Geography"
        | "Head of Business Studies"
        | "Head of PE"
        | "Head of KS1"
        | "Head of KS2"
        | "Head of KS3"
        | "Head of KS4"
        | "Virtual School Headteacher"
        | "Deputy Virtual School Headteacher"
        | "Post-16 Team Leader"
        | "SEN Team"
        | "Inclusion Team"
        | "UASC Team"
        | "Caseworker"
        | "Designated Teacher"
        | "Attendance Officer"
        | "Coordinator"
        | "Children's Commissioner"
        | "Timesheet Contact";
      /**
       * The name of the role if the role is 'Other'
       *
       * @maxLength 128
       */
      roleOther?: string;
      /**
       * The unique identifier of the contact
       *
       * @format uuid
       */
      contactId?: string;
      /**
       * Timesheet Portal details for this contact
       */
      timesheetPortal?: {
        /**
         * The Timesheet Portal approver GUID linked to this client contact
         *
         * @format uuid
         */
        id?: string;
        /**
         * The Timesheet Portal approver code
         *
         * @maxLength 256
         */
        code?: string;
      };
      /**
       * Whether the contact has opted out of communications
       */
      optOut?: boolean;
      /**
       * Any additional notes about the contact
       *
       * @maxLength 175
       */
      note?: string;
      /**
       * Whether the contact is a decision maker
       */
      decisionMaker?: boolean;
      /**
       * The status of the contact
       */
      status?: "Active" | "Passive" | "Inactive";
      /**
       * The Client associated with the contact.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      clientId?: string;
      /**
       * The name of the Client associated with the contact.
       *
       * @maxLength 256
       */
      clientName?: string;
      /**
       * The address of the Client associated with the contact.
       */
      clientAddress?: {
        /**
         * The first line of the address
         *
         * @maxLength 256
         */
        addressLine1?: string;
        /**
         * The second line of the address
         *
         * @maxLength 256
         */
        addressLine2?: string;
        /**
         * The third line of the address
         *
         * @maxLength 256
         */
        addressLine3?: string;
        /**
         * The city of the address
         *
         * @maxLength 256
         */
        city?: string;
        /**
         * The county of the address
         *
         * @maxLength 256
         */
        county?: string;
        /**
         * The borough of the address
         *
         * @maxLength 256
         */
        borough?: string;
        /**
         * The postcode of the address
         *
         * @maxLength 10
         */
        postcode?: string;
        /**
         * The country of the address
         *
         * @maxLength 256
         */
        country?: string;
        /**
         * The latitude of the address
         *
         * @minimum -90
         * @maximum 90
         */
        lat?: number;
        /**
         * The longitude of the address
         *
         * @minimum -180
         * @maximum 180
         */
        lng?: number;
      };
      /**
       * The local education authority of the associated Client.
       *
       * @maxLength 128
       */
      clientLea?: string;
      /**
       * The sector of the associated Client.
       *
       * @maxLength 128
       */
      clientType?: string;
      /**
       * The multi-academy trust of the associated Client.
       *
       * @maxLength 256
       */
      clientMultiAcademyTrust?: string;
      /**
       * The borough of the associated Client.
       *
       * @maxLength 128
       */
      clientBorough?: string;
      /**
       * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
       */
      clientAssignee?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * The specialism of the associated Client.
       *
       * @maxLength 256
       */
      clientSpecialism?: string;
      /**
       * When the associated Client was last updated.
       *
       * @format date-time
       */
      clientUpdatedAt?: string;
      /**
       * The associated Client's distance in miles from the requested location.
       *
       * @minimum 0
       */
      distance?: number;
      /**
       * Search relevance relative to the strongest result, where 100 is the strongest match.
       *
       * @minimum 0
       * @maximum 100
       */
      relevanceScore?: number;
      /**
       * Contact and Client fields that explain why the contact matched.
       *
       * @maxItems 5
       */
      matchReasons?: {
        /**
         * The contact or Client information that matched the search.
         *
         * @maxLength 256
         */
        field?: string;
        /**
         * A short excerpt showing why the contact matched.
         *
         * @maxLength 1000
         */
        text?: string;
        /**
         * The exact terms within the content that matched the search.
         *
         * @maxItems 25
         */
        matchedTerms?: string[];
      }[];
    }[];
    /**
     * The borough of the client
     *
     * @maxLength 128
     */
    borough?: string;
    /**
     * The local education authority of the client
     *
     * @maxLength 128
     */
    lea?: string;
    /**
     * The type of the client
     *
     * @maxLength 128
     */
    type?: string;
    /**
     * The multi academy trust that the client belongs to
     *
     * @maxLength 256
     */
    multiAcademyTrust?: string;
    /**
     * The color (HEX format prefixed with #) associated with the client
     *
     * @pattern ^#[0-9a-fA-F]{6}$
     */
    color?: string;
    /**
     * A custom ID for the client.
     *
     * @maxLength 256
     */
    customId?: string;
    /**
     * A company-defined identifier used to match the client with external records.
     *
     * @maxLength 256
     */
    internalId?: string;
    /**
     * The user assigned to the client. If expanded, a user object else the user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * Tags associated with this client. If expanded, an array of tag objects; otherwise, an array of tag IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * The date when the TOB was sent
     *
     * @format date-time
     */
    tobSentAt?: string;
    /**
     * The website of the client
     *
     * @format uri
     */
    website?: string;
    /**
     * Whether the client is archived
     */
    archived?: boolean;
    /**
     * The specialism of the client
     *
     * @maxLength 256
     */
    specialism?: string;
    /**
     * The current booking IDs associated with the client
     */
    currentBooking?: string[];
    /**
     * The number of active bookings associated with the client, constrained by the requested booking date range when present.
     *
     * @minimum 0
     */
    bookingCount?: number;
    /**
     * Any important notes related to the client
     *
     * @maxLength 1024
     */
    importantNotes?: string;
    /**
     * The booking roles associated with the client
     */
    roles?: {
      /**
       * The occupation of the individual in the booking role
       *
       * @maxLength 256
       */
      occupation?: string;
      /**
       * The pay rate for the booking role
       *
       * @maxLength 256
       */
      payRate?: string;
      /**
       * The charge rate for the booking role
       *
       * @maxLength 256
       */
      chargeRate?: string;
      /**
       * The type of pay for the booking role
       */
      payType?: "hourly" | "daily" | any | null;
      /**
       * The length of the day for the booking role
       */
      dayLength?: "full" | "half" | any | null;
      /**
       * The AM/PM designation for the day length
       */
      dayLengthAmPm?: "AM" | "PM" | any | null;
    }[];
    /**
     * The start time for the client
     */
    startTime?: string;
    /**
     * The end time for the client
     */
    endTime?: string;
    /**
     * Any additional instructions for the client
     *
     * @maxLength 1024
     */
    additionalInstructions?: string;
    /**
     * Any additional instructions specific to the client
     */
    additionalInstructionsClient?: string;
    /**
     * Tag highlight colour (HEX format prefixed with #)
     *
     * @pattern ^#[0-9a-fA-F]{6}$
     */
    highlight?: string;
    /**
     * The area of the client
     */
    area?: string;
    /**
     * The governance type of the client
     */
    governance?:
      | "Academy"
      | "Childrens Centre"
      | "College"
      | "Independant"
      | "LEA"
      | "University";
    /**
     * The MAT HQ details of the client
     */
    mathq?: "Unsure" | "Yes" | "No";
    /**
     * The Ofsted rating of the client
     */
    ofstedRating?:
      | "Outstanding"
      | "Good"
      | "Requires Improvement"
      | "Inadequate"
      | "NA - Independent"
      | "NA - Academy";
    /**
     * The date when the Ofsted report was checked
     *
     * @format date-time
     */
    dateOfstedReportChecked?: string | null;
    /**
     * The boarding details of the client
     */
    boarding?: "Unsure" | "Yes" | "No" | any | null;
    /**
     * The licence to issue sponsorship for the client
     */
    licenceToIssueSponsorship?: "Unsure" | "Yes" | "No" | any | null;
    /**
     * The total agency spend for the client
     */
    agencySpendTotal?: number;
    /**
     * Whether the client is VAT exempt
     */
    vatExempt?: boolean;
    /**
     * Whether to split invoices for the client
     */
    splitInvoices?: boolean;
    /**
     * The switch board details for the client
     */
    switchBoard?: string;
    /**
     * Whether the client is a GCA client
     */
    ccsClient?: boolean;
    /**
     * The GCA client URN
     */
    ccsClientUrn?: string;
    /**
     * The GCA client Ofsted URN
     */
    ccsClientOfstedUrn?: string;
    /**
     * The GCA client lot number
     */
    ccsClientLotNumber?: string;
    /**
     * The buyer reference supplied by a GCA client, when available.
     *
     * @maxLength 255
     */
    buyerReference?: string | null;
    /**
     * The GCA client invoice date
     *
     * @format date-time
     */
    ccsClientInvoiceDate?: string;
    /**
     * The key stage for the client
     */
    keyStage?: string;
    /**
     * The contact record for this client. If expanded, a contact object else the contact record ID.
     */
    contact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
        };
    /**
     * The date when the client was last contacted.
     *
     * @format date-time
     */
    lastContactedDate?: string;
    /**
     * Search relevance relative to the strongest result, where 100 is the strongest match.
     *
     * @minimum 0
     * @maximum 100
     */
    relevanceScore?: number;
    /**
     * Client fields that explain why the client matched.
     *
     * @maxItems 5
     */
    matchReasons?: {
      /**
       * The client information that matched the search.
       *
       * @maxLength 256
       */
      field?: string;
      /**
       * A short excerpt showing why the client matched.
       *
       * @maxLength 1000
       */
      text?: string;
      /**
       * The exact terms within the content that matched the search.
       *
       * @maxItems 25
       */
      matchedTerms?: string[];
    }[];
    /**
     * The Client's distance in miles from the requested location. Present only when location filtering is used.
     *
     * @minimum 0
     */
    distance?: number;
    /**
     * The migration that created this client
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Whether this migrated client has been approved
     */
    migrationApproval?: "pending" | "approved" | "rejected";
    /**
     * Evertime details for this client
     */
    evertime?: {
      /**
       * The Evertime ClientId used for this client when exporting payment runs
       *
       * @maxLength 256
       */
      id?: string;
      /**
       * The Evertime contacts to create from Edun (clientContactId set) or reference (clientContactId unset) when exporting payment runs
       */
      contacts?: {
        /**
         * The Evertime ClientContactId
         *
         * @maxLength 256
         */
        evertimeId?: string;
        /**
         * The Edun client contact this Evertime contact is created from; absent when the Evertime contact is only referenced (never created or overwritten)
         *
         * @maxLength 256
         */
        clientContactId?: string;
      }[];
      /**
       * The Evertime locations to create from the client's address (create set) or reference (create unset) when exporting payment runs
       */
      locations?: {
        /**
         * The Evertime LocationId
         *
         * @maxLength 256
         */
        evertimeId?: string;
        /**
         * True when this location is created from the client's address; false when the Evertime location is only referenced (never created or overwritten)
         */
        create?: boolean;
      }[];
      /**
       * Whether the matched Evertime client's name also matches the one held in Edun
       */
      nameMatch?: boolean;
    };
    /**
     * Timesheet Portal details for this client
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal client GUID used when exporting payment runs
       *
       * @format uuid
       */
      id?: string;
      /**
       * The Timesheet Portal client code
       *
       * @maxLength 256
       */
      code?: string;
    };
  };
};

export type CreateClientRequestBody = {
  /**
   * The name of the client
   *
   * @maxLength 256
   */
  name: string;
  /**
   * The address of the client
   */
  address: {
    /**
     * The first line of the address
     *
     * @maxLength 256
     */
    addressLine1?: string;
    /**
     * The second line of the address
     *
     * @maxLength 256
     */
    addressLine2?: string;
    /**
     * The third line of the address
     *
     * @maxLength 256
     */
    addressLine3?: string;
    /**
     * The city of the address
     *
     * @maxLength 256
     */
    city?: string;
    /**
     * The county of the address
     *
     * @maxLength 256
     */
    county?: string;
    /**
     * The borough of the address
     *
     * @maxLength 256
     */
    borough?: string;
    /**
     * The postcode of the address
     *
     * @maxLength 10
     */
    postcode?: string;
    /**
     * The country of the address
     *
     * @maxLength 256
     */
    country?: string;
    /**
     * The latitude of the address
     *
     * @minimum -90
     * @maximum 90
     */
    lat?: number;
    /**
     * The longitude of the address
     *
     * @minimum -180
     * @maximum 180
     */
    lng?: number;
  };
  /**
   * The telephone number of the client
   *
   * @maxLength 32
   */
  telephone?: string;
  /**
   * The email address of the client
   *
   * @format email
   */
  email?: string;
  /**
   * Array of client contacts
   */
  contacts: {
    /**
     * The unique identifier of the contact
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id: string;
    /**
     * The forename of the contact
     *
     * @maxLength 128
     */
    forename: string;
    /**
     * The surname of the contact
     *
     * @maxLength 128
     */
    surname?: string;
    /**
     * The email address of the contact
     *
     * @format email
     */
    email: string;
    /**
     * The telephone number of the contact
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * The mobile number of the contact
     *
     * @maxLength 32
     */
    mobile?: string;
    /**
     * The extension number of the contact
     *
     * @maxLength 8
     */
    extension?: string;
    /**
     * The role of the contact
     */
    role?:
      | "Head Teacher"
      | "Deputy Head"
      | "Cover Manager"
      | "HR Manager"
      | "Finance Manager"
      | "Head of Maths"
      | "Head of Science"
      | "Head of English"
      | "Assistant Headteacher"
      | "Headteacher's PA"
      | "SENCO"
      | "Other"
      | "Business Manager"
      | "Business Administrator"
      | "Invoice Contact"
      | "Academy Recruitment Contact"
      | "Nursery Manager"
      | "CEO"
      | "COO"
      | "Executive Headteacher"
      | "Inclusion Manager"
      | "EYFS Lead"
      | "Admin Team"
      | "Head of Humanities"
      | "Head of History"
      | "Head of Geography"
      | "Head of Business Studies"
      | "Head of PE"
      | "Head of KS1"
      | "Head of KS2"
      | "Head of KS3"
      | "Head of KS4"
      | "Virtual School Headteacher"
      | "Deputy Virtual School Headteacher"
      | "Post-16 Team Leader"
      | "SEN Team"
      | "Inclusion Team"
      | "UASC Team"
      | "Caseworker"
      | "Designated Teacher"
      | "Attendance Officer"
      | "Coordinator"
      | "Children's Commissioner"
      | "Timesheet Contact";
    /**
     * The name of the role if the role is 'Other'
     *
     * @maxLength 128
     */
    roleOther?: string;
    /**
     * The unique identifier of the contact
     *
     * @format uuid
     */
    contactId?: string;
    /**
     * Timesheet Portal details for this contact
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal approver GUID linked to this client contact
       *
       * @format uuid
       */
      id?: string;
      /**
       * The Timesheet Portal approver code
       *
       * @maxLength 256
       */
      code?: string;
    };
    /**
     * Whether the contact has opted out of communications
     */
    optOut?: boolean;
    /**
     * Any additional notes about the contact
     *
     * @maxLength 175
     */
    note?: string;
    /**
     * Whether the contact is a decision maker
     */
    decisionMaker?: boolean;
    /**
     * The status of the contact
     */
    status: "Active" | "Passive" | "Inactive";
    /**
     * The Client associated with the contact.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientId?: string;
    /**
     * The name of the Client associated with the contact.
     *
     * @maxLength 256
     */
    clientName?: string;
    /**
     * The address of the Client associated with the contact.
     */
    clientAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The local education authority of the associated Client.
     *
     * @maxLength 128
     */
    clientLea?: string;
    /**
     * The sector of the associated Client.
     *
     * @maxLength 128
     */
    clientType?: string;
    /**
     * The multi-academy trust of the associated Client.
     *
     * @maxLength 256
     */
    clientMultiAcademyTrust?: string;
    /**
     * The borough of the associated Client.
     *
     * @maxLength 128
     */
    clientBorough?: string;
    /**
     * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
     */
    clientAssignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold: {
            min: number | string;
            commission: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The specialism of the associated Client.
     *
     * @maxLength 256
     */
    clientSpecialism?: string;
    /**
     * When the associated Client was last updated.
     *
     * @format date-time
     */
    clientUpdatedAt?: string;
    /**
     * The associated Client's distance in miles from the requested location.
     *
     * @minimum 0
     */
    distance?: number;
    /**
     * Search relevance relative to the strongest result, where 100 is the strongest match.
     *
     * @minimum 0
     * @maximum 100
     */
    relevanceScore?: number;
    /**
     * Contact and Client fields that explain why the contact matched.
     *
     * @maxItems 5
     */
    matchReasons?: {
      /**
       * The contact or Client information that matched the search.
       *
       * @maxLength 256
       */
      field: string;
      /**
       * A short excerpt showing why the contact matched.
       *
       * @maxLength 1000
       */
      text: string;
      /**
       * The exact terms within the content that matched the search.
       *
       * @maxItems 25
       */
      matchedTerms?: string[];
    }[];
  }[];
  /**
   * The borough of the client
   *
   * @maxLength 128
   */
  borough?: string;
  /**
   * The local education authority of the client
   *
   * @maxLength 128
   */
  lea?: string;
  /**
   * The type of the client
   *
   * @maxLength 128
   */
  type?: string;
  /**
   * The multi academy trust that the client belongs to
   *
   * @maxLength 256
   */
  multiAcademyTrust?: string;
  /**
   * The color (HEX format prefixed with #) associated with the client
   *
   * @pattern ^#[0-9a-fA-F]{6}$
   */
  color?: string;
  /**
   * A custom ID for the client.
   *
   * @maxLength 256
   */
  customId?: string;
  /**
   * A company-defined identifier used to match the client with external records.
   *
   * @maxLength 256
   */
  internalId?: string;
  /**
   * The user assigned to the client. If expanded, a user object else the user ID.
   */
  assignee?:
    | string
    | {
        /**
         * The unique identifier for the user
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The email address of the user
         *
         * @format email
         */
        email: string;
        /**
         * The first name of the user
         *
         * @maxLength 128
         */
        firstName: string;
        /**
         * The last name of the user
         *
         * @maxLength 128
         */
        lastName: string;
        /**
         * The username of the user
         *
         * @maxLength 128
         */
        username: string;
        /**
         * The local education authority of the user
         *
         * @maxLength 128
         */
        lea?: string;
        /**
         * Array of client IDs associated with the user
         */
        clients: string[];
        /**
         * The position or job title of the user
         *
         * @maxLength 128
         */
        position: string;
        /**
         * Array of permissions assigned to the user
         */
        permissions: number[];
        /**
         * The activation code for the user
         *
         * @maxLength 256
         */
        activationCode?: string;
        /**
         * The unique identifier of the company the user belongs to.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customer: string;
        /**
         * Indicates whether the user account is disabled
         */
        disabled: boolean;
        /**
         * Array of commission thresholds for the user
         */
        commissionThreshold: {
          min: number | string;
          commission: number | string;
          type?: "Fixed" | "Variable";
          weekly?: boolean;
        }[];
        /**
         * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
         */
        branches:
          | string[]
          | {
              /**
               * The unique identifier for the branch
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The name of the branch
               *
               * @maxLength 256
               */
              name: string;
              /**
               * The code of the branch
               *
               * @maxLength 32
               */
              code?: string;
              /**
               * The contact email of the branch
               *
               * @maxLength 256
               * @format email
               */
              contactEmail?: string;
              /**
               * The contact phone number of the branch
               *
               * @maxLength 32
               */
              contactTelephone?: string;
              /**
               * The contact website of the branch
               *
               * @maxLength 512
               */
              contactWebsite?: string;
              /**
               * The accent color for the customer that the branch belongs to
               *
               * @maxLength 7
               */
              customerAccentColor?: string;
              /**
               * The address of the branch
               */
              address?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
              /**
               * Indicates whether the branch is archived
               */
              archived: boolean;
              /**
               * The number of users assigned to the branch. Only returned when explicitly selected.
               *
               * @minimum 0
               */
              userCount?: number;
            }[];
        /**
         * Indicates whether to display the onboarding process for the user
         */
        displayOnboarding: boolean;
        /**
         * A custom identifier for the user
         *
         * @maxLength 256
         */
        _customId?: string;
        /**
         * The signature of the user in base64 format
         *
         * @maxLength 2048
         */
        signature?: string | null;
        /**
         * The phone number of the user
         *
         * @maxLength 32
         */
        phone?: string;
        /**
         * Indicates whether the user is the default candidate watcher
         */
        defaultCandidateWatcher: boolean;
        /**
         * The user's default task workspace
         *
         * @pattern ^[0-9a-f]{24}$
         */
        defaultTaskWorkspace?: string | null;
        /**
         * Indicates whether the user has team admin privileges
         */
        teamAdmin: boolean;
        /**
         * The contact record for this user. If expanded, a contact object else the contact record ID.
         */
        contact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        /**
         * The date and time the user account was created
         */
        createdAt?: string;
        /**
         * Evertime details for this user
         */
        evertime?: {
          /**
           * The Evertime ConsultantId used for this user when exporting payment runs
           *
           * @maxLength 256
           */
          id?: string;
        };
        /**
         * Timesheet Portal details for this user
         */
        timesheetPortal?: {
          /**
           * The Timesheet Portal administrator GUID used when exporting payment runs
           *
           * @format uuid
           */
          id?: string;
          /**
           * The Timesheet Portal administrator code
           *
           * @maxLength 256
           */
          code?: string;
        };
      };
  /**
   * Tags associated with this client. If expanded, an array of tag objects; otherwise, an array of tag IDs.
   */
  tags?:
    | string[]
    | {
        /**
         * The unique identifier of the tag
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The label displayed wherever this tag is applied.
         *
         * @minLength 1
         * @maxLength 128
         */
        name: string;
        /**
         * The tag display colour as a hexadecimal value, for example #2563EB.
         *
         * @maxLength 32
         */
        color?: string | null;
      }[];
  /**
   * The date when the TOB was sent
   *
   * @format date-time
   */
  tobSentAt?: string;
  /**
   * The website of the client
   *
   * @format uri
   */
  website?: string;
  /**
   * Whether the client is archived
   */
  archived?: boolean;
  /**
   * The specialism of the client
   *
   * @maxLength 256
   */
  specialism?: string;
  /**
   * The current booking IDs associated with the client
   */
  currentBooking: string[];
  /**
   * The number of active bookings associated with the client, constrained by the requested booking date range when present.
   *
   * @minimum 0
   */
  bookingCount?: number;
  /**
   * Any important notes related to the client
   *
   * @maxLength 1024
   */
  importantNotes: string;
  /**
   * The booking roles associated with the client
   */
  roles: {
    /**
     * The occupation of the individual in the booking role
     *
     * @maxLength 256
     */
    occupation?: string;
    /**
     * The pay rate for the booking role
     *
     * @maxLength 256
     */
    payRate?: string;
    /**
     * The charge rate for the booking role
     *
     * @maxLength 256
     */
    chargeRate?: string;
    /**
     * The type of pay for the booking role
     */
    payType?: "hourly" | "daily" | any | null;
    /**
     * The length of the day for the booking role
     */
    dayLength?: "full" | "half" | any | null;
    /**
     * The AM/PM designation for the day length
     */
    dayLengthAmPm?: "AM" | "PM" | any | null;
  }[];
  /**
   * The start time for the client
   */
  startTime?: string;
  /**
   * The end time for the client
   */
  endTime?: string;
  /**
   * Any additional instructions for the client
   *
   * @maxLength 1024
   */
  additionalInstructions?: string;
  /**
   * Any additional instructions specific to the client
   */
  additionalInstructionsClient?: string;
  /**
   * Tag highlight colour (HEX format prefixed with #)
   *
   * @pattern ^#[0-9a-fA-F]{6}$
   */
  highlight?: string;
  /**
   * The area of the client
   */
  area?: string;
  /**
   * The governance type of the client
   */
  governance?:
    | "Academy"
    | "Childrens Centre"
    | "College"
    | "Independant"
    | "LEA"
    | "University";
  /**
   * The MAT HQ details of the client
   */
  mathq?: "Unsure" | "Yes" | "No";
  /**
   * The Ofsted rating of the client
   */
  ofstedRating?:
    | "Outstanding"
    | "Good"
    | "Requires Improvement"
    | "Inadequate"
    | "NA - Independent"
    | "NA - Academy";
  /**
   * The date when the Ofsted report was checked
   *
   * @format date-time
   */
  dateOfstedReportChecked?: string | null;
  /**
   * The boarding details of the client
   */
  boarding?: "Unsure" | "Yes" | "No" | any | null;
  /**
   * The licence to issue sponsorship for the client
   */
  licenceToIssueSponsorship?: "Unsure" | "Yes" | "No" | any | null;
  /**
   * The total agency spend for the client
   */
  agencySpendTotal?: number;
  /**
   * Whether the client is VAT exempt
   */
  vatExempt?: boolean;
  /**
   * Whether to split invoices for the client
   */
  splitInvoices?: boolean;
  /**
   * The switch board details for the client
   */
  switchBoard?: string;
  /**
   * Whether the client is a GCA client
   */
  ccsClient?: boolean;
  /**
   * The GCA client URN
   */
  ccsClientUrn?: string;
  /**
   * The GCA client Ofsted URN
   */
  ccsClientOfstedUrn?: string;
  /**
   * The GCA client lot number
   */
  ccsClientLotNumber?: string;
  /**
   * The buyer reference supplied by a GCA client, when available.
   *
   * @maxLength 255
   */
  buyerReference?: string | null;
  /**
   * The GCA client invoice date
   *
   * @format date-time
   */
  ccsClientInvoiceDate?: string;
  /**
   * The key stage for the client
   */
  keyStage?: string;
  /**
   * The contact record for this client. If expanded, a contact object else the contact record ID.
   */
  contact?:
    | string
    | {
        /**
         * The unique identifier of the contact
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The forename of the contact
         *
         * @maxLength 128
         */
        forename: string;
        /**
         * The surname of the contact
         *
         * @maxLength 128
         */
        surname?: string;
        /**
         * The email address of the contact
         *
         * @format email
         */
        email: string;
        /**
         * The telephone number of the contact
         *
         * @maxLength 32
         */
        telephone?: string;
        /**
         * The mobile number of the contact
         *
         * @maxLength 32
         */
        mobile?: string;
        /**
         * The extension number of the contact
         *
         * @maxLength 8
         */
        extension?: string;
      };
  /**
   * The date when the client was last contacted.
   *
   * @format date-time
   */
  lastContactedDate?: string;
  /**
   * Search relevance relative to the strongest result, where 100 is the strongest match.
   *
   * @minimum 0
   * @maximum 100
   */
  relevanceScore?: number;
  /**
   * Client fields that explain why the client matched.
   *
   * @maxItems 5
   */
  matchReasons?: {
    /**
     * The client information that matched the search.
     *
     * @maxLength 256
     */
    field: string;
    /**
     * A short excerpt showing why the client matched.
     *
     * @maxLength 1000
     */
    text: string;
    /**
     * The exact terms within the content that matched the search.
     *
     * @maxItems 25
     */
    matchedTerms?: string[];
  }[];
  /**
   * The Client's distance in miles from the requested location. Present only when location filtering is used.
   *
   * @minimum 0
   */
  distance?: number;
  /**
   * Evertime details for this client
   */
  evertime?: {
    /**
     * The Evertime ClientId used for this client when exporting payment runs
     *
     * @maxLength 256
     */
    id?: string;
    /**
     * The Evertime contacts to create from Edun (clientContactId set) or reference (clientContactId unset) when exporting payment runs
     */
    contacts?: {
      /**
       * The Evertime ClientContactId
       *
       * @maxLength 256
       */
      evertimeId: string;
      /**
       * The Edun client contact this Evertime contact is created from; absent when the Evertime contact is only referenced (never created or overwritten)
       *
       * @maxLength 256
       */
      clientContactId?: string;
    }[];
    /**
     * The Evertime locations to create from the client's address (create set) or reference (create unset) when exporting payment runs
     */
    locations?: {
      /**
       * The Evertime LocationId
       *
       * @maxLength 256
       */
      evertimeId: string;
      /**
       * True when this location is created from the client's address; false when the Evertime location is only referenced (never created or overwritten)
       */
      create: boolean;
    }[];
    /**
     * Whether the matched Evertime client's name also matches the one held in Edun
     */
    nameMatch?: boolean;
  };
  /**
   * Timesheet Portal details for this client
   */
  timesheetPortal?: {
    /**
     * The Timesheet Portal client GUID used when exporting payment runs
     *
     * @format uuid
     */
    id?: string;
    /**
     * The Timesheet Portal client code
     *
     * @maxLength 256
     */
    code?: string;
  };
};

export type CreateClientVariables = {
  body: CreateClientRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateClient = (
  variables: CreateClientVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateClientResponse,
    CreateClientError,
    CreateClientRequestBody,
    {},
    {},
    {}
  >({ url: "/clients", method: "post", ...variables, signal });

export const useCreateClient = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateClientResponse,
      CreateClientError,
      CreateClientVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateClientResponse,
    CreateClientError,
    CreateClientVariables
  >({
    mutationFn: (variables: CreateClientVariables) =>
      fetchCreateClient(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetClientContactsQueryParams = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Find relevant Client contacts using contact and Client details.
   *
   * @minLength 1
   * @maxLength 256
   */
  q?: string;
  /**
   * Search Client contacts by name, email, or Client.
   *
   * @maxLength 256
   */
  search?: string;
  /**
   * The field used to order contacts. Prefix with - for descending order.
   */
  sort?:
    | "forename"
    | "-forename"
    | "surname"
    | "-surname"
    | "email"
    | "-email"
    | "telephone"
    | "-telephone"
    | "role"
    | "-role"
    | "status"
    | "-status"
    | "decisionMaker"
    | "-decisionMaker"
    | "clientName"
    | "-clientName"
    | "clientAddress.city"
    | "-clientAddress.city"
    | "clientAddress.county"
    | "-clientAddress.county"
    | "clientAddress.postcode"
    | "-clientAddress.postcode"
    | "clientLea"
    | "-clientLea"
    | "clientType"
    | "-clientType"
    | "clientMultiAcademyTrust"
    | "-clientMultiAcademyTrust"
    | "clientBorough"
    | "-clientBorough"
    | "clientAssignee"
    | "-clientAssignee"
    | "clientSpecialism"
    | "-clientSpecialism"
    | "clientUpdatedAt"
    | "-clientUpdatedAt"
    | "distance"
    | "-distance"
    | "relevanceScore"
    | "-relevanceScore";
  /**
   * The fields to include in the response in dot notation format.
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "forename"
    | "surname"
    | "email"
    | "telephone"
    | "mobile"
    | "extension"
    | "role"
    | "roleOther"
    | "contactId"
    | "timesheetPortal.id"
    | "timesheetPortal.code"
    | "timesheetPortal"
    | "optOut"
    | "note"
    | "decisionMaker"
    | "status"
    | "clientId"
    | "clientName"
    | "clientAddress.addressLine1"
    | "clientAddress.addressLine2"
    | "clientAddress.addressLine3"
    | "clientAddress.city"
    | "clientAddress.county"
    | "clientAddress.borough"
    | "clientAddress.postcode"
    | "clientAddress.country"
    | "clientAddress.lat"
    | "clientAddress.lng"
    | "clientAddress"
    | "clientLea"
    | "clientType"
    | "clientMultiAcademyTrust"
    | "clientBorough"
    | "clientAssignee"
    | "clientAssignee.id"
    | "clientAssignee.email"
    | "clientAssignee.firstName"
    | "clientAssignee.lastName"
    | "clientAssignee.username"
    | "clientAssignee.lea"
    | "clientAssignee.clients"
    | "clientAssignee.position"
    | "clientAssignee.permissions"
    | "clientAssignee.activationCode"
    | "clientAssignee.customer"
    | "clientAssignee.disabled"
    | "clientAssignee.commissionThreshold"
    | "clientAssignee.branches"
    | "clientAssignee.displayOnboarding"
    | "clientAssignee._customId"
    | "clientAssignee.signature"
    | "clientAssignee.phone"
    | "clientAssignee.defaultCandidateWatcher"
    | "clientAssignee.defaultTaskWorkspace"
    | "clientAssignee.teamAdmin"
    | "clientAssignee.contact"
    | "clientAssignee.contact.id"
    | "clientAssignee.contact.forename"
    | "clientAssignee.contact.surname"
    | "clientAssignee.contact.email"
    | "clientAssignee.contact.telephone"
    | "clientAssignee.contact.mobile"
    | "clientAssignee.contact.extension"
    | "clientAssignee.createdAt"
    | "clientAssignee.evertime.id"
    | "clientAssignee.evertime"
    | "clientAssignee.timesheetPortal.id"
    | "clientAssignee.timesheetPortal.code"
    | "clientAssignee.timesheetPortal"
    | "clientSpecialism"
    | "clientUpdatedAt"
    | "distance"
    | "relevanceScore"
    | "matchReasons"
    | ""
  )[];
  /**
   * Related fields to include as objects in the response.
   *
   * @maxItems 1
   */
  expand?: "clientAssignee"[];
  /**
   * Filter Client contacts using contact and associated Client fields.
   */
  filter?: {
    /**
     * Filter contacts by status.
     *
     * @maxItems 3
     */
    status?: ("Active" | "Passive" | "Inactive")[];
    /**
     * Filter contacts by role.
     *
     * @maxItems 44
     */
    role?: (
      | "Head Teacher"
      | "Deputy Head"
      | "Cover Manager"
      | "HR Manager"
      | "Finance Manager"
      | "Head of Maths"
      | "Head of Science"
      | "Head of English"
      | "Assistant Headteacher"
      | "Headteacher's PA"
      | "SENCO"
      | "Other"
      | "Business Manager"
      | "Business Administrator"
      | "Invoice Contact"
      | "Academy Recruitment Contact"
      | "Nursery Manager"
      | "CEO"
      | "COO"
      | "Executive Headteacher"
      | "Inclusion Manager"
      | "EYFS Lead"
      | "Admin Team"
      | "Head of Humanities"
      | "Head of History"
      | "Head of Geography"
      | "Head of Business Studies"
      | "Head of PE"
      | "Head of KS1"
      | "Head of KS2"
      | "Head of KS3"
      | "Head of KS4"
      | "Virtual School Headteacher"
      | "Deputy Virtual School Headteacher"
      | "Post-16 Team Leader"
      | "SEN Team"
      | "Inclusion Team"
      | "UASC Team"
      | "Caseworker"
      | "Designated Teacher"
      | "Attendance Officer"
      | "Coordinator"
      | "Children's Commissioner"
      | "Timesheet Contact"
    )[];
    /**
     * Filter contacts by forename.
     *
     * @maxLength 128
     */
    forename?: string;
    /**
     * Filter contacts by surname.
     *
     * @maxLength 128
     */
    surname?: string;
    /**
     * Filter contacts by email address.
     *
     * @maxLength 256
     */
    email?: string;
    /**
     * Filter contacts by decision-maker status.
     */
    decisionMaker?: "true" | "false";
    /**
     * Filter contacts by opt-out status.
     */
    optOut?: "true" | "false";
    /**
     * Filter contacts by Client.
     *
     * @maxItems 50
     */
    clientId?: string[];
    /**
     * Filter contacts by Client name.
     *
     * @maxLength 256
     */
    clientName?: string;
    /**
     * Filter contacts by associated Client address fields.
     */
    clientAddress?: {
      /**
       * @maxLength 128
       */
      city?: string;
      /**
       * @maxLength 128
       */
      county?: string;
      /**
       * @maxLength 32
       */
      postcode?: string;
    };
    /**
     * Filter contacts by Client local education authority.
     *
     * @maxLength 128
     */
    clientLea?: string;
    /**
     * Filter contacts by Client sector.
     *
     * @maxLength 128
     */
    clientType?: string;
    /**
     * Filter contacts by Client multi-academy trust.
     *
     * @maxLength 256
     */
    clientMultiAcademyTrust?: string;
    /**
     * Filter contacts by Client borough.
     *
     * @maxLength 128
     */
    clientBorough?: string;
    /**
     * Filter contacts by Client assignee.
     *
     * @maxItems 25
     */
    clientAssignee?: string[];
    /**
     * Filter contacts by Client specialism.
     *
     * @maxItems 25
     */
    clientSpecialism?: string[];
    /**
     * Filter contacts by when their Client was updated.
     */
    clientUpdatedAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter contacts by distance from their Client address.
     */
    location?: {
      /**
       * @minimum -90
       * @maximum 90
       */
      lat: number | null;
      /**
       * @minimum -180
       * @maximum 180
       */
      lng: number | null;
      /**
       * @minimum 0
       * @exclusiveMinimum true
       * @maximum 500
       */
      radius: number;
    };
  };
};

export type GetClientContactsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetClientContactsResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the contact
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The forename of the contact
     *
     * @maxLength 128
     */
    forename?: string;
    /**
     * The surname of the contact
     *
     * @maxLength 128
     */
    surname?: string;
    /**
     * The email address of the contact
     *
     * @format email
     */
    email?: string;
    /**
     * The telephone number of the contact
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * The mobile number of the contact
     *
     * @maxLength 32
     */
    mobile?: string;
    /**
     * The extension number of the contact
     *
     * @maxLength 8
     */
    extension?: string;
    /**
     * The role of the contact
     */
    role?:
      | "Head Teacher"
      | "Deputy Head"
      | "Cover Manager"
      | "HR Manager"
      | "Finance Manager"
      | "Head of Maths"
      | "Head of Science"
      | "Head of English"
      | "Assistant Headteacher"
      | "Headteacher's PA"
      | "SENCO"
      | "Other"
      | "Business Manager"
      | "Business Administrator"
      | "Invoice Contact"
      | "Academy Recruitment Contact"
      | "Nursery Manager"
      | "CEO"
      | "COO"
      | "Executive Headteacher"
      | "Inclusion Manager"
      | "EYFS Lead"
      | "Admin Team"
      | "Head of Humanities"
      | "Head of History"
      | "Head of Geography"
      | "Head of Business Studies"
      | "Head of PE"
      | "Head of KS1"
      | "Head of KS2"
      | "Head of KS3"
      | "Head of KS4"
      | "Virtual School Headteacher"
      | "Deputy Virtual School Headteacher"
      | "Post-16 Team Leader"
      | "SEN Team"
      | "Inclusion Team"
      | "UASC Team"
      | "Caseworker"
      | "Designated Teacher"
      | "Attendance Officer"
      | "Coordinator"
      | "Children's Commissioner"
      | "Timesheet Contact";
    /**
     * The name of the role if the role is 'Other'
     *
     * @maxLength 128
     */
    roleOther?: string;
    /**
     * The unique identifier of the contact
     *
     * @format uuid
     */
    contactId?: string;
    /**
     * Timesheet Portal details for this contact
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal approver GUID linked to this client contact
       *
       * @format uuid
       */
      id?: string;
      /**
       * The Timesheet Portal approver code
       *
       * @maxLength 256
       */
      code?: string;
    };
    /**
     * Whether the contact has opted out of communications
     */
    optOut?: boolean;
    /**
     * Any additional notes about the contact
     *
     * @maxLength 175
     */
    note?: string;
    /**
     * Whether the contact is a decision maker
     */
    decisionMaker?: boolean;
    /**
     * The status of the contact
     */
    status?: "Active" | "Passive" | "Inactive";
    /**
     * The Client associated with the contact.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientId?: string;
    /**
     * The name of the Client associated with the contact.
     *
     * @maxLength 256
     */
    clientName?: string;
    /**
     * The address of the Client associated with the contact.
     */
    clientAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The local education authority of the associated Client.
     *
     * @maxLength 128
     */
    clientLea?: string;
    /**
     * The sector of the associated Client.
     *
     * @maxLength 128
     */
    clientType?: string;
    /**
     * The multi-academy trust of the associated Client.
     *
     * @maxLength 256
     */
    clientMultiAcademyTrust?: string;
    /**
     * The borough of the associated Client.
     *
     * @maxLength 128
     */
    clientBorough?: string;
    /**
     * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
     */
    clientAssignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The specialism of the associated Client.
     *
     * @maxLength 256
     */
    clientSpecialism?: string;
    /**
     * When the associated Client was last updated.
     *
     * @format date-time
     */
    clientUpdatedAt?: string;
    /**
     * The associated Client's distance in miles from the requested location.
     *
     * @minimum 0
     */
    distance?: number;
    /**
     * Search relevance relative to the strongest result, where 100 is the strongest match.
     *
     * @minimum 0
     * @maximum 100
     */
    relevanceScore?: number;
    /**
     * Contact and Client fields that explain why the contact matched.
     *
     * @maxItems 5
     */
    matchReasons?: {
      /**
       * The contact or Client information that matched the search.
       *
       * @maxLength 256
       */
      field?: string;
      /**
       * A short excerpt showing why the contact matched.
       *
       * @maxLength 1000
       */
      text?: string;
      /**
       * The exact terms within the content that matched the search.
       *
       * @maxItems 25
       */
      matchedTerms?: string[];
    }[];
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
  /**
   * How the AI search query was interpreted. Present only when AI search is used.
   */
  aiSearch?: {
    /**
     * The remaining Client Contact search text after structured filters were extracted.
     *
     * @maxLength 256
     */
    query: string;
    /**
     * Terms omitted from relevance scoring because they describe Client Contact context.
     *
     * @maxItems 10
     */
    ignoredTerms: string[];
    filters: {
      location?: {
        /**
         * @minLength 1
         * @maxLength 256
         */
        name: string;
        distance?: {
          /**
           * @minimum 0
           * @exclusiveMinimum true
           * @maximum 500
           */
          value: number;
          unit: "miles" | "kilometres";
        };
      };
    };
  };
};

export type GetClientContactsVariables = {
  queryParams?: GetClientContactsQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetClientContacts = (
  variables: GetClientContactsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetClientContactsResponse,
    GetClientContactsError,
    undefined,
    {},
    GetClientContactsQueryParams,
    {}
  >({ url: "/clients/contacts", method: "get", ...variables, signal });

export function getClientContactsQuery(variables: GetClientContactsVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetClientContactsResponse>;
};

export function getClientContactsQuery(
  variables: GetClientContactsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetClientContactsResponse>)
    | reactQuery.SkipToken;
};

export function getClientContactsQuery(
  variables: GetClientContactsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/clients/contacts",
      operationId: "getClientContacts",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetClientContacts(variables, signal),
  };
}

export const useSuspenseGetClientContacts = <
  TData = GetClientContactsResponse,
>(
  variables: GetClientContactsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetClientContactsResponse,
      GetClientContactsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetClientContactsResponse,
    GetClientContactsError,
    TData
  >({
    ...getClientContactsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetClientContacts = <TData = GetClientContactsResponse,>(
  variables: GetClientContactsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetClientContactsResponse,
      GetClientContactsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetClientContactsResponse,
    GetClientContactsError,
    TData
  >({
    ...getClientContactsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type ExportClientContactsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type ExportClientContactsRequestBody = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Find relevant Client contacts using contact and Client details.
   *
   * @minLength 1
   * @maxLength 256
   */
  q?: string;
  /**
   * Search Client contacts by name, email, or Client.
   *
   * @maxLength 256
   */
  search?: string;
  /**
   * The field used to order contacts. Prefix with - for descending order.
   */
  sort?:
    | "forename"
    | "-forename"
    | "surname"
    | "-surname"
    | "email"
    | "-email"
    | "telephone"
    | "-telephone"
    | "role"
    | "-role"
    | "status"
    | "-status"
    | "decisionMaker"
    | "-decisionMaker"
    | "clientName"
    | "-clientName"
    | "clientAddress.city"
    | "-clientAddress.city"
    | "clientAddress.county"
    | "-clientAddress.county"
    | "clientAddress.postcode"
    | "-clientAddress.postcode"
    | "clientLea"
    | "-clientLea"
    | "clientType"
    | "-clientType"
    | "clientMultiAcademyTrust"
    | "-clientMultiAcademyTrust"
    | "clientBorough"
    | "-clientBorough"
    | "clientAssignee"
    | "-clientAssignee"
    | "clientSpecialism"
    | "-clientSpecialism"
    | "clientUpdatedAt"
    | "-clientUpdatedAt"
    | "distance"
    | "-distance"
    | "relevanceScore"
    | "-relevanceScore";
  /**
   * Related fields to include as objects in the response.
   *
   * @maxItems 1
   */
  expand?: "clientAssignee"[];
  /**
   * Filter Client contacts using contact and associated Client fields.
   */
  filter?: {
    /**
     * Filter contacts by status.
     *
     * @maxItems 3
     */
    status?: ("Active" | "Passive" | "Inactive")[];
    /**
     * Filter contacts by role.
     *
     * @maxItems 44
     */
    role?: (
      | "Head Teacher"
      | "Deputy Head"
      | "Cover Manager"
      | "HR Manager"
      | "Finance Manager"
      | "Head of Maths"
      | "Head of Science"
      | "Head of English"
      | "Assistant Headteacher"
      | "Headteacher's PA"
      | "SENCO"
      | "Other"
      | "Business Manager"
      | "Business Administrator"
      | "Invoice Contact"
      | "Academy Recruitment Contact"
      | "Nursery Manager"
      | "CEO"
      | "COO"
      | "Executive Headteacher"
      | "Inclusion Manager"
      | "EYFS Lead"
      | "Admin Team"
      | "Head of Humanities"
      | "Head of History"
      | "Head of Geography"
      | "Head of Business Studies"
      | "Head of PE"
      | "Head of KS1"
      | "Head of KS2"
      | "Head of KS3"
      | "Head of KS4"
      | "Virtual School Headteacher"
      | "Deputy Virtual School Headteacher"
      | "Post-16 Team Leader"
      | "SEN Team"
      | "Inclusion Team"
      | "UASC Team"
      | "Caseworker"
      | "Designated Teacher"
      | "Attendance Officer"
      | "Coordinator"
      | "Children's Commissioner"
      | "Timesheet Contact"
    )[];
    /**
     * Filter contacts by forename.
     *
     * @maxLength 128
     */
    forename?: string;
    /**
     * Filter contacts by surname.
     *
     * @maxLength 128
     */
    surname?: string;
    /**
     * Filter contacts by email address.
     *
     * @maxLength 256
     */
    email?: string;
    /**
     * Filter contacts by decision-maker status.
     */
    decisionMaker?: "true" | "false";
    /**
     * Filter contacts by opt-out status.
     */
    optOut?: "true" | "false";
    /**
     * Filter contacts by Client.
     *
     * @maxItems 50
     */
    clientId?: string[];
    /**
     * Filter contacts by Client name.
     *
     * @maxLength 256
     */
    clientName?: string;
    /**
     * Filter contacts by associated Client address fields.
     */
    clientAddress?: {
      /**
       * @maxLength 128
       */
      city?: string;
      /**
       * @maxLength 128
       */
      county?: string;
      /**
       * @maxLength 32
       */
      postcode?: string;
    };
    /**
     * Filter contacts by Client local education authority.
     *
     * @maxLength 128
     */
    clientLea?: string;
    /**
     * Filter contacts by Client sector.
     *
     * @maxLength 128
     */
    clientType?: string;
    /**
     * Filter contacts by Client multi-academy trust.
     *
     * @maxLength 256
     */
    clientMultiAcademyTrust?: string;
    /**
     * Filter contacts by Client borough.
     *
     * @maxLength 128
     */
    clientBorough?: string;
    /**
     * Filter contacts by Client assignee.
     *
     * @maxItems 25
     */
    clientAssignee?: string[];
    /**
     * Filter contacts by Client specialism.
     *
     * @maxItems 25
     */
    clientSpecialism?: string[];
    /**
     * Filter contacts by when their Client was updated.
     */
    clientUpdatedAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Filter contacts by distance from their Client address.
     */
    location?: {
      /**
       * @minimum -90
       * @maximum 90
       */
      lat: number | null;
      /**
       * @minimum -180
       * @maximum 180
       */
      lng: number | null;
      /**
       * @minimum 0
       * @exclusiveMinimum true
       * @maximum 500
       */
      radius: number;
    };
  };
  /**
   * File format for the Client Contact export.
   *
   * @default csv
   */
  format?: "csv" | "xlsx";
  /**
   * @minItems 1
   * @maxItems 16
   */
  select: (
    | "id"
    | "forename"
    | "surname"
    | "email"
    | "telephone"
    | "mobile"
    | "extension"
    | "role"
    | "roleOther"
    | "contactId"
    | "timesheetPortal.id"
    | "timesheetPortal.code"
    | "timesheetPortal"
    | "optOut"
    | "note"
    | "decisionMaker"
    | "status"
    | "clientId"
    | "clientName"
    | "clientAddress.addressLine1"
    | "clientAddress.addressLine2"
    | "clientAddress.addressLine3"
    | "clientAddress.city"
    | "clientAddress.county"
    | "clientAddress.borough"
    | "clientAddress.postcode"
    | "clientAddress.country"
    | "clientAddress.lat"
    | "clientAddress.lng"
    | "clientAddress"
    | "clientLea"
    | "clientType"
    | "clientMultiAcademyTrust"
    | "clientBorough"
    | "clientAssignee"
    | "clientAssignee.id"
    | "clientAssignee.email"
    | "clientAssignee.firstName"
    | "clientAssignee.lastName"
    | "clientAssignee.username"
    | "clientAssignee.lea"
    | "clientAssignee.clients"
    | "clientAssignee.position"
    | "clientAssignee.permissions"
    | "clientAssignee.activationCode"
    | "clientAssignee.customer"
    | "clientAssignee.disabled"
    | "clientAssignee.commissionThreshold"
    | "clientAssignee.branches"
    | "clientAssignee.displayOnboarding"
    | "clientAssignee._customId"
    | "clientAssignee.signature"
    | "clientAssignee.phone"
    | "clientAssignee.defaultCandidateWatcher"
    | "clientAssignee.defaultTaskWorkspace"
    | "clientAssignee.teamAdmin"
    | "clientAssignee.contact"
    | "clientAssignee.contact.id"
    | "clientAssignee.contact.forename"
    | "clientAssignee.contact.surname"
    | "clientAssignee.contact.email"
    | "clientAssignee.contact.telephone"
    | "clientAssignee.contact.mobile"
    | "clientAssignee.contact.extension"
    | "clientAssignee.createdAt"
    | "clientAssignee.evertime.id"
    | "clientAssignee.evertime"
    | "clientAssignee.timesheetPortal.id"
    | "clientAssignee.timesheetPortal.code"
    | "clientAssignee.timesheetPortal"
    | "clientSpecialism"
    | "clientUpdatedAt"
    | "distance"
    | "relevanceScore"
    | "matchReasons"
    | ""
  )[];
};

export type ExportClientContactsVariables = {
  body: ExportClientContactsRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchExportClientContacts = (
  variables: ExportClientContactsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    ExportClientContactsError,
    ExportClientContactsRequestBody,
    {},
    {},
    {}
  >({ url: "/clients/contacts/exports", method: "post", ...variables, signal });

export const useExportClientContacts = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      ExportClientContactsError,
      ExportClientContactsVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    ExportClientContactsError,
    ExportClientContactsVariables
  >({
    mutationFn: (variables: ExportClientContactsVariables) =>
      fetchExportClientContacts(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type ExportClientsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type ExportClientsRequestBody =
  | {
      /**
       * The page number to retrieve
       *
       * @minimum 1
       */
      page?: number;
      /**
       * The number of items to retrieve per page
       *
       * @minimum 1
       * @maximum 100
       */
      pageSize?: number;
      /**
       * Find relevant clients using their details and location.
       *
       * @minLength 1
       * @maxLength 256
       */
      q?: string;
      /**
       * Search clients by name
       *
       * @maxLength 256
       */
      search?: string;
      /**
       * The field to sort results by. Prefix with - for descending order
       */
      sort?:
        | "name"
        | "-name"
        | "createdAt"
        | "-createdAt"
        | "assignee"
        | "-assignee"
        | "telephone"
        | "-telephone"
        | "email"
        | "-email"
        | "address.postcode"
        | "-address.postcode"
        | "address.county"
        | "-address.county"
        | "lea"
        | "-lea"
        | "type"
        | "-type"
        | "multiAcademyTrust"
        | "-multiAcademyTrust"
        | "address.city"
        | "-address.city"
        | "borough"
        | "-borough"
        | "specialism"
        | "-specialism"
        | "internalId"
        | "-internalId"
        | "lastContactedDate"
        | "-lastContactedDate"
        | "distance"
        | "-distance";
      /**
       * Related fields to include as objects in the response.
       *
       * @maxItems 25
       */
      expand?: ("assignee" | "tags")[];
      /**
       * Additional fields to include in the response
       */
      include?: "bookings"[];
      /**
       * Filter clients based on various criteria
       */
      filter?: {
        /**
         * Filter clients by their IDs. Maximum 50.
         *
         * @maxItems 50
         */
        ids?: string[];
        /**
         * Filter clients by name.
         *
         * @maxLength 256
         */
        name?: string;
        /**
         * Filter clients by telephone number.
         *
         * @maxLength 32
         */
        telephone?: string;
        /**
         * Filter clients by email address.
         *
         * @maxLength 256
         */
        email?: string;
        /**
         * Address fields used to filter clients. Multiple supplied fields are combined.
         */
        address?: {
          /**
           * Filter clients by postcode.
           *
           * @maxLength 32
           */
          postcode?: string;
          /**
           * Filter clients by county.
           *
           * @maxLength 128
           */
          county?: string;
          /**
           * Filter clients by city.
           *
           * @maxLength 128
           */
          city?: string;
        };
        /**
         * Filter clients by local education authority.
         *
         * @maxLength 128
         */
        lea?: string;
        /**
         * Filter clients by type.
         *
         * @maxLength 128
         */
        type?: string;
        /**
         * Filter clients by multi academy trust.
         *
         * @maxLength 256
         */
        multiAcademyTrust?: string;
        /**
         * Filter clients by borough.
         *
         * @maxLength 128
         */
        borough?: string;
        /**
         * Filter clients by specialism.
         *
         * @maxItems 25
         */
        specialism?: string[];
        /**
         * Filter clients by their company-defined identifier.
         *
         * @maxLength 256
         */
        internalId?: string;
        /**
         * Filter clients by whether they are a GCA client.
         */
        ccsClient?: "true" | "false";
        /**
         * Filter clients by GCA client URN.
         *
         * @maxLength 256
         */
        ccsClientUrn?: string;
        /**
         * Filter clients by assigned users.
         *
         * @maxItems 25
         */
        assignee?: string[];
        /**
         * Filter clients by last contact date.
         */
        lastContactedDateFilter?: {
          /**
           * The inclusive absolute start of the date range.
           *
           * @format date-time
           */
          from?: string | null;
          /**
           * The inclusive absolute end of the date range.
           *
           * @format date-time
           */
          to?: string | null;
          /**
           * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
           */
          relative?: {
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            from: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            to: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
          };
        };
        /**
         * Filter clients with an active booking overlapping an absolute or relative date range.
         */
        bookedInRange?: {
          /**
           * The inclusive absolute start of the date range.
           *
           * @format date-time
           */
          from?: string | null;
          /**
           * The inclusive absolute end of the date range.
           *
           * @format date-time
           */
          to?: string | null;
          /**
           * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
           */
          relative?: {
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            from: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            to: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
          };
        };
        /**
         * Filter clients without an active booking overlapping an absolute or relative date range.
         */
        notBookedInRange?: {
          /**
           * The inclusive absolute start of the date range.
           *
           * @format date-time
           */
          from?: string | null;
          /**
           * The inclusive absolute end of the date range.
           *
           * @format date-time
           */
          to?: string | null;
          /**
           * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
           */
          relative?: {
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            from: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            to: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
          };
        };
        /**
         * Filter clients by tags they have.
         *
         * @maxItems 25
         */
        tags?: string[];
        /**
         * Find clients with coordinates relative to a location. A positive radius limits results to that distance in miles; omit it or use zero for no distance limit.
         */
        location?: {
          /**
           * @minimum -90
           * @maximum 90
           */
          lat: number | null;
          /**
           * @minimum -180
           * @maximum 180
           */
          lng: number | null;
          /**
           * @minimum 0
           * @maximum 500
           */
          radius?: number | null;
        };
        /**
         * Filter clients by an associated contact.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        ["contacts.contactId"]?: string;
        /**
         * Filter clients by migration.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        migrationId?: string;
        /**
         * Filter clients by migration approval.
         *
         * @maxItems 3
         */
        migrationApproval?: ("pending" | "approved" | "rejected")[];
      };
      /**
       * File format for the Client export.
       *
       * @default csv
       */
      format?: "csv" | "xlsx";
      mode: "clients";
      /**
       * @minItems 1
       * @maxItems 16
       */
      select: (
        | "id"
        | "name"
        | "address.addressLine1"
        | "address.addressLine2"
        | "address.addressLine3"
        | "address.city"
        | "address.county"
        | "address.borough"
        | "address.postcode"
        | "address.country"
        | "address.lat"
        | "address.lng"
        | "address"
        | "telephone"
        | "email"
        | "contacts"
        | "borough"
        | "lea"
        | "type"
        | "multiAcademyTrust"
        | "color"
        | "customId"
        | "internalId"
        | "assignee"
        | "assignee.id"
        | "assignee.email"
        | "assignee.firstName"
        | "assignee.lastName"
        | "assignee.username"
        | "assignee.lea"
        | "assignee.clients"
        | "assignee.position"
        | "assignee.permissions"
        | "assignee.activationCode"
        | "assignee.customer"
        | "assignee.disabled"
        | "assignee.commissionThreshold"
        | "assignee.branches"
        | "assignee.displayOnboarding"
        | "assignee._customId"
        | "assignee.signature"
        | "assignee.phone"
        | "assignee.defaultCandidateWatcher"
        | "assignee.defaultTaskWorkspace"
        | "assignee.teamAdmin"
        | "assignee.contact"
        | "assignee.contact.id"
        | "assignee.contact.forename"
        | "assignee.contact.surname"
        | "assignee.contact.email"
        | "assignee.contact.telephone"
        | "assignee.contact.mobile"
        | "assignee.contact.extension"
        | "assignee.createdAt"
        | "assignee.evertime.id"
        | "assignee.evertime"
        | "assignee.timesheetPortal.id"
        | "assignee.timesheetPortal.code"
        | "assignee.timesheetPortal"
        | "tags"
        | "tobSentAt"
        | "website"
        | "archived"
        | "specialism"
        | "currentBooking"
        | "bookingCount"
        | "importantNotes"
        | "roles"
        | "startTime"
        | "endTime"
        | "additionalInstructions"
        | "additionalInstructionsClient"
        | "highlight"
        | "area"
        | "governance"
        | "mathq"
        | "ofstedRating"
        | "dateOfstedReportChecked"
        | "boarding"
        | "licenceToIssueSponsorship"
        | "agencySpendTotal"
        | "vatExempt"
        | "splitInvoices"
        | "switchBoard"
        | "ccsClient"
        | "ccsClientUrn"
        | "ccsClientOfstedUrn"
        | "ccsClientLotNumber"
        | "buyerReference"
        | "ccsClientInvoiceDate"
        | "keyStage"
        | "contact"
        | "contact.id"
        | "contact.forename"
        | "contact.surname"
        | "contact.email"
        | "contact.telephone"
        | "contact.mobile"
        | "contact.extension"
        | "lastContactedDate"
        | "relevanceScore"
        | "matchReasons"
        | "distance"
        | "migrationId"
        | "migrationApproval"
        | "evertime.id"
        | "evertime.contacts"
        | "evertime.locations"
        | "evertime.nameMatch"
        | "evertime"
        | "timesheetPortal.id"
        | "timesheetPortal.code"
        | "timesheetPortal"
        | ""
      )[];
      /**
       * The contact role whose details should be appended to each Client row.
       */
      contactRole?:
        | "Head Teacher"
        | "Deputy Head"
        | "Cover Manager"
        | "HR Manager"
        | "Finance Manager"
        | "Head of Maths"
        | "Head of Science"
        | "Head of English"
        | "Assistant Headteacher"
        | "Headteacher's PA"
        | "SENCO"
        | "Other"
        | "Business Manager"
        | "Business Administrator"
        | "Invoice Contact"
        | "Academy Recruitment Contact"
        | "Nursery Manager"
        | "CEO"
        | "COO"
        | "Executive Headteacher"
        | "Inclusion Manager"
        | "EYFS Lead"
        | "Admin Team"
        | "Head of Humanities"
        | "Head of History"
        | "Head of Geography"
        | "Head of Business Studies"
        | "Head of PE"
        | "Head of KS1"
        | "Head of KS2"
        | "Head of KS3"
        | "Head of KS4"
        | "Virtual School Headteacher"
        | "Deputy Virtual School Headteacher"
        | "Post-16 Team Leader"
        | "SEN Team"
        | "Inclusion Team"
        | "UASC Team"
        | "Caseworker"
        | "Designated Teacher"
        | "Attendance Officer"
        | "Coordinator"
        | "Children's Commissioner"
        | "Timesheet Contact";
    }
  | {
      /**
       * The page number to retrieve
       *
       * @minimum 1
       */
      page?: number;
      /**
       * The number of items to retrieve per page
       *
       * @minimum 1
       * @maximum 100
       */
      pageSize?: number;
      /**
       * Find relevant clients using their details and location.
       *
       * @minLength 1
       * @maxLength 256
       */
      q?: string;
      /**
       * Search clients by name
       *
       * @maxLength 256
       */
      search?: string;
      /**
       * The field to sort results by. Prefix with - for descending order
       */
      sort?:
        | "name"
        | "-name"
        | "createdAt"
        | "-createdAt"
        | "assignee"
        | "-assignee"
        | "telephone"
        | "-telephone"
        | "email"
        | "-email"
        | "address.postcode"
        | "-address.postcode"
        | "address.county"
        | "-address.county"
        | "lea"
        | "-lea"
        | "type"
        | "-type"
        | "multiAcademyTrust"
        | "-multiAcademyTrust"
        | "address.city"
        | "-address.city"
        | "borough"
        | "-borough"
        | "specialism"
        | "-specialism"
        | "internalId"
        | "-internalId"
        | "lastContactedDate"
        | "-lastContactedDate"
        | "distance"
        | "-distance";
      /**
       * Related fields to include as objects in the response.
       *
       * @maxItems 25
       */
      expand?: ("assignee" | "tags")[];
      /**
       * Additional fields to include in the response
       */
      include?: "bookings"[];
      /**
       * Filter clients based on various criteria
       */
      filter?: {
        /**
         * Filter clients by their IDs. Maximum 50.
         *
         * @maxItems 50
         */
        ids?: string[];
        /**
         * Filter clients by name.
         *
         * @maxLength 256
         */
        name?: string;
        /**
         * Filter clients by telephone number.
         *
         * @maxLength 32
         */
        telephone?: string;
        /**
         * Filter clients by email address.
         *
         * @maxLength 256
         */
        email?: string;
        /**
         * Address fields used to filter clients. Multiple supplied fields are combined.
         */
        address?: {
          /**
           * Filter clients by postcode.
           *
           * @maxLength 32
           */
          postcode?: string;
          /**
           * Filter clients by county.
           *
           * @maxLength 128
           */
          county?: string;
          /**
           * Filter clients by city.
           *
           * @maxLength 128
           */
          city?: string;
        };
        /**
         * Filter clients by local education authority.
         *
         * @maxLength 128
         */
        lea?: string;
        /**
         * Filter clients by type.
         *
         * @maxLength 128
         */
        type?: string;
        /**
         * Filter clients by multi academy trust.
         *
         * @maxLength 256
         */
        multiAcademyTrust?: string;
        /**
         * Filter clients by borough.
         *
         * @maxLength 128
         */
        borough?: string;
        /**
         * Filter clients by specialism.
         *
         * @maxItems 25
         */
        specialism?: string[];
        /**
         * Filter clients by their company-defined identifier.
         *
         * @maxLength 256
         */
        internalId?: string;
        /**
         * Filter clients by whether they are a GCA client.
         */
        ccsClient?: "true" | "false";
        /**
         * Filter clients by GCA client URN.
         *
         * @maxLength 256
         */
        ccsClientUrn?: string;
        /**
         * Filter clients by assigned users.
         *
         * @maxItems 25
         */
        assignee?: string[];
        /**
         * Filter clients by last contact date.
         */
        lastContactedDateFilter?: {
          /**
           * The inclusive absolute start of the date range.
           *
           * @format date-time
           */
          from?: string | null;
          /**
           * The inclusive absolute end of the date range.
           *
           * @format date-time
           */
          to?: string | null;
          /**
           * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
           */
          relative?: {
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            from: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            to: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
          };
        };
        /**
         * Filter clients with an active booking overlapping an absolute or relative date range.
         */
        bookedInRange?: {
          /**
           * The inclusive absolute start of the date range.
           *
           * @format date-time
           */
          from?: string | null;
          /**
           * The inclusive absolute end of the date range.
           *
           * @format date-time
           */
          to?: string | null;
          /**
           * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
           */
          relative?: {
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            from: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            to: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
          };
        };
        /**
         * Filter clients without an active booking overlapping an absolute or relative date range.
         */
        notBookedInRange?: {
          /**
           * The inclusive absolute start of the date range.
           *
           * @format date-time
           */
          from?: string | null;
          /**
           * The inclusive absolute end of the date range.
           *
           * @format date-time
           */
          to?: string | null;
          /**
           * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
           */
          relative?: {
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            from: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
            /**
             * A date boundary calculated from the current day, week, or month when the query runs.
             */
            to: {
              /**
               * The current calendar point from which a relative date boundary is calculated.
               */
              anchor: "today" | "startOfWeek" | "startOfMonth";
              /**
               * The number of units before or after the selected calendar anchor.
               *
               * @minimum -120
               * @maximum 120
               */
              offset: number | null;
              /**
               * The unit used to offset a relative date boundary.
               */
              unit: "days" | "weeks" | "months";
            };
          };
        };
        /**
         * Filter clients by tags they have.
         *
         * @maxItems 25
         */
        tags?: string[];
        /**
         * Find clients with coordinates relative to a location. A positive radius limits results to that distance in miles; omit it or use zero for no distance limit.
         */
        location?: {
          /**
           * @minimum -90
           * @maximum 90
           */
          lat: number | null;
          /**
           * @minimum -180
           * @maximum 180
           */
          lng: number | null;
          /**
           * @minimum 0
           * @maximum 500
           */
          radius?: number | null;
        };
        /**
         * Filter clients by an associated contact.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        ["contacts.contactId"]?: string;
        /**
         * Filter clients by migration.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        migrationId?: string;
        /**
         * Filter clients by migration approval.
         *
         * @maxItems 3
         */
        migrationApproval?: ("pending" | "approved" | "rejected")[];
      };
      /**
       * File format for the Client export.
       *
       * @default csv
       */
      format?: "csv" | "xlsx";
      mode: "contacts";
      /**
       * When provided, export only contacts with this role.
       */
      contactRole?:
        | "Head Teacher"
        | "Deputy Head"
        | "Cover Manager"
        | "HR Manager"
        | "Finance Manager"
        | "Head of Maths"
        | "Head of Science"
        | "Head of English"
        | "Assistant Headteacher"
        | "Headteacher's PA"
        | "SENCO"
        | "Other"
        | "Business Manager"
        | "Business Administrator"
        | "Invoice Contact"
        | "Academy Recruitment Contact"
        | "Nursery Manager"
        | "CEO"
        | "COO"
        | "Executive Headteacher"
        | "Inclusion Manager"
        | "EYFS Lead"
        | "Admin Team"
        | "Head of Humanities"
        | "Head of History"
        | "Head of Geography"
        | "Head of Business Studies"
        | "Head of PE"
        | "Head of KS1"
        | "Head of KS2"
        | "Head of KS3"
        | "Head of KS4"
        | "Virtual School Headteacher"
        | "Deputy Virtual School Headteacher"
        | "Post-16 Team Leader"
        | "SEN Team"
        | "Inclusion Team"
        | "UASC Team"
        | "Caseworker"
        | "Designated Teacher"
        | "Attendance Officer"
        | "Coordinator"
        | "Children's Commissioner"
        | "Timesheet Contact";
    };

export type ExportClientsVariables = {
  body?: ExportClientsRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchExportClients = (
  variables: ExportClientsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    ExportClientsError,
    ExportClientsRequestBody,
    {},
    {},
    {}
  >({ url: "/clients/exports", method: "post", ...variables, signal });

export const useExportClients = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      ExportClientsError,
      ExportClientsVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    ExportClientsError,
    ExportClientsVariables
  >({
    mutationFn: (variables: ExportClientsVariables) =>
      fetchExportClients(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetClientPathParams = {
  /**
   * The unique identifier for the client.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  clientId: string;
};

export type GetClientQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "name"
    | "address.addressLine1"
    | "address.addressLine2"
    | "address.addressLine3"
    | "address.city"
    | "address.county"
    | "address.borough"
    | "address.postcode"
    | "address.country"
    | "address.lat"
    | "address.lng"
    | "address"
    | "telephone"
    | "email"
    | "contacts"
    | "borough"
    | "lea"
    | "type"
    | "multiAcademyTrust"
    | "color"
    | "customId"
    | "internalId"
    | "assignee"
    | "assignee.id"
    | "assignee.email"
    | "assignee.firstName"
    | "assignee.lastName"
    | "assignee.username"
    | "assignee.lea"
    | "assignee.clients"
    | "assignee.position"
    | "assignee.permissions"
    | "assignee.activationCode"
    | "assignee.customer"
    | "assignee.disabled"
    | "assignee.commissionThreshold"
    | "assignee.branches"
    | "assignee.displayOnboarding"
    | "assignee._customId"
    | "assignee.signature"
    | "assignee.phone"
    | "assignee.defaultCandidateWatcher"
    | "assignee.defaultTaskWorkspace"
    | "assignee.teamAdmin"
    | "assignee.contact"
    | "assignee.contact.id"
    | "assignee.contact.forename"
    | "assignee.contact.surname"
    | "assignee.contact.email"
    | "assignee.contact.telephone"
    | "assignee.contact.mobile"
    | "assignee.contact.extension"
    | "assignee.createdAt"
    | "assignee.evertime.id"
    | "assignee.evertime"
    | "assignee.timesheetPortal.id"
    | "assignee.timesheetPortal.code"
    | "assignee.timesheetPortal"
    | "tags"
    | "tobSentAt"
    | "website"
    | "archived"
    | "specialism"
    | "currentBooking"
    | "bookingCount"
    | "importantNotes"
    | "roles"
    | "startTime"
    | "endTime"
    | "additionalInstructions"
    | "additionalInstructionsClient"
    | "highlight"
    | "area"
    | "governance"
    | "mathq"
    | "ofstedRating"
    | "dateOfstedReportChecked"
    | "boarding"
    | "licenceToIssueSponsorship"
    | "agencySpendTotal"
    | "vatExempt"
    | "splitInvoices"
    | "switchBoard"
    | "ccsClient"
    | "ccsClientUrn"
    | "ccsClientOfstedUrn"
    | "ccsClientLotNumber"
    | "buyerReference"
    | "ccsClientInvoiceDate"
    | "keyStage"
    | "contact"
    | "contact.id"
    | "contact.forename"
    | "contact.surname"
    | "contact.email"
    | "contact.telephone"
    | "contact.mobile"
    | "contact.extension"
    | "lastContactedDate"
    | "relevanceScore"
    | "matchReasons"
    | "distance"
    | "migrationId"
    | "migrationApproval"
    | "evertime.id"
    | "evertime.contacts"
    | "evertime.locations"
    | "evertime.nameMatch"
    | "evertime"
    | "timesheetPortal.id"
    | "timesheetPortal.code"
    | "timesheetPortal"
    | ""
  )[];
  /**
   * Additional fields to expand in the response
   */
  expand?: "assignee"[];
};

export type GetClientError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetClientResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the client
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the client
     *
     * @maxLength 256
     */
    name?: string;
    /**
     * The address of the client
     */
    address?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The telephone number of the client
     *
     * @maxLength 32
     */
    telephone?: string;
    /**
     * The email address of the client
     *
     * @format email
     */
    email?: string;
    /**
     * Array of client contacts
     */
    contacts?: {
      /**
       * The unique identifier of the contact
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The forename of the contact
       *
       * @maxLength 128
       */
      forename?: string;
      /**
       * The surname of the contact
       *
       * @maxLength 128
       */
      surname?: string;
      /**
       * The email address of the contact
       *
       * @format email
       */
      email?: string;
      /**
       * The telephone number of the contact
       *
       * @maxLength 32
       */
      telephone?: string;
      /**
       * The mobile number of the contact
       *
       * @maxLength 32
       */
      mobile?: string;
      /**
       * The extension number of the contact
       *
       * @maxLength 8
       */
      extension?: string;
      /**
       * The role of the contact
       */
      role?:
        | "Head Teacher"
        | "Deputy Head"
        | "Cover Manager"
        | "HR Manager"
        | "Finance Manager"
        | "Head of Maths"
        | "Head of Science"
        | "Head of English"
        | "Assistant Headteacher"
        | "Headteacher's PA"
        | "SENCO"
        | "Other"
        | "Business Manager"
        | "Business Administrator"
        | "Invoice Contact"
        | "Academy Recruitment Contact"
        | "Nursery Manager"
        | "CEO"
        | "COO"
        | "Executive Headteacher"
        | "Inclusion Manager"
        | "EYFS Lead"
        | "Admin Team"
        | "Head of Humanities"
        | "Head of History"
        | "Head of Geography"
        | "Head of Business Studies"
        | "Head of PE"
        | "Head of KS1"
        | "Head of KS2"
        | "Head of KS3"
        | "Head of KS4"
        | "Virtual School Headteacher"
        | "Deputy Virtual School Headteacher"
        | "Post-16 Team Leader"
        | "SEN Team"
        | "Inclusion Team"
        | "UASC Team"
        | "Caseworker"
        | "Designated Teacher"
        | "Attendance Officer"
        | "Coordinator"
        | "Children's Commissioner"
        | "Timesheet Contact";
      /**
       * The name of the role if the role is 'Other'
       *
       * @maxLength 128
       */
      roleOther?: string;
      /**
       * The unique identifier of the contact
       *
       * @format uuid
       */
      contactId?: string;
      /**
       * Timesheet Portal details for this contact
       */
      timesheetPortal?: {
        /**
         * The Timesheet Portal approver GUID linked to this client contact
         *
         * @format uuid
         */
        id?: string;
        /**
         * The Timesheet Portal approver code
         *
         * @maxLength 256
         */
        code?: string;
      };
      /**
       * Whether the contact has opted out of communications
       */
      optOut?: boolean;
      /**
       * Any additional notes about the contact
       *
       * @maxLength 175
       */
      note?: string;
      /**
       * Whether the contact is a decision maker
       */
      decisionMaker?: boolean;
      /**
       * The status of the contact
       */
      status?: "Active" | "Passive" | "Inactive";
      /**
       * The Client associated with the contact.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      clientId?: string;
      /**
       * The name of the Client associated with the contact.
       *
       * @maxLength 256
       */
      clientName?: string;
      /**
       * The address of the Client associated with the contact.
       */
      clientAddress?: {
        /**
         * The first line of the address
         *
         * @maxLength 256
         */
        addressLine1?: string;
        /**
         * The second line of the address
         *
         * @maxLength 256
         */
        addressLine2?: string;
        /**
         * The third line of the address
         *
         * @maxLength 256
         */
        addressLine3?: string;
        /**
         * The city of the address
         *
         * @maxLength 256
         */
        city?: string;
        /**
         * The county of the address
         *
         * @maxLength 256
         */
        county?: string;
        /**
         * The borough of the address
         *
         * @maxLength 256
         */
        borough?: string;
        /**
         * The postcode of the address
         *
         * @maxLength 10
         */
        postcode?: string;
        /**
         * The country of the address
         *
         * @maxLength 256
         */
        country?: string;
        /**
         * The latitude of the address
         *
         * @minimum -90
         * @maximum 90
         */
        lat?: number;
        /**
         * The longitude of the address
         *
         * @minimum -180
         * @maximum 180
         */
        lng?: number;
      };
      /**
       * The local education authority of the associated Client.
       *
       * @maxLength 128
       */
      clientLea?: string;
      /**
       * The sector of the associated Client.
       *
       * @maxLength 128
       */
      clientType?: string;
      /**
       * The multi-academy trust of the associated Client.
       *
       * @maxLength 256
       */
      clientMultiAcademyTrust?: string;
      /**
       * The borough of the associated Client.
       *
       * @maxLength 128
       */
      clientBorough?: string;
      /**
       * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
       */
      clientAssignee?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * The specialism of the associated Client.
       *
       * @maxLength 256
       */
      clientSpecialism?: string;
      /**
       * When the associated Client was last updated.
       *
       * @format date-time
       */
      clientUpdatedAt?: string;
      /**
       * The associated Client's distance in miles from the requested location.
       *
       * @minimum 0
       */
      distance?: number;
      /**
       * Search relevance relative to the strongest result, where 100 is the strongest match.
       *
       * @minimum 0
       * @maximum 100
       */
      relevanceScore?: number;
      /**
       * Contact and Client fields that explain why the contact matched.
       *
       * @maxItems 5
       */
      matchReasons?: {
        /**
         * The contact or Client information that matched the search.
         *
         * @maxLength 256
         */
        field?: string;
        /**
         * A short excerpt showing why the contact matched.
         *
         * @maxLength 1000
         */
        text?: string;
        /**
         * The exact terms within the content that matched the search.
         *
         * @maxItems 25
         */
        matchedTerms?: string[];
      }[];
    }[];
    /**
     * The borough of the client
     *
     * @maxLength 128
     */
    borough?: string;
    /**
     * The local education authority of the client
     *
     * @maxLength 128
     */
    lea?: string;
    /**
     * The type of the client
     *
     * @maxLength 128
     */
    type?: string;
    /**
     * The multi academy trust that the client belongs to
     *
     * @maxLength 256
     */
    multiAcademyTrust?: string;
    /**
     * The color (HEX format prefixed with #) associated with the client
     *
     * @pattern ^#[0-9a-fA-F]{6}$
     */
    color?: string;
    /**
     * A custom ID for the client.
     *
     * @maxLength 256
     */
    customId?: string;
    /**
     * A company-defined identifier used to match the client with external records.
     *
     * @maxLength 256
     */
    internalId?: string;
    /**
     * The user assigned to the client. If expanded, a user object else the user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * Tags associated with this client. If expanded, an array of tag objects; otherwise, an array of tag IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * The date when the TOB was sent
     *
     * @format date-time
     */
    tobSentAt?: string;
    /**
     * The website of the client
     *
     * @format uri
     */
    website?: string;
    /**
     * Whether the client is archived
     */
    archived?: boolean;
    /**
     * The specialism of the client
     *
     * @maxLength 256
     */
    specialism?: string;
    /**
     * The current booking IDs associated with the client
     */
    currentBooking?: string[];
    /**
     * The number of active bookings associated with the client, constrained by the requested booking date range when present.
     *
     * @minimum 0
     */
    bookingCount?: number;
    /**
     * Any important notes related to the client
     *
     * @maxLength 1024
     */
    importantNotes?: string;
    /**
     * The booking roles associated with the client
     */
    roles?: {
      /**
       * The occupation of the individual in the booking role
       *
       * @maxLength 256
       */
      occupation?: string;
      /**
       * The pay rate for the booking role
       *
       * @maxLength 256
       */
      payRate?: string;
      /**
       * The charge rate for the booking role
       *
       * @maxLength 256
       */
      chargeRate?: string;
      /**
       * The type of pay for the booking role
       */
      payType?: "hourly" | "daily" | any | null;
      /**
       * The length of the day for the booking role
       */
      dayLength?: "full" | "half" | any | null;
      /**
       * The AM/PM designation for the day length
       */
      dayLengthAmPm?: "AM" | "PM" | any | null;
    }[];
    /**
     * The start time for the client
     */
    startTime?: string;
    /**
     * The end time for the client
     */
    endTime?: string;
    /**
     * Any additional instructions for the client
     *
     * @maxLength 1024
     */
    additionalInstructions?: string;
    /**
     * Any additional instructions specific to the client
     */
    additionalInstructionsClient?: string;
    /**
     * Tag highlight colour (HEX format prefixed with #)
     *
     * @pattern ^#[0-9a-fA-F]{6}$
     */
    highlight?: string;
    /**
     * The area of the client
     */
    area?: string;
    /**
     * The governance type of the client
     */
    governance?:
      | "Academy"
      | "Childrens Centre"
      | "College"
      | "Independant"
      | "LEA"
      | "University";
    /**
     * The MAT HQ details of the client
     */
    mathq?: "Unsure" | "Yes" | "No";
    /**
     * The Ofsted rating of the client
     */
    ofstedRating?:
      | "Outstanding"
      | "Good"
      | "Requires Improvement"
      | "Inadequate"
      | "NA - Independent"
      | "NA - Academy";
    /**
     * The date when the Ofsted report was checked
     *
     * @format date-time
     */
    dateOfstedReportChecked?: string | null;
    /**
     * The boarding details of the client
     */
    boarding?: "Unsure" | "Yes" | "No" | any | null;
    /**
     * The licence to issue sponsorship for the client
     */
    licenceToIssueSponsorship?: "Unsure" | "Yes" | "No" | any | null;
    /**
     * The total agency spend for the client
     */
    agencySpendTotal?: number;
    /**
     * Whether the client is VAT exempt
     */
    vatExempt?: boolean;
    /**
     * Whether to split invoices for the client
     */
    splitInvoices?: boolean;
    /**
     * The switch board details for the client
     */
    switchBoard?: string;
    /**
     * Whether the client is a GCA client
     */
    ccsClient?: boolean;
    /**
     * The GCA client URN
     */
    ccsClientUrn?: string;
    /**
     * The GCA client Ofsted URN
     */
    ccsClientOfstedUrn?: string;
    /**
     * The GCA client lot number
     */
    ccsClientLotNumber?: string;
    /**
     * The buyer reference supplied by a GCA client, when available.
     *
     * @maxLength 255
     */
    buyerReference?: string | null;
    /**
     * The GCA client invoice date
     *
     * @format date-time
     */
    ccsClientInvoiceDate?: string;
    /**
     * The key stage for the client
     */
    keyStage?: string;
    /**
     * The contact record for this client. If expanded, a contact object else the contact record ID.
     */
    contact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
        };
    /**
     * The date when the client was last contacted.
     *
     * @format date-time
     */
    lastContactedDate?: string;
    /**
     * Search relevance relative to the strongest result, where 100 is the strongest match.
     *
     * @minimum 0
     * @maximum 100
     */
    relevanceScore?: number;
    /**
     * Client fields that explain why the client matched.
     *
     * @maxItems 5
     */
    matchReasons?: {
      /**
       * The client information that matched the search.
       *
       * @maxLength 256
       */
      field?: string;
      /**
       * A short excerpt showing why the client matched.
       *
       * @maxLength 1000
       */
      text?: string;
      /**
       * The exact terms within the content that matched the search.
       *
       * @maxItems 25
       */
      matchedTerms?: string[];
    }[];
    /**
     * The Client's distance in miles from the requested location. Present only when location filtering is used.
     *
     * @minimum 0
     */
    distance?: number;
    /**
     * The migration that created this client
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Whether this migrated client has been approved
     */
    migrationApproval?: "pending" | "approved" | "rejected";
    /**
     * Evertime details for this client
     */
    evertime?: {
      /**
       * The Evertime ClientId used for this client when exporting payment runs
       *
       * @maxLength 256
       */
      id?: string;
      /**
       * The Evertime contacts to create from Edun (clientContactId set) or reference (clientContactId unset) when exporting payment runs
       */
      contacts?: {
        /**
         * The Evertime ClientContactId
         *
         * @maxLength 256
         */
        evertimeId?: string;
        /**
         * The Edun client contact this Evertime contact is created from; absent when the Evertime contact is only referenced (never created or overwritten)
         *
         * @maxLength 256
         */
        clientContactId?: string;
      }[];
      /**
       * The Evertime locations to create from the client's address (create set) or reference (create unset) when exporting payment runs
       */
      locations?: {
        /**
         * The Evertime LocationId
         *
         * @maxLength 256
         */
        evertimeId?: string;
        /**
         * True when this location is created from the client's address; false when the Evertime location is only referenced (never created or overwritten)
         */
        create?: boolean;
      }[];
      /**
       * Whether the matched Evertime client's name also matches the one held in Edun
       */
      nameMatch?: boolean;
    };
    /**
     * Timesheet Portal details for this client
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal client GUID used when exporting payment runs
       *
       * @format uuid
       */
      id?: string;
      /**
       * The Timesheet Portal client code
       *
       * @maxLength 256
       */
      code?: string;
    };
  };
};

export type GetClientVariables = {
  pathParams: GetClientPathParams;
  queryParams?: GetClientQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetClient = (
  variables: GetClientVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetClientResponse,
    GetClientError,
    undefined,
    {},
    GetClientQueryParams,
    GetClientPathParams
  >({ url: "/clients/{clientId}", method: "get", ...variables, signal });

export function getClientQuery(variables: GetClientVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetClientResponse>;
};

export function getClientQuery(
  variables: GetClientVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetClientResponse>)
    | reactQuery.SkipToken;
};

export function getClientQuery(
  variables: GetClientVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/clients/{clientId}",
      operationId: "getClient",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetClient(variables, signal),
  };
}

export const useSuspenseGetClient = <TData = GetClientResponse,>(
  variables: GetClientVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetClientResponse, GetClientError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<GetClientResponse, GetClientError, TData>({
    ...getClientQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetClient = <TData = GetClientResponse,>(
  variables: GetClientVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetClientResponse, GetClientError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetClientResponse, GetClientError, TData>({
    ...getClientQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetVacanciesQueryParams = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Search vacancies by role
   *
   * @maxLength 256
   */
  search?: string;
  /**
   * The field to sort the results by
   */
  sort?:
    | "createdAt"
    | "-createdAt"
    | "updatedAt"
    | "-updatedAt"
    | "role"
    | "-role"
    | "start"
    | "-start"
    | "end"
    | "-end";
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "candidates"
    | "client"
    | "client.id"
    | "client.name"
    | "client.address.addressLine1"
    | "client.address.addressLine2"
    | "client.address.addressLine3"
    | "client.address.city"
    | "client.address.county"
    | "client.address.borough"
    | "client.address.postcode"
    | "client.address.country"
    | "client.address.lat"
    | "client.address.lng"
    | "client.address"
    | "client.telephone"
    | "client.email"
    | "client.contacts"
    | "client.borough"
    | "client.lea"
    | "client.type"
    | "client.multiAcademyTrust"
    | "client.color"
    | "client.customId"
    | "client.internalId"
    | "client.assignee"
    | "client.assignee.id"
    | "client.assignee.email"
    | "client.assignee.firstName"
    | "client.assignee.lastName"
    | "client.assignee.username"
    | "client.assignee.lea"
    | "client.assignee.clients"
    | "client.assignee.position"
    | "client.assignee.permissions"
    | "client.assignee.activationCode"
    | "client.assignee.customer"
    | "client.assignee.disabled"
    | "client.assignee.commissionThreshold"
    | "client.assignee.branches"
    | "client.assignee.displayOnboarding"
    | "client.assignee._customId"
    | "client.assignee.signature"
    | "client.assignee.phone"
    | "client.assignee.defaultCandidateWatcher"
    | "client.assignee.defaultTaskWorkspace"
    | "client.assignee.teamAdmin"
    | "client.assignee.contact"
    | "client.assignee.contact.id"
    | "client.assignee.contact.forename"
    | "client.assignee.contact.surname"
    | "client.assignee.contact.email"
    | "client.assignee.contact.telephone"
    | "client.assignee.contact.mobile"
    | "client.assignee.contact.extension"
    | "client.assignee.createdAt"
    | "client.assignee.evertime.id"
    | "client.assignee.evertime"
    | "client.assignee.timesheetPortal.id"
    | "client.assignee.timesheetPortal.code"
    | "client.assignee.timesheetPortal"
    | "client.tags"
    | "client.tobSentAt"
    | "client.website"
    | "client.archived"
    | "client.specialism"
    | "client.currentBooking"
    | "client.bookingCount"
    | "client.importantNotes"
    | "client.roles"
    | "client.startTime"
    | "client.endTime"
    | "client.additionalInstructions"
    | "client.additionalInstructionsClient"
    | "client.highlight"
    | "client.area"
    | "client.governance"
    | "client.mathq"
    | "client.ofstedRating"
    | "client.dateOfstedReportChecked"
    | "client.boarding"
    | "client.licenceToIssueSponsorship"
    | "client.agencySpendTotal"
    | "client.vatExempt"
    | "client.splitInvoices"
    | "client.switchBoard"
    | "client.ccsClient"
    | "client.ccsClientUrn"
    | "client.ccsClientOfstedUrn"
    | "client.ccsClientLotNumber"
    | "client.buyerReference"
    | "client.ccsClientInvoiceDate"
    | "client.keyStage"
    | "client.contact"
    | "client.contact.id"
    | "client.contact.forename"
    | "client.contact.surname"
    | "client.contact.email"
    | "client.contact.telephone"
    | "client.contact.mobile"
    | "client.contact.extension"
    | "client.lastContactedDate"
    | "client.relevanceScore"
    | "client.matchReasons"
    | "client.distance"
    | "client.migrationId"
    | "client.migrationApproval"
    | "client.evertime.id"
    | "client.evertime.contacts"
    | "client.evertime.locations"
    | "client.evertime.nameMatch"
    | "client.evertime"
    | "client.timesheetPortal.id"
    | "client.timesheetPortal.code"
    | "client.timesheetPortal"
    | "clientAddress.addressLine1"
    | "clientAddress.addressLine2"
    | "clientAddress.addressLine3"
    | "clientAddress.city"
    | "clientAddress.county"
    | "clientAddress.borough"
    | "clientAddress.postcode"
    | "clientAddress.country"
    | "clientAddress.lat"
    | "clientAddress.lng"
    | "clientAddress"
    | "studentId"
    | "postcode"
    | "role"
    | "roleCode"
    | "start"
    | "end"
    | "startTime"
    | "endTime"
    | "days"
    | "chargeRate"
    | "feePercentage"
    | "awr"
    | "awrReminder"
    | "hours"
    | "payRate"
    | "arrivalTime"
    | "clientContact"
    | "clientContact.id"
    | "clientContact.forename"
    | "clientContact.surname"
    | "clientContact.email"
    | "clientContact.telephone"
    | "clientContact.mobile"
    | "clientContact.extension"
    | "clientContact.role"
    | "clientContact.roleOther"
    | "clientContact.contactId"
    | "clientContact.timesheetPortal.id"
    | "clientContact.timesheetPortal.code"
    | "clientContact.timesheetPortal"
    | "clientContact.optOut"
    | "clientContact.note"
    | "clientContact.decisionMaker"
    | "clientContact.status"
    | "clientContact.clientId"
    | "clientContact.clientName"
    | "clientContact.clientAddress.addressLine1"
    | "clientContact.clientAddress.addressLine2"
    | "clientContact.clientAddress.addressLine3"
    | "clientContact.clientAddress.city"
    | "clientContact.clientAddress.county"
    | "clientContact.clientAddress.borough"
    | "clientContact.clientAddress.postcode"
    | "clientContact.clientAddress.country"
    | "clientContact.clientAddress.lat"
    | "clientContact.clientAddress.lng"
    | "clientContact.clientAddress"
    | "clientContact.clientLea"
    | "clientContact.clientType"
    | "clientContact.clientMultiAcademyTrust"
    | "clientContact.clientBorough"
    | "clientContact.clientAssignee"
    | "clientContact.clientAssignee.id"
    | "clientContact.clientAssignee.email"
    | "clientContact.clientAssignee.firstName"
    | "clientContact.clientAssignee.lastName"
    | "clientContact.clientAssignee.username"
    | "clientContact.clientAssignee.lea"
    | "clientContact.clientAssignee.clients"
    | "clientContact.clientAssignee.position"
    | "clientContact.clientAssignee.permissions"
    | "clientContact.clientAssignee.activationCode"
    | "clientContact.clientAssignee.customer"
    | "clientContact.clientAssignee.disabled"
    | "clientContact.clientAssignee.commissionThreshold"
    | "clientContact.clientAssignee.branches"
    | "clientContact.clientAssignee.displayOnboarding"
    | "clientContact.clientAssignee._customId"
    | "clientContact.clientAssignee.signature"
    | "clientContact.clientAssignee.phone"
    | "clientContact.clientAssignee.defaultCandidateWatcher"
    | "clientContact.clientAssignee.defaultTaskWorkspace"
    | "clientContact.clientAssignee.teamAdmin"
    | "clientContact.clientAssignee.contact"
    | "clientContact.clientAssignee.contact.id"
    | "clientContact.clientAssignee.contact.forename"
    | "clientContact.clientAssignee.contact.surname"
    | "clientContact.clientAssignee.contact.email"
    | "clientContact.clientAssignee.contact.telephone"
    | "clientContact.clientAssignee.contact.mobile"
    | "clientContact.clientAssignee.contact.extension"
    | "clientContact.clientAssignee.createdAt"
    | "clientContact.clientAssignee.evertime.id"
    | "clientContact.clientAssignee.evertime"
    | "clientContact.clientAssignee.timesheetPortal.id"
    | "clientContact.clientAssignee.timesheetPortal.code"
    | "clientContact.clientAssignee.timesheetPortal"
    | "clientContact.clientSpecialism"
    | "clientContact.clientUpdatedAt"
    | "clientContact.distance"
    | "clientContact.relevanceScore"
    | "clientContact.matchReasons"
    | "additionalInstructions"
    | "additionalInstructionsClient"
    | "status"
    | "assignee"
    | "assignee.id"
    | "assignee.email"
    | "assignee.firstName"
    | "assignee.lastName"
    | "assignee.username"
    | "assignee.lea"
    | "assignee.clients"
    | "assignee.position"
    | "assignee.permissions"
    | "assignee.activationCode"
    | "assignee.customer"
    | "assignee.disabled"
    | "assignee.commissionThreshold"
    | "assignee.branches"
    | "assignee.displayOnboarding"
    | "assignee._customId"
    | "assignee.signature"
    | "assignee.phone"
    | "assignee.defaultCandidateWatcher"
    | "assignee.defaultTaskWorkspace"
    | "assignee.teamAdmin"
    | "assignee.contact"
    | "assignee.contact.id"
    | "assignee.contact.forename"
    | "assignee.contact.surname"
    | "assignee.contact.email"
    | "assignee.contact.telephone"
    | "assignee.contact.mobile"
    | "assignee.contact.extension"
    | "assignee.createdAt"
    | "assignee.evertime.id"
    | "assignee.evertime"
    | "assignee.timesheetPortal.id"
    | "assignee.timesheetPortal.code"
    | "assignee.timesheetPortal"
    | "payType"
    | "type"
    | "subType"
    | "candidatePayType"
    | "clientName"
    | "jobDescription"
    | "salaryPayType"
    | "salary"
    | "weeklyRota.1.start"
    | "weeklyRota.1.end"
    | "weeklyRota.1.overridePayment"
    | "weeklyRota.1.chargeRate"
    | "weeklyRota.1.payRate"
    | "weeklyRota.1"
    | "weeklyRota.2.start"
    | "weeklyRota.2.end"
    | "weeklyRota.2.overridePayment"
    | "weeklyRota.2.chargeRate"
    | "weeklyRota.2.payRate"
    | "weeklyRota.2"
    | "weeklyRota.3.start"
    | "weeklyRota.3.end"
    | "weeklyRota.3.overridePayment"
    | "weeklyRota.3.chargeRate"
    | "weeklyRota.3.payRate"
    | "weeklyRota.3"
    | "weeklyRota.4.start"
    | "weeklyRota.4.end"
    | "weeklyRota.4.overridePayment"
    | "weeklyRota.4.chargeRate"
    | "weeklyRota.4.payRate"
    | "weeklyRota.4"
    | "weeklyRota.5.start"
    | "weeklyRota.5.end"
    | "weeklyRota.5.overridePayment"
    | "weeklyRota.5.chargeRate"
    | "weeklyRota.5.payRate"
    | "weeklyRota.5"
    | "weeklyRota.6.start"
    | "weeklyRota.6.end"
    | "weeklyRota.6.overridePayment"
    | "weeklyRota.6.chargeRate"
    | "weeklyRota.6.payRate"
    | "weeklyRota.6"
    | "weeklyRota.7.start"
    | "weeklyRota.7.end"
    | "weeklyRota.7.overridePayment"
    | "weeklyRota.7.chargeRate"
    | "weeklyRota.7.payRate"
    | "weeklyRota.7"
    | "weeklyRota"
    | "distance"
    | "includeSchool"
    | "noVacancies"
    | "jobBoard"
    | "clientInterestComments"
    | "healthSafetyCheck"
    | "healthSafetyCheckContact"
    | "poNumber"
    | "cvLibraryId"
    | "archived"
    | "archiveReason"
    | "completed"
    | "occupation"
    | "postedOnDate"
    | ""
  )[];
  /**
   * Related resources to return as objects instead of IDs. Supported values are documented for this endpoint.
   */
  expand?: "client"[];
  /**
   * Filter vacancies based on various criteria
   */
  filter?: {
    /**
     * Filter vacancies by their IDs. Maximum 50.
     *
     * @maxItems 50
     */
    ids?: string[];
    /**
     * Filter vacancies by their booked status
     */
    booked?: boolean;
  };
};

export type GetVacanciesError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetVacanciesResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the vacancy
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * List of candidates associated with the vacancy
     */
    candidates?: {
      /**
       * The candidate applying for the vacancy, either by ID or full details depending on select query
       */
      candidate?:
        | string
        | {
            /**
             * The unique identifier of the candidate
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The forename of the candidate
             *
             * @minLength 1
             * @maxLength 256
             */
            forename?: string;
            /**
             * The surname of the candidate
             *
             * @minLength 1
             * @maxLength 256
             */
            surname?: string;
            /**
             * Any previous names of the candidate
             *
             * @maxLength 256
             */
            previousNames?: string;
            /**
             * The email of the candidate. This will be used for communication.
             *
             * @format email
             */
            email?: string;
            /**
             * The telephone number of the candidate
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * The mobile number of the candidate
             *
             * @maxLength 32
             */
            mobile?: string;
            /**
             * The occupation of the candidate
             */
            occupation?:
              | "Teacher"
              | "Cover Supervisor"
              | "Teaching Assistant"
              | "School Support Staff"
              | "Primary Teacher"
              | "Secondary Teacher"
              | "Head Teacher"
              | "Senior Leader"
              | "Tutor"
              | "Mentor"
              | "Learning Support Assistant"
              | "Finance Officer"
              | "HR Administrator"
              | "HR Manager"
              | "Office Manager"
              | "Administrator"
              | "Personal Assistant"
              | "Pastoral Manager"
              | "Receptionist"
              | "Instructor"
              | "Caretaker"
              | "Nursery Assistant"
              | "Nursery Nurse"
              | "Deputy Nursery Manager"
              | "Lunchtime Supervisor"
              | "Classroom Graduate"
              | "Primary Cover Supervisor"
              | "HLTA"
              | "Nurture Teacher"
              | "Nurture Mentor"
              | "SENDCo"
              | "Registered Manager"
              | "Regional Manager"
              | "Education Director"
              | "Residential Director"
              | "Deputy Manager"
              | "Residential Support Worker"
              | "Sports Coach"
              | "Executive Headteacher"
              | "General Practitioner"
              | "Advance Nurse Practitioner (ANP)"
              | "Emergency Nurse Practitioner (ENP)"
              | "Advanced Clinical Practitioner (ACP)"
              | "Emergency Clinical Practitioner (ECP)"
              | "Consultant Psychiatrist"
              | "Psychiatrist \u2013 SPR"
              | "IT Technician"
              | "Art Technician"
              | "DT Technician"
              | "Science Technician"
              | "Healthcare Assistant/ Residential Support Worker"
              | "Healthcare Assistant"
              | "Senior Healthcare Assistant"
              | "Team Leader"
              | "Registered General Nurse"
              | "Registered Mental Nurse"
              | "Registered Nurse Learning Disabilities"
              | "Overseas Trained Teacher";
            /**
             * The subject the candidate specializes in
             *
             * @maxLength 256
             */
            subject?: string;
            /**
             * The address of the resource
             */
            address?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The qualifications of the candidate
             *
             * @maxItems 32
             */
            qualifications?: {
              /**
               * The type of the qualification
               *
               * @maxLength 256
               */
              type?: string;
              /**
               * The place where the qualification was obtained
               *
               * @maxLength 256
               */
              place?: string;
              /**
               * The name of the qualification
               *
               * @maxLength 256
               */
              qualificationName?: string;
              /**
               * The date the qualification was obtained
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              date?: string;
              /**
               * The unique identifier of the qualification
               *
               * @maxLength 256
               */
              qualificationId?: string;
              /**
               * The order index of the qualification
               *
               * @minimum 0
               */
              orderIndex?: number;
              /**
               * The expiry date of the qualification
               *
               * @maxLength 256
               */
              qualificationExpiry?: string;
              /**
               * Whether to exclude this qualification from vetting
               */
              excludeFromVetting?: boolean;
              /**
               * The date the qualification was created
               *
               * @format date-time
               */
              createdAt?: string | null;
            }[];
            /**
             * The employment history of the candidate
             *
             * @maxItems 32
             */
            employment?: {
              /**
               * The name of the employer
               *
               * @maxLength 256
               */
              employerName?: string;
              /**
               * The date the candidate started employment
               *
               * @maxLength 256
               */
              dateStarted?: string;
              /**
               * The date the candidate ended employment
               *
               * @maxLength 256
               */
              dateEnded?: string;
              /**
               * The job title of the candidate
               *
               * @maxLength 256
               */
              jobTitle?: string;
              /**
               * The reason the candidate left this employment
               *
               * @maxLength 1024
               */
              reasonForLeaving?: string;
              /**
               * The duties of the candidate in this role. Consultant only, visible on Edun CV
               *
               * @maxLength 1024
               */
              duties?: string;
              /**
               * The salary of the candidate in this role
               *
               * @maxLength 256
               */
              salary?: string;
              /**
               * The address of the candidate's reference for this employment
               */
              referenceAddress?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
              /**
               * The unique identifier for this employment record
               *
               * @maxLength 256
               */
              employmentId?: string;
              /**
               * The order index of this employment record
               *
               * @minimum 0
               */
              orderIndex?: number;
            }[];
            /**
             * The references for the candidate
             *
             * @maxItems 32
             */
            references?: {
              /**
               * The title of the reference
               *
               * @maxLength 16
               */
              referenceTitle?: string;
              /**
               * The forename of the reference
               *
               * @maxLength 256
               */
              referenceForename?: string;
              /**
               * The surname of the reference
               *
               * @maxLength 256
               */
              referenceSurname?: string;
              /**
               * The company of the reference
               *
               * @maxLength 256
               */
              referenceCompany?: string;
              /**
               * The position of the reference
               *
               * @maxLength 256
               */
              referencePosition?: string;
              /**
               * The email of the reference
               *
               * @format email
               */
              referenceEmail?: string;
              /**
               * The telephone number of the reference
               *
               * @maxLength 32
               */
              referenceTelephone?: string;
              /**
               * The type of the reference
               */
              referenceType?:
                | "Education"
                | "Professional"
                | "Character"
                | "Factual"
                | "Academic"
                | "Volunteer";
              /**
               * The duration of the reference
               *
               * @maxLength 128
               */
              referenceDuration?: string;
              /**
               * The ID of the reference
               *
               * @maxLength 256
               */
              referenceId?: string;
              /**
               * This field only applies if the reference immediate consent question is enabled
               * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
               */
              referenceApply?: boolean;
              /**
               * Any additional notes about the reference
               *
               * @maxLength 1024
               */
              referenceNote?: string;
              /**
               * The start date of the reference
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              dateFrom?: string;
              /**
               * The end date of the reference
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              dateTo?: string;
            }[];
            /**
             * Whether the candidate has consented to a reference check
             */
            referenceCheckConsent?: boolean;
            /**
             * The name of the person who provided consent for the reference check
             *
             * @maxLength 256
             */
            referenceCheckConsentName?: string;
            /**
             * The date when consent for the reference check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            referenceCheckConsentDate?: string;
            /**
             * The date of birth of the candidate
             *
             * @format date-time
             */
            dob?: string | null;
            /**
             * The sex of the candidate
             */
            sex?: "Male" | "Female" | "Other" | "Unknown";
            /**
             * The status of the candidate
             */
            status?:
              | "Pending"
              | "Approved"
              | "Rejected"
              | "Anonymised"
              | "Archived"
              | "Enquired"
              | "Hold"
              | "Future"
              | "DNC"
              | "Pre";
            /**
             * The current status of the onboarding process for the candidate
             */
            onboardingStatus?: "Not Started" | "In Progress" | "Completed";
            /**
             * Whether the onboarding information has been sent to the candidate
             */
            onboardingSent?: boolean;
            nextOfKin?: {
              /**
               * The name of the next of kin
               *
               * @maxLength 256
               */
              nextOfKinName?: string;
              /**
               * The relationship of the next of kin to the candidate
               *
               * @maxLength 256
               */
              nextOfKinRelationship?: string;
              /**
               * The telephone number of the next of kin
               *
               * @maxLength 32
               */
              nextOfKinTelephone?: string;
              /**
               * The email address of the next of kin
               *
               * @format email
               */
              nextOfKinEmail?: string;
              nextOfKinAddress?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
            };
            criminalRecord?: {
              /**
               * Whether the candidate has a DBS certificate
               */
              dbsHeld?: boolean;
              /**
               * The unique identifier for the DBS certificate
               *
               * @maxLength 256
               */
              dbsNumber?: string;
              /**
               * The type of DBS check
               */
              dbsType?:
                | "Basic"
                | "Standard"
                | "Enhanced"
                | "Enhanced with Barred List"
                | "Child workforce"
                | "Child and adult workforce"
                | "Adult workforce";
              /**
               * The date when the DBS check was last performed
               *
               * @maxLength 256
               */
              dbsLastChecked?: string;
              /**
               * The DBS update service number for the candidate
               *
               * @maxLength 256
               */
              dbsUpdateServiceNumber?: string;
              /**
               * The result of the prohibition check
               *
               * @maxLength 256
               */
              prohibitionCheck?: string;
              /**
               * The result of the overseas check
               *
               * @maxLength 256
               */
              overseasCheck?: string;
              /**
               * Any additional notes regarding the overseas check
               *
               * @maxLength 256
               */
              overseasCheckNote?: string;
              /**
               * Whether the candidate has a convicted offence
               */
              convictedOffence?: boolean;
              /**
               * The description of the convicted offence
               *
               * @maxLength 1024
               */
              convictedOffenceDescription?: string;
              /**
               * Whether the candidate has any convictions
               */
              convictions?: boolean;
              /**
               * The description of the convictions
               *
               * @maxLength 1024
               */
              convictionsDescription?: string;
              /**
               * Whether the candidate is registered to the DBS update service
               */
              registeredToUpdateService?: boolean;
              /**
               * Whether the candidate consents to update service checks
               */
              consentUpdateServiceChecks?: boolean;
              /**
               * Any additional notes regarding the candidate's criminal record
               *
               * @maxLength 1024
               */
              note?: string;
              /**
               * The date when the update service check was performed
               *
               * @maxLength 64
               */
              updateServiceCheckDate?: string;
              /**
               * Whether the candidate has lived abroad
               */
              abroad?: boolean;
              /**
               * Any additional notes regarding the overseas declaration
               *
               * @maxLength 1024
               */
              overseasDeclarationExtended?: string;
              /**
               * Whether the candidate has been subject to educational disciplinary proceedings
               */
              educationalDisciplinaryProceedings?: boolean;
              /**
               * Any comments regarding the DBS check
               *
               * @maxLength 1024
               */
              dbsComments?: string;
              /**
               * Whether the candidate has read the DBS declaration
               */
              dbsDeclarationRead?: boolean;
              /**
               * Whether the candidate has any overseas offences
               */
              overseasOffence?: boolean;
              /**
               * The description of the overseas offence
               *
               * @maxLength 1024
               */
              overseasOffenceDescription?: string;
              /**
               * Whether the candidate has a current criminal offence
               */
              currentCriminalOffence?: boolean;
              /**
               * The description of the current criminal offence
               *
               * @maxLength 1024
               */
              currentCriminalOffenceDescription?: string;
              /**
               * Whether the DBS was issued by a business
               */
              dbsIssuedByBusiness?: boolean;
              /**
               * Whether the DBS was issued by the current employer
               */
              dbsIssuedByCurrentEmployer?: boolean;
              /**
               * Whether a Section 128 check is required for the candidate.
               */
              section128Required?: boolean;
              /**
               * The date when the Section 128 check was completed.
               */
              section128Checked?: string;
            };
            /**
             * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
             */
            criminalRecordDeclaration?: {
              /**
               * The ordered questions included in the question set.
               *
               * @maxItems 100
               */
              questions?: {
                /**
                 * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * A stable key used to associate the question with current and historical answers.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                key?: string;
                /**
                 * The question shown to the person completing the form.
                 *
                 * @minLength 1
                 * @maxLength 500
                 */
                label?: string;
                /**
                 * Optional supporting text displayed with the question.
                 *
                 * @maxLength 2000
                 */
                description?: string;
                /**
                 * The type of answer collected for the question.
                 */
                type?:
                  | "checkbox"
                  | "date"
                  | "daterange"
                  | "dropdown"
                  | "number"
                  | "signature"
                  | "text"
                  | "textarea";
                /**
                 * Whether an answer is required while the question is visible.
                 */
                required?: boolean;
                /**
                 * Whether the question is included in new forms. Historical responses retain disabled questions.
                 */
                enabled?: boolean;
                /**
                 * The display order of the question.
                 *
                 * @minimum 0
                 * @maximum 1000
                 */
                order?: number;
                /**
                 * Optional rules that determine when the question is shown.
                 */
                condition?: {
                  /**
                   * Whether any rule or every rule must match before the question is shown.
                   */
                  mode?: "any" | "all";
                  /**
                   * The comparisons used to determine question visibility.
                   *
                   * @minItems 1
                   * @maxItems 20
                   */
                  rules?: {
                    /**
                     * The stable key of the question that controls visibility.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    questionKey?: string;
                    /**
                     * How the controlling answer is compared with the value.
                     */
                    operator?: "equals" | "notEquals";
                    /**
                     * The answer value used to determine visibility.
                     */
                    value?: string | boolean | number | (void | null);
                  }[];
                };
                /**
                 * The available answers for a dropdown question.
                 *
                 * @minItems 1
                 * @maxItems 100
                 */
                options?: {
                  /**
                   * The value stored when this option is selected.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  value?: string;
                  /**
                   * The option displayed to the person answering the question.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  label?: string;
                }[];
                /**
                 * Whether this question belongs to the standard template.
                 */
                ["default"]?: boolean;
                /**
                 * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                 */
                audience?: "candidate" | "staff";
                /**
                 * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                 */
                fixedRole?: "fullName" | "signature";
              }[];
              /**
               * The answers recorded for the point-in-time question set.
               *
               * @maxItems 100
               */
              answers?: {
                /**
                 * The identifier of the question being answered.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                questionId?: string;
                /**
                 * The stable key of the question being answered.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey?: string;
                /**
                 * The submitted answer to the question.
                 */
                value?: string | string | boolean | number | (void | null);
              }[];
              /**
               * When the question set was submitted.
               *
               * @format date-time
               */
              submittedAt?: string;
              /**
               * The network address recorded when the response was submitted.
               *
               * @maxLength 64
               */
              submittedIp?: string;
              /**
               * When the question configuration captured by this response was last updated.
               *
               * @format date-time
               */
              configurationUpdatedAt?: string;
            };
            /**
             * Whether the candidate confirmed their criminal record declaration.
             */
            criminalRecordCheckConsent?: boolean;
            /**
             * The name used to confirm the criminal record declaration.
             *
             * @maxLength 256
             */
            criminalRecordCheckConsentName?: string;
            /**
             * The date the criminal record declaration was confirmed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            criminalRecordCheckConsentDate?: string;
            safeInEducation?: {
              /**
               * Whether the candidate is barred from working in education
               */
              barred?: boolean;
              /**
               * The date when the barred status was checked
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              barredCheckDate?: string;
              /**
               * Whether the candidate has a waiver for the barred status
               */
              waiver?: boolean;
              /**
               * Any orders related to the candidate's barred status
               */
              ordersYou?: boolean;
              /**
               * Any orders related to the candidate's barred status for children
               */
              ordersChild?: boolean;
              /**
               * Whether the candidate has any previous safeguarding allegations
               */
              previousSafeguardingAllegations?: boolean;
              /**
               * Whether the candidate has been cautioned
               */
              cautioned?: boolean;
              /**
               * Whether the candidate has a violent offence
               */
              violentOffence?: boolean;
              /**
               * Whether the candidate has lived or worked in another country
               */
              anotherCountry?: boolean;
              /**
               * Whether the candidate has read the Keeping Children Safe in Education document
               */
              readKCSIE?: boolean;
              /**
               * Whether the candidate has read the Disqualification under the Childcare Act guidance
               */
              readChildcareAct?: boolean;
              /**
               * Whether the candidate is prohibited from teaching
               */
              prohibitedFromTeaching?: boolean;
              /**
               * Whether the candidate is known to the police
               */
              knownPolice?: boolean;
              /**
               * Whether the candidate is prohibited from management roles
               */
              prohibitedFromManagement?: boolean;
              /**
               * Whether the candidate has any criminal offences under law
               */
              criminalOffencesLaw?: boolean;
              /**
               * Any additional details regarding the candidate's safe in education status
               */
              safeInEducationDetails?: string;
              safeguardingQuestionaire?: {
                /**
                 * What is the primary purpose of safeguarding in a school environment?
                 */
                primaryPurposeSafeguarding?:
                  | "enhance-academic-performance"
                  | "ensure-safe-learning-environment"
                  | "promote-healthy-relationships"
                  | "facilitate-extracurricular-activities";
                /**
                 * Who is typically responsible for coordinating safeguarding efforts within a school?
                 */
                typicallyResponsibleCoordinatingSafeguarding?:
                  | "janitorial-staff"
                  | "parents-association"
                  | "school-nurse"
                  | "designated-safeguarding-lead";
                /**
                 * Which of the following is considered a potential sign of child abuse or neglect?
                 */
                signOfAbuse?:
                  | "student-excelling"
                  | "frequent-extracurricular"
                  | "unexplained-injuries"
                  | "not-listening";
                /**
                 * How should a teacher respond if a student discloses information about potential abuse or harm?
                 */
                studentDisclosesInformation?:
                  | "ignore-disclosure"
                  | "report-disclosure"
                  | "share-information"
                  | "advise-independent";
                /**
                 * What is the purpose of a school's Child Protection Policy?
                 */
                schoolsChildProtectionPolicy?:
                  | "enforcing-strict-disciplinary-measures"
                  | "identifying-troublemakers"
                  | "outlining-procedures-for-protecting-students-from-harm"
                  | "establishing-academic-achievement-standards";
                /**
                 * What does the term 'FGM' stand for in the context of safeguarding?
                 */
                fgmDefinition?:
                  | "female-general-medicine"
                  | "forced-genetic-modification"
                  | "female-genital-mutilation"
                  | "foundation-gender-equality";
                /**
                 * Which of the following is a key principle of online safety for students?
                 */
                keyPrincipleOnlineSafetyStudents?:
                  | "unlimited-screen-time"
                  | "anon-online-interaction"
                  | "responsible-digital-citizen"
                  | "restrict-edu-sites";
                /**
                 * When planning a school trip, what should teachers consider regarding safeguarding?
                 */
                teachersConsiderRegardingSafeguarding?:
                  | "minimise-supervision"
                  | "appropriate-staff-ratios"
                  | "exclude-medical-conditions"
                  | "allow-roaming";
                /**
                 * What is the purpose of a criminal background check for teachers?
                 */
                criminalBackgroundCheckTeachers?:
                  | "evaluating-teaching-proficiency"
                  | "verifying-immigration-status"
                  | "assessing-financial-stability"
                  | "identifying-criminal-history";
                /**
                 * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
                 */
                studentConfides?:
                  | "assure-secret-safe"
                  | "encourage-share-friends"
                  | "explain-no-guarantee"
                  | "disclose-to-others";
              };
            };
            socialMedia?: {
              /**
               * The Facebook profile URL of the candidate
               *
               * @maxLength 256
               */
              facebook?: string;
              /**
               * The Instagram profile URL of the candidate
               *
               * @maxLength 256
               */
              instagram?: string;
              /**
               * The Twitter profile URL of the candidate
               *
               * @maxLength 256
               */
              twitter?: string;
              /**
               * The LinkedIn profile URL of the candidate
               *
               * @maxLength 256
               */
              linkedIn?: string;
              /**
               * The TikTok profile URL of the candidate
               *
               * @maxLength 256
               */
              tiktok?: string;
              /**
               * Any other social media profile URLs of the candidate
               *
               * @maxLength 256
               */
              others?: string;
              /**
               * Whether the social media profiles have been checked
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              socialMediaChecked?: string;
              /**
               * The date the candidate's Google search check was completed
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              googleCheckedDate?: string;
            };
            /**
             * Whether the candidate has consented to a safe in education check
             */
            safeInEducationCheckConsent?: boolean;
            /**
             * The name of the person who provided consent for the safe in education check
             *
             * @maxLength 256
             */
            safeInEducationCheckConsentName?: string;
            /**
             * The date when consent for the safe in education check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            safeInEducationCheckConsentDate?: string;
            /**
             * The health record of the individual
             */
            healthRecord?: {
              /**
               * Whether the individual has a physical ability condition
               */
              physicalAbilityCondition?: boolean;
              /**
               * Whether the individual is confused
               */
              confused?: boolean;
              /**
               * Whether the individual has hearing issues
               */
              hearing?: boolean;
              /**
               * Whether the individual has eyesight issues
               */
              eyesight?: boolean;
              /**
               * Whether the individual has depression
               */
              depression?: boolean;
              /**
               * Whether the individual has severe pain
               */
              severePain?: boolean;
              /**
               * Whether the individual has excessive drowsiness
               */
              excessiveDrowsiness?: boolean;
              /**
               * Whether the individual has epilepsy
               */
              epilepsy?: boolean;
              /**
               * Whether the individual has heart issues
               */
              heart?: boolean;
              /**
               * Whether the individual has asthma
               */
              asthma?: boolean;
              /**
               * Whether the individual has mental health issues
               */
              mentalHealth?: boolean;
              /**
               * Whether the individual is an expectant mother
               */
              expectantMother?: boolean;
              /**
               * Whether the individual has allergies
               */
              allergies?: boolean;
              /**
               * Whether the individual is taking medication
               */
              medication?: boolean;
              /**
               * Whether the individual has any health issues
               */
              healthIssues?: boolean;
              /**
               * A description of the individual's health issues
               *
               * @maxLength 2048
               */
              healthIssuesDescription?: string;
              /**
               * Any disability needs of the individual
               *
               * @maxLength 2048
               */
              disabilityNeeds?: string;
              /**
               * Whether the individual has any contagious diseases
               */
              contagiousDiseases?: boolean;
              /**
               * Whether the individual's health affects their ability
               */
              affectAbility?: boolean;
              /**
               * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
               */
              notificationAgree?: boolean;
              /**
               * Whether the individual has opted into the SAS Wellbeing App
               */
              sasOptIn?: boolean;
              /**
               * The individual's signature
               *
               * @maxLength 256
               */
              signature?: string;
              /**
               * The individual's drawn signature in base64url format
               *
               * @maxLength 4096
               */
              drawnSignature?: string;
              /**
               * The date the individual signed the form
               *
               * @format date-time
               */
              signatureDate?: string | null;
              /**
               * Whether the individual has been vaccinated against measles
               */
              vaccinationMeasles?: boolean;
              /**
               * Whether the individual has been vaccinated against mumps
               */
              vaccinationMumps?: boolean;
              /**
               * Whether the individual has been vaccinated against rubella
               */
              vaccinationRubella?: boolean;
              /**
               * Whether the individual has been vaccinated against varicella
               */
              vaccinationVaricella?: boolean;
              /**
               * Whether the individual has been vaccinated against tuberculosis
               */
              vaccinationTuberculosis?: boolean;
              /**
               * Whether the individual has been vaccinated against hepatitis B
               */
              vaccinationHepatitisB?: boolean;
              /**
               * Whether the individual has been vaccinated against hepatitis C
               */
              vaccinationHepatitisC?: boolean;
              /**
               * Whether the individual has been vaccinated against HIV
               */
              vaccinationHIV?: boolean;
              /**
               * Whether the individual has been vaccinated against COVID-19
               */
              vaccinationCovid?: boolean;
              /**
               * Whether the individual has received the 1st dose of the COVID-19 vaccine
               */
              vaccinationCovid1?: boolean;
              /**
               * Whether the individual has received the 2nd dose of the COVID-19 vaccine
               */
              vaccinationCovid2?: boolean;
              /**
               * Whether the individual has received the COVID-19 booster vaccine
               */
              vaccinationCovidBooster?: boolean;
              /**
               * Whether the individual has received the COVID-19 influenza vaccine
               */
              vaccinationCovidInfluenza?: boolean;
              /**
               * Whether the individual has an override for fit to work requirements
               */
              fitToWorkOverride?: boolean;
            };
            /**
             * The candidate's latest submitted health declaration, including the exact questions presented.
             */
            healthDeclaration?: {
              /**
               * The ordered questions included in the question set.
               *
               * @maxItems 100
               */
              questions?: {
                /**
                 * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * A stable key used to associate the question with current and historical answers.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                key?: string;
                /**
                 * The question shown to the person completing the form.
                 *
                 * @minLength 1
                 * @maxLength 500
                 */
                label?: string;
                /**
                 * Optional supporting text displayed with the question.
                 *
                 * @maxLength 2000
                 */
                description?: string;
                /**
                 * The type of answer collected for the question.
                 */
                type?:
                  | "checkbox"
                  | "date"
                  | "daterange"
                  | "dropdown"
                  | "number"
                  | "signature"
                  | "text"
                  | "textarea";
                /**
                 * Whether an answer is required while the question is visible.
                 */
                required?: boolean;
                /**
                 * Whether the question is included in new forms. Historical responses retain disabled questions.
                 */
                enabled?: boolean;
                /**
                 * The display order of the question.
                 *
                 * @minimum 0
                 * @maximum 1000
                 */
                order?: number;
                /**
                 * Optional rules that determine when the question is shown.
                 */
                condition?: {
                  /**
                   * Whether any rule or every rule must match before the question is shown.
                   */
                  mode?: "any" | "all";
                  /**
                   * The comparisons used to determine question visibility.
                   *
                   * @minItems 1
                   * @maxItems 20
                   */
                  rules?: {
                    /**
                     * The stable key of the question that controls visibility.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    questionKey?: string;
                    /**
                     * How the controlling answer is compared with the value.
                     */
                    operator?: "equals" | "notEquals";
                    /**
                     * The answer value used to determine visibility.
                     */
                    value?: string | boolean | number | (void | null);
                  }[];
                };
                /**
                 * The available answers for a dropdown question.
                 *
                 * @minItems 1
                 * @maxItems 100
                 */
                options?: {
                  /**
                   * The value stored when this option is selected.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  value?: string;
                  /**
                   * The option displayed to the person answering the question.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  label?: string;
                }[];
                /**
                 * Whether this question belongs to the standard template.
                 */
                ["default"]?: boolean;
                /**
                 * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                 */
                audience?: "candidate" | "staff";
                /**
                 * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                 */
                fixedRole?: "fullName" | "signature";
              }[];
              /**
               * The answers recorded for the point-in-time question set.
               *
               * @maxItems 100
               */
              answers?: {
                /**
                 * The identifier of the question being answered.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                questionId?: string;
                /**
                 * The stable key of the question being answered.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey?: string;
                /**
                 * The submitted answer to the question.
                 */
                value?: string | string | boolean | number | (void | null);
              }[];
              /**
               * When the question set was submitted.
               *
               * @format date-time
               */
              submittedAt?: string;
              /**
               * The network address recorded when the response was submitted.
               *
               * @maxLength 64
               */
              submittedIp?: string;
              /**
               * When the question configuration captured by this response was last updated.
               *
               * @format date-time
               */
              configurationUpdatedAt?: string;
            };
            /**
             * Whether the candidate confirmed their health declaration.
             */
            healthRecordCheckConsent?: boolean;
            /**
             * The name used to confirm the health declaration.
             *
             * @maxLength 256
             */
            healthRecordCheckConsentName?: string;
            /**
             * The date the health declaration was confirmed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            healthRecordCheckConsentDate?: string;
            /**
             * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
             */
            bankDetails?: {
              /**
               * The name on the bank account
               */
              bankAccountName?: string;
              /**
               * The name of the bank
               */
              bankName?: string;
              /**
               * The account number
               */
              accountNumber?: string;
              /**
               * The sort code
               */
              sortCode?: string;
              /**
               * The National Insurance number
               */
              niNumber?: string;
              /**
               * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
               */
              jobOp1?: boolean;
              /**
               * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
               */
              jobOp2?: boolean;
              /**
               * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
               */
              jobOp3?: boolean;
              studentLoan?: {
                /**
                 * Boolean flag for if candidate has student loan
                 */
                studentLoan?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 1
                 */
                plan1?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 2
                 */
                plan2?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 4
                 */
                plan4?: boolean;
                /**
                 * Boolean flag for if candidate has postgrad loan
                 */
                postGradLoan?: boolean;
              };
            };
            /**
             * Whether the candidate has confirmed their details are correct.
             */
            declarationCheckConsent?: boolean;
            /**
             * The name of the person who confirmed their details are correct.
             *
             * @maxLength 256
             */
            declarationCheckConsentName?: string;
            /**
             * The date when consent for the declaration check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            declarationCheckConsentDate?: string;
            /**
             * Whether the candidate has consented to a contract for service
             */
            contractForServiceConsent?: boolean;
            /**
             * The name of the person who provided consent for the contract for service
             *
             * @maxLength 256
             */
            contractForServiceName?: string;
            /**
             * The date when consent for the contract for service was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            contractForServiceDate?: string;
            /**
             * The candidate has completed their onboarding form.
             */
            confirmed?: boolean;
            /**
             * The ID of the user who created the candidate record.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            createdBy?: string;
            /**
             * The ID of the customer associated with the candidate.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customerId?: string;
            /**
             * The migration that created this candidate
             *
             * @pattern ^[0-9a-f]{24}$
             */
            migrationId?: string;
            /**
             * Whether this migrated candidate has been approved
             */
            migrationApproval?: "pending" | "approved" | "rejected";
            /**
             * The user assigned to the candidate. If expanded, a user object else the user ID.
             */
            assignee?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * The ID of the current booking for the candidate.
             */
            currentBooking?: string[];
            /**
             * The URL of the candidate's profile photo.
             *
             * @maxLength 256
             */
            profilePhoto?: string;
            /**
             * The pay type for the candidate.
             */
            payType?:
              | "PAYE"
              | "Umbrella"
              | "Limited Company"
              | "Not Applicable";
            /**
             * The pay interval for the candidate.
             */
            payInterval?:
              | "Monthly"
              | "Weekly"
              | "Weekly Pay in Arrears"
              | "Bi-Weekly"
              | any
              | null;
            /**
             * The pay rate for the candidate.
             */
            payRate?: string;
            /**
             * A custom ID for the candidate.
             *
             * @maxLength 256
             */
            customId?: string;
            /**
             * A company-defined identifier used to match the candidate with external records.
             *
             * @maxLength 256
             */
            _internalId?: string;
            /**
             * The date when the candidate's status was last changed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            statusChangeDate?: string;
            /**
             * Whether the candidate has opted out of communications.
             */
            optOut?: boolean;
            /**
             * The TRN number for the candidate.
             *
             * @maxLength 256
             */
            trnNumber?: string;
            /**
             * The date when the TRN number was last checked.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            trnNumberCheckedDate?: string;
            /**
             * Any notes related to the TRN number.
             *
             * @maxLength 1024
             */
            trnNumberNotes?: string;
            /**
             * The date when the candidate's employment is set to start.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            employmentStartDate?: string;
            /**
             * Whether the candidate has confirmed their Early Years Workforce status.
             */
            ewcConfirmation?: boolean;
            /**
             * The share code for the candidate's Home Office application.
             *
             * @maxLength 256
             */
            homeOfficeShareCode?: string;
            /**
             * Whether the candidate has been archived.
             */
            archived?: boolean;
            /**
             * Whether the candidate is using an umbrella company.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            umbrella?: string;
            /**
             * ID of the PAYE umbrella company.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            payeUmbrella?: string;
            /**
             * The name of the umbrella company the candidate is using.
             *
             * @maxLength 256
             */
            umbrellaCompanyName?: string;
            /**
             * The code of the umbrella company the candidate is using.
             *
             * @maxLength 256
             */
            umbrellaCompanyCode?: string;
            /**
             * The transport method for the candidate.
             */
            transport?:
              | "Drives Own Vehicle"
              | "Use Public Transport"
              | "Access to Another Vehicle"
              | "Bicycle"
              | "No Access"
              | "Other";
            /**
             * Any important notes related to the candidate.
             *
             * @maxLength 1024
             */
            importantNotes?: string;
            /**
             * The date when the candidate was last updated.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            candidateLastUpdated?: string;
            /**
             * The source from which the candidate was obtained.
             */
            source?:
              | (
                  | "Company Website"
                  | "iCan Teach UK Website"
                  | "Apply to Education"
                  | "CV Library - CV Search"
                  | "CV Library Advert"
                  | "CV Library - Watchdog"
                  | "Guardian- CV Search"
                  | "Guardian Advert"
                  | "Indeed - CV Search"
                  | "Indeed Advert"
                  | "Reed - CV Search"
                  | "Reed Advert"
                  | "Reed \u2013 Watchdog"
                  | "SENploy - Advert"
                  | "Total Jobs"
                  | "Other Job Board"
                  | "Google"
                  | "LinkedIn"
                  | "Linkedin Recruiter"
                  | "LinkedIn Advert"
                  | "Referral - School"
                  | "Referral - Candidate"
                  | "UK University"
                  | "Irish University"
                  | "Canadian University"
                  | "Marketing Campaign"
                  | "Woo"
                  | "Job Show"
                  | "Recruitment Fair"
                  | "Career Fair"
                  | "Volcanic"
                  | "Broadbean"
                  | "Headhunt"
                  | "Referral"
                  | "Direct"
                  | "Other"
                )
              | (
                  | "Company Website"
                  | "Direct Call"
                  | "CV Library Watchdog"
                  | "CV Library Advert"
                  | "CV Library CV Search"
                  | "Reed Watchdog"
                  | "Reed Advert"
                  | "Reed CV Search"
                  | "Indeed Watchdog"
                  | "Indeed Advert"
                  | "Indeed CV Search"
                  | "Job Centre"
                  | "Returning Candidate"
                  | "Referral Candidate"
                  | "Referral Client"
                  | "Training Provider"
                  | "Recruitment Fair"
                  | "University/College"
                );
            /**
             * Any other source from which the candidate was obtained.
             *
             * @maxLength 256
             */
            sourceOther?: string;
            /**
             * Whether the candidate has given consent for online checks.
             */
            onlineCheckConsent?: boolean;
            /**
             * The title of the candidate.
             */
            title?:
              | "Mr"
              | "Mrs"
              | "Ms"
              | "Miss"
              | "Dr"
              | "Mx"
              | "Lady"
              | "Lord"
              | "Other";
            /**
             * The pronoun used by the candidate.
             */
            pronoun?:
              | "She/Her/Hers"
              | "He/Him/His"
              | "They/Them/Theirs"
              | "Ze/Hir/Hirs"
              | "Ze/Zir/Zirs"
              | "Other"
              | "Prefer not to say";
            /**
             * The date when the candidate was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
            /**
             * The date when the candidate was last contacted.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            lastContactedDate?: string;
            /**
             * A short, prominent note shown with the candidate to communicate important context.
             */
            highlight?: string;
            /**
             * The pay scale for the candidate's teaching position.
             *
             * @maxLength 256
             */
            teacherPayScale?: string;
            /**
             * Whether the candidate has a P45.
             */
            p45?: boolean;
            /**
             * The type of role the candidate is applying for.
             */
            roleType?:
              | "Senior Leadership Positions"
              | "Teaching Roles"
              | "School Support Positions";
            /**
             * The Right to Work verification status for the candidate.
             *
             * @maxLength 256
             */
            rtwVerification?: string;
            /**
             * Any notes related to the Right to Work verification status for the candidate.
             *
             * @maxLength 1024
             */
            rtwVerificationNote?: string;
            /**
             * The Right to Work verification status for the candidate.
             *
             * @maxLength 256
             */
            rtwVerificationStatus?: string;
            /**
             * The location where the candidate desires to work.
             *
             * @maxLength 256
             */
            desiredLocation?: string;
            /**
             * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
             */
            complianceUser?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
             */
            watchers?:
              | string[]
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                }[];
            /**
             * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
             */
            tags?:
              | string[]
              | {
                  /**
                   * The unique identifier of the tag
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The label displayed wherever this tag is applied.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  name?: string;
                  /**
                   * The tag display colour as a hexadecimal value, for example #2563EB.
                   *
                   * @maxLength 32
                   */
                  color?: string | null;
                }[];
            /**
             * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
             *
             * @maxItems 5
             */
            documents?: {
              /**
               * The unique identifier of the document
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The name of the document
               *
               * @maxLength 512
               */
              name?: string;
              /**
               * The file reference used to retrieve the document
               *
               * @maxLength 2048
               */
              reference?: string;
              /**
               * The file's media type
               *
               * @maxLength 256
               */
              fileType?: string;
              /**
               * The area of the platform the document belongs to
               *
               * @maxLength 256
               */
              type?: string;
              /**
               * The configured category of the document
               *
               * @maxLength 256
               */
              docType?: string;
              /**
               * The document's expiry date, when applicable
               *
               * @maxLength 64
               */
              expiry?: string;
              /**
               * The document's issued date, when applicable
               *
               * @maxLength 64
               */
              issued?: string;
              /**
               * Whether the document has been archived
               */
              documentArchived?: boolean;
              /**
               * The date the document was uploaded
               *
               * @format date-time
               */
              createdAt?: string;
            }[];
            /**
             * The total number of documents attached to the candidate.
             *
             * @minimum 0
             */
            documentsCount?: number;
            /**
             * The locations where the candidate desires to work.
             */
            desiredLocationMultiple?: string[];
            /**
             * Daily availability for the requested period. Returned by the availability view only.
             *
             * @maxItems 31
             */
            availabilityDays?: {
              /**
               * Calendar date in YYYY-MM-DD format.
               *
               * @maxLength 10
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              date?: string;
              /**
               * Availability or booking state for this date.
               */
              status?:
                | "available"
                | "unavailable"
                | "booked"
                | "halfDay"
                | "awaiting"
                | "unknown";
              /**
               * Recorded availability, or null when unrecorded.
               */
              available?: boolean | null;
              /**
               * Morning or afternoon availability.
               */
              partial?: "AM" | "PM" | any | null;
              /**
               * Additional availability information.
               *
               * @maxLength 4096
               */
              note?: string | null;
              /**
               * Active bookings on this date.
               *
               * @maxItems 100
               */
              bookings?: {
                /**
                 * The unique identifier of the resource
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * @maxLength 512
                 */
                clientName?: string;
                /**
                 * @maxLength 512
                 */
                role?: string;
                /**
                 * @maxLength 32
                 */
                timeDesignation?: string;
              }[];
            }[];
            /**
             * The date when the candidate's availability was last checked.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            availabilityLastChecked?: string;
            /**
             * Whether the candidate is subject to notice period.
             */
            subjectToNotice?: "Unsure" | "Yes" | "No";
            /**
             * The subsidiary subjects the candidate is qualified to teach.
             */
            subsidiarySubject?: string[];
            /**
             * The candidate's preferred employment type.
             */
            employmentPreference?: (
              | "Day To Day"
              | "Long Term"
              | "Fixed Term"
              | "Perm"
            )[];
            /**
             * Any other names the candidate is known by.
             *
             * @maxLength 256
             */
            otherNames?: string;
            /**
             * The name of the candidate's limited company, if applicable.
             *
             * @maxLength 256
             */
            ltdCompany?: string;
            /**
             * Whether the candidate's employment has been verified.
             */
            employmentVerified?: boolean;
            /**
             * The DBS verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSVerification?: string;
            /**
             * Any notes related to the DBS verification status for the candidate.
             *
             * @maxLength 1024
             */
            candidateDBSVerificationNote?: string;
            /**
             * The DBS verification status for the candidate.
             */
            candidateDBSVerificationStatus?: "Cleared" | "Alerts";
            /**
             * The DBS check verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSCheckVerification?: string;
            /**
             * Any notes related to the DBS check verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSCheckVerificationNote?: string;
            /**
             * The DBS check verification status for the candidate.
             */
            candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
            /**
             * Any notes related to the DBS update service verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSUpdateServiceVerificationNote?: string;
            /**
             * The DBS update service verification status for the candidate.
             */
            candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
            /**
             * Any notes related to the TRN verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateTRNVerificationNote?: string;
            /**
             * The TRN verification status for the candidate.
             */
            candidateTRNVerificationStatus?:
              | "Completed Induction"
              | "Not Yet Completed Induction"
              | "Induction Exempt"
              | "ALERTS - Failed Induction"
              | "Induction Not Required";
            /**
             * The health and safety verification status for the candidate.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            candidateHealthSafetyVerification?: string;
            /**
             * Any notes related to the health and safety verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateHealthSafetyVerificationNote?: string;
            /**
             * The health and safety verification status for the candidate.
             */
            candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
            /**
             * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
             */
            onboardingSession?:
              | string
              | {
                  /**
                   * Unique identifier for the onboarding session
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The URL for the onboarding session
                   *
                   * @format uri
                   */
                  url?: string;
                  /**
                   * The frozen health declaration configuration for this onboarding session.
                   */
                  healthDeclarationConfig?: {
                    /**
                     * The ordered questions included in the question set.
                     *
                     * @maxItems 100
                     */
                    questions?: {
                      /**
                       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * A stable key used to associate the question with current and historical answers.
                       *
                       * @minLength 1
                       * @maxLength 128
                       */
                      key?: string;
                      /**
                       * The question shown to the person completing the form.
                       *
                       * @minLength 1
                       * @maxLength 500
                       */
                      label?: string;
                      /**
                       * Optional supporting text displayed with the question.
                       *
                       * @maxLength 2000
                       */
                      description?: string;
                      /**
                       * The type of answer collected for the question.
                       */
                      type?:
                        | "checkbox"
                        | "date"
                        | "daterange"
                        | "dropdown"
                        | "number"
                        | "signature"
                        | "text"
                        | "textarea";
                      /**
                       * Whether an answer is required while the question is visible.
                       */
                      required?: boolean;
                      /**
                       * Whether the question is included in new forms. Historical responses retain disabled questions.
                       */
                      enabled?: boolean;
                      /**
                       * The display order of the question.
                       *
                       * @minimum 0
                       * @maximum 1000
                       */
                      order?: number;
                      /**
                       * Optional rules that determine when the question is shown.
                       */
                      condition?: {
                        /**
                         * Whether any rule or every rule must match before the question is shown.
                         */
                        mode?: "any" | "all";
                        /**
                         * The comparisons used to determine question visibility.
                         *
                         * @minItems 1
                         * @maxItems 20
                         */
                        rules?: {
                          /**
                           * The stable key of the question that controls visibility.
                           *
                           * @minLength 1
                           * @maxLength 128
                           */
                          questionKey?: string;
                          /**
                           * How the controlling answer is compared with the value.
                           */
                          operator?: "equals" | "notEquals";
                          /**
                           * The answer value used to determine visibility.
                           */
                          value?: string | boolean | number | (void | null);
                        }[];
                      };
                      /**
                       * The available answers for a dropdown question.
                       *
                       * @minItems 1
                       * @maxItems 100
                       */
                      options?: {
                        /**
                         * The value stored when this option is selected.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        value?: string;
                        /**
                         * The option displayed to the person answering the question.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        label?: string;
                      }[];
                      /**
                       * Whether this question belongs to the standard template.
                       */
                      ["default"]?: boolean;
                      /**
                       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                       */
                      audience?: "candidate" | "staff";
                      /**
                       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                       */
                      fixedRole?: "fullName" | "signature";
                    }[];
                    /**
                     * When this question configuration was last updated.
                     *
                     * @format date-time
                     */
                    updatedAt?: string;
                  };
                  /**
                   * The frozen Criminal Record & Security Checks configuration for this onboarding session.
                   */
                  criminalRecordConfig?: {
                    /**
                     * The ordered questions included in the question set.
                     *
                     * @maxItems 100
                     */
                    questions?: {
                      /**
                       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * A stable key used to associate the question with current and historical answers.
                       *
                       * @minLength 1
                       * @maxLength 128
                       */
                      key?: string;
                      /**
                       * The question shown to the person completing the form.
                       *
                       * @minLength 1
                       * @maxLength 500
                       */
                      label?: string;
                      /**
                       * Optional supporting text displayed with the question.
                       *
                       * @maxLength 2000
                       */
                      description?: string;
                      /**
                       * The type of answer collected for the question.
                       */
                      type?:
                        | "checkbox"
                        | "date"
                        | "daterange"
                        | "dropdown"
                        | "number"
                        | "signature"
                        | "text"
                        | "textarea";
                      /**
                       * Whether an answer is required while the question is visible.
                       */
                      required?: boolean;
                      /**
                       * Whether the question is included in new forms. Historical responses retain disabled questions.
                       */
                      enabled?: boolean;
                      /**
                       * The display order of the question.
                       *
                       * @minimum 0
                       * @maximum 1000
                       */
                      order?: number;
                      /**
                       * Optional rules that determine when the question is shown.
                       */
                      condition?: {
                        /**
                         * Whether any rule or every rule must match before the question is shown.
                         */
                        mode?: "any" | "all";
                        /**
                         * The comparisons used to determine question visibility.
                         *
                         * @minItems 1
                         * @maxItems 20
                         */
                        rules?: {
                          /**
                           * The stable key of the question that controls visibility.
                           *
                           * @minLength 1
                           * @maxLength 128
                           */
                          questionKey?: string;
                          /**
                           * How the controlling answer is compared with the value.
                           */
                          operator?: "equals" | "notEquals";
                          /**
                           * The answer value used to determine visibility.
                           */
                          value?: string | boolean | number | (void | null);
                        }[];
                      };
                      /**
                       * The available answers for a dropdown question.
                       *
                       * @minItems 1
                       * @maxItems 100
                       */
                      options?: {
                        /**
                         * The value stored when this option is selected.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        value?: string;
                        /**
                         * The option displayed to the person answering the question.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        label?: string;
                      }[];
                      /**
                       * Whether this question belongs to the standard template.
                       */
                      ["default"]?: boolean;
                      /**
                       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                       */
                      audience?: "candidate" | "staff";
                      /**
                       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                       */
                      fixedRole?: "fullName" | "signature";
                    }[];
                    /**
                     * When this question configuration was last updated.
                     *
                     * @format date-time
                     */
                    updatedAt?: string;
                  };
                };
            /**
             * The contact record for this candidate. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * Whether the candidate has opted out of 48 hour communications.
             */
            optOut48Hours?: boolean;
            /**
             * Whether the candidate has consented to REC audit checks.
             */
            recAuditConsent?: boolean | null;
            /**
             * The date the candidate's REC audit consent was recorded.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            recAuditConsentDate?: string;
            /**
             * The method through which REC audit consent was captured.
             *
             * @maxLength 256
             */
            recAuditConsentMethod?: string;
            /**
             * The date when the candidate record was created.
             *
             * @format date-time
             */
            createdAt?: string;
            /**
             * The mobile app link status for the candidate.
             */
            candidateMobileAppStatus?:
              | "Unlinked"
              | "Scheduled Unlink"
              | "Linked"
              | "Invited"
              | "Not Invited";
            /**
             * Evertime details for this candidate
             */
            evertime?: {
              /**
               * The Evertime CandidateId used for this candidate when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
              /**
               * Whether the matched Evertime candidate's NI number also matches the one held in Edun
               */
              niNumberMatch?: boolean;
            };
            /**
             * Search relevance relative to the strongest result, where 100 is the strongest match.
             *
             * @minimum 0
             * @maximum 100
             */
            relevanceScore?: number;
            /**
             * Candidate fields or attached document excerpts that explain why the candidate matched.
             *
             * @maxItems 5
             */
            matchReasons?: {
              /**
               * Whether the matching information came from the candidate record or an attached document.
               */
              source?: "candidate" | "document";
              /**
               * The candidate or document information that matched the search.
               *
               * @maxLength 256
               */
              field?: string;
              /**
               * A short excerpt showing why the result matched.
               *
               * @maxLength 1000
               */
              text?: string;
              /**
               * The exact terms within the content that matched the search, including synonym matches.
               *
               * @maxItems 25
               */
              matchedTerms?: string[];
              /**
               * The name of the attached document when the match came from a document.
               *
               * @maxLength 512
               */
              documentName?: string | null;
              /**
               * The unique identifier of the attached document when the match came from a document.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              documentId?: string | null;
            }[];
            /**
             * The candidate's distance in miles from the requested location. Present only when location filtering is used.
             *
             * @minimum 0
             */
            distance?: number;
            /**
             * Timesheet Portal details for this candidate
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal contractor GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal contractor code
               *
               * @maxLength 256
               */
              code?: string;
            };
            /**
             * Invite to candidate to link their mobile app.
             */
            mobileAppLinkInvite?: {
              /**
               * Boolean status of whether the link invite is being used
               *
               * @default false
               */
              isUsed?: boolean;
              /**
               * Creation date of the invite
               *
               * @format date-time
               * @default 2026-10-30T13:39:09.027Z
               */
              createdAt?: string;
              /**
               * ID of candidate using this link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              usedBy?: string;
              /**
               * Date of when link used
               *
               * @format date-time
               */
              usedAt?: string;
              /**
               * If unlinked, ID of user that unlinked this candidate's link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              unlinkedBy?: string;
              /**
               * If unlinked, date of when this link was unlinked
               *
               * @format date-time
               */
              unlinkedAt?: string;
              /**
               * If deleted, ID of user that deleted this candidate's link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              deletedBy?: string;
              /**
               * If deleted, date of when this link was deleted
               *
               * @format date-time
               */
              deletedAt?: string;
              /**
               * Current status of this mobile app link
               */
              status?:
                | "Sent"
                | "Linked"
                | "Unlinked"
                | "ScheduledUnlink"
                | "Not Sent"
                | "Rescinded";
            };
          };
      /**
       * The current status of the candidate
       */
      status?: "Pending" | "Interested" | "Not Interested";
      /**
       * The client's status for the candidate
       */
      clientStatus?: "Pending" | "Interested" | "Not Interested";
      /**
       * The date when the client expressed interest in the candidate
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      clientInterestSentAt?: string | null;
      /**
       * Indicates if this is the default document for the candidate
       */
      defaultDocument?: boolean;
      /**
       * The document ID associated with the candidate
       *
       * @pattern ^[0-9a-f]{24}$
       */
      document?: string;
      /**
       * The name of the document associated with the candidate
       */
      documentName?: string;
      /**
       * The reason for the candidate's status
       */
      reason?: string;
    }[];
    /**
     * The client associated with the vacancy, if expanded client schema else client id
     */
    client?:
      | string
      | {
          /**
           * The unique identifier of the client
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The name of the client
           *
           * @maxLength 256
           */
          name?: string;
          /**
           * The address of the client
           */
          address?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The telephone number of the client
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The email address of the client
           *
           * @format email
           */
          email?: string;
          /**
           * Array of client contacts
           */
          contacts?: {
            /**
             * The unique identifier of the contact
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The forename of the contact
             *
             * @maxLength 128
             */
            forename?: string;
            /**
             * The surname of the contact
             *
             * @maxLength 128
             */
            surname?: string;
            /**
             * The email address of the contact
             *
             * @format email
             */
            email?: string;
            /**
             * The telephone number of the contact
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * The mobile number of the contact
             *
             * @maxLength 32
             */
            mobile?: string;
            /**
             * The extension number of the contact
             *
             * @maxLength 8
             */
            extension?: string;
            /**
             * The role of the contact
             */
            role?:
              | "Head Teacher"
              | "Deputy Head"
              | "Cover Manager"
              | "HR Manager"
              | "Finance Manager"
              | "Head of Maths"
              | "Head of Science"
              | "Head of English"
              | "Assistant Headteacher"
              | "Headteacher's PA"
              | "SENCO"
              | "Other"
              | "Business Manager"
              | "Business Administrator"
              | "Invoice Contact"
              | "Academy Recruitment Contact"
              | "Nursery Manager"
              | "CEO"
              | "COO"
              | "Executive Headteacher"
              | "Inclusion Manager"
              | "EYFS Lead"
              | "Admin Team"
              | "Head of Humanities"
              | "Head of History"
              | "Head of Geography"
              | "Head of Business Studies"
              | "Head of PE"
              | "Head of KS1"
              | "Head of KS2"
              | "Head of KS3"
              | "Head of KS4"
              | "Virtual School Headteacher"
              | "Deputy Virtual School Headteacher"
              | "Post-16 Team Leader"
              | "SEN Team"
              | "Inclusion Team"
              | "UASC Team"
              | "Caseworker"
              | "Designated Teacher"
              | "Attendance Officer"
              | "Coordinator"
              | "Children's Commissioner"
              | "Timesheet Contact";
            /**
             * The name of the role if the role is 'Other'
             *
             * @maxLength 128
             */
            roleOther?: string;
            /**
             * The unique identifier of the contact
             *
             * @format uuid
             */
            contactId?: string;
            /**
             * Timesheet Portal details for this contact
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal approver GUID linked to this client contact
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal approver code
               *
               * @maxLength 256
               */
              code?: string;
            };
            /**
             * Whether the contact has opted out of communications
             */
            optOut?: boolean;
            /**
             * Any additional notes about the contact
             *
             * @maxLength 175
             */
            note?: string;
            /**
             * Whether the contact is a decision maker
             */
            decisionMaker?: boolean;
            /**
             * The status of the contact
             */
            status?: "Active" | "Passive" | "Inactive";
            /**
             * The Client associated with the contact.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            clientId?: string;
            /**
             * The name of the Client associated with the contact.
             *
             * @maxLength 256
             */
            clientName?: string;
            /**
             * The address of the Client associated with the contact.
             */
            clientAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The local education authority of the associated Client.
             *
             * @maxLength 128
             */
            clientLea?: string;
            /**
             * The sector of the associated Client.
             *
             * @maxLength 128
             */
            clientType?: string;
            /**
             * The multi-academy trust of the associated Client.
             *
             * @maxLength 256
             */
            clientMultiAcademyTrust?: string;
            /**
             * The borough of the associated Client.
             *
             * @maxLength 128
             */
            clientBorough?: string;
            /**
             * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
             */
            clientAssignee?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * The specialism of the associated Client.
             *
             * @maxLength 256
             */
            clientSpecialism?: string;
            /**
             * When the associated Client was last updated.
             *
             * @format date-time
             */
            clientUpdatedAt?: string;
            /**
             * The associated Client's distance in miles from the requested location.
             *
             * @minimum 0
             */
            distance?: number;
            /**
             * Search relevance relative to the strongest result, where 100 is the strongest match.
             *
             * @minimum 0
             * @maximum 100
             */
            relevanceScore?: number;
            /**
             * Contact and Client fields that explain why the contact matched.
             *
             * @maxItems 5
             */
            matchReasons?: {
              /**
               * The contact or Client information that matched the search.
               *
               * @maxLength 256
               */
              field?: string;
              /**
               * A short excerpt showing why the contact matched.
               *
               * @maxLength 1000
               */
              text?: string;
              /**
               * The exact terms within the content that matched the search.
               *
               * @maxItems 25
               */
              matchedTerms?: string[];
            }[];
          }[];
          /**
           * The borough of the client
           *
           * @maxLength 128
           */
          borough?: string;
          /**
           * The local education authority of the client
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * The type of the client
           *
           * @maxLength 128
           */
          type?: string;
          /**
           * The multi academy trust that the client belongs to
           *
           * @maxLength 256
           */
          multiAcademyTrust?: string;
          /**
           * The color (HEX format prefixed with #) associated with the client
           *
           * @pattern ^#[0-9a-fA-F]{6}$
           */
          color?: string;
          /**
           * A custom ID for the client.
           *
           * @maxLength 256
           */
          customId?: string;
          /**
           * A company-defined identifier used to match the client with external records.
           *
           * @maxLength 256
           */
          internalId?: string;
          /**
           * The user assigned to the client. If expanded, a user object else the user ID.
           */
          assignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * Tags associated with this client. If expanded, an array of tag objects; otherwise, an array of tag IDs.
           */
          tags?:
            | string[]
            | {
                /**
                 * The unique identifier of the tag
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The label displayed wherever this tag is applied.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                name?: string;
                /**
                 * The tag display colour as a hexadecimal value, for example #2563EB.
                 *
                 * @maxLength 32
                 */
                color?: string | null;
              }[];
          /**
           * The date when the TOB was sent
           *
           * @format date-time
           */
          tobSentAt?: string;
          /**
           * The website of the client
           *
           * @format uri
           */
          website?: string;
          /**
           * Whether the client is archived
           */
          archived?: boolean;
          /**
           * The specialism of the client
           *
           * @maxLength 256
           */
          specialism?: string;
          /**
           * The current booking IDs associated with the client
           */
          currentBooking?: string[];
          /**
           * The number of active bookings associated with the client, constrained by the requested booking date range when present.
           *
           * @minimum 0
           */
          bookingCount?: number;
          /**
           * Any important notes related to the client
           *
           * @maxLength 1024
           */
          importantNotes?: string;
          /**
           * The booking roles associated with the client
           */
          roles?: {
            /**
             * The occupation of the individual in the booking role
             *
             * @maxLength 256
             */
            occupation?: string;
            /**
             * The pay rate for the booking role
             *
             * @maxLength 256
             */
            payRate?: string;
            /**
             * The charge rate for the booking role
             *
             * @maxLength 256
             */
            chargeRate?: string;
            /**
             * The type of pay for the booking role
             */
            payType?: "hourly" | "daily" | any | null;
            /**
             * The length of the day for the booking role
             */
            dayLength?: "full" | "half" | any | null;
            /**
             * The AM/PM designation for the day length
             */
            dayLengthAmPm?: "AM" | "PM" | any | null;
          }[];
          /**
           * The start time for the client
           */
          startTime?: string;
          /**
           * The end time for the client
           */
          endTime?: string;
          /**
           * Any additional instructions for the client
           *
           * @maxLength 1024
           */
          additionalInstructions?: string;
          /**
           * Any additional instructions specific to the client
           */
          additionalInstructionsClient?: string;
          /**
           * Tag highlight colour (HEX format prefixed with #)
           *
           * @pattern ^#[0-9a-fA-F]{6}$
           */
          highlight?: string;
          /**
           * The area of the client
           */
          area?: string;
          /**
           * The governance type of the client
           */
          governance?:
            | "Academy"
            | "Childrens Centre"
            | "College"
            | "Independant"
            | "LEA"
            | "University";
          /**
           * The MAT HQ details of the client
           */
          mathq?: "Unsure" | "Yes" | "No";
          /**
           * The Ofsted rating of the client
           */
          ofstedRating?:
            | "Outstanding"
            | "Good"
            | "Requires Improvement"
            | "Inadequate"
            | "NA - Independent"
            | "NA - Academy";
          /**
           * The date when the Ofsted report was checked
           *
           * @format date-time
           */
          dateOfstedReportChecked?: string | null;
          /**
           * The boarding details of the client
           */
          boarding?: "Unsure" | "Yes" | "No" | any | null;
          /**
           * The licence to issue sponsorship for the client
           */
          licenceToIssueSponsorship?: "Unsure" | "Yes" | "No" | any | null;
          /**
           * The total agency spend for the client
           */
          agencySpendTotal?: number;
          /**
           * Whether the client is VAT exempt
           */
          vatExempt?: boolean;
          /**
           * Whether to split invoices for the client
           */
          splitInvoices?: boolean;
          /**
           * The switch board details for the client
           */
          switchBoard?: string;
          /**
           * Whether the client is a GCA client
           */
          ccsClient?: boolean;
          /**
           * The GCA client URN
           */
          ccsClientUrn?: string;
          /**
           * The GCA client Ofsted URN
           */
          ccsClientOfstedUrn?: string;
          /**
           * The GCA client lot number
           */
          ccsClientLotNumber?: string;
          /**
           * The buyer reference supplied by a GCA client, when available.
           *
           * @maxLength 255
           */
          buyerReference?: string | null;
          /**
           * The GCA client invoice date
           *
           * @format date-time
           */
          ccsClientInvoiceDate?: string;
          /**
           * The key stage for the client
           */
          keyStage?: string;
          /**
           * The contact record for this client. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date when the client was last contacted.
           *
           * @format date-time
           */
          lastContactedDate?: string;
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Client fields that explain why the client matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * The client information that matched the search.
             *
             * @maxLength 256
             */
            field?: string;
            /**
             * A short excerpt showing why the client matched.
             *
             * @maxLength 1000
             */
            text?: string;
            /**
             * The exact terms within the content that matched the search.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
          }[];
          /**
           * The Client's distance in miles from the requested location. Present only when location filtering is used.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * The migration that created this client
           *
           * @pattern ^[0-9a-f]{24}$
           */
          migrationId?: string;
          /**
           * Whether this migrated client has been approved
           */
          migrationApproval?: "pending" | "approved" | "rejected";
          /**
           * Evertime details for this client
           */
          evertime?: {
            /**
             * The Evertime ClientId used for this client when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
            /**
             * The Evertime contacts to create from Edun (clientContactId set) or reference (clientContactId unset) when exporting payment runs
             */
            contacts?: {
              /**
               * The Evertime ClientContactId
               *
               * @maxLength 256
               */
              evertimeId?: string;
              /**
               * The Edun client contact this Evertime contact is created from; absent when the Evertime contact is only referenced (never created or overwritten)
               *
               * @maxLength 256
               */
              clientContactId?: string;
            }[];
            /**
             * The Evertime locations to create from the client's address (create set) or reference (create unset) when exporting payment runs
             */
            locations?: {
              /**
               * The Evertime LocationId
               *
               * @maxLength 256
               */
              evertimeId?: string;
              /**
               * True when this location is created from the client's address; false when the Evertime location is only referenced (never created or overwritten)
               */
              create?: boolean;
            }[];
            /**
             * Whether the matched Evertime client's name also matches the one held in Edun
             */
            nameMatch?: boolean;
          };
          /**
           * Timesheet Portal details for this client
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal client GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal client code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The address of the vacancy associated with the vacancy
     */
    clientAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The student associated with the vacancy
     *
     * @pattern ^[0-9a-f]{24}$
     */
    studentId?: string;
    /**
     * The postcode of the vacancy location
     *
     * @maxLength 10
     */
    postcode?: string;
    /**
     * The name of the role the vacancy is for
     *
     * @maxLength 256
     */
    role?: string;
    /**
     * The code associated with the role
     *
     * @maxLength 256
     */
    roleCode?: string;
    /**
     * Start date and time for the vacancy
     *
     * @format date-time
     */
    start?: string;
    /**
     * End date and time for the vacancy
     *
     * @format date-time
     */
    end?: string;
    /**
     * Start time for the vacancy
     */
    startTime?: string;
    /**
     * End time for the vacancy
     */
    endTime?: string;
    /**
     * The individual working days included in the vacancy.
     */
    days?: {
      /**
       * The start date and time for the booking day
       */
      start?: string;
      /**
       * The end date and time for the booking day
       */
      end?: string;
      /**
       * The length of the booking day
       */
      length?: "Half Day" | "Full Day";
      /**
       * Indicates whether the booking day is in the AM or PM
       */
      amPm?: "AM" | "PM";
      /**
       * The current status of the booking day
       */
      status?:
        | "Active"
        | "Sickness"
        | "NoShow"
        | "Client Cancelled"
        | "Act Of God"
        | "Personal Appointment";
      /**
       * The charge rate for the booking day
       *
       * @maxLength 32
       */
      chargeRate?: string;
      /**
       * The pay rate for the booking day
       *
       * @maxLength 32
       */
      payRate?: string;
      /**
       * Break duration inherited for this booking day.
       */
      breaks?: string;
      /**
       * Expenses payable for this booking day.
       */
      expenses?: string;
      /**
       * Indicates whether the booking day is flagged for removal
       */
      flaggedForRemoval?: boolean;
    }[];
    /**
     * The rate charged to the client for this vacancy.
     *
     * @maxLength 32
     */
    chargeRate?: string;
    /**
     * The fee percentage for the vacancy
     *
     * @maxLength 8
     */
    feePercentage?: string;
    /**
     * The Agency Workers Regulations value recorded for the vacancy.
     *
     * @maxLength 8
     */
    awr?: string;
    /**
     * The reminder value used to monitor Agency Workers Regulations requirements.
     *
     * @maxLength 8
     */
    awrReminder?: string;
    /**
     * The expected number of working hours for the vacancy.
     *
     * @maxLength 16
     */
    hours?: string;
    /**
     * The pay rate for the vacancy
     *
     * @maxLength 32
     */
    payRate?: string;
    /**
     * The arrival time for the vacancy
     */
    arrivalTime?: string;
    /**
     * The contact person at the vacancy for the vacancy
     */
    clientContact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
          /**
           * The role of the contact
           */
          role?:
            | "Head Teacher"
            | "Deputy Head"
            | "Cover Manager"
            | "HR Manager"
            | "Finance Manager"
            | "Head of Maths"
            | "Head of Science"
            | "Head of English"
            | "Assistant Headteacher"
            | "Headteacher's PA"
            | "SENCO"
            | "Other"
            | "Business Manager"
            | "Business Administrator"
            | "Invoice Contact"
            | "Academy Recruitment Contact"
            | "Nursery Manager"
            | "CEO"
            | "COO"
            | "Executive Headteacher"
            | "Inclusion Manager"
            | "EYFS Lead"
            | "Admin Team"
            | "Head of Humanities"
            | "Head of History"
            | "Head of Geography"
            | "Head of Business Studies"
            | "Head of PE"
            | "Head of KS1"
            | "Head of KS2"
            | "Head of KS3"
            | "Head of KS4"
            | "Virtual School Headteacher"
            | "Deputy Virtual School Headteacher"
            | "Post-16 Team Leader"
            | "SEN Team"
            | "Inclusion Team"
            | "UASC Team"
            | "Caseworker"
            | "Designated Teacher"
            | "Attendance Officer"
            | "Coordinator"
            | "Children's Commissioner"
            | "Timesheet Contact";
          /**
           * The name of the role if the role is 'Other'
           *
           * @maxLength 128
           */
          roleOther?: string;
          /**
           * The unique identifier of the contact
           *
           * @format uuid
           */
          contactId?: string;
          /**
           * Timesheet Portal details for this contact
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal approver GUID linked to this client contact
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal approver code
             *
             * @maxLength 256
             */
            code?: string;
          };
          /**
           * Whether the contact has opted out of communications
           */
          optOut?: boolean;
          /**
           * Any additional notes about the contact
           *
           * @maxLength 175
           */
          note?: string;
          /**
           * Whether the contact is a decision maker
           */
          decisionMaker?: boolean;
          /**
           * The status of the contact
           */
          status?: "Active" | "Passive" | "Inactive";
          /**
           * The Client associated with the contact.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          clientId?: string;
          /**
           * The name of the Client associated with the contact.
           *
           * @maxLength 256
           */
          clientName?: string;
          /**
           * The address of the Client associated with the contact.
           */
          clientAddress?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The local education authority of the associated Client.
           *
           * @maxLength 128
           */
          clientLea?: string;
          /**
           * The sector of the associated Client.
           *
           * @maxLength 128
           */
          clientType?: string;
          /**
           * The multi-academy trust of the associated Client.
           *
           * @maxLength 256
           */
          clientMultiAcademyTrust?: string;
          /**
           * The borough of the associated Client.
           *
           * @maxLength 128
           */
          clientBorough?: string;
          /**
           * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
           */
          clientAssignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * The specialism of the associated Client.
           *
           * @maxLength 256
           */
          clientSpecialism?: string;
          /**
           * When the associated Client was last updated.
           *
           * @format date-time
           */
          clientUpdatedAt?: string;
          /**
           * The associated Client's distance in miles from the requested location.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Contact and Client fields that explain why the contact matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * The contact or Client information that matched the search.
             *
             * @maxLength 256
             */
            field?: string;
            /**
             * A short excerpt showing why the contact matched.
             *
             * @maxLength 1000
             */
            text?: string;
            /**
             * The exact terms within the content that matched the search.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
          }[];
        };
    /**
     * Any additional instructions for the vacancy
     *
     * @maxLength 1024
     */
    additionalInstructions?: string;
    /**
     * Any additional instructions specific to the vacancy
     *
     * @maxLength 1024
     */
    additionalInstructionsClient?: string;
    /**
     * The current status of the vacancy
     */
    status?: "active" | "cancelled";
    /**
     * The user assigned to the vacancy. If expanded, a user object else the user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The pay type for the vacancy
     */
    payType?: "daily" | "hourly";
    /**
     * The type of the vacancy
     */
    type?: "standard" | "interview" | "trial" | "permanent" | "tutor";
    /**
     * The sub-type of the vacancy
     */
    subType?:
      | "tutor-standard"
      | "tutor-mentor"
      | "tutor-basic"
      | "face-to-face";
    /**
     * The payment arrangement expected for the candidate.
     */
    candidatePayType?: "PAYE" | "Umbrella" | "Limited Company";
    /**
     * The client name displayed with the vacancy.
     *
     * @maxLength 256
     */
    clientName?: string;
    /**
     * A description of the role, responsibilities, and requirements.
     *
     * @maxLength 2048
     */
    jobDescription?: string;
    /**
     * Whether the salary is expressed as an annual or daily amount.
     */
    salaryPayType?: "annual" | "daily";
    /**
     * The salary amount advertised for a permanent vacancy.
     *
     * @maxLength 32
     */
    salary?: string;
    /**
     * The weekly rota for the vacancy
     */
    weeklyRota?: {
      /**
       * The rota for Monday
       */
      ["1"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Tuesday
       */
      ["2"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Wednesday
       */
      ["3"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Thursday
       */
      ["4"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Friday
       */
      ["5"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Saturday
       */
      ["6"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Sunday
       */
      ["7"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
    };
    /**
     * The calculated distance to the vacancy when a location-based request supplies a reference point.
     */
    distance?: number;
    /**
     * Whether to include the school name in the vacancy details
     */
    includeSchool?: boolean;
    /**
     * Vacant bookings can be completed to multiple candidates at once. Specify the total number of available vacancies for this booking. This value is only visible within Edun, not visible externally.
     *  If left empty, 1 available vacancy for this placement will be presumed.
     *
     * @maxLength 256
     */
    noVacancies?: string;
    /**
     * Whether the vacancy should be shared to the job board
     */
    jobBoard?: boolean;
    /**
     * Notes about the client's interest in candidates for this vacancy.
     *
     * @maxLength 1024
     */
    clientInterestComments?: string;
    /**
     * Whether the required health and safety check has been recorded as complete.
     */
    healthSafetyCheck?: boolean;
    /**
     * The client contact associated with the health and safety check.
     *
     * @maxLength 256
     */
    healthSafetyCheckContact?: string;
    /**
     * The purchase order number for the vacancy
     */
    poNumber?: string;
    /**
     * The identifier assigned when the vacancy is published to CV-Library.
     */
    cvLibraryId?: number;
    /**
     * Whether the vacancy is archived
     */
    archived?: boolean;
    /**
     * The reason the vacancy was archived.
     *
     * @maxLength 1024
     */
    archiveReason?: string;
    /**
     * Whether the vacancy is completed
     */
    completed?: boolean;
    /**
     * The occupation for the vacancy
     *
     * @maxLength 256
     */
    occupation?: string;
    /**
     * The date the vacancy was posted
     *
     * @format date-time
     */
    postedOnDate?: string | null;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetVacanciesVariables = {
  queryParams?: GetVacanciesQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetVacancies = (
  variables: GetVacanciesVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetVacanciesResponse,
    GetVacanciesError,
    undefined,
    {},
    GetVacanciesQueryParams,
    {}
  >({ url: "/vacancies", method: "get", ...variables, signal });

export function getVacanciesQuery(variables: GetVacanciesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetVacanciesResponse>;
};

export function getVacanciesQuery(
  variables: GetVacanciesVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetVacanciesResponse>)
    | reactQuery.SkipToken;
};

export function getVacanciesQuery(
  variables: GetVacanciesVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/vacancies",
      operationId: "getVacancies",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetVacancies(variables, signal),
  };
}

export const useSuspenseGetVacancies = <TData = GetVacanciesResponse,>(
  variables: GetVacanciesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetVacanciesResponse, GetVacanciesError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetVacanciesResponse,
    GetVacanciesError,
    TData
  >({
    ...getVacanciesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetVacancies = <TData = GetVacanciesResponse,>(
  variables: GetVacanciesVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetVacanciesResponse, GetVacanciesError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetVacanciesResponse, GetVacanciesError, TData>({
    ...getVacanciesQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateVacancyError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 501;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not implemented
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateVacancyResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the vacancy
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * List of candidates associated with the vacancy
     */
    candidates?: {
      /**
       * The candidate applying for the vacancy, either by ID or full details depending on select query
       */
      candidate?:
        | string
        | {
            /**
             * The unique identifier of the candidate
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The forename of the candidate
             *
             * @minLength 1
             * @maxLength 256
             */
            forename?: string;
            /**
             * The surname of the candidate
             *
             * @minLength 1
             * @maxLength 256
             */
            surname?: string;
            /**
             * Any previous names of the candidate
             *
             * @maxLength 256
             */
            previousNames?: string;
            /**
             * The email of the candidate. This will be used for communication.
             *
             * @format email
             */
            email?: string;
            /**
             * The telephone number of the candidate
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * The mobile number of the candidate
             *
             * @maxLength 32
             */
            mobile?: string;
            /**
             * The occupation of the candidate
             */
            occupation?:
              | "Teacher"
              | "Cover Supervisor"
              | "Teaching Assistant"
              | "School Support Staff"
              | "Primary Teacher"
              | "Secondary Teacher"
              | "Head Teacher"
              | "Senior Leader"
              | "Tutor"
              | "Mentor"
              | "Learning Support Assistant"
              | "Finance Officer"
              | "HR Administrator"
              | "HR Manager"
              | "Office Manager"
              | "Administrator"
              | "Personal Assistant"
              | "Pastoral Manager"
              | "Receptionist"
              | "Instructor"
              | "Caretaker"
              | "Nursery Assistant"
              | "Nursery Nurse"
              | "Deputy Nursery Manager"
              | "Lunchtime Supervisor"
              | "Classroom Graduate"
              | "Primary Cover Supervisor"
              | "HLTA"
              | "Nurture Teacher"
              | "Nurture Mentor"
              | "SENDCo"
              | "Registered Manager"
              | "Regional Manager"
              | "Education Director"
              | "Residential Director"
              | "Deputy Manager"
              | "Residential Support Worker"
              | "Sports Coach"
              | "Executive Headteacher"
              | "General Practitioner"
              | "Advance Nurse Practitioner (ANP)"
              | "Emergency Nurse Practitioner (ENP)"
              | "Advanced Clinical Practitioner (ACP)"
              | "Emergency Clinical Practitioner (ECP)"
              | "Consultant Psychiatrist"
              | "Psychiatrist \u2013 SPR"
              | "IT Technician"
              | "Art Technician"
              | "DT Technician"
              | "Science Technician"
              | "Healthcare Assistant/ Residential Support Worker"
              | "Healthcare Assistant"
              | "Senior Healthcare Assistant"
              | "Team Leader"
              | "Registered General Nurse"
              | "Registered Mental Nurse"
              | "Registered Nurse Learning Disabilities"
              | "Overseas Trained Teacher";
            /**
             * The subject the candidate specializes in
             *
             * @maxLength 256
             */
            subject?: string;
            /**
             * The address of the resource
             */
            address?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The qualifications of the candidate
             *
             * @maxItems 32
             */
            qualifications?: {
              /**
               * The type of the qualification
               *
               * @maxLength 256
               */
              type?: string;
              /**
               * The place where the qualification was obtained
               *
               * @maxLength 256
               */
              place?: string;
              /**
               * The name of the qualification
               *
               * @maxLength 256
               */
              qualificationName?: string;
              /**
               * The date the qualification was obtained
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              date?: string;
              /**
               * The unique identifier of the qualification
               *
               * @maxLength 256
               */
              qualificationId?: string;
              /**
               * The order index of the qualification
               *
               * @minimum 0
               */
              orderIndex?: number;
              /**
               * The expiry date of the qualification
               *
               * @maxLength 256
               */
              qualificationExpiry?: string;
              /**
               * Whether to exclude this qualification from vetting
               */
              excludeFromVetting?: boolean;
              /**
               * The date the qualification was created
               *
               * @format date-time
               */
              createdAt?: string | null;
            }[];
            /**
             * The employment history of the candidate
             *
             * @maxItems 32
             */
            employment?: {
              /**
               * The name of the employer
               *
               * @maxLength 256
               */
              employerName?: string;
              /**
               * The date the candidate started employment
               *
               * @maxLength 256
               */
              dateStarted?: string;
              /**
               * The date the candidate ended employment
               *
               * @maxLength 256
               */
              dateEnded?: string;
              /**
               * The job title of the candidate
               *
               * @maxLength 256
               */
              jobTitle?: string;
              /**
               * The reason the candidate left this employment
               *
               * @maxLength 1024
               */
              reasonForLeaving?: string;
              /**
               * The duties of the candidate in this role. Consultant only, visible on Edun CV
               *
               * @maxLength 1024
               */
              duties?: string;
              /**
               * The salary of the candidate in this role
               *
               * @maxLength 256
               */
              salary?: string;
              /**
               * The address of the candidate's reference for this employment
               */
              referenceAddress?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
              /**
               * The unique identifier for this employment record
               *
               * @maxLength 256
               */
              employmentId?: string;
              /**
               * The order index of this employment record
               *
               * @minimum 0
               */
              orderIndex?: number;
            }[];
            /**
             * The references for the candidate
             *
             * @maxItems 32
             */
            references?: {
              /**
               * The title of the reference
               *
               * @maxLength 16
               */
              referenceTitle?: string;
              /**
               * The forename of the reference
               *
               * @maxLength 256
               */
              referenceForename?: string;
              /**
               * The surname of the reference
               *
               * @maxLength 256
               */
              referenceSurname?: string;
              /**
               * The company of the reference
               *
               * @maxLength 256
               */
              referenceCompany?: string;
              /**
               * The position of the reference
               *
               * @maxLength 256
               */
              referencePosition?: string;
              /**
               * The email of the reference
               *
               * @format email
               */
              referenceEmail?: string;
              /**
               * The telephone number of the reference
               *
               * @maxLength 32
               */
              referenceTelephone?: string;
              /**
               * The type of the reference
               */
              referenceType?:
                | "Education"
                | "Professional"
                | "Character"
                | "Factual"
                | "Academic"
                | "Volunteer";
              /**
               * The duration of the reference
               *
               * @maxLength 128
               */
              referenceDuration?: string;
              /**
               * The ID of the reference
               *
               * @maxLength 256
               */
              referenceId?: string;
              /**
               * This field only applies if the reference immediate consent question is enabled
               * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
               */
              referenceApply?: boolean;
              /**
               * Any additional notes about the reference
               *
               * @maxLength 1024
               */
              referenceNote?: string;
              /**
               * The start date of the reference
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              dateFrom?: string;
              /**
               * The end date of the reference
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              dateTo?: string;
            }[];
            /**
             * Whether the candidate has consented to a reference check
             */
            referenceCheckConsent?: boolean;
            /**
             * The name of the person who provided consent for the reference check
             *
             * @maxLength 256
             */
            referenceCheckConsentName?: string;
            /**
             * The date when consent for the reference check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            referenceCheckConsentDate?: string;
            /**
             * The date of birth of the candidate
             *
             * @format date-time
             */
            dob?: string | null;
            /**
             * The sex of the candidate
             */
            sex?: "Male" | "Female" | "Other" | "Unknown";
            /**
             * The status of the candidate
             */
            status?:
              | "Pending"
              | "Approved"
              | "Rejected"
              | "Anonymised"
              | "Archived"
              | "Enquired"
              | "Hold"
              | "Future"
              | "DNC"
              | "Pre";
            /**
             * The current status of the onboarding process for the candidate
             */
            onboardingStatus?: "Not Started" | "In Progress" | "Completed";
            /**
             * Whether the onboarding information has been sent to the candidate
             */
            onboardingSent?: boolean;
            nextOfKin?: {
              /**
               * The name of the next of kin
               *
               * @maxLength 256
               */
              nextOfKinName?: string;
              /**
               * The relationship of the next of kin to the candidate
               *
               * @maxLength 256
               */
              nextOfKinRelationship?: string;
              /**
               * The telephone number of the next of kin
               *
               * @maxLength 32
               */
              nextOfKinTelephone?: string;
              /**
               * The email address of the next of kin
               *
               * @format email
               */
              nextOfKinEmail?: string;
              nextOfKinAddress?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
            };
            criminalRecord?: {
              /**
               * Whether the candidate has a DBS certificate
               */
              dbsHeld?: boolean;
              /**
               * The unique identifier for the DBS certificate
               *
               * @maxLength 256
               */
              dbsNumber?: string;
              /**
               * The type of DBS check
               */
              dbsType?:
                | "Basic"
                | "Standard"
                | "Enhanced"
                | "Enhanced with Barred List"
                | "Child workforce"
                | "Child and adult workforce"
                | "Adult workforce";
              /**
               * The date when the DBS check was last performed
               *
               * @maxLength 256
               */
              dbsLastChecked?: string;
              /**
               * The DBS update service number for the candidate
               *
               * @maxLength 256
               */
              dbsUpdateServiceNumber?: string;
              /**
               * The result of the prohibition check
               *
               * @maxLength 256
               */
              prohibitionCheck?: string;
              /**
               * The result of the overseas check
               *
               * @maxLength 256
               */
              overseasCheck?: string;
              /**
               * Any additional notes regarding the overseas check
               *
               * @maxLength 256
               */
              overseasCheckNote?: string;
              /**
               * Whether the candidate has a convicted offence
               */
              convictedOffence?: boolean;
              /**
               * The description of the convicted offence
               *
               * @maxLength 1024
               */
              convictedOffenceDescription?: string;
              /**
               * Whether the candidate has any convictions
               */
              convictions?: boolean;
              /**
               * The description of the convictions
               *
               * @maxLength 1024
               */
              convictionsDescription?: string;
              /**
               * Whether the candidate is registered to the DBS update service
               */
              registeredToUpdateService?: boolean;
              /**
               * Whether the candidate consents to update service checks
               */
              consentUpdateServiceChecks?: boolean;
              /**
               * Any additional notes regarding the candidate's criminal record
               *
               * @maxLength 1024
               */
              note?: string;
              /**
               * The date when the update service check was performed
               *
               * @maxLength 64
               */
              updateServiceCheckDate?: string;
              /**
               * Whether the candidate has lived abroad
               */
              abroad?: boolean;
              /**
               * Any additional notes regarding the overseas declaration
               *
               * @maxLength 1024
               */
              overseasDeclarationExtended?: string;
              /**
               * Whether the candidate has been subject to educational disciplinary proceedings
               */
              educationalDisciplinaryProceedings?: boolean;
              /**
               * Any comments regarding the DBS check
               *
               * @maxLength 1024
               */
              dbsComments?: string;
              /**
               * Whether the candidate has read the DBS declaration
               */
              dbsDeclarationRead?: boolean;
              /**
               * Whether the candidate has any overseas offences
               */
              overseasOffence?: boolean;
              /**
               * The description of the overseas offence
               *
               * @maxLength 1024
               */
              overseasOffenceDescription?: string;
              /**
               * Whether the candidate has a current criminal offence
               */
              currentCriminalOffence?: boolean;
              /**
               * The description of the current criminal offence
               *
               * @maxLength 1024
               */
              currentCriminalOffenceDescription?: string;
              /**
               * Whether the DBS was issued by a business
               */
              dbsIssuedByBusiness?: boolean;
              /**
               * Whether the DBS was issued by the current employer
               */
              dbsIssuedByCurrentEmployer?: boolean;
              /**
               * Whether a Section 128 check is required for the candidate.
               */
              section128Required?: boolean;
              /**
               * The date when the Section 128 check was completed.
               */
              section128Checked?: string;
            };
            /**
             * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
             */
            criminalRecordDeclaration?: {
              /**
               * The ordered questions included in the question set.
               *
               * @maxItems 100
               */
              questions?: {
                /**
                 * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * A stable key used to associate the question with current and historical answers.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                key?: string;
                /**
                 * The question shown to the person completing the form.
                 *
                 * @minLength 1
                 * @maxLength 500
                 */
                label?: string;
                /**
                 * Optional supporting text displayed with the question.
                 *
                 * @maxLength 2000
                 */
                description?: string;
                /**
                 * The type of answer collected for the question.
                 */
                type?:
                  | "checkbox"
                  | "date"
                  | "daterange"
                  | "dropdown"
                  | "number"
                  | "signature"
                  | "text"
                  | "textarea";
                /**
                 * Whether an answer is required while the question is visible.
                 */
                required?: boolean;
                /**
                 * Whether the question is included in new forms. Historical responses retain disabled questions.
                 */
                enabled?: boolean;
                /**
                 * The display order of the question.
                 *
                 * @minimum 0
                 * @maximum 1000
                 */
                order?: number;
                /**
                 * Optional rules that determine when the question is shown.
                 */
                condition?: {
                  /**
                   * Whether any rule or every rule must match before the question is shown.
                   */
                  mode?: "any" | "all";
                  /**
                   * The comparisons used to determine question visibility.
                   *
                   * @minItems 1
                   * @maxItems 20
                   */
                  rules?: {
                    /**
                     * The stable key of the question that controls visibility.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    questionKey?: string;
                    /**
                     * How the controlling answer is compared with the value.
                     */
                    operator?: "equals" | "notEquals";
                    /**
                     * The answer value used to determine visibility.
                     */
                    value?: string | boolean | number | (void | null);
                  }[];
                };
                /**
                 * The available answers for a dropdown question.
                 *
                 * @minItems 1
                 * @maxItems 100
                 */
                options?: {
                  /**
                   * The value stored when this option is selected.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  value?: string;
                  /**
                   * The option displayed to the person answering the question.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  label?: string;
                }[];
                /**
                 * Whether this question belongs to the standard template.
                 */
                ["default"]?: boolean;
                /**
                 * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                 */
                audience?: "candidate" | "staff";
                /**
                 * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                 */
                fixedRole?: "fullName" | "signature";
              }[];
              /**
               * The answers recorded for the point-in-time question set.
               *
               * @maxItems 100
               */
              answers?: {
                /**
                 * The identifier of the question being answered.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                questionId?: string;
                /**
                 * The stable key of the question being answered.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey?: string;
                /**
                 * The submitted answer to the question.
                 */
                value?: string | string | boolean | number | (void | null);
              }[];
              /**
               * When the question set was submitted.
               *
               * @format date-time
               */
              submittedAt?: string;
              /**
               * The network address recorded when the response was submitted.
               *
               * @maxLength 64
               */
              submittedIp?: string;
              /**
               * When the question configuration captured by this response was last updated.
               *
               * @format date-time
               */
              configurationUpdatedAt?: string;
            };
            /**
             * Whether the candidate confirmed their criminal record declaration.
             */
            criminalRecordCheckConsent?: boolean;
            /**
             * The name used to confirm the criminal record declaration.
             *
             * @maxLength 256
             */
            criminalRecordCheckConsentName?: string;
            /**
             * The date the criminal record declaration was confirmed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            criminalRecordCheckConsentDate?: string;
            safeInEducation?: {
              /**
               * Whether the candidate is barred from working in education
               */
              barred?: boolean;
              /**
               * The date when the barred status was checked
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              barredCheckDate?: string;
              /**
               * Whether the candidate has a waiver for the barred status
               */
              waiver?: boolean;
              /**
               * Any orders related to the candidate's barred status
               */
              ordersYou?: boolean;
              /**
               * Any orders related to the candidate's barred status for children
               */
              ordersChild?: boolean;
              /**
               * Whether the candidate has any previous safeguarding allegations
               */
              previousSafeguardingAllegations?: boolean;
              /**
               * Whether the candidate has been cautioned
               */
              cautioned?: boolean;
              /**
               * Whether the candidate has a violent offence
               */
              violentOffence?: boolean;
              /**
               * Whether the candidate has lived or worked in another country
               */
              anotherCountry?: boolean;
              /**
               * Whether the candidate has read the Keeping Children Safe in Education document
               */
              readKCSIE?: boolean;
              /**
               * Whether the candidate has read the Disqualification under the Childcare Act guidance
               */
              readChildcareAct?: boolean;
              /**
               * Whether the candidate is prohibited from teaching
               */
              prohibitedFromTeaching?: boolean;
              /**
               * Whether the candidate is known to the police
               */
              knownPolice?: boolean;
              /**
               * Whether the candidate is prohibited from management roles
               */
              prohibitedFromManagement?: boolean;
              /**
               * Whether the candidate has any criminal offences under law
               */
              criminalOffencesLaw?: boolean;
              /**
               * Any additional details regarding the candidate's safe in education status
               */
              safeInEducationDetails?: string;
              safeguardingQuestionaire?: {
                /**
                 * What is the primary purpose of safeguarding in a school environment?
                 */
                primaryPurposeSafeguarding?:
                  | "enhance-academic-performance"
                  | "ensure-safe-learning-environment"
                  | "promote-healthy-relationships"
                  | "facilitate-extracurricular-activities";
                /**
                 * Who is typically responsible for coordinating safeguarding efforts within a school?
                 */
                typicallyResponsibleCoordinatingSafeguarding?:
                  | "janitorial-staff"
                  | "parents-association"
                  | "school-nurse"
                  | "designated-safeguarding-lead";
                /**
                 * Which of the following is considered a potential sign of child abuse or neglect?
                 */
                signOfAbuse?:
                  | "student-excelling"
                  | "frequent-extracurricular"
                  | "unexplained-injuries"
                  | "not-listening";
                /**
                 * How should a teacher respond if a student discloses information about potential abuse or harm?
                 */
                studentDisclosesInformation?:
                  | "ignore-disclosure"
                  | "report-disclosure"
                  | "share-information"
                  | "advise-independent";
                /**
                 * What is the purpose of a school's Child Protection Policy?
                 */
                schoolsChildProtectionPolicy?:
                  | "enforcing-strict-disciplinary-measures"
                  | "identifying-troublemakers"
                  | "outlining-procedures-for-protecting-students-from-harm"
                  | "establishing-academic-achievement-standards";
                /**
                 * What does the term 'FGM' stand for in the context of safeguarding?
                 */
                fgmDefinition?:
                  | "female-general-medicine"
                  | "forced-genetic-modification"
                  | "female-genital-mutilation"
                  | "foundation-gender-equality";
                /**
                 * Which of the following is a key principle of online safety for students?
                 */
                keyPrincipleOnlineSafetyStudents?:
                  | "unlimited-screen-time"
                  | "anon-online-interaction"
                  | "responsible-digital-citizen"
                  | "restrict-edu-sites";
                /**
                 * When planning a school trip, what should teachers consider regarding safeguarding?
                 */
                teachersConsiderRegardingSafeguarding?:
                  | "minimise-supervision"
                  | "appropriate-staff-ratios"
                  | "exclude-medical-conditions"
                  | "allow-roaming";
                /**
                 * What is the purpose of a criminal background check for teachers?
                 */
                criminalBackgroundCheckTeachers?:
                  | "evaluating-teaching-proficiency"
                  | "verifying-immigration-status"
                  | "assessing-financial-stability"
                  | "identifying-criminal-history";
                /**
                 * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
                 */
                studentConfides?:
                  | "assure-secret-safe"
                  | "encourage-share-friends"
                  | "explain-no-guarantee"
                  | "disclose-to-others";
              };
            };
            socialMedia?: {
              /**
               * The Facebook profile URL of the candidate
               *
               * @maxLength 256
               */
              facebook?: string;
              /**
               * The Instagram profile URL of the candidate
               *
               * @maxLength 256
               */
              instagram?: string;
              /**
               * The Twitter profile URL of the candidate
               *
               * @maxLength 256
               */
              twitter?: string;
              /**
               * The LinkedIn profile URL of the candidate
               *
               * @maxLength 256
               */
              linkedIn?: string;
              /**
               * The TikTok profile URL of the candidate
               *
               * @maxLength 256
               */
              tiktok?: string;
              /**
               * Any other social media profile URLs of the candidate
               *
               * @maxLength 256
               */
              others?: string;
              /**
               * Whether the social media profiles have been checked
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              socialMediaChecked?: string;
              /**
               * The date the candidate's Google search check was completed
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              googleCheckedDate?: string;
            };
            /**
             * Whether the candidate has consented to a safe in education check
             */
            safeInEducationCheckConsent?: boolean;
            /**
             * The name of the person who provided consent for the safe in education check
             *
             * @maxLength 256
             */
            safeInEducationCheckConsentName?: string;
            /**
             * The date when consent for the safe in education check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            safeInEducationCheckConsentDate?: string;
            /**
             * The health record of the individual
             */
            healthRecord?: {
              /**
               * Whether the individual has a physical ability condition
               */
              physicalAbilityCondition?: boolean;
              /**
               * Whether the individual is confused
               */
              confused?: boolean;
              /**
               * Whether the individual has hearing issues
               */
              hearing?: boolean;
              /**
               * Whether the individual has eyesight issues
               */
              eyesight?: boolean;
              /**
               * Whether the individual has depression
               */
              depression?: boolean;
              /**
               * Whether the individual has severe pain
               */
              severePain?: boolean;
              /**
               * Whether the individual has excessive drowsiness
               */
              excessiveDrowsiness?: boolean;
              /**
               * Whether the individual has epilepsy
               */
              epilepsy?: boolean;
              /**
               * Whether the individual has heart issues
               */
              heart?: boolean;
              /**
               * Whether the individual has asthma
               */
              asthma?: boolean;
              /**
               * Whether the individual has mental health issues
               */
              mentalHealth?: boolean;
              /**
               * Whether the individual is an expectant mother
               */
              expectantMother?: boolean;
              /**
               * Whether the individual has allergies
               */
              allergies?: boolean;
              /**
               * Whether the individual is taking medication
               */
              medication?: boolean;
              /**
               * Whether the individual has any health issues
               */
              healthIssues?: boolean;
              /**
               * A description of the individual's health issues
               *
               * @maxLength 2048
               */
              healthIssuesDescription?: string;
              /**
               * Any disability needs of the individual
               *
               * @maxLength 2048
               */
              disabilityNeeds?: string;
              /**
               * Whether the individual has any contagious diseases
               */
              contagiousDiseases?: boolean;
              /**
               * Whether the individual's health affects their ability
               */
              affectAbility?: boolean;
              /**
               * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
               */
              notificationAgree?: boolean;
              /**
               * Whether the individual has opted into the SAS Wellbeing App
               */
              sasOptIn?: boolean;
              /**
               * The individual's signature
               *
               * @maxLength 256
               */
              signature?: string;
              /**
               * The individual's drawn signature in base64url format
               *
               * @maxLength 4096
               */
              drawnSignature?: string;
              /**
               * The date the individual signed the form
               *
               * @format date-time
               */
              signatureDate?: string | null;
              /**
               * Whether the individual has been vaccinated against measles
               */
              vaccinationMeasles?: boolean;
              /**
               * Whether the individual has been vaccinated against mumps
               */
              vaccinationMumps?: boolean;
              /**
               * Whether the individual has been vaccinated against rubella
               */
              vaccinationRubella?: boolean;
              /**
               * Whether the individual has been vaccinated against varicella
               */
              vaccinationVaricella?: boolean;
              /**
               * Whether the individual has been vaccinated against tuberculosis
               */
              vaccinationTuberculosis?: boolean;
              /**
               * Whether the individual has been vaccinated against hepatitis B
               */
              vaccinationHepatitisB?: boolean;
              /**
               * Whether the individual has been vaccinated against hepatitis C
               */
              vaccinationHepatitisC?: boolean;
              /**
               * Whether the individual has been vaccinated against HIV
               */
              vaccinationHIV?: boolean;
              /**
               * Whether the individual has been vaccinated against COVID-19
               */
              vaccinationCovid?: boolean;
              /**
               * Whether the individual has received the 1st dose of the COVID-19 vaccine
               */
              vaccinationCovid1?: boolean;
              /**
               * Whether the individual has received the 2nd dose of the COVID-19 vaccine
               */
              vaccinationCovid2?: boolean;
              /**
               * Whether the individual has received the COVID-19 booster vaccine
               */
              vaccinationCovidBooster?: boolean;
              /**
               * Whether the individual has received the COVID-19 influenza vaccine
               */
              vaccinationCovidInfluenza?: boolean;
              /**
               * Whether the individual has an override for fit to work requirements
               */
              fitToWorkOverride?: boolean;
            };
            /**
             * The candidate's latest submitted health declaration, including the exact questions presented.
             */
            healthDeclaration?: {
              /**
               * The ordered questions included in the question set.
               *
               * @maxItems 100
               */
              questions?: {
                /**
                 * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * A stable key used to associate the question with current and historical answers.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                key?: string;
                /**
                 * The question shown to the person completing the form.
                 *
                 * @minLength 1
                 * @maxLength 500
                 */
                label?: string;
                /**
                 * Optional supporting text displayed with the question.
                 *
                 * @maxLength 2000
                 */
                description?: string;
                /**
                 * The type of answer collected for the question.
                 */
                type?:
                  | "checkbox"
                  | "date"
                  | "daterange"
                  | "dropdown"
                  | "number"
                  | "signature"
                  | "text"
                  | "textarea";
                /**
                 * Whether an answer is required while the question is visible.
                 */
                required?: boolean;
                /**
                 * Whether the question is included in new forms. Historical responses retain disabled questions.
                 */
                enabled?: boolean;
                /**
                 * The display order of the question.
                 *
                 * @minimum 0
                 * @maximum 1000
                 */
                order?: number;
                /**
                 * Optional rules that determine when the question is shown.
                 */
                condition?: {
                  /**
                   * Whether any rule or every rule must match before the question is shown.
                   */
                  mode?: "any" | "all";
                  /**
                   * The comparisons used to determine question visibility.
                   *
                   * @minItems 1
                   * @maxItems 20
                   */
                  rules?: {
                    /**
                     * The stable key of the question that controls visibility.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    questionKey?: string;
                    /**
                     * How the controlling answer is compared with the value.
                     */
                    operator?: "equals" | "notEquals";
                    /**
                     * The answer value used to determine visibility.
                     */
                    value?: string | boolean | number | (void | null);
                  }[];
                };
                /**
                 * The available answers for a dropdown question.
                 *
                 * @minItems 1
                 * @maxItems 100
                 */
                options?: {
                  /**
                   * The value stored when this option is selected.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  value?: string;
                  /**
                   * The option displayed to the person answering the question.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  label?: string;
                }[];
                /**
                 * Whether this question belongs to the standard template.
                 */
                ["default"]?: boolean;
                /**
                 * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                 */
                audience?: "candidate" | "staff";
                /**
                 * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                 */
                fixedRole?: "fullName" | "signature";
              }[];
              /**
               * The answers recorded for the point-in-time question set.
               *
               * @maxItems 100
               */
              answers?: {
                /**
                 * The identifier of the question being answered.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                questionId?: string;
                /**
                 * The stable key of the question being answered.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey?: string;
                /**
                 * The submitted answer to the question.
                 */
                value?: string | string | boolean | number | (void | null);
              }[];
              /**
               * When the question set was submitted.
               *
               * @format date-time
               */
              submittedAt?: string;
              /**
               * The network address recorded when the response was submitted.
               *
               * @maxLength 64
               */
              submittedIp?: string;
              /**
               * When the question configuration captured by this response was last updated.
               *
               * @format date-time
               */
              configurationUpdatedAt?: string;
            };
            /**
             * Whether the candidate confirmed their health declaration.
             */
            healthRecordCheckConsent?: boolean;
            /**
             * The name used to confirm the health declaration.
             *
             * @maxLength 256
             */
            healthRecordCheckConsentName?: string;
            /**
             * The date the health declaration was confirmed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            healthRecordCheckConsentDate?: string;
            /**
             * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
             */
            bankDetails?: {
              /**
               * The name on the bank account
               */
              bankAccountName?: string;
              /**
               * The name of the bank
               */
              bankName?: string;
              /**
               * The account number
               */
              accountNumber?: string;
              /**
               * The sort code
               */
              sortCode?: string;
              /**
               * The National Insurance number
               */
              niNumber?: string;
              /**
               * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
               */
              jobOp1?: boolean;
              /**
               * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
               */
              jobOp2?: boolean;
              /**
               * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
               */
              jobOp3?: boolean;
              studentLoan?: {
                /**
                 * Boolean flag for if candidate has student loan
                 */
                studentLoan?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 1
                 */
                plan1?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 2
                 */
                plan2?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 4
                 */
                plan4?: boolean;
                /**
                 * Boolean flag for if candidate has postgrad loan
                 */
                postGradLoan?: boolean;
              };
            };
            /**
             * Whether the candidate has confirmed their details are correct.
             */
            declarationCheckConsent?: boolean;
            /**
             * The name of the person who confirmed their details are correct.
             *
             * @maxLength 256
             */
            declarationCheckConsentName?: string;
            /**
             * The date when consent for the declaration check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            declarationCheckConsentDate?: string;
            /**
             * Whether the candidate has consented to a contract for service
             */
            contractForServiceConsent?: boolean;
            /**
             * The name of the person who provided consent for the contract for service
             *
             * @maxLength 256
             */
            contractForServiceName?: string;
            /**
             * The date when consent for the contract for service was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            contractForServiceDate?: string;
            /**
             * The candidate has completed their onboarding form.
             */
            confirmed?: boolean;
            /**
             * The ID of the user who created the candidate record.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            createdBy?: string;
            /**
             * The ID of the customer associated with the candidate.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customerId?: string;
            /**
             * The migration that created this candidate
             *
             * @pattern ^[0-9a-f]{24}$
             */
            migrationId?: string;
            /**
             * Whether this migrated candidate has been approved
             */
            migrationApproval?: "pending" | "approved" | "rejected";
            /**
             * The user assigned to the candidate. If expanded, a user object else the user ID.
             */
            assignee?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * The ID of the current booking for the candidate.
             */
            currentBooking?: string[];
            /**
             * The URL of the candidate's profile photo.
             *
             * @maxLength 256
             */
            profilePhoto?: string;
            /**
             * The pay type for the candidate.
             */
            payType?:
              | "PAYE"
              | "Umbrella"
              | "Limited Company"
              | "Not Applicable";
            /**
             * The pay interval for the candidate.
             */
            payInterval?:
              | "Monthly"
              | "Weekly"
              | "Weekly Pay in Arrears"
              | "Bi-Weekly"
              | any
              | null;
            /**
             * The pay rate for the candidate.
             */
            payRate?: string;
            /**
             * A custom ID for the candidate.
             *
             * @maxLength 256
             */
            customId?: string;
            /**
             * A company-defined identifier used to match the candidate with external records.
             *
             * @maxLength 256
             */
            _internalId?: string;
            /**
             * The date when the candidate's status was last changed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            statusChangeDate?: string;
            /**
             * Whether the candidate has opted out of communications.
             */
            optOut?: boolean;
            /**
             * The TRN number for the candidate.
             *
             * @maxLength 256
             */
            trnNumber?: string;
            /**
             * The date when the TRN number was last checked.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            trnNumberCheckedDate?: string;
            /**
             * Any notes related to the TRN number.
             *
             * @maxLength 1024
             */
            trnNumberNotes?: string;
            /**
             * The date when the candidate's employment is set to start.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            employmentStartDate?: string;
            /**
             * Whether the candidate has confirmed their Early Years Workforce status.
             */
            ewcConfirmation?: boolean;
            /**
             * The share code for the candidate's Home Office application.
             *
             * @maxLength 256
             */
            homeOfficeShareCode?: string;
            /**
             * Whether the candidate has been archived.
             */
            archived?: boolean;
            /**
             * Whether the candidate is using an umbrella company.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            umbrella?: string;
            /**
             * ID of the PAYE umbrella company.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            payeUmbrella?: string;
            /**
             * The name of the umbrella company the candidate is using.
             *
             * @maxLength 256
             */
            umbrellaCompanyName?: string;
            /**
             * The code of the umbrella company the candidate is using.
             *
             * @maxLength 256
             */
            umbrellaCompanyCode?: string;
            /**
             * The transport method for the candidate.
             */
            transport?:
              | "Drives Own Vehicle"
              | "Use Public Transport"
              | "Access to Another Vehicle"
              | "Bicycle"
              | "No Access"
              | "Other";
            /**
             * Any important notes related to the candidate.
             *
             * @maxLength 1024
             */
            importantNotes?: string;
            /**
             * The date when the candidate was last updated.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            candidateLastUpdated?: string;
            /**
             * The source from which the candidate was obtained.
             */
            source?:
              | (
                  | "Company Website"
                  | "iCan Teach UK Website"
                  | "Apply to Education"
                  | "CV Library - CV Search"
                  | "CV Library Advert"
                  | "CV Library - Watchdog"
                  | "Guardian- CV Search"
                  | "Guardian Advert"
                  | "Indeed - CV Search"
                  | "Indeed Advert"
                  | "Reed - CV Search"
                  | "Reed Advert"
                  | "Reed \u2013 Watchdog"
                  | "SENploy - Advert"
                  | "Total Jobs"
                  | "Other Job Board"
                  | "Google"
                  | "LinkedIn"
                  | "Linkedin Recruiter"
                  | "LinkedIn Advert"
                  | "Referral - School"
                  | "Referral - Candidate"
                  | "UK University"
                  | "Irish University"
                  | "Canadian University"
                  | "Marketing Campaign"
                  | "Woo"
                  | "Job Show"
                  | "Recruitment Fair"
                  | "Career Fair"
                  | "Volcanic"
                  | "Broadbean"
                  | "Headhunt"
                  | "Referral"
                  | "Direct"
                  | "Other"
                )
              | (
                  | "Company Website"
                  | "Direct Call"
                  | "CV Library Watchdog"
                  | "CV Library Advert"
                  | "CV Library CV Search"
                  | "Reed Watchdog"
                  | "Reed Advert"
                  | "Reed CV Search"
                  | "Indeed Watchdog"
                  | "Indeed Advert"
                  | "Indeed CV Search"
                  | "Job Centre"
                  | "Returning Candidate"
                  | "Referral Candidate"
                  | "Referral Client"
                  | "Training Provider"
                  | "Recruitment Fair"
                  | "University/College"
                );
            /**
             * Any other source from which the candidate was obtained.
             *
             * @maxLength 256
             */
            sourceOther?: string;
            /**
             * Whether the candidate has given consent for online checks.
             */
            onlineCheckConsent?: boolean;
            /**
             * The title of the candidate.
             */
            title?:
              | "Mr"
              | "Mrs"
              | "Ms"
              | "Miss"
              | "Dr"
              | "Mx"
              | "Lady"
              | "Lord"
              | "Other";
            /**
             * The pronoun used by the candidate.
             */
            pronoun?:
              | "She/Her/Hers"
              | "He/Him/His"
              | "They/Them/Theirs"
              | "Ze/Hir/Hirs"
              | "Ze/Zir/Zirs"
              | "Other"
              | "Prefer not to say";
            /**
             * The date when the candidate was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
            /**
             * The date when the candidate was last contacted.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            lastContactedDate?: string;
            /**
             * A short, prominent note shown with the candidate to communicate important context.
             */
            highlight?: string;
            /**
             * The pay scale for the candidate's teaching position.
             *
             * @maxLength 256
             */
            teacherPayScale?: string;
            /**
             * Whether the candidate has a P45.
             */
            p45?: boolean;
            /**
             * The type of role the candidate is applying for.
             */
            roleType?:
              | "Senior Leadership Positions"
              | "Teaching Roles"
              | "School Support Positions";
            /**
             * The Right to Work verification status for the candidate.
             *
             * @maxLength 256
             */
            rtwVerification?: string;
            /**
             * Any notes related to the Right to Work verification status for the candidate.
             *
             * @maxLength 1024
             */
            rtwVerificationNote?: string;
            /**
             * The Right to Work verification status for the candidate.
             *
             * @maxLength 256
             */
            rtwVerificationStatus?: string;
            /**
             * The location where the candidate desires to work.
             *
             * @maxLength 256
             */
            desiredLocation?: string;
            /**
             * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
             */
            complianceUser?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
             */
            watchers?:
              | string[]
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                }[];
            /**
             * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
             */
            tags?:
              | string[]
              | {
                  /**
                   * The unique identifier of the tag
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The label displayed wherever this tag is applied.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  name?: string;
                  /**
                   * The tag display colour as a hexadecimal value, for example #2563EB.
                   *
                   * @maxLength 32
                   */
                  color?: string | null;
                }[];
            /**
             * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
             *
             * @maxItems 5
             */
            documents?: {
              /**
               * The unique identifier of the document
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The name of the document
               *
               * @maxLength 512
               */
              name?: string;
              /**
               * The file reference used to retrieve the document
               *
               * @maxLength 2048
               */
              reference?: string;
              /**
               * The file's media type
               *
               * @maxLength 256
               */
              fileType?: string;
              /**
               * The area of the platform the document belongs to
               *
               * @maxLength 256
               */
              type?: string;
              /**
               * The configured category of the document
               *
               * @maxLength 256
               */
              docType?: string;
              /**
               * The document's expiry date, when applicable
               *
               * @maxLength 64
               */
              expiry?: string;
              /**
               * The document's issued date, when applicable
               *
               * @maxLength 64
               */
              issued?: string;
              /**
               * Whether the document has been archived
               */
              documentArchived?: boolean;
              /**
               * The date the document was uploaded
               *
               * @format date-time
               */
              createdAt?: string;
            }[];
            /**
             * The total number of documents attached to the candidate.
             *
             * @minimum 0
             */
            documentsCount?: number;
            /**
             * The locations where the candidate desires to work.
             */
            desiredLocationMultiple?: string[];
            /**
             * Daily availability for the requested period. Returned by the availability view only.
             *
             * @maxItems 31
             */
            availabilityDays?: {
              /**
               * Calendar date in YYYY-MM-DD format.
               *
               * @maxLength 10
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              date?: string;
              /**
               * Availability or booking state for this date.
               */
              status?:
                | "available"
                | "unavailable"
                | "booked"
                | "halfDay"
                | "awaiting"
                | "unknown";
              /**
               * Recorded availability, or null when unrecorded.
               */
              available?: boolean | null;
              /**
               * Morning or afternoon availability.
               */
              partial?: "AM" | "PM" | any | null;
              /**
               * Additional availability information.
               *
               * @maxLength 4096
               */
              note?: string | null;
              /**
               * Active bookings on this date.
               *
               * @maxItems 100
               */
              bookings?: {
                /**
                 * The unique identifier of the resource
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * @maxLength 512
                 */
                clientName?: string;
                /**
                 * @maxLength 512
                 */
                role?: string;
                /**
                 * @maxLength 32
                 */
                timeDesignation?: string;
              }[];
            }[];
            /**
             * The date when the candidate's availability was last checked.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            availabilityLastChecked?: string;
            /**
             * Whether the candidate is subject to notice period.
             */
            subjectToNotice?: "Unsure" | "Yes" | "No";
            /**
             * The subsidiary subjects the candidate is qualified to teach.
             */
            subsidiarySubject?: string[];
            /**
             * The candidate's preferred employment type.
             */
            employmentPreference?: (
              | "Day To Day"
              | "Long Term"
              | "Fixed Term"
              | "Perm"
            )[];
            /**
             * Any other names the candidate is known by.
             *
             * @maxLength 256
             */
            otherNames?: string;
            /**
             * The name of the candidate's limited company, if applicable.
             *
             * @maxLength 256
             */
            ltdCompany?: string;
            /**
             * Whether the candidate's employment has been verified.
             */
            employmentVerified?: boolean;
            /**
             * The DBS verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSVerification?: string;
            /**
             * Any notes related to the DBS verification status for the candidate.
             *
             * @maxLength 1024
             */
            candidateDBSVerificationNote?: string;
            /**
             * The DBS verification status for the candidate.
             */
            candidateDBSVerificationStatus?: "Cleared" | "Alerts";
            /**
             * The DBS check verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSCheckVerification?: string;
            /**
             * Any notes related to the DBS check verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSCheckVerificationNote?: string;
            /**
             * The DBS check verification status for the candidate.
             */
            candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
            /**
             * Any notes related to the DBS update service verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSUpdateServiceVerificationNote?: string;
            /**
             * The DBS update service verification status for the candidate.
             */
            candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
            /**
             * Any notes related to the TRN verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateTRNVerificationNote?: string;
            /**
             * The TRN verification status for the candidate.
             */
            candidateTRNVerificationStatus?:
              | "Completed Induction"
              | "Not Yet Completed Induction"
              | "Induction Exempt"
              | "ALERTS - Failed Induction"
              | "Induction Not Required";
            /**
             * The health and safety verification status for the candidate.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            candidateHealthSafetyVerification?: string;
            /**
             * Any notes related to the health and safety verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateHealthSafetyVerificationNote?: string;
            /**
             * The health and safety verification status for the candidate.
             */
            candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
            /**
             * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
             */
            onboardingSession?:
              | string
              | {
                  /**
                   * Unique identifier for the onboarding session
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The URL for the onboarding session
                   *
                   * @format uri
                   */
                  url?: string;
                  /**
                   * The frozen health declaration configuration for this onboarding session.
                   */
                  healthDeclarationConfig?: {
                    /**
                     * The ordered questions included in the question set.
                     *
                     * @maxItems 100
                     */
                    questions?: {
                      /**
                       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * A stable key used to associate the question with current and historical answers.
                       *
                       * @minLength 1
                       * @maxLength 128
                       */
                      key?: string;
                      /**
                       * The question shown to the person completing the form.
                       *
                       * @minLength 1
                       * @maxLength 500
                       */
                      label?: string;
                      /**
                       * Optional supporting text displayed with the question.
                       *
                       * @maxLength 2000
                       */
                      description?: string;
                      /**
                       * The type of answer collected for the question.
                       */
                      type?:
                        | "checkbox"
                        | "date"
                        | "daterange"
                        | "dropdown"
                        | "number"
                        | "signature"
                        | "text"
                        | "textarea";
                      /**
                       * Whether an answer is required while the question is visible.
                       */
                      required?: boolean;
                      /**
                       * Whether the question is included in new forms. Historical responses retain disabled questions.
                       */
                      enabled?: boolean;
                      /**
                       * The display order of the question.
                       *
                       * @minimum 0
                       * @maximum 1000
                       */
                      order?: number;
                      /**
                       * Optional rules that determine when the question is shown.
                       */
                      condition?: {
                        /**
                         * Whether any rule or every rule must match before the question is shown.
                         */
                        mode?: "any" | "all";
                        /**
                         * The comparisons used to determine question visibility.
                         *
                         * @minItems 1
                         * @maxItems 20
                         */
                        rules?: {
                          /**
                           * The stable key of the question that controls visibility.
                           *
                           * @minLength 1
                           * @maxLength 128
                           */
                          questionKey?: string;
                          /**
                           * How the controlling answer is compared with the value.
                           */
                          operator?: "equals" | "notEquals";
                          /**
                           * The answer value used to determine visibility.
                           */
                          value?: string | boolean | number | (void | null);
                        }[];
                      };
                      /**
                       * The available answers for a dropdown question.
                       *
                       * @minItems 1
                       * @maxItems 100
                       */
                      options?: {
                        /**
                         * The value stored when this option is selected.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        value?: string;
                        /**
                         * The option displayed to the person answering the question.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        label?: string;
                      }[];
                      /**
                       * Whether this question belongs to the standard template.
                       */
                      ["default"]?: boolean;
                      /**
                       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                       */
                      audience?: "candidate" | "staff";
                      /**
                       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                       */
                      fixedRole?: "fullName" | "signature";
                    }[];
                    /**
                     * When this question configuration was last updated.
                     *
                     * @format date-time
                     */
                    updatedAt?: string;
                  };
                  /**
                   * The frozen Criminal Record & Security Checks configuration for this onboarding session.
                   */
                  criminalRecordConfig?: {
                    /**
                     * The ordered questions included in the question set.
                     *
                     * @maxItems 100
                     */
                    questions?: {
                      /**
                       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * A stable key used to associate the question with current and historical answers.
                       *
                       * @minLength 1
                       * @maxLength 128
                       */
                      key?: string;
                      /**
                       * The question shown to the person completing the form.
                       *
                       * @minLength 1
                       * @maxLength 500
                       */
                      label?: string;
                      /**
                       * Optional supporting text displayed with the question.
                       *
                       * @maxLength 2000
                       */
                      description?: string;
                      /**
                       * The type of answer collected for the question.
                       */
                      type?:
                        | "checkbox"
                        | "date"
                        | "daterange"
                        | "dropdown"
                        | "number"
                        | "signature"
                        | "text"
                        | "textarea";
                      /**
                       * Whether an answer is required while the question is visible.
                       */
                      required?: boolean;
                      /**
                       * Whether the question is included in new forms. Historical responses retain disabled questions.
                       */
                      enabled?: boolean;
                      /**
                       * The display order of the question.
                       *
                       * @minimum 0
                       * @maximum 1000
                       */
                      order?: number;
                      /**
                       * Optional rules that determine when the question is shown.
                       */
                      condition?: {
                        /**
                         * Whether any rule or every rule must match before the question is shown.
                         */
                        mode?: "any" | "all";
                        /**
                         * The comparisons used to determine question visibility.
                         *
                         * @minItems 1
                         * @maxItems 20
                         */
                        rules?: {
                          /**
                           * The stable key of the question that controls visibility.
                           *
                           * @minLength 1
                           * @maxLength 128
                           */
                          questionKey?: string;
                          /**
                           * How the controlling answer is compared with the value.
                           */
                          operator?: "equals" | "notEquals";
                          /**
                           * The answer value used to determine visibility.
                           */
                          value?: string | boolean | number | (void | null);
                        }[];
                      };
                      /**
                       * The available answers for a dropdown question.
                       *
                       * @minItems 1
                       * @maxItems 100
                       */
                      options?: {
                        /**
                         * The value stored when this option is selected.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        value?: string;
                        /**
                         * The option displayed to the person answering the question.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        label?: string;
                      }[];
                      /**
                       * Whether this question belongs to the standard template.
                       */
                      ["default"]?: boolean;
                      /**
                       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                       */
                      audience?: "candidate" | "staff";
                      /**
                       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                       */
                      fixedRole?: "fullName" | "signature";
                    }[];
                    /**
                     * When this question configuration was last updated.
                     *
                     * @format date-time
                     */
                    updatedAt?: string;
                  };
                };
            /**
             * The contact record for this candidate. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * Whether the candidate has opted out of 48 hour communications.
             */
            optOut48Hours?: boolean;
            /**
             * Whether the candidate has consented to REC audit checks.
             */
            recAuditConsent?: boolean | null;
            /**
             * The date the candidate's REC audit consent was recorded.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            recAuditConsentDate?: string;
            /**
             * The method through which REC audit consent was captured.
             *
             * @maxLength 256
             */
            recAuditConsentMethod?: string;
            /**
             * The date when the candidate record was created.
             *
             * @format date-time
             */
            createdAt?: string;
            /**
             * The mobile app link status for the candidate.
             */
            candidateMobileAppStatus?:
              | "Unlinked"
              | "Scheduled Unlink"
              | "Linked"
              | "Invited"
              | "Not Invited";
            /**
             * Evertime details for this candidate
             */
            evertime?: {
              /**
               * The Evertime CandidateId used for this candidate when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
              /**
               * Whether the matched Evertime candidate's NI number also matches the one held in Edun
               */
              niNumberMatch?: boolean;
            };
            /**
             * Search relevance relative to the strongest result, where 100 is the strongest match.
             *
             * @minimum 0
             * @maximum 100
             */
            relevanceScore?: number;
            /**
             * Candidate fields or attached document excerpts that explain why the candidate matched.
             *
             * @maxItems 5
             */
            matchReasons?: {
              /**
               * Whether the matching information came from the candidate record or an attached document.
               */
              source?: "candidate" | "document";
              /**
               * The candidate or document information that matched the search.
               *
               * @maxLength 256
               */
              field?: string;
              /**
               * A short excerpt showing why the result matched.
               *
               * @maxLength 1000
               */
              text?: string;
              /**
               * The exact terms within the content that matched the search, including synonym matches.
               *
               * @maxItems 25
               */
              matchedTerms?: string[];
              /**
               * The name of the attached document when the match came from a document.
               *
               * @maxLength 512
               */
              documentName?: string | null;
              /**
               * The unique identifier of the attached document when the match came from a document.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              documentId?: string | null;
            }[];
            /**
             * The candidate's distance in miles from the requested location. Present only when location filtering is used.
             *
             * @minimum 0
             */
            distance?: number;
            /**
             * Timesheet Portal details for this candidate
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal contractor GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal contractor code
               *
               * @maxLength 256
               */
              code?: string;
            };
            /**
             * Invite to candidate to link their mobile app.
             */
            mobileAppLinkInvite?: {
              /**
               * Boolean status of whether the link invite is being used
               *
               * @default false
               */
              isUsed?: boolean;
              /**
               * Creation date of the invite
               *
               * @format date-time
               * @default 2026-10-30T13:39:09.027Z
               */
              createdAt?: string;
              /**
               * ID of candidate using this link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              usedBy?: string;
              /**
               * Date of when link used
               *
               * @format date-time
               */
              usedAt?: string;
              /**
               * If unlinked, ID of user that unlinked this candidate's link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              unlinkedBy?: string;
              /**
               * If unlinked, date of when this link was unlinked
               *
               * @format date-time
               */
              unlinkedAt?: string;
              /**
               * If deleted, ID of user that deleted this candidate's link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              deletedBy?: string;
              /**
               * If deleted, date of when this link was deleted
               *
               * @format date-time
               */
              deletedAt?: string;
              /**
               * Current status of this mobile app link
               */
              status?:
                | "Sent"
                | "Linked"
                | "Unlinked"
                | "ScheduledUnlink"
                | "Not Sent"
                | "Rescinded";
            };
          };
      /**
       * The current status of the candidate
       */
      status?: "Pending" | "Interested" | "Not Interested";
      /**
       * The client's status for the candidate
       */
      clientStatus?: "Pending" | "Interested" | "Not Interested";
      /**
       * The date when the client expressed interest in the candidate
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      clientInterestSentAt?: string | null;
      /**
       * Indicates if this is the default document for the candidate
       */
      defaultDocument?: boolean;
      /**
       * The document ID associated with the candidate
       *
       * @pattern ^[0-9a-f]{24}$
       */
      document?: string;
      /**
       * The name of the document associated with the candidate
       */
      documentName?: string;
      /**
       * The reason for the candidate's status
       */
      reason?: string;
    }[];
    /**
     * The client associated with the vacancy, if expanded client schema else client id
     */
    client?:
      | string
      | {
          /**
           * The unique identifier of the client
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The name of the client
           *
           * @maxLength 256
           */
          name?: string;
          /**
           * The address of the client
           */
          address?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The telephone number of the client
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The email address of the client
           *
           * @format email
           */
          email?: string;
          /**
           * Array of client contacts
           */
          contacts?: {
            /**
             * The unique identifier of the contact
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The forename of the contact
             *
             * @maxLength 128
             */
            forename?: string;
            /**
             * The surname of the contact
             *
             * @maxLength 128
             */
            surname?: string;
            /**
             * The email address of the contact
             *
             * @format email
             */
            email?: string;
            /**
             * The telephone number of the contact
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * The mobile number of the contact
             *
             * @maxLength 32
             */
            mobile?: string;
            /**
             * The extension number of the contact
             *
             * @maxLength 8
             */
            extension?: string;
            /**
             * The role of the contact
             */
            role?:
              | "Head Teacher"
              | "Deputy Head"
              | "Cover Manager"
              | "HR Manager"
              | "Finance Manager"
              | "Head of Maths"
              | "Head of Science"
              | "Head of English"
              | "Assistant Headteacher"
              | "Headteacher's PA"
              | "SENCO"
              | "Other"
              | "Business Manager"
              | "Business Administrator"
              | "Invoice Contact"
              | "Academy Recruitment Contact"
              | "Nursery Manager"
              | "CEO"
              | "COO"
              | "Executive Headteacher"
              | "Inclusion Manager"
              | "EYFS Lead"
              | "Admin Team"
              | "Head of Humanities"
              | "Head of History"
              | "Head of Geography"
              | "Head of Business Studies"
              | "Head of PE"
              | "Head of KS1"
              | "Head of KS2"
              | "Head of KS3"
              | "Head of KS4"
              | "Virtual School Headteacher"
              | "Deputy Virtual School Headteacher"
              | "Post-16 Team Leader"
              | "SEN Team"
              | "Inclusion Team"
              | "UASC Team"
              | "Caseworker"
              | "Designated Teacher"
              | "Attendance Officer"
              | "Coordinator"
              | "Children's Commissioner"
              | "Timesheet Contact";
            /**
             * The name of the role if the role is 'Other'
             *
             * @maxLength 128
             */
            roleOther?: string;
            /**
             * The unique identifier of the contact
             *
             * @format uuid
             */
            contactId?: string;
            /**
             * Timesheet Portal details for this contact
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal approver GUID linked to this client contact
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal approver code
               *
               * @maxLength 256
               */
              code?: string;
            };
            /**
             * Whether the contact has opted out of communications
             */
            optOut?: boolean;
            /**
             * Any additional notes about the contact
             *
             * @maxLength 175
             */
            note?: string;
            /**
             * Whether the contact is a decision maker
             */
            decisionMaker?: boolean;
            /**
             * The status of the contact
             */
            status?: "Active" | "Passive" | "Inactive";
            /**
             * The Client associated with the contact.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            clientId?: string;
            /**
             * The name of the Client associated with the contact.
             *
             * @maxLength 256
             */
            clientName?: string;
            /**
             * The address of the Client associated with the contact.
             */
            clientAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The local education authority of the associated Client.
             *
             * @maxLength 128
             */
            clientLea?: string;
            /**
             * The sector of the associated Client.
             *
             * @maxLength 128
             */
            clientType?: string;
            /**
             * The multi-academy trust of the associated Client.
             *
             * @maxLength 256
             */
            clientMultiAcademyTrust?: string;
            /**
             * The borough of the associated Client.
             *
             * @maxLength 128
             */
            clientBorough?: string;
            /**
             * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
             */
            clientAssignee?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * The specialism of the associated Client.
             *
             * @maxLength 256
             */
            clientSpecialism?: string;
            /**
             * When the associated Client was last updated.
             *
             * @format date-time
             */
            clientUpdatedAt?: string;
            /**
             * The associated Client's distance in miles from the requested location.
             *
             * @minimum 0
             */
            distance?: number;
            /**
             * Search relevance relative to the strongest result, where 100 is the strongest match.
             *
             * @minimum 0
             * @maximum 100
             */
            relevanceScore?: number;
            /**
             * Contact and Client fields that explain why the contact matched.
             *
             * @maxItems 5
             */
            matchReasons?: {
              /**
               * The contact or Client information that matched the search.
               *
               * @maxLength 256
               */
              field?: string;
              /**
               * A short excerpt showing why the contact matched.
               *
               * @maxLength 1000
               */
              text?: string;
              /**
               * The exact terms within the content that matched the search.
               *
               * @maxItems 25
               */
              matchedTerms?: string[];
            }[];
          }[];
          /**
           * The borough of the client
           *
           * @maxLength 128
           */
          borough?: string;
          /**
           * The local education authority of the client
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * The type of the client
           *
           * @maxLength 128
           */
          type?: string;
          /**
           * The multi academy trust that the client belongs to
           *
           * @maxLength 256
           */
          multiAcademyTrust?: string;
          /**
           * The color (HEX format prefixed with #) associated with the client
           *
           * @pattern ^#[0-9a-fA-F]{6}$
           */
          color?: string;
          /**
           * A custom ID for the client.
           *
           * @maxLength 256
           */
          customId?: string;
          /**
           * A company-defined identifier used to match the client with external records.
           *
           * @maxLength 256
           */
          internalId?: string;
          /**
           * The user assigned to the client. If expanded, a user object else the user ID.
           */
          assignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * Tags associated with this client. If expanded, an array of tag objects; otherwise, an array of tag IDs.
           */
          tags?:
            | string[]
            | {
                /**
                 * The unique identifier of the tag
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The label displayed wherever this tag is applied.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                name?: string;
                /**
                 * The tag display colour as a hexadecimal value, for example #2563EB.
                 *
                 * @maxLength 32
                 */
                color?: string | null;
              }[];
          /**
           * The date when the TOB was sent
           *
           * @format date-time
           */
          tobSentAt?: string;
          /**
           * The website of the client
           *
           * @format uri
           */
          website?: string;
          /**
           * Whether the client is archived
           */
          archived?: boolean;
          /**
           * The specialism of the client
           *
           * @maxLength 256
           */
          specialism?: string;
          /**
           * The current booking IDs associated with the client
           */
          currentBooking?: string[];
          /**
           * The number of active bookings associated with the client, constrained by the requested booking date range when present.
           *
           * @minimum 0
           */
          bookingCount?: number;
          /**
           * Any important notes related to the client
           *
           * @maxLength 1024
           */
          importantNotes?: string;
          /**
           * The booking roles associated with the client
           */
          roles?: {
            /**
             * The occupation of the individual in the booking role
             *
             * @maxLength 256
             */
            occupation?: string;
            /**
             * The pay rate for the booking role
             *
             * @maxLength 256
             */
            payRate?: string;
            /**
             * The charge rate for the booking role
             *
             * @maxLength 256
             */
            chargeRate?: string;
            /**
             * The type of pay for the booking role
             */
            payType?: "hourly" | "daily" | any | null;
            /**
             * The length of the day for the booking role
             */
            dayLength?: "full" | "half" | any | null;
            /**
             * The AM/PM designation for the day length
             */
            dayLengthAmPm?: "AM" | "PM" | any | null;
          }[];
          /**
           * The start time for the client
           */
          startTime?: string;
          /**
           * The end time for the client
           */
          endTime?: string;
          /**
           * Any additional instructions for the client
           *
           * @maxLength 1024
           */
          additionalInstructions?: string;
          /**
           * Any additional instructions specific to the client
           */
          additionalInstructionsClient?: string;
          /**
           * Tag highlight colour (HEX format prefixed with #)
           *
           * @pattern ^#[0-9a-fA-F]{6}$
           */
          highlight?: string;
          /**
           * The area of the client
           */
          area?: string;
          /**
           * The governance type of the client
           */
          governance?:
            | "Academy"
            | "Childrens Centre"
            | "College"
            | "Independant"
            | "LEA"
            | "University";
          /**
           * The MAT HQ details of the client
           */
          mathq?: "Unsure" | "Yes" | "No";
          /**
           * The Ofsted rating of the client
           */
          ofstedRating?:
            | "Outstanding"
            | "Good"
            | "Requires Improvement"
            | "Inadequate"
            | "NA - Independent"
            | "NA - Academy";
          /**
           * The date when the Ofsted report was checked
           *
           * @format date-time
           */
          dateOfstedReportChecked?: string | null;
          /**
           * The boarding details of the client
           */
          boarding?: "Unsure" | "Yes" | "No" | any | null;
          /**
           * The licence to issue sponsorship for the client
           */
          licenceToIssueSponsorship?: "Unsure" | "Yes" | "No" | any | null;
          /**
           * The total agency spend for the client
           */
          agencySpendTotal?: number;
          /**
           * Whether the client is VAT exempt
           */
          vatExempt?: boolean;
          /**
           * Whether to split invoices for the client
           */
          splitInvoices?: boolean;
          /**
           * The switch board details for the client
           */
          switchBoard?: string;
          /**
           * Whether the client is a GCA client
           */
          ccsClient?: boolean;
          /**
           * The GCA client URN
           */
          ccsClientUrn?: string;
          /**
           * The GCA client Ofsted URN
           */
          ccsClientOfstedUrn?: string;
          /**
           * The GCA client lot number
           */
          ccsClientLotNumber?: string;
          /**
           * The buyer reference supplied by a GCA client, when available.
           *
           * @maxLength 255
           */
          buyerReference?: string | null;
          /**
           * The GCA client invoice date
           *
           * @format date-time
           */
          ccsClientInvoiceDate?: string;
          /**
           * The key stage for the client
           */
          keyStage?: string;
          /**
           * The contact record for this client. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date when the client was last contacted.
           *
           * @format date-time
           */
          lastContactedDate?: string;
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Client fields that explain why the client matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * The client information that matched the search.
             *
             * @maxLength 256
             */
            field?: string;
            /**
             * A short excerpt showing why the client matched.
             *
             * @maxLength 1000
             */
            text?: string;
            /**
             * The exact terms within the content that matched the search.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
          }[];
          /**
           * The Client's distance in miles from the requested location. Present only when location filtering is used.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * The migration that created this client
           *
           * @pattern ^[0-9a-f]{24}$
           */
          migrationId?: string;
          /**
           * Whether this migrated client has been approved
           */
          migrationApproval?: "pending" | "approved" | "rejected";
          /**
           * Evertime details for this client
           */
          evertime?: {
            /**
             * The Evertime ClientId used for this client when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
            /**
             * The Evertime contacts to create from Edun (clientContactId set) or reference (clientContactId unset) when exporting payment runs
             */
            contacts?: {
              /**
               * The Evertime ClientContactId
               *
               * @maxLength 256
               */
              evertimeId?: string;
              /**
               * The Edun client contact this Evertime contact is created from; absent when the Evertime contact is only referenced (never created or overwritten)
               *
               * @maxLength 256
               */
              clientContactId?: string;
            }[];
            /**
             * The Evertime locations to create from the client's address (create set) or reference (create unset) when exporting payment runs
             */
            locations?: {
              /**
               * The Evertime LocationId
               *
               * @maxLength 256
               */
              evertimeId?: string;
              /**
               * True when this location is created from the client's address; false when the Evertime location is only referenced (never created or overwritten)
               */
              create?: boolean;
            }[];
            /**
             * Whether the matched Evertime client's name also matches the one held in Edun
             */
            nameMatch?: boolean;
          };
          /**
           * Timesheet Portal details for this client
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal client GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal client code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The address of the vacancy associated with the vacancy
     */
    clientAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The student associated with the vacancy
     *
     * @pattern ^[0-9a-f]{24}$
     */
    studentId?: string;
    /**
     * The postcode of the vacancy location
     *
     * @maxLength 10
     */
    postcode?: string;
    /**
     * The name of the role the vacancy is for
     *
     * @maxLength 256
     */
    role?: string;
    /**
     * The code associated with the role
     *
     * @maxLength 256
     */
    roleCode?: string;
    /**
     * Start date and time for the vacancy
     *
     * @format date-time
     */
    start?: string;
    /**
     * End date and time for the vacancy
     *
     * @format date-time
     */
    end?: string;
    /**
     * Start time for the vacancy
     */
    startTime?: string;
    /**
     * End time for the vacancy
     */
    endTime?: string;
    /**
     * The individual working days included in the vacancy.
     */
    days?: {
      /**
       * The start date and time for the booking day
       */
      start?: string;
      /**
       * The end date and time for the booking day
       */
      end?: string;
      /**
       * The length of the booking day
       */
      length?: "Half Day" | "Full Day";
      /**
       * Indicates whether the booking day is in the AM or PM
       */
      amPm?: "AM" | "PM";
      /**
       * The current status of the booking day
       */
      status?:
        | "Active"
        | "Sickness"
        | "NoShow"
        | "Client Cancelled"
        | "Act Of God"
        | "Personal Appointment";
      /**
       * The charge rate for the booking day
       *
       * @maxLength 32
       */
      chargeRate?: string;
      /**
       * The pay rate for the booking day
       *
       * @maxLength 32
       */
      payRate?: string;
      /**
       * Break duration inherited for this booking day.
       */
      breaks?: string;
      /**
       * Expenses payable for this booking day.
       */
      expenses?: string;
      /**
       * Indicates whether the booking day is flagged for removal
       */
      flaggedForRemoval?: boolean;
    }[];
    /**
     * The rate charged to the client for this vacancy.
     *
     * @maxLength 32
     */
    chargeRate?: string;
    /**
     * The fee percentage for the vacancy
     *
     * @maxLength 8
     */
    feePercentage?: string;
    /**
     * The Agency Workers Regulations value recorded for the vacancy.
     *
     * @maxLength 8
     */
    awr?: string;
    /**
     * The reminder value used to monitor Agency Workers Regulations requirements.
     *
     * @maxLength 8
     */
    awrReminder?: string;
    /**
     * The expected number of working hours for the vacancy.
     *
     * @maxLength 16
     */
    hours?: string;
    /**
     * The pay rate for the vacancy
     *
     * @maxLength 32
     */
    payRate?: string;
    /**
     * The arrival time for the vacancy
     */
    arrivalTime?: string;
    /**
     * The contact person at the vacancy for the vacancy
     */
    clientContact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
          /**
           * The role of the contact
           */
          role?:
            | "Head Teacher"
            | "Deputy Head"
            | "Cover Manager"
            | "HR Manager"
            | "Finance Manager"
            | "Head of Maths"
            | "Head of Science"
            | "Head of English"
            | "Assistant Headteacher"
            | "Headteacher's PA"
            | "SENCO"
            | "Other"
            | "Business Manager"
            | "Business Administrator"
            | "Invoice Contact"
            | "Academy Recruitment Contact"
            | "Nursery Manager"
            | "CEO"
            | "COO"
            | "Executive Headteacher"
            | "Inclusion Manager"
            | "EYFS Lead"
            | "Admin Team"
            | "Head of Humanities"
            | "Head of History"
            | "Head of Geography"
            | "Head of Business Studies"
            | "Head of PE"
            | "Head of KS1"
            | "Head of KS2"
            | "Head of KS3"
            | "Head of KS4"
            | "Virtual School Headteacher"
            | "Deputy Virtual School Headteacher"
            | "Post-16 Team Leader"
            | "SEN Team"
            | "Inclusion Team"
            | "UASC Team"
            | "Caseworker"
            | "Designated Teacher"
            | "Attendance Officer"
            | "Coordinator"
            | "Children's Commissioner"
            | "Timesheet Contact";
          /**
           * The name of the role if the role is 'Other'
           *
           * @maxLength 128
           */
          roleOther?: string;
          /**
           * The unique identifier of the contact
           *
           * @format uuid
           */
          contactId?: string;
          /**
           * Timesheet Portal details for this contact
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal approver GUID linked to this client contact
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal approver code
             *
             * @maxLength 256
             */
            code?: string;
          };
          /**
           * Whether the contact has opted out of communications
           */
          optOut?: boolean;
          /**
           * Any additional notes about the contact
           *
           * @maxLength 175
           */
          note?: string;
          /**
           * Whether the contact is a decision maker
           */
          decisionMaker?: boolean;
          /**
           * The status of the contact
           */
          status?: "Active" | "Passive" | "Inactive";
          /**
           * The Client associated with the contact.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          clientId?: string;
          /**
           * The name of the Client associated with the contact.
           *
           * @maxLength 256
           */
          clientName?: string;
          /**
           * The address of the Client associated with the contact.
           */
          clientAddress?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The local education authority of the associated Client.
           *
           * @maxLength 128
           */
          clientLea?: string;
          /**
           * The sector of the associated Client.
           *
           * @maxLength 128
           */
          clientType?: string;
          /**
           * The multi-academy trust of the associated Client.
           *
           * @maxLength 256
           */
          clientMultiAcademyTrust?: string;
          /**
           * The borough of the associated Client.
           *
           * @maxLength 128
           */
          clientBorough?: string;
          /**
           * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
           */
          clientAssignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * The specialism of the associated Client.
           *
           * @maxLength 256
           */
          clientSpecialism?: string;
          /**
           * When the associated Client was last updated.
           *
           * @format date-time
           */
          clientUpdatedAt?: string;
          /**
           * The associated Client's distance in miles from the requested location.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Contact and Client fields that explain why the contact matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * The contact or Client information that matched the search.
             *
             * @maxLength 256
             */
            field?: string;
            /**
             * A short excerpt showing why the contact matched.
             *
             * @maxLength 1000
             */
            text?: string;
            /**
             * The exact terms within the content that matched the search.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
          }[];
        };
    /**
     * Any additional instructions for the vacancy
     *
     * @maxLength 1024
     */
    additionalInstructions?: string;
    /**
     * Any additional instructions specific to the vacancy
     *
     * @maxLength 1024
     */
    additionalInstructionsClient?: string;
    /**
     * The current status of the vacancy
     */
    status?: "active" | "cancelled";
    /**
     * The user assigned to the vacancy. If expanded, a user object else the user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The pay type for the vacancy
     */
    payType?: "daily" | "hourly";
    /**
     * The type of the vacancy
     */
    type?: "standard" | "interview" | "trial" | "permanent" | "tutor";
    /**
     * The sub-type of the vacancy
     */
    subType?:
      | "tutor-standard"
      | "tutor-mentor"
      | "tutor-basic"
      | "face-to-face";
    /**
     * The payment arrangement expected for the candidate.
     */
    candidatePayType?: "PAYE" | "Umbrella" | "Limited Company";
    /**
     * The client name displayed with the vacancy.
     *
     * @maxLength 256
     */
    clientName?: string;
    /**
     * A description of the role, responsibilities, and requirements.
     *
     * @maxLength 2048
     */
    jobDescription?: string;
    /**
     * Whether the salary is expressed as an annual or daily amount.
     */
    salaryPayType?: "annual" | "daily";
    /**
     * The salary amount advertised for a permanent vacancy.
     *
     * @maxLength 32
     */
    salary?: string;
    /**
     * The weekly rota for the vacancy
     */
    weeklyRota?: {
      /**
       * The rota for Monday
       */
      ["1"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Tuesday
       */
      ["2"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Wednesday
       */
      ["3"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Thursday
       */
      ["4"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Friday
       */
      ["5"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Saturday
       */
      ["6"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Sunday
       */
      ["7"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
    };
    /**
     * The calculated distance to the vacancy when a location-based request supplies a reference point.
     */
    distance?: number;
    /**
     * Whether to include the school name in the vacancy details
     */
    includeSchool?: boolean;
    /**
     * Vacant bookings can be completed to multiple candidates at once. Specify the total number of available vacancies for this booking. This value is only visible within Edun, not visible externally.
     *  If left empty, 1 available vacancy for this placement will be presumed.
     *
     * @maxLength 256
     */
    noVacancies?: string;
    /**
     * Whether the vacancy should be shared to the job board
     */
    jobBoard?: boolean;
    /**
     * Notes about the client's interest in candidates for this vacancy.
     *
     * @maxLength 1024
     */
    clientInterestComments?: string;
    /**
     * Whether the required health and safety check has been recorded as complete.
     */
    healthSafetyCheck?: boolean;
    /**
     * The client contact associated with the health and safety check.
     *
     * @maxLength 256
     */
    healthSafetyCheckContact?: string;
    /**
     * The purchase order number for the vacancy
     */
    poNumber?: string;
    /**
     * The identifier assigned when the vacancy is published to CV-Library.
     */
    cvLibraryId?: number;
    /**
     * Whether the vacancy is archived
     */
    archived?: boolean;
    /**
     * The reason the vacancy was archived.
     *
     * @maxLength 1024
     */
    archiveReason?: string;
    /**
     * Whether the vacancy is completed
     */
    completed?: boolean;
    /**
     * The occupation for the vacancy
     *
     * @maxLength 256
     */
    occupation?: string;
    /**
     * The date the vacancy was posted
     *
     * @format date-time
     */
    postedOnDate?: string | null;
  };
};

export type CreateVacancyRequestBody = {
  /**
   * List of candidates associated with the vacancy
   */
  candidates: {
    /**
     * The candidate applying for the vacancy, either by ID or full details depending on select query
     */
    candidate:
      | string
      | {
          /**
           * The unique identifier of the candidate
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id: string;
          /**
           * The forename of the candidate
           *
           * @minLength 1
           * @maxLength 256
           */
          forename: string;
          /**
           * The surname of the candidate
           *
           * @minLength 1
           * @maxLength 256
           */
          surname: string;
          /**
           * Any previous names of the candidate
           *
           * @maxLength 256
           */
          previousNames?: string;
          /**
           * The email of the candidate. This will be used for communication.
           *
           * @format email
           */
          email: string;
          /**
           * The telephone number of the candidate
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the candidate
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The occupation of the candidate
           */
          occupation?:
            | "Teacher"
            | "Cover Supervisor"
            | "Teaching Assistant"
            | "School Support Staff"
            | "Primary Teacher"
            | "Secondary Teacher"
            | "Head Teacher"
            | "Senior Leader"
            | "Tutor"
            | "Mentor"
            | "Learning Support Assistant"
            | "Finance Officer"
            | "HR Administrator"
            | "HR Manager"
            | "Office Manager"
            | "Administrator"
            | "Personal Assistant"
            | "Pastoral Manager"
            | "Receptionist"
            | "Instructor"
            | "Caretaker"
            | "Nursery Assistant"
            | "Nursery Nurse"
            | "Deputy Nursery Manager"
            | "Lunchtime Supervisor"
            | "Classroom Graduate"
            | "Primary Cover Supervisor"
            | "HLTA"
            | "Nurture Teacher"
            | "Nurture Mentor"
            | "SENDCo"
            | "Registered Manager"
            | "Regional Manager"
            | "Education Director"
            | "Residential Director"
            | "Deputy Manager"
            | "Residential Support Worker"
            | "Sports Coach"
            | "Executive Headteacher"
            | "General Practitioner"
            | "Advance Nurse Practitioner (ANP)"
            | "Emergency Nurse Practitioner (ENP)"
            | "Advanced Clinical Practitioner (ACP)"
            | "Emergency Clinical Practitioner (ECP)"
            | "Consultant Psychiatrist"
            | "Psychiatrist \u2013 SPR"
            | "IT Technician"
            | "Art Technician"
            | "DT Technician"
            | "Science Technician"
            | "Healthcare Assistant/ Residential Support Worker"
            | "Healthcare Assistant"
            | "Senior Healthcare Assistant"
            | "Team Leader"
            | "Registered General Nurse"
            | "Registered Mental Nurse"
            | "Registered Nurse Learning Disabilities"
            | "Overseas Trained Teacher";
          /**
           * The subject the candidate specializes in
           *
           * @maxLength 256
           */
          subject?: string;
          /**
           * The address of the resource
           */
          address: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The qualifications of the candidate
           *
           * @maxItems 32
           */
          qualifications: {
            /**
             * The type of the qualification
             *
             * @maxLength 256
             */
            type?: string;
            /**
             * The place where the qualification was obtained
             *
             * @maxLength 256
             */
            place?: string;
            /**
             * The name of the qualification
             *
             * @maxLength 256
             */
            qualificationName?: string;
            /**
             * The date the qualification was obtained
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date?: string;
            /**
             * The unique identifier of the qualification
             *
             * @maxLength 256
             */
            qualificationId?: string;
            /**
             * The order index of the qualification
             *
             * @minimum 0
             */
            orderIndex?: number;
            /**
             * The expiry date of the qualification
             *
             * @maxLength 256
             */
            qualificationExpiry?: string;
            /**
             * Whether to exclude this qualification from vetting
             */
            excludeFromVetting?: boolean;
            /**
             * The date the qualification was created
             *
             * @format date-time
             */
            createdAt: string | null;
          }[];
          /**
           * The employment history of the candidate
           *
           * @maxItems 32
           */
          employment: {
            /**
             * The name of the employer
             *
             * @maxLength 256
             */
            employerName: string;
            /**
             * The date the candidate started employment
             *
             * @maxLength 256
             */
            dateStarted: string;
            /**
             * The date the candidate ended employment
             *
             * @maxLength 256
             */
            dateEnded?: string;
            /**
             * The job title of the candidate
             *
             * @maxLength 256
             */
            jobTitle?: string;
            /**
             * The reason the candidate left this employment
             *
             * @maxLength 1024
             */
            reasonForLeaving?: string;
            /**
             * The duties of the candidate in this role. Consultant only, visible on Edun CV
             *
             * @maxLength 1024
             */
            duties?: string;
            /**
             * The salary of the candidate in this role
             *
             * @maxLength 256
             */
            salary?: string;
            /**
             * The address of the candidate's reference for this employment
             */
            referenceAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The unique identifier for this employment record
             *
             * @maxLength 256
             */
            employmentId?: string;
            /**
             * The order index of this employment record
             *
             * @minimum 0
             */
            orderIndex?: number;
          }[];
          /**
           * The references for the candidate
           *
           * @maxItems 32
           */
          references: {
            /**
             * The title of the reference
             *
             * @maxLength 16
             */
            referenceTitle?: string;
            /**
             * The forename of the reference
             *
             * @maxLength 256
             */
            referenceForename: string;
            /**
             * The surname of the reference
             *
             * @maxLength 256
             */
            referenceSurname: string;
            /**
             * The company of the reference
             *
             * @maxLength 256
             */
            referenceCompany?: string;
            /**
             * The position of the reference
             *
             * @maxLength 256
             */
            referencePosition?: string;
            /**
             * The email of the reference
             *
             * @format email
             */
            referenceEmail: string;
            /**
             * The telephone number of the reference
             *
             * @maxLength 32
             */
            referenceTelephone: string;
            /**
             * The type of the reference
             */
            referenceType:
              | "Education"
              | "Professional"
              | "Character"
              | "Factual"
              | "Academic"
              | "Volunteer";
            /**
             * The duration of the reference
             *
             * @maxLength 128
             */
            referenceDuration: string;
            /**
             * The ID of the reference
             *
             * @maxLength 256
             */
            referenceId?: string;
            /**
             * This field only applies if the reference immediate consent question is enabled
             * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
             */
            referenceApply?: boolean;
            /**
             * Any additional notes about the reference
             *
             * @maxLength 1024
             */
            referenceNote?: string;
            /**
             * The start date of the reference
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            dateFrom?: string;
            /**
             * The end date of the reference
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            dateTo?: string;
          }[];
          /**
           * Whether the candidate has consented to a reference check
           */
          referenceCheckConsent?: boolean;
          /**
           * The name of the person who provided consent for the reference check
           *
           * @maxLength 256
           */
          referenceCheckConsentName?: string;
          /**
           * The date when consent for the reference check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          referenceCheckConsentDate?: string;
          /**
           * The date of birth of the candidate
           *
           * @format date-time
           */
          dob?: string | null;
          /**
           * The sex of the candidate
           */
          sex?: "Male" | "Female" | "Other" | "Unknown";
          /**
           * The status of the candidate
           */
          status:
            | "Pending"
            | "Approved"
            | "Rejected"
            | "Anonymised"
            | "Archived"
            | "Enquired"
            | "Hold"
            | "Future"
            | "DNC"
            | "Pre";
          /**
           * The current status of the onboarding process for the candidate
           */
          onboardingStatus: "Not Started" | "In Progress" | "Completed";
          /**
           * Whether the onboarding information has been sent to the candidate
           */
          onboardingSent?: boolean;
          /**
           * The next of kin information for the candidate
           */
          nextOfKin?: {
            /**
             * The name of the next of kin
             *
             * @maxLength 256
             */
            nextOfKinName?: string;
            /**
             * The relationship of the next of kin to the candidate
             *
             * @maxLength 256
             */
            nextOfKinRelationship?: string;
            /**
             * The telephone number of the next of kin
             *
             * @maxLength 32
             */
            nextOfKinTelephone?: string;
            /**
             * The email address of the next of kin
             *
             * @format email
             */
            nextOfKinEmail?: string;
            /**
             * The address of the resource
             */
            nextOfKinAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
          };
          /**
           * The criminal record information for the candidate
           */
          criminalRecord?: {
            /**
             * Whether the candidate has a DBS certificate
             */
            dbsHeld?: boolean;
            /**
             * The unique identifier for the DBS certificate
             *
             * @maxLength 256
             */
            dbsNumber?: string;
            /**
             * The type of DBS check
             */
            dbsType?:
              | "Basic"
              | "Standard"
              | "Enhanced"
              | "Enhanced with Barred List"
              | "Child workforce"
              | "Child and adult workforce"
              | "Adult workforce";
            /**
             * The date when the DBS check was last performed
             *
             * @maxLength 256
             */
            dbsLastChecked?: string;
            /**
             * The DBS update service number for the candidate
             *
             * @maxLength 256
             */
            dbsUpdateServiceNumber?: string;
            /**
             * The result of the prohibition check
             *
             * @maxLength 256
             */
            prohibitionCheck?: string;
            /**
             * The result of the overseas check
             *
             * @maxLength 256
             */
            overseasCheck?: string;
            /**
             * Any additional notes regarding the overseas check
             *
             * @maxLength 256
             */
            overseasCheckNote?: string;
            /**
             * Whether the candidate has a convicted offence
             */
            convictedOffence?: boolean;
            /**
             * The description of the convicted offence
             *
             * @maxLength 1024
             */
            convictedOffenceDescription?: string;
            /**
             * Whether the candidate has any convictions
             */
            convictions?: boolean;
            /**
             * The description of the convictions
             *
             * @maxLength 1024
             */
            convictionsDescription?: string;
            /**
             * Whether the candidate is registered to the DBS update service
             */
            registeredToUpdateService?: boolean;
            /**
             * Whether the candidate consents to update service checks
             */
            consentUpdateServiceChecks?: boolean;
            /**
             * Any additional notes regarding the candidate's criminal record
             *
             * @maxLength 1024
             */
            note?: string;
            /**
             * The date when the update service check was performed
             *
             * @maxLength 64
             */
            updateServiceCheckDate?: string;
            /**
             * Whether the candidate has lived abroad
             */
            abroad?: boolean;
            /**
             * Any additional notes regarding the overseas declaration
             *
             * @maxLength 1024
             */
            overseasDeclarationExtended?: string;
            /**
             * Whether the candidate has been subject to educational disciplinary proceedings
             */
            educationalDisciplinaryProceedings?: boolean;
            /**
             * Any comments regarding the DBS check
             *
             * @maxLength 1024
             */
            dbsComments?: string;
            /**
             * Whether the candidate has read the DBS declaration
             */
            dbsDeclarationRead?: boolean;
            /**
             * Whether the candidate has any overseas offences
             */
            overseasOffence?: boolean;
            /**
             * The description of the overseas offence
             *
             * @maxLength 1024
             */
            overseasOffenceDescription?: string;
            /**
             * Whether the candidate has a current criminal offence
             */
            currentCriminalOffence?: boolean;
            /**
             * The description of the current criminal offence
             *
             * @maxLength 1024
             */
            currentCriminalOffenceDescription?: string;
            /**
             * Whether the DBS was issued by a business
             */
            dbsIssuedByBusiness?: boolean;
            /**
             * Whether the DBS was issued by the current employer
             */
            dbsIssuedByCurrentEmployer?: boolean;
            /**
             * Whether a Section 128 check is required for the candidate.
             */
            section128Required?: boolean;
            /**
             * The date when the Section 128 check was completed.
             */
            section128Checked?: string;
          };
          /**
           * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
           */
          criminalRecordDeclaration?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * The answers recorded for the point-in-time question set.
             *
             * @maxItems 100
             */
            answers: {
              /**
               * The identifier of the question being answered.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              questionId: string;
              /**
               * The stable key of the question being answered.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey: string;
              /**
               * The submitted answer to the question.
               */
              value: string | string | boolean | number | (void | null);
            }[];
            /**
             * When the question set was submitted.
             *
             * @format date-time
             */
            submittedAt?: string;
            /**
             * The network address recorded when the response was submitted.
             *
             * @maxLength 64
             */
            submittedIp?: string;
            /**
             * When the question configuration captured by this response was last updated.
             *
             * @format date-time
             */
            configurationUpdatedAt?: string;
          };
          /**
           * Whether the candidate confirmed their criminal record declaration.
           */
          criminalRecordCheckConsent?: boolean;
          /**
           * The name used to confirm the criminal record declaration.
           *
           * @maxLength 256
           */
          criminalRecordCheckConsentName?: string;
          /**
           * The date the criminal record declaration was confirmed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          criminalRecordCheckConsentDate?: string;
          /**
           * The safe in education information for the candidate
           */
          safeInEducation?: {
            /**
             * Whether the candidate is barred from working in education
             */
            barred?: boolean;
            /**
             * The date when the barred status was checked
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            barredCheckDate?: string;
            /**
             * Whether the candidate has a waiver for the barred status
             */
            waiver?: boolean;
            /**
             * Any orders related to the candidate's barred status
             */
            ordersYou?: boolean;
            /**
             * Any orders related to the candidate's barred status for children
             */
            ordersChild?: boolean;
            /**
             * Whether the candidate has any previous safeguarding allegations
             */
            previousSafeguardingAllegations?: boolean;
            /**
             * Whether the candidate has been cautioned
             */
            cautioned?: boolean;
            /**
             * Whether the candidate has a violent offence
             */
            violentOffence?: boolean;
            /**
             * Whether the candidate has lived or worked in another country
             */
            anotherCountry?: boolean;
            /**
             * Whether the candidate has read the Keeping Children Safe in Education document
             */
            readKCSIE?: boolean;
            /**
             * Whether the candidate has read the Disqualification under the Childcare Act guidance
             */
            readChildcareAct?: boolean;
            /**
             * Whether the candidate is prohibited from teaching
             */
            prohibitedFromTeaching?: boolean;
            /**
             * Whether the candidate is known to the police
             */
            knownPolice?: boolean;
            /**
             * Whether the candidate is prohibited from management roles
             */
            prohibitedFromManagement?: boolean;
            /**
             * Whether the candidate has any criminal offences under law
             */
            criminalOffencesLaw?: boolean;
            /**
             * Any additional details regarding the candidate's safe in education status
             */
            safeInEducationDetails?: string;
            /**
             * The safeguarding questionnaire responses for the candidate
             */
            safeguardingQuestionaire?: {
              /**
               * What is the primary purpose of safeguarding in a school environment?
               */
              primaryPurposeSafeguarding:
                | "enhance-academic-performance"
                | "ensure-safe-learning-environment"
                | "promote-healthy-relationships"
                | "facilitate-extracurricular-activities";
              /**
               * Who is typically responsible for coordinating safeguarding efforts within a school?
               */
              typicallyResponsibleCoordinatingSafeguarding:
                | "janitorial-staff"
                | "parents-association"
                | "school-nurse"
                | "designated-safeguarding-lead";
              /**
               * Which of the following is considered a potential sign of child abuse or neglect?
               */
              signOfAbuse:
                | "student-excelling"
                | "frequent-extracurricular"
                | "unexplained-injuries"
                | "not-listening";
              /**
               * How should a teacher respond if a student discloses information about potential abuse or harm?
               */
              studentDisclosesInformation:
                | "ignore-disclosure"
                | "report-disclosure"
                | "share-information"
                | "advise-independent";
              /**
               * What is the purpose of a school's Child Protection Policy?
               */
              schoolsChildProtectionPolicy:
                | "enforcing-strict-disciplinary-measures"
                | "identifying-troublemakers"
                | "outlining-procedures-for-protecting-students-from-harm"
                | "establishing-academic-achievement-standards";
              /**
               * What does the term 'FGM' stand for in the context of safeguarding?
               */
              fgmDefinition:
                | "female-general-medicine"
                | "forced-genetic-modification"
                | "female-genital-mutilation"
                | "foundation-gender-equality";
              /**
               * Which of the following is a key principle of online safety for students?
               */
              keyPrincipleOnlineSafetyStudents:
                | "unlimited-screen-time"
                | "anon-online-interaction"
                | "responsible-digital-citizen"
                | "restrict-edu-sites";
              /**
               * When planning a school trip, what should teachers consider regarding safeguarding?
               */
              teachersConsiderRegardingSafeguarding:
                | "minimise-supervision"
                | "appropriate-staff-ratios"
                | "exclude-medical-conditions"
                | "allow-roaming";
              /**
               * What is the purpose of a criminal background check for teachers?
               */
              criminalBackgroundCheckTeachers:
                | "evaluating-teaching-proficiency"
                | "verifying-immigration-status"
                | "assessing-financial-stability"
                | "identifying-criminal-history";
              /**
               * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
               */
              studentConfides:
                | "assure-secret-safe"
                | "encourage-share-friends"
                | "explain-no-guarantee"
                | "disclose-to-others";
            };
          };
          /**
           * The social media information for the candidate
           */
          socialMedia?: {
            /**
             * The Facebook profile URL of the candidate
             *
             * @maxLength 256
             */
            facebook?: string;
            /**
             * The Instagram profile URL of the candidate
             *
             * @maxLength 256
             */
            instagram?: string;
            /**
             * The Twitter profile URL of the candidate
             *
             * @maxLength 256
             */
            twitter?: string;
            /**
             * The LinkedIn profile URL of the candidate
             *
             * @maxLength 256
             */
            linkedIn?: string;
            /**
             * The TikTok profile URL of the candidate
             *
             * @maxLength 256
             */
            tiktok?: string;
            /**
             * Any other social media profile URLs of the candidate
             *
             * @maxLength 256
             */
            others?: string;
            /**
             * Whether the social media profiles have been checked
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            socialMediaChecked?: string;
            /**
             * The date the candidate's Google search check was completed
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            googleCheckedDate?: string;
          };
          /**
           * Whether the candidate has consented to a safe in education check
           */
          safeInEducationCheckConsent: boolean;
          /**
           * The name of the person who provided consent for the safe in education check
           *
           * @maxLength 256
           */
          safeInEducationCheckConsentName: string;
          /**
           * The date when consent for the safe in education check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          safeInEducationCheckConsentDate: string;
          /**
           * The health record of the individual
           */
          healthRecord: {
            /**
             * Whether the individual has a physical ability condition
             */
            physicalAbilityCondition?: boolean;
            /**
             * Whether the individual is confused
             */
            confused?: boolean;
            /**
             * Whether the individual has hearing issues
             */
            hearing?: boolean;
            /**
             * Whether the individual has eyesight issues
             */
            eyesight?: boolean;
            /**
             * Whether the individual has depression
             */
            depression?: boolean;
            /**
             * Whether the individual has severe pain
             */
            severePain?: boolean;
            /**
             * Whether the individual has excessive drowsiness
             */
            excessiveDrowsiness?: boolean;
            /**
             * Whether the individual has epilepsy
             */
            epilepsy?: boolean;
            /**
             * Whether the individual has heart issues
             */
            heart?: boolean;
            /**
             * Whether the individual has asthma
             */
            asthma?: boolean;
            /**
             * Whether the individual has mental health issues
             */
            mentalHealth?: boolean;
            /**
             * Whether the individual is an expectant mother
             */
            expectantMother?: boolean;
            /**
             * Whether the individual has allergies
             */
            allergies?: boolean;
            /**
             * Whether the individual is taking medication
             */
            medication?: boolean;
            /**
             * Whether the individual has any health issues
             */
            healthIssues?: boolean;
            /**
             * A description of the individual's health issues
             *
             * @maxLength 2048
             */
            healthIssuesDescription?: string;
            /**
             * Any disability needs of the individual
             *
             * @maxLength 2048
             */
            disabilityNeeds?: string;
            /**
             * Whether the individual has any contagious diseases
             */
            contagiousDiseases?: boolean;
            /**
             * Whether the individual's health affects their ability
             */
            affectAbility?: boolean;
            /**
             * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
             */
            notificationAgree?: boolean;
            /**
             * Whether the individual has opted into the SAS Wellbeing App
             */
            sasOptIn?: boolean;
            /**
             * The individual's signature
             *
             * @maxLength 256
             */
            signature?: string;
            /**
             * The individual's drawn signature in base64url format
             *
             * @maxLength 4096
             */
            drawnSignature?: string;
            /**
             * The date the individual signed the form
             *
             * @format date-time
             */
            signatureDate?: string | null;
            /**
             * Whether the individual has been vaccinated against measles
             */
            vaccinationMeasles?: boolean;
            /**
             * Whether the individual has been vaccinated against mumps
             */
            vaccinationMumps?: boolean;
            /**
             * Whether the individual has been vaccinated against rubella
             */
            vaccinationRubella?: boolean;
            /**
             * Whether the individual has been vaccinated against varicella
             */
            vaccinationVaricella?: boolean;
            /**
             * Whether the individual has been vaccinated against tuberculosis
             */
            vaccinationTuberculosis?: boolean;
            /**
             * Whether the individual has been vaccinated against hepatitis B
             */
            vaccinationHepatitisB?: boolean;
            /**
             * Whether the individual has been vaccinated against hepatitis C
             */
            vaccinationHepatitisC?: boolean;
            /**
             * Whether the individual has been vaccinated against HIV
             */
            vaccinationHIV?: boolean;
            /**
             * Whether the individual has been vaccinated against COVID-19
             */
            vaccinationCovid?: boolean;
            /**
             * Whether the individual has received the 1st dose of the COVID-19 vaccine
             */
            vaccinationCovid1?: boolean;
            /**
             * Whether the individual has received the 2nd dose of the COVID-19 vaccine
             */
            vaccinationCovid2?: boolean;
            /**
             * Whether the individual has received the COVID-19 booster vaccine
             */
            vaccinationCovidBooster?: boolean;
            /**
             * Whether the individual has received the COVID-19 influenza vaccine
             */
            vaccinationCovidInfluenza?: boolean;
            /**
             * Whether the individual has an override for fit to work requirements
             */
            fitToWorkOverride?: boolean;
          };
          /**
           * The candidate's latest submitted health declaration, including the exact questions presented.
           */
          healthDeclaration?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * The answers recorded for the point-in-time question set.
             *
             * @maxItems 100
             */
            answers: {
              /**
               * The identifier of the question being answered.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              questionId: string;
              /**
               * The stable key of the question being answered.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey: string;
              /**
               * The submitted answer to the question.
               */
              value: string | string | boolean | number | (void | null);
            }[];
            /**
             * When the question set was submitted.
             *
             * @format date-time
             */
            submittedAt?: string;
            /**
             * The network address recorded when the response was submitted.
             *
             * @maxLength 64
             */
            submittedIp?: string;
            /**
             * When the question configuration captured by this response was last updated.
             *
             * @format date-time
             */
            configurationUpdatedAt?: string;
          };
          /**
           * Whether the candidate confirmed their health declaration.
           */
          healthRecordCheckConsent?: boolean;
          /**
           * The name used to confirm the health declaration.
           *
           * @maxLength 256
           */
          healthRecordCheckConsentName?: string;
          /**
           * The date the health declaration was confirmed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          healthRecordCheckConsentDate?: string;
          /**
           * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
           */
          bankDetails: {
            /**
             * The name on the bank account
             */
            bankAccountName?: string;
            /**
             * The name of the bank
             */
            bankName?: string;
            /**
             * The account number
             */
            accountNumber?: string;
            /**
             * The sort code
             */
            sortCode?: string;
            /**
             * The National Insurance number
             */
            niNumber?: string;
            /**
             * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
             */
            jobOp1?: boolean;
            /**
             * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
             */
            jobOp2?: boolean;
            /**
             * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
             */
            jobOp3?: boolean;
            /**
             * Details about the candidate's student loan status
             */
            studentLoan?: {
              /**
               * Boolean flag for if candidate has student loan
               */
              studentLoan: boolean;
              /**
               * Boolean flag for if candidate is on plan 1
               */
              plan1: boolean;
              /**
               * Boolean flag for if candidate is on plan 2
               */
              plan2: boolean;
              /**
               * Boolean flag for if candidate is on plan 4
               */
              plan4: boolean;
              /**
               * Boolean flag for if candidate has postgrad loan
               */
              postGradLoan: boolean;
            };
          };
          /**
           * Whether the candidate has confirmed their details are correct.
           */
          declarationCheckConsent: boolean;
          /**
           * The name of the person who confirmed their details are correct.
           *
           * @maxLength 256
           */
          declarationCheckConsentName?: string;
          /**
           * The date when consent for the declaration check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          declarationCheckConsentDate?: string;
          /**
           * Whether the candidate has consented to a contract for service
           */
          contractForServiceConsent: boolean;
          /**
           * The name of the person who provided consent for the contract for service
           *
           * @maxLength 256
           */
          contractForServiceName?: string;
          /**
           * The date when consent for the contract for service was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          contractForServiceDate?: string;
          /**
           * The candidate has completed their onboarding form.
           */
          confirmed: boolean;
          /**
           * The ID of the user who created the candidate record.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          createdBy: string;
          /**
           * The ID of the customer associated with the candidate.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customerId: string;
          /**
           * The migration that created this candidate
           *
           * @pattern ^[0-9a-f]{24}$
           */
          migrationId?: string;
          /**
           * Whether this migrated candidate has been approved
           */
          migrationApproval?: "pending" | "approved" | "rejected";
          /**
           * The user assigned to the candidate. If expanded, a user object else the user ID.
           */
          assignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold: {
                  min: number | string;
                  commission: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * The ID of the current booking for the candidate.
           */
          currentBooking?: string[];
          /**
           * The URL of the candidate's profile photo.
           *
           * @maxLength 256
           */
          profilePhoto?: string;
          /**
           * The pay type for the candidate.
           */
          payType: "PAYE" | "Umbrella" | "Limited Company" | "Not Applicable";
          /**
           * The pay interval for the candidate.
           */
          payInterval:
            | "Monthly"
            | "Weekly"
            | "Weekly Pay in Arrears"
            | "Bi-Weekly"
            | any
            | null;
          /**
           * The pay rate for the candidate.
           */
          payRate?: string;
          /**
           * A custom ID for the candidate.
           *
           * @maxLength 256
           */
          customId?: string;
          /**
           * A company-defined identifier used to match the candidate with external records.
           *
           * @maxLength 256
           */
          _internalId?: string;
          /**
           * The date when the candidate's status was last changed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          statusChangeDate?: string;
          /**
           * Whether the candidate has opted out of communications.
           */
          optOut?: boolean;
          /**
           * The TRN number for the candidate.
           *
           * @maxLength 256
           */
          trnNumber?: string;
          /**
           * The date when the TRN number was last checked.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          trnNumberCheckedDate?: string;
          /**
           * Any notes related to the TRN number.
           *
           * @maxLength 1024
           */
          trnNumberNotes?: string;
          /**
           * The date when the candidate's employment is set to start.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          employmentStartDate?: string;
          /**
           * Whether the candidate has confirmed their Early Years Workforce status.
           */
          ewcConfirmation?: boolean;
          /**
           * The share code for the candidate's Home Office application.
           *
           * @maxLength 256
           */
          homeOfficeShareCode?: string;
          /**
           * Whether the candidate has been archived.
           */
          archived?: boolean;
          /**
           * Whether the candidate is using an umbrella company.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          umbrella?: string;
          /**
           * ID of the PAYE umbrella company.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          payeUmbrella?: string;
          /**
           * The name of the umbrella company the candidate is using.
           *
           * @maxLength 256
           */
          umbrellaCompanyName?: string;
          /**
           * The code of the umbrella company the candidate is using.
           *
           * @maxLength 256
           */
          umbrellaCompanyCode?: string;
          /**
           * The transport method for the candidate.
           */
          transport?:
            | "Drives Own Vehicle"
            | "Use Public Transport"
            | "Access to Another Vehicle"
            | "Bicycle"
            | "No Access"
            | "Other";
          /**
           * Any important notes related to the candidate.
           *
           * @maxLength 1024
           */
          importantNotes?: string;
          /**
           * The date when the candidate was last updated.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          candidateLastUpdated?: string;
          /**
           * The source from which the candidate was obtained.
           */
          source?:
            | (
                | "Company Website"
                | "iCan Teach UK Website"
                | "Apply to Education"
                | "CV Library - CV Search"
                | "CV Library Advert"
                | "CV Library - Watchdog"
                | "Guardian- CV Search"
                | "Guardian Advert"
                | "Indeed - CV Search"
                | "Indeed Advert"
                | "Reed - CV Search"
                | "Reed Advert"
                | "Reed \u2013 Watchdog"
                | "SENploy - Advert"
                | "Total Jobs"
                | "Other Job Board"
                | "Google"
                | "LinkedIn"
                | "Linkedin Recruiter"
                | "LinkedIn Advert"
                | "Referral - School"
                | "Referral - Candidate"
                | "UK University"
                | "Irish University"
                | "Canadian University"
                | "Marketing Campaign"
                | "Woo"
                | "Job Show"
                | "Recruitment Fair"
                | "Career Fair"
                | "Volcanic"
                | "Broadbean"
                | "Headhunt"
                | "Referral"
                | "Direct"
                | "Other"
              )
            | (
                | "Company Website"
                | "Direct Call"
                | "CV Library Watchdog"
                | "CV Library Advert"
                | "CV Library CV Search"
                | "Reed Watchdog"
                | "Reed Advert"
                | "Reed CV Search"
                | "Indeed Watchdog"
                | "Indeed Advert"
                | "Indeed CV Search"
                | "Job Centre"
                | "Returning Candidate"
                | "Referral Candidate"
                | "Referral Client"
                | "Training Provider"
                | "Recruitment Fair"
                | "University/College"
              );
          /**
           * Any other source from which the candidate was obtained.
           *
           * @maxLength 256
           */
          sourceOther?: string;
          /**
           * Whether the candidate has given consent for online checks.
           */
          onlineCheckConsent?: boolean;
          /**
           * The title of the candidate.
           */
          title?:
            | "Mr"
            | "Mrs"
            | "Ms"
            | "Miss"
            | "Dr"
            | "Mx"
            | "Lady"
            | "Lord"
            | "Other";
          /**
           * The pronoun used by the candidate.
           */
          pronoun?:
            | "She/Her/Hers"
            | "He/Him/His"
            | "They/Them/Theirs"
            | "Ze/Hir/Hirs"
            | "Ze/Zir/Zirs"
            | "Other"
            | "Prefer not to say";
          /**
           * The date when the candidate was last updated.
           *
           * @format date-time
           */
          updatedAt?: string;
          /**
           * The date when the candidate was last contacted.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          lastContactedDate?: string;
          /**
           * A short, prominent note shown with the candidate to communicate important context.
           */
          highlight?: string;
          /**
           * The pay scale for the candidate's teaching position.
           *
           * @maxLength 256
           */
          teacherPayScale?: string;
          /**
           * Whether the candidate has a P45.
           */
          p45?: boolean;
          /**
           * The type of role the candidate is applying for.
           */
          roleType?:
            | "Senior Leadership Positions"
            | "Teaching Roles"
            | "School Support Positions";
          /**
           * The Right to Work verification status for the candidate.
           *
           * @maxLength 256
           */
          rtwVerification?: string;
          /**
           * Any notes related to the Right to Work verification status for the candidate.
           *
           * @maxLength 1024
           */
          rtwVerificationNote?: string;
          /**
           * The Right to Work verification status for the candidate.
           *
           * @maxLength 256
           */
          rtwVerificationStatus?: string;
          /**
           * The location where the candidate desires to work.
           *
           * @maxLength 256
           */
          desiredLocation?: string;
          /**
           * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
           */
          complianceUser?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold: {
                  min: number | string;
                  commission: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
           */
          watchers?:
            | string[]
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold: {
                  min: number | string;
                  commission: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              }[];
          /**
           * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
           */
          tags?:
            | string[]
            | {
                /**
                 * The unique identifier of the tag
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id: string;
                /**
                 * The label displayed wherever this tag is applied.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                name: string;
                /**
                 * The tag display colour as a hexadecimal value, for example #2563EB.
                 *
                 * @maxLength 32
                 */
                color?: string | null;
              }[];
          /**
           * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
           *
           * @maxItems 5
           */
          documents?: Schemas.Document[];
          /**
           * The total number of documents attached to the candidate.
           *
           * @minimum 0
           */
          documentsCount?: number;
          /**
           * The locations where the candidate desires to work.
           */
          desiredLocationMultiple?: string[];
          /**
           * Daily availability for the requested period. Returned by the availability view only.
           *
           * @maxItems 31
           */
          availabilityDays?: {
            /**
             * Calendar date in YYYY-MM-DD format.
             *
             * @maxLength 10
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date: string;
            /**
             * Availability or booking state for this date.
             */
            status:
              | "available"
              | "unavailable"
              | "booked"
              | "halfDay"
              | "awaiting"
              | "unknown";
            /**
             * Recorded availability, or null when unrecorded.
             */
            available: boolean | null;
            /**
             * Morning or afternoon availability.
             */
            partial: "AM" | "PM" | any | null;
            /**
             * Additional availability information.
             *
             * @maxLength 4096
             */
            note?: string | null;
            /**
             * Active bookings on this date.
             *
             * @maxItems 100
             */
            bookings: {
              /**
               * The unique identifier of the resource
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * @maxLength 512
               */
              clientName?: string;
              /**
               * @maxLength 512
               */
              role?: string;
              /**
               * @maxLength 32
               */
              timeDesignation?: string;
            }[];
          }[];
          /**
           * The date when the candidate's availability was last checked.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          availabilityLastChecked?: string;
          /**
           * Whether the candidate is subject to notice period.
           */
          subjectToNotice: "Unsure" | "Yes" | "No";
          /**
           * The subsidiary subjects the candidate is qualified to teach.
           */
          subsidiarySubject?: string[];
          /**
           * The candidate's preferred employment type.
           */
          employmentPreference?: (
            | "Day To Day"
            | "Long Term"
            | "Fixed Term"
            | "Perm"
          )[];
          /**
           * Any other names the candidate is known by.
           *
           * @maxLength 256
           */
          otherNames?: string;
          /**
           * The name of the candidate's limited company, if applicable.
           *
           * @maxLength 256
           */
          ltdCompany?: string;
          /**
           * Whether the candidate's employment has been verified.
           */
          employmentVerified?: boolean;
          /**
           * The DBS verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSVerification?: string;
          /**
           * Any notes related to the DBS verification status for the candidate.
           *
           * @maxLength 1024
           */
          candidateDBSVerificationNote?: string;
          /**
           * The DBS verification status for the candidate.
           */
          candidateDBSVerificationStatus?: "Cleared" | "Alerts";
          /**
           * The DBS check verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSCheckVerification?: string;
          /**
           * Any notes related to the DBS check verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSCheckVerificationNote?: string;
          /**
           * The DBS check verification status for the candidate.
           */
          candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
          /**
           * Any notes related to the DBS update service verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSUpdateServiceVerificationNote?: string;
          /**
           * The DBS update service verification status for the candidate.
           */
          candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
          /**
           * Any notes related to the TRN verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateTRNVerificationNote?: string;
          /**
           * The TRN verification status for the candidate.
           */
          candidateTRNVerificationStatus?:
            | "Completed Induction"
            | "Not Yet Completed Induction"
            | "Induction Exempt"
            | "ALERTS - Failed Induction"
            | "Induction Not Required";
          /**
           * The health and safety verification status for the candidate.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          candidateHealthSafetyVerification?: string;
          /**
           * Any notes related to the health and safety verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateHealthSafetyVerificationNote?: string;
          /**
           * The health and safety verification status for the candidate.
           */
          candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
          /**
           * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
           */
          onboardingSession:
            | string
            | {
                /**
                 * Unique identifier for the onboarding session
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id: string;
                /**
                 * The URL for the onboarding session
                 *
                 * @format uri
                 */
                url: string;
                /**
                 * The frozen health declaration configuration for this onboarding session.
                 */
                healthDeclarationConfig?: {
                  /**
                   * The ordered questions included in the question set.
                   *
                   * @maxItems 100
                   */
                  questions: {
                    /**
                     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id?: string;
                    /**
                     * A stable key used to associate the question with current and historical answers.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    key: string;
                    /**
                     * The question shown to the person completing the form.
                     *
                     * @minLength 1
                     * @maxLength 500
                     */
                    label: string;
                    /**
                     * Optional supporting text displayed with the question.
                     *
                     * @maxLength 2000
                     */
                    description?: string;
                    /**
                     * The type of answer collected for the question.
                     */
                    type:
                      | "checkbox"
                      | "date"
                      | "daterange"
                      | "dropdown"
                      | "number"
                      | "signature"
                      | "text"
                      | "textarea";
                    /**
                     * Whether an answer is required while the question is visible.
                     */
                    required: boolean;
                    /**
                     * Whether the question is included in new forms. Historical responses retain disabled questions.
                     */
                    enabled: boolean;
                    /**
                     * The display order of the question.
                     *
                     * @minimum 0
                     * @maximum 1000
                     */
                    order: number;
                    /**
                     * Optional rules that determine when the question is shown.
                     */
                    condition?: {
                      /**
                       * Whether any rule or every rule must match before the question is shown.
                       */
                      mode: "any" | "all";
                      /**
                       * The comparisons used to determine question visibility.
                       *
                       * @minItems 1
                       * @maxItems 20
                       */
                      rules: {
                        /**
                         * The stable key of the question that controls visibility.
                         *
                         * @minLength 1
                         * @maxLength 128
                         */
                        questionKey: string;
                        /**
                         * How the controlling answer is compared with the value.
                         */
                        operator: "equals" | "notEquals";
                        /**
                         * The answer value used to determine visibility.
                         */
                        value: string | boolean | number | (void | null);
                      }[];
                    };
                    /**
                     * The available answers for a dropdown question.
                     *
                     * @minItems 1
                     * @maxItems 100
                     */
                    options?: {
                      /**
                       * The value stored when this option is selected.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      value: string;
                      /**
                       * The option displayed to the person answering the question.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      label: string;
                    }[];
                    /**
                     * Whether this question belongs to the standard template.
                     */
                    ["default"]?: boolean;
                    /**
                     * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                     */
                    audience?: "candidate" | "staff";
                    /**
                     * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                     */
                    fixedRole?: "fullName" | "signature";
                  }[];
                  /**
                   * When this question configuration was last updated.
                   *
                   * @format date-time
                   */
                  updatedAt?: string;
                };
                /**
                 * The frozen Criminal Record & Security Checks configuration for this onboarding session.
                 */
                criminalRecordConfig?: {
                  /**
                   * The ordered questions included in the question set.
                   *
                   * @maxItems 100
                   */
                  questions: {
                    /**
                     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id?: string;
                    /**
                     * A stable key used to associate the question with current and historical answers.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    key: string;
                    /**
                     * The question shown to the person completing the form.
                     *
                     * @minLength 1
                     * @maxLength 500
                     */
                    label: string;
                    /**
                     * Optional supporting text displayed with the question.
                     *
                     * @maxLength 2000
                     */
                    description?: string;
                    /**
                     * The type of answer collected for the question.
                     */
                    type:
                      | "checkbox"
                      | "date"
                      | "daterange"
                      | "dropdown"
                      | "number"
                      | "signature"
                      | "text"
                      | "textarea";
                    /**
                     * Whether an answer is required while the question is visible.
                     */
                    required: boolean;
                    /**
                     * Whether the question is included in new forms. Historical responses retain disabled questions.
                     */
                    enabled: boolean;
                    /**
                     * The display order of the question.
                     *
                     * @minimum 0
                     * @maximum 1000
                     */
                    order: number;
                    /**
                     * Optional rules that determine when the question is shown.
                     */
                    condition?: {
                      /**
                       * Whether any rule or every rule must match before the question is shown.
                       */
                      mode: "any" | "all";
                      /**
                       * The comparisons used to determine question visibility.
                       *
                       * @minItems 1
                       * @maxItems 20
                       */
                      rules: {
                        /**
                         * The stable key of the question that controls visibility.
                         *
                         * @minLength 1
                         * @maxLength 128
                         */
                        questionKey: string;
                        /**
                         * How the controlling answer is compared with the value.
                         */
                        operator: "equals" | "notEquals";
                        /**
                         * The answer value used to determine visibility.
                         */
                        value: string | boolean | number | (void | null);
                      }[];
                    };
                    /**
                     * The available answers for a dropdown question.
                     *
                     * @minItems 1
                     * @maxItems 100
                     */
                    options?: {
                      /**
                       * The value stored when this option is selected.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      value: string;
                      /**
                       * The option displayed to the person answering the question.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      label: string;
                    }[];
                    /**
                     * Whether this question belongs to the standard template.
                     */
                    ["default"]?: boolean;
                    /**
                     * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                     */
                    audience?: "candidate" | "staff";
                    /**
                     * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                     */
                    fixedRole?: "fullName" | "signature";
                  }[];
                  /**
                   * When this question configuration was last updated.
                   *
                   * @format date-time
                   */
                  updatedAt?: string;
                };
              };
          /**
           * The contact record for this candidate. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * Whether the candidate has opted out of 48 hour communications.
           */
          optOut48Hours?: boolean;
          /**
           * Whether the candidate has consented to REC audit checks.
           */
          recAuditConsent: boolean | null;
          /**
           * The date the candidate's REC audit consent was recorded.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          recAuditConsentDate?: string;
          /**
           * The method through which REC audit consent was captured.
           *
           * @maxLength 256
           */
          recAuditConsentMethod?: string;
          /**
           * The date when the candidate record was created.
           *
           * @format date-time
           */
          createdAt?: string;
          /**
           * The mobile app link status for the candidate.
           */
          candidateMobileAppStatus?:
            | "Unlinked"
            | "Scheduled Unlink"
            | "Linked"
            | "Invited"
            | "Not Invited";
          /**
           * Evertime details for this candidate
           */
          evertime?: {
            /**
             * The Evertime CandidateId used for this candidate when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
            /**
             * Whether the matched Evertime candidate's NI number also matches the one held in Edun
             */
            niNumberMatch?: boolean;
          };
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Candidate fields or attached document excerpts that explain why the candidate matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * Whether the matching information came from the candidate record or an attached document.
             */
            source: "candidate" | "document";
            /**
             * The candidate or document information that matched the search.
             *
             * @maxLength 256
             */
            field: string;
            /**
             * A short excerpt showing why the result matched.
             *
             * @maxLength 1000
             */
            text: string;
            /**
             * The exact terms within the content that matched the search, including synonym matches.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
            /**
             * The name of the attached document when the match came from a document.
             *
             * @maxLength 512
             */
            documentName?: string | null;
            /**
             * The unique identifier of the attached document when the match came from a document.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            documentId?: string | null;
          }[];
          /**
           * The candidate's distance in miles from the requested location. Present only when location filtering is used.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * Timesheet Portal details for this candidate
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal contractor GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal contractor code
             *
             * @maxLength 256
             */
            code?: string;
          };
          /**
           * Invite to candidate to link their mobile app.
           */
          mobileAppLinkInvite?: {
            /**
             * Boolean status of whether the link invite is being used
             *
             * @default false
             */
            isUsed?: boolean;
            /**
             * Creation date of the invite
             *
             * @format date-time
             * @default 2026-10-30T13:39:09.288Z
             */
            createdAt?: string;
            /**
             * ID of candidate using this link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            usedBy?: string;
            /**
             * Date of when link used
             *
             * @format date-time
             */
            usedAt?: string;
            /**
             * If unlinked, ID of user that unlinked this candidate's link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            unlinkedBy?: string;
            /**
             * If unlinked, date of when this link was unlinked
             *
             * @format date-time
             */
            unlinkedAt?: string;
            /**
             * If deleted, ID of user that deleted this candidate's link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            deletedBy?: string;
            /**
             * If deleted, date of when this link was deleted
             *
             * @format date-time
             */
            deletedAt?: string;
            /**
             * Current status of this mobile app link
             */
            status?:
              | "Sent"
              | "Linked"
              | "Unlinked"
              | "ScheduledUnlink"
              | "Not Sent"
              | "Rescinded";
          };
        };
    /**
     * The current status of the candidate
     */
    status: "Pending" | "Interested" | "Not Interested";
    /**
     * The client's status for the candidate
     */
    clientStatus: "Pending" | "Interested" | "Not Interested";
    /**
     * The date when the client expressed interest in the candidate
     *
     * @pattern ^\d{4}-\d{2}-\d{2}$
     */
    clientInterestSentAt?: string | null;
    /**
     * Indicates if this is the default document for the candidate
     */
    defaultDocument: boolean;
    /**
     * The document ID associated with the candidate
     *
     * @pattern ^[0-9a-f]{24}$
     */
    document: string;
    /**
     * The name of the document associated with the candidate
     */
    documentName: string;
    /**
     * The reason for the candidate's status
     */
    reason: string;
  }[];
  /**
   * The client associated with the vacancy, if expanded client schema else client id
   */
  client:
    | string
    | {
        /**
         * The unique identifier of the client
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The name of the client
         *
         * @maxLength 256
         */
        name: string;
        /**
         * The address of the client
         */
        address: {
          /**
           * The first line of the address
           *
           * @maxLength 256
           */
          addressLine1?: string;
          /**
           * The second line of the address
           *
           * @maxLength 256
           */
          addressLine2?: string;
          /**
           * The third line of the address
           *
           * @maxLength 256
           */
          addressLine3?: string;
          /**
           * The city of the address
           *
           * @maxLength 256
           */
          city?: string;
          /**
           * The county of the address
           *
           * @maxLength 256
           */
          county?: string;
          /**
           * The borough of the address
           *
           * @maxLength 256
           */
          borough?: string;
          /**
           * The postcode of the address
           *
           * @maxLength 10
           */
          postcode?: string;
          /**
           * The country of the address
           *
           * @maxLength 256
           */
          country?: string;
          /**
           * The latitude of the address
           *
           * @minimum -90
           * @maximum 90
           */
          lat?: number;
          /**
           * The longitude of the address
           *
           * @minimum -180
           * @maximum 180
           */
          lng?: number;
        };
        /**
         * The telephone number of the client
         *
         * @maxLength 32
         */
        telephone?: string;
        /**
         * The email address of the client
         *
         * @format email
         */
        email?: string;
        /**
         * Array of client contacts
         */
        contacts: {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
          /**
           * The role of the contact
           */
          role?:
            | "Head Teacher"
            | "Deputy Head"
            | "Cover Manager"
            | "HR Manager"
            | "Finance Manager"
            | "Head of Maths"
            | "Head of Science"
            | "Head of English"
            | "Assistant Headteacher"
            | "Headteacher's PA"
            | "SENCO"
            | "Other"
            | "Business Manager"
            | "Business Administrator"
            | "Invoice Contact"
            | "Academy Recruitment Contact"
            | "Nursery Manager"
            | "CEO"
            | "COO"
            | "Executive Headteacher"
            | "Inclusion Manager"
            | "EYFS Lead"
            | "Admin Team"
            | "Head of Humanities"
            | "Head of History"
            | "Head of Geography"
            | "Head of Business Studies"
            | "Head of PE"
            | "Head of KS1"
            | "Head of KS2"
            | "Head of KS3"
            | "Head of KS4"
            | "Virtual School Headteacher"
            | "Deputy Virtual School Headteacher"
            | "Post-16 Team Leader"
            | "SEN Team"
            | "Inclusion Team"
            | "UASC Team"
            | "Caseworker"
            | "Designated Teacher"
            | "Attendance Officer"
            | "Coordinator"
            | "Children's Commissioner"
            | "Timesheet Contact";
          /**
           * The name of the role if the role is 'Other'
           *
           * @maxLength 128
           */
          roleOther?: string;
          /**
           * The unique identifier of the contact
           *
           * @format uuid
           */
          contactId?: string;
          /**
           * Timesheet Portal details for this contact
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal approver GUID linked to this client contact
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal approver code
             *
             * @maxLength 256
             */
            code?: string;
          };
          /**
           * Whether the contact has opted out of communications
           */
          optOut?: boolean;
          /**
           * Any additional notes about the contact
           *
           * @maxLength 175
           */
          note?: string;
          /**
           * Whether the contact is a decision maker
           */
          decisionMaker?: boolean;
          /**
           * The status of the contact
           */
          status: "Active" | "Passive" | "Inactive";
          /**
           * The Client associated with the contact.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          clientId?: string;
          /**
           * The name of the Client associated with the contact.
           *
           * @maxLength 256
           */
          clientName?: string;
          /**
           * The address of the Client associated with the contact.
           */
          clientAddress?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The local education authority of the associated Client.
           *
           * @maxLength 128
           */
          clientLea?: string;
          /**
           * The sector of the associated Client.
           *
           * @maxLength 128
           */
          clientType?: string;
          /**
           * The multi-academy trust of the associated Client.
           *
           * @maxLength 256
           */
          clientMultiAcademyTrust?: string;
          /**
           * The borough of the associated Client.
           *
           * @maxLength 128
           */
          clientBorough?: string;
          /**
           * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
           */
          clientAssignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold: {
                  min: number | string;
                  commission: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * The specialism of the associated Client.
           *
           * @maxLength 256
           */
          clientSpecialism?: string;
          /**
           * When the associated Client was last updated.
           *
           * @format date-time
           */
          clientUpdatedAt?: string;
          /**
           * The associated Client's distance in miles from the requested location.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Contact and Client fields that explain why the contact matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * The contact or Client information that matched the search.
             *
             * @maxLength 256
             */
            field: string;
            /**
             * A short excerpt showing why the contact matched.
             *
             * @maxLength 1000
             */
            text: string;
            /**
             * The exact terms within the content that matched the search.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
          }[];
        }[];
        /**
         * The borough of the client
         *
         * @maxLength 128
         */
        borough?: string;
        /**
         * The local education authority of the client
         *
         * @maxLength 128
         */
        lea?: string;
        /**
         * The type of the client
         *
         * @maxLength 128
         */
        type?: string;
        /**
         * The multi academy trust that the client belongs to
         *
         * @maxLength 256
         */
        multiAcademyTrust?: string;
        /**
         * The color (HEX format prefixed with #) associated with the client
         *
         * @pattern ^#[0-9a-fA-F]{6}$
         */
        color?: string;
        /**
         * A custom ID for the client.
         *
         * @maxLength 256
         */
        customId?: string;
        /**
         * A company-defined identifier used to match the client with external records.
         *
         * @maxLength 256
         */
        internalId?: string;
        /**
         * The user assigned to the client. If expanded, a user object else the user ID.
         */
        assignee?:
          | string
          | {
              /**
               * The unique identifier for the user
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The email address of the user
               *
               * @format email
               */
              email: string;
              /**
               * The first name of the user
               *
               * @maxLength 128
               */
              firstName: string;
              /**
               * The last name of the user
               *
               * @maxLength 128
               */
              lastName: string;
              /**
               * The username of the user
               *
               * @maxLength 128
               */
              username: string;
              /**
               * The local education authority of the user
               *
               * @maxLength 128
               */
              lea?: string;
              /**
               * Array of client IDs associated with the user
               */
              clients: string[];
              /**
               * The position or job title of the user
               *
               * @maxLength 128
               */
              position: string;
              /**
               * Array of permissions assigned to the user
               */
              permissions: number[];
              /**
               * The activation code for the user
               *
               * @maxLength 256
               */
              activationCode?: string;
              /**
               * The unique identifier of the company the user belongs to.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              customer: string;
              /**
               * Indicates whether the user account is disabled
               */
              disabled: boolean;
              /**
               * Array of commission thresholds for the user
               */
              commissionThreshold: {
                min: number | string;
                commission: number | string;
                type?: "Fixed" | "Variable";
                weekly?: boolean;
              }[];
              /**
               * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
               */
              branches:
                | string[]
                | {
                    /**
                     * The unique identifier for the branch
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id: string;
                    /**
                     * The name of the branch
                     *
                     * @maxLength 256
                     */
                    name: string;
                    /**
                     * The code of the branch
                     *
                     * @maxLength 32
                     */
                    code?: string;
                    /**
                     * The contact email of the branch
                     *
                     * @maxLength 256
                     * @format email
                     */
                    contactEmail?: string;
                    /**
                     * The contact phone number of the branch
                     *
                     * @maxLength 32
                     */
                    contactTelephone?: string;
                    /**
                     * The contact website of the branch
                     *
                     * @maxLength 512
                     */
                    contactWebsite?: string;
                    /**
                     * The accent color for the customer that the branch belongs to
                     *
                     * @maxLength 7
                     */
                    customerAccentColor?: string;
                    /**
                     * The address of the branch
                     */
                    address?: {
                      /**
                       * The first line of the address
                       *
                       * @maxLength 256
                       */
                      addressLine1?: string;
                      /**
                       * The second line of the address
                       *
                       * @maxLength 256
                       */
                      addressLine2?: string;
                      /**
                       * The third line of the address
                       *
                       * @maxLength 256
                       */
                      addressLine3?: string;
                      /**
                       * The city of the address
                       *
                       * @maxLength 256
                       */
                      city?: string;
                      /**
                       * The county of the address
                       *
                       * @maxLength 256
                       */
                      county?: string;
                      /**
                       * The borough of the address
                       *
                       * @maxLength 256
                       */
                      borough?: string;
                      /**
                       * The postcode of the address
                       *
                       * @maxLength 10
                       */
                      postcode?: string;
                      /**
                       * The country of the address
                       *
                       * @maxLength 256
                       */
                      country?: string;
                      /**
                       * The latitude of the address
                       *
                       * @minimum -90
                       * @maximum 90
                       */
                      lat?: number;
                      /**
                       * The longitude of the address
                       *
                       * @minimum -180
                       * @maximum 180
                       */
                      lng?: number;
                    };
                    /**
                     * Indicates whether the branch is archived
                     */
                    archived: boolean;
                    /**
                     * The number of users assigned to the branch. Only returned when explicitly selected.
                     *
                     * @minimum 0
                     */
                    userCount?: number;
                  }[];
              /**
               * Indicates whether to display the onboarding process for the user
               */
              displayOnboarding: boolean;
              /**
               * A custom identifier for the user
               *
               * @maxLength 256
               */
              _customId?: string;
              /**
               * The signature of the user in base64 format
               *
               * @maxLength 2048
               */
              signature?: string | null;
              /**
               * The phone number of the user
               *
               * @maxLength 32
               */
              phone?: string;
              /**
               * Indicates whether the user is the default candidate watcher
               */
              defaultCandidateWatcher: boolean;
              /**
               * The user's default task workspace
               *
               * @pattern ^[0-9a-f]{24}$
               */
              defaultTaskWorkspace?: string | null;
              /**
               * Indicates whether the user has team admin privileges
               */
              teamAdmin: boolean;
              /**
               * The contact record for this user. If expanded, a contact object else the contact record ID.
               */
              contact?:
                | string
                | {
                    /**
                     * The unique identifier of the contact
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id: string;
                    /**
                     * The forename of the contact
                     *
                     * @maxLength 128
                     */
                    forename: string;
                    /**
                     * The surname of the contact
                     *
                     * @maxLength 128
                     */
                    surname?: string;
                    /**
                     * The email address of the contact
                     *
                     * @format email
                     */
                    email: string;
                    /**
                     * The telephone number of the contact
                     *
                     * @maxLength 32
                     */
                    telephone?: string;
                    /**
                     * The mobile number of the contact
                     *
                     * @maxLength 32
                     */
                    mobile?: string;
                    /**
                     * The extension number of the contact
                     *
                     * @maxLength 8
                     */
                    extension?: string;
                  };
              /**
               * The date and time the user account was created
               */
              createdAt?: string;
              /**
               * Evertime details for this user
               */
              evertime?: {
                /**
                 * The Evertime ConsultantId used for this user when exporting payment runs
                 *
                 * @maxLength 256
                 */
                id?: string;
              };
              /**
               * Timesheet Portal details for this user
               */
              timesheetPortal?: {
                /**
                 * The Timesheet Portal administrator GUID used when exporting payment runs
                 *
                 * @format uuid
                 */
                id?: string;
                /**
                 * The Timesheet Portal administrator code
                 *
                 * @maxLength 256
                 */
                code?: string;
              };
            };
        /**
         * Tags associated with this client. If expanded, an array of tag objects; otherwise, an array of tag IDs.
         */
        tags?:
          | string[]
          | {
              /**
               * The unique identifier of the tag
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The label displayed wherever this tag is applied.
               *
               * @minLength 1
               * @maxLength 128
               */
              name: string;
              /**
               * The tag display colour as a hexadecimal value, for example #2563EB.
               *
               * @maxLength 32
               */
              color?: string | null;
            }[];
        /**
         * The date when the TOB was sent
         *
         * @format date-time
         */
        tobSentAt?: string;
        /**
         * The website of the client
         *
         * @format uri
         */
        website?: string;
        /**
         * Whether the client is archived
         */
        archived?: boolean;
        /**
         * The specialism of the client
         *
         * @maxLength 256
         */
        specialism?: string;
        /**
         * The current booking IDs associated with the client
         */
        currentBooking: string[];
        /**
         * The number of active bookings associated with the client, constrained by the requested booking date range when present.
         *
         * @minimum 0
         */
        bookingCount?: number;
        /**
         * Any important notes related to the client
         *
         * @maxLength 1024
         */
        importantNotes: string;
        /**
         * The booking roles associated with the client
         */
        roles: {
          /**
           * The occupation of the individual in the booking role
           *
           * @maxLength 256
           */
          occupation?: string;
          /**
           * The pay rate for the booking role
           *
           * @maxLength 256
           */
          payRate?: string;
          /**
           * The charge rate for the booking role
           *
           * @maxLength 256
           */
          chargeRate?: string;
          /**
           * The type of pay for the booking role
           */
          payType?: "hourly" | "daily" | any | null;
          /**
           * The length of the day for the booking role
           */
          dayLength?: "full" | "half" | any | null;
          /**
           * The AM/PM designation for the day length
           */
          dayLengthAmPm?: "AM" | "PM" | any | null;
        }[];
        /**
         * The start time for the client
         */
        startTime?: string;
        /**
         * The end time for the client
         */
        endTime?: string;
        /**
         * Any additional instructions for the client
         *
         * @maxLength 1024
         */
        additionalInstructions?: string;
        /**
         * Any additional instructions specific to the client
         */
        additionalInstructionsClient?: string;
        /**
         * Tag highlight colour (HEX format prefixed with #)
         *
         * @pattern ^#[0-9a-fA-F]{6}$
         */
        highlight?: string;
        /**
         * The area of the client
         */
        area?: string;
        /**
         * The governance type of the client
         */
        governance?:
          | "Academy"
          | "Childrens Centre"
          | "College"
          | "Independant"
          | "LEA"
          | "University";
        /**
         * The MAT HQ details of the client
         */
        mathq?: "Unsure" | "Yes" | "No";
        /**
         * The Ofsted rating of the client
         */
        ofstedRating?:
          | "Outstanding"
          | "Good"
          | "Requires Improvement"
          | "Inadequate"
          | "NA - Independent"
          | "NA - Academy";
        /**
         * The date when the Ofsted report was checked
         *
         * @format date-time
         */
        dateOfstedReportChecked?: string | null;
        /**
         * The boarding details of the client
         */
        boarding?: "Unsure" | "Yes" | "No" | any | null;
        /**
         * The licence to issue sponsorship for the client
         */
        licenceToIssueSponsorship?: "Unsure" | "Yes" | "No" | any | null;
        /**
         * The total agency spend for the client
         */
        agencySpendTotal?: number;
        /**
         * Whether the client is VAT exempt
         */
        vatExempt?: boolean;
        /**
         * Whether to split invoices for the client
         */
        splitInvoices?: boolean;
        /**
         * The switch board details for the client
         */
        switchBoard?: string;
        /**
         * Whether the client is a GCA client
         */
        ccsClient?: boolean;
        /**
         * The GCA client URN
         */
        ccsClientUrn?: string;
        /**
         * The GCA client Ofsted URN
         */
        ccsClientOfstedUrn?: string;
        /**
         * The GCA client lot number
         */
        ccsClientLotNumber?: string;
        /**
         * The buyer reference supplied by a GCA client, when available.
         *
         * @maxLength 255
         */
        buyerReference?: string | null;
        /**
         * The GCA client invoice date
         *
         * @format date-time
         */
        ccsClientInvoiceDate?: string;
        /**
         * The key stage for the client
         */
        keyStage?: string;
        /**
         * The contact record for this client. If expanded, a contact object else the contact record ID.
         */
        contact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        /**
         * The date when the client was last contacted.
         *
         * @format date-time
         */
        lastContactedDate?: string;
        /**
         * Search relevance relative to the strongest result, where 100 is the strongest match.
         *
         * @minimum 0
         * @maximum 100
         */
        relevanceScore?: number;
        /**
         * Client fields that explain why the client matched.
         *
         * @maxItems 5
         */
        matchReasons?: {
          /**
           * The client information that matched the search.
           *
           * @maxLength 256
           */
          field: string;
          /**
           * A short excerpt showing why the client matched.
           *
           * @maxLength 1000
           */
          text: string;
          /**
           * The exact terms within the content that matched the search.
           *
           * @maxItems 25
           */
          matchedTerms?: string[];
        }[];
        /**
         * The Client's distance in miles from the requested location. Present only when location filtering is used.
         *
         * @minimum 0
         */
        distance?: number;
        /**
         * The migration that created this client
         *
         * @pattern ^[0-9a-f]{24}$
         */
        migrationId?: string;
        /**
         * Whether this migrated client has been approved
         */
        migrationApproval?: "pending" | "approved" | "rejected";
        /**
         * Evertime details for this client
         */
        evertime?: {
          /**
           * The Evertime ClientId used for this client when exporting payment runs
           *
           * @maxLength 256
           */
          id?: string;
          /**
           * The Evertime contacts to create from Edun (clientContactId set) or reference (clientContactId unset) when exporting payment runs
           */
          contacts?: {
            /**
             * The Evertime ClientContactId
             *
             * @maxLength 256
             */
            evertimeId: string;
            /**
             * The Edun client contact this Evertime contact is created from; absent when the Evertime contact is only referenced (never created or overwritten)
             *
             * @maxLength 256
             */
            clientContactId?: string;
          }[];
          /**
           * The Evertime locations to create from the client's address (create set) or reference (create unset) when exporting payment runs
           */
          locations?: {
            /**
             * The Evertime LocationId
             *
             * @maxLength 256
             */
            evertimeId: string;
            /**
             * True when this location is created from the client's address; false when the Evertime location is only referenced (never created or overwritten)
             */
            create: boolean;
          }[];
          /**
           * Whether the matched Evertime client's name also matches the one held in Edun
           */
          nameMatch?: boolean;
        };
        /**
         * Timesheet Portal details for this client
         */
        timesheetPortal?: {
          /**
           * The Timesheet Portal client GUID used when exporting payment runs
           *
           * @format uuid
           */
          id?: string;
          /**
           * The Timesheet Portal client code
           *
           * @maxLength 256
           */
          code?: string;
        };
      };
  /**
   * The address of the vacancy associated with the vacancy
   */
  clientAddress?: {
    /**
     * The first line of the address
     *
     * @maxLength 256
     */
    addressLine1?: string;
    /**
     * The second line of the address
     *
     * @maxLength 256
     */
    addressLine2?: string;
    /**
     * The third line of the address
     *
     * @maxLength 256
     */
    addressLine3?: string;
    /**
     * The city of the address
     *
     * @maxLength 256
     */
    city?: string;
    /**
     * The county of the address
     *
     * @maxLength 256
     */
    county?: string;
    /**
     * The borough of the address
     *
     * @maxLength 256
     */
    borough?: string;
    /**
     * The postcode of the address
     *
     * @maxLength 10
     */
    postcode?: string;
    /**
     * The country of the address
     *
     * @maxLength 256
     */
    country?: string;
    /**
     * The latitude of the address
     *
     * @minimum -90
     * @maximum 90
     */
    lat?: number;
    /**
     * The longitude of the address
     *
     * @minimum -180
     * @maximum 180
     */
    lng?: number;
  };
  /**
   * The student associated with the vacancy
   *
   * @pattern ^[0-9a-f]{24}$
   */
  studentId?: string;
  /**
   * The postcode of the vacancy location
   *
   * @maxLength 10
   */
  postcode: string;
  /**
   * The name of the role the vacancy is for
   *
   * @maxLength 256
   */
  role?: string;
  /**
   * The code associated with the role
   *
   * @maxLength 256
   */
  roleCode?: string;
  /**
   * Start date and time for the vacancy
   *
   * @format date-time
   */
  start?: string;
  /**
   * End date and time for the vacancy
   *
   * @format date-time
   */
  end?: string;
  /**
   * Start time for the vacancy
   */
  startTime?: string;
  /**
   * End time for the vacancy
   */
  endTime?: string;
  /**
   * The individual working days included in the vacancy.
   */
  days: {
    /**
     * The start date and time for the booking day
     */
    start: string;
    /**
     * The end date and time for the booking day
     */
    end: string;
    /**
     * The length of the booking day
     */
    length: "Half Day" | "Full Day";
    /**
     * Indicates whether the booking day is in the AM or PM
     */
    amPm: "AM" | "PM";
    /**
     * The current status of the booking day
     */
    status:
      | "Active"
      | "Sickness"
      | "NoShow"
      | "Client Cancelled"
      | "Act Of God"
      | "Personal Appointment";
    /**
     * The charge rate for the booking day
     *
     * @maxLength 32
     */
    chargeRate?: string;
    /**
     * The pay rate for the booking day
     *
     * @maxLength 32
     */
    payRate?: string;
    /**
     * Break duration inherited for this booking day.
     */
    breaks?: string;
    /**
     * Expenses payable for this booking day.
     */
    expenses?: string;
    /**
     * Indicates whether the booking day is flagged for removal
     */
    flaggedForRemoval?: boolean;
  }[];
  /**
   * The rate charged to the client for this vacancy.
   *
   * @maxLength 32
   */
  chargeRate?: string;
  /**
   * The fee percentage for the vacancy
   *
   * @maxLength 8
   */
  feePercentage?: string;
  /**
   * The Agency Workers Regulations value recorded for the vacancy.
   *
   * @maxLength 8
   */
  awr?: string;
  /**
   * The reminder value used to monitor Agency Workers Regulations requirements.
   *
   * @maxLength 8
   */
  awrReminder?: string;
  /**
   * The expected number of working hours for the vacancy.
   *
   * @maxLength 16
   */
  hours?: string;
  /**
   * The pay rate for the vacancy
   *
   * @maxLength 32
   */
  payRate: string;
  /**
   * The arrival time for the vacancy
   */
  arrivalTime?: string;
  /**
   * The contact person at the vacancy for the vacancy
   */
  clientContact?:
    | string
    | {
        /**
         * The unique identifier of the contact
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The forename of the contact
         *
         * @maxLength 128
         */
        forename: string;
        /**
         * The surname of the contact
         *
         * @maxLength 128
         */
        surname?: string;
        /**
         * The email address of the contact
         *
         * @format email
         */
        email: string;
        /**
         * The telephone number of the contact
         *
         * @maxLength 32
         */
        telephone?: string;
        /**
         * The mobile number of the contact
         *
         * @maxLength 32
         */
        mobile?: string;
        /**
         * The extension number of the contact
         *
         * @maxLength 8
         */
        extension?: string;
        /**
         * The role of the contact
         */
        role?:
          | "Head Teacher"
          | "Deputy Head"
          | "Cover Manager"
          | "HR Manager"
          | "Finance Manager"
          | "Head of Maths"
          | "Head of Science"
          | "Head of English"
          | "Assistant Headteacher"
          | "Headteacher's PA"
          | "SENCO"
          | "Other"
          | "Business Manager"
          | "Business Administrator"
          | "Invoice Contact"
          | "Academy Recruitment Contact"
          | "Nursery Manager"
          | "CEO"
          | "COO"
          | "Executive Headteacher"
          | "Inclusion Manager"
          | "EYFS Lead"
          | "Admin Team"
          | "Head of Humanities"
          | "Head of History"
          | "Head of Geography"
          | "Head of Business Studies"
          | "Head of PE"
          | "Head of KS1"
          | "Head of KS2"
          | "Head of KS3"
          | "Head of KS4"
          | "Virtual School Headteacher"
          | "Deputy Virtual School Headteacher"
          | "Post-16 Team Leader"
          | "SEN Team"
          | "Inclusion Team"
          | "UASC Team"
          | "Caseworker"
          | "Designated Teacher"
          | "Attendance Officer"
          | "Coordinator"
          | "Children's Commissioner"
          | "Timesheet Contact";
        /**
         * The name of the role if the role is 'Other'
         *
         * @maxLength 128
         */
        roleOther?: string;
        /**
         * The unique identifier of the contact
         *
         * @format uuid
         */
        contactId?: string;
        /**
         * Timesheet Portal details for this contact
         */
        timesheetPortal?: {
          /**
           * The Timesheet Portal approver GUID linked to this client contact
           *
           * @format uuid
           */
          id?: string;
          /**
           * The Timesheet Portal approver code
           *
           * @maxLength 256
           */
          code?: string;
        };
        /**
         * Whether the contact has opted out of communications
         */
        optOut?: boolean;
        /**
         * Any additional notes about the contact
         *
         * @maxLength 175
         */
        note?: string;
        /**
         * Whether the contact is a decision maker
         */
        decisionMaker?: boolean;
        /**
         * The status of the contact
         */
        status: "Active" | "Passive" | "Inactive";
        /**
         * The Client associated with the contact.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        clientId?: string;
        /**
         * The name of the Client associated with the contact.
         *
         * @maxLength 256
         */
        clientName?: string;
        /**
         * The address of the Client associated with the contact.
         */
        clientAddress?: {
          /**
           * The first line of the address
           *
           * @maxLength 256
           */
          addressLine1?: string;
          /**
           * The second line of the address
           *
           * @maxLength 256
           */
          addressLine2?: string;
          /**
           * The third line of the address
           *
           * @maxLength 256
           */
          addressLine3?: string;
          /**
           * The city of the address
           *
           * @maxLength 256
           */
          city?: string;
          /**
           * The county of the address
           *
           * @maxLength 256
           */
          county?: string;
          /**
           * The borough of the address
           *
           * @maxLength 256
           */
          borough?: string;
          /**
           * The postcode of the address
           *
           * @maxLength 10
           */
          postcode?: string;
          /**
           * The country of the address
           *
           * @maxLength 256
           */
          country?: string;
          /**
           * The latitude of the address
           *
           * @minimum -90
           * @maximum 90
           */
          lat?: number;
          /**
           * The longitude of the address
           *
           * @minimum -180
           * @maximum 180
           */
          lng?: number;
        };
        /**
         * The local education authority of the associated Client.
         *
         * @maxLength 128
         */
        clientLea?: string;
        /**
         * The sector of the associated Client.
         *
         * @maxLength 128
         */
        clientType?: string;
        /**
         * The multi-academy trust of the associated Client.
         *
         * @maxLength 256
         */
        clientMultiAcademyTrust?: string;
        /**
         * The borough of the associated Client.
         *
         * @maxLength 128
         */
        clientBorough?: string;
        /**
         * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
         */
        clientAssignee?:
          | string
          | {
              /**
               * The unique identifier for the user
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The email address of the user
               *
               * @format email
               */
              email: string;
              /**
               * The first name of the user
               *
               * @maxLength 128
               */
              firstName: string;
              /**
               * The last name of the user
               *
               * @maxLength 128
               */
              lastName: string;
              /**
               * The username of the user
               *
               * @maxLength 128
               */
              username: string;
              /**
               * The local education authority of the user
               *
               * @maxLength 128
               */
              lea?: string;
              /**
               * Array of client IDs associated with the user
               */
              clients: string[];
              /**
               * The position or job title of the user
               *
               * @maxLength 128
               */
              position: string;
              /**
               * Array of permissions assigned to the user
               */
              permissions: number[];
              /**
               * The activation code for the user
               *
               * @maxLength 256
               */
              activationCode?: string;
              /**
               * The unique identifier of the company the user belongs to.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              customer: string;
              /**
               * Indicates whether the user account is disabled
               */
              disabled: boolean;
              /**
               * Array of commission thresholds for the user
               */
              commissionThreshold: {
                min: number | string;
                commission: number | string;
                type?: "Fixed" | "Variable";
                weekly?: boolean;
              }[];
              /**
               * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
               */
              branches:
                | string[]
                | {
                    /**
                     * The unique identifier for the branch
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id: string;
                    /**
                     * The name of the branch
                     *
                     * @maxLength 256
                     */
                    name: string;
                    /**
                     * The code of the branch
                     *
                     * @maxLength 32
                     */
                    code?: string;
                    /**
                     * The contact email of the branch
                     *
                     * @maxLength 256
                     * @format email
                     */
                    contactEmail?: string;
                    /**
                     * The contact phone number of the branch
                     *
                     * @maxLength 32
                     */
                    contactTelephone?: string;
                    /**
                     * The contact website of the branch
                     *
                     * @maxLength 512
                     */
                    contactWebsite?: string;
                    /**
                     * The accent color for the customer that the branch belongs to
                     *
                     * @maxLength 7
                     */
                    customerAccentColor?: string;
                    /**
                     * The address of the branch
                     */
                    address?: {
                      /**
                       * The first line of the address
                       *
                       * @maxLength 256
                       */
                      addressLine1?: string;
                      /**
                       * The second line of the address
                       *
                       * @maxLength 256
                       */
                      addressLine2?: string;
                      /**
                       * The third line of the address
                       *
                       * @maxLength 256
                       */
                      addressLine3?: string;
                      /**
                       * The city of the address
                       *
                       * @maxLength 256
                       */
                      city?: string;
                      /**
                       * The county of the address
                       *
                       * @maxLength 256
                       */
                      county?: string;
                      /**
                       * The borough of the address
                       *
                       * @maxLength 256
                       */
                      borough?: string;
                      /**
                       * The postcode of the address
                       *
                       * @maxLength 10
                       */
                      postcode?: string;
                      /**
                       * The country of the address
                       *
                       * @maxLength 256
                       */
                      country?: string;
                      /**
                       * The latitude of the address
                       *
                       * @minimum -90
                       * @maximum 90
                       */
                      lat?: number;
                      /**
                       * The longitude of the address
                       *
                       * @minimum -180
                       * @maximum 180
                       */
                      lng?: number;
                    };
                    /**
                     * Indicates whether the branch is archived
                     */
                    archived: boolean;
                    /**
                     * The number of users assigned to the branch. Only returned when explicitly selected.
                     *
                     * @minimum 0
                     */
                    userCount?: number;
                  }[];
              /**
               * Indicates whether to display the onboarding process for the user
               */
              displayOnboarding: boolean;
              /**
               * A custom identifier for the user
               *
               * @maxLength 256
               */
              _customId?: string;
              /**
               * The signature of the user in base64 format
               *
               * @maxLength 2048
               */
              signature?: string | null;
              /**
               * The phone number of the user
               *
               * @maxLength 32
               */
              phone?: string;
              /**
               * Indicates whether the user is the default candidate watcher
               */
              defaultCandidateWatcher: boolean;
              /**
               * The user's default task workspace
               *
               * @pattern ^[0-9a-f]{24}$
               */
              defaultTaskWorkspace?: string | null;
              /**
               * Indicates whether the user has team admin privileges
               */
              teamAdmin: boolean;
              /**
               * The contact record for this user. If expanded, a contact object else the contact record ID.
               */
              contact?:
                | string
                | {
                    /**
                     * The unique identifier of the contact
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id: string;
                    /**
                     * The forename of the contact
                     *
                     * @maxLength 128
                     */
                    forename: string;
                    /**
                     * The surname of the contact
                     *
                     * @maxLength 128
                     */
                    surname?: string;
                    /**
                     * The email address of the contact
                     *
                     * @format email
                     */
                    email: string;
                    /**
                     * The telephone number of the contact
                     *
                     * @maxLength 32
                     */
                    telephone?: string;
                    /**
                     * The mobile number of the contact
                     *
                     * @maxLength 32
                     */
                    mobile?: string;
                    /**
                     * The extension number of the contact
                     *
                     * @maxLength 8
                     */
                    extension?: string;
                  };
              /**
               * The date and time the user account was created
               */
              createdAt?: string;
              /**
               * Evertime details for this user
               */
              evertime?: {
                /**
                 * The Evertime ConsultantId used for this user when exporting payment runs
                 *
                 * @maxLength 256
                 */
                id?: string;
              };
              /**
               * Timesheet Portal details for this user
               */
              timesheetPortal?: {
                /**
                 * The Timesheet Portal administrator GUID used when exporting payment runs
                 *
                 * @format uuid
                 */
                id?: string;
                /**
                 * The Timesheet Portal administrator code
                 *
                 * @maxLength 256
                 */
                code?: string;
              };
            };
        /**
         * The specialism of the associated Client.
         *
         * @maxLength 256
         */
        clientSpecialism?: string;
        /**
         * When the associated Client was last updated.
         *
         * @format date-time
         */
        clientUpdatedAt?: string;
        /**
         * The associated Client's distance in miles from the requested location.
         *
         * @minimum 0
         */
        distance?: number;
        /**
         * Search relevance relative to the strongest result, where 100 is the strongest match.
         *
         * @minimum 0
         * @maximum 100
         */
        relevanceScore?: number;
        /**
         * Contact and Client fields that explain why the contact matched.
         *
         * @maxItems 5
         */
        matchReasons?: {
          /**
           * The contact or Client information that matched the search.
           *
           * @maxLength 256
           */
          field: string;
          /**
           * A short excerpt showing why the contact matched.
           *
           * @maxLength 1000
           */
          text: string;
          /**
           * The exact terms within the content that matched the search.
           *
           * @maxItems 25
           */
          matchedTerms?: string[];
        }[];
      };
  /**
   * Any additional instructions for the vacancy
   *
   * @maxLength 1024
   */
  additionalInstructions?: string;
  /**
   * Any additional instructions specific to the vacancy
   *
   * @maxLength 1024
   */
  additionalInstructionsClient?: string;
  /**
   * The current status of the vacancy
   */
  status: "active" | "cancelled";
  /**
   * The user assigned to the vacancy. If expanded, a user object else the user ID.
   */
  assignee?:
    | string
    | {
        /**
         * The unique identifier for the user
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id: string;
        /**
         * The email address of the user
         *
         * @format email
         */
        email: string;
        /**
         * The first name of the user
         *
         * @maxLength 128
         */
        firstName: string;
        /**
         * The last name of the user
         *
         * @maxLength 128
         */
        lastName: string;
        /**
         * The username of the user
         *
         * @maxLength 128
         */
        username: string;
        /**
         * The local education authority of the user
         *
         * @maxLength 128
         */
        lea?: string;
        /**
         * Array of client IDs associated with the user
         */
        clients: string[];
        /**
         * The position or job title of the user
         *
         * @maxLength 128
         */
        position: string;
        /**
         * Array of permissions assigned to the user
         */
        permissions: number[];
        /**
         * The activation code for the user
         *
         * @maxLength 256
         */
        activationCode?: string;
        /**
         * The unique identifier of the company the user belongs to.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customer: string;
        /**
         * Indicates whether the user account is disabled
         */
        disabled: boolean;
        /**
         * Array of commission thresholds for the user
         */
        commissionThreshold: {
          min: number | string;
          commission: number | string;
          type?: "Fixed" | "Variable";
          weekly?: boolean;
        }[];
        /**
         * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
         */
        branches:
          | string[]
          | {
              /**
               * The unique identifier for the branch
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The name of the branch
               *
               * @maxLength 256
               */
              name: string;
              /**
               * The code of the branch
               *
               * @maxLength 32
               */
              code?: string;
              /**
               * The contact email of the branch
               *
               * @maxLength 256
               * @format email
               */
              contactEmail?: string;
              /**
               * The contact phone number of the branch
               *
               * @maxLength 32
               */
              contactTelephone?: string;
              /**
               * The contact website of the branch
               *
               * @maxLength 512
               */
              contactWebsite?: string;
              /**
               * The accent color for the customer that the branch belongs to
               *
               * @maxLength 7
               */
              customerAccentColor?: string;
              /**
               * The address of the branch
               */
              address?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
              /**
               * Indicates whether the branch is archived
               */
              archived: boolean;
              /**
               * The number of users assigned to the branch. Only returned when explicitly selected.
               *
               * @minimum 0
               */
              userCount?: number;
            }[];
        /**
         * Indicates whether to display the onboarding process for the user
         */
        displayOnboarding: boolean;
        /**
         * A custom identifier for the user
         *
         * @maxLength 256
         */
        _customId?: string;
        /**
         * The signature of the user in base64 format
         *
         * @maxLength 2048
         */
        signature?: string | null;
        /**
         * The phone number of the user
         *
         * @maxLength 32
         */
        phone?: string;
        /**
         * Indicates whether the user is the default candidate watcher
         */
        defaultCandidateWatcher: boolean;
        /**
         * The user's default task workspace
         *
         * @pattern ^[0-9a-f]{24}$
         */
        defaultTaskWorkspace?: string | null;
        /**
         * Indicates whether the user has team admin privileges
         */
        teamAdmin: boolean;
        /**
         * The contact record for this user. If expanded, a contact object else the contact record ID.
         */
        contact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        /**
         * The date and time the user account was created
         */
        createdAt?: string;
        /**
         * Evertime details for this user
         */
        evertime?: {
          /**
           * The Evertime ConsultantId used for this user when exporting payment runs
           *
           * @maxLength 256
           */
          id?: string;
        };
        /**
         * Timesheet Portal details for this user
         */
        timesheetPortal?: {
          /**
           * The Timesheet Portal administrator GUID used when exporting payment runs
           *
           * @format uuid
           */
          id?: string;
          /**
           * The Timesheet Portal administrator code
           *
           * @maxLength 256
           */
          code?: string;
        };
      };
  /**
   * The pay type for the vacancy
   */
  payType?: "daily" | "hourly";
  /**
   * The type of the vacancy
   */
  type: "standard" | "interview" | "trial" | "permanent" | "tutor";
  /**
   * The sub-type of the vacancy
   */
  subType?: "tutor-standard" | "tutor-mentor" | "tutor-basic" | "face-to-face";
  /**
   * The payment arrangement expected for the candidate.
   */
  candidatePayType?: "PAYE" | "Umbrella" | "Limited Company";
  /**
   * The client name displayed with the vacancy.
   *
   * @maxLength 256
   */
  clientName?: string;
  /**
   * A description of the role, responsibilities, and requirements.
   *
   * @maxLength 2048
   */
  jobDescription?: string;
  /**
   * Whether the salary is expressed as an annual or daily amount.
   */
  salaryPayType?: "annual" | "daily";
  /**
   * The salary amount advertised for a permanent vacancy.
   *
   * @maxLength 32
   */
  salary?: string;
  /**
   * The weekly rota for the vacancy
   */
  weeklyRota?: {
    /**
     * The rota for Monday
     */
    ["1"]: {
      /**
       * The start time for the day
       */
      start: string;
      /**
       * The end time for the day
       */
      end: string;
      /**
       * Whether to override the default payment for the day
       */
      overridePayment?: boolean;
      /**
       * The charge rate for the day
       */
      chargeRate?: string;
      /**
       * The pay rate for the day
       */
      payRate?: string;
    };
    /**
     * The rota for Tuesday
     */
    ["2"]: {
      /**
       * The start time for the day
       */
      start: string;
      /**
       * The end time for the day
       */
      end: string;
      /**
       * Whether to override the default payment for the day
       */
      overridePayment?: boolean;
      /**
       * The charge rate for the day
       */
      chargeRate?: string;
      /**
       * The pay rate for the day
       */
      payRate?: string;
    };
    /**
     * The rota for Wednesday
     */
    ["3"]: {
      /**
       * The start time for the day
       */
      start: string;
      /**
       * The end time for the day
       */
      end: string;
      /**
       * Whether to override the default payment for the day
       */
      overridePayment?: boolean;
      /**
       * The charge rate for the day
       */
      chargeRate?: string;
      /**
       * The pay rate for the day
       */
      payRate?: string;
    };
    /**
     * The rota for Thursday
     */
    ["4"]: {
      /**
       * The start time for the day
       */
      start: string;
      /**
       * The end time for the day
       */
      end: string;
      /**
       * Whether to override the default payment for the day
       */
      overridePayment?: boolean;
      /**
       * The charge rate for the day
       */
      chargeRate?: string;
      /**
       * The pay rate for the day
       */
      payRate?: string;
    };
    /**
     * The rota for Friday
     */
    ["5"]: {
      /**
       * The start time for the day
       */
      start: string;
      /**
       * The end time for the day
       */
      end: string;
      /**
       * Whether to override the default payment for the day
       */
      overridePayment?: boolean;
      /**
       * The charge rate for the day
       */
      chargeRate?: string;
      /**
       * The pay rate for the day
       */
      payRate?: string;
    };
    /**
     * The rota for Saturday
     */
    ["6"]: {
      /**
       * The start time for the day
       */
      start: string;
      /**
       * The end time for the day
       */
      end: string;
      /**
       * Whether to override the default payment for the day
       */
      overridePayment?: boolean;
      /**
       * The charge rate for the day
       */
      chargeRate?: string;
      /**
       * The pay rate for the day
       */
      payRate?: string;
    };
    /**
     * The rota for Sunday
     */
    ["7"]: {
      /**
       * The start time for the day
       */
      start: string;
      /**
       * The end time for the day
       */
      end: string;
      /**
       * Whether to override the default payment for the day
       */
      overridePayment?: boolean;
      /**
       * The charge rate for the day
       */
      chargeRate?: string;
      /**
       * The pay rate for the day
       */
      payRate?: string;
    };
  };
  /**
   * The calculated distance to the vacancy when a location-based request supplies a reference point.
   */
  distance?: number;
  /**
   * Whether to include the school name in the vacancy details
   */
  includeSchool?: boolean;
  /**
   * Vacant bookings can be completed to multiple candidates at once. Specify the total number of available vacancies for this booking. This value is only visible within Edun, not visible externally.
   *  If left empty, 1 available vacancy for this placement will be presumed.
   *
   * @maxLength 256
   */
  noVacancies?: string;
  /**
   * Whether the vacancy should be shared to the job board
   */
  jobBoard?: boolean;
  /**
   * Notes about the client's interest in candidates for this vacancy.
   *
   * @maxLength 1024
   */
  clientInterestComments?: string;
  /**
   * Whether the required health and safety check has been recorded as complete.
   */
  healthSafetyCheck?: boolean;
  /**
   * The client contact associated with the health and safety check.
   *
   * @maxLength 256
   */
  healthSafetyCheckContact?: string;
  /**
   * The purchase order number for the vacancy
   */
  poNumber?: string;
  /**
   * The identifier assigned when the vacancy is published to CV-Library.
   */
  cvLibraryId?: number;
  /**
   * Whether the vacancy is archived
   */
  archived?: boolean;
  /**
   * The reason the vacancy was archived.
   *
   * @maxLength 1024
   */
  archiveReason?: string;
  /**
   * Whether the vacancy is completed
   */
  completed?: boolean;
  /**
   * The occupation for the vacancy
   *
   * @maxLength 256
   */
  occupation?: string;
  /**
   * The date the vacancy was posted
   *
   * @format date-time
   */
  postedOnDate?: string | null;
};

export type CreateVacancyVariables = {
  body: CreateVacancyRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateVacancy = (
  variables: CreateVacancyVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateVacancyResponse,
    CreateVacancyError,
    CreateVacancyRequestBody,
    {},
    {},
    {}
  >({ url: "/vacancies", method: "post", ...variables, signal });

export const useCreateVacancy = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateVacancyResponse,
      CreateVacancyError,
      CreateVacancyVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateVacancyResponse,
    CreateVacancyError,
    CreateVacancyVariables
  >({
    mutationFn: (variables: CreateVacancyVariables) =>
      fetchCreateVacancy(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetVacancyPathParams = {
  /**
   * The unique identifier for the vacancy.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  vacancyId: string;
};

export type GetVacancyQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "candidates"
    | "client"
    | "client.id"
    | "client.name"
    | "client.address.addressLine1"
    | "client.address.addressLine2"
    | "client.address.addressLine3"
    | "client.address.city"
    | "client.address.county"
    | "client.address.borough"
    | "client.address.postcode"
    | "client.address.country"
    | "client.address.lat"
    | "client.address.lng"
    | "client.address"
    | "client.telephone"
    | "client.email"
    | "client.contacts"
    | "client.borough"
    | "client.lea"
    | "client.type"
    | "client.multiAcademyTrust"
    | "client.color"
    | "client.customId"
    | "client.internalId"
    | "client.assignee"
    | "client.assignee.id"
    | "client.assignee.email"
    | "client.assignee.firstName"
    | "client.assignee.lastName"
    | "client.assignee.username"
    | "client.assignee.lea"
    | "client.assignee.clients"
    | "client.assignee.position"
    | "client.assignee.permissions"
    | "client.assignee.activationCode"
    | "client.assignee.customer"
    | "client.assignee.disabled"
    | "client.assignee.commissionThreshold"
    | "client.assignee.branches"
    | "client.assignee.displayOnboarding"
    | "client.assignee._customId"
    | "client.assignee.signature"
    | "client.assignee.phone"
    | "client.assignee.defaultCandidateWatcher"
    | "client.assignee.defaultTaskWorkspace"
    | "client.assignee.teamAdmin"
    | "client.assignee.contact"
    | "client.assignee.contact.id"
    | "client.assignee.contact.forename"
    | "client.assignee.contact.surname"
    | "client.assignee.contact.email"
    | "client.assignee.contact.telephone"
    | "client.assignee.contact.mobile"
    | "client.assignee.contact.extension"
    | "client.assignee.createdAt"
    | "client.assignee.evertime.id"
    | "client.assignee.evertime"
    | "client.assignee.timesheetPortal.id"
    | "client.assignee.timesheetPortal.code"
    | "client.assignee.timesheetPortal"
    | "client.tags"
    | "client.tobSentAt"
    | "client.website"
    | "client.archived"
    | "client.specialism"
    | "client.currentBooking"
    | "client.bookingCount"
    | "client.importantNotes"
    | "client.roles"
    | "client.startTime"
    | "client.endTime"
    | "client.additionalInstructions"
    | "client.additionalInstructionsClient"
    | "client.highlight"
    | "client.area"
    | "client.governance"
    | "client.mathq"
    | "client.ofstedRating"
    | "client.dateOfstedReportChecked"
    | "client.boarding"
    | "client.licenceToIssueSponsorship"
    | "client.agencySpendTotal"
    | "client.vatExempt"
    | "client.splitInvoices"
    | "client.switchBoard"
    | "client.ccsClient"
    | "client.ccsClientUrn"
    | "client.ccsClientOfstedUrn"
    | "client.ccsClientLotNumber"
    | "client.buyerReference"
    | "client.ccsClientInvoiceDate"
    | "client.keyStage"
    | "client.contact"
    | "client.contact.id"
    | "client.contact.forename"
    | "client.contact.surname"
    | "client.contact.email"
    | "client.contact.telephone"
    | "client.contact.mobile"
    | "client.contact.extension"
    | "client.lastContactedDate"
    | "client.relevanceScore"
    | "client.matchReasons"
    | "client.distance"
    | "client.migrationId"
    | "client.migrationApproval"
    | "client.evertime.id"
    | "client.evertime.contacts"
    | "client.evertime.locations"
    | "client.evertime.nameMatch"
    | "client.evertime"
    | "client.timesheetPortal.id"
    | "client.timesheetPortal.code"
    | "client.timesheetPortal"
    | "clientAddress.addressLine1"
    | "clientAddress.addressLine2"
    | "clientAddress.addressLine3"
    | "clientAddress.city"
    | "clientAddress.county"
    | "clientAddress.borough"
    | "clientAddress.postcode"
    | "clientAddress.country"
    | "clientAddress.lat"
    | "clientAddress.lng"
    | "clientAddress"
    | "studentId"
    | "postcode"
    | "role"
    | "roleCode"
    | "start"
    | "end"
    | "startTime"
    | "endTime"
    | "days"
    | "chargeRate"
    | "feePercentage"
    | "awr"
    | "awrReminder"
    | "hours"
    | "payRate"
    | "arrivalTime"
    | "clientContact"
    | "clientContact.id"
    | "clientContact.forename"
    | "clientContact.surname"
    | "clientContact.email"
    | "clientContact.telephone"
    | "clientContact.mobile"
    | "clientContact.extension"
    | "clientContact.role"
    | "clientContact.roleOther"
    | "clientContact.contactId"
    | "clientContact.timesheetPortal.id"
    | "clientContact.timesheetPortal.code"
    | "clientContact.timesheetPortal"
    | "clientContact.optOut"
    | "clientContact.note"
    | "clientContact.decisionMaker"
    | "clientContact.status"
    | "clientContact.clientId"
    | "clientContact.clientName"
    | "clientContact.clientAddress.addressLine1"
    | "clientContact.clientAddress.addressLine2"
    | "clientContact.clientAddress.addressLine3"
    | "clientContact.clientAddress.city"
    | "clientContact.clientAddress.county"
    | "clientContact.clientAddress.borough"
    | "clientContact.clientAddress.postcode"
    | "clientContact.clientAddress.country"
    | "clientContact.clientAddress.lat"
    | "clientContact.clientAddress.lng"
    | "clientContact.clientAddress"
    | "clientContact.clientLea"
    | "clientContact.clientType"
    | "clientContact.clientMultiAcademyTrust"
    | "clientContact.clientBorough"
    | "clientContact.clientAssignee"
    | "clientContact.clientAssignee.id"
    | "clientContact.clientAssignee.email"
    | "clientContact.clientAssignee.firstName"
    | "clientContact.clientAssignee.lastName"
    | "clientContact.clientAssignee.username"
    | "clientContact.clientAssignee.lea"
    | "clientContact.clientAssignee.clients"
    | "clientContact.clientAssignee.position"
    | "clientContact.clientAssignee.permissions"
    | "clientContact.clientAssignee.activationCode"
    | "clientContact.clientAssignee.customer"
    | "clientContact.clientAssignee.disabled"
    | "clientContact.clientAssignee.commissionThreshold"
    | "clientContact.clientAssignee.branches"
    | "clientContact.clientAssignee.displayOnboarding"
    | "clientContact.clientAssignee._customId"
    | "clientContact.clientAssignee.signature"
    | "clientContact.clientAssignee.phone"
    | "clientContact.clientAssignee.defaultCandidateWatcher"
    | "clientContact.clientAssignee.defaultTaskWorkspace"
    | "clientContact.clientAssignee.teamAdmin"
    | "clientContact.clientAssignee.contact"
    | "clientContact.clientAssignee.contact.id"
    | "clientContact.clientAssignee.contact.forename"
    | "clientContact.clientAssignee.contact.surname"
    | "clientContact.clientAssignee.contact.email"
    | "clientContact.clientAssignee.contact.telephone"
    | "clientContact.clientAssignee.contact.mobile"
    | "clientContact.clientAssignee.contact.extension"
    | "clientContact.clientAssignee.createdAt"
    | "clientContact.clientAssignee.evertime.id"
    | "clientContact.clientAssignee.evertime"
    | "clientContact.clientAssignee.timesheetPortal.id"
    | "clientContact.clientAssignee.timesheetPortal.code"
    | "clientContact.clientAssignee.timesheetPortal"
    | "clientContact.clientSpecialism"
    | "clientContact.clientUpdatedAt"
    | "clientContact.distance"
    | "clientContact.relevanceScore"
    | "clientContact.matchReasons"
    | "additionalInstructions"
    | "additionalInstructionsClient"
    | "status"
    | "assignee"
    | "assignee.id"
    | "assignee.email"
    | "assignee.firstName"
    | "assignee.lastName"
    | "assignee.username"
    | "assignee.lea"
    | "assignee.clients"
    | "assignee.position"
    | "assignee.permissions"
    | "assignee.activationCode"
    | "assignee.customer"
    | "assignee.disabled"
    | "assignee.commissionThreshold"
    | "assignee.branches"
    | "assignee.displayOnboarding"
    | "assignee._customId"
    | "assignee.signature"
    | "assignee.phone"
    | "assignee.defaultCandidateWatcher"
    | "assignee.defaultTaskWorkspace"
    | "assignee.teamAdmin"
    | "assignee.contact"
    | "assignee.contact.id"
    | "assignee.contact.forename"
    | "assignee.contact.surname"
    | "assignee.contact.email"
    | "assignee.contact.telephone"
    | "assignee.contact.mobile"
    | "assignee.contact.extension"
    | "assignee.createdAt"
    | "assignee.evertime.id"
    | "assignee.evertime"
    | "assignee.timesheetPortal.id"
    | "assignee.timesheetPortal.code"
    | "assignee.timesheetPortal"
    | "payType"
    | "type"
    | "subType"
    | "candidatePayType"
    | "clientName"
    | "jobDescription"
    | "salaryPayType"
    | "salary"
    | "weeklyRota.1.start"
    | "weeklyRota.1.end"
    | "weeklyRota.1.overridePayment"
    | "weeklyRota.1.chargeRate"
    | "weeklyRota.1.payRate"
    | "weeklyRota.1"
    | "weeklyRota.2.start"
    | "weeklyRota.2.end"
    | "weeklyRota.2.overridePayment"
    | "weeklyRota.2.chargeRate"
    | "weeklyRota.2.payRate"
    | "weeklyRota.2"
    | "weeklyRota.3.start"
    | "weeklyRota.3.end"
    | "weeklyRota.3.overridePayment"
    | "weeklyRota.3.chargeRate"
    | "weeklyRota.3.payRate"
    | "weeklyRota.3"
    | "weeklyRota.4.start"
    | "weeklyRota.4.end"
    | "weeklyRota.4.overridePayment"
    | "weeklyRota.4.chargeRate"
    | "weeklyRota.4.payRate"
    | "weeklyRota.4"
    | "weeklyRota.5.start"
    | "weeklyRota.5.end"
    | "weeklyRota.5.overridePayment"
    | "weeklyRota.5.chargeRate"
    | "weeklyRota.5.payRate"
    | "weeklyRota.5"
    | "weeklyRota.6.start"
    | "weeklyRota.6.end"
    | "weeklyRota.6.overridePayment"
    | "weeklyRota.6.chargeRate"
    | "weeklyRota.6.payRate"
    | "weeklyRota.6"
    | "weeklyRota.7.start"
    | "weeklyRota.7.end"
    | "weeklyRota.7.overridePayment"
    | "weeklyRota.7.chargeRate"
    | "weeklyRota.7.payRate"
    | "weeklyRota.7"
    | "weeklyRota"
    | "distance"
    | "includeSchool"
    | "noVacancies"
    | "jobBoard"
    | "clientInterestComments"
    | "healthSafetyCheck"
    | "healthSafetyCheckContact"
    | "poNumber"
    | "cvLibraryId"
    | "archived"
    | "archiveReason"
    | "completed"
    | "occupation"
    | "postedOnDate"
    | ""
  )[];
  /**
   * Additional fields to expand in the response
   */
  expand?: ("assignee" | "client" | "clientContact")[];
};

export type GetVacancyError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetVacancyResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the vacancy
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * List of candidates associated with the vacancy
     */
    candidates?: {
      /**
       * The candidate applying for the vacancy, either by ID or full details depending on select query
       */
      candidate?:
        | string
        | {
            /**
             * The unique identifier of the candidate
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The forename of the candidate
             *
             * @minLength 1
             * @maxLength 256
             */
            forename?: string;
            /**
             * The surname of the candidate
             *
             * @minLength 1
             * @maxLength 256
             */
            surname?: string;
            /**
             * Any previous names of the candidate
             *
             * @maxLength 256
             */
            previousNames?: string;
            /**
             * The email of the candidate. This will be used for communication.
             *
             * @format email
             */
            email?: string;
            /**
             * The telephone number of the candidate
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * The mobile number of the candidate
             *
             * @maxLength 32
             */
            mobile?: string;
            /**
             * The occupation of the candidate
             */
            occupation?:
              | "Teacher"
              | "Cover Supervisor"
              | "Teaching Assistant"
              | "School Support Staff"
              | "Primary Teacher"
              | "Secondary Teacher"
              | "Head Teacher"
              | "Senior Leader"
              | "Tutor"
              | "Mentor"
              | "Learning Support Assistant"
              | "Finance Officer"
              | "HR Administrator"
              | "HR Manager"
              | "Office Manager"
              | "Administrator"
              | "Personal Assistant"
              | "Pastoral Manager"
              | "Receptionist"
              | "Instructor"
              | "Caretaker"
              | "Nursery Assistant"
              | "Nursery Nurse"
              | "Deputy Nursery Manager"
              | "Lunchtime Supervisor"
              | "Classroom Graduate"
              | "Primary Cover Supervisor"
              | "HLTA"
              | "Nurture Teacher"
              | "Nurture Mentor"
              | "SENDCo"
              | "Registered Manager"
              | "Regional Manager"
              | "Education Director"
              | "Residential Director"
              | "Deputy Manager"
              | "Residential Support Worker"
              | "Sports Coach"
              | "Executive Headteacher"
              | "General Practitioner"
              | "Advance Nurse Practitioner (ANP)"
              | "Emergency Nurse Practitioner (ENP)"
              | "Advanced Clinical Practitioner (ACP)"
              | "Emergency Clinical Practitioner (ECP)"
              | "Consultant Psychiatrist"
              | "Psychiatrist \u2013 SPR"
              | "IT Technician"
              | "Art Technician"
              | "DT Technician"
              | "Science Technician"
              | "Healthcare Assistant/ Residential Support Worker"
              | "Healthcare Assistant"
              | "Senior Healthcare Assistant"
              | "Team Leader"
              | "Registered General Nurse"
              | "Registered Mental Nurse"
              | "Registered Nurse Learning Disabilities"
              | "Overseas Trained Teacher";
            /**
             * The subject the candidate specializes in
             *
             * @maxLength 256
             */
            subject?: string;
            /**
             * The address of the resource
             */
            address?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The qualifications of the candidate
             *
             * @maxItems 32
             */
            qualifications?: {
              /**
               * The type of the qualification
               *
               * @maxLength 256
               */
              type?: string;
              /**
               * The place where the qualification was obtained
               *
               * @maxLength 256
               */
              place?: string;
              /**
               * The name of the qualification
               *
               * @maxLength 256
               */
              qualificationName?: string;
              /**
               * The date the qualification was obtained
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              date?: string;
              /**
               * The unique identifier of the qualification
               *
               * @maxLength 256
               */
              qualificationId?: string;
              /**
               * The order index of the qualification
               *
               * @minimum 0
               */
              orderIndex?: number;
              /**
               * The expiry date of the qualification
               *
               * @maxLength 256
               */
              qualificationExpiry?: string;
              /**
               * Whether to exclude this qualification from vetting
               */
              excludeFromVetting?: boolean;
              /**
               * The date the qualification was created
               *
               * @format date-time
               */
              createdAt?: string | null;
            }[];
            /**
             * The employment history of the candidate
             *
             * @maxItems 32
             */
            employment?: {
              /**
               * The name of the employer
               *
               * @maxLength 256
               */
              employerName?: string;
              /**
               * The date the candidate started employment
               *
               * @maxLength 256
               */
              dateStarted?: string;
              /**
               * The date the candidate ended employment
               *
               * @maxLength 256
               */
              dateEnded?: string;
              /**
               * The job title of the candidate
               *
               * @maxLength 256
               */
              jobTitle?: string;
              /**
               * The reason the candidate left this employment
               *
               * @maxLength 1024
               */
              reasonForLeaving?: string;
              /**
               * The duties of the candidate in this role. Consultant only, visible on Edun CV
               *
               * @maxLength 1024
               */
              duties?: string;
              /**
               * The salary of the candidate in this role
               *
               * @maxLength 256
               */
              salary?: string;
              /**
               * The address of the candidate's reference for this employment
               */
              referenceAddress?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
              /**
               * The unique identifier for this employment record
               *
               * @maxLength 256
               */
              employmentId?: string;
              /**
               * The order index of this employment record
               *
               * @minimum 0
               */
              orderIndex?: number;
            }[];
            /**
             * The references for the candidate
             *
             * @maxItems 32
             */
            references?: {
              /**
               * The title of the reference
               *
               * @maxLength 16
               */
              referenceTitle?: string;
              /**
               * The forename of the reference
               *
               * @maxLength 256
               */
              referenceForename?: string;
              /**
               * The surname of the reference
               *
               * @maxLength 256
               */
              referenceSurname?: string;
              /**
               * The company of the reference
               *
               * @maxLength 256
               */
              referenceCompany?: string;
              /**
               * The position of the reference
               *
               * @maxLength 256
               */
              referencePosition?: string;
              /**
               * The email of the reference
               *
               * @format email
               */
              referenceEmail?: string;
              /**
               * The telephone number of the reference
               *
               * @maxLength 32
               */
              referenceTelephone?: string;
              /**
               * The type of the reference
               */
              referenceType?:
                | "Education"
                | "Professional"
                | "Character"
                | "Factual"
                | "Academic"
                | "Volunteer";
              /**
               * The duration of the reference
               *
               * @maxLength 128
               */
              referenceDuration?: string;
              /**
               * The ID of the reference
               *
               * @maxLength 256
               */
              referenceId?: string;
              /**
               * This field only applies if the reference immediate consent question is enabled
               * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
               */
              referenceApply?: boolean;
              /**
               * Any additional notes about the reference
               *
               * @maxLength 1024
               */
              referenceNote?: string;
              /**
               * The start date of the reference
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              dateFrom?: string;
              /**
               * The end date of the reference
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              dateTo?: string;
            }[];
            /**
             * Whether the candidate has consented to a reference check
             */
            referenceCheckConsent?: boolean;
            /**
             * The name of the person who provided consent for the reference check
             *
             * @maxLength 256
             */
            referenceCheckConsentName?: string;
            /**
             * The date when consent for the reference check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            referenceCheckConsentDate?: string;
            /**
             * The date of birth of the candidate
             *
             * @format date-time
             */
            dob?: string | null;
            /**
             * The sex of the candidate
             */
            sex?: "Male" | "Female" | "Other" | "Unknown";
            /**
             * The status of the candidate
             */
            status?:
              | "Pending"
              | "Approved"
              | "Rejected"
              | "Anonymised"
              | "Archived"
              | "Enquired"
              | "Hold"
              | "Future"
              | "DNC"
              | "Pre";
            /**
             * The current status of the onboarding process for the candidate
             */
            onboardingStatus?: "Not Started" | "In Progress" | "Completed";
            /**
             * Whether the onboarding information has been sent to the candidate
             */
            onboardingSent?: boolean;
            nextOfKin?: {
              /**
               * The name of the next of kin
               *
               * @maxLength 256
               */
              nextOfKinName?: string;
              /**
               * The relationship of the next of kin to the candidate
               *
               * @maxLength 256
               */
              nextOfKinRelationship?: string;
              /**
               * The telephone number of the next of kin
               *
               * @maxLength 32
               */
              nextOfKinTelephone?: string;
              /**
               * The email address of the next of kin
               *
               * @format email
               */
              nextOfKinEmail?: string;
              nextOfKinAddress?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
            };
            criminalRecord?: {
              /**
               * Whether the candidate has a DBS certificate
               */
              dbsHeld?: boolean;
              /**
               * The unique identifier for the DBS certificate
               *
               * @maxLength 256
               */
              dbsNumber?: string;
              /**
               * The type of DBS check
               */
              dbsType?:
                | "Basic"
                | "Standard"
                | "Enhanced"
                | "Enhanced with Barred List"
                | "Child workforce"
                | "Child and adult workforce"
                | "Adult workforce";
              /**
               * The date when the DBS check was last performed
               *
               * @maxLength 256
               */
              dbsLastChecked?: string;
              /**
               * The DBS update service number for the candidate
               *
               * @maxLength 256
               */
              dbsUpdateServiceNumber?: string;
              /**
               * The result of the prohibition check
               *
               * @maxLength 256
               */
              prohibitionCheck?: string;
              /**
               * The result of the overseas check
               *
               * @maxLength 256
               */
              overseasCheck?: string;
              /**
               * Any additional notes regarding the overseas check
               *
               * @maxLength 256
               */
              overseasCheckNote?: string;
              /**
               * Whether the candidate has a convicted offence
               */
              convictedOffence?: boolean;
              /**
               * The description of the convicted offence
               *
               * @maxLength 1024
               */
              convictedOffenceDescription?: string;
              /**
               * Whether the candidate has any convictions
               */
              convictions?: boolean;
              /**
               * The description of the convictions
               *
               * @maxLength 1024
               */
              convictionsDescription?: string;
              /**
               * Whether the candidate is registered to the DBS update service
               */
              registeredToUpdateService?: boolean;
              /**
               * Whether the candidate consents to update service checks
               */
              consentUpdateServiceChecks?: boolean;
              /**
               * Any additional notes regarding the candidate's criminal record
               *
               * @maxLength 1024
               */
              note?: string;
              /**
               * The date when the update service check was performed
               *
               * @maxLength 64
               */
              updateServiceCheckDate?: string;
              /**
               * Whether the candidate has lived abroad
               */
              abroad?: boolean;
              /**
               * Any additional notes regarding the overseas declaration
               *
               * @maxLength 1024
               */
              overseasDeclarationExtended?: string;
              /**
               * Whether the candidate has been subject to educational disciplinary proceedings
               */
              educationalDisciplinaryProceedings?: boolean;
              /**
               * Any comments regarding the DBS check
               *
               * @maxLength 1024
               */
              dbsComments?: string;
              /**
               * Whether the candidate has read the DBS declaration
               */
              dbsDeclarationRead?: boolean;
              /**
               * Whether the candidate has any overseas offences
               */
              overseasOffence?: boolean;
              /**
               * The description of the overseas offence
               *
               * @maxLength 1024
               */
              overseasOffenceDescription?: string;
              /**
               * Whether the candidate has a current criminal offence
               */
              currentCriminalOffence?: boolean;
              /**
               * The description of the current criminal offence
               *
               * @maxLength 1024
               */
              currentCriminalOffenceDescription?: string;
              /**
               * Whether the DBS was issued by a business
               */
              dbsIssuedByBusiness?: boolean;
              /**
               * Whether the DBS was issued by the current employer
               */
              dbsIssuedByCurrentEmployer?: boolean;
              /**
               * Whether a Section 128 check is required for the candidate.
               */
              section128Required?: boolean;
              /**
               * The date when the Section 128 check was completed.
               */
              section128Checked?: string;
            };
            /**
             * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
             */
            criminalRecordDeclaration?: {
              /**
               * The ordered questions included in the question set.
               *
               * @maxItems 100
               */
              questions?: {
                /**
                 * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * A stable key used to associate the question with current and historical answers.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                key?: string;
                /**
                 * The question shown to the person completing the form.
                 *
                 * @minLength 1
                 * @maxLength 500
                 */
                label?: string;
                /**
                 * Optional supporting text displayed with the question.
                 *
                 * @maxLength 2000
                 */
                description?: string;
                /**
                 * The type of answer collected for the question.
                 */
                type?:
                  | "checkbox"
                  | "date"
                  | "daterange"
                  | "dropdown"
                  | "number"
                  | "signature"
                  | "text"
                  | "textarea";
                /**
                 * Whether an answer is required while the question is visible.
                 */
                required?: boolean;
                /**
                 * Whether the question is included in new forms. Historical responses retain disabled questions.
                 */
                enabled?: boolean;
                /**
                 * The display order of the question.
                 *
                 * @minimum 0
                 * @maximum 1000
                 */
                order?: number;
                /**
                 * Optional rules that determine when the question is shown.
                 */
                condition?: {
                  /**
                   * Whether any rule or every rule must match before the question is shown.
                   */
                  mode?: "any" | "all";
                  /**
                   * The comparisons used to determine question visibility.
                   *
                   * @minItems 1
                   * @maxItems 20
                   */
                  rules?: {
                    /**
                     * The stable key of the question that controls visibility.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    questionKey?: string;
                    /**
                     * How the controlling answer is compared with the value.
                     */
                    operator?: "equals" | "notEquals";
                    /**
                     * The answer value used to determine visibility.
                     */
                    value?: string | boolean | number | (void | null);
                  }[];
                };
                /**
                 * The available answers for a dropdown question.
                 *
                 * @minItems 1
                 * @maxItems 100
                 */
                options?: {
                  /**
                   * The value stored when this option is selected.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  value?: string;
                  /**
                   * The option displayed to the person answering the question.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  label?: string;
                }[];
                /**
                 * Whether this question belongs to the standard template.
                 */
                ["default"]?: boolean;
                /**
                 * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                 */
                audience?: "candidate" | "staff";
                /**
                 * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                 */
                fixedRole?: "fullName" | "signature";
              }[];
              /**
               * The answers recorded for the point-in-time question set.
               *
               * @maxItems 100
               */
              answers?: {
                /**
                 * The identifier of the question being answered.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                questionId?: string;
                /**
                 * The stable key of the question being answered.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey?: string;
                /**
                 * The submitted answer to the question.
                 */
                value?: string | string | boolean | number | (void | null);
              }[];
              /**
               * When the question set was submitted.
               *
               * @format date-time
               */
              submittedAt?: string;
              /**
               * The network address recorded when the response was submitted.
               *
               * @maxLength 64
               */
              submittedIp?: string;
              /**
               * When the question configuration captured by this response was last updated.
               *
               * @format date-time
               */
              configurationUpdatedAt?: string;
            };
            /**
             * Whether the candidate confirmed their criminal record declaration.
             */
            criminalRecordCheckConsent?: boolean;
            /**
             * The name used to confirm the criminal record declaration.
             *
             * @maxLength 256
             */
            criminalRecordCheckConsentName?: string;
            /**
             * The date the criminal record declaration was confirmed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            criminalRecordCheckConsentDate?: string;
            safeInEducation?: {
              /**
               * Whether the candidate is barred from working in education
               */
              barred?: boolean;
              /**
               * The date when the barred status was checked
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              barredCheckDate?: string;
              /**
               * Whether the candidate has a waiver for the barred status
               */
              waiver?: boolean;
              /**
               * Any orders related to the candidate's barred status
               */
              ordersYou?: boolean;
              /**
               * Any orders related to the candidate's barred status for children
               */
              ordersChild?: boolean;
              /**
               * Whether the candidate has any previous safeguarding allegations
               */
              previousSafeguardingAllegations?: boolean;
              /**
               * Whether the candidate has been cautioned
               */
              cautioned?: boolean;
              /**
               * Whether the candidate has a violent offence
               */
              violentOffence?: boolean;
              /**
               * Whether the candidate has lived or worked in another country
               */
              anotherCountry?: boolean;
              /**
               * Whether the candidate has read the Keeping Children Safe in Education document
               */
              readKCSIE?: boolean;
              /**
               * Whether the candidate has read the Disqualification under the Childcare Act guidance
               */
              readChildcareAct?: boolean;
              /**
               * Whether the candidate is prohibited from teaching
               */
              prohibitedFromTeaching?: boolean;
              /**
               * Whether the candidate is known to the police
               */
              knownPolice?: boolean;
              /**
               * Whether the candidate is prohibited from management roles
               */
              prohibitedFromManagement?: boolean;
              /**
               * Whether the candidate has any criminal offences under law
               */
              criminalOffencesLaw?: boolean;
              /**
               * Any additional details regarding the candidate's safe in education status
               */
              safeInEducationDetails?: string;
              safeguardingQuestionaire?: {
                /**
                 * What is the primary purpose of safeguarding in a school environment?
                 */
                primaryPurposeSafeguarding?:
                  | "enhance-academic-performance"
                  | "ensure-safe-learning-environment"
                  | "promote-healthy-relationships"
                  | "facilitate-extracurricular-activities";
                /**
                 * Who is typically responsible for coordinating safeguarding efforts within a school?
                 */
                typicallyResponsibleCoordinatingSafeguarding?:
                  | "janitorial-staff"
                  | "parents-association"
                  | "school-nurse"
                  | "designated-safeguarding-lead";
                /**
                 * Which of the following is considered a potential sign of child abuse or neglect?
                 */
                signOfAbuse?:
                  | "student-excelling"
                  | "frequent-extracurricular"
                  | "unexplained-injuries"
                  | "not-listening";
                /**
                 * How should a teacher respond if a student discloses information about potential abuse or harm?
                 */
                studentDisclosesInformation?:
                  | "ignore-disclosure"
                  | "report-disclosure"
                  | "share-information"
                  | "advise-independent";
                /**
                 * What is the purpose of a school's Child Protection Policy?
                 */
                schoolsChildProtectionPolicy?:
                  | "enforcing-strict-disciplinary-measures"
                  | "identifying-troublemakers"
                  | "outlining-procedures-for-protecting-students-from-harm"
                  | "establishing-academic-achievement-standards";
                /**
                 * What does the term 'FGM' stand for in the context of safeguarding?
                 */
                fgmDefinition?:
                  | "female-general-medicine"
                  | "forced-genetic-modification"
                  | "female-genital-mutilation"
                  | "foundation-gender-equality";
                /**
                 * Which of the following is a key principle of online safety for students?
                 */
                keyPrincipleOnlineSafetyStudents?:
                  | "unlimited-screen-time"
                  | "anon-online-interaction"
                  | "responsible-digital-citizen"
                  | "restrict-edu-sites";
                /**
                 * When planning a school trip, what should teachers consider regarding safeguarding?
                 */
                teachersConsiderRegardingSafeguarding?:
                  | "minimise-supervision"
                  | "appropriate-staff-ratios"
                  | "exclude-medical-conditions"
                  | "allow-roaming";
                /**
                 * What is the purpose of a criminal background check for teachers?
                 */
                criminalBackgroundCheckTeachers?:
                  | "evaluating-teaching-proficiency"
                  | "verifying-immigration-status"
                  | "assessing-financial-stability"
                  | "identifying-criminal-history";
                /**
                 * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
                 */
                studentConfides?:
                  | "assure-secret-safe"
                  | "encourage-share-friends"
                  | "explain-no-guarantee"
                  | "disclose-to-others";
              };
            };
            socialMedia?: {
              /**
               * The Facebook profile URL of the candidate
               *
               * @maxLength 256
               */
              facebook?: string;
              /**
               * The Instagram profile URL of the candidate
               *
               * @maxLength 256
               */
              instagram?: string;
              /**
               * The Twitter profile URL of the candidate
               *
               * @maxLength 256
               */
              twitter?: string;
              /**
               * The LinkedIn profile URL of the candidate
               *
               * @maxLength 256
               */
              linkedIn?: string;
              /**
               * The TikTok profile URL of the candidate
               *
               * @maxLength 256
               */
              tiktok?: string;
              /**
               * Any other social media profile URLs of the candidate
               *
               * @maxLength 256
               */
              others?: string;
              /**
               * Whether the social media profiles have been checked
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              socialMediaChecked?: string;
              /**
               * The date the candidate's Google search check was completed
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              googleCheckedDate?: string;
            };
            /**
             * Whether the candidate has consented to a safe in education check
             */
            safeInEducationCheckConsent?: boolean;
            /**
             * The name of the person who provided consent for the safe in education check
             *
             * @maxLength 256
             */
            safeInEducationCheckConsentName?: string;
            /**
             * The date when consent for the safe in education check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            safeInEducationCheckConsentDate?: string;
            /**
             * The health record of the individual
             */
            healthRecord?: {
              /**
               * Whether the individual has a physical ability condition
               */
              physicalAbilityCondition?: boolean;
              /**
               * Whether the individual is confused
               */
              confused?: boolean;
              /**
               * Whether the individual has hearing issues
               */
              hearing?: boolean;
              /**
               * Whether the individual has eyesight issues
               */
              eyesight?: boolean;
              /**
               * Whether the individual has depression
               */
              depression?: boolean;
              /**
               * Whether the individual has severe pain
               */
              severePain?: boolean;
              /**
               * Whether the individual has excessive drowsiness
               */
              excessiveDrowsiness?: boolean;
              /**
               * Whether the individual has epilepsy
               */
              epilepsy?: boolean;
              /**
               * Whether the individual has heart issues
               */
              heart?: boolean;
              /**
               * Whether the individual has asthma
               */
              asthma?: boolean;
              /**
               * Whether the individual has mental health issues
               */
              mentalHealth?: boolean;
              /**
               * Whether the individual is an expectant mother
               */
              expectantMother?: boolean;
              /**
               * Whether the individual has allergies
               */
              allergies?: boolean;
              /**
               * Whether the individual is taking medication
               */
              medication?: boolean;
              /**
               * Whether the individual has any health issues
               */
              healthIssues?: boolean;
              /**
               * A description of the individual's health issues
               *
               * @maxLength 2048
               */
              healthIssuesDescription?: string;
              /**
               * Any disability needs of the individual
               *
               * @maxLength 2048
               */
              disabilityNeeds?: string;
              /**
               * Whether the individual has any contagious diseases
               */
              contagiousDiseases?: boolean;
              /**
               * Whether the individual's health affects their ability
               */
              affectAbility?: boolean;
              /**
               * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
               */
              notificationAgree?: boolean;
              /**
               * Whether the individual has opted into the SAS Wellbeing App
               */
              sasOptIn?: boolean;
              /**
               * The individual's signature
               *
               * @maxLength 256
               */
              signature?: string;
              /**
               * The individual's drawn signature in base64url format
               *
               * @maxLength 4096
               */
              drawnSignature?: string;
              /**
               * The date the individual signed the form
               *
               * @format date-time
               */
              signatureDate?: string | null;
              /**
               * Whether the individual has been vaccinated against measles
               */
              vaccinationMeasles?: boolean;
              /**
               * Whether the individual has been vaccinated against mumps
               */
              vaccinationMumps?: boolean;
              /**
               * Whether the individual has been vaccinated against rubella
               */
              vaccinationRubella?: boolean;
              /**
               * Whether the individual has been vaccinated against varicella
               */
              vaccinationVaricella?: boolean;
              /**
               * Whether the individual has been vaccinated against tuberculosis
               */
              vaccinationTuberculosis?: boolean;
              /**
               * Whether the individual has been vaccinated against hepatitis B
               */
              vaccinationHepatitisB?: boolean;
              /**
               * Whether the individual has been vaccinated against hepatitis C
               */
              vaccinationHepatitisC?: boolean;
              /**
               * Whether the individual has been vaccinated against HIV
               */
              vaccinationHIV?: boolean;
              /**
               * Whether the individual has been vaccinated against COVID-19
               */
              vaccinationCovid?: boolean;
              /**
               * Whether the individual has received the 1st dose of the COVID-19 vaccine
               */
              vaccinationCovid1?: boolean;
              /**
               * Whether the individual has received the 2nd dose of the COVID-19 vaccine
               */
              vaccinationCovid2?: boolean;
              /**
               * Whether the individual has received the COVID-19 booster vaccine
               */
              vaccinationCovidBooster?: boolean;
              /**
               * Whether the individual has received the COVID-19 influenza vaccine
               */
              vaccinationCovidInfluenza?: boolean;
              /**
               * Whether the individual has an override for fit to work requirements
               */
              fitToWorkOverride?: boolean;
            };
            /**
             * The candidate's latest submitted health declaration, including the exact questions presented.
             */
            healthDeclaration?: {
              /**
               * The ordered questions included in the question set.
               *
               * @maxItems 100
               */
              questions?: {
                /**
                 * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * A stable key used to associate the question with current and historical answers.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                key?: string;
                /**
                 * The question shown to the person completing the form.
                 *
                 * @minLength 1
                 * @maxLength 500
                 */
                label?: string;
                /**
                 * Optional supporting text displayed with the question.
                 *
                 * @maxLength 2000
                 */
                description?: string;
                /**
                 * The type of answer collected for the question.
                 */
                type?:
                  | "checkbox"
                  | "date"
                  | "daterange"
                  | "dropdown"
                  | "number"
                  | "signature"
                  | "text"
                  | "textarea";
                /**
                 * Whether an answer is required while the question is visible.
                 */
                required?: boolean;
                /**
                 * Whether the question is included in new forms. Historical responses retain disabled questions.
                 */
                enabled?: boolean;
                /**
                 * The display order of the question.
                 *
                 * @minimum 0
                 * @maximum 1000
                 */
                order?: number;
                /**
                 * Optional rules that determine when the question is shown.
                 */
                condition?: {
                  /**
                   * Whether any rule or every rule must match before the question is shown.
                   */
                  mode?: "any" | "all";
                  /**
                   * The comparisons used to determine question visibility.
                   *
                   * @minItems 1
                   * @maxItems 20
                   */
                  rules?: {
                    /**
                     * The stable key of the question that controls visibility.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    questionKey?: string;
                    /**
                     * How the controlling answer is compared with the value.
                     */
                    operator?: "equals" | "notEquals";
                    /**
                     * The answer value used to determine visibility.
                     */
                    value?: string | boolean | number | (void | null);
                  }[];
                };
                /**
                 * The available answers for a dropdown question.
                 *
                 * @minItems 1
                 * @maxItems 100
                 */
                options?: {
                  /**
                   * The value stored when this option is selected.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  value?: string;
                  /**
                   * The option displayed to the person answering the question.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  label?: string;
                }[];
                /**
                 * Whether this question belongs to the standard template.
                 */
                ["default"]?: boolean;
                /**
                 * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                 */
                audience?: "candidate" | "staff";
                /**
                 * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                 */
                fixedRole?: "fullName" | "signature";
              }[];
              /**
               * The answers recorded for the point-in-time question set.
               *
               * @maxItems 100
               */
              answers?: {
                /**
                 * The identifier of the question being answered.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                questionId?: string;
                /**
                 * The stable key of the question being answered.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey?: string;
                /**
                 * The submitted answer to the question.
                 */
                value?: string | string | boolean | number | (void | null);
              }[];
              /**
               * When the question set was submitted.
               *
               * @format date-time
               */
              submittedAt?: string;
              /**
               * The network address recorded when the response was submitted.
               *
               * @maxLength 64
               */
              submittedIp?: string;
              /**
               * When the question configuration captured by this response was last updated.
               *
               * @format date-time
               */
              configurationUpdatedAt?: string;
            };
            /**
             * Whether the candidate confirmed their health declaration.
             */
            healthRecordCheckConsent?: boolean;
            /**
             * The name used to confirm the health declaration.
             *
             * @maxLength 256
             */
            healthRecordCheckConsentName?: string;
            /**
             * The date the health declaration was confirmed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            healthRecordCheckConsentDate?: string;
            /**
             * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
             */
            bankDetails?: {
              /**
               * The name on the bank account
               */
              bankAccountName?: string;
              /**
               * The name of the bank
               */
              bankName?: string;
              /**
               * The account number
               */
              accountNumber?: string;
              /**
               * The sort code
               */
              sortCode?: string;
              /**
               * The National Insurance number
               */
              niNumber?: string;
              /**
               * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
               */
              jobOp1?: boolean;
              /**
               * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
               */
              jobOp2?: boolean;
              /**
               * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
               */
              jobOp3?: boolean;
              studentLoan?: {
                /**
                 * Boolean flag for if candidate has student loan
                 */
                studentLoan?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 1
                 */
                plan1?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 2
                 */
                plan2?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 4
                 */
                plan4?: boolean;
                /**
                 * Boolean flag for if candidate has postgrad loan
                 */
                postGradLoan?: boolean;
              };
            };
            /**
             * Whether the candidate has confirmed their details are correct.
             */
            declarationCheckConsent?: boolean;
            /**
             * The name of the person who confirmed their details are correct.
             *
             * @maxLength 256
             */
            declarationCheckConsentName?: string;
            /**
             * The date when consent for the declaration check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            declarationCheckConsentDate?: string;
            /**
             * Whether the candidate has consented to a contract for service
             */
            contractForServiceConsent?: boolean;
            /**
             * The name of the person who provided consent for the contract for service
             *
             * @maxLength 256
             */
            contractForServiceName?: string;
            /**
             * The date when consent for the contract for service was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            contractForServiceDate?: string;
            /**
             * The candidate has completed their onboarding form.
             */
            confirmed?: boolean;
            /**
             * The ID of the user who created the candidate record.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            createdBy?: string;
            /**
             * The ID of the customer associated with the candidate.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customerId?: string;
            /**
             * The migration that created this candidate
             *
             * @pattern ^[0-9a-f]{24}$
             */
            migrationId?: string;
            /**
             * Whether this migrated candidate has been approved
             */
            migrationApproval?: "pending" | "approved" | "rejected";
            /**
             * The user assigned to the candidate. If expanded, a user object else the user ID.
             */
            assignee?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * The ID of the current booking for the candidate.
             */
            currentBooking?: string[];
            /**
             * The URL of the candidate's profile photo.
             *
             * @maxLength 256
             */
            profilePhoto?: string;
            /**
             * The pay type for the candidate.
             */
            payType?:
              | "PAYE"
              | "Umbrella"
              | "Limited Company"
              | "Not Applicable";
            /**
             * The pay interval for the candidate.
             */
            payInterval?:
              | "Monthly"
              | "Weekly"
              | "Weekly Pay in Arrears"
              | "Bi-Weekly"
              | any
              | null;
            /**
             * The pay rate for the candidate.
             */
            payRate?: string;
            /**
             * A custom ID for the candidate.
             *
             * @maxLength 256
             */
            customId?: string;
            /**
             * A company-defined identifier used to match the candidate with external records.
             *
             * @maxLength 256
             */
            _internalId?: string;
            /**
             * The date when the candidate's status was last changed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            statusChangeDate?: string;
            /**
             * Whether the candidate has opted out of communications.
             */
            optOut?: boolean;
            /**
             * The TRN number for the candidate.
             *
             * @maxLength 256
             */
            trnNumber?: string;
            /**
             * The date when the TRN number was last checked.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            trnNumberCheckedDate?: string;
            /**
             * Any notes related to the TRN number.
             *
             * @maxLength 1024
             */
            trnNumberNotes?: string;
            /**
             * The date when the candidate's employment is set to start.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            employmentStartDate?: string;
            /**
             * Whether the candidate has confirmed their Early Years Workforce status.
             */
            ewcConfirmation?: boolean;
            /**
             * The share code for the candidate's Home Office application.
             *
             * @maxLength 256
             */
            homeOfficeShareCode?: string;
            /**
             * Whether the candidate has been archived.
             */
            archived?: boolean;
            /**
             * Whether the candidate is using an umbrella company.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            umbrella?: string;
            /**
             * ID of the PAYE umbrella company.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            payeUmbrella?: string;
            /**
             * The name of the umbrella company the candidate is using.
             *
             * @maxLength 256
             */
            umbrellaCompanyName?: string;
            /**
             * The code of the umbrella company the candidate is using.
             *
             * @maxLength 256
             */
            umbrellaCompanyCode?: string;
            /**
             * The transport method for the candidate.
             */
            transport?:
              | "Drives Own Vehicle"
              | "Use Public Transport"
              | "Access to Another Vehicle"
              | "Bicycle"
              | "No Access"
              | "Other";
            /**
             * Any important notes related to the candidate.
             *
             * @maxLength 1024
             */
            importantNotes?: string;
            /**
             * The date when the candidate was last updated.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            candidateLastUpdated?: string;
            /**
             * The source from which the candidate was obtained.
             */
            source?:
              | (
                  | "Company Website"
                  | "iCan Teach UK Website"
                  | "Apply to Education"
                  | "CV Library - CV Search"
                  | "CV Library Advert"
                  | "CV Library - Watchdog"
                  | "Guardian- CV Search"
                  | "Guardian Advert"
                  | "Indeed - CV Search"
                  | "Indeed Advert"
                  | "Reed - CV Search"
                  | "Reed Advert"
                  | "Reed \u2013 Watchdog"
                  | "SENploy - Advert"
                  | "Total Jobs"
                  | "Other Job Board"
                  | "Google"
                  | "LinkedIn"
                  | "Linkedin Recruiter"
                  | "LinkedIn Advert"
                  | "Referral - School"
                  | "Referral - Candidate"
                  | "UK University"
                  | "Irish University"
                  | "Canadian University"
                  | "Marketing Campaign"
                  | "Woo"
                  | "Job Show"
                  | "Recruitment Fair"
                  | "Career Fair"
                  | "Volcanic"
                  | "Broadbean"
                  | "Headhunt"
                  | "Referral"
                  | "Direct"
                  | "Other"
                )
              | (
                  | "Company Website"
                  | "Direct Call"
                  | "CV Library Watchdog"
                  | "CV Library Advert"
                  | "CV Library CV Search"
                  | "Reed Watchdog"
                  | "Reed Advert"
                  | "Reed CV Search"
                  | "Indeed Watchdog"
                  | "Indeed Advert"
                  | "Indeed CV Search"
                  | "Job Centre"
                  | "Returning Candidate"
                  | "Referral Candidate"
                  | "Referral Client"
                  | "Training Provider"
                  | "Recruitment Fair"
                  | "University/College"
                );
            /**
             * Any other source from which the candidate was obtained.
             *
             * @maxLength 256
             */
            sourceOther?: string;
            /**
             * Whether the candidate has given consent for online checks.
             */
            onlineCheckConsent?: boolean;
            /**
             * The title of the candidate.
             */
            title?:
              | "Mr"
              | "Mrs"
              | "Ms"
              | "Miss"
              | "Dr"
              | "Mx"
              | "Lady"
              | "Lord"
              | "Other";
            /**
             * The pronoun used by the candidate.
             */
            pronoun?:
              | "She/Her/Hers"
              | "He/Him/His"
              | "They/Them/Theirs"
              | "Ze/Hir/Hirs"
              | "Ze/Zir/Zirs"
              | "Other"
              | "Prefer not to say";
            /**
             * The date when the candidate was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
            /**
             * The date when the candidate was last contacted.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            lastContactedDate?: string;
            /**
             * A short, prominent note shown with the candidate to communicate important context.
             */
            highlight?: string;
            /**
             * The pay scale for the candidate's teaching position.
             *
             * @maxLength 256
             */
            teacherPayScale?: string;
            /**
             * Whether the candidate has a P45.
             */
            p45?: boolean;
            /**
             * The type of role the candidate is applying for.
             */
            roleType?:
              | "Senior Leadership Positions"
              | "Teaching Roles"
              | "School Support Positions";
            /**
             * The Right to Work verification status for the candidate.
             *
             * @maxLength 256
             */
            rtwVerification?: string;
            /**
             * Any notes related to the Right to Work verification status for the candidate.
             *
             * @maxLength 1024
             */
            rtwVerificationNote?: string;
            /**
             * The Right to Work verification status for the candidate.
             *
             * @maxLength 256
             */
            rtwVerificationStatus?: string;
            /**
             * The location where the candidate desires to work.
             *
             * @maxLength 256
             */
            desiredLocation?: string;
            /**
             * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
             */
            complianceUser?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
             */
            watchers?:
              | string[]
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                }[];
            /**
             * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
             */
            tags?:
              | string[]
              | {
                  /**
                   * The unique identifier of the tag
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The label displayed wherever this tag is applied.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  name?: string;
                  /**
                   * The tag display colour as a hexadecimal value, for example #2563EB.
                   *
                   * @maxLength 32
                   */
                  color?: string | null;
                }[];
            /**
             * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
             *
             * @maxItems 5
             */
            documents?: {
              /**
               * The unique identifier of the document
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The name of the document
               *
               * @maxLength 512
               */
              name?: string;
              /**
               * The file reference used to retrieve the document
               *
               * @maxLength 2048
               */
              reference?: string;
              /**
               * The file's media type
               *
               * @maxLength 256
               */
              fileType?: string;
              /**
               * The area of the platform the document belongs to
               *
               * @maxLength 256
               */
              type?: string;
              /**
               * The configured category of the document
               *
               * @maxLength 256
               */
              docType?: string;
              /**
               * The document's expiry date, when applicable
               *
               * @maxLength 64
               */
              expiry?: string;
              /**
               * The document's issued date, when applicable
               *
               * @maxLength 64
               */
              issued?: string;
              /**
               * Whether the document has been archived
               */
              documentArchived?: boolean;
              /**
               * The date the document was uploaded
               *
               * @format date-time
               */
              createdAt?: string;
            }[];
            /**
             * The total number of documents attached to the candidate.
             *
             * @minimum 0
             */
            documentsCount?: number;
            /**
             * The locations where the candidate desires to work.
             */
            desiredLocationMultiple?: string[];
            /**
             * Daily availability for the requested period. Returned by the availability view only.
             *
             * @maxItems 31
             */
            availabilityDays?: {
              /**
               * Calendar date in YYYY-MM-DD format.
               *
               * @maxLength 10
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              date?: string;
              /**
               * Availability or booking state for this date.
               */
              status?:
                | "available"
                | "unavailable"
                | "booked"
                | "halfDay"
                | "awaiting"
                | "unknown";
              /**
               * Recorded availability, or null when unrecorded.
               */
              available?: boolean | null;
              /**
               * Morning or afternoon availability.
               */
              partial?: "AM" | "PM" | any | null;
              /**
               * Additional availability information.
               *
               * @maxLength 4096
               */
              note?: string | null;
              /**
               * Active bookings on this date.
               *
               * @maxItems 100
               */
              bookings?: {
                /**
                 * The unique identifier of the resource
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * @maxLength 512
                 */
                clientName?: string;
                /**
                 * @maxLength 512
                 */
                role?: string;
                /**
                 * @maxLength 32
                 */
                timeDesignation?: string;
              }[];
            }[];
            /**
             * The date when the candidate's availability was last checked.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            availabilityLastChecked?: string;
            /**
             * Whether the candidate is subject to notice period.
             */
            subjectToNotice?: "Unsure" | "Yes" | "No";
            /**
             * The subsidiary subjects the candidate is qualified to teach.
             */
            subsidiarySubject?: string[];
            /**
             * The candidate's preferred employment type.
             */
            employmentPreference?: (
              | "Day To Day"
              | "Long Term"
              | "Fixed Term"
              | "Perm"
            )[];
            /**
             * Any other names the candidate is known by.
             *
             * @maxLength 256
             */
            otherNames?: string;
            /**
             * The name of the candidate's limited company, if applicable.
             *
             * @maxLength 256
             */
            ltdCompany?: string;
            /**
             * Whether the candidate's employment has been verified.
             */
            employmentVerified?: boolean;
            /**
             * The DBS verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSVerification?: string;
            /**
             * Any notes related to the DBS verification status for the candidate.
             *
             * @maxLength 1024
             */
            candidateDBSVerificationNote?: string;
            /**
             * The DBS verification status for the candidate.
             */
            candidateDBSVerificationStatus?: "Cleared" | "Alerts";
            /**
             * The DBS check verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSCheckVerification?: string;
            /**
             * Any notes related to the DBS check verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSCheckVerificationNote?: string;
            /**
             * The DBS check verification status for the candidate.
             */
            candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
            /**
             * Any notes related to the DBS update service verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSUpdateServiceVerificationNote?: string;
            /**
             * The DBS update service verification status for the candidate.
             */
            candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
            /**
             * Any notes related to the TRN verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateTRNVerificationNote?: string;
            /**
             * The TRN verification status for the candidate.
             */
            candidateTRNVerificationStatus?:
              | "Completed Induction"
              | "Not Yet Completed Induction"
              | "Induction Exempt"
              | "ALERTS - Failed Induction"
              | "Induction Not Required";
            /**
             * The health and safety verification status for the candidate.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            candidateHealthSafetyVerification?: string;
            /**
             * Any notes related to the health and safety verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateHealthSafetyVerificationNote?: string;
            /**
             * The health and safety verification status for the candidate.
             */
            candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
            /**
             * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
             */
            onboardingSession?:
              | string
              | {
                  /**
                   * Unique identifier for the onboarding session
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The URL for the onboarding session
                   *
                   * @format uri
                   */
                  url?: string;
                  /**
                   * The frozen health declaration configuration for this onboarding session.
                   */
                  healthDeclarationConfig?: {
                    /**
                     * The ordered questions included in the question set.
                     *
                     * @maxItems 100
                     */
                    questions?: {
                      /**
                       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * A stable key used to associate the question with current and historical answers.
                       *
                       * @minLength 1
                       * @maxLength 128
                       */
                      key?: string;
                      /**
                       * The question shown to the person completing the form.
                       *
                       * @minLength 1
                       * @maxLength 500
                       */
                      label?: string;
                      /**
                       * Optional supporting text displayed with the question.
                       *
                       * @maxLength 2000
                       */
                      description?: string;
                      /**
                       * The type of answer collected for the question.
                       */
                      type?:
                        | "checkbox"
                        | "date"
                        | "daterange"
                        | "dropdown"
                        | "number"
                        | "signature"
                        | "text"
                        | "textarea";
                      /**
                       * Whether an answer is required while the question is visible.
                       */
                      required?: boolean;
                      /**
                       * Whether the question is included in new forms. Historical responses retain disabled questions.
                       */
                      enabled?: boolean;
                      /**
                       * The display order of the question.
                       *
                       * @minimum 0
                       * @maximum 1000
                       */
                      order?: number;
                      /**
                       * Optional rules that determine when the question is shown.
                       */
                      condition?: {
                        /**
                         * Whether any rule or every rule must match before the question is shown.
                         */
                        mode?: "any" | "all";
                        /**
                         * The comparisons used to determine question visibility.
                         *
                         * @minItems 1
                         * @maxItems 20
                         */
                        rules?: {
                          /**
                           * The stable key of the question that controls visibility.
                           *
                           * @minLength 1
                           * @maxLength 128
                           */
                          questionKey?: string;
                          /**
                           * How the controlling answer is compared with the value.
                           */
                          operator?: "equals" | "notEquals";
                          /**
                           * The answer value used to determine visibility.
                           */
                          value?: string | boolean | number | (void | null);
                        }[];
                      };
                      /**
                       * The available answers for a dropdown question.
                       *
                       * @minItems 1
                       * @maxItems 100
                       */
                      options?: {
                        /**
                         * The value stored when this option is selected.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        value?: string;
                        /**
                         * The option displayed to the person answering the question.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        label?: string;
                      }[];
                      /**
                       * Whether this question belongs to the standard template.
                       */
                      ["default"]?: boolean;
                      /**
                       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                       */
                      audience?: "candidate" | "staff";
                      /**
                       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                       */
                      fixedRole?: "fullName" | "signature";
                    }[];
                    /**
                     * When this question configuration was last updated.
                     *
                     * @format date-time
                     */
                    updatedAt?: string;
                  };
                  /**
                   * The frozen Criminal Record & Security Checks configuration for this onboarding session.
                   */
                  criminalRecordConfig?: {
                    /**
                     * The ordered questions included in the question set.
                     *
                     * @maxItems 100
                     */
                    questions?: {
                      /**
                       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * A stable key used to associate the question with current and historical answers.
                       *
                       * @minLength 1
                       * @maxLength 128
                       */
                      key?: string;
                      /**
                       * The question shown to the person completing the form.
                       *
                       * @minLength 1
                       * @maxLength 500
                       */
                      label?: string;
                      /**
                       * Optional supporting text displayed with the question.
                       *
                       * @maxLength 2000
                       */
                      description?: string;
                      /**
                       * The type of answer collected for the question.
                       */
                      type?:
                        | "checkbox"
                        | "date"
                        | "daterange"
                        | "dropdown"
                        | "number"
                        | "signature"
                        | "text"
                        | "textarea";
                      /**
                       * Whether an answer is required while the question is visible.
                       */
                      required?: boolean;
                      /**
                       * Whether the question is included in new forms. Historical responses retain disabled questions.
                       */
                      enabled?: boolean;
                      /**
                       * The display order of the question.
                       *
                       * @minimum 0
                       * @maximum 1000
                       */
                      order?: number;
                      /**
                       * Optional rules that determine when the question is shown.
                       */
                      condition?: {
                        /**
                         * Whether any rule or every rule must match before the question is shown.
                         */
                        mode?: "any" | "all";
                        /**
                         * The comparisons used to determine question visibility.
                         *
                         * @minItems 1
                         * @maxItems 20
                         */
                        rules?: {
                          /**
                           * The stable key of the question that controls visibility.
                           *
                           * @minLength 1
                           * @maxLength 128
                           */
                          questionKey?: string;
                          /**
                           * How the controlling answer is compared with the value.
                           */
                          operator?: "equals" | "notEquals";
                          /**
                           * The answer value used to determine visibility.
                           */
                          value?: string | boolean | number | (void | null);
                        }[];
                      };
                      /**
                       * The available answers for a dropdown question.
                       *
                       * @minItems 1
                       * @maxItems 100
                       */
                      options?: {
                        /**
                         * The value stored when this option is selected.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        value?: string;
                        /**
                         * The option displayed to the person answering the question.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        label?: string;
                      }[];
                      /**
                       * Whether this question belongs to the standard template.
                       */
                      ["default"]?: boolean;
                      /**
                       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                       */
                      audience?: "candidate" | "staff";
                      /**
                       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                       */
                      fixedRole?: "fullName" | "signature";
                    }[];
                    /**
                     * When this question configuration was last updated.
                     *
                     * @format date-time
                     */
                    updatedAt?: string;
                  };
                };
            /**
             * The contact record for this candidate. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * Whether the candidate has opted out of 48 hour communications.
             */
            optOut48Hours?: boolean;
            /**
             * Whether the candidate has consented to REC audit checks.
             */
            recAuditConsent?: boolean | null;
            /**
             * The date the candidate's REC audit consent was recorded.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            recAuditConsentDate?: string;
            /**
             * The method through which REC audit consent was captured.
             *
             * @maxLength 256
             */
            recAuditConsentMethod?: string;
            /**
             * The date when the candidate record was created.
             *
             * @format date-time
             */
            createdAt?: string;
            /**
             * The mobile app link status for the candidate.
             */
            candidateMobileAppStatus?:
              | "Unlinked"
              | "Scheduled Unlink"
              | "Linked"
              | "Invited"
              | "Not Invited";
            /**
             * Evertime details for this candidate
             */
            evertime?: {
              /**
               * The Evertime CandidateId used for this candidate when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
              /**
               * Whether the matched Evertime candidate's NI number also matches the one held in Edun
               */
              niNumberMatch?: boolean;
            };
            /**
             * Search relevance relative to the strongest result, where 100 is the strongest match.
             *
             * @minimum 0
             * @maximum 100
             */
            relevanceScore?: number;
            /**
             * Candidate fields or attached document excerpts that explain why the candidate matched.
             *
             * @maxItems 5
             */
            matchReasons?: {
              /**
               * Whether the matching information came from the candidate record or an attached document.
               */
              source?: "candidate" | "document";
              /**
               * The candidate or document information that matched the search.
               *
               * @maxLength 256
               */
              field?: string;
              /**
               * A short excerpt showing why the result matched.
               *
               * @maxLength 1000
               */
              text?: string;
              /**
               * The exact terms within the content that matched the search, including synonym matches.
               *
               * @maxItems 25
               */
              matchedTerms?: string[];
              /**
               * The name of the attached document when the match came from a document.
               *
               * @maxLength 512
               */
              documentName?: string | null;
              /**
               * The unique identifier of the attached document when the match came from a document.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              documentId?: string | null;
            }[];
            /**
             * The candidate's distance in miles from the requested location. Present only when location filtering is used.
             *
             * @minimum 0
             */
            distance?: number;
            /**
             * Timesheet Portal details for this candidate
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal contractor GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal contractor code
               *
               * @maxLength 256
               */
              code?: string;
            };
            /**
             * Invite to candidate to link their mobile app.
             */
            mobileAppLinkInvite?: {
              /**
               * Boolean status of whether the link invite is being used
               *
               * @default false
               */
              isUsed?: boolean;
              /**
               * Creation date of the invite
               *
               * @format date-time
               * @default 2026-10-30T13:39:09.027Z
               */
              createdAt?: string;
              /**
               * ID of candidate using this link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              usedBy?: string;
              /**
               * Date of when link used
               *
               * @format date-time
               */
              usedAt?: string;
              /**
               * If unlinked, ID of user that unlinked this candidate's link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              unlinkedBy?: string;
              /**
               * If unlinked, date of when this link was unlinked
               *
               * @format date-time
               */
              unlinkedAt?: string;
              /**
               * If deleted, ID of user that deleted this candidate's link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              deletedBy?: string;
              /**
               * If deleted, date of when this link was deleted
               *
               * @format date-time
               */
              deletedAt?: string;
              /**
               * Current status of this mobile app link
               */
              status?:
                | "Sent"
                | "Linked"
                | "Unlinked"
                | "ScheduledUnlink"
                | "Not Sent"
                | "Rescinded";
            };
          };
      /**
       * The current status of the candidate
       */
      status?: "Pending" | "Interested" | "Not Interested";
      /**
       * The client's status for the candidate
       */
      clientStatus?: "Pending" | "Interested" | "Not Interested";
      /**
       * The date when the client expressed interest in the candidate
       *
       * @pattern ^\d{4}-\d{2}-\d{2}$
       */
      clientInterestSentAt?: string | null;
      /**
       * Indicates if this is the default document for the candidate
       */
      defaultDocument?: boolean;
      /**
       * The document ID associated with the candidate
       *
       * @pattern ^[0-9a-f]{24}$
       */
      document?: string;
      /**
       * The name of the document associated with the candidate
       */
      documentName?: string;
      /**
       * The reason for the candidate's status
       */
      reason?: string;
    }[];
    /**
     * The client associated with the vacancy, if expanded client schema else client id
     */
    client?:
      | string
      | {
          /**
           * The unique identifier of the client
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The name of the client
           *
           * @maxLength 256
           */
          name?: string;
          /**
           * The address of the client
           */
          address?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The telephone number of the client
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The email address of the client
           *
           * @format email
           */
          email?: string;
          /**
           * Array of client contacts
           */
          contacts?: {
            /**
             * The unique identifier of the contact
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The forename of the contact
             *
             * @maxLength 128
             */
            forename?: string;
            /**
             * The surname of the contact
             *
             * @maxLength 128
             */
            surname?: string;
            /**
             * The email address of the contact
             *
             * @format email
             */
            email?: string;
            /**
             * The telephone number of the contact
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * The mobile number of the contact
             *
             * @maxLength 32
             */
            mobile?: string;
            /**
             * The extension number of the contact
             *
             * @maxLength 8
             */
            extension?: string;
            /**
             * The role of the contact
             */
            role?:
              | "Head Teacher"
              | "Deputy Head"
              | "Cover Manager"
              | "HR Manager"
              | "Finance Manager"
              | "Head of Maths"
              | "Head of Science"
              | "Head of English"
              | "Assistant Headteacher"
              | "Headteacher's PA"
              | "SENCO"
              | "Other"
              | "Business Manager"
              | "Business Administrator"
              | "Invoice Contact"
              | "Academy Recruitment Contact"
              | "Nursery Manager"
              | "CEO"
              | "COO"
              | "Executive Headteacher"
              | "Inclusion Manager"
              | "EYFS Lead"
              | "Admin Team"
              | "Head of Humanities"
              | "Head of History"
              | "Head of Geography"
              | "Head of Business Studies"
              | "Head of PE"
              | "Head of KS1"
              | "Head of KS2"
              | "Head of KS3"
              | "Head of KS4"
              | "Virtual School Headteacher"
              | "Deputy Virtual School Headteacher"
              | "Post-16 Team Leader"
              | "SEN Team"
              | "Inclusion Team"
              | "UASC Team"
              | "Caseworker"
              | "Designated Teacher"
              | "Attendance Officer"
              | "Coordinator"
              | "Children's Commissioner"
              | "Timesheet Contact";
            /**
             * The name of the role if the role is 'Other'
             *
             * @maxLength 128
             */
            roleOther?: string;
            /**
             * The unique identifier of the contact
             *
             * @format uuid
             */
            contactId?: string;
            /**
             * Timesheet Portal details for this contact
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal approver GUID linked to this client contact
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal approver code
               *
               * @maxLength 256
               */
              code?: string;
            };
            /**
             * Whether the contact has opted out of communications
             */
            optOut?: boolean;
            /**
             * Any additional notes about the contact
             *
             * @maxLength 175
             */
            note?: string;
            /**
             * Whether the contact is a decision maker
             */
            decisionMaker?: boolean;
            /**
             * The status of the contact
             */
            status?: "Active" | "Passive" | "Inactive";
            /**
             * The Client associated with the contact.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            clientId?: string;
            /**
             * The name of the Client associated with the contact.
             *
             * @maxLength 256
             */
            clientName?: string;
            /**
             * The address of the Client associated with the contact.
             */
            clientAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The local education authority of the associated Client.
             *
             * @maxLength 128
             */
            clientLea?: string;
            /**
             * The sector of the associated Client.
             *
             * @maxLength 128
             */
            clientType?: string;
            /**
             * The multi-academy trust of the associated Client.
             *
             * @maxLength 256
             */
            clientMultiAcademyTrust?: string;
            /**
             * The borough of the associated Client.
             *
             * @maxLength 128
             */
            clientBorough?: string;
            /**
             * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
             */
            clientAssignee?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * The specialism of the associated Client.
             *
             * @maxLength 256
             */
            clientSpecialism?: string;
            /**
             * When the associated Client was last updated.
             *
             * @format date-time
             */
            clientUpdatedAt?: string;
            /**
             * The associated Client's distance in miles from the requested location.
             *
             * @minimum 0
             */
            distance?: number;
            /**
             * Search relevance relative to the strongest result, where 100 is the strongest match.
             *
             * @minimum 0
             * @maximum 100
             */
            relevanceScore?: number;
            /**
             * Contact and Client fields that explain why the contact matched.
             *
             * @maxItems 5
             */
            matchReasons?: {
              /**
               * The contact or Client information that matched the search.
               *
               * @maxLength 256
               */
              field?: string;
              /**
               * A short excerpt showing why the contact matched.
               *
               * @maxLength 1000
               */
              text?: string;
              /**
               * The exact terms within the content that matched the search.
               *
               * @maxItems 25
               */
              matchedTerms?: string[];
            }[];
          }[];
          /**
           * The borough of the client
           *
           * @maxLength 128
           */
          borough?: string;
          /**
           * The local education authority of the client
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * The type of the client
           *
           * @maxLength 128
           */
          type?: string;
          /**
           * The multi academy trust that the client belongs to
           *
           * @maxLength 256
           */
          multiAcademyTrust?: string;
          /**
           * The color (HEX format prefixed with #) associated with the client
           *
           * @pattern ^#[0-9a-fA-F]{6}$
           */
          color?: string;
          /**
           * A custom ID for the client.
           *
           * @maxLength 256
           */
          customId?: string;
          /**
           * A company-defined identifier used to match the client with external records.
           *
           * @maxLength 256
           */
          internalId?: string;
          /**
           * The user assigned to the client. If expanded, a user object else the user ID.
           */
          assignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * Tags associated with this client. If expanded, an array of tag objects; otherwise, an array of tag IDs.
           */
          tags?:
            | string[]
            | {
                /**
                 * The unique identifier of the tag
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The label displayed wherever this tag is applied.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                name?: string;
                /**
                 * The tag display colour as a hexadecimal value, for example #2563EB.
                 *
                 * @maxLength 32
                 */
                color?: string | null;
              }[];
          /**
           * The date when the TOB was sent
           *
           * @format date-time
           */
          tobSentAt?: string;
          /**
           * The website of the client
           *
           * @format uri
           */
          website?: string;
          /**
           * Whether the client is archived
           */
          archived?: boolean;
          /**
           * The specialism of the client
           *
           * @maxLength 256
           */
          specialism?: string;
          /**
           * The current booking IDs associated with the client
           */
          currentBooking?: string[];
          /**
           * The number of active bookings associated with the client, constrained by the requested booking date range when present.
           *
           * @minimum 0
           */
          bookingCount?: number;
          /**
           * Any important notes related to the client
           *
           * @maxLength 1024
           */
          importantNotes?: string;
          /**
           * The booking roles associated with the client
           */
          roles?: {
            /**
             * The occupation of the individual in the booking role
             *
             * @maxLength 256
             */
            occupation?: string;
            /**
             * The pay rate for the booking role
             *
             * @maxLength 256
             */
            payRate?: string;
            /**
             * The charge rate for the booking role
             *
             * @maxLength 256
             */
            chargeRate?: string;
            /**
             * The type of pay for the booking role
             */
            payType?: "hourly" | "daily" | any | null;
            /**
             * The length of the day for the booking role
             */
            dayLength?: "full" | "half" | any | null;
            /**
             * The AM/PM designation for the day length
             */
            dayLengthAmPm?: "AM" | "PM" | any | null;
          }[];
          /**
           * The start time for the client
           */
          startTime?: string;
          /**
           * The end time for the client
           */
          endTime?: string;
          /**
           * Any additional instructions for the client
           *
           * @maxLength 1024
           */
          additionalInstructions?: string;
          /**
           * Any additional instructions specific to the client
           */
          additionalInstructionsClient?: string;
          /**
           * Tag highlight colour (HEX format prefixed with #)
           *
           * @pattern ^#[0-9a-fA-F]{6}$
           */
          highlight?: string;
          /**
           * The area of the client
           */
          area?: string;
          /**
           * The governance type of the client
           */
          governance?:
            | "Academy"
            | "Childrens Centre"
            | "College"
            | "Independant"
            | "LEA"
            | "University";
          /**
           * The MAT HQ details of the client
           */
          mathq?: "Unsure" | "Yes" | "No";
          /**
           * The Ofsted rating of the client
           */
          ofstedRating?:
            | "Outstanding"
            | "Good"
            | "Requires Improvement"
            | "Inadequate"
            | "NA - Independent"
            | "NA - Academy";
          /**
           * The date when the Ofsted report was checked
           *
           * @format date-time
           */
          dateOfstedReportChecked?: string | null;
          /**
           * The boarding details of the client
           */
          boarding?: "Unsure" | "Yes" | "No" | any | null;
          /**
           * The licence to issue sponsorship for the client
           */
          licenceToIssueSponsorship?: "Unsure" | "Yes" | "No" | any | null;
          /**
           * The total agency spend for the client
           */
          agencySpendTotal?: number;
          /**
           * Whether the client is VAT exempt
           */
          vatExempt?: boolean;
          /**
           * Whether to split invoices for the client
           */
          splitInvoices?: boolean;
          /**
           * The switch board details for the client
           */
          switchBoard?: string;
          /**
           * Whether the client is a GCA client
           */
          ccsClient?: boolean;
          /**
           * The GCA client URN
           */
          ccsClientUrn?: string;
          /**
           * The GCA client Ofsted URN
           */
          ccsClientOfstedUrn?: string;
          /**
           * The GCA client lot number
           */
          ccsClientLotNumber?: string;
          /**
           * The buyer reference supplied by a GCA client, when available.
           *
           * @maxLength 255
           */
          buyerReference?: string | null;
          /**
           * The GCA client invoice date
           *
           * @format date-time
           */
          ccsClientInvoiceDate?: string;
          /**
           * The key stage for the client
           */
          keyStage?: string;
          /**
           * The contact record for this client. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date when the client was last contacted.
           *
           * @format date-time
           */
          lastContactedDate?: string;
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Client fields that explain why the client matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * The client information that matched the search.
             *
             * @maxLength 256
             */
            field?: string;
            /**
             * A short excerpt showing why the client matched.
             *
             * @maxLength 1000
             */
            text?: string;
            /**
             * The exact terms within the content that matched the search.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
          }[];
          /**
           * The Client's distance in miles from the requested location. Present only when location filtering is used.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * The migration that created this client
           *
           * @pattern ^[0-9a-f]{24}$
           */
          migrationId?: string;
          /**
           * Whether this migrated client has been approved
           */
          migrationApproval?: "pending" | "approved" | "rejected";
          /**
           * Evertime details for this client
           */
          evertime?: {
            /**
             * The Evertime ClientId used for this client when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
            /**
             * The Evertime contacts to create from Edun (clientContactId set) or reference (clientContactId unset) when exporting payment runs
             */
            contacts?: {
              /**
               * The Evertime ClientContactId
               *
               * @maxLength 256
               */
              evertimeId?: string;
              /**
               * The Edun client contact this Evertime contact is created from; absent when the Evertime contact is only referenced (never created or overwritten)
               *
               * @maxLength 256
               */
              clientContactId?: string;
            }[];
            /**
             * The Evertime locations to create from the client's address (create set) or reference (create unset) when exporting payment runs
             */
            locations?: {
              /**
               * The Evertime LocationId
               *
               * @maxLength 256
               */
              evertimeId?: string;
              /**
               * True when this location is created from the client's address; false when the Evertime location is only referenced (never created or overwritten)
               */
              create?: boolean;
            }[];
            /**
             * Whether the matched Evertime client's name also matches the one held in Edun
             */
            nameMatch?: boolean;
          };
          /**
           * Timesheet Portal details for this client
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal client GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal client code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The address of the vacancy associated with the vacancy
     */
    clientAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The student associated with the vacancy
     *
     * @pattern ^[0-9a-f]{24}$
     */
    studentId?: string;
    /**
     * The postcode of the vacancy location
     *
     * @maxLength 10
     */
    postcode?: string;
    /**
     * The name of the role the vacancy is for
     *
     * @maxLength 256
     */
    role?: string;
    /**
     * The code associated with the role
     *
     * @maxLength 256
     */
    roleCode?: string;
    /**
     * Start date and time for the vacancy
     *
     * @format date-time
     */
    start?: string;
    /**
     * End date and time for the vacancy
     *
     * @format date-time
     */
    end?: string;
    /**
     * Start time for the vacancy
     */
    startTime?: string;
    /**
     * End time for the vacancy
     */
    endTime?: string;
    /**
     * The individual working days included in the vacancy.
     */
    days?: {
      /**
       * The start date and time for the booking day
       */
      start?: string;
      /**
       * The end date and time for the booking day
       */
      end?: string;
      /**
       * The length of the booking day
       */
      length?: "Half Day" | "Full Day";
      /**
       * Indicates whether the booking day is in the AM or PM
       */
      amPm?: "AM" | "PM";
      /**
       * The current status of the booking day
       */
      status?:
        | "Active"
        | "Sickness"
        | "NoShow"
        | "Client Cancelled"
        | "Act Of God"
        | "Personal Appointment";
      /**
       * The charge rate for the booking day
       *
       * @maxLength 32
       */
      chargeRate?: string;
      /**
       * The pay rate for the booking day
       *
       * @maxLength 32
       */
      payRate?: string;
      /**
       * Break duration inherited for this booking day.
       */
      breaks?: string;
      /**
       * Expenses payable for this booking day.
       */
      expenses?: string;
      /**
       * Indicates whether the booking day is flagged for removal
       */
      flaggedForRemoval?: boolean;
    }[];
    /**
     * The rate charged to the client for this vacancy.
     *
     * @maxLength 32
     */
    chargeRate?: string;
    /**
     * The fee percentage for the vacancy
     *
     * @maxLength 8
     */
    feePercentage?: string;
    /**
     * The Agency Workers Regulations value recorded for the vacancy.
     *
     * @maxLength 8
     */
    awr?: string;
    /**
     * The reminder value used to monitor Agency Workers Regulations requirements.
     *
     * @maxLength 8
     */
    awrReminder?: string;
    /**
     * The expected number of working hours for the vacancy.
     *
     * @maxLength 16
     */
    hours?: string;
    /**
     * The pay rate for the vacancy
     *
     * @maxLength 32
     */
    payRate?: string;
    /**
     * The arrival time for the vacancy
     */
    arrivalTime?: string;
    /**
     * The contact person at the vacancy for the vacancy
     */
    clientContact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
          /**
           * The role of the contact
           */
          role?:
            | "Head Teacher"
            | "Deputy Head"
            | "Cover Manager"
            | "HR Manager"
            | "Finance Manager"
            | "Head of Maths"
            | "Head of Science"
            | "Head of English"
            | "Assistant Headteacher"
            | "Headteacher's PA"
            | "SENCO"
            | "Other"
            | "Business Manager"
            | "Business Administrator"
            | "Invoice Contact"
            | "Academy Recruitment Contact"
            | "Nursery Manager"
            | "CEO"
            | "COO"
            | "Executive Headteacher"
            | "Inclusion Manager"
            | "EYFS Lead"
            | "Admin Team"
            | "Head of Humanities"
            | "Head of History"
            | "Head of Geography"
            | "Head of Business Studies"
            | "Head of PE"
            | "Head of KS1"
            | "Head of KS2"
            | "Head of KS3"
            | "Head of KS4"
            | "Virtual School Headteacher"
            | "Deputy Virtual School Headteacher"
            | "Post-16 Team Leader"
            | "SEN Team"
            | "Inclusion Team"
            | "UASC Team"
            | "Caseworker"
            | "Designated Teacher"
            | "Attendance Officer"
            | "Coordinator"
            | "Children's Commissioner"
            | "Timesheet Contact";
          /**
           * The name of the role if the role is 'Other'
           *
           * @maxLength 128
           */
          roleOther?: string;
          /**
           * The unique identifier of the contact
           *
           * @format uuid
           */
          contactId?: string;
          /**
           * Timesheet Portal details for this contact
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal approver GUID linked to this client contact
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal approver code
             *
             * @maxLength 256
             */
            code?: string;
          };
          /**
           * Whether the contact has opted out of communications
           */
          optOut?: boolean;
          /**
           * Any additional notes about the contact
           *
           * @maxLength 175
           */
          note?: string;
          /**
           * Whether the contact is a decision maker
           */
          decisionMaker?: boolean;
          /**
           * The status of the contact
           */
          status?: "Active" | "Passive" | "Inactive";
          /**
           * The Client associated with the contact.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          clientId?: string;
          /**
           * The name of the Client associated with the contact.
           *
           * @maxLength 256
           */
          clientName?: string;
          /**
           * The address of the Client associated with the contact.
           */
          clientAddress?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The local education authority of the associated Client.
           *
           * @maxLength 128
           */
          clientLea?: string;
          /**
           * The sector of the associated Client.
           *
           * @maxLength 128
           */
          clientType?: string;
          /**
           * The multi-academy trust of the associated Client.
           *
           * @maxLength 256
           */
          clientMultiAcademyTrust?: string;
          /**
           * The borough of the associated Client.
           *
           * @maxLength 128
           */
          clientBorough?: string;
          /**
           * The user assigned to the associated Client. If expanded, a user object; otherwise, a user ID.
           */
          clientAssignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * The specialism of the associated Client.
           *
           * @maxLength 256
           */
          clientSpecialism?: string;
          /**
           * When the associated Client was last updated.
           *
           * @format date-time
           */
          clientUpdatedAt?: string;
          /**
           * The associated Client's distance in miles from the requested location.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Contact and Client fields that explain why the contact matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * The contact or Client information that matched the search.
             *
             * @maxLength 256
             */
            field?: string;
            /**
             * A short excerpt showing why the contact matched.
             *
             * @maxLength 1000
             */
            text?: string;
            /**
             * The exact terms within the content that matched the search.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
          }[];
        };
    /**
     * Any additional instructions for the vacancy
     *
     * @maxLength 1024
     */
    additionalInstructions?: string;
    /**
     * Any additional instructions specific to the vacancy
     *
     * @maxLength 1024
     */
    additionalInstructionsClient?: string;
    /**
     * The current status of the vacancy
     */
    status?: "active" | "cancelled";
    /**
     * The user assigned to the vacancy. If expanded, a user object else the user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The pay type for the vacancy
     */
    payType?: "daily" | "hourly";
    /**
     * The type of the vacancy
     */
    type?: "standard" | "interview" | "trial" | "permanent" | "tutor";
    /**
     * The sub-type of the vacancy
     */
    subType?:
      | "tutor-standard"
      | "tutor-mentor"
      | "tutor-basic"
      | "face-to-face";
    /**
     * The payment arrangement expected for the candidate.
     */
    candidatePayType?: "PAYE" | "Umbrella" | "Limited Company";
    /**
     * The client name displayed with the vacancy.
     *
     * @maxLength 256
     */
    clientName?: string;
    /**
     * A description of the role, responsibilities, and requirements.
     *
     * @maxLength 2048
     */
    jobDescription?: string;
    /**
     * Whether the salary is expressed as an annual or daily amount.
     */
    salaryPayType?: "annual" | "daily";
    /**
     * The salary amount advertised for a permanent vacancy.
     *
     * @maxLength 32
     */
    salary?: string;
    /**
     * The weekly rota for the vacancy
     */
    weeklyRota?: {
      /**
       * The rota for Monday
       */
      ["1"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Tuesday
       */
      ["2"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Wednesday
       */
      ["3"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Thursday
       */
      ["4"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Friday
       */
      ["5"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Saturday
       */
      ["6"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Sunday
       */
      ["7"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
    };
    /**
     * The calculated distance to the vacancy when a location-based request supplies a reference point.
     */
    distance?: number;
    /**
     * Whether to include the school name in the vacancy details
     */
    includeSchool?: boolean;
    /**
     * Vacant bookings can be completed to multiple candidates at once. Specify the total number of available vacancies for this booking. This value is only visible within Edun, not visible externally.
     *  If left empty, 1 available vacancy for this placement will be presumed.
     *
     * @maxLength 256
     */
    noVacancies?: string;
    /**
     * Whether the vacancy should be shared to the job board
     */
    jobBoard?: boolean;
    /**
     * Notes about the client's interest in candidates for this vacancy.
     *
     * @maxLength 1024
     */
    clientInterestComments?: string;
    /**
     * Whether the required health and safety check has been recorded as complete.
     */
    healthSafetyCheck?: boolean;
    /**
     * The client contact associated with the health and safety check.
     *
     * @maxLength 256
     */
    healthSafetyCheckContact?: string;
    /**
     * The purchase order number for the vacancy
     */
    poNumber?: string;
    /**
     * The identifier assigned when the vacancy is published to CV-Library.
     */
    cvLibraryId?: number;
    /**
     * Whether the vacancy is archived
     */
    archived?: boolean;
    /**
     * The reason the vacancy was archived.
     *
     * @maxLength 1024
     */
    archiveReason?: string;
    /**
     * Whether the vacancy is completed
     */
    completed?: boolean;
    /**
     * The occupation for the vacancy
     *
     * @maxLength 256
     */
    occupation?: string;
    /**
     * The date the vacancy was posted
     *
     * @format date-time
     */
    postedOnDate?: string | null;
  };
};

export type GetVacancyVariables = {
  pathParams: GetVacancyPathParams;
  queryParams?: GetVacancyQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetVacancy = (
  variables: GetVacancyVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetVacancyResponse,
    GetVacancyError,
    undefined,
    {},
    GetVacancyQueryParams,
    GetVacancyPathParams
  >({ url: "/vacancies/{vacancyId}", method: "get", ...variables, signal });

export function getVacancyQuery(variables: GetVacancyVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetVacancyResponse>;
};

export function getVacancyQuery(
  variables: GetVacancyVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetVacancyResponse>)
    | reactQuery.SkipToken;
};

export function getVacancyQuery(
  variables: GetVacancyVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/vacancies/{vacancyId}",
      operationId: "getVacancy",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetVacancy(variables, signal),
  };
}

export const useSuspenseGetVacancy = <TData = GetVacancyResponse,>(
  variables: GetVacancyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetVacancyResponse, GetVacancyError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetVacancyResponse,
    GetVacancyError,
    TData
  >({
    ...getVacancyQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetVacancy = <TData = GetVacancyResponse,>(
  variables: GetVacancyVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetVacancyResponse, GetVacancyError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetVacancyResponse, GetVacancyError, TData>({
    ...getVacancyQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetMessagesQueryParams = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * The field to sort the results by
   */
  sort?: "createdAt" | "-createdAt";
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "customer"
    | "direction"
    | "senderContact"
    | "senderContact.id"
    | "senderContact.forename"
    | "senderContact.surname"
    | "senderContact.email"
    | "senderContact.telephone"
    | "senderContact.mobile"
    | "senderContact.extension"
    | "recipientContacts"
    | "createdAt"
    | "channel"
    | "callId"
    | "durationSeconds"
    | "transcript"
    | "summary"
    | "recordingUrl"
    | ""
    | "tags"
    | "emailId"
    | "subject"
    | "textBody"
    | "htmlBody"
    | "attachments"
  )[];
  /**
   * Additional fields to expand in the response
   */
  expand?: ("senderContact" | "recipientContacts")[];
  filter?: {
    /**
     * Filter by many sender contact ids
     */
    senderContact?: string[];
    /**
     * Filter by many recipient contact ids
     */
    recipientContacts?: string[];
  };
};

export type GetMessagesError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetMessagesResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: (
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @pattern ^[0-9a-f]{24}$
         */
        customer?: string;
        direction?: "inbound" | "outbound";
        senderContact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        recipientContacts?:
          | string[]
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            }[];
        /**
         * The date the message was created
         *
         * @format date-time
         */
        createdAt?: string;
        channel?: "cloudcall";
        callId?: string;
        /**
         * @minimum 0
         */
        durationSeconds?: number;
        /**
         * Raw transcript of the call. Truncated if in a list of messages
         */
        transcript?: string;
        /**
         * AI summary of the call. Truncated if in a list of messages
         */
        summary?: string;
        recordingUrl?: string;
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @pattern ^[0-9a-f]{24}$
         */
        customer?: string;
        direction?: "inbound" | "outbound";
        senderContact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        recipientContacts?:
          | string[]
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            }[];
        /**
         * The date the message was created
         *
         * @format date-time
         */
        createdAt?: string;
        channel?: "dialpad";
        callId?: string;
        /**
         * @minimum 0
         */
        durationSeconds?: number;
        /**
         * Raw transcript of the call. Truncated if in a list of messages
         */
        transcript?: string;
        /**
         * AI summary of the call. Truncated if in a list of messages
         */
        summary?: string;
        recordingUrl?: string;
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @pattern ^[0-9a-f]{24}$
         */
        customer?: string;
        direction?: "inbound" | "outbound";
        senderContact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        recipientContacts?:
          | string[]
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            }[];
        /**
         * The date the message was created
         *
         * @format date-time
         */
        createdAt?: string;
        channel?: "devyce";
        callId?: string;
        /**
         * @minimum 0
         */
        durationSeconds?: number;
        tags?: string[];
        transcript?: string;
        recordingUrl?: string;
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @pattern ^[0-9a-f]{24}$
         */
        customer?: string;
        direction?: "inbound" | "outbound";
        senderContact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        recipientContacts?:
          | string[]
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            }[];
        /**
         * The date the message was created
         *
         * @format date-time
         */
        createdAt?: string;
        channel?: "email";
        emailId?: string;
        subject?: string;
        textBody?: string;
        htmlBody?: string;
        attachments?: {
          filename?: string;
          mimeType?: string;
          /**
           * @minimum 0
           */
          sizeBytes?: number;
        }[];
      }
  )[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetMessagesVariables = {
  queryParams?: GetMessagesQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetMessages = (
  variables: GetMessagesVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetMessagesResponse,
    GetMessagesError,
    undefined,
    {},
    GetMessagesQueryParams,
    {}
  >({ url: "/messages", method: "get", ...variables, signal });

export function getMessagesQuery(variables: GetMessagesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetMessagesResponse>;
};

export function getMessagesQuery(
  variables: GetMessagesVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetMessagesResponse>)
    | reactQuery.SkipToken;
};

export function getMessagesQuery(
  variables: GetMessagesVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/messages",
      operationId: "getMessages",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetMessages(variables, signal),
  };
}

export const useSuspenseGetMessages = <TData = GetMessagesResponse,>(
  variables: GetMessagesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetMessagesResponse, GetMessagesError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetMessagesResponse,
    GetMessagesError,
    TData
  >({
    ...getMessagesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetMessages = <TData = GetMessagesResponse,>(
  variables: GetMessagesVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetMessagesResponse, GetMessagesError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetMessagesResponse, GetMessagesError, TData>({
    ...getMessagesQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetMessagePathParams = {
  /**
   * The unique identifier for the message.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  messageId: string;
};

export type GetMessageQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "customer"
    | "direction"
    | "senderContact"
    | "senderContact.id"
    | "senderContact.forename"
    | "senderContact.surname"
    | "senderContact.email"
    | "senderContact.telephone"
    | "senderContact.mobile"
    | "senderContact.extension"
    | "recipientContacts"
    | "createdAt"
    | "channel"
    | "callId"
    | "durationSeconds"
    | "transcript"
    | "summary"
    | "recordingUrl"
    | ""
    | "tags"
    | "emailId"
    | "subject"
    | "textBody"
    | "htmlBody"
    | "attachments"
  )[];
  /**
   * Additional fields to expand in the response
   */
  expand?: ("senderContact" | "recipientContacts")[];
};

export type GetMessageError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetMessageResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data:
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @pattern ^[0-9a-f]{24}$
         */
        customer?: string;
        direction?: "inbound" | "outbound";
        senderContact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        recipientContacts?:
          | string[]
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            }[];
        /**
         * The date the message was created
         *
         * @format date-time
         */
        createdAt?: string;
        channel?: "cloudcall";
        callId?: string;
        /**
         * @minimum 0
         */
        durationSeconds?: number;
        /**
         * Raw transcript of the call. Truncated if in a list of messages
         */
        transcript?: string;
        /**
         * AI summary of the call. Truncated if in a list of messages
         */
        summary?: string;
        recordingUrl?: string;
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @pattern ^[0-9a-f]{24}$
         */
        customer?: string;
        direction?: "inbound" | "outbound";
        senderContact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        recipientContacts?:
          | string[]
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            }[];
        /**
         * The date the message was created
         *
         * @format date-time
         */
        createdAt?: string;
        channel?: "dialpad";
        callId?: string;
        /**
         * @minimum 0
         */
        durationSeconds?: number;
        /**
         * Raw transcript of the call. Truncated if in a list of messages
         */
        transcript?: string;
        /**
         * AI summary of the call. Truncated if in a list of messages
         */
        summary?: string;
        recordingUrl?: string;
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @pattern ^[0-9a-f]{24}$
         */
        customer?: string;
        direction?: "inbound" | "outbound";
        senderContact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        recipientContacts?:
          | string[]
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            }[];
        /**
         * The date the message was created
         *
         * @format date-time
         */
        createdAt?: string;
        channel?: "devyce";
        callId?: string;
        /**
         * @minimum 0
         */
        durationSeconds?: number;
        tags?: string[];
        transcript?: string;
        recordingUrl?: string;
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * @pattern ^[0-9a-f]{24}$
         */
        customer?: string;
        direction?: "inbound" | "outbound";
        senderContact?:
          | string
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            };
        recipientContacts?:
          | string[]
          | {
              /**
               * The unique identifier of the contact
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The forename of the contact
               *
               * @maxLength 128
               */
              forename?: string;
              /**
               * The surname of the contact
               *
               * @maxLength 128
               */
              surname?: string;
              /**
               * The email address of the contact
               *
               * @format email
               */
              email?: string;
              /**
               * The telephone number of the contact
               *
               * @maxLength 32
               */
              telephone?: string;
              /**
               * The mobile number of the contact
               *
               * @maxLength 32
               */
              mobile?: string;
              /**
               * The extension number of the contact
               *
               * @maxLength 8
               */
              extension?: string;
            }[];
        /**
         * The date the message was created
         *
         * @format date-time
         */
        createdAt?: string;
        channel?: "email";
        emailId?: string;
        subject?: string;
        textBody?: string;
        htmlBody?: string;
        attachments?: {
          filename?: string;
          mimeType?: string;
          /**
           * @minimum 0
           */
          sizeBytes?: number;
        }[];
      };
};

export type GetMessageVariables = {
  pathParams: GetMessagePathParams;
  queryParams?: GetMessageQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetMessage = (
  variables: GetMessageVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetMessageResponse,
    GetMessageError,
    undefined,
    {},
    GetMessageQueryParams,
    GetMessagePathParams
  >({ url: "/messages/{messageId}", method: "get", ...variables, signal });

export function getMessageQuery(variables: GetMessageVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetMessageResponse>;
};

export function getMessageQuery(
  variables: GetMessageVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetMessageResponse>)
    | reactQuery.SkipToken;
};

export function getMessageQuery(
  variables: GetMessageVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/messages/{messageId}",
      operationId: "getMessage",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetMessage(variables, signal),
  };
}

export const useSuspenseGetMessage = <TData = GetMessageResponse,>(
  variables: GetMessageVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetMessageResponse, GetMessageError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetMessageResponse,
    GetMessageError,
    TData
  >({
    ...getMessageQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetMessage = <TData = GetMessageResponse,>(
  variables: GetMessageVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetMessageResponse, GetMessageError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetMessageResponse, GetMessageError, TData>({
    ...getMessageQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetUsersQueryParams = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * The field to sort the results by
   */
  sort?: "firstName" | "lastName" | "email" | "username" | "createdAt";
  /**
   * The order in which to sort the results
   */
  order?: "asc" | "desc";
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "email"
    | "firstName"
    | "lastName"
    | "username"
    | "lea"
    | "clients"
    | "position"
    | "permissions"
    | "activationCode"
    | "customer"
    | "disabled"
    | "commissionThreshold"
    | "branches"
    | "displayOnboarding"
    | "_customId"
    | "signature"
    | "phone"
    | "defaultCandidateWatcher"
    | "defaultTaskWorkspace"
    | "teamAdmin"
    | "contact"
    | "contact.id"
    | "contact.forename"
    | "contact.surname"
    | "contact.email"
    | "contact.telephone"
    | "contact.mobile"
    | "contact.extension"
    | "createdAt"
    | "evertime.id"
    | "evertime"
    | "timesheetPortal.id"
    | "timesheetPortal.code"
    | "timesheetPortal"
    | ""
  )[];
  /**
   * Additional fields to expand in the response
   */
  expand?: string[];
  /**
   * Filter users based on various criteria
   */
  filter?: {
    /**
     * Filter users by a list of IDs
     *
     * @maxItems 50
     */
    ids?: string[];
    /**
     * Filter users assigned to any of these branch IDs
     *
     * @maxItems 50
     */
    branches?: string[];
    /**
     * Filter users by first name (case-insensitive regex)
     *
     * @maxLength 256
     */
    firstName?: string;
    /**
     * Filter users by last name (case-insensitive regex)
     *
     * @maxLength 256
     */
    lastName?: string;
    /**
     * Filter users by email address (case-insensitive regex)
     *
     * @maxLength 256
     */
    email?: string;
    /**
     * Filter users by username (case-insensitive regex)
     *
     * @maxLength 256
     */
    username?: string;
    /**
     * Filter users by position/job title (case-insensitive regex)
     *
     * @maxLength 256
     */
    position?: string;
    /**
     * Filter users by their disabled status
     */
    disabled?: ("true" | "false")[];
    /**
     * Filter users by account creation date
     */
    createdAt?: {
      /**
       * @format date-time
       */
      from?: string | null;
      /**
       * @format date-time
       */
      to?: string | null;
    };
  };
  /**
   * Search users by first or last name
   */
  search?: string;
};

export type GetUsersError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetUsersResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the user
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The email address of the user
     *
     * @format email
     */
    email?: string;
    /**
     * The first name of the user
     *
     * @maxLength 128
     */
    firstName?: string;
    /**
     * The last name of the user
     *
     * @maxLength 128
     */
    lastName?: string;
    /**
     * The username of the user
     *
     * @maxLength 128
     */
    username?: string;
    /**
     * The local education authority of the user
     *
     * @maxLength 128
     */
    lea?: string;
    /**
     * Array of client IDs associated with the user
     */
    clients?: string[];
    /**
     * The position or job title of the user
     *
     * @maxLength 128
     */
    position?: string;
    /**
     * Array of permissions assigned to the user
     */
    permissions?: number[];
    /**
     * The activation code for the user
     *
     * @maxLength 256
     */
    activationCode?: string;
    /**
     * The unique identifier of the company the user belongs to.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customer?: string;
    /**
     * Indicates whether the user account is disabled
     */
    disabled?: boolean;
    /**
     * Array of commission thresholds for the user
     */
    commissionThreshold?: {
      min?: number | string;
      commission?: number | string;
      type?: "Fixed" | "Variable";
      weekly?: boolean;
    }[];
    /**
     * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
     */
    branches?:
      | string[]
      | {
          /**
           * The unique identifier for the branch
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The name of the branch
           *
           * @maxLength 256
           */
          name?: string;
          /**
           * The code of the branch
           *
           * @maxLength 32
           */
          code?: string;
          /**
           * The contact email of the branch
           *
           * @maxLength 256
           * @format email
           */
          contactEmail?: string;
          /**
           * The contact phone number of the branch
           *
           * @maxLength 32
           */
          contactTelephone?: string;
          /**
           * The contact website of the branch
           *
           * @maxLength 512
           */
          contactWebsite?: string;
          /**
           * The accent color for the customer that the branch belongs to
           *
           * @maxLength 7
           */
          customerAccentColor?: string;
          /**
           * The address of the branch
           */
          address?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * Indicates whether the branch is archived
           */
          archived?: boolean;
          /**
           * The number of users assigned to the branch. Only returned when explicitly selected.
           *
           * @minimum 0
           */
          userCount?: number;
        }[];
    /**
     * Indicates whether to display the onboarding process for the user
     */
    displayOnboarding?: boolean;
    /**
     * A custom identifier for the user
     *
     * @maxLength 256
     */
    _customId?: string;
    /**
     * The signature of the user in base64 format
     *
     * @maxLength 2048
     */
    signature?: string | null;
    /**
     * The phone number of the user
     *
     * @maxLength 32
     */
    phone?: string;
    /**
     * Indicates whether the user is the default candidate watcher
     */
    defaultCandidateWatcher?: boolean;
    /**
     * The user's default task workspace
     *
     * @pattern ^[0-9a-f]{24}$
     */
    defaultTaskWorkspace?: string | null;
    /**
     * Indicates whether the user has team admin privileges
     */
    teamAdmin?: boolean;
    /**
     * The contact record for this user. If expanded, a contact object else the contact record ID.
     */
    contact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
        };
    /**
     * The date and time the user account was created
     */
    createdAt?: string;
    /**
     * Evertime details for this user
     */
    evertime?: {
      /**
       * The Evertime ConsultantId used for this user when exporting payment runs
       *
       * @maxLength 256
       */
      id?: string;
    };
    /**
     * Timesheet Portal details for this user
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal administrator GUID used when exporting payment runs
       *
       * @format uuid
       */
      id?: string;
      /**
       * The Timesheet Portal administrator code
       *
       * @maxLength 256
       */
      code?: string;
    };
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetUsersVariables = {
  queryParams?: GetUsersQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetUsers = (
  variables: GetUsersVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetUsersResponse,
    GetUsersError,
    undefined,
    {},
    GetUsersQueryParams,
    {}
  >({ url: "/users", method: "get", ...variables, signal });

export function getUsersQuery(variables: GetUsersVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetUsersResponse>;
};

export function getUsersQuery(
  variables: GetUsersVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetUsersResponse>)
    | reactQuery.SkipToken;
};

export function getUsersQuery(
  variables: GetUsersVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/users",
      operationId: "getUsers",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetUsers(variables, signal),
  };
}

export const useSuspenseGetUsers = <TData = GetUsersResponse,>(
  variables: GetUsersVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetUsersResponse, GetUsersError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<GetUsersResponse, GetUsersError, TData>({
    ...getUsersQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetUsers = <TData = GetUsersResponse,>(
  variables: GetUsersVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetUsersResponse, GetUsersError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetUsersResponse, GetUsersError, TData>({
    ...getUsersQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateUserError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 409;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Resource limit reached
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateUserResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the user
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The email address of the user
     *
     * @format email
     */
    email?: string;
    /**
     * The first name of the user
     *
     * @maxLength 128
     */
    firstName?: string;
    /**
     * The last name of the user
     *
     * @maxLength 128
     */
    lastName?: string;
    /**
     * The username of the user
     *
     * @maxLength 128
     */
    username?: string;
    /**
     * The local education authority of the user
     *
     * @maxLength 128
     */
    lea?: string;
    /**
     * Array of client IDs associated with the user
     */
    clients?: string[];
    /**
     * The position or job title of the user
     *
     * @maxLength 128
     */
    position?: string;
    /**
     * Array of permissions assigned to the user
     */
    permissions?: number[];
    /**
     * The activation code for the user
     *
     * @maxLength 256
     */
    activationCode?: string;
    /**
     * The unique identifier of the company the user belongs to.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customer?: string;
    /**
     * Indicates whether the user account is disabled
     */
    disabled?: boolean;
    /**
     * Array of commission thresholds for the user
     */
    commissionThreshold?: {
      min?: number | string;
      commission?: number | string;
      type?: "Fixed" | "Variable";
      weekly?: boolean;
    }[];
    /**
     * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
     */
    branches?:
      | string[]
      | {
          /**
           * The unique identifier for the branch
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The name of the branch
           *
           * @maxLength 256
           */
          name?: string;
          /**
           * The code of the branch
           *
           * @maxLength 32
           */
          code?: string;
          /**
           * The contact email of the branch
           *
           * @maxLength 256
           * @format email
           */
          contactEmail?: string;
          /**
           * The contact phone number of the branch
           *
           * @maxLength 32
           */
          contactTelephone?: string;
          /**
           * The contact website of the branch
           *
           * @maxLength 512
           */
          contactWebsite?: string;
          /**
           * The accent color for the customer that the branch belongs to
           *
           * @maxLength 7
           */
          customerAccentColor?: string;
          /**
           * The address of the branch
           */
          address?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * Indicates whether the branch is archived
           */
          archived?: boolean;
          /**
           * The number of users assigned to the branch. Only returned when explicitly selected.
           *
           * @minimum 0
           */
          userCount?: number;
        }[];
    /**
     * Indicates whether to display the onboarding process for the user
     */
    displayOnboarding?: boolean;
    /**
     * A custom identifier for the user
     *
     * @maxLength 256
     */
    _customId?: string;
    /**
     * The signature of the user in base64 format
     *
     * @maxLength 2048
     */
    signature?: string | null;
    /**
     * The phone number of the user
     *
     * @maxLength 32
     */
    phone?: string;
    /**
     * Indicates whether the user is the default candidate watcher
     */
    defaultCandidateWatcher?: boolean;
    /**
     * The user's default task workspace
     *
     * @pattern ^[0-9a-f]{24}$
     */
    defaultTaskWorkspace?: string | null;
    /**
     * Indicates whether the user has team admin privileges
     */
    teamAdmin?: boolean;
    /**
     * The contact record for this user. If expanded, a contact object else the contact record ID.
     */
    contact?:
      | string
      | {
          /**
           * The unique identifier of the contact
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the contact
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * The surname of the contact
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * The email address of the contact
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the contact
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the contact
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The extension number of the contact
           *
           * @maxLength 8
           */
          extension?: string;
        };
    /**
     * The date and time the user account was created
     */
    createdAt?: string;
    /**
     * Evertime details for this user
     */
    evertime?: {
      /**
       * The Evertime ConsultantId used for this user when exporting payment runs
       *
       * @maxLength 256
       */
      id?: string;
    };
    /**
     * Timesheet Portal details for this user
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal administrator GUID used when exporting payment runs
       *
       * @format uuid
       */
      id?: string;
      /**
       * The Timesheet Portal administrator code
       *
       * @maxLength 256
       */
      code?: string;
    };
  };
};

export type CreateUserRequestBody = {
  /**
   * Username for the new user, must be at least 4 characters
   *
   * @minLength 4
   * @maxLength 128
   */
  username: string;
  /**
   * Email address for the new user
   *
   * @format email
   */
  email: string;
  /**
   * First name of the new user
   *
   * @minLength 1
   * @maxLength 128
   */
  firstName: string;
  /**
   * Last name of the new user
   *
   * @minLength 1
   * @maxLength 128
   */
  lastName: string;
  /**
   * Job title or position of the new user
   *
   * @minLength 1
   * @maxLength 128
   */
  position: string;
  /**
   * The account type to assign to the new user
   */
  accountType: string;
  /**
   * Array of permission codes to assign to the new user
   */
  permissions: number[];
  /**
   * The local education authority of the user
   *
   * @maxLength 128
   */
  lea?: string;
  /**
   * Phone number of the new user
   *
   * @maxLength 32
   */
  phone?: string;
  /**
   * Whether the user is the default candidate watcher
   */
  defaultCandidateWatcher?: boolean;
  /**
   * The user's default task workspace
   *
   * @pattern ^[0-9a-f]{24}$
   */
  defaultTaskWorkspace?: string | null;
  /**
   * Whether the user has team admin privileges
   */
  teamAdmin?: boolean;
  /**
   * Commission thresholds for the user. Defaults to the customer's booking costs commission if not provided.
   */
  commissionThreshold?: {
    min: number | string;
    commission: number | string;
    type?: "Fixed" | "Variable";
    weekly?: boolean;
  }[];
  /**
   * A custom identifier for the user
   *
   * @maxLength 256
   */
  _customId?: string;
  /**
   * Whether to send an activation email to the new user
   */
  sendActivation?: boolean;
};

export type CreateUserVariables = {
  body: CreateUserRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateUser = (
  variables: CreateUserVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateUserResponse,
    CreateUserError,
    CreateUserRequestBody,
    {},
    {},
    {}
  >({ url: "/users", method: "post", ...variables, signal });

export const useCreateUser = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateUserResponse,
      CreateUserError,
      CreateUserVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateUserResponse,
    CreateUserError,
    CreateUserVariables
  >({
    mutationFn: (variables: CreateUserVariables) =>
      fetchCreateUser(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetUserInfoQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | (
        | "id"
        | "email"
        | "firstName"
        | "lastName"
        | "username"
        | "position"
        | "permissions"
        | "phone"
        | "signature"
        | "defaultTaskWorkspace"
        | "savedSearches.candidates"
        | "savedSearches.defaultCandidate"
        | "savedSearches.clients"
        | "savedSearches.defaultClient"
        | "savedSearches.clientContacts"
        | "savedSearches.defaultClientContact"
        | "savedSearches"
        | "integrationConfiguration.cloudcall.enabled"
        | "integrationConfiguration.cloudcall.subscribed"
        | "integrationConfiguration.cloudcall"
        | "integrationConfiguration.dialpad.enabled"
        | "integrationConfiguration.dialpad.subscribed"
        | "integrationConfiguration.dialpad"
        | "integrationConfiguration.devyce.enabled"
        | "integrationConfiguration.devyce"
        | "integrationConfiguration"
        | "emailPreferences.candidateOnboardingComplete"
        | "emailPreferences.candidateOnboardingDocumentComplete"
        | "emailPreferences.candidateOnboardingReferenceComplete"
        | "emailPreferences.candidateOnboardingBankComplete"
        | "emailPreferences.timesheetAssignee"
        | "emailPreferences.candidateAssignee"
        | "emailPreferences.bookingAssignee"
        | "emailPreferences.bookingPlaceholderAssignee"
        | "emailPreferences.timesheetCandidateStatusUpdate"
        | "emailPreferences.timesheetClientStatusUpdate"
        | "emailPreferences.timesheetCandidateQueryUpdate"
        | "emailPreferences.timesheetClientQueryUpdate"
        | "emailPreferences.documentExpiry"
        | "emailPreferences.bookingInterviewAttendanceUpdate"
        | "emailPreferences.bookingTrialAttendanceUpdate"
        | "emailPreferences.placementApplication"
        | "emailPreferences.candidateInterviewConfirmationComplete"
        | "emailPreferences.smsNotification"
        | "emailPreferences.watcherUpdateExternalBooking"
        | "emailPreferences.watcherClientCandidateInterest"
        | "emailPreferences.watcherClientCandidateInterestSubmit"
        | "emailPreferences.watcherExternalSubmit"
        | "emailPreferences.watcherDocumentRequestSubmitted"
        | "emailPreferences.watcherReferenceSubissionConfirmation"
        | "emailPreferences.watcherUpdateOnboard"
        | "emailPreferences.watcherCandidateBankRequestSubmitted"
        | "emailPreferences.watcherAvailabilityEnquiryExternal"
        | "emailPreferences.watcherTimesheetConfirmation"
        | "emailPreferences.watcherPartialTimesheetConfirmation"
        | "emailPreferences.notificationMethod"
        | "emailPreferences"
        | ""
      )
    | "customer"
  )[];
  /**
   * Related resources to return as objects instead of IDs. Use emailPreferences for email settings and signature for the user's structured email signature.
   */
  expand?: (
    | "emailPreferences"
    | "watchersEmailPreferences"
    | "signature"
    | "customer"
  )[];
};

export type GetUserInfoError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetUserInfoResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the user
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The email address of the user
     *
     * @format email
     */
    email?: string;
    /**
     * The first name of the user
     *
     * @minLength 1
     * @maxLength 128
     */
    firstName?: string;
    /**
     * The last name of the user
     *
     * @minLength 1
     * @maxLength 128
     */
    lastName?: string;
    /**
     * The username of the user
     *
     * @minLength 4
     * @maxLength 128
     */
    username?: string;
    /**
     * The position or job title of the user
     *
     * @maxLength 128
     */
    position?: string;
    /**
     * The permission identifiers assigned to the current user.
     *
     * @maxItems 250
     */
    permissions?: number[];
    /**
     * The phone number of the user
     *
     * @maxLength 32
     */
    phone?: string;
    /**
     * User's marketing email signature
     */
    signature?: string | null;
    /**
     * The user's default task workspace
     *
     * @pattern ^[0-9a-f]{24}$
     */
    defaultTaskWorkspace?: string | null;
    /**
     * Saved searches starred by the user, grouped by the type of record they search.
     */
    savedSearches?: {
      /**
       * The candidate saved searches starred by the user.
       *
       * @maxItems 25
       */
      candidates?: string[];
      /**
       * The saved search loaded automatically when the user opens the candidates page. Null uses the standard candidates view.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      defaultCandidate?: string | null;
      /**
       * The client saved searches starred by the user.
       *
       * @maxItems 25
       */
      clients?: string[];
      /**
       * The saved search loaded automatically when the user opens the clients page. Null uses the standard clients view.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      defaultClient?: string | null;
      /**
       * The Client Contact saved searches starred by the user.
       *
       * @maxItems 25
       */
      clientContacts?: string[];
      /**
       * The saved search loaded automatically when the user opens the Client Contacts page. Null uses the standard Client Contacts view.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      defaultClientContact?: string | null;
    };
    /**
     * Calling integrations enabled for the user. Availability also depends on the company's integration configuration and the user's permissions.
     */
    integrationConfiguration?: {
      /**
       * The user's CloudCall VoIP integration settings.
       */
      cloudcall?: {
        /**
         * Whether CloudCall integration is enabled for the user
         */
        enabled?: boolean;
        /**
         * Whether Dialpad integration webhook subscription exists for this users organisation
         */
        subscribed?: boolean;
      };
      /**
       * The user's Dialpad VoIP integration settings.
       */
      dialpad?: {
        /**
         * Whether Dialpad integration is enabled for the user
         */
        enabled?: boolean;
        /**
         * Whether Dialpad integration webhook subscription exists for this users organisation
         */
        subscribed?: boolean;
      };
      /**
       * The user's Devyce VoIP integration settings.
       */
      devyce?: {
        /**
         * Whether Devyce integration is enabled for the user
         */
        enabled?: boolean;
      };
    };
    /**
     * User's email notification preferences and watcher notification preferences.
     */
    emailPreferences?: {
      /**
       * Receive notifications when candidate onboarding is complete
       */
      candidateOnboardingComplete?: boolean;
      /**
       * Receive notifications when candidate onboarding documents are complete
       */
      candidateOnboardingDocumentComplete?: boolean;
      /**
       * Receive notifications when candidate onboarding references are complete
       */
      candidateOnboardingReferenceComplete?: boolean;
      /**
       * Receive notifications when candidate onboarding bank details are complete
       */
      candidateOnboardingBankComplete?: boolean;
      /**
       * Receive notifications for timesheet assignments
       */
      timesheetAssignee?: boolean;
      /**
       * Receive notifications for candidate assignments
       */
      candidateAssignee?: boolean;
      /**
       * Receive notifications for booking assignments
       */
      bookingAssignee?: boolean;
      /**
       * Receive notifications for booking placeholder assignments
       */
      bookingPlaceholderAssignee?: boolean;
      /**
       * Receive notifications for candidate timesheet status updates
       */
      timesheetCandidateStatusUpdate?: boolean;
      /**
       * Receive notifications for client timesheet status updates
       */
      timesheetClientStatusUpdate?: boolean;
      /**
       * Receive notifications for candidate timesheet query updates
       */
      timesheetCandidateQueryUpdate?: boolean;
      /**
       * Receive notifications for client timesheet query updates
       */
      timesheetClientQueryUpdate?: boolean;
      /**
       * Receive notifications for document expiry
       */
      documentExpiry?: boolean;
      /**
       * Receive notifications for interview attendance updates
       */
      bookingInterviewAttendanceUpdate?: boolean;
      /**
       * Receive notifications for trial attendance updates
       */
      bookingTrialAttendanceUpdate?: boolean;
      /**
       * Receive notifications for placement applications
       */
      placementApplication?: boolean;
      /**
       * Receive notifications when candidate interview confirmations are complete
       */
      candidateInterviewConfirmationComplete?: boolean;
      /**
       * Receive SMS notifications
       */
      smsNotification?: boolean;
      /**
       * Receive notifications when an external booking you are watching is updated
       */
      watcherUpdateExternalBooking?: boolean;
      /**
       * Receive notifications when a client shows interest in a candidate you are watching
       */
      watcherClientCandidateInterest?: boolean;
      /**
       * Receive notifications when a client submits interest in a candidate you are watching
       */
      watcherClientCandidateInterestSubmit?: boolean;
      /**
       * Receive notifications when an external submission is made for a candidate you are watching
       */
      watcherExternalSubmit?: boolean;
      /**
       * Receive notifications when a document request is submitted for a candidate you are watching
       */
      watcherDocumentRequestSubmitted?: boolean;
      /**
       * Receive notifications when a reference submission is confirmed for a candidate you are watching
       */
      watcherReferenceSubissionConfirmation?: boolean;
      /**
       * Receive notifications when onboarding is updated for a candidate you are watching
       */
      watcherUpdateOnboard?: boolean;
      /**
       * Receive notifications when a bank request is submitted for a candidate you are watching
       */
      watcherCandidateBankRequestSubmitted?: boolean;
      /**
       * Receive notifications when an external availability enquiry is made for a candidate you are watching
       */
      watcherAvailabilityEnquiryExternal?: boolean;
      /**
       * Receive notifications when a timesheet is confirmed for a candidate you are watching
       */
      watcherTimesheetConfirmation?: boolean;
      /**
       * Receive notifications when a partial timesheet is confirmed for a candidate you are watching
       */
      watcherPartialTimesheetConfirmation?: boolean;
      /**
       * How the user receives watcher notifications: InApp, Email, or Both
       */
      notificationMethod?: "InApp" | "Email" | "Both";
    };
  };
};

export type GetUserInfoVariables = {
  queryParams?: GetUserInfoQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetUserInfo = (
  variables: GetUserInfoVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetUserInfoResponse,
    GetUserInfoError,
    undefined,
    {},
    GetUserInfoQueryParams,
    {}
  >({ url: "/me", method: "get", ...variables, signal });

export function getUserInfoQuery(variables: GetUserInfoVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetUserInfoResponse>;
};

export function getUserInfoQuery(
  variables: GetUserInfoVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetUserInfoResponse>)
    | reactQuery.SkipToken;
};

export function getUserInfoQuery(
  variables: GetUserInfoVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/me",
      operationId: "getUserInfo",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetUserInfo(variables, signal),
  };
}

export const useSuspenseGetUserInfo = <TData = GetUserInfoResponse,>(
  variables: GetUserInfoVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetUserInfoResponse, GetUserInfoError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetUserInfoResponse,
    GetUserInfoError,
    TData
  >({
    ...getUserInfoQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetUserInfo = <TData = GetUserInfoResponse,>(
  variables: GetUserInfoVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetUserInfoResponse, GetUserInfoError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetUserInfoResponse, GetUserInfoError, TData>({
    ...getUserInfoQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateUserInfoQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | (
        | "id"
        | "email"
        | "firstName"
        | "lastName"
        | "username"
        | "position"
        | "permissions"
        | "phone"
        | "signature"
        | "defaultTaskWorkspace"
        | "savedSearches.candidates"
        | "savedSearches.defaultCandidate"
        | "savedSearches.clients"
        | "savedSearches.defaultClient"
        | "savedSearches.clientContacts"
        | "savedSearches.defaultClientContact"
        | "savedSearches"
        | "integrationConfiguration.cloudcall.enabled"
        | "integrationConfiguration.cloudcall.subscribed"
        | "integrationConfiguration.cloudcall"
        | "integrationConfiguration.dialpad.enabled"
        | "integrationConfiguration.dialpad.subscribed"
        | "integrationConfiguration.dialpad"
        | "integrationConfiguration.devyce.enabled"
        | "integrationConfiguration.devyce"
        | "integrationConfiguration"
        | "emailPreferences.candidateOnboardingComplete"
        | "emailPreferences.candidateOnboardingDocumentComplete"
        | "emailPreferences.candidateOnboardingReferenceComplete"
        | "emailPreferences.candidateOnboardingBankComplete"
        | "emailPreferences.timesheetAssignee"
        | "emailPreferences.candidateAssignee"
        | "emailPreferences.bookingAssignee"
        | "emailPreferences.bookingPlaceholderAssignee"
        | "emailPreferences.timesheetCandidateStatusUpdate"
        | "emailPreferences.timesheetClientStatusUpdate"
        | "emailPreferences.timesheetCandidateQueryUpdate"
        | "emailPreferences.timesheetClientQueryUpdate"
        | "emailPreferences.documentExpiry"
        | "emailPreferences.bookingInterviewAttendanceUpdate"
        | "emailPreferences.bookingTrialAttendanceUpdate"
        | "emailPreferences.placementApplication"
        | "emailPreferences.candidateInterviewConfirmationComplete"
        | "emailPreferences.smsNotification"
        | "emailPreferences.watcherUpdateExternalBooking"
        | "emailPreferences.watcherClientCandidateInterest"
        | "emailPreferences.watcherClientCandidateInterestSubmit"
        | "emailPreferences.watcherExternalSubmit"
        | "emailPreferences.watcherDocumentRequestSubmitted"
        | "emailPreferences.watcherReferenceSubissionConfirmation"
        | "emailPreferences.watcherUpdateOnboard"
        | "emailPreferences.watcherCandidateBankRequestSubmitted"
        | "emailPreferences.watcherAvailabilityEnquiryExternal"
        | "emailPreferences.watcherTimesheetConfirmation"
        | "emailPreferences.watcherPartialTimesheetConfirmation"
        | "emailPreferences.notificationMethod"
        | "emailPreferences"
        | ""
      )
    | "customer"
  )[];
  /**
   * Related resources to return as objects instead of IDs. Use emailPreferences for email settings and signature for the user's structured email signature.
   */
  expand?: (
    | "emailPreferences"
    | "watchersEmailPreferences"
    | "signature"
    | "customer"
  )[];
};

export type UpdateUserInfoError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateUserInfoResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the user
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The email address of the user
     *
     * @format email
     */
    email?: string;
    /**
     * The first name of the user
     *
     * @minLength 1
     * @maxLength 128
     */
    firstName?: string;
    /**
     * The last name of the user
     *
     * @minLength 1
     * @maxLength 128
     */
    lastName?: string;
    /**
     * The username of the user
     *
     * @minLength 4
     * @maxLength 128
     */
    username?: string;
    /**
     * The position or job title of the user
     *
     * @maxLength 128
     */
    position?: string;
    /**
     * The permission identifiers assigned to the current user.
     *
     * @maxItems 250
     */
    permissions?: number[];
    /**
     * The phone number of the user
     *
     * @maxLength 32
     */
    phone?: string;
    /**
     * User's marketing email signature
     */
    signature?: string | null;
    /**
     * The user's default task workspace
     *
     * @pattern ^[0-9a-f]{24}$
     */
    defaultTaskWorkspace?: string | null;
    /**
     * Saved searches starred by the user, grouped by the type of record they search.
     */
    savedSearches?: {
      /**
       * The candidate saved searches starred by the user.
       *
       * @maxItems 25
       */
      candidates?: string[];
      /**
       * The saved search loaded automatically when the user opens the candidates page. Null uses the standard candidates view.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      defaultCandidate?: string | null;
      /**
       * The client saved searches starred by the user.
       *
       * @maxItems 25
       */
      clients?: string[];
      /**
       * The saved search loaded automatically when the user opens the clients page. Null uses the standard clients view.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      defaultClient?: string | null;
      /**
       * The Client Contact saved searches starred by the user.
       *
       * @maxItems 25
       */
      clientContacts?: string[];
      /**
       * The saved search loaded automatically when the user opens the Client Contacts page. Null uses the standard Client Contacts view.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      defaultClientContact?: string | null;
    };
    /**
     * Calling integrations enabled for the user. Availability also depends on the company's integration configuration and the user's permissions.
     */
    integrationConfiguration?: {
      /**
       * The user's CloudCall VoIP integration settings.
       */
      cloudcall?: {
        /**
         * Whether CloudCall integration is enabled for the user
         */
        enabled?: boolean;
        /**
         * Whether Dialpad integration webhook subscription exists for this users organisation
         */
        subscribed?: boolean;
      };
      /**
       * The user's Dialpad VoIP integration settings.
       */
      dialpad?: {
        /**
         * Whether Dialpad integration is enabled for the user
         */
        enabled?: boolean;
        /**
         * Whether Dialpad integration webhook subscription exists for this users organisation
         */
        subscribed?: boolean;
      };
      /**
       * The user's Devyce VoIP integration settings.
       */
      devyce?: {
        /**
         * Whether Devyce integration is enabled for the user
         */
        enabled?: boolean;
      };
    };
    /**
     * User's email notification preferences and watcher notification preferences.
     */
    emailPreferences?: {
      /**
       * Receive notifications when candidate onboarding is complete
       */
      candidateOnboardingComplete?: boolean;
      /**
       * Receive notifications when candidate onboarding documents are complete
       */
      candidateOnboardingDocumentComplete?: boolean;
      /**
       * Receive notifications when candidate onboarding references are complete
       */
      candidateOnboardingReferenceComplete?: boolean;
      /**
       * Receive notifications when candidate onboarding bank details are complete
       */
      candidateOnboardingBankComplete?: boolean;
      /**
       * Receive notifications for timesheet assignments
       */
      timesheetAssignee?: boolean;
      /**
       * Receive notifications for candidate assignments
       */
      candidateAssignee?: boolean;
      /**
       * Receive notifications for booking assignments
       */
      bookingAssignee?: boolean;
      /**
       * Receive notifications for booking placeholder assignments
       */
      bookingPlaceholderAssignee?: boolean;
      /**
       * Receive notifications for candidate timesheet status updates
       */
      timesheetCandidateStatusUpdate?: boolean;
      /**
       * Receive notifications for client timesheet status updates
       */
      timesheetClientStatusUpdate?: boolean;
      /**
       * Receive notifications for candidate timesheet query updates
       */
      timesheetCandidateQueryUpdate?: boolean;
      /**
       * Receive notifications for client timesheet query updates
       */
      timesheetClientQueryUpdate?: boolean;
      /**
       * Receive notifications for document expiry
       */
      documentExpiry?: boolean;
      /**
       * Receive notifications for interview attendance updates
       */
      bookingInterviewAttendanceUpdate?: boolean;
      /**
       * Receive notifications for trial attendance updates
       */
      bookingTrialAttendanceUpdate?: boolean;
      /**
       * Receive notifications for placement applications
       */
      placementApplication?: boolean;
      /**
       * Receive notifications when candidate interview confirmations are complete
       */
      candidateInterviewConfirmationComplete?: boolean;
      /**
       * Receive SMS notifications
       */
      smsNotification?: boolean;
      /**
       * Receive notifications when an external booking you are watching is updated
       */
      watcherUpdateExternalBooking?: boolean;
      /**
       * Receive notifications when a client shows interest in a candidate you are watching
       */
      watcherClientCandidateInterest?: boolean;
      /**
       * Receive notifications when a client submits interest in a candidate you are watching
       */
      watcherClientCandidateInterestSubmit?: boolean;
      /**
       * Receive notifications when an external submission is made for a candidate you are watching
       */
      watcherExternalSubmit?: boolean;
      /**
       * Receive notifications when a document request is submitted for a candidate you are watching
       */
      watcherDocumentRequestSubmitted?: boolean;
      /**
       * Receive notifications when a reference submission is confirmed for a candidate you are watching
       */
      watcherReferenceSubissionConfirmation?: boolean;
      /**
       * Receive notifications when onboarding is updated for a candidate you are watching
       */
      watcherUpdateOnboard?: boolean;
      /**
       * Receive notifications when a bank request is submitted for a candidate you are watching
       */
      watcherCandidateBankRequestSubmitted?: boolean;
      /**
       * Receive notifications when an external availability enquiry is made for a candidate you are watching
       */
      watcherAvailabilityEnquiryExternal?: boolean;
      /**
       * Receive notifications when a timesheet is confirmed for a candidate you are watching
       */
      watcherTimesheetConfirmation?: boolean;
      /**
       * Receive notifications when a partial timesheet is confirmed for a candidate you are watching
       */
      watcherPartialTimesheetConfirmation?: boolean;
      /**
       * How the user receives watcher notifications: InApp, Email, or Both
       */
      notificationMethod?: "InApp" | "Email" | "Both";
    };
  };
};

export type UpdateUserInfoRequestBody = {
  /**
   * The email address of the user
   *
   * @format email
   */
  email?: string;
  /**
   * The first name of the user
   *
   * @minLength 1
   * @maxLength 128
   */
  firstName?: string;
  /**
   * The last name of the user
   *
   * @minLength 1
   * @maxLength 128
   */
  lastName?: string;
  /**
   * The username of the user
   *
   * @minLength 4
   * @maxLength 128
   */
  username?: string;
  /**
   * The position or job title of the user
   *
   * @maxLength 128
   */
  position?: string;
  /**
   * The phone number of the user
   *
   * @maxLength 32
   */
  phone?: string;
  /**
   * User's marketing email signature
   */
  signature?: string | null;
  /**
   * The user's default task workspace
   *
   * @pattern ^[0-9a-f]{24}$
   */
  defaultTaskWorkspace?: string | null;
  /**
   * Updates to the saved searches starred by the user, grouped by the type of record they search.
   */
  savedSearches?: {
    /**
     * The candidate saved searches starred by the user.
     *
     * @maxItems 25
     */
    candidates?: string[];
    /**
     * The saved search loaded automatically when the user opens the candidates page. Null uses the standard candidates view.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    defaultCandidate?: string | null;
    /**
     * The client saved searches starred by the user.
     *
     * @maxItems 25
     */
    clients?: string[];
    /**
     * The saved search loaded automatically when the user opens the clients page. Null uses the standard clients view.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    defaultClient?: string | null;
    /**
     * The Client Contact saved searches starred by the user.
     *
     * @maxItems 25
     */
    clientContacts?: string[];
    /**
     * The saved search loaded automatically when the user opens the Client Contacts page. Null uses the standard Client Contacts view.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    defaultClientContact?: string | null;
  };
  /**
   * Updates to calling integrations enabled for the user. Availability also depends on company configuration and user permissions.
   */
  integrationConfiguration?: {
    /**
     * The user's CloudCall VoIP integration settings.
     */
    cloudcall?: {
      /**
       * Whether CloudCall integration is enabled for the user
       */
      enabled: boolean;
      /**
       * Whether Dialpad integration webhook subscription exists for this users organisation
       */
      subscribed: boolean;
    };
    /**
     * The user's Dialpad VoIP integration settings.
     */
    dialpad: {
      /**
       * Whether Dialpad integration is enabled for the user
       */
      enabled: boolean;
      /**
       * Whether Dialpad integration webhook subscription exists for this users organisation
       */
      subscribed: boolean;
    };
    /**
     * The user's Devyce VoIP integration settings.
     */
    devyce?: {
      /**
       * Whether Devyce integration is enabled for the user
       */
      enabled: boolean;
    };
  };
  /**
   * User's email notification preferences and watcher notification preferences.
   */
  emailPreferences?: {
    /**
     * Receive notifications when candidate onboarding is complete
     */
    candidateOnboardingComplete?: boolean;
    /**
     * Receive notifications when candidate onboarding documents are complete
     */
    candidateOnboardingDocumentComplete?: boolean;
    /**
     * Receive notifications when candidate onboarding references are complete
     */
    candidateOnboardingReferenceComplete?: boolean;
    /**
     * Receive notifications when candidate onboarding bank details are complete
     */
    candidateOnboardingBankComplete?: boolean;
    /**
     * Receive notifications for timesheet assignments
     */
    timesheetAssignee?: boolean;
    /**
     * Receive notifications for candidate assignments
     */
    candidateAssignee?: boolean;
    /**
     * Receive notifications for booking assignments
     */
    bookingAssignee?: boolean;
    /**
     * Receive notifications for booking placeholder assignments
     */
    bookingPlaceholderAssignee?: boolean;
    /**
     * Receive notifications for candidate timesheet status updates
     */
    timesheetCandidateStatusUpdate?: boolean;
    /**
     * Receive notifications for client timesheet status updates
     */
    timesheetClientStatusUpdate?: boolean;
    /**
     * Receive notifications for candidate timesheet query updates
     */
    timesheetCandidateQueryUpdate?: boolean;
    /**
     * Receive notifications for client timesheet query updates
     */
    timesheetClientQueryUpdate?: boolean;
    /**
     * Receive notifications for document expiry
     */
    documentExpiry?: boolean;
    /**
     * Receive notifications for interview attendance updates
     */
    bookingInterviewAttendanceUpdate?: boolean;
    /**
     * Receive notifications for trial attendance updates
     */
    bookingTrialAttendanceUpdate?: boolean;
    /**
     * Receive notifications for placement applications
     */
    placementApplication?: boolean;
    /**
     * Receive notifications when candidate interview confirmations are complete
     */
    candidateInterviewConfirmationComplete?: boolean;
    /**
     * Receive SMS notifications
     */
    smsNotification?: boolean;
    /**
     * Receive notifications when an external booking you are watching is updated
     */
    watcherUpdateExternalBooking?: boolean;
    /**
     * Receive notifications when a client shows interest in a candidate you are watching
     */
    watcherClientCandidateInterest?: boolean;
    /**
     * Receive notifications when a client submits interest in a candidate you are watching
     */
    watcherClientCandidateInterestSubmit?: boolean;
    /**
     * Receive notifications when an external submission is made for a candidate you are watching
     */
    watcherExternalSubmit?: boolean;
    /**
     * Receive notifications when a document request is submitted for a candidate you are watching
     */
    watcherDocumentRequestSubmitted?: boolean;
    /**
     * Receive notifications when a reference submission is confirmed for a candidate you are watching
     */
    watcherReferenceSubissionConfirmation?: boolean;
    /**
     * Receive notifications when onboarding is updated for a candidate you are watching
     */
    watcherUpdateOnboard?: boolean;
    /**
     * Receive notifications when a bank request is submitted for a candidate you are watching
     */
    watcherCandidateBankRequestSubmitted?: boolean;
    /**
     * Receive notifications when an external availability enquiry is made for a candidate you are watching
     */
    watcherAvailabilityEnquiryExternal?: boolean;
    /**
     * Receive notifications when a timesheet is confirmed for a candidate you are watching
     */
    watcherTimesheetConfirmation?: boolean;
    /**
     * Receive notifications when a partial timesheet is confirmed for a candidate you are watching
     */
    watcherPartialTimesheetConfirmation?: boolean;
    /**
     * How the user receives watcher notifications: InApp, Email, or Both
     */
    notificationMethod?: "InApp" | "Email" | "Both";
  };
};

export type UpdateUserInfoVariables = {
  body?: UpdateUserInfoRequestBody;
  queryParams?: UpdateUserInfoQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateUserInfo = (
  variables: UpdateUserInfoVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateUserInfoResponse,
    UpdateUserInfoError,
    UpdateUserInfoRequestBody,
    {},
    UpdateUserInfoQueryParams,
    {}
  >({ url: "/me", method: "patch", ...variables, signal });

export const useUpdateUserInfo = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateUserInfoResponse,
      UpdateUserInfoError,
      UpdateUserInfoVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateUserInfoResponse,
    UpdateUserInfoError,
    UpdateUserInfoVariables
  >({
    mutationFn: (variables: UpdateUserInfoVariables) =>
      fetchUpdateUserInfo(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type ConfigureUserIntegrationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type ConfigureUserIntegrationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    redirectUrl?: string;
  };
};

export type ConfigureUserIntegrationRequestBody = {
  /**
   * The integration provider to configure for the user
   */
  provider: "cloudcall" | "dialpad" | "devyce";
};

export type ConfigureUserIntegrationVariables = {
  body: ConfigureUserIntegrationRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchConfigureUserIntegration = (
  variables: ConfigureUserIntegrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    ConfigureUserIntegrationResponse,
    ConfigureUserIntegrationError,
    ConfigureUserIntegrationRequestBody,
    {},
    {},
    {}
  >({ url: "/me/integrations", method: "post", ...variables, signal });

export const useConfigureUserIntegration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      ConfigureUserIntegrationResponse,
      ConfigureUserIntegrationError,
      ConfigureUserIntegrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    ConfigureUserIntegrationResponse,
    ConfigureUserIntegrationError,
    ConfigureUserIntegrationVariables
  >({
    mutationFn: (variables: ConfigureUserIntegrationVariables) =>
      fetchConfigureUserIntegration(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type RemoveUserIntegrationPathParams = {
  /**
   * The integration provider to delete configuration for on the user
   */
  provider: "cloudcall" | "dialpad" | "devyce";
};

export type RemoveUserIntegrationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type RemoveUserIntegrationVariables = {
  pathParams: RemoveUserIntegrationPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchRemoveUserIntegration = (
  variables: RemoveUserIntegrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    RemoveUserIntegrationError,
    undefined,
    {},
    {},
    RemoveUserIntegrationPathParams
  >({
    url: "/me/integrations/{provider}",
    method: "delete",
    ...variables,
    signal,
  });

export const useRemoveUserIntegration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      RemoveUserIntegrationError,
      RemoveUserIntegrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    RemoveUserIntegrationError,
    RemoveUserIntegrationVariables
  >({
    mutationFn: (variables: RemoveUserIntegrationVariables) =>
      fetchRemoveUserIntegration(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CreateMigrationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateMigrationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the migration.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The type of migration
     */
    type?:
      | "Candidate"
      | "Client"
      | "ClientContact"
      | "Booking"
      | "Remark"
      | "Document";
    /**
     * The current status of the migration
     *
     * @default pending
     */
    status?:
      | "pending"
      | "validating"
      | "validated"
      | "validation_error"
      | "importing"
      | "completed"
      | "completed_with_errors"
      | "failed";
    /**
     * Whether this migration has been approved
     *
     * @default pending
     */
    approval?: "pending" | "approved" | "rejected";
    /**
     * A user-facing name for this migration.
     *
     * @minLength 1
     * @maxLength 256
     */
    name?: string;
    /**
     * An opaque reference to the uploaded source file. Do not construct or modify this value.
     */
    blobName?: string;
    /**
     * Opaque references to prepared migration data. These values are managed by Edun and must not be modified.
     */
    ndjsonBlobs?: string[];
    /**
     * The customer this migration belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customerId?: string;
    /**
     * The user who initiated the migration
     *
     * @pattern ^[0-9a-f]{24}$
     */
    userId?: string;
    /**
     * Durable function status query URL
     *
     * @format uri
     */
    statusUrl?: string;
    /**
     * Checksum of uploaded file for integrity verification
     */
    fileChecksum?: string;
    /**
     * Structured JSON output of the import process
     */
    importOutput?: {
      /**
       * Results of validating the uploaded rows.
       */
      validation?: {
        /**
         * Number of rows that passed validation.
         *
         * @minimum 0
         */
        validCount?: number;
        /**
         * Number of rows that failed validation.
         *
         * @minimum 0
         */
        invalidCount?: number;
        /**
         * Number of errors reported during this stage.
         *
         * @minimum 0
         */
        errorCount?: number;
      };
      /**
       * Results of saving the validated rows.
       */
      ["import"]?: {
        /**
         * Number of rows successfully processed.
         *
         * @minimum 0
         */
        insertedCount?: number;
        /**
         * Number of errors reported during this stage.
         *
         * @minimum 0
         */
        errorCount?: number;
      };
      /**
       * Opaque references to the prepared migration data.
       *
       * @maxItems 1000
       */
      ndjsonBlobs?: string[];
      /**
       * Details of a migration that could not finish.
       */
      failure?: {
        /**
         * The reason the migration could not finish.
         *
         * @maxLength 16384
         */
        message?: string;
      };
    };
    /**
     * The date this migration was approved.
     *
     * @format date-time
     */
    approvedAt?: string;
    /**
     * The user who approved this migration.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    approvedBy?: string;
    /**
     * Additional options used by migration imports
     */
    importOptions?:
      | {
          operation?: "create";
        }
      | {
          operation?: "update";
        }
      | {
          operation?: "appendToParent";
        }
      | {
          operation?: "updateInParent";
        };
  };
};

export type CreateMigrationRequestBody =
  | {
      /**
       * A user-facing name that helps identify this migration. If omitted, Edun derives a name from the upload.
       *
       * @minLength 1
       * @maxLength 256
       */
      name?: string;
      /**
       * Imports candidate resources.
       */
      type: "Candidate";
      /**
       * Controls whether matching candidate resources are created, updated, or skipped.
       */
      importOptions?:
        | {
            operation: "create";
          }
        | {
            operation: "update";
          };
      /**
       * CSV or JSONL (.jsonl) file to import. JSONL must contain one JSON object per line, not an array.
       *
       * @format binary
       */
      file: Blob;
    }
  | {
      /**
       * A user-facing name that helps identify this migration. If omitted, Edun derives a name from the upload.
       *
       * @minLength 1
       * @maxLength 256
       */
      name?: string;
      /**
       * Imports client resources.
       */
      type: "Client";
      /**
       * Controls whether matching client resources are created, updated, or skipped.
       */
      importOptions?:
        | {
            operation: "create";
          }
        | {
            operation: "update";
          };
      /**
       * CSV or JSONL (.jsonl) file to import. JSONL must contain one JSON object per line, not an array.
       *
       * @format binary
       */
      file: Blob;
    }
  | {
      /**
       * A user-facing name that helps identify this migration. If omitted, Edun derives a name from the upload.
       *
       * @minLength 1
       * @maxLength 256
       */
      name?: string;
      /**
       * Imports client contact resources.
       */
      type: "ClientContact";
      /**
       * Both contact operations update matching contacts within the selected client and append contacts with new identifiers when no match is found.
       */
      importOptions?:
        | {
            operation: "appendToParent";
          }
        | {
            operation: "updateInParent";
          };
      /**
       * CSV or JSONL (.jsonl) file to import. JSONL must contain one JSON object per line, not an array.
       *
       * @format binary
       */
      file: Blob;
    }
  | {
      /**
       * A user-facing name that helps identify this migration. If omitted, Edun derives a name from the upload.
       *
       * @minLength 1
       * @maxLength 256
       */
      name?: string;
      /**
       * Imports booking resources.
       */
      type: "Booking";
      /**
       * Controls whether matching booking resources are created, updated, or skipped.
       */
      importOptions?:
        | {
            operation: "create";
          }
        | {
            operation: "update";
          };
      /**
       * CSV or JSONL (.jsonl) file to import. JSONL must contain one JSON object per line, not an array.
       *
       * @format binary
       */
      file: Blob;
    }
  | {
      /**
       * A user-facing name that helps identify this migration. If omitted, Edun derives a name from the upload.
       *
       * @minLength 1
       * @maxLength 256
       */
      name?: string;
      type: "Remark";
      importOptions?: {
        operation: "create";
      };
      /**
       * CSV or JSONL (.jsonl) file to import. JSONL must contain one JSON object per line, not an array.
       *
       * @format binary
       */
      file: Blob;
    }
  | {
      /**
       * A user-facing name that helps identify this migration. If omitted, Edun derives a name from the upload.
       *
       * @minLength 1
       * @maxLength 256
       */
      name?: string;
      type: "Document";
      importOptions?: {
        operation: "create";
      };
      /**
       * CSV or JSONL (.jsonl) file to import. JSONL must contain one JSON object per line, not an array.
       *
       * @format binary
       */
      file: Blob;
    };

export type CreateMigrationVariables = {
  body?: CreateMigrationRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateMigration = (
  variables: CreateMigrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateMigrationResponse,
    CreateMigrationError,
    CreateMigrationRequestBody,
    {},
    {},
    {}
  >({ url: "/migrations", method: "post", ...variables, signal });

export const useCreateMigration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateMigrationResponse,
      CreateMigrationError,
      CreateMigrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateMigrationResponse,
    CreateMigrationError,
    CreateMigrationVariables
  >({
    mutationFn: (variables: CreateMigrationVariables) =>
      fetchCreateMigration(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type ListMigrationsQueryParams = {
  select?: (
    | "id"
    | "name"
    | "type"
    | "status"
    | "approval"
    | "blobName"
    | "ndjsonBlobs"
    | "importOutput"
    | "importOptions"
  )[];
};

export type ListMigrationsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type ListMigrationsResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the migration.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The type of migration
     */
    type?:
      | "Candidate"
      | "Client"
      | "ClientContact"
      | "Booking"
      | "Remark"
      | "Document";
    /**
     * The current status of the migration
     *
     * @default pending
     */
    status?:
      | "pending"
      | "validating"
      | "validated"
      | "validation_error"
      | "importing"
      | "completed"
      | "completed_with_errors"
      | "failed";
    /**
     * Whether this migration has been approved
     *
     * @default pending
     */
    approval?: "pending" | "approved" | "rejected";
    /**
     * A user-facing name for this migration.
     *
     * @minLength 1
     * @maxLength 256
     */
    name?: string;
    /**
     * An opaque reference to the uploaded source file. Do not construct or modify this value.
     */
    blobName?: string;
    /**
     * Opaque references to prepared migration data. These values are managed by Edun and must not be modified.
     */
    ndjsonBlobs?: string[];
    /**
     * The customer this migration belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customerId?: string;
    /**
     * The user who initiated the migration
     *
     * @pattern ^[0-9a-f]{24}$
     */
    userId?: string;
    /**
     * Durable function status query URL
     *
     * @format uri
     */
    statusUrl?: string;
    /**
     * Checksum of uploaded file for integrity verification
     */
    fileChecksum?: string;
    /**
     * Structured JSON output of the import process
     */
    importOutput?: {
      /**
       * Results of validating the uploaded rows.
       */
      validation?: {
        /**
         * Number of rows that passed validation.
         *
         * @minimum 0
         */
        validCount?: number;
        /**
         * Number of rows that failed validation.
         *
         * @minimum 0
         */
        invalidCount?: number;
        /**
         * Number of errors reported during this stage.
         *
         * @minimum 0
         */
        errorCount?: number;
      };
      /**
       * Results of saving the validated rows.
       */
      ["import"]?: {
        /**
         * Number of rows successfully processed.
         *
         * @minimum 0
         */
        insertedCount?: number;
        /**
         * Number of errors reported during this stage.
         *
         * @minimum 0
         */
        errorCount?: number;
      };
      /**
       * Opaque references to the prepared migration data.
       *
       * @maxItems 1000
       */
      ndjsonBlobs?: string[];
      /**
       * Details of a migration that could not finish.
       */
      failure?: {
        /**
         * The reason the migration could not finish.
         *
         * @maxLength 16384
         */
        message?: string;
      };
    };
    /**
     * The date this migration was approved.
     *
     * @format date-time
     */
    approvedAt?: string;
    /**
     * The user who approved this migration.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    approvedBy?: string;
    /**
     * Additional options used by migration imports
     */
    importOptions?:
      | {
          operation?: "create";
        }
      | {
          operation?: "update";
        }
      | {
          operation?: "appendToParent";
        }
      | {
          operation?: "updateInParent";
        };
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type ListMigrationsVariables = {
  queryParams?: ListMigrationsQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchListMigrations = (
  variables: ListMigrationsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    ListMigrationsResponse,
    ListMigrationsError,
    undefined,
    {},
    ListMigrationsQueryParams,
    {}
  >({ url: "/migrations", method: "get", ...variables, signal });

export function listMigrationsQuery(variables: ListMigrationsVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<ListMigrationsResponse>;
};

export function listMigrationsQuery(
  variables: ListMigrationsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<ListMigrationsResponse>)
    | reactQuery.SkipToken;
};

export function listMigrationsQuery(
  variables: ListMigrationsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/migrations",
      operationId: "listMigrations",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchListMigrations(variables, signal),
  };
}

export const useSuspenseListMigrations = <TData = ListMigrationsResponse,>(
  variables: ListMigrationsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      ListMigrationsResponse,
      ListMigrationsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    ListMigrationsResponse,
    ListMigrationsError,
    TData
  >({
    ...listMigrationsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useListMigrations = <TData = ListMigrationsResponse,>(
  variables: ListMigrationsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      ListMigrationsResponse,
      ListMigrationsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    ListMigrationsResponse,
    ListMigrationsError,
    TData
  >({
    ...listMigrationsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetMigrationPathParams = {
  /**
   * The unique identifier of the resource
   *
   * @pattern ^[0-9a-f]{24}$
   */
  migrationId: string;
};

export type GetMigrationQueryParams = {
  select?: (
    | "id"
    | "name"
    | "type"
    | "status"
    | "approval"
    | "blobName"
    | "ndjsonBlobs"
    | "importOutput"
    | "importOptions"
  )[];
};

export type GetMigrationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetMigrationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the migration.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The type of migration
     */
    type?:
      | "Candidate"
      | "Client"
      | "ClientContact"
      | "Booking"
      | "Remark"
      | "Document";
    /**
     * The current status of the migration
     *
     * @default pending
     */
    status?:
      | "pending"
      | "validating"
      | "validated"
      | "validation_error"
      | "importing"
      | "completed"
      | "completed_with_errors"
      | "failed";
    /**
     * Whether this migration has been approved
     *
     * @default pending
     */
    approval?: "pending" | "approved" | "rejected";
    /**
     * A user-facing name for this migration.
     *
     * @minLength 1
     * @maxLength 256
     */
    name?: string;
    /**
     * An opaque reference to the uploaded source file. Do not construct or modify this value.
     */
    blobName?: string;
    /**
     * Opaque references to prepared migration data. These values are managed by Edun and must not be modified.
     */
    ndjsonBlobs?: string[];
    /**
     * The customer this migration belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customerId?: string;
    /**
     * The user who initiated the migration
     *
     * @pattern ^[0-9a-f]{24}$
     */
    userId?: string;
    /**
     * Durable function status query URL
     *
     * @format uri
     */
    statusUrl?: string;
    /**
     * Checksum of uploaded file for integrity verification
     */
    fileChecksum?: string;
    /**
     * Structured JSON output of the import process
     */
    importOutput?: {
      /**
       * Results of validating the uploaded rows.
       */
      validation?: {
        /**
         * Number of rows that passed validation.
         *
         * @minimum 0
         */
        validCount?: number;
        /**
         * Number of rows that failed validation.
         *
         * @minimum 0
         */
        invalidCount?: number;
        /**
         * Number of errors reported during this stage.
         *
         * @minimum 0
         */
        errorCount?: number;
      };
      /**
       * Results of saving the validated rows.
       */
      ["import"]?: {
        /**
         * Number of rows successfully processed.
         *
         * @minimum 0
         */
        insertedCount?: number;
        /**
         * Number of errors reported during this stage.
         *
         * @minimum 0
         */
        errorCount?: number;
      };
      /**
       * Opaque references to the prepared migration data.
       *
       * @maxItems 1000
       */
      ndjsonBlobs?: string[];
      /**
       * Details of a migration that could not finish.
       */
      failure?: {
        /**
         * The reason the migration could not finish.
         *
         * @maxLength 16384
         */
        message?: string;
      };
    };
    /**
     * The date this migration was approved.
     *
     * @format date-time
     */
    approvedAt?: string;
    /**
     * The user who approved this migration.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    approvedBy?: string;
    /**
     * Additional options used by migration imports
     */
    importOptions?:
      | {
          operation?: "create";
        }
      | {
          operation?: "update";
        }
      | {
          operation?: "appendToParent";
        }
      | {
          operation?: "updateInParent";
        };
  };
};

export type GetMigrationVariables = {
  pathParams: GetMigrationPathParams;
  queryParams?: GetMigrationQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetMigration = (
  variables: GetMigrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetMigrationResponse,
    GetMigrationError,
    undefined,
    {},
    GetMigrationQueryParams,
    GetMigrationPathParams
  >({ url: "/migrations/{migrationId}", method: "get", ...variables, signal });

export function getMigrationQuery(variables: GetMigrationVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetMigrationResponse>;
};

export function getMigrationQuery(
  variables: GetMigrationVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetMigrationResponse>)
    | reactQuery.SkipToken;
};

export function getMigrationQuery(
  variables: GetMigrationVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/migrations/{migrationId}",
      operationId: "getMigration",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetMigration(variables, signal),
  };
}

export const useSuspenseGetMigration = <TData = GetMigrationResponse,>(
  variables: GetMigrationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetMigrationResponse, GetMigrationError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetMigrationResponse,
    GetMigrationError,
    TData
  >({
    ...getMigrationQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetMigration = <TData = GetMigrationResponse,>(
  variables: GetMigrationVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetMigrationResponse, GetMigrationError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetMigrationResponse, GetMigrationError, TData>({
    ...getMigrationQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateMigrationPathParams = {
  /**
   * The unique identifier of the resource
   *
   * @pattern ^[0-9a-f]{24}$
   */
  migrationId: string;
};

export type UpdateMigrationError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateMigrationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the migration.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The type of migration
     */
    type?:
      | "Candidate"
      | "Client"
      | "ClientContact"
      | "Booking"
      | "Remark"
      | "Document";
    /**
     * The current status of the migration
     *
     * @default pending
     */
    status?:
      | "pending"
      | "validating"
      | "validated"
      | "validation_error"
      | "importing"
      | "completed"
      | "completed_with_errors"
      | "failed";
    /**
     * Whether this migration has been approved
     *
     * @default pending
     */
    approval?: "pending" | "approved" | "rejected";
    /**
     * A user-facing name for this migration.
     *
     * @minLength 1
     * @maxLength 256
     */
    name?: string;
    /**
     * An opaque reference to the uploaded source file. Do not construct or modify this value.
     */
    blobName?: string;
    /**
     * Opaque references to prepared migration data. These values are managed by Edun and must not be modified.
     */
    ndjsonBlobs?: string[];
    /**
     * The customer this migration belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customerId?: string;
    /**
     * The user who initiated the migration
     *
     * @pattern ^[0-9a-f]{24}$
     */
    userId?: string;
    /**
     * Durable function status query URL
     *
     * @format uri
     */
    statusUrl?: string;
    /**
     * Checksum of uploaded file for integrity verification
     */
    fileChecksum?: string;
    /**
     * Structured JSON output of the import process
     */
    importOutput?: {
      /**
       * Results of validating the uploaded rows.
       */
      validation?: {
        /**
         * Number of rows that passed validation.
         *
         * @minimum 0
         */
        validCount?: number;
        /**
         * Number of rows that failed validation.
         *
         * @minimum 0
         */
        invalidCount?: number;
        /**
         * Number of errors reported during this stage.
         *
         * @minimum 0
         */
        errorCount?: number;
      };
      /**
       * Results of saving the validated rows.
       */
      ["import"]?: {
        /**
         * Number of rows successfully processed.
         *
         * @minimum 0
         */
        insertedCount?: number;
        /**
         * Number of errors reported during this stage.
         *
         * @minimum 0
         */
        errorCount?: number;
      };
      /**
       * Opaque references to the prepared migration data.
       *
       * @maxItems 1000
       */
      ndjsonBlobs?: string[];
      /**
       * Details of a migration that could not finish.
       */
      failure?: {
        /**
         * The reason the migration could not finish.
         *
         * @maxLength 16384
         */
        message?: string;
      };
    };
    /**
     * The date this migration was approved.
     *
     * @format date-time
     */
    approvedAt?: string;
    /**
     * The user who approved this migration.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    approvedBy?: string;
    /**
     * Additional options used by migration imports
     */
    importOptions?:
      | {
          operation?: "create";
        }
      | {
          operation?: "update";
        }
      | {
          operation?: "appendToParent";
        }
      | {
          operation?: "updateInParent";
        };
  };
};

export type UpdateMigrationRequestBody = {
  /**
   * Approves the prepared migration for processing or rejects it without importing its data. Migration management permission is required.
   */
  approval: "approved" | "rejected";
};

export type UpdateMigrationVariables = {
  body: UpdateMigrationRequestBody;
  pathParams: UpdateMigrationPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateMigration = (
  variables: UpdateMigrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateMigrationResponse,
    UpdateMigrationError,
    UpdateMigrationRequestBody,
    {},
    {},
    UpdateMigrationPathParams
  >({
    url: "/migrations/{migrationId}",
    method: "patch",
    ...variables,
    signal,
  });

export const useUpdateMigration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateMigrationResponse,
      UpdateMigrationError,
      UpdateMigrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateMigrationResponse,
    UpdateMigrationError,
    UpdateMigrationVariables
  >({
    mutationFn: (variables: UpdateMigrationVariables) =>
      fetchUpdateMigration(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type ListMigrationErrorsPathParams = {
  /**
   * The unique identifier of the resource
   *
   * @pattern ^[0-9a-f]{24}$
   */
  migrationId: string;
};

export type ListMigrationErrorsQueryParams = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * The field to sort migration errors by
   */
  sort?: "row" | "-row" | "createdAt" | "-createdAt";
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "migrationId"
    | "stage"
    | "row"
    | "rows"
    | "field"
    | "value"
    | "expected"
    | "message"
    | "code"
    | ""
  )[];
  /**
   * Filter migration errors
   */
  filter?: {
    /**
     * Filter errors by migration stage
     *
     * @maxItems 2
     */
    stage?: ("validation" | "import")[];
  };
};

export type ListMigrationErrorsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type ListMigrationErrorsResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the migration error
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The migration this error belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * The migration stage where the error occurred
     */
    stage?: "validation" | "import";
    /**
     * The first CSV row/JSON object related to this error
     */
    row?: number;
    /**
     * The CSV rows/JSON objects that produced this error
     */
    rows?: number[];
    /**
     * The field/path that failed validation or import
     */
    field?: string;
    /**
     * The supplied value that caused the error
     */
    value?: void | null;
    /**
     * The expected value, format, type, or allowed values
     */
    expected?: void | null;
    /**
     * A short human-readable error message
     */
    message?: string;
    /**
     * A machine-readable error code
     */
    code?: string | number;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type ListMigrationErrorsVariables = {
  pathParams: ListMigrationErrorsPathParams;
  queryParams?: ListMigrationErrorsQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchListMigrationErrors = (
  variables: ListMigrationErrorsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    ListMigrationErrorsResponse,
    ListMigrationErrorsError,
    undefined,
    {},
    ListMigrationErrorsQueryParams,
    ListMigrationErrorsPathParams
  >({
    url: "/migrations/{migrationId}/errors",
    method: "get",
    ...variables,
    signal,
  });

export function listMigrationErrorsQuery(
  variables: ListMigrationErrorsVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<ListMigrationErrorsResponse>;
};

export function listMigrationErrorsQuery(
  variables: ListMigrationErrorsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<ListMigrationErrorsResponse>)
    | reactQuery.SkipToken;
};

export function listMigrationErrorsQuery(
  variables: ListMigrationErrorsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/migrations/{migrationId}/errors",
      operationId: "listMigrationErrors",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchListMigrationErrors(variables, signal),
  };
}

export const useSuspenseListMigrationErrors = <
  TData = ListMigrationErrorsResponse,
>(
  variables: ListMigrationErrorsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      ListMigrationErrorsResponse,
      ListMigrationErrorsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    ListMigrationErrorsResponse,
    ListMigrationErrorsError,
    TData
  >({
    ...listMigrationErrorsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useListMigrationErrors = <TData = ListMigrationErrorsResponse,>(
  variables: ListMigrationErrorsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      ListMigrationErrorsResponse,
      ListMigrationErrorsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    ListMigrationErrorsResponse,
    ListMigrationErrorsError,
    TData
  >({
    ...listMigrationErrorsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetMigrationErrorPathParams = {
  /**
   * The unique identifier for the migration
   *
   * @pattern ^[0-9a-f]{24}$
   */
  migrationId: string;
  /**
   * The unique identifier for the migration error
   *
   * @pattern ^[0-9a-f]{24}$
   */
  migrationErrorId: string;
};

export type GetMigrationErrorQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "migrationId"
    | "stage"
    | "row"
    | "rows"
    | "field"
    | "value"
    | "expected"
    | "message"
    | "code"
    | ""
  )[];
};

export type GetMigrationErrorError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetMigrationErrorResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the migration error
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The migration this error belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * The migration stage where the error occurred
     */
    stage?: "validation" | "import";
    /**
     * The first CSV row/JSON object related to this error
     */
    row?: number;
    /**
     * The CSV rows/JSON objects that produced this error
     */
    rows?: number[];
    /**
     * The field/path that failed validation or import
     */
    field?: string;
    /**
     * The supplied value that caused the error
     */
    value?: void | null;
    /**
     * The expected value, format, type, or allowed values
     */
    expected?: void | null;
    /**
     * A short human-readable error message
     */
    message?: string;
    /**
     * A machine-readable error code
     */
    code?: string | number;
  };
};

export type GetMigrationErrorVariables = {
  pathParams: GetMigrationErrorPathParams;
  queryParams?: GetMigrationErrorQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetMigrationError = (
  variables: GetMigrationErrorVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetMigrationErrorResponse,
    GetMigrationErrorError,
    undefined,
    {},
    GetMigrationErrorQueryParams,
    GetMigrationErrorPathParams
  >({
    url: "/migrations/{migrationId}/errors/{migrationErrorId}",
    method: "get",
    ...variables,
    signal,
  });

export function getMigrationErrorQuery(variables: GetMigrationErrorVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetMigrationErrorResponse>;
};

export function getMigrationErrorQuery(
  variables: GetMigrationErrorVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetMigrationErrorResponse>)
    | reactQuery.SkipToken;
};

export function getMigrationErrorQuery(
  variables: GetMigrationErrorVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/migrations/{migrationId}/errors/{migrationErrorId}",
      operationId: "getMigrationError",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetMigrationError(variables, signal),
  };
}

export const useSuspenseGetMigrationError = <
  TData = GetMigrationErrorResponse,
>(
  variables: GetMigrationErrorVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetMigrationErrorResponse,
      GetMigrationErrorError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetMigrationErrorResponse,
    GetMigrationErrorError,
    TData
  >({
    ...getMigrationErrorQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetMigrationError = <TData = GetMigrationErrorResponse,>(
  variables: GetMigrationErrorVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetMigrationErrorResponse,
      GetMigrationErrorError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetMigrationErrorResponse,
    GetMigrationErrorError,
    TData
  >({
    ...getMigrationErrorQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetCandidateRemarksPathParams = {
  /**
   * The unique identifier for the candidate.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId: string;
};

export type GetCandidateRemarksQueryParams = {
  /**
   * The page number to retrieve
   *
   * @minimum 1
   */
  page?: number;
  /**
   * The number of items to retrieve per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Filter remarks by the associated resource ID
   *
   * @pattern ^[0-9a-f]{24}$
   */
  referenceId?: string;
  /**
   * The field to sort the results by
   */
  sort?: "createdAt" | "updatedAt";
  /**
   * The order in which to sort the results
   */
  order?: "asc" | "desc";
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "content"
    | "contentPlainText"
    | "type"
    | "referenceId"
    | "createdBy"
    | "createdById"
    | "replyTo"
    | "mentions"
    | "createdAt"
    | "updatedAt"
    | ""
  )[];
};

export type GetCandidateRemarksError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetCandidateRemarksResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the remark
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The content of the remark in markdown/rich text format
     */
    content?: string;
    /**
     * The plain text version of the remark content
     */
    contentPlainText?: string;
    /**
     * The type identifier for the remark
     */
    type?: number;
    /**
     * The ID of the resource this remark is associated with
     *
     * @pattern ^[0-9a-f]{24}$
     */
    referenceId?: string;
    /**
     * The display name of the user who created the remark
     *
     * @maxLength 256
     */
    createdBy?: string;
    /**
     * The ID of the user who created the remark
     *
     * @pattern ^[0-9a-f]{24}$
     */
    createdById?: string;
    /**
     * The ID of the remark this is a reply to, or null if top-level
     *
     * @pattern ^[0-9a-f]{24}$
     */
    replyTo?: string | null;
    /**
     * Array of user IDs mentioned in this remark
     */
    mentions?: string[];
    /**
     * The date and time the remark was created
     *
     * @format date-time
     */
    createdAt?: string;
    /**
     * The date and time the remark was last updated
     *
     * @format date-time
     */
    updatedAt?: string;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetCandidateRemarksVariables = {
  pathParams: GetCandidateRemarksPathParams;
  queryParams?: GetCandidateRemarksQueryParams;
} & Apiv2Context["fetcherOptions"];

/**
 * Deprecated and retained for compatibility with existing integrations. Do not use this operation for new integrations. Remarks are returned only when the authenticated user can access the candidate.
 */
export const fetchGetCandidateRemarks = (
  variables: GetCandidateRemarksVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetCandidateRemarksResponse,
    GetCandidateRemarksError,
    undefined,
    {},
    GetCandidateRemarksQueryParams,
    GetCandidateRemarksPathParams
  >({
    url: "/candidates/{candidateId}/remarks",
    method: "get",
    ...variables,
    signal,
  });

/**
 * Deprecated and retained for compatibility with existing integrations. Do not use this operation for new integrations. Remarks are returned only when the authenticated user can access the candidate.
 */
export function getCandidateRemarksQuery(
  variables: GetCandidateRemarksVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetCandidateRemarksResponse>;
};

export function getCandidateRemarksQuery(
  variables: GetCandidateRemarksVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetCandidateRemarksResponse>)
    | reactQuery.SkipToken;
};

export function getCandidateRemarksQuery(
  variables: GetCandidateRemarksVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/candidates/{candidateId}/remarks",
      operationId: "getCandidateRemarks",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetCandidateRemarks(variables, signal),
  };
}

/**
 * Deprecated and retained for compatibility with existing integrations. Do not use this operation for new integrations. Remarks are returned only when the authenticated user can access the candidate.
 */
export const useSuspenseGetCandidateRemarks = <
  TData = GetCandidateRemarksResponse,
>(
  variables: GetCandidateRemarksVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCandidateRemarksResponse,
      GetCandidateRemarksError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetCandidateRemarksResponse,
    GetCandidateRemarksError,
    TData
  >({
    ...getCandidateRemarksQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

/**
 * Deprecated and retained for compatibility with existing integrations. Do not use this operation for new integrations. Remarks are returned only when the authenticated user can access the candidate.
 */
export const useGetCandidateRemarks = <TData = GetCandidateRemarksResponse,>(
  variables: GetCandidateRemarksVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCandidateRemarksResponse,
      GetCandidateRemarksError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetCandidateRemarksResponse,
    GetCandidateRemarksError,
    TData
  >({
    ...getCandidateRemarksQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetCompanyQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "name"
    | "logo"
    | "contactEmail"
    | "contactTelephone"
    | "contactWebsite"
    | "address"
    | "companyNumber"
    | "customerForegroundColor"
    | "customerBackgroundColor"
    | "customerAccentColor"
    | "tuitionReportQuestions.TutorStandard.questions"
    | "tuitionReportQuestions.TutorStandard.required"
    | "tuitionReportQuestions.TutorStandard"
    | "tuitionReportQuestions.TutorMentor.questions"
    | "tuitionReportQuestions.TutorMentor.required"
    | "tuitionReportQuestions.TutorMentor"
    | "tuitionReportQuestions.TutorBasic.questions"
    | "tuitionReportQuestions.TutorBasic.required"
    | "tuitionReportQuestions.TutorBasic"
    | "tuitionReportQuestions"
    | "healthDeclarationConfig.questions"
    | "healthDeclarationConfig.updatedAt"
    | "healthDeclarationConfig"
    | "criminalRecordConfig.questions"
    | "criminalRecordConfig.updatedAt"
    | "criminalRecordConfig"
    | "bookingConfirmationConfig.candidate.standard.rows"
    | "bookingConfirmationConfig.candidate.standard.confirmationText"
    | "bookingConfirmationConfig.candidate.standard.includeWrittenStatement"
    | "bookingConfirmationConfig.candidate.standard.includeWrittenStatementSignature"
    | "bookingConfirmationConfig.candidate.standard.writtenStatement.hoursWork"
    | "bookingConfirmationConfig.candidate.standard.writtenStatement.renumeration"
    | "bookingConfirmationConfig.candidate.standard.writtenStatement.holidays"
    | "bookingConfirmationConfig.candidate.standard.writtenStatement.sickness"
    | "bookingConfirmationConfig.candidate.standard.writtenStatement.benefits"
    | "bookingConfirmationConfig.candidate.standard.writtenStatement.termination"
    | "bookingConfirmationConfig.candidate.standard.writtenStatement.collectiveAgreements"
    | "bookingConfirmationConfig.candidate.standard.writtenStatement"
    | "bookingConfirmationConfig.candidate.standard.options.showHolidayPayPercentage"
    | "bookingConfirmationConfig.candidate.standard.options.showStandardAttendanceButton"
    | "bookingConfirmationConfig.candidate.standard.options.showAcknowledgementButton"
    | "bookingConfirmationConfig.candidate.standard.options"
    | "bookingConfirmationConfig.candidate.standard"
    | "bookingConfirmationConfig.candidate.tutor.rows"
    | "bookingConfirmationConfig.candidate.tutor.confirmationText"
    | "bookingConfirmationConfig.candidate.tutor.includeWrittenStatement"
    | "bookingConfirmationConfig.candidate.tutor.includeWrittenStatementSignature"
    | "bookingConfirmationConfig.candidate.tutor.writtenStatement.hoursWork"
    | "bookingConfirmationConfig.candidate.tutor.writtenStatement.renumeration"
    | "bookingConfirmationConfig.candidate.tutor.writtenStatement.holidays"
    | "bookingConfirmationConfig.candidate.tutor.writtenStatement.sickness"
    | "bookingConfirmationConfig.candidate.tutor.writtenStatement.benefits"
    | "bookingConfirmationConfig.candidate.tutor.writtenStatement.termination"
    | "bookingConfirmationConfig.candidate.tutor.writtenStatement.collectiveAgreements"
    | "bookingConfirmationConfig.candidate.tutor.writtenStatement"
    | "bookingConfirmationConfig.candidate.tutor.options.showHolidayPayPercentage"
    | "bookingConfirmationConfig.candidate.tutor.options.showAcknowledgementButton"
    | "bookingConfirmationConfig.candidate.tutor.options"
    | "bookingConfirmationConfig.candidate.tutor"
    | "bookingConfirmationConfig.candidate.interview.rows"
    | "bookingConfirmationConfig.candidate.interview.confirmationText"
    | "bookingConfirmationConfig.candidate.interview.includeWrittenStatement"
    | "bookingConfirmationConfig.candidate.interview.includeWrittenStatementSignature"
    | "bookingConfirmationConfig.candidate.interview.writtenStatement.hoursWork"
    | "bookingConfirmationConfig.candidate.interview.writtenStatement.renumeration"
    | "bookingConfirmationConfig.candidate.interview.writtenStatement.holidays"
    | "bookingConfirmationConfig.candidate.interview.writtenStatement.sickness"
    | "bookingConfirmationConfig.candidate.interview.writtenStatement.benefits"
    | "bookingConfirmationConfig.candidate.interview.writtenStatement.termination"
    | "bookingConfirmationConfig.candidate.interview.writtenStatement.collectiveAgreements"
    | "bookingConfirmationConfig.candidate.interview.writtenStatement"
    | "bookingConfirmationConfig.candidate.interview"
    | "bookingConfirmationConfig.candidate.trial.rows"
    | "bookingConfirmationConfig.candidate.trial.confirmationText"
    | "bookingConfirmationConfig.candidate.trial.includeWrittenStatement"
    | "bookingConfirmationConfig.candidate.trial.includeWrittenStatementSignature"
    | "bookingConfirmationConfig.candidate.trial.writtenStatement.hoursWork"
    | "bookingConfirmationConfig.candidate.trial.writtenStatement.renumeration"
    | "bookingConfirmationConfig.candidate.trial.writtenStatement.holidays"
    | "bookingConfirmationConfig.candidate.trial.writtenStatement.sickness"
    | "bookingConfirmationConfig.candidate.trial.writtenStatement.benefits"
    | "bookingConfirmationConfig.candidate.trial.writtenStatement.termination"
    | "bookingConfirmationConfig.candidate.trial.writtenStatement.collectiveAgreements"
    | "bookingConfirmationConfig.candidate.trial.writtenStatement"
    | "bookingConfirmationConfig.candidate.trial.options.showHolidayPayPercentage"
    | "bookingConfirmationConfig.candidate.trial.options"
    | "bookingConfirmationConfig.candidate.trial"
    | "bookingConfirmationConfig.candidate.permanent.rows"
    | "bookingConfirmationConfig.candidate.permanent.confirmationText"
    | "bookingConfirmationConfig.candidate.permanent.includeWrittenStatement"
    | "bookingConfirmationConfig.candidate.permanent.includeWrittenStatementSignature"
    | "bookingConfirmationConfig.candidate.permanent.writtenStatement.hoursWork"
    | "bookingConfirmationConfig.candidate.permanent.writtenStatement.renumeration"
    | "bookingConfirmationConfig.candidate.permanent.writtenStatement.holidays"
    | "bookingConfirmationConfig.candidate.permanent.writtenStatement.sickness"
    | "bookingConfirmationConfig.candidate.permanent.writtenStatement.benefits"
    | "bookingConfirmationConfig.candidate.permanent.writtenStatement.termination"
    | "bookingConfirmationConfig.candidate.permanent.writtenStatement.collectiveAgreements"
    | "bookingConfirmationConfig.candidate.permanent.writtenStatement"
    | "bookingConfirmationConfig.candidate.permanent"
    | "bookingConfirmationConfig.candidate"
    | "bookingConfirmationConfig.client.standard.rows"
    | "bookingConfirmationConfig.client.standard.confirmationText"
    | "bookingConfirmationConfig.client.standard.options.showQualificationDates"
    | "bookingConfirmationConfig.client.standard.options.showFitnessToWorkDate"
    | "bookingConfirmationConfig.client.standard.options.showReferenceCount"
    | "bookingConfirmationConfig.client.standard.options"
    | "bookingConfirmationConfig.client.standard"
    | "bookingConfirmationConfig.client.tutor.rows"
    | "bookingConfirmationConfig.client.tutor.confirmationText"
    | "bookingConfirmationConfig.client.tutor.options.showQualificationDates"
    | "bookingConfirmationConfig.client.tutor.options.showFitnessToWorkDate"
    | "bookingConfirmationConfig.client.tutor.options.showReferenceCount"
    | "bookingConfirmationConfig.client.tutor.options"
    | "bookingConfirmationConfig.client.tutor"
    | "bookingConfirmationConfig.client.interview.rows"
    | "bookingConfirmationConfig.client.interview.confirmationText"
    | "bookingConfirmationConfig.client.interview"
    | "bookingConfirmationConfig.client.trial.rows"
    | "bookingConfirmationConfig.client.trial.confirmationText"
    | "bookingConfirmationConfig.client.trial.options.showQualificationDates"
    | "bookingConfirmationConfig.client.trial.options.showFitnessToWorkDate"
    | "bookingConfirmationConfig.client.trial.options.showReferenceCount"
    | "bookingConfirmationConfig.client.trial.options"
    | "bookingConfirmationConfig.client.trial"
    | "bookingConfirmationConfig.client.permanent.rows"
    | "bookingConfirmationConfig.client.permanent.confirmationText"
    | "bookingConfirmationConfig.client.permanent.options.showQualificationDates"
    | "bookingConfirmationConfig.client.permanent.options.showFitnessToWorkDate"
    | "bookingConfirmationConfig.client.permanent.options.showReferenceCount"
    | "bookingConfirmationConfig.client.permanent.options"
    | "bookingConfirmationConfig.client.permanent"
    | "bookingConfirmationConfig.client"
    | "bookingConfirmationConfig"
    | "jobFeedBackgroundColor"
    | "modules.tasks.enabled"
    | "modules.tasks"
    | "modules.forms.enabled"
    | "modules.forms"
    | "modules"
    | "integrations.matrixOrganisationReferences"
    | "integrations.cvLibrary.username"
    | "integrations.cvLibrary.enabled"
    | "integrations.cvLibrary"
    | "integrations.quba.key"
    | "integrations.quba.expiry"
    | "integrations.quba"
    | "integrations.devyce.enabled"
    | "integrations.devyce.apiKeySet"
    | "integrations.devyce.organisationId"
    | "integrations.devyce.lastSuccessfulPollAt"
    | "integrations.devyce"
    | "integrations.evertime.enabled"
    | "integrations.evertime"
    | "integrations.timesheetPortal.subdomain"
    | "integrations.timesheetPortal.enabled"
    | "integrations.timesheetPortal"
    | "integrations.xero.user"
    | "integrations.xero.tenant.createdDateUtc"
    | "integrations.xero.tenant.updateDateUtc"
    | "integrations.xero.tenant"
    | "integrations.xero"
    | "integrations"
    | "marketingSettings.verifiedDomain"
    | "marketingSettings.domain"
    | "marketingSettings.sendPrefix"
    | "marketingSettings.signature"
    | "marketingSettings"
    | "gcaLotConfig.lots"
    | "gcaLotConfig.defaultLotId"
    | "gcaLotConfig"
    | "paymentRunExportSettings.fiscalYearStartMonth"
    | "paymentRunExportSettings.vatPercent"
    | "paymentRunExportSettings"
    | ""
  )[];
  /**
   * Additional fields to expand in the response
   */
  expand?: (
    | "tuitionReportQuestions"
    | "healthDeclarationConfig"
    | "criminalRecordConfig"
    | "matrixOrganisationReferences"
    | "jobBoard"
    | "qubaDataSyncApiKey"
    | "marketingSettings"
  )[];
  /**
   * Filter company data based on various criteria
   */
  filter?: {
    /**
     * Per-subtype filter options for tuition report questions. e.g. filter[tuitionReportQuestions][TutorStandard][questions][enabled]=true
     */
    tuitionReportQuestions?: {
      TutorStandard?: {
        /**
         * Filter options for questions within this subtype
         */
        questions?: {
          /**
           * When 'true', only include enabled questions for this subtype.
           */
          enabled?: "true" | "false";
        };
      };
      TutorMentor?: {
        /**
         * Filter options for questions within this subtype
         */
        questions?: {
          /**
           * When 'true', only include enabled questions for this subtype.
           */
          enabled?: "true" | "false";
        };
      };
      TutorBasic?: {
        /**
         * Filter options for questions within this subtype
         */
        questions?: {
          /**
           * When 'true', only include enabled questions for this subtype.
           */
          enabled?: "true" | "false";
        };
      };
    };
  };
};

export type GetCompanyError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetCompanyResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the company
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The company name displayed to users and in company-branded communications.
     *
     * @maxLength 256
     */
    name?: string;
    /**
     * URL of the company logo
     *
     * @maxLength 2048
     */
    logo?: string;
    /**
     * Primary contact email for the company
     *
     * @maxLength 320
     * @format email
     */
    contactEmail?: string;
    /**
     * Primary contact telephone for the company
     *
     * @maxLength 50
     */
    contactTelephone?: string;
    /**
     * Company website URL
     *
     * @maxLength 2048
     */
    contactWebsite?: string;
    /**
     * The company address
     *
     * @maxLength 512
     */
    address?: string;
    /**
     * Registered company number
     *
     * @maxLength 128
     */
    companyNumber?: string;
    /**
     * Brand foreground color (hex)
     *
     * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
     */
    customerForegroundColor?: string;
    /**
     * Brand background color (hex)
     *
     * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
     */
    customerBackgroundColor?: string;
    /**
     * Brand accent color (hex)
     *
     * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
     */
    customerAccentColor?: string;
    /**
     * Custom tuition report questions per booking subtype. If not set, the system defaults are used.
     */
    tuitionReportQuestions?: {
      TutorStandard?: {
        /**
         * @maxItems 100
         */
        questions?: {
          /**
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * @minLength 1
           * @maxLength 128
           */
          key?: string;
          /**
           * @minLength 1
           * @maxLength 500
           */
          label?: string;
          /**
           * @maxLength 2000
           */
          description?: string;
          type?:
            | "checkbox"
            | "date"
            | "daterange"
            | "dropdown"
            | "number"
            | "signature"
            | "text"
            | "textarea";
          required?: boolean;
          enabled?: boolean;
          /**
           * @minimum 0
           * @maximum 1000
           */
          order?: number;
          condition?: {
            mode?: "any" | "all";
            /**
             * @minItems 1
             * @maxItems 20
             */
            rules?: {
              /**
               * The stable key of the question that controls visibility.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * How the controlling answer is compared with the value.
               */
              operator?: "equals" | "notEquals";
              /**
               * The answer value used to determine visibility.
               */
              value?: string | boolean | number | (void | null);
            }[];
          };
          /**
           * @minItems 1
           * @maxItems 100
           */
          options?: {
            /**
             * The value stored when this option is selected.
             *
             * @minLength 1
             * @maxLength 200
             */
            value?: string;
            /**
             * The option displayed to the person answering the question.
             *
             * @minLength 1
             * @maxLength 200
             */
            label?: string;
          }[];
          ["default"]?: boolean;
          audience?: "candidate" | "staff";
          fixedRole?: "fullName" | "signature";
        }[];
        required?: boolean;
      };
      TutorMentor?: {
        /**
         * @maxItems 100
         */
        questions?: {
          /**
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * @minLength 1
           * @maxLength 128
           */
          key?: string;
          /**
           * @minLength 1
           * @maxLength 500
           */
          label?: string;
          /**
           * @maxLength 2000
           */
          description?: string;
          type?:
            | "checkbox"
            | "date"
            | "daterange"
            | "dropdown"
            | "number"
            | "signature"
            | "text"
            | "textarea";
          required?: boolean;
          enabled?: boolean;
          /**
           * @minimum 0
           * @maximum 1000
           */
          order?: number;
          condition?: {
            mode?: "any" | "all";
            /**
             * @minItems 1
             * @maxItems 20
             */
            rules?: {
              /**
               * The stable key of the question that controls visibility.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * How the controlling answer is compared with the value.
               */
              operator?: "equals" | "notEquals";
              /**
               * The answer value used to determine visibility.
               */
              value?: string | boolean | number | (void | null);
            }[];
          };
          /**
           * @minItems 1
           * @maxItems 100
           */
          options?: {
            /**
             * The value stored when this option is selected.
             *
             * @minLength 1
             * @maxLength 200
             */
            value?: string;
            /**
             * The option displayed to the person answering the question.
             *
             * @minLength 1
             * @maxLength 200
             */
            label?: string;
          }[];
          ["default"]?: boolean;
          audience?: "candidate" | "staff";
          fixedRole?: "fullName" | "signature";
        }[];
        required?: boolean;
      };
      TutorBasic?: {
        /**
         * @maxItems 100
         */
        questions?: {
          /**
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * @minLength 1
           * @maxLength 128
           */
          key?: string;
          /**
           * @minLength 1
           * @maxLength 500
           */
          label?: string;
          /**
           * @maxLength 2000
           */
          description?: string;
          type?:
            | "checkbox"
            | "date"
            | "daterange"
            | "dropdown"
            | "number"
            | "signature"
            | "text"
            | "textarea";
          required?: boolean;
          enabled?: boolean;
          /**
           * @minimum 0
           * @maximum 1000
           */
          order?: number;
          condition?: {
            mode?: "any" | "all";
            /**
             * @minItems 1
             * @maxItems 20
             */
            rules?: {
              /**
               * The stable key of the question that controls visibility.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * How the controlling answer is compared with the value.
               */
              operator?: "equals" | "notEquals";
              /**
               * The answer value used to determine visibility.
               */
              value?: string | boolean | number | (void | null);
            }[];
          };
          /**
           * @minItems 1
           * @maxItems 100
           */
          options?: {
            /**
             * The value stored when this option is selected.
             *
             * @minLength 1
             * @maxLength 200
             */
            value?: string;
            /**
             * The option displayed to the person answering the question.
             *
             * @minLength 1
             * @maxLength 200
             */
            label?: string;
          }[];
          ["default"]?: boolean;
          audience?: "candidate" | "staff";
          fixedRole?: "fullName" | "signature";
        }[];
        required?: boolean;
      };
    };
    /**
     * The questions presented in new health declarations. If not set, the standard questions are used.
     */
    healthDeclarationConfig?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * When this question configuration was last updated.
       *
       * @format date-time
       */
      updatedAt?: string;
    };
    /**
     * The questions presented in new Criminal Record & Security Checks declarations. If not set, the standard questions are used.
     */
    criminalRecordConfig?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * When this question configuration was last updated.
       *
       * @format date-time
       */
      updatedAt?: string;
    };
    /**
     * Table row visibility, titles, and related display options for booking confirmations.
     */
    bookingConfirmationConfig?: {
      /**
       * Variant-specific display settings for candidate booking confirmations.
       */
      candidate?: {
        standard?: {
          /**
           * Rows available in standard candidate booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "role"
              | "teacherRegistrationNumber"
              | "start"
              | "end"
              | "duration"
              | "durationDetail"
              | "clientName"
              | "address"
              | "payRate"
              | "paymentType"
              | "arrivalTime"
              | "schoolContact"
              | "healthAndSafetyRisks"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
           */
          includeWrittenStatement?: boolean;
          /**
           * Whether the attached written statement of particulars includes a signature for this booking type.
           */
          includeWrittenStatementSignature?: boolean;
          /**
           * Written statement of particulars content attached to candidate confirmations for this booking type.
           */
          writtenStatement?: {
            /**
             * Written statement entries describing working hours.
             *
             * @maxItems 100
             */
            hoursWork?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing remuneration.
             *
             * @maxItems 100
             */
            renumeration?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing holiday entitlement.
             *
             * @maxItems 100
             */
            holidays?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing sickness arrangements.
             *
             * @maxItems 100
             */
            sickness?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing benefits.
             *
             * @maxItems 100
             */
            benefits?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing termination terms.
             *
             * @maxItems 100
             */
            termination?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing applicable collective agreements.
             *
             * @maxItems 100
             */
            collectiveAgreements?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
          };
          /**
           * Options available in standard candidate confirmations.
           */
          options?: {
            /**
             * Whether candidate pay details include the holiday pay percentage.
             */
            showHolidayPayPercentage?: boolean;
            /**
             * Whether standard booking confirmations include an attendance button.
             */
            showStandardAttendanceButton?: boolean;
            /**
             * Whether booking confirmations include an acknowledgement button.
             */
            showAcknowledgementButton?: boolean;
          };
        };
        tutor?: {
          /**
           * Rows available in tutor candidate booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "studentName"
              | "role"
              | "teacherRegistrationNumber"
              | "start"
              | "end"
              | "duration"
              | "durationDetail"
              | "clientName"
              | "address"
              | "payRate"
              | "paymentType"
              | "arrivalTime"
              | "schoolContact"
              | "healthAndSafetyRisks"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
           */
          includeWrittenStatement?: boolean;
          /**
           * Whether the attached written statement of particulars includes a signature for this booking type.
           */
          includeWrittenStatementSignature?: boolean;
          /**
           * Written statement of particulars content attached to candidate confirmations for this booking type.
           */
          writtenStatement?: {
            /**
             * Written statement entries describing working hours.
             *
             * @maxItems 100
             */
            hoursWork?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing remuneration.
             *
             * @maxItems 100
             */
            renumeration?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing holiday entitlement.
             *
             * @maxItems 100
             */
            holidays?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing sickness arrangements.
             *
             * @maxItems 100
             */
            sickness?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing benefits.
             *
             * @maxItems 100
             */
            benefits?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing termination terms.
             *
             * @maxItems 100
             */
            termination?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing applicable collective agreements.
             *
             * @maxItems 100
             */
            collectiveAgreements?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
          };
          /**
           * Options available in tutor candidate confirmations.
           */
          options?: {
            /**
             * Whether candidate pay details include the holiday pay percentage.
             */
            showHolidayPayPercentage?: boolean;
            /**
             * Whether booking confirmations include an acknowledgement button.
             */
            showAcknowledgementButton?: boolean;
          };
        };
        interview?: {
          /**
           * Rows available in interview candidate booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "start"
              | "address"
              | "jobDescription"
              | "healthAndSafetyRisks"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
           */
          includeWrittenStatement?: boolean;
          /**
           * Whether the attached written statement of particulars includes a signature for this booking type.
           */
          includeWrittenStatementSignature?: boolean;
          /**
           * Written statement of particulars content attached to candidate confirmations for this booking type.
           */
          writtenStatement?: {
            /**
             * Written statement entries describing working hours.
             *
             * @maxItems 100
             */
            hoursWork?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing remuneration.
             *
             * @maxItems 100
             */
            renumeration?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing holiday entitlement.
             *
             * @maxItems 100
             */
            holidays?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing sickness arrangements.
             *
             * @maxItems 100
             */
            sickness?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing benefits.
             *
             * @maxItems 100
             */
            benefits?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing termination terms.
             *
             * @maxItems 100
             */
            termination?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing applicable collective agreements.
             *
             * @maxItems 100
             */
            collectiveAgreements?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
          };
        };
        trial?: {
          /**
           * Rows available in trial candidate booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "start"
              | "end"
              | "payRate"
              | "address"
              | "healthAndSafetyRisks"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
           */
          includeWrittenStatement?: boolean;
          /**
           * Whether the attached written statement of particulars includes a signature for this booking type.
           */
          includeWrittenStatementSignature?: boolean;
          /**
           * Written statement of particulars content attached to candidate confirmations for this booking type.
           */
          writtenStatement?: {
            /**
             * Written statement entries describing working hours.
             *
             * @maxItems 100
             */
            hoursWork?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing remuneration.
             *
             * @maxItems 100
             */
            renumeration?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing holiday entitlement.
             *
             * @maxItems 100
             */
            holidays?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing sickness arrangements.
             *
             * @maxItems 100
             */
            sickness?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing benefits.
             *
             * @maxItems 100
             */
            benefits?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing termination terms.
             *
             * @maxItems 100
             */
            termination?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing applicable collective agreements.
             *
             * @maxItems 100
             */
            collectiveAgreements?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
          };
          /**
           * Options available in trial candidate confirmations.
           */
          options?: {
            /**
             * Whether candidate pay details include the holiday pay percentage.
             */
            showHolidayPayPercentage?: boolean;
          };
        };
        permanent?: {
          /**
           * Rows available in permanent candidate booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "role"
              | "start"
              | "clientName"
              | "address"
              | "salary"
              | "arrivalTime"
              | "schoolContact"
              | "healthAndSafetyRisks"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
           */
          includeWrittenStatement?: boolean;
          /**
           * Whether the attached written statement of particulars includes a signature for this booking type.
           */
          includeWrittenStatementSignature?: boolean;
          /**
           * Written statement of particulars content attached to candidate confirmations for this booking type.
           */
          writtenStatement?: {
            /**
             * Written statement entries describing working hours.
             *
             * @maxItems 100
             */
            hoursWork?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing remuneration.
             *
             * @maxItems 100
             */
            renumeration?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing holiday entitlement.
             *
             * @maxItems 100
             */
            holidays?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing sickness arrangements.
             *
             * @maxItems 100
             */
            sickness?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing benefits.
             *
             * @maxItems 100
             */
            benefits?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing termination terms.
             *
             * @maxItems 100
             */
            termination?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing applicable collective agreements.
             *
             * @maxItems 100
             */
            collectiveAgreements?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
          };
        };
      };
      /**
       * Variant-specific display settings for client booking confirmations.
       */
      client?: {
        standard?: {
          /**
           * Rows available in standard client booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "dateOfBirth"
              | "clientName"
              | "invoiceContact"
              | "role"
              | "qualification"
              | "occupation"
              | "address"
              | "dbsNumber"
              | "dbsType"
              | "dbsCertificateIssueDate"
              | "dbsUpdateServiceNumber"
              | "dbsUpdateServiceChecked"
              | "barredListChecked"
              | "dbsComments"
              | "barredListCheckDate"
              | "prohibitionCheck"
              | "overseasCheck"
              | "section128Check"
              | "rightToWorkVerification"
              | "rightToWorkCheck"
              | "idCheck"
              | "teacherRegistrationNumber"
              | "ewcConfirmation"
              | "nationalInsuranceNumber"
              | "referenceCheck"
              | "safeguardingQualification"
              | "childcareDisqualificationDeclaration"
              | "fitnessToWork"
              | "readKcsie"
              | "safeInEducationCheck"
              | "anaphylaxisTraining"
              | "socialMediaCheck"
              | "googleCheckDate"
              | "start"
              | "end"
              | "duration"
              | "durationDetail"
              | "awrInformation"
              | "chargeRate"
              | "agencyFee"
              | "bookedBy"
              | "approvedBy"
              | "buyersName"
              | "buyersReference"
              | "relevantKnowledgeAndAppropriateSkills"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Options available in standard, tutor, trial, and permanent client confirmations.
           */
          options?: {
            /**
             * Whether qualification details include their uploaded dates.
             */
            showQualificationDates?: boolean;
            /**
             * Whether fitness-to-work details include the declaration date.
             */
            showFitnessToWorkDate?: boolean;
            /**
             * Whether reference details include the number of references received.
             */
            showReferenceCount?: boolean;
          };
        };
        tutor?: {
          /**
           * Rows available in tutor client booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "studentName"
              | "dateOfBirth"
              | "clientName"
              | "invoiceContact"
              | "role"
              | "qualification"
              | "occupation"
              | "address"
              | "dbsNumber"
              | "dbsType"
              | "dbsCertificateIssueDate"
              | "dbsUpdateServiceNumber"
              | "dbsUpdateServiceChecked"
              | "barredListChecked"
              | "dbsComments"
              | "barredListCheckDate"
              | "prohibitionCheck"
              | "overseasCheck"
              | "section128Check"
              | "rightToWorkVerification"
              | "rightToWorkCheck"
              | "idCheck"
              | "teacherRegistrationNumber"
              | "ewcConfirmation"
              | "nationalInsuranceNumber"
              | "referenceCheck"
              | "safeguardingQualification"
              | "childcareDisqualificationDeclaration"
              | "fitnessToWork"
              | "readKcsie"
              | "safeInEducationCheck"
              | "socialMediaCheck"
              | "googleCheckDate"
              | "start"
              | "end"
              | "duration"
              | "durationDetail"
              | "awrInformation"
              | "chargeRate"
              | "agencyFee"
              | "bookedBy"
              | "approvedBy"
              | "buyersName"
              | "buyersReference"
              | "relevantKnowledgeAndAppropriateSkills"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Options available in standard, tutor, trial, and permanent client confirmations.
           */
          options?: {
            /**
             * Whether qualification details include their uploaded dates.
             */
            showQualificationDates?: boolean;
            /**
             * Whether fitness-to-work details include the declaration date.
             */
            showFitnessToWorkDate?: boolean;
            /**
             * Whether reference details include the number of references received.
             */
            showReferenceCount?: boolean;
          };
        };
        interview?: {
          /**
           * Rows available in interview client booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "clientName"
              | "invoiceContact"
              | "role"
              | "anaphylaxisTraining"
              | "start"
              | "chargeRate"
              | "agencyFee"
              | "buyersName"
              | "buyersReference"
              | "relevantKnowledgeAndAppropriateSkills"
              | "additionalInstructions"
              | "address";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
        };
        trial?: {
          /**
           * Rows available in trial client booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "dateOfBirth"
              | "clientName"
              | "invoiceContact"
              | "role"
              | "qualification"
              | "occupation"
              | "address"
              | "dbsNumber"
              | "dbsType"
              | "dbsCertificateIssueDate"
              | "dbsUpdateServiceNumber"
              | "dbsUpdateServiceChecked"
              | "barredListChecked"
              | "dbsComments"
              | "barredListCheckDate"
              | "prohibitionCheck"
              | "overseasCheck"
              | "section128Check"
              | "rightToWorkVerification"
              | "rightToWorkCheck"
              | "idCheck"
              | "teacherRegistrationNumber"
              | "ewcConfirmation"
              | "nationalInsuranceNumber"
              | "referenceCheck"
              | "safeguardingQualification"
              | "childcareDisqualificationDeclaration"
              | "fitnessToWork"
              | "readKcsie"
              | "safeInEducationCheck"
              | "anaphylaxisTraining"
              | "socialMediaCheck"
              | "googleCheckDate"
              | "start"
              | "end"
              | "chargeRate"
              | "agencyFee"
              | "buyersName"
              | "buyersReference"
              | "relevantKnowledgeAndAppropriateSkills"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Options available in standard, tutor, trial, and permanent client confirmations.
           */
          options?: {
            /**
             * Whether qualification details include their uploaded dates.
             */
            showQualificationDates?: boolean;
            /**
             * Whether fitness-to-work details include the declaration date.
             */
            showFitnessToWorkDate?: boolean;
            /**
             * Whether reference details include the number of references received.
             */
            showReferenceCount?: boolean;
          };
        };
        permanent?: {
          /**
           * Rows available in permanent client booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "dateOfBirth"
              | "clientName"
              | "invoiceContact"
              | "role"
              | "qualification"
              | "occupation"
              | "address"
              | "dbsNumber"
              | "dbsType"
              | "dbsCertificateIssueDate"
              | "dbsUpdateServiceNumber"
              | "dbsUpdateServiceChecked"
              | "barredListChecked"
              | "dbsComments"
              | "barredListCheckDate"
              | "prohibitionCheck"
              | "overseasCheck"
              | "section128Check"
              | "rightToWorkVerification"
              | "rightToWorkCheck"
              | "teacherRegistrationNumber"
              | "ewcConfirmation"
              | "nationalInsuranceNumber"
              | "referenceCheck"
              | "safeguardingQualification"
              | "childcareDisqualificationDeclaration"
              | "fitnessToWork"
              | "readKcsie"
              | "safeInEducationCheck"
              | "anaphylaxisTraining"
              | "socialMediaCheck"
              | "googleCheckDate"
              | "start"
              | "chargeRate"
              | "agencyFee"
              | "buyersName"
              | "buyersReference"
              | "relevantKnowledgeAndAppropriateSkills"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Options available in standard, tutor, trial, and permanent client confirmations.
           */
          options?: {
            /**
             * Whether qualification details include their uploaded dates.
             */
            showQualificationDates?: boolean;
            /**
             * Whether fitness-to-work details include the declaration date.
             */
            showFitnessToWorkDate?: boolean;
            /**
             * Whether reference details include the number of references received.
             */
            showReferenceCount?: boolean;
          };
        };
      };
    };
    /**
     * Brand job feed background color (hex)
     *
     * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
     */
    jobFeedBackgroundColor?: string;
    /**
     * Feature-specific configuration
     */
    modules?: {
      tasks?: {
        /**
         * Whether the Tasks module is enabled for this company
         */
        enabled?: boolean;
      };
      forms?: {
        /**
         * Whether the Forms module is enabled for this company
         */
        enabled?: boolean;
      };
    };
    /**
     * Third-party integration configuration for this company
     */
    integrations?: {
      /**
       * eBulkPlus organisation references configured for this company
       */
      matrixOrganisationReferences?:
        | {
            /**
             * References must be 1-30 characters long and contain only letters, numbers, and underscores
             *
             * @pattern ^\w{1,30}$
             */
            reference?: string;
            /**
             * Human-readable label for this reference
             */
            displayName?: string;
            /**
             * Whether this is the default reference
             */
            isDefault?: boolean;
          }[]
        | null;
      cvLibrary?: {
        /**
         * CV Library API integration username
         */
        username?: string;
        /**
         * Whether CV Library credentials have been configured
         */
        enabled?: boolean;
      };
      quba?: {
        /**
         * The Quba data sync API key
         */
        key?: string;
        /**
         * ISO 8601 date string when the key expires
         */
        expiry?: string;
      };
      devyce?: {
        /**
         * Whether the Devyce integration is enabled for the company
         */
        enabled?: boolean;
        /**
         * Whether a Devyce API key is configured
         */
        apiKeySet?: boolean;
        /**
         * Configured Devyce organisation identifier
         */
        organisationId?: string;
        /**
         * Timestamp of the last successful Devyce polling run
         *
         * @format date-time
         */
        lastSuccessfulPollAt?: string;
      };
      evertime?: {
        /**
         * Whether the Evertime integration is enabled for the company
         */
        enabled?: boolean;
      };
      timesheetPortal?: {
        /**
         * The company's Timesheet Portal API subdomain
         *
         * @minLength 1
         * @maxLength 63
         * @pattern ^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i
         */
        subdomain?: string;
        /**
         * Whether the Timesheet Portal integration is enabled for the company
         */
        enabled?: boolean;
      };
      xero?: {
        /**
         * User associated with this Xero connection
         *
         * @pattern ^[0-9a-f]{24}$
         */
        user?: string;
        /**
         * Tenant information associated with this Xero connection
         */
        tenant?: {
          /**
           * UTC timestamp of when this tenant record was created
           */
          createdDateUtc?: string;
          /**
           * UTC timestamp of when this tenant record was last updated
           */
          updateDateUtc?: string;
        };
      };
    };
    /**
     * Company-level marketing configuration. Expand with expand[]=marketingSettings to include in the response.
     */
    marketingSettings?: {
      /**
       * Whether the company domain has been verified. When true, campaigns can be sent from the company's own domain.
       */
      verifiedDomain?: boolean;
      /**
       * The company's verified sending domain
       */
      domain?: string;
      /**
       * The email address prefix used when sending campaigns
       */
      sendPrefix?: string;
      /**
       * The company marketing email signature stored as a serialised Unlayer editor design JSON string
       */
      signature?: string | null;
    };
    /**
     * Company-level configuration of GCA lot options and the default lot applied to new GCA bookings
     */
    gcaLotConfig?: {
      /**
       * The GCA lot options available to this company
       *
       * @maxItems 50
       */
      lots?: {
        /**
         * The unique identifier of the GCA lot option
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The name of the GCA lot, e.g. 'Lot 2 – Master Vendor'
         *
         * @minLength 1
         * @maxLength 256
         */
        label?: string;
        /**
         * The service description automatically applied to GCA bookings using this lot
         *
         * @minLength 1
         * @maxLength 256
         */
        serviceDescription?: string;
      }[];
      /**
       * The ID of the lot option automatically applied to new GCA bookings
       *
       * @pattern ^[0-9a-f]{24}$
       */
      defaultLotId?: string | null;
    };
    /**
     * Financial values used by calculated payment-run export columns
     */
    paymentRunExportSettings?: {
      /**
       * @minimum 1
       * @maximum 12
       */
      fiscalYearStartMonth?: number | null;
      /**
       * @minimum 0
       * @maximum 100
       */
      vatPercent?: number | null;
    };
  };
};

export type GetCompanyVariables = {
  queryParams?: GetCompanyQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetCompany = (
  variables: GetCompanyVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetCompanyResponse,
    GetCompanyError,
    undefined,
    {},
    GetCompanyQueryParams,
    {}
  >({ url: "/company", method: "get", ...variables, signal });

export function getCompanyQuery(variables: GetCompanyVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetCompanyResponse>;
};

export function getCompanyQuery(
  variables: GetCompanyVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetCompanyResponse>)
    | reactQuery.SkipToken;
};

export function getCompanyQuery(
  variables: GetCompanyVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/company",
      operationId: "getCompany",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetCompany(variables, signal),
  };
}

export const useSuspenseGetCompany = <TData = GetCompanyResponse,>(
  variables: GetCompanyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetCompanyResponse, GetCompanyError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetCompanyResponse,
    GetCompanyError,
    TData
  >({
    ...getCompanyQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetCompany = <TData = GetCompanyResponse,>(
  variables: GetCompanyVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetCompanyResponse, GetCompanyError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetCompanyResponse, GetCompanyError, TData>({
    ...getCompanyQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateCompanyError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateCompanyResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the company
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The company name displayed to users and in company-branded communications.
     *
     * @maxLength 256
     */
    name?: string;
    /**
     * URL of the company logo
     *
     * @maxLength 2048
     */
    logo?: string;
    /**
     * Primary contact email for the company
     *
     * @maxLength 320
     * @format email
     */
    contactEmail?: string;
    /**
     * Primary contact telephone for the company
     *
     * @maxLength 50
     */
    contactTelephone?: string;
    /**
     * Company website URL
     *
     * @maxLength 2048
     */
    contactWebsite?: string;
    /**
     * The company address
     *
     * @maxLength 512
     */
    address?: string;
    /**
     * Registered company number
     *
     * @maxLength 128
     */
    companyNumber?: string;
    /**
     * Brand foreground color (hex)
     *
     * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
     */
    customerForegroundColor?: string;
    /**
     * Brand background color (hex)
     *
     * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
     */
    customerBackgroundColor?: string;
    /**
     * Brand accent color (hex)
     *
     * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
     */
    customerAccentColor?: string;
    /**
     * Custom tuition report questions per booking subtype. If not set, the system defaults are used.
     */
    tuitionReportQuestions?: {
      TutorStandard?: {
        /**
         * @maxItems 100
         */
        questions?: {
          /**
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * @minLength 1
           * @maxLength 128
           */
          key?: string;
          /**
           * @minLength 1
           * @maxLength 500
           */
          label?: string;
          /**
           * @maxLength 2000
           */
          description?: string;
          type?:
            | "checkbox"
            | "date"
            | "daterange"
            | "dropdown"
            | "number"
            | "signature"
            | "text"
            | "textarea";
          required?: boolean;
          enabled?: boolean;
          /**
           * @minimum 0
           * @maximum 1000
           */
          order?: number;
          condition?: {
            mode?: "any" | "all";
            /**
             * @minItems 1
             * @maxItems 20
             */
            rules?: {
              /**
               * The stable key of the question that controls visibility.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * How the controlling answer is compared with the value.
               */
              operator?: "equals" | "notEquals";
              /**
               * The answer value used to determine visibility.
               */
              value?: string | boolean | number | (void | null);
            }[];
          };
          /**
           * @minItems 1
           * @maxItems 100
           */
          options?: {
            /**
             * The value stored when this option is selected.
             *
             * @minLength 1
             * @maxLength 200
             */
            value?: string;
            /**
             * The option displayed to the person answering the question.
             *
             * @minLength 1
             * @maxLength 200
             */
            label?: string;
          }[];
          ["default"]?: boolean;
          audience?: "candidate" | "staff";
          fixedRole?: "fullName" | "signature";
        }[];
        required?: boolean;
      };
      TutorMentor?: {
        /**
         * @maxItems 100
         */
        questions?: {
          /**
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * @minLength 1
           * @maxLength 128
           */
          key?: string;
          /**
           * @minLength 1
           * @maxLength 500
           */
          label?: string;
          /**
           * @maxLength 2000
           */
          description?: string;
          type?:
            | "checkbox"
            | "date"
            | "daterange"
            | "dropdown"
            | "number"
            | "signature"
            | "text"
            | "textarea";
          required?: boolean;
          enabled?: boolean;
          /**
           * @minimum 0
           * @maximum 1000
           */
          order?: number;
          condition?: {
            mode?: "any" | "all";
            /**
             * @minItems 1
             * @maxItems 20
             */
            rules?: {
              /**
               * The stable key of the question that controls visibility.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * How the controlling answer is compared with the value.
               */
              operator?: "equals" | "notEquals";
              /**
               * The answer value used to determine visibility.
               */
              value?: string | boolean | number | (void | null);
            }[];
          };
          /**
           * @minItems 1
           * @maxItems 100
           */
          options?: {
            /**
             * The value stored when this option is selected.
             *
             * @minLength 1
             * @maxLength 200
             */
            value?: string;
            /**
             * The option displayed to the person answering the question.
             *
             * @minLength 1
             * @maxLength 200
             */
            label?: string;
          }[];
          ["default"]?: boolean;
          audience?: "candidate" | "staff";
          fixedRole?: "fullName" | "signature";
        }[];
        required?: boolean;
      };
      TutorBasic?: {
        /**
         * @maxItems 100
         */
        questions?: {
          /**
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * @minLength 1
           * @maxLength 128
           */
          key?: string;
          /**
           * @minLength 1
           * @maxLength 500
           */
          label?: string;
          /**
           * @maxLength 2000
           */
          description?: string;
          type?:
            | "checkbox"
            | "date"
            | "daterange"
            | "dropdown"
            | "number"
            | "signature"
            | "text"
            | "textarea";
          required?: boolean;
          enabled?: boolean;
          /**
           * @minimum 0
           * @maximum 1000
           */
          order?: number;
          condition?: {
            mode?: "any" | "all";
            /**
             * @minItems 1
             * @maxItems 20
             */
            rules?: {
              /**
               * The stable key of the question that controls visibility.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * How the controlling answer is compared with the value.
               */
              operator?: "equals" | "notEquals";
              /**
               * The answer value used to determine visibility.
               */
              value?: string | boolean | number | (void | null);
            }[];
          };
          /**
           * @minItems 1
           * @maxItems 100
           */
          options?: {
            /**
             * The value stored when this option is selected.
             *
             * @minLength 1
             * @maxLength 200
             */
            value?: string;
            /**
             * The option displayed to the person answering the question.
             *
             * @minLength 1
             * @maxLength 200
             */
            label?: string;
          }[];
          ["default"]?: boolean;
          audience?: "candidate" | "staff";
          fixedRole?: "fullName" | "signature";
        }[];
        required?: boolean;
      };
    };
    /**
     * The questions presented in new health declarations. If not set, the standard questions are used.
     */
    healthDeclarationConfig?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * When this question configuration was last updated.
       *
       * @format date-time
       */
      updatedAt?: string;
    };
    /**
     * The questions presented in new Criminal Record & Security Checks declarations. If not set, the standard questions are used.
     */
    criminalRecordConfig?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * When this question configuration was last updated.
       *
       * @format date-time
       */
      updatedAt?: string;
    };
    /**
     * Table row visibility, titles, and related display options for booking confirmations.
     */
    bookingConfirmationConfig?: {
      /**
       * Variant-specific display settings for candidate booking confirmations.
       */
      candidate?: {
        standard?: {
          /**
           * Rows available in standard candidate booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "role"
              | "teacherRegistrationNumber"
              | "start"
              | "end"
              | "duration"
              | "durationDetail"
              | "clientName"
              | "address"
              | "payRate"
              | "paymentType"
              | "arrivalTime"
              | "schoolContact"
              | "healthAndSafetyRisks"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
           */
          includeWrittenStatement?: boolean;
          /**
           * Whether the attached written statement of particulars includes a signature for this booking type.
           */
          includeWrittenStatementSignature?: boolean;
          /**
           * Written statement of particulars content attached to candidate confirmations for this booking type.
           */
          writtenStatement?: {
            /**
             * Written statement entries describing working hours.
             *
             * @maxItems 100
             */
            hoursWork?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing remuneration.
             *
             * @maxItems 100
             */
            renumeration?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing holiday entitlement.
             *
             * @maxItems 100
             */
            holidays?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing sickness arrangements.
             *
             * @maxItems 100
             */
            sickness?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing benefits.
             *
             * @maxItems 100
             */
            benefits?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing termination terms.
             *
             * @maxItems 100
             */
            termination?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing applicable collective agreements.
             *
             * @maxItems 100
             */
            collectiveAgreements?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
          };
          /**
           * Options available in standard candidate confirmations.
           */
          options?: {
            /**
             * Whether candidate pay details include the holiday pay percentage.
             */
            showHolidayPayPercentage?: boolean;
            /**
             * Whether standard booking confirmations include an attendance button.
             */
            showStandardAttendanceButton?: boolean;
            /**
             * Whether booking confirmations include an acknowledgement button.
             */
            showAcknowledgementButton?: boolean;
          };
        };
        tutor?: {
          /**
           * Rows available in tutor candidate booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "studentName"
              | "role"
              | "teacherRegistrationNumber"
              | "start"
              | "end"
              | "duration"
              | "durationDetail"
              | "clientName"
              | "address"
              | "payRate"
              | "paymentType"
              | "arrivalTime"
              | "schoolContact"
              | "healthAndSafetyRisks"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
           */
          includeWrittenStatement?: boolean;
          /**
           * Whether the attached written statement of particulars includes a signature for this booking type.
           */
          includeWrittenStatementSignature?: boolean;
          /**
           * Written statement of particulars content attached to candidate confirmations for this booking type.
           */
          writtenStatement?: {
            /**
             * Written statement entries describing working hours.
             *
             * @maxItems 100
             */
            hoursWork?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing remuneration.
             *
             * @maxItems 100
             */
            renumeration?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing holiday entitlement.
             *
             * @maxItems 100
             */
            holidays?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing sickness arrangements.
             *
             * @maxItems 100
             */
            sickness?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing benefits.
             *
             * @maxItems 100
             */
            benefits?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing termination terms.
             *
             * @maxItems 100
             */
            termination?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing applicable collective agreements.
             *
             * @maxItems 100
             */
            collectiveAgreements?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
          };
          /**
           * Options available in tutor candidate confirmations.
           */
          options?: {
            /**
             * Whether candidate pay details include the holiday pay percentage.
             */
            showHolidayPayPercentage?: boolean;
            /**
             * Whether booking confirmations include an acknowledgement button.
             */
            showAcknowledgementButton?: boolean;
          };
        };
        interview?: {
          /**
           * Rows available in interview candidate booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "start"
              | "address"
              | "jobDescription"
              | "healthAndSafetyRisks"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
           */
          includeWrittenStatement?: boolean;
          /**
           * Whether the attached written statement of particulars includes a signature for this booking type.
           */
          includeWrittenStatementSignature?: boolean;
          /**
           * Written statement of particulars content attached to candidate confirmations for this booking type.
           */
          writtenStatement?: {
            /**
             * Written statement entries describing working hours.
             *
             * @maxItems 100
             */
            hoursWork?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing remuneration.
             *
             * @maxItems 100
             */
            renumeration?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing holiday entitlement.
             *
             * @maxItems 100
             */
            holidays?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing sickness arrangements.
             *
             * @maxItems 100
             */
            sickness?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing benefits.
             *
             * @maxItems 100
             */
            benefits?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing termination terms.
             *
             * @maxItems 100
             */
            termination?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing applicable collective agreements.
             *
             * @maxItems 100
             */
            collectiveAgreements?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
          };
        };
        trial?: {
          /**
           * Rows available in trial candidate booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "start"
              | "end"
              | "payRate"
              | "address"
              | "healthAndSafetyRisks"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
           */
          includeWrittenStatement?: boolean;
          /**
           * Whether the attached written statement of particulars includes a signature for this booking type.
           */
          includeWrittenStatementSignature?: boolean;
          /**
           * Written statement of particulars content attached to candidate confirmations for this booking type.
           */
          writtenStatement?: {
            /**
             * Written statement entries describing working hours.
             *
             * @maxItems 100
             */
            hoursWork?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing remuneration.
             *
             * @maxItems 100
             */
            renumeration?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing holiday entitlement.
             *
             * @maxItems 100
             */
            holidays?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing sickness arrangements.
             *
             * @maxItems 100
             */
            sickness?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing benefits.
             *
             * @maxItems 100
             */
            benefits?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing termination terms.
             *
             * @maxItems 100
             */
            termination?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing applicable collective agreements.
             *
             * @maxItems 100
             */
            collectiveAgreements?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
          };
          /**
           * Options available in trial candidate confirmations.
           */
          options?: {
            /**
             * Whether candidate pay details include the holiday pay percentage.
             */
            showHolidayPayPercentage?: boolean;
          };
        };
        permanent?: {
          /**
           * Rows available in permanent candidate booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "role"
              | "start"
              | "clientName"
              | "address"
              | "salary"
              | "arrivalTime"
              | "schoolContact"
              | "healthAndSafetyRisks"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
           */
          includeWrittenStatement?: boolean;
          /**
           * Whether the attached written statement of particulars includes a signature for this booking type.
           */
          includeWrittenStatementSignature?: boolean;
          /**
           * Written statement of particulars content attached to candidate confirmations for this booking type.
           */
          writtenStatement?: {
            /**
             * Written statement entries describing working hours.
             *
             * @maxItems 100
             */
            hoursWork?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing remuneration.
             *
             * @maxItems 100
             */
            renumeration?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing holiday entitlement.
             *
             * @maxItems 100
             */
            holidays?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing sickness arrangements.
             *
             * @maxItems 100
             */
            sickness?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing benefits.
             *
             * @maxItems 100
             */
            benefits?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing termination terms.
             *
             * @maxItems 100
             */
            termination?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
            /**
             * Written statement entries describing applicable collective agreements.
             *
             * @maxItems 100
             */
            collectiveAgreements?:
              | {
                  /**
                   * Content included in this written statement section.
                   *
                   * @maxLength 20000
                   */
                  value?: string;
                }[]
              | null;
          };
        };
      };
      /**
       * Variant-specific display settings for client booking confirmations.
       */
      client?: {
        standard?: {
          /**
           * Rows available in standard client booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "dateOfBirth"
              | "clientName"
              | "invoiceContact"
              | "role"
              | "qualification"
              | "occupation"
              | "address"
              | "dbsNumber"
              | "dbsType"
              | "dbsCertificateIssueDate"
              | "dbsUpdateServiceNumber"
              | "dbsUpdateServiceChecked"
              | "barredListChecked"
              | "dbsComments"
              | "barredListCheckDate"
              | "prohibitionCheck"
              | "overseasCheck"
              | "section128Check"
              | "rightToWorkVerification"
              | "rightToWorkCheck"
              | "idCheck"
              | "teacherRegistrationNumber"
              | "ewcConfirmation"
              | "nationalInsuranceNumber"
              | "referenceCheck"
              | "safeguardingQualification"
              | "childcareDisqualificationDeclaration"
              | "fitnessToWork"
              | "readKcsie"
              | "safeInEducationCheck"
              | "anaphylaxisTraining"
              | "socialMediaCheck"
              | "googleCheckDate"
              | "start"
              | "end"
              | "duration"
              | "durationDetail"
              | "awrInformation"
              | "chargeRate"
              | "agencyFee"
              | "bookedBy"
              | "approvedBy"
              | "buyersName"
              | "buyersReference"
              | "relevantKnowledgeAndAppropriateSkills"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Options available in standard, tutor, trial, and permanent client confirmations.
           */
          options?: {
            /**
             * Whether qualification details include their uploaded dates.
             */
            showQualificationDates?: boolean;
            /**
             * Whether fitness-to-work details include the declaration date.
             */
            showFitnessToWorkDate?: boolean;
            /**
             * Whether reference details include the number of references received.
             */
            showReferenceCount?: boolean;
          };
        };
        tutor?: {
          /**
           * Rows available in tutor client booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "studentName"
              | "dateOfBirth"
              | "clientName"
              | "invoiceContact"
              | "role"
              | "qualification"
              | "occupation"
              | "address"
              | "dbsNumber"
              | "dbsType"
              | "dbsCertificateIssueDate"
              | "dbsUpdateServiceNumber"
              | "dbsUpdateServiceChecked"
              | "barredListChecked"
              | "dbsComments"
              | "barredListCheckDate"
              | "prohibitionCheck"
              | "overseasCheck"
              | "section128Check"
              | "rightToWorkVerification"
              | "rightToWorkCheck"
              | "idCheck"
              | "teacherRegistrationNumber"
              | "ewcConfirmation"
              | "nationalInsuranceNumber"
              | "referenceCheck"
              | "safeguardingQualification"
              | "childcareDisqualificationDeclaration"
              | "fitnessToWork"
              | "readKcsie"
              | "safeInEducationCheck"
              | "socialMediaCheck"
              | "googleCheckDate"
              | "start"
              | "end"
              | "duration"
              | "durationDetail"
              | "awrInformation"
              | "chargeRate"
              | "agencyFee"
              | "bookedBy"
              | "approvedBy"
              | "buyersName"
              | "buyersReference"
              | "relevantKnowledgeAndAppropriateSkills"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Options available in standard, tutor, trial, and permanent client confirmations.
           */
          options?: {
            /**
             * Whether qualification details include their uploaded dates.
             */
            showQualificationDates?: boolean;
            /**
             * Whether fitness-to-work details include the declaration date.
             */
            showFitnessToWorkDate?: boolean;
            /**
             * Whether reference details include the number of references received.
             */
            showReferenceCount?: boolean;
          };
        };
        interview?: {
          /**
           * Rows available in interview client booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "clientName"
              | "invoiceContact"
              | "role"
              | "anaphylaxisTraining"
              | "start"
              | "chargeRate"
              | "agencyFee"
              | "buyersName"
              | "buyersReference"
              | "relevantKnowledgeAndAppropriateSkills"
              | "additionalInstructions"
              | "address";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
        };
        trial?: {
          /**
           * Rows available in trial client booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "dateOfBirth"
              | "clientName"
              | "invoiceContact"
              | "role"
              | "qualification"
              | "occupation"
              | "address"
              | "dbsNumber"
              | "dbsType"
              | "dbsCertificateIssueDate"
              | "dbsUpdateServiceNumber"
              | "dbsUpdateServiceChecked"
              | "barredListChecked"
              | "dbsComments"
              | "barredListCheckDate"
              | "prohibitionCheck"
              | "overseasCheck"
              | "section128Check"
              | "rightToWorkVerification"
              | "rightToWorkCheck"
              | "idCheck"
              | "teacherRegistrationNumber"
              | "ewcConfirmation"
              | "nationalInsuranceNumber"
              | "referenceCheck"
              | "safeguardingQualification"
              | "childcareDisqualificationDeclaration"
              | "fitnessToWork"
              | "readKcsie"
              | "safeInEducationCheck"
              | "anaphylaxisTraining"
              | "socialMediaCheck"
              | "googleCheckDate"
              | "start"
              | "end"
              | "chargeRate"
              | "agencyFee"
              | "buyersName"
              | "buyersReference"
              | "relevantKnowledgeAndAppropriateSkills"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Options available in standard, tutor, trial, and permanent client confirmations.
           */
          options?: {
            /**
             * Whether qualification details include their uploaded dates.
             */
            showQualificationDates?: boolean;
            /**
             * Whether fitness-to-work details include the declaration date.
             */
            showFitnessToWorkDate?: boolean;
            /**
             * Whether reference details include the number of references received.
             */
            showReferenceCount?: boolean;
          };
        };
        permanent?: {
          /**
           * Rows available in permanent client booking confirmations.
           */
          rows?: {
            /**
             * Whether this row is included when it has a value.
             */
            isEnabled?: boolean;
            /**
             * A custom title for the first column. When omitted, the standard title is used.
             *
             * @maxLength 256
             */
            title?: string;
            /**
             * The row represented by this configuration.
             */
            key?:
              | "candidateName"
              | "dateOfBirth"
              | "clientName"
              | "invoiceContact"
              | "role"
              | "qualification"
              | "occupation"
              | "address"
              | "dbsNumber"
              | "dbsType"
              | "dbsCertificateIssueDate"
              | "dbsUpdateServiceNumber"
              | "dbsUpdateServiceChecked"
              | "barredListChecked"
              | "dbsComments"
              | "barredListCheckDate"
              | "prohibitionCheck"
              | "overseasCheck"
              | "section128Check"
              | "rightToWorkVerification"
              | "rightToWorkCheck"
              | "teacherRegistrationNumber"
              | "ewcConfirmation"
              | "nationalInsuranceNumber"
              | "referenceCheck"
              | "safeguardingQualification"
              | "childcareDisqualificationDeclaration"
              | "fitnessToWork"
              | "readKcsie"
              | "safeInEducationCheck"
              | "anaphylaxisTraining"
              | "socialMediaCheck"
              | "googleCheckDate"
              | "start"
              | "chargeRate"
              | "agencyFee"
              | "buyersName"
              | "buyersReference"
              | "relevantKnowledgeAndAppropriateSkills"
              | "additionalInstructions";
          }[];
          /**
           * Custom content shown after the booking confirmation details for this audience and booking type.
           *
           * @maxLength 20000
           */
          confirmationText?: string;
          /**
           * Options available in standard, tutor, trial, and permanent client confirmations.
           */
          options?: {
            /**
             * Whether qualification details include their uploaded dates.
             */
            showQualificationDates?: boolean;
            /**
             * Whether fitness-to-work details include the declaration date.
             */
            showFitnessToWorkDate?: boolean;
            /**
             * Whether reference details include the number of references received.
             */
            showReferenceCount?: boolean;
          };
        };
      };
    };
    /**
     * Brand job feed background color (hex)
     *
     * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
     */
    jobFeedBackgroundColor?: string;
    /**
     * Feature-specific configuration
     */
    modules?: {
      tasks?: {
        /**
         * Whether the Tasks module is enabled for this company
         */
        enabled?: boolean;
      };
      forms?: {
        /**
         * Whether the Forms module is enabled for this company
         */
        enabled?: boolean;
      };
    };
    /**
     * Third-party integration configuration for this company
     */
    integrations?: {
      /**
       * eBulkPlus organisation references configured for this company
       */
      matrixOrganisationReferences?:
        | {
            /**
             * References must be 1-30 characters long and contain only letters, numbers, and underscores
             *
             * @pattern ^\w{1,30}$
             */
            reference?: string;
            /**
             * Human-readable label for this reference
             */
            displayName?: string;
            /**
             * Whether this is the default reference
             */
            isDefault?: boolean;
          }[]
        | null;
      cvLibrary?: {
        /**
         * CV Library API integration username
         */
        username?: string;
        /**
         * Whether CV Library credentials have been configured
         */
        enabled?: boolean;
      };
      quba?: {
        /**
         * The Quba data sync API key
         */
        key?: string;
        /**
         * ISO 8601 date string when the key expires
         */
        expiry?: string;
      };
      devyce?: {
        /**
         * Whether the Devyce integration is enabled for the company
         */
        enabled?: boolean;
        /**
         * Whether a Devyce API key is configured
         */
        apiKeySet?: boolean;
        /**
         * Configured Devyce organisation identifier
         */
        organisationId?: string;
        /**
         * Timestamp of the last successful Devyce polling run
         *
         * @format date-time
         */
        lastSuccessfulPollAt?: string;
      };
      evertime?: {
        /**
         * Whether the Evertime integration is enabled for the company
         */
        enabled?: boolean;
      };
      timesheetPortal?: {
        /**
         * The company's Timesheet Portal API subdomain
         *
         * @minLength 1
         * @maxLength 63
         * @pattern ^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i
         */
        subdomain?: string;
        /**
         * Whether the Timesheet Portal integration is enabled for the company
         */
        enabled?: boolean;
      };
      xero?: {
        /**
         * User associated with this Xero connection
         *
         * @pattern ^[0-9a-f]{24}$
         */
        user?: string;
        /**
         * Tenant information associated with this Xero connection
         */
        tenant?: {
          /**
           * UTC timestamp of when this tenant record was created
           */
          createdDateUtc?: string;
          /**
           * UTC timestamp of when this tenant record was last updated
           */
          updateDateUtc?: string;
        };
      };
    };
    /**
     * Company-level marketing configuration. Expand with expand[]=marketingSettings to include in the response.
     */
    marketingSettings?: {
      /**
       * Whether the company domain has been verified. When true, campaigns can be sent from the company's own domain.
       */
      verifiedDomain?: boolean;
      /**
       * The company's verified sending domain
       */
      domain?: string;
      /**
       * The email address prefix used when sending campaigns
       */
      sendPrefix?: string;
      /**
       * The company marketing email signature stored as a serialised Unlayer editor design JSON string
       */
      signature?: string | null;
    };
    /**
     * Company-level configuration of GCA lot options and the default lot applied to new GCA bookings
     */
    gcaLotConfig?: {
      /**
       * The GCA lot options available to this company
       *
       * @maxItems 50
       */
      lots?: {
        /**
         * The unique identifier of the GCA lot option
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The name of the GCA lot, e.g. 'Lot 2 – Master Vendor'
         *
         * @minLength 1
         * @maxLength 256
         */
        label?: string;
        /**
         * The service description automatically applied to GCA bookings using this lot
         *
         * @minLength 1
         * @maxLength 256
         */
        serviceDescription?: string;
      }[];
      /**
       * The ID of the lot option automatically applied to new GCA bookings
       *
       * @pattern ^[0-9a-f]{24}$
       */
      defaultLotId?: string | null;
    };
    /**
     * Financial values used by calculated payment-run export columns
     */
    paymentRunExportSettings?: {
      /**
       * @minimum 1
       * @maximum 12
       */
      fiscalYearStartMonth?: number | null;
      /**
       * @minimum 0
       * @maximum 100
       */
      vatPercent?: number | null;
    };
  };
};

export type UpdateCompanyRequestBody = {
  /**
   * The company name displayed to users and in company-branded communications.
   *
   * @maxLength 256
   */
  name?: string;
  /**
   * URL of the company logo
   *
   * @maxLength 2048
   */
  logo?: string;
  /**
   * Primary contact email for the company
   *
   * @maxLength 320
   * @format email
   */
  contactEmail?: string;
  /**
   * Primary contact telephone for the company
   *
   * @maxLength 50
   */
  contactTelephone?: string;
  /**
   * Company website URL
   *
   * @maxLength 2048
   */
  contactWebsite?: string;
  /**
   * The company address
   *
   * @maxLength 512
   */
  address?: string;
  /**
   * Registered company number
   *
   * @maxLength 128
   */
  companyNumber?: string;
  /**
   * Brand foreground color (hex)
   *
   * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
   */
  customerForegroundColor?: string;
  /**
   * Brand background color (hex)
   *
   * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
   */
  customerBackgroundColor?: string;
  /**
   * Brand accent color (hex)
   *
   * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
   */
  customerAccentColor?: string;
  /**
   * Brand job feed background color (hex)
   *
   * @pattern ^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$
   */
  jobFeedBackgroundColor?: string;
  /**
   * Feature-specific configuration
   */
  modules?: {
    tasks?: {
      /**
       * Whether the Tasks module is enabled for this company
       */
      enabled?: boolean;
    };
    forms?: {
      /**
       * Whether the Forms module is enabled for this company
       */
      enabled?: boolean;
    };
  };
  /**
   * Table row visibility, titles, and related display options for booking confirmations.
   */
  bookingConfirmationConfig?: {
    /**
     * Variant-specific display settings for candidate booking confirmations.
     */
    candidate: {
      standard: {
        /**
         * Rows available in standard candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "role"
            | "teacherRegistrationNumber"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "clientName"
            | "address"
            | "payRate"
            | "paymentType"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in standard candidate confirmations.
         */
        options: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage: boolean;
          /**
           * Whether standard booking confirmations include an attendance button.
           */
          showStandardAttendanceButton: boolean;
          /**
           * Whether booking confirmations include an acknowledgement button.
           */
          showAcknowledgementButton: boolean;
        };
      };
      tutor: {
        /**
         * Rows available in tutor candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "studentName"
            | "role"
            | "teacherRegistrationNumber"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "clientName"
            | "address"
            | "payRate"
            | "paymentType"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in tutor candidate confirmations.
         */
        options: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage: boolean;
          /**
           * Whether booking confirmations include an acknowledgement button.
           */
          showAcknowledgementButton: boolean;
        };
      };
      interview: {
        /**
         * Rows available in interview candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "start"
            | "address"
            | "jobDescription"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
      };
      trial: {
        /**
         * Rows available in trial candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "start"
            | "end"
            | "payRate"
            | "address"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in trial candidate confirmations.
         */
        options: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage: boolean;
        };
      };
      permanent: {
        /**
         * Rows available in permanent candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "role"
            | "start"
            | "clientName"
            | "address"
            | "salary"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
      };
    };
    /**
     * Variant-specific display settings for client booking confirmations.
     */
    client: {
      standard: {
        /**
         * Rows available in standard client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "awrInformation"
            | "chargeRate"
            | "agencyFee"
            | "bookedBy"
            | "approvedBy"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
      tutor: {
        /**
         * Rows available in tutor client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "studentName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "awrInformation"
            | "chargeRate"
            | "agencyFee"
            | "bookedBy"
            | "approvedBy"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
      interview: {
        /**
         * Rows available in interview client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "anaphylaxisTraining"
            | "start"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions"
            | "address";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
      };
      trial: {
        /**
         * Rows available in trial client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
      permanent: {
        /**
         * Rows available in permanent client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
    };
  };
  /**
   * Financial values used by calculated payment-run export columns
   */
  paymentRunExportSettings?: {
    /**
     * @minimum 1
     * @maximum 12
     */
    fiscalYearStartMonth?: number | null;
    /**
     * @minimum 0
     * @maximum 100
     */
    vatPercent?: number | null;
  };
  /**
   * eBulkPlus organisation references configured for this company
   */
  matrixOrganisationReferences?:
    | {
        /**
         * References must be 1-30 characters long and contain only letters, numbers, and underscores
         *
         * @pattern ^\w{1,30}$
         */
        reference: string;
        /**
         * Human-readable label for this reference
         */
        displayName?: string;
        /**
         * Whether this is the default reference
         */
        isDefault: boolean;
      }[]
    | null;
  /**
   * Partial update — only the subtype keys included in the request will be updated. Each subtype object is partially merged.
   */
  tuitionReportQuestions?: {
    TutorStandard?: {
      /**
       * Ordered tuition report questions to save for this subtype.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The stable question key. Omit it for a new question and one will be assigned automatically.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
      }[];
      /**
       * Whether the tuition report is mandatory for this subtype. When true, candidates cannot skip the report.
       */
      required?: boolean;
    };
    TutorMentor?: {
      /**
       * Ordered tuition report questions to save for this subtype.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The stable question key. Omit it for a new question and one will be assigned automatically.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
      }[];
      /**
       * Whether the tuition report is mandatory for this subtype. When true, candidates cannot skip the report.
       */
      required?: boolean;
    };
    TutorBasic?: {
      /**
       * Ordered tuition report questions to save for this subtype.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The stable question key. Omit it for a new question and one will be assigned automatically.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
      }[];
      /**
       * Whether the tuition report is mandatory for this subtype. When true, candidates cannot skip the report.
       */
      required?: boolean;
    };
  };
  /**
   * Replaces the health declaration configuration used for new declarations.
   */
  healthDeclarationConfig?: {
    /**
     * The ordered questions to save in the set.
     *
     * @maxItems 100
     */
    questions: {
      /**
       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The stable question key. Omit it for a new question and one will be assigned automatically.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
    }[];
  };
  /**
   * Replaces the criminal record declaration configuration used for new onboarding sessions.
   */
  criminalRecordConfig?: {
    /**
     * The ordered questions to save in the set.
     *
     * @maxItems 100
     */
    questions: {
      /**
       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The stable question key. Omit it for a new question and one will be assigned automatically.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
    }[];
  };
  /**
   * Partial update of company marketing settings
   */
  marketingSettings?: {
    /**
     * The company marketing email signature as a serialised Unlayer editor design JSON string. Pass null or omit to clear the signature.
     */
    signature?: string | null;
  };
  /**
   * Partial update of company GCA lot configuration
   */
  gcaLotConfig?: {
    /**
     * Replaces the full list of GCA lot options for this company
     *
     * @maxItems 50
     */
    lots?: {
      /**
       * Existing lot option ID to update. Omit when adding a new lot option.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The name of the GCA lot, e.g. 'Lot 2 – Master Vendor'
       *
       * @minLength 1
       * @maxLength 256
       */
      label: string;
      /**
       * The service description automatically applied to GCA bookings using this lot
       *
       * @minLength 1
       * @maxLength 256
       */
      serviceDescription: string;
    }[];
    /**
     * The ID of the lot option to use as the default for new GCA bookings
     *
     * @pattern ^[0-9a-f]{24}$
     */
    defaultLotId?: string | null;
  };
};

export type UpdateCompanyVariables = {
  body?: UpdateCompanyRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateCompany = (
  variables: UpdateCompanyVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateCompanyResponse,
    UpdateCompanyError,
    UpdateCompanyRequestBody,
    {},
    {},
    {}
  >({ url: "/company", method: "patch", ...variables, signal });

export const useUpdateCompany = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateCompanyResponse,
      UpdateCompanyError,
      UpdateCompanyVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateCompanyResponse,
    UpdateCompanyError,
    UpdateCompanyVariables
  >({
    mutationFn: (variables: UpdateCompanyVariables) =>
      fetchUpdateCompany(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type UpdateCompanyCvLibraryIntegrationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateCompanyCvLibraryIntegrationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * CV Library API integration username
     */
    username?: string;
    /**
     * Whether CV Library credentials have been configured
     */
    enabled?: boolean;
  };
};

export type UpdateCompanyCvLibraryIntegrationRequestBody = {
  /**
   * CV Library API integration username
   *
   * @minLength 1
   */
  username: string;
  /**
   * CV Library API integration password. Required for initial setup.
   *
   * @minLength 1
   */
  password?: string;
};

export type UpdateCompanyCvLibraryIntegrationVariables = {
  body: UpdateCompanyCvLibraryIntegrationRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateCompanyCvLibraryIntegration = (
  variables: UpdateCompanyCvLibraryIntegrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateCompanyCvLibraryIntegrationResponse,
    UpdateCompanyCvLibraryIntegrationError,
    UpdateCompanyCvLibraryIntegrationRequestBody,
    {},
    {},
    {}
  >({
    url: "/company/integrations/cv-library",
    method: "patch",
    ...variables,
    signal,
  });

export const useUpdateCompanyCvLibraryIntegration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateCompanyCvLibraryIntegrationResponse,
      UpdateCompanyCvLibraryIntegrationError,
      UpdateCompanyCvLibraryIntegrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateCompanyCvLibraryIntegrationResponse,
    UpdateCompanyCvLibraryIntegrationError,
    UpdateCompanyCvLibraryIntegrationVariables
  >({
    mutationFn: (variables: UpdateCompanyCvLibraryIntegrationVariables) =>
      fetchUpdateCompanyCvLibraryIntegration(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type CreateCompanyQubaApiKeyError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateCompanyQubaApiKeyResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The Quba data sync API key
     */
    key?: string;
    /**
     * ISO 8601 date string when the key expires
     */
    expiry?: string;
  };
};

export type CreateCompanyQubaApiKeyVariables = Apiv2Context["fetcherOptions"];

export const fetchCreateCompanyQubaApiKey = (
  variables: CreateCompanyQubaApiKeyVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateCompanyQubaApiKeyResponse,
    CreateCompanyQubaApiKeyError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/company/integrations/quba/api-keys",
    method: "post",
    ...variables,
    signal,
  });

export const useCreateCompanyQubaApiKey = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateCompanyQubaApiKeyResponse,
      CreateCompanyQubaApiKeyError,
      CreateCompanyQubaApiKeyVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateCompanyQubaApiKeyResponse,
    CreateCompanyQubaApiKeyError,
    CreateCompanyQubaApiKeyVariables
  >({
    mutationFn: (variables: CreateCompanyQubaApiKeyVariables) =>
      fetchCreateCompanyQubaApiKey(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type DeleteDevyceIntegrationError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type DeleteDevyceIntegrationVariables = Apiv2Context["fetcherOptions"];

export const fetchDeleteDevyceIntegration = (
  variables: DeleteDevyceIntegrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<undefined, DeleteDevyceIntegrationError, undefined, {}, {}, {}>({
    url: "/company/integrations/devyce",
    method: "delete",
    ...variables,
    signal,
  });

export const useDeleteDevyceIntegration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteDevyceIntegrationError,
      DeleteDevyceIntegrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    DeleteDevyceIntegrationError,
    DeleteDevyceIntegrationVariables
  >({
    mutationFn: (variables: DeleteDevyceIntegrationVariables) =>
      fetchDeleteDevyceIntegration(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type UpdateDevyceIntegrationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateDevyceIntegrationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * Whether the Devyce integration is enabled for the company
     */
    enabled?: boolean;
    /**
     * Whether a Devyce API key is configured
     */
    apiKeySet?: boolean;
    /**
     * Configured Devyce organisation identifier
     */
    organisationId?: string;
    /**
     * Timestamp of the last successful Devyce polling run
     *
     * @format date-time
     */
    lastSuccessfulPollAt?: string;
  };
};

export type UpdateDevyceIntegrationRequestBody = {
  /**
   * The Devyce API key used for company-level integration
   *
   * @minLength 1
   * @maxLength 100
   */
  apiKey: string;
  /**
   * The Devyce organisation identifier for this company
   *
   * @minLength 1
   * @maxLength 100
   */
  organisationId: string;
};

export type UpdateDevyceIntegrationVariables = {
  body: UpdateDevyceIntegrationRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateDevyceIntegration = (
  variables: UpdateDevyceIntegrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateDevyceIntegrationResponse,
    UpdateDevyceIntegrationError,
    UpdateDevyceIntegrationRequestBody,
    {},
    {},
    {}
  >({
    url: "/company/integrations/devyce",
    method: "put",
    ...variables,
    signal,
  });

export const useUpdateDevyceIntegration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateDevyceIntegrationResponse,
      UpdateDevyceIntegrationError,
      UpdateDevyceIntegrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateDevyceIntegrationResponse,
    UpdateDevyceIntegrationError,
    UpdateDevyceIntegrationVariables
  >({
    mutationFn: (variables: UpdateDevyceIntegrationVariables) =>
      fetchUpdateDevyceIntegration(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type DeleteEvertimeIntegrationError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type DeleteEvertimeIntegrationVariables = Apiv2Context["fetcherOptions"];

export const fetchDeleteEvertimeIntegration = (
  variables: DeleteEvertimeIntegrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<undefined, DeleteEvertimeIntegrationError, undefined, {}, {}, {}>({
    url: "/company/integrations/evertime",
    method: "delete",
    ...variables,
    signal,
  });

export const useDeleteEvertimeIntegration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteEvertimeIntegrationError,
      DeleteEvertimeIntegrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    DeleteEvertimeIntegrationError,
    DeleteEvertimeIntegrationVariables
  >({
    mutationFn: (variables: DeleteEvertimeIntegrationVariables) =>
      fetchDeleteEvertimeIntegration(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type UpdateEvertimeIntegrationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateEvertimeIntegrationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * Whether the Evertime integration is enabled for the company
     */
    enabled?: boolean;
  };
};

export type UpdateEvertimeIntegrationRequestBody = {
  /**
   * The Evertime API key used for company-level integration
   *
   * @minLength 1
   * @maxLength 500
   */
  apiKey: string;
};

export type UpdateEvertimeIntegrationVariables = {
  body: UpdateEvertimeIntegrationRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateEvertimeIntegration = (
  variables: UpdateEvertimeIntegrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateEvertimeIntegrationResponse,
    UpdateEvertimeIntegrationError,
    UpdateEvertimeIntegrationRequestBody,
    {},
    {},
    {}
  >({
    url: "/company/integrations/evertime",
    method: "put",
    ...variables,
    signal,
  });

export const useUpdateEvertimeIntegration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateEvertimeIntegrationResponse,
      UpdateEvertimeIntegrationError,
      UpdateEvertimeIntegrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateEvertimeIntegrationResponse,
    UpdateEvertimeIntegrationError,
    UpdateEvertimeIntegrationVariables
  >({
    mutationFn: (variables: UpdateEvertimeIntegrationVariables) =>
      fetchUpdateEvertimeIntegration(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type DeleteTimesheetPortalIntegrationError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type DeleteTimesheetPortalIntegrationVariables =
  Apiv2Context["fetcherOptions"];

export const fetchDeleteTimesheetPortalIntegration = (
  variables: DeleteTimesheetPortalIntegrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    DeleteTimesheetPortalIntegrationError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/company/integrations/timesheet-portal",
    method: "delete",
    ...variables,
    signal,
  });

export const useDeleteTimesheetPortalIntegration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteTimesheetPortalIntegrationError,
      DeleteTimesheetPortalIntegrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    DeleteTimesheetPortalIntegrationError,
    DeleteTimesheetPortalIntegrationVariables
  >({
    mutationFn: (variables: DeleteTimesheetPortalIntegrationVariables) =>
      fetchDeleteTimesheetPortalIntegration(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type UpdateTimesheetPortalIntegrationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateTimesheetPortalIntegrationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The company's Timesheet Portal API subdomain
     *
     * @minLength 1
     * @maxLength 63
     * @pattern ^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i
     */
    subdomain?: string;
    /**
     * Whether the Timesheet Portal integration is enabled for the company
     */
    enabled?: boolean;
  };
};

export type UpdateTimesheetPortalIntegrationRequestBody = {
  /**
   * The company's Timesheet Portal API subdomain
   *
   * @minLength 1
   * @maxLength 63
   * @pattern ^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i
   */
  subdomain: string;
  /**
   * The client ID used to authenticate with Timesheet Portal
   *
   * @minLength 1
   * @maxLength 500
   */
  clientId: string;
  /**
   * The client secret used to authenticate with Timesheet Portal
   *
   * @minLength 1
   * @maxLength 500
   */
  clientSecret: string;
};

export type UpdateTimesheetPortalIntegrationVariables = {
  body: UpdateTimesheetPortalIntegrationRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateTimesheetPortalIntegration = (
  variables: UpdateTimesheetPortalIntegrationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateTimesheetPortalIntegrationResponse,
    UpdateTimesheetPortalIntegrationError,
    UpdateTimesheetPortalIntegrationRequestBody,
    {},
    {},
    {}
  >({
    url: "/company/integrations/timesheet-portal",
    method: "put",
    ...variables,
    signal,
  });

export const useUpdateTimesheetPortalIntegration = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateTimesheetPortalIntegrationResponse,
      UpdateTimesheetPortalIntegrationError,
      UpdateTimesheetPortalIntegrationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateTimesheetPortalIntegrationResponse,
    UpdateTimesheetPortalIntegrationError,
    UpdateTimesheetPortalIntegrationVariables
  >({
    mutationFn: (variables: UpdateTimesheetPortalIntegrationVariables) =>
      fetchUpdateTimesheetPortalIntegration(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type PostTimesheetExportPathParams = {
  /**
   * The unique identifier of the timesheet
   *
   * @pattern ^[0-9a-f]{24}$
   */
  timesheetId: string;
};

export type PostTimesheetExportError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type PostTimesheetExportRequestBody = {
  /**
   * Whether the PDF is emailed to the client instead of downloaded
   */
  emailToClient: boolean;
  /**
   * Whether candidate and client query correspondence is included in the PDF
   */
  includeCorrespondence: boolean;
};

export type PostTimesheetExportVariables = {
  body: PostTimesheetExportRequestBody;
  pathParams: PostTimesheetExportPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchPostTimesheetExport = (
  variables: PostTimesheetExportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    PostTimesheetExportError,
    PostTimesheetExportRequestBody,
    {},
    {},
    PostTimesheetExportPathParams
  >({
    url: "/timesheets/{timesheetId}/exports",
    method: "post",
    ...variables,
    signal,
  });

export const usePostTimesheetExport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      PostTimesheetExportError,
      PostTimesheetExportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    PostTimesheetExportError,
    PostTimesheetExportVariables
  >({
    mutationFn: (variables: PostTimesheetExportVariables) =>
      fetchPostTimesheetExport(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTimesheetTuitionReportPathParams = {
  /**
   * The unique identifier of the timesheet
   *
   * @pattern ^[0-9a-f]{24}$
   */
  timesheetId: string;
};

export type GetTimesheetTuitionReportError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetTimesheetTuitionReportResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the timesheet
     *
     * @pattern ^[0-9a-f]{24}$
     */
    timesheetId?: string;
    /**
     * The booking subtype that determines the question configuration
     */
    subtype?: "TutorStandard" | "TutorMentor" | "TutorBasic";
    /**
     * Ordered list of questions with their submitted answers
     */
    questions?: {
      /**
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * A stable key used to associate the question with current and historical answers.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label?: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type?:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required?: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled?: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order?: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode?: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules?: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator?: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value?: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value?: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label?: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
      /**
       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
       */
      audience?: "candidate" | "staff";
      /**
       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
       */
      fixedRole?: "fullName" | "signature";
      /**
       * The submitted answer, when one has been recorded.
       */
      value?: string | string | boolean | number | (void | null);
    }[];
    /**
     * Whether the tuition report is mandatory for this subtype
     */
    required?: boolean;
  };
};

export type GetTimesheetTuitionReportVariables = {
  pathParams: GetTimesheetTuitionReportPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetTimesheetTuitionReport = (
  variables: GetTimesheetTuitionReportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetTimesheetTuitionReportResponse,
    GetTimesheetTuitionReportError,
    undefined,
    {},
    {},
    GetTimesheetTuitionReportPathParams
  >({
    url: "/timesheets/{timesheetId}/tuition-report",
    method: "get",
    ...variables,
    signal,
  });

export function getTimesheetTuitionReportQuery(
  variables: GetTimesheetTuitionReportVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (
    options: QueryFnOptions,
  ) => Promise<GetTimesheetTuitionReportResponse>;
};

export function getTimesheetTuitionReportQuery(
  variables: GetTimesheetTuitionReportVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetTimesheetTuitionReportResponse>)
    | reactQuery.SkipToken;
};

export function getTimesheetTuitionReportQuery(
  variables: GetTimesheetTuitionReportVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/timesheets/{timesheetId}/tuition-report",
      operationId: "getTimesheetTuitionReport",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetTimesheetTuitionReport(variables, signal),
  };
}

export const useSuspenseGetTimesheetTuitionReport = <
  TData = GetTimesheetTuitionReportResponse,
>(
  variables: GetTimesheetTuitionReportVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetTimesheetTuitionReportResponse,
      GetTimesheetTuitionReportError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetTimesheetTuitionReportResponse,
    GetTimesheetTuitionReportError,
    TData
  >({
    ...getTimesheetTuitionReportQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTimesheetTuitionReport = <
  TData = GetTimesheetTuitionReportResponse,
>(
  variables: GetTimesheetTuitionReportVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetTimesheetTuitionReportResponse,
      GetTimesheetTuitionReportError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetTimesheetTuitionReportResponse,
    GetTimesheetTuitionReportError,
    TData
  >({
    ...getTimesheetTuitionReportQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type PutTimesheetTuitionReportPathParams = {
  /**
   * The unique identifier of the timesheet
   *
   * @pattern ^[0-9a-f]{24}$
   */
  timesheetId: string;
};

export type PutTimesheetTuitionReportError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type PutTimesheetTuitionReportResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the timesheet
     *
     * @pattern ^[0-9a-f]{24}$
     */
    timesheetId?: string;
    /**
     * The booking subtype that determines the question configuration
     */
    subtype?: "TutorStandard" | "TutorMentor" | "TutorBasic";
    /**
     * Ordered list of questions with their submitted answers
     */
    questions?: {
      /**
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * A stable key used to associate the question with current and historical answers.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label?: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type?:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required?: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled?: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order?: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode?: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules?: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator?: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value?: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value?: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label?: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
      /**
       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
       */
      audience?: "candidate" | "staff";
      /**
       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
       */
      fixedRole?: "fullName" | "signature";
      /**
       * The submitted answer, when one has been recorded.
       */
      value?: string | string | boolean | number | (void | null);
    }[];
    /**
     * Whether the tuition report is mandatory for this subtype
     */
    required?: boolean;
  };
};

export type PutTimesheetTuitionReportRequestBody = {
  /**
   * Array of answers to tuition report questions
   */
  answers: {
    /**
     * The identifier of the question being answered.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    questionId: string;
    /**
     * The stable question key. Older consumers may omit it when a question ID is supplied.
     *
     * @minLength 1
     * @maxLength 128
     */
    questionKey?: string;
    /**
     * The submitted answer to the question.
     */
    value: string | string | boolean | number | (void | null);
  }[];
};

export type PutTimesheetTuitionReportVariables = {
  body: PutTimesheetTuitionReportRequestBody;
  pathParams: PutTimesheetTuitionReportPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchPutTimesheetTuitionReport = (
  variables: PutTimesheetTuitionReportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    PutTimesheetTuitionReportResponse,
    PutTimesheetTuitionReportError,
    PutTimesheetTuitionReportRequestBody,
    {},
    {},
    PutTimesheetTuitionReportPathParams
  >({
    url: "/timesheets/{timesheetId}/tuition-report",
    method: "put",
    ...variables,
    signal,
  });

export const usePutTimesheetTuitionReport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      PutTimesheetTuitionReportResponse,
      PutTimesheetTuitionReportError,
      PutTimesheetTuitionReportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    PutTimesheetTuitionReportResponse,
    PutTimesheetTuitionReportError,
    PutTimesheetTuitionReportVariables
  >({
    mutationFn: (variables: PutTimesheetTuitionReportVariables) =>
      fetchPutTimesheetTuitionReport(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type PostTimesheetTuitionReportExportPathParams = {
  /**
   * The unique identifier of the timesheet
   *
   * @pattern ^[0-9a-f]{24}$
   */
  timesheetId: string;
};

export type PostTimesheetTuitionReportExportError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type PostTimesheetTuitionReportExportVariables = {
  pathParams: PostTimesheetTuitionReportExportPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchPostTimesheetTuitionReportExport = (
  variables: PostTimesheetTuitionReportExportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    PostTimesheetTuitionReportExportError,
    undefined,
    {},
    {},
    PostTimesheetTuitionReportExportPathParams
  >({
    url: "/timesheets/{timesheetId}/tuition-report/exports",
    method: "post",
    ...variables,
    signal,
  });

export const usePostTimesheetTuitionReportExport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      PostTimesheetTuitionReportExportError,
      PostTimesheetTuitionReportExportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    PostTimesheetTuitionReportExportError,
    PostTimesheetTuitionReportExportVariables
  >({
    mutationFn: (variables: PostTimesheetTuitionReportExportVariables) =>
      fetchPostTimesheetTuitionReportExport(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type PostStudentTuitionReportExportPathParams = {
  /**
   * The unique identifier of the student
   *
   * @pattern ^[0-9a-f]{24}$
   */
  studentId: string;
};

export type PostStudentTuitionReportExportError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type PostStudentTuitionReportExportRequestBody = {
  /**
   * Start of the date range for the export
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  startDate: string;
  /**
   * End of the date range for the export
   *
   * @pattern ^\d{4}-\d{2}-\d{2}$
   */
  endDate: string;
};

export type PostStudentTuitionReportExportVariables = {
  body: PostStudentTuitionReportExportRequestBody;
  pathParams: PostStudentTuitionReportExportPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchPostStudentTuitionReportExport = (
  variables: PostStudentTuitionReportExportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    PostStudentTuitionReportExportError,
    PostStudentTuitionReportExportRequestBody,
    {},
    {},
    PostStudentTuitionReportExportPathParams
  >({
    url: "/students/{studentId}/tuition-reports/exports",
    method: "post",
    ...variables,
    signal,
  });

export const usePostStudentTuitionReportExport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      PostStudentTuitionReportExportError,
      PostStudentTuitionReportExportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    PostStudentTuitionReportExportError,
    PostStudentTuitionReportExportVariables
  >({
    mutationFn: (variables: PostStudentTuitionReportExportVariables) =>
      fetchPostStudentTuitionReportExport(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTaskWorkspacesQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "name"
    | "description"
    | "statuses"
    | "createdAt"
    | "updatedAt"
    | ""
  )[];
};

export type GetTaskWorkspacesError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetTaskWorkspacesResponse = {
  /**
   * Task workspaces visible to the authenticated user. Company-wide visibility requires the relevant task workspace permission.
   */
  data: {
    /**
     * The unique identifier of the task workspace
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id: string;
    /**
     * The name of the task workspace
     *
     * @minLength 1
     * @maxLength 128
     */
    name: string;
    /**
     * Optional task workspace description
     *
     * @maxLength 1024
     */
    description?: string | null;
    /**
     * Statuses available for tasks in this workspace
     *
     * @minItems 1
     * @maxItems 50
     */
    statuses: {
      /**
       * Unique identifier for the status (slug)
       *
       * @minLength 1
       * @maxLength 128
       */
      id: string;
      /**
       * Human-readable label for the status
       *
       * @minLength 1
       * @maxLength 128
       */
      label: string;
      /**
       * The canonical status this maps to for reporting
       */
      canonicalStatus: "todo" | "in_progress" | "done";
      /**
       * Display order of the status
       *
       * @minimum 0
       */
      order: number;
    }[];
    /**
     * Creation timestamp
     */
    createdAt: string;
    /**
     * Last update timestamp
     */
    updatedAt: string;
  }[];
};

export type GetTaskWorkspacesVariables = {
  queryParams?: GetTaskWorkspacesQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetTaskWorkspaces = (
  variables: GetTaskWorkspacesVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetTaskWorkspacesResponse,
    GetTaskWorkspacesError,
    undefined,
    {},
    GetTaskWorkspacesQueryParams,
    {}
  >({ url: "/task-workspaces", method: "get", ...variables, signal });

export function getTaskWorkspacesQuery(variables: GetTaskWorkspacesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetTaskWorkspacesResponse>;
};

export function getTaskWorkspacesQuery(
  variables: GetTaskWorkspacesVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetTaskWorkspacesResponse>)
    | reactQuery.SkipToken;
};

export function getTaskWorkspacesQuery(
  variables: GetTaskWorkspacesVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/task-workspaces",
      operationId: "getTaskWorkspaces",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetTaskWorkspaces(variables, signal),
  };
}

export const useSuspenseGetTaskWorkspaces = <
  TData = GetTaskWorkspacesResponse,
>(
  variables: GetTaskWorkspacesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetTaskWorkspacesResponse,
      GetTaskWorkspacesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetTaskWorkspacesResponse,
    GetTaskWorkspacesError,
    TData
  >({
    ...getTaskWorkspacesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTaskWorkspaces = <TData = GetTaskWorkspacesResponse,>(
  variables: GetTaskWorkspacesVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetTaskWorkspacesResponse,
      GetTaskWorkspacesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetTaskWorkspacesResponse,
    GetTaskWorkspacesError,
    TData
  >({
    ...getTaskWorkspacesQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateTaskWorkspaceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateTaskWorkspaceResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the task workspace
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the task workspace
     *
     * @minLength 1
     * @maxLength 128
     */
    name?: string;
    /**
     * Optional task workspace description
     *
     * @maxLength 1024
     */
    description?: string | null;
    /**
     * Statuses available for tasks in this workspace
     *
     * @minItems 1
     * @maxItems 50
     */
    statuses?: {
      /**
       * Unique identifier for the status (slug)
       *
       * @minLength 1
       * @maxLength 128
       */
      id?: string;
      /**
       * Human-readable label for the status
       *
       * @minLength 1
       * @maxLength 128
       */
      label?: string;
      /**
       * The canonical status this maps to for reporting
       */
      canonicalStatus?: "todo" | "in_progress" | "done";
      /**
       * Display order of the status
       *
       * @minimum 0
       */
      order?: number;
    }[];
    /**
     * Creation timestamp
     */
    createdAt?: string;
    /**
     * Last update timestamp
     */
    updatedAt?: string;
  };
};

export type CreateTaskWorkspaceRequestBody = {
  /**
   * The name of the task workspace
   *
   * @minLength 1
   * @maxLength 128
   */
  name: string;
  /**
   * Optional task workspace description
   *
   * @maxLength 1024
   */
  description?: string | null;
  /**
   * Initial task status configuration. Defaults are used when omitted.
   *
   * @minItems 1
   * @maxItems 50
   */
  statuses?: {
    /**
     * Unique identifier for the status (slug)
     *
     * @minLength 1
     * @maxLength 128
     */
    id: string;
    /**
     * Human-readable label for the status
     *
     * @minLength 1
     * @maxLength 128
     */
    label: string;
    /**
     * The canonical status this maps to for reporting
     */
    canonicalStatus: "todo" | "in_progress" | "done";
    /**
     * Display order of the status
     *
     * @minimum 0
     */
    order: number;
  }[];
  /**
   * Whether to set the created workspace as the user's default workspace
   */
  setDefault?: boolean;
};

export type CreateTaskWorkspaceVariables = {
  body: CreateTaskWorkspaceRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateTaskWorkspace = (
  variables: CreateTaskWorkspaceVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateTaskWorkspaceResponse,
    CreateTaskWorkspaceError,
    CreateTaskWorkspaceRequestBody,
    {},
    {},
    {}
  >({ url: "/task-workspaces", method: "post", ...variables, signal });

export const useCreateTaskWorkspace = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateTaskWorkspaceResponse,
      CreateTaskWorkspaceError,
      CreateTaskWorkspaceVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateTaskWorkspaceResponse,
    CreateTaskWorkspaceError,
    CreateTaskWorkspaceVariables
  >({
    mutationFn: (variables: CreateTaskWorkspaceVariables) =>
      fetchCreateTaskWorkspace(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTaskWorkspacePathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
};

export type GetTaskWorkspaceQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "name"
    | "description"
    | "statuses"
    | "createdAt"
    | "updatedAt"
    | ""
  )[];
};

export type GetTaskWorkspaceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetTaskWorkspaceResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the task workspace
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the task workspace
     *
     * @minLength 1
     * @maxLength 128
     */
    name?: string;
    /**
     * Optional task workspace description
     *
     * @maxLength 1024
     */
    description?: string | null;
    /**
     * Statuses available for tasks in this workspace
     *
     * @minItems 1
     * @maxItems 50
     */
    statuses?: {
      /**
       * Unique identifier for the status (slug)
       *
       * @minLength 1
       * @maxLength 128
       */
      id?: string;
      /**
       * Human-readable label for the status
       *
       * @minLength 1
       * @maxLength 128
       */
      label?: string;
      /**
       * The canonical status this maps to for reporting
       */
      canonicalStatus?: "todo" | "in_progress" | "done";
      /**
       * Display order of the status
       *
       * @minimum 0
       */
      order?: number;
    }[];
    /**
     * Creation timestamp
     */
    createdAt?: string;
    /**
     * Last update timestamp
     */
    updatedAt?: string;
  };
};

export type GetTaskWorkspaceVariables = {
  pathParams: GetTaskWorkspacePathParams;
  queryParams?: GetTaskWorkspaceQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetTaskWorkspace = (
  variables: GetTaskWorkspaceVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetTaskWorkspaceResponse,
    GetTaskWorkspaceError,
    undefined,
    {},
    GetTaskWorkspaceQueryParams,
    GetTaskWorkspacePathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}",
    method: "get",
    ...variables,
    signal,
  });

export function getTaskWorkspaceQuery(variables: GetTaskWorkspaceVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetTaskWorkspaceResponse>;
};

export function getTaskWorkspaceQuery(
  variables: GetTaskWorkspaceVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetTaskWorkspaceResponse>)
    | reactQuery.SkipToken;
};

export function getTaskWorkspaceQuery(
  variables: GetTaskWorkspaceVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/task-workspaces/{taskWorkspaceId}",
      operationId: "getTaskWorkspace",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetTaskWorkspace(variables, signal),
  };
}

export const useSuspenseGetTaskWorkspace = <TData = GetTaskWorkspaceResponse,>(
  variables: GetTaskWorkspaceVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetTaskWorkspaceResponse,
      GetTaskWorkspaceError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetTaskWorkspaceResponse,
    GetTaskWorkspaceError,
    TData
  >({
    ...getTaskWorkspaceQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTaskWorkspace = <TData = GetTaskWorkspaceResponse,>(
  variables: GetTaskWorkspaceVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetTaskWorkspaceResponse,
      GetTaskWorkspaceError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetTaskWorkspaceResponse,
    GetTaskWorkspaceError,
    TData
  >({
    ...getTaskWorkspaceQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateTaskWorkspacePathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
};

export type UpdateTaskWorkspaceError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateTaskWorkspaceResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the task workspace
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the task workspace
     *
     * @minLength 1
     * @maxLength 128
     */
    name?: string;
    /**
     * Optional task workspace description
     *
     * @maxLength 1024
     */
    description?: string | null;
    /**
     * Statuses available for tasks in this workspace
     *
     * @minItems 1
     * @maxItems 50
     */
    statuses?: {
      /**
       * Unique identifier for the status (slug)
       *
       * @minLength 1
       * @maxLength 128
       */
      id?: string;
      /**
       * Human-readable label for the status
       *
       * @minLength 1
       * @maxLength 128
       */
      label?: string;
      /**
       * The canonical status this maps to for reporting
       */
      canonicalStatus?: "todo" | "in_progress" | "done";
      /**
       * Display order of the status
       *
       * @minimum 0
       */
      order?: number;
    }[];
    /**
     * Creation timestamp
     */
    createdAt?: string;
    /**
     * Last update timestamp
     */
    updatedAt?: string;
  };
};

export type UpdateTaskWorkspaceRequestBody = {
  /**
   * The name of the task workspace
   *
   * @minLength 1
   * @maxLength 128
   */
  name?: string;
  /**
   * Optional task workspace description
   *
   * @maxLength 1024
   */
  description?: string | null;
  /**
   * Statuses available for tasks in this workspace
   *
   * @minItems 1
   * @maxItems 50
   */
  statuses?: {
    /**
     * Unique identifier for the status (slug)
     *
     * @minLength 1
     * @maxLength 128
     */
    id: string;
    /**
     * Human-readable label for the status
     *
     * @minLength 1
     * @maxLength 128
     */
    label: string;
    /**
     * The canonical status this maps to for reporting
     */
    canonicalStatus: "todo" | "in_progress" | "done";
    /**
     * Display order of the status
     *
     * @minimum 0
     */
    order: number;
  }[];
  /**
   * Whether to set this workspace as the user's default workspace
   */
  setDefault?: boolean;
};

export type UpdateTaskWorkspaceVariables = {
  body?: UpdateTaskWorkspaceRequestBody;
  pathParams: UpdateTaskWorkspacePathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateTaskWorkspace = (
  variables: UpdateTaskWorkspaceVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateTaskWorkspaceResponse,
    UpdateTaskWorkspaceError,
    UpdateTaskWorkspaceRequestBody,
    {},
    {},
    UpdateTaskWorkspacePathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}",
    method: "patch",
    ...variables,
    signal,
  });

export const useUpdateTaskWorkspace = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateTaskWorkspaceResponse,
      UpdateTaskWorkspaceError,
      UpdateTaskWorkspaceVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateTaskWorkspaceResponse,
    UpdateTaskWorkspaceError,
    UpdateTaskWorkspaceVariables
  >({
    mutationFn: (variables: UpdateTaskWorkspaceVariables) =>
      fetchUpdateTaskWorkspace(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTasksPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
};

export type GetTasksQueryParams = {
  /**
   * Task filter object
   */
  filter?: {
    /**
     * Filter by assignee user ID
     *
     * @pattern ^[0-9a-f]{24}$
     */
    assignee?: string;
    /**
     * Filter by assignee user IDs
     */
    assigneeIds?: string[];
    /**
     * Filter by whether assignee is unassigned
     */
    unassigned?: "true" | "false";
    /**
     * Filter by creator user ID
     *
     * @pattern ^[0-9a-f]{24}$
     */
    createdBy?: string;
    /**
     * Filter by statusId
     *
     * @maxLength 128
     */
    status?: string;
    /**
     * Filter by canonical status
     */
    canonicalStatus?: "todo" | "in_progress" | "done";
    /**
     * Return tasks associated with any of the supplied tag identifiers.
     */
    tags?: string[];
    /**
     * Filter by linked entity type
     */
    linkedEntityType?: "candidate" | "client" | "booking" | "vacancy";
    /**
     * Filter by linked entity ID
     *
     * @pattern ^[0-9a-f]{24}$
     */
    linkedEntityId?: string;
    /**
     * Filter by linked candidate IDs
     */
    candidateIds?: string[];
    /**
     * Filter by linked client IDs
     */
    clientIds?: string[];
    /**
     * Filter by linked vacancy IDs
     */
    vacancyIds?: string[];
    /**
     * Filter by linked booking IDs
     */
    bookingIds?: string[];
    /**
     * Filter tasks with dueDate greater than or equal to this ISO datetime
     *
     * @format date-time
     */
    dueDateFrom?: string;
    /**
     * Filter tasks with dueDate less than or equal to this ISO datetime
     *
     * @format date-time
     */
    dueDateTo?: string;
    /**
     * Filter to completed/incomplete tasks
     */
    completed?: "true" | "false";
    /**
     * Include incomplete tasks plus tasks completed after this ISO datetime
     *
     * @format date-time
     */
    completedAfter?: string;
  };
  /**
   * The fields to include in the response in dot notation format
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "taskWorkspaceId"
    | "title"
    | "description"
    | "statusId"
    | "canonicalStatus"
    | "priority"
    | "assignee"
    | "assignee.id"
    | "assignee.email"
    | "assignee.firstName"
    | "assignee.lastName"
    | "assignee.username"
    | "assignee.lea"
    | "assignee.clients"
    | "assignee.position"
    | "assignee.permissions"
    | "assignee.activationCode"
    | "assignee.customer"
    | "assignee.disabled"
    | "assignee.commissionThreshold"
    | "assignee.branches"
    | "assignee.displayOnboarding"
    | "assignee._customId"
    | "assignee.signature"
    | "assignee.phone"
    | "assignee.defaultCandidateWatcher"
    | "assignee.defaultTaskWorkspace"
    | "assignee.teamAdmin"
    | "assignee.contact"
    | "assignee.contact.id"
    | "assignee.contact.forename"
    | "assignee.contact.surname"
    | "assignee.contact.email"
    | "assignee.contact.telephone"
    | "assignee.contact.mobile"
    | "assignee.contact.extension"
    | "assignee.createdAt"
    | "assignee.evertime.id"
    | "assignee.evertime"
    | "assignee.timesheetPortal.id"
    | "assignee.timesheetPortal.code"
    | "assignee.timesheetPortal"
    | "createdBy"
    | "createdBy.id"
    | "createdBy.email"
    | "createdBy.firstName"
    | "createdBy.lastName"
    | "createdBy.username"
    | "createdBy.lea"
    | "createdBy.clients"
    | "createdBy.position"
    | "createdBy.permissions"
    | "createdBy.activationCode"
    | "createdBy.customer"
    | "createdBy.disabled"
    | "createdBy.commissionThreshold"
    | "createdBy.branches"
    | "createdBy.displayOnboarding"
    | "createdBy._customId"
    | "createdBy.signature"
    | "createdBy.phone"
    | "createdBy.defaultCandidateWatcher"
    | "createdBy.defaultTaskWorkspace"
    | "createdBy.teamAdmin"
    | "createdBy.contact"
    | "createdBy.contact.id"
    | "createdBy.contact.forename"
    | "createdBy.contact.surname"
    | "createdBy.contact.email"
    | "createdBy.contact.telephone"
    | "createdBy.contact.mobile"
    | "createdBy.contact.extension"
    | "createdBy.createdAt"
    | "createdBy.evertime.id"
    | "createdBy.evertime"
    | "createdBy.timesheetPortal.id"
    | "createdBy.timesheetPortal.code"
    | "createdBy.timesheetPortal"
    | "dueDate"
    | "completedAt"
    | "tags"
    | "linkedEntityType"
    | "linkedEntityId"
    | "linkedEntityPermissionDenied"
    | "checklist"
    | "attachments"
    | "createdAt"
    | "updatedAt"
    | ""
  )[];
  /**
   * Related resources to return as objects instead of IDs. Supported values are documented for this endpoint.
   *
   * @maxItems 25
   */
  expand?: (
    | "tags"
    | "assignee"
    | "createdBy"
    | "checklist.completedBy"
    | "attachments.uploadedBy"
  )[];
  /**
   * Page number
   *
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Field used to order tasks. Prefix the field with '-' for descending order.
   */
  sort?:
    | "createdAt"
    | "-createdAt"
    | "dueDate"
    | "-dueDate"
    | "updatedAt"
    | "-updatedAt";
};

export type GetTasksError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetTasksResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the task
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The workspace this task belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    taskWorkspaceId?: string;
    /**
     * The title of the task
     *
     * @minLength 1
     * @maxLength 512
     */
    title?: string;
    /**
     * Detailed description of the task
     *
     * @maxLength 4096
     */
    description?: string | null;
    /**
     * The ID of the configured status
     *
     * @maxLength 128
     */
    statusId?: string;
    /**
     * The canonical status for filtering and reporting
     */
    canonicalStatus?: "todo" | "in_progress" | "done";
    /**
     * The priority of the task
     */
    priority?: "low" | "medium" | "high" | "urgent" | any | null;
    /**
     * Assignee reference. If expanded, a user object; otherwise, a user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        }
      | (void | null);
    /**
     * Creator reference. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * ISO timestamp when the task is due
     */
    dueDate?: string | null;
    /**
     * ISO timestamp when the task was completed
     */
    completedAt?: string | null;
    /**
     * Tags applied to the task. If expanded, an array of tag objects; otherwise, an array of tag IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * Type of linked CRM record
     */
    linkedEntityType?:
      | "candidate"
      | "client"
      | "booking"
      | "vacancy"
      | any
      | null;
    /**
     * ID of the linked CRM record
     *
     * @pattern ^[0-9a-f]{24}$
     */
    linkedEntityId?: string | null;
    /**
     * True when the user cannot view the linked record
     */
    linkedEntityPermissionDenied?: boolean;
    /**
     * Checklist items for the task
     *
     * @maxItems 100
     */
    checklist?: {
      /**
       * Checklist item identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The checklist item text
       *
       * @minLength 1
       * @maxLength 512
       */
      title?: string;
      /**
       * Whether the item is completed
       */
      completed?: boolean;
      /**
       * ISO timestamp when the item was completed
       */
      completedAt?: string | null;
      /**
       * User who completed the item. If expanded, a user object; otherwise, a user ID.
       */
      completedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          }
        | (void | null);
    }[];
    /**
     * Files attached to the task
     *
     * @maxItems 50
     */
    attachments?: {
      /**
       * Attachment identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The original file name
       *
       * @maxLength 512
       */
      fileName?: string;
      /**
       * MIME type of the file
       *
       * @maxLength 128
       */
      fileType?: string;
      /**
       * Size of the file in bytes
       *
       * @minimum 0
       */
      fileSize?: number;
      /**
       * An opaque reference used to retrieve the attachment. Do not construct or modify this value.
       *
       * @maxLength 1024
       */
      blobName?: string;
      /**
       * User who uploaded the file. If expanded, a user object; otherwise, a user ID.
       */
      uploadedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * ISO timestamp when the file was uploaded
       */
      uploadedAt?: string;
    }[];
    /**
     * Creation timestamp
     */
    createdAt?: string;
    /**
     * Last update timestamp
     */
    updatedAt?: string;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetTasksVariables = {
  pathParams: GetTasksPathParams;
  queryParams?: GetTasksQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetTasks = (
  variables: GetTasksVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetTasksResponse,
    GetTasksError,
    undefined,
    {},
    GetTasksQueryParams,
    GetTasksPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks",
    method: "get",
    ...variables,
    signal,
  });

export function getTasksQuery(variables: GetTasksVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetTasksResponse>;
};

export function getTasksQuery(
  variables: GetTasksVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetTasksResponse>)
    | reactQuery.SkipToken;
};

export function getTasksQuery(
  variables: GetTasksVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/task-workspaces/{taskWorkspaceId}/tasks",
      operationId: "getTasks",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetTasks(variables, signal),
  };
}

export const useSuspenseGetTasks = <TData = GetTasksResponse,>(
  variables: GetTasksVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetTasksResponse, GetTasksError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<GetTasksResponse, GetTasksError, TData>({
    ...getTasksQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTasks = <TData = GetTasksResponse,>(
  variables: GetTasksVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetTasksResponse, GetTasksError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetTasksResponse, GetTasksError, TData>({
    ...getTasksQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateTaskPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
};

export type CreateTaskError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateTaskResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the task
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The workspace this task belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    taskWorkspaceId?: string;
    /**
     * The title of the task
     *
     * @minLength 1
     * @maxLength 512
     */
    title?: string;
    /**
     * Detailed description of the task
     *
     * @maxLength 4096
     */
    description?: string | null;
    /**
     * The ID of the configured status
     *
     * @maxLength 128
     */
    statusId?: string;
    /**
     * The canonical status for filtering and reporting
     */
    canonicalStatus?: "todo" | "in_progress" | "done";
    /**
     * The priority of the task
     */
    priority?: "low" | "medium" | "high" | "urgent" | any | null;
    /**
     * Assignee reference. If expanded, a user object; otherwise, a user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        }
      | (void | null);
    /**
     * Creator reference. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * ISO timestamp when the task is due
     */
    dueDate?: string | null;
    /**
     * ISO timestamp when the task was completed
     */
    completedAt?: string | null;
    /**
     * Tags applied to the task. If expanded, an array of tag objects; otherwise, an array of tag IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * Type of linked CRM record
     */
    linkedEntityType?:
      | "candidate"
      | "client"
      | "booking"
      | "vacancy"
      | any
      | null;
    /**
     * ID of the linked CRM record
     *
     * @pattern ^[0-9a-f]{24}$
     */
    linkedEntityId?: string | null;
    /**
     * True when the user cannot view the linked record
     */
    linkedEntityPermissionDenied?: boolean;
    /**
     * Checklist items for the task
     *
     * @maxItems 100
     */
    checklist?: {
      /**
       * Checklist item identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The checklist item text
       *
       * @minLength 1
       * @maxLength 512
       */
      title?: string;
      /**
       * Whether the item is completed
       */
      completed?: boolean;
      /**
       * ISO timestamp when the item was completed
       */
      completedAt?: string | null;
      /**
       * User who completed the item. If expanded, a user object; otherwise, a user ID.
       */
      completedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          }
        | (void | null);
    }[];
    /**
     * Files attached to the task
     *
     * @maxItems 50
     */
    attachments?: {
      /**
       * Attachment identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The original file name
       *
       * @maxLength 512
       */
      fileName?: string;
      /**
       * MIME type of the file
       *
       * @maxLength 128
       */
      fileType?: string;
      /**
       * Size of the file in bytes
       *
       * @minimum 0
       */
      fileSize?: number;
      /**
       * An opaque reference used to retrieve the attachment. Do not construct or modify this value.
       *
       * @maxLength 1024
       */
      blobName?: string;
      /**
       * User who uploaded the file. If expanded, a user object; otherwise, a user ID.
       */
      uploadedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * ISO timestamp when the file was uploaded
       */
      uploadedAt?: string;
    }[];
    /**
     * Creation timestamp
     */
    createdAt?: string;
    /**
     * Last update timestamp
     */
    updatedAt?: string;
  };
};

export type CreateTaskRequestBody = {
  /**
   * The title of the task
   *
   * @minLength 1
   * @maxLength 512
   */
  title: string;
  /**
   * Detailed description of the task
   *
   * @maxLength 4096
   */
  description?: string | null;
  /**
   * The ID of the configured status
   *
   * @maxLength 128
   */
  statusId: string;
  /**
   * The priority of the task
   */
  priority?: "low" | "medium" | "high" | "urgent" | any | null;
  /**
   * The user responsible for the task, or null when the task is unassigned.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  assignee?: string | null;
  /**
   * ISO timestamp when the task is due
   */
  dueDate?: string | null;
  /**
   * Tag IDs applied to the task
   *
   * @maxItems 32
   */
  tags?: string[];
  /**
   * Type of linked CRM record
   */
  linkedEntityType?:
    | "candidate"
    | "client"
    | "booking"
    | "vacancy"
    | any
    | null;
  /**
   * ID of the linked CRM record
   *
   * @pattern ^[0-9a-f]{24}$
   */
  linkedEntityId?: string | null;
  /**
   * Initial checklist items
   *
   * @maxItems 100
   */
  checklist?: {
    /**
     * Checklist item text
     *
     * @minLength 1
     * @maxLength 512
     */
    title: string;
  }[];
};

export type CreateTaskVariables = {
  body: CreateTaskRequestBody;
  pathParams: CreateTaskPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateTask = (
  variables: CreateTaskVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateTaskResponse,
    CreateTaskError,
    CreateTaskRequestBody,
    {},
    {},
    CreateTaskPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks",
    method: "post",
    ...variables,
    signal,
  });

export const useCreateTask = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateTaskResponse,
      CreateTaskError,
      CreateTaskVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateTaskResponse,
    CreateTaskError,
    CreateTaskVariables
  >({
    mutationFn: (variables: CreateTaskVariables) =>
      fetchCreateTask(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTaskPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
  /**
   * The task identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskId: string;
};

export type GetTaskQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "taskWorkspaceId"
    | "title"
    | "description"
    | "statusId"
    | "canonicalStatus"
    | "priority"
    | "assignee"
    | "assignee.id"
    | "assignee.email"
    | "assignee.firstName"
    | "assignee.lastName"
    | "assignee.username"
    | "assignee.lea"
    | "assignee.clients"
    | "assignee.position"
    | "assignee.permissions"
    | "assignee.activationCode"
    | "assignee.customer"
    | "assignee.disabled"
    | "assignee.commissionThreshold"
    | "assignee.branches"
    | "assignee.displayOnboarding"
    | "assignee._customId"
    | "assignee.signature"
    | "assignee.phone"
    | "assignee.defaultCandidateWatcher"
    | "assignee.defaultTaskWorkspace"
    | "assignee.teamAdmin"
    | "assignee.contact"
    | "assignee.contact.id"
    | "assignee.contact.forename"
    | "assignee.contact.surname"
    | "assignee.contact.email"
    | "assignee.contact.telephone"
    | "assignee.contact.mobile"
    | "assignee.contact.extension"
    | "assignee.createdAt"
    | "assignee.evertime.id"
    | "assignee.evertime"
    | "assignee.timesheetPortal.id"
    | "assignee.timesheetPortal.code"
    | "assignee.timesheetPortal"
    | "createdBy"
    | "createdBy.id"
    | "createdBy.email"
    | "createdBy.firstName"
    | "createdBy.lastName"
    | "createdBy.username"
    | "createdBy.lea"
    | "createdBy.clients"
    | "createdBy.position"
    | "createdBy.permissions"
    | "createdBy.activationCode"
    | "createdBy.customer"
    | "createdBy.disabled"
    | "createdBy.commissionThreshold"
    | "createdBy.branches"
    | "createdBy.displayOnboarding"
    | "createdBy._customId"
    | "createdBy.signature"
    | "createdBy.phone"
    | "createdBy.defaultCandidateWatcher"
    | "createdBy.defaultTaskWorkspace"
    | "createdBy.teamAdmin"
    | "createdBy.contact"
    | "createdBy.contact.id"
    | "createdBy.contact.forename"
    | "createdBy.contact.surname"
    | "createdBy.contact.email"
    | "createdBy.contact.telephone"
    | "createdBy.contact.mobile"
    | "createdBy.contact.extension"
    | "createdBy.createdAt"
    | "createdBy.evertime.id"
    | "createdBy.evertime"
    | "createdBy.timesheetPortal.id"
    | "createdBy.timesheetPortal.code"
    | "createdBy.timesheetPortal"
    | "dueDate"
    | "completedAt"
    | "tags"
    | "linkedEntityType"
    | "linkedEntityId"
    | "linkedEntityPermissionDenied"
    | "checklist"
    | "attachments"
    | "createdAt"
    | "updatedAt"
    | ""
  )[];
  /**
   * Related resources to return as objects instead of IDs. Supported values are documented for this endpoint.
   *
   * @maxItems 25
   */
  expand?: (
    | "tags"
    | "assignee"
    | "createdBy"
    | "checklist.completedBy"
    | "attachments.uploadedBy"
  )[];
};

export type GetTaskError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetTaskResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the task
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The workspace this task belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    taskWorkspaceId?: string;
    /**
     * The title of the task
     *
     * @minLength 1
     * @maxLength 512
     */
    title?: string;
    /**
     * Detailed description of the task
     *
     * @maxLength 4096
     */
    description?: string | null;
    /**
     * The ID of the configured status
     *
     * @maxLength 128
     */
    statusId?: string;
    /**
     * The canonical status for filtering and reporting
     */
    canonicalStatus?: "todo" | "in_progress" | "done";
    /**
     * The priority of the task
     */
    priority?: "low" | "medium" | "high" | "urgent" | any | null;
    /**
     * Assignee reference. If expanded, a user object; otherwise, a user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        }
      | (void | null);
    /**
     * Creator reference. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * ISO timestamp when the task is due
     */
    dueDate?: string | null;
    /**
     * ISO timestamp when the task was completed
     */
    completedAt?: string | null;
    /**
     * Tags applied to the task. If expanded, an array of tag objects; otherwise, an array of tag IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * Type of linked CRM record
     */
    linkedEntityType?:
      | "candidate"
      | "client"
      | "booking"
      | "vacancy"
      | any
      | null;
    /**
     * ID of the linked CRM record
     *
     * @pattern ^[0-9a-f]{24}$
     */
    linkedEntityId?: string | null;
    /**
     * True when the user cannot view the linked record
     */
    linkedEntityPermissionDenied?: boolean;
    /**
     * Checklist items for the task
     *
     * @maxItems 100
     */
    checklist?: {
      /**
       * Checklist item identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The checklist item text
       *
       * @minLength 1
       * @maxLength 512
       */
      title?: string;
      /**
       * Whether the item is completed
       */
      completed?: boolean;
      /**
       * ISO timestamp when the item was completed
       */
      completedAt?: string | null;
      /**
       * User who completed the item. If expanded, a user object; otherwise, a user ID.
       */
      completedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          }
        | (void | null);
    }[];
    /**
     * Files attached to the task
     *
     * @maxItems 50
     */
    attachments?: {
      /**
       * Attachment identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The original file name
       *
       * @maxLength 512
       */
      fileName?: string;
      /**
       * MIME type of the file
       *
       * @maxLength 128
       */
      fileType?: string;
      /**
       * Size of the file in bytes
       *
       * @minimum 0
       */
      fileSize?: number;
      /**
       * An opaque reference used to retrieve the attachment. Do not construct or modify this value.
       *
       * @maxLength 1024
       */
      blobName?: string;
      /**
       * User who uploaded the file. If expanded, a user object; otherwise, a user ID.
       */
      uploadedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * ISO timestamp when the file was uploaded
       */
      uploadedAt?: string;
    }[];
    /**
     * Creation timestamp
     */
    createdAt?: string;
    /**
     * Last update timestamp
     */
    updatedAt?: string;
  };
};

export type GetTaskVariables = {
  pathParams: GetTaskPathParams;
  queryParams?: GetTaskQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetTask = (
  variables: GetTaskVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetTaskResponse,
    GetTaskError,
    undefined,
    {},
    GetTaskQueryParams,
    GetTaskPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}",
    method: "get",
    ...variables,
    signal,
  });

export function getTaskQuery(variables: GetTaskVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetTaskResponse>;
};

export function getTaskQuery(
  variables: GetTaskVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetTaskResponse>)
    | reactQuery.SkipToken;
};

export function getTaskQuery(
  variables: GetTaskVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}",
      operationId: "getTask",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetTask(variables, signal),
  };
}

export const useSuspenseGetTask = <TData = GetTaskResponse,>(
  variables: GetTaskVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetTaskResponse, GetTaskError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<GetTaskResponse, GetTaskError, TData>({
    ...getTaskQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTask = <TData = GetTaskResponse,>(
  variables: GetTaskVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetTaskResponse, GetTaskError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetTaskResponse, GetTaskError, TData>({
    ...getTaskQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateTaskPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
  /**
   * The task identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskId: string;
};

export type UpdateTaskError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateTaskResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the task
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The workspace this task belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    taskWorkspaceId?: string;
    /**
     * The title of the task
     *
     * @minLength 1
     * @maxLength 512
     */
    title?: string;
    /**
     * Detailed description of the task
     *
     * @maxLength 4096
     */
    description?: string | null;
    /**
     * The ID of the configured status
     *
     * @maxLength 128
     */
    statusId?: string;
    /**
     * The canonical status for filtering and reporting
     */
    canonicalStatus?: "todo" | "in_progress" | "done";
    /**
     * The priority of the task
     */
    priority?: "low" | "medium" | "high" | "urgent" | any | null;
    /**
     * Assignee reference. If expanded, a user object; otherwise, a user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        }
      | (void | null);
    /**
     * Creator reference. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * ISO timestamp when the task is due
     */
    dueDate?: string | null;
    /**
     * ISO timestamp when the task was completed
     */
    completedAt?: string | null;
    /**
     * Tags applied to the task. If expanded, an array of tag objects; otherwise, an array of tag IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * Type of linked CRM record
     */
    linkedEntityType?:
      | "candidate"
      | "client"
      | "booking"
      | "vacancy"
      | any
      | null;
    /**
     * ID of the linked CRM record
     *
     * @pattern ^[0-9a-f]{24}$
     */
    linkedEntityId?: string | null;
    /**
     * True when the user cannot view the linked record
     */
    linkedEntityPermissionDenied?: boolean;
    /**
     * Checklist items for the task
     *
     * @maxItems 100
     */
    checklist?: {
      /**
       * Checklist item identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The checklist item text
       *
       * @minLength 1
       * @maxLength 512
       */
      title?: string;
      /**
       * Whether the item is completed
       */
      completed?: boolean;
      /**
       * ISO timestamp when the item was completed
       */
      completedAt?: string | null;
      /**
       * User who completed the item. If expanded, a user object; otherwise, a user ID.
       */
      completedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          }
        | (void | null);
    }[];
    /**
     * Files attached to the task
     *
     * @maxItems 50
     */
    attachments?: {
      /**
       * Attachment identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The original file name
       *
       * @maxLength 512
       */
      fileName?: string;
      /**
       * MIME type of the file
       *
       * @maxLength 128
       */
      fileType?: string;
      /**
       * Size of the file in bytes
       *
       * @minimum 0
       */
      fileSize?: number;
      /**
       * An opaque reference used to retrieve the attachment. Do not construct or modify this value.
       *
       * @maxLength 1024
       */
      blobName?: string;
      /**
       * User who uploaded the file. If expanded, a user object; otherwise, a user ID.
       */
      uploadedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * ISO timestamp when the file was uploaded
       */
      uploadedAt?: string;
    }[];
    /**
     * Creation timestamp
     */
    createdAt?: string;
    /**
     * Last update timestamp
     */
    updatedAt?: string;
  };
};

export type UpdateTaskRequestBody = {
  /**
   * The title of the task
   *
   * @minLength 1
   * @maxLength 512
   */
  title?: string;
  /**
   * Detailed description of the task
   *
   * @maxLength 4096
   */
  description?: string | null;
  /**
   * The ID of the configured status
   *
   * @maxLength 128
   */
  statusId?: string;
  /**
   * The priority of the task
   */
  priority?: "low" | "medium" | "high" | "urgent" | any | null;
  /**
   * The user to make responsible for the task, or null to leave it unassigned.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  assignee?: string | null;
  /**
   * ISO timestamp when the task is due
   */
  dueDate?: string | null;
  /**
   * ISO timestamp when the task was completed
   */
  completedAt?: string | null;
  /**
   * Tag IDs applied to the task
   *
   * @maxItems 32
   */
  tags?: string[];
  /**
   * Type of linked CRM record
   */
  linkedEntityType?:
    | "candidate"
    | "client"
    | "booking"
    | "vacancy"
    | any
    | null;
  /**
   * ID of the linked CRM record
   *
   * @pattern ^[0-9a-f]{24}$
   */
  linkedEntityId?: string | null;
  /**
   * Replace checklist items on update
   *
   * @maxItems 100
   */
  checklist?: {
    /**
     * The unique identifier of the resource
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * @minLength 1
     * @maxLength 512
     */
    title: string;
    completed?: boolean;
  }[];
};

export type UpdateTaskVariables = {
  body?: UpdateTaskRequestBody;
  pathParams: UpdateTaskPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateTask = (
  variables: UpdateTaskVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateTaskResponse,
    UpdateTaskError,
    UpdateTaskRequestBody,
    {},
    {},
    UpdateTaskPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}",
    method: "patch",
    ...variables,
    signal,
  });

export const useUpdateTask = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateTaskResponse,
      UpdateTaskError,
      UpdateTaskVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateTaskResponse,
    UpdateTaskError,
    UpdateTaskVariables
  >({
    mutationFn: (variables: UpdateTaskVariables) =>
      fetchUpdateTask(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type DeleteTaskPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
  /**
   * The task identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskId: string;
};

export type DeleteTaskError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type DeleteTaskVariables = {
  pathParams: DeleteTaskPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchDeleteTask = (
  variables: DeleteTaskVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    DeleteTaskError,
    undefined,
    {},
    {},
    DeleteTaskPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useDeleteTask = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteTaskError,
      DeleteTaskVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    DeleteTaskError,
    DeleteTaskVariables
  >({
    mutationFn: (variables: DeleteTaskVariables) =>
      fetchDeleteTask(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTaskActivitiesPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
  /**
   * The task identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskId: string;
};

export type GetTaskActivitiesQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "taskId"
    | "type"
    | "author"
    | "author.id"
    | "author.email"
    | "author.firstName"
    | "author.lastName"
    | "author.username"
    | "author.lea"
    | "author.clients"
    | "author.position"
    | "author.permissions"
    | "author.activationCode"
    | "author.customer"
    | "author.disabled"
    | "author.commissionThreshold"
    | "author.branches"
    | "author.displayOnboarding"
    | "author._customId"
    | "author.signature"
    | "author.phone"
    | "author.defaultCandidateWatcher"
    | "author.defaultTaskWorkspace"
    | "author.teamAdmin"
    | "author.contact"
    | "author.contact.id"
    | "author.contact.forename"
    | "author.contact.surname"
    | "author.contact.email"
    | "author.contact.telephone"
    | "author.contact.mobile"
    | "author.contact.extension"
    | "author.createdAt"
    | "author.evertime.id"
    | "author.evertime"
    | "author.timesheetPortal.id"
    | "author.timesheetPortal.code"
    | "author.timesheetPortal"
    | "content"
    | "metadataJson"
    | "createdAt"
    | ""
  )[];
  /**
   * Related resources to return as objects instead of IDs. Supported values are documented for this endpoint.
   *
   * @maxItems 25
   */
  expand?: "author"[];
  /**
   * Page number
   *
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
};

export type GetTaskActivitiesError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetTaskActivitiesResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * Activity identifier
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The parent task
     *
     * @pattern ^[0-9a-f]{24}$
     */
    taskId?: string;
    /**
     * The activity type
     */
    type?:
      | "created"
      | "comment"
      | "status_change"
      | "assignee_change"
      | "team_change"
      | "due_date_change"
      | "title_change"
      | "description_change"
      | "priority_change"
      | "tags_update"
      | "checklist_update"
      | "attachment_added"
      | "attachment_removed"
      | "completed"
      | "reopened"
      | "linked_entity_type_change"
      | "linked_entity_id_change";
    /**
     * User who triggered the activity. If populated, a user object; otherwise, a user ID.
     */
    author?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * Comment text (for type=comment)
     *
     * @maxLength 4096
     */
    content?: string | null;
    /**
     * JSON-encoded metadata for the activity
     */
    metadataJson?: string | null;
    /**
     * ISO timestamp when the activity occurred
     */
    createdAt?: string;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetTaskActivitiesVariables = {
  pathParams: GetTaskActivitiesPathParams;
  queryParams?: GetTaskActivitiesQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetTaskActivities = (
  variables: GetTaskActivitiesVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetTaskActivitiesResponse,
    GetTaskActivitiesError,
    undefined,
    {},
    GetTaskActivitiesQueryParams,
    GetTaskActivitiesPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}/activities",
    method: "get",
    ...variables,
    signal,
  });

export function getTaskActivitiesQuery(variables: GetTaskActivitiesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetTaskActivitiesResponse>;
};

export function getTaskActivitiesQuery(
  variables: GetTaskActivitiesVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetTaskActivitiesResponse>)
    | reactQuery.SkipToken;
};

export function getTaskActivitiesQuery(
  variables: GetTaskActivitiesVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}/activities",
      operationId: "getTaskActivities",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetTaskActivities(variables, signal),
  };
}

export const useSuspenseGetTaskActivities = <
  TData = GetTaskActivitiesResponse,
>(
  variables: GetTaskActivitiesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetTaskActivitiesResponse,
      GetTaskActivitiesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetTaskActivitiesResponse,
    GetTaskActivitiesError,
    TData
  >({
    ...getTaskActivitiesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTaskActivities = <TData = GetTaskActivitiesResponse,>(
  variables: GetTaskActivitiesVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetTaskActivitiesResponse,
      GetTaskActivitiesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetTaskActivitiesResponse,
    GetTaskActivitiesError,
    TData
  >({
    ...getTaskActivitiesQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateTaskActivityPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
  /**
   * The task identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskId: string;
};

export type CreateTaskActivityError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateTaskActivityResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * Activity identifier
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The parent task
     *
     * @pattern ^[0-9a-f]{24}$
     */
    taskId?: string;
    /**
     * The activity type
     */
    type?:
      | "created"
      | "comment"
      | "status_change"
      | "assignee_change"
      | "team_change"
      | "due_date_change"
      | "title_change"
      | "description_change"
      | "priority_change"
      | "tags_update"
      | "checklist_update"
      | "attachment_added"
      | "attachment_removed"
      | "completed"
      | "reopened"
      | "linked_entity_type_change"
      | "linked_entity_id_change";
    /**
     * User who triggered the activity. If populated, a user object; otherwise, a user ID.
     */
    author?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * Comment text (for type=comment)
     *
     * @maxLength 4096
     */
    content?: string | null;
    /**
     * JSON-encoded metadata for the activity
     */
    metadataJson?: string | null;
    /**
     * ISO timestamp when the activity occurred
     */
    createdAt?: string;
  };
};

export type CreateTaskActivityRequestBody = {
  /**
   * The comment to add to the task. Content must not be empty and is limited to 4,096 characters.
   *
   * @minLength 1
   * @maxLength 4096
   */
  content: string;
};

export type CreateTaskActivityVariables = {
  body: CreateTaskActivityRequestBody;
  pathParams: CreateTaskActivityPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateTaskActivity = (
  variables: CreateTaskActivityVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateTaskActivityResponse,
    CreateTaskActivityError,
    CreateTaskActivityRequestBody,
    {},
    {},
    CreateTaskActivityPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}/activities",
    method: "post",
    ...variables,
    signal,
  });

export const useCreateTaskActivity = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateTaskActivityResponse,
      CreateTaskActivityError,
      CreateTaskActivityVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateTaskActivityResponse,
    CreateTaskActivityError,
    CreateTaskActivityVariables
  >({
    mutationFn: (variables: CreateTaskActivityVariables) =>
      fetchCreateTaskActivity(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type UpdateTaskChecklistItemPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
  /**
   * The task identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskId: string;
  /**
   * The checklist item identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  itemId: string;
};

export type UpdateTaskChecklistItemError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateTaskChecklistItemResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the task
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The workspace this task belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    taskWorkspaceId?: string;
    /**
     * The title of the task
     *
     * @minLength 1
     * @maxLength 512
     */
    title?: string;
    /**
     * Detailed description of the task
     *
     * @maxLength 4096
     */
    description?: string | null;
    /**
     * The ID of the configured status
     *
     * @maxLength 128
     */
    statusId?: string;
    /**
     * The canonical status for filtering and reporting
     */
    canonicalStatus?: "todo" | "in_progress" | "done";
    /**
     * The priority of the task
     */
    priority?: "low" | "medium" | "high" | "urgent" | any | null;
    /**
     * Assignee reference. If expanded, a user object; otherwise, a user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        }
      | (void | null);
    /**
     * Creator reference. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * ISO timestamp when the task is due
     */
    dueDate?: string | null;
    /**
     * ISO timestamp when the task was completed
     */
    completedAt?: string | null;
    /**
     * Tags applied to the task. If expanded, an array of tag objects; otherwise, an array of tag IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * Type of linked CRM record
     */
    linkedEntityType?:
      | "candidate"
      | "client"
      | "booking"
      | "vacancy"
      | any
      | null;
    /**
     * ID of the linked CRM record
     *
     * @pattern ^[0-9a-f]{24}$
     */
    linkedEntityId?: string | null;
    /**
     * True when the user cannot view the linked record
     */
    linkedEntityPermissionDenied?: boolean;
    /**
     * Checklist items for the task
     *
     * @maxItems 100
     */
    checklist?: {
      /**
       * Checklist item identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The checklist item text
       *
       * @minLength 1
       * @maxLength 512
       */
      title?: string;
      /**
       * Whether the item is completed
       */
      completed?: boolean;
      /**
       * ISO timestamp when the item was completed
       */
      completedAt?: string | null;
      /**
       * User who completed the item. If expanded, a user object; otherwise, a user ID.
       */
      completedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          }
        | (void | null);
    }[];
    /**
     * Files attached to the task
     *
     * @maxItems 50
     */
    attachments?: {
      /**
       * Attachment identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The original file name
       *
       * @maxLength 512
       */
      fileName?: string;
      /**
       * MIME type of the file
       *
       * @maxLength 128
       */
      fileType?: string;
      /**
       * Size of the file in bytes
       *
       * @minimum 0
       */
      fileSize?: number;
      /**
       * An opaque reference used to retrieve the attachment. Do not construct or modify this value.
       *
       * @maxLength 1024
       */
      blobName?: string;
      /**
       * User who uploaded the file. If expanded, a user object; otherwise, a user ID.
       */
      uploadedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * ISO timestamp when the file was uploaded
       */
      uploadedAt?: string;
    }[];
    /**
     * Creation timestamp
     */
    createdAt?: string;
    /**
     * Last update timestamp
     */
    updatedAt?: string;
  };
};

export type UpdateTaskChecklistItemRequestBody = {
  /**
   * Whether to mark the item as completed or not
   */
  completed: boolean;
};

export type UpdateTaskChecklistItemVariables = {
  body: UpdateTaskChecklistItemRequestBody;
  pathParams: UpdateTaskChecklistItemPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateTaskChecklistItem = (
  variables: UpdateTaskChecklistItemVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateTaskChecklistItemResponse,
    UpdateTaskChecklistItemError,
    UpdateTaskChecklistItemRequestBody,
    {},
    {},
    UpdateTaskChecklistItemPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}/checklist/{itemId}",
    method: "patch",
    ...variables,
    signal,
  });

export const useUpdateTaskChecklistItem = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateTaskChecklistItemResponse,
      UpdateTaskChecklistItemError,
      UpdateTaskChecklistItemVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateTaskChecklistItemResponse,
    UpdateTaskChecklistItemError,
    UpdateTaskChecklistItemVariables
  >({
    mutationFn: (variables: UpdateTaskChecklistItemVariables) =>
      fetchUpdateTaskChecklistItem(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CreateTaskAttachmentPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
  /**
   * The task identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskId: string;
};

export type CreateTaskAttachmentError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateTaskAttachmentResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the task
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The workspace this task belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    taskWorkspaceId?: string;
    /**
     * The title of the task
     *
     * @minLength 1
     * @maxLength 512
     */
    title?: string;
    /**
     * Detailed description of the task
     *
     * @maxLength 4096
     */
    description?: string | null;
    /**
     * The ID of the configured status
     *
     * @maxLength 128
     */
    statusId?: string;
    /**
     * The canonical status for filtering and reporting
     */
    canonicalStatus?: "todo" | "in_progress" | "done";
    /**
     * The priority of the task
     */
    priority?: "low" | "medium" | "high" | "urgent" | any | null;
    /**
     * Assignee reference. If expanded, a user object; otherwise, a user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        }
      | (void | null);
    /**
     * Creator reference. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * ISO timestamp when the task is due
     */
    dueDate?: string | null;
    /**
     * ISO timestamp when the task was completed
     */
    completedAt?: string | null;
    /**
     * Tags applied to the task. If expanded, an array of tag objects; otherwise, an array of tag IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * Type of linked CRM record
     */
    linkedEntityType?:
      | "candidate"
      | "client"
      | "booking"
      | "vacancy"
      | any
      | null;
    /**
     * ID of the linked CRM record
     *
     * @pattern ^[0-9a-f]{24}$
     */
    linkedEntityId?: string | null;
    /**
     * True when the user cannot view the linked record
     */
    linkedEntityPermissionDenied?: boolean;
    /**
     * Checklist items for the task
     *
     * @maxItems 100
     */
    checklist?: {
      /**
       * Checklist item identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The checklist item text
       *
       * @minLength 1
       * @maxLength 512
       */
      title?: string;
      /**
       * Whether the item is completed
       */
      completed?: boolean;
      /**
       * ISO timestamp when the item was completed
       */
      completedAt?: string | null;
      /**
       * User who completed the item. If expanded, a user object; otherwise, a user ID.
       */
      completedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          }
        | (void | null);
    }[];
    /**
     * Files attached to the task
     *
     * @maxItems 50
     */
    attachments?: {
      /**
       * Attachment identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The original file name
       *
       * @maxLength 512
       */
      fileName?: string;
      /**
       * MIME type of the file
       *
       * @maxLength 128
       */
      fileType?: string;
      /**
       * Size of the file in bytes
       *
       * @minimum 0
       */
      fileSize?: number;
      /**
       * An opaque reference used to retrieve the attachment. Do not construct or modify this value.
       *
       * @maxLength 1024
       */
      blobName?: string;
      /**
       * User who uploaded the file. If expanded, a user object; otherwise, a user ID.
       */
      uploadedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * ISO timestamp when the file was uploaded
       */
      uploadedAt?: string;
    }[];
    /**
     * Creation timestamp
     */
    createdAt?: string;
    /**
     * Last update timestamp
     */
    updatedAt?: string;
  };
};

export type CreateTaskAttachmentRequestBody = {
  /**
   * The binary file to upload. Supported formats and size limits depend on the operation.
   *
   * @format binary
   */
  file: Blob;
};

export type CreateTaskAttachmentVariables = {
  body: CreateTaskAttachmentRequestBody;
  pathParams: CreateTaskAttachmentPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateTaskAttachment = (
  variables: CreateTaskAttachmentVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateTaskAttachmentResponse,
    CreateTaskAttachmentError,
    CreateTaskAttachmentRequestBody,
    {},
    {},
    CreateTaskAttachmentPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}/attachments",
    method: "post",
    ...variables,
    signal,
  });

export const useCreateTaskAttachment = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateTaskAttachmentResponse,
      CreateTaskAttachmentError,
      CreateTaskAttachmentVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateTaskAttachmentResponse,
    CreateTaskAttachmentError,
    CreateTaskAttachmentVariables
  >({
    mutationFn: (variables: CreateTaskAttachmentVariables) =>
      fetchCreateTaskAttachment(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type DownloadTaskAttachmentPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
  /**
   * The task identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskId: string;
  /**
   * The attachment identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  attachmentId: string;
};

export type DownloadTaskAttachmentError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type DownloadTaskAttachmentVariables = {
  pathParams: DownloadTaskAttachmentPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchDownloadTaskAttachment = (
  variables: DownloadTaskAttachmentVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    Blob,
    DownloadTaskAttachmentError,
    undefined,
    {},
    {},
    DownloadTaskAttachmentPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}/attachments/{attachmentId}/download",
    method: "get",
    ...variables,
    signal,
  });

export function downloadTaskAttachmentQuery(
  variables: DownloadTaskAttachmentVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<Blob>;
};

export function downloadTaskAttachmentQuery(
  variables: DownloadTaskAttachmentVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: ((options: QueryFnOptions) => Promise<Blob>) | reactQuery.SkipToken;
};

export function downloadTaskAttachmentQuery(
  variables: DownloadTaskAttachmentVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}/attachments/{attachmentId}/download",
      operationId: "downloadTaskAttachment",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchDownloadTaskAttachment(variables, signal),
  };
}

export const useSuspenseDownloadTaskAttachment = <TData = Blob,>(
  variables: DownloadTaskAttachmentVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Blob, DownloadTaskAttachmentError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<Blob, DownloadTaskAttachmentError, TData>({
    ...downloadTaskAttachmentQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useDownloadTaskAttachment = <TData = Blob,>(
  variables: DownloadTaskAttachmentVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<Blob, DownloadTaskAttachmentError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<Blob, DownloadTaskAttachmentError, TData>({
    ...downloadTaskAttachmentQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type DeleteTaskAttachmentPathParams = {
  /**
   * The task workspace identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskWorkspaceId: string;
  /**
   * The task identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  taskId: string;
  /**
   * The attachment identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  attachmentId: string;
};

export type DeleteTaskAttachmentError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type DeleteTaskAttachmentResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the task
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The workspace this task belongs to
     *
     * @pattern ^[0-9a-f]{24}$
     */
    taskWorkspaceId?: string;
    /**
     * The title of the task
     *
     * @minLength 1
     * @maxLength 512
     */
    title?: string;
    /**
     * Detailed description of the task
     *
     * @maxLength 4096
     */
    description?: string | null;
    /**
     * The ID of the configured status
     *
     * @maxLength 128
     */
    statusId?: string;
    /**
     * The canonical status for filtering and reporting
     */
    canonicalStatus?: "todo" | "in_progress" | "done";
    /**
     * The priority of the task
     */
    priority?: "low" | "medium" | "high" | "urgent" | any | null;
    /**
     * Assignee reference. If expanded, a user object; otherwise, a user ID.
     */
    assignee?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        }
      | (void | null);
    /**
     * Creator reference. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * ISO timestamp when the task is due
     */
    dueDate?: string | null;
    /**
     * ISO timestamp when the task was completed
     */
    completedAt?: string | null;
    /**
     * Tags applied to the task. If expanded, an array of tag objects; otherwise, an array of tag IDs.
     */
    tags?:
      | string[]
      | {
          /**
           * The unique identifier of the tag
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The label displayed wherever this tag is applied.
           *
           * @minLength 1
           * @maxLength 128
           */
          name?: string;
          /**
           * The tag display colour as a hexadecimal value, for example #2563EB.
           *
           * @maxLength 32
           */
          color?: string | null;
        }[];
    /**
     * Type of linked CRM record
     */
    linkedEntityType?:
      | "candidate"
      | "client"
      | "booking"
      | "vacancy"
      | any
      | null;
    /**
     * ID of the linked CRM record
     *
     * @pattern ^[0-9a-f]{24}$
     */
    linkedEntityId?: string | null;
    /**
     * True when the user cannot view the linked record
     */
    linkedEntityPermissionDenied?: boolean;
    /**
     * Checklist items for the task
     *
     * @maxItems 100
     */
    checklist?: {
      /**
       * Checklist item identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The checklist item text
       *
       * @minLength 1
       * @maxLength 512
       */
      title?: string;
      /**
       * Whether the item is completed
       */
      completed?: boolean;
      /**
       * ISO timestamp when the item was completed
       */
      completedAt?: string | null;
      /**
       * User who completed the item. If expanded, a user object; otherwise, a user ID.
       */
      completedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          }
        | (void | null);
    }[];
    /**
     * Files attached to the task
     *
     * @maxItems 50
     */
    attachments?: {
      /**
       * Attachment identifier
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The original file name
       *
       * @maxLength 512
       */
      fileName?: string;
      /**
       * MIME type of the file
       *
       * @maxLength 128
       */
      fileType?: string;
      /**
       * Size of the file in bytes
       *
       * @minimum 0
       */
      fileSize?: number;
      /**
       * An opaque reference used to retrieve the attachment. Do not construct or modify this value.
       *
       * @maxLength 1024
       */
      blobName?: string;
      /**
       * User who uploaded the file. If expanded, a user object; otherwise, a user ID.
       */
      uploadedBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * ISO timestamp when the file was uploaded
       */
      uploadedAt?: string;
    }[];
    /**
     * Creation timestamp
     */
    createdAt?: string;
    /**
     * Last update timestamp
     */
    updatedAt?: string;
  };
};

export type DeleteTaskAttachmentVariables = {
  pathParams: DeleteTaskAttachmentPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchDeleteTaskAttachment = (
  variables: DeleteTaskAttachmentVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    DeleteTaskAttachmentResponse,
    DeleteTaskAttachmentError,
    undefined,
    {},
    {},
    DeleteTaskAttachmentPathParams
  >({
    url: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}/attachments/{attachmentId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useDeleteTaskAttachment = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      DeleteTaskAttachmentResponse,
      DeleteTaskAttachmentError,
      DeleteTaskAttachmentVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    DeleteTaskAttachmentResponse,
    DeleteTaskAttachmentError,
    DeleteTaskAttachmentVariables
  >({
    mutationFn: (variables: DeleteTaskAttachmentVariables) =>
      fetchDeleteTaskAttachment(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetTagsQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   *
   * @maxItems 250
   */
  select?: ("id" | "name" | "color" | "")[];
  /**
   * Search tags by name
   *
   * @maxLength 256
   */
  search?: string;
  /**
   * Page number
   *
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * Field used to order tags. Prefix the field with '-' for descending order.
   */
  sort?:
    | "name"
    | "-name"
    | "createdAt"
    | "-createdAt"
    | "updatedAt"
    | "-updatedAt";
};

export type GetTagsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetTagsResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the tag
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The label displayed wherever this tag is applied.
     *
     * @minLength 1
     * @maxLength 128
     */
    name?: string;
    /**
     * The tag display colour as a hexadecimal value, for example #2563EB.
     *
     * @maxLength 32
     */
    color?: string | null;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetTagsVariables = {
  queryParams?: GetTagsQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetTags = (
  variables: GetTagsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetTagsResponse,
    GetTagsError,
    undefined,
    {},
    GetTagsQueryParams,
    {}
  >({ url: "/tags", method: "get", ...variables, signal });

export function getTagsQuery(variables: GetTagsVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetTagsResponse>;
};

export function getTagsQuery(
  variables: GetTagsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetTagsResponse>)
    | reactQuery.SkipToken;
};

export function getTagsQuery(
  variables: GetTagsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/tags",
      operationId: "getTags",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetTags(variables, signal),
  };
}

export const useSuspenseGetTags = <TData = GetTagsResponse,>(
  variables: GetTagsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetTagsResponse, GetTagsError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<GetTagsResponse, GetTagsError, TData>({
    ...getTagsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetTags = <TData = GetTagsResponse,>(
  variables: GetTagsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetTagsResponse, GetTagsError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetTagsResponse, GetTagsError, TData>({
    ...getTagsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateTagError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateTagResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the tag
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The label displayed wherever this tag is applied.
     *
     * @minLength 1
     * @maxLength 128
     */
    name?: string;
    /**
     * The tag display colour as a hexadecimal value, for example #2563EB.
     *
     * @maxLength 32
     */
    color?: string | null;
  };
};

export type CreateTagRequestBody = {
  /**
   * The label to display wherever the new tag is applied. Tag names must not be empty.
   *
   * @minLength 1
   * @maxLength 128
   */
  name: string;
  /**
   * The tag display colour as a hexadecimal value, for example #2563EB.
   *
   * @maxLength 32
   */
  color?: string | null;
};

export type CreateTagVariables = {
  body: CreateTagRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateTag = (
  variables: CreateTagVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateTagResponse,
    CreateTagError,
    CreateTagRequestBody,
    {},
    {},
    {}
  >({ url: "/tags", method: "post", ...variables, signal });

export const useCreateTag = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateTagResponse,
      CreateTagError,
      CreateTagVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateTagResponse,
    CreateTagError,
    CreateTagVariables
  >({
    mutationFn: (variables: CreateTagVariables) =>
      fetchCreateTag(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetBookingsQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "updatedAt"
    | "candidate"
    | "client"
    | "customer"
    | "assignee"
    | "student"
    | "clientName"
    | "candidateName"
    | "studentName"
    | "clientAddress.addressLine1"
    | "clientAddress.addressLine2"
    | "clientAddress.addressLine3"
    | "clientAddress.city"
    | "clientAddress.county"
    | "clientAddress.borough"
    | "clientAddress.postcode"
    | "clientAddress.country"
    | "clientAddress.lat"
    | "clientAddress.lng"
    | "clientAddress"
    | "role"
    | "roleCode"
    | "type"
    | "subType"
    | "status"
    | "start"
    | "end"
    | "startTime"
    | "endTime"
    | "arrivalTime"
    | "days"
    | "dayLength"
    | "dayAmPm"
    | "weeklyRota.1.start"
    | "weeklyRota.1.end"
    | "weeklyRota.1.overridePayment"
    | "weeklyRota.1.chargeRate"
    | "weeklyRota.1.payRate"
    | "weeklyRota.1"
    | "weeklyRota.2.start"
    | "weeklyRota.2.end"
    | "weeklyRota.2.overridePayment"
    | "weeklyRota.2.chargeRate"
    | "weeklyRota.2.payRate"
    | "weeklyRota.2"
    | "weeklyRota.3.start"
    | "weeklyRota.3.end"
    | "weeklyRota.3.overridePayment"
    | "weeklyRota.3.chargeRate"
    | "weeklyRota.3.payRate"
    | "weeklyRota.3"
    | "weeklyRota.4.start"
    | "weeklyRota.4.end"
    | "weeklyRota.4.overridePayment"
    | "weeklyRota.4.chargeRate"
    | "weeklyRota.4.payRate"
    | "weeklyRota.4"
    | "weeklyRota.5.start"
    | "weeklyRota.5.end"
    | "weeklyRota.5.overridePayment"
    | "weeklyRota.5.chargeRate"
    | "weeklyRota.5.payRate"
    | "weeklyRota.5"
    | "weeklyRota.6.start"
    | "weeklyRota.6.end"
    | "weeklyRota.6.overridePayment"
    | "weeklyRota.6.chargeRate"
    | "weeklyRota.6.payRate"
    | "weeklyRota.6"
    | "weeklyRota.7.start"
    | "weeklyRota.7.end"
    | "weeklyRota.7.overridePayment"
    | "weeklyRota.7.chargeRate"
    | "weeklyRota.7.payRate"
    | "weeklyRota.7"
    | "weeklyRota"
    | "chargeRate"
    | "payRate"
    | "feePercentage"
    | "hours"
    | "payType"
    | "candidatePayType"
    | "salaryPayType"
    | "salary"
    | "oncosts"
    | "breaks"
    | "commission"
    | "commissionType"
    | "commissionWeekly"
    | "bookingSplits"
    | "excludeFromProfitMargins"
    | "excludeFromCommission"
    | "awr"
    | "awrReminder"
    | "clientContact"
    | "timesheetContact"
    | "invoiceContact"
    | "additionalClientContacts"
    | "additionalInstructions"
    | "additionalInstructionsClient"
    | "jobDescription"
    | "rightToWorkCheck"
    | "healthSafetyCheck"
    | "healthSafetyCheckContact"
    | "attending"
    | "trnNumber"
    | "trnNumberCheckedDate"
    | "declarationIncludes"
    | "poNumber"
    | "bookingSuccess"
    | "bookingCancelReason"
    | "occupation"
    | "subject"
    | "archived"
    | "completed"
    | "ccsClientBooking"
    | "jobType"
    | "keyStage"
    | "feeType"
    | "curriculumSubject"
    | "ccsNominatedWorker"
    | "ccsRelevantKnowledgeAndAppropriateSkills"
    | "buyersReference"
    | "agencyFee"
    | "expenses"
    | "ccsLotOptionId"
    | "ccsLotNumber"
    | "ccsServiceDescription"
    | "_placementId"
    | "evertime.placementId"
    | "evertime.id"
    | "evertime"
    | "timesheetPortal.placementId"
    | "timesheetPortal.placementCode"
    | "timesheetPortal.id"
    | "timesheetPortal"
    | "_parentBooking"
    | "_vacancyId"
    | "_manualParentBooking"
    | "synced"
    | "syncedAt"
    | "migrationId"
    | "migrationApproval"
    | ""
  )[];
  /**
   * Search bookings by role, client name or candidate name
   *
   * @maxLength 256
   */
  search?: string;
  /**
   * Filter bookings based on various criteria
   */
  filter?: {
    /**
     * Filter bookings by their IDs. Maximum 50.
     *
     * @maxItems 50
     */
    ids?: string[];
    /**
     * Filter bookings for one candidate or any of several candidates.
     *
     * @minItems 1
     * @maxItems 100
     */
    candidate?: string[];
    /**
     * Filter bookings for one client or any of several clients.
     *
     * @minItems 1
     * @maxItems 10
     */
    client?: string[];
    /**
     * Filter bookings from one migration. Requires permission to manage migrations.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Filter bookings by their migration approval decision.
     *
     * @maxItems 3
     */
    migrationApproval?: ("pending" | "approved" | "rejected")[];
    /**
     * Filter bookings that overlap an absolute or relative date range.
     */
    overlaps?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
  };
  /**
   * Page number
   *
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
};

export type GetBookingsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetBookingsResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the booking
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * Last modification time; used to detect stale draft assignments.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?$
     */
    updatedAt?: string;
    /**
     * Reference to the candidate
     *
     * @pattern ^[0-9a-f]{24}$
     */
    candidate?: string;
    /**
     * Reference to the client
     *
     * @pattern ^[0-9a-f]{24}$
     */
    client?: string;
    /**
     * Reference to the customer/company
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customer?: string;
    /**
     * Reference to the assigned user
     *
     * @pattern ^[0-9a-f]{24}$
     */
    assignee?: string;
    /**
     * Reference to the student
     *
     * @pattern ^[0-9a-f]{24}$
     */
    student?: string | null;
    /**
     * Denormalised client name
     */
    clientName?: string;
    /**
     * Denormalised candidate name
     */
    candidateName?: string;
    /**
     * Denormalised student name
     */
    studentName?: string;
    /**
     * Client site address for this booking
     */
    clientAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The role for this booking
     */
    role?: string;
    /**
     * The role code
     */
    roleCode?: string | null;
    /**
     * The type of booking
     */
    type?: "Standard" | "Interview" | "Trial" | "Perm" | "Tutor";
    /**
     * The sub-type of booking (tutor categories)
     */
    subType?:
      | "TutorStandard"
      | "TutorMentor"
      | "TutorBasic"
      | "FaceToFace"
      | any
      | null;
    /**
     * The booking status
     */
    status?: "Active" | "Cancelled";
    /**
     * Booking start date
     */
    start?: string;
    /**
     * Booking end date
     */
    end?: string;
    /**
     * Daily start time
     */
    startTime?: string;
    /**
     * Daily end time
     */
    endTime?: string;
    /**
     * Expected arrival time
     */
    arrivalTime?: string;
    /**
     * Individual booking days
     */
    days?: {
      /**
       * The start date and time for the booking day
       */
      start?: string;
      /**
       * The end date and time for the booking day
       */
      end?: string;
      /**
       * The length of the booking day
       */
      length?: "Half Day" | "Full Day";
      /**
       * Indicates whether the booking day is in the AM or PM
       */
      amPm?: "AM" | "PM";
      /**
       * The current status of the booking day
       */
      status?:
        | "Active"
        | "Sickness"
        | "NoShow"
        | "Client Cancelled"
        | "Act Of God"
        | "Personal Appointment";
      /**
       * The charge rate for the booking day
       *
       * @maxLength 32
       */
      chargeRate?: string;
      /**
       * The pay rate for the booking day
       *
       * @maxLength 32
       */
      payRate?: string;
      /**
       * Break duration inherited for this booking day.
       */
      breaks?: string;
      /**
       * Expenses payable for this booking day.
       */
      expenses?: string;
      /**
       * Indicates whether the booking day is flagged for removal
       */
      flaggedForRemoval?: boolean;
    }[];
    /**
     * Default day length
     */
    dayLength?: "Full" | "Half" | "Mixed" | any | null;
    /**
     * AM or PM for half-day bookings
     */
    dayAmPm?: "AM" | "PM" | any | null;
    /**
     * Weekly rota pattern
     */
    weeklyRota?: {
      /**
       * The rota for Monday
       */
      ["1"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Tuesday
       */
      ["2"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Wednesday
       */
      ["3"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Thursday
       */
      ["4"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Friday
       */
      ["5"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Saturday
       */
      ["6"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Sunday
       */
      ["7"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
    };
    /**
     * The rate charged to the client for the booked work.
     */
    chargeRate?: string;
    /**
     * The rate paid to the candidate for the booked work.
     */
    payRate?: string;
    /**
     * The percentage fee applied to the booking when percentage-based charging is used.
     */
    feePercentage?: string;
    /**
     * The expected number of working hours for the booking.
     */
    hours?: string;
    /**
     * Whether pay is daily or hourly
     */
    payType?: "Day" | "Hour";
    /**
     * Candidate payment method
     */
    candidatePayType?: "Paye" | "Umbrella" | "LtdCompany" | "NotApplicable";
    /**
     * Salary payment frequency
     */
    salaryPayType?: "Annual" | "Daily" | any | null;
    /**
     * Salary amount
     */
    salary?: string;
    /**
     * On-costs percentage
     */
    oncosts?: number;
    /**
     * Break duration
     */
    breaks?: string | null;
    /**
     * Commission amount (number or structured)
     */
    commission?: number | string;
    /**
     * Fixed or variable commission
     */
    commissionType?: "Fixed" | "Variable";
    /**
     * Whether commission is calculated weekly
     */
    commissionWeekly?: boolean;
    /**
     * Commission splits between users
     */
    bookingSplits?:
      | {
          /**
           * Reference to the split user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          user?: string;
          /**
           * Denormalised name of the split user
           */
          usersname?: string;
          /**
           * The split percentage for this user
           */
          split?: number;
          /**
           * Commission for this split
           */
          commission?: number | string;
        }[]
      | null;
    /**
     * Exclude from profit margin calculations
     */
    excludeFromProfitMargins?: boolean;
    /**
     * Exclude from commission calculations
     */
    excludeFromCommission?: boolean;
    /**
     * Agency Workers Regulations status
     */
    awr?: string;
    /**
     * The reminder date used to monitor Agency Workers Regulations requirements.
     */
    awrReminder?: string;
    /**
     * Primary client contact
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientContact?: string | null;
    /**
     * Timesheet approver contact
     *
     * @pattern ^[0-9a-f]{24}$
     */
    timesheetContact?: string | null;
    /**
     * Invoice contact
     *
     * @pattern ^[0-9a-f]{24}$
     */
    invoiceContact?: string | null;
    /**
     * Additional client contact references
     */
    additionalClientContacts?: string[] | null;
    /**
     * Additional booking instructions for users managing the placement. These are not the client-facing instructions.
     */
    additionalInstructions?: string;
    /**
     * Instructions visible to the client
     */
    additionalInstructionsClient?: string;
    /**
     * Job description for this booking
     */
    jobDescription?: string;
    /**
     * Right to work check status
     */
    rightToWorkCheck?: string | null;
    /**
     * Whether H&S check has been completed
     */
    healthSafetyCheck?: boolean;
    /**
     * Contact for H&S check
     */
    healthSafetyCheckContact?: string;
    /**
     * Whether the candidate is attending
     */
    attending?: boolean | null;
    /**
     * Teacher Reference Number
     */
    trnNumber?: string;
    /**
     * When the TRN was last verified
     */
    trnNumberCheckedDate?: string;
    /**
     * List of declaration items
     */
    declarationIncludes?: string[] | null;
    /**
     * Purchase order number
     */
    poNumber?: string;
    /**
     * Booking success details
     */
    bookingSuccess?: string | null;
    /**
     * Numeric reason code for cancellation
     */
    bookingCancelReason?: number | null;
    /**
     * The occupation for this booking
     */
    occupation?: string | null;
    /**
     * Subject for this booking
     */
    subject?: string | null;
    /**
     * Whether the booking is archived
     */
    archived?: boolean | null;
    /**
     * Whether the booking is completed
     */
    completed?: boolean | null;
    /**
     * Whether this is a GCA client booking
     */
    ccsClientBooking?: boolean | null;
    /**
     * The type of work for a GCA booking, when specified.
     */
    jobType?:
      | "Teacher: SEND and non-SEND"
      | "Educational Support Staff non SEND"
      | "Educational Support Staff SEND"
      | "Senior Roles"
      | "Facilities Management"
      | "Admin and Clerical"
      | "Other"
      | any
      | null;
    /**
     * The education stage for a GCA booking, when specified.
     */
    keyStage?:
      | "Early Years"
      | "Key Stage 1"
      | "Early Years and Key Stage 1"
      | "Key Stage 2"
      | "Key Stage 1 and 2"
      | "Key Stage 3"
      | "Key Stage 4"
      | "Key Stage 3 and 4"
      | "Key Stage 5 / FE"
      | "Non-School Setting"
      | "School Administration / Non-Teaching"
      | any
      | null;
    /**
     * The fee arrangement for a GCA booking, when specified.
     */
    feeType?:
      | "Contingent"
      | "Temp to Perm Fee"
      | "Fixed Term Appointment"
      | "Permanent Appointment"
      | any
      | null;
    /**
     * The curriculum subject for a GCA booking, when specified.
     *
     * @minLength 1
     * @maxLength 256
     */
    curriculumSubject?: string | null;
    /**
     * Whether the candidate is a GCA nominated worker
     */
    ccsNominatedWorker?: boolean | null;
    /**
     * Whether the candidate has the relevant knowledge and appropriate skills for this GCA booking.
     */
    ccsRelevantKnowledgeAndAppropriateSkills?: boolean | null;
    /**
     * The buyer-provided reference for this GCA booking.
     *
     * @maxLength 256
     */
    buyersReference?: string | null;
    /**
     * The supplier fee recorded for this GCA booking.
     *
     * @maxLength 64
     */
    agencyFee?: string | null;
    /**
     * Expenses payable for the booking.
     *
     * @maxLength 4096
     */
    expenses?: string;
    /**
     * The configured GCA lot option selected for this booking
     *
     * @pattern ^[0-9a-f]{24}$
     */
    ccsLotOptionId?: string | null;
    /**
     * The GCA lot selected for this booking
     *
     * @maxLength 256
     */
    ccsLotNumber?: string | null;
    /**
     * The service description associated with the selected GCA lot
     *
     * @maxLength 256
     */
    ccsServiceDescription?: string | null;
    /**
     * External placement identifier
     */
    _placementId?: string;
    /**
     * Evertime identifiers for this booking
     */
    evertime?: {
      /**
       * The Evertime PlacementId used for this booking's placement
       *
       * @maxLength 256
       */
      placementId?: string;
      /**
       * Timesheet identifier returned by Evertime
       *
       * @minimum 0
       * @exclusiveMinimum true
       */
      id?: number;
    };
    /**
     * Timesheet Portal identifiers for this booking
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal placement GUID used for this booking's job
       *
       * @format uuid
       */
      placementId?: string;
      /**
       * The Timesheet Portal placement code
       *
       * @maxLength 256
       */
      placementCode?: string;
      /**
       * The Timesheet Portal timesheet GUID when this booking is exported without a timesheet record
       *
       * @format uuid
       */
      id?: string;
    };
    /**
     * Reference to the parent booking
     *
     * @pattern ^[0-9a-f]{24}$
     */
    _parentBooking?: string;
    /**
     * Reference to the originating vacancy
     *
     * @pattern ^[0-9a-f]{24}$
     */
    _vacancyId?: string | null;
    /**
     * Manually linked parent booking reference
     */
    _manualParentBooking?: string | null;
    /**
     * Whether data has been synced
     */
    synced?: boolean;
    /**
     * When data was last synced
     *
     * @format date-time
     */
    syncedAt?: string | null;
    /**
     * The migration that created this booking
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Whether this migrated booking has been approved
     */
    migrationApproval?: "pending" | "approved" | "rejected";
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetBookingsVariables = {
  queryParams?: GetBookingsQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetBookings = (
  variables: GetBookingsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetBookingsResponse,
    GetBookingsError,
    undefined,
    {},
    GetBookingsQueryParams,
    {}
  >({ url: "/bookings", method: "get", ...variables, signal });

export function getBookingsQuery(variables: GetBookingsVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetBookingsResponse>;
};

export function getBookingsQuery(
  variables: GetBookingsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetBookingsResponse>)
    | reactQuery.SkipToken;
};

export function getBookingsQuery(
  variables: GetBookingsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/bookings",
      operationId: "getBookings",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetBookings(variables, signal),
  };
}

export const useSuspenseGetBookings = <TData = GetBookingsResponse,>(
  variables: GetBookingsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetBookingsResponse, GetBookingsError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetBookingsResponse,
    GetBookingsError,
    TData
  >({
    ...getBookingsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetBookings = <TData = GetBookingsResponse,>(
  variables: GetBookingsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetBookingsResponse, GetBookingsError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetBookingsResponse, GetBookingsError, TData>({
    ...getBookingsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetBookingPathParams = {
  /**
   * The unique identifier for the booking.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  bookingId: string;
};

export type GetBookingQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "updatedAt"
    | "candidate"
    | "client"
    | "customer"
    | "assignee"
    | "student"
    | "clientName"
    | "candidateName"
    | "studentName"
    | "clientAddress.addressLine1"
    | "clientAddress.addressLine2"
    | "clientAddress.addressLine3"
    | "clientAddress.city"
    | "clientAddress.county"
    | "clientAddress.borough"
    | "clientAddress.postcode"
    | "clientAddress.country"
    | "clientAddress.lat"
    | "clientAddress.lng"
    | "clientAddress"
    | "role"
    | "roleCode"
    | "type"
    | "subType"
    | "status"
    | "start"
    | "end"
    | "startTime"
    | "endTime"
    | "arrivalTime"
    | "days"
    | "dayLength"
    | "dayAmPm"
    | "weeklyRota.1.start"
    | "weeklyRota.1.end"
    | "weeklyRota.1.overridePayment"
    | "weeklyRota.1.chargeRate"
    | "weeklyRota.1.payRate"
    | "weeklyRota.1"
    | "weeklyRota.2.start"
    | "weeklyRota.2.end"
    | "weeklyRota.2.overridePayment"
    | "weeklyRota.2.chargeRate"
    | "weeklyRota.2.payRate"
    | "weeklyRota.2"
    | "weeklyRota.3.start"
    | "weeklyRota.3.end"
    | "weeklyRota.3.overridePayment"
    | "weeklyRota.3.chargeRate"
    | "weeklyRota.3.payRate"
    | "weeklyRota.3"
    | "weeklyRota.4.start"
    | "weeklyRota.4.end"
    | "weeklyRota.4.overridePayment"
    | "weeklyRota.4.chargeRate"
    | "weeklyRota.4.payRate"
    | "weeklyRota.4"
    | "weeklyRota.5.start"
    | "weeklyRota.5.end"
    | "weeklyRota.5.overridePayment"
    | "weeklyRota.5.chargeRate"
    | "weeklyRota.5.payRate"
    | "weeklyRota.5"
    | "weeklyRota.6.start"
    | "weeklyRota.6.end"
    | "weeklyRota.6.overridePayment"
    | "weeklyRota.6.chargeRate"
    | "weeklyRota.6.payRate"
    | "weeklyRota.6"
    | "weeklyRota.7.start"
    | "weeklyRota.7.end"
    | "weeklyRota.7.overridePayment"
    | "weeklyRota.7.chargeRate"
    | "weeklyRota.7.payRate"
    | "weeklyRota.7"
    | "weeklyRota"
    | "chargeRate"
    | "payRate"
    | "feePercentage"
    | "hours"
    | "payType"
    | "candidatePayType"
    | "salaryPayType"
    | "salary"
    | "oncosts"
    | "breaks"
    | "commission"
    | "commissionType"
    | "commissionWeekly"
    | "bookingSplits"
    | "excludeFromProfitMargins"
    | "excludeFromCommission"
    | "awr"
    | "awrReminder"
    | "clientContact"
    | "timesheetContact"
    | "invoiceContact"
    | "additionalClientContacts"
    | "additionalInstructions"
    | "additionalInstructionsClient"
    | "jobDescription"
    | "rightToWorkCheck"
    | "healthSafetyCheck"
    | "healthSafetyCheckContact"
    | "attending"
    | "trnNumber"
    | "trnNumberCheckedDate"
    | "declarationIncludes"
    | "poNumber"
    | "bookingSuccess"
    | "bookingCancelReason"
    | "occupation"
    | "subject"
    | "archived"
    | "completed"
    | "ccsClientBooking"
    | "jobType"
    | "keyStage"
    | "feeType"
    | "curriculumSubject"
    | "ccsNominatedWorker"
    | "ccsRelevantKnowledgeAndAppropriateSkills"
    | "buyersReference"
    | "agencyFee"
    | "expenses"
    | "ccsLotOptionId"
    | "ccsLotNumber"
    | "ccsServiceDescription"
    | "_placementId"
    | "evertime.placementId"
    | "evertime.id"
    | "evertime"
    | "timesheetPortal.placementId"
    | "timesheetPortal.placementCode"
    | "timesheetPortal.id"
    | "timesheetPortal"
    | "_parentBooking"
    | "_vacancyId"
    | "_manualParentBooking"
    | "synced"
    | "syncedAt"
    | "migrationId"
    | "migrationApproval"
    | ""
  )[];
};

export type GetBookingError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetBookingResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the booking
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * Last modification time; used to detect stale draft assignments.
     *
     * @pattern ^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?$
     */
    updatedAt?: string;
    /**
     * Reference to the candidate
     *
     * @pattern ^[0-9a-f]{24}$
     */
    candidate?: string;
    /**
     * Reference to the client
     *
     * @pattern ^[0-9a-f]{24}$
     */
    client?: string;
    /**
     * Reference to the customer/company
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customer?: string;
    /**
     * Reference to the assigned user
     *
     * @pattern ^[0-9a-f]{24}$
     */
    assignee?: string;
    /**
     * Reference to the student
     *
     * @pattern ^[0-9a-f]{24}$
     */
    student?: string | null;
    /**
     * Denormalised client name
     */
    clientName?: string;
    /**
     * Denormalised candidate name
     */
    candidateName?: string;
    /**
     * Denormalised student name
     */
    studentName?: string;
    /**
     * Client site address for this booking
     */
    clientAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * The role for this booking
     */
    role?: string;
    /**
     * The role code
     */
    roleCode?: string | null;
    /**
     * The type of booking
     */
    type?: "Standard" | "Interview" | "Trial" | "Perm" | "Tutor";
    /**
     * The sub-type of booking (tutor categories)
     */
    subType?:
      | "TutorStandard"
      | "TutorMentor"
      | "TutorBasic"
      | "FaceToFace"
      | any
      | null;
    /**
     * The booking status
     */
    status?: "Active" | "Cancelled";
    /**
     * Booking start date
     */
    start?: string;
    /**
     * Booking end date
     */
    end?: string;
    /**
     * Daily start time
     */
    startTime?: string;
    /**
     * Daily end time
     */
    endTime?: string;
    /**
     * Expected arrival time
     */
    arrivalTime?: string;
    /**
     * Individual booking days
     */
    days?: {
      /**
       * The start date and time for the booking day
       */
      start?: string;
      /**
       * The end date and time for the booking day
       */
      end?: string;
      /**
       * The length of the booking day
       */
      length?: "Half Day" | "Full Day";
      /**
       * Indicates whether the booking day is in the AM or PM
       */
      amPm?: "AM" | "PM";
      /**
       * The current status of the booking day
       */
      status?:
        | "Active"
        | "Sickness"
        | "NoShow"
        | "Client Cancelled"
        | "Act Of God"
        | "Personal Appointment";
      /**
       * The charge rate for the booking day
       *
       * @maxLength 32
       */
      chargeRate?: string;
      /**
       * The pay rate for the booking day
       *
       * @maxLength 32
       */
      payRate?: string;
      /**
       * Break duration inherited for this booking day.
       */
      breaks?: string;
      /**
       * Expenses payable for this booking day.
       */
      expenses?: string;
      /**
       * Indicates whether the booking day is flagged for removal
       */
      flaggedForRemoval?: boolean;
    }[];
    /**
     * Default day length
     */
    dayLength?: "Full" | "Half" | "Mixed" | any | null;
    /**
     * AM or PM for half-day bookings
     */
    dayAmPm?: "AM" | "PM" | any | null;
    /**
     * Weekly rota pattern
     */
    weeklyRota?: {
      /**
       * The rota for Monday
       */
      ["1"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Tuesday
       */
      ["2"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Wednesday
       */
      ["3"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Thursday
       */
      ["4"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Friday
       */
      ["5"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Saturday
       */
      ["6"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
      /**
       * The rota for Sunday
       */
      ["7"]?: {
        /**
         * The start time for the day
         */
        start?: string;
        /**
         * The end time for the day
         */
        end?: string;
        /**
         * Whether to override the default payment for the day
         */
        overridePayment?: boolean;
        /**
         * The charge rate for the day
         */
        chargeRate?: string;
        /**
         * The pay rate for the day
         */
        payRate?: string;
      };
    };
    /**
     * The rate charged to the client for the booked work.
     */
    chargeRate?: string;
    /**
     * The rate paid to the candidate for the booked work.
     */
    payRate?: string;
    /**
     * The percentage fee applied to the booking when percentage-based charging is used.
     */
    feePercentage?: string;
    /**
     * The expected number of working hours for the booking.
     */
    hours?: string;
    /**
     * Whether pay is daily or hourly
     */
    payType?: "Day" | "Hour";
    /**
     * Candidate payment method
     */
    candidatePayType?: "Paye" | "Umbrella" | "LtdCompany" | "NotApplicable";
    /**
     * Salary payment frequency
     */
    salaryPayType?: "Annual" | "Daily" | any | null;
    /**
     * Salary amount
     */
    salary?: string;
    /**
     * On-costs percentage
     */
    oncosts?: number;
    /**
     * Break duration
     */
    breaks?: string | null;
    /**
     * Commission amount (number or structured)
     */
    commission?: number | string;
    /**
     * Fixed or variable commission
     */
    commissionType?: "Fixed" | "Variable";
    /**
     * Whether commission is calculated weekly
     */
    commissionWeekly?: boolean;
    /**
     * Commission splits between users
     */
    bookingSplits?:
      | {
          /**
           * Reference to the split user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          user?: string;
          /**
           * Denormalised name of the split user
           */
          usersname?: string;
          /**
           * The split percentage for this user
           */
          split?: number;
          /**
           * Commission for this split
           */
          commission?: number | string;
        }[]
      | null;
    /**
     * Exclude from profit margin calculations
     */
    excludeFromProfitMargins?: boolean;
    /**
     * Exclude from commission calculations
     */
    excludeFromCommission?: boolean;
    /**
     * Agency Workers Regulations status
     */
    awr?: string;
    /**
     * The reminder date used to monitor Agency Workers Regulations requirements.
     */
    awrReminder?: string;
    /**
     * Primary client contact
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientContact?: string | null;
    /**
     * Timesheet approver contact
     *
     * @pattern ^[0-9a-f]{24}$
     */
    timesheetContact?: string | null;
    /**
     * Invoice contact
     *
     * @pattern ^[0-9a-f]{24}$
     */
    invoiceContact?: string | null;
    /**
     * Additional client contact references
     */
    additionalClientContacts?: string[] | null;
    /**
     * Additional booking instructions for users managing the placement. These are not the client-facing instructions.
     */
    additionalInstructions?: string;
    /**
     * Instructions visible to the client
     */
    additionalInstructionsClient?: string;
    /**
     * Job description for this booking
     */
    jobDescription?: string;
    /**
     * Right to work check status
     */
    rightToWorkCheck?: string | null;
    /**
     * Whether H&S check has been completed
     */
    healthSafetyCheck?: boolean;
    /**
     * Contact for H&S check
     */
    healthSafetyCheckContact?: string;
    /**
     * Whether the candidate is attending
     */
    attending?: boolean | null;
    /**
     * Teacher Reference Number
     */
    trnNumber?: string;
    /**
     * When the TRN was last verified
     */
    trnNumberCheckedDate?: string;
    /**
     * List of declaration items
     */
    declarationIncludes?: string[] | null;
    /**
     * Purchase order number
     */
    poNumber?: string;
    /**
     * Booking success details
     */
    bookingSuccess?: string | null;
    /**
     * Numeric reason code for cancellation
     */
    bookingCancelReason?: number | null;
    /**
     * The occupation for this booking
     */
    occupation?: string | null;
    /**
     * Subject for this booking
     */
    subject?: string | null;
    /**
     * Whether the booking is archived
     */
    archived?: boolean | null;
    /**
     * Whether the booking is completed
     */
    completed?: boolean | null;
    /**
     * Whether this is a GCA client booking
     */
    ccsClientBooking?: boolean | null;
    /**
     * The type of work for a GCA booking, when specified.
     */
    jobType?:
      | "Teacher: SEND and non-SEND"
      | "Educational Support Staff non SEND"
      | "Educational Support Staff SEND"
      | "Senior Roles"
      | "Facilities Management"
      | "Admin and Clerical"
      | "Other"
      | any
      | null;
    /**
     * The education stage for a GCA booking, when specified.
     */
    keyStage?:
      | "Early Years"
      | "Key Stage 1"
      | "Early Years and Key Stage 1"
      | "Key Stage 2"
      | "Key Stage 1 and 2"
      | "Key Stage 3"
      | "Key Stage 4"
      | "Key Stage 3 and 4"
      | "Key Stage 5 / FE"
      | "Non-School Setting"
      | "School Administration / Non-Teaching"
      | any
      | null;
    /**
     * The fee arrangement for a GCA booking, when specified.
     */
    feeType?:
      | "Contingent"
      | "Temp to Perm Fee"
      | "Fixed Term Appointment"
      | "Permanent Appointment"
      | any
      | null;
    /**
     * The curriculum subject for a GCA booking, when specified.
     *
     * @minLength 1
     * @maxLength 256
     */
    curriculumSubject?: string | null;
    /**
     * Whether the candidate is a GCA nominated worker
     */
    ccsNominatedWorker?: boolean | null;
    /**
     * Whether the candidate has the relevant knowledge and appropriate skills for this GCA booking.
     */
    ccsRelevantKnowledgeAndAppropriateSkills?: boolean | null;
    /**
     * The buyer-provided reference for this GCA booking.
     *
     * @maxLength 256
     */
    buyersReference?: string | null;
    /**
     * The supplier fee recorded for this GCA booking.
     *
     * @maxLength 64
     */
    agencyFee?: string | null;
    /**
     * Expenses payable for the booking.
     *
     * @maxLength 4096
     */
    expenses?: string;
    /**
     * The configured GCA lot option selected for this booking
     *
     * @pattern ^[0-9a-f]{24}$
     */
    ccsLotOptionId?: string | null;
    /**
     * The GCA lot selected for this booking
     *
     * @maxLength 256
     */
    ccsLotNumber?: string | null;
    /**
     * The service description associated with the selected GCA lot
     *
     * @maxLength 256
     */
    ccsServiceDescription?: string | null;
    /**
     * External placement identifier
     */
    _placementId?: string;
    /**
     * Evertime identifiers for this booking
     */
    evertime?: {
      /**
       * The Evertime PlacementId used for this booking's placement
       *
       * @maxLength 256
       */
      placementId?: string;
      /**
       * Timesheet identifier returned by Evertime
       *
       * @minimum 0
       * @exclusiveMinimum true
       */
      id?: number;
    };
    /**
     * Timesheet Portal identifiers for this booking
     */
    timesheetPortal?: {
      /**
       * The Timesheet Portal placement GUID used for this booking's job
       *
       * @format uuid
       */
      placementId?: string;
      /**
       * The Timesheet Portal placement code
       *
       * @maxLength 256
       */
      placementCode?: string;
      /**
       * The Timesheet Portal timesheet GUID when this booking is exported without a timesheet record
       *
       * @format uuid
       */
      id?: string;
    };
    /**
     * Reference to the parent booking
     *
     * @pattern ^[0-9a-f]{24}$
     */
    _parentBooking?: string;
    /**
     * Reference to the originating vacancy
     *
     * @pattern ^[0-9a-f]{24}$
     */
    _vacancyId?: string | null;
    /**
     * Manually linked parent booking reference
     */
    _manualParentBooking?: string | null;
    /**
     * Whether data has been synced
     */
    synced?: boolean;
    /**
     * When data was last synced
     *
     * @format date-time
     */
    syncedAt?: string | null;
    /**
     * The migration that created this booking
     *
     * @pattern ^[0-9a-f]{24}$
     */
    migrationId?: string;
    /**
     * Whether this migrated booking has been approved
     */
    migrationApproval?: "pending" | "approved" | "rejected";
  };
};

export type GetBookingVariables = {
  pathParams: GetBookingPathParams;
  queryParams?: GetBookingQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetBooking = (
  variables: GetBookingVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetBookingResponse,
    GetBookingError,
    undefined,
    {},
    GetBookingQueryParams,
    GetBookingPathParams
  >({ url: "/bookings/{bookingId}", method: "get", ...variables, signal });

export function getBookingQuery(variables: GetBookingVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetBookingResponse>;
};

export function getBookingQuery(
  variables: GetBookingVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetBookingResponse>)
    | reactQuery.SkipToken;
};

export function getBookingQuery(
  variables: GetBookingVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/bookings/{bookingId}",
      operationId: "getBooking",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetBooking(variables, signal),
  };
}

export const useSuspenseGetBooking = <TData = GetBookingResponse,>(
  variables: GetBookingVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetBookingResponse, GetBookingError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetBookingResponse,
    GetBookingError,
    TData
  >({
    ...getBookingQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetBooking = <TData = GetBookingResponse,>(
  variables: GetBookingVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetBookingResponse, GetBookingError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetBookingResponse, GetBookingError, TData>({
    ...getBookingQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type SendBookingConfirmationPathParams = {
  /**
   * The unique identifier for the booking.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  bookingId: string;
};

export type SendBookingConfirmationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type SendBookingConfirmationResponse = {
  /**
   * Confirmation that the booking email was sent.
   *
   * @maxLength 256
   */
  message: string;
};

export type SendBookingConfirmationRequestBody = {
  /**
   * Whether to send the confirmation to the booked candidate or the configured client contacts.
   */
  audience: "candidate" | "client";
  /**
   * The user who initiated the confirmation when sent through a trusted service.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  requestedBy?: string;
  /**
   * Optional inclusive date range limiting the booking days included in the confirmation.
   */
  coverage?: {
    /**
     * First booking date to include in a partial client confirmation.
     *
     * @format date
     */
    from: string;
    /**
     * Last booking date to include in a partial client confirmation.
     *
     * @format date
     */
    to: string;
  };
};

export type SendBookingConfirmationVariables = {
  body: SendBookingConfirmationRequestBody;
  pathParams: SendBookingConfirmationPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchSendBookingConfirmation = (
  variables: SendBookingConfirmationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    SendBookingConfirmationResponse,
    SendBookingConfirmationError,
    SendBookingConfirmationRequestBody,
    {},
    {},
    SendBookingConfirmationPathParams
  >({
    url: "/bookings/{bookingId}/confirmations",
    method: "post",
    ...variables,
    signal,
  });

export const useSendBookingConfirmation = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      SendBookingConfirmationResponse,
      SendBookingConfirmationError,
      SendBookingConfirmationVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    SendBookingConfirmationResponse,
    SendBookingConfirmationError,
    SendBookingConfirmationVariables
  >({
    mutationFn: (variables: SendBookingConfirmationVariables) =>
      fetchSendBookingConfirmation(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type CreateBookingConfirmationPreviewError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateBookingConfirmationPreviewResponse = {
  /**
   * Rendered HTML for the booking confirmation preview.
   *
   * @maxLength 2000000
   */
  html: string;
};

export type CreateBookingConfirmationPreviewRequestBody = {
  audience: "candidate" | "client";
  booking: {
    /**
     * The candidate included in the confirmation.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    candidate: string;
    /**
     * The client included in the confirmation.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    client: string;
    /**
     * Denormalised student name
     */
    studentName?: string;
    /**
     * The user assigned to the booking.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    assignee?: string;
    /**
     * The address to display in the confirmation.
     */
    clientAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    } | null;
    /**
     * The role covered by the booking.
     *
     * @maxLength 512
     */
    role?: string | null;
    /**
     * The booking days to include in the confirmation.
     *
     * @minItems 1
     * @maxItems 3660
     */
    days: {
      /**
       * The start date and time of the booking day.
       *
       * @maxLength 64
       */
      start: string;
      /**
       * The end date and time of the booking day.
       *
       * @maxLength 64
       */
      end: string;
      /**
       * The current status of the booking day.
       */
      status?:
        | (
            | "Active"
            | "Sickness"
            | "NoShow"
            | "Client Cancelled"
            | "Act Of God"
            | "Personal Appointment"
          )
        | number;
      /**
       * The length of the booking day.
       */
      length?: ("Half Day" | "Full Day") | number | (void | null);
      /**
       * Whether a half day takes place in the morning or afternoon.
       */
      amPm?: "AM" | "PM" | any | null;
      /**
       * The charge rate for this booking day.
       *
       * @maxLength 64
       */
      chargeRate?: string | null;
      /**
       * The pay rate for this booking day.
       *
       * @maxLength 64
       */
      payRate?: string | null;
      /**
       * Whether this day should be excluded from the confirmation.
       */
      flaggedForRemoval?: boolean;
    }[];
    /**
     * The rate charged to the client.
     *
     * @maxLength 64
     */
    chargeRate?: string | null;
    /**
     * The rate paid to the candidate.
     *
     * @maxLength 64
     */
    payRate?: string | null;
    /**
     * Whether the rates are daily or hourly.
     */
    payType?: ("Day" | "Hour") | number | (void | null);
    /**
     * The candidate's payment method.
     */
    candidatePayType?:
      | ("Paye" | "Umbrella" | "LtdCompany" | "NotApplicable")
      | number
      | (void | null);
    /**
     * Whether the salary is annual or daily.
     */
    salaryPayType?: ("Annual" | "Daily") | number | (void | null);
    /**
     * The type of booking being confirmed.
     */
    type: ("Standard" | "Interview" | "Trial" | "Perm" | "Tutor") | number;
    /**
     * The candidate's expected arrival time.
     *
     * @maxLength 64
     */
    arrivalTime?: string | null;
    /**
     * The primary client contact for the booking.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientContact?: string | null;
    /**
     * The invoice contact shown in the client confirmation.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    invoiceContact?: string | null;
    /**
     * Additional instructions shown to the candidate.
     *
     * @maxLength 20000
     */
    additionalInstructions?: string | null;
    /**
     * Additional instructions shown to the client.
     *
     * @maxLength 20000
     */
    additionalInstructionsClient?: string | null;
    /**
     * The job description shown in the confirmation.
     *
     * @maxLength 20000
     */
    jobDescription?: string | null;
    /**
     * The salary or daily rate offered for the position.
     *
     * @maxLength 64
     */
    salary?: string | null;
    /**
     * Agency Workers Regulations information for the booking.
     *
     * @maxLength 4000
     */
    awr?: string | null;
    /**
     * Whether the health and safety check has been completed.
     */
    healthSafetyCheck?: boolean | null;
    /**
     * The person who confirmed the health and safety check.
     *
     * @maxLength 512
     */
    healthSafetyCheckContact?: string | null;
    /**
     * Whether the candidate has confirmed attendance.
     */
    attending?: boolean | null;
    /**
     * The teacher registration number shown in the confirmation.
     *
     * @maxLength 128
     */
    trnNumber?: string | null;
    /**
     * Whether this is a GCA client booking.
     */
    ccsClientBooking?: boolean | null;
    /**
     * Whether the candidate has the relevant knowledge and appropriate skills.
     */
    ccsRelevantKnowledgeAndAppropriateSkills?: boolean | null;
    /**
     * The buyer-provided reference for the booking.
     *
     * @maxLength 256
     */
    buyersReference?: string | null;
    /**
     * The supplier fee shown in the confirmation.
     *
     * @maxLength 64
     */
    agencyFee?: string | null;
  };
  /**
   * Whether the preview uses representative candidate and client data instead of existing records.
   */
  sampleData?: boolean;
  /**
   * Optional unsaved configuration to apply when rendering the preview.
   */
  bookingConfirmationConfig?: {
    /**
     * Variant-specific display settings for candidate booking confirmations.
     */
    candidate: {
      standard: {
        /**
         * Rows available in standard candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "role"
            | "teacherRegistrationNumber"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "clientName"
            | "address"
            | "payRate"
            | "paymentType"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in standard candidate confirmations.
         */
        options: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage: boolean;
          /**
           * Whether standard booking confirmations include an attendance button.
           */
          showStandardAttendanceButton: boolean;
          /**
           * Whether booking confirmations include an acknowledgement button.
           */
          showAcknowledgementButton: boolean;
        };
      };
      tutor: {
        /**
         * Rows available in tutor candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "studentName"
            | "role"
            | "teacherRegistrationNumber"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "clientName"
            | "address"
            | "payRate"
            | "paymentType"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in tutor candidate confirmations.
         */
        options: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage: boolean;
          /**
           * Whether booking confirmations include an acknowledgement button.
           */
          showAcknowledgementButton: boolean;
        };
      };
      interview: {
        /**
         * Rows available in interview candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "start"
            | "address"
            | "jobDescription"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
      };
      trial: {
        /**
         * Rows available in trial candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "start"
            | "end"
            | "payRate"
            | "address"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in trial candidate confirmations.
         */
        options: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage: boolean;
        };
      };
      permanent: {
        /**
         * Rows available in permanent candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "role"
            | "start"
            | "clientName"
            | "address"
            | "salary"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
      };
    };
    /**
     * Variant-specific display settings for client booking confirmations.
     */
    client: {
      standard: {
        /**
         * Rows available in standard client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "awrInformation"
            | "chargeRate"
            | "agencyFee"
            | "bookedBy"
            | "approvedBy"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
      tutor: {
        /**
         * Rows available in tutor client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "studentName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "awrInformation"
            | "chargeRate"
            | "agencyFee"
            | "bookedBy"
            | "approvedBy"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
      interview: {
        /**
         * Rows available in interview client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "anaphylaxisTraining"
            | "start"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions"
            | "address";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
      };
      trial: {
        /**
         * Rows available in trial client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
      permanent: {
        /**
         * Rows available in permanent client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
    };
  };
};

export type CreateBookingConfirmationPreviewVariables = {
  body: CreateBookingConfirmationPreviewRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateBookingConfirmationPreview = (
  variables: CreateBookingConfirmationPreviewVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateBookingConfirmationPreviewResponse,
    CreateBookingConfirmationPreviewError,
    CreateBookingConfirmationPreviewRequestBody,
    {},
    {},
    {}
  >({
    url: "/booking-confirmation-previews",
    method: "post",
    ...variables,
    signal,
  });

export const useCreateBookingConfirmationPreview = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateBookingConfirmationPreviewResponse,
      CreateBookingConfirmationPreviewError,
      CreateBookingConfirmationPreviewVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateBookingConfirmationPreviewResponse,
    CreateBookingConfirmationPreviewError,
    CreateBookingConfirmationPreviewVariables
  >({
    mutationFn: (variables: CreateBookingConfirmationPreviewVariables) =>
      fetchCreateBookingConfirmationPreview(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type CreateBookingConfirmationExportError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateBookingConfirmationExportRequestBody = {
  audience: "candidate" | "client";
  booking: {
    /**
     * The candidate included in the confirmation.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    candidate: string;
    /**
     * The client included in the confirmation.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    client: string;
    /**
     * Denormalised student name
     */
    studentName?: string;
    /**
     * The user assigned to the booking.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    assignee?: string;
    /**
     * The address to display in the confirmation.
     */
    clientAddress?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    } | null;
    /**
     * The role covered by the booking.
     *
     * @maxLength 512
     */
    role?: string | null;
    /**
     * The booking days to include in the confirmation.
     *
     * @minItems 1
     * @maxItems 3660
     */
    days: {
      /**
       * The start date and time of the booking day.
       *
       * @maxLength 64
       */
      start: string;
      /**
       * The end date and time of the booking day.
       *
       * @maxLength 64
       */
      end: string;
      /**
       * The current status of the booking day.
       */
      status?:
        | (
            | "Active"
            | "Sickness"
            | "NoShow"
            | "Client Cancelled"
            | "Act Of God"
            | "Personal Appointment"
          )
        | number;
      /**
       * The length of the booking day.
       */
      length?: ("Half Day" | "Full Day") | number | (void | null);
      /**
       * Whether a half day takes place in the morning or afternoon.
       */
      amPm?: "AM" | "PM" | any | null;
      /**
       * The charge rate for this booking day.
       *
       * @maxLength 64
       */
      chargeRate?: string | null;
      /**
       * The pay rate for this booking day.
       *
       * @maxLength 64
       */
      payRate?: string | null;
      /**
       * Whether this day should be excluded from the confirmation.
       */
      flaggedForRemoval?: boolean;
    }[];
    /**
     * The rate charged to the client.
     *
     * @maxLength 64
     */
    chargeRate?: string | null;
    /**
     * The rate paid to the candidate.
     *
     * @maxLength 64
     */
    payRate?: string | null;
    /**
     * Whether the rates are daily or hourly.
     */
    payType?: ("Day" | "Hour") | number | (void | null);
    /**
     * The candidate's payment method.
     */
    candidatePayType?:
      | ("Paye" | "Umbrella" | "LtdCompany" | "NotApplicable")
      | number
      | (void | null);
    /**
     * Whether the salary is annual or daily.
     */
    salaryPayType?: ("Annual" | "Daily") | number | (void | null);
    /**
     * The type of booking being confirmed.
     */
    type: ("Standard" | "Interview" | "Trial" | "Perm" | "Tutor") | number;
    /**
     * The candidate's expected arrival time.
     *
     * @maxLength 64
     */
    arrivalTime?: string | null;
    /**
     * The primary client contact for the booking.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientContact?: string | null;
    /**
     * The invoice contact shown in the client confirmation.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    invoiceContact?: string | null;
    /**
     * Additional instructions shown to the candidate.
     *
     * @maxLength 20000
     */
    additionalInstructions?: string | null;
    /**
     * Additional instructions shown to the client.
     *
     * @maxLength 20000
     */
    additionalInstructionsClient?: string | null;
    /**
     * The job description shown in the confirmation.
     *
     * @maxLength 20000
     */
    jobDescription?: string | null;
    /**
     * The salary or daily rate offered for the position.
     *
     * @maxLength 64
     */
    salary?: string | null;
    /**
     * Agency Workers Regulations information for the booking.
     *
     * @maxLength 4000
     */
    awr?: string | null;
    /**
     * Whether the health and safety check has been completed.
     */
    healthSafetyCheck?: boolean | null;
    /**
     * The person who confirmed the health and safety check.
     *
     * @maxLength 512
     */
    healthSafetyCheckContact?: string | null;
    /**
     * Whether the candidate has confirmed attendance.
     */
    attending?: boolean | null;
    /**
     * The teacher registration number shown in the confirmation.
     *
     * @maxLength 128
     */
    trnNumber?: string | null;
    /**
     * Whether this is a GCA client booking.
     */
    ccsClientBooking?: boolean | null;
    /**
     * Whether the candidate has the relevant knowledge and appropriate skills.
     */
    ccsRelevantKnowledgeAndAppropriateSkills?: boolean | null;
    /**
     * The buyer-provided reference for the booking.
     *
     * @maxLength 256
     */
    buyersReference?: string | null;
    /**
     * The supplier fee shown in the confirmation.
     *
     * @maxLength 64
     */
    agencyFee?: string | null;
  };
  /**
   * Whether the preview uses representative candidate and client data instead of existing records.
   */
  sampleData?: boolean;
  /**
   * Optional unsaved configuration to apply when rendering the preview.
   */
  bookingConfirmationConfig?: {
    /**
     * Variant-specific display settings for candidate booking confirmations.
     */
    candidate: {
      standard: {
        /**
         * Rows available in standard candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "role"
            | "teacherRegistrationNumber"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "clientName"
            | "address"
            | "payRate"
            | "paymentType"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in standard candidate confirmations.
         */
        options: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage: boolean;
          /**
           * Whether standard booking confirmations include an attendance button.
           */
          showStandardAttendanceButton: boolean;
          /**
           * Whether booking confirmations include an acknowledgement button.
           */
          showAcknowledgementButton: boolean;
        };
      };
      tutor: {
        /**
         * Rows available in tutor candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "studentName"
            | "role"
            | "teacherRegistrationNumber"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "clientName"
            | "address"
            | "payRate"
            | "paymentType"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in tutor candidate confirmations.
         */
        options: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage: boolean;
          /**
           * Whether booking confirmations include an acknowledgement button.
           */
          showAcknowledgementButton: boolean;
        };
      };
      interview: {
        /**
         * Rows available in interview candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "start"
            | "address"
            | "jobDescription"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
      };
      trial: {
        /**
         * Rows available in trial candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "start"
            | "end"
            | "payRate"
            | "address"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in trial candidate confirmations.
         */
        options: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage: boolean;
        };
      };
      permanent: {
        /**
         * Rows available in permanent candidate booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "role"
            | "start"
            | "clientName"
            | "address"
            | "salary"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
      };
    };
    /**
     * Variant-specific display settings for client booking confirmations.
     */
    client: {
      standard: {
        /**
         * Rows available in standard client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "awrInformation"
            | "chargeRate"
            | "agencyFee"
            | "bookedBy"
            | "approvedBy"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
      tutor: {
        /**
         * Rows available in tutor client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "studentName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "awrInformation"
            | "chargeRate"
            | "agencyFee"
            | "bookedBy"
            | "approvedBy"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
      interview: {
        /**
         * Rows available in interview client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "anaphylaxisTraining"
            | "start"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions"
            | "address";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
      };
      trial: {
        /**
         * Rows available in trial client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
      permanent: {
        /**
         * Rows available in permanent client booking confirmations.
         */
        rows: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount: boolean;
        };
      };
    };
  };
};

export type CreateBookingConfirmationExportVariables = {
  body: CreateBookingConfirmationExportRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateBookingConfirmationExport = (
  variables: CreateBookingConfirmationExportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    CreateBookingConfirmationExportError,
    CreateBookingConfirmationExportRequestBody,
    {},
    {},
    {}
  >({
    url: "/booking-confirmation-exports",
    method: "post",
    ...variables,
    signal,
  });

export const useCreateBookingConfirmationExport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      CreateBookingConfirmationExportError,
      CreateBookingConfirmationExportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    CreateBookingConfirmationExportError,
    CreateBookingConfirmationExportVariables
  >({
    mutationFn: (variables: CreateBookingConfirmationExportVariables) =>
      fetchCreateBookingConfirmationExport(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type GetCompanyAuthClientsQueryParams = {
  /**
   * The fields to include in each API client response
   *
   * @maxItems 50
   */
  select?: (
    | "id"
    | "name"
    | "redirectUrls"
    | "grantTypes"
    | "clientSecret"
    | "createdAt"
    | ""
  )[];
  /**
   * Page number
   *
   * @minimum 1
   */
  page?: number;
  /**
   * Number of API clients per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
};

export type GetCompanyAuthClientsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetCompanyAuthClientsResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The public identifier for the API client
     *
     * @minLength 32
     * @maxLength 64
     */
    id?: string;
    /**
     * A recognisable name for the API client
     *
     * @minLength 1
     * @maxLength 128
     */
    name?: string;
    /**
     * The allowed authorization callback URLs for this client
     *
     * @minItems 1
     * @maxItems 10
     */
    redirectUrls?: string[];
    /**
     * The OAuth 2.0 grant types enabled for this client
     *
     * @minItems 1
     * @maxItems 2
     */
    grantTypes?: ("authorization_code" | "refresh_token")[];
    /**
     * The one-time client secret returned when the client is created or its secret is rotated
     *
     * @minLength 43
     * @maxLength 128
     */
    clientSecret?: string;
    /**
     * When the API client was created
     *
     * @format date-time
     */
    createdAt?: string;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetCompanyAuthClientsVariables = {
  queryParams?: GetCompanyAuthClientsQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetCompanyAuthClients = (
  variables: GetCompanyAuthClientsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetCompanyAuthClientsResponse,
    GetCompanyAuthClientsError,
    undefined,
    {},
    GetCompanyAuthClientsQueryParams,
    {}
  >({ url: "/company/auth-clients", method: "get", ...variables, signal });

export function getCompanyAuthClientsQuery(
  variables: GetCompanyAuthClientsVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetCompanyAuthClientsResponse>;
};

export function getCompanyAuthClientsQuery(
  variables: GetCompanyAuthClientsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetCompanyAuthClientsResponse>)
    | reactQuery.SkipToken;
};

export function getCompanyAuthClientsQuery(
  variables: GetCompanyAuthClientsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/company/auth-clients",
      operationId: "getCompanyAuthClients",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetCompanyAuthClients(variables, signal),
  };
}

export const useSuspenseGetCompanyAuthClients = <
  TData = GetCompanyAuthClientsResponse,
>(
  variables: GetCompanyAuthClientsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCompanyAuthClientsResponse,
      GetCompanyAuthClientsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetCompanyAuthClientsResponse,
    GetCompanyAuthClientsError,
    TData
  >({
    ...getCompanyAuthClientsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetCompanyAuthClients = <
  TData = GetCompanyAuthClientsResponse,
>(
  variables: GetCompanyAuthClientsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCompanyAuthClientsResponse,
      GetCompanyAuthClientsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetCompanyAuthClientsResponse,
    GetCompanyAuthClientsError,
    TData
  >({
    ...getCompanyAuthClientsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateCompanyAuthClientError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 409;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Resource limit reached
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateCompanyAuthClientResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The public identifier for the API client
     *
     * @minLength 32
     * @maxLength 64
     */
    id?: string;
    /**
     * A recognisable name for the API client
     *
     * @minLength 1
     * @maxLength 128
     */
    name?: string;
    /**
     * The allowed authorization callback URLs for this client
     *
     * @minItems 1
     * @maxItems 10
     */
    redirectUrls?: string[];
    /**
     * The OAuth 2.0 grant types enabled for this client
     *
     * @minItems 1
     * @maxItems 2
     */
    grantTypes?: ("authorization_code" | "refresh_token")[];
    /**
     * The one-time client secret returned when the client is created or its secret is rotated
     *
     * @minLength 43
     * @maxLength 128
     */
    clientSecret?: string;
    /**
     * When the API client was created
     *
     * @format date-time
     */
    createdAt?: string;
  };
};

export type CreateCompanyAuthClientRequestBody = {
  /**
   * A recognisable name for the API client
   *
   * @minLength 1
   * @maxLength 128
   */
  name: string;
  /**
   * The allowed authorization callback URLs for this client
   *
   * @minItems 1
   * @maxItems 10
   */
  redirectUrls: string[];
  /**
   * The OAuth 2.0 grant types enabled for this client
   *
   * @minItems 1
   * @maxItems 2
   */
  grantTypes: ("authorization_code" | "refresh_token")[];
};

export type CreateCompanyAuthClientVariables = {
  body: CreateCompanyAuthClientRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateCompanyAuthClient = (
  variables: CreateCompanyAuthClientVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateCompanyAuthClientResponse,
    CreateCompanyAuthClientError,
    CreateCompanyAuthClientRequestBody,
    {},
    {},
    {}
  >({ url: "/company/auth-clients", method: "post", ...variables, signal });

export const useCreateCompanyAuthClient = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateCompanyAuthClientResponse,
      CreateCompanyAuthClientError,
      CreateCompanyAuthClientVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateCompanyAuthClientResponse,
    CreateCompanyAuthClientError,
    CreateCompanyAuthClientVariables
  >({
    mutationFn: (variables: CreateCompanyAuthClientVariables) =>
      fetchCreateCompanyAuthClient(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type UpdateCompanyAuthClientPathParams = {
  /**
   * The identifier of the API client
   *
   * @pattern ^[0-9a-f]{32}$
   */
  authClientId: string;
};

export type UpdateCompanyAuthClientError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateCompanyAuthClientResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The public identifier for the API client
     *
     * @minLength 32
     * @maxLength 64
     */
    id?: string;
    /**
     * A recognisable name for the API client
     *
     * @minLength 1
     * @maxLength 128
     */
    name?: string;
    /**
     * The allowed authorization callback URLs for this client
     *
     * @minItems 1
     * @maxItems 10
     */
    redirectUrls?: string[];
    /**
     * The OAuth 2.0 grant types enabled for this client
     *
     * @minItems 1
     * @maxItems 2
     */
    grantTypes?: ("authorization_code" | "refresh_token")[];
    /**
     * The one-time client secret returned when the client is created or its secret is rotated
     *
     * @minLength 43
     * @maxLength 128
     */
    clientSecret?: string;
    /**
     * When the API client was created
     *
     * @format date-time
     */
    createdAt?: string;
  };
};

export type UpdateCompanyAuthClientRequestBody = {
  /**
   * A recognisable name for the API client
   *
   * @minLength 1
   * @maxLength 128
   */
  name: string;
  /**
   * The allowed authorization callback URLs for this client
   *
   * @minItems 1
   * @maxItems 10
   */
  redirectUrls: string[];
  /**
   * The OAuth 2.0 grant types enabled for this client
   *
   * @minItems 1
   * @maxItems 2
   */
  grantTypes: ("authorization_code" | "refresh_token")[];
  /**
   * Whether to replace the current client secret. The replacement is returned only once
   */
  rotateSecret?: boolean;
};

export type UpdateCompanyAuthClientVariables = {
  body: UpdateCompanyAuthClientRequestBody;
  pathParams: UpdateCompanyAuthClientPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateCompanyAuthClient = (
  variables: UpdateCompanyAuthClientVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateCompanyAuthClientResponse,
    UpdateCompanyAuthClientError,
    UpdateCompanyAuthClientRequestBody,
    {},
    {},
    UpdateCompanyAuthClientPathParams
  >({
    url: "/company/auth-clients/{authClientId}",
    method: "put",
    ...variables,
    signal,
  });

export const useUpdateCompanyAuthClient = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateCompanyAuthClientResponse,
      UpdateCompanyAuthClientError,
      UpdateCompanyAuthClientVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateCompanyAuthClientResponse,
    UpdateCompanyAuthClientError,
    UpdateCompanyAuthClientVariables
  >({
    mutationFn: (variables: UpdateCompanyAuthClientVariables) =>
      fetchUpdateCompanyAuthClient(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type DeleteCompanyAuthClientPathParams = {
  /**
   * The identifier of the API client
   *
   * @pattern ^[0-9a-f]{32}$
   */
  authClientId: string;
};

export type DeleteCompanyAuthClientError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type DeleteCompanyAuthClientVariables = {
  pathParams: DeleteCompanyAuthClientPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchDeleteCompanyAuthClient = (
  variables: DeleteCompanyAuthClientVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    DeleteCompanyAuthClientError,
    undefined,
    {},
    {},
    DeleteCompanyAuthClientPathParams
  >({
    url: "/company/auth-clients/{authClientId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useDeleteCompanyAuthClient = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteCompanyAuthClientError,
      DeleteCompanyAuthClientVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    DeleteCompanyAuthClientError,
    DeleteCompanyAuthClientVariables
  >({
    mutationFn: (variables: DeleteCompanyAuthClientVariables) =>
      fetchDeleteCompanyAuthClient(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetCompanyOccupationsError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetCompanyOccupationsResponse = {
  /**
   * Occupation options configured for the company.
   */
  data: {
    /**
     * The occupation name
     *
     * @minLength 1
     * @maxLength 200
     */
    name: string;
    /**
     * Whether the occupation is visible in selectors and forms
     */
    visible: boolean;
  }[];
};

export type GetCompanyOccupationsVariables = Apiv2Context["fetcherOptions"];

export const fetchGetCompanyOccupations = (
  variables: GetCompanyOccupationsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetCompanyOccupationsResponse,
    GetCompanyOccupationsError,
    undefined,
    {},
    {},
    {}
  >({ url: "/company/occupations", method: "get", ...variables, signal });

export function getCompanyOccupationsQuery(
  variables: GetCompanyOccupationsVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetCompanyOccupationsResponse>;
};

export function getCompanyOccupationsQuery(
  variables: GetCompanyOccupationsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetCompanyOccupationsResponse>)
    | reactQuery.SkipToken;
};

export function getCompanyOccupationsQuery(
  variables: GetCompanyOccupationsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/company/occupations",
      operationId: "getCompanyOccupations",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetCompanyOccupations(variables, signal),
  };
}

export const useSuspenseGetCompanyOccupations = <
  TData = GetCompanyOccupationsResponse,
>(
  variables: GetCompanyOccupationsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCompanyOccupationsResponse,
      GetCompanyOccupationsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetCompanyOccupationsResponse,
    GetCompanyOccupationsError,
    TData
  >({
    ...getCompanyOccupationsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetCompanyOccupations = <
  TData = GetCompanyOccupationsResponse,
>(
  variables: GetCompanyOccupationsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetCompanyOccupationsResponse,
      GetCompanyOccupationsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetCompanyOccupationsResponse,
    GetCompanyOccupationsError,
    TData
  >({
    ...getCompanyOccupationsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetSavedSearchesQueryParams = {
  /**
   * The fields to include in the response in dot notation format.
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "customerId"
    | "shared"
    | "level"
    | "createdBy"
    | "name"
    | "description"
    | "columns"
    | "createdAt"
    | "updatedAt"
    | "type"
    | "query.q"
    | "query.search"
    | "query.sort"
    | "query.select"
    | "query.expand"
    | "query.filter.availabilityPeriod.from"
    | "query.filter.availabilityPeriod.to"
    | "query.filter.availabilityPeriod.relative.from.anchor"
    | "query.filter.availabilityPeriod.relative.from.offset"
    | "query.filter.availabilityPeriod.relative.from.unit"
    | "query.filter.availabilityPeriod.relative.from"
    | "query.filter.availabilityPeriod.relative.to.anchor"
    | "query.filter.availabilityPeriod.relative.to.offset"
    | "query.filter.availabilityPeriod.relative.to.unit"
    | "query.filter.availabilityPeriod.relative.to"
    | "query.filter.availabilityPeriod.relative"
    | "query.filter.availabilityPeriod"
    | "query.filter.availabilityStatuses"
    | "query.filter.ids"
    | "query.filter.status"
    | "query.filter.onboardingStatus"
    | "query.filter.activeOnboardingSession"
    | "query.filter.onboardingSent"
    | "query.filter.section128Required"
    | "query.filter.confirmed"
    | "query.filter.optOut"
    | "query.filter.optOut48Hours"
    | "query.filter.fitToWorkOverride"
    | "query.filter.affectAbility"
    | "query.filter.ewcConfirmation"
    | "query.filter.dbsHeld"
    | "query.filter.dbsRegisteredToUpdateService"
    | "query.filter.firstName"
    | "query.filter.lastName"
    | "query.filter.email"
    | "query.filter.mobile"
    | "query.filter.address.city"
    | "query.filter.address.postcode"
    | "query.filter.address.county"
    | "query.filter.address.borough"
    | "query.filter.address.addressLine1"
    | "query.filter.address.addressLine2"
    | "query.filter.address.addressLine3"
    | "query.filter.address"
    | "query.filter.occupation"
    | "query.filter.specialism"
    | "query.filter._internalId"
    | "query.filter.complianceUser"
    | "query.filter.assignee"
    | "query.filter.watcher"
    | "query.filter.team"
    | "query.filter.contactId"
    | "query.filter.currentBooking"
    | "query.filter.bookedInRange.from"
    | "query.filter.bookedInRange.to"
    | "query.filter.bookedInRange.relative.from.anchor"
    | "query.filter.bookedInRange.relative.from.offset"
    | "query.filter.bookedInRange.relative.from.unit"
    | "query.filter.bookedInRange.relative.from"
    | "query.filter.bookedInRange.relative.to.anchor"
    | "query.filter.bookedInRange.relative.to.offset"
    | "query.filter.bookedInRange.relative.to.unit"
    | "query.filter.bookedInRange.relative.to"
    | "query.filter.bookedInRange.relative"
    | "query.filter.bookedInRange"
    | "query.filter.notBookedInRange.from"
    | "query.filter.notBookedInRange.to"
    | "query.filter.notBookedInRange.relative.from.anchor"
    | "query.filter.notBookedInRange.relative.from.offset"
    | "query.filter.notBookedInRange.relative.from.unit"
    | "query.filter.notBookedInRange.relative.from"
    | "query.filter.notBookedInRange.relative.to.anchor"
    | "query.filter.notBookedInRange.relative.to.offset"
    | "query.filter.notBookedInRange.relative.to.unit"
    | "query.filter.notBookedInRange.relative.to"
    | "query.filter.notBookedInRange.relative"
    | "query.filter.notBookedInRange"
    | "query.filter.tags"
    | "query.filter.location.lat"
    | "query.filter.location.lng"
    | "query.filter.location.radius"
    | "query.filter.location"
    | "query.filter.statusChangeDate.from"
    | "query.filter.statusChangeDate.to"
    | "query.filter.statusChangeDate.relative.from.anchor"
    | "query.filter.statusChangeDate.relative.from.offset"
    | "query.filter.statusChangeDate.relative.from.unit"
    | "query.filter.statusChangeDate.relative.from"
    | "query.filter.statusChangeDate.relative.to.anchor"
    | "query.filter.statusChangeDate.relative.to.offset"
    | "query.filter.statusChangeDate.relative.to.unit"
    | "query.filter.statusChangeDate.relative.to"
    | "query.filter.statusChangeDate.relative"
    | "query.filter.statusChangeDate"
    | "query.filter.lastContactedDateFilter.from"
    | "query.filter.lastContactedDateFilter.to"
    | "query.filter.lastContactedDateFilter.relative.from.anchor"
    | "query.filter.lastContactedDateFilter.relative.from.offset"
    | "query.filter.lastContactedDateFilter.relative.from.unit"
    | "query.filter.lastContactedDateFilter.relative.from"
    | "query.filter.lastContactedDateFilter.relative.to.anchor"
    | "query.filter.lastContactedDateFilter.relative.to.offset"
    | "query.filter.lastContactedDateFilter.relative.to.unit"
    | "query.filter.lastContactedDateFilter.relative.to"
    | "query.filter.lastContactedDateFilter.relative"
    | "query.filter.lastContactedDateFilter"
    | "query.filter.employmentPreference"
    | "query.filter.transport"
    | "query.filter.trnNumber"
    | "query.filter.desiredLocation"
    | "query.filter.subjectToNotice"
    | "query.filter.title"
    | "query.filter.sex"
    | "query.filter.pronoun"
    | "query.filter.roleType"
    | "query.filter.payType"
    | "query.filter.payInterval"
    | "query.filter.dbsType"
    | "query.filter.source"
    | "query.filter.telephone"
    | "query.filter.previousNames"
    | "query.filter.otherNames"
    | "query.filter.dob.from"
    | "query.filter.dob.to"
    | "query.filter.dob.relative.from.anchor"
    | "query.filter.dob.relative.from.offset"
    | "query.filter.dob.relative.from.unit"
    | "query.filter.dob.relative.from"
    | "query.filter.dob.relative.to.anchor"
    | "query.filter.dob.relative.to.offset"
    | "query.filter.dob.relative.to.unit"
    | "query.filter.dob.relative.to"
    | "query.filter.dob.relative"
    | "query.filter.dob"
    | "query.filter.trnNumberCheckedDate.from"
    | "query.filter.trnNumberCheckedDate.to"
    | "query.filter.trnNumberCheckedDate.relative.from.anchor"
    | "query.filter.trnNumberCheckedDate.relative.from.offset"
    | "query.filter.trnNumberCheckedDate.relative.from.unit"
    | "query.filter.trnNumberCheckedDate.relative.from"
    | "query.filter.trnNumberCheckedDate.relative.to.anchor"
    | "query.filter.trnNumberCheckedDate.relative.to.offset"
    | "query.filter.trnNumberCheckedDate.relative.to.unit"
    | "query.filter.trnNumberCheckedDate.relative.to"
    | "query.filter.trnNumberCheckedDate.relative"
    | "query.filter.trnNumberCheckedDate"
    | "query.filter.dbsLastChecked.from"
    | "query.filter.dbsLastChecked.to"
    | "query.filter.dbsLastChecked.relative.from.anchor"
    | "query.filter.dbsLastChecked.relative.from.offset"
    | "query.filter.dbsLastChecked.relative.from.unit"
    | "query.filter.dbsLastChecked.relative.from"
    | "query.filter.dbsLastChecked.relative.to.anchor"
    | "query.filter.dbsLastChecked.relative.to.offset"
    | "query.filter.dbsLastChecked.relative.to.unit"
    | "query.filter.dbsLastChecked.relative.to"
    | "query.filter.dbsLastChecked.relative"
    | "query.filter.dbsLastChecked"
    | "query.filter.dbsUpdateServiceCheckDate.from"
    | "query.filter.dbsUpdateServiceCheckDate.to"
    | "query.filter.dbsUpdateServiceCheckDate.relative.from.anchor"
    | "query.filter.dbsUpdateServiceCheckDate.relative.from.offset"
    | "query.filter.dbsUpdateServiceCheckDate.relative.from.unit"
    | "query.filter.dbsUpdateServiceCheckDate.relative.from"
    | "query.filter.dbsUpdateServiceCheckDate.relative.to.anchor"
    | "query.filter.dbsUpdateServiceCheckDate.relative.to.offset"
    | "query.filter.dbsUpdateServiceCheckDate.relative.to.unit"
    | "query.filter.dbsUpdateServiceCheckDate.relative.to"
    | "query.filter.dbsUpdateServiceCheckDate.relative"
    | "query.filter.dbsUpdateServiceCheckDate"
    | "query.filter.rtwVerificationDate.from"
    | "query.filter.rtwVerificationDate.to"
    | "query.filter.rtwVerificationDate.relative.from.anchor"
    | "query.filter.rtwVerificationDate.relative.from.offset"
    | "query.filter.rtwVerificationDate.relative.from.unit"
    | "query.filter.rtwVerificationDate.relative.from"
    | "query.filter.rtwVerificationDate.relative.to.anchor"
    | "query.filter.rtwVerificationDate.relative.to.offset"
    | "query.filter.rtwVerificationDate.relative.to.unit"
    | "query.filter.rtwVerificationDate.relative.to"
    | "query.filter.rtwVerificationDate.relative"
    | "query.filter.rtwVerificationDate"
    | "query.filter.section128CheckedDate.from"
    | "query.filter.section128CheckedDate.to"
    | "query.filter.section128CheckedDate.relative.from.anchor"
    | "query.filter.section128CheckedDate.relative.from.offset"
    | "query.filter.section128CheckedDate.relative.from.unit"
    | "query.filter.section128CheckedDate.relative.from"
    | "query.filter.section128CheckedDate.relative.to.anchor"
    | "query.filter.section128CheckedDate.relative.to.offset"
    | "query.filter.section128CheckedDate.relative.to.unit"
    | "query.filter.section128CheckedDate.relative.to"
    | "query.filter.section128CheckedDate.relative"
    | "query.filter.section128CheckedDate"
    | "query.filter.prohibitionCheckDate.from"
    | "query.filter.prohibitionCheckDate.to"
    | "query.filter.prohibitionCheckDate.relative.from.anchor"
    | "query.filter.prohibitionCheckDate.relative.from.offset"
    | "query.filter.prohibitionCheckDate.relative.from.unit"
    | "query.filter.prohibitionCheckDate.relative.from"
    | "query.filter.prohibitionCheckDate.relative.to.anchor"
    | "query.filter.prohibitionCheckDate.relative.to.offset"
    | "query.filter.prohibitionCheckDate.relative.to.unit"
    | "query.filter.prohibitionCheckDate.relative.to"
    | "query.filter.prohibitionCheckDate.relative"
    | "query.filter.prohibitionCheckDate"
    | "query.filter.createdAt.from"
    | "query.filter.createdAt.to"
    | "query.filter.createdAt.relative.from.anchor"
    | "query.filter.createdAt.relative.from.offset"
    | "query.filter.createdAt.relative.from.unit"
    | "query.filter.createdAt.relative.from"
    | "query.filter.createdAt.relative.to.anchor"
    | "query.filter.createdAt.relative.to.offset"
    | "query.filter.createdAt.relative.to.unit"
    | "query.filter.createdAt.relative.to"
    | "query.filter.createdAt.relative"
    | "query.filter.createdAt"
    | "query.filter.updatedAt.from"
    | "query.filter.updatedAt.to"
    | "query.filter.updatedAt.relative.from.anchor"
    | "query.filter.updatedAt.relative.from.offset"
    | "query.filter.updatedAt.relative.from.unit"
    | "query.filter.updatedAt.relative.from"
    | "query.filter.updatedAt.relative.to.anchor"
    | "query.filter.updatedAt.relative.to.offset"
    | "query.filter.updatedAt.relative.to.unit"
    | "query.filter.updatedAt.relative.to"
    | "query.filter.updatedAt.relative"
    | "query.filter.updatedAt"
    | "query.filter.candidateMobileAppStatus"
    | "query.filter.recAuditConsent"
    | "query.filter"
    | "query"
    | "query.include"
    | "query.filter.name"
    | "query.filter.lea"
    | "query.filter.type"
    | "query.filter.multiAcademyTrust"
    | "query.filter.borough"
    | "query.filter.internalId"
    | "query.filter.ccsClient"
    | "query.filter.ccsClientUrn"
    | "query.filter.contacts.contactId"
    | "query.filter.role"
    | "query.filter.forename"
    | "query.filter.surname"
    | "query.filter.decisionMaker"
    | "query.filter.clientId"
    | "query.filter.clientName"
    | "query.filter.clientAddress.city"
    | "query.filter.clientAddress.county"
    | "query.filter.clientAddress.postcode"
    | "query.filter.clientAddress"
    | "query.filter.clientLea"
    | "query.filter.clientType"
    | "query.filter.clientMultiAcademyTrust"
    | "query.filter.clientBorough"
    | "query.filter.clientAssignee"
    | "query.filter.clientSpecialism"
    | "query.filter.clientUpdatedAt.from"
    | "query.filter.clientUpdatedAt.to"
    | "query.filter.clientUpdatedAt.relative.from.anchor"
    | "query.filter.clientUpdatedAt.relative.from.offset"
    | "query.filter.clientUpdatedAt.relative.from.unit"
    | "query.filter.clientUpdatedAt.relative.from"
    | "query.filter.clientUpdatedAt.relative.to.anchor"
    | "query.filter.clientUpdatedAt.relative.to.offset"
    | "query.filter.clientUpdatedAt.relative.to.unit"
    | "query.filter.clientUpdatedAt.relative.to"
    | "query.filter.clientUpdatedAt.relative"
    | "query.filter.clientUpdatedAt"
    | "presentation.mode"
    | "presentation.availability.period.from"
    | "presentation.availability.period.to"
    | "presentation.availability.period.relative.from.anchor"
    | "presentation.availability.period.relative.from.offset"
    | "presentation.availability.period.relative.from.unit"
    | "presentation.availability.period.relative.from"
    | "presentation.availability.period.relative.to.anchor"
    | "presentation.availability.period.relative.to.offset"
    | "presentation.availability.period.relative.to.unit"
    | "presentation.availability.period.relative.to"
    | "presentation.availability.period.relative"
    | "presentation.availability.period"
    | "presentation.availability.statuses"
    | "presentation.availability.draftClient"
    | "presentation.availability.draftMode"
    | "presentation.availability"
    | "presentation"
    | ""
  )[];
  /**
   * Filters that limit which saved searches are returned.
   */
  filter?: {
    /**
     * Return only system, company, or user saved searches.
     *
     * @minItems 1
     * @maxItems 3
     */
    level?: ("system" | "company" | "user")[];
    /**
     * Return saved searches for this type of record.
     */
    type?: "candidates" | "clients" | "clientContacts";
    /**
     * When true, return only saved searches starred by the authenticated user.
     */
    starred?: "true";
  };
  /**
   * Search saved searches by name.
   *
   * @maxLength 128
   */
  search?: string;
  /**
   * Page number
   *
   * @minimum 1
   */
  page?: number;
  /**
   * Number of saved searches per page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
  /**
   * The field used to order saved searches.
   */
  sort?:
    | "name"
    | "-name"
    | "createdAt"
    | "-createdAt"
    | "updatedAt"
    | "-updatedAt";
};

export type GetSavedSearchesError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetSavedSearchesResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: (
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved candidate search.
         */
        type?: "candidates";
        /**
         * The candidate query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * @maxLength 256
           */
          search?: string;
          sort?:
            | (
                | "relevanceScore"
                | "-relevanceScore"
                | "forename"
                | "-forename"
                | "surname"
                | "-surname"
                | "mobile"
                | "-mobile"
                | "email"
                | "-email"
                | "address.city"
                | "-address.city"
                | "address.postcode"
                | "-address.postcode"
                | "address.county"
                | "-address.county"
                | "address.borough"
                | "-address.borough"
                | "address.addressLine1"
                | "-address.addressLine1"
                | "address.addressLine2"
                | "-address.addressLine2"
                | "address.addressLine3"
                | "-address.addressLine3"
                | "subject"
                | "-subject"
                | "statusChangeDate"
                | "-statusChangeDate"
                | "lastContactedDate"
                | "-lastContactedDate"
                | "daysSinceLastContactDisplay"
                | "-daysSinceLastContactDisplay"
                | "status"
                | "-status"
                | "occupation"
                | "-occupation"
                | "dob"
                | "-dob"
                | "trnNumberCheckedDate"
                | "-trnNumberCheckedDate"
                | "criminalRecord.dbsLastChecked"
                | "-criminalRecord.dbsLastChecked"
                | "criminalRecord.updateServiceCheckDate"
                | "-criminalRecord.updateServiceCheckDate"
                | "rtwVerification"
                | "-rtwVerification"
                | "criminalRecord.section128Checked"
                | "-criminalRecord.section128Checked"
                | "criminalRecord.prohibitionCheck"
                | "-criminalRecord.prohibitionCheck"
                | "createdAt"
                | "-createdAt"
                | "updatedAt"
                | "-updatedAt"
                | "_internalId"
                | "-_internalId"
                | "trnNumber"
                | "-trnNumber"
                | "desiredLocation"
                | "-desiredLocation"
                | "telephone"
                | "-telephone"
                | "previousNames"
                | "-previousNames"
                | "otherNames"
                | "-otherNames"
                | "location"
                | "-location"
              )
            | ("availabilityPriority" | "-availabilityPriority");
          /**
           * @maxItems 250
           */
          select?: (
            | "id"
            | "forename"
            | "surname"
            | "previousNames"
            | "email"
            | "telephone"
            | "mobile"
            | "occupation"
            | "subject"
            | "address.addressLine1"
            | "address.addressLine2"
            | "address.addressLine3"
            | "address.city"
            | "address.county"
            | "address.borough"
            | "address.postcode"
            | "address.country"
            | "address.lat"
            | "address.lng"
            | "qualifications"
            | "employment"
            | "references"
            | "referenceCheckConsent"
            | "referenceCheckConsentName"
            | "referenceCheckConsentDate"
            | "dob"
            | "sex"
            | "status"
            | "onboardingStatus"
            | "onboardingSent"
            | "nextOfKin.nextOfKinName"
            | "nextOfKin.nextOfKinRelationship"
            | "nextOfKin.nextOfKinTelephone"
            | "nextOfKin.nextOfKinEmail"
            | "nextOfKin.nextOfKinAddress.addressLine1"
            | "nextOfKin.nextOfKinAddress.addressLine2"
            | "nextOfKin.nextOfKinAddress.addressLine3"
            | "nextOfKin.nextOfKinAddress.city"
            | "nextOfKin.nextOfKinAddress.county"
            | "nextOfKin.nextOfKinAddress.borough"
            | "nextOfKin.nextOfKinAddress.postcode"
            | "nextOfKin.nextOfKinAddress.country"
            | "nextOfKin.nextOfKinAddress.lat"
            | "nextOfKin.nextOfKinAddress.lng"
            | "criminalRecord.dbsHeld"
            | "criminalRecord.dbsNumber"
            | "criminalRecord.dbsType"
            | "criminalRecord.dbsLastChecked"
            | "criminalRecord.dbsUpdateServiceNumber"
            | "criminalRecord.prohibitionCheck"
            | "criminalRecord.overseasCheck"
            | "criminalRecord.overseasCheckNote"
            | "criminalRecord.convictedOffence"
            | "criminalRecord.convictedOffenceDescription"
            | "criminalRecord.convictions"
            | "criminalRecord.convictionsDescription"
            | "criminalRecord.registeredToUpdateService"
            | "criminalRecord.consentUpdateServiceChecks"
            | "criminalRecord.note"
            | "criminalRecord.updateServiceCheckDate"
            | "criminalRecord.abroad"
            | "criminalRecord.overseasDeclarationExtended"
            | "criminalRecord.educationalDisciplinaryProceedings"
            | "criminalRecord.dbsComments"
            | "criminalRecord.dbsDeclarationRead"
            | "criminalRecord.overseasOffence"
            | "criminalRecord.overseasOffenceDescription"
            | "criminalRecord.currentCriminalOffence"
            | "criminalRecord.currentCriminalOffenceDescription"
            | "criminalRecord.dbsIssuedByBusiness"
            | "criminalRecord.dbsIssuedByCurrentEmployer"
            | "criminalRecord.section128Required"
            | "criminalRecord.section128Checked"
            | "criminalRecordDeclaration.questions"
            | "criminalRecordDeclaration.answers"
            | "criminalRecordDeclaration.submittedAt"
            | "criminalRecordDeclaration.submittedIp"
            | "criminalRecordDeclaration.configurationUpdatedAt"
            | "criminalRecordCheckConsent"
            | "criminalRecordCheckConsentName"
            | "criminalRecordCheckConsentDate"
            | "safeInEducation.barred"
            | "safeInEducation.barredCheckDate"
            | "safeInEducation.waiver"
            | "safeInEducation.ordersYou"
            | "safeInEducation.ordersChild"
            | "safeInEducation.previousSafeguardingAllegations"
            | "safeInEducation.cautioned"
            | "safeInEducation.violentOffence"
            | "safeInEducation.anotherCountry"
            | "safeInEducation.readKCSIE"
            | "safeInEducation.readChildcareAct"
            | "safeInEducation.prohibitedFromTeaching"
            | "safeInEducation.knownPolice"
            | "safeInEducation.prohibitedFromManagement"
            | "safeInEducation.criminalOffencesLaw"
            | "safeInEducation.safeInEducationDetails"
            | "safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.signOfAbuse"
            | "safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
            | "safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
            | "safeInEducation.safeguardingQuestionaire.fgmDefinition"
            | "safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
            | "safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
            | "safeInEducation.safeguardingQuestionaire.studentConfides"
            | "socialMedia.facebook"
            | "socialMedia.instagram"
            | "socialMedia.twitter"
            | "socialMedia.linkedIn"
            | "socialMedia.tiktok"
            | "socialMedia.others"
            | "socialMedia.socialMediaChecked"
            | "socialMedia.googleCheckedDate"
            | "safeInEducationCheckConsent"
            | "safeInEducationCheckConsentName"
            | "safeInEducationCheckConsentDate"
            | "healthRecord.physicalAbilityCondition"
            | "healthRecord.confused"
            | "healthRecord.hearing"
            | "healthRecord.eyesight"
            | "healthRecord.depression"
            | "healthRecord.severePain"
            | "healthRecord.excessiveDrowsiness"
            | "healthRecord.epilepsy"
            | "healthRecord.heart"
            | "healthRecord.asthma"
            | "healthRecord.mentalHealth"
            | "healthRecord.expectantMother"
            | "healthRecord.allergies"
            | "healthRecord.medication"
            | "healthRecord.healthIssues"
            | "healthRecord.healthIssuesDescription"
            | "healthRecord.disabilityNeeds"
            | "healthRecord.contagiousDiseases"
            | "healthRecord.affectAbility"
            | "healthRecord.notificationAgree"
            | "healthRecord.sasOptIn"
            | "healthRecord.signature"
            | "healthRecord.drawnSignature"
            | "healthRecord.signatureDate"
            | "healthRecord.vaccinationMeasles"
            | "healthRecord.vaccinationMumps"
            | "healthRecord.vaccinationRubella"
            | "healthRecord.vaccinationVaricella"
            | "healthRecord.vaccinationTuberculosis"
            | "healthRecord.vaccinationHepatitisB"
            | "healthRecord.vaccinationHepatitisC"
            | "healthRecord.vaccinationHIV"
            | "healthRecord.vaccinationCovid"
            | "healthRecord.vaccinationCovid1"
            | "healthRecord.vaccinationCovid2"
            | "healthRecord.vaccinationCovidBooster"
            | "healthRecord.vaccinationCovidInfluenza"
            | "healthRecord.fitToWorkOverride"
            | "healthDeclaration.questions"
            | "healthDeclaration.answers"
            | "healthDeclaration.submittedAt"
            | "healthDeclaration.submittedIp"
            | "healthDeclaration.configurationUpdatedAt"
            | "healthRecordCheckConsent"
            | "healthRecordCheckConsentName"
            | "healthRecordCheckConsentDate"
            | "bankDetails.bankAccountName"
            | "bankDetails.bankName"
            | "bankDetails.accountNumber"
            | "bankDetails.sortCode"
            | "bankDetails.niNumber"
            | "bankDetails.jobOp1"
            | "bankDetails.jobOp2"
            | "bankDetails.jobOp3"
            | "bankDetails.studentLoan.studentLoan"
            | "bankDetails.studentLoan.plan1"
            | "bankDetails.studentLoan.plan2"
            | "bankDetails.studentLoan.plan4"
            | "bankDetails.studentLoan.postGradLoan"
            | "declarationCheckConsent"
            | "declarationCheckConsentName"
            | "declarationCheckConsentDate"
            | "contractForServiceConsent"
            | "contractForServiceName"
            | "contractForServiceDate"
            | "confirmed"
            | "createdBy"
            | "customerId"
            | "migrationId"
            | "migrationApproval"
            | "assignee"
            | "assignee.id"
            | "assignee.email"
            | "assignee.firstName"
            | "assignee.lastName"
            | "assignee.username"
            | "assignee.lea"
            | "assignee.clients"
            | "assignee.position"
            | "assignee.permissions"
            | "assignee.activationCode"
            | "assignee.customer"
            | "assignee.disabled"
            | "assignee.commissionThreshold"
            | "assignee.branches"
            | "assignee.displayOnboarding"
            | "assignee._customId"
            | "assignee.signature"
            | "assignee.phone"
            | "assignee.defaultCandidateWatcher"
            | "assignee.defaultTaskWorkspace"
            | "assignee.teamAdmin"
            | "assignee.contact"
            | "assignee.contact.id"
            | "assignee.contact.forename"
            | "assignee.contact.surname"
            | "assignee.contact.email"
            | "assignee.contact.telephone"
            | "assignee.contact.mobile"
            | "assignee.contact.extension"
            | "assignee.createdAt"
            | "assignee.evertime.id"
            | "assignee.timesheetPortal.id"
            | "assignee.timesheetPortal.code"
            | "currentBooking"
            | "profilePhoto"
            | "payType"
            | "payInterval"
            | "payRate"
            | "customId"
            | "_internalId"
            | "statusChangeDate"
            | "optOut"
            | "trnNumber"
            | "trnNumberCheckedDate"
            | "trnNumberNotes"
            | "employmentStartDate"
            | "ewcConfirmation"
            | "homeOfficeShareCode"
            | "archived"
            | "umbrella"
            | "payeUmbrella"
            | "umbrellaCompanyName"
            | "umbrellaCompanyCode"
            | "transport"
            | "importantNotes"
            | "candidateLastUpdated"
            | "source"
            | "sourceOther"
            | "onlineCheckConsent"
            | "title"
            | "pronoun"
            | "updatedAt"
            | "lastContactedDate"
            | "highlight"
            | "teacherPayScale"
            | "p45"
            | "roleType"
            | "rtwVerification"
            | "rtwVerificationNote"
            | "rtwVerificationStatus"
            | "desiredLocation"
            | "complianceUser"
            | "complianceUser.id"
            | "complianceUser.email"
            | "complianceUser.firstName"
            | "complianceUser.lastName"
            | "complianceUser.username"
            | "complianceUser.lea"
            | "complianceUser.clients"
            | "complianceUser.position"
            | "complianceUser.permissions"
            | "complianceUser.activationCode"
            | "complianceUser.customer"
            | "complianceUser.disabled"
            | "complianceUser.commissionThreshold"
            | "complianceUser.branches"
            | "complianceUser.displayOnboarding"
            | "complianceUser._customId"
            | "complianceUser.signature"
            | "complianceUser.phone"
            | "complianceUser.defaultCandidateWatcher"
            | "complianceUser.defaultTaskWorkspace"
            | "complianceUser.teamAdmin"
            | "complianceUser.contact"
            | "complianceUser.contact.id"
            | "complianceUser.contact.forename"
            | "complianceUser.contact.surname"
            | "complianceUser.contact.email"
            | "complianceUser.contact.telephone"
            | "complianceUser.contact.mobile"
            | "complianceUser.contact.extension"
            | "complianceUser.createdAt"
            | "complianceUser.evertime.id"
            | "complianceUser.timesheetPortal.id"
            | "complianceUser.timesheetPortal.code"
            | "watchers"
            | "tags"
            | "documents"
            | "documentsCount"
            | "desiredLocationMultiple"
            | "availabilityDays"
            | "availabilityLastChecked"
            | "subjectToNotice"
            | "subsidiarySubject"
            | "employmentPreference"
            | "otherNames"
            | "ltdCompany"
            | "employmentVerified"
            | "candidateDBSVerification"
            | "candidateDBSVerificationNote"
            | "candidateDBSVerificationStatus"
            | "candidateDBSCheckVerification"
            | "candidateDBSCheckVerificationNote"
            | "candidateDBSCheckVerificationStatus"
            | "candidateDBSUpdateServiceVerificationNote"
            | "candidateDBSUpdateServiceVerificationStatus"
            | "candidateTRNVerificationNote"
            | "candidateTRNVerificationStatus"
            | "candidateHealthSafetyVerification"
            | "candidateHealthSafetyVerificationNote"
            | "candidateHealthSafetyVerificationStatus"
            | "onboardingSession"
            | "onboardingSession.id"
            | "onboardingSession.url"
            | "onboardingSession.healthDeclarationConfig.questions"
            | "onboardingSession.healthDeclarationConfig.updatedAt"
            | "onboardingSession.criminalRecordConfig.questions"
            | "onboardingSession.criminalRecordConfig.updatedAt"
            | "contact"
            | "contact.id"
            | "contact.forename"
            | "contact.surname"
            | "contact.email"
            | "contact.telephone"
            | "contact.mobile"
            | "contact.extension"
            | "optOut48Hours"
            | "recAuditConsent"
            | "recAuditConsentDate"
            | "recAuditConsentMethod"
            | "createdAt"
            | "candidateMobileAppStatus"
            | "evertime.id"
            | "evertime.niNumberMatch"
            | "relevanceScore"
            | "matchReasons"
            | "distance"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "mobileAppLinkInvite.isUsed"
            | "mobileAppLinkInvite.createdAt"
            | "mobileAppLinkInvite.usedBy"
            | "mobileAppLinkInvite.usedAt"
            | "mobileAppLinkInvite.unlinkedBy"
            | "mobileAppLinkInvite.unlinkedAt"
            | "mobileAppLinkInvite.deletedBy"
            | "mobileAppLinkInvite.deletedAt"
            | "mobileAppLinkInvite.status"
          )[];
          /**
           * @maxItems 100
           */
          expand?: (
            | "assignee"
            | "contact"
            | "customer"
            | "umbrella"
            | "payeUmbrella"
            | "complianceUser"
            | "onboardingSession"
            | "watchers"
            | "tags"
            | "documents"
          )[];
          filter?: {
            /**
             * Inclusive calendar dates, up to 31 days. Both boundaries are required when selecting availability days, filtering availability statuses or sorting by availability priority.
             */
            availabilityPeriod?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Match candidates with any selected status during the availability period. Requires an availability period. Empty selects all statuses.
             *
             * @maxItems 5
             */
            availabilityStatuses?: (
              | "available"
              | "unavailable"
              | "booked"
              | "awaiting"
              | "notRecorded"
            )[];
            /**
             * @maxItems 50
             */
            ids?: string[];
            /**
             * @maxItems 25
             */
            status?: (
              | "Pending"
              | "Approved"
              | "Rejected"
              | "Anonymised"
              | "Archived"
              | "Enquired"
              | "Hold"
              | "Future"
              | "DNC"
              | "Pre"
            )[];
            /**
             * @maxItems 25
             */
            onboardingStatus?: ("Not Started" | "In Progress" | "Completed")[];
            activeOnboardingSession?: boolean;
            onboardingSent?: "true" | "false";
            section128Required?: "true" | "false";
            confirmed?: "true" | "false";
            optOut?: "true" | "false";
            optOut48Hours?: "true" | "false";
            fitToWorkOverride?: "true" | "false";
            affectAbility?: "true" | "false";
            ewcConfirmation?: "true" | "false";
            dbsHeld?: "true" | "false";
            dbsRegisteredToUpdateService?: "true" | "false";
            /**
             * @maxLength 500
             */
            firstName?: string;
            /**
             * @maxLength 500
             */
            lastName?: string;
            /**
             * @maxLength 500
             */
            email?: string;
            /**
             * @maxLength 500
             */
            mobile?: string;
            /**
             * Address fields used to filter candidates. Multiple supplied fields are combined.
             */
            address?: {
              /**
               * @maxLength 500
               */
              city?: string;
              /**
               * @maxLength 20
               */
              postcode?: string;
              /**
               * @maxLength 500
               */
              county?: string;
              /**
               * @maxLength 500
               */
              borough?: string;
              /**
               * @maxLength 500
               */
              addressLine1?: string;
              /**
               * @maxLength 500
               */
              addressLine2?: string;
              /**
               * @maxLength 500
               */
              addressLine3?: string;
            };
            /**
             * @maxItems 25
             */
            occupation?: string[];
            /**
             * @maxLength 500
             */
            specialism?: string;
            /**
             * @maxLength 500
             */
            _internalId?: string;
            /**
             * @maxItems 25
             */
            complianceUser?: string[];
            /**
             * @maxItems 25
             */
            assignee?: string[];
            /**
             * @maxItems 25
             */
            watcher?: string[];
            /**
             * @maxItems 25
             */
            team?: string[];
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            contactId?: string;
            currentBooking?: "true" | "false";
            bookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            notBookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            tags?: string[];
            location?: {
              lat?: number | null;
              lng?: number | null;
              radius?: number | null;
            };
            statusChangeDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            lastContactedDateFilter?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            employmentPreference?: (
              | "Day To Day"
              | "Long Term"
              | "Fixed Term"
              | "Perm"
            )[];
            /**
             * @maxItems 25
             */
            transport?: (
              | "Drives Own Vehicle"
              | "Use Public Transport"
              | "Access to Another Vehicle"
              | "Bicycle"
              | "No Access"
              | "Other"
            )[];
            /**
             * @maxLength 500
             */
            trnNumber?: string;
            /**
             * @maxLength 500
             */
            desiredLocation?: string;
            /**
             * @maxItems 25
             */
            subjectToNotice?: ("Unsure" | "Yes" | "No")[];
            /**
             * @maxItems 25
             */
            title?: (
              | "Mr"
              | "Mrs"
              | "Ms"
              | "Miss"
              | "Dr"
              | "Mx"
              | "Lady"
              | "Lord"
              | "Other"
            )[];
            /**
             * @maxItems 25
             */
            sex?: ("Male" | "Female" | "Other" | "Unknown")[];
            /**
             * @maxItems 25
             */
            pronoun?: (
              | "She/Her/Hers"
              | "He/Him/His"
              | "They/Them/Theirs"
              | "Ze/Hir/Hirs"
              | "Ze/Zir/Zirs"
              | "Other"
              | "Prefer not to say"
            )[];
            /**
             * @maxItems 25
             */
            roleType?: (
              | "Senior Leadership Positions"
              | "Teaching Roles"
              | "School Support Positions"
            )[];
            /**
             * @maxItems 25
             */
            payType?: (
              | "PAYE"
              | "Umbrella"
              | "Limited Company"
              | "Not Applicable"
            )[];
            /**
             * @maxItems 25
             */
            payInterval?: (
              | "Monthly"
              | "Weekly"
              | "Weekly Pay in Arrears"
              | "Bi-Weekly"
            )[];
            /**
             * @maxItems 25
             */
            dbsType?: (
              | "Basic"
              | "Standard"
              | "Enhanced"
              | "Enhanced with Barred List"
              | "Child workforce"
              | "Child and adult workforce"
              | "Adult workforce"
            )[];
            source?: (
              | (
                  | "Company Website"
                  | "iCan Teach UK Website"
                  | "Apply to Education"
                  | "CV Library - CV Search"
                  | "CV Library Advert"
                  | "CV Library - Watchdog"
                  | "Guardian- CV Search"
                  | "Guardian Advert"
                  | "Indeed - CV Search"
                  | "Indeed Advert"
                  | "Reed - CV Search"
                  | "Reed Advert"
                  | "Reed \u2013 Watchdog"
                  | "SENploy - Advert"
                  | "Total Jobs"
                  | "Other Job Board"
                  | "Google"
                  | "LinkedIn"
                  | "Linkedin Recruiter"
                  | "LinkedIn Advert"
                  | "Referral - School"
                  | "Referral - Candidate"
                  | "UK University"
                  | "Irish University"
                  | "Canadian University"
                  | "Marketing Campaign"
                  | "Woo"
                  | "Job Show"
                  | "Recruitment Fair"
                  | "Career Fair"
                  | "Volcanic"
                  | "Broadbean"
                  | "Headhunt"
                  | "Referral"
                  | "Direct"
                  | "Other"
                )
              | (
                  | "Company Website"
                  | "Direct Call"
                  | "CV Library Watchdog"
                  | "CV Library Advert"
                  | "CV Library CV Search"
                  | "Reed Watchdog"
                  | "Reed Advert"
                  | "Reed CV Search"
                  | "Indeed Watchdog"
                  | "Indeed Advert"
                  | "Indeed CV Search"
                  | "Job Centre"
                  | "Returning Candidate"
                  | "Referral Candidate"
                  | "Referral Client"
                  | "Training Provider"
                  | "Recruitment Fair"
                  | "University/College"
                )
            )[];
            /**
             * @maxLength 32
             */
            telephone?: string;
            /**
             * @maxLength 500
             */
            previousNames?: string;
            /**
             * @maxLength 500
             */
            otherNames?: string;
            dob?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            trnNumberCheckedDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            dbsLastChecked?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            dbsUpdateServiceCheckDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            rtwVerificationDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            section128CheckedDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            prohibitionCheckDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            createdAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            updatedAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            candidateMobileAppStatus?: (
              | "Unlinked"
              | "Scheduled Unlink"
              | "Linked"
              | "Invited"
              | "Not Invited"
            )[];
            recAuditConsent?: "true" | "false";
          };
        };
        presentation?: {
          mode?: "table" | "availability";
          availability?: {
            /**
             * An absolute or relative date range. Relative boundaries take precedence when supplied.
             */
            period?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 5
             */
            statuses?: (
              | "available"
              | "unavailable"
              | "booked"
              | "awaiting"
              | "notRecorded"
            )[];
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            draftClient?: string;
            draftMode?: "week" | "all";
          };
        };
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved client search.
         */
        type?: "clients";
        /**
         * The client query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * @maxLength 256
           */
          search?: string;
          sort?:
            | "name"
            | "-name"
            | "createdAt"
            | "-createdAt"
            | "assignee"
            | "-assignee"
            | "telephone"
            | "-telephone"
            | "email"
            | "-email"
            | "address.postcode"
            | "-address.postcode"
            | "address.county"
            | "-address.county"
            | "lea"
            | "-lea"
            | "type"
            | "-type"
            | "multiAcademyTrust"
            | "-multiAcademyTrust"
            | "address.city"
            | "-address.city"
            | "borough"
            | "-borough"
            | "specialism"
            | "-specialism"
            | "internalId"
            | "-internalId"
            | "lastContactedDate"
            | "-lastContactedDate"
            | "distance"
            | "-distance";
          select?: (
            | "id"
            | "name"
            | "address.addressLine1"
            | "address.addressLine2"
            | "address.addressLine3"
            | "address.city"
            | "address.county"
            | "address.borough"
            | "address.postcode"
            | "address.country"
            | "address.lat"
            | "address.lng"
            | "address"
            | "telephone"
            | "email"
            | "contacts"
            | "borough"
            | "lea"
            | "type"
            | "multiAcademyTrust"
            | "color"
            | "customId"
            | "internalId"
            | "assignee"
            | "assignee.id"
            | "assignee.email"
            | "assignee.firstName"
            | "assignee.lastName"
            | "assignee.username"
            | "assignee.lea"
            | "assignee.clients"
            | "assignee.position"
            | "assignee.permissions"
            | "assignee.activationCode"
            | "assignee.customer"
            | "assignee.disabled"
            | "assignee.commissionThreshold"
            | "assignee.branches"
            | "assignee.displayOnboarding"
            | "assignee._customId"
            | "assignee.signature"
            | "assignee.phone"
            | "assignee.defaultCandidateWatcher"
            | "assignee.defaultTaskWorkspace"
            | "assignee.teamAdmin"
            | "assignee.contact"
            | "assignee.contact.id"
            | "assignee.contact.forename"
            | "assignee.contact.surname"
            | "assignee.contact.email"
            | "assignee.contact.telephone"
            | "assignee.contact.mobile"
            | "assignee.contact.extension"
            | "assignee.createdAt"
            | "assignee.evertime.id"
            | "assignee.evertime"
            | "assignee.timesheetPortal.id"
            | "assignee.timesheetPortal.code"
            | "assignee.timesheetPortal"
            | "tags"
            | "tobSentAt"
            | "website"
            | "archived"
            | "specialism"
            | "currentBooking"
            | "bookingCount"
            | "importantNotes"
            | "roles"
            | "startTime"
            | "endTime"
            | "additionalInstructions"
            | "additionalInstructionsClient"
            | "highlight"
            | "area"
            | "governance"
            | "mathq"
            | "ofstedRating"
            | "dateOfstedReportChecked"
            | "boarding"
            | "licenceToIssueSponsorship"
            | "agencySpendTotal"
            | "vatExempt"
            | "splitInvoices"
            | "switchBoard"
            | "ccsClient"
            | "ccsClientUrn"
            | "ccsClientOfstedUrn"
            | "ccsClientLotNumber"
            | "buyerReference"
            | "ccsClientInvoiceDate"
            | "keyStage"
            | "contact"
            | "contact.id"
            | "contact.forename"
            | "contact.surname"
            | "contact.email"
            | "contact.telephone"
            | "contact.mobile"
            | "contact.extension"
            | "lastContactedDate"
            | "relevanceScore"
            | "matchReasons"
            | "distance"
            | "migrationId"
            | "migrationApproval"
            | "evertime.id"
            | "evertime.contacts"
            | "evertime.locations"
            | "evertime.nameMatch"
            | "evertime"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "timesheetPortal"
            | ""
          )[];
          /**
           * @maxItems 25
           */
          expand?: ("assignee" | "tags")[];
          include?: "bookings"[];
          filter?: {
            /**
             * @maxItems 50
             */
            ids?: string[];
            /**
             * Filter clients by name.
             *
             * @maxLength 256
             */
            name?: string;
            /**
             * Filter clients by telephone number.
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * Filter clients by email address.
             *
             * @maxLength 256
             */
            email?: string;
            /**
             * Address fields used to filter clients. Multiple supplied fields are combined.
             */
            address?: {
              /**
               * Filter clients by postcode.
               *
               * @maxLength 32
               */
              postcode?: string;
              /**
               * Filter clients by county.
               *
               * @maxLength 128
               */
              county?: string;
              /**
               * Filter clients by city.
               *
               * @maxLength 128
               */
              city?: string;
            };
            /**
             * Filter clients by local education authority.
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Filter clients by type.
             *
             * @maxLength 128
             */
            type?: string;
            /**
             * Filter clients by multi academy trust.
             *
             * @maxLength 256
             */
            multiAcademyTrust?: string;
            /**
             * Filter clients by borough.
             *
             * @maxLength 128
             */
            borough?: string;
            /**
             * Filter clients by specialism.
             *
             * @maxItems 25
             */
            specialism?: string[];
            /**
             * Filter clients by their company-defined identifier.
             *
             * @maxLength 256
             */
            internalId?: string;
            /**
             * Filter clients by whether they are a GCA client.
             */
            ccsClient?: "true" | "false";
            /**
             * Filter clients by GCA client URN.
             *
             * @maxLength 256
             */
            ccsClientUrn?: string;
            /**
             * Filter clients by assigned users.
             *
             * @maxItems 25
             */
            assignee?: string[];
            /**
             * Filter clients by last contact date.
             */
            lastContactedDateFilter?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients with an active booking overlapping an absolute or relative date range.
             */
            bookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients without an active booking overlapping an absolute or relative date range.
             */
            notBookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients by tags they have.
             *
             * @maxItems 25
             */
            tags?: string[];
            location?: {
              /**
               * @minimum -90
               * @maximum 90
               */
              lat?: number | null;
              /**
               * @minimum -180
               * @maximum 180
               */
              lng?: number | null;
              /**
               * @minimum 0
               * @maximum 500
               */
              radius?: number | null;
            };
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            ["contacts.contactId"]?: string;
          };
        };
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved Client Contact search.
         */
        type?: "clientContacts";
        /**
         * The Client Contact query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * Find relevant Client contacts using contact and Client details.
           *
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * Search Client contacts by name, email, or Client.
           *
           * @maxLength 256
           */
          search?: string;
          /**
           * The field used to order contacts. Prefix with - for descending order.
           */
          sort?:
            | "forename"
            | "-forename"
            | "surname"
            | "-surname"
            | "email"
            | "-email"
            | "telephone"
            | "-telephone"
            | "role"
            | "-role"
            | "status"
            | "-status"
            | "decisionMaker"
            | "-decisionMaker"
            | "clientName"
            | "-clientName"
            | "clientAddress.city"
            | "-clientAddress.city"
            | "clientAddress.county"
            | "-clientAddress.county"
            | "clientAddress.postcode"
            | "-clientAddress.postcode"
            | "clientLea"
            | "-clientLea"
            | "clientType"
            | "-clientType"
            | "clientMultiAcademyTrust"
            | "-clientMultiAcademyTrust"
            | "clientBorough"
            | "-clientBorough"
            | "clientAssignee"
            | "-clientAssignee"
            | "clientSpecialism"
            | "-clientSpecialism"
            | "clientUpdatedAt"
            | "-clientUpdatedAt"
            | "distance"
            | "-distance"
            | "relevanceScore"
            | "-relevanceScore";
          /**
           * The fields to include in the response in dot notation format.
           *
           * @maxItems 250
           */
          select?: (
            | "id"
            | "forename"
            | "surname"
            | "email"
            | "telephone"
            | "mobile"
            | "extension"
            | "role"
            | "roleOther"
            | "contactId"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "timesheetPortal"
            | "optOut"
            | "note"
            | "decisionMaker"
            | "status"
            | "clientId"
            | "clientName"
            | "clientAddress.addressLine1"
            | "clientAddress.addressLine2"
            | "clientAddress.addressLine3"
            | "clientAddress.city"
            | "clientAddress.county"
            | "clientAddress.borough"
            | "clientAddress.postcode"
            | "clientAddress.country"
            | "clientAddress.lat"
            | "clientAddress.lng"
            | "clientAddress"
            | "clientLea"
            | "clientType"
            | "clientMultiAcademyTrust"
            | "clientBorough"
            | "clientAssignee"
            | "clientAssignee.id"
            | "clientAssignee.email"
            | "clientAssignee.firstName"
            | "clientAssignee.lastName"
            | "clientAssignee.username"
            | "clientAssignee.lea"
            | "clientAssignee.clients"
            | "clientAssignee.position"
            | "clientAssignee.permissions"
            | "clientAssignee.activationCode"
            | "clientAssignee.customer"
            | "clientAssignee.disabled"
            | "clientAssignee.commissionThreshold"
            | "clientAssignee.branches"
            | "clientAssignee.displayOnboarding"
            | "clientAssignee._customId"
            | "clientAssignee.signature"
            | "clientAssignee.phone"
            | "clientAssignee.defaultCandidateWatcher"
            | "clientAssignee.defaultTaskWorkspace"
            | "clientAssignee.teamAdmin"
            | "clientAssignee.contact"
            | "clientAssignee.contact.id"
            | "clientAssignee.contact.forename"
            | "clientAssignee.contact.surname"
            | "clientAssignee.contact.email"
            | "clientAssignee.contact.telephone"
            | "clientAssignee.contact.mobile"
            | "clientAssignee.contact.extension"
            | "clientAssignee.createdAt"
            | "clientAssignee.evertime.id"
            | "clientAssignee.evertime"
            | "clientAssignee.timesheetPortal.id"
            | "clientAssignee.timesheetPortal.code"
            | "clientAssignee.timesheetPortal"
            | "clientSpecialism"
            | "clientUpdatedAt"
            | "distance"
            | "relevanceScore"
            | "matchReasons"
            | ""
          )[];
          /**
           * Related fields to include as objects in the response.
           *
           * @maxItems 1
           */
          expand?: "clientAssignee"[];
          /**
           * Filter Client contacts using contact and associated Client fields.
           */
          filter?: {
            /**
             * Filter contacts by status.
             *
             * @maxItems 3
             */
            status?: ("Active" | "Passive" | "Inactive")[];
            /**
             * Filter contacts by role.
             *
             * @maxItems 44
             */
            role?: (
              | "Head Teacher"
              | "Deputy Head"
              | "Cover Manager"
              | "HR Manager"
              | "Finance Manager"
              | "Head of Maths"
              | "Head of Science"
              | "Head of English"
              | "Assistant Headteacher"
              | "Headteacher's PA"
              | "SENCO"
              | "Other"
              | "Business Manager"
              | "Business Administrator"
              | "Invoice Contact"
              | "Academy Recruitment Contact"
              | "Nursery Manager"
              | "CEO"
              | "COO"
              | "Executive Headteacher"
              | "Inclusion Manager"
              | "EYFS Lead"
              | "Admin Team"
              | "Head of Humanities"
              | "Head of History"
              | "Head of Geography"
              | "Head of Business Studies"
              | "Head of PE"
              | "Head of KS1"
              | "Head of KS2"
              | "Head of KS3"
              | "Head of KS4"
              | "Virtual School Headteacher"
              | "Deputy Virtual School Headteacher"
              | "Post-16 Team Leader"
              | "SEN Team"
              | "Inclusion Team"
              | "UASC Team"
              | "Caseworker"
              | "Designated Teacher"
              | "Attendance Officer"
              | "Coordinator"
              | "Children's Commissioner"
              | "Timesheet Contact"
            )[];
            /**
             * Filter contacts by forename.
             *
             * @maxLength 128
             */
            forename?: string;
            /**
             * Filter contacts by surname.
             *
             * @maxLength 128
             */
            surname?: string;
            /**
             * Filter contacts by email address.
             *
             * @maxLength 256
             */
            email?: string;
            /**
             * Filter contacts by decision-maker status.
             */
            decisionMaker?: "true" | "false";
            /**
             * Filter contacts by opt-out status.
             */
            optOut?: "true" | "false";
            /**
             * Filter contacts by Client.
             *
             * @maxItems 50
             */
            clientId?: string[];
            /**
             * Filter contacts by Client name.
             *
             * @maxLength 256
             */
            clientName?: string;
            /**
             * Filter contacts by associated Client address fields.
             */
            clientAddress?: {
              /**
               * @maxLength 128
               */
              city?: string;
              /**
               * @maxLength 128
               */
              county?: string;
              /**
               * @maxLength 32
               */
              postcode?: string;
            };
            /**
             * Filter contacts by Client local education authority.
             *
             * @maxLength 128
             */
            clientLea?: string;
            /**
             * Filter contacts by Client sector.
             *
             * @maxLength 128
             */
            clientType?: string;
            /**
             * Filter contacts by Client multi-academy trust.
             *
             * @maxLength 256
             */
            clientMultiAcademyTrust?: string;
            /**
             * Filter contacts by Client borough.
             *
             * @maxLength 128
             */
            clientBorough?: string;
            /**
             * Filter contacts by Client assignee.
             *
             * @maxItems 25
             */
            clientAssignee?: string[];
            /**
             * Filter contacts by Client specialism.
             *
             * @maxItems 25
             */
            clientSpecialism?: string[];
            /**
             * Filter contacts by when their Client was updated.
             */
            clientUpdatedAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter contacts by distance from their Client address.
             */
            location?: {
              /**
               * @minimum -90
               * @maximum 90
               */
              lat?: number | null;
              /**
               * @minimum -180
               * @maximum 180
               */
              lng?: number | null;
              /**
               * @minimum 0
               * @exclusiveMinimum true
               * @maximum 500
               */
              radius?: number;
            };
          };
        };
      }
  )[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetSavedSearchesVariables = {
  queryParams?: GetSavedSearchesQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetSavedSearches = (
  variables: GetSavedSearchesVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetSavedSearchesResponse,
    GetSavedSearchesError,
    undefined,
    {},
    GetSavedSearchesQueryParams,
    {}
  >({ url: "/saved-searches", method: "get", ...variables, signal });

export function getSavedSearchesQuery(variables: GetSavedSearchesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetSavedSearchesResponse>;
};

export function getSavedSearchesQuery(
  variables: GetSavedSearchesVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetSavedSearchesResponse>)
    | reactQuery.SkipToken;
};

export function getSavedSearchesQuery(
  variables: GetSavedSearchesVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/saved-searches",
      operationId: "getSavedSearches",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetSavedSearches(variables, signal),
  };
}

export const useSuspenseGetSavedSearches = <TData = GetSavedSearchesResponse,>(
  variables: GetSavedSearchesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetSavedSearchesResponse,
      GetSavedSearchesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetSavedSearchesResponse,
    GetSavedSearchesError,
    TData
  >({
    ...getSavedSearchesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetSavedSearches = <TData = GetSavedSearchesResponse,>(
  variables: GetSavedSearchesVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetSavedSearchesResponse,
      GetSavedSearchesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetSavedSearchesResponse,
    GetSavedSearchesError,
    TData
  >({
    ...getSavedSearchesQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateSavedSearchError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 409;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateSavedSearchResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data:
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved candidate search.
         */
        type?: "candidates";
        /**
         * The candidate query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * @maxLength 256
           */
          search?: string;
          sort?:
            | (
                | "relevanceScore"
                | "-relevanceScore"
                | "forename"
                | "-forename"
                | "surname"
                | "-surname"
                | "mobile"
                | "-mobile"
                | "email"
                | "-email"
                | "address.city"
                | "-address.city"
                | "address.postcode"
                | "-address.postcode"
                | "address.county"
                | "-address.county"
                | "address.borough"
                | "-address.borough"
                | "address.addressLine1"
                | "-address.addressLine1"
                | "address.addressLine2"
                | "-address.addressLine2"
                | "address.addressLine3"
                | "-address.addressLine3"
                | "subject"
                | "-subject"
                | "statusChangeDate"
                | "-statusChangeDate"
                | "lastContactedDate"
                | "-lastContactedDate"
                | "daysSinceLastContactDisplay"
                | "-daysSinceLastContactDisplay"
                | "status"
                | "-status"
                | "occupation"
                | "-occupation"
                | "dob"
                | "-dob"
                | "trnNumberCheckedDate"
                | "-trnNumberCheckedDate"
                | "criminalRecord.dbsLastChecked"
                | "-criminalRecord.dbsLastChecked"
                | "criminalRecord.updateServiceCheckDate"
                | "-criminalRecord.updateServiceCheckDate"
                | "rtwVerification"
                | "-rtwVerification"
                | "criminalRecord.section128Checked"
                | "-criminalRecord.section128Checked"
                | "criminalRecord.prohibitionCheck"
                | "-criminalRecord.prohibitionCheck"
                | "createdAt"
                | "-createdAt"
                | "updatedAt"
                | "-updatedAt"
                | "_internalId"
                | "-_internalId"
                | "trnNumber"
                | "-trnNumber"
                | "desiredLocation"
                | "-desiredLocation"
                | "telephone"
                | "-telephone"
                | "previousNames"
                | "-previousNames"
                | "otherNames"
                | "-otherNames"
                | "location"
                | "-location"
              )
            | ("availabilityPriority" | "-availabilityPriority");
          /**
           * @maxItems 250
           */
          select?: (
            | "id"
            | "forename"
            | "surname"
            | "previousNames"
            | "email"
            | "telephone"
            | "mobile"
            | "occupation"
            | "subject"
            | "address.addressLine1"
            | "address.addressLine2"
            | "address.addressLine3"
            | "address.city"
            | "address.county"
            | "address.borough"
            | "address.postcode"
            | "address.country"
            | "address.lat"
            | "address.lng"
            | "qualifications"
            | "employment"
            | "references"
            | "referenceCheckConsent"
            | "referenceCheckConsentName"
            | "referenceCheckConsentDate"
            | "dob"
            | "sex"
            | "status"
            | "onboardingStatus"
            | "onboardingSent"
            | "nextOfKin.nextOfKinName"
            | "nextOfKin.nextOfKinRelationship"
            | "nextOfKin.nextOfKinTelephone"
            | "nextOfKin.nextOfKinEmail"
            | "nextOfKin.nextOfKinAddress.addressLine1"
            | "nextOfKin.nextOfKinAddress.addressLine2"
            | "nextOfKin.nextOfKinAddress.addressLine3"
            | "nextOfKin.nextOfKinAddress.city"
            | "nextOfKin.nextOfKinAddress.county"
            | "nextOfKin.nextOfKinAddress.borough"
            | "nextOfKin.nextOfKinAddress.postcode"
            | "nextOfKin.nextOfKinAddress.country"
            | "nextOfKin.nextOfKinAddress.lat"
            | "nextOfKin.nextOfKinAddress.lng"
            | "criminalRecord.dbsHeld"
            | "criminalRecord.dbsNumber"
            | "criminalRecord.dbsType"
            | "criminalRecord.dbsLastChecked"
            | "criminalRecord.dbsUpdateServiceNumber"
            | "criminalRecord.prohibitionCheck"
            | "criminalRecord.overseasCheck"
            | "criminalRecord.overseasCheckNote"
            | "criminalRecord.convictedOffence"
            | "criminalRecord.convictedOffenceDescription"
            | "criminalRecord.convictions"
            | "criminalRecord.convictionsDescription"
            | "criminalRecord.registeredToUpdateService"
            | "criminalRecord.consentUpdateServiceChecks"
            | "criminalRecord.note"
            | "criminalRecord.updateServiceCheckDate"
            | "criminalRecord.abroad"
            | "criminalRecord.overseasDeclarationExtended"
            | "criminalRecord.educationalDisciplinaryProceedings"
            | "criminalRecord.dbsComments"
            | "criminalRecord.dbsDeclarationRead"
            | "criminalRecord.overseasOffence"
            | "criminalRecord.overseasOffenceDescription"
            | "criminalRecord.currentCriminalOffence"
            | "criminalRecord.currentCriminalOffenceDescription"
            | "criminalRecord.dbsIssuedByBusiness"
            | "criminalRecord.dbsIssuedByCurrentEmployer"
            | "criminalRecord.section128Required"
            | "criminalRecord.section128Checked"
            | "criminalRecordDeclaration.questions"
            | "criminalRecordDeclaration.answers"
            | "criminalRecordDeclaration.submittedAt"
            | "criminalRecordDeclaration.submittedIp"
            | "criminalRecordDeclaration.configurationUpdatedAt"
            | "criminalRecordCheckConsent"
            | "criminalRecordCheckConsentName"
            | "criminalRecordCheckConsentDate"
            | "safeInEducation.barred"
            | "safeInEducation.barredCheckDate"
            | "safeInEducation.waiver"
            | "safeInEducation.ordersYou"
            | "safeInEducation.ordersChild"
            | "safeInEducation.previousSafeguardingAllegations"
            | "safeInEducation.cautioned"
            | "safeInEducation.violentOffence"
            | "safeInEducation.anotherCountry"
            | "safeInEducation.readKCSIE"
            | "safeInEducation.readChildcareAct"
            | "safeInEducation.prohibitedFromTeaching"
            | "safeInEducation.knownPolice"
            | "safeInEducation.prohibitedFromManagement"
            | "safeInEducation.criminalOffencesLaw"
            | "safeInEducation.safeInEducationDetails"
            | "safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.signOfAbuse"
            | "safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
            | "safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
            | "safeInEducation.safeguardingQuestionaire.fgmDefinition"
            | "safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
            | "safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
            | "safeInEducation.safeguardingQuestionaire.studentConfides"
            | "socialMedia.facebook"
            | "socialMedia.instagram"
            | "socialMedia.twitter"
            | "socialMedia.linkedIn"
            | "socialMedia.tiktok"
            | "socialMedia.others"
            | "socialMedia.socialMediaChecked"
            | "socialMedia.googleCheckedDate"
            | "safeInEducationCheckConsent"
            | "safeInEducationCheckConsentName"
            | "safeInEducationCheckConsentDate"
            | "healthRecord.physicalAbilityCondition"
            | "healthRecord.confused"
            | "healthRecord.hearing"
            | "healthRecord.eyesight"
            | "healthRecord.depression"
            | "healthRecord.severePain"
            | "healthRecord.excessiveDrowsiness"
            | "healthRecord.epilepsy"
            | "healthRecord.heart"
            | "healthRecord.asthma"
            | "healthRecord.mentalHealth"
            | "healthRecord.expectantMother"
            | "healthRecord.allergies"
            | "healthRecord.medication"
            | "healthRecord.healthIssues"
            | "healthRecord.healthIssuesDescription"
            | "healthRecord.disabilityNeeds"
            | "healthRecord.contagiousDiseases"
            | "healthRecord.affectAbility"
            | "healthRecord.notificationAgree"
            | "healthRecord.sasOptIn"
            | "healthRecord.signature"
            | "healthRecord.drawnSignature"
            | "healthRecord.signatureDate"
            | "healthRecord.vaccinationMeasles"
            | "healthRecord.vaccinationMumps"
            | "healthRecord.vaccinationRubella"
            | "healthRecord.vaccinationVaricella"
            | "healthRecord.vaccinationTuberculosis"
            | "healthRecord.vaccinationHepatitisB"
            | "healthRecord.vaccinationHepatitisC"
            | "healthRecord.vaccinationHIV"
            | "healthRecord.vaccinationCovid"
            | "healthRecord.vaccinationCovid1"
            | "healthRecord.vaccinationCovid2"
            | "healthRecord.vaccinationCovidBooster"
            | "healthRecord.vaccinationCovidInfluenza"
            | "healthRecord.fitToWorkOverride"
            | "healthDeclaration.questions"
            | "healthDeclaration.answers"
            | "healthDeclaration.submittedAt"
            | "healthDeclaration.submittedIp"
            | "healthDeclaration.configurationUpdatedAt"
            | "healthRecordCheckConsent"
            | "healthRecordCheckConsentName"
            | "healthRecordCheckConsentDate"
            | "bankDetails.bankAccountName"
            | "bankDetails.bankName"
            | "bankDetails.accountNumber"
            | "bankDetails.sortCode"
            | "bankDetails.niNumber"
            | "bankDetails.jobOp1"
            | "bankDetails.jobOp2"
            | "bankDetails.jobOp3"
            | "bankDetails.studentLoan.studentLoan"
            | "bankDetails.studentLoan.plan1"
            | "bankDetails.studentLoan.plan2"
            | "bankDetails.studentLoan.plan4"
            | "bankDetails.studentLoan.postGradLoan"
            | "declarationCheckConsent"
            | "declarationCheckConsentName"
            | "declarationCheckConsentDate"
            | "contractForServiceConsent"
            | "contractForServiceName"
            | "contractForServiceDate"
            | "confirmed"
            | "createdBy"
            | "customerId"
            | "migrationId"
            | "migrationApproval"
            | "assignee"
            | "assignee.id"
            | "assignee.email"
            | "assignee.firstName"
            | "assignee.lastName"
            | "assignee.username"
            | "assignee.lea"
            | "assignee.clients"
            | "assignee.position"
            | "assignee.permissions"
            | "assignee.activationCode"
            | "assignee.customer"
            | "assignee.disabled"
            | "assignee.commissionThreshold"
            | "assignee.branches"
            | "assignee.displayOnboarding"
            | "assignee._customId"
            | "assignee.signature"
            | "assignee.phone"
            | "assignee.defaultCandidateWatcher"
            | "assignee.defaultTaskWorkspace"
            | "assignee.teamAdmin"
            | "assignee.contact"
            | "assignee.contact.id"
            | "assignee.contact.forename"
            | "assignee.contact.surname"
            | "assignee.contact.email"
            | "assignee.contact.telephone"
            | "assignee.contact.mobile"
            | "assignee.contact.extension"
            | "assignee.createdAt"
            | "assignee.evertime.id"
            | "assignee.timesheetPortal.id"
            | "assignee.timesheetPortal.code"
            | "currentBooking"
            | "profilePhoto"
            | "payType"
            | "payInterval"
            | "payRate"
            | "customId"
            | "_internalId"
            | "statusChangeDate"
            | "optOut"
            | "trnNumber"
            | "trnNumberCheckedDate"
            | "trnNumberNotes"
            | "employmentStartDate"
            | "ewcConfirmation"
            | "homeOfficeShareCode"
            | "archived"
            | "umbrella"
            | "payeUmbrella"
            | "umbrellaCompanyName"
            | "umbrellaCompanyCode"
            | "transport"
            | "importantNotes"
            | "candidateLastUpdated"
            | "source"
            | "sourceOther"
            | "onlineCheckConsent"
            | "title"
            | "pronoun"
            | "updatedAt"
            | "lastContactedDate"
            | "highlight"
            | "teacherPayScale"
            | "p45"
            | "roleType"
            | "rtwVerification"
            | "rtwVerificationNote"
            | "rtwVerificationStatus"
            | "desiredLocation"
            | "complianceUser"
            | "complianceUser.id"
            | "complianceUser.email"
            | "complianceUser.firstName"
            | "complianceUser.lastName"
            | "complianceUser.username"
            | "complianceUser.lea"
            | "complianceUser.clients"
            | "complianceUser.position"
            | "complianceUser.permissions"
            | "complianceUser.activationCode"
            | "complianceUser.customer"
            | "complianceUser.disabled"
            | "complianceUser.commissionThreshold"
            | "complianceUser.branches"
            | "complianceUser.displayOnboarding"
            | "complianceUser._customId"
            | "complianceUser.signature"
            | "complianceUser.phone"
            | "complianceUser.defaultCandidateWatcher"
            | "complianceUser.defaultTaskWorkspace"
            | "complianceUser.teamAdmin"
            | "complianceUser.contact"
            | "complianceUser.contact.id"
            | "complianceUser.contact.forename"
            | "complianceUser.contact.surname"
            | "complianceUser.contact.email"
            | "complianceUser.contact.telephone"
            | "complianceUser.contact.mobile"
            | "complianceUser.contact.extension"
            | "complianceUser.createdAt"
            | "complianceUser.evertime.id"
            | "complianceUser.timesheetPortal.id"
            | "complianceUser.timesheetPortal.code"
            | "watchers"
            | "tags"
            | "documents"
            | "documentsCount"
            | "desiredLocationMultiple"
            | "availabilityDays"
            | "availabilityLastChecked"
            | "subjectToNotice"
            | "subsidiarySubject"
            | "employmentPreference"
            | "otherNames"
            | "ltdCompany"
            | "employmentVerified"
            | "candidateDBSVerification"
            | "candidateDBSVerificationNote"
            | "candidateDBSVerificationStatus"
            | "candidateDBSCheckVerification"
            | "candidateDBSCheckVerificationNote"
            | "candidateDBSCheckVerificationStatus"
            | "candidateDBSUpdateServiceVerificationNote"
            | "candidateDBSUpdateServiceVerificationStatus"
            | "candidateTRNVerificationNote"
            | "candidateTRNVerificationStatus"
            | "candidateHealthSafetyVerification"
            | "candidateHealthSafetyVerificationNote"
            | "candidateHealthSafetyVerificationStatus"
            | "onboardingSession"
            | "onboardingSession.id"
            | "onboardingSession.url"
            | "onboardingSession.healthDeclarationConfig.questions"
            | "onboardingSession.healthDeclarationConfig.updatedAt"
            | "onboardingSession.criminalRecordConfig.questions"
            | "onboardingSession.criminalRecordConfig.updatedAt"
            | "contact"
            | "contact.id"
            | "contact.forename"
            | "contact.surname"
            | "contact.email"
            | "contact.telephone"
            | "contact.mobile"
            | "contact.extension"
            | "optOut48Hours"
            | "recAuditConsent"
            | "recAuditConsentDate"
            | "recAuditConsentMethod"
            | "createdAt"
            | "candidateMobileAppStatus"
            | "evertime.id"
            | "evertime.niNumberMatch"
            | "relevanceScore"
            | "matchReasons"
            | "distance"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "mobileAppLinkInvite.isUsed"
            | "mobileAppLinkInvite.createdAt"
            | "mobileAppLinkInvite.usedBy"
            | "mobileAppLinkInvite.usedAt"
            | "mobileAppLinkInvite.unlinkedBy"
            | "mobileAppLinkInvite.unlinkedAt"
            | "mobileAppLinkInvite.deletedBy"
            | "mobileAppLinkInvite.deletedAt"
            | "mobileAppLinkInvite.status"
          )[];
          /**
           * @maxItems 100
           */
          expand?: (
            | "assignee"
            | "contact"
            | "customer"
            | "umbrella"
            | "payeUmbrella"
            | "complianceUser"
            | "onboardingSession"
            | "watchers"
            | "tags"
            | "documents"
          )[];
          filter?: {
            /**
             * Inclusive calendar dates, up to 31 days. Both boundaries are required when selecting availability days, filtering availability statuses or sorting by availability priority.
             */
            availabilityPeriod?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Match candidates with any selected status during the availability period. Requires an availability period. Empty selects all statuses.
             *
             * @maxItems 5
             */
            availabilityStatuses?: (
              | "available"
              | "unavailable"
              | "booked"
              | "awaiting"
              | "notRecorded"
            )[];
            /**
             * @maxItems 50
             */
            ids?: string[];
            /**
             * @maxItems 25
             */
            status?: (
              | "Pending"
              | "Approved"
              | "Rejected"
              | "Anonymised"
              | "Archived"
              | "Enquired"
              | "Hold"
              | "Future"
              | "DNC"
              | "Pre"
            )[];
            /**
             * @maxItems 25
             */
            onboardingStatus?: ("Not Started" | "In Progress" | "Completed")[];
            activeOnboardingSession?: boolean;
            onboardingSent?: "true" | "false";
            section128Required?: "true" | "false";
            confirmed?: "true" | "false";
            optOut?: "true" | "false";
            optOut48Hours?: "true" | "false";
            fitToWorkOverride?: "true" | "false";
            affectAbility?: "true" | "false";
            ewcConfirmation?: "true" | "false";
            dbsHeld?: "true" | "false";
            dbsRegisteredToUpdateService?: "true" | "false";
            /**
             * @maxLength 500
             */
            firstName?: string;
            /**
             * @maxLength 500
             */
            lastName?: string;
            /**
             * @maxLength 500
             */
            email?: string;
            /**
             * @maxLength 500
             */
            mobile?: string;
            /**
             * Address fields used to filter candidates. Multiple supplied fields are combined.
             */
            address?: {
              /**
               * @maxLength 500
               */
              city?: string;
              /**
               * @maxLength 20
               */
              postcode?: string;
              /**
               * @maxLength 500
               */
              county?: string;
              /**
               * @maxLength 500
               */
              borough?: string;
              /**
               * @maxLength 500
               */
              addressLine1?: string;
              /**
               * @maxLength 500
               */
              addressLine2?: string;
              /**
               * @maxLength 500
               */
              addressLine3?: string;
            };
            /**
             * @maxItems 25
             */
            occupation?: string[];
            /**
             * @maxLength 500
             */
            specialism?: string;
            /**
             * @maxLength 500
             */
            _internalId?: string;
            /**
             * @maxItems 25
             */
            complianceUser?: string[];
            /**
             * @maxItems 25
             */
            assignee?: string[];
            /**
             * @maxItems 25
             */
            watcher?: string[];
            /**
             * @maxItems 25
             */
            team?: string[];
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            contactId?: string;
            currentBooking?: "true" | "false";
            bookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            notBookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            tags?: string[];
            location?: {
              lat?: number | null;
              lng?: number | null;
              radius?: number | null;
            };
            statusChangeDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            lastContactedDateFilter?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            employmentPreference?: (
              | "Day To Day"
              | "Long Term"
              | "Fixed Term"
              | "Perm"
            )[];
            /**
             * @maxItems 25
             */
            transport?: (
              | "Drives Own Vehicle"
              | "Use Public Transport"
              | "Access to Another Vehicle"
              | "Bicycle"
              | "No Access"
              | "Other"
            )[];
            /**
             * @maxLength 500
             */
            trnNumber?: string;
            /**
             * @maxLength 500
             */
            desiredLocation?: string;
            /**
             * @maxItems 25
             */
            subjectToNotice?: ("Unsure" | "Yes" | "No")[];
            /**
             * @maxItems 25
             */
            title?: (
              | "Mr"
              | "Mrs"
              | "Ms"
              | "Miss"
              | "Dr"
              | "Mx"
              | "Lady"
              | "Lord"
              | "Other"
            )[];
            /**
             * @maxItems 25
             */
            sex?: ("Male" | "Female" | "Other" | "Unknown")[];
            /**
             * @maxItems 25
             */
            pronoun?: (
              | "She/Her/Hers"
              | "He/Him/His"
              | "They/Them/Theirs"
              | "Ze/Hir/Hirs"
              | "Ze/Zir/Zirs"
              | "Other"
              | "Prefer not to say"
            )[];
            /**
             * @maxItems 25
             */
            roleType?: (
              | "Senior Leadership Positions"
              | "Teaching Roles"
              | "School Support Positions"
            )[];
            /**
             * @maxItems 25
             */
            payType?: (
              | "PAYE"
              | "Umbrella"
              | "Limited Company"
              | "Not Applicable"
            )[];
            /**
             * @maxItems 25
             */
            payInterval?: (
              | "Monthly"
              | "Weekly"
              | "Weekly Pay in Arrears"
              | "Bi-Weekly"
            )[];
            /**
             * @maxItems 25
             */
            dbsType?: (
              | "Basic"
              | "Standard"
              | "Enhanced"
              | "Enhanced with Barred List"
              | "Child workforce"
              | "Child and adult workforce"
              | "Adult workforce"
            )[];
            source?: (
              | (
                  | "Company Website"
                  | "iCan Teach UK Website"
                  | "Apply to Education"
                  | "CV Library - CV Search"
                  | "CV Library Advert"
                  | "CV Library - Watchdog"
                  | "Guardian- CV Search"
                  | "Guardian Advert"
                  | "Indeed - CV Search"
                  | "Indeed Advert"
                  | "Reed - CV Search"
                  | "Reed Advert"
                  | "Reed \u2013 Watchdog"
                  | "SENploy - Advert"
                  | "Total Jobs"
                  | "Other Job Board"
                  | "Google"
                  | "LinkedIn"
                  | "Linkedin Recruiter"
                  | "LinkedIn Advert"
                  | "Referral - School"
                  | "Referral - Candidate"
                  | "UK University"
                  | "Irish University"
                  | "Canadian University"
                  | "Marketing Campaign"
                  | "Woo"
                  | "Job Show"
                  | "Recruitment Fair"
                  | "Career Fair"
                  | "Volcanic"
                  | "Broadbean"
                  | "Headhunt"
                  | "Referral"
                  | "Direct"
                  | "Other"
                )
              | (
                  | "Company Website"
                  | "Direct Call"
                  | "CV Library Watchdog"
                  | "CV Library Advert"
                  | "CV Library CV Search"
                  | "Reed Watchdog"
                  | "Reed Advert"
                  | "Reed CV Search"
                  | "Indeed Watchdog"
                  | "Indeed Advert"
                  | "Indeed CV Search"
                  | "Job Centre"
                  | "Returning Candidate"
                  | "Referral Candidate"
                  | "Referral Client"
                  | "Training Provider"
                  | "Recruitment Fair"
                  | "University/College"
                )
            )[];
            /**
             * @maxLength 32
             */
            telephone?: string;
            /**
             * @maxLength 500
             */
            previousNames?: string;
            /**
             * @maxLength 500
             */
            otherNames?: string;
            dob?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            trnNumberCheckedDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            dbsLastChecked?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            dbsUpdateServiceCheckDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            rtwVerificationDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            section128CheckedDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            prohibitionCheckDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            createdAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            updatedAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            candidateMobileAppStatus?: (
              | "Unlinked"
              | "Scheduled Unlink"
              | "Linked"
              | "Invited"
              | "Not Invited"
            )[];
            recAuditConsent?: "true" | "false";
          };
        };
        presentation?: {
          mode?: "table" | "availability";
          availability?: {
            /**
             * An absolute or relative date range. Relative boundaries take precedence when supplied.
             */
            period?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 5
             */
            statuses?: (
              | "available"
              | "unavailable"
              | "booked"
              | "awaiting"
              | "notRecorded"
            )[];
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            draftClient?: string;
            draftMode?: "week" | "all";
          };
        };
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved client search.
         */
        type?: "clients";
        /**
         * The client query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * @maxLength 256
           */
          search?: string;
          sort?:
            | "name"
            | "-name"
            | "createdAt"
            | "-createdAt"
            | "assignee"
            | "-assignee"
            | "telephone"
            | "-telephone"
            | "email"
            | "-email"
            | "address.postcode"
            | "-address.postcode"
            | "address.county"
            | "-address.county"
            | "lea"
            | "-lea"
            | "type"
            | "-type"
            | "multiAcademyTrust"
            | "-multiAcademyTrust"
            | "address.city"
            | "-address.city"
            | "borough"
            | "-borough"
            | "specialism"
            | "-specialism"
            | "internalId"
            | "-internalId"
            | "lastContactedDate"
            | "-lastContactedDate"
            | "distance"
            | "-distance";
          select?: (
            | "id"
            | "name"
            | "address.addressLine1"
            | "address.addressLine2"
            | "address.addressLine3"
            | "address.city"
            | "address.county"
            | "address.borough"
            | "address.postcode"
            | "address.country"
            | "address.lat"
            | "address.lng"
            | "address"
            | "telephone"
            | "email"
            | "contacts"
            | "borough"
            | "lea"
            | "type"
            | "multiAcademyTrust"
            | "color"
            | "customId"
            | "internalId"
            | "assignee"
            | "assignee.id"
            | "assignee.email"
            | "assignee.firstName"
            | "assignee.lastName"
            | "assignee.username"
            | "assignee.lea"
            | "assignee.clients"
            | "assignee.position"
            | "assignee.permissions"
            | "assignee.activationCode"
            | "assignee.customer"
            | "assignee.disabled"
            | "assignee.commissionThreshold"
            | "assignee.branches"
            | "assignee.displayOnboarding"
            | "assignee._customId"
            | "assignee.signature"
            | "assignee.phone"
            | "assignee.defaultCandidateWatcher"
            | "assignee.defaultTaskWorkspace"
            | "assignee.teamAdmin"
            | "assignee.contact"
            | "assignee.contact.id"
            | "assignee.contact.forename"
            | "assignee.contact.surname"
            | "assignee.contact.email"
            | "assignee.contact.telephone"
            | "assignee.contact.mobile"
            | "assignee.contact.extension"
            | "assignee.createdAt"
            | "assignee.evertime.id"
            | "assignee.evertime"
            | "assignee.timesheetPortal.id"
            | "assignee.timesheetPortal.code"
            | "assignee.timesheetPortal"
            | "tags"
            | "tobSentAt"
            | "website"
            | "archived"
            | "specialism"
            | "currentBooking"
            | "bookingCount"
            | "importantNotes"
            | "roles"
            | "startTime"
            | "endTime"
            | "additionalInstructions"
            | "additionalInstructionsClient"
            | "highlight"
            | "area"
            | "governance"
            | "mathq"
            | "ofstedRating"
            | "dateOfstedReportChecked"
            | "boarding"
            | "licenceToIssueSponsorship"
            | "agencySpendTotal"
            | "vatExempt"
            | "splitInvoices"
            | "switchBoard"
            | "ccsClient"
            | "ccsClientUrn"
            | "ccsClientOfstedUrn"
            | "ccsClientLotNumber"
            | "buyerReference"
            | "ccsClientInvoiceDate"
            | "keyStage"
            | "contact"
            | "contact.id"
            | "contact.forename"
            | "contact.surname"
            | "contact.email"
            | "contact.telephone"
            | "contact.mobile"
            | "contact.extension"
            | "lastContactedDate"
            | "relevanceScore"
            | "matchReasons"
            | "distance"
            | "migrationId"
            | "migrationApproval"
            | "evertime.id"
            | "evertime.contacts"
            | "evertime.locations"
            | "evertime.nameMatch"
            | "evertime"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "timesheetPortal"
            | ""
          )[];
          /**
           * @maxItems 25
           */
          expand?: ("assignee" | "tags")[];
          include?: "bookings"[];
          filter?: {
            /**
             * @maxItems 50
             */
            ids?: string[];
            /**
             * Filter clients by name.
             *
             * @maxLength 256
             */
            name?: string;
            /**
             * Filter clients by telephone number.
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * Filter clients by email address.
             *
             * @maxLength 256
             */
            email?: string;
            /**
             * Address fields used to filter clients. Multiple supplied fields are combined.
             */
            address?: {
              /**
               * Filter clients by postcode.
               *
               * @maxLength 32
               */
              postcode?: string;
              /**
               * Filter clients by county.
               *
               * @maxLength 128
               */
              county?: string;
              /**
               * Filter clients by city.
               *
               * @maxLength 128
               */
              city?: string;
            };
            /**
             * Filter clients by local education authority.
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Filter clients by type.
             *
             * @maxLength 128
             */
            type?: string;
            /**
             * Filter clients by multi academy trust.
             *
             * @maxLength 256
             */
            multiAcademyTrust?: string;
            /**
             * Filter clients by borough.
             *
             * @maxLength 128
             */
            borough?: string;
            /**
             * Filter clients by specialism.
             *
             * @maxItems 25
             */
            specialism?: string[];
            /**
             * Filter clients by their company-defined identifier.
             *
             * @maxLength 256
             */
            internalId?: string;
            /**
             * Filter clients by whether they are a GCA client.
             */
            ccsClient?: "true" | "false";
            /**
             * Filter clients by GCA client URN.
             *
             * @maxLength 256
             */
            ccsClientUrn?: string;
            /**
             * Filter clients by assigned users.
             *
             * @maxItems 25
             */
            assignee?: string[];
            /**
             * Filter clients by last contact date.
             */
            lastContactedDateFilter?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients with an active booking overlapping an absolute or relative date range.
             */
            bookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients without an active booking overlapping an absolute or relative date range.
             */
            notBookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients by tags they have.
             *
             * @maxItems 25
             */
            tags?: string[];
            location?: {
              /**
               * @minimum -90
               * @maximum 90
               */
              lat?: number | null;
              /**
               * @minimum -180
               * @maximum 180
               */
              lng?: number | null;
              /**
               * @minimum 0
               * @maximum 500
               */
              radius?: number | null;
            };
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            ["contacts.contactId"]?: string;
          };
        };
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved Client Contact search.
         */
        type?: "clientContacts";
        /**
         * The Client Contact query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * Find relevant Client contacts using contact and Client details.
           *
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * Search Client contacts by name, email, or Client.
           *
           * @maxLength 256
           */
          search?: string;
          /**
           * The field used to order contacts. Prefix with - for descending order.
           */
          sort?:
            | "forename"
            | "-forename"
            | "surname"
            | "-surname"
            | "email"
            | "-email"
            | "telephone"
            | "-telephone"
            | "role"
            | "-role"
            | "status"
            | "-status"
            | "decisionMaker"
            | "-decisionMaker"
            | "clientName"
            | "-clientName"
            | "clientAddress.city"
            | "-clientAddress.city"
            | "clientAddress.county"
            | "-clientAddress.county"
            | "clientAddress.postcode"
            | "-clientAddress.postcode"
            | "clientLea"
            | "-clientLea"
            | "clientType"
            | "-clientType"
            | "clientMultiAcademyTrust"
            | "-clientMultiAcademyTrust"
            | "clientBorough"
            | "-clientBorough"
            | "clientAssignee"
            | "-clientAssignee"
            | "clientSpecialism"
            | "-clientSpecialism"
            | "clientUpdatedAt"
            | "-clientUpdatedAt"
            | "distance"
            | "-distance"
            | "relevanceScore"
            | "-relevanceScore";
          /**
           * The fields to include in the response in dot notation format.
           *
           * @maxItems 250
           */
          select?: (
            | "id"
            | "forename"
            | "surname"
            | "email"
            | "telephone"
            | "mobile"
            | "extension"
            | "role"
            | "roleOther"
            | "contactId"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "timesheetPortal"
            | "optOut"
            | "note"
            | "decisionMaker"
            | "status"
            | "clientId"
            | "clientName"
            | "clientAddress.addressLine1"
            | "clientAddress.addressLine2"
            | "clientAddress.addressLine3"
            | "clientAddress.city"
            | "clientAddress.county"
            | "clientAddress.borough"
            | "clientAddress.postcode"
            | "clientAddress.country"
            | "clientAddress.lat"
            | "clientAddress.lng"
            | "clientAddress"
            | "clientLea"
            | "clientType"
            | "clientMultiAcademyTrust"
            | "clientBorough"
            | "clientAssignee"
            | "clientAssignee.id"
            | "clientAssignee.email"
            | "clientAssignee.firstName"
            | "clientAssignee.lastName"
            | "clientAssignee.username"
            | "clientAssignee.lea"
            | "clientAssignee.clients"
            | "clientAssignee.position"
            | "clientAssignee.permissions"
            | "clientAssignee.activationCode"
            | "clientAssignee.customer"
            | "clientAssignee.disabled"
            | "clientAssignee.commissionThreshold"
            | "clientAssignee.branches"
            | "clientAssignee.displayOnboarding"
            | "clientAssignee._customId"
            | "clientAssignee.signature"
            | "clientAssignee.phone"
            | "clientAssignee.defaultCandidateWatcher"
            | "clientAssignee.defaultTaskWorkspace"
            | "clientAssignee.teamAdmin"
            | "clientAssignee.contact"
            | "clientAssignee.contact.id"
            | "clientAssignee.contact.forename"
            | "clientAssignee.contact.surname"
            | "clientAssignee.contact.email"
            | "clientAssignee.contact.telephone"
            | "clientAssignee.contact.mobile"
            | "clientAssignee.contact.extension"
            | "clientAssignee.createdAt"
            | "clientAssignee.evertime.id"
            | "clientAssignee.evertime"
            | "clientAssignee.timesheetPortal.id"
            | "clientAssignee.timesheetPortal.code"
            | "clientAssignee.timesheetPortal"
            | "clientSpecialism"
            | "clientUpdatedAt"
            | "distance"
            | "relevanceScore"
            | "matchReasons"
            | ""
          )[];
          /**
           * Related fields to include as objects in the response.
           *
           * @maxItems 1
           */
          expand?: "clientAssignee"[];
          /**
           * Filter Client contacts using contact and associated Client fields.
           */
          filter?: {
            /**
             * Filter contacts by status.
             *
             * @maxItems 3
             */
            status?: ("Active" | "Passive" | "Inactive")[];
            /**
             * Filter contacts by role.
             *
             * @maxItems 44
             */
            role?: (
              | "Head Teacher"
              | "Deputy Head"
              | "Cover Manager"
              | "HR Manager"
              | "Finance Manager"
              | "Head of Maths"
              | "Head of Science"
              | "Head of English"
              | "Assistant Headteacher"
              | "Headteacher's PA"
              | "SENCO"
              | "Other"
              | "Business Manager"
              | "Business Administrator"
              | "Invoice Contact"
              | "Academy Recruitment Contact"
              | "Nursery Manager"
              | "CEO"
              | "COO"
              | "Executive Headteacher"
              | "Inclusion Manager"
              | "EYFS Lead"
              | "Admin Team"
              | "Head of Humanities"
              | "Head of History"
              | "Head of Geography"
              | "Head of Business Studies"
              | "Head of PE"
              | "Head of KS1"
              | "Head of KS2"
              | "Head of KS3"
              | "Head of KS4"
              | "Virtual School Headteacher"
              | "Deputy Virtual School Headteacher"
              | "Post-16 Team Leader"
              | "SEN Team"
              | "Inclusion Team"
              | "UASC Team"
              | "Caseworker"
              | "Designated Teacher"
              | "Attendance Officer"
              | "Coordinator"
              | "Children's Commissioner"
              | "Timesheet Contact"
            )[];
            /**
             * Filter contacts by forename.
             *
             * @maxLength 128
             */
            forename?: string;
            /**
             * Filter contacts by surname.
             *
             * @maxLength 128
             */
            surname?: string;
            /**
             * Filter contacts by email address.
             *
             * @maxLength 256
             */
            email?: string;
            /**
             * Filter contacts by decision-maker status.
             */
            decisionMaker?: "true" | "false";
            /**
             * Filter contacts by opt-out status.
             */
            optOut?: "true" | "false";
            /**
             * Filter contacts by Client.
             *
             * @maxItems 50
             */
            clientId?: string[];
            /**
             * Filter contacts by Client name.
             *
             * @maxLength 256
             */
            clientName?: string;
            /**
             * Filter contacts by associated Client address fields.
             */
            clientAddress?: {
              /**
               * @maxLength 128
               */
              city?: string;
              /**
               * @maxLength 128
               */
              county?: string;
              /**
               * @maxLength 32
               */
              postcode?: string;
            };
            /**
             * Filter contacts by Client local education authority.
             *
             * @maxLength 128
             */
            clientLea?: string;
            /**
             * Filter contacts by Client sector.
             *
             * @maxLength 128
             */
            clientType?: string;
            /**
             * Filter contacts by Client multi-academy trust.
             *
             * @maxLength 256
             */
            clientMultiAcademyTrust?: string;
            /**
             * Filter contacts by Client borough.
             *
             * @maxLength 128
             */
            clientBorough?: string;
            /**
             * Filter contacts by Client assignee.
             *
             * @maxItems 25
             */
            clientAssignee?: string[];
            /**
             * Filter contacts by Client specialism.
             *
             * @maxItems 25
             */
            clientSpecialism?: string[];
            /**
             * Filter contacts by when their Client was updated.
             */
            clientUpdatedAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter contacts by distance from their Client address.
             */
            location?: {
              /**
               * @minimum -90
               * @maximum 90
               */
              lat?: number | null;
              /**
               * @minimum -180
               * @maximum 180
               */
              lng?: number | null;
              /**
               * @minimum 0
               * @exclusiveMinimum true
               * @maximum 500
               */
              radius?: number;
            };
          };
        };
      };
};

export type CreateSavedSearchRequestBody =
  | {
      /**
       * Whether the saved search is available to everyone in its company.
       */
      shared: boolean;
      /**
       * A saved candidate search.
       */
      type: "candidates";
      /**
       * The name shown when choosing the saved search.
       *
       * @minLength 1
       * @maxLength 128
       */
      name: string;
      /**
       * A short explanation of what the saved search finds.
       *
       * @maxLength 512
       */
      description?: string;
      /**
       * The candidate query applied by this saved search, including filters, sorting, selected fields, and expansions.
       */
      query: {
        /**
         * Find relevant candidates using candidate details and attached document analysis.
         *
         * @minLength 1
         * @maxLength 256
         */
        q?: string;
        /**
         * Search candidates by name (forename or surname)
         *
         * @maxLength 256
         */
        search?: string;
        /**
         * The field to sort by. Prefix with '-' for descending. Availability priority requires an availability period and ranks free time, counting a full available day as one and a half-day as half.
         */
        sort?:
          | (
              | "relevanceScore"
              | "-relevanceScore"
              | "forename"
              | "-forename"
              | "surname"
              | "-surname"
              | "mobile"
              | "-mobile"
              | "email"
              | "-email"
              | "address.city"
              | "-address.city"
              | "address.postcode"
              | "-address.postcode"
              | "address.county"
              | "-address.county"
              | "address.borough"
              | "-address.borough"
              | "address.addressLine1"
              | "-address.addressLine1"
              | "address.addressLine2"
              | "-address.addressLine2"
              | "address.addressLine3"
              | "-address.addressLine3"
              | "subject"
              | "-subject"
              | "statusChangeDate"
              | "-statusChangeDate"
              | "lastContactedDate"
              | "-lastContactedDate"
              | "daysSinceLastContactDisplay"
              | "-daysSinceLastContactDisplay"
              | "status"
              | "-status"
              | "occupation"
              | "-occupation"
              | "dob"
              | "-dob"
              | "trnNumberCheckedDate"
              | "-trnNumberCheckedDate"
              | "criminalRecord.dbsLastChecked"
              | "-criminalRecord.dbsLastChecked"
              | "criminalRecord.updateServiceCheckDate"
              | "-criminalRecord.updateServiceCheckDate"
              | "rtwVerification"
              | "-rtwVerification"
              | "criminalRecord.section128Checked"
              | "-criminalRecord.section128Checked"
              | "criminalRecord.prohibitionCheck"
              | "-criminalRecord.prohibitionCheck"
              | "createdAt"
              | "-createdAt"
              | "updatedAt"
              | "-updatedAt"
              | "_internalId"
              | "-_internalId"
              | "trnNumber"
              | "-trnNumber"
              | "desiredLocation"
              | "-desiredLocation"
              | "telephone"
              | "-telephone"
              | "previousNames"
              | "-previousNames"
              | "otherNames"
              | "-otherNames"
              | "location"
              | "-location"
            )
          | ("availabilityPriority" | "-availabilityPriority");
        /**
         * The fields to include in the response in dot notation format
         *
         * @maxItems 250
         */
        select?: (
          | "id"
          | "forename"
          | "surname"
          | "previousNames"
          | "email"
          | "telephone"
          | "mobile"
          | "occupation"
          | "subject"
          | "address.addressLine1"
          | "address.addressLine2"
          | "address.addressLine3"
          | "address.city"
          | "address.county"
          | "address.borough"
          | "address.postcode"
          | "address.country"
          | "address.lat"
          | "address.lng"
          | "qualifications"
          | "employment"
          | "references"
          | "referenceCheckConsent"
          | "referenceCheckConsentName"
          | "referenceCheckConsentDate"
          | "dob"
          | "sex"
          | "status"
          | "onboardingStatus"
          | "onboardingSent"
          | "nextOfKin.nextOfKinName"
          | "nextOfKin.nextOfKinRelationship"
          | "nextOfKin.nextOfKinTelephone"
          | "nextOfKin.nextOfKinEmail"
          | "nextOfKin.nextOfKinAddress.addressLine1"
          | "nextOfKin.nextOfKinAddress.addressLine2"
          | "nextOfKin.nextOfKinAddress.addressLine3"
          | "nextOfKin.nextOfKinAddress.city"
          | "nextOfKin.nextOfKinAddress.county"
          | "nextOfKin.nextOfKinAddress.borough"
          | "nextOfKin.nextOfKinAddress.postcode"
          | "nextOfKin.nextOfKinAddress.country"
          | "nextOfKin.nextOfKinAddress.lat"
          | "nextOfKin.nextOfKinAddress.lng"
          | "criminalRecord.dbsHeld"
          | "criminalRecord.dbsNumber"
          | "criminalRecord.dbsType"
          | "criminalRecord.dbsLastChecked"
          | "criminalRecord.dbsUpdateServiceNumber"
          | "criminalRecord.prohibitionCheck"
          | "criminalRecord.overseasCheck"
          | "criminalRecord.overseasCheckNote"
          | "criminalRecord.convictedOffence"
          | "criminalRecord.convictedOffenceDescription"
          | "criminalRecord.convictions"
          | "criminalRecord.convictionsDescription"
          | "criminalRecord.registeredToUpdateService"
          | "criminalRecord.consentUpdateServiceChecks"
          | "criminalRecord.note"
          | "criminalRecord.updateServiceCheckDate"
          | "criminalRecord.abroad"
          | "criminalRecord.overseasDeclarationExtended"
          | "criminalRecord.educationalDisciplinaryProceedings"
          | "criminalRecord.dbsComments"
          | "criminalRecord.dbsDeclarationRead"
          | "criminalRecord.overseasOffence"
          | "criminalRecord.overseasOffenceDescription"
          | "criminalRecord.currentCriminalOffence"
          | "criminalRecord.currentCriminalOffenceDescription"
          | "criminalRecord.dbsIssuedByBusiness"
          | "criminalRecord.dbsIssuedByCurrentEmployer"
          | "criminalRecord.section128Required"
          | "criminalRecord.section128Checked"
          | "criminalRecordDeclaration.questions"
          | "criminalRecordDeclaration.answers"
          | "criminalRecordDeclaration.submittedAt"
          | "criminalRecordDeclaration.submittedIp"
          | "criminalRecordDeclaration.configurationUpdatedAt"
          | "criminalRecordCheckConsent"
          | "criminalRecordCheckConsentName"
          | "criminalRecordCheckConsentDate"
          | "safeInEducation.barred"
          | "safeInEducation.barredCheckDate"
          | "safeInEducation.waiver"
          | "safeInEducation.ordersYou"
          | "safeInEducation.ordersChild"
          | "safeInEducation.previousSafeguardingAllegations"
          | "safeInEducation.cautioned"
          | "safeInEducation.violentOffence"
          | "safeInEducation.anotherCountry"
          | "safeInEducation.readKCSIE"
          | "safeInEducation.readChildcareAct"
          | "safeInEducation.prohibitedFromTeaching"
          | "safeInEducation.knownPolice"
          | "safeInEducation.prohibitedFromManagement"
          | "safeInEducation.criminalOffencesLaw"
          | "safeInEducation.safeInEducationDetails"
          | "safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
          | "safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
          | "safeInEducation.safeguardingQuestionaire.signOfAbuse"
          | "safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
          | "safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
          | "safeInEducation.safeguardingQuestionaire.fgmDefinition"
          | "safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
          | "safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
          | "safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
          | "safeInEducation.safeguardingQuestionaire.studentConfides"
          | "socialMedia.facebook"
          | "socialMedia.instagram"
          | "socialMedia.twitter"
          | "socialMedia.linkedIn"
          | "socialMedia.tiktok"
          | "socialMedia.others"
          | "socialMedia.socialMediaChecked"
          | "socialMedia.googleCheckedDate"
          | "safeInEducationCheckConsent"
          | "safeInEducationCheckConsentName"
          | "safeInEducationCheckConsentDate"
          | "healthRecord.physicalAbilityCondition"
          | "healthRecord.confused"
          | "healthRecord.hearing"
          | "healthRecord.eyesight"
          | "healthRecord.depression"
          | "healthRecord.severePain"
          | "healthRecord.excessiveDrowsiness"
          | "healthRecord.epilepsy"
          | "healthRecord.heart"
          | "healthRecord.asthma"
          | "healthRecord.mentalHealth"
          | "healthRecord.expectantMother"
          | "healthRecord.allergies"
          | "healthRecord.medication"
          | "healthRecord.healthIssues"
          | "healthRecord.healthIssuesDescription"
          | "healthRecord.disabilityNeeds"
          | "healthRecord.contagiousDiseases"
          | "healthRecord.affectAbility"
          | "healthRecord.notificationAgree"
          | "healthRecord.sasOptIn"
          | "healthRecord.signature"
          | "healthRecord.drawnSignature"
          | "healthRecord.signatureDate"
          | "healthRecord.vaccinationMeasles"
          | "healthRecord.vaccinationMumps"
          | "healthRecord.vaccinationRubella"
          | "healthRecord.vaccinationVaricella"
          | "healthRecord.vaccinationTuberculosis"
          | "healthRecord.vaccinationHepatitisB"
          | "healthRecord.vaccinationHepatitisC"
          | "healthRecord.vaccinationHIV"
          | "healthRecord.vaccinationCovid"
          | "healthRecord.vaccinationCovid1"
          | "healthRecord.vaccinationCovid2"
          | "healthRecord.vaccinationCovidBooster"
          | "healthRecord.vaccinationCovidInfluenza"
          | "healthRecord.fitToWorkOverride"
          | "healthDeclaration.questions"
          | "healthDeclaration.answers"
          | "healthDeclaration.submittedAt"
          | "healthDeclaration.submittedIp"
          | "healthDeclaration.configurationUpdatedAt"
          | "healthRecordCheckConsent"
          | "healthRecordCheckConsentName"
          | "healthRecordCheckConsentDate"
          | "bankDetails.bankAccountName"
          | "bankDetails.bankName"
          | "bankDetails.accountNumber"
          | "bankDetails.sortCode"
          | "bankDetails.niNumber"
          | "bankDetails.jobOp1"
          | "bankDetails.jobOp2"
          | "bankDetails.jobOp3"
          | "bankDetails.studentLoan.studentLoan"
          | "bankDetails.studentLoan.plan1"
          | "bankDetails.studentLoan.plan2"
          | "bankDetails.studentLoan.plan4"
          | "bankDetails.studentLoan.postGradLoan"
          | "declarationCheckConsent"
          | "declarationCheckConsentName"
          | "declarationCheckConsentDate"
          | "contractForServiceConsent"
          | "contractForServiceName"
          | "contractForServiceDate"
          | "confirmed"
          | "createdBy"
          | "customerId"
          | "migrationId"
          | "migrationApproval"
          | "assignee"
          | "assignee.id"
          | "assignee.email"
          | "assignee.firstName"
          | "assignee.lastName"
          | "assignee.username"
          | "assignee.lea"
          | "assignee.clients"
          | "assignee.position"
          | "assignee.permissions"
          | "assignee.activationCode"
          | "assignee.customer"
          | "assignee.disabled"
          | "assignee.commissionThreshold"
          | "assignee.branches"
          | "assignee.displayOnboarding"
          | "assignee._customId"
          | "assignee.signature"
          | "assignee.phone"
          | "assignee.defaultCandidateWatcher"
          | "assignee.defaultTaskWorkspace"
          | "assignee.teamAdmin"
          | "assignee.contact"
          | "assignee.contact.id"
          | "assignee.contact.forename"
          | "assignee.contact.surname"
          | "assignee.contact.email"
          | "assignee.contact.telephone"
          | "assignee.contact.mobile"
          | "assignee.contact.extension"
          | "assignee.createdAt"
          | "assignee.evertime.id"
          | "assignee.timesheetPortal.id"
          | "assignee.timesheetPortal.code"
          | "currentBooking"
          | "profilePhoto"
          | "payType"
          | "payInterval"
          | "payRate"
          | "customId"
          | "_internalId"
          | "statusChangeDate"
          | "optOut"
          | "trnNumber"
          | "trnNumberCheckedDate"
          | "trnNumberNotes"
          | "employmentStartDate"
          | "ewcConfirmation"
          | "homeOfficeShareCode"
          | "archived"
          | "umbrella"
          | "payeUmbrella"
          | "umbrellaCompanyName"
          | "umbrellaCompanyCode"
          | "transport"
          | "importantNotes"
          | "candidateLastUpdated"
          | "source"
          | "sourceOther"
          | "onlineCheckConsent"
          | "title"
          | "pronoun"
          | "updatedAt"
          | "lastContactedDate"
          | "highlight"
          | "teacherPayScale"
          | "p45"
          | "roleType"
          | "rtwVerification"
          | "rtwVerificationNote"
          | "rtwVerificationStatus"
          | "desiredLocation"
          | "complianceUser"
          | "complianceUser.id"
          | "complianceUser.email"
          | "complianceUser.firstName"
          | "complianceUser.lastName"
          | "complianceUser.username"
          | "complianceUser.lea"
          | "complianceUser.clients"
          | "complianceUser.position"
          | "complianceUser.permissions"
          | "complianceUser.activationCode"
          | "complianceUser.customer"
          | "complianceUser.disabled"
          | "complianceUser.commissionThreshold"
          | "complianceUser.branches"
          | "complianceUser.displayOnboarding"
          | "complianceUser._customId"
          | "complianceUser.signature"
          | "complianceUser.phone"
          | "complianceUser.defaultCandidateWatcher"
          | "complianceUser.defaultTaskWorkspace"
          | "complianceUser.teamAdmin"
          | "complianceUser.contact"
          | "complianceUser.contact.id"
          | "complianceUser.contact.forename"
          | "complianceUser.contact.surname"
          | "complianceUser.contact.email"
          | "complianceUser.contact.telephone"
          | "complianceUser.contact.mobile"
          | "complianceUser.contact.extension"
          | "complianceUser.createdAt"
          | "complianceUser.evertime.id"
          | "complianceUser.timesheetPortal.id"
          | "complianceUser.timesheetPortal.code"
          | "watchers"
          | "tags"
          | "documents"
          | "documentsCount"
          | "desiredLocationMultiple"
          | "availabilityDays"
          | "availabilityLastChecked"
          | "subjectToNotice"
          | "subsidiarySubject"
          | "employmentPreference"
          | "otherNames"
          | "ltdCompany"
          | "employmentVerified"
          | "candidateDBSVerification"
          | "candidateDBSVerificationNote"
          | "candidateDBSVerificationStatus"
          | "candidateDBSCheckVerification"
          | "candidateDBSCheckVerificationNote"
          | "candidateDBSCheckVerificationStatus"
          | "candidateDBSUpdateServiceVerificationNote"
          | "candidateDBSUpdateServiceVerificationStatus"
          | "candidateTRNVerificationNote"
          | "candidateTRNVerificationStatus"
          | "candidateHealthSafetyVerification"
          | "candidateHealthSafetyVerificationNote"
          | "candidateHealthSafetyVerificationStatus"
          | "onboardingSession"
          | "onboardingSession.id"
          | "onboardingSession.url"
          | "onboardingSession.healthDeclarationConfig.questions"
          | "onboardingSession.healthDeclarationConfig.updatedAt"
          | "onboardingSession.criminalRecordConfig.questions"
          | "onboardingSession.criminalRecordConfig.updatedAt"
          | "contact"
          | "contact.id"
          | "contact.forename"
          | "contact.surname"
          | "contact.email"
          | "contact.telephone"
          | "contact.mobile"
          | "contact.extension"
          | "optOut48Hours"
          | "recAuditConsent"
          | "recAuditConsentDate"
          | "recAuditConsentMethod"
          | "createdAt"
          | "candidateMobileAppStatus"
          | "evertime.id"
          | "evertime.niNumberMatch"
          | "relevanceScore"
          | "matchReasons"
          | "distance"
          | "timesheetPortal.id"
          | "timesheetPortal.code"
          | "mobileAppLinkInvite.isUsed"
          | "mobileAppLinkInvite.createdAt"
          | "mobileAppLinkInvite.usedBy"
          | "mobileAppLinkInvite.usedAt"
          | "mobileAppLinkInvite.unlinkedBy"
          | "mobileAppLinkInvite.unlinkedAt"
          | "mobileAppLinkInvite.deletedBy"
          | "mobileAppLinkInvite.deletedAt"
          | "mobileAppLinkInvite.status"
        )[];
        /**
         * Related resources to return as objects instead of IDs. Supported values are documented for this endpoint.
         *
         * @maxItems 100
         */
        expand?: (
          | "assignee"
          | "contact"
          | "customer"
          | "umbrella"
          | "payeUmbrella"
          | "complianceUser"
          | "onboardingSession"
          | "watchers"
          | "tags"
          | "documents"
        )[];
        filter?: {
          /**
           * Inclusive calendar dates, up to 31 days. Both boundaries are required when selecting availability days, filtering availability statuses or sorting by availability priority.
           */
          availabilityPeriod?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Match candidates with any selected status during the availability period. Requires an availability period. Empty selects all statuses.
           *
           * @maxItems 5
           */
          availabilityStatuses?: (
            | "available"
            | "unavailable"
            | "booked"
            | "awaiting"
            | "notRecorded"
          )[];
          /**
           * Filter candidates by their IDs. Maximum 50.
           *
           * @maxItems 50
           */
          ids?: string[];
          /**
           * Filter candidates by their status
           *
           * @maxItems 25
           */
          status?: (
            | "Pending"
            | "Approved"
            | "Rejected"
            | "Anonymised"
            | "Archived"
            | "Enquired"
            | "Hold"
            | "Future"
            | "DNC"
            | "Pre"
          )[];
          /**
           * Filter candidates by their onboarding status
           *
           * @maxItems 25
           */
          onboardingStatus?: ("Not Started" | "In Progress" | "Completed")[];
          /**
           * Filter candidates by if they have an active onboarding session
           */
          activeOnboardingSession?: boolean;
          /**
           * Filter candidates by if they have been sent onboarding materials
           */
          onboardingSent?: "true" | "false";
          /**
           * Filter candidates by if they require section 128
           */
          section128Required?: "true" | "false";
          /**
           * Filter candidates by confirmed status
           */
          confirmed?: "true" | "false";
          /**
           * Filter candidates by opt out status
           */
          optOut?: "true" | "false";
          /**
           * Filter candidates by opt out 48 hours status
           */
          optOut48Hours?: "true" | "false";
          /**
           * Filter candidates by fit to work override status
           */
          fitToWorkOverride?: "true" | "false";
          /**
           * Filter candidates by whether they have health conditions
           */
          affectAbility?: "true" | "false";
          /**
           * Filter candidates by EWC confirmation status
           */
          ewcConfirmation?: "true" | "false";
          /**
           * Filter candidates by whether they hold a DBS certificate
           */
          dbsHeld?: "true" | "false";
          /**
           * Filter candidates by DBS update service registration
           */
          dbsRegisteredToUpdateService?: "true" | "false";
          /**
           * Filter candidates by their first name
           *
           * @maxLength 500
           */
          firstName?: string;
          /**
           * Filter candidates by their last name
           *
           * @maxLength 500
           */
          lastName?: string;
          /**
           * Filter candidates by their email address
           *
           * @maxLength 500
           */
          email?: string;
          /**
           * Filter candidates by their mobile number
           *
           * @maxLength 500
           */
          mobile?: string;
          /**
           * Address fields used to filter candidates. Multiple supplied fields are combined.
           */
          address?: {
            /**
             * Filter candidates by their city
             *
             * @maxLength 500
             */
            city?: string;
            /**
             * Filter candidates by their postcode
             *
             * @maxLength 20
             */
            postcode?: string;
            /**
             * Filter candidates by their county
             *
             * @maxLength 500
             */
            county?: string;
            /**
             * Filter candidates by their borough
             *
             * @maxLength 500
             */
            borough?: string;
            /**
             * Filter candidates by address line 1
             *
             * @maxLength 500
             */
            addressLine1?: string;
            /**
             * Filter candidates by address line 2
             *
             * @maxLength 500
             */
            addressLine2?: string;
            /**
             * Filter candidates by address line 3
             *
             * @maxLength 500
             */
            addressLine3?: string;
          };
          /**
           * Filter candidates by their occupation
           *
           * @maxItems 25
           */
          occupation?: string[];
          /**
           * Filter candidates by their specialism
           *
           * @maxLength 500
           */
          specialism?: string;
          /**
           * Filter candidates by their company-defined secondary identifier.
           *
           * @maxLength 500
           */
          _internalId?: string;
          /**
           * Filter candidates by their compliance user
           *
           * @maxItems 25
           */
          complianceUser?: string[];
          /**
           * Filter candidates by their assignee
           *
           * @maxItems 25
           */
          assignee?: string[];
          /**
           * Filter candidates by their watcher
           *
           * @maxItems 25
           */
          watcher?: string[];
          /**
           * Filter candidates by their team
           *
           * @maxItems 25
           */
          team?: string[];
          /**
           * Filter candidates by their associated contact ID
           *
           * @pattern ^[0-9a-f]{24}$
           */
          contactId?: string;
          /**
           * Filter candidates by whether they have an active booking
           */
          currentBooking?: "true" | "false";
          /**
           * Filter candidates with an active booking overlapping an absolute or relative date range.
           */
          bookedInRange?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates without an active booking overlapping an absolute or relative date range. Combine with `bookedInRange` to find candidates booked in one period but not another.
           */
          notBookedInRange?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by their tags
           *
           * @maxItems 25
           */
          tags?: string[];
          /**
           * Filter candidates by location from coordinates
           */
          location?: {
            lat?: number | null;
            lng?: number | null;
            radius?: number | null;
          };
          /**
           * Filter candidates by last status changed
           */
          statusChangeDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by last contact
           */
          lastContactedDateFilter?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by employment preference
           *
           * @maxItems 25
           */
          employmentPreference?: (
            | "Day To Day"
            | "Long Term"
            | "Fixed Term"
            | "Perm"
          )[];
          /**
           * Filter candidates by their chosen mode of transport
           *
           * @maxItems 25
           */
          transport?: (
            | "Drives Own Vehicle"
            | "Use Public Transport"
            | "Access to Another Vehicle"
            | "Bicycle"
            | "No Access"
            | "Other"
          )[];
          /**
           * Filter candidates by their TRN number
           *
           * @maxLength 500
           */
          trnNumber?: string;
          /**
           * Filter candidates by their desired location
           *
           * @maxLength 500
           */
          desiredLocation?: string;
          /**
           * Filter candidates by whether they are subject to a notice period
           *
           * @maxItems 25
           */
          subjectToNotice?: ("Unsure" | "Yes" | "No")[];
          /**
           * Filter candidates by title
           *
           * @maxItems 25
           */
          title?: (
            | "Mr"
            | "Mrs"
            | "Ms"
            | "Miss"
            | "Dr"
            | "Mx"
            | "Lady"
            | "Lord"
            | "Other"
          )[];
          /**
           * Filter candidates by sex
           *
           * @maxItems 25
           */
          sex?: ("Male" | "Female" | "Other" | "Unknown")[];
          /**
           * Filter candidates by pronoun
           *
           * @maxItems 25
           */
          pronoun?: (
            | "She/Her/Hers"
            | "He/Him/His"
            | "They/Them/Theirs"
            | "Ze/Hir/Hirs"
            | "Ze/Zir/Zirs"
            | "Other"
            | "Prefer not to say"
          )[];
          /**
           * Filter candidates by role type
           *
           * @maxItems 25
           */
          roleType?: (
            | "Senior Leadership Positions"
            | "Teaching Roles"
            | "School Support Positions"
          )[];
          /**
           * Filter candidates by pay type
           *
           * @maxItems 25
           */
          payType?: (
            | "PAYE"
            | "Umbrella"
            | "Limited Company"
            | "Not Applicable"
          )[];
          /**
           * Filter candidates by payment interval
           *
           * @maxItems 25
           */
          payInterval?: (
            | "Monthly"
            | "Weekly"
            | "Weekly Pay in Arrears"
            | "Bi-Weekly"
          )[];
          /**
           * Filter candidates by DBS check type
           *
           * @maxItems 25
           */
          dbsType?: (
            | "Basic"
            | "Standard"
            | "Enhanced"
            | "Enhanced with Barred List"
            | "Child workforce"
            | "Child and adult workforce"
            | "Adult workforce"
          )[];
          /**
           * Filter candidates by source
           */
          source?: (
            | (
                | "Company Website"
                | "iCan Teach UK Website"
                | "Apply to Education"
                | "CV Library - CV Search"
                | "CV Library Advert"
                | "CV Library - Watchdog"
                | "Guardian- CV Search"
                | "Guardian Advert"
                | "Indeed - CV Search"
                | "Indeed Advert"
                | "Reed - CV Search"
                | "Reed Advert"
                | "Reed \u2013 Watchdog"
                | "SENploy - Advert"
                | "Total Jobs"
                | "Other Job Board"
                | "Google"
                | "LinkedIn"
                | "Linkedin Recruiter"
                | "LinkedIn Advert"
                | "Referral - School"
                | "Referral - Candidate"
                | "UK University"
                | "Irish University"
                | "Canadian University"
                | "Marketing Campaign"
                | "Woo"
                | "Job Show"
                | "Recruitment Fair"
                | "Career Fair"
                | "Volcanic"
                | "Broadbean"
                | "Headhunt"
                | "Referral"
                | "Direct"
                | "Other"
              )
            | (
                | "Company Website"
                | "Direct Call"
                | "CV Library Watchdog"
                | "CV Library Advert"
                | "CV Library CV Search"
                | "Reed Watchdog"
                | "Reed Advert"
                | "Reed CV Search"
                | "Indeed Watchdog"
                | "Indeed Advert"
                | "Indeed CV Search"
                | "Job Centre"
                | "Returning Candidate"
                | "Referral Candidate"
                | "Referral Client"
                | "Training Provider"
                | "Recruitment Fair"
                | "University/College"
              )
          )[];
          /**
           * Filter candidates by their telephone number
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * Filter candidates by their previous names
           *
           * @maxLength 500
           */
          previousNames?: string;
          /**
           * Filter candidates by their other names
           *
           * @maxLength 500
           */
          otherNames?: string;
          /**
           * Filter candidates by date of birth
           */
          dob?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by TRN number checked date
           */
          trnNumberCheckedDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by DBS last checked date
           */
          dbsLastChecked?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by DBS update service check date
           */
          dbsUpdateServiceCheckDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by RTW verification date
           */
          rtwVerificationDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by Section 128 checked date
           */
          section128CheckedDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by prohibition check date
           */
          prohibitionCheckDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by created date
           */
          createdAt?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by last updated date
           */
          updatedAt?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by mobile app status
           *
           * @maxItems 25
           */
          candidateMobileAppStatus?: (
            | "Unlinked"
            | "Scheduled Unlink"
            | "Linked"
            | "Invited"
            | "Not Invited"
          )[];
          /**
           * Filter candidates by REC audit consent status
           */
          recAuditConsent?: "true" | "false";
        };
      };
      /**
       * The included table column identifiers in display order.
       *
       * @minItems 1
       * @maxItems 250
       */
      columns: string[];
      /**
       * Optional availability-view settings. Older saved searches use the standard candidate table.
       */
      presentation?: {
        mode: "table" | "availability";
        availability?: {
          /**
           * An absolute or relative date range. Relative boundaries take precedence when supplied.
           */
          period: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * @maxItems 5
           */
          statuses: (
            | "available"
            | "unavailable"
            | "booked"
            | "awaiting"
            | "notRecorded"
          )[];
          /**
           * The unique identifier of the resource
           *
           * @pattern ^[0-9a-f]{24}$
           */
          draftClient?: string;
          draftMode: "week" | "all";
        };
      };
    }
  | {
      /**
       * Whether the saved search is available to everyone in its company.
       */
      shared: boolean;
      /**
       * A saved client search.
       */
      type: "clients";
      /**
       * The name shown when choosing the saved search.
       *
       * @minLength 1
       * @maxLength 128
       */
      name: string;
      /**
       * A short explanation of what the saved search finds.
       *
       * @maxLength 512
       */
      description?: string;
      /**
       * The client query applied by this saved search, including filters, sorting, selected fields, and expansions.
       */
      query: {
        /**
         * Find relevant clients using their details and location.
         *
         * @minLength 1
         * @maxLength 256
         */
        q?: string;
        /**
         * Search clients by name
         *
         * @maxLength 256
         */
        search?: string;
        /**
         * The field to sort results by. Prefix with - for descending order
         */
        sort?:
          | "name"
          | "-name"
          | "createdAt"
          | "-createdAt"
          | "assignee"
          | "-assignee"
          | "telephone"
          | "-telephone"
          | "email"
          | "-email"
          | "address.postcode"
          | "-address.postcode"
          | "address.county"
          | "-address.county"
          | "lea"
          | "-lea"
          | "type"
          | "-type"
          | "multiAcademyTrust"
          | "-multiAcademyTrust"
          | "address.city"
          | "-address.city"
          | "borough"
          | "-borough"
          | "specialism"
          | "-specialism"
          | "internalId"
          | "-internalId"
          | "lastContactedDate"
          | "-lastContactedDate"
          | "distance"
          | "-distance";
        /**
         * The fields to include in the response in dot notation format
         */
        select?: (
          | "id"
          | "name"
          | "address.addressLine1"
          | "address.addressLine2"
          | "address.addressLine3"
          | "address.city"
          | "address.county"
          | "address.borough"
          | "address.postcode"
          | "address.country"
          | "address.lat"
          | "address.lng"
          | "address"
          | "telephone"
          | "email"
          | "contacts"
          | "borough"
          | "lea"
          | "type"
          | "multiAcademyTrust"
          | "color"
          | "customId"
          | "internalId"
          | "assignee"
          | "assignee.id"
          | "assignee.email"
          | "assignee.firstName"
          | "assignee.lastName"
          | "assignee.username"
          | "assignee.lea"
          | "assignee.clients"
          | "assignee.position"
          | "assignee.permissions"
          | "assignee.activationCode"
          | "assignee.customer"
          | "assignee.disabled"
          | "assignee.commissionThreshold"
          | "assignee.branches"
          | "assignee.displayOnboarding"
          | "assignee._customId"
          | "assignee.signature"
          | "assignee.phone"
          | "assignee.defaultCandidateWatcher"
          | "assignee.defaultTaskWorkspace"
          | "assignee.teamAdmin"
          | "assignee.contact"
          | "assignee.contact.id"
          | "assignee.contact.forename"
          | "assignee.contact.surname"
          | "assignee.contact.email"
          | "assignee.contact.telephone"
          | "assignee.contact.mobile"
          | "assignee.contact.extension"
          | "assignee.createdAt"
          | "assignee.evertime.id"
          | "assignee.evertime"
          | "assignee.timesheetPortal.id"
          | "assignee.timesheetPortal.code"
          | "assignee.timesheetPortal"
          | "tags"
          | "tobSentAt"
          | "website"
          | "archived"
          | "specialism"
          | "currentBooking"
          | "bookingCount"
          | "importantNotes"
          | "roles"
          | "startTime"
          | "endTime"
          | "additionalInstructions"
          | "additionalInstructionsClient"
          | "highlight"
          | "area"
          | "governance"
          | "mathq"
          | "ofstedRating"
          | "dateOfstedReportChecked"
          | "boarding"
          | "licenceToIssueSponsorship"
          | "agencySpendTotal"
          | "vatExempt"
          | "splitInvoices"
          | "switchBoard"
          | "ccsClient"
          | "ccsClientUrn"
          | "ccsClientOfstedUrn"
          | "ccsClientLotNumber"
          | "buyerReference"
          | "ccsClientInvoiceDate"
          | "keyStage"
          | "contact"
          | "contact.id"
          | "contact.forename"
          | "contact.surname"
          | "contact.email"
          | "contact.telephone"
          | "contact.mobile"
          | "contact.extension"
          | "lastContactedDate"
          | "relevanceScore"
          | "matchReasons"
          | "distance"
          | "migrationId"
          | "migrationApproval"
          | "evertime.id"
          | "evertime.contacts"
          | "evertime.locations"
          | "evertime.nameMatch"
          | "evertime"
          | "timesheetPortal.id"
          | "timesheetPortal.code"
          | "timesheetPortal"
          | ""
        )[];
        /**
         * Related fields to include as objects in the response.
         *
         * @maxItems 25
         */
        expand?: ("assignee" | "tags")[];
        /**
         * Additional fields to include in the response
         */
        include?: "bookings"[];
        filter?: {
          /**
           * Filter clients by their IDs. Maximum 50.
           *
           * @maxItems 50
           */
          ids?: string[];
          /**
           * Filter clients by name.
           *
           * @maxLength 256
           */
          name?: string;
          /**
           * Filter clients by telephone number.
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * Filter clients by email address.
           *
           * @maxLength 256
           */
          email?: string;
          /**
           * Address fields used to filter clients. Multiple supplied fields are combined.
           */
          address?: {
            /**
             * Filter clients by postcode.
             *
             * @maxLength 32
             */
            postcode?: string;
            /**
             * Filter clients by county.
             *
             * @maxLength 128
             */
            county?: string;
            /**
             * Filter clients by city.
             *
             * @maxLength 128
             */
            city?: string;
          };
          /**
           * Filter clients by local education authority.
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Filter clients by type.
           *
           * @maxLength 128
           */
          type?: string;
          /**
           * Filter clients by multi academy trust.
           *
           * @maxLength 256
           */
          multiAcademyTrust?: string;
          /**
           * Filter clients by borough.
           *
           * @maxLength 128
           */
          borough?: string;
          /**
           * Filter clients by specialism.
           *
           * @maxItems 25
           */
          specialism?: string[];
          /**
           * Filter clients by their company-defined identifier.
           *
           * @maxLength 256
           */
          internalId?: string;
          /**
           * Filter clients by whether they are a GCA client.
           */
          ccsClient?: "true" | "false";
          /**
           * Filter clients by GCA client URN.
           *
           * @maxLength 256
           */
          ccsClientUrn?: string;
          /**
           * Filter clients by assigned users.
           *
           * @maxItems 25
           */
          assignee?: string[];
          /**
           * Filter clients by last contact date.
           */
          lastContactedDateFilter?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter clients with an active booking overlapping an absolute or relative date range.
           */
          bookedInRange?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter clients without an active booking overlapping an absolute or relative date range.
           */
          notBookedInRange?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter clients by tags they have.
           *
           * @maxItems 25
           */
          tags?: string[];
          /**
           * Find clients with coordinates relative to a location. A positive radius limits results to that distance in miles; omit it or use zero for no distance limit.
           */
          location?: {
            /**
             * @minimum -90
             * @maximum 90
             */
            lat: number | null;
            /**
             * @minimum -180
             * @maximum 180
             */
            lng: number | null;
            /**
             * @minimum 0
             * @maximum 500
             */
            radius?: number | null;
          };
          /**
           * Filter clients by an associated contact.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          ["contacts.contactId"]?: string;
        };
      };
      /**
       * The included table column identifiers in display order.
       *
       * @minItems 1
       * @maxItems 250
       */
      columns: string[];
    }
  | {
      /**
       * Whether the saved search is available to everyone in its company.
       */
      shared: boolean;
      /**
       * A saved Client Contact search.
       */
      type: "clientContacts";
      /**
       * The name shown when choosing the saved search.
       *
       * @minLength 1
       * @maxLength 128
       */
      name: string;
      /**
       * A short explanation of what the saved search finds.
       *
       * @maxLength 512
       */
      description?: string;
      /**
       * The Client Contact query applied by this saved search, including filters, sorting, selected fields, and expansions.
       */
      query: {
        /**
         * Find relevant Client contacts using contact and Client details.
         *
         * @minLength 1
         * @maxLength 256
         */
        q?: string;
        /**
         * Search Client contacts by name, email, or Client.
         *
         * @maxLength 256
         */
        search?: string;
        /**
         * The field used to order contacts. Prefix with - for descending order.
         */
        sort?:
          | "forename"
          | "-forename"
          | "surname"
          | "-surname"
          | "email"
          | "-email"
          | "telephone"
          | "-telephone"
          | "role"
          | "-role"
          | "status"
          | "-status"
          | "decisionMaker"
          | "-decisionMaker"
          | "clientName"
          | "-clientName"
          | "clientAddress.city"
          | "-clientAddress.city"
          | "clientAddress.county"
          | "-clientAddress.county"
          | "clientAddress.postcode"
          | "-clientAddress.postcode"
          | "clientLea"
          | "-clientLea"
          | "clientType"
          | "-clientType"
          | "clientMultiAcademyTrust"
          | "-clientMultiAcademyTrust"
          | "clientBorough"
          | "-clientBorough"
          | "clientAssignee"
          | "-clientAssignee"
          | "clientSpecialism"
          | "-clientSpecialism"
          | "clientUpdatedAt"
          | "-clientUpdatedAt"
          | "distance"
          | "-distance"
          | "relevanceScore"
          | "-relevanceScore";
        /**
         * The fields to include in the response in dot notation format.
         *
         * @maxItems 250
         */
        select?: (
          | "id"
          | "forename"
          | "surname"
          | "email"
          | "telephone"
          | "mobile"
          | "extension"
          | "role"
          | "roleOther"
          | "contactId"
          | "timesheetPortal.id"
          | "timesheetPortal.code"
          | "timesheetPortal"
          | "optOut"
          | "note"
          | "decisionMaker"
          | "status"
          | "clientId"
          | "clientName"
          | "clientAddress.addressLine1"
          | "clientAddress.addressLine2"
          | "clientAddress.addressLine3"
          | "clientAddress.city"
          | "clientAddress.county"
          | "clientAddress.borough"
          | "clientAddress.postcode"
          | "clientAddress.country"
          | "clientAddress.lat"
          | "clientAddress.lng"
          | "clientAddress"
          | "clientLea"
          | "clientType"
          | "clientMultiAcademyTrust"
          | "clientBorough"
          | "clientAssignee"
          | "clientAssignee.id"
          | "clientAssignee.email"
          | "clientAssignee.firstName"
          | "clientAssignee.lastName"
          | "clientAssignee.username"
          | "clientAssignee.lea"
          | "clientAssignee.clients"
          | "clientAssignee.position"
          | "clientAssignee.permissions"
          | "clientAssignee.activationCode"
          | "clientAssignee.customer"
          | "clientAssignee.disabled"
          | "clientAssignee.commissionThreshold"
          | "clientAssignee.branches"
          | "clientAssignee.displayOnboarding"
          | "clientAssignee._customId"
          | "clientAssignee.signature"
          | "clientAssignee.phone"
          | "clientAssignee.defaultCandidateWatcher"
          | "clientAssignee.defaultTaskWorkspace"
          | "clientAssignee.teamAdmin"
          | "clientAssignee.contact"
          | "clientAssignee.contact.id"
          | "clientAssignee.contact.forename"
          | "clientAssignee.contact.surname"
          | "clientAssignee.contact.email"
          | "clientAssignee.contact.telephone"
          | "clientAssignee.contact.mobile"
          | "clientAssignee.contact.extension"
          | "clientAssignee.createdAt"
          | "clientAssignee.evertime.id"
          | "clientAssignee.evertime"
          | "clientAssignee.timesheetPortal.id"
          | "clientAssignee.timesheetPortal.code"
          | "clientAssignee.timesheetPortal"
          | "clientSpecialism"
          | "clientUpdatedAt"
          | "distance"
          | "relevanceScore"
          | "matchReasons"
          | ""
        )[];
        /**
         * Related fields to include as objects in the response.
         *
         * @maxItems 1
         */
        expand?: "clientAssignee"[];
        /**
         * Filter Client contacts using contact and associated Client fields.
         */
        filter?: {
          /**
           * Filter contacts by status.
           *
           * @maxItems 3
           */
          status?: ("Active" | "Passive" | "Inactive")[];
          /**
           * Filter contacts by role.
           *
           * @maxItems 44
           */
          role?: (
            | "Head Teacher"
            | "Deputy Head"
            | "Cover Manager"
            | "HR Manager"
            | "Finance Manager"
            | "Head of Maths"
            | "Head of Science"
            | "Head of English"
            | "Assistant Headteacher"
            | "Headteacher's PA"
            | "SENCO"
            | "Other"
            | "Business Manager"
            | "Business Administrator"
            | "Invoice Contact"
            | "Academy Recruitment Contact"
            | "Nursery Manager"
            | "CEO"
            | "COO"
            | "Executive Headteacher"
            | "Inclusion Manager"
            | "EYFS Lead"
            | "Admin Team"
            | "Head of Humanities"
            | "Head of History"
            | "Head of Geography"
            | "Head of Business Studies"
            | "Head of PE"
            | "Head of KS1"
            | "Head of KS2"
            | "Head of KS3"
            | "Head of KS4"
            | "Virtual School Headteacher"
            | "Deputy Virtual School Headteacher"
            | "Post-16 Team Leader"
            | "SEN Team"
            | "Inclusion Team"
            | "UASC Team"
            | "Caseworker"
            | "Designated Teacher"
            | "Attendance Officer"
            | "Coordinator"
            | "Children's Commissioner"
            | "Timesheet Contact"
          )[];
          /**
           * Filter contacts by forename.
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * Filter contacts by surname.
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * Filter contacts by email address.
           *
           * @maxLength 256
           */
          email?: string;
          /**
           * Filter contacts by decision-maker status.
           */
          decisionMaker?: "true" | "false";
          /**
           * Filter contacts by opt-out status.
           */
          optOut?: "true" | "false";
          /**
           * Filter contacts by Client.
           *
           * @maxItems 50
           */
          clientId?: string[];
          /**
           * Filter contacts by Client name.
           *
           * @maxLength 256
           */
          clientName?: string;
          /**
           * Filter contacts by associated Client address fields.
           */
          clientAddress?: {
            /**
             * @maxLength 128
             */
            city?: string;
            /**
             * @maxLength 128
             */
            county?: string;
            /**
             * @maxLength 32
             */
            postcode?: string;
          };
          /**
           * Filter contacts by Client local education authority.
           *
           * @maxLength 128
           */
          clientLea?: string;
          /**
           * Filter contacts by Client sector.
           *
           * @maxLength 128
           */
          clientType?: string;
          /**
           * Filter contacts by Client multi-academy trust.
           *
           * @maxLength 256
           */
          clientMultiAcademyTrust?: string;
          /**
           * Filter contacts by Client borough.
           *
           * @maxLength 128
           */
          clientBorough?: string;
          /**
           * Filter contacts by Client assignee.
           *
           * @maxItems 25
           */
          clientAssignee?: string[];
          /**
           * Filter contacts by Client specialism.
           *
           * @maxItems 25
           */
          clientSpecialism?: string[];
          /**
           * Filter contacts by when their Client was updated.
           */
          clientUpdatedAt?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter contacts by distance from their Client address.
           */
          location?: {
            /**
             * @minimum -90
             * @maximum 90
             */
            lat: number | null;
            /**
             * @minimum -180
             * @maximum 180
             */
            lng: number | null;
            /**
             * @minimum 0
             * @exclusiveMinimum true
             * @maximum 500
             */
            radius: number;
          };
        };
      };
      /**
       * The included table column identifiers in display order.
       *
       * @minItems 1
       * @maxItems 250
       */
      columns: string[];
    };

export type CreateSavedSearchVariables = {
  body?: CreateSavedSearchRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateSavedSearch = (
  variables: CreateSavedSearchVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateSavedSearchResponse,
    CreateSavedSearchError,
    CreateSavedSearchRequestBody,
    {},
    {},
    {}
  >({ url: "/saved-searches", method: "post", ...variables, signal });

export const useCreateSavedSearch = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateSavedSearchResponse,
      CreateSavedSearchError,
      CreateSavedSearchVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateSavedSearchResponse,
    CreateSavedSearchError,
    CreateSavedSearchVariables
  >({
    mutationFn: (variables: CreateSavedSearchVariables) =>
      fetchCreateSavedSearch(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetSavedSearchPathParams = {
  /**
   * The unique identifier of the saved search.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  savedSearchId: string;
};

export type GetSavedSearchQueryParams = {
  /**
   * The fields to include in the response in dot notation format.
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "customerId"
    | "shared"
    | "level"
    | "createdBy"
    | "name"
    | "description"
    | "columns"
    | "createdAt"
    | "updatedAt"
    | "type"
    | "query.q"
    | "query.search"
    | "query.sort"
    | "query.select"
    | "query.expand"
    | "query.filter.availabilityPeriod.from"
    | "query.filter.availabilityPeriod.to"
    | "query.filter.availabilityPeriod.relative.from.anchor"
    | "query.filter.availabilityPeriod.relative.from.offset"
    | "query.filter.availabilityPeriod.relative.from.unit"
    | "query.filter.availabilityPeriod.relative.from"
    | "query.filter.availabilityPeriod.relative.to.anchor"
    | "query.filter.availabilityPeriod.relative.to.offset"
    | "query.filter.availabilityPeriod.relative.to.unit"
    | "query.filter.availabilityPeriod.relative.to"
    | "query.filter.availabilityPeriod.relative"
    | "query.filter.availabilityPeriod"
    | "query.filter.availabilityStatuses"
    | "query.filter.ids"
    | "query.filter.status"
    | "query.filter.onboardingStatus"
    | "query.filter.activeOnboardingSession"
    | "query.filter.onboardingSent"
    | "query.filter.section128Required"
    | "query.filter.confirmed"
    | "query.filter.optOut"
    | "query.filter.optOut48Hours"
    | "query.filter.fitToWorkOverride"
    | "query.filter.affectAbility"
    | "query.filter.ewcConfirmation"
    | "query.filter.dbsHeld"
    | "query.filter.dbsRegisteredToUpdateService"
    | "query.filter.firstName"
    | "query.filter.lastName"
    | "query.filter.email"
    | "query.filter.mobile"
    | "query.filter.address.city"
    | "query.filter.address.postcode"
    | "query.filter.address.county"
    | "query.filter.address.borough"
    | "query.filter.address.addressLine1"
    | "query.filter.address.addressLine2"
    | "query.filter.address.addressLine3"
    | "query.filter.address"
    | "query.filter.occupation"
    | "query.filter.specialism"
    | "query.filter._internalId"
    | "query.filter.complianceUser"
    | "query.filter.assignee"
    | "query.filter.watcher"
    | "query.filter.team"
    | "query.filter.contactId"
    | "query.filter.currentBooking"
    | "query.filter.bookedInRange.from"
    | "query.filter.bookedInRange.to"
    | "query.filter.bookedInRange.relative.from.anchor"
    | "query.filter.bookedInRange.relative.from.offset"
    | "query.filter.bookedInRange.relative.from.unit"
    | "query.filter.bookedInRange.relative.from"
    | "query.filter.bookedInRange.relative.to.anchor"
    | "query.filter.bookedInRange.relative.to.offset"
    | "query.filter.bookedInRange.relative.to.unit"
    | "query.filter.bookedInRange.relative.to"
    | "query.filter.bookedInRange.relative"
    | "query.filter.bookedInRange"
    | "query.filter.notBookedInRange.from"
    | "query.filter.notBookedInRange.to"
    | "query.filter.notBookedInRange.relative.from.anchor"
    | "query.filter.notBookedInRange.relative.from.offset"
    | "query.filter.notBookedInRange.relative.from.unit"
    | "query.filter.notBookedInRange.relative.from"
    | "query.filter.notBookedInRange.relative.to.anchor"
    | "query.filter.notBookedInRange.relative.to.offset"
    | "query.filter.notBookedInRange.relative.to.unit"
    | "query.filter.notBookedInRange.relative.to"
    | "query.filter.notBookedInRange.relative"
    | "query.filter.notBookedInRange"
    | "query.filter.tags"
    | "query.filter.location.lat"
    | "query.filter.location.lng"
    | "query.filter.location.radius"
    | "query.filter.location"
    | "query.filter.statusChangeDate.from"
    | "query.filter.statusChangeDate.to"
    | "query.filter.statusChangeDate.relative.from.anchor"
    | "query.filter.statusChangeDate.relative.from.offset"
    | "query.filter.statusChangeDate.relative.from.unit"
    | "query.filter.statusChangeDate.relative.from"
    | "query.filter.statusChangeDate.relative.to.anchor"
    | "query.filter.statusChangeDate.relative.to.offset"
    | "query.filter.statusChangeDate.relative.to.unit"
    | "query.filter.statusChangeDate.relative.to"
    | "query.filter.statusChangeDate.relative"
    | "query.filter.statusChangeDate"
    | "query.filter.lastContactedDateFilter.from"
    | "query.filter.lastContactedDateFilter.to"
    | "query.filter.lastContactedDateFilter.relative.from.anchor"
    | "query.filter.lastContactedDateFilter.relative.from.offset"
    | "query.filter.lastContactedDateFilter.relative.from.unit"
    | "query.filter.lastContactedDateFilter.relative.from"
    | "query.filter.lastContactedDateFilter.relative.to.anchor"
    | "query.filter.lastContactedDateFilter.relative.to.offset"
    | "query.filter.lastContactedDateFilter.relative.to.unit"
    | "query.filter.lastContactedDateFilter.relative.to"
    | "query.filter.lastContactedDateFilter.relative"
    | "query.filter.lastContactedDateFilter"
    | "query.filter.employmentPreference"
    | "query.filter.transport"
    | "query.filter.trnNumber"
    | "query.filter.desiredLocation"
    | "query.filter.subjectToNotice"
    | "query.filter.title"
    | "query.filter.sex"
    | "query.filter.pronoun"
    | "query.filter.roleType"
    | "query.filter.payType"
    | "query.filter.payInterval"
    | "query.filter.dbsType"
    | "query.filter.source"
    | "query.filter.telephone"
    | "query.filter.previousNames"
    | "query.filter.otherNames"
    | "query.filter.dob.from"
    | "query.filter.dob.to"
    | "query.filter.dob.relative.from.anchor"
    | "query.filter.dob.relative.from.offset"
    | "query.filter.dob.relative.from.unit"
    | "query.filter.dob.relative.from"
    | "query.filter.dob.relative.to.anchor"
    | "query.filter.dob.relative.to.offset"
    | "query.filter.dob.relative.to.unit"
    | "query.filter.dob.relative.to"
    | "query.filter.dob.relative"
    | "query.filter.dob"
    | "query.filter.trnNumberCheckedDate.from"
    | "query.filter.trnNumberCheckedDate.to"
    | "query.filter.trnNumberCheckedDate.relative.from.anchor"
    | "query.filter.trnNumberCheckedDate.relative.from.offset"
    | "query.filter.trnNumberCheckedDate.relative.from.unit"
    | "query.filter.trnNumberCheckedDate.relative.from"
    | "query.filter.trnNumberCheckedDate.relative.to.anchor"
    | "query.filter.trnNumberCheckedDate.relative.to.offset"
    | "query.filter.trnNumberCheckedDate.relative.to.unit"
    | "query.filter.trnNumberCheckedDate.relative.to"
    | "query.filter.trnNumberCheckedDate.relative"
    | "query.filter.trnNumberCheckedDate"
    | "query.filter.dbsLastChecked.from"
    | "query.filter.dbsLastChecked.to"
    | "query.filter.dbsLastChecked.relative.from.anchor"
    | "query.filter.dbsLastChecked.relative.from.offset"
    | "query.filter.dbsLastChecked.relative.from.unit"
    | "query.filter.dbsLastChecked.relative.from"
    | "query.filter.dbsLastChecked.relative.to.anchor"
    | "query.filter.dbsLastChecked.relative.to.offset"
    | "query.filter.dbsLastChecked.relative.to.unit"
    | "query.filter.dbsLastChecked.relative.to"
    | "query.filter.dbsLastChecked.relative"
    | "query.filter.dbsLastChecked"
    | "query.filter.dbsUpdateServiceCheckDate.from"
    | "query.filter.dbsUpdateServiceCheckDate.to"
    | "query.filter.dbsUpdateServiceCheckDate.relative.from.anchor"
    | "query.filter.dbsUpdateServiceCheckDate.relative.from.offset"
    | "query.filter.dbsUpdateServiceCheckDate.relative.from.unit"
    | "query.filter.dbsUpdateServiceCheckDate.relative.from"
    | "query.filter.dbsUpdateServiceCheckDate.relative.to.anchor"
    | "query.filter.dbsUpdateServiceCheckDate.relative.to.offset"
    | "query.filter.dbsUpdateServiceCheckDate.relative.to.unit"
    | "query.filter.dbsUpdateServiceCheckDate.relative.to"
    | "query.filter.dbsUpdateServiceCheckDate.relative"
    | "query.filter.dbsUpdateServiceCheckDate"
    | "query.filter.rtwVerificationDate.from"
    | "query.filter.rtwVerificationDate.to"
    | "query.filter.rtwVerificationDate.relative.from.anchor"
    | "query.filter.rtwVerificationDate.relative.from.offset"
    | "query.filter.rtwVerificationDate.relative.from.unit"
    | "query.filter.rtwVerificationDate.relative.from"
    | "query.filter.rtwVerificationDate.relative.to.anchor"
    | "query.filter.rtwVerificationDate.relative.to.offset"
    | "query.filter.rtwVerificationDate.relative.to.unit"
    | "query.filter.rtwVerificationDate.relative.to"
    | "query.filter.rtwVerificationDate.relative"
    | "query.filter.rtwVerificationDate"
    | "query.filter.section128CheckedDate.from"
    | "query.filter.section128CheckedDate.to"
    | "query.filter.section128CheckedDate.relative.from.anchor"
    | "query.filter.section128CheckedDate.relative.from.offset"
    | "query.filter.section128CheckedDate.relative.from.unit"
    | "query.filter.section128CheckedDate.relative.from"
    | "query.filter.section128CheckedDate.relative.to.anchor"
    | "query.filter.section128CheckedDate.relative.to.offset"
    | "query.filter.section128CheckedDate.relative.to.unit"
    | "query.filter.section128CheckedDate.relative.to"
    | "query.filter.section128CheckedDate.relative"
    | "query.filter.section128CheckedDate"
    | "query.filter.prohibitionCheckDate.from"
    | "query.filter.prohibitionCheckDate.to"
    | "query.filter.prohibitionCheckDate.relative.from.anchor"
    | "query.filter.prohibitionCheckDate.relative.from.offset"
    | "query.filter.prohibitionCheckDate.relative.from.unit"
    | "query.filter.prohibitionCheckDate.relative.from"
    | "query.filter.prohibitionCheckDate.relative.to.anchor"
    | "query.filter.prohibitionCheckDate.relative.to.offset"
    | "query.filter.prohibitionCheckDate.relative.to.unit"
    | "query.filter.prohibitionCheckDate.relative.to"
    | "query.filter.prohibitionCheckDate.relative"
    | "query.filter.prohibitionCheckDate"
    | "query.filter.createdAt.from"
    | "query.filter.createdAt.to"
    | "query.filter.createdAt.relative.from.anchor"
    | "query.filter.createdAt.relative.from.offset"
    | "query.filter.createdAt.relative.from.unit"
    | "query.filter.createdAt.relative.from"
    | "query.filter.createdAt.relative.to.anchor"
    | "query.filter.createdAt.relative.to.offset"
    | "query.filter.createdAt.relative.to.unit"
    | "query.filter.createdAt.relative.to"
    | "query.filter.createdAt.relative"
    | "query.filter.createdAt"
    | "query.filter.updatedAt.from"
    | "query.filter.updatedAt.to"
    | "query.filter.updatedAt.relative.from.anchor"
    | "query.filter.updatedAt.relative.from.offset"
    | "query.filter.updatedAt.relative.from.unit"
    | "query.filter.updatedAt.relative.from"
    | "query.filter.updatedAt.relative.to.anchor"
    | "query.filter.updatedAt.relative.to.offset"
    | "query.filter.updatedAt.relative.to.unit"
    | "query.filter.updatedAt.relative.to"
    | "query.filter.updatedAt.relative"
    | "query.filter.updatedAt"
    | "query.filter.candidateMobileAppStatus"
    | "query.filter.recAuditConsent"
    | "query.filter"
    | "query"
    | "query.include"
    | "query.filter.name"
    | "query.filter.lea"
    | "query.filter.type"
    | "query.filter.multiAcademyTrust"
    | "query.filter.borough"
    | "query.filter.internalId"
    | "query.filter.ccsClient"
    | "query.filter.ccsClientUrn"
    | "query.filter.contacts.contactId"
    | "query.filter.role"
    | "query.filter.forename"
    | "query.filter.surname"
    | "query.filter.decisionMaker"
    | "query.filter.clientId"
    | "query.filter.clientName"
    | "query.filter.clientAddress.city"
    | "query.filter.clientAddress.county"
    | "query.filter.clientAddress.postcode"
    | "query.filter.clientAddress"
    | "query.filter.clientLea"
    | "query.filter.clientType"
    | "query.filter.clientMultiAcademyTrust"
    | "query.filter.clientBorough"
    | "query.filter.clientAssignee"
    | "query.filter.clientSpecialism"
    | "query.filter.clientUpdatedAt.from"
    | "query.filter.clientUpdatedAt.to"
    | "query.filter.clientUpdatedAt.relative.from.anchor"
    | "query.filter.clientUpdatedAt.relative.from.offset"
    | "query.filter.clientUpdatedAt.relative.from.unit"
    | "query.filter.clientUpdatedAt.relative.from"
    | "query.filter.clientUpdatedAt.relative.to.anchor"
    | "query.filter.clientUpdatedAt.relative.to.offset"
    | "query.filter.clientUpdatedAt.relative.to.unit"
    | "query.filter.clientUpdatedAt.relative.to"
    | "query.filter.clientUpdatedAt.relative"
    | "query.filter.clientUpdatedAt"
    | "presentation.mode"
    | "presentation.availability.period.from"
    | "presentation.availability.period.to"
    | "presentation.availability.period.relative.from.anchor"
    | "presentation.availability.period.relative.from.offset"
    | "presentation.availability.period.relative.from.unit"
    | "presentation.availability.period.relative.from"
    | "presentation.availability.period.relative.to.anchor"
    | "presentation.availability.period.relative.to.offset"
    | "presentation.availability.period.relative.to.unit"
    | "presentation.availability.period.relative.to"
    | "presentation.availability.period.relative"
    | "presentation.availability.period"
    | "presentation.availability.statuses"
    | "presentation.availability.draftClient"
    | "presentation.availability.draftMode"
    | "presentation.availability"
    | "presentation"
    | ""
  )[];
};

export type GetSavedSearchError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetSavedSearchResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data:
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved candidate search.
         */
        type?: "candidates";
        /**
         * The candidate query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * @maxLength 256
           */
          search?: string;
          sort?:
            | (
                | "relevanceScore"
                | "-relevanceScore"
                | "forename"
                | "-forename"
                | "surname"
                | "-surname"
                | "mobile"
                | "-mobile"
                | "email"
                | "-email"
                | "address.city"
                | "-address.city"
                | "address.postcode"
                | "-address.postcode"
                | "address.county"
                | "-address.county"
                | "address.borough"
                | "-address.borough"
                | "address.addressLine1"
                | "-address.addressLine1"
                | "address.addressLine2"
                | "-address.addressLine2"
                | "address.addressLine3"
                | "-address.addressLine3"
                | "subject"
                | "-subject"
                | "statusChangeDate"
                | "-statusChangeDate"
                | "lastContactedDate"
                | "-lastContactedDate"
                | "daysSinceLastContactDisplay"
                | "-daysSinceLastContactDisplay"
                | "status"
                | "-status"
                | "occupation"
                | "-occupation"
                | "dob"
                | "-dob"
                | "trnNumberCheckedDate"
                | "-trnNumberCheckedDate"
                | "criminalRecord.dbsLastChecked"
                | "-criminalRecord.dbsLastChecked"
                | "criminalRecord.updateServiceCheckDate"
                | "-criminalRecord.updateServiceCheckDate"
                | "rtwVerification"
                | "-rtwVerification"
                | "criminalRecord.section128Checked"
                | "-criminalRecord.section128Checked"
                | "criminalRecord.prohibitionCheck"
                | "-criminalRecord.prohibitionCheck"
                | "createdAt"
                | "-createdAt"
                | "updatedAt"
                | "-updatedAt"
                | "_internalId"
                | "-_internalId"
                | "trnNumber"
                | "-trnNumber"
                | "desiredLocation"
                | "-desiredLocation"
                | "telephone"
                | "-telephone"
                | "previousNames"
                | "-previousNames"
                | "otherNames"
                | "-otherNames"
                | "location"
                | "-location"
              )
            | ("availabilityPriority" | "-availabilityPriority");
          /**
           * @maxItems 250
           */
          select?: (
            | "id"
            | "forename"
            | "surname"
            | "previousNames"
            | "email"
            | "telephone"
            | "mobile"
            | "occupation"
            | "subject"
            | "address.addressLine1"
            | "address.addressLine2"
            | "address.addressLine3"
            | "address.city"
            | "address.county"
            | "address.borough"
            | "address.postcode"
            | "address.country"
            | "address.lat"
            | "address.lng"
            | "qualifications"
            | "employment"
            | "references"
            | "referenceCheckConsent"
            | "referenceCheckConsentName"
            | "referenceCheckConsentDate"
            | "dob"
            | "sex"
            | "status"
            | "onboardingStatus"
            | "onboardingSent"
            | "nextOfKin.nextOfKinName"
            | "nextOfKin.nextOfKinRelationship"
            | "nextOfKin.nextOfKinTelephone"
            | "nextOfKin.nextOfKinEmail"
            | "nextOfKin.nextOfKinAddress.addressLine1"
            | "nextOfKin.nextOfKinAddress.addressLine2"
            | "nextOfKin.nextOfKinAddress.addressLine3"
            | "nextOfKin.nextOfKinAddress.city"
            | "nextOfKin.nextOfKinAddress.county"
            | "nextOfKin.nextOfKinAddress.borough"
            | "nextOfKin.nextOfKinAddress.postcode"
            | "nextOfKin.nextOfKinAddress.country"
            | "nextOfKin.nextOfKinAddress.lat"
            | "nextOfKin.nextOfKinAddress.lng"
            | "criminalRecord.dbsHeld"
            | "criminalRecord.dbsNumber"
            | "criminalRecord.dbsType"
            | "criminalRecord.dbsLastChecked"
            | "criminalRecord.dbsUpdateServiceNumber"
            | "criminalRecord.prohibitionCheck"
            | "criminalRecord.overseasCheck"
            | "criminalRecord.overseasCheckNote"
            | "criminalRecord.convictedOffence"
            | "criminalRecord.convictedOffenceDescription"
            | "criminalRecord.convictions"
            | "criminalRecord.convictionsDescription"
            | "criminalRecord.registeredToUpdateService"
            | "criminalRecord.consentUpdateServiceChecks"
            | "criminalRecord.note"
            | "criminalRecord.updateServiceCheckDate"
            | "criminalRecord.abroad"
            | "criminalRecord.overseasDeclarationExtended"
            | "criminalRecord.educationalDisciplinaryProceedings"
            | "criminalRecord.dbsComments"
            | "criminalRecord.dbsDeclarationRead"
            | "criminalRecord.overseasOffence"
            | "criminalRecord.overseasOffenceDescription"
            | "criminalRecord.currentCriminalOffence"
            | "criminalRecord.currentCriminalOffenceDescription"
            | "criminalRecord.dbsIssuedByBusiness"
            | "criminalRecord.dbsIssuedByCurrentEmployer"
            | "criminalRecord.section128Required"
            | "criminalRecord.section128Checked"
            | "criminalRecordDeclaration.questions"
            | "criminalRecordDeclaration.answers"
            | "criminalRecordDeclaration.submittedAt"
            | "criminalRecordDeclaration.submittedIp"
            | "criminalRecordDeclaration.configurationUpdatedAt"
            | "criminalRecordCheckConsent"
            | "criminalRecordCheckConsentName"
            | "criminalRecordCheckConsentDate"
            | "safeInEducation.barred"
            | "safeInEducation.barredCheckDate"
            | "safeInEducation.waiver"
            | "safeInEducation.ordersYou"
            | "safeInEducation.ordersChild"
            | "safeInEducation.previousSafeguardingAllegations"
            | "safeInEducation.cautioned"
            | "safeInEducation.violentOffence"
            | "safeInEducation.anotherCountry"
            | "safeInEducation.readKCSIE"
            | "safeInEducation.readChildcareAct"
            | "safeInEducation.prohibitedFromTeaching"
            | "safeInEducation.knownPolice"
            | "safeInEducation.prohibitedFromManagement"
            | "safeInEducation.criminalOffencesLaw"
            | "safeInEducation.safeInEducationDetails"
            | "safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.signOfAbuse"
            | "safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
            | "safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
            | "safeInEducation.safeguardingQuestionaire.fgmDefinition"
            | "safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
            | "safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
            | "safeInEducation.safeguardingQuestionaire.studentConfides"
            | "socialMedia.facebook"
            | "socialMedia.instagram"
            | "socialMedia.twitter"
            | "socialMedia.linkedIn"
            | "socialMedia.tiktok"
            | "socialMedia.others"
            | "socialMedia.socialMediaChecked"
            | "socialMedia.googleCheckedDate"
            | "safeInEducationCheckConsent"
            | "safeInEducationCheckConsentName"
            | "safeInEducationCheckConsentDate"
            | "healthRecord.physicalAbilityCondition"
            | "healthRecord.confused"
            | "healthRecord.hearing"
            | "healthRecord.eyesight"
            | "healthRecord.depression"
            | "healthRecord.severePain"
            | "healthRecord.excessiveDrowsiness"
            | "healthRecord.epilepsy"
            | "healthRecord.heart"
            | "healthRecord.asthma"
            | "healthRecord.mentalHealth"
            | "healthRecord.expectantMother"
            | "healthRecord.allergies"
            | "healthRecord.medication"
            | "healthRecord.healthIssues"
            | "healthRecord.healthIssuesDescription"
            | "healthRecord.disabilityNeeds"
            | "healthRecord.contagiousDiseases"
            | "healthRecord.affectAbility"
            | "healthRecord.notificationAgree"
            | "healthRecord.sasOptIn"
            | "healthRecord.signature"
            | "healthRecord.drawnSignature"
            | "healthRecord.signatureDate"
            | "healthRecord.vaccinationMeasles"
            | "healthRecord.vaccinationMumps"
            | "healthRecord.vaccinationRubella"
            | "healthRecord.vaccinationVaricella"
            | "healthRecord.vaccinationTuberculosis"
            | "healthRecord.vaccinationHepatitisB"
            | "healthRecord.vaccinationHepatitisC"
            | "healthRecord.vaccinationHIV"
            | "healthRecord.vaccinationCovid"
            | "healthRecord.vaccinationCovid1"
            | "healthRecord.vaccinationCovid2"
            | "healthRecord.vaccinationCovidBooster"
            | "healthRecord.vaccinationCovidInfluenza"
            | "healthRecord.fitToWorkOverride"
            | "healthDeclaration.questions"
            | "healthDeclaration.answers"
            | "healthDeclaration.submittedAt"
            | "healthDeclaration.submittedIp"
            | "healthDeclaration.configurationUpdatedAt"
            | "healthRecordCheckConsent"
            | "healthRecordCheckConsentName"
            | "healthRecordCheckConsentDate"
            | "bankDetails.bankAccountName"
            | "bankDetails.bankName"
            | "bankDetails.accountNumber"
            | "bankDetails.sortCode"
            | "bankDetails.niNumber"
            | "bankDetails.jobOp1"
            | "bankDetails.jobOp2"
            | "bankDetails.jobOp3"
            | "bankDetails.studentLoan.studentLoan"
            | "bankDetails.studentLoan.plan1"
            | "bankDetails.studentLoan.plan2"
            | "bankDetails.studentLoan.plan4"
            | "bankDetails.studentLoan.postGradLoan"
            | "declarationCheckConsent"
            | "declarationCheckConsentName"
            | "declarationCheckConsentDate"
            | "contractForServiceConsent"
            | "contractForServiceName"
            | "contractForServiceDate"
            | "confirmed"
            | "createdBy"
            | "customerId"
            | "migrationId"
            | "migrationApproval"
            | "assignee"
            | "assignee.id"
            | "assignee.email"
            | "assignee.firstName"
            | "assignee.lastName"
            | "assignee.username"
            | "assignee.lea"
            | "assignee.clients"
            | "assignee.position"
            | "assignee.permissions"
            | "assignee.activationCode"
            | "assignee.customer"
            | "assignee.disabled"
            | "assignee.commissionThreshold"
            | "assignee.branches"
            | "assignee.displayOnboarding"
            | "assignee._customId"
            | "assignee.signature"
            | "assignee.phone"
            | "assignee.defaultCandidateWatcher"
            | "assignee.defaultTaskWorkspace"
            | "assignee.teamAdmin"
            | "assignee.contact"
            | "assignee.contact.id"
            | "assignee.contact.forename"
            | "assignee.contact.surname"
            | "assignee.contact.email"
            | "assignee.contact.telephone"
            | "assignee.contact.mobile"
            | "assignee.contact.extension"
            | "assignee.createdAt"
            | "assignee.evertime.id"
            | "assignee.timesheetPortal.id"
            | "assignee.timesheetPortal.code"
            | "currentBooking"
            | "profilePhoto"
            | "payType"
            | "payInterval"
            | "payRate"
            | "customId"
            | "_internalId"
            | "statusChangeDate"
            | "optOut"
            | "trnNumber"
            | "trnNumberCheckedDate"
            | "trnNumberNotes"
            | "employmentStartDate"
            | "ewcConfirmation"
            | "homeOfficeShareCode"
            | "archived"
            | "umbrella"
            | "payeUmbrella"
            | "umbrellaCompanyName"
            | "umbrellaCompanyCode"
            | "transport"
            | "importantNotes"
            | "candidateLastUpdated"
            | "source"
            | "sourceOther"
            | "onlineCheckConsent"
            | "title"
            | "pronoun"
            | "updatedAt"
            | "lastContactedDate"
            | "highlight"
            | "teacherPayScale"
            | "p45"
            | "roleType"
            | "rtwVerification"
            | "rtwVerificationNote"
            | "rtwVerificationStatus"
            | "desiredLocation"
            | "complianceUser"
            | "complianceUser.id"
            | "complianceUser.email"
            | "complianceUser.firstName"
            | "complianceUser.lastName"
            | "complianceUser.username"
            | "complianceUser.lea"
            | "complianceUser.clients"
            | "complianceUser.position"
            | "complianceUser.permissions"
            | "complianceUser.activationCode"
            | "complianceUser.customer"
            | "complianceUser.disabled"
            | "complianceUser.commissionThreshold"
            | "complianceUser.branches"
            | "complianceUser.displayOnboarding"
            | "complianceUser._customId"
            | "complianceUser.signature"
            | "complianceUser.phone"
            | "complianceUser.defaultCandidateWatcher"
            | "complianceUser.defaultTaskWorkspace"
            | "complianceUser.teamAdmin"
            | "complianceUser.contact"
            | "complianceUser.contact.id"
            | "complianceUser.contact.forename"
            | "complianceUser.contact.surname"
            | "complianceUser.contact.email"
            | "complianceUser.contact.telephone"
            | "complianceUser.contact.mobile"
            | "complianceUser.contact.extension"
            | "complianceUser.createdAt"
            | "complianceUser.evertime.id"
            | "complianceUser.timesheetPortal.id"
            | "complianceUser.timesheetPortal.code"
            | "watchers"
            | "tags"
            | "documents"
            | "documentsCount"
            | "desiredLocationMultiple"
            | "availabilityDays"
            | "availabilityLastChecked"
            | "subjectToNotice"
            | "subsidiarySubject"
            | "employmentPreference"
            | "otherNames"
            | "ltdCompany"
            | "employmentVerified"
            | "candidateDBSVerification"
            | "candidateDBSVerificationNote"
            | "candidateDBSVerificationStatus"
            | "candidateDBSCheckVerification"
            | "candidateDBSCheckVerificationNote"
            | "candidateDBSCheckVerificationStatus"
            | "candidateDBSUpdateServiceVerificationNote"
            | "candidateDBSUpdateServiceVerificationStatus"
            | "candidateTRNVerificationNote"
            | "candidateTRNVerificationStatus"
            | "candidateHealthSafetyVerification"
            | "candidateHealthSafetyVerificationNote"
            | "candidateHealthSafetyVerificationStatus"
            | "onboardingSession"
            | "onboardingSession.id"
            | "onboardingSession.url"
            | "onboardingSession.healthDeclarationConfig.questions"
            | "onboardingSession.healthDeclarationConfig.updatedAt"
            | "onboardingSession.criminalRecordConfig.questions"
            | "onboardingSession.criminalRecordConfig.updatedAt"
            | "contact"
            | "contact.id"
            | "contact.forename"
            | "contact.surname"
            | "contact.email"
            | "contact.telephone"
            | "contact.mobile"
            | "contact.extension"
            | "optOut48Hours"
            | "recAuditConsent"
            | "recAuditConsentDate"
            | "recAuditConsentMethod"
            | "createdAt"
            | "candidateMobileAppStatus"
            | "evertime.id"
            | "evertime.niNumberMatch"
            | "relevanceScore"
            | "matchReasons"
            | "distance"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "mobileAppLinkInvite.isUsed"
            | "mobileAppLinkInvite.createdAt"
            | "mobileAppLinkInvite.usedBy"
            | "mobileAppLinkInvite.usedAt"
            | "mobileAppLinkInvite.unlinkedBy"
            | "mobileAppLinkInvite.unlinkedAt"
            | "mobileAppLinkInvite.deletedBy"
            | "mobileAppLinkInvite.deletedAt"
            | "mobileAppLinkInvite.status"
          )[];
          /**
           * @maxItems 100
           */
          expand?: (
            | "assignee"
            | "contact"
            | "customer"
            | "umbrella"
            | "payeUmbrella"
            | "complianceUser"
            | "onboardingSession"
            | "watchers"
            | "tags"
            | "documents"
          )[];
          filter?: {
            /**
             * Inclusive calendar dates, up to 31 days. Both boundaries are required when selecting availability days, filtering availability statuses or sorting by availability priority.
             */
            availabilityPeriod?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Match candidates with any selected status during the availability period. Requires an availability period. Empty selects all statuses.
             *
             * @maxItems 5
             */
            availabilityStatuses?: (
              | "available"
              | "unavailable"
              | "booked"
              | "awaiting"
              | "notRecorded"
            )[];
            /**
             * @maxItems 50
             */
            ids?: string[];
            /**
             * @maxItems 25
             */
            status?: (
              | "Pending"
              | "Approved"
              | "Rejected"
              | "Anonymised"
              | "Archived"
              | "Enquired"
              | "Hold"
              | "Future"
              | "DNC"
              | "Pre"
            )[];
            /**
             * @maxItems 25
             */
            onboardingStatus?: ("Not Started" | "In Progress" | "Completed")[];
            activeOnboardingSession?: boolean;
            onboardingSent?: "true" | "false";
            section128Required?: "true" | "false";
            confirmed?: "true" | "false";
            optOut?: "true" | "false";
            optOut48Hours?: "true" | "false";
            fitToWorkOverride?: "true" | "false";
            affectAbility?: "true" | "false";
            ewcConfirmation?: "true" | "false";
            dbsHeld?: "true" | "false";
            dbsRegisteredToUpdateService?: "true" | "false";
            /**
             * @maxLength 500
             */
            firstName?: string;
            /**
             * @maxLength 500
             */
            lastName?: string;
            /**
             * @maxLength 500
             */
            email?: string;
            /**
             * @maxLength 500
             */
            mobile?: string;
            /**
             * Address fields used to filter candidates. Multiple supplied fields are combined.
             */
            address?: {
              /**
               * @maxLength 500
               */
              city?: string;
              /**
               * @maxLength 20
               */
              postcode?: string;
              /**
               * @maxLength 500
               */
              county?: string;
              /**
               * @maxLength 500
               */
              borough?: string;
              /**
               * @maxLength 500
               */
              addressLine1?: string;
              /**
               * @maxLength 500
               */
              addressLine2?: string;
              /**
               * @maxLength 500
               */
              addressLine3?: string;
            };
            /**
             * @maxItems 25
             */
            occupation?: string[];
            /**
             * @maxLength 500
             */
            specialism?: string;
            /**
             * @maxLength 500
             */
            _internalId?: string;
            /**
             * @maxItems 25
             */
            complianceUser?: string[];
            /**
             * @maxItems 25
             */
            assignee?: string[];
            /**
             * @maxItems 25
             */
            watcher?: string[];
            /**
             * @maxItems 25
             */
            team?: string[];
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            contactId?: string;
            currentBooking?: "true" | "false";
            bookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            notBookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            tags?: string[];
            location?: {
              lat?: number | null;
              lng?: number | null;
              radius?: number | null;
            };
            statusChangeDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            lastContactedDateFilter?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            employmentPreference?: (
              | "Day To Day"
              | "Long Term"
              | "Fixed Term"
              | "Perm"
            )[];
            /**
             * @maxItems 25
             */
            transport?: (
              | "Drives Own Vehicle"
              | "Use Public Transport"
              | "Access to Another Vehicle"
              | "Bicycle"
              | "No Access"
              | "Other"
            )[];
            /**
             * @maxLength 500
             */
            trnNumber?: string;
            /**
             * @maxLength 500
             */
            desiredLocation?: string;
            /**
             * @maxItems 25
             */
            subjectToNotice?: ("Unsure" | "Yes" | "No")[];
            /**
             * @maxItems 25
             */
            title?: (
              | "Mr"
              | "Mrs"
              | "Ms"
              | "Miss"
              | "Dr"
              | "Mx"
              | "Lady"
              | "Lord"
              | "Other"
            )[];
            /**
             * @maxItems 25
             */
            sex?: ("Male" | "Female" | "Other" | "Unknown")[];
            /**
             * @maxItems 25
             */
            pronoun?: (
              | "She/Her/Hers"
              | "He/Him/His"
              | "They/Them/Theirs"
              | "Ze/Hir/Hirs"
              | "Ze/Zir/Zirs"
              | "Other"
              | "Prefer not to say"
            )[];
            /**
             * @maxItems 25
             */
            roleType?: (
              | "Senior Leadership Positions"
              | "Teaching Roles"
              | "School Support Positions"
            )[];
            /**
             * @maxItems 25
             */
            payType?: (
              | "PAYE"
              | "Umbrella"
              | "Limited Company"
              | "Not Applicable"
            )[];
            /**
             * @maxItems 25
             */
            payInterval?: (
              | "Monthly"
              | "Weekly"
              | "Weekly Pay in Arrears"
              | "Bi-Weekly"
            )[];
            /**
             * @maxItems 25
             */
            dbsType?: (
              | "Basic"
              | "Standard"
              | "Enhanced"
              | "Enhanced with Barred List"
              | "Child workforce"
              | "Child and adult workforce"
              | "Adult workforce"
            )[];
            source?: (
              | (
                  | "Company Website"
                  | "iCan Teach UK Website"
                  | "Apply to Education"
                  | "CV Library - CV Search"
                  | "CV Library Advert"
                  | "CV Library - Watchdog"
                  | "Guardian- CV Search"
                  | "Guardian Advert"
                  | "Indeed - CV Search"
                  | "Indeed Advert"
                  | "Reed - CV Search"
                  | "Reed Advert"
                  | "Reed \u2013 Watchdog"
                  | "SENploy - Advert"
                  | "Total Jobs"
                  | "Other Job Board"
                  | "Google"
                  | "LinkedIn"
                  | "Linkedin Recruiter"
                  | "LinkedIn Advert"
                  | "Referral - School"
                  | "Referral - Candidate"
                  | "UK University"
                  | "Irish University"
                  | "Canadian University"
                  | "Marketing Campaign"
                  | "Woo"
                  | "Job Show"
                  | "Recruitment Fair"
                  | "Career Fair"
                  | "Volcanic"
                  | "Broadbean"
                  | "Headhunt"
                  | "Referral"
                  | "Direct"
                  | "Other"
                )
              | (
                  | "Company Website"
                  | "Direct Call"
                  | "CV Library Watchdog"
                  | "CV Library Advert"
                  | "CV Library CV Search"
                  | "Reed Watchdog"
                  | "Reed Advert"
                  | "Reed CV Search"
                  | "Indeed Watchdog"
                  | "Indeed Advert"
                  | "Indeed CV Search"
                  | "Job Centre"
                  | "Returning Candidate"
                  | "Referral Candidate"
                  | "Referral Client"
                  | "Training Provider"
                  | "Recruitment Fair"
                  | "University/College"
                )
            )[];
            /**
             * @maxLength 32
             */
            telephone?: string;
            /**
             * @maxLength 500
             */
            previousNames?: string;
            /**
             * @maxLength 500
             */
            otherNames?: string;
            dob?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            trnNumberCheckedDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            dbsLastChecked?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            dbsUpdateServiceCheckDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            rtwVerificationDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            section128CheckedDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            prohibitionCheckDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            createdAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            updatedAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            candidateMobileAppStatus?: (
              | "Unlinked"
              | "Scheduled Unlink"
              | "Linked"
              | "Invited"
              | "Not Invited"
            )[];
            recAuditConsent?: "true" | "false";
          };
        };
        presentation?: {
          mode?: "table" | "availability";
          availability?: {
            /**
             * An absolute or relative date range. Relative boundaries take precedence when supplied.
             */
            period?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 5
             */
            statuses?: (
              | "available"
              | "unavailable"
              | "booked"
              | "awaiting"
              | "notRecorded"
            )[];
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            draftClient?: string;
            draftMode?: "week" | "all";
          };
        };
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved client search.
         */
        type?: "clients";
        /**
         * The client query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * @maxLength 256
           */
          search?: string;
          sort?:
            | "name"
            | "-name"
            | "createdAt"
            | "-createdAt"
            | "assignee"
            | "-assignee"
            | "telephone"
            | "-telephone"
            | "email"
            | "-email"
            | "address.postcode"
            | "-address.postcode"
            | "address.county"
            | "-address.county"
            | "lea"
            | "-lea"
            | "type"
            | "-type"
            | "multiAcademyTrust"
            | "-multiAcademyTrust"
            | "address.city"
            | "-address.city"
            | "borough"
            | "-borough"
            | "specialism"
            | "-specialism"
            | "internalId"
            | "-internalId"
            | "lastContactedDate"
            | "-lastContactedDate"
            | "distance"
            | "-distance";
          select?: (
            | "id"
            | "name"
            | "address.addressLine1"
            | "address.addressLine2"
            | "address.addressLine3"
            | "address.city"
            | "address.county"
            | "address.borough"
            | "address.postcode"
            | "address.country"
            | "address.lat"
            | "address.lng"
            | "address"
            | "telephone"
            | "email"
            | "contacts"
            | "borough"
            | "lea"
            | "type"
            | "multiAcademyTrust"
            | "color"
            | "customId"
            | "internalId"
            | "assignee"
            | "assignee.id"
            | "assignee.email"
            | "assignee.firstName"
            | "assignee.lastName"
            | "assignee.username"
            | "assignee.lea"
            | "assignee.clients"
            | "assignee.position"
            | "assignee.permissions"
            | "assignee.activationCode"
            | "assignee.customer"
            | "assignee.disabled"
            | "assignee.commissionThreshold"
            | "assignee.branches"
            | "assignee.displayOnboarding"
            | "assignee._customId"
            | "assignee.signature"
            | "assignee.phone"
            | "assignee.defaultCandidateWatcher"
            | "assignee.defaultTaskWorkspace"
            | "assignee.teamAdmin"
            | "assignee.contact"
            | "assignee.contact.id"
            | "assignee.contact.forename"
            | "assignee.contact.surname"
            | "assignee.contact.email"
            | "assignee.contact.telephone"
            | "assignee.contact.mobile"
            | "assignee.contact.extension"
            | "assignee.createdAt"
            | "assignee.evertime.id"
            | "assignee.evertime"
            | "assignee.timesheetPortal.id"
            | "assignee.timesheetPortal.code"
            | "assignee.timesheetPortal"
            | "tags"
            | "tobSentAt"
            | "website"
            | "archived"
            | "specialism"
            | "currentBooking"
            | "bookingCount"
            | "importantNotes"
            | "roles"
            | "startTime"
            | "endTime"
            | "additionalInstructions"
            | "additionalInstructionsClient"
            | "highlight"
            | "area"
            | "governance"
            | "mathq"
            | "ofstedRating"
            | "dateOfstedReportChecked"
            | "boarding"
            | "licenceToIssueSponsorship"
            | "agencySpendTotal"
            | "vatExempt"
            | "splitInvoices"
            | "switchBoard"
            | "ccsClient"
            | "ccsClientUrn"
            | "ccsClientOfstedUrn"
            | "ccsClientLotNumber"
            | "buyerReference"
            | "ccsClientInvoiceDate"
            | "keyStage"
            | "contact"
            | "contact.id"
            | "contact.forename"
            | "contact.surname"
            | "contact.email"
            | "contact.telephone"
            | "contact.mobile"
            | "contact.extension"
            | "lastContactedDate"
            | "relevanceScore"
            | "matchReasons"
            | "distance"
            | "migrationId"
            | "migrationApproval"
            | "evertime.id"
            | "evertime.contacts"
            | "evertime.locations"
            | "evertime.nameMatch"
            | "evertime"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "timesheetPortal"
            | ""
          )[];
          /**
           * @maxItems 25
           */
          expand?: ("assignee" | "tags")[];
          include?: "bookings"[];
          filter?: {
            /**
             * @maxItems 50
             */
            ids?: string[];
            /**
             * Filter clients by name.
             *
             * @maxLength 256
             */
            name?: string;
            /**
             * Filter clients by telephone number.
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * Filter clients by email address.
             *
             * @maxLength 256
             */
            email?: string;
            /**
             * Address fields used to filter clients. Multiple supplied fields are combined.
             */
            address?: {
              /**
               * Filter clients by postcode.
               *
               * @maxLength 32
               */
              postcode?: string;
              /**
               * Filter clients by county.
               *
               * @maxLength 128
               */
              county?: string;
              /**
               * Filter clients by city.
               *
               * @maxLength 128
               */
              city?: string;
            };
            /**
             * Filter clients by local education authority.
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Filter clients by type.
             *
             * @maxLength 128
             */
            type?: string;
            /**
             * Filter clients by multi academy trust.
             *
             * @maxLength 256
             */
            multiAcademyTrust?: string;
            /**
             * Filter clients by borough.
             *
             * @maxLength 128
             */
            borough?: string;
            /**
             * Filter clients by specialism.
             *
             * @maxItems 25
             */
            specialism?: string[];
            /**
             * Filter clients by their company-defined identifier.
             *
             * @maxLength 256
             */
            internalId?: string;
            /**
             * Filter clients by whether they are a GCA client.
             */
            ccsClient?: "true" | "false";
            /**
             * Filter clients by GCA client URN.
             *
             * @maxLength 256
             */
            ccsClientUrn?: string;
            /**
             * Filter clients by assigned users.
             *
             * @maxItems 25
             */
            assignee?: string[];
            /**
             * Filter clients by last contact date.
             */
            lastContactedDateFilter?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients with an active booking overlapping an absolute or relative date range.
             */
            bookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients without an active booking overlapping an absolute or relative date range.
             */
            notBookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients by tags they have.
             *
             * @maxItems 25
             */
            tags?: string[];
            location?: {
              /**
               * @minimum -90
               * @maximum 90
               */
              lat?: number | null;
              /**
               * @minimum -180
               * @maximum 180
               */
              lng?: number | null;
              /**
               * @minimum 0
               * @maximum 500
               */
              radius?: number | null;
            };
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            ["contacts.contactId"]?: string;
          };
        };
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved Client Contact search.
         */
        type?: "clientContacts";
        /**
         * The Client Contact query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * Find relevant Client contacts using contact and Client details.
           *
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * Search Client contacts by name, email, or Client.
           *
           * @maxLength 256
           */
          search?: string;
          /**
           * The field used to order contacts. Prefix with - for descending order.
           */
          sort?:
            | "forename"
            | "-forename"
            | "surname"
            | "-surname"
            | "email"
            | "-email"
            | "telephone"
            | "-telephone"
            | "role"
            | "-role"
            | "status"
            | "-status"
            | "decisionMaker"
            | "-decisionMaker"
            | "clientName"
            | "-clientName"
            | "clientAddress.city"
            | "-clientAddress.city"
            | "clientAddress.county"
            | "-clientAddress.county"
            | "clientAddress.postcode"
            | "-clientAddress.postcode"
            | "clientLea"
            | "-clientLea"
            | "clientType"
            | "-clientType"
            | "clientMultiAcademyTrust"
            | "-clientMultiAcademyTrust"
            | "clientBorough"
            | "-clientBorough"
            | "clientAssignee"
            | "-clientAssignee"
            | "clientSpecialism"
            | "-clientSpecialism"
            | "clientUpdatedAt"
            | "-clientUpdatedAt"
            | "distance"
            | "-distance"
            | "relevanceScore"
            | "-relevanceScore";
          /**
           * The fields to include in the response in dot notation format.
           *
           * @maxItems 250
           */
          select?: (
            | "id"
            | "forename"
            | "surname"
            | "email"
            | "telephone"
            | "mobile"
            | "extension"
            | "role"
            | "roleOther"
            | "contactId"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "timesheetPortal"
            | "optOut"
            | "note"
            | "decisionMaker"
            | "status"
            | "clientId"
            | "clientName"
            | "clientAddress.addressLine1"
            | "clientAddress.addressLine2"
            | "clientAddress.addressLine3"
            | "clientAddress.city"
            | "clientAddress.county"
            | "clientAddress.borough"
            | "clientAddress.postcode"
            | "clientAddress.country"
            | "clientAddress.lat"
            | "clientAddress.lng"
            | "clientAddress"
            | "clientLea"
            | "clientType"
            | "clientMultiAcademyTrust"
            | "clientBorough"
            | "clientAssignee"
            | "clientAssignee.id"
            | "clientAssignee.email"
            | "clientAssignee.firstName"
            | "clientAssignee.lastName"
            | "clientAssignee.username"
            | "clientAssignee.lea"
            | "clientAssignee.clients"
            | "clientAssignee.position"
            | "clientAssignee.permissions"
            | "clientAssignee.activationCode"
            | "clientAssignee.customer"
            | "clientAssignee.disabled"
            | "clientAssignee.commissionThreshold"
            | "clientAssignee.branches"
            | "clientAssignee.displayOnboarding"
            | "clientAssignee._customId"
            | "clientAssignee.signature"
            | "clientAssignee.phone"
            | "clientAssignee.defaultCandidateWatcher"
            | "clientAssignee.defaultTaskWorkspace"
            | "clientAssignee.teamAdmin"
            | "clientAssignee.contact"
            | "clientAssignee.contact.id"
            | "clientAssignee.contact.forename"
            | "clientAssignee.contact.surname"
            | "clientAssignee.contact.email"
            | "clientAssignee.contact.telephone"
            | "clientAssignee.contact.mobile"
            | "clientAssignee.contact.extension"
            | "clientAssignee.createdAt"
            | "clientAssignee.evertime.id"
            | "clientAssignee.evertime"
            | "clientAssignee.timesheetPortal.id"
            | "clientAssignee.timesheetPortal.code"
            | "clientAssignee.timesheetPortal"
            | "clientSpecialism"
            | "clientUpdatedAt"
            | "distance"
            | "relevanceScore"
            | "matchReasons"
            | ""
          )[];
          /**
           * Related fields to include as objects in the response.
           *
           * @maxItems 1
           */
          expand?: "clientAssignee"[];
          /**
           * Filter Client contacts using contact and associated Client fields.
           */
          filter?: {
            /**
             * Filter contacts by status.
             *
             * @maxItems 3
             */
            status?: ("Active" | "Passive" | "Inactive")[];
            /**
             * Filter contacts by role.
             *
             * @maxItems 44
             */
            role?: (
              | "Head Teacher"
              | "Deputy Head"
              | "Cover Manager"
              | "HR Manager"
              | "Finance Manager"
              | "Head of Maths"
              | "Head of Science"
              | "Head of English"
              | "Assistant Headteacher"
              | "Headteacher's PA"
              | "SENCO"
              | "Other"
              | "Business Manager"
              | "Business Administrator"
              | "Invoice Contact"
              | "Academy Recruitment Contact"
              | "Nursery Manager"
              | "CEO"
              | "COO"
              | "Executive Headteacher"
              | "Inclusion Manager"
              | "EYFS Lead"
              | "Admin Team"
              | "Head of Humanities"
              | "Head of History"
              | "Head of Geography"
              | "Head of Business Studies"
              | "Head of PE"
              | "Head of KS1"
              | "Head of KS2"
              | "Head of KS3"
              | "Head of KS4"
              | "Virtual School Headteacher"
              | "Deputy Virtual School Headteacher"
              | "Post-16 Team Leader"
              | "SEN Team"
              | "Inclusion Team"
              | "UASC Team"
              | "Caseworker"
              | "Designated Teacher"
              | "Attendance Officer"
              | "Coordinator"
              | "Children's Commissioner"
              | "Timesheet Contact"
            )[];
            /**
             * Filter contacts by forename.
             *
             * @maxLength 128
             */
            forename?: string;
            /**
             * Filter contacts by surname.
             *
             * @maxLength 128
             */
            surname?: string;
            /**
             * Filter contacts by email address.
             *
             * @maxLength 256
             */
            email?: string;
            /**
             * Filter contacts by decision-maker status.
             */
            decisionMaker?: "true" | "false";
            /**
             * Filter contacts by opt-out status.
             */
            optOut?: "true" | "false";
            /**
             * Filter contacts by Client.
             *
             * @maxItems 50
             */
            clientId?: string[];
            /**
             * Filter contacts by Client name.
             *
             * @maxLength 256
             */
            clientName?: string;
            /**
             * Filter contacts by associated Client address fields.
             */
            clientAddress?: {
              /**
               * @maxLength 128
               */
              city?: string;
              /**
               * @maxLength 128
               */
              county?: string;
              /**
               * @maxLength 32
               */
              postcode?: string;
            };
            /**
             * Filter contacts by Client local education authority.
             *
             * @maxLength 128
             */
            clientLea?: string;
            /**
             * Filter contacts by Client sector.
             *
             * @maxLength 128
             */
            clientType?: string;
            /**
             * Filter contacts by Client multi-academy trust.
             *
             * @maxLength 256
             */
            clientMultiAcademyTrust?: string;
            /**
             * Filter contacts by Client borough.
             *
             * @maxLength 128
             */
            clientBorough?: string;
            /**
             * Filter contacts by Client assignee.
             *
             * @maxItems 25
             */
            clientAssignee?: string[];
            /**
             * Filter contacts by Client specialism.
             *
             * @maxItems 25
             */
            clientSpecialism?: string[];
            /**
             * Filter contacts by when their Client was updated.
             */
            clientUpdatedAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter contacts by distance from their Client address.
             */
            location?: {
              /**
               * @minimum -90
               * @maximum 90
               */
              lat?: number | null;
              /**
               * @minimum -180
               * @maximum 180
               */
              lng?: number | null;
              /**
               * @minimum 0
               * @exclusiveMinimum true
               * @maximum 500
               */
              radius?: number;
            };
          };
        };
      };
};

export type GetSavedSearchVariables = {
  pathParams: GetSavedSearchPathParams;
  queryParams?: GetSavedSearchQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetSavedSearch = (
  variables: GetSavedSearchVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetSavedSearchResponse,
    GetSavedSearchError,
    undefined,
    {},
    GetSavedSearchQueryParams,
    GetSavedSearchPathParams
  >({
    url: "/saved-searches/{savedSearchId}",
    method: "get",
    ...variables,
    signal,
  });

export function getSavedSearchQuery(variables: GetSavedSearchVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetSavedSearchResponse>;
};

export function getSavedSearchQuery(
  variables: GetSavedSearchVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetSavedSearchResponse>)
    | reactQuery.SkipToken;
};

export function getSavedSearchQuery(
  variables: GetSavedSearchVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/saved-searches/{savedSearchId}",
      operationId: "getSavedSearch",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetSavedSearch(variables, signal),
  };
}

export const useSuspenseGetSavedSearch = <TData = GetSavedSearchResponse,>(
  variables: GetSavedSearchVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetSavedSearchResponse,
      GetSavedSearchError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetSavedSearchResponse,
    GetSavedSearchError,
    TData
  >({
    ...getSavedSearchQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetSavedSearch = <TData = GetSavedSearchResponse,>(
  variables: GetSavedSearchVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetSavedSearchResponse,
      GetSavedSearchError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetSavedSearchResponse,
    GetSavedSearchError,
    TData
  >({
    ...getSavedSearchQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateSavedSearchPathParams = {
  /**
   * The unique identifier of the saved search.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  savedSearchId: string;
};

export type UpdateSavedSearchError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 409;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateSavedSearchResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data:
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved candidate search.
         */
        type?: "candidates";
        /**
         * The candidate query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * @maxLength 256
           */
          search?: string;
          sort?:
            | (
                | "relevanceScore"
                | "-relevanceScore"
                | "forename"
                | "-forename"
                | "surname"
                | "-surname"
                | "mobile"
                | "-mobile"
                | "email"
                | "-email"
                | "address.city"
                | "-address.city"
                | "address.postcode"
                | "-address.postcode"
                | "address.county"
                | "-address.county"
                | "address.borough"
                | "-address.borough"
                | "address.addressLine1"
                | "-address.addressLine1"
                | "address.addressLine2"
                | "-address.addressLine2"
                | "address.addressLine3"
                | "-address.addressLine3"
                | "subject"
                | "-subject"
                | "statusChangeDate"
                | "-statusChangeDate"
                | "lastContactedDate"
                | "-lastContactedDate"
                | "daysSinceLastContactDisplay"
                | "-daysSinceLastContactDisplay"
                | "status"
                | "-status"
                | "occupation"
                | "-occupation"
                | "dob"
                | "-dob"
                | "trnNumberCheckedDate"
                | "-trnNumberCheckedDate"
                | "criminalRecord.dbsLastChecked"
                | "-criminalRecord.dbsLastChecked"
                | "criminalRecord.updateServiceCheckDate"
                | "-criminalRecord.updateServiceCheckDate"
                | "rtwVerification"
                | "-rtwVerification"
                | "criminalRecord.section128Checked"
                | "-criminalRecord.section128Checked"
                | "criminalRecord.prohibitionCheck"
                | "-criminalRecord.prohibitionCheck"
                | "createdAt"
                | "-createdAt"
                | "updatedAt"
                | "-updatedAt"
                | "_internalId"
                | "-_internalId"
                | "trnNumber"
                | "-trnNumber"
                | "desiredLocation"
                | "-desiredLocation"
                | "telephone"
                | "-telephone"
                | "previousNames"
                | "-previousNames"
                | "otherNames"
                | "-otherNames"
                | "location"
                | "-location"
              )
            | ("availabilityPriority" | "-availabilityPriority");
          /**
           * @maxItems 250
           */
          select?: (
            | "id"
            | "forename"
            | "surname"
            | "previousNames"
            | "email"
            | "telephone"
            | "mobile"
            | "occupation"
            | "subject"
            | "address.addressLine1"
            | "address.addressLine2"
            | "address.addressLine3"
            | "address.city"
            | "address.county"
            | "address.borough"
            | "address.postcode"
            | "address.country"
            | "address.lat"
            | "address.lng"
            | "qualifications"
            | "employment"
            | "references"
            | "referenceCheckConsent"
            | "referenceCheckConsentName"
            | "referenceCheckConsentDate"
            | "dob"
            | "sex"
            | "status"
            | "onboardingStatus"
            | "onboardingSent"
            | "nextOfKin.nextOfKinName"
            | "nextOfKin.nextOfKinRelationship"
            | "nextOfKin.nextOfKinTelephone"
            | "nextOfKin.nextOfKinEmail"
            | "nextOfKin.nextOfKinAddress.addressLine1"
            | "nextOfKin.nextOfKinAddress.addressLine2"
            | "nextOfKin.nextOfKinAddress.addressLine3"
            | "nextOfKin.nextOfKinAddress.city"
            | "nextOfKin.nextOfKinAddress.county"
            | "nextOfKin.nextOfKinAddress.borough"
            | "nextOfKin.nextOfKinAddress.postcode"
            | "nextOfKin.nextOfKinAddress.country"
            | "nextOfKin.nextOfKinAddress.lat"
            | "nextOfKin.nextOfKinAddress.lng"
            | "criminalRecord.dbsHeld"
            | "criminalRecord.dbsNumber"
            | "criminalRecord.dbsType"
            | "criminalRecord.dbsLastChecked"
            | "criminalRecord.dbsUpdateServiceNumber"
            | "criminalRecord.prohibitionCheck"
            | "criminalRecord.overseasCheck"
            | "criminalRecord.overseasCheckNote"
            | "criminalRecord.convictedOffence"
            | "criminalRecord.convictedOffenceDescription"
            | "criminalRecord.convictions"
            | "criminalRecord.convictionsDescription"
            | "criminalRecord.registeredToUpdateService"
            | "criminalRecord.consentUpdateServiceChecks"
            | "criminalRecord.note"
            | "criminalRecord.updateServiceCheckDate"
            | "criminalRecord.abroad"
            | "criminalRecord.overseasDeclarationExtended"
            | "criminalRecord.educationalDisciplinaryProceedings"
            | "criminalRecord.dbsComments"
            | "criminalRecord.dbsDeclarationRead"
            | "criminalRecord.overseasOffence"
            | "criminalRecord.overseasOffenceDescription"
            | "criminalRecord.currentCriminalOffence"
            | "criminalRecord.currentCriminalOffenceDescription"
            | "criminalRecord.dbsIssuedByBusiness"
            | "criminalRecord.dbsIssuedByCurrentEmployer"
            | "criminalRecord.section128Required"
            | "criminalRecord.section128Checked"
            | "criminalRecordDeclaration.questions"
            | "criminalRecordDeclaration.answers"
            | "criminalRecordDeclaration.submittedAt"
            | "criminalRecordDeclaration.submittedIp"
            | "criminalRecordDeclaration.configurationUpdatedAt"
            | "criminalRecordCheckConsent"
            | "criminalRecordCheckConsentName"
            | "criminalRecordCheckConsentDate"
            | "safeInEducation.barred"
            | "safeInEducation.barredCheckDate"
            | "safeInEducation.waiver"
            | "safeInEducation.ordersYou"
            | "safeInEducation.ordersChild"
            | "safeInEducation.previousSafeguardingAllegations"
            | "safeInEducation.cautioned"
            | "safeInEducation.violentOffence"
            | "safeInEducation.anotherCountry"
            | "safeInEducation.readKCSIE"
            | "safeInEducation.readChildcareAct"
            | "safeInEducation.prohibitedFromTeaching"
            | "safeInEducation.knownPolice"
            | "safeInEducation.prohibitedFromManagement"
            | "safeInEducation.criminalOffencesLaw"
            | "safeInEducation.safeInEducationDetails"
            | "safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.signOfAbuse"
            | "safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
            | "safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
            | "safeInEducation.safeguardingQuestionaire.fgmDefinition"
            | "safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
            | "safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
            | "safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
            | "safeInEducation.safeguardingQuestionaire.studentConfides"
            | "socialMedia.facebook"
            | "socialMedia.instagram"
            | "socialMedia.twitter"
            | "socialMedia.linkedIn"
            | "socialMedia.tiktok"
            | "socialMedia.others"
            | "socialMedia.socialMediaChecked"
            | "socialMedia.googleCheckedDate"
            | "safeInEducationCheckConsent"
            | "safeInEducationCheckConsentName"
            | "safeInEducationCheckConsentDate"
            | "healthRecord.physicalAbilityCondition"
            | "healthRecord.confused"
            | "healthRecord.hearing"
            | "healthRecord.eyesight"
            | "healthRecord.depression"
            | "healthRecord.severePain"
            | "healthRecord.excessiveDrowsiness"
            | "healthRecord.epilepsy"
            | "healthRecord.heart"
            | "healthRecord.asthma"
            | "healthRecord.mentalHealth"
            | "healthRecord.expectantMother"
            | "healthRecord.allergies"
            | "healthRecord.medication"
            | "healthRecord.healthIssues"
            | "healthRecord.healthIssuesDescription"
            | "healthRecord.disabilityNeeds"
            | "healthRecord.contagiousDiseases"
            | "healthRecord.affectAbility"
            | "healthRecord.notificationAgree"
            | "healthRecord.sasOptIn"
            | "healthRecord.signature"
            | "healthRecord.drawnSignature"
            | "healthRecord.signatureDate"
            | "healthRecord.vaccinationMeasles"
            | "healthRecord.vaccinationMumps"
            | "healthRecord.vaccinationRubella"
            | "healthRecord.vaccinationVaricella"
            | "healthRecord.vaccinationTuberculosis"
            | "healthRecord.vaccinationHepatitisB"
            | "healthRecord.vaccinationHepatitisC"
            | "healthRecord.vaccinationHIV"
            | "healthRecord.vaccinationCovid"
            | "healthRecord.vaccinationCovid1"
            | "healthRecord.vaccinationCovid2"
            | "healthRecord.vaccinationCovidBooster"
            | "healthRecord.vaccinationCovidInfluenza"
            | "healthRecord.fitToWorkOverride"
            | "healthDeclaration.questions"
            | "healthDeclaration.answers"
            | "healthDeclaration.submittedAt"
            | "healthDeclaration.submittedIp"
            | "healthDeclaration.configurationUpdatedAt"
            | "healthRecordCheckConsent"
            | "healthRecordCheckConsentName"
            | "healthRecordCheckConsentDate"
            | "bankDetails.bankAccountName"
            | "bankDetails.bankName"
            | "bankDetails.accountNumber"
            | "bankDetails.sortCode"
            | "bankDetails.niNumber"
            | "bankDetails.jobOp1"
            | "bankDetails.jobOp2"
            | "bankDetails.jobOp3"
            | "bankDetails.studentLoan.studentLoan"
            | "bankDetails.studentLoan.plan1"
            | "bankDetails.studentLoan.plan2"
            | "bankDetails.studentLoan.plan4"
            | "bankDetails.studentLoan.postGradLoan"
            | "declarationCheckConsent"
            | "declarationCheckConsentName"
            | "declarationCheckConsentDate"
            | "contractForServiceConsent"
            | "contractForServiceName"
            | "contractForServiceDate"
            | "confirmed"
            | "createdBy"
            | "customerId"
            | "migrationId"
            | "migrationApproval"
            | "assignee"
            | "assignee.id"
            | "assignee.email"
            | "assignee.firstName"
            | "assignee.lastName"
            | "assignee.username"
            | "assignee.lea"
            | "assignee.clients"
            | "assignee.position"
            | "assignee.permissions"
            | "assignee.activationCode"
            | "assignee.customer"
            | "assignee.disabled"
            | "assignee.commissionThreshold"
            | "assignee.branches"
            | "assignee.displayOnboarding"
            | "assignee._customId"
            | "assignee.signature"
            | "assignee.phone"
            | "assignee.defaultCandidateWatcher"
            | "assignee.defaultTaskWorkspace"
            | "assignee.teamAdmin"
            | "assignee.contact"
            | "assignee.contact.id"
            | "assignee.contact.forename"
            | "assignee.contact.surname"
            | "assignee.contact.email"
            | "assignee.contact.telephone"
            | "assignee.contact.mobile"
            | "assignee.contact.extension"
            | "assignee.createdAt"
            | "assignee.evertime.id"
            | "assignee.timesheetPortal.id"
            | "assignee.timesheetPortal.code"
            | "currentBooking"
            | "profilePhoto"
            | "payType"
            | "payInterval"
            | "payRate"
            | "customId"
            | "_internalId"
            | "statusChangeDate"
            | "optOut"
            | "trnNumber"
            | "trnNumberCheckedDate"
            | "trnNumberNotes"
            | "employmentStartDate"
            | "ewcConfirmation"
            | "homeOfficeShareCode"
            | "archived"
            | "umbrella"
            | "payeUmbrella"
            | "umbrellaCompanyName"
            | "umbrellaCompanyCode"
            | "transport"
            | "importantNotes"
            | "candidateLastUpdated"
            | "source"
            | "sourceOther"
            | "onlineCheckConsent"
            | "title"
            | "pronoun"
            | "updatedAt"
            | "lastContactedDate"
            | "highlight"
            | "teacherPayScale"
            | "p45"
            | "roleType"
            | "rtwVerification"
            | "rtwVerificationNote"
            | "rtwVerificationStatus"
            | "desiredLocation"
            | "complianceUser"
            | "complianceUser.id"
            | "complianceUser.email"
            | "complianceUser.firstName"
            | "complianceUser.lastName"
            | "complianceUser.username"
            | "complianceUser.lea"
            | "complianceUser.clients"
            | "complianceUser.position"
            | "complianceUser.permissions"
            | "complianceUser.activationCode"
            | "complianceUser.customer"
            | "complianceUser.disabled"
            | "complianceUser.commissionThreshold"
            | "complianceUser.branches"
            | "complianceUser.displayOnboarding"
            | "complianceUser._customId"
            | "complianceUser.signature"
            | "complianceUser.phone"
            | "complianceUser.defaultCandidateWatcher"
            | "complianceUser.defaultTaskWorkspace"
            | "complianceUser.teamAdmin"
            | "complianceUser.contact"
            | "complianceUser.contact.id"
            | "complianceUser.contact.forename"
            | "complianceUser.contact.surname"
            | "complianceUser.contact.email"
            | "complianceUser.contact.telephone"
            | "complianceUser.contact.mobile"
            | "complianceUser.contact.extension"
            | "complianceUser.createdAt"
            | "complianceUser.evertime.id"
            | "complianceUser.timesheetPortal.id"
            | "complianceUser.timesheetPortal.code"
            | "watchers"
            | "tags"
            | "documents"
            | "documentsCount"
            | "desiredLocationMultiple"
            | "availabilityDays"
            | "availabilityLastChecked"
            | "subjectToNotice"
            | "subsidiarySubject"
            | "employmentPreference"
            | "otherNames"
            | "ltdCompany"
            | "employmentVerified"
            | "candidateDBSVerification"
            | "candidateDBSVerificationNote"
            | "candidateDBSVerificationStatus"
            | "candidateDBSCheckVerification"
            | "candidateDBSCheckVerificationNote"
            | "candidateDBSCheckVerificationStatus"
            | "candidateDBSUpdateServiceVerificationNote"
            | "candidateDBSUpdateServiceVerificationStatus"
            | "candidateTRNVerificationNote"
            | "candidateTRNVerificationStatus"
            | "candidateHealthSafetyVerification"
            | "candidateHealthSafetyVerificationNote"
            | "candidateHealthSafetyVerificationStatus"
            | "onboardingSession"
            | "onboardingSession.id"
            | "onboardingSession.url"
            | "onboardingSession.healthDeclarationConfig.questions"
            | "onboardingSession.healthDeclarationConfig.updatedAt"
            | "onboardingSession.criminalRecordConfig.questions"
            | "onboardingSession.criminalRecordConfig.updatedAt"
            | "contact"
            | "contact.id"
            | "contact.forename"
            | "contact.surname"
            | "contact.email"
            | "contact.telephone"
            | "contact.mobile"
            | "contact.extension"
            | "optOut48Hours"
            | "recAuditConsent"
            | "recAuditConsentDate"
            | "recAuditConsentMethod"
            | "createdAt"
            | "candidateMobileAppStatus"
            | "evertime.id"
            | "evertime.niNumberMatch"
            | "relevanceScore"
            | "matchReasons"
            | "distance"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "mobileAppLinkInvite.isUsed"
            | "mobileAppLinkInvite.createdAt"
            | "mobileAppLinkInvite.usedBy"
            | "mobileAppLinkInvite.usedAt"
            | "mobileAppLinkInvite.unlinkedBy"
            | "mobileAppLinkInvite.unlinkedAt"
            | "mobileAppLinkInvite.deletedBy"
            | "mobileAppLinkInvite.deletedAt"
            | "mobileAppLinkInvite.status"
          )[];
          /**
           * @maxItems 100
           */
          expand?: (
            | "assignee"
            | "contact"
            | "customer"
            | "umbrella"
            | "payeUmbrella"
            | "complianceUser"
            | "onboardingSession"
            | "watchers"
            | "tags"
            | "documents"
          )[];
          filter?: {
            /**
             * Inclusive calendar dates, up to 31 days. Both boundaries are required when selecting availability days, filtering availability statuses or sorting by availability priority.
             */
            availabilityPeriod?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Match candidates with any selected status during the availability period. Requires an availability period. Empty selects all statuses.
             *
             * @maxItems 5
             */
            availabilityStatuses?: (
              | "available"
              | "unavailable"
              | "booked"
              | "awaiting"
              | "notRecorded"
            )[];
            /**
             * @maxItems 50
             */
            ids?: string[];
            /**
             * @maxItems 25
             */
            status?: (
              | "Pending"
              | "Approved"
              | "Rejected"
              | "Anonymised"
              | "Archived"
              | "Enquired"
              | "Hold"
              | "Future"
              | "DNC"
              | "Pre"
            )[];
            /**
             * @maxItems 25
             */
            onboardingStatus?: ("Not Started" | "In Progress" | "Completed")[];
            activeOnboardingSession?: boolean;
            onboardingSent?: "true" | "false";
            section128Required?: "true" | "false";
            confirmed?: "true" | "false";
            optOut?: "true" | "false";
            optOut48Hours?: "true" | "false";
            fitToWorkOverride?: "true" | "false";
            affectAbility?: "true" | "false";
            ewcConfirmation?: "true" | "false";
            dbsHeld?: "true" | "false";
            dbsRegisteredToUpdateService?: "true" | "false";
            /**
             * @maxLength 500
             */
            firstName?: string;
            /**
             * @maxLength 500
             */
            lastName?: string;
            /**
             * @maxLength 500
             */
            email?: string;
            /**
             * @maxLength 500
             */
            mobile?: string;
            /**
             * Address fields used to filter candidates. Multiple supplied fields are combined.
             */
            address?: {
              /**
               * @maxLength 500
               */
              city?: string;
              /**
               * @maxLength 20
               */
              postcode?: string;
              /**
               * @maxLength 500
               */
              county?: string;
              /**
               * @maxLength 500
               */
              borough?: string;
              /**
               * @maxLength 500
               */
              addressLine1?: string;
              /**
               * @maxLength 500
               */
              addressLine2?: string;
              /**
               * @maxLength 500
               */
              addressLine3?: string;
            };
            /**
             * @maxItems 25
             */
            occupation?: string[];
            /**
             * @maxLength 500
             */
            specialism?: string;
            /**
             * @maxLength 500
             */
            _internalId?: string;
            /**
             * @maxItems 25
             */
            complianceUser?: string[];
            /**
             * @maxItems 25
             */
            assignee?: string[];
            /**
             * @maxItems 25
             */
            watcher?: string[];
            /**
             * @maxItems 25
             */
            team?: string[];
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            contactId?: string;
            currentBooking?: "true" | "false";
            bookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            notBookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            tags?: string[];
            location?: {
              lat?: number | null;
              lng?: number | null;
              radius?: number | null;
            };
            statusChangeDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            lastContactedDateFilter?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            employmentPreference?: (
              | "Day To Day"
              | "Long Term"
              | "Fixed Term"
              | "Perm"
            )[];
            /**
             * @maxItems 25
             */
            transport?: (
              | "Drives Own Vehicle"
              | "Use Public Transport"
              | "Access to Another Vehicle"
              | "Bicycle"
              | "No Access"
              | "Other"
            )[];
            /**
             * @maxLength 500
             */
            trnNumber?: string;
            /**
             * @maxLength 500
             */
            desiredLocation?: string;
            /**
             * @maxItems 25
             */
            subjectToNotice?: ("Unsure" | "Yes" | "No")[];
            /**
             * @maxItems 25
             */
            title?: (
              | "Mr"
              | "Mrs"
              | "Ms"
              | "Miss"
              | "Dr"
              | "Mx"
              | "Lady"
              | "Lord"
              | "Other"
            )[];
            /**
             * @maxItems 25
             */
            sex?: ("Male" | "Female" | "Other" | "Unknown")[];
            /**
             * @maxItems 25
             */
            pronoun?: (
              | "She/Her/Hers"
              | "He/Him/His"
              | "They/Them/Theirs"
              | "Ze/Hir/Hirs"
              | "Ze/Zir/Zirs"
              | "Other"
              | "Prefer not to say"
            )[];
            /**
             * @maxItems 25
             */
            roleType?: (
              | "Senior Leadership Positions"
              | "Teaching Roles"
              | "School Support Positions"
            )[];
            /**
             * @maxItems 25
             */
            payType?: (
              | "PAYE"
              | "Umbrella"
              | "Limited Company"
              | "Not Applicable"
            )[];
            /**
             * @maxItems 25
             */
            payInterval?: (
              | "Monthly"
              | "Weekly"
              | "Weekly Pay in Arrears"
              | "Bi-Weekly"
            )[];
            /**
             * @maxItems 25
             */
            dbsType?: (
              | "Basic"
              | "Standard"
              | "Enhanced"
              | "Enhanced with Barred List"
              | "Child workforce"
              | "Child and adult workforce"
              | "Adult workforce"
            )[];
            source?: (
              | (
                  | "Company Website"
                  | "iCan Teach UK Website"
                  | "Apply to Education"
                  | "CV Library - CV Search"
                  | "CV Library Advert"
                  | "CV Library - Watchdog"
                  | "Guardian- CV Search"
                  | "Guardian Advert"
                  | "Indeed - CV Search"
                  | "Indeed Advert"
                  | "Reed - CV Search"
                  | "Reed Advert"
                  | "Reed \u2013 Watchdog"
                  | "SENploy - Advert"
                  | "Total Jobs"
                  | "Other Job Board"
                  | "Google"
                  | "LinkedIn"
                  | "Linkedin Recruiter"
                  | "LinkedIn Advert"
                  | "Referral - School"
                  | "Referral - Candidate"
                  | "UK University"
                  | "Irish University"
                  | "Canadian University"
                  | "Marketing Campaign"
                  | "Woo"
                  | "Job Show"
                  | "Recruitment Fair"
                  | "Career Fair"
                  | "Volcanic"
                  | "Broadbean"
                  | "Headhunt"
                  | "Referral"
                  | "Direct"
                  | "Other"
                )
              | (
                  | "Company Website"
                  | "Direct Call"
                  | "CV Library Watchdog"
                  | "CV Library Advert"
                  | "CV Library CV Search"
                  | "Reed Watchdog"
                  | "Reed Advert"
                  | "Reed CV Search"
                  | "Indeed Watchdog"
                  | "Indeed Advert"
                  | "Indeed CV Search"
                  | "Job Centre"
                  | "Returning Candidate"
                  | "Referral Candidate"
                  | "Referral Client"
                  | "Training Provider"
                  | "Recruitment Fair"
                  | "University/College"
                )
            )[];
            /**
             * @maxLength 32
             */
            telephone?: string;
            /**
             * @maxLength 500
             */
            previousNames?: string;
            /**
             * @maxLength 500
             */
            otherNames?: string;
            dob?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            trnNumberCheckedDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            dbsLastChecked?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            dbsUpdateServiceCheckDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            rtwVerificationDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            section128CheckedDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            prohibitionCheckDate?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            createdAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            updatedAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 25
             */
            candidateMobileAppStatus?: (
              | "Unlinked"
              | "Scheduled Unlink"
              | "Linked"
              | "Invited"
              | "Not Invited"
            )[];
            recAuditConsent?: "true" | "false";
          };
        };
        presentation?: {
          mode?: "table" | "availability";
          availability?: {
            /**
             * An absolute or relative date range. Relative boundaries take precedence when supplied.
             */
            period?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * @maxItems 5
             */
            statuses?: (
              | "available"
              | "unavailable"
              | "booked"
              | "awaiting"
              | "notRecorded"
            )[];
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            draftClient?: string;
            draftMode?: "week" | "all";
          };
        };
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved client search.
         */
        type?: "clients";
        /**
         * The client query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * @maxLength 256
           */
          search?: string;
          sort?:
            | "name"
            | "-name"
            | "createdAt"
            | "-createdAt"
            | "assignee"
            | "-assignee"
            | "telephone"
            | "-telephone"
            | "email"
            | "-email"
            | "address.postcode"
            | "-address.postcode"
            | "address.county"
            | "-address.county"
            | "lea"
            | "-lea"
            | "type"
            | "-type"
            | "multiAcademyTrust"
            | "-multiAcademyTrust"
            | "address.city"
            | "-address.city"
            | "borough"
            | "-borough"
            | "specialism"
            | "-specialism"
            | "internalId"
            | "-internalId"
            | "lastContactedDate"
            | "-lastContactedDate"
            | "distance"
            | "-distance";
          select?: (
            | "id"
            | "name"
            | "address.addressLine1"
            | "address.addressLine2"
            | "address.addressLine3"
            | "address.city"
            | "address.county"
            | "address.borough"
            | "address.postcode"
            | "address.country"
            | "address.lat"
            | "address.lng"
            | "address"
            | "telephone"
            | "email"
            | "contacts"
            | "borough"
            | "lea"
            | "type"
            | "multiAcademyTrust"
            | "color"
            | "customId"
            | "internalId"
            | "assignee"
            | "assignee.id"
            | "assignee.email"
            | "assignee.firstName"
            | "assignee.lastName"
            | "assignee.username"
            | "assignee.lea"
            | "assignee.clients"
            | "assignee.position"
            | "assignee.permissions"
            | "assignee.activationCode"
            | "assignee.customer"
            | "assignee.disabled"
            | "assignee.commissionThreshold"
            | "assignee.branches"
            | "assignee.displayOnboarding"
            | "assignee._customId"
            | "assignee.signature"
            | "assignee.phone"
            | "assignee.defaultCandidateWatcher"
            | "assignee.defaultTaskWorkspace"
            | "assignee.teamAdmin"
            | "assignee.contact"
            | "assignee.contact.id"
            | "assignee.contact.forename"
            | "assignee.contact.surname"
            | "assignee.contact.email"
            | "assignee.contact.telephone"
            | "assignee.contact.mobile"
            | "assignee.contact.extension"
            | "assignee.createdAt"
            | "assignee.evertime.id"
            | "assignee.evertime"
            | "assignee.timesheetPortal.id"
            | "assignee.timesheetPortal.code"
            | "assignee.timesheetPortal"
            | "tags"
            | "tobSentAt"
            | "website"
            | "archived"
            | "specialism"
            | "currentBooking"
            | "bookingCount"
            | "importantNotes"
            | "roles"
            | "startTime"
            | "endTime"
            | "additionalInstructions"
            | "additionalInstructionsClient"
            | "highlight"
            | "area"
            | "governance"
            | "mathq"
            | "ofstedRating"
            | "dateOfstedReportChecked"
            | "boarding"
            | "licenceToIssueSponsorship"
            | "agencySpendTotal"
            | "vatExempt"
            | "splitInvoices"
            | "switchBoard"
            | "ccsClient"
            | "ccsClientUrn"
            | "ccsClientOfstedUrn"
            | "ccsClientLotNumber"
            | "buyerReference"
            | "ccsClientInvoiceDate"
            | "keyStage"
            | "contact"
            | "contact.id"
            | "contact.forename"
            | "contact.surname"
            | "contact.email"
            | "contact.telephone"
            | "contact.mobile"
            | "contact.extension"
            | "lastContactedDate"
            | "relevanceScore"
            | "matchReasons"
            | "distance"
            | "migrationId"
            | "migrationApproval"
            | "evertime.id"
            | "evertime.contacts"
            | "evertime.locations"
            | "evertime.nameMatch"
            | "evertime"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "timesheetPortal"
            | ""
          )[];
          /**
           * @maxItems 25
           */
          expand?: ("assignee" | "tags")[];
          include?: "bookings"[];
          filter?: {
            /**
             * @maxItems 50
             */
            ids?: string[];
            /**
             * Filter clients by name.
             *
             * @maxLength 256
             */
            name?: string;
            /**
             * Filter clients by telephone number.
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * Filter clients by email address.
             *
             * @maxLength 256
             */
            email?: string;
            /**
             * Address fields used to filter clients. Multiple supplied fields are combined.
             */
            address?: {
              /**
               * Filter clients by postcode.
               *
               * @maxLength 32
               */
              postcode?: string;
              /**
               * Filter clients by county.
               *
               * @maxLength 128
               */
              county?: string;
              /**
               * Filter clients by city.
               *
               * @maxLength 128
               */
              city?: string;
            };
            /**
             * Filter clients by local education authority.
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Filter clients by type.
             *
             * @maxLength 128
             */
            type?: string;
            /**
             * Filter clients by multi academy trust.
             *
             * @maxLength 256
             */
            multiAcademyTrust?: string;
            /**
             * Filter clients by borough.
             *
             * @maxLength 128
             */
            borough?: string;
            /**
             * Filter clients by specialism.
             *
             * @maxItems 25
             */
            specialism?: string[];
            /**
             * Filter clients by their company-defined identifier.
             *
             * @maxLength 256
             */
            internalId?: string;
            /**
             * Filter clients by whether they are a GCA client.
             */
            ccsClient?: "true" | "false";
            /**
             * Filter clients by GCA client URN.
             *
             * @maxLength 256
             */
            ccsClientUrn?: string;
            /**
             * Filter clients by assigned users.
             *
             * @maxItems 25
             */
            assignee?: string[];
            /**
             * Filter clients by last contact date.
             */
            lastContactedDateFilter?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients with an active booking overlapping an absolute or relative date range.
             */
            bookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients without an active booking overlapping an absolute or relative date range.
             */
            notBookedInRange?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter clients by tags they have.
             *
             * @maxItems 25
             */
            tags?: string[];
            location?: {
              /**
               * @minimum -90
               * @maximum 90
               */
              lat?: number | null;
              /**
               * @minimum -180
               * @maximum 180
               */
              lng?: number | null;
              /**
               * @minimum 0
               * @maximum 500
               */
              radius?: number | null;
            };
            /**
             * @pattern ^[0-9a-f]{24}$
             */
            ["contacts.contactId"]?: string;
          };
        };
      }
    | {
        /**
         * The unique identifier of the resource
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The company that owns the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customerId?: string | null;
        /**
         * Whether the saved search is available to everyone in its company.
         */
        shared?: boolean;
        /**
         * Whether the search is provided by Edun, shared with a company, or private to one user.
         */
        level?: "system" | "company" | "user";
        /**
         * The user who created the search. Not present for an Edun-provided system search.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        createdBy?: string | null;
        /**
         * The name shown when choosing the saved search.
         *
         * @minLength 1
         * @maxLength 128
         */
        name?: string;
        /**
         * A short explanation of what the saved search finds.
         *
         * @maxLength 512
         */
        description?: string;
        /**
         * The included table column identifiers in display order.
         *
         * @minItems 1
         * @maxItems 250
         */
        columns?: string[];
        /**
         * The date and time the saved search was created.
         */
        createdAt?: string;
        /**
         * The date and time the saved search was last updated.
         */
        updatedAt?: string;
        /**
         * A saved Client Contact search.
         */
        type?: "clientContacts";
        /**
         * The Client Contact query applied by this saved search, including filters, sorting, selected fields, and expansions.
         */
        query?: {
          /**
           * Find relevant Client contacts using contact and Client details.
           *
           * @minLength 1
           * @maxLength 256
           */
          q?: string;
          /**
           * Search Client contacts by name, email, or Client.
           *
           * @maxLength 256
           */
          search?: string;
          /**
           * The field used to order contacts. Prefix with - for descending order.
           */
          sort?:
            | "forename"
            | "-forename"
            | "surname"
            | "-surname"
            | "email"
            | "-email"
            | "telephone"
            | "-telephone"
            | "role"
            | "-role"
            | "status"
            | "-status"
            | "decisionMaker"
            | "-decisionMaker"
            | "clientName"
            | "-clientName"
            | "clientAddress.city"
            | "-clientAddress.city"
            | "clientAddress.county"
            | "-clientAddress.county"
            | "clientAddress.postcode"
            | "-clientAddress.postcode"
            | "clientLea"
            | "-clientLea"
            | "clientType"
            | "-clientType"
            | "clientMultiAcademyTrust"
            | "-clientMultiAcademyTrust"
            | "clientBorough"
            | "-clientBorough"
            | "clientAssignee"
            | "-clientAssignee"
            | "clientSpecialism"
            | "-clientSpecialism"
            | "clientUpdatedAt"
            | "-clientUpdatedAt"
            | "distance"
            | "-distance"
            | "relevanceScore"
            | "-relevanceScore";
          /**
           * The fields to include in the response in dot notation format.
           *
           * @maxItems 250
           */
          select?: (
            | "id"
            | "forename"
            | "surname"
            | "email"
            | "telephone"
            | "mobile"
            | "extension"
            | "role"
            | "roleOther"
            | "contactId"
            | "timesheetPortal.id"
            | "timesheetPortal.code"
            | "timesheetPortal"
            | "optOut"
            | "note"
            | "decisionMaker"
            | "status"
            | "clientId"
            | "clientName"
            | "clientAddress.addressLine1"
            | "clientAddress.addressLine2"
            | "clientAddress.addressLine3"
            | "clientAddress.city"
            | "clientAddress.county"
            | "clientAddress.borough"
            | "clientAddress.postcode"
            | "clientAddress.country"
            | "clientAddress.lat"
            | "clientAddress.lng"
            | "clientAddress"
            | "clientLea"
            | "clientType"
            | "clientMultiAcademyTrust"
            | "clientBorough"
            | "clientAssignee"
            | "clientAssignee.id"
            | "clientAssignee.email"
            | "clientAssignee.firstName"
            | "clientAssignee.lastName"
            | "clientAssignee.username"
            | "clientAssignee.lea"
            | "clientAssignee.clients"
            | "clientAssignee.position"
            | "clientAssignee.permissions"
            | "clientAssignee.activationCode"
            | "clientAssignee.customer"
            | "clientAssignee.disabled"
            | "clientAssignee.commissionThreshold"
            | "clientAssignee.branches"
            | "clientAssignee.displayOnboarding"
            | "clientAssignee._customId"
            | "clientAssignee.signature"
            | "clientAssignee.phone"
            | "clientAssignee.defaultCandidateWatcher"
            | "clientAssignee.defaultTaskWorkspace"
            | "clientAssignee.teamAdmin"
            | "clientAssignee.contact"
            | "clientAssignee.contact.id"
            | "clientAssignee.contact.forename"
            | "clientAssignee.contact.surname"
            | "clientAssignee.contact.email"
            | "clientAssignee.contact.telephone"
            | "clientAssignee.contact.mobile"
            | "clientAssignee.contact.extension"
            | "clientAssignee.createdAt"
            | "clientAssignee.evertime.id"
            | "clientAssignee.evertime"
            | "clientAssignee.timesheetPortal.id"
            | "clientAssignee.timesheetPortal.code"
            | "clientAssignee.timesheetPortal"
            | "clientSpecialism"
            | "clientUpdatedAt"
            | "distance"
            | "relevanceScore"
            | "matchReasons"
            | ""
          )[];
          /**
           * Related fields to include as objects in the response.
           *
           * @maxItems 1
           */
          expand?: "clientAssignee"[];
          /**
           * Filter Client contacts using contact and associated Client fields.
           */
          filter?: {
            /**
             * Filter contacts by status.
             *
             * @maxItems 3
             */
            status?: ("Active" | "Passive" | "Inactive")[];
            /**
             * Filter contacts by role.
             *
             * @maxItems 44
             */
            role?: (
              | "Head Teacher"
              | "Deputy Head"
              | "Cover Manager"
              | "HR Manager"
              | "Finance Manager"
              | "Head of Maths"
              | "Head of Science"
              | "Head of English"
              | "Assistant Headteacher"
              | "Headteacher's PA"
              | "SENCO"
              | "Other"
              | "Business Manager"
              | "Business Administrator"
              | "Invoice Contact"
              | "Academy Recruitment Contact"
              | "Nursery Manager"
              | "CEO"
              | "COO"
              | "Executive Headteacher"
              | "Inclusion Manager"
              | "EYFS Lead"
              | "Admin Team"
              | "Head of Humanities"
              | "Head of History"
              | "Head of Geography"
              | "Head of Business Studies"
              | "Head of PE"
              | "Head of KS1"
              | "Head of KS2"
              | "Head of KS3"
              | "Head of KS4"
              | "Virtual School Headteacher"
              | "Deputy Virtual School Headteacher"
              | "Post-16 Team Leader"
              | "SEN Team"
              | "Inclusion Team"
              | "UASC Team"
              | "Caseworker"
              | "Designated Teacher"
              | "Attendance Officer"
              | "Coordinator"
              | "Children's Commissioner"
              | "Timesheet Contact"
            )[];
            /**
             * Filter contacts by forename.
             *
             * @maxLength 128
             */
            forename?: string;
            /**
             * Filter contacts by surname.
             *
             * @maxLength 128
             */
            surname?: string;
            /**
             * Filter contacts by email address.
             *
             * @maxLength 256
             */
            email?: string;
            /**
             * Filter contacts by decision-maker status.
             */
            decisionMaker?: "true" | "false";
            /**
             * Filter contacts by opt-out status.
             */
            optOut?: "true" | "false";
            /**
             * Filter contacts by Client.
             *
             * @maxItems 50
             */
            clientId?: string[];
            /**
             * Filter contacts by Client name.
             *
             * @maxLength 256
             */
            clientName?: string;
            /**
             * Filter contacts by associated Client address fields.
             */
            clientAddress?: {
              /**
               * @maxLength 128
               */
              city?: string;
              /**
               * @maxLength 128
               */
              county?: string;
              /**
               * @maxLength 32
               */
              postcode?: string;
            };
            /**
             * Filter contacts by Client local education authority.
             *
             * @maxLength 128
             */
            clientLea?: string;
            /**
             * Filter contacts by Client sector.
             *
             * @maxLength 128
             */
            clientType?: string;
            /**
             * Filter contacts by Client multi-academy trust.
             *
             * @maxLength 256
             */
            clientMultiAcademyTrust?: string;
            /**
             * Filter contacts by Client borough.
             *
             * @maxLength 128
             */
            clientBorough?: string;
            /**
             * Filter contacts by Client assignee.
             *
             * @maxItems 25
             */
            clientAssignee?: string[];
            /**
             * Filter contacts by Client specialism.
             *
             * @maxItems 25
             */
            clientSpecialism?: string[];
            /**
             * Filter contacts by when their Client was updated.
             */
            clientUpdatedAt?: {
              /**
               * The inclusive absolute start of the date range.
               *
               * @format date-time
               */
              from?: string | null;
              /**
               * The inclusive absolute end of the date range.
               *
               * @format date-time
               */
              to?: string | null;
              /**
               * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
               */
              relative?: {
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                from?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
                /**
                 * A date boundary calculated from the current day, week, or month when the query runs.
                 */
                to?: {
                  /**
                   * The current calendar point from which a relative date boundary is calculated.
                   */
                  anchor?: "today" | "startOfWeek" | "startOfMonth";
                  /**
                   * The number of units before or after the selected calendar anchor.
                   *
                   * @minimum -120
                   * @maximum 120
                   */
                  offset?: number | null;
                  /**
                   * The unit used to offset a relative date boundary.
                   */
                  unit?: "days" | "weeks" | "months";
                };
              };
            };
            /**
             * Filter contacts by distance from their Client address.
             */
            location?: {
              /**
               * @minimum -90
               * @maximum 90
               */
              lat?: number | null;
              /**
               * @minimum -180
               * @maximum 180
               */
              lng?: number | null;
              /**
               * @minimum 0
               * @exclusiveMinimum true
               * @maximum 500
               */
              radius?: number;
            };
          };
        };
      };
};

export type UpdateSavedSearchRequestBody =
  | {
      /**
       * Whether the saved search is available to everyone in its company.
       */
      shared?: boolean;
      /**
       * The name shown when choosing the saved search.
       *
       * @minLength 1
       * @maxLength 128
       */
      name?: string;
      /**
       * A short explanation of what the saved search finds.
       *
       * @maxLength 512
       */
      description?: string;
      /**
       * The included table column identifiers in display order.
       *
       * @minItems 1
       * @maxItems 250
       */
      columns?: string[];
      /**
       * The type of records configured by the saved search.
       */
      type?: "candidates" | "clients" | "clientContacts";
    }
  | {
      /**
       * Whether the saved search is available to everyone in its company.
       */
      shared?: boolean;
      /**
       * The name shown when choosing the saved search.
       *
       * @minLength 1
       * @maxLength 128
       */
      name?: string;
      /**
       * A short explanation of what the saved search finds.
       *
       * @maxLength 512
       */
      description?: string;
      /**
       * The included table column identifiers in display order.
       *
       * @minItems 1
       * @maxItems 250
       */
      columns?: string[];
      type: "candidates";
      query?: {
        /**
         * Find relevant candidates using candidate details and attached document analysis.
         *
         * @minLength 1
         * @maxLength 256
         */
        q?: string;
        /**
         * Search candidates by name (forename or surname)
         *
         * @maxLength 256
         */
        search?: string;
        /**
         * The field to sort by. Prefix with '-' for descending. Availability priority requires an availability period and ranks free time, counting a full available day as one and a half-day as half.
         */
        sort?:
          | (
              | "relevanceScore"
              | "-relevanceScore"
              | "forename"
              | "-forename"
              | "surname"
              | "-surname"
              | "mobile"
              | "-mobile"
              | "email"
              | "-email"
              | "address.city"
              | "-address.city"
              | "address.postcode"
              | "-address.postcode"
              | "address.county"
              | "-address.county"
              | "address.borough"
              | "-address.borough"
              | "address.addressLine1"
              | "-address.addressLine1"
              | "address.addressLine2"
              | "-address.addressLine2"
              | "address.addressLine3"
              | "-address.addressLine3"
              | "subject"
              | "-subject"
              | "statusChangeDate"
              | "-statusChangeDate"
              | "lastContactedDate"
              | "-lastContactedDate"
              | "daysSinceLastContactDisplay"
              | "-daysSinceLastContactDisplay"
              | "status"
              | "-status"
              | "occupation"
              | "-occupation"
              | "dob"
              | "-dob"
              | "trnNumberCheckedDate"
              | "-trnNumberCheckedDate"
              | "criminalRecord.dbsLastChecked"
              | "-criminalRecord.dbsLastChecked"
              | "criminalRecord.updateServiceCheckDate"
              | "-criminalRecord.updateServiceCheckDate"
              | "rtwVerification"
              | "-rtwVerification"
              | "criminalRecord.section128Checked"
              | "-criminalRecord.section128Checked"
              | "criminalRecord.prohibitionCheck"
              | "-criminalRecord.prohibitionCheck"
              | "createdAt"
              | "-createdAt"
              | "updatedAt"
              | "-updatedAt"
              | "_internalId"
              | "-_internalId"
              | "trnNumber"
              | "-trnNumber"
              | "desiredLocation"
              | "-desiredLocation"
              | "telephone"
              | "-telephone"
              | "previousNames"
              | "-previousNames"
              | "otherNames"
              | "-otherNames"
              | "location"
              | "-location"
            )
          | ("availabilityPriority" | "-availabilityPriority");
        /**
         * The fields to include in the response in dot notation format
         *
         * @maxItems 250
         */
        select?: (
          | "id"
          | "forename"
          | "surname"
          | "previousNames"
          | "email"
          | "telephone"
          | "mobile"
          | "occupation"
          | "subject"
          | "address.addressLine1"
          | "address.addressLine2"
          | "address.addressLine3"
          | "address.city"
          | "address.county"
          | "address.borough"
          | "address.postcode"
          | "address.country"
          | "address.lat"
          | "address.lng"
          | "qualifications"
          | "employment"
          | "references"
          | "referenceCheckConsent"
          | "referenceCheckConsentName"
          | "referenceCheckConsentDate"
          | "dob"
          | "sex"
          | "status"
          | "onboardingStatus"
          | "onboardingSent"
          | "nextOfKin.nextOfKinName"
          | "nextOfKin.nextOfKinRelationship"
          | "nextOfKin.nextOfKinTelephone"
          | "nextOfKin.nextOfKinEmail"
          | "nextOfKin.nextOfKinAddress.addressLine1"
          | "nextOfKin.nextOfKinAddress.addressLine2"
          | "nextOfKin.nextOfKinAddress.addressLine3"
          | "nextOfKin.nextOfKinAddress.city"
          | "nextOfKin.nextOfKinAddress.county"
          | "nextOfKin.nextOfKinAddress.borough"
          | "nextOfKin.nextOfKinAddress.postcode"
          | "nextOfKin.nextOfKinAddress.country"
          | "nextOfKin.nextOfKinAddress.lat"
          | "nextOfKin.nextOfKinAddress.lng"
          | "criminalRecord.dbsHeld"
          | "criminalRecord.dbsNumber"
          | "criminalRecord.dbsType"
          | "criminalRecord.dbsLastChecked"
          | "criminalRecord.dbsUpdateServiceNumber"
          | "criminalRecord.prohibitionCheck"
          | "criminalRecord.overseasCheck"
          | "criminalRecord.overseasCheckNote"
          | "criminalRecord.convictedOffence"
          | "criminalRecord.convictedOffenceDescription"
          | "criminalRecord.convictions"
          | "criminalRecord.convictionsDescription"
          | "criminalRecord.registeredToUpdateService"
          | "criminalRecord.consentUpdateServiceChecks"
          | "criminalRecord.note"
          | "criminalRecord.updateServiceCheckDate"
          | "criminalRecord.abroad"
          | "criminalRecord.overseasDeclarationExtended"
          | "criminalRecord.educationalDisciplinaryProceedings"
          | "criminalRecord.dbsComments"
          | "criminalRecord.dbsDeclarationRead"
          | "criminalRecord.overseasOffence"
          | "criminalRecord.overseasOffenceDescription"
          | "criminalRecord.currentCriminalOffence"
          | "criminalRecord.currentCriminalOffenceDescription"
          | "criminalRecord.dbsIssuedByBusiness"
          | "criminalRecord.dbsIssuedByCurrentEmployer"
          | "criminalRecord.section128Required"
          | "criminalRecord.section128Checked"
          | "criminalRecordDeclaration.questions"
          | "criminalRecordDeclaration.answers"
          | "criminalRecordDeclaration.submittedAt"
          | "criminalRecordDeclaration.submittedIp"
          | "criminalRecordDeclaration.configurationUpdatedAt"
          | "criminalRecordCheckConsent"
          | "criminalRecordCheckConsentName"
          | "criminalRecordCheckConsentDate"
          | "safeInEducation.barred"
          | "safeInEducation.barredCheckDate"
          | "safeInEducation.waiver"
          | "safeInEducation.ordersYou"
          | "safeInEducation.ordersChild"
          | "safeInEducation.previousSafeguardingAllegations"
          | "safeInEducation.cautioned"
          | "safeInEducation.violentOffence"
          | "safeInEducation.anotherCountry"
          | "safeInEducation.readKCSIE"
          | "safeInEducation.readChildcareAct"
          | "safeInEducation.prohibitedFromTeaching"
          | "safeInEducation.knownPolice"
          | "safeInEducation.prohibitedFromManagement"
          | "safeInEducation.criminalOffencesLaw"
          | "safeInEducation.safeInEducationDetails"
          | "safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
          | "safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
          | "safeInEducation.safeguardingQuestionaire.signOfAbuse"
          | "safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
          | "safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
          | "safeInEducation.safeguardingQuestionaire.fgmDefinition"
          | "safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
          | "safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
          | "safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
          | "safeInEducation.safeguardingQuestionaire.studentConfides"
          | "socialMedia.facebook"
          | "socialMedia.instagram"
          | "socialMedia.twitter"
          | "socialMedia.linkedIn"
          | "socialMedia.tiktok"
          | "socialMedia.others"
          | "socialMedia.socialMediaChecked"
          | "socialMedia.googleCheckedDate"
          | "safeInEducationCheckConsent"
          | "safeInEducationCheckConsentName"
          | "safeInEducationCheckConsentDate"
          | "healthRecord.physicalAbilityCondition"
          | "healthRecord.confused"
          | "healthRecord.hearing"
          | "healthRecord.eyesight"
          | "healthRecord.depression"
          | "healthRecord.severePain"
          | "healthRecord.excessiveDrowsiness"
          | "healthRecord.epilepsy"
          | "healthRecord.heart"
          | "healthRecord.asthma"
          | "healthRecord.mentalHealth"
          | "healthRecord.expectantMother"
          | "healthRecord.allergies"
          | "healthRecord.medication"
          | "healthRecord.healthIssues"
          | "healthRecord.healthIssuesDescription"
          | "healthRecord.disabilityNeeds"
          | "healthRecord.contagiousDiseases"
          | "healthRecord.affectAbility"
          | "healthRecord.notificationAgree"
          | "healthRecord.sasOptIn"
          | "healthRecord.signature"
          | "healthRecord.drawnSignature"
          | "healthRecord.signatureDate"
          | "healthRecord.vaccinationMeasles"
          | "healthRecord.vaccinationMumps"
          | "healthRecord.vaccinationRubella"
          | "healthRecord.vaccinationVaricella"
          | "healthRecord.vaccinationTuberculosis"
          | "healthRecord.vaccinationHepatitisB"
          | "healthRecord.vaccinationHepatitisC"
          | "healthRecord.vaccinationHIV"
          | "healthRecord.vaccinationCovid"
          | "healthRecord.vaccinationCovid1"
          | "healthRecord.vaccinationCovid2"
          | "healthRecord.vaccinationCovidBooster"
          | "healthRecord.vaccinationCovidInfluenza"
          | "healthRecord.fitToWorkOverride"
          | "healthDeclaration.questions"
          | "healthDeclaration.answers"
          | "healthDeclaration.submittedAt"
          | "healthDeclaration.submittedIp"
          | "healthDeclaration.configurationUpdatedAt"
          | "healthRecordCheckConsent"
          | "healthRecordCheckConsentName"
          | "healthRecordCheckConsentDate"
          | "bankDetails.bankAccountName"
          | "bankDetails.bankName"
          | "bankDetails.accountNumber"
          | "bankDetails.sortCode"
          | "bankDetails.niNumber"
          | "bankDetails.jobOp1"
          | "bankDetails.jobOp2"
          | "bankDetails.jobOp3"
          | "bankDetails.studentLoan.studentLoan"
          | "bankDetails.studentLoan.plan1"
          | "bankDetails.studentLoan.plan2"
          | "bankDetails.studentLoan.plan4"
          | "bankDetails.studentLoan.postGradLoan"
          | "declarationCheckConsent"
          | "declarationCheckConsentName"
          | "declarationCheckConsentDate"
          | "contractForServiceConsent"
          | "contractForServiceName"
          | "contractForServiceDate"
          | "confirmed"
          | "createdBy"
          | "customerId"
          | "migrationId"
          | "migrationApproval"
          | "assignee"
          | "assignee.id"
          | "assignee.email"
          | "assignee.firstName"
          | "assignee.lastName"
          | "assignee.username"
          | "assignee.lea"
          | "assignee.clients"
          | "assignee.position"
          | "assignee.permissions"
          | "assignee.activationCode"
          | "assignee.customer"
          | "assignee.disabled"
          | "assignee.commissionThreshold"
          | "assignee.branches"
          | "assignee.displayOnboarding"
          | "assignee._customId"
          | "assignee.signature"
          | "assignee.phone"
          | "assignee.defaultCandidateWatcher"
          | "assignee.defaultTaskWorkspace"
          | "assignee.teamAdmin"
          | "assignee.contact"
          | "assignee.contact.id"
          | "assignee.contact.forename"
          | "assignee.contact.surname"
          | "assignee.contact.email"
          | "assignee.contact.telephone"
          | "assignee.contact.mobile"
          | "assignee.contact.extension"
          | "assignee.createdAt"
          | "assignee.evertime.id"
          | "assignee.timesheetPortal.id"
          | "assignee.timesheetPortal.code"
          | "currentBooking"
          | "profilePhoto"
          | "payType"
          | "payInterval"
          | "payRate"
          | "customId"
          | "_internalId"
          | "statusChangeDate"
          | "optOut"
          | "trnNumber"
          | "trnNumberCheckedDate"
          | "trnNumberNotes"
          | "employmentStartDate"
          | "ewcConfirmation"
          | "homeOfficeShareCode"
          | "archived"
          | "umbrella"
          | "payeUmbrella"
          | "umbrellaCompanyName"
          | "umbrellaCompanyCode"
          | "transport"
          | "importantNotes"
          | "candidateLastUpdated"
          | "source"
          | "sourceOther"
          | "onlineCheckConsent"
          | "title"
          | "pronoun"
          | "updatedAt"
          | "lastContactedDate"
          | "highlight"
          | "teacherPayScale"
          | "p45"
          | "roleType"
          | "rtwVerification"
          | "rtwVerificationNote"
          | "rtwVerificationStatus"
          | "desiredLocation"
          | "complianceUser"
          | "complianceUser.id"
          | "complianceUser.email"
          | "complianceUser.firstName"
          | "complianceUser.lastName"
          | "complianceUser.username"
          | "complianceUser.lea"
          | "complianceUser.clients"
          | "complianceUser.position"
          | "complianceUser.permissions"
          | "complianceUser.activationCode"
          | "complianceUser.customer"
          | "complianceUser.disabled"
          | "complianceUser.commissionThreshold"
          | "complianceUser.branches"
          | "complianceUser.displayOnboarding"
          | "complianceUser._customId"
          | "complianceUser.signature"
          | "complianceUser.phone"
          | "complianceUser.defaultCandidateWatcher"
          | "complianceUser.defaultTaskWorkspace"
          | "complianceUser.teamAdmin"
          | "complianceUser.contact"
          | "complianceUser.contact.id"
          | "complianceUser.contact.forename"
          | "complianceUser.contact.surname"
          | "complianceUser.contact.email"
          | "complianceUser.contact.telephone"
          | "complianceUser.contact.mobile"
          | "complianceUser.contact.extension"
          | "complianceUser.createdAt"
          | "complianceUser.evertime.id"
          | "complianceUser.timesheetPortal.id"
          | "complianceUser.timesheetPortal.code"
          | "watchers"
          | "tags"
          | "documents"
          | "documentsCount"
          | "desiredLocationMultiple"
          | "availabilityDays"
          | "availabilityLastChecked"
          | "subjectToNotice"
          | "subsidiarySubject"
          | "employmentPreference"
          | "otherNames"
          | "ltdCompany"
          | "employmentVerified"
          | "candidateDBSVerification"
          | "candidateDBSVerificationNote"
          | "candidateDBSVerificationStatus"
          | "candidateDBSCheckVerification"
          | "candidateDBSCheckVerificationNote"
          | "candidateDBSCheckVerificationStatus"
          | "candidateDBSUpdateServiceVerificationNote"
          | "candidateDBSUpdateServiceVerificationStatus"
          | "candidateTRNVerificationNote"
          | "candidateTRNVerificationStatus"
          | "candidateHealthSafetyVerification"
          | "candidateHealthSafetyVerificationNote"
          | "candidateHealthSafetyVerificationStatus"
          | "onboardingSession"
          | "onboardingSession.id"
          | "onboardingSession.url"
          | "onboardingSession.healthDeclarationConfig.questions"
          | "onboardingSession.healthDeclarationConfig.updatedAt"
          | "onboardingSession.criminalRecordConfig.questions"
          | "onboardingSession.criminalRecordConfig.updatedAt"
          | "contact"
          | "contact.id"
          | "contact.forename"
          | "contact.surname"
          | "contact.email"
          | "contact.telephone"
          | "contact.mobile"
          | "contact.extension"
          | "optOut48Hours"
          | "recAuditConsent"
          | "recAuditConsentDate"
          | "recAuditConsentMethod"
          | "createdAt"
          | "candidateMobileAppStatus"
          | "evertime.id"
          | "evertime.niNumberMatch"
          | "relevanceScore"
          | "matchReasons"
          | "distance"
          | "timesheetPortal.id"
          | "timesheetPortal.code"
          | "mobileAppLinkInvite.isUsed"
          | "mobileAppLinkInvite.createdAt"
          | "mobileAppLinkInvite.usedBy"
          | "mobileAppLinkInvite.usedAt"
          | "mobileAppLinkInvite.unlinkedBy"
          | "mobileAppLinkInvite.unlinkedAt"
          | "mobileAppLinkInvite.deletedBy"
          | "mobileAppLinkInvite.deletedAt"
          | "mobileAppLinkInvite.status"
        )[];
        /**
         * Related resources to return as objects instead of IDs. Supported values are documented for this endpoint.
         *
         * @maxItems 100
         */
        expand?: (
          | "assignee"
          | "contact"
          | "customer"
          | "umbrella"
          | "payeUmbrella"
          | "complianceUser"
          | "onboardingSession"
          | "watchers"
          | "tags"
          | "documents"
        )[];
        filter?: {
          /**
           * Inclusive calendar dates, up to 31 days. Both boundaries are required when selecting availability days, filtering availability statuses or sorting by availability priority.
           */
          availabilityPeriod?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Match candidates with any selected status during the availability period. Requires an availability period. Empty selects all statuses.
           *
           * @maxItems 5
           */
          availabilityStatuses?: (
            | "available"
            | "unavailable"
            | "booked"
            | "awaiting"
            | "notRecorded"
          )[];
          /**
           * Filter candidates by their IDs. Maximum 50.
           *
           * @maxItems 50
           */
          ids?: string[];
          /**
           * Filter candidates by their status
           *
           * @maxItems 25
           */
          status?: (
            | "Pending"
            | "Approved"
            | "Rejected"
            | "Anonymised"
            | "Archived"
            | "Enquired"
            | "Hold"
            | "Future"
            | "DNC"
            | "Pre"
          )[];
          /**
           * Filter candidates by their onboarding status
           *
           * @maxItems 25
           */
          onboardingStatus?: ("Not Started" | "In Progress" | "Completed")[];
          /**
           * Filter candidates by if they have an active onboarding session
           */
          activeOnboardingSession?: boolean;
          /**
           * Filter candidates by if they have been sent onboarding materials
           */
          onboardingSent?: "true" | "false";
          /**
           * Filter candidates by if they require section 128
           */
          section128Required?: "true" | "false";
          /**
           * Filter candidates by confirmed status
           */
          confirmed?: "true" | "false";
          /**
           * Filter candidates by opt out status
           */
          optOut?: "true" | "false";
          /**
           * Filter candidates by opt out 48 hours status
           */
          optOut48Hours?: "true" | "false";
          /**
           * Filter candidates by fit to work override status
           */
          fitToWorkOverride?: "true" | "false";
          /**
           * Filter candidates by whether they have health conditions
           */
          affectAbility?: "true" | "false";
          /**
           * Filter candidates by EWC confirmation status
           */
          ewcConfirmation?: "true" | "false";
          /**
           * Filter candidates by whether they hold a DBS certificate
           */
          dbsHeld?: "true" | "false";
          /**
           * Filter candidates by DBS update service registration
           */
          dbsRegisteredToUpdateService?: "true" | "false";
          /**
           * Filter candidates by their first name
           *
           * @maxLength 500
           */
          firstName?: string;
          /**
           * Filter candidates by their last name
           *
           * @maxLength 500
           */
          lastName?: string;
          /**
           * Filter candidates by their email address
           *
           * @maxLength 500
           */
          email?: string;
          /**
           * Filter candidates by their mobile number
           *
           * @maxLength 500
           */
          mobile?: string;
          /**
           * Address fields used to filter candidates. Multiple supplied fields are combined.
           */
          address?: {
            /**
             * Filter candidates by their city
             *
             * @maxLength 500
             */
            city?: string;
            /**
             * Filter candidates by their postcode
             *
             * @maxLength 20
             */
            postcode?: string;
            /**
             * Filter candidates by their county
             *
             * @maxLength 500
             */
            county?: string;
            /**
             * Filter candidates by their borough
             *
             * @maxLength 500
             */
            borough?: string;
            /**
             * Filter candidates by address line 1
             *
             * @maxLength 500
             */
            addressLine1?: string;
            /**
             * Filter candidates by address line 2
             *
             * @maxLength 500
             */
            addressLine2?: string;
            /**
             * Filter candidates by address line 3
             *
             * @maxLength 500
             */
            addressLine3?: string;
          };
          /**
           * Filter candidates by their occupation
           *
           * @maxItems 25
           */
          occupation?: string[];
          /**
           * Filter candidates by their specialism
           *
           * @maxLength 500
           */
          specialism?: string;
          /**
           * Filter candidates by their company-defined secondary identifier.
           *
           * @maxLength 500
           */
          _internalId?: string;
          /**
           * Filter candidates by their compliance user
           *
           * @maxItems 25
           */
          complianceUser?: string[];
          /**
           * Filter candidates by their assignee
           *
           * @maxItems 25
           */
          assignee?: string[];
          /**
           * Filter candidates by their watcher
           *
           * @maxItems 25
           */
          watcher?: string[];
          /**
           * Filter candidates by their team
           *
           * @maxItems 25
           */
          team?: string[];
          /**
           * Filter candidates by their associated contact ID
           *
           * @pattern ^[0-9a-f]{24}$
           */
          contactId?: string;
          /**
           * Filter candidates by whether they have an active booking
           */
          currentBooking?: "true" | "false";
          /**
           * Filter candidates with an active booking overlapping an absolute or relative date range.
           */
          bookedInRange?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates without an active booking overlapping an absolute or relative date range. Combine with `bookedInRange` to find candidates booked in one period but not another.
           */
          notBookedInRange?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by their tags
           *
           * @maxItems 25
           */
          tags?: string[];
          /**
           * Filter candidates by location from coordinates
           */
          location?: {
            lat?: number | null;
            lng?: number | null;
            radius?: number | null;
          };
          /**
           * Filter candidates by last status changed
           */
          statusChangeDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by last contact
           */
          lastContactedDateFilter?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by employment preference
           *
           * @maxItems 25
           */
          employmentPreference?: (
            | "Day To Day"
            | "Long Term"
            | "Fixed Term"
            | "Perm"
          )[];
          /**
           * Filter candidates by their chosen mode of transport
           *
           * @maxItems 25
           */
          transport?: (
            | "Drives Own Vehicle"
            | "Use Public Transport"
            | "Access to Another Vehicle"
            | "Bicycle"
            | "No Access"
            | "Other"
          )[];
          /**
           * Filter candidates by their TRN number
           *
           * @maxLength 500
           */
          trnNumber?: string;
          /**
           * Filter candidates by their desired location
           *
           * @maxLength 500
           */
          desiredLocation?: string;
          /**
           * Filter candidates by whether they are subject to a notice period
           *
           * @maxItems 25
           */
          subjectToNotice?: ("Unsure" | "Yes" | "No")[];
          /**
           * Filter candidates by title
           *
           * @maxItems 25
           */
          title?: (
            | "Mr"
            | "Mrs"
            | "Ms"
            | "Miss"
            | "Dr"
            | "Mx"
            | "Lady"
            | "Lord"
            | "Other"
          )[];
          /**
           * Filter candidates by sex
           *
           * @maxItems 25
           */
          sex?: ("Male" | "Female" | "Other" | "Unknown")[];
          /**
           * Filter candidates by pronoun
           *
           * @maxItems 25
           */
          pronoun?: (
            | "She/Her/Hers"
            | "He/Him/His"
            | "They/Them/Theirs"
            | "Ze/Hir/Hirs"
            | "Ze/Zir/Zirs"
            | "Other"
            | "Prefer not to say"
          )[];
          /**
           * Filter candidates by role type
           *
           * @maxItems 25
           */
          roleType?: (
            | "Senior Leadership Positions"
            | "Teaching Roles"
            | "School Support Positions"
          )[];
          /**
           * Filter candidates by pay type
           *
           * @maxItems 25
           */
          payType?: (
            | "PAYE"
            | "Umbrella"
            | "Limited Company"
            | "Not Applicable"
          )[];
          /**
           * Filter candidates by payment interval
           *
           * @maxItems 25
           */
          payInterval?: (
            | "Monthly"
            | "Weekly"
            | "Weekly Pay in Arrears"
            | "Bi-Weekly"
          )[];
          /**
           * Filter candidates by DBS check type
           *
           * @maxItems 25
           */
          dbsType?: (
            | "Basic"
            | "Standard"
            | "Enhanced"
            | "Enhanced with Barred List"
            | "Child workforce"
            | "Child and adult workforce"
            | "Adult workforce"
          )[];
          /**
           * Filter candidates by source
           */
          source?: (
            | (
                | "Company Website"
                | "iCan Teach UK Website"
                | "Apply to Education"
                | "CV Library - CV Search"
                | "CV Library Advert"
                | "CV Library - Watchdog"
                | "Guardian- CV Search"
                | "Guardian Advert"
                | "Indeed - CV Search"
                | "Indeed Advert"
                | "Reed - CV Search"
                | "Reed Advert"
                | "Reed \u2013 Watchdog"
                | "SENploy - Advert"
                | "Total Jobs"
                | "Other Job Board"
                | "Google"
                | "LinkedIn"
                | "Linkedin Recruiter"
                | "LinkedIn Advert"
                | "Referral - School"
                | "Referral - Candidate"
                | "UK University"
                | "Irish University"
                | "Canadian University"
                | "Marketing Campaign"
                | "Woo"
                | "Job Show"
                | "Recruitment Fair"
                | "Career Fair"
                | "Volcanic"
                | "Broadbean"
                | "Headhunt"
                | "Referral"
                | "Direct"
                | "Other"
              )
            | (
                | "Company Website"
                | "Direct Call"
                | "CV Library Watchdog"
                | "CV Library Advert"
                | "CV Library CV Search"
                | "Reed Watchdog"
                | "Reed Advert"
                | "Reed CV Search"
                | "Indeed Watchdog"
                | "Indeed Advert"
                | "Indeed CV Search"
                | "Job Centre"
                | "Returning Candidate"
                | "Referral Candidate"
                | "Referral Client"
                | "Training Provider"
                | "Recruitment Fair"
                | "University/College"
              )
          )[];
          /**
           * Filter candidates by their telephone number
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * Filter candidates by their previous names
           *
           * @maxLength 500
           */
          previousNames?: string;
          /**
           * Filter candidates by their other names
           *
           * @maxLength 500
           */
          otherNames?: string;
          /**
           * Filter candidates by date of birth
           */
          dob?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by TRN number checked date
           */
          trnNumberCheckedDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by DBS last checked date
           */
          dbsLastChecked?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by DBS update service check date
           */
          dbsUpdateServiceCheckDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by RTW verification date
           */
          rtwVerificationDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by Section 128 checked date
           */
          section128CheckedDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by prohibition check date
           */
          prohibitionCheckDate?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by created date
           */
          createdAt?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by last updated date
           */
          updatedAt?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter candidates by mobile app status
           *
           * @maxItems 25
           */
          candidateMobileAppStatus?: (
            | "Unlinked"
            | "Scheduled Unlink"
            | "Linked"
            | "Invited"
            | "Not Invited"
          )[];
          /**
           * Filter candidates by REC audit consent status
           */
          recAuditConsent?: "true" | "false";
        };
      };
      /**
       * Optional availability-view settings. Older saved searches use the standard candidate table.
       */
      presentation?: {
        mode: "table" | "availability";
        availability?: {
          /**
           * An absolute or relative date range. Relative boundaries take precedence when supplied.
           */
          period: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * @maxItems 5
           */
          statuses: (
            | "available"
            | "unavailable"
            | "booked"
            | "awaiting"
            | "notRecorded"
          )[];
          /**
           * The unique identifier of the resource
           *
           * @pattern ^[0-9a-f]{24}$
           */
          draftClient?: string;
          draftMode: "week" | "all";
        };
      };
    }
  | {
      /**
       * Whether the saved search is available to everyone in its company.
       */
      shared?: boolean;
      /**
       * The name shown when choosing the saved search.
       *
       * @minLength 1
       * @maxLength 128
       */
      name?: string;
      /**
       * A short explanation of what the saved search finds.
       *
       * @maxLength 512
       */
      description?: string;
      /**
       * The included table column identifiers in display order.
       *
       * @minItems 1
       * @maxItems 250
       */
      columns?: string[];
      type: "clients";
      query?: {
        /**
         * Find relevant clients using their details and location.
         *
         * @minLength 1
         * @maxLength 256
         */
        q?: string;
        /**
         * Search clients by name
         *
         * @maxLength 256
         */
        search?: string;
        /**
         * The field to sort results by. Prefix with - for descending order
         */
        sort?:
          | "name"
          | "-name"
          | "createdAt"
          | "-createdAt"
          | "assignee"
          | "-assignee"
          | "telephone"
          | "-telephone"
          | "email"
          | "-email"
          | "address.postcode"
          | "-address.postcode"
          | "address.county"
          | "-address.county"
          | "lea"
          | "-lea"
          | "type"
          | "-type"
          | "multiAcademyTrust"
          | "-multiAcademyTrust"
          | "address.city"
          | "-address.city"
          | "borough"
          | "-borough"
          | "specialism"
          | "-specialism"
          | "internalId"
          | "-internalId"
          | "lastContactedDate"
          | "-lastContactedDate"
          | "distance"
          | "-distance";
        /**
         * The fields to include in the response in dot notation format
         */
        select?: (
          | "id"
          | "name"
          | "address.addressLine1"
          | "address.addressLine2"
          | "address.addressLine3"
          | "address.city"
          | "address.county"
          | "address.borough"
          | "address.postcode"
          | "address.country"
          | "address.lat"
          | "address.lng"
          | "address"
          | "telephone"
          | "email"
          | "contacts"
          | "borough"
          | "lea"
          | "type"
          | "multiAcademyTrust"
          | "color"
          | "customId"
          | "internalId"
          | "assignee"
          | "assignee.id"
          | "assignee.email"
          | "assignee.firstName"
          | "assignee.lastName"
          | "assignee.username"
          | "assignee.lea"
          | "assignee.clients"
          | "assignee.position"
          | "assignee.permissions"
          | "assignee.activationCode"
          | "assignee.customer"
          | "assignee.disabled"
          | "assignee.commissionThreshold"
          | "assignee.branches"
          | "assignee.displayOnboarding"
          | "assignee._customId"
          | "assignee.signature"
          | "assignee.phone"
          | "assignee.defaultCandidateWatcher"
          | "assignee.defaultTaskWorkspace"
          | "assignee.teamAdmin"
          | "assignee.contact"
          | "assignee.contact.id"
          | "assignee.contact.forename"
          | "assignee.contact.surname"
          | "assignee.contact.email"
          | "assignee.contact.telephone"
          | "assignee.contact.mobile"
          | "assignee.contact.extension"
          | "assignee.createdAt"
          | "assignee.evertime.id"
          | "assignee.evertime"
          | "assignee.timesheetPortal.id"
          | "assignee.timesheetPortal.code"
          | "assignee.timesheetPortal"
          | "tags"
          | "tobSentAt"
          | "website"
          | "archived"
          | "specialism"
          | "currentBooking"
          | "bookingCount"
          | "importantNotes"
          | "roles"
          | "startTime"
          | "endTime"
          | "additionalInstructions"
          | "additionalInstructionsClient"
          | "highlight"
          | "area"
          | "governance"
          | "mathq"
          | "ofstedRating"
          | "dateOfstedReportChecked"
          | "boarding"
          | "licenceToIssueSponsorship"
          | "agencySpendTotal"
          | "vatExempt"
          | "splitInvoices"
          | "switchBoard"
          | "ccsClient"
          | "ccsClientUrn"
          | "ccsClientOfstedUrn"
          | "ccsClientLotNumber"
          | "buyerReference"
          | "ccsClientInvoiceDate"
          | "keyStage"
          | "contact"
          | "contact.id"
          | "contact.forename"
          | "contact.surname"
          | "contact.email"
          | "contact.telephone"
          | "contact.mobile"
          | "contact.extension"
          | "lastContactedDate"
          | "relevanceScore"
          | "matchReasons"
          | "distance"
          | "migrationId"
          | "migrationApproval"
          | "evertime.id"
          | "evertime.contacts"
          | "evertime.locations"
          | "evertime.nameMatch"
          | "evertime"
          | "timesheetPortal.id"
          | "timesheetPortal.code"
          | "timesheetPortal"
          | ""
        )[];
        /**
         * Related fields to include as objects in the response.
         *
         * @maxItems 25
         */
        expand?: ("assignee" | "tags")[];
        /**
         * Additional fields to include in the response
         */
        include?: "bookings"[];
        filter?: {
          /**
           * Filter clients by their IDs. Maximum 50.
           *
           * @maxItems 50
           */
          ids?: string[];
          /**
           * Filter clients by name.
           *
           * @maxLength 256
           */
          name?: string;
          /**
           * Filter clients by telephone number.
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * Filter clients by email address.
           *
           * @maxLength 256
           */
          email?: string;
          /**
           * Address fields used to filter clients. Multiple supplied fields are combined.
           */
          address?: {
            /**
             * Filter clients by postcode.
             *
             * @maxLength 32
             */
            postcode?: string;
            /**
             * Filter clients by county.
             *
             * @maxLength 128
             */
            county?: string;
            /**
             * Filter clients by city.
             *
             * @maxLength 128
             */
            city?: string;
          };
          /**
           * Filter clients by local education authority.
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Filter clients by type.
           *
           * @maxLength 128
           */
          type?: string;
          /**
           * Filter clients by multi academy trust.
           *
           * @maxLength 256
           */
          multiAcademyTrust?: string;
          /**
           * Filter clients by borough.
           *
           * @maxLength 128
           */
          borough?: string;
          /**
           * Filter clients by specialism.
           *
           * @maxItems 25
           */
          specialism?: string[];
          /**
           * Filter clients by their company-defined identifier.
           *
           * @maxLength 256
           */
          internalId?: string;
          /**
           * Filter clients by whether they are a GCA client.
           */
          ccsClient?: "true" | "false";
          /**
           * Filter clients by GCA client URN.
           *
           * @maxLength 256
           */
          ccsClientUrn?: string;
          /**
           * Filter clients by assigned users.
           *
           * @maxItems 25
           */
          assignee?: string[];
          /**
           * Filter clients by last contact date.
           */
          lastContactedDateFilter?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter clients with an active booking overlapping an absolute or relative date range.
           */
          bookedInRange?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter clients without an active booking overlapping an absolute or relative date range.
           */
          notBookedInRange?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter clients by tags they have.
           *
           * @maxItems 25
           */
          tags?: string[];
          /**
           * Find clients with coordinates relative to a location. A positive radius limits results to that distance in miles; omit it or use zero for no distance limit.
           */
          location?: {
            /**
             * @minimum -90
             * @maximum 90
             */
            lat: number | null;
            /**
             * @minimum -180
             * @maximum 180
             */
            lng: number | null;
            /**
             * @minimum 0
             * @maximum 500
             */
            radius?: number | null;
          };
          /**
           * Filter clients by an associated contact.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          ["contacts.contactId"]?: string;
        };
      };
    }
  | {
      /**
       * Whether the saved search is available to everyone in its company.
       */
      shared?: boolean;
      /**
       * The name shown when choosing the saved search.
       *
       * @minLength 1
       * @maxLength 128
       */
      name?: string;
      /**
       * A short explanation of what the saved search finds.
       *
       * @maxLength 512
       */
      description?: string;
      /**
       * The included table column identifiers in display order.
       *
       * @minItems 1
       * @maxItems 250
       */
      columns?: string[];
      type: "clientContacts";
      query?: {
        /**
         * Find relevant Client contacts using contact and Client details.
         *
         * @minLength 1
         * @maxLength 256
         */
        q?: string;
        /**
         * Search Client contacts by name, email, or Client.
         *
         * @maxLength 256
         */
        search?: string;
        /**
         * The field used to order contacts. Prefix with - for descending order.
         */
        sort?:
          | "forename"
          | "-forename"
          | "surname"
          | "-surname"
          | "email"
          | "-email"
          | "telephone"
          | "-telephone"
          | "role"
          | "-role"
          | "status"
          | "-status"
          | "decisionMaker"
          | "-decisionMaker"
          | "clientName"
          | "-clientName"
          | "clientAddress.city"
          | "-clientAddress.city"
          | "clientAddress.county"
          | "-clientAddress.county"
          | "clientAddress.postcode"
          | "-clientAddress.postcode"
          | "clientLea"
          | "-clientLea"
          | "clientType"
          | "-clientType"
          | "clientMultiAcademyTrust"
          | "-clientMultiAcademyTrust"
          | "clientBorough"
          | "-clientBorough"
          | "clientAssignee"
          | "-clientAssignee"
          | "clientSpecialism"
          | "-clientSpecialism"
          | "clientUpdatedAt"
          | "-clientUpdatedAt"
          | "distance"
          | "-distance"
          | "relevanceScore"
          | "-relevanceScore";
        /**
         * The fields to include in the response in dot notation format.
         *
         * @maxItems 250
         */
        select?: (
          | "id"
          | "forename"
          | "surname"
          | "email"
          | "telephone"
          | "mobile"
          | "extension"
          | "role"
          | "roleOther"
          | "contactId"
          | "timesheetPortal.id"
          | "timesheetPortal.code"
          | "timesheetPortal"
          | "optOut"
          | "note"
          | "decisionMaker"
          | "status"
          | "clientId"
          | "clientName"
          | "clientAddress.addressLine1"
          | "clientAddress.addressLine2"
          | "clientAddress.addressLine3"
          | "clientAddress.city"
          | "clientAddress.county"
          | "clientAddress.borough"
          | "clientAddress.postcode"
          | "clientAddress.country"
          | "clientAddress.lat"
          | "clientAddress.lng"
          | "clientAddress"
          | "clientLea"
          | "clientType"
          | "clientMultiAcademyTrust"
          | "clientBorough"
          | "clientAssignee"
          | "clientAssignee.id"
          | "clientAssignee.email"
          | "clientAssignee.firstName"
          | "clientAssignee.lastName"
          | "clientAssignee.username"
          | "clientAssignee.lea"
          | "clientAssignee.clients"
          | "clientAssignee.position"
          | "clientAssignee.permissions"
          | "clientAssignee.activationCode"
          | "clientAssignee.customer"
          | "clientAssignee.disabled"
          | "clientAssignee.commissionThreshold"
          | "clientAssignee.branches"
          | "clientAssignee.displayOnboarding"
          | "clientAssignee._customId"
          | "clientAssignee.signature"
          | "clientAssignee.phone"
          | "clientAssignee.defaultCandidateWatcher"
          | "clientAssignee.defaultTaskWorkspace"
          | "clientAssignee.teamAdmin"
          | "clientAssignee.contact"
          | "clientAssignee.contact.id"
          | "clientAssignee.contact.forename"
          | "clientAssignee.contact.surname"
          | "clientAssignee.contact.email"
          | "clientAssignee.contact.telephone"
          | "clientAssignee.contact.mobile"
          | "clientAssignee.contact.extension"
          | "clientAssignee.createdAt"
          | "clientAssignee.evertime.id"
          | "clientAssignee.evertime"
          | "clientAssignee.timesheetPortal.id"
          | "clientAssignee.timesheetPortal.code"
          | "clientAssignee.timesheetPortal"
          | "clientSpecialism"
          | "clientUpdatedAt"
          | "distance"
          | "relevanceScore"
          | "matchReasons"
          | ""
        )[];
        /**
         * Related fields to include as objects in the response.
         *
         * @maxItems 1
         */
        expand?: "clientAssignee"[];
        filter?: {
          /**
           * Filter contacts by status.
           *
           * @maxItems 3
           */
          status?: ("Active" | "Passive" | "Inactive")[];
          /**
           * Filter contacts by role.
           *
           * @maxItems 44
           */
          role?: (
            | "Head Teacher"
            | "Deputy Head"
            | "Cover Manager"
            | "HR Manager"
            | "Finance Manager"
            | "Head of Maths"
            | "Head of Science"
            | "Head of English"
            | "Assistant Headteacher"
            | "Headteacher's PA"
            | "SENCO"
            | "Other"
            | "Business Manager"
            | "Business Administrator"
            | "Invoice Contact"
            | "Academy Recruitment Contact"
            | "Nursery Manager"
            | "CEO"
            | "COO"
            | "Executive Headteacher"
            | "Inclusion Manager"
            | "EYFS Lead"
            | "Admin Team"
            | "Head of Humanities"
            | "Head of History"
            | "Head of Geography"
            | "Head of Business Studies"
            | "Head of PE"
            | "Head of KS1"
            | "Head of KS2"
            | "Head of KS3"
            | "Head of KS4"
            | "Virtual School Headteacher"
            | "Deputy Virtual School Headteacher"
            | "Post-16 Team Leader"
            | "SEN Team"
            | "Inclusion Team"
            | "UASC Team"
            | "Caseworker"
            | "Designated Teacher"
            | "Attendance Officer"
            | "Coordinator"
            | "Children's Commissioner"
            | "Timesheet Contact"
          )[];
          /**
           * Filter contacts by forename.
           *
           * @maxLength 128
           */
          forename?: string;
          /**
           * Filter contacts by surname.
           *
           * @maxLength 128
           */
          surname?: string;
          /**
           * Filter contacts by email address.
           *
           * @maxLength 256
           */
          email?: string;
          /**
           * Filter contacts by decision-maker status.
           */
          decisionMaker?: "true" | "false";
          /**
           * Filter contacts by opt-out status.
           */
          optOut?: "true" | "false";
          /**
           * Filter contacts by Client.
           *
           * @maxItems 50
           */
          clientId?: string[];
          /**
           * Filter contacts by Client name.
           *
           * @maxLength 256
           */
          clientName?: string;
          /**
           * Filter contacts by associated Client address fields.
           */
          clientAddress?: {
            /**
             * @maxLength 128
             */
            city?: string;
            /**
             * @maxLength 128
             */
            county?: string;
            /**
             * @maxLength 32
             */
            postcode?: string;
          };
          /**
           * Filter contacts by Client local education authority.
           *
           * @maxLength 128
           */
          clientLea?: string;
          /**
           * Filter contacts by Client sector.
           *
           * @maxLength 128
           */
          clientType?: string;
          /**
           * Filter contacts by Client multi-academy trust.
           *
           * @maxLength 256
           */
          clientMultiAcademyTrust?: string;
          /**
           * Filter contacts by Client borough.
           *
           * @maxLength 128
           */
          clientBorough?: string;
          /**
           * Filter contacts by Client assignee.
           *
           * @maxItems 25
           */
          clientAssignee?: string[];
          /**
           * Filter contacts by Client specialism.
           *
           * @maxItems 25
           */
          clientSpecialism?: string[];
          /**
           * Filter contacts by when their Client was updated.
           */
          clientUpdatedAt?: {
            /**
             * The inclusive absolute start of the date range.
             *
             * @format date-time
             */
            from?: string | null;
            /**
             * The inclusive absolute end of the date range.
             *
             * @format date-time
             */
            to?: string | null;
            /**
             * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
             */
            relative?: {
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              from: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
              /**
               * A date boundary calculated from the current day, week, or month when the query runs.
               */
              to: {
                /**
                 * The current calendar point from which a relative date boundary is calculated.
                 */
                anchor: "today" | "startOfWeek" | "startOfMonth";
                /**
                 * The number of units before or after the selected calendar anchor.
                 *
                 * @minimum -120
                 * @maximum 120
                 */
                offset: number | null;
                /**
                 * The unit used to offset a relative date boundary.
                 */
                unit: "days" | "weeks" | "months";
              };
            };
          };
          /**
           * Filter contacts by distance from their Client address.
           */
          location?: {
            /**
             * @minimum -90
             * @maximum 90
             */
            lat: number | null;
            /**
             * @minimum -180
             * @maximum 180
             */
            lng: number | null;
            /**
             * @minimum 0
             * @exclusiveMinimum true
             * @maximum 500
             */
            radius: number;
          };
        };
      };
    };

export type UpdateSavedSearchVariables = {
  body?: UpdateSavedSearchRequestBody;
  pathParams: UpdateSavedSearchPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateSavedSearch = (
  variables: UpdateSavedSearchVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateSavedSearchResponse,
    UpdateSavedSearchError,
    UpdateSavedSearchRequestBody,
    {},
    {},
    UpdateSavedSearchPathParams
  >({
    url: "/saved-searches/{savedSearchId}",
    method: "patch",
    ...variables,
    signal,
  });

export const useUpdateSavedSearch = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateSavedSearchResponse,
      UpdateSavedSearchError,
      UpdateSavedSearchVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateSavedSearchResponse,
    UpdateSavedSearchError,
    UpdateSavedSearchVariables
  >({
    mutationFn: (variables: UpdateSavedSearchVariables) =>
      fetchUpdateSavedSearch(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type DeleteSavedSearchPathParams = {
  /**
   * The unique identifier of the saved search.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  savedSearchId: string;
};

export type DeleteSavedSearchError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type DeleteSavedSearchVariables = {
  pathParams: DeleteSavedSearchPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchDeleteSavedSearch = (
  variables: DeleteSavedSearchVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    DeleteSavedSearchError,
    undefined,
    {},
    {},
    DeleteSavedSearchPathParams
  >({
    url: "/saved-searches/{savedSearchId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useDeleteSavedSearch = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteSavedSearchError,
      DeleteSavedSearchVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    DeleteSavedSearchError,
    DeleteSavedSearchVariables
  >({
    mutationFn: (variables: DeleteSavedSearchVariables) =>
      fetchDeleteSavedSearch(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetBranchesQueryParams = {
  /**
   * The fields to include in each branch in dot notation format
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "name"
    | "code"
    | "contactEmail"
    | "contactTelephone"
    | "contactWebsite"
    | "customerAccentColor"
    | "address.addressLine1"
    | "address.addressLine2"
    | "address.addressLine3"
    | "address.city"
    | "address.county"
    | "address.borough"
    | "address.postcode"
    | "address.country"
    | "address.lat"
    | "address.lng"
    | "address"
    | "archived"
    | "userCount"
    | ""
  )[];
  /**
   * Search branches by name or code
   *
   * @maxLength 256
   */
  search?: string;
  /**
   * Filter branches by their properties
   */
  filter?: {
    /**
     * Filter branches by a list of IDs
     *
     * @maxItems 50
     */
    ids?: string[];
    /**
     * Filter branches by their archived status
     */
    archived?: "true" | "false";
  };
  /**
   * The field and direction used to sort branches
   */
  sort?: "name" | "-name" | "code" | "-code";
  /**
   * Page number
   *
   * @minimum 1
   */
  page?: number;
  /**
   * Number of branches per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
};

export type GetBranchesError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetBranchesResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the branch
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the branch
     *
     * @maxLength 256
     */
    name?: string;
    /**
     * The code of the branch
     *
     * @maxLength 32
     */
    code?: string;
    /**
     * The contact email of the branch
     *
     * @maxLength 256
     * @format email
     */
    contactEmail?: string;
    /**
     * The contact phone number of the branch
     *
     * @maxLength 32
     */
    contactTelephone?: string;
    /**
     * The contact website of the branch
     *
     * @maxLength 512
     */
    contactWebsite?: string;
    /**
     * The accent color for the customer that the branch belongs to
     *
     * @maxLength 7
     */
    customerAccentColor?: string;
    /**
     * The address of the branch
     */
    address?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * Indicates whether the branch is archived
     */
    archived?: boolean;
    /**
     * The number of users assigned to the branch. Only returned when explicitly selected.
     *
     * @minimum 0
     */
    userCount?: number;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetBranchesVariables = {
  queryParams?: GetBranchesQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetBranches = (
  variables: GetBranchesVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetBranchesResponse,
    GetBranchesError,
    undefined,
    {},
    GetBranchesQueryParams,
    {}
  >({ url: "/branches", method: "get", ...variables, signal });

export function getBranchesQuery(variables: GetBranchesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetBranchesResponse>;
};

export function getBranchesQuery(
  variables: GetBranchesVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetBranchesResponse>)
    | reactQuery.SkipToken;
};

export function getBranchesQuery(
  variables: GetBranchesVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/branches",
      operationId: "getBranches",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetBranches(variables, signal),
  };
}

export const useSuspenseGetBranches = <TData = GetBranchesResponse,>(
  variables: GetBranchesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetBranchesResponse, GetBranchesError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetBranchesResponse,
    GetBranchesError,
    TData
  >({
    ...getBranchesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetBranches = <TData = GetBranchesResponse,>(
  variables: GetBranchesVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetBranchesResponse, GetBranchesError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetBranchesResponse, GetBranchesError, TData>({
    ...getBranchesQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateBranchError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateBranchResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the branch
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the branch
     *
     * @maxLength 256
     */
    name?: string;
    /**
     * The code of the branch
     *
     * @maxLength 32
     */
    code?: string;
    /**
     * The contact email of the branch
     *
     * @maxLength 256
     * @format email
     */
    contactEmail?: string;
    /**
     * The contact phone number of the branch
     *
     * @maxLength 32
     */
    contactTelephone?: string;
    /**
     * The contact website of the branch
     *
     * @maxLength 512
     */
    contactWebsite?: string;
    /**
     * The accent color for the customer that the branch belongs to
     *
     * @maxLength 7
     */
    customerAccentColor?: string;
    /**
     * The address of the branch
     */
    address?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * Indicates whether the branch is archived
     */
    archived?: boolean;
    /**
     * The number of users assigned to the branch. Only returned when explicitly selected.
     *
     * @minimum 0
     */
    userCount?: number;
  };
};

export type CreateBranchRequestBody = {
  /**
   * The contact email of the branch
   *
   * @maxLength 256
   * @format email
   */
  contactEmail?: string;
  /**
   * The contact phone number of the branch
   *
   * @maxLength 32
   */
  contactTelephone?: string;
  /**
   * The contact website of the branch
   *
   * @maxLength 512
   */
  contactWebsite?: string;
  /**
   * The accent color for the customer that the branch belongs to
   *
   * @maxLength 7
   */
  customerAccentColor?: string;
  /**
   * The name of the branch
   *
   * @minLength 1
   * @maxLength 256
   */
  name: string;
  /**
   * The code of the branch
   *
   * @minLength 1
   * @maxLength 32
   */
  code: string;
  /**
   * The address of the branch. Address line 1 is required.
   */
  address: {
    /**
     * The first line of the address
     *
     * @minLength 1
     * @maxLength 256
     */
    addressLine1: string;
    /**
     * The second line of the address
     *
     * @maxLength 256
     */
    addressLine2?: string;
    /**
     * The third line of the address
     *
     * @maxLength 256
     */
    addressLine3?: string;
    /**
     * The city of the address
     *
     * @maxLength 256
     */
    city?: string;
    /**
     * The county of the address
     *
     * @maxLength 256
     */
    county?: string;
    /**
     * The borough of the address
     *
     * @maxLength 256
     */
    borough?: string;
    /**
     * The postcode of the address
     *
     * @maxLength 10
     */
    postcode?: string;
    /**
     * The country of the address
     *
     * @maxLength 256
     */
    country?: string;
    /**
     * The latitude of the address
     *
     * @minimum -90
     * @maximum 90
     */
    lat?: number;
    /**
     * The longitude of the address
     *
     * @minimum -180
     * @maximum 180
     */
    lng?: number;
  };
};

export type CreateBranchVariables = {
  body: CreateBranchRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateBranch = (
  variables: CreateBranchVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateBranchResponse,
    CreateBranchError,
    CreateBranchRequestBody,
    {},
    {},
    {}
  >({ url: "/branches", method: "post", ...variables, signal });

export const useCreateBranch = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateBranchResponse,
      CreateBranchError,
      CreateBranchVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateBranchResponse,
    CreateBranchError,
    CreateBranchVariables
  >({
    mutationFn: (variables: CreateBranchVariables) =>
      fetchCreateBranch(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetBranchPathParams = {
  /**
   * The unique identifier of the branch
   *
   * @pattern ^[0-9a-f]{24}$
   */
  branchId: string;
};

export type GetBranchQueryParams = {
  /**
   * The fields to include in each branch in dot notation format
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "name"
    | "code"
    | "contactEmail"
    | "contactTelephone"
    | "contactWebsite"
    | "customerAccentColor"
    | "address.addressLine1"
    | "address.addressLine2"
    | "address.addressLine3"
    | "address.city"
    | "address.county"
    | "address.borough"
    | "address.postcode"
    | "address.country"
    | "address.lat"
    | "address.lng"
    | "address"
    | "archived"
    | "userCount"
    | ""
  )[];
};

export type GetBranchError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetBranchResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the branch
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the branch
     *
     * @maxLength 256
     */
    name?: string;
    /**
     * The code of the branch
     *
     * @maxLength 32
     */
    code?: string;
    /**
     * The contact email of the branch
     *
     * @maxLength 256
     * @format email
     */
    contactEmail?: string;
    /**
     * The contact phone number of the branch
     *
     * @maxLength 32
     */
    contactTelephone?: string;
    /**
     * The contact website of the branch
     *
     * @maxLength 512
     */
    contactWebsite?: string;
    /**
     * The accent color for the customer that the branch belongs to
     *
     * @maxLength 7
     */
    customerAccentColor?: string;
    /**
     * The address of the branch
     */
    address?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * Indicates whether the branch is archived
     */
    archived?: boolean;
    /**
     * The number of users assigned to the branch. Only returned when explicitly selected.
     *
     * @minimum 0
     */
    userCount?: number;
  };
};

export type GetBranchVariables = {
  pathParams: GetBranchPathParams;
  queryParams?: GetBranchQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetBranch = (
  variables: GetBranchVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetBranchResponse,
    GetBranchError,
    undefined,
    {},
    GetBranchQueryParams,
    GetBranchPathParams
  >({ url: "/branches/{branchId}", method: "get", ...variables, signal });

export function getBranchQuery(variables: GetBranchVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetBranchResponse>;
};

export function getBranchQuery(
  variables: GetBranchVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetBranchResponse>)
    | reactQuery.SkipToken;
};

export function getBranchQuery(
  variables: GetBranchVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/branches/{branchId}",
      operationId: "getBranch",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetBranch(variables, signal),
  };
}

export const useSuspenseGetBranch = <TData = GetBranchResponse,>(
  variables: GetBranchVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetBranchResponse, GetBranchError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<GetBranchResponse, GetBranchError, TData>({
    ...getBranchQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetBranch = <TData = GetBranchResponse,>(
  variables: GetBranchVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetBranchResponse, GetBranchError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetBranchResponse, GetBranchError, TData>({
    ...getBranchQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateBranchPathParams = {
  /**
   * The unique identifier of the branch
   *
   * @pattern ^[0-9a-f]{24}$
   */
  branchId: string;
};

export type UpdateBranchError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateBranchResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier for the branch
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The name of the branch
     *
     * @maxLength 256
     */
    name?: string;
    /**
     * The code of the branch
     *
     * @maxLength 32
     */
    code?: string;
    /**
     * The contact email of the branch
     *
     * @maxLength 256
     * @format email
     */
    contactEmail?: string;
    /**
     * The contact phone number of the branch
     *
     * @maxLength 32
     */
    contactTelephone?: string;
    /**
     * The contact website of the branch
     *
     * @maxLength 512
     */
    contactWebsite?: string;
    /**
     * The accent color for the customer that the branch belongs to
     *
     * @maxLength 7
     */
    customerAccentColor?: string;
    /**
     * The address of the branch
     */
    address?: {
      /**
       * The first line of the address
       *
       * @maxLength 256
       */
      addressLine1?: string;
      /**
       * The second line of the address
       *
       * @maxLength 256
       */
      addressLine2?: string;
      /**
       * The third line of the address
       *
       * @maxLength 256
       */
      addressLine3?: string;
      /**
       * The city of the address
       *
       * @maxLength 256
       */
      city?: string;
      /**
       * The county of the address
       *
       * @maxLength 256
       */
      county?: string;
      /**
       * The borough of the address
       *
       * @maxLength 256
       */
      borough?: string;
      /**
       * The postcode of the address
       *
       * @maxLength 10
       */
      postcode?: string;
      /**
       * The country of the address
       *
       * @maxLength 256
       */
      country?: string;
      /**
       * The latitude of the address
       *
       * @minimum -90
       * @maximum 90
       */
      lat?: number;
      /**
       * The longitude of the address
       *
       * @minimum -180
       * @maximum 180
       */
      lng?: number;
    };
    /**
     * Indicates whether the branch is archived
     */
    archived?: boolean;
    /**
     * The number of users assigned to the branch. Only returned when explicitly selected.
     *
     * @minimum 0
     */
    userCount?: number;
  };
};

export type UpdateBranchRequestBody = {
  /**
   * The name of the branch
   *
   * @minLength 1
   * @maxLength 256
   */
  name?: string;
  /**
   * The code of the branch. It cannot be cleared.
   *
   * @minLength 1
   * @maxLength 32
   */
  code?: string;
  /**
   * The contact email of the branch. Send null to clear it.
   *
   * @maxLength 256
   * @format email
   */
  contactEmail?: string | null;
  /**
   * The contact phone number of the branch. Send null to clear it.
   *
   * @maxLength 32
   */
  contactTelephone?: string | null;
  /**
   * The contact website of the branch. Send null to clear it.
   *
   * @maxLength 512
   */
  contactWebsite?: string | null;
  /**
   * The accent color for the customer that the branch belongs to. Send null to clear it.
   *
   * @maxLength 7
   */
  customerAccentColor?: string | null;
  /**
   * The address of the branch. Replaces the whole address; it cannot be cleared.
   */
  address?: {
    /**
     * The first line of the address
     *
     * @minLength 1
     * @maxLength 256
     */
    addressLine1: string;
    /**
     * The second line of the address
     *
     * @maxLength 256
     */
    addressLine2?: string;
    /**
     * The third line of the address
     *
     * @maxLength 256
     */
    addressLine3?: string;
    /**
     * The city of the address
     *
     * @maxLength 256
     */
    city?: string;
    /**
     * The county of the address
     *
     * @maxLength 256
     */
    county?: string;
    /**
     * The borough of the address
     *
     * @maxLength 256
     */
    borough?: string;
    /**
     * The postcode of the address
     *
     * @maxLength 10
     */
    postcode?: string;
    /**
     * The country of the address
     *
     * @maxLength 256
     */
    country?: string;
    /**
     * The latitude of the address
     *
     * @minimum -90
     * @maximum 90
     */
    lat?: number;
    /**
     * The longitude of the address
     *
     * @minimum -180
     * @maximum 180
     */
    lng?: number;
  };
  /**
   * Users to assign to or unassign from a branch
   */
  users?: {
    /**
     * IDs of users to assign to the branch
     *
     * @maxItems 100
     */
    add?: string[];
    /**
     * IDs of users to unassign from the branch
     *
     * @maxItems 100
     */
    remove?: string[];
  };
};

export type UpdateBranchVariables = {
  body?: UpdateBranchRequestBody;
  pathParams: UpdateBranchPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateBranch = (
  variables: UpdateBranchVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateBranchResponse,
    UpdateBranchError,
    UpdateBranchRequestBody,
    {},
    {},
    UpdateBranchPathParams
  >({ url: "/branches/{branchId}", method: "patch", ...variables, signal });

export const useUpdateBranch = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateBranchResponse,
      UpdateBranchError,
      UpdateBranchVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateBranchResponse,
    UpdateBranchError,
    UpdateBranchVariables
  >({
    mutationFn: (variables: UpdateBranchVariables) =>
      fetchUpdateBranch(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type DeleteBranchPathParams = {
  /**
   * The unique identifier of the branch
   *
   * @pattern ^[0-9a-f]{24}$
   */
  branchId: string;
};

export type DeleteBranchError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type DeleteBranchVariables = {
  pathParams: DeleteBranchPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchDeleteBranch = (
  variables: DeleteBranchVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    DeleteBranchError,
    undefined,
    {},
    {},
    DeleteBranchPathParams
  >({ url: "/branches/{branchId}", method: "delete", ...variables, signal });

export const useDeleteBranch = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeleteBranchError,
      DeleteBranchVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    DeleteBranchError,
    DeleteBranchVariables
  >({
    mutationFn: (variables: DeleteBranchVariables) =>
      fetchDeleteBranch(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetExportConfigPathParams = {
  /**
   * The type of export config
   */
  type: "paymentRuns";
};

export type GetExportConfigQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "customer"
    | "type"
    | "config"
    | "showHeaders"
    | "layout"
    | "includeParentRow"
    | "includeSummaryRow"
    | "fiscalYearStartMonth"
    | "vatPercent"
    | ""
  )[];
};

export type GetExportConfigError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetExportConfigResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * Customer that owns this configuration
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customer?: string;
    /**
     * The type of export this config applies to
     */
    type?: "paymentRuns";
    /**
     * Contains the configuration items themselves
     */
    config?: {
      /**
       * The name of the datapoint in the exported file
       *
       * @maxLength 1000
       */
      header?: string;
      /**
       * Database data from which to derive
       */
      value?:
        | (
            | "candidate.candidateId"
            | "candidate.candidateName"
            | "candidate.timesheetId"
            | "candidate.timesheetInvoiceNumber"
            | "candidate.timesheetDate"
            | "candidate.timesheetNumber"
            | "candidate.timesheetStart"
            | "candidate.timesheetEnd"
            | "candidate.timesheetLastDay"
            | "candidate.timesheetApprover"
            | "candidate.timesheetLastSunday"
            | "candidate.timesheetStudentName"
            | "candidate.customClientId"
            | "candidate.customClientInternalId"
            | "candidate.clientName"
            | "candidate.clientId"
            | "candidate.customCandidateId"
            | "candidate.customCandidateInternalId"
            | "candidate.customCandidateIdPayType"
            | "candidate.clientContact"
            | "candidate.invoiceContact"
            | "candidate.total"
            | "candidate.peDate"
            | "candidate.bookingPayType"
            | "candidate.payRate"
            | "candidate.chargeRate"
            | "candidate.units"
            | "candidate.hours"
            | "candidate.hoursPerDay"
            | "candidate.consultant"
            | "candidate.role"
            | "candidate.clientTimesheetStatus"
            | "candidate.isCandidateFirstBooking"
            | "candidate.isCandidateFirstBookingWithClient"
            | "candidate.isClientFirstBooking"
            | "candidate.consultant1Id"
            | "candidate.consultant1Perc"
            | "candidate.consultant2Id"
            | "candidate.consultant2Perc"
            | "candidate.consultant3Id"
            | "candidate.consultant3Perc"
            | "candidate.consultant4Id"
            | "candidate.consultant4Perc"
            | "candidate.consultant5Id"
            | "candidate.consultant5Perc"
            | "candidate.placementId"
            | "candidate.officeNumber"
            | "candidate.poNumber"
            | "candidate.payInterval"
            | "candidate._internalId"
            | "candidate.candidatePayType"
            | "candidate.umbrellaName"
            | "candidate.umbrellaCode"
            | "candidate.umbrellaId"
            | "candidate.bookingStartDate"
            | "candidate.assigneeBranchCodes"
            | "candidate.niNumberTrimmed"
            | "candidate.sortCodeTrimmed"
            | "candidate.accountNumberTrimmed"
            | "candidate.accountNameTrimmed"
            | "candidate.bookingOnCosts"
            | "candidate.payRateExclusiveHoliday"
            | "candidate.payRateHolidayPay"
            | "candidate.payeRate"
            | "candidate.payeMargin"
            | "candidate.payeWeeklyMargin"
            | "candidate.grossMargin"
            | "candidate.totalCharge"
            | "candidate.totalExcludingHolidayPay"
            | "candidate.totalHolidayPay"
            | "candidate.candidateDetails.title"
            | "candidate.candidateDetails.forename"
            | "candidate.candidateDetails.surname"
            | "candidate.candidateDetails.sex"
            | "candidate.candidateDetails.dob"
            | "candidate.candidateDetails.email"
            | "candidate.candidateDetails.telephone"
            | "candidate.candidateDetails.mobile"
            | "candidate.candidateDetails.address.addressLine1"
            | "candidate.candidateDetails.address.postcode"
            | "candidate.candidateDetails.gender"
            | "candidate.candidateDetails.bankDetails.niNumber"
            | "candidate.days.start"
            | "candidate.days.end"
            | "candidate.days.length"
            | "candidate.days.amPm"
            | "candidate.days.status"
            | "candidate.days.chargeRate"
            | "candidate.days.payRate"
            | "candidate.days.flaggedForRemoval"
            | "day.start"
            | "day.end"
            | "day.length"
            | "day.amPm"
            | "day.status"
            | "day.chargeRate"
            | "day.payRate"
            | "day.flaggedForRemoval"
            | "candidate.umbrellaAddress.addressLine1"
            | "candidate.umbrellaAddress.addressLine2"
            | "candidate.umbrellaAddress.addressLine3"
            | "candidate.umbrellaAddress.city"
            | "candidate.umbrellaAddress.county"
            | "candidate.umbrellaAddress.borough"
            | "candidate.umbrellaAddress.postcode"
            | "candidate.umbrellaAddress.country"
            | "candidate.umbrellaAddress.lat"
            | "candidate.umbrellaAddress.lng"
            | "candidate.bankDetails.bankAccountName"
            | "candidate.bankDetails.bankName"
            | "candidate.bankDetails.accountNumber"
            | "candidate.bankDetails.sortCode"
            | "candidate.bankDetails.niNumber"
            | "candidate.bankDetails.jobOp1"
            | "candidate.bankDetails.jobOp2"
            | "candidate.bankDetails.jobOp3"
            | "candidate.bankDetails.studentLoan.studentLoan"
            | "candidate.bankDetails.studentLoan.plan1"
            | "candidate.bankDetails.studentLoan.plan2"
            | "candidate.bankDetails.studentLoan.plan4"
            | "candidate.bankDetails.studentLoan.postGradLoan"
            | "candidate.timesheets.id"
            | "candidate.timesheets.candidateName"
            | "candidate.timesheets.candidate"
            | "candidate.timesheets.clientName"
            | "candidate.timesheets.clientEmail"
            | "candidate.timesheets.client"
            | "candidate.timesheets.booking"
            | "candidate.timesheets.days.start"
            | "candidate.timesheets.days.end"
            | "candidate.timesheets.days.length"
            | "candidate.timesheets.days.amPm"
            | "candidate.timesheets.days.status"
            | "candidate.timesheets.days.chargeRate"
            | "candidate.timesheets.days.payRate"
            | "candidate.timesheets.days.flaggedForRemoval"
            | "candidate.timesheets.status"
            | "candidate.timesheets.statusChangedOn"
            | "candidate.timesheets.statusChangeBy"
            | "candidate.timesheets.statusChangeByJobRole"
            | "candidate.timesheets.statusChangeBySignature"
            | "candidate.timesheets.statusChangeByDrawnSignature"
            | "candidate.timesheets.statusChangeBySignatureDate"
            | "candidate.timesheets.rejectionReason"
            | "candidate.timesheets.statusCandidate"
            | "candidate.timesheets.statusChangedOnCandidate"
            | "candidate.timesheets.statusChangeByCandidate"
            | "candidate.timesheets.statusChangeByJobRoleCandidate"
            | "candidate.timesheets.statusChangeByCandidateSignature"
            | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
            | "candidate.timesheets.statusChangeByCandidateSignatureDate"
            | "candidate.timesheets.rejectionReasonCandidate"
            | "candidate.timesheets.createdBy"
            | "candidate.timesheets.group"
            | "candidate.timesheets.resendTimesheetEmail"
            | "candidate.timesheets.customer"
            | "candidate.timesheets.assignee"
            | "candidate.timesheets.queryResponse"
            | "candidate.timesheets.invoiceNumber"
            | "candidate.timesheets.timesheetNumber"
            | "candidate.timesheets.type"
            | "candidate.timesheets.subType"
            | "candidate.timesheets.tuitionReport"
            | "candidate.timesheets.tuitionReportAnswers.questionId"
            | "candidate.timesheets.tuitionReportAnswers.value"
            | "candidate.timesheets.tuitionReportPending"
            | "candidate.timesheets.role"
            | "candidate.timesheets.statusApprovalIp"
            | "candidate.timesheets.statusApprovalCandidateIp"
          )
        | "";
      /**
       * Format to use for datetime fields
       */
      date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
      /**
       * Flush
       */
      flush?: boolean;
      /**
       * Value written to the export when the selected source field has no value.
       *
       * @maxLength 1000
       */
      defaultValue?: string;
      /**
       * Match
       */
      match?: {
        /**
         * @maxLength 1000
         */
        field?: string;
        /**
         * @maxLength 1000
         */
        match?: string;
        /**
         * @maxLength 1000
         */
        display?: string;
        /**
         * @maxLength 1000
         */
        displayValue?: string;
      };
      /**
       * Text replacement/processing to perform on this config item
       *
       * @maxItems 5
       */
      conditional?: {
        /**
         * Database data from which to derive
         */
        value?:
          | "candidate.candidateId"
          | "candidate.candidateName"
          | "candidate.timesheetId"
          | "candidate.timesheetInvoiceNumber"
          | "candidate.timesheetDate"
          | "candidate.timesheetNumber"
          | "candidate.timesheetStart"
          | "candidate.timesheetEnd"
          | "candidate.timesheetLastDay"
          | "candidate.timesheetApprover"
          | "candidate.timesheetLastSunday"
          | "candidate.timesheetStudentName"
          | "candidate.customClientId"
          | "candidate.customClientInternalId"
          | "candidate.clientName"
          | "candidate.clientId"
          | "candidate.customCandidateId"
          | "candidate.customCandidateInternalId"
          | "candidate.customCandidateIdPayType"
          | "candidate.clientContact"
          | "candidate.invoiceContact"
          | "candidate.total"
          | "candidate.peDate"
          | "candidate.bookingPayType"
          | "candidate.payRate"
          | "candidate.chargeRate"
          | "candidate.units"
          | "candidate.hours"
          | "candidate.hoursPerDay"
          | "candidate.consultant"
          | "candidate.role"
          | "candidate.clientTimesheetStatus"
          | "candidate.isCandidateFirstBooking"
          | "candidate.isCandidateFirstBookingWithClient"
          | "candidate.isClientFirstBooking"
          | "candidate.consultant1Id"
          | "candidate.consultant1Perc"
          | "candidate.consultant2Id"
          | "candidate.consultant2Perc"
          | "candidate.consultant3Id"
          | "candidate.consultant3Perc"
          | "candidate.consultant4Id"
          | "candidate.consultant4Perc"
          | "candidate.consultant5Id"
          | "candidate.consultant5Perc"
          | "candidate.placementId"
          | "candidate.officeNumber"
          | "candidate.poNumber"
          | "candidate.payInterval"
          | "candidate._internalId"
          | "candidate.candidatePayType"
          | "candidate.umbrellaName"
          | "candidate.umbrellaCode"
          | "candidate.umbrellaId"
          | "candidate.bookingStartDate"
          | "candidate.assigneeBranchCodes"
          | "candidate.niNumberTrimmed"
          | "candidate.sortCodeTrimmed"
          | "candidate.accountNumberTrimmed"
          | "candidate.accountNameTrimmed"
          | "candidate.bookingOnCosts"
          | "candidate.payRateExclusiveHoliday"
          | "candidate.payRateHolidayPay"
          | "candidate.payeRate"
          | "candidate.payeMargin"
          | "candidate.payeWeeklyMargin"
          | "candidate.grossMargin"
          | "candidate.totalCharge"
          | "candidate.totalExcludingHolidayPay"
          | "candidate.totalHolidayPay"
          | "candidate.candidateDetails.title"
          | "candidate.candidateDetails.forename"
          | "candidate.candidateDetails.surname"
          | "candidate.candidateDetails.sex"
          | "candidate.candidateDetails.dob"
          | "candidate.candidateDetails.email"
          | "candidate.candidateDetails.telephone"
          | "candidate.candidateDetails.mobile"
          | "candidate.candidateDetails.address.addressLine1"
          | "candidate.candidateDetails.address.postcode"
          | "candidate.candidateDetails.gender"
          | "candidate.candidateDetails.bankDetails.niNumber"
          | "candidate.days.start"
          | "candidate.days.end"
          | "candidate.days.length"
          | "candidate.days.amPm"
          | "candidate.days.status"
          | "candidate.days.chargeRate"
          | "candidate.days.payRate"
          | "candidate.days.flaggedForRemoval"
          | "day.start"
          | "day.end"
          | "day.length"
          | "day.amPm"
          | "day.status"
          | "day.chargeRate"
          | "day.payRate"
          | "day.flaggedForRemoval"
          | "candidate.umbrellaAddress.addressLine1"
          | "candidate.umbrellaAddress.addressLine2"
          | "candidate.umbrellaAddress.addressLine3"
          | "candidate.umbrellaAddress.city"
          | "candidate.umbrellaAddress.county"
          | "candidate.umbrellaAddress.borough"
          | "candidate.umbrellaAddress.postcode"
          | "candidate.umbrellaAddress.country"
          | "candidate.umbrellaAddress.lat"
          | "candidate.umbrellaAddress.lng"
          | "candidate.bankDetails.bankAccountName"
          | "candidate.bankDetails.bankName"
          | "candidate.bankDetails.accountNumber"
          | "candidate.bankDetails.sortCode"
          | "candidate.bankDetails.niNumber"
          | "candidate.bankDetails.jobOp1"
          | "candidate.bankDetails.jobOp2"
          | "candidate.bankDetails.jobOp3"
          | "candidate.bankDetails.studentLoan.studentLoan"
          | "candidate.bankDetails.studentLoan.plan1"
          | "candidate.bankDetails.studentLoan.plan2"
          | "candidate.bankDetails.studentLoan.plan4"
          | "candidate.bankDetails.studentLoan.postGradLoan"
          | "candidate.timesheets.id"
          | "candidate.timesheets.candidateName"
          | "candidate.timesheets.candidate"
          | "candidate.timesheets.clientName"
          | "candidate.timesheets.clientEmail"
          | "candidate.timesheets.client"
          | "candidate.timesheets.booking"
          | "candidate.timesheets.days.start"
          | "candidate.timesheets.days.end"
          | "candidate.timesheets.days.length"
          | "candidate.timesheets.days.amPm"
          | "candidate.timesheets.days.status"
          | "candidate.timesheets.days.chargeRate"
          | "candidate.timesheets.days.payRate"
          | "candidate.timesheets.days.flaggedForRemoval"
          | "candidate.timesheets.status"
          | "candidate.timesheets.statusChangedOn"
          | "candidate.timesheets.statusChangeBy"
          | "candidate.timesheets.statusChangeByJobRole"
          | "candidate.timesheets.statusChangeBySignature"
          | "candidate.timesheets.statusChangeByDrawnSignature"
          | "candidate.timesheets.statusChangeBySignatureDate"
          | "candidate.timesheets.rejectionReason"
          | "candidate.timesheets.statusCandidate"
          | "candidate.timesheets.statusChangedOnCandidate"
          | "candidate.timesheets.statusChangeByCandidate"
          | "candidate.timesheets.statusChangeByJobRoleCandidate"
          | "candidate.timesheets.statusChangeByCandidateSignature"
          | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
          | "candidate.timesheets.statusChangeByCandidateSignatureDate"
          | "candidate.timesheets.rejectionReasonCandidate"
          | "candidate.timesheets.createdBy"
          | "candidate.timesheets.group"
          | "candidate.timesheets.resendTimesheetEmail"
          | "candidate.timesheets.customer"
          | "candidate.timesheets.assignee"
          | "candidate.timesheets.queryResponse"
          | "candidate.timesheets.invoiceNumber"
          | "candidate.timesheets.timesheetNumber"
          | "candidate.timesheets.type"
          | "candidate.timesheets.subType"
          | "candidate.timesheets.tuitionReport"
          | "candidate.timesheets.tuitionReportAnswers.questionId"
          | "candidate.timesheets.tuitionReportAnswers.value"
          | "candidate.timesheets.tuitionReportPending"
          | "candidate.timesheets.role"
          | "candidate.timesheets.statusApprovalIp"
          | "candidate.timesheets.statusApprovalCandidateIp";
        /**
         * The data instance that should be replaced
         *
         * @minItems 1
         */
        match?: (string | number | boolean | (string | null) | (void | null))[];
        /**
         * Replacement if condition is met
         *
         * @maxLength 1000
         */
        ["true"]?: string;
        /**
         * Replacement if condition is not met
         *
         * @maxLength 1000
         */
        ["false"]?: string;
      }[];
      /**
       * Boolean
       */
      boolean?: boolean;
      /**
       * Way in which to format different data types
       */
      formatType?: "string" | "number" | "currency";
      /**
       * Configured value for this column in a flat booking row
       */
      source?:
        | {
            kind?: "field";
            value?:
              | "candidate.candidateId"
              | "candidate.candidateName"
              | "candidate.timesheetId"
              | "candidate.timesheetInvoiceNumber"
              | "candidate.timesheetDate"
              | "candidate.timesheetNumber"
              | "candidate.timesheetStart"
              | "candidate.timesheetEnd"
              | "candidate.timesheetLastDay"
              | "candidate.timesheetApprover"
              | "candidate.timesheetLastSunday"
              | "candidate.timesheetStudentName"
              | "candidate.customClientId"
              | "candidate.customClientInternalId"
              | "candidate.clientName"
              | "candidate.clientId"
              | "candidate.customCandidateId"
              | "candidate.customCandidateInternalId"
              | "candidate.customCandidateIdPayType"
              | "candidate.clientContact"
              | "candidate.invoiceContact"
              | "candidate.total"
              | "candidate.peDate"
              | "candidate.bookingPayType"
              | "candidate.payRate"
              | "candidate.chargeRate"
              | "candidate.units"
              | "candidate.hours"
              | "candidate.hoursPerDay"
              | "candidate.consultant"
              | "candidate.role"
              | "candidate.clientTimesheetStatus"
              | "candidate.isCandidateFirstBooking"
              | "candidate.isCandidateFirstBookingWithClient"
              | "candidate.isClientFirstBooking"
              | "candidate.consultant1Id"
              | "candidate.consultant1Perc"
              | "candidate.consultant2Id"
              | "candidate.consultant2Perc"
              | "candidate.consultant3Id"
              | "candidate.consultant3Perc"
              | "candidate.consultant4Id"
              | "candidate.consultant4Perc"
              | "candidate.consultant5Id"
              | "candidate.consultant5Perc"
              | "candidate.placementId"
              | "candidate.officeNumber"
              | "candidate.poNumber"
              | "candidate.payInterval"
              | "candidate._internalId"
              | "candidate.candidatePayType"
              | "candidate.umbrellaName"
              | "candidate.umbrellaCode"
              | "candidate.umbrellaId"
              | "candidate.bookingStartDate"
              | "candidate.assigneeBranchCodes"
              | "candidate.niNumberTrimmed"
              | "candidate.sortCodeTrimmed"
              | "candidate.accountNumberTrimmed"
              | "candidate.accountNameTrimmed"
              | "candidate.bookingOnCosts"
              | "candidate.payRateExclusiveHoliday"
              | "candidate.payRateHolidayPay"
              | "candidate.payeRate"
              | "candidate.payeMargin"
              | "candidate.payeWeeklyMargin"
              | "candidate.grossMargin"
              | "candidate.totalCharge"
              | "candidate.totalExcludingHolidayPay"
              | "candidate.totalHolidayPay"
              | "candidate.candidateDetails.title"
              | "candidate.candidateDetails.forename"
              | "candidate.candidateDetails.surname"
              | "candidate.candidateDetails.sex"
              | "candidate.candidateDetails.dob"
              | "candidate.candidateDetails.email"
              | "candidate.candidateDetails.telephone"
              | "candidate.candidateDetails.mobile"
              | "candidate.candidateDetails.address.addressLine1"
              | "candidate.candidateDetails.address.postcode"
              | "candidate.candidateDetails.gender"
              | "candidate.candidateDetails.bankDetails.niNumber"
              | "candidate.days.start"
              | "candidate.days.end"
              | "candidate.days.length"
              | "candidate.days.amPm"
              | "candidate.days.status"
              | "candidate.days.chargeRate"
              | "candidate.days.payRate"
              | "candidate.days.flaggedForRemoval"
              | "day.start"
              | "day.end"
              | "day.length"
              | "day.amPm"
              | "day.status"
              | "day.chargeRate"
              | "day.payRate"
              | "day.flaggedForRemoval"
              | "candidate.umbrellaAddress.addressLine1"
              | "candidate.umbrellaAddress.addressLine2"
              | "candidate.umbrellaAddress.addressLine3"
              | "candidate.umbrellaAddress.city"
              | "candidate.umbrellaAddress.county"
              | "candidate.umbrellaAddress.borough"
              | "candidate.umbrellaAddress.postcode"
              | "candidate.umbrellaAddress.country"
              | "candidate.umbrellaAddress.lat"
              | "candidate.umbrellaAddress.lng"
              | "candidate.bankDetails.bankAccountName"
              | "candidate.bankDetails.bankName"
              | "candidate.bankDetails.accountNumber"
              | "candidate.bankDetails.sortCode"
              | "candidate.bankDetails.niNumber"
              | "candidate.bankDetails.jobOp1"
              | "candidate.bankDetails.jobOp2"
              | "candidate.bankDetails.jobOp3"
              | "candidate.bankDetails.studentLoan.studentLoan"
              | "candidate.bankDetails.studentLoan.plan1"
              | "candidate.bankDetails.studentLoan.plan2"
              | "candidate.bankDetails.studentLoan.plan4"
              | "candidate.bankDetails.studentLoan.postGradLoan"
              | "candidate.timesheets.id"
              | "candidate.timesheets.candidateName"
              | "candidate.timesheets.candidate"
              | "candidate.timesheets.clientName"
              | "candidate.timesheets.clientEmail"
              | "candidate.timesheets.client"
              | "candidate.timesheets.booking"
              | "candidate.timesheets.days.start"
              | "candidate.timesheets.days.end"
              | "candidate.timesheets.days.length"
              | "candidate.timesheets.days.amPm"
              | "candidate.timesheets.days.status"
              | "candidate.timesheets.days.chargeRate"
              | "candidate.timesheets.days.payRate"
              | "candidate.timesheets.days.flaggedForRemoval"
              | "candidate.timesheets.status"
              | "candidate.timesheets.statusChangedOn"
              | "candidate.timesheets.statusChangeBy"
              | "candidate.timesheets.statusChangeByJobRole"
              | "candidate.timesheets.statusChangeBySignature"
              | "candidate.timesheets.statusChangeByDrawnSignature"
              | "candidate.timesheets.statusChangeBySignatureDate"
              | "candidate.timesheets.rejectionReason"
              | "candidate.timesheets.statusCandidate"
              | "candidate.timesheets.statusChangedOnCandidate"
              | "candidate.timesheets.statusChangeByCandidate"
              | "candidate.timesheets.statusChangeByJobRoleCandidate"
              | "candidate.timesheets.statusChangeByCandidateSignature"
              | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
              | "candidate.timesheets.statusChangeByCandidateSignatureDate"
              | "candidate.timesheets.rejectionReasonCandidate"
              | "candidate.timesheets.createdBy"
              | "candidate.timesheets.group"
              | "candidate.timesheets.resendTimesheetEmail"
              | "candidate.timesheets.customer"
              | "candidate.timesheets.assignee"
              | "candidate.timesheets.queryResponse"
              | "candidate.timesheets.invoiceNumber"
              | "candidate.timesheets.timesheetNumber"
              | "candidate.timesheets.type"
              | "candidate.timesheets.subType"
              | "candidate.timesheets.tuitionReport"
              | "candidate.timesheets.tuitionReportAnswers.questionId"
              | "candidate.timesheets.tuitionReportAnswers.value"
              | "candidate.timesheets.tuitionReportPending"
              | "candidate.timesheets.role"
              | "candidate.timesheets.statusApprovalIp"
              | "candidate.timesheets.statusApprovalCandidateIp";
            /**
             * @maxLength 100
             */
            prefix?: string;
            /**
             * @maxLength 100
             */
            suffix?: string;
            /**
             * @maxItems 20
             */
            cases?: {
              match?: string | number | boolean;
              /**
               * @maxLength 1000
               */
              value?: string;
            }[];
            /**
             * @maxLength 1000
             */
            fallback?: string;
            date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
          }
        | {
            kind?: "static";
            /**
             * @maxLength 1000
             */
            value?: string;
          }
        | {
            kind?: "calculated";
            value?:
              | "rowNumber"
              | "invoiceFriday"
              | "financialYear"
              | "financialPeriod"
              | "bookingWeekMonday"
              | "dayWorked"
              | "dailyNetCharge"
              | "dailyVat"
              | "dailyPay"
              | "bookingNetTotal"
              | "bookingVatTotal"
              | "bookingGrossTotal";
            sign?: "positive" | "negative";
            date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
          }
        | {
            kind?: "concat";
            /**
             * @minItems 1
             * @maxItems 20
             */
            parts?: (
              | {
                  kind?: "field";
                  value?:
                    | "candidate.candidateId"
                    | "candidate.candidateName"
                    | "candidate.timesheetId"
                    | "candidate.timesheetInvoiceNumber"
                    | "candidate.timesheetDate"
                    | "candidate.timesheetNumber"
                    | "candidate.timesheetStart"
                    | "candidate.timesheetEnd"
                    | "candidate.timesheetLastDay"
                    | "candidate.timesheetApprover"
                    | "candidate.timesheetLastSunday"
                    | "candidate.timesheetStudentName"
                    | "candidate.customClientId"
                    | "candidate.customClientInternalId"
                    | "candidate.clientName"
                    | "candidate.clientId"
                    | "candidate.customCandidateId"
                    | "candidate.customCandidateInternalId"
                    | "candidate.customCandidateIdPayType"
                    | "candidate.clientContact"
                    | "candidate.invoiceContact"
                    | "candidate.total"
                    | "candidate.peDate"
                    | "candidate.bookingPayType"
                    | "candidate.payRate"
                    | "candidate.chargeRate"
                    | "candidate.units"
                    | "candidate.hours"
                    | "candidate.hoursPerDay"
                    | "candidate.consultant"
                    | "candidate.role"
                    | "candidate.clientTimesheetStatus"
                    | "candidate.isCandidateFirstBooking"
                    | "candidate.isCandidateFirstBookingWithClient"
                    | "candidate.isClientFirstBooking"
                    | "candidate.consultant1Id"
                    | "candidate.consultant1Perc"
                    | "candidate.consultant2Id"
                    | "candidate.consultant2Perc"
                    | "candidate.consultant3Id"
                    | "candidate.consultant3Perc"
                    | "candidate.consultant4Id"
                    | "candidate.consultant4Perc"
                    | "candidate.consultant5Id"
                    | "candidate.consultant5Perc"
                    | "candidate.placementId"
                    | "candidate.officeNumber"
                    | "candidate.poNumber"
                    | "candidate.payInterval"
                    | "candidate._internalId"
                    | "candidate.candidatePayType"
                    | "candidate.umbrellaName"
                    | "candidate.umbrellaCode"
                    | "candidate.umbrellaId"
                    | "candidate.bookingStartDate"
                    | "candidate.assigneeBranchCodes"
                    | "candidate.niNumberTrimmed"
                    | "candidate.sortCodeTrimmed"
                    | "candidate.accountNumberTrimmed"
                    | "candidate.accountNameTrimmed"
                    | "candidate.bookingOnCosts"
                    | "candidate.payRateExclusiveHoliday"
                    | "candidate.payRateHolidayPay"
                    | "candidate.payeRate"
                    | "candidate.payeMargin"
                    | "candidate.payeWeeklyMargin"
                    | "candidate.grossMargin"
                    | "candidate.totalCharge"
                    | "candidate.totalExcludingHolidayPay"
                    | "candidate.totalHolidayPay"
                    | "candidate.candidateDetails.title"
                    | "candidate.candidateDetails.forename"
                    | "candidate.candidateDetails.surname"
                    | "candidate.candidateDetails.sex"
                    | "candidate.candidateDetails.dob"
                    | "candidate.candidateDetails.email"
                    | "candidate.candidateDetails.telephone"
                    | "candidate.candidateDetails.mobile"
                    | "candidate.candidateDetails.address.addressLine1"
                    | "candidate.candidateDetails.address.postcode"
                    | "candidate.candidateDetails.gender"
                    | "candidate.candidateDetails.bankDetails.niNumber"
                    | "candidate.days.start"
                    | "candidate.days.end"
                    | "candidate.days.length"
                    | "candidate.days.amPm"
                    | "candidate.days.status"
                    | "candidate.days.chargeRate"
                    | "candidate.days.payRate"
                    | "candidate.days.flaggedForRemoval"
                    | "day.start"
                    | "day.end"
                    | "day.length"
                    | "day.amPm"
                    | "day.status"
                    | "day.chargeRate"
                    | "day.payRate"
                    | "day.flaggedForRemoval"
                    | "candidate.umbrellaAddress.addressLine1"
                    | "candidate.umbrellaAddress.addressLine2"
                    | "candidate.umbrellaAddress.addressLine3"
                    | "candidate.umbrellaAddress.city"
                    | "candidate.umbrellaAddress.county"
                    | "candidate.umbrellaAddress.borough"
                    | "candidate.umbrellaAddress.postcode"
                    | "candidate.umbrellaAddress.country"
                    | "candidate.umbrellaAddress.lat"
                    | "candidate.umbrellaAddress.lng"
                    | "candidate.bankDetails.bankAccountName"
                    | "candidate.bankDetails.bankName"
                    | "candidate.bankDetails.accountNumber"
                    | "candidate.bankDetails.sortCode"
                    | "candidate.bankDetails.niNumber"
                    | "candidate.bankDetails.jobOp1"
                    | "candidate.bankDetails.jobOp2"
                    | "candidate.bankDetails.jobOp3"
                    | "candidate.bankDetails.studentLoan.studentLoan"
                    | "candidate.bankDetails.studentLoan.plan1"
                    | "candidate.bankDetails.studentLoan.plan2"
                    | "candidate.bankDetails.studentLoan.plan4"
                    | "candidate.bankDetails.studentLoan.postGradLoan"
                    | "candidate.timesheets.id"
                    | "candidate.timesheets.candidateName"
                    | "candidate.timesheets.candidate"
                    | "candidate.timesheets.clientName"
                    | "candidate.timesheets.clientEmail"
                    | "candidate.timesheets.client"
                    | "candidate.timesheets.booking"
                    | "candidate.timesheets.days.start"
                    | "candidate.timesheets.days.end"
                    | "candidate.timesheets.days.length"
                    | "candidate.timesheets.days.amPm"
                    | "candidate.timesheets.days.status"
                    | "candidate.timesheets.days.chargeRate"
                    | "candidate.timesheets.days.payRate"
                    | "candidate.timesheets.days.flaggedForRemoval"
                    | "candidate.timesheets.status"
                    | "candidate.timesheets.statusChangedOn"
                    | "candidate.timesheets.statusChangeBy"
                    | "candidate.timesheets.statusChangeByJobRole"
                    | "candidate.timesheets.statusChangeBySignature"
                    | "candidate.timesheets.statusChangeByDrawnSignature"
                    | "candidate.timesheets.statusChangeBySignatureDate"
                    | "candidate.timesheets.rejectionReason"
                    | "candidate.timesheets.statusCandidate"
                    | "candidate.timesheets.statusChangedOnCandidate"
                    | "candidate.timesheets.statusChangeByCandidate"
                    | "candidate.timesheets.statusChangeByJobRoleCandidate"
                    | "candidate.timesheets.statusChangeByCandidateSignature"
                    | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                    | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                    | "candidate.timesheets.rejectionReasonCandidate"
                    | "candidate.timesheets.createdBy"
                    | "candidate.timesheets.group"
                    | "candidate.timesheets.resendTimesheetEmail"
                    | "candidate.timesheets.customer"
                    | "candidate.timesheets.assignee"
                    | "candidate.timesheets.queryResponse"
                    | "candidate.timesheets.invoiceNumber"
                    | "candidate.timesheets.timesheetNumber"
                    | "candidate.timesheets.type"
                    | "candidate.timesheets.subType"
                    | "candidate.timesheets.tuitionReport"
                    | "candidate.timesheets.tuitionReportAnswers.questionId"
                    | "candidate.timesheets.tuitionReportAnswers.value"
                    | "candidate.timesheets.tuitionReportPending"
                    | "candidate.timesheets.role"
                    | "candidate.timesheets.statusApprovalIp"
                    | "candidate.timesheets.statusApprovalCandidateIp";
                  /**
                   * @maxLength 100
                   */
                  prefix?: string;
                  /**
                   * @maxLength 100
                   */
                  suffix?: string;
                  /**
                   * @maxItems 20
                   */
                  cases?: {
                    match?: string | number | boolean;
                    /**
                     * @maxLength 1000
                     */
                    value?: string;
                  }[];
                  /**
                   * @maxLength 1000
                   */
                  fallback?: string;
                  date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                }
              | {
                  kind?: "static";
                  /**
                   * @maxLength 1000
                   */
                  value?: string;
                }
              | {
                  kind?: "calculated";
                  value?:
                    | "rowNumber"
                    | "invoiceFriday"
                    | "financialYear"
                    | "financialPeriod"
                    | "bookingWeekMonday"
                    | "dayWorked"
                    | "dailyNetCharge"
                    | "dailyVat"
                    | "dailyPay"
                    | "bookingNetTotal"
                    | "bookingVatTotal"
                    | "bookingGrossTotal";
                  sign?: "positive" | "negative";
                  date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                }
            )[];
            /**
             * @maxLength 100
             */
            separator?: string;
          };
      /**
       * Independent values for parent, repeated day and summary rows
       */
      rowValues?: {
        parent?:
          | {
              kind?: "field";
              value?:
                | "candidate.candidateId"
                | "candidate.candidateName"
                | "candidate.timesheetId"
                | "candidate.timesheetInvoiceNumber"
                | "candidate.timesheetDate"
                | "candidate.timesheetNumber"
                | "candidate.timesheetStart"
                | "candidate.timesheetEnd"
                | "candidate.timesheetLastDay"
                | "candidate.timesheetApprover"
                | "candidate.timesheetLastSunday"
                | "candidate.timesheetStudentName"
                | "candidate.customClientId"
                | "candidate.customClientInternalId"
                | "candidate.clientName"
                | "candidate.clientId"
                | "candidate.customCandidateId"
                | "candidate.customCandidateInternalId"
                | "candidate.customCandidateIdPayType"
                | "candidate.clientContact"
                | "candidate.invoiceContact"
                | "candidate.total"
                | "candidate.peDate"
                | "candidate.bookingPayType"
                | "candidate.payRate"
                | "candidate.chargeRate"
                | "candidate.units"
                | "candidate.hours"
                | "candidate.hoursPerDay"
                | "candidate.consultant"
                | "candidate.role"
                | "candidate.clientTimesheetStatus"
                | "candidate.isCandidateFirstBooking"
                | "candidate.isCandidateFirstBookingWithClient"
                | "candidate.isClientFirstBooking"
                | "candidate.consultant1Id"
                | "candidate.consultant1Perc"
                | "candidate.consultant2Id"
                | "candidate.consultant2Perc"
                | "candidate.consultant3Id"
                | "candidate.consultant3Perc"
                | "candidate.consultant4Id"
                | "candidate.consultant4Perc"
                | "candidate.consultant5Id"
                | "candidate.consultant5Perc"
                | "candidate.placementId"
                | "candidate.officeNumber"
                | "candidate.poNumber"
                | "candidate.payInterval"
                | "candidate._internalId"
                | "candidate.candidatePayType"
                | "candidate.umbrellaName"
                | "candidate.umbrellaCode"
                | "candidate.umbrellaId"
                | "candidate.bookingStartDate"
                | "candidate.assigneeBranchCodes"
                | "candidate.niNumberTrimmed"
                | "candidate.sortCodeTrimmed"
                | "candidate.accountNumberTrimmed"
                | "candidate.accountNameTrimmed"
                | "candidate.bookingOnCosts"
                | "candidate.payRateExclusiveHoliday"
                | "candidate.payRateHolidayPay"
                | "candidate.payeRate"
                | "candidate.payeMargin"
                | "candidate.payeWeeklyMargin"
                | "candidate.grossMargin"
                | "candidate.totalCharge"
                | "candidate.totalExcludingHolidayPay"
                | "candidate.totalHolidayPay"
                | "candidate.candidateDetails.title"
                | "candidate.candidateDetails.forename"
                | "candidate.candidateDetails.surname"
                | "candidate.candidateDetails.sex"
                | "candidate.candidateDetails.dob"
                | "candidate.candidateDetails.email"
                | "candidate.candidateDetails.telephone"
                | "candidate.candidateDetails.mobile"
                | "candidate.candidateDetails.address.addressLine1"
                | "candidate.candidateDetails.address.postcode"
                | "candidate.candidateDetails.gender"
                | "candidate.candidateDetails.bankDetails.niNumber"
                | "candidate.days.start"
                | "candidate.days.end"
                | "candidate.days.length"
                | "candidate.days.amPm"
                | "candidate.days.status"
                | "candidate.days.chargeRate"
                | "candidate.days.payRate"
                | "candidate.days.flaggedForRemoval"
                | "day.start"
                | "day.end"
                | "day.length"
                | "day.amPm"
                | "day.status"
                | "day.chargeRate"
                | "day.payRate"
                | "day.flaggedForRemoval"
                | "candidate.umbrellaAddress.addressLine1"
                | "candidate.umbrellaAddress.addressLine2"
                | "candidate.umbrellaAddress.addressLine3"
                | "candidate.umbrellaAddress.city"
                | "candidate.umbrellaAddress.county"
                | "candidate.umbrellaAddress.borough"
                | "candidate.umbrellaAddress.postcode"
                | "candidate.umbrellaAddress.country"
                | "candidate.umbrellaAddress.lat"
                | "candidate.umbrellaAddress.lng"
                | "candidate.bankDetails.bankAccountName"
                | "candidate.bankDetails.bankName"
                | "candidate.bankDetails.accountNumber"
                | "candidate.bankDetails.sortCode"
                | "candidate.bankDetails.niNumber"
                | "candidate.bankDetails.jobOp1"
                | "candidate.bankDetails.jobOp2"
                | "candidate.bankDetails.jobOp3"
                | "candidate.bankDetails.studentLoan.studentLoan"
                | "candidate.bankDetails.studentLoan.plan1"
                | "candidate.bankDetails.studentLoan.plan2"
                | "candidate.bankDetails.studentLoan.plan4"
                | "candidate.bankDetails.studentLoan.postGradLoan"
                | "candidate.timesheets.id"
                | "candidate.timesheets.candidateName"
                | "candidate.timesheets.candidate"
                | "candidate.timesheets.clientName"
                | "candidate.timesheets.clientEmail"
                | "candidate.timesheets.client"
                | "candidate.timesheets.booking"
                | "candidate.timesheets.days.start"
                | "candidate.timesheets.days.end"
                | "candidate.timesheets.days.length"
                | "candidate.timesheets.days.amPm"
                | "candidate.timesheets.days.status"
                | "candidate.timesheets.days.chargeRate"
                | "candidate.timesheets.days.payRate"
                | "candidate.timesheets.days.flaggedForRemoval"
                | "candidate.timesheets.status"
                | "candidate.timesheets.statusChangedOn"
                | "candidate.timesheets.statusChangeBy"
                | "candidate.timesheets.statusChangeByJobRole"
                | "candidate.timesheets.statusChangeBySignature"
                | "candidate.timesheets.statusChangeByDrawnSignature"
                | "candidate.timesheets.statusChangeBySignatureDate"
                | "candidate.timesheets.rejectionReason"
                | "candidate.timesheets.statusCandidate"
                | "candidate.timesheets.statusChangedOnCandidate"
                | "candidate.timesheets.statusChangeByCandidate"
                | "candidate.timesheets.statusChangeByJobRoleCandidate"
                | "candidate.timesheets.statusChangeByCandidateSignature"
                | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                | "candidate.timesheets.rejectionReasonCandidate"
                | "candidate.timesheets.createdBy"
                | "candidate.timesheets.group"
                | "candidate.timesheets.resendTimesheetEmail"
                | "candidate.timesheets.customer"
                | "candidate.timesheets.assignee"
                | "candidate.timesheets.queryResponse"
                | "candidate.timesheets.invoiceNumber"
                | "candidate.timesheets.timesheetNumber"
                | "candidate.timesheets.type"
                | "candidate.timesheets.subType"
                | "candidate.timesheets.tuitionReport"
                | "candidate.timesheets.tuitionReportAnswers.questionId"
                | "candidate.timesheets.tuitionReportAnswers.value"
                | "candidate.timesheets.tuitionReportPending"
                | "candidate.timesheets.role"
                | "candidate.timesheets.statusApprovalIp"
                | "candidate.timesheets.statusApprovalCandidateIp";
              /**
               * @maxLength 100
               */
              prefix?: string;
              /**
               * @maxLength 100
               */
              suffix?: string;
              /**
               * @maxItems 20
               */
              cases?: {
                match?: string | number | boolean;
                /**
                 * @maxLength 1000
                 */
                value?: string;
              }[];
              /**
               * @maxLength 1000
               */
              fallback?: string;
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "static";
              /**
               * @maxLength 1000
               */
              value?: string;
            }
          | {
              kind?: "calculated";
              value?:
                | "rowNumber"
                | "invoiceFriday"
                | "financialYear"
                | "financialPeriod"
                | "bookingWeekMonday"
                | "dayWorked"
                | "dailyNetCharge"
                | "dailyVat"
                | "dailyPay"
                | "bookingNetTotal"
                | "bookingVatTotal"
                | "bookingGrossTotal";
              sign?: "positive" | "negative";
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "concat";
              /**
               * @minItems 1
               * @maxItems 20
               */
              parts?: (
                | {
                    kind?: "field";
                    value?:
                      | "candidate.candidateId"
                      | "candidate.candidateName"
                      | "candidate.timesheetId"
                      | "candidate.timesheetInvoiceNumber"
                      | "candidate.timesheetDate"
                      | "candidate.timesheetNumber"
                      | "candidate.timesheetStart"
                      | "candidate.timesheetEnd"
                      | "candidate.timesheetLastDay"
                      | "candidate.timesheetApprover"
                      | "candidate.timesheetLastSunday"
                      | "candidate.timesheetStudentName"
                      | "candidate.customClientId"
                      | "candidate.customClientInternalId"
                      | "candidate.clientName"
                      | "candidate.clientId"
                      | "candidate.customCandidateId"
                      | "candidate.customCandidateInternalId"
                      | "candidate.customCandidateIdPayType"
                      | "candidate.clientContact"
                      | "candidate.invoiceContact"
                      | "candidate.total"
                      | "candidate.peDate"
                      | "candidate.bookingPayType"
                      | "candidate.payRate"
                      | "candidate.chargeRate"
                      | "candidate.units"
                      | "candidate.hours"
                      | "candidate.hoursPerDay"
                      | "candidate.consultant"
                      | "candidate.role"
                      | "candidate.clientTimesheetStatus"
                      | "candidate.isCandidateFirstBooking"
                      | "candidate.isCandidateFirstBookingWithClient"
                      | "candidate.isClientFirstBooking"
                      | "candidate.consultant1Id"
                      | "candidate.consultant1Perc"
                      | "candidate.consultant2Id"
                      | "candidate.consultant2Perc"
                      | "candidate.consultant3Id"
                      | "candidate.consultant3Perc"
                      | "candidate.consultant4Id"
                      | "candidate.consultant4Perc"
                      | "candidate.consultant5Id"
                      | "candidate.consultant5Perc"
                      | "candidate.placementId"
                      | "candidate.officeNumber"
                      | "candidate.poNumber"
                      | "candidate.payInterval"
                      | "candidate._internalId"
                      | "candidate.candidatePayType"
                      | "candidate.umbrellaName"
                      | "candidate.umbrellaCode"
                      | "candidate.umbrellaId"
                      | "candidate.bookingStartDate"
                      | "candidate.assigneeBranchCodes"
                      | "candidate.niNumberTrimmed"
                      | "candidate.sortCodeTrimmed"
                      | "candidate.accountNumberTrimmed"
                      | "candidate.accountNameTrimmed"
                      | "candidate.bookingOnCosts"
                      | "candidate.payRateExclusiveHoliday"
                      | "candidate.payRateHolidayPay"
                      | "candidate.payeRate"
                      | "candidate.payeMargin"
                      | "candidate.payeWeeklyMargin"
                      | "candidate.grossMargin"
                      | "candidate.totalCharge"
                      | "candidate.totalExcludingHolidayPay"
                      | "candidate.totalHolidayPay"
                      | "candidate.candidateDetails.title"
                      | "candidate.candidateDetails.forename"
                      | "candidate.candidateDetails.surname"
                      | "candidate.candidateDetails.sex"
                      | "candidate.candidateDetails.dob"
                      | "candidate.candidateDetails.email"
                      | "candidate.candidateDetails.telephone"
                      | "candidate.candidateDetails.mobile"
                      | "candidate.candidateDetails.address.addressLine1"
                      | "candidate.candidateDetails.address.postcode"
                      | "candidate.candidateDetails.gender"
                      | "candidate.candidateDetails.bankDetails.niNumber"
                      | "candidate.days.start"
                      | "candidate.days.end"
                      | "candidate.days.length"
                      | "candidate.days.amPm"
                      | "candidate.days.status"
                      | "candidate.days.chargeRate"
                      | "candidate.days.payRate"
                      | "candidate.days.flaggedForRemoval"
                      | "day.start"
                      | "day.end"
                      | "day.length"
                      | "day.amPm"
                      | "day.status"
                      | "day.chargeRate"
                      | "day.payRate"
                      | "day.flaggedForRemoval"
                      | "candidate.umbrellaAddress.addressLine1"
                      | "candidate.umbrellaAddress.addressLine2"
                      | "candidate.umbrellaAddress.addressLine3"
                      | "candidate.umbrellaAddress.city"
                      | "candidate.umbrellaAddress.county"
                      | "candidate.umbrellaAddress.borough"
                      | "candidate.umbrellaAddress.postcode"
                      | "candidate.umbrellaAddress.country"
                      | "candidate.umbrellaAddress.lat"
                      | "candidate.umbrellaAddress.lng"
                      | "candidate.bankDetails.bankAccountName"
                      | "candidate.bankDetails.bankName"
                      | "candidate.bankDetails.accountNumber"
                      | "candidate.bankDetails.sortCode"
                      | "candidate.bankDetails.niNumber"
                      | "candidate.bankDetails.jobOp1"
                      | "candidate.bankDetails.jobOp2"
                      | "candidate.bankDetails.jobOp3"
                      | "candidate.bankDetails.studentLoan.studentLoan"
                      | "candidate.bankDetails.studentLoan.plan1"
                      | "candidate.bankDetails.studentLoan.plan2"
                      | "candidate.bankDetails.studentLoan.plan4"
                      | "candidate.bankDetails.studentLoan.postGradLoan"
                      | "candidate.timesheets.id"
                      | "candidate.timesheets.candidateName"
                      | "candidate.timesheets.candidate"
                      | "candidate.timesheets.clientName"
                      | "candidate.timesheets.clientEmail"
                      | "candidate.timesheets.client"
                      | "candidate.timesheets.booking"
                      | "candidate.timesheets.days.start"
                      | "candidate.timesheets.days.end"
                      | "candidate.timesheets.days.length"
                      | "candidate.timesheets.days.amPm"
                      | "candidate.timesheets.days.status"
                      | "candidate.timesheets.days.chargeRate"
                      | "candidate.timesheets.days.payRate"
                      | "candidate.timesheets.days.flaggedForRemoval"
                      | "candidate.timesheets.status"
                      | "candidate.timesheets.statusChangedOn"
                      | "candidate.timesheets.statusChangeBy"
                      | "candidate.timesheets.statusChangeByJobRole"
                      | "candidate.timesheets.statusChangeBySignature"
                      | "candidate.timesheets.statusChangeByDrawnSignature"
                      | "candidate.timesheets.statusChangeBySignatureDate"
                      | "candidate.timesheets.rejectionReason"
                      | "candidate.timesheets.statusCandidate"
                      | "candidate.timesheets.statusChangedOnCandidate"
                      | "candidate.timesheets.statusChangeByCandidate"
                      | "candidate.timesheets.statusChangeByJobRoleCandidate"
                      | "candidate.timesheets.statusChangeByCandidateSignature"
                      | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                      | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                      | "candidate.timesheets.rejectionReasonCandidate"
                      | "candidate.timesheets.createdBy"
                      | "candidate.timesheets.group"
                      | "candidate.timesheets.resendTimesheetEmail"
                      | "candidate.timesheets.customer"
                      | "candidate.timesheets.assignee"
                      | "candidate.timesheets.queryResponse"
                      | "candidate.timesheets.invoiceNumber"
                      | "candidate.timesheets.timesheetNumber"
                      | "candidate.timesheets.type"
                      | "candidate.timesheets.subType"
                      | "candidate.timesheets.tuitionReport"
                      | "candidate.timesheets.tuitionReportAnswers.questionId"
                      | "candidate.timesheets.tuitionReportAnswers.value"
                      | "candidate.timesheets.tuitionReportPending"
                      | "candidate.timesheets.role"
                      | "candidate.timesheets.statusApprovalIp"
                      | "candidate.timesheets.statusApprovalCandidateIp";
                    /**
                     * @maxLength 100
                     */
                    prefix?: string;
                    /**
                     * @maxLength 100
                     */
                    suffix?: string;
                    /**
                     * @maxItems 20
                     */
                    cases?: {
                      match?: string | number | boolean;
                      /**
                       * @maxLength 1000
                       */
                      value?: string;
                    }[];
                    /**
                     * @maxLength 1000
                     */
                    fallback?: string;
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
                | {
                    kind?: "static";
                    /**
                     * @maxLength 1000
                     */
                    value?: string;
                  }
                | {
                    kind?: "calculated";
                    value?:
                      | "rowNumber"
                      | "invoiceFriday"
                      | "financialYear"
                      | "financialPeriod"
                      | "bookingWeekMonday"
                      | "dayWorked"
                      | "dailyNetCharge"
                      | "dailyVat"
                      | "dailyPay"
                      | "bookingNetTotal"
                      | "bookingVatTotal"
                      | "bookingGrossTotal";
                    sign?: "positive" | "negative";
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
              )[];
              /**
               * @maxLength 100
               */
              separator?: string;
            };
        day?:
          | {
              kind?: "field";
              value?:
                | "candidate.candidateId"
                | "candidate.candidateName"
                | "candidate.timesheetId"
                | "candidate.timesheetInvoiceNumber"
                | "candidate.timesheetDate"
                | "candidate.timesheetNumber"
                | "candidate.timesheetStart"
                | "candidate.timesheetEnd"
                | "candidate.timesheetLastDay"
                | "candidate.timesheetApprover"
                | "candidate.timesheetLastSunday"
                | "candidate.timesheetStudentName"
                | "candidate.customClientId"
                | "candidate.customClientInternalId"
                | "candidate.clientName"
                | "candidate.clientId"
                | "candidate.customCandidateId"
                | "candidate.customCandidateInternalId"
                | "candidate.customCandidateIdPayType"
                | "candidate.clientContact"
                | "candidate.invoiceContact"
                | "candidate.total"
                | "candidate.peDate"
                | "candidate.bookingPayType"
                | "candidate.payRate"
                | "candidate.chargeRate"
                | "candidate.units"
                | "candidate.hours"
                | "candidate.hoursPerDay"
                | "candidate.consultant"
                | "candidate.role"
                | "candidate.clientTimesheetStatus"
                | "candidate.isCandidateFirstBooking"
                | "candidate.isCandidateFirstBookingWithClient"
                | "candidate.isClientFirstBooking"
                | "candidate.consultant1Id"
                | "candidate.consultant1Perc"
                | "candidate.consultant2Id"
                | "candidate.consultant2Perc"
                | "candidate.consultant3Id"
                | "candidate.consultant3Perc"
                | "candidate.consultant4Id"
                | "candidate.consultant4Perc"
                | "candidate.consultant5Id"
                | "candidate.consultant5Perc"
                | "candidate.placementId"
                | "candidate.officeNumber"
                | "candidate.poNumber"
                | "candidate.payInterval"
                | "candidate._internalId"
                | "candidate.candidatePayType"
                | "candidate.umbrellaName"
                | "candidate.umbrellaCode"
                | "candidate.umbrellaId"
                | "candidate.bookingStartDate"
                | "candidate.assigneeBranchCodes"
                | "candidate.niNumberTrimmed"
                | "candidate.sortCodeTrimmed"
                | "candidate.accountNumberTrimmed"
                | "candidate.accountNameTrimmed"
                | "candidate.bookingOnCosts"
                | "candidate.payRateExclusiveHoliday"
                | "candidate.payRateHolidayPay"
                | "candidate.payeRate"
                | "candidate.payeMargin"
                | "candidate.payeWeeklyMargin"
                | "candidate.grossMargin"
                | "candidate.totalCharge"
                | "candidate.totalExcludingHolidayPay"
                | "candidate.totalHolidayPay"
                | "candidate.candidateDetails.title"
                | "candidate.candidateDetails.forename"
                | "candidate.candidateDetails.surname"
                | "candidate.candidateDetails.sex"
                | "candidate.candidateDetails.dob"
                | "candidate.candidateDetails.email"
                | "candidate.candidateDetails.telephone"
                | "candidate.candidateDetails.mobile"
                | "candidate.candidateDetails.address.addressLine1"
                | "candidate.candidateDetails.address.postcode"
                | "candidate.candidateDetails.gender"
                | "candidate.candidateDetails.bankDetails.niNumber"
                | "candidate.days.start"
                | "candidate.days.end"
                | "candidate.days.length"
                | "candidate.days.amPm"
                | "candidate.days.status"
                | "candidate.days.chargeRate"
                | "candidate.days.payRate"
                | "candidate.days.flaggedForRemoval"
                | "day.start"
                | "day.end"
                | "day.length"
                | "day.amPm"
                | "day.status"
                | "day.chargeRate"
                | "day.payRate"
                | "day.flaggedForRemoval"
                | "candidate.umbrellaAddress.addressLine1"
                | "candidate.umbrellaAddress.addressLine2"
                | "candidate.umbrellaAddress.addressLine3"
                | "candidate.umbrellaAddress.city"
                | "candidate.umbrellaAddress.county"
                | "candidate.umbrellaAddress.borough"
                | "candidate.umbrellaAddress.postcode"
                | "candidate.umbrellaAddress.country"
                | "candidate.umbrellaAddress.lat"
                | "candidate.umbrellaAddress.lng"
                | "candidate.bankDetails.bankAccountName"
                | "candidate.bankDetails.bankName"
                | "candidate.bankDetails.accountNumber"
                | "candidate.bankDetails.sortCode"
                | "candidate.bankDetails.niNumber"
                | "candidate.bankDetails.jobOp1"
                | "candidate.bankDetails.jobOp2"
                | "candidate.bankDetails.jobOp3"
                | "candidate.bankDetails.studentLoan.studentLoan"
                | "candidate.bankDetails.studentLoan.plan1"
                | "candidate.bankDetails.studentLoan.plan2"
                | "candidate.bankDetails.studentLoan.plan4"
                | "candidate.bankDetails.studentLoan.postGradLoan"
                | "candidate.timesheets.id"
                | "candidate.timesheets.candidateName"
                | "candidate.timesheets.candidate"
                | "candidate.timesheets.clientName"
                | "candidate.timesheets.clientEmail"
                | "candidate.timesheets.client"
                | "candidate.timesheets.booking"
                | "candidate.timesheets.days.start"
                | "candidate.timesheets.days.end"
                | "candidate.timesheets.days.length"
                | "candidate.timesheets.days.amPm"
                | "candidate.timesheets.days.status"
                | "candidate.timesheets.days.chargeRate"
                | "candidate.timesheets.days.payRate"
                | "candidate.timesheets.days.flaggedForRemoval"
                | "candidate.timesheets.status"
                | "candidate.timesheets.statusChangedOn"
                | "candidate.timesheets.statusChangeBy"
                | "candidate.timesheets.statusChangeByJobRole"
                | "candidate.timesheets.statusChangeBySignature"
                | "candidate.timesheets.statusChangeByDrawnSignature"
                | "candidate.timesheets.statusChangeBySignatureDate"
                | "candidate.timesheets.rejectionReason"
                | "candidate.timesheets.statusCandidate"
                | "candidate.timesheets.statusChangedOnCandidate"
                | "candidate.timesheets.statusChangeByCandidate"
                | "candidate.timesheets.statusChangeByJobRoleCandidate"
                | "candidate.timesheets.statusChangeByCandidateSignature"
                | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                | "candidate.timesheets.rejectionReasonCandidate"
                | "candidate.timesheets.createdBy"
                | "candidate.timesheets.group"
                | "candidate.timesheets.resendTimesheetEmail"
                | "candidate.timesheets.customer"
                | "candidate.timesheets.assignee"
                | "candidate.timesheets.queryResponse"
                | "candidate.timesheets.invoiceNumber"
                | "candidate.timesheets.timesheetNumber"
                | "candidate.timesheets.type"
                | "candidate.timesheets.subType"
                | "candidate.timesheets.tuitionReport"
                | "candidate.timesheets.tuitionReportAnswers.questionId"
                | "candidate.timesheets.tuitionReportAnswers.value"
                | "candidate.timesheets.tuitionReportPending"
                | "candidate.timesheets.role"
                | "candidate.timesheets.statusApprovalIp"
                | "candidate.timesheets.statusApprovalCandidateIp";
              /**
               * @maxLength 100
               */
              prefix?: string;
              /**
               * @maxLength 100
               */
              suffix?: string;
              /**
               * @maxItems 20
               */
              cases?: {
                match?: string | number | boolean;
                /**
                 * @maxLength 1000
                 */
                value?: string;
              }[];
              /**
               * @maxLength 1000
               */
              fallback?: string;
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "static";
              /**
               * @maxLength 1000
               */
              value?: string;
            }
          | {
              kind?: "calculated";
              value?:
                | "rowNumber"
                | "invoiceFriday"
                | "financialYear"
                | "financialPeriod"
                | "bookingWeekMonday"
                | "dayWorked"
                | "dailyNetCharge"
                | "dailyVat"
                | "dailyPay"
                | "bookingNetTotal"
                | "bookingVatTotal"
                | "bookingGrossTotal";
              sign?: "positive" | "negative";
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "concat";
              /**
               * @minItems 1
               * @maxItems 20
               */
              parts?: (
                | {
                    kind?: "field";
                    value?:
                      | "candidate.candidateId"
                      | "candidate.candidateName"
                      | "candidate.timesheetId"
                      | "candidate.timesheetInvoiceNumber"
                      | "candidate.timesheetDate"
                      | "candidate.timesheetNumber"
                      | "candidate.timesheetStart"
                      | "candidate.timesheetEnd"
                      | "candidate.timesheetLastDay"
                      | "candidate.timesheetApprover"
                      | "candidate.timesheetLastSunday"
                      | "candidate.timesheetStudentName"
                      | "candidate.customClientId"
                      | "candidate.customClientInternalId"
                      | "candidate.clientName"
                      | "candidate.clientId"
                      | "candidate.customCandidateId"
                      | "candidate.customCandidateInternalId"
                      | "candidate.customCandidateIdPayType"
                      | "candidate.clientContact"
                      | "candidate.invoiceContact"
                      | "candidate.total"
                      | "candidate.peDate"
                      | "candidate.bookingPayType"
                      | "candidate.payRate"
                      | "candidate.chargeRate"
                      | "candidate.units"
                      | "candidate.hours"
                      | "candidate.hoursPerDay"
                      | "candidate.consultant"
                      | "candidate.role"
                      | "candidate.clientTimesheetStatus"
                      | "candidate.isCandidateFirstBooking"
                      | "candidate.isCandidateFirstBookingWithClient"
                      | "candidate.isClientFirstBooking"
                      | "candidate.consultant1Id"
                      | "candidate.consultant1Perc"
                      | "candidate.consultant2Id"
                      | "candidate.consultant2Perc"
                      | "candidate.consultant3Id"
                      | "candidate.consultant3Perc"
                      | "candidate.consultant4Id"
                      | "candidate.consultant4Perc"
                      | "candidate.consultant5Id"
                      | "candidate.consultant5Perc"
                      | "candidate.placementId"
                      | "candidate.officeNumber"
                      | "candidate.poNumber"
                      | "candidate.payInterval"
                      | "candidate._internalId"
                      | "candidate.candidatePayType"
                      | "candidate.umbrellaName"
                      | "candidate.umbrellaCode"
                      | "candidate.umbrellaId"
                      | "candidate.bookingStartDate"
                      | "candidate.assigneeBranchCodes"
                      | "candidate.niNumberTrimmed"
                      | "candidate.sortCodeTrimmed"
                      | "candidate.accountNumberTrimmed"
                      | "candidate.accountNameTrimmed"
                      | "candidate.bookingOnCosts"
                      | "candidate.payRateExclusiveHoliday"
                      | "candidate.payRateHolidayPay"
                      | "candidate.payeRate"
                      | "candidate.payeMargin"
                      | "candidate.payeWeeklyMargin"
                      | "candidate.grossMargin"
                      | "candidate.totalCharge"
                      | "candidate.totalExcludingHolidayPay"
                      | "candidate.totalHolidayPay"
                      | "candidate.candidateDetails.title"
                      | "candidate.candidateDetails.forename"
                      | "candidate.candidateDetails.surname"
                      | "candidate.candidateDetails.sex"
                      | "candidate.candidateDetails.dob"
                      | "candidate.candidateDetails.email"
                      | "candidate.candidateDetails.telephone"
                      | "candidate.candidateDetails.mobile"
                      | "candidate.candidateDetails.address.addressLine1"
                      | "candidate.candidateDetails.address.postcode"
                      | "candidate.candidateDetails.gender"
                      | "candidate.candidateDetails.bankDetails.niNumber"
                      | "candidate.days.start"
                      | "candidate.days.end"
                      | "candidate.days.length"
                      | "candidate.days.amPm"
                      | "candidate.days.status"
                      | "candidate.days.chargeRate"
                      | "candidate.days.payRate"
                      | "candidate.days.flaggedForRemoval"
                      | "day.start"
                      | "day.end"
                      | "day.length"
                      | "day.amPm"
                      | "day.status"
                      | "day.chargeRate"
                      | "day.payRate"
                      | "day.flaggedForRemoval"
                      | "candidate.umbrellaAddress.addressLine1"
                      | "candidate.umbrellaAddress.addressLine2"
                      | "candidate.umbrellaAddress.addressLine3"
                      | "candidate.umbrellaAddress.city"
                      | "candidate.umbrellaAddress.county"
                      | "candidate.umbrellaAddress.borough"
                      | "candidate.umbrellaAddress.postcode"
                      | "candidate.umbrellaAddress.country"
                      | "candidate.umbrellaAddress.lat"
                      | "candidate.umbrellaAddress.lng"
                      | "candidate.bankDetails.bankAccountName"
                      | "candidate.bankDetails.bankName"
                      | "candidate.bankDetails.accountNumber"
                      | "candidate.bankDetails.sortCode"
                      | "candidate.bankDetails.niNumber"
                      | "candidate.bankDetails.jobOp1"
                      | "candidate.bankDetails.jobOp2"
                      | "candidate.bankDetails.jobOp3"
                      | "candidate.bankDetails.studentLoan.studentLoan"
                      | "candidate.bankDetails.studentLoan.plan1"
                      | "candidate.bankDetails.studentLoan.plan2"
                      | "candidate.bankDetails.studentLoan.plan4"
                      | "candidate.bankDetails.studentLoan.postGradLoan"
                      | "candidate.timesheets.id"
                      | "candidate.timesheets.candidateName"
                      | "candidate.timesheets.candidate"
                      | "candidate.timesheets.clientName"
                      | "candidate.timesheets.clientEmail"
                      | "candidate.timesheets.client"
                      | "candidate.timesheets.booking"
                      | "candidate.timesheets.days.start"
                      | "candidate.timesheets.days.end"
                      | "candidate.timesheets.days.length"
                      | "candidate.timesheets.days.amPm"
                      | "candidate.timesheets.days.status"
                      | "candidate.timesheets.days.chargeRate"
                      | "candidate.timesheets.days.payRate"
                      | "candidate.timesheets.days.flaggedForRemoval"
                      | "candidate.timesheets.status"
                      | "candidate.timesheets.statusChangedOn"
                      | "candidate.timesheets.statusChangeBy"
                      | "candidate.timesheets.statusChangeByJobRole"
                      | "candidate.timesheets.statusChangeBySignature"
                      | "candidate.timesheets.statusChangeByDrawnSignature"
                      | "candidate.timesheets.statusChangeBySignatureDate"
                      | "candidate.timesheets.rejectionReason"
                      | "candidate.timesheets.statusCandidate"
                      | "candidate.timesheets.statusChangedOnCandidate"
                      | "candidate.timesheets.statusChangeByCandidate"
                      | "candidate.timesheets.statusChangeByJobRoleCandidate"
                      | "candidate.timesheets.statusChangeByCandidateSignature"
                      | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                      | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                      | "candidate.timesheets.rejectionReasonCandidate"
                      | "candidate.timesheets.createdBy"
                      | "candidate.timesheets.group"
                      | "candidate.timesheets.resendTimesheetEmail"
                      | "candidate.timesheets.customer"
                      | "candidate.timesheets.assignee"
                      | "candidate.timesheets.queryResponse"
                      | "candidate.timesheets.invoiceNumber"
                      | "candidate.timesheets.timesheetNumber"
                      | "candidate.timesheets.type"
                      | "candidate.timesheets.subType"
                      | "candidate.timesheets.tuitionReport"
                      | "candidate.timesheets.tuitionReportAnswers.questionId"
                      | "candidate.timesheets.tuitionReportAnswers.value"
                      | "candidate.timesheets.tuitionReportPending"
                      | "candidate.timesheets.role"
                      | "candidate.timesheets.statusApprovalIp"
                      | "candidate.timesheets.statusApprovalCandidateIp";
                    /**
                     * @maxLength 100
                     */
                    prefix?: string;
                    /**
                     * @maxLength 100
                     */
                    suffix?: string;
                    /**
                     * @maxItems 20
                     */
                    cases?: {
                      match?: string | number | boolean;
                      /**
                       * @maxLength 1000
                       */
                      value?: string;
                    }[];
                    /**
                     * @maxLength 1000
                     */
                    fallback?: string;
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
                | {
                    kind?: "static";
                    /**
                     * @maxLength 1000
                     */
                    value?: string;
                  }
                | {
                    kind?: "calculated";
                    value?:
                      | "rowNumber"
                      | "invoiceFriday"
                      | "financialYear"
                      | "financialPeriod"
                      | "bookingWeekMonday"
                      | "dayWorked"
                      | "dailyNetCharge"
                      | "dailyVat"
                      | "dailyPay"
                      | "bookingNetTotal"
                      | "bookingVatTotal"
                      | "bookingGrossTotal";
                    sign?: "positive" | "negative";
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
              )[];
              /**
               * @maxLength 100
               */
              separator?: string;
            };
        summary?:
          | {
              kind?: "field";
              value?:
                | "candidate.candidateId"
                | "candidate.candidateName"
                | "candidate.timesheetId"
                | "candidate.timesheetInvoiceNumber"
                | "candidate.timesheetDate"
                | "candidate.timesheetNumber"
                | "candidate.timesheetStart"
                | "candidate.timesheetEnd"
                | "candidate.timesheetLastDay"
                | "candidate.timesheetApprover"
                | "candidate.timesheetLastSunday"
                | "candidate.timesheetStudentName"
                | "candidate.customClientId"
                | "candidate.customClientInternalId"
                | "candidate.clientName"
                | "candidate.clientId"
                | "candidate.customCandidateId"
                | "candidate.customCandidateInternalId"
                | "candidate.customCandidateIdPayType"
                | "candidate.clientContact"
                | "candidate.invoiceContact"
                | "candidate.total"
                | "candidate.peDate"
                | "candidate.bookingPayType"
                | "candidate.payRate"
                | "candidate.chargeRate"
                | "candidate.units"
                | "candidate.hours"
                | "candidate.hoursPerDay"
                | "candidate.consultant"
                | "candidate.role"
                | "candidate.clientTimesheetStatus"
                | "candidate.isCandidateFirstBooking"
                | "candidate.isCandidateFirstBookingWithClient"
                | "candidate.isClientFirstBooking"
                | "candidate.consultant1Id"
                | "candidate.consultant1Perc"
                | "candidate.consultant2Id"
                | "candidate.consultant2Perc"
                | "candidate.consultant3Id"
                | "candidate.consultant3Perc"
                | "candidate.consultant4Id"
                | "candidate.consultant4Perc"
                | "candidate.consultant5Id"
                | "candidate.consultant5Perc"
                | "candidate.placementId"
                | "candidate.officeNumber"
                | "candidate.poNumber"
                | "candidate.payInterval"
                | "candidate._internalId"
                | "candidate.candidatePayType"
                | "candidate.umbrellaName"
                | "candidate.umbrellaCode"
                | "candidate.umbrellaId"
                | "candidate.bookingStartDate"
                | "candidate.assigneeBranchCodes"
                | "candidate.niNumberTrimmed"
                | "candidate.sortCodeTrimmed"
                | "candidate.accountNumberTrimmed"
                | "candidate.accountNameTrimmed"
                | "candidate.bookingOnCosts"
                | "candidate.payRateExclusiveHoliday"
                | "candidate.payRateHolidayPay"
                | "candidate.payeRate"
                | "candidate.payeMargin"
                | "candidate.payeWeeklyMargin"
                | "candidate.grossMargin"
                | "candidate.totalCharge"
                | "candidate.totalExcludingHolidayPay"
                | "candidate.totalHolidayPay"
                | "candidate.candidateDetails.title"
                | "candidate.candidateDetails.forename"
                | "candidate.candidateDetails.surname"
                | "candidate.candidateDetails.sex"
                | "candidate.candidateDetails.dob"
                | "candidate.candidateDetails.email"
                | "candidate.candidateDetails.telephone"
                | "candidate.candidateDetails.mobile"
                | "candidate.candidateDetails.address.addressLine1"
                | "candidate.candidateDetails.address.postcode"
                | "candidate.candidateDetails.gender"
                | "candidate.candidateDetails.bankDetails.niNumber"
                | "candidate.days.start"
                | "candidate.days.end"
                | "candidate.days.length"
                | "candidate.days.amPm"
                | "candidate.days.status"
                | "candidate.days.chargeRate"
                | "candidate.days.payRate"
                | "candidate.days.flaggedForRemoval"
                | "day.start"
                | "day.end"
                | "day.length"
                | "day.amPm"
                | "day.status"
                | "day.chargeRate"
                | "day.payRate"
                | "day.flaggedForRemoval"
                | "candidate.umbrellaAddress.addressLine1"
                | "candidate.umbrellaAddress.addressLine2"
                | "candidate.umbrellaAddress.addressLine3"
                | "candidate.umbrellaAddress.city"
                | "candidate.umbrellaAddress.county"
                | "candidate.umbrellaAddress.borough"
                | "candidate.umbrellaAddress.postcode"
                | "candidate.umbrellaAddress.country"
                | "candidate.umbrellaAddress.lat"
                | "candidate.umbrellaAddress.lng"
                | "candidate.bankDetails.bankAccountName"
                | "candidate.bankDetails.bankName"
                | "candidate.bankDetails.accountNumber"
                | "candidate.bankDetails.sortCode"
                | "candidate.bankDetails.niNumber"
                | "candidate.bankDetails.jobOp1"
                | "candidate.bankDetails.jobOp2"
                | "candidate.bankDetails.jobOp3"
                | "candidate.bankDetails.studentLoan.studentLoan"
                | "candidate.bankDetails.studentLoan.plan1"
                | "candidate.bankDetails.studentLoan.plan2"
                | "candidate.bankDetails.studentLoan.plan4"
                | "candidate.bankDetails.studentLoan.postGradLoan"
                | "candidate.timesheets.id"
                | "candidate.timesheets.candidateName"
                | "candidate.timesheets.candidate"
                | "candidate.timesheets.clientName"
                | "candidate.timesheets.clientEmail"
                | "candidate.timesheets.client"
                | "candidate.timesheets.booking"
                | "candidate.timesheets.days.start"
                | "candidate.timesheets.days.end"
                | "candidate.timesheets.days.length"
                | "candidate.timesheets.days.amPm"
                | "candidate.timesheets.days.status"
                | "candidate.timesheets.days.chargeRate"
                | "candidate.timesheets.days.payRate"
                | "candidate.timesheets.days.flaggedForRemoval"
                | "candidate.timesheets.status"
                | "candidate.timesheets.statusChangedOn"
                | "candidate.timesheets.statusChangeBy"
                | "candidate.timesheets.statusChangeByJobRole"
                | "candidate.timesheets.statusChangeBySignature"
                | "candidate.timesheets.statusChangeByDrawnSignature"
                | "candidate.timesheets.statusChangeBySignatureDate"
                | "candidate.timesheets.rejectionReason"
                | "candidate.timesheets.statusCandidate"
                | "candidate.timesheets.statusChangedOnCandidate"
                | "candidate.timesheets.statusChangeByCandidate"
                | "candidate.timesheets.statusChangeByJobRoleCandidate"
                | "candidate.timesheets.statusChangeByCandidateSignature"
                | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                | "candidate.timesheets.rejectionReasonCandidate"
                | "candidate.timesheets.createdBy"
                | "candidate.timesheets.group"
                | "candidate.timesheets.resendTimesheetEmail"
                | "candidate.timesheets.customer"
                | "candidate.timesheets.assignee"
                | "candidate.timesheets.queryResponse"
                | "candidate.timesheets.invoiceNumber"
                | "candidate.timesheets.timesheetNumber"
                | "candidate.timesheets.type"
                | "candidate.timesheets.subType"
                | "candidate.timesheets.tuitionReport"
                | "candidate.timesheets.tuitionReportAnswers.questionId"
                | "candidate.timesheets.tuitionReportAnswers.value"
                | "candidate.timesheets.tuitionReportPending"
                | "candidate.timesheets.role"
                | "candidate.timesheets.statusApprovalIp"
                | "candidate.timesheets.statusApprovalCandidateIp";
              /**
               * @maxLength 100
               */
              prefix?: string;
              /**
               * @maxLength 100
               */
              suffix?: string;
              /**
               * @maxItems 20
               */
              cases?: {
                match?: string | number | boolean;
                /**
                 * @maxLength 1000
                 */
                value?: string;
              }[];
              /**
               * @maxLength 1000
               */
              fallback?: string;
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "static";
              /**
               * @maxLength 1000
               */
              value?: string;
            }
          | {
              kind?: "calculated";
              value?:
                | "rowNumber"
                | "invoiceFriday"
                | "financialYear"
                | "financialPeriod"
                | "bookingWeekMonday"
                | "dayWorked"
                | "dailyNetCharge"
                | "dailyVat"
                | "dailyPay"
                | "bookingNetTotal"
                | "bookingVatTotal"
                | "bookingGrossTotal";
              sign?: "positive" | "negative";
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "concat";
              /**
               * @minItems 1
               * @maxItems 20
               */
              parts?: (
                | {
                    kind?: "field";
                    value?:
                      | "candidate.candidateId"
                      | "candidate.candidateName"
                      | "candidate.timesheetId"
                      | "candidate.timesheetInvoiceNumber"
                      | "candidate.timesheetDate"
                      | "candidate.timesheetNumber"
                      | "candidate.timesheetStart"
                      | "candidate.timesheetEnd"
                      | "candidate.timesheetLastDay"
                      | "candidate.timesheetApprover"
                      | "candidate.timesheetLastSunday"
                      | "candidate.timesheetStudentName"
                      | "candidate.customClientId"
                      | "candidate.customClientInternalId"
                      | "candidate.clientName"
                      | "candidate.clientId"
                      | "candidate.customCandidateId"
                      | "candidate.customCandidateInternalId"
                      | "candidate.customCandidateIdPayType"
                      | "candidate.clientContact"
                      | "candidate.invoiceContact"
                      | "candidate.total"
                      | "candidate.peDate"
                      | "candidate.bookingPayType"
                      | "candidate.payRate"
                      | "candidate.chargeRate"
                      | "candidate.units"
                      | "candidate.hours"
                      | "candidate.hoursPerDay"
                      | "candidate.consultant"
                      | "candidate.role"
                      | "candidate.clientTimesheetStatus"
                      | "candidate.isCandidateFirstBooking"
                      | "candidate.isCandidateFirstBookingWithClient"
                      | "candidate.isClientFirstBooking"
                      | "candidate.consultant1Id"
                      | "candidate.consultant1Perc"
                      | "candidate.consultant2Id"
                      | "candidate.consultant2Perc"
                      | "candidate.consultant3Id"
                      | "candidate.consultant3Perc"
                      | "candidate.consultant4Id"
                      | "candidate.consultant4Perc"
                      | "candidate.consultant5Id"
                      | "candidate.consultant5Perc"
                      | "candidate.placementId"
                      | "candidate.officeNumber"
                      | "candidate.poNumber"
                      | "candidate.payInterval"
                      | "candidate._internalId"
                      | "candidate.candidatePayType"
                      | "candidate.umbrellaName"
                      | "candidate.umbrellaCode"
                      | "candidate.umbrellaId"
                      | "candidate.bookingStartDate"
                      | "candidate.assigneeBranchCodes"
                      | "candidate.niNumberTrimmed"
                      | "candidate.sortCodeTrimmed"
                      | "candidate.accountNumberTrimmed"
                      | "candidate.accountNameTrimmed"
                      | "candidate.bookingOnCosts"
                      | "candidate.payRateExclusiveHoliday"
                      | "candidate.payRateHolidayPay"
                      | "candidate.payeRate"
                      | "candidate.payeMargin"
                      | "candidate.payeWeeklyMargin"
                      | "candidate.grossMargin"
                      | "candidate.totalCharge"
                      | "candidate.totalExcludingHolidayPay"
                      | "candidate.totalHolidayPay"
                      | "candidate.candidateDetails.title"
                      | "candidate.candidateDetails.forename"
                      | "candidate.candidateDetails.surname"
                      | "candidate.candidateDetails.sex"
                      | "candidate.candidateDetails.dob"
                      | "candidate.candidateDetails.email"
                      | "candidate.candidateDetails.telephone"
                      | "candidate.candidateDetails.mobile"
                      | "candidate.candidateDetails.address.addressLine1"
                      | "candidate.candidateDetails.address.postcode"
                      | "candidate.candidateDetails.gender"
                      | "candidate.candidateDetails.bankDetails.niNumber"
                      | "candidate.days.start"
                      | "candidate.days.end"
                      | "candidate.days.length"
                      | "candidate.days.amPm"
                      | "candidate.days.status"
                      | "candidate.days.chargeRate"
                      | "candidate.days.payRate"
                      | "candidate.days.flaggedForRemoval"
                      | "day.start"
                      | "day.end"
                      | "day.length"
                      | "day.amPm"
                      | "day.status"
                      | "day.chargeRate"
                      | "day.payRate"
                      | "day.flaggedForRemoval"
                      | "candidate.umbrellaAddress.addressLine1"
                      | "candidate.umbrellaAddress.addressLine2"
                      | "candidate.umbrellaAddress.addressLine3"
                      | "candidate.umbrellaAddress.city"
                      | "candidate.umbrellaAddress.county"
                      | "candidate.umbrellaAddress.borough"
                      | "candidate.umbrellaAddress.postcode"
                      | "candidate.umbrellaAddress.country"
                      | "candidate.umbrellaAddress.lat"
                      | "candidate.umbrellaAddress.lng"
                      | "candidate.bankDetails.bankAccountName"
                      | "candidate.bankDetails.bankName"
                      | "candidate.bankDetails.accountNumber"
                      | "candidate.bankDetails.sortCode"
                      | "candidate.bankDetails.niNumber"
                      | "candidate.bankDetails.jobOp1"
                      | "candidate.bankDetails.jobOp2"
                      | "candidate.bankDetails.jobOp3"
                      | "candidate.bankDetails.studentLoan.studentLoan"
                      | "candidate.bankDetails.studentLoan.plan1"
                      | "candidate.bankDetails.studentLoan.plan2"
                      | "candidate.bankDetails.studentLoan.plan4"
                      | "candidate.bankDetails.studentLoan.postGradLoan"
                      | "candidate.timesheets.id"
                      | "candidate.timesheets.candidateName"
                      | "candidate.timesheets.candidate"
                      | "candidate.timesheets.clientName"
                      | "candidate.timesheets.clientEmail"
                      | "candidate.timesheets.client"
                      | "candidate.timesheets.booking"
                      | "candidate.timesheets.days.start"
                      | "candidate.timesheets.days.end"
                      | "candidate.timesheets.days.length"
                      | "candidate.timesheets.days.amPm"
                      | "candidate.timesheets.days.status"
                      | "candidate.timesheets.days.chargeRate"
                      | "candidate.timesheets.days.payRate"
                      | "candidate.timesheets.days.flaggedForRemoval"
                      | "candidate.timesheets.status"
                      | "candidate.timesheets.statusChangedOn"
                      | "candidate.timesheets.statusChangeBy"
                      | "candidate.timesheets.statusChangeByJobRole"
                      | "candidate.timesheets.statusChangeBySignature"
                      | "candidate.timesheets.statusChangeByDrawnSignature"
                      | "candidate.timesheets.statusChangeBySignatureDate"
                      | "candidate.timesheets.rejectionReason"
                      | "candidate.timesheets.statusCandidate"
                      | "candidate.timesheets.statusChangedOnCandidate"
                      | "candidate.timesheets.statusChangeByCandidate"
                      | "candidate.timesheets.statusChangeByJobRoleCandidate"
                      | "candidate.timesheets.statusChangeByCandidateSignature"
                      | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                      | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                      | "candidate.timesheets.rejectionReasonCandidate"
                      | "candidate.timesheets.createdBy"
                      | "candidate.timesheets.group"
                      | "candidate.timesheets.resendTimesheetEmail"
                      | "candidate.timesheets.customer"
                      | "candidate.timesheets.assignee"
                      | "candidate.timesheets.queryResponse"
                      | "candidate.timesheets.invoiceNumber"
                      | "candidate.timesheets.timesheetNumber"
                      | "candidate.timesheets.type"
                      | "candidate.timesheets.subType"
                      | "candidate.timesheets.tuitionReport"
                      | "candidate.timesheets.tuitionReportAnswers.questionId"
                      | "candidate.timesheets.tuitionReportAnswers.value"
                      | "candidate.timesheets.tuitionReportPending"
                      | "candidate.timesheets.role"
                      | "candidate.timesheets.statusApprovalIp"
                      | "candidate.timesheets.statusApprovalCandidateIp";
                    /**
                     * @maxLength 100
                     */
                    prefix?: string;
                    /**
                     * @maxLength 100
                     */
                    suffix?: string;
                    /**
                     * @maxItems 20
                     */
                    cases?: {
                      match?: string | number | boolean;
                      /**
                       * @maxLength 1000
                       */
                      value?: string;
                    }[];
                    /**
                     * @maxLength 1000
                     */
                    fallback?: string;
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
                | {
                    kind?: "static";
                    /**
                     * @maxLength 1000
                     */
                    value?: string;
                  }
                | {
                    kind?: "calculated";
                    value?:
                      | "rowNumber"
                      | "invoiceFriday"
                      | "financialYear"
                      | "financialPeriod"
                      | "bookingWeekMonday"
                      | "dayWorked"
                      | "dailyNetCharge"
                      | "dailyVat"
                      | "dailyPay"
                      | "bookingNetTotal"
                      | "bookingVatTotal"
                      | "bookingGrossTotal";
                    sign?: "positive" | "negative";
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
              )[];
              /**
               * @maxLength 100
               */
              separator?: string;
            };
      };
    }[];
    /**
     * Boolean flag of whether to show header names in the exported file
     */
    showHeaders?: boolean;
    /**
     * Flat booking rows or parent, day and summary rows
     */
    layout?: "flat" | "structured";
    /**
     * Include a parent row before the worked days of each payment-run item
     */
    includeParentRow?: boolean;
    /**
     * Include a summary row after the worked days of each payment-run item
     */
    includeSummaryRow?: boolean;
    /**
     * @minimum 1
     * @maximum 12
     */
    fiscalYearStartMonth?: number | null;
    /**
     * @minimum 0
     * @maximum 100
     */
    vatPercent?: number | null;
  };
};

export type GetExportConfigVariables = {
  pathParams: GetExportConfigPathParams;
  queryParams?: GetExportConfigQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetExportConfig = (
  variables: GetExportConfigVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetExportConfigResponse,
    GetExportConfigError,
    undefined,
    {},
    GetExportConfigQueryParams,
    GetExportConfigPathParams
  >({
    url: "/company/export-configs/{type}",
    method: "get",
    ...variables,
    signal,
  });

export function getExportConfigQuery(variables: GetExportConfigVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetExportConfigResponse>;
};

export function getExportConfigQuery(
  variables: GetExportConfigVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetExportConfigResponse>)
    | reactQuery.SkipToken;
};

export function getExportConfigQuery(
  variables: GetExportConfigVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/company/export-configs/{type}",
      operationId: "getExportConfig",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetExportConfig(variables, signal),
  };
}

export const useSuspenseGetExportConfig = <TData = GetExportConfigResponse,>(
  variables: GetExportConfigVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetExportConfigResponse,
      GetExportConfigError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetExportConfigResponse,
    GetExportConfigError,
    TData
  >({
    ...getExportConfigQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetExportConfig = <TData = GetExportConfigResponse,>(
  variables: GetExportConfigVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetExportConfigResponse,
      GetExportConfigError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetExportConfigResponse,
    GetExportConfigError,
    TData
  >({
    ...getExportConfigQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateExportConfigPathParams = {
  /**
   * The type of export config
   */
  type: "paymentRuns";
};

export type UpdateExportConfigQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "customer"
    | "type"
    | "config"
    | "showHeaders"
    | "layout"
    | "includeParentRow"
    | "includeSummaryRow"
    | "fiscalYearStartMonth"
    | "vatPercent"
    | ""
  )[];
};

export type UpdateExportConfigError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateExportConfigResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * Customer that owns this configuration
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customer?: string;
    /**
     * The type of export this config applies to
     */
    type?: "paymentRuns";
    /**
     * Contains the configuration items themselves
     */
    config?: {
      /**
       * The name of the datapoint in the exported file
       *
       * @maxLength 1000
       */
      header?: string;
      /**
       * Database data from which to derive
       */
      value?:
        | (
            | "candidate.candidateId"
            | "candidate.candidateName"
            | "candidate.timesheetId"
            | "candidate.timesheetInvoiceNumber"
            | "candidate.timesheetDate"
            | "candidate.timesheetNumber"
            | "candidate.timesheetStart"
            | "candidate.timesheetEnd"
            | "candidate.timesheetLastDay"
            | "candidate.timesheetApprover"
            | "candidate.timesheetLastSunday"
            | "candidate.timesheetStudentName"
            | "candidate.customClientId"
            | "candidate.customClientInternalId"
            | "candidate.clientName"
            | "candidate.clientId"
            | "candidate.customCandidateId"
            | "candidate.customCandidateInternalId"
            | "candidate.customCandidateIdPayType"
            | "candidate.clientContact"
            | "candidate.invoiceContact"
            | "candidate.total"
            | "candidate.peDate"
            | "candidate.bookingPayType"
            | "candidate.payRate"
            | "candidate.chargeRate"
            | "candidate.units"
            | "candidate.hours"
            | "candidate.hoursPerDay"
            | "candidate.consultant"
            | "candidate.role"
            | "candidate.clientTimesheetStatus"
            | "candidate.isCandidateFirstBooking"
            | "candidate.isCandidateFirstBookingWithClient"
            | "candidate.isClientFirstBooking"
            | "candidate.consultant1Id"
            | "candidate.consultant1Perc"
            | "candidate.consultant2Id"
            | "candidate.consultant2Perc"
            | "candidate.consultant3Id"
            | "candidate.consultant3Perc"
            | "candidate.consultant4Id"
            | "candidate.consultant4Perc"
            | "candidate.consultant5Id"
            | "candidate.consultant5Perc"
            | "candidate.placementId"
            | "candidate.officeNumber"
            | "candidate.poNumber"
            | "candidate.payInterval"
            | "candidate._internalId"
            | "candidate.candidatePayType"
            | "candidate.umbrellaName"
            | "candidate.umbrellaCode"
            | "candidate.umbrellaId"
            | "candidate.bookingStartDate"
            | "candidate.assigneeBranchCodes"
            | "candidate.niNumberTrimmed"
            | "candidate.sortCodeTrimmed"
            | "candidate.accountNumberTrimmed"
            | "candidate.accountNameTrimmed"
            | "candidate.bookingOnCosts"
            | "candidate.payRateExclusiveHoliday"
            | "candidate.payRateHolidayPay"
            | "candidate.payeRate"
            | "candidate.payeMargin"
            | "candidate.payeWeeklyMargin"
            | "candidate.grossMargin"
            | "candidate.totalCharge"
            | "candidate.totalExcludingHolidayPay"
            | "candidate.totalHolidayPay"
            | "candidate.candidateDetails.title"
            | "candidate.candidateDetails.forename"
            | "candidate.candidateDetails.surname"
            | "candidate.candidateDetails.sex"
            | "candidate.candidateDetails.dob"
            | "candidate.candidateDetails.email"
            | "candidate.candidateDetails.telephone"
            | "candidate.candidateDetails.mobile"
            | "candidate.candidateDetails.address.addressLine1"
            | "candidate.candidateDetails.address.postcode"
            | "candidate.candidateDetails.gender"
            | "candidate.candidateDetails.bankDetails.niNumber"
            | "candidate.days.start"
            | "candidate.days.end"
            | "candidate.days.length"
            | "candidate.days.amPm"
            | "candidate.days.status"
            | "candidate.days.chargeRate"
            | "candidate.days.payRate"
            | "candidate.days.flaggedForRemoval"
            | "day.start"
            | "day.end"
            | "day.length"
            | "day.amPm"
            | "day.status"
            | "day.chargeRate"
            | "day.payRate"
            | "day.flaggedForRemoval"
            | "candidate.umbrellaAddress.addressLine1"
            | "candidate.umbrellaAddress.addressLine2"
            | "candidate.umbrellaAddress.addressLine3"
            | "candidate.umbrellaAddress.city"
            | "candidate.umbrellaAddress.county"
            | "candidate.umbrellaAddress.borough"
            | "candidate.umbrellaAddress.postcode"
            | "candidate.umbrellaAddress.country"
            | "candidate.umbrellaAddress.lat"
            | "candidate.umbrellaAddress.lng"
            | "candidate.bankDetails.bankAccountName"
            | "candidate.bankDetails.bankName"
            | "candidate.bankDetails.accountNumber"
            | "candidate.bankDetails.sortCode"
            | "candidate.bankDetails.niNumber"
            | "candidate.bankDetails.jobOp1"
            | "candidate.bankDetails.jobOp2"
            | "candidate.bankDetails.jobOp3"
            | "candidate.bankDetails.studentLoan.studentLoan"
            | "candidate.bankDetails.studentLoan.plan1"
            | "candidate.bankDetails.studentLoan.plan2"
            | "candidate.bankDetails.studentLoan.plan4"
            | "candidate.bankDetails.studentLoan.postGradLoan"
            | "candidate.timesheets.id"
            | "candidate.timesheets.candidateName"
            | "candidate.timesheets.candidate"
            | "candidate.timesheets.clientName"
            | "candidate.timesheets.clientEmail"
            | "candidate.timesheets.client"
            | "candidate.timesheets.booking"
            | "candidate.timesheets.days.start"
            | "candidate.timesheets.days.end"
            | "candidate.timesheets.days.length"
            | "candidate.timesheets.days.amPm"
            | "candidate.timesheets.days.status"
            | "candidate.timesheets.days.chargeRate"
            | "candidate.timesheets.days.payRate"
            | "candidate.timesheets.days.flaggedForRemoval"
            | "candidate.timesheets.status"
            | "candidate.timesheets.statusChangedOn"
            | "candidate.timesheets.statusChangeBy"
            | "candidate.timesheets.statusChangeByJobRole"
            | "candidate.timesheets.statusChangeBySignature"
            | "candidate.timesheets.statusChangeByDrawnSignature"
            | "candidate.timesheets.statusChangeBySignatureDate"
            | "candidate.timesheets.rejectionReason"
            | "candidate.timesheets.statusCandidate"
            | "candidate.timesheets.statusChangedOnCandidate"
            | "candidate.timesheets.statusChangeByCandidate"
            | "candidate.timesheets.statusChangeByJobRoleCandidate"
            | "candidate.timesheets.statusChangeByCandidateSignature"
            | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
            | "candidate.timesheets.statusChangeByCandidateSignatureDate"
            | "candidate.timesheets.rejectionReasonCandidate"
            | "candidate.timesheets.createdBy"
            | "candidate.timesheets.group"
            | "candidate.timesheets.resendTimesheetEmail"
            | "candidate.timesheets.customer"
            | "candidate.timesheets.assignee"
            | "candidate.timesheets.queryResponse"
            | "candidate.timesheets.invoiceNumber"
            | "candidate.timesheets.timesheetNumber"
            | "candidate.timesheets.type"
            | "candidate.timesheets.subType"
            | "candidate.timesheets.tuitionReport"
            | "candidate.timesheets.tuitionReportAnswers.questionId"
            | "candidate.timesheets.tuitionReportAnswers.value"
            | "candidate.timesheets.tuitionReportPending"
            | "candidate.timesheets.role"
            | "candidate.timesheets.statusApprovalIp"
            | "candidate.timesheets.statusApprovalCandidateIp"
          )
        | "";
      /**
       * Format to use for datetime fields
       */
      date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
      /**
       * Flush
       */
      flush?: boolean;
      /**
       * Value written to the export when the selected source field has no value.
       *
       * @maxLength 1000
       */
      defaultValue?: string;
      /**
       * Match
       */
      match?: {
        /**
         * @maxLength 1000
         */
        field?: string;
        /**
         * @maxLength 1000
         */
        match?: string;
        /**
         * @maxLength 1000
         */
        display?: string;
        /**
         * @maxLength 1000
         */
        displayValue?: string;
      };
      /**
       * Text replacement/processing to perform on this config item
       *
       * @maxItems 5
       */
      conditional?: {
        /**
         * Database data from which to derive
         */
        value?:
          | "candidate.candidateId"
          | "candidate.candidateName"
          | "candidate.timesheetId"
          | "candidate.timesheetInvoiceNumber"
          | "candidate.timesheetDate"
          | "candidate.timesheetNumber"
          | "candidate.timesheetStart"
          | "candidate.timesheetEnd"
          | "candidate.timesheetLastDay"
          | "candidate.timesheetApprover"
          | "candidate.timesheetLastSunday"
          | "candidate.timesheetStudentName"
          | "candidate.customClientId"
          | "candidate.customClientInternalId"
          | "candidate.clientName"
          | "candidate.clientId"
          | "candidate.customCandidateId"
          | "candidate.customCandidateInternalId"
          | "candidate.customCandidateIdPayType"
          | "candidate.clientContact"
          | "candidate.invoiceContact"
          | "candidate.total"
          | "candidate.peDate"
          | "candidate.bookingPayType"
          | "candidate.payRate"
          | "candidate.chargeRate"
          | "candidate.units"
          | "candidate.hours"
          | "candidate.hoursPerDay"
          | "candidate.consultant"
          | "candidate.role"
          | "candidate.clientTimesheetStatus"
          | "candidate.isCandidateFirstBooking"
          | "candidate.isCandidateFirstBookingWithClient"
          | "candidate.isClientFirstBooking"
          | "candidate.consultant1Id"
          | "candidate.consultant1Perc"
          | "candidate.consultant2Id"
          | "candidate.consultant2Perc"
          | "candidate.consultant3Id"
          | "candidate.consultant3Perc"
          | "candidate.consultant4Id"
          | "candidate.consultant4Perc"
          | "candidate.consultant5Id"
          | "candidate.consultant5Perc"
          | "candidate.placementId"
          | "candidate.officeNumber"
          | "candidate.poNumber"
          | "candidate.payInterval"
          | "candidate._internalId"
          | "candidate.candidatePayType"
          | "candidate.umbrellaName"
          | "candidate.umbrellaCode"
          | "candidate.umbrellaId"
          | "candidate.bookingStartDate"
          | "candidate.assigneeBranchCodes"
          | "candidate.niNumberTrimmed"
          | "candidate.sortCodeTrimmed"
          | "candidate.accountNumberTrimmed"
          | "candidate.accountNameTrimmed"
          | "candidate.bookingOnCosts"
          | "candidate.payRateExclusiveHoliday"
          | "candidate.payRateHolidayPay"
          | "candidate.payeRate"
          | "candidate.payeMargin"
          | "candidate.payeWeeklyMargin"
          | "candidate.grossMargin"
          | "candidate.totalCharge"
          | "candidate.totalExcludingHolidayPay"
          | "candidate.totalHolidayPay"
          | "candidate.candidateDetails.title"
          | "candidate.candidateDetails.forename"
          | "candidate.candidateDetails.surname"
          | "candidate.candidateDetails.sex"
          | "candidate.candidateDetails.dob"
          | "candidate.candidateDetails.email"
          | "candidate.candidateDetails.telephone"
          | "candidate.candidateDetails.mobile"
          | "candidate.candidateDetails.address.addressLine1"
          | "candidate.candidateDetails.address.postcode"
          | "candidate.candidateDetails.gender"
          | "candidate.candidateDetails.bankDetails.niNumber"
          | "candidate.days.start"
          | "candidate.days.end"
          | "candidate.days.length"
          | "candidate.days.amPm"
          | "candidate.days.status"
          | "candidate.days.chargeRate"
          | "candidate.days.payRate"
          | "candidate.days.flaggedForRemoval"
          | "day.start"
          | "day.end"
          | "day.length"
          | "day.amPm"
          | "day.status"
          | "day.chargeRate"
          | "day.payRate"
          | "day.flaggedForRemoval"
          | "candidate.umbrellaAddress.addressLine1"
          | "candidate.umbrellaAddress.addressLine2"
          | "candidate.umbrellaAddress.addressLine3"
          | "candidate.umbrellaAddress.city"
          | "candidate.umbrellaAddress.county"
          | "candidate.umbrellaAddress.borough"
          | "candidate.umbrellaAddress.postcode"
          | "candidate.umbrellaAddress.country"
          | "candidate.umbrellaAddress.lat"
          | "candidate.umbrellaAddress.lng"
          | "candidate.bankDetails.bankAccountName"
          | "candidate.bankDetails.bankName"
          | "candidate.bankDetails.accountNumber"
          | "candidate.bankDetails.sortCode"
          | "candidate.bankDetails.niNumber"
          | "candidate.bankDetails.jobOp1"
          | "candidate.bankDetails.jobOp2"
          | "candidate.bankDetails.jobOp3"
          | "candidate.bankDetails.studentLoan.studentLoan"
          | "candidate.bankDetails.studentLoan.plan1"
          | "candidate.bankDetails.studentLoan.plan2"
          | "candidate.bankDetails.studentLoan.plan4"
          | "candidate.bankDetails.studentLoan.postGradLoan"
          | "candidate.timesheets.id"
          | "candidate.timesheets.candidateName"
          | "candidate.timesheets.candidate"
          | "candidate.timesheets.clientName"
          | "candidate.timesheets.clientEmail"
          | "candidate.timesheets.client"
          | "candidate.timesheets.booking"
          | "candidate.timesheets.days.start"
          | "candidate.timesheets.days.end"
          | "candidate.timesheets.days.length"
          | "candidate.timesheets.days.amPm"
          | "candidate.timesheets.days.status"
          | "candidate.timesheets.days.chargeRate"
          | "candidate.timesheets.days.payRate"
          | "candidate.timesheets.days.flaggedForRemoval"
          | "candidate.timesheets.status"
          | "candidate.timesheets.statusChangedOn"
          | "candidate.timesheets.statusChangeBy"
          | "candidate.timesheets.statusChangeByJobRole"
          | "candidate.timesheets.statusChangeBySignature"
          | "candidate.timesheets.statusChangeByDrawnSignature"
          | "candidate.timesheets.statusChangeBySignatureDate"
          | "candidate.timesheets.rejectionReason"
          | "candidate.timesheets.statusCandidate"
          | "candidate.timesheets.statusChangedOnCandidate"
          | "candidate.timesheets.statusChangeByCandidate"
          | "candidate.timesheets.statusChangeByJobRoleCandidate"
          | "candidate.timesheets.statusChangeByCandidateSignature"
          | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
          | "candidate.timesheets.statusChangeByCandidateSignatureDate"
          | "candidate.timesheets.rejectionReasonCandidate"
          | "candidate.timesheets.createdBy"
          | "candidate.timesheets.group"
          | "candidate.timesheets.resendTimesheetEmail"
          | "candidate.timesheets.customer"
          | "candidate.timesheets.assignee"
          | "candidate.timesheets.queryResponse"
          | "candidate.timesheets.invoiceNumber"
          | "candidate.timesheets.timesheetNumber"
          | "candidate.timesheets.type"
          | "candidate.timesheets.subType"
          | "candidate.timesheets.tuitionReport"
          | "candidate.timesheets.tuitionReportAnswers.questionId"
          | "candidate.timesheets.tuitionReportAnswers.value"
          | "candidate.timesheets.tuitionReportPending"
          | "candidate.timesheets.role"
          | "candidate.timesheets.statusApprovalIp"
          | "candidate.timesheets.statusApprovalCandidateIp";
        /**
         * The data instance that should be replaced
         *
         * @minItems 1
         */
        match?: (string | number | boolean | (string | null) | (void | null))[];
        /**
         * Replacement if condition is met
         *
         * @maxLength 1000
         */
        ["true"]?: string;
        /**
         * Replacement if condition is not met
         *
         * @maxLength 1000
         */
        ["false"]?: string;
      }[];
      /**
       * Boolean
       */
      boolean?: boolean;
      /**
       * Way in which to format different data types
       */
      formatType?: "string" | "number" | "currency";
      /**
       * Configured value for this column in a flat booking row
       */
      source?:
        | {
            kind?: "field";
            value?:
              | "candidate.candidateId"
              | "candidate.candidateName"
              | "candidate.timesheetId"
              | "candidate.timesheetInvoiceNumber"
              | "candidate.timesheetDate"
              | "candidate.timesheetNumber"
              | "candidate.timesheetStart"
              | "candidate.timesheetEnd"
              | "candidate.timesheetLastDay"
              | "candidate.timesheetApprover"
              | "candidate.timesheetLastSunday"
              | "candidate.timesheetStudentName"
              | "candidate.customClientId"
              | "candidate.customClientInternalId"
              | "candidate.clientName"
              | "candidate.clientId"
              | "candidate.customCandidateId"
              | "candidate.customCandidateInternalId"
              | "candidate.customCandidateIdPayType"
              | "candidate.clientContact"
              | "candidate.invoiceContact"
              | "candidate.total"
              | "candidate.peDate"
              | "candidate.bookingPayType"
              | "candidate.payRate"
              | "candidate.chargeRate"
              | "candidate.units"
              | "candidate.hours"
              | "candidate.hoursPerDay"
              | "candidate.consultant"
              | "candidate.role"
              | "candidate.clientTimesheetStatus"
              | "candidate.isCandidateFirstBooking"
              | "candidate.isCandidateFirstBookingWithClient"
              | "candidate.isClientFirstBooking"
              | "candidate.consultant1Id"
              | "candidate.consultant1Perc"
              | "candidate.consultant2Id"
              | "candidate.consultant2Perc"
              | "candidate.consultant3Id"
              | "candidate.consultant3Perc"
              | "candidate.consultant4Id"
              | "candidate.consultant4Perc"
              | "candidate.consultant5Id"
              | "candidate.consultant5Perc"
              | "candidate.placementId"
              | "candidate.officeNumber"
              | "candidate.poNumber"
              | "candidate.payInterval"
              | "candidate._internalId"
              | "candidate.candidatePayType"
              | "candidate.umbrellaName"
              | "candidate.umbrellaCode"
              | "candidate.umbrellaId"
              | "candidate.bookingStartDate"
              | "candidate.assigneeBranchCodes"
              | "candidate.niNumberTrimmed"
              | "candidate.sortCodeTrimmed"
              | "candidate.accountNumberTrimmed"
              | "candidate.accountNameTrimmed"
              | "candidate.bookingOnCosts"
              | "candidate.payRateExclusiveHoliday"
              | "candidate.payRateHolidayPay"
              | "candidate.payeRate"
              | "candidate.payeMargin"
              | "candidate.payeWeeklyMargin"
              | "candidate.grossMargin"
              | "candidate.totalCharge"
              | "candidate.totalExcludingHolidayPay"
              | "candidate.totalHolidayPay"
              | "candidate.candidateDetails.title"
              | "candidate.candidateDetails.forename"
              | "candidate.candidateDetails.surname"
              | "candidate.candidateDetails.sex"
              | "candidate.candidateDetails.dob"
              | "candidate.candidateDetails.email"
              | "candidate.candidateDetails.telephone"
              | "candidate.candidateDetails.mobile"
              | "candidate.candidateDetails.address.addressLine1"
              | "candidate.candidateDetails.address.postcode"
              | "candidate.candidateDetails.gender"
              | "candidate.candidateDetails.bankDetails.niNumber"
              | "candidate.days.start"
              | "candidate.days.end"
              | "candidate.days.length"
              | "candidate.days.amPm"
              | "candidate.days.status"
              | "candidate.days.chargeRate"
              | "candidate.days.payRate"
              | "candidate.days.flaggedForRemoval"
              | "day.start"
              | "day.end"
              | "day.length"
              | "day.amPm"
              | "day.status"
              | "day.chargeRate"
              | "day.payRate"
              | "day.flaggedForRemoval"
              | "candidate.umbrellaAddress.addressLine1"
              | "candidate.umbrellaAddress.addressLine2"
              | "candidate.umbrellaAddress.addressLine3"
              | "candidate.umbrellaAddress.city"
              | "candidate.umbrellaAddress.county"
              | "candidate.umbrellaAddress.borough"
              | "candidate.umbrellaAddress.postcode"
              | "candidate.umbrellaAddress.country"
              | "candidate.umbrellaAddress.lat"
              | "candidate.umbrellaAddress.lng"
              | "candidate.bankDetails.bankAccountName"
              | "candidate.bankDetails.bankName"
              | "candidate.bankDetails.accountNumber"
              | "candidate.bankDetails.sortCode"
              | "candidate.bankDetails.niNumber"
              | "candidate.bankDetails.jobOp1"
              | "candidate.bankDetails.jobOp2"
              | "candidate.bankDetails.jobOp3"
              | "candidate.bankDetails.studentLoan.studentLoan"
              | "candidate.bankDetails.studentLoan.plan1"
              | "candidate.bankDetails.studentLoan.plan2"
              | "candidate.bankDetails.studentLoan.plan4"
              | "candidate.bankDetails.studentLoan.postGradLoan"
              | "candidate.timesheets.id"
              | "candidate.timesheets.candidateName"
              | "candidate.timesheets.candidate"
              | "candidate.timesheets.clientName"
              | "candidate.timesheets.clientEmail"
              | "candidate.timesheets.client"
              | "candidate.timesheets.booking"
              | "candidate.timesheets.days.start"
              | "candidate.timesheets.days.end"
              | "candidate.timesheets.days.length"
              | "candidate.timesheets.days.amPm"
              | "candidate.timesheets.days.status"
              | "candidate.timesheets.days.chargeRate"
              | "candidate.timesheets.days.payRate"
              | "candidate.timesheets.days.flaggedForRemoval"
              | "candidate.timesheets.status"
              | "candidate.timesheets.statusChangedOn"
              | "candidate.timesheets.statusChangeBy"
              | "candidate.timesheets.statusChangeByJobRole"
              | "candidate.timesheets.statusChangeBySignature"
              | "candidate.timesheets.statusChangeByDrawnSignature"
              | "candidate.timesheets.statusChangeBySignatureDate"
              | "candidate.timesheets.rejectionReason"
              | "candidate.timesheets.statusCandidate"
              | "candidate.timesheets.statusChangedOnCandidate"
              | "candidate.timesheets.statusChangeByCandidate"
              | "candidate.timesheets.statusChangeByJobRoleCandidate"
              | "candidate.timesheets.statusChangeByCandidateSignature"
              | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
              | "candidate.timesheets.statusChangeByCandidateSignatureDate"
              | "candidate.timesheets.rejectionReasonCandidate"
              | "candidate.timesheets.createdBy"
              | "candidate.timesheets.group"
              | "candidate.timesheets.resendTimesheetEmail"
              | "candidate.timesheets.customer"
              | "candidate.timesheets.assignee"
              | "candidate.timesheets.queryResponse"
              | "candidate.timesheets.invoiceNumber"
              | "candidate.timesheets.timesheetNumber"
              | "candidate.timesheets.type"
              | "candidate.timesheets.subType"
              | "candidate.timesheets.tuitionReport"
              | "candidate.timesheets.tuitionReportAnswers.questionId"
              | "candidate.timesheets.tuitionReportAnswers.value"
              | "candidate.timesheets.tuitionReportPending"
              | "candidate.timesheets.role"
              | "candidate.timesheets.statusApprovalIp"
              | "candidate.timesheets.statusApprovalCandidateIp";
            /**
             * @maxLength 100
             */
            prefix?: string;
            /**
             * @maxLength 100
             */
            suffix?: string;
            /**
             * @maxItems 20
             */
            cases?: {
              match?: string | number | boolean;
              /**
               * @maxLength 1000
               */
              value?: string;
            }[];
            /**
             * @maxLength 1000
             */
            fallback?: string;
            date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
          }
        | {
            kind?: "static";
            /**
             * @maxLength 1000
             */
            value?: string;
          }
        | {
            kind?: "calculated";
            value?:
              | "rowNumber"
              | "invoiceFriday"
              | "financialYear"
              | "financialPeriod"
              | "bookingWeekMonday"
              | "dayWorked"
              | "dailyNetCharge"
              | "dailyVat"
              | "dailyPay"
              | "bookingNetTotal"
              | "bookingVatTotal"
              | "bookingGrossTotal";
            sign?: "positive" | "negative";
            date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
          }
        | {
            kind?: "concat";
            /**
             * @minItems 1
             * @maxItems 20
             */
            parts?: (
              | {
                  kind?: "field";
                  value?:
                    | "candidate.candidateId"
                    | "candidate.candidateName"
                    | "candidate.timesheetId"
                    | "candidate.timesheetInvoiceNumber"
                    | "candidate.timesheetDate"
                    | "candidate.timesheetNumber"
                    | "candidate.timesheetStart"
                    | "candidate.timesheetEnd"
                    | "candidate.timesheetLastDay"
                    | "candidate.timesheetApprover"
                    | "candidate.timesheetLastSunday"
                    | "candidate.timesheetStudentName"
                    | "candidate.customClientId"
                    | "candidate.customClientInternalId"
                    | "candidate.clientName"
                    | "candidate.clientId"
                    | "candidate.customCandidateId"
                    | "candidate.customCandidateInternalId"
                    | "candidate.customCandidateIdPayType"
                    | "candidate.clientContact"
                    | "candidate.invoiceContact"
                    | "candidate.total"
                    | "candidate.peDate"
                    | "candidate.bookingPayType"
                    | "candidate.payRate"
                    | "candidate.chargeRate"
                    | "candidate.units"
                    | "candidate.hours"
                    | "candidate.hoursPerDay"
                    | "candidate.consultant"
                    | "candidate.role"
                    | "candidate.clientTimesheetStatus"
                    | "candidate.isCandidateFirstBooking"
                    | "candidate.isCandidateFirstBookingWithClient"
                    | "candidate.isClientFirstBooking"
                    | "candidate.consultant1Id"
                    | "candidate.consultant1Perc"
                    | "candidate.consultant2Id"
                    | "candidate.consultant2Perc"
                    | "candidate.consultant3Id"
                    | "candidate.consultant3Perc"
                    | "candidate.consultant4Id"
                    | "candidate.consultant4Perc"
                    | "candidate.consultant5Id"
                    | "candidate.consultant5Perc"
                    | "candidate.placementId"
                    | "candidate.officeNumber"
                    | "candidate.poNumber"
                    | "candidate.payInterval"
                    | "candidate._internalId"
                    | "candidate.candidatePayType"
                    | "candidate.umbrellaName"
                    | "candidate.umbrellaCode"
                    | "candidate.umbrellaId"
                    | "candidate.bookingStartDate"
                    | "candidate.assigneeBranchCodes"
                    | "candidate.niNumberTrimmed"
                    | "candidate.sortCodeTrimmed"
                    | "candidate.accountNumberTrimmed"
                    | "candidate.accountNameTrimmed"
                    | "candidate.bookingOnCosts"
                    | "candidate.payRateExclusiveHoliday"
                    | "candidate.payRateHolidayPay"
                    | "candidate.payeRate"
                    | "candidate.payeMargin"
                    | "candidate.payeWeeklyMargin"
                    | "candidate.grossMargin"
                    | "candidate.totalCharge"
                    | "candidate.totalExcludingHolidayPay"
                    | "candidate.totalHolidayPay"
                    | "candidate.candidateDetails.title"
                    | "candidate.candidateDetails.forename"
                    | "candidate.candidateDetails.surname"
                    | "candidate.candidateDetails.sex"
                    | "candidate.candidateDetails.dob"
                    | "candidate.candidateDetails.email"
                    | "candidate.candidateDetails.telephone"
                    | "candidate.candidateDetails.mobile"
                    | "candidate.candidateDetails.address.addressLine1"
                    | "candidate.candidateDetails.address.postcode"
                    | "candidate.candidateDetails.gender"
                    | "candidate.candidateDetails.bankDetails.niNumber"
                    | "candidate.days.start"
                    | "candidate.days.end"
                    | "candidate.days.length"
                    | "candidate.days.amPm"
                    | "candidate.days.status"
                    | "candidate.days.chargeRate"
                    | "candidate.days.payRate"
                    | "candidate.days.flaggedForRemoval"
                    | "day.start"
                    | "day.end"
                    | "day.length"
                    | "day.amPm"
                    | "day.status"
                    | "day.chargeRate"
                    | "day.payRate"
                    | "day.flaggedForRemoval"
                    | "candidate.umbrellaAddress.addressLine1"
                    | "candidate.umbrellaAddress.addressLine2"
                    | "candidate.umbrellaAddress.addressLine3"
                    | "candidate.umbrellaAddress.city"
                    | "candidate.umbrellaAddress.county"
                    | "candidate.umbrellaAddress.borough"
                    | "candidate.umbrellaAddress.postcode"
                    | "candidate.umbrellaAddress.country"
                    | "candidate.umbrellaAddress.lat"
                    | "candidate.umbrellaAddress.lng"
                    | "candidate.bankDetails.bankAccountName"
                    | "candidate.bankDetails.bankName"
                    | "candidate.bankDetails.accountNumber"
                    | "candidate.bankDetails.sortCode"
                    | "candidate.bankDetails.niNumber"
                    | "candidate.bankDetails.jobOp1"
                    | "candidate.bankDetails.jobOp2"
                    | "candidate.bankDetails.jobOp3"
                    | "candidate.bankDetails.studentLoan.studentLoan"
                    | "candidate.bankDetails.studentLoan.plan1"
                    | "candidate.bankDetails.studentLoan.plan2"
                    | "candidate.bankDetails.studentLoan.plan4"
                    | "candidate.bankDetails.studentLoan.postGradLoan"
                    | "candidate.timesheets.id"
                    | "candidate.timesheets.candidateName"
                    | "candidate.timesheets.candidate"
                    | "candidate.timesheets.clientName"
                    | "candidate.timesheets.clientEmail"
                    | "candidate.timesheets.client"
                    | "candidate.timesheets.booking"
                    | "candidate.timesheets.days.start"
                    | "candidate.timesheets.days.end"
                    | "candidate.timesheets.days.length"
                    | "candidate.timesheets.days.amPm"
                    | "candidate.timesheets.days.status"
                    | "candidate.timesheets.days.chargeRate"
                    | "candidate.timesheets.days.payRate"
                    | "candidate.timesheets.days.flaggedForRemoval"
                    | "candidate.timesheets.status"
                    | "candidate.timesheets.statusChangedOn"
                    | "candidate.timesheets.statusChangeBy"
                    | "candidate.timesheets.statusChangeByJobRole"
                    | "candidate.timesheets.statusChangeBySignature"
                    | "candidate.timesheets.statusChangeByDrawnSignature"
                    | "candidate.timesheets.statusChangeBySignatureDate"
                    | "candidate.timesheets.rejectionReason"
                    | "candidate.timesheets.statusCandidate"
                    | "candidate.timesheets.statusChangedOnCandidate"
                    | "candidate.timesheets.statusChangeByCandidate"
                    | "candidate.timesheets.statusChangeByJobRoleCandidate"
                    | "candidate.timesheets.statusChangeByCandidateSignature"
                    | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                    | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                    | "candidate.timesheets.rejectionReasonCandidate"
                    | "candidate.timesheets.createdBy"
                    | "candidate.timesheets.group"
                    | "candidate.timesheets.resendTimesheetEmail"
                    | "candidate.timesheets.customer"
                    | "candidate.timesheets.assignee"
                    | "candidate.timesheets.queryResponse"
                    | "candidate.timesheets.invoiceNumber"
                    | "candidate.timesheets.timesheetNumber"
                    | "candidate.timesheets.type"
                    | "candidate.timesheets.subType"
                    | "candidate.timesheets.tuitionReport"
                    | "candidate.timesheets.tuitionReportAnswers.questionId"
                    | "candidate.timesheets.tuitionReportAnswers.value"
                    | "candidate.timesheets.tuitionReportPending"
                    | "candidate.timesheets.role"
                    | "candidate.timesheets.statusApprovalIp"
                    | "candidate.timesheets.statusApprovalCandidateIp";
                  /**
                   * @maxLength 100
                   */
                  prefix?: string;
                  /**
                   * @maxLength 100
                   */
                  suffix?: string;
                  /**
                   * @maxItems 20
                   */
                  cases?: {
                    match?: string | number | boolean;
                    /**
                     * @maxLength 1000
                     */
                    value?: string;
                  }[];
                  /**
                   * @maxLength 1000
                   */
                  fallback?: string;
                  date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                }
              | {
                  kind?: "static";
                  /**
                   * @maxLength 1000
                   */
                  value?: string;
                }
              | {
                  kind?: "calculated";
                  value?:
                    | "rowNumber"
                    | "invoiceFriday"
                    | "financialYear"
                    | "financialPeriod"
                    | "bookingWeekMonday"
                    | "dayWorked"
                    | "dailyNetCharge"
                    | "dailyVat"
                    | "dailyPay"
                    | "bookingNetTotal"
                    | "bookingVatTotal"
                    | "bookingGrossTotal";
                  sign?: "positive" | "negative";
                  date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                }
            )[];
            /**
             * @maxLength 100
             */
            separator?: string;
          };
      /**
       * Independent values for parent, repeated day and summary rows
       */
      rowValues?: {
        parent?:
          | {
              kind?: "field";
              value?:
                | "candidate.candidateId"
                | "candidate.candidateName"
                | "candidate.timesheetId"
                | "candidate.timesheetInvoiceNumber"
                | "candidate.timesheetDate"
                | "candidate.timesheetNumber"
                | "candidate.timesheetStart"
                | "candidate.timesheetEnd"
                | "candidate.timesheetLastDay"
                | "candidate.timesheetApprover"
                | "candidate.timesheetLastSunday"
                | "candidate.timesheetStudentName"
                | "candidate.customClientId"
                | "candidate.customClientInternalId"
                | "candidate.clientName"
                | "candidate.clientId"
                | "candidate.customCandidateId"
                | "candidate.customCandidateInternalId"
                | "candidate.customCandidateIdPayType"
                | "candidate.clientContact"
                | "candidate.invoiceContact"
                | "candidate.total"
                | "candidate.peDate"
                | "candidate.bookingPayType"
                | "candidate.payRate"
                | "candidate.chargeRate"
                | "candidate.units"
                | "candidate.hours"
                | "candidate.hoursPerDay"
                | "candidate.consultant"
                | "candidate.role"
                | "candidate.clientTimesheetStatus"
                | "candidate.isCandidateFirstBooking"
                | "candidate.isCandidateFirstBookingWithClient"
                | "candidate.isClientFirstBooking"
                | "candidate.consultant1Id"
                | "candidate.consultant1Perc"
                | "candidate.consultant2Id"
                | "candidate.consultant2Perc"
                | "candidate.consultant3Id"
                | "candidate.consultant3Perc"
                | "candidate.consultant4Id"
                | "candidate.consultant4Perc"
                | "candidate.consultant5Id"
                | "candidate.consultant5Perc"
                | "candidate.placementId"
                | "candidate.officeNumber"
                | "candidate.poNumber"
                | "candidate.payInterval"
                | "candidate._internalId"
                | "candidate.candidatePayType"
                | "candidate.umbrellaName"
                | "candidate.umbrellaCode"
                | "candidate.umbrellaId"
                | "candidate.bookingStartDate"
                | "candidate.assigneeBranchCodes"
                | "candidate.niNumberTrimmed"
                | "candidate.sortCodeTrimmed"
                | "candidate.accountNumberTrimmed"
                | "candidate.accountNameTrimmed"
                | "candidate.bookingOnCosts"
                | "candidate.payRateExclusiveHoliday"
                | "candidate.payRateHolidayPay"
                | "candidate.payeRate"
                | "candidate.payeMargin"
                | "candidate.payeWeeklyMargin"
                | "candidate.grossMargin"
                | "candidate.totalCharge"
                | "candidate.totalExcludingHolidayPay"
                | "candidate.totalHolidayPay"
                | "candidate.candidateDetails.title"
                | "candidate.candidateDetails.forename"
                | "candidate.candidateDetails.surname"
                | "candidate.candidateDetails.sex"
                | "candidate.candidateDetails.dob"
                | "candidate.candidateDetails.email"
                | "candidate.candidateDetails.telephone"
                | "candidate.candidateDetails.mobile"
                | "candidate.candidateDetails.address.addressLine1"
                | "candidate.candidateDetails.address.postcode"
                | "candidate.candidateDetails.gender"
                | "candidate.candidateDetails.bankDetails.niNumber"
                | "candidate.days.start"
                | "candidate.days.end"
                | "candidate.days.length"
                | "candidate.days.amPm"
                | "candidate.days.status"
                | "candidate.days.chargeRate"
                | "candidate.days.payRate"
                | "candidate.days.flaggedForRemoval"
                | "day.start"
                | "day.end"
                | "day.length"
                | "day.amPm"
                | "day.status"
                | "day.chargeRate"
                | "day.payRate"
                | "day.flaggedForRemoval"
                | "candidate.umbrellaAddress.addressLine1"
                | "candidate.umbrellaAddress.addressLine2"
                | "candidate.umbrellaAddress.addressLine3"
                | "candidate.umbrellaAddress.city"
                | "candidate.umbrellaAddress.county"
                | "candidate.umbrellaAddress.borough"
                | "candidate.umbrellaAddress.postcode"
                | "candidate.umbrellaAddress.country"
                | "candidate.umbrellaAddress.lat"
                | "candidate.umbrellaAddress.lng"
                | "candidate.bankDetails.bankAccountName"
                | "candidate.bankDetails.bankName"
                | "candidate.bankDetails.accountNumber"
                | "candidate.bankDetails.sortCode"
                | "candidate.bankDetails.niNumber"
                | "candidate.bankDetails.jobOp1"
                | "candidate.bankDetails.jobOp2"
                | "candidate.bankDetails.jobOp3"
                | "candidate.bankDetails.studentLoan.studentLoan"
                | "candidate.bankDetails.studentLoan.plan1"
                | "candidate.bankDetails.studentLoan.plan2"
                | "candidate.bankDetails.studentLoan.plan4"
                | "candidate.bankDetails.studentLoan.postGradLoan"
                | "candidate.timesheets.id"
                | "candidate.timesheets.candidateName"
                | "candidate.timesheets.candidate"
                | "candidate.timesheets.clientName"
                | "candidate.timesheets.clientEmail"
                | "candidate.timesheets.client"
                | "candidate.timesheets.booking"
                | "candidate.timesheets.days.start"
                | "candidate.timesheets.days.end"
                | "candidate.timesheets.days.length"
                | "candidate.timesheets.days.amPm"
                | "candidate.timesheets.days.status"
                | "candidate.timesheets.days.chargeRate"
                | "candidate.timesheets.days.payRate"
                | "candidate.timesheets.days.flaggedForRemoval"
                | "candidate.timesheets.status"
                | "candidate.timesheets.statusChangedOn"
                | "candidate.timesheets.statusChangeBy"
                | "candidate.timesheets.statusChangeByJobRole"
                | "candidate.timesheets.statusChangeBySignature"
                | "candidate.timesheets.statusChangeByDrawnSignature"
                | "candidate.timesheets.statusChangeBySignatureDate"
                | "candidate.timesheets.rejectionReason"
                | "candidate.timesheets.statusCandidate"
                | "candidate.timesheets.statusChangedOnCandidate"
                | "candidate.timesheets.statusChangeByCandidate"
                | "candidate.timesheets.statusChangeByJobRoleCandidate"
                | "candidate.timesheets.statusChangeByCandidateSignature"
                | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                | "candidate.timesheets.rejectionReasonCandidate"
                | "candidate.timesheets.createdBy"
                | "candidate.timesheets.group"
                | "candidate.timesheets.resendTimesheetEmail"
                | "candidate.timesheets.customer"
                | "candidate.timesheets.assignee"
                | "candidate.timesheets.queryResponse"
                | "candidate.timesheets.invoiceNumber"
                | "candidate.timesheets.timesheetNumber"
                | "candidate.timesheets.type"
                | "candidate.timesheets.subType"
                | "candidate.timesheets.tuitionReport"
                | "candidate.timesheets.tuitionReportAnswers.questionId"
                | "candidate.timesheets.tuitionReportAnswers.value"
                | "candidate.timesheets.tuitionReportPending"
                | "candidate.timesheets.role"
                | "candidate.timesheets.statusApprovalIp"
                | "candidate.timesheets.statusApprovalCandidateIp";
              /**
               * @maxLength 100
               */
              prefix?: string;
              /**
               * @maxLength 100
               */
              suffix?: string;
              /**
               * @maxItems 20
               */
              cases?: {
                match?: string | number | boolean;
                /**
                 * @maxLength 1000
                 */
                value?: string;
              }[];
              /**
               * @maxLength 1000
               */
              fallback?: string;
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "static";
              /**
               * @maxLength 1000
               */
              value?: string;
            }
          | {
              kind?: "calculated";
              value?:
                | "rowNumber"
                | "invoiceFriday"
                | "financialYear"
                | "financialPeriod"
                | "bookingWeekMonday"
                | "dayWorked"
                | "dailyNetCharge"
                | "dailyVat"
                | "dailyPay"
                | "bookingNetTotal"
                | "bookingVatTotal"
                | "bookingGrossTotal";
              sign?: "positive" | "negative";
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "concat";
              /**
               * @minItems 1
               * @maxItems 20
               */
              parts?: (
                | {
                    kind?: "field";
                    value?:
                      | "candidate.candidateId"
                      | "candidate.candidateName"
                      | "candidate.timesheetId"
                      | "candidate.timesheetInvoiceNumber"
                      | "candidate.timesheetDate"
                      | "candidate.timesheetNumber"
                      | "candidate.timesheetStart"
                      | "candidate.timesheetEnd"
                      | "candidate.timesheetLastDay"
                      | "candidate.timesheetApprover"
                      | "candidate.timesheetLastSunday"
                      | "candidate.timesheetStudentName"
                      | "candidate.customClientId"
                      | "candidate.customClientInternalId"
                      | "candidate.clientName"
                      | "candidate.clientId"
                      | "candidate.customCandidateId"
                      | "candidate.customCandidateInternalId"
                      | "candidate.customCandidateIdPayType"
                      | "candidate.clientContact"
                      | "candidate.invoiceContact"
                      | "candidate.total"
                      | "candidate.peDate"
                      | "candidate.bookingPayType"
                      | "candidate.payRate"
                      | "candidate.chargeRate"
                      | "candidate.units"
                      | "candidate.hours"
                      | "candidate.hoursPerDay"
                      | "candidate.consultant"
                      | "candidate.role"
                      | "candidate.clientTimesheetStatus"
                      | "candidate.isCandidateFirstBooking"
                      | "candidate.isCandidateFirstBookingWithClient"
                      | "candidate.isClientFirstBooking"
                      | "candidate.consultant1Id"
                      | "candidate.consultant1Perc"
                      | "candidate.consultant2Id"
                      | "candidate.consultant2Perc"
                      | "candidate.consultant3Id"
                      | "candidate.consultant3Perc"
                      | "candidate.consultant4Id"
                      | "candidate.consultant4Perc"
                      | "candidate.consultant5Id"
                      | "candidate.consultant5Perc"
                      | "candidate.placementId"
                      | "candidate.officeNumber"
                      | "candidate.poNumber"
                      | "candidate.payInterval"
                      | "candidate._internalId"
                      | "candidate.candidatePayType"
                      | "candidate.umbrellaName"
                      | "candidate.umbrellaCode"
                      | "candidate.umbrellaId"
                      | "candidate.bookingStartDate"
                      | "candidate.assigneeBranchCodes"
                      | "candidate.niNumberTrimmed"
                      | "candidate.sortCodeTrimmed"
                      | "candidate.accountNumberTrimmed"
                      | "candidate.accountNameTrimmed"
                      | "candidate.bookingOnCosts"
                      | "candidate.payRateExclusiveHoliday"
                      | "candidate.payRateHolidayPay"
                      | "candidate.payeRate"
                      | "candidate.payeMargin"
                      | "candidate.payeWeeklyMargin"
                      | "candidate.grossMargin"
                      | "candidate.totalCharge"
                      | "candidate.totalExcludingHolidayPay"
                      | "candidate.totalHolidayPay"
                      | "candidate.candidateDetails.title"
                      | "candidate.candidateDetails.forename"
                      | "candidate.candidateDetails.surname"
                      | "candidate.candidateDetails.sex"
                      | "candidate.candidateDetails.dob"
                      | "candidate.candidateDetails.email"
                      | "candidate.candidateDetails.telephone"
                      | "candidate.candidateDetails.mobile"
                      | "candidate.candidateDetails.address.addressLine1"
                      | "candidate.candidateDetails.address.postcode"
                      | "candidate.candidateDetails.gender"
                      | "candidate.candidateDetails.bankDetails.niNumber"
                      | "candidate.days.start"
                      | "candidate.days.end"
                      | "candidate.days.length"
                      | "candidate.days.amPm"
                      | "candidate.days.status"
                      | "candidate.days.chargeRate"
                      | "candidate.days.payRate"
                      | "candidate.days.flaggedForRemoval"
                      | "day.start"
                      | "day.end"
                      | "day.length"
                      | "day.amPm"
                      | "day.status"
                      | "day.chargeRate"
                      | "day.payRate"
                      | "day.flaggedForRemoval"
                      | "candidate.umbrellaAddress.addressLine1"
                      | "candidate.umbrellaAddress.addressLine2"
                      | "candidate.umbrellaAddress.addressLine3"
                      | "candidate.umbrellaAddress.city"
                      | "candidate.umbrellaAddress.county"
                      | "candidate.umbrellaAddress.borough"
                      | "candidate.umbrellaAddress.postcode"
                      | "candidate.umbrellaAddress.country"
                      | "candidate.umbrellaAddress.lat"
                      | "candidate.umbrellaAddress.lng"
                      | "candidate.bankDetails.bankAccountName"
                      | "candidate.bankDetails.bankName"
                      | "candidate.bankDetails.accountNumber"
                      | "candidate.bankDetails.sortCode"
                      | "candidate.bankDetails.niNumber"
                      | "candidate.bankDetails.jobOp1"
                      | "candidate.bankDetails.jobOp2"
                      | "candidate.bankDetails.jobOp3"
                      | "candidate.bankDetails.studentLoan.studentLoan"
                      | "candidate.bankDetails.studentLoan.plan1"
                      | "candidate.bankDetails.studentLoan.plan2"
                      | "candidate.bankDetails.studentLoan.plan4"
                      | "candidate.bankDetails.studentLoan.postGradLoan"
                      | "candidate.timesheets.id"
                      | "candidate.timesheets.candidateName"
                      | "candidate.timesheets.candidate"
                      | "candidate.timesheets.clientName"
                      | "candidate.timesheets.clientEmail"
                      | "candidate.timesheets.client"
                      | "candidate.timesheets.booking"
                      | "candidate.timesheets.days.start"
                      | "candidate.timesheets.days.end"
                      | "candidate.timesheets.days.length"
                      | "candidate.timesheets.days.amPm"
                      | "candidate.timesheets.days.status"
                      | "candidate.timesheets.days.chargeRate"
                      | "candidate.timesheets.days.payRate"
                      | "candidate.timesheets.days.flaggedForRemoval"
                      | "candidate.timesheets.status"
                      | "candidate.timesheets.statusChangedOn"
                      | "candidate.timesheets.statusChangeBy"
                      | "candidate.timesheets.statusChangeByJobRole"
                      | "candidate.timesheets.statusChangeBySignature"
                      | "candidate.timesheets.statusChangeByDrawnSignature"
                      | "candidate.timesheets.statusChangeBySignatureDate"
                      | "candidate.timesheets.rejectionReason"
                      | "candidate.timesheets.statusCandidate"
                      | "candidate.timesheets.statusChangedOnCandidate"
                      | "candidate.timesheets.statusChangeByCandidate"
                      | "candidate.timesheets.statusChangeByJobRoleCandidate"
                      | "candidate.timesheets.statusChangeByCandidateSignature"
                      | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                      | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                      | "candidate.timesheets.rejectionReasonCandidate"
                      | "candidate.timesheets.createdBy"
                      | "candidate.timesheets.group"
                      | "candidate.timesheets.resendTimesheetEmail"
                      | "candidate.timesheets.customer"
                      | "candidate.timesheets.assignee"
                      | "candidate.timesheets.queryResponse"
                      | "candidate.timesheets.invoiceNumber"
                      | "candidate.timesheets.timesheetNumber"
                      | "candidate.timesheets.type"
                      | "candidate.timesheets.subType"
                      | "candidate.timesheets.tuitionReport"
                      | "candidate.timesheets.tuitionReportAnswers.questionId"
                      | "candidate.timesheets.tuitionReportAnswers.value"
                      | "candidate.timesheets.tuitionReportPending"
                      | "candidate.timesheets.role"
                      | "candidate.timesheets.statusApprovalIp"
                      | "candidate.timesheets.statusApprovalCandidateIp";
                    /**
                     * @maxLength 100
                     */
                    prefix?: string;
                    /**
                     * @maxLength 100
                     */
                    suffix?: string;
                    /**
                     * @maxItems 20
                     */
                    cases?: {
                      match?: string | number | boolean;
                      /**
                       * @maxLength 1000
                       */
                      value?: string;
                    }[];
                    /**
                     * @maxLength 1000
                     */
                    fallback?: string;
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
                | {
                    kind?: "static";
                    /**
                     * @maxLength 1000
                     */
                    value?: string;
                  }
                | {
                    kind?: "calculated";
                    value?:
                      | "rowNumber"
                      | "invoiceFriday"
                      | "financialYear"
                      | "financialPeriod"
                      | "bookingWeekMonday"
                      | "dayWorked"
                      | "dailyNetCharge"
                      | "dailyVat"
                      | "dailyPay"
                      | "bookingNetTotal"
                      | "bookingVatTotal"
                      | "bookingGrossTotal";
                    sign?: "positive" | "negative";
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
              )[];
              /**
               * @maxLength 100
               */
              separator?: string;
            };
        day?:
          | {
              kind?: "field";
              value?:
                | "candidate.candidateId"
                | "candidate.candidateName"
                | "candidate.timesheetId"
                | "candidate.timesheetInvoiceNumber"
                | "candidate.timesheetDate"
                | "candidate.timesheetNumber"
                | "candidate.timesheetStart"
                | "candidate.timesheetEnd"
                | "candidate.timesheetLastDay"
                | "candidate.timesheetApprover"
                | "candidate.timesheetLastSunday"
                | "candidate.timesheetStudentName"
                | "candidate.customClientId"
                | "candidate.customClientInternalId"
                | "candidate.clientName"
                | "candidate.clientId"
                | "candidate.customCandidateId"
                | "candidate.customCandidateInternalId"
                | "candidate.customCandidateIdPayType"
                | "candidate.clientContact"
                | "candidate.invoiceContact"
                | "candidate.total"
                | "candidate.peDate"
                | "candidate.bookingPayType"
                | "candidate.payRate"
                | "candidate.chargeRate"
                | "candidate.units"
                | "candidate.hours"
                | "candidate.hoursPerDay"
                | "candidate.consultant"
                | "candidate.role"
                | "candidate.clientTimesheetStatus"
                | "candidate.isCandidateFirstBooking"
                | "candidate.isCandidateFirstBookingWithClient"
                | "candidate.isClientFirstBooking"
                | "candidate.consultant1Id"
                | "candidate.consultant1Perc"
                | "candidate.consultant2Id"
                | "candidate.consultant2Perc"
                | "candidate.consultant3Id"
                | "candidate.consultant3Perc"
                | "candidate.consultant4Id"
                | "candidate.consultant4Perc"
                | "candidate.consultant5Id"
                | "candidate.consultant5Perc"
                | "candidate.placementId"
                | "candidate.officeNumber"
                | "candidate.poNumber"
                | "candidate.payInterval"
                | "candidate._internalId"
                | "candidate.candidatePayType"
                | "candidate.umbrellaName"
                | "candidate.umbrellaCode"
                | "candidate.umbrellaId"
                | "candidate.bookingStartDate"
                | "candidate.assigneeBranchCodes"
                | "candidate.niNumberTrimmed"
                | "candidate.sortCodeTrimmed"
                | "candidate.accountNumberTrimmed"
                | "candidate.accountNameTrimmed"
                | "candidate.bookingOnCosts"
                | "candidate.payRateExclusiveHoliday"
                | "candidate.payRateHolidayPay"
                | "candidate.payeRate"
                | "candidate.payeMargin"
                | "candidate.payeWeeklyMargin"
                | "candidate.grossMargin"
                | "candidate.totalCharge"
                | "candidate.totalExcludingHolidayPay"
                | "candidate.totalHolidayPay"
                | "candidate.candidateDetails.title"
                | "candidate.candidateDetails.forename"
                | "candidate.candidateDetails.surname"
                | "candidate.candidateDetails.sex"
                | "candidate.candidateDetails.dob"
                | "candidate.candidateDetails.email"
                | "candidate.candidateDetails.telephone"
                | "candidate.candidateDetails.mobile"
                | "candidate.candidateDetails.address.addressLine1"
                | "candidate.candidateDetails.address.postcode"
                | "candidate.candidateDetails.gender"
                | "candidate.candidateDetails.bankDetails.niNumber"
                | "candidate.days.start"
                | "candidate.days.end"
                | "candidate.days.length"
                | "candidate.days.amPm"
                | "candidate.days.status"
                | "candidate.days.chargeRate"
                | "candidate.days.payRate"
                | "candidate.days.flaggedForRemoval"
                | "day.start"
                | "day.end"
                | "day.length"
                | "day.amPm"
                | "day.status"
                | "day.chargeRate"
                | "day.payRate"
                | "day.flaggedForRemoval"
                | "candidate.umbrellaAddress.addressLine1"
                | "candidate.umbrellaAddress.addressLine2"
                | "candidate.umbrellaAddress.addressLine3"
                | "candidate.umbrellaAddress.city"
                | "candidate.umbrellaAddress.county"
                | "candidate.umbrellaAddress.borough"
                | "candidate.umbrellaAddress.postcode"
                | "candidate.umbrellaAddress.country"
                | "candidate.umbrellaAddress.lat"
                | "candidate.umbrellaAddress.lng"
                | "candidate.bankDetails.bankAccountName"
                | "candidate.bankDetails.bankName"
                | "candidate.bankDetails.accountNumber"
                | "candidate.bankDetails.sortCode"
                | "candidate.bankDetails.niNumber"
                | "candidate.bankDetails.jobOp1"
                | "candidate.bankDetails.jobOp2"
                | "candidate.bankDetails.jobOp3"
                | "candidate.bankDetails.studentLoan.studentLoan"
                | "candidate.bankDetails.studentLoan.plan1"
                | "candidate.bankDetails.studentLoan.plan2"
                | "candidate.bankDetails.studentLoan.plan4"
                | "candidate.bankDetails.studentLoan.postGradLoan"
                | "candidate.timesheets.id"
                | "candidate.timesheets.candidateName"
                | "candidate.timesheets.candidate"
                | "candidate.timesheets.clientName"
                | "candidate.timesheets.clientEmail"
                | "candidate.timesheets.client"
                | "candidate.timesheets.booking"
                | "candidate.timesheets.days.start"
                | "candidate.timesheets.days.end"
                | "candidate.timesheets.days.length"
                | "candidate.timesheets.days.amPm"
                | "candidate.timesheets.days.status"
                | "candidate.timesheets.days.chargeRate"
                | "candidate.timesheets.days.payRate"
                | "candidate.timesheets.days.flaggedForRemoval"
                | "candidate.timesheets.status"
                | "candidate.timesheets.statusChangedOn"
                | "candidate.timesheets.statusChangeBy"
                | "candidate.timesheets.statusChangeByJobRole"
                | "candidate.timesheets.statusChangeBySignature"
                | "candidate.timesheets.statusChangeByDrawnSignature"
                | "candidate.timesheets.statusChangeBySignatureDate"
                | "candidate.timesheets.rejectionReason"
                | "candidate.timesheets.statusCandidate"
                | "candidate.timesheets.statusChangedOnCandidate"
                | "candidate.timesheets.statusChangeByCandidate"
                | "candidate.timesheets.statusChangeByJobRoleCandidate"
                | "candidate.timesheets.statusChangeByCandidateSignature"
                | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                | "candidate.timesheets.rejectionReasonCandidate"
                | "candidate.timesheets.createdBy"
                | "candidate.timesheets.group"
                | "candidate.timesheets.resendTimesheetEmail"
                | "candidate.timesheets.customer"
                | "candidate.timesheets.assignee"
                | "candidate.timesheets.queryResponse"
                | "candidate.timesheets.invoiceNumber"
                | "candidate.timesheets.timesheetNumber"
                | "candidate.timesheets.type"
                | "candidate.timesheets.subType"
                | "candidate.timesheets.tuitionReport"
                | "candidate.timesheets.tuitionReportAnswers.questionId"
                | "candidate.timesheets.tuitionReportAnswers.value"
                | "candidate.timesheets.tuitionReportPending"
                | "candidate.timesheets.role"
                | "candidate.timesheets.statusApprovalIp"
                | "candidate.timesheets.statusApprovalCandidateIp";
              /**
               * @maxLength 100
               */
              prefix?: string;
              /**
               * @maxLength 100
               */
              suffix?: string;
              /**
               * @maxItems 20
               */
              cases?: {
                match?: string | number | boolean;
                /**
                 * @maxLength 1000
                 */
                value?: string;
              }[];
              /**
               * @maxLength 1000
               */
              fallback?: string;
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "static";
              /**
               * @maxLength 1000
               */
              value?: string;
            }
          | {
              kind?: "calculated";
              value?:
                | "rowNumber"
                | "invoiceFriday"
                | "financialYear"
                | "financialPeriod"
                | "bookingWeekMonday"
                | "dayWorked"
                | "dailyNetCharge"
                | "dailyVat"
                | "dailyPay"
                | "bookingNetTotal"
                | "bookingVatTotal"
                | "bookingGrossTotal";
              sign?: "positive" | "negative";
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "concat";
              /**
               * @minItems 1
               * @maxItems 20
               */
              parts?: (
                | {
                    kind?: "field";
                    value?:
                      | "candidate.candidateId"
                      | "candidate.candidateName"
                      | "candidate.timesheetId"
                      | "candidate.timesheetInvoiceNumber"
                      | "candidate.timesheetDate"
                      | "candidate.timesheetNumber"
                      | "candidate.timesheetStart"
                      | "candidate.timesheetEnd"
                      | "candidate.timesheetLastDay"
                      | "candidate.timesheetApprover"
                      | "candidate.timesheetLastSunday"
                      | "candidate.timesheetStudentName"
                      | "candidate.customClientId"
                      | "candidate.customClientInternalId"
                      | "candidate.clientName"
                      | "candidate.clientId"
                      | "candidate.customCandidateId"
                      | "candidate.customCandidateInternalId"
                      | "candidate.customCandidateIdPayType"
                      | "candidate.clientContact"
                      | "candidate.invoiceContact"
                      | "candidate.total"
                      | "candidate.peDate"
                      | "candidate.bookingPayType"
                      | "candidate.payRate"
                      | "candidate.chargeRate"
                      | "candidate.units"
                      | "candidate.hours"
                      | "candidate.hoursPerDay"
                      | "candidate.consultant"
                      | "candidate.role"
                      | "candidate.clientTimesheetStatus"
                      | "candidate.isCandidateFirstBooking"
                      | "candidate.isCandidateFirstBookingWithClient"
                      | "candidate.isClientFirstBooking"
                      | "candidate.consultant1Id"
                      | "candidate.consultant1Perc"
                      | "candidate.consultant2Id"
                      | "candidate.consultant2Perc"
                      | "candidate.consultant3Id"
                      | "candidate.consultant3Perc"
                      | "candidate.consultant4Id"
                      | "candidate.consultant4Perc"
                      | "candidate.consultant5Id"
                      | "candidate.consultant5Perc"
                      | "candidate.placementId"
                      | "candidate.officeNumber"
                      | "candidate.poNumber"
                      | "candidate.payInterval"
                      | "candidate._internalId"
                      | "candidate.candidatePayType"
                      | "candidate.umbrellaName"
                      | "candidate.umbrellaCode"
                      | "candidate.umbrellaId"
                      | "candidate.bookingStartDate"
                      | "candidate.assigneeBranchCodes"
                      | "candidate.niNumberTrimmed"
                      | "candidate.sortCodeTrimmed"
                      | "candidate.accountNumberTrimmed"
                      | "candidate.accountNameTrimmed"
                      | "candidate.bookingOnCosts"
                      | "candidate.payRateExclusiveHoliday"
                      | "candidate.payRateHolidayPay"
                      | "candidate.payeRate"
                      | "candidate.payeMargin"
                      | "candidate.payeWeeklyMargin"
                      | "candidate.grossMargin"
                      | "candidate.totalCharge"
                      | "candidate.totalExcludingHolidayPay"
                      | "candidate.totalHolidayPay"
                      | "candidate.candidateDetails.title"
                      | "candidate.candidateDetails.forename"
                      | "candidate.candidateDetails.surname"
                      | "candidate.candidateDetails.sex"
                      | "candidate.candidateDetails.dob"
                      | "candidate.candidateDetails.email"
                      | "candidate.candidateDetails.telephone"
                      | "candidate.candidateDetails.mobile"
                      | "candidate.candidateDetails.address.addressLine1"
                      | "candidate.candidateDetails.address.postcode"
                      | "candidate.candidateDetails.gender"
                      | "candidate.candidateDetails.bankDetails.niNumber"
                      | "candidate.days.start"
                      | "candidate.days.end"
                      | "candidate.days.length"
                      | "candidate.days.amPm"
                      | "candidate.days.status"
                      | "candidate.days.chargeRate"
                      | "candidate.days.payRate"
                      | "candidate.days.flaggedForRemoval"
                      | "day.start"
                      | "day.end"
                      | "day.length"
                      | "day.amPm"
                      | "day.status"
                      | "day.chargeRate"
                      | "day.payRate"
                      | "day.flaggedForRemoval"
                      | "candidate.umbrellaAddress.addressLine1"
                      | "candidate.umbrellaAddress.addressLine2"
                      | "candidate.umbrellaAddress.addressLine3"
                      | "candidate.umbrellaAddress.city"
                      | "candidate.umbrellaAddress.county"
                      | "candidate.umbrellaAddress.borough"
                      | "candidate.umbrellaAddress.postcode"
                      | "candidate.umbrellaAddress.country"
                      | "candidate.umbrellaAddress.lat"
                      | "candidate.umbrellaAddress.lng"
                      | "candidate.bankDetails.bankAccountName"
                      | "candidate.bankDetails.bankName"
                      | "candidate.bankDetails.accountNumber"
                      | "candidate.bankDetails.sortCode"
                      | "candidate.bankDetails.niNumber"
                      | "candidate.bankDetails.jobOp1"
                      | "candidate.bankDetails.jobOp2"
                      | "candidate.bankDetails.jobOp3"
                      | "candidate.bankDetails.studentLoan.studentLoan"
                      | "candidate.bankDetails.studentLoan.plan1"
                      | "candidate.bankDetails.studentLoan.plan2"
                      | "candidate.bankDetails.studentLoan.plan4"
                      | "candidate.bankDetails.studentLoan.postGradLoan"
                      | "candidate.timesheets.id"
                      | "candidate.timesheets.candidateName"
                      | "candidate.timesheets.candidate"
                      | "candidate.timesheets.clientName"
                      | "candidate.timesheets.clientEmail"
                      | "candidate.timesheets.client"
                      | "candidate.timesheets.booking"
                      | "candidate.timesheets.days.start"
                      | "candidate.timesheets.days.end"
                      | "candidate.timesheets.days.length"
                      | "candidate.timesheets.days.amPm"
                      | "candidate.timesheets.days.status"
                      | "candidate.timesheets.days.chargeRate"
                      | "candidate.timesheets.days.payRate"
                      | "candidate.timesheets.days.flaggedForRemoval"
                      | "candidate.timesheets.status"
                      | "candidate.timesheets.statusChangedOn"
                      | "candidate.timesheets.statusChangeBy"
                      | "candidate.timesheets.statusChangeByJobRole"
                      | "candidate.timesheets.statusChangeBySignature"
                      | "candidate.timesheets.statusChangeByDrawnSignature"
                      | "candidate.timesheets.statusChangeBySignatureDate"
                      | "candidate.timesheets.rejectionReason"
                      | "candidate.timesheets.statusCandidate"
                      | "candidate.timesheets.statusChangedOnCandidate"
                      | "candidate.timesheets.statusChangeByCandidate"
                      | "candidate.timesheets.statusChangeByJobRoleCandidate"
                      | "candidate.timesheets.statusChangeByCandidateSignature"
                      | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                      | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                      | "candidate.timesheets.rejectionReasonCandidate"
                      | "candidate.timesheets.createdBy"
                      | "candidate.timesheets.group"
                      | "candidate.timesheets.resendTimesheetEmail"
                      | "candidate.timesheets.customer"
                      | "candidate.timesheets.assignee"
                      | "candidate.timesheets.queryResponse"
                      | "candidate.timesheets.invoiceNumber"
                      | "candidate.timesheets.timesheetNumber"
                      | "candidate.timesheets.type"
                      | "candidate.timesheets.subType"
                      | "candidate.timesheets.tuitionReport"
                      | "candidate.timesheets.tuitionReportAnswers.questionId"
                      | "candidate.timesheets.tuitionReportAnswers.value"
                      | "candidate.timesheets.tuitionReportPending"
                      | "candidate.timesheets.role"
                      | "candidate.timesheets.statusApprovalIp"
                      | "candidate.timesheets.statusApprovalCandidateIp";
                    /**
                     * @maxLength 100
                     */
                    prefix?: string;
                    /**
                     * @maxLength 100
                     */
                    suffix?: string;
                    /**
                     * @maxItems 20
                     */
                    cases?: {
                      match?: string | number | boolean;
                      /**
                       * @maxLength 1000
                       */
                      value?: string;
                    }[];
                    /**
                     * @maxLength 1000
                     */
                    fallback?: string;
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
                | {
                    kind?: "static";
                    /**
                     * @maxLength 1000
                     */
                    value?: string;
                  }
                | {
                    kind?: "calculated";
                    value?:
                      | "rowNumber"
                      | "invoiceFriday"
                      | "financialYear"
                      | "financialPeriod"
                      | "bookingWeekMonday"
                      | "dayWorked"
                      | "dailyNetCharge"
                      | "dailyVat"
                      | "dailyPay"
                      | "bookingNetTotal"
                      | "bookingVatTotal"
                      | "bookingGrossTotal";
                    sign?: "positive" | "negative";
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
              )[];
              /**
               * @maxLength 100
               */
              separator?: string;
            };
        summary?:
          | {
              kind?: "field";
              value?:
                | "candidate.candidateId"
                | "candidate.candidateName"
                | "candidate.timesheetId"
                | "candidate.timesheetInvoiceNumber"
                | "candidate.timesheetDate"
                | "candidate.timesheetNumber"
                | "candidate.timesheetStart"
                | "candidate.timesheetEnd"
                | "candidate.timesheetLastDay"
                | "candidate.timesheetApprover"
                | "candidate.timesheetLastSunday"
                | "candidate.timesheetStudentName"
                | "candidate.customClientId"
                | "candidate.customClientInternalId"
                | "candidate.clientName"
                | "candidate.clientId"
                | "candidate.customCandidateId"
                | "candidate.customCandidateInternalId"
                | "candidate.customCandidateIdPayType"
                | "candidate.clientContact"
                | "candidate.invoiceContact"
                | "candidate.total"
                | "candidate.peDate"
                | "candidate.bookingPayType"
                | "candidate.payRate"
                | "candidate.chargeRate"
                | "candidate.units"
                | "candidate.hours"
                | "candidate.hoursPerDay"
                | "candidate.consultant"
                | "candidate.role"
                | "candidate.clientTimesheetStatus"
                | "candidate.isCandidateFirstBooking"
                | "candidate.isCandidateFirstBookingWithClient"
                | "candidate.isClientFirstBooking"
                | "candidate.consultant1Id"
                | "candidate.consultant1Perc"
                | "candidate.consultant2Id"
                | "candidate.consultant2Perc"
                | "candidate.consultant3Id"
                | "candidate.consultant3Perc"
                | "candidate.consultant4Id"
                | "candidate.consultant4Perc"
                | "candidate.consultant5Id"
                | "candidate.consultant5Perc"
                | "candidate.placementId"
                | "candidate.officeNumber"
                | "candidate.poNumber"
                | "candidate.payInterval"
                | "candidate._internalId"
                | "candidate.candidatePayType"
                | "candidate.umbrellaName"
                | "candidate.umbrellaCode"
                | "candidate.umbrellaId"
                | "candidate.bookingStartDate"
                | "candidate.assigneeBranchCodes"
                | "candidate.niNumberTrimmed"
                | "candidate.sortCodeTrimmed"
                | "candidate.accountNumberTrimmed"
                | "candidate.accountNameTrimmed"
                | "candidate.bookingOnCosts"
                | "candidate.payRateExclusiveHoliday"
                | "candidate.payRateHolidayPay"
                | "candidate.payeRate"
                | "candidate.payeMargin"
                | "candidate.payeWeeklyMargin"
                | "candidate.grossMargin"
                | "candidate.totalCharge"
                | "candidate.totalExcludingHolidayPay"
                | "candidate.totalHolidayPay"
                | "candidate.candidateDetails.title"
                | "candidate.candidateDetails.forename"
                | "candidate.candidateDetails.surname"
                | "candidate.candidateDetails.sex"
                | "candidate.candidateDetails.dob"
                | "candidate.candidateDetails.email"
                | "candidate.candidateDetails.telephone"
                | "candidate.candidateDetails.mobile"
                | "candidate.candidateDetails.address.addressLine1"
                | "candidate.candidateDetails.address.postcode"
                | "candidate.candidateDetails.gender"
                | "candidate.candidateDetails.bankDetails.niNumber"
                | "candidate.days.start"
                | "candidate.days.end"
                | "candidate.days.length"
                | "candidate.days.amPm"
                | "candidate.days.status"
                | "candidate.days.chargeRate"
                | "candidate.days.payRate"
                | "candidate.days.flaggedForRemoval"
                | "day.start"
                | "day.end"
                | "day.length"
                | "day.amPm"
                | "day.status"
                | "day.chargeRate"
                | "day.payRate"
                | "day.flaggedForRemoval"
                | "candidate.umbrellaAddress.addressLine1"
                | "candidate.umbrellaAddress.addressLine2"
                | "candidate.umbrellaAddress.addressLine3"
                | "candidate.umbrellaAddress.city"
                | "candidate.umbrellaAddress.county"
                | "candidate.umbrellaAddress.borough"
                | "candidate.umbrellaAddress.postcode"
                | "candidate.umbrellaAddress.country"
                | "candidate.umbrellaAddress.lat"
                | "candidate.umbrellaAddress.lng"
                | "candidate.bankDetails.bankAccountName"
                | "candidate.bankDetails.bankName"
                | "candidate.bankDetails.accountNumber"
                | "candidate.bankDetails.sortCode"
                | "candidate.bankDetails.niNumber"
                | "candidate.bankDetails.jobOp1"
                | "candidate.bankDetails.jobOp2"
                | "candidate.bankDetails.jobOp3"
                | "candidate.bankDetails.studentLoan.studentLoan"
                | "candidate.bankDetails.studentLoan.plan1"
                | "candidate.bankDetails.studentLoan.plan2"
                | "candidate.bankDetails.studentLoan.plan4"
                | "candidate.bankDetails.studentLoan.postGradLoan"
                | "candidate.timesheets.id"
                | "candidate.timesheets.candidateName"
                | "candidate.timesheets.candidate"
                | "candidate.timesheets.clientName"
                | "candidate.timesheets.clientEmail"
                | "candidate.timesheets.client"
                | "candidate.timesheets.booking"
                | "candidate.timesheets.days.start"
                | "candidate.timesheets.days.end"
                | "candidate.timesheets.days.length"
                | "candidate.timesheets.days.amPm"
                | "candidate.timesheets.days.status"
                | "candidate.timesheets.days.chargeRate"
                | "candidate.timesheets.days.payRate"
                | "candidate.timesheets.days.flaggedForRemoval"
                | "candidate.timesheets.status"
                | "candidate.timesheets.statusChangedOn"
                | "candidate.timesheets.statusChangeBy"
                | "candidate.timesheets.statusChangeByJobRole"
                | "candidate.timesheets.statusChangeBySignature"
                | "candidate.timesheets.statusChangeByDrawnSignature"
                | "candidate.timesheets.statusChangeBySignatureDate"
                | "candidate.timesheets.rejectionReason"
                | "candidate.timesheets.statusCandidate"
                | "candidate.timesheets.statusChangedOnCandidate"
                | "candidate.timesheets.statusChangeByCandidate"
                | "candidate.timesheets.statusChangeByJobRoleCandidate"
                | "candidate.timesheets.statusChangeByCandidateSignature"
                | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                | "candidate.timesheets.rejectionReasonCandidate"
                | "candidate.timesheets.createdBy"
                | "candidate.timesheets.group"
                | "candidate.timesheets.resendTimesheetEmail"
                | "candidate.timesheets.customer"
                | "candidate.timesheets.assignee"
                | "candidate.timesheets.queryResponse"
                | "candidate.timesheets.invoiceNumber"
                | "candidate.timesheets.timesheetNumber"
                | "candidate.timesheets.type"
                | "candidate.timesheets.subType"
                | "candidate.timesheets.tuitionReport"
                | "candidate.timesheets.tuitionReportAnswers.questionId"
                | "candidate.timesheets.tuitionReportAnswers.value"
                | "candidate.timesheets.tuitionReportPending"
                | "candidate.timesheets.role"
                | "candidate.timesheets.statusApprovalIp"
                | "candidate.timesheets.statusApprovalCandidateIp";
              /**
               * @maxLength 100
               */
              prefix?: string;
              /**
               * @maxLength 100
               */
              suffix?: string;
              /**
               * @maxItems 20
               */
              cases?: {
                match?: string | number | boolean;
                /**
                 * @maxLength 1000
                 */
                value?: string;
              }[];
              /**
               * @maxLength 1000
               */
              fallback?: string;
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "static";
              /**
               * @maxLength 1000
               */
              value?: string;
            }
          | {
              kind?: "calculated";
              value?:
                | "rowNumber"
                | "invoiceFriday"
                | "financialYear"
                | "financialPeriod"
                | "bookingWeekMonday"
                | "dayWorked"
                | "dailyNetCharge"
                | "dailyVat"
                | "dailyPay"
                | "bookingNetTotal"
                | "bookingVatTotal"
                | "bookingGrossTotal";
              sign?: "positive" | "negative";
              date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
            }
          | {
              kind?: "concat";
              /**
               * @minItems 1
               * @maxItems 20
               */
              parts?: (
                | {
                    kind?: "field";
                    value?:
                      | "candidate.candidateId"
                      | "candidate.candidateName"
                      | "candidate.timesheetId"
                      | "candidate.timesheetInvoiceNumber"
                      | "candidate.timesheetDate"
                      | "candidate.timesheetNumber"
                      | "candidate.timesheetStart"
                      | "candidate.timesheetEnd"
                      | "candidate.timesheetLastDay"
                      | "candidate.timesheetApprover"
                      | "candidate.timesheetLastSunday"
                      | "candidate.timesheetStudentName"
                      | "candidate.customClientId"
                      | "candidate.customClientInternalId"
                      | "candidate.clientName"
                      | "candidate.clientId"
                      | "candidate.customCandidateId"
                      | "candidate.customCandidateInternalId"
                      | "candidate.customCandidateIdPayType"
                      | "candidate.clientContact"
                      | "candidate.invoiceContact"
                      | "candidate.total"
                      | "candidate.peDate"
                      | "candidate.bookingPayType"
                      | "candidate.payRate"
                      | "candidate.chargeRate"
                      | "candidate.units"
                      | "candidate.hours"
                      | "candidate.hoursPerDay"
                      | "candidate.consultant"
                      | "candidate.role"
                      | "candidate.clientTimesheetStatus"
                      | "candidate.isCandidateFirstBooking"
                      | "candidate.isCandidateFirstBookingWithClient"
                      | "candidate.isClientFirstBooking"
                      | "candidate.consultant1Id"
                      | "candidate.consultant1Perc"
                      | "candidate.consultant2Id"
                      | "candidate.consultant2Perc"
                      | "candidate.consultant3Id"
                      | "candidate.consultant3Perc"
                      | "candidate.consultant4Id"
                      | "candidate.consultant4Perc"
                      | "candidate.consultant5Id"
                      | "candidate.consultant5Perc"
                      | "candidate.placementId"
                      | "candidate.officeNumber"
                      | "candidate.poNumber"
                      | "candidate.payInterval"
                      | "candidate._internalId"
                      | "candidate.candidatePayType"
                      | "candidate.umbrellaName"
                      | "candidate.umbrellaCode"
                      | "candidate.umbrellaId"
                      | "candidate.bookingStartDate"
                      | "candidate.assigneeBranchCodes"
                      | "candidate.niNumberTrimmed"
                      | "candidate.sortCodeTrimmed"
                      | "candidate.accountNumberTrimmed"
                      | "candidate.accountNameTrimmed"
                      | "candidate.bookingOnCosts"
                      | "candidate.payRateExclusiveHoliday"
                      | "candidate.payRateHolidayPay"
                      | "candidate.payeRate"
                      | "candidate.payeMargin"
                      | "candidate.payeWeeklyMargin"
                      | "candidate.grossMargin"
                      | "candidate.totalCharge"
                      | "candidate.totalExcludingHolidayPay"
                      | "candidate.totalHolidayPay"
                      | "candidate.candidateDetails.title"
                      | "candidate.candidateDetails.forename"
                      | "candidate.candidateDetails.surname"
                      | "candidate.candidateDetails.sex"
                      | "candidate.candidateDetails.dob"
                      | "candidate.candidateDetails.email"
                      | "candidate.candidateDetails.telephone"
                      | "candidate.candidateDetails.mobile"
                      | "candidate.candidateDetails.address.addressLine1"
                      | "candidate.candidateDetails.address.postcode"
                      | "candidate.candidateDetails.gender"
                      | "candidate.candidateDetails.bankDetails.niNumber"
                      | "candidate.days.start"
                      | "candidate.days.end"
                      | "candidate.days.length"
                      | "candidate.days.amPm"
                      | "candidate.days.status"
                      | "candidate.days.chargeRate"
                      | "candidate.days.payRate"
                      | "candidate.days.flaggedForRemoval"
                      | "day.start"
                      | "day.end"
                      | "day.length"
                      | "day.amPm"
                      | "day.status"
                      | "day.chargeRate"
                      | "day.payRate"
                      | "day.flaggedForRemoval"
                      | "candidate.umbrellaAddress.addressLine1"
                      | "candidate.umbrellaAddress.addressLine2"
                      | "candidate.umbrellaAddress.addressLine3"
                      | "candidate.umbrellaAddress.city"
                      | "candidate.umbrellaAddress.county"
                      | "candidate.umbrellaAddress.borough"
                      | "candidate.umbrellaAddress.postcode"
                      | "candidate.umbrellaAddress.country"
                      | "candidate.umbrellaAddress.lat"
                      | "candidate.umbrellaAddress.lng"
                      | "candidate.bankDetails.bankAccountName"
                      | "candidate.bankDetails.bankName"
                      | "candidate.bankDetails.accountNumber"
                      | "candidate.bankDetails.sortCode"
                      | "candidate.bankDetails.niNumber"
                      | "candidate.bankDetails.jobOp1"
                      | "candidate.bankDetails.jobOp2"
                      | "candidate.bankDetails.jobOp3"
                      | "candidate.bankDetails.studentLoan.studentLoan"
                      | "candidate.bankDetails.studentLoan.plan1"
                      | "candidate.bankDetails.studentLoan.plan2"
                      | "candidate.bankDetails.studentLoan.plan4"
                      | "candidate.bankDetails.studentLoan.postGradLoan"
                      | "candidate.timesheets.id"
                      | "candidate.timesheets.candidateName"
                      | "candidate.timesheets.candidate"
                      | "candidate.timesheets.clientName"
                      | "candidate.timesheets.clientEmail"
                      | "candidate.timesheets.client"
                      | "candidate.timesheets.booking"
                      | "candidate.timesheets.days.start"
                      | "candidate.timesheets.days.end"
                      | "candidate.timesheets.days.length"
                      | "candidate.timesheets.days.amPm"
                      | "candidate.timesheets.days.status"
                      | "candidate.timesheets.days.chargeRate"
                      | "candidate.timesheets.days.payRate"
                      | "candidate.timesheets.days.flaggedForRemoval"
                      | "candidate.timesheets.status"
                      | "candidate.timesheets.statusChangedOn"
                      | "candidate.timesheets.statusChangeBy"
                      | "candidate.timesheets.statusChangeByJobRole"
                      | "candidate.timesheets.statusChangeBySignature"
                      | "candidate.timesheets.statusChangeByDrawnSignature"
                      | "candidate.timesheets.statusChangeBySignatureDate"
                      | "candidate.timesheets.rejectionReason"
                      | "candidate.timesheets.statusCandidate"
                      | "candidate.timesheets.statusChangedOnCandidate"
                      | "candidate.timesheets.statusChangeByCandidate"
                      | "candidate.timesheets.statusChangeByJobRoleCandidate"
                      | "candidate.timesheets.statusChangeByCandidateSignature"
                      | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                      | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                      | "candidate.timesheets.rejectionReasonCandidate"
                      | "candidate.timesheets.createdBy"
                      | "candidate.timesheets.group"
                      | "candidate.timesheets.resendTimesheetEmail"
                      | "candidate.timesheets.customer"
                      | "candidate.timesheets.assignee"
                      | "candidate.timesheets.queryResponse"
                      | "candidate.timesheets.invoiceNumber"
                      | "candidate.timesheets.timesheetNumber"
                      | "candidate.timesheets.type"
                      | "candidate.timesheets.subType"
                      | "candidate.timesheets.tuitionReport"
                      | "candidate.timesheets.tuitionReportAnswers.questionId"
                      | "candidate.timesheets.tuitionReportAnswers.value"
                      | "candidate.timesheets.tuitionReportPending"
                      | "candidate.timesheets.role"
                      | "candidate.timesheets.statusApprovalIp"
                      | "candidate.timesheets.statusApprovalCandidateIp";
                    /**
                     * @maxLength 100
                     */
                    prefix?: string;
                    /**
                     * @maxLength 100
                     */
                    suffix?: string;
                    /**
                     * @maxItems 20
                     */
                    cases?: {
                      match?: string | number | boolean;
                      /**
                       * @maxLength 1000
                       */
                      value?: string;
                    }[];
                    /**
                     * @maxLength 1000
                     */
                    fallback?: string;
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
                | {
                    kind?: "static";
                    /**
                     * @maxLength 1000
                     */
                    value?: string;
                  }
                | {
                    kind?: "calculated";
                    value?:
                      | "rowNumber"
                      | "invoiceFriday"
                      | "financialYear"
                      | "financialPeriod"
                      | "bookingWeekMonday"
                      | "dayWorked"
                      | "dailyNetCharge"
                      | "dailyVat"
                      | "dailyPay"
                      | "bookingNetTotal"
                      | "bookingVatTotal"
                      | "bookingGrossTotal";
                    sign?: "positive" | "negative";
                    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                  }
              )[];
              /**
               * @maxLength 100
               */
              separator?: string;
            };
      };
    }[];
    /**
     * Boolean flag of whether to show header names in the exported file
     */
    showHeaders?: boolean;
    /**
     * Flat booking rows or parent, day and summary rows
     */
    layout?: "flat" | "structured";
    /**
     * Include a parent row before the worked days of each payment-run item
     */
    includeParentRow?: boolean;
    /**
     * Include a summary row after the worked days of each payment-run item
     */
    includeSummaryRow?: boolean;
    /**
     * @minimum 1
     * @maximum 12
     */
    fiscalYearStartMonth?: number | null;
    /**
     * @minimum 0
     * @maximum 100
     */
    vatPercent?: number | null;
  };
};

export type UpdateExportConfigRequestBody = {
  /**
   * Contains the configuration items themselves
   */
  config: {
    /**
     * The name of the datapoint in the exported file
     *
     * @maxLength 1000
     */
    header: string;
    /**
     * Database data from which to derive
     */
    value?:
      | (
          | "candidate.candidateId"
          | "candidate.candidateName"
          | "candidate.timesheetId"
          | "candidate.timesheetInvoiceNumber"
          | "candidate.timesheetDate"
          | "candidate.timesheetNumber"
          | "candidate.timesheetStart"
          | "candidate.timesheetEnd"
          | "candidate.timesheetLastDay"
          | "candidate.timesheetApprover"
          | "candidate.timesheetLastSunday"
          | "candidate.timesheetStudentName"
          | "candidate.customClientId"
          | "candidate.customClientInternalId"
          | "candidate.clientName"
          | "candidate.clientId"
          | "candidate.customCandidateId"
          | "candidate.customCandidateInternalId"
          | "candidate.customCandidateIdPayType"
          | "candidate.clientContact"
          | "candidate.invoiceContact"
          | "candidate.total"
          | "candidate.peDate"
          | "candidate.bookingPayType"
          | "candidate.payRate"
          | "candidate.chargeRate"
          | "candidate.units"
          | "candidate.hours"
          | "candidate.hoursPerDay"
          | "candidate.consultant"
          | "candidate.role"
          | "candidate.clientTimesheetStatus"
          | "candidate.isCandidateFirstBooking"
          | "candidate.isCandidateFirstBookingWithClient"
          | "candidate.isClientFirstBooking"
          | "candidate.consultant1Id"
          | "candidate.consultant1Perc"
          | "candidate.consultant2Id"
          | "candidate.consultant2Perc"
          | "candidate.consultant3Id"
          | "candidate.consultant3Perc"
          | "candidate.consultant4Id"
          | "candidate.consultant4Perc"
          | "candidate.consultant5Id"
          | "candidate.consultant5Perc"
          | "candidate.placementId"
          | "candidate.officeNumber"
          | "candidate.poNumber"
          | "candidate.payInterval"
          | "candidate._internalId"
          | "candidate.candidatePayType"
          | "candidate.umbrellaName"
          | "candidate.umbrellaCode"
          | "candidate.umbrellaId"
          | "candidate.bookingStartDate"
          | "candidate.assigneeBranchCodes"
          | "candidate.niNumberTrimmed"
          | "candidate.sortCodeTrimmed"
          | "candidate.accountNumberTrimmed"
          | "candidate.accountNameTrimmed"
          | "candidate.bookingOnCosts"
          | "candidate.payRateExclusiveHoliday"
          | "candidate.payRateHolidayPay"
          | "candidate.payeRate"
          | "candidate.payeMargin"
          | "candidate.payeWeeklyMargin"
          | "candidate.grossMargin"
          | "candidate.totalCharge"
          | "candidate.totalExcludingHolidayPay"
          | "candidate.totalHolidayPay"
          | "candidate.candidateDetails.title"
          | "candidate.candidateDetails.forename"
          | "candidate.candidateDetails.surname"
          | "candidate.candidateDetails.sex"
          | "candidate.candidateDetails.dob"
          | "candidate.candidateDetails.email"
          | "candidate.candidateDetails.telephone"
          | "candidate.candidateDetails.mobile"
          | "candidate.candidateDetails.address.addressLine1"
          | "candidate.candidateDetails.address.postcode"
          | "candidate.candidateDetails.gender"
          | "candidate.candidateDetails.bankDetails.niNumber"
          | "candidate.days.start"
          | "candidate.days.end"
          | "candidate.days.length"
          | "candidate.days.amPm"
          | "candidate.days.status"
          | "candidate.days.chargeRate"
          | "candidate.days.payRate"
          | "candidate.days.flaggedForRemoval"
          | "day.start"
          | "day.end"
          | "day.length"
          | "day.amPm"
          | "day.status"
          | "day.chargeRate"
          | "day.payRate"
          | "day.flaggedForRemoval"
          | "candidate.umbrellaAddress.addressLine1"
          | "candidate.umbrellaAddress.addressLine2"
          | "candidate.umbrellaAddress.addressLine3"
          | "candidate.umbrellaAddress.city"
          | "candidate.umbrellaAddress.county"
          | "candidate.umbrellaAddress.borough"
          | "candidate.umbrellaAddress.postcode"
          | "candidate.umbrellaAddress.country"
          | "candidate.umbrellaAddress.lat"
          | "candidate.umbrellaAddress.lng"
          | "candidate.bankDetails.bankAccountName"
          | "candidate.bankDetails.bankName"
          | "candidate.bankDetails.accountNumber"
          | "candidate.bankDetails.sortCode"
          | "candidate.bankDetails.niNumber"
          | "candidate.bankDetails.jobOp1"
          | "candidate.bankDetails.jobOp2"
          | "candidate.bankDetails.jobOp3"
          | "candidate.bankDetails.studentLoan.studentLoan"
          | "candidate.bankDetails.studentLoan.plan1"
          | "candidate.bankDetails.studentLoan.plan2"
          | "candidate.bankDetails.studentLoan.plan4"
          | "candidate.bankDetails.studentLoan.postGradLoan"
          | "candidate.timesheets.id"
          | "candidate.timesheets.candidateName"
          | "candidate.timesheets.candidate"
          | "candidate.timesheets.clientName"
          | "candidate.timesheets.clientEmail"
          | "candidate.timesheets.client"
          | "candidate.timesheets.booking"
          | "candidate.timesheets.days.start"
          | "candidate.timesheets.days.end"
          | "candidate.timesheets.days.length"
          | "candidate.timesheets.days.amPm"
          | "candidate.timesheets.days.status"
          | "candidate.timesheets.days.chargeRate"
          | "candidate.timesheets.days.payRate"
          | "candidate.timesheets.days.flaggedForRemoval"
          | "candidate.timesheets.status"
          | "candidate.timesheets.statusChangedOn"
          | "candidate.timesheets.statusChangeBy"
          | "candidate.timesheets.statusChangeByJobRole"
          | "candidate.timesheets.statusChangeBySignature"
          | "candidate.timesheets.statusChangeByDrawnSignature"
          | "candidate.timesheets.statusChangeBySignatureDate"
          | "candidate.timesheets.rejectionReason"
          | "candidate.timesheets.statusCandidate"
          | "candidate.timesheets.statusChangedOnCandidate"
          | "candidate.timesheets.statusChangeByCandidate"
          | "candidate.timesheets.statusChangeByJobRoleCandidate"
          | "candidate.timesheets.statusChangeByCandidateSignature"
          | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
          | "candidate.timesheets.statusChangeByCandidateSignatureDate"
          | "candidate.timesheets.rejectionReasonCandidate"
          | "candidate.timesheets.createdBy"
          | "candidate.timesheets.group"
          | "candidate.timesheets.resendTimesheetEmail"
          | "candidate.timesheets.customer"
          | "candidate.timesheets.assignee"
          | "candidate.timesheets.queryResponse"
          | "candidate.timesheets.invoiceNumber"
          | "candidate.timesheets.timesheetNumber"
          | "candidate.timesheets.type"
          | "candidate.timesheets.subType"
          | "candidate.timesheets.tuitionReport"
          | "candidate.timesheets.tuitionReportAnswers.questionId"
          | "candidate.timesheets.tuitionReportAnswers.value"
          | "candidate.timesheets.tuitionReportPending"
          | "candidate.timesheets.role"
          | "candidate.timesheets.statusApprovalIp"
          | "candidate.timesheets.statusApprovalCandidateIp"
        )
      | "";
    /**
     * Format to use for datetime fields
     */
    date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
    /**
     * Flush
     */
    flush?: boolean;
    /**
     * Value written to the export when the selected source field has no value.
     *
     * @maxLength 1000
     */
    defaultValue?: string;
    /**
     * Match
     */
    match?: {
      /**
       * @maxLength 1000
       */
      field: string;
      /**
       * @maxLength 1000
       */
      match: string;
      /**
       * @maxLength 1000
       */
      display: string;
      /**
       * @maxLength 1000
       */
      displayValue: string;
    };
    /**
     * Text replacement/processing to perform on this config item
     *
     * @maxItems 5
     */
    conditional?: {
      /**
       * Database data from which to derive
       */
      value:
        | "candidate.candidateId"
        | "candidate.candidateName"
        | "candidate.timesheetId"
        | "candidate.timesheetInvoiceNumber"
        | "candidate.timesheetDate"
        | "candidate.timesheetNumber"
        | "candidate.timesheetStart"
        | "candidate.timesheetEnd"
        | "candidate.timesheetLastDay"
        | "candidate.timesheetApprover"
        | "candidate.timesheetLastSunday"
        | "candidate.timesheetStudentName"
        | "candidate.customClientId"
        | "candidate.customClientInternalId"
        | "candidate.clientName"
        | "candidate.clientId"
        | "candidate.customCandidateId"
        | "candidate.customCandidateInternalId"
        | "candidate.customCandidateIdPayType"
        | "candidate.clientContact"
        | "candidate.invoiceContact"
        | "candidate.total"
        | "candidate.peDate"
        | "candidate.bookingPayType"
        | "candidate.payRate"
        | "candidate.chargeRate"
        | "candidate.units"
        | "candidate.hours"
        | "candidate.hoursPerDay"
        | "candidate.consultant"
        | "candidate.role"
        | "candidate.clientTimesheetStatus"
        | "candidate.isCandidateFirstBooking"
        | "candidate.isCandidateFirstBookingWithClient"
        | "candidate.isClientFirstBooking"
        | "candidate.consultant1Id"
        | "candidate.consultant1Perc"
        | "candidate.consultant2Id"
        | "candidate.consultant2Perc"
        | "candidate.consultant3Id"
        | "candidate.consultant3Perc"
        | "candidate.consultant4Id"
        | "candidate.consultant4Perc"
        | "candidate.consultant5Id"
        | "candidate.consultant5Perc"
        | "candidate.placementId"
        | "candidate.officeNumber"
        | "candidate.poNumber"
        | "candidate.payInterval"
        | "candidate._internalId"
        | "candidate.candidatePayType"
        | "candidate.umbrellaName"
        | "candidate.umbrellaCode"
        | "candidate.umbrellaId"
        | "candidate.bookingStartDate"
        | "candidate.assigneeBranchCodes"
        | "candidate.niNumberTrimmed"
        | "candidate.sortCodeTrimmed"
        | "candidate.accountNumberTrimmed"
        | "candidate.accountNameTrimmed"
        | "candidate.bookingOnCosts"
        | "candidate.payRateExclusiveHoliday"
        | "candidate.payRateHolidayPay"
        | "candidate.payeRate"
        | "candidate.payeMargin"
        | "candidate.payeWeeklyMargin"
        | "candidate.grossMargin"
        | "candidate.totalCharge"
        | "candidate.totalExcludingHolidayPay"
        | "candidate.totalHolidayPay"
        | "candidate.candidateDetails.title"
        | "candidate.candidateDetails.forename"
        | "candidate.candidateDetails.surname"
        | "candidate.candidateDetails.sex"
        | "candidate.candidateDetails.dob"
        | "candidate.candidateDetails.email"
        | "candidate.candidateDetails.telephone"
        | "candidate.candidateDetails.mobile"
        | "candidate.candidateDetails.address.addressLine1"
        | "candidate.candidateDetails.address.postcode"
        | "candidate.candidateDetails.gender"
        | "candidate.candidateDetails.bankDetails.niNumber"
        | "candidate.days.start"
        | "candidate.days.end"
        | "candidate.days.length"
        | "candidate.days.amPm"
        | "candidate.days.status"
        | "candidate.days.chargeRate"
        | "candidate.days.payRate"
        | "candidate.days.flaggedForRemoval"
        | "day.start"
        | "day.end"
        | "day.length"
        | "day.amPm"
        | "day.status"
        | "day.chargeRate"
        | "day.payRate"
        | "day.flaggedForRemoval"
        | "candidate.umbrellaAddress.addressLine1"
        | "candidate.umbrellaAddress.addressLine2"
        | "candidate.umbrellaAddress.addressLine3"
        | "candidate.umbrellaAddress.city"
        | "candidate.umbrellaAddress.county"
        | "candidate.umbrellaAddress.borough"
        | "candidate.umbrellaAddress.postcode"
        | "candidate.umbrellaAddress.country"
        | "candidate.umbrellaAddress.lat"
        | "candidate.umbrellaAddress.lng"
        | "candidate.bankDetails.bankAccountName"
        | "candidate.bankDetails.bankName"
        | "candidate.bankDetails.accountNumber"
        | "candidate.bankDetails.sortCode"
        | "candidate.bankDetails.niNumber"
        | "candidate.bankDetails.jobOp1"
        | "candidate.bankDetails.jobOp2"
        | "candidate.bankDetails.jobOp3"
        | "candidate.bankDetails.studentLoan.studentLoan"
        | "candidate.bankDetails.studentLoan.plan1"
        | "candidate.bankDetails.studentLoan.plan2"
        | "candidate.bankDetails.studentLoan.plan4"
        | "candidate.bankDetails.studentLoan.postGradLoan"
        | "candidate.timesheets.id"
        | "candidate.timesheets.candidateName"
        | "candidate.timesheets.candidate"
        | "candidate.timesheets.clientName"
        | "candidate.timesheets.clientEmail"
        | "candidate.timesheets.client"
        | "candidate.timesheets.booking"
        | "candidate.timesheets.days.start"
        | "candidate.timesheets.days.end"
        | "candidate.timesheets.days.length"
        | "candidate.timesheets.days.amPm"
        | "candidate.timesheets.days.status"
        | "candidate.timesheets.days.chargeRate"
        | "candidate.timesheets.days.payRate"
        | "candidate.timesheets.days.flaggedForRemoval"
        | "candidate.timesheets.status"
        | "candidate.timesheets.statusChangedOn"
        | "candidate.timesheets.statusChangeBy"
        | "candidate.timesheets.statusChangeByJobRole"
        | "candidate.timesheets.statusChangeBySignature"
        | "candidate.timesheets.statusChangeByDrawnSignature"
        | "candidate.timesheets.statusChangeBySignatureDate"
        | "candidate.timesheets.rejectionReason"
        | "candidate.timesheets.statusCandidate"
        | "candidate.timesheets.statusChangedOnCandidate"
        | "candidate.timesheets.statusChangeByCandidate"
        | "candidate.timesheets.statusChangeByJobRoleCandidate"
        | "candidate.timesheets.statusChangeByCandidateSignature"
        | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
        | "candidate.timesheets.statusChangeByCandidateSignatureDate"
        | "candidate.timesheets.rejectionReasonCandidate"
        | "candidate.timesheets.createdBy"
        | "candidate.timesheets.group"
        | "candidate.timesheets.resendTimesheetEmail"
        | "candidate.timesheets.customer"
        | "candidate.timesheets.assignee"
        | "candidate.timesheets.queryResponse"
        | "candidate.timesheets.invoiceNumber"
        | "candidate.timesheets.timesheetNumber"
        | "candidate.timesheets.type"
        | "candidate.timesheets.subType"
        | "candidate.timesheets.tuitionReport"
        | "candidate.timesheets.tuitionReportAnswers.questionId"
        | "candidate.timesheets.tuitionReportAnswers.value"
        | "candidate.timesheets.tuitionReportPending"
        | "candidate.timesheets.role"
        | "candidate.timesheets.statusApprovalIp"
        | "candidate.timesheets.statusApprovalCandidateIp";
      /**
       * The data instance that should be replaced
       *
       * @minItems 1
       */
      match: (string | number | boolean | (string | null) | (void | null))[];
      /**
       * Replacement if condition is met
       *
       * @maxLength 1000
       */
      ["true"]: string;
      /**
       * Replacement if condition is not met
       *
       * @maxLength 1000
       */
      ["false"]: string;
    }[];
    /**
     * Boolean
     */
    boolean?: boolean;
    /**
     * Way in which to format different data types
     */
    formatType?: "string" | "number" | "currency";
    /**
     * Configured value for this column in a flat booking row
     */
    source?:
      | {
          kind: "field";
          value:
            | "candidate.candidateId"
            | "candidate.candidateName"
            | "candidate.timesheetId"
            | "candidate.timesheetInvoiceNumber"
            | "candidate.timesheetDate"
            | "candidate.timesheetNumber"
            | "candidate.timesheetStart"
            | "candidate.timesheetEnd"
            | "candidate.timesheetLastDay"
            | "candidate.timesheetApprover"
            | "candidate.timesheetLastSunday"
            | "candidate.timesheetStudentName"
            | "candidate.customClientId"
            | "candidate.customClientInternalId"
            | "candidate.clientName"
            | "candidate.clientId"
            | "candidate.customCandidateId"
            | "candidate.customCandidateInternalId"
            | "candidate.customCandidateIdPayType"
            | "candidate.clientContact"
            | "candidate.invoiceContact"
            | "candidate.total"
            | "candidate.peDate"
            | "candidate.bookingPayType"
            | "candidate.payRate"
            | "candidate.chargeRate"
            | "candidate.units"
            | "candidate.hours"
            | "candidate.hoursPerDay"
            | "candidate.consultant"
            | "candidate.role"
            | "candidate.clientTimesheetStatus"
            | "candidate.isCandidateFirstBooking"
            | "candidate.isCandidateFirstBookingWithClient"
            | "candidate.isClientFirstBooking"
            | "candidate.consultant1Id"
            | "candidate.consultant1Perc"
            | "candidate.consultant2Id"
            | "candidate.consultant2Perc"
            | "candidate.consultant3Id"
            | "candidate.consultant3Perc"
            | "candidate.consultant4Id"
            | "candidate.consultant4Perc"
            | "candidate.consultant5Id"
            | "candidate.consultant5Perc"
            | "candidate.placementId"
            | "candidate.officeNumber"
            | "candidate.poNumber"
            | "candidate.payInterval"
            | "candidate._internalId"
            | "candidate.candidatePayType"
            | "candidate.umbrellaName"
            | "candidate.umbrellaCode"
            | "candidate.umbrellaId"
            | "candidate.bookingStartDate"
            | "candidate.assigneeBranchCodes"
            | "candidate.niNumberTrimmed"
            | "candidate.sortCodeTrimmed"
            | "candidate.accountNumberTrimmed"
            | "candidate.accountNameTrimmed"
            | "candidate.bookingOnCosts"
            | "candidate.payRateExclusiveHoliday"
            | "candidate.payRateHolidayPay"
            | "candidate.payeRate"
            | "candidate.payeMargin"
            | "candidate.payeWeeklyMargin"
            | "candidate.grossMargin"
            | "candidate.totalCharge"
            | "candidate.totalExcludingHolidayPay"
            | "candidate.totalHolidayPay"
            | "candidate.candidateDetails.title"
            | "candidate.candidateDetails.forename"
            | "candidate.candidateDetails.surname"
            | "candidate.candidateDetails.sex"
            | "candidate.candidateDetails.dob"
            | "candidate.candidateDetails.email"
            | "candidate.candidateDetails.telephone"
            | "candidate.candidateDetails.mobile"
            | "candidate.candidateDetails.address.addressLine1"
            | "candidate.candidateDetails.address.postcode"
            | "candidate.candidateDetails.gender"
            | "candidate.candidateDetails.bankDetails.niNumber"
            | "candidate.days.start"
            | "candidate.days.end"
            | "candidate.days.length"
            | "candidate.days.amPm"
            | "candidate.days.status"
            | "candidate.days.chargeRate"
            | "candidate.days.payRate"
            | "candidate.days.flaggedForRemoval"
            | "day.start"
            | "day.end"
            | "day.length"
            | "day.amPm"
            | "day.status"
            | "day.chargeRate"
            | "day.payRate"
            | "day.flaggedForRemoval"
            | "candidate.umbrellaAddress.addressLine1"
            | "candidate.umbrellaAddress.addressLine2"
            | "candidate.umbrellaAddress.addressLine3"
            | "candidate.umbrellaAddress.city"
            | "candidate.umbrellaAddress.county"
            | "candidate.umbrellaAddress.borough"
            | "candidate.umbrellaAddress.postcode"
            | "candidate.umbrellaAddress.country"
            | "candidate.umbrellaAddress.lat"
            | "candidate.umbrellaAddress.lng"
            | "candidate.bankDetails.bankAccountName"
            | "candidate.bankDetails.bankName"
            | "candidate.bankDetails.accountNumber"
            | "candidate.bankDetails.sortCode"
            | "candidate.bankDetails.niNumber"
            | "candidate.bankDetails.jobOp1"
            | "candidate.bankDetails.jobOp2"
            | "candidate.bankDetails.jobOp3"
            | "candidate.bankDetails.studentLoan.studentLoan"
            | "candidate.bankDetails.studentLoan.plan1"
            | "candidate.bankDetails.studentLoan.plan2"
            | "candidate.bankDetails.studentLoan.plan4"
            | "candidate.bankDetails.studentLoan.postGradLoan"
            | "candidate.timesheets.id"
            | "candidate.timesheets.candidateName"
            | "candidate.timesheets.candidate"
            | "candidate.timesheets.clientName"
            | "candidate.timesheets.clientEmail"
            | "candidate.timesheets.client"
            | "candidate.timesheets.booking"
            | "candidate.timesheets.days.start"
            | "candidate.timesheets.days.end"
            | "candidate.timesheets.days.length"
            | "candidate.timesheets.days.amPm"
            | "candidate.timesheets.days.status"
            | "candidate.timesheets.days.chargeRate"
            | "candidate.timesheets.days.payRate"
            | "candidate.timesheets.days.flaggedForRemoval"
            | "candidate.timesheets.status"
            | "candidate.timesheets.statusChangedOn"
            | "candidate.timesheets.statusChangeBy"
            | "candidate.timesheets.statusChangeByJobRole"
            | "candidate.timesheets.statusChangeBySignature"
            | "candidate.timesheets.statusChangeByDrawnSignature"
            | "candidate.timesheets.statusChangeBySignatureDate"
            | "candidate.timesheets.rejectionReason"
            | "candidate.timesheets.statusCandidate"
            | "candidate.timesheets.statusChangedOnCandidate"
            | "candidate.timesheets.statusChangeByCandidate"
            | "candidate.timesheets.statusChangeByJobRoleCandidate"
            | "candidate.timesheets.statusChangeByCandidateSignature"
            | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
            | "candidate.timesheets.statusChangeByCandidateSignatureDate"
            | "candidate.timesheets.rejectionReasonCandidate"
            | "candidate.timesheets.createdBy"
            | "candidate.timesheets.group"
            | "candidate.timesheets.resendTimesheetEmail"
            | "candidate.timesheets.customer"
            | "candidate.timesheets.assignee"
            | "candidate.timesheets.queryResponse"
            | "candidate.timesheets.invoiceNumber"
            | "candidate.timesheets.timesheetNumber"
            | "candidate.timesheets.type"
            | "candidate.timesheets.subType"
            | "candidate.timesheets.tuitionReport"
            | "candidate.timesheets.tuitionReportAnswers.questionId"
            | "candidate.timesheets.tuitionReportAnswers.value"
            | "candidate.timesheets.tuitionReportPending"
            | "candidate.timesheets.role"
            | "candidate.timesheets.statusApprovalIp"
            | "candidate.timesheets.statusApprovalCandidateIp";
          /**
           * @maxLength 100
           */
          prefix?: string;
          /**
           * @maxLength 100
           */
          suffix?: string;
          /**
           * @maxItems 20
           */
          cases?: {
            match: string | number | boolean;
            /**
             * @maxLength 1000
             */
            value: string;
          }[];
          /**
           * @maxLength 1000
           */
          fallback?: string;
          date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
        }
      | {
          kind: "static";
          /**
           * @maxLength 1000
           */
          value: string;
        }
      | {
          kind: "calculated";
          value:
            | "rowNumber"
            | "invoiceFriday"
            | "financialYear"
            | "financialPeriod"
            | "bookingWeekMonday"
            | "dayWorked"
            | "dailyNetCharge"
            | "dailyVat"
            | "dailyPay"
            | "bookingNetTotal"
            | "bookingVatTotal"
            | "bookingGrossTotal";
          sign?: "positive" | "negative";
          date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
        }
      | {
          kind: "concat";
          /**
           * @minItems 1
           * @maxItems 20
           */
          parts: (
            | {
                kind: "field";
                value:
                  | "candidate.candidateId"
                  | "candidate.candidateName"
                  | "candidate.timesheetId"
                  | "candidate.timesheetInvoiceNumber"
                  | "candidate.timesheetDate"
                  | "candidate.timesheetNumber"
                  | "candidate.timesheetStart"
                  | "candidate.timesheetEnd"
                  | "candidate.timesheetLastDay"
                  | "candidate.timesheetApprover"
                  | "candidate.timesheetLastSunday"
                  | "candidate.timesheetStudentName"
                  | "candidate.customClientId"
                  | "candidate.customClientInternalId"
                  | "candidate.clientName"
                  | "candidate.clientId"
                  | "candidate.customCandidateId"
                  | "candidate.customCandidateInternalId"
                  | "candidate.customCandidateIdPayType"
                  | "candidate.clientContact"
                  | "candidate.invoiceContact"
                  | "candidate.total"
                  | "candidate.peDate"
                  | "candidate.bookingPayType"
                  | "candidate.payRate"
                  | "candidate.chargeRate"
                  | "candidate.units"
                  | "candidate.hours"
                  | "candidate.hoursPerDay"
                  | "candidate.consultant"
                  | "candidate.role"
                  | "candidate.clientTimesheetStatus"
                  | "candidate.isCandidateFirstBooking"
                  | "candidate.isCandidateFirstBookingWithClient"
                  | "candidate.isClientFirstBooking"
                  | "candidate.consultant1Id"
                  | "candidate.consultant1Perc"
                  | "candidate.consultant2Id"
                  | "candidate.consultant2Perc"
                  | "candidate.consultant3Id"
                  | "candidate.consultant3Perc"
                  | "candidate.consultant4Id"
                  | "candidate.consultant4Perc"
                  | "candidate.consultant5Id"
                  | "candidate.consultant5Perc"
                  | "candidate.placementId"
                  | "candidate.officeNumber"
                  | "candidate.poNumber"
                  | "candidate.payInterval"
                  | "candidate._internalId"
                  | "candidate.candidatePayType"
                  | "candidate.umbrellaName"
                  | "candidate.umbrellaCode"
                  | "candidate.umbrellaId"
                  | "candidate.bookingStartDate"
                  | "candidate.assigneeBranchCodes"
                  | "candidate.niNumberTrimmed"
                  | "candidate.sortCodeTrimmed"
                  | "candidate.accountNumberTrimmed"
                  | "candidate.accountNameTrimmed"
                  | "candidate.bookingOnCosts"
                  | "candidate.payRateExclusiveHoliday"
                  | "candidate.payRateHolidayPay"
                  | "candidate.payeRate"
                  | "candidate.payeMargin"
                  | "candidate.payeWeeklyMargin"
                  | "candidate.grossMargin"
                  | "candidate.totalCharge"
                  | "candidate.totalExcludingHolidayPay"
                  | "candidate.totalHolidayPay"
                  | "candidate.candidateDetails.title"
                  | "candidate.candidateDetails.forename"
                  | "candidate.candidateDetails.surname"
                  | "candidate.candidateDetails.sex"
                  | "candidate.candidateDetails.dob"
                  | "candidate.candidateDetails.email"
                  | "candidate.candidateDetails.telephone"
                  | "candidate.candidateDetails.mobile"
                  | "candidate.candidateDetails.address.addressLine1"
                  | "candidate.candidateDetails.address.postcode"
                  | "candidate.candidateDetails.gender"
                  | "candidate.candidateDetails.bankDetails.niNumber"
                  | "candidate.days.start"
                  | "candidate.days.end"
                  | "candidate.days.length"
                  | "candidate.days.amPm"
                  | "candidate.days.status"
                  | "candidate.days.chargeRate"
                  | "candidate.days.payRate"
                  | "candidate.days.flaggedForRemoval"
                  | "day.start"
                  | "day.end"
                  | "day.length"
                  | "day.amPm"
                  | "day.status"
                  | "day.chargeRate"
                  | "day.payRate"
                  | "day.flaggedForRemoval"
                  | "candidate.umbrellaAddress.addressLine1"
                  | "candidate.umbrellaAddress.addressLine2"
                  | "candidate.umbrellaAddress.addressLine3"
                  | "candidate.umbrellaAddress.city"
                  | "candidate.umbrellaAddress.county"
                  | "candidate.umbrellaAddress.borough"
                  | "candidate.umbrellaAddress.postcode"
                  | "candidate.umbrellaAddress.country"
                  | "candidate.umbrellaAddress.lat"
                  | "candidate.umbrellaAddress.lng"
                  | "candidate.bankDetails.bankAccountName"
                  | "candidate.bankDetails.bankName"
                  | "candidate.bankDetails.accountNumber"
                  | "candidate.bankDetails.sortCode"
                  | "candidate.bankDetails.niNumber"
                  | "candidate.bankDetails.jobOp1"
                  | "candidate.bankDetails.jobOp2"
                  | "candidate.bankDetails.jobOp3"
                  | "candidate.bankDetails.studentLoan.studentLoan"
                  | "candidate.bankDetails.studentLoan.plan1"
                  | "candidate.bankDetails.studentLoan.plan2"
                  | "candidate.bankDetails.studentLoan.plan4"
                  | "candidate.bankDetails.studentLoan.postGradLoan"
                  | "candidate.timesheets.id"
                  | "candidate.timesheets.candidateName"
                  | "candidate.timesheets.candidate"
                  | "candidate.timesheets.clientName"
                  | "candidate.timesheets.clientEmail"
                  | "candidate.timesheets.client"
                  | "candidate.timesheets.booking"
                  | "candidate.timesheets.days.start"
                  | "candidate.timesheets.days.end"
                  | "candidate.timesheets.days.length"
                  | "candidate.timesheets.days.amPm"
                  | "candidate.timesheets.days.status"
                  | "candidate.timesheets.days.chargeRate"
                  | "candidate.timesheets.days.payRate"
                  | "candidate.timesheets.days.flaggedForRemoval"
                  | "candidate.timesheets.status"
                  | "candidate.timesheets.statusChangedOn"
                  | "candidate.timesheets.statusChangeBy"
                  | "candidate.timesheets.statusChangeByJobRole"
                  | "candidate.timesheets.statusChangeBySignature"
                  | "candidate.timesheets.statusChangeByDrawnSignature"
                  | "candidate.timesheets.statusChangeBySignatureDate"
                  | "candidate.timesheets.rejectionReason"
                  | "candidate.timesheets.statusCandidate"
                  | "candidate.timesheets.statusChangedOnCandidate"
                  | "candidate.timesheets.statusChangeByCandidate"
                  | "candidate.timesheets.statusChangeByJobRoleCandidate"
                  | "candidate.timesheets.statusChangeByCandidateSignature"
                  | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                  | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                  | "candidate.timesheets.rejectionReasonCandidate"
                  | "candidate.timesheets.createdBy"
                  | "candidate.timesheets.group"
                  | "candidate.timesheets.resendTimesheetEmail"
                  | "candidate.timesheets.customer"
                  | "candidate.timesheets.assignee"
                  | "candidate.timesheets.queryResponse"
                  | "candidate.timesheets.invoiceNumber"
                  | "candidate.timesheets.timesheetNumber"
                  | "candidate.timesheets.type"
                  | "candidate.timesheets.subType"
                  | "candidate.timesheets.tuitionReport"
                  | "candidate.timesheets.tuitionReportAnswers.questionId"
                  | "candidate.timesheets.tuitionReportAnswers.value"
                  | "candidate.timesheets.tuitionReportPending"
                  | "candidate.timesheets.role"
                  | "candidate.timesheets.statusApprovalIp"
                  | "candidate.timesheets.statusApprovalCandidateIp";
                /**
                 * @maxLength 100
                 */
                prefix?: string;
                /**
                 * @maxLength 100
                 */
                suffix?: string;
                /**
                 * @maxItems 20
                 */
                cases?: {
                  match: string | number | boolean;
                  /**
                   * @maxLength 1000
                   */
                  value: string;
                }[];
                /**
                 * @maxLength 1000
                 */
                fallback?: string;
                date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
              }
            | {
                kind: "static";
                /**
                 * @maxLength 1000
                 */
                value: string;
              }
            | {
                kind: "calculated";
                value:
                  | "rowNumber"
                  | "invoiceFriday"
                  | "financialYear"
                  | "financialPeriod"
                  | "bookingWeekMonday"
                  | "dayWorked"
                  | "dailyNetCharge"
                  | "dailyVat"
                  | "dailyPay"
                  | "bookingNetTotal"
                  | "bookingVatTotal"
                  | "bookingGrossTotal";
                sign?: "positive" | "negative";
                date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
              }
          )[];
          /**
           * @maxLength 100
           */
          separator: string;
        };
    /**
     * Independent values for parent, repeated day and summary rows
     */
    rowValues?: {
      parent?:
        | {
            kind: "field";
            value:
              | "candidate.candidateId"
              | "candidate.candidateName"
              | "candidate.timesheetId"
              | "candidate.timesheetInvoiceNumber"
              | "candidate.timesheetDate"
              | "candidate.timesheetNumber"
              | "candidate.timesheetStart"
              | "candidate.timesheetEnd"
              | "candidate.timesheetLastDay"
              | "candidate.timesheetApprover"
              | "candidate.timesheetLastSunday"
              | "candidate.timesheetStudentName"
              | "candidate.customClientId"
              | "candidate.customClientInternalId"
              | "candidate.clientName"
              | "candidate.clientId"
              | "candidate.customCandidateId"
              | "candidate.customCandidateInternalId"
              | "candidate.customCandidateIdPayType"
              | "candidate.clientContact"
              | "candidate.invoiceContact"
              | "candidate.total"
              | "candidate.peDate"
              | "candidate.bookingPayType"
              | "candidate.payRate"
              | "candidate.chargeRate"
              | "candidate.units"
              | "candidate.hours"
              | "candidate.hoursPerDay"
              | "candidate.consultant"
              | "candidate.role"
              | "candidate.clientTimesheetStatus"
              | "candidate.isCandidateFirstBooking"
              | "candidate.isCandidateFirstBookingWithClient"
              | "candidate.isClientFirstBooking"
              | "candidate.consultant1Id"
              | "candidate.consultant1Perc"
              | "candidate.consultant2Id"
              | "candidate.consultant2Perc"
              | "candidate.consultant3Id"
              | "candidate.consultant3Perc"
              | "candidate.consultant4Id"
              | "candidate.consultant4Perc"
              | "candidate.consultant5Id"
              | "candidate.consultant5Perc"
              | "candidate.placementId"
              | "candidate.officeNumber"
              | "candidate.poNumber"
              | "candidate.payInterval"
              | "candidate._internalId"
              | "candidate.candidatePayType"
              | "candidate.umbrellaName"
              | "candidate.umbrellaCode"
              | "candidate.umbrellaId"
              | "candidate.bookingStartDate"
              | "candidate.assigneeBranchCodes"
              | "candidate.niNumberTrimmed"
              | "candidate.sortCodeTrimmed"
              | "candidate.accountNumberTrimmed"
              | "candidate.accountNameTrimmed"
              | "candidate.bookingOnCosts"
              | "candidate.payRateExclusiveHoliday"
              | "candidate.payRateHolidayPay"
              | "candidate.payeRate"
              | "candidate.payeMargin"
              | "candidate.payeWeeklyMargin"
              | "candidate.grossMargin"
              | "candidate.totalCharge"
              | "candidate.totalExcludingHolidayPay"
              | "candidate.totalHolidayPay"
              | "candidate.candidateDetails.title"
              | "candidate.candidateDetails.forename"
              | "candidate.candidateDetails.surname"
              | "candidate.candidateDetails.sex"
              | "candidate.candidateDetails.dob"
              | "candidate.candidateDetails.email"
              | "candidate.candidateDetails.telephone"
              | "candidate.candidateDetails.mobile"
              | "candidate.candidateDetails.address.addressLine1"
              | "candidate.candidateDetails.address.postcode"
              | "candidate.candidateDetails.gender"
              | "candidate.candidateDetails.bankDetails.niNumber"
              | "candidate.days.start"
              | "candidate.days.end"
              | "candidate.days.length"
              | "candidate.days.amPm"
              | "candidate.days.status"
              | "candidate.days.chargeRate"
              | "candidate.days.payRate"
              | "candidate.days.flaggedForRemoval"
              | "day.start"
              | "day.end"
              | "day.length"
              | "day.amPm"
              | "day.status"
              | "day.chargeRate"
              | "day.payRate"
              | "day.flaggedForRemoval"
              | "candidate.umbrellaAddress.addressLine1"
              | "candidate.umbrellaAddress.addressLine2"
              | "candidate.umbrellaAddress.addressLine3"
              | "candidate.umbrellaAddress.city"
              | "candidate.umbrellaAddress.county"
              | "candidate.umbrellaAddress.borough"
              | "candidate.umbrellaAddress.postcode"
              | "candidate.umbrellaAddress.country"
              | "candidate.umbrellaAddress.lat"
              | "candidate.umbrellaAddress.lng"
              | "candidate.bankDetails.bankAccountName"
              | "candidate.bankDetails.bankName"
              | "candidate.bankDetails.accountNumber"
              | "candidate.bankDetails.sortCode"
              | "candidate.bankDetails.niNumber"
              | "candidate.bankDetails.jobOp1"
              | "candidate.bankDetails.jobOp2"
              | "candidate.bankDetails.jobOp3"
              | "candidate.bankDetails.studentLoan.studentLoan"
              | "candidate.bankDetails.studentLoan.plan1"
              | "candidate.bankDetails.studentLoan.plan2"
              | "candidate.bankDetails.studentLoan.plan4"
              | "candidate.bankDetails.studentLoan.postGradLoan"
              | "candidate.timesheets.id"
              | "candidate.timesheets.candidateName"
              | "candidate.timesheets.candidate"
              | "candidate.timesheets.clientName"
              | "candidate.timesheets.clientEmail"
              | "candidate.timesheets.client"
              | "candidate.timesheets.booking"
              | "candidate.timesheets.days.start"
              | "candidate.timesheets.days.end"
              | "candidate.timesheets.days.length"
              | "candidate.timesheets.days.amPm"
              | "candidate.timesheets.days.status"
              | "candidate.timesheets.days.chargeRate"
              | "candidate.timesheets.days.payRate"
              | "candidate.timesheets.days.flaggedForRemoval"
              | "candidate.timesheets.status"
              | "candidate.timesheets.statusChangedOn"
              | "candidate.timesheets.statusChangeBy"
              | "candidate.timesheets.statusChangeByJobRole"
              | "candidate.timesheets.statusChangeBySignature"
              | "candidate.timesheets.statusChangeByDrawnSignature"
              | "candidate.timesheets.statusChangeBySignatureDate"
              | "candidate.timesheets.rejectionReason"
              | "candidate.timesheets.statusCandidate"
              | "candidate.timesheets.statusChangedOnCandidate"
              | "candidate.timesheets.statusChangeByCandidate"
              | "candidate.timesheets.statusChangeByJobRoleCandidate"
              | "candidate.timesheets.statusChangeByCandidateSignature"
              | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
              | "candidate.timesheets.statusChangeByCandidateSignatureDate"
              | "candidate.timesheets.rejectionReasonCandidate"
              | "candidate.timesheets.createdBy"
              | "candidate.timesheets.group"
              | "candidate.timesheets.resendTimesheetEmail"
              | "candidate.timesheets.customer"
              | "candidate.timesheets.assignee"
              | "candidate.timesheets.queryResponse"
              | "candidate.timesheets.invoiceNumber"
              | "candidate.timesheets.timesheetNumber"
              | "candidate.timesheets.type"
              | "candidate.timesheets.subType"
              | "candidate.timesheets.tuitionReport"
              | "candidate.timesheets.tuitionReportAnswers.questionId"
              | "candidate.timesheets.tuitionReportAnswers.value"
              | "candidate.timesheets.tuitionReportPending"
              | "candidate.timesheets.role"
              | "candidate.timesheets.statusApprovalIp"
              | "candidate.timesheets.statusApprovalCandidateIp";
            /**
             * @maxLength 100
             */
            prefix?: string;
            /**
             * @maxLength 100
             */
            suffix?: string;
            /**
             * @maxItems 20
             */
            cases?: {
              match: string | number | boolean;
              /**
               * @maxLength 1000
               */
              value: string;
            }[];
            /**
             * @maxLength 1000
             */
            fallback?: string;
            date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
          }
        | {
            kind: "static";
            /**
             * @maxLength 1000
             */
            value: string;
          }
        | {
            kind: "calculated";
            value:
              | "rowNumber"
              | "invoiceFriday"
              | "financialYear"
              | "financialPeriod"
              | "bookingWeekMonday"
              | "dayWorked"
              | "dailyNetCharge"
              | "dailyVat"
              | "dailyPay"
              | "bookingNetTotal"
              | "bookingVatTotal"
              | "bookingGrossTotal";
            sign?: "positive" | "negative";
            date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
          }
        | {
            kind: "concat";
            /**
             * @minItems 1
             * @maxItems 20
             */
            parts: (
              | {
                  kind: "field";
                  value:
                    | "candidate.candidateId"
                    | "candidate.candidateName"
                    | "candidate.timesheetId"
                    | "candidate.timesheetInvoiceNumber"
                    | "candidate.timesheetDate"
                    | "candidate.timesheetNumber"
                    | "candidate.timesheetStart"
                    | "candidate.timesheetEnd"
                    | "candidate.timesheetLastDay"
                    | "candidate.timesheetApprover"
                    | "candidate.timesheetLastSunday"
                    | "candidate.timesheetStudentName"
                    | "candidate.customClientId"
                    | "candidate.customClientInternalId"
                    | "candidate.clientName"
                    | "candidate.clientId"
                    | "candidate.customCandidateId"
                    | "candidate.customCandidateInternalId"
                    | "candidate.customCandidateIdPayType"
                    | "candidate.clientContact"
                    | "candidate.invoiceContact"
                    | "candidate.total"
                    | "candidate.peDate"
                    | "candidate.bookingPayType"
                    | "candidate.payRate"
                    | "candidate.chargeRate"
                    | "candidate.units"
                    | "candidate.hours"
                    | "candidate.hoursPerDay"
                    | "candidate.consultant"
                    | "candidate.role"
                    | "candidate.clientTimesheetStatus"
                    | "candidate.isCandidateFirstBooking"
                    | "candidate.isCandidateFirstBookingWithClient"
                    | "candidate.isClientFirstBooking"
                    | "candidate.consultant1Id"
                    | "candidate.consultant1Perc"
                    | "candidate.consultant2Id"
                    | "candidate.consultant2Perc"
                    | "candidate.consultant3Id"
                    | "candidate.consultant3Perc"
                    | "candidate.consultant4Id"
                    | "candidate.consultant4Perc"
                    | "candidate.consultant5Id"
                    | "candidate.consultant5Perc"
                    | "candidate.placementId"
                    | "candidate.officeNumber"
                    | "candidate.poNumber"
                    | "candidate.payInterval"
                    | "candidate._internalId"
                    | "candidate.candidatePayType"
                    | "candidate.umbrellaName"
                    | "candidate.umbrellaCode"
                    | "candidate.umbrellaId"
                    | "candidate.bookingStartDate"
                    | "candidate.assigneeBranchCodes"
                    | "candidate.niNumberTrimmed"
                    | "candidate.sortCodeTrimmed"
                    | "candidate.accountNumberTrimmed"
                    | "candidate.accountNameTrimmed"
                    | "candidate.bookingOnCosts"
                    | "candidate.payRateExclusiveHoliday"
                    | "candidate.payRateHolidayPay"
                    | "candidate.payeRate"
                    | "candidate.payeMargin"
                    | "candidate.payeWeeklyMargin"
                    | "candidate.grossMargin"
                    | "candidate.totalCharge"
                    | "candidate.totalExcludingHolidayPay"
                    | "candidate.totalHolidayPay"
                    | "candidate.candidateDetails.title"
                    | "candidate.candidateDetails.forename"
                    | "candidate.candidateDetails.surname"
                    | "candidate.candidateDetails.sex"
                    | "candidate.candidateDetails.dob"
                    | "candidate.candidateDetails.email"
                    | "candidate.candidateDetails.telephone"
                    | "candidate.candidateDetails.mobile"
                    | "candidate.candidateDetails.address.addressLine1"
                    | "candidate.candidateDetails.address.postcode"
                    | "candidate.candidateDetails.gender"
                    | "candidate.candidateDetails.bankDetails.niNumber"
                    | "candidate.days.start"
                    | "candidate.days.end"
                    | "candidate.days.length"
                    | "candidate.days.amPm"
                    | "candidate.days.status"
                    | "candidate.days.chargeRate"
                    | "candidate.days.payRate"
                    | "candidate.days.flaggedForRemoval"
                    | "day.start"
                    | "day.end"
                    | "day.length"
                    | "day.amPm"
                    | "day.status"
                    | "day.chargeRate"
                    | "day.payRate"
                    | "day.flaggedForRemoval"
                    | "candidate.umbrellaAddress.addressLine1"
                    | "candidate.umbrellaAddress.addressLine2"
                    | "candidate.umbrellaAddress.addressLine3"
                    | "candidate.umbrellaAddress.city"
                    | "candidate.umbrellaAddress.county"
                    | "candidate.umbrellaAddress.borough"
                    | "candidate.umbrellaAddress.postcode"
                    | "candidate.umbrellaAddress.country"
                    | "candidate.umbrellaAddress.lat"
                    | "candidate.umbrellaAddress.lng"
                    | "candidate.bankDetails.bankAccountName"
                    | "candidate.bankDetails.bankName"
                    | "candidate.bankDetails.accountNumber"
                    | "candidate.bankDetails.sortCode"
                    | "candidate.bankDetails.niNumber"
                    | "candidate.bankDetails.jobOp1"
                    | "candidate.bankDetails.jobOp2"
                    | "candidate.bankDetails.jobOp3"
                    | "candidate.bankDetails.studentLoan.studentLoan"
                    | "candidate.bankDetails.studentLoan.plan1"
                    | "candidate.bankDetails.studentLoan.plan2"
                    | "candidate.bankDetails.studentLoan.plan4"
                    | "candidate.bankDetails.studentLoan.postGradLoan"
                    | "candidate.timesheets.id"
                    | "candidate.timesheets.candidateName"
                    | "candidate.timesheets.candidate"
                    | "candidate.timesheets.clientName"
                    | "candidate.timesheets.clientEmail"
                    | "candidate.timesheets.client"
                    | "candidate.timesheets.booking"
                    | "candidate.timesheets.days.start"
                    | "candidate.timesheets.days.end"
                    | "candidate.timesheets.days.length"
                    | "candidate.timesheets.days.amPm"
                    | "candidate.timesheets.days.status"
                    | "candidate.timesheets.days.chargeRate"
                    | "candidate.timesheets.days.payRate"
                    | "candidate.timesheets.days.flaggedForRemoval"
                    | "candidate.timesheets.status"
                    | "candidate.timesheets.statusChangedOn"
                    | "candidate.timesheets.statusChangeBy"
                    | "candidate.timesheets.statusChangeByJobRole"
                    | "candidate.timesheets.statusChangeBySignature"
                    | "candidate.timesheets.statusChangeByDrawnSignature"
                    | "candidate.timesheets.statusChangeBySignatureDate"
                    | "candidate.timesheets.rejectionReason"
                    | "candidate.timesheets.statusCandidate"
                    | "candidate.timesheets.statusChangedOnCandidate"
                    | "candidate.timesheets.statusChangeByCandidate"
                    | "candidate.timesheets.statusChangeByJobRoleCandidate"
                    | "candidate.timesheets.statusChangeByCandidateSignature"
                    | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                    | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                    | "candidate.timesheets.rejectionReasonCandidate"
                    | "candidate.timesheets.createdBy"
                    | "candidate.timesheets.group"
                    | "candidate.timesheets.resendTimesheetEmail"
                    | "candidate.timesheets.customer"
                    | "candidate.timesheets.assignee"
                    | "candidate.timesheets.queryResponse"
                    | "candidate.timesheets.invoiceNumber"
                    | "candidate.timesheets.timesheetNumber"
                    | "candidate.timesheets.type"
                    | "candidate.timesheets.subType"
                    | "candidate.timesheets.tuitionReport"
                    | "candidate.timesheets.tuitionReportAnswers.questionId"
                    | "candidate.timesheets.tuitionReportAnswers.value"
                    | "candidate.timesheets.tuitionReportPending"
                    | "candidate.timesheets.role"
                    | "candidate.timesheets.statusApprovalIp"
                    | "candidate.timesheets.statusApprovalCandidateIp";
                  /**
                   * @maxLength 100
                   */
                  prefix?: string;
                  /**
                   * @maxLength 100
                   */
                  suffix?: string;
                  /**
                   * @maxItems 20
                   */
                  cases?: {
                    match: string | number | boolean;
                    /**
                     * @maxLength 1000
                     */
                    value: string;
                  }[];
                  /**
                   * @maxLength 1000
                   */
                  fallback?: string;
                  date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                }
              | {
                  kind: "static";
                  /**
                   * @maxLength 1000
                   */
                  value: string;
                }
              | {
                  kind: "calculated";
                  value:
                    | "rowNumber"
                    | "invoiceFriday"
                    | "financialYear"
                    | "financialPeriod"
                    | "bookingWeekMonday"
                    | "dayWorked"
                    | "dailyNetCharge"
                    | "dailyVat"
                    | "dailyPay"
                    | "bookingNetTotal"
                    | "bookingVatTotal"
                    | "bookingGrossTotal";
                  sign?: "positive" | "negative";
                  date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                }
            )[];
            /**
             * @maxLength 100
             */
            separator: string;
          };
      day?:
        | {
            kind: "field";
            value:
              | "candidate.candidateId"
              | "candidate.candidateName"
              | "candidate.timesheetId"
              | "candidate.timesheetInvoiceNumber"
              | "candidate.timesheetDate"
              | "candidate.timesheetNumber"
              | "candidate.timesheetStart"
              | "candidate.timesheetEnd"
              | "candidate.timesheetLastDay"
              | "candidate.timesheetApprover"
              | "candidate.timesheetLastSunday"
              | "candidate.timesheetStudentName"
              | "candidate.customClientId"
              | "candidate.customClientInternalId"
              | "candidate.clientName"
              | "candidate.clientId"
              | "candidate.customCandidateId"
              | "candidate.customCandidateInternalId"
              | "candidate.customCandidateIdPayType"
              | "candidate.clientContact"
              | "candidate.invoiceContact"
              | "candidate.total"
              | "candidate.peDate"
              | "candidate.bookingPayType"
              | "candidate.payRate"
              | "candidate.chargeRate"
              | "candidate.units"
              | "candidate.hours"
              | "candidate.hoursPerDay"
              | "candidate.consultant"
              | "candidate.role"
              | "candidate.clientTimesheetStatus"
              | "candidate.isCandidateFirstBooking"
              | "candidate.isCandidateFirstBookingWithClient"
              | "candidate.isClientFirstBooking"
              | "candidate.consultant1Id"
              | "candidate.consultant1Perc"
              | "candidate.consultant2Id"
              | "candidate.consultant2Perc"
              | "candidate.consultant3Id"
              | "candidate.consultant3Perc"
              | "candidate.consultant4Id"
              | "candidate.consultant4Perc"
              | "candidate.consultant5Id"
              | "candidate.consultant5Perc"
              | "candidate.placementId"
              | "candidate.officeNumber"
              | "candidate.poNumber"
              | "candidate.payInterval"
              | "candidate._internalId"
              | "candidate.candidatePayType"
              | "candidate.umbrellaName"
              | "candidate.umbrellaCode"
              | "candidate.umbrellaId"
              | "candidate.bookingStartDate"
              | "candidate.assigneeBranchCodes"
              | "candidate.niNumberTrimmed"
              | "candidate.sortCodeTrimmed"
              | "candidate.accountNumberTrimmed"
              | "candidate.accountNameTrimmed"
              | "candidate.bookingOnCosts"
              | "candidate.payRateExclusiveHoliday"
              | "candidate.payRateHolidayPay"
              | "candidate.payeRate"
              | "candidate.payeMargin"
              | "candidate.payeWeeklyMargin"
              | "candidate.grossMargin"
              | "candidate.totalCharge"
              | "candidate.totalExcludingHolidayPay"
              | "candidate.totalHolidayPay"
              | "candidate.candidateDetails.title"
              | "candidate.candidateDetails.forename"
              | "candidate.candidateDetails.surname"
              | "candidate.candidateDetails.sex"
              | "candidate.candidateDetails.dob"
              | "candidate.candidateDetails.email"
              | "candidate.candidateDetails.telephone"
              | "candidate.candidateDetails.mobile"
              | "candidate.candidateDetails.address.addressLine1"
              | "candidate.candidateDetails.address.postcode"
              | "candidate.candidateDetails.gender"
              | "candidate.candidateDetails.bankDetails.niNumber"
              | "candidate.days.start"
              | "candidate.days.end"
              | "candidate.days.length"
              | "candidate.days.amPm"
              | "candidate.days.status"
              | "candidate.days.chargeRate"
              | "candidate.days.payRate"
              | "candidate.days.flaggedForRemoval"
              | "day.start"
              | "day.end"
              | "day.length"
              | "day.amPm"
              | "day.status"
              | "day.chargeRate"
              | "day.payRate"
              | "day.flaggedForRemoval"
              | "candidate.umbrellaAddress.addressLine1"
              | "candidate.umbrellaAddress.addressLine2"
              | "candidate.umbrellaAddress.addressLine3"
              | "candidate.umbrellaAddress.city"
              | "candidate.umbrellaAddress.county"
              | "candidate.umbrellaAddress.borough"
              | "candidate.umbrellaAddress.postcode"
              | "candidate.umbrellaAddress.country"
              | "candidate.umbrellaAddress.lat"
              | "candidate.umbrellaAddress.lng"
              | "candidate.bankDetails.bankAccountName"
              | "candidate.bankDetails.bankName"
              | "candidate.bankDetails.accountNumber"
              | "candidate.bankDetails.sortCode"
              | "candidate.bankDetails.niNumber"
              | "candidate.bankDetails.jobOp1"
              | "candidate.bankDetails.jobOp2"
              | "candidate.bankDetails.jobOp3"
              | "candidate.bankDetails.studentLoan.studentLoan"
              | "candidate.bankDetails.studentLoan.plan1"
              | "candidate.bankDetails.studentLoan.plan2"
              | "candidate.bankDetails.studentLoan.plan4"
              | "candidate.bankDetails.studentLoan.postGradLoan"
              | "candidate.timesheets.id"
              | "candidate.timesheets.candidateName"
              | "candidate.timesheets.candidate"
              | "candidate.timesheets.clientName"
              | "candidate.timesheets.clientEmail"
              | "candidate.timesheets.client"
              | "candidate.timesheets.booking"
              | "candidate.timesheets.days.start"
              | "candidate.timesheets.days.end"
              | "candidate.timesheets.days.length"
              | "candidate.timesheets.days.amPm"
              | "candidate.timesheets.days.status"
              | "candidate.timesheets.days.chargeRate"
              | "candidate.timesheets.days.payRate"
              | "candidate.timesheets.days.flaggedForRemoval"
              | "candidate.timesheets.status"
              | "candidate.timesheets.statusChangedOn"
              | "candidate.timesheets.statusChangeBy"
              | "candidate.timesheets.statusChangeByJobRole"
              | "candidate.timesheets.statusChangeBySignature"
              | "candidate.timesheets.statusChangeByDrawnSignature"
              | "candidate.timesheets.statusChangeBySignatureDate"
              | "candidate.timesheets.rejectionReason"
              | "candidate.timesheets.statusCandidate"
              | "candidate.timesheets.statusChangedOnCandidate"
              | "candidate.timesheets.statusChangeByCandidate"
              | "candidate.timesheets.statusChangeByJobRoleCandidate"
              | "candidate.timesheets.statusChangeByCandidateSignature"
              | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
              | "candidate.timesheets.statusChangeByCandidateSignatureDate"
              | "candidate.timesheets.rejectionReasonCandidate"
              | "candidate.timesheets.createdBy"
              | "candidate.timesheets.group"
              | "candidate.timesheets.resendTimesheetEmail"
              | "candidate.timesheets.customer"
              | "candidate.timesheets.assignee"
              | "candidate.timesheets.queryResponse"
              | "candidate.timesheets.invoiceNumber"
              | "candidate.timesheets.timesheetNumber"
              | "candidate.timesheets.type"
              | "candidate.timesheets.subType"
              | "candidate.timesheets.tuitionReport"
              | "candidate.timesheets.tuitionReportAnswers.questionId"
              | "candidate.timesheets.tuitionReportAnswers.value"
              | "candidate.timesheets.tuitionReportPending"
              | "candidate.timesheets.role"
              | "candidate.timesheets.statusApprovalIp"
              | "candidate.timesheets.statusApprovalCandidateIp";
            /**
             * @maxLength 100
             */
            prefix?: string;
            /**
             * @maxLength 100
             */
            suffix?: string;
            /**
             * @maxItems 20
             */
            cases?: {
              match: string | number | boolean;
              /**
               * @maxLength 1000
               */
              value: string;
            }[];
            /**
             * @maxLength 1000
             */
            fallback?: string;
            date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
          }
        | {
            kind: "static";
            /**
             * @maxLength 1000
             */
            value: string;
          }
        | {
            kind: "calculated";
            value:
              | "rowNumber"
              | "invoiceFriday"
              | "financialYear"
              | "financialPeriod"
              | "bookingWeekMonday"
              | "dayWorked"
              | "dailyNetCharge"
              | "dailyVat"
              | "dailyPay"
              | "bookingNetTotal"
              | "bookingVatTotal"
              | "bookingGrossTotal";
            sign?: "positive" | "negative";
            date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
          }
        | {
            kind: "concat";
            /**
             * @minItems 1
             * @maxItems 20
             */
            parts: (
              | {
                  kind: "field";
                  value:
                    | "candidate.candidateId"
                    | "candidate.candidateName"
                    | "candidate.timesheetId"
                    | "candidate.timesheetInvoiceNumber"
                    | "candidate.timesheetDate"
                    | "candidate.timesheetNumber"
                    | "candidate.timesheetStart"
                    | "candidate.timesheetEnd"
                    | "candidate.timesheetLastDay"
                    | "candidate.timesheetApprover"
                    | "candidate.timesheetLastSunday"
                    | "candidate.timesheetStudentName"
                    | "candidate.customClientId"
                    | "candidate.customClientInternalId"
                    | "candidate.clientName"
                    | "candidate.clientId"
                    | "candidate.customCandidateId"
                    | "candidate.customCandidateInternalId"
                    | "candidate.customCandidateIdPayType"
                    | "candidate.clientContact"
                    | "candidate.invoiceContact"
                    | "candidate.total"
                    | "candidate.peDate"
                    | "candidate.bookingPayType"
                    | "candidate.payRate"
                    | "candidate.chargeRate"
                    | "candidate.units"
                    | "candidate.hours"
                    | "candidate.hoursPerDay"
                    | "candidate.consultant"
                    | "candidate.role"
                    | "candidate.clientTimesheetStatus"
                    | "candidate.isCandidateFirstBooking"
                    | "candidate.isCandidateFirstBookingWithClient"
                    | "candidate.isClientFirstBooking"
                    | "candidate.consultant1Id"
                    | "candidate.consultant1Perc"
                    | "candidate.consultant2Id"
                    | "candidate.consultant2Perc"
                    | "candidate.consultant3Id"
                    | "candidate.consultant3Perc"
                    | "candidate.consultant4Id"
                    | "candidate.consultant4Perc"
                    | "candidate.consultant5Id"
                    | "candidate.consultant5Perc"
                    | "candidate.placementId"
                    | "candidate.officeNumber"
                    | "candidate.poNumber"
                    | "candidate.payInterval"
                    | "candidate._internalId"
                    | "candidate.candidatePayType"
                    | "candidate.umbrellaName"
                    | "candidate.umbrellaCode"
                    | "candidate.umbrellaId"
                    | "candidate.bookingStartDate"
                    | "candidate.assigneeBranchCodes"
                    | "candidate.niNumberTrimmed"
                    | "candidate.sortCodeTrimmed"
                    | "candidate.accountNumberTrimmed"
                    | "candidate.accountNameTrimmed"
                    | "candidate.bookingOnCosts"
                    | "candidate.payRateExclusiveHoliday"
                    | "candidate.payRateHolidayPay"
                    | "candidate.payeRate"
                    | "candidate.payeMargin"
                    | "candidate.payeWeeklyMargin"
                    | "candidate.grossMargin"
                    | "candidate.totalCharge"
                    | "candidate.totalExcludingHolidayPay"
                    | "candidate.totalHolidayPay"
                    | "candidate.candidateDetails.title"
                    | "candidate.candidateDetails.forename"
                    | "candidate.candidateDetails.surname"
                    | "candidate.candidateDetails.sex"
                    | "candidate.candidateDetails.dob"
                    | "candidate.candidateDetails.email"
                    | "candidate.candidateDetails.telephone"
                    | "candidate.candidateDetails.mobile"
                    | "candidate.candidateDetails.address.addressLine1"
                    | "candidate.candidateDetails.address.postcode"
                    | "candidate.candidateDetails.gender"
                    | "candidate.candidateDetails.bankDetails.niNumber"
                    | "candidate.days.start"
                    | "candidate.days.end"
                    | "candidate.days.length"
                    | "candidate.days.amPm"
                    | "candidate.days.status"
                    | "candidate.days.chargeRate"
                    | "candidate.days.payRate"
                    | "candidate.days.flaggedForRemoval"
                    | "day.start"
                    | "day.end"
                    | "day.length"
                    | "day.amPm"
                    | "day.status"
                    | "day.chargeRate"
                    | "day.payRate"
                    | "day.flaggedForRemoval"
                    | "candidate.umbrellaAddress.addressLine1"
                    | "candidate.umbrellaAddress.addressLine2"
                    | "candidate.umbrellaAddress.addressLine3"
                    | "candidate.umbrellaAddress.city"
                    | "candidate.umbrellaAddress.county"
                    | "candidate.umbrellaAddress.borough"
                    | "candidate.umbrellaAddress.postcode"
                    | "candidate.umbrellaAddress.country"
                    | "candidate.umbrellaAddress.lat"
                    | "candidate.umbrellaAddress.lng"
                    | "candidate.bankDetails.bankAccountName"
                    | "candidate.bankDetails.bankName"
                    | "candidate.bankDetails.accountNumber"
                    | "candidate.bankDetails.sortCode"
                    | "candidate.bankDetails.niNumber"
                    | "candidate.bankDetails.jobOp1"
                    | "candidate.bankDetails.jobOp2"
                    | "candidate.bankDetails.jobOp3"
                    | "candidate.bankDetails.studentLoan.studentLoan"
                    | "candidate.bankDetails.studentLoan.plan1"
                    | "candidate.bankDetails.studentLoan.plan2"
                    | "candidate.bankDetails.studentLoan.plan4"
                    | "candidate.bankDetails.studentLoan.postGradLoan"
                    | "candidate.timesheets.id"
                    | "candidate.timesheets.candidateName"
                    | "candidate.timesheets.candidate"
                    | "candidate.timesheets.clientName"
                    | "candidate.timesheets.clientEmail"
                    | "candidate.timesheets.client"
                    | "candidate.timesheets.booking"
                    | "candidate.timesheets.days.start"
                    | "candidate.timesheets.days.end"
                    | "candidate.timesheets.days.length"
                    | "candidate.timesheets.days.amPm"
                    | "candidate.timesheets.days.status"
                    | "candidate.timesheets.days.chargeRate"
                    | "candidate.timesheets.days.payRate"
                    | "candidate.timesheets.days.flaggedForRemoval"
                    | "candidate.timesheets.status"
                    | "candidate.timesheets.statusChangedOn"
                    | "candidate.timesheets.statusChangeBy"
                    | "candidate.timesheets.statusChangeByJobRole"
                    | "candidate.timesheets.statusChangeBySignature"
                    | "candidate.timesheets.statusChangeByDrawnSignature"
                    | "candidate.timesheets.statusChangeBySignatureDate"
                    | "candidate.timesheets.rejectionReason"
                    | "candidate.timesheets.statusCandidate"
                    | "candidate.timesheets.statusChangedOnCandidate"
                    | "candidate.timesheets.statusChangeByCandidate"
                    | "candidate.timesheets.statusChangeByJobRoleCandidate"
                    | "candidate.timesheets.statusChangeByCandidateSignature"
                    | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                    | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                    | "candidate.timesheets.rejectionReasonCandidate"
                    | "candidate.timesheets.createdBy"
                    | "candidate.timesheets.group"
                    | "candidate.timesheets.resendTimesheetEmail"
                    | "candidate.timesheets.customer"
                    | "candidate.timesheets.assignee"
                    | "candidate.timesheets.queryResponse"
                    | "candidate.timesheets.invoiceNumber"
                    | "candidate.timesheets.timesheetNumber"
                    | "candidate.timesheets.type"
                    | "candidate.timesheets.subType"
                    | "candidate.timesheets.tuitionReport"
                    | "candidate.timesheets.tuitionReportAnswers.questionId"
                    | "candidate.timesheets.tuitionReportAnswers.value"
                    | "candidate.timesheets.tuitionReportPending"
                    | "candidate.timesheets.role"
                    | "candidate.timesheets.statusApprovalIp"
                    | "candidate.timesheets.statusApprovalCandidateIp";
                  /**
                   * @maxLength 100
                   */
                  prefix?: string;
                  /**
                   * @maxLength 100
                   */
                  suffix?: string;
                  /**
                   * @maxItems 20
                   */
                  cases?: {
                    match: string | number | boolean;
                    /**
                     * @maxLength 1000
                     */
                    value: string;
                  }[];
                  /**
                   * @maxLength 1000
                   */
                  fallback?: string;
                  date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                }
              | {
                  kind: "static";
                  /**
                   * @maxLength 1000
                   */
                  value: string;
                }
              | {
                  kind: "calculated";
                  value:
                    | "rowNumber"
                    | "invoiceFriday"
                    | "financialYear"
                    | "financialPeriod"
                    | "bookingWeekMonday"
                    | "dayWorked"
                    | "dailyNetCharge"
                    | "dailyVat"
                    | "dailyPay"
                    | "bookingNetTotal"
                    | "bookingVatTotal"
                    | "bookingGrossTotal";
                  sign?: "positive" | "negative";
                  date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                }
            )[];
            /**
             * @maxLength 100
             */
            separator: string;
          };
      summary?:
        | {
            kind: "field";
            value:
              | "candidate.candidateId"
              | "candidate.candidateName"
              | "candidate.timesheetId"
              | "candidate.timesheetInvoiceNumber"
              | "candidate.timesheetDate"
              | "candidate.timesheetNumber"
              | "candidate.timesheetStart"
              | "candidate.timesheetEnd"
              | "candidate.timesheetLastDay"
              | "candidate.timesheetApprover"
              | "candidate.timesheetLastSunday"
              | "candidate.timesheetStudentName"
              | "candidate.customClientId"
              | "candidate.customClientInternalId"
              | "candidate.clientName"
              | "candidate.clientId"
              | "candidate.customCandidateId"
              | "candidate.customCandidateInternalId"
              | "candidate.customCandidateIdPayType"
              | "candidate.clientContact"
              | "candidate.invoiceContact"
              | "candidate.total"
              | "candidate.peDate"
              | "candidate.bookingPayType"
              | "candidate.payRate"
              | "candidate.chargeRate"
              | "candidate.units"
              | "candidate.hours"
              | "candidate.hoursPerDay"
              | "candidate.consultant"
              | "candidate.role"
              | "candidate.clientTimesheetStatus"
              | "candidate.isCandidateFirstBooking"
              | "candidate.isCandidateFirstBookingWithClient"
              | "candidate.isClientFirstBooking"
              | "candidate.consultant1Id"
              | "candidate.consultant1Perc"
              | "candidate.consultant2Id"
              | "candidate.consultant2Perc"
              | "candidate.consultant3Id"
              | "candidate.consultant3Perc"
              | "candidate.consultant4Id"
              | "candidate.consultant4Perc"
              | "candidate.consultant5Id"
              | "candidate.consultant5Perc"
              | "candidate.placementId"
              | "candidate.officeNumber"
              | "candidate.poNumber"
              | "candidate.payInterval"
              | "candidate._internalId"
              | "candidate.candidatePayType"
              | "candidate.umbrellaName"
              | "candidate.umbrellaCode"
              | "candidate.umbrellaId"
              | "candidate.bookingStartDate"
              | "candidate.assigneeBranchCodes"
              | "candidate.niNumberTrimmed"
              | "candidate.sortCodeTrimmed"
              | "candidate.accountNumberTrimmed"
              | "candidate.accountNameTrimmed"
              | "candidate.bookingOnCosts"
              | "candidate.payRateExclusiveHoliday"
              | "candidate.payRateHolidayPay"
              | "candidate.payeRate"
              | "candidate.payeMargin"
              | "candidate.payeWeeklyMargin"
              | "candidate.grossMargin"
              | "candidate.totalCharge"
              | "candidate.totalExcludingHolidayPay"
              | "candidate.totalHolidayPay"
              | "candidate.candidateDetails.title"
              | "candidate.candidateDetails.forename"
              | "candidate.candidateDetails.surname"
              | "candidate.candidateDetails.sex"
              | "candidate.candidateDetails.dob"
              | "candidate.candidateDetails.email"
              | "candidate.candidateDetails.telephone"
              | "candidate.candidateDetails.mobile"
              | "candidate.candidateDetails.address.addressLine1"
              | "candidate.candidateDetails.address.postcode"
              | "candidate.candidateDetails.gender"
              | "candidate.candidateDetails.bankDetails.niNumber"
              | "candidate.days.start"
              | "candidate.days.end"
              | "candidate.days.length"
              | "candidate.days.amPm"
              | "candidate.days.status"
              | "candidate.days.chargeRate"
              | "candidate.days.payRate"
              | "candidate.days.flaggedForRemoval"
              | "day.start"
              | "day.end"
              | "day.length"
              | "day.amPm"
              | "day.status"
              | "day.chargeRate"
              | "day.payRate"
              | "day.flaggedForRemoval"
              | "candidate.umbrellaAddress.addressLine1"
              | "candidate.umbrellaAddress.addressLine2"
              | "candidate.umbrellaAddress.addressLine3"
              | "candidate.umbrellaAddress.city"
              | "candidate.umbrellaAddress.county"
              | "candidate.umbrellaAddress.borough"
              | "candidate.umbrellaAddress.postcode"
              | "candidate.umbrellaAddress.country"
              | "candidate.umbrellaAddress.lat"
              | "candidate.umbrellaAddress.lng"
              | "candidate.bankDetails.bankAccountName"
              | "candidate.bankDetails.bankName"
              | "candidate.bankDetails.accountNumber"
              | "candidate.bankDetails.sortCode"
              | "candidate.bankDetails.niNumber"
              | "candidate.bankDetails.jobOp1"
              | "candidate.bankDetails.jobOp2"
              | "candidate.bankDetails.jobOp3"
              | "candidate.bankDetails.studentLoan.studentLoan"
              | "candidate.bankDetails.studentLoan.plan1"
              | "candidate.bankDetails.studentLoan.plan2"
              | "candidate.bankDetails.studentLoan.plan4"
              | "candidate.bankDetails.studentLoan.postGradLoan"
              | "candidate.timesheets.id"
              | "candidate.timesheets.candidateName"
              | "candidate.timesheets.candidate"
              | "candidate.timesheets.clientName"
              | "candidate.timesheets.clientEmail"
              | "candidate.timesheets.client"
              | "candidate.timesheets.booking"
              | "candidate.timesheets.days.start"
              | "candidate.timesheets.days.end"
              | "candidate.timesheets.days.length"
              | "candidate.timesheets.days.amPm"
              | "candidate.timesheets.days.status"
              | "candidate.timesheets.days.chargeRate"
              | "candidate.timesheets.days.payRate"
              | "candidate.timesheets.days.flaggedForRemoval"
              | "candidate.timesheets.status"
              | "candidate.timesheets.statusChangedOn"
              | "candidate.timesheets.statusChangeBy"
              | "candidate.timesheets.statusChangeByJobRole"
              | "candidate.timesheets.statusChangeBySignature"
              | "candidate.timesheets.statusChangeByDrawnSignature"
              | "candidate.timesheets.statusChangeBySignatureDate"
              | "candidate.timesheets.rejectionReason"
              | "candidate.timesheets.statusCandidate"
              | "candidate.timesheets.statusChangedOnCandidate"
              | "candidate.timesheets.statusChangeByCandidate"
              | "candidate.timesheets.statusChangeByJobRoleCandidate"
              | "candidate.timesheets.statusChangeByCandidateSignature"
              | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
              | "candidate.timesheets.statusChangeByCandidateSignatureDate"
              | "candidate.timesheets.rejectionReasonCandidate"
              | "candidate.timesheets.createdBy"
              | "candidate.timesheets.group"
              | "candidate.timesheets.resendTimesheetEmail"
              | "candidate.timesheets.customer"
              | "candidate.timesheets.assignee"
              | "candidate.timesheets.queryResponse"
              | "candidate.timesheets.invoiceNumber"
              | "candidate.timesheets.timesheetNumber"
              | "candidate.timesheets.type"
              | "candidate.timesheets.subType"
              | "candidate.timesheets.tuitionReport"
              | "candidate.timesheets.tuitionReportAnswers.questionId"
              | "candidate.timesheets.tuitionReportAnswers.value"
              | "candidate.timesheets.tuitionReportPending"
              | "candidate.timesheets.role"
              | "candidate.timesheets.statusApprovalIp"
              | "candidate.timesheets.statusApprovalCandidateIp";
            /**
             * @maxLength 100
             */
            prefix?: string;
            /**
             * @maxLength 100
             */
            suffix?: string;
            /**
             * @maxItems 20
             */
            cases?: {
              match: string | number | boolean;
              /**
               * @maxLength 1000
               */
              value: string;
            }[];
            /**
             * @maxLength 1000
             */
            fallback?: string;
            date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
          }
        | {
            kind: "static";
            /**
             * @maxLength 1000
             */
            value: string;
          }
        | {
            kind: "calculated";
            value:
              | "rowNumber"
              | "invoiceFriday"
              | "financialYear"
              | "financialPeriod"
              | "bookingWeekMonday"
              | "dayWorked"
              | "dailyNetCharge"
              | "dailyVat"
              | "dailyPay"
              | "bookingNetTotal"
              | "bookingVatTotal"
              | "bookingGrossTotal";
            sign?: "positive" | "negative";
            date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
          }
        | {
            kind: "concat";
            /**
             * @minItems 1
             * @maxItems 20
             */
            parts: (
              | {
                  kind: "field";
                  value:
                    | "candidate.candidateId"
                    | "candidate.candidateName"
                    | "candidate.timesheetId"
                    | "candidate.timesheetInvoiceNumber"
                    | "candidate.timesheetDate"
                    | "candidate.timesheetNumber"
                    | "candidate.timesheetStart"
                    | "candidate.timesheetEnd"
                    | "candidate.timesheetLastDay"
                    | "candidate.timesheetApprover"
                    | "candidate.timesheetLastSunday"
                    | "candidate.timesheetStudentName"
                    | "candidate.customClientId"
                    | "candidate.customClientInternalId"
                    | "candidate.clientName"
                    | "candidate.clientId"
                    | "candidate.customCandidateId"
                    | "candidate.customCandidateInternalId"
                    | "candidate.customCandidateIdPayType"
                    | "candidate.clientContact"
                    | "candidate.invoiceContact"
                    | "candidate.total"
                    | "candidate.peDate"
                    | "candidate.bookingPayType"
                    | "candidate.payRate"
                    | "candidate.chargeRate"
                    | "candidate.units"
                    | "candidate.hours"
                    | "candidate.hoursPerDay"
                    | "candidate.consultant"
                    | "candidate.role"
                    | "candidate.clientTimesheetStatus"
                    | "candidate.isCandidateFirstBooking"
                    | "candidate.isCandidateFirstBookingWithClient"
                    | "candidate.isClientFirstBooking"
                    | "candidate.consultant1Id"
                    | "candidate.consultant1Perc"
                    | "candidate.consultant2Id"
                    | "candidate.consultant2Perc"
                    | "candidate.consultant3Id"
                    | "candidate.consultant3Perc"
                    | "candidate.consultant4Id"
                    | "candidate.consultant4Perc"
                    | "candidate.consultant5Id"
                    | "candidate.consultant5Perc"
                    | "candidate.placementId"
                    | "candidate.officeNumber"
                    | "candidate.poNumber"
                    | "candidate.payInterval"
                    | "candidate._internalId"
                    | "candidate.candidatePayType"
                    | "candidate.umbrellaName"
                    | "candidate.umbrellaCode"
                    | "candidate.umbrellaId"
                    | "candidate.bookingStartDate"
                    | "candidate.assigneeBranchCodes"
                    | "candidate.niNumberTrimmed"
                    | "candidate.sortCodeTrimmed"
                    | "candidate.accountNumberTrimmed"
                    | "candidate.accountNameTrimmed"
                    | "candidate.bookingOnCosts"
                    | "candidate.payRateExclusiveHoliday"
                    | "candidate.payRateHolidayPay"
                    | "candidate.payeRate"
                    | "candidate.payeMargin"
                    | "candidate.payeWeeklyMargin"
                    | "candidate.grossMargin"
                    | "candidate.totalCharge"
                    | "candidate.totalExcludingHolidayPay"
                    | "candidate.totalHolidayPay"
                    | "candidate.candidateDetails.title"
                    | "candidate.candidateDetails.forename"
                    | "candidate.candidateDetails.surname"
                    | "candidate.candidateDetails.sex"
                    | "candidate.candidateDetails.dob"
                    | "candidate.candidateDetails.email"
                    | "candidate.candidateDetails.telephone"
                    | "candidate.candidateDetails.mobile"
                    | "candidate.candidateDetails.address.addressLine1"
                    | "candidate.candidateDetails.address.postcode"
                    | "candidate.candidateDetails.gender"
                    | "candidate.candidateDetails.bankDetails.niNumber"
                    | "candidate.days.start"
                    | "candidate.days.end"
                    | "candidate.days.length"
                    | "candidate.days.amPm"
                    | "candidate.days.status"
                    | "candidate.days.chargeRate"
                    | "candidate.days.payRate"
                    | "candidate.days.flaggedForRemoval"
                    | "day.start"
                    | "day.end"
                    | "day.length"
                    | "day.amPm"
                    | "day.status"
                    | "day.chargeRate"
                    | "day.payRate"
                    | "day.flaggedForRemoval"
                    | "candidate.umbrellaAddress.addressLine1"
                    | "candidate.umbrellaAddress.addressLine2"
                    | "candidate.umbrellaAddress.addressLine3"
                    | "candidate.umbrellaAddress.city"
                    | "candidate.umbrellaAddress.county"
                    | "candidate.umbrellaAddress.borough"
                    | "candidate.umbrellaAddress.postcode"
                    | "candidate.umbrellaAddress.country"
                    | "candidate.umbrellaAddress.lat"
                    | "candidate.umbrellaAddress.lng"
                    | "candidate.bankDetails.bankAccountName"
                    | "candidate.bankDetails.bankName"
                    | "candidate.bankDetails.accountNumber"
                    | "candidate.bankDetails.sortCode"
                    | "candidate.bankDetails.niNumber"
                    | "candidate.bankDetails.jobOp1"
                    | "candidate.bankDetails.jobOp2"
                    | "candidate.bankDetails.jobOp3"
                    | "candidate.bankDetails.studentLoan.studentLoan"
                    | "candidate.bankDetails.studentLoan.plan1"
                    | "candidate.bankDetails.studentLoan.plan2"
                    | "candidate.bankDetails.studentLoan.plan4"
                    | "candidate.bankDetails.studentLoan.postGradLoan"
                    | "candidate.timesheets.id"
                    | "candidate.timesheets.candidateName"
                    | "candidate.timesheets.candidate"
                    | "candidate.timesheets.clientName"
                    | "candidate.timesheets.clientEmail"
                    | "candidate.timesheets.client"
                    | "candidate.timesheets.booking"
                    | "candidate.timesheets.days.start"
                    | "candidate.timesheets.days.end"
                    | "candidate.timesheets.days.length"
                    | "candidate.timesheets.days.amPm"
                    | "candidate.timesheets.days.status"
                    | "candidate.timesheets.days.chargeRate"
                    | "candidate.timesheets.days.payRate"
                    | "candidate.timesheets.days.flaggedForRemoval"
                    | "candidate.timesheets.status"
                    | "candidate.timesheets.statusChangedOn"
                    | "candidate.timesheets.statusChangeBy"
                    | "candidate.timesheets.statusChangeByJobRole"
                    | "candidate.timesheets.statusChangeBySignature"
                    | "candidate.timesheets.statusChangeByDrawnSignature"
                    | "candidate.timesheets.statusChangeBySignatureDate"
                    | "candidate.timesheets.rejectionReason"
                    | "candidate.timesheets.statusCandidate"
                    | "candidate.timesheets.statusChangedOnCandidate"
                    | "candidate.timesheets.statusChangeByCandidate"
                    | "candidate.timesheets.statusChangeByJobRoleCandidate"
                    | "candidate.timesheets.statusChangeByCandidateSignature"
                    | "candidate.timesheets.statusChangeByCandidateDrawnSignature"
                    | "candidate.timesheets.statusChangeByCandidateSignatureDate"
                    | "candidate.timesheets.rejectionReasonCandidate"
                    | "candidate.timesheets.createdBy"
                    | "candidate.timesheets.group"
                    | "candidate.timesheets.resendTimesheetEmail"
                    | "candidate.timesheets.customer"
                    | "candidate.timesheets.assignee"
                    | "candidate.timesheets.queryResponse"
                    | "candidate.timesheets.invoiceNumber"
                    | "candidate.timesheets.timesheetNumber"
                    | "candidate.timesheets.type"
                    | "candidate.timesheets.subType"
                    | "candidate.timesheets.tuitionReport"
                    | "candidate.timesheets.tuitionReportAnswers.questionId"
                    | "candidate.timesheets.tuitionReportAnswers.value"
                    | "candidate.timesheets.tuitionReportPending"
                    | "candidate.timesheets.role"
                    | "candidate.timesheets.statusApprovalIp"
                    | "candidate.timesheets.statusApprovalCandidateIp";
                  /**
                   * @maxLength 100
                   */
                  prefix?: string;
                  /**
                   * @maxLength 100
                   */
                  suffix?: string;
                  /**
                   * @maxItems 20
                   */
                  cases?: {
                    match: string | number | boolean;
                    /**
                     * @maxLength 1000
                     */
                    value: string;
                  }[];
                  /**
                   * @maxLength 1000
                   */
                  fallback?: string;
                  date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                }
              | {
                  kind: "static";
                  /**
                   * @maxLength 1000
                   */
                  value: string;
                }
              | {
                  kind: "calculated";
                  value:
                    | "rowNumber"
                    | "invoiceFriday"
                    | "financialYear"
                    | "financialPeriod"
                    | "bookingWeekMonday"
                    | "dayWorked"
                    | "dailyNetCharge"
                    | "dailyVat"
                    | "dailyPay"
                    | "bookingNetTotal"
                    | "bookingVatTotal"
                    | "bookingGrossTotal";
                  sign?: "positive" | "negative";
                  date?: "DD/MM/YYYY" | "DD-MMM-YY" | "HH:mm";
                }
            )[];
            /**
             * @maxLength 100
             */
            separator: string;
          };
    };
  }[];
  /**
   * Boolean flag of whether to show header names in the exported file
   */
  showHeaders?: boolean;
  /**
   * Flat booking rows or parent, day and summary rows
   */
  layout?: "flat" | "structured";
  /**
   * Include a parent row before the worked days of each payment-run item
   */
  includeParentRow?: boolean;
  /**
   * Include a summary row after the worked days of each payment-run item
   */
  includeSummaryRow?: boolean;
  /**
   * Fiscal year start month
   *
   * @minimum 1
   * @maximum 12
   */
  fiscalYearStartMonth?: number | null;
  /**
   * VAT percentage
   *
   * @minimum 0
   * @maximum 100
   */
  vatPercent?: number | null;
};

export type UpdateExportConfigVariables = {
  body: UpdateExportConfigRequestBody;
  pathParams: UpdateExportConfigPathParams;
  queryParams?: UpdateExportConfigQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateExportConfig = (
  variables: UpdateExportConfigVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateExportConfigResponse,
    UpdateExportConfigError,
    UpdateExportConfigRequestBody,
    {},
    UpdateExportConfigQueryParams,
    UpdateExportConfigPathParams
  >({
    url: "/company/export-configs/{type}",
    method: "put",
    ...variables,
    signal,
  });

export const useUpdateExportConfig = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateExportConfigResponse,
      UpdateExportConfigError,
      UpdateExportConfigVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateExportConfigResponse,
    UpdateExportConfigError,
    UpdateExportConfigVariables
  >({
    mutationFn: (variables: UpdateExportConfigVariables) =>
      fetchUpdateExportConfig(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetPaymentRunPathParams = {
  /**
   * The payment run identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  paymentRunId: string;
};

export type GetPaymentRunQueryParams = {
  /**
   * The fields to include in the response in dot notation format
   */
  select?: (
    | "id"
    | "exports.xlsx.status"
    | "exports.xlsx.processingError"
    | "exports.xlsx"
    | "exports.evertime.status"
    | "exports.evertime.processingErrors"
    | "exports.evertime.processingErrorStatus"
    | "exports.evertime.lockedAt"
    | "exports.evertime.completedAt"
    | "exports.evertime"
    | "exports.timesheetportal.status"
    | "exports.timesheetportal.processingErrors"
    | "exports.timesheetportal.processingErrorStatus"
    | "exports.timesheetportal.retryAt"
    | "exports.timesheetportal.lockedAt"
    | "exports.timesheetportal.completedAt"
    | "exports.timesheetportal"
    | "exports"
    | "candidates"
    | "estimatedPaymentDate"
    | "customer"
    | "runTotal"
    | "enabled"
    | "createdAt"
    | ""
  )[];
};

export type GetPaymentRunError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetPaymentRunResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the payment run
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * Processing state and results for each payment run export target
     */
    exports?: {
      xlsx?: {
        status?: "pending" | "processing" | "complete" | "failed";
        /**
         * @maxLength 1000
         */
        processingError?: string;
      };
      evertime?: {
        status?:
          | "pending"
          | "consultant_discovery"
          | "consultant_confirmation"
          | "consultant_processing"
          | "candidate_discovery"
          | "candidate_confirmation"
          | "candidate_processing"
          | "client_discovery"
          | "client_confirmation"
          | "client_processing"
          | "placement_discovery"
          | "placement_confirmation"
          | "placement_processing"
          | "timesheet_processing"
          | "complete"
          | "failed";
        /**
         * @maxItems 5000
         */
        processingErrors?: {
          /**
           * The type of record that failed during the export
           */
          entityType?:
            | "export"
            | "consultant"
            | "administrator"
            | "rate"
            | "candidate"
            | "contractor"
            | "client"
            | "approver"
            | "placement"
            | "job"
            | "timesheet";
          /**
           * The Edun ID of the record that failed, when available
           *
           * @maxLength 256
           */
          entityId?: string;
          /**
           * The company-defined identifier of the record, when available
           *
           * @maxLength 256
           */
          customId?: string;
          /**
           * The reason the record failed to export
           *
           * @maxLength 1000
           */
          message?: string;
        }[];
        /**
         * The Evertime export stage at which the last failure occurred
         */
        processingErrorStatus?:
          | "pending"
          | "consultant_discovery"
          | "consultant_confirmation"
          | "consultant_processing"
          | "candidate_discovery"
          | "candidate_confirmation"
          | "candidate_processing"
          | "client_discovery"
          | "client_confirmation"
          | "client_processing"
          | "placement_discovery"
          | "placement_confirmation"
          | "placement_processing"
          | "timesheet_processing"
          | "complete"
          | "failed";
        /**
         * @format date-time
         */
        lockedAt?: string;
        /**
         * When the Evertime export last completed successfully
         *
         * @format date-time
         */
        completedAt?: string;
      };
      timesheetportal?: {
        status?:
          | "pending"
          | "admin_discovery"
          | "admin_confirmation"
          | "admin_processing"
          | "rate_discovery"
          | "rate_confirmation"
          | "rate_processing"
          | "client_discovery"
          | "client_confirmation"
          | "client_processing"
          | "contractor_discovery"
          | "contractor_confirmation"
          | "contractor_processing"
          | "approver_discovery"
          | "approver_confirmation"
          | "approver_processing"
          | "job_discovery"
          | "job_confirmation"
          | "job_processing"
          | "timesheet_processing"
          | "complete"
          | "failed";
        /**
         * @maxItems 5000
         */
        processingErrors?: {
          /**
           * The type of record that failed during the export
           */
          entityType?:
            | "export"
            | "consultant"
            | "administrator"
            | "rate"
            | "candidate"
            | "contractor"
            | "client"
            | "approver"
            | "placement"
            | "job"
            | "timesheet";
          /**
           * The Edun ID of the record that failed, when available
           *
           * @maxLength 256
           */
          entityId?: string;
          /**
           * The company-defined identifier of the record, when available
           *
           * @maxLength 256
           */
          customId?: string;
          /**
           * The reason the record failed to export
           *
           * @maxLength 1000
           */
          message?: string;
        }[];
        /**
         * The Timesheet Portal export stage at which the last failure occurred
         */
        processingErrorStatus?:
          | "pending"
          | "admin_discovery"
          | "admin_confirmation"
          | "admin_processing"
          | "rate_discovery"
          | "rate_confirmation"
          | "rate_processing"
          | "client_discovery"
          | "client_confirmation"
          | "client_processing"
          | "contractor_discovery"
          | "contractor_confirmation"
          | "contractor_processing"
          | "approver_discovery"
          | "approver_confirmation"
          | "approver_processing"
          | "job_discovery"
          | "job_confirmation"
          | "job_processing"
          | "timesheet_processing"
          | "complete"
          | "failed";
        /**
         * When a rate-limited Timesheet Portal export can next be attempted
         *
         * @format date-time
         */
        retryAt?: string;
        /**
         * @format date-time
         */
        lockedAt?: string;
        /**
         * When the Timesheet Portal export last completed successfully
         *
         * @format date-time
         */
        completedAt?: string;
      };
    };
    candidates?: {
      /**
       * Unique ID for the candidate
       *
       * @pattern ^[0-9a-f]{24}$
       */
      candidateId?: string;
      /**
       * Full name of the candidate
       */
      candidateName?: string;
      /**
       * Detailed candidate information for payroll export
       */
      candidateDetails?: {
        /**
         * The title of the candidate.
         */
        title?:
          | "Mr"
          | "Mrs"
          | "Ms"
          | "Miss"
          | "Dr"
          | "Mx"
          | "Lady"
          | "Lord"
          | "Other";
        /**
         * The forename of the candidate
         *
         * @minLength 1
         * @maxLength 256
         */
        forename?: string;
        /**
         * The surname of the candidate
         *
         * @minLength 1
         * @maxLength 256
         */
        surname?: string;
        /**
         * The sex of the candidate
         */
        sex?: "Male" | "Female" | "Other" | "Unknown";
        /**
         * The date of birth of the candidate
         *
         * @format date-time
         */
        dob?: string | null;
        /**
         * The email of the candidate. This will be used for communication.
         *
         * @format email
         */
        email?: string;
        /**
         * The telephone number of the candidate
         *
         * @maxLength 32
         */
        telephone?: string;
        /**
         * The mobile number of the candidate
         *
         * @maxLength 32
         */
        mobile?: string;
      };
      /**
       * Unique ID for the associated timesheet
       *
       * @pattern ^[0-9a-f]{24}$
       */
      timesheetId?: string;
      /**
       * List of timesheets linked to this payment run
       */
      timesheets?: {
        /**
         * The unique identifier of the timesheet
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The name of the candidate on this timesheet
         *
         * @maxLength 256
         */
        candidateName?: string;
        /**
         * Reference to the candidate
         *
         * @pattern ^[0-9a-f]{24}$
         */
        candidate?: string;
        /**
         * The name of the client on this timesheet
         *
         * @maxLength 256
         */
        clientName?: string;
        /**
         * The email of the client contact
         *
         * @maxLength 256
         */
        clientEmail?: string;
        /**
         * Reference to the client
         *
         * @pattern ^[0-9a-f]{24}$
         */
        client?: string;
        /**
         * Reference to the booking
         *
         * @pattern ^[0-9a-f]{24}$
         */
        booking?: string;
        /**
         * The booking days covered by this timesheet
         */
        days?: {
          /**
           * The start date and time for the booking day
           */
          start?: string;
          /**
           * The end date and time for the booking day
           */
          end?: string;
          /**
           * The length of the booking day
           */
          length?: "Half Day" | "Full Day";
          /**
           * Indicates whether the booking day is in the AM or PM
           */
          amPm?: "AM" | "PM";
          /**
           * The current status of the booking day
           */
          status?:
            | "Active"
            | "Sickness"
            | "NoShow"
            | "Client Cancelled"
            | "Act Of God"
            | "Personal Appointment";
          /**
           * The charge rate for the booking day
           *
           * @maxLength 32
           */
          chargeRate?: string;
          /**
           * The pay rate for the booking day
           *
           * @maxLength 32
           */
          payRate?: string;
          /**
           * Break duration inherited for this booking day.
           */
          breaks?: string;
          /**
           * Expenses payable for this booking day.
           */
          expenses?: string;
          /**
           * Indicates whether the booking day is flagged for removal
           */
          flaggedForRemoval?: boolean;
        }[];
        /**
         * Client approval status
         */
        status?: "Pending" | "Approved" | "Rejected";
        /**
         * When the client status was last changed
         */
        statusChangedOn?: string;
        /**
         * Who changed the client status
         */
        statusChangeBy?: string;
        /**
         * Job role of the person who changed the client status
         */
        statusChangeByJobRole?: string;
        /**
         * Typed signature of the client approver
         */
        statusChangeBySignature?: string;
        /**
         * Base64-encoded drawn signature of the client approver
         */
        statusChangeByDrawnSignature?: string;
        /**
         * When the client signature was provided
         *
         * @format date-time
         */
        statusChangeBySignatureDate?: string;
        /**
         * Reason for client rejection
         */
        rejectionReason?: string;
        /**
         * Candidate approval status
         */
        statusCandidate?: "Pending" | "Approved" | "Rejected";
        /**
         * When the candidate status was last changed
         */
        statusChangedOnCandidate?: string;
        /**
         * Who changed the candidate status
         */
        statusChangeByCandidate?: string;
        /**
         * Job role of the person who changed the candidate status
         */
        statusChangeByJobRoleCandidate?: string;
        /**
         * Typed signature of the candidate approver
         */
        statusChangeByCandidateSignature?: string;
        /**
         * Base64-encoded drawn signature of the candidate approver
         */
        statusChangeByCandidateDrawnSignature?: string;
        /**
         * When the candidate signature was provided
         *
         * @format date-time
         */
        statusChangeByCandidateSignatureDate?: string;
        /**
         * Reason for candidate rejection
         */
        rejectionReasonCandidate?: string;
        /**
         * Who created the timesheet
         */
        createdBy?: string;
        /**
         * Timesheet group identifier for linked timesheets
         */
        group?: string;
        /**
         * Whether to resend the timesheet approval email
         */
        resendTimesheetEmail?: boolean;
        /**
         * Reference to the customer/company
         *
         * @pattern ^[0-9a-f]{24}$
         */
        customer?: string;
        /**
         * Evertime details for this timesheet
         */
        evertime?: {
          /**
           * Timesheet identifier returned by Evertime
           *
           * @minimum 0
           * @exclusiveMinimum true
           */
          id?: number;
        };
        /**
         * Timesheet Portal details for this timesheet
         */
        timesheetPortal?: {
          /**
           * The Timesheet Portal timesheet GUID
           *
           * @format uuid
           */
          id?: string;
        };
        /**
         * Reference to the assigned user
         *
         * @pattern ^[0-9a-f]{24}$
         */
        assignee?: string;
        /**
         * Reference to the query response
         *
         * @pattern ^[0-9a-f]{24}$
         */
        queryResponse?: string | null;
        /**
         * The invoice number if invoiced
         */
        invoiceNumber?: string;
        /**
         * The unique timesheet number
         */
        timesheetNumber?: string;
        /**
         * The type of booking
         */
        type?:
          | "Standard"
          | "Interview"
          | "Trial"
          | "Perm"
          | "Tutor"
          | any
          | null;
        /**
         * The sub-type of booking
         */
        subType?:
          | "TutorStandard"
          | "TutorMentor"
          | "TutorBasic"
          | "FaceToFace"
          | any
          | null;
        /**
         * Previously submitted tuition report data. This compatibility field may vary by the report form; use tuitionReportAnswers for structured answers when available.
         */
        tuitionReport?: {
          [key: string]: void | null;
        } | null;
        /**
         * Dynamic tuition report answers keyed by question ID
         */
        tuitionReportAnswers?: {
          /**
           * The identifier of the question being answered.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          questionId?: string;
          /**
           * The stable question key. Older consumers may omit it when a question ID is supplied.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * The submitted answer to the question.
           */
          value?: string | string | boolean | number | (void | null);
        }[];
        /**
         * Whether the tuition report is still pending submission
         */
        tuitionReportPending?: boolean;
        /**
         * The role associated with this timesheet
         */
        role?: string | null;
        /**
         * IP address of the client when approving
         */
        statusApprovalIp?: string;
        /**
         * IP address of the candidate when approving
         */
        statusApprovalCandidateIp?: string;
        /**
         * Boolean flag indicating whether this timesheet is locked by a payment run
         */
        lockedViaPaymentRun?: boolean;
      }[];
      /**
       * Primary date associated with the timesheet
       */
      timesheetDate?: string;
      /**
       * Number for the timesheet
       */
      timesheetNumber?: string;
      /**
       * Start date of the timesheet period
       */
      timesheetStart?: string;
      /**
       * End date of the timesheet period
       */
      timesheetEnd?: string;
      /**
       * Last working day within the timesheet
       */
      timesheetLastDay?: string;
      /**
       * Person who approved the timesheet
       */
      timesheetApprover?: string;
      /**
       * Last Sunday within the timesheet
       */
      timesheetLastSunday?: string;
      /**
       * Name of the student associated with the timesheet if applicable
       */
      timesheetStudentName?: string;
      /**
       * Custom client ID
       */
      customClientId?: string;
      /**
       * The company-defined client identifier included in payment exports.
       */
      customClientInternalId?: string;
      /**
       * Name of the client
       */
      clientName?: string;
      /**
       * Unique ID for the client
       *
       * @pattern ^[0-9a-f]{24}$
       */
      clientId?: string;
      /**
       * Custom candidate ID
       */
      customCandidateId?: string;
      /**
       * The company-defined candidate identifier included in payment exports.
       */
      customCandidateInternalId?: string;
      /**
       * The company-defined pay type code included for the candidate in payment exports.
       */
      customCandidateIdPayType?: string;
      /**
       * Primary contact at the client organisation
       */
      clientContact?: string;
      /**
       * Contact responsible for invoicing
       */
      invoiceContact?: string;
      /**
       * Total payable amount
       */
      total?: number;
      /**
       * End date of the payroll period
       */
      peDate?: string;
      /**
       * Pay type associated with the booking
       */
      bookingPayType?: string;
      /**
       * Candidate pay rate for the booking
       */
      payRate?: string;
      /**
       * Rate charged to the client for the candidate work
       */
      chargeRate?: string;
      /**
       * Number of units used for billing
       */
      units?: string;
      /**
       * Total hours worked in the timesheet period
       */
      hours?: string;
      /**
       * Breakdown of hours worked per day
       */
      hoursPerDay?: string;
      /**
       * Consultant responsible for the booking
       */
      consultant?: string;
      /**
       * Job role performed by the candidate
       */
      role?: string;
      /**
       * Status of the timesheet as recorded by the client
       */
      clientTimesheetStatus?: string;
      /**
       * List of individual days included in the booking
       */
      days?: {
        /**
         * The start date and time for the booking day
         */
        start?: string;
        /**
         * The end date and time for the booking day
         */
        end?: string;
        /**
         * The length of the booking day
         */
        length?: "Half Day" | "Full Day";
        /**
         * Indicates whether the booking day is in the AM or PM
         */
        amPm?: "AM" | "PM";
        /**
         * The current status of the booking day
         */
        status?:
          | "Active"
          | "Sickness"
          | "NoShow"
          | "Client Cancelled"
          | "Act Of God"
          | "Personal Appointment";
        /**
         * The charge rate for the booking day
         *
         * @maxLength 32
         */
        chargeRate?: string;
        /**
         * The pay rate for the booking day
         *
         * @maxLength 32
         */
        payRate?: string;
        /**
         * Break duration inherited for this booking day.
         */
        breaks?: string;
        /**
         * Expenses payable for this booking day.
         */
        expenses?: string;
        /**
         * Indicates whether the booking day is flagged for removal
         */
        flaggedForRemoval?: boolean;
      }[];
      /**
       * Indicates whether this is the candidate first booking
       */
      isCandidateFirstBooking?: boolean;
      /**
       * Indicates whether this is the candidate first booking with this client
       */
      isCandidateFirstBookingWithClient?: boolean;
      /**
       * Indicates whether this is the client first booking with the agency
       */
      isClientFirstBooking?: boolean;
      /**
       * ID for the first consultant involved in the split
       */
      consultant1Id?: string;
      /**
       * Percentage allocation for consultant 1
       */
      consultant1Perc?: number;
      /**
       * ID for the second consultant involved in the split
       */
      consultant2Id?: string;
      /**
       * Percentage allocation for consultant 2
       */
      consultant2Perc?: number;
      /**
       * ID for the third consultant involved in the split
       */
      consultant3Id?: string;
      /**
       * Percentage allocation for consultant 3
       */
      consultant3Perc?: number;
      /**
       * ID for the fourth consultant involved in the split
       */
      consultant4Id?: string;
      /**
       * Percentage allocation for consultant 4
       */
      consultant4Perc?: number;
      /**
       * ID for the fifth consultant involved in the split
       */
      consultant5Id?: string;
      /**
       * Percentage allocation for consultant 5
       */
      consultant5Perc?: number;
      /**
       * ID for the placement
       */
      placementId?: string;
      /**
       * Office reference number associated with the booking
       */
      officeNumber?: number;
      /**
       * PO number provided
       */
      poNumber?: string;
      /**
       * Frequency of pay
       */
      payInterval?: string;
      /**
       * An opaque reference for this payment entry. Do not construct or modify this value.
       */
      _internalId?: string;
      /**
       * Pay type assigned to the candidate
       */
      candidatePayType?: string;
      /**
       * Name of the umbrella company used
       */
      umbrellaName?: string;
      /**
       * Code for the umbrella company if applicable
       */
      umbrellaCode?: string;
      /**
       * Unique ID for the umbrella company
       */
      umbrellaId?: string;
      /**
       * Registered address of the umbrella company
       */
      umbrellaAddress?: {
        /**
         * The first line of the address
         *
         * @maxLength 256
         */
        addressLine1?: string;
        /**
         * The second line of the address
         *
         * @maxLength 256
         */
        addressLine2?: string;
        /**
         * The third line of the address
         *
         * @maxLength 256
         */
        addressLine3?: string;
        /**
         * The city of the address
         *
         * @maxLength 256
         */
        city?: string;
        /**
         * The county of the address
         *
         * @maxLength 256
         */
        county?: string;
        /**
         * The borough of the address
         *
         * @maxLength 256
         */
        borough?: string;
        /**
         * The postcode of the address
         *
         * @maxLength 10
         */
        postcode?: string;
        /**
         * The country of the address
         *
         * @maxLength 256
         */
        country?: string;
        /**
         * The latitude of the address
         *
         * @minimum -90
         * @maximum 90
         */
        lat?: number;
        /**
         * The longitude of the address
         *
         * @minimum -180
         * @maximum 180
         */
        lng?: number;
      };
      /**
       * Start date of the candidate booking
       */
      bookingStartDate?: string;
      /**
       * Branch codes associated with the assignment
       */
      assigneeBranchCodes?: string;
      /**
       * Candidate National Insurance number, trimmed
       */
      niNumberTrimmed?: string;
      /**
       * Candidate bank sort code, trimmed
       */
      sortCodeTrimmed?: string;
      /**
       * Candidate bank account number, trimmed
       */
      accountNumberTrimmed?: string;
      /**
       * Name on the candidate bank account, trimmed
       */
      accountNameTrimmed?: string;
      /**
       * Additional costs associated with the booking
       */
      bookingOnCosts?: number;
      /**
       * Full bank details for candidate payment
       */
      bankDetails?: {
        /**
         * The name on the bank account
         */
        bankAccountName?: string;
        /**
         * The name of the bank
         */
        bankName?: string;
        /**
         * The account number
         */
        accountNumber?: string;
        /**
         * The sort code
         */
        sortCode?: string;
        /**
         * The National Insurance number
         */
        niNumber?: string;
        /**
         * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
         */
        jobOp1?: boolean;
        /**
         * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
         */
        jobOp2?: boolean;
        /**
         * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
         */
        jobOp3?: boolean;
        studentLoan?: {
          /**
           * Boolean flag for if candidate has student loan
           */
          studentLoan?: boolean;
          /**
           * Boolean flag for if candidate is on plan 1
           */
          plan1?: boolean;
          /**
           * Boolean flag for if candidate is on plan 2
           */
          plan2?: boolean;
          /**
           * Boolean flag for if candidate is on plan 4
           */
          plan4?: boolean;
          /**
           * Boolean flag for if candidate has postgrad loan
           */
          postGradLoan?: boolean;
        };
      };
      /**
       * Candidate pay rate excluding holiday pay
       */
      payRateExclusiveHoliday?: string;
      /**
       * Holiday pay component of the candidate rate
       */
      payRateHolidayPay?: string;
      /**
       * PAYE pay rate for the candidate
       */
      payeRate?: string;
      /**
       * Margin applied to PAYE candidates
       */
      payeMargin?: string;
      /**
       * Weekly margin applied to PAYE candidates
       */
      payeWeeklyMargin?: string;
      /**
       * Total gross margin for the booking
       */
      grossMargin?: string;
      /**
       * Total amount charged to the client
       */
      totalCharge?: number;
    }[];
    estimatedPaymentDate?: string;
    /**
     * The unique identifier of the resource
     *
     * @pattern ^[0-9a-f]{24}$
     */
    customer?: string;
    runTotal?: number;
    enabled?: boolean;
    /**
     * @format date-time
     */
    createdAt?: string;
  };
};

export type GetPaymentRunVariables = {
  pathParams: GetPaymentRunPathParams;
  queryParams?: GetPaymentRunQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetPaymentRun = (
  variables: GetPaymentRunVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetPaymentRunResponse,
    GetPaymentRunError,
    undefined,
    {},
    GetPaymentRunQueryParams,
    GetPaymentRunPathParams
  >({
    url: "/payment-runs/{paymentRunId}",
    method: "get",
    ...variables,
    signal,
  });

export function getPaymentRunQuery(variables: GetPaymentRunVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetPaymentRunResponse>;
};

export function getPaymentRunQuery(
  variables: GetPaymentRunVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetPaymentRunResponse>)
    | reactQuery.SkipToken;
};

export function getPaymentRunQuery(
  variables: GetPaymentRunVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/payment-runs/{paymentRunId}",
      operationId: "getPaymentRun",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetPaymentRun(variables, signal),
  };
}

export const useSuspenseGetPaymentRun = <TData = GetPaymentRunResponse,>(
  variables: GetPaymentRunVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetPaymentRunResponse,
      GetPaymentRunError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetPaymentRunResponse,
    GetPaymentRunError,
    TData
  >({
    ...getPaymentRunQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetPaymentRun = <TData = GetPaymentRunResponse,>(
  variables: GetPaymentRunVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetPaymentRunResponse,
      GetPaymentRunError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetPaymentRunResponse, GetPaymentRunError, TData>({
    ...getPaymentRunQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type DeletePaymentRunPathParams = {
  /**
   * The payment run identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  paymentRunId: string;
};

export type DeletePaymentRunError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type DeletePaymentRunVariables = {
  pathParams: DeletePaymentRunPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchDeletePaymentRun = (
  variables: DeletePaymentRunVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    DeletePaymentRunError,
    undefined,
    {},
    {},
    DeletePaymentRunPathParams
  >({
    url: "/payment-runs/{paymentRunId}",
    method: "delete",
    ...variables,
    signal,
  });

export const useDeletePaymentRun = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      DeletePaymentRunError,
      DeletePaymentRunVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    DeletePaymentRunError,
    DeletePaymentRunVariables
  >({
    mutationFn: (variables: DeletePaymentRunVariables) =>
      fetchDeletePaymentRun(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type ExportPaymentRunPathParams = {
  /**
   * The payment run identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  paymentRunId: string;
};

export type ExportPaymentRunError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 409;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 501;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type ExportPaymentRunResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The payment run queued for export
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The export format for the payment run
     */
    target?: "xlsx" | "evertime" | "timesheetportal";
    /**
     * The processing status of the queued export
     */
    status?:
      | ("pending" | "processing" | "complete" | "failed")
      | (
          | "pending"
          | "consultant_discovery"
          | "consultant_confirmation"
          | "consultant_processing"
          | "candidate_discovery"
          | "candidate_confirmation"
          | "candidate_processing"
          | "client_discovery"
          | "client_confirmation"
          | "client_processing"
          | "placement_discovery"
          | "placement_confirmation"
          | "placement_processing"
          | "timesheet_processing"
          | "complete"
          | "failed"
        )
      | (
          | "pending"
          | "admin_discovery"
          | "admin_confirmation"
          | "admin_processing"
          | "rate_discovery"
          | "rate_confirmation"
          | "rate_processing"
          | "client_discovery"
          | "client_confirmation"
          | "client_processing"
          | "contractor_discovery"
          | "contractor_confirmation"
          | "contractor_processing"
          | "approver_discovery"
          | "approver_confirmation"
          | "approver_processing"
          | "job_discovery"
          | "job_confirmation"
          | "job_processing"
          | "timesheet_processing"
          | "complete"
          | "failed"
        );
  };
};

export type ExportPaymentRunRequestBody = {
  /**
   * The export format for the payment run
   */
  target: "xlsx" | "evertime" | "timesheetportal";
};

export type ExportPaymentRunVariables = {
  body: ExportPaymentRunRequestBody;
  pathParams: ExportPaymentRunPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchExportPaymentRun = (
  variables: ExportPaymentRunVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    ExportPaymentRunResponse,
    ExportPaymentRunError,
    ExportPaymentRunRequestBody,
    {},
    {},
    ExportPaymentRunPathParams
  >({
    url: "/payment-runs/{paymentRunId}/exports",
    method: "post",
    ...variables,
    signal,
  });

export const useExportPaymentRun = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      ExportPaymentRunResponse,
      ExportPaymentRunError,
      ExportPaymentRunVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    ExportPaymentRunResponse,
    ExportPaymentRunError,
    ExportPaymentRunVariables
  >({
    mutationFn: (variables: ExportPaymentRunVariables) =>
      fetchExportPaymentRun(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type RetryPaymentRunExportPathParams = {
  /**
   * The payment run identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  paymentRunId: string;
  /**
   * The payment run export target
   */
  target: "xlsx" | "evertime" | "timesheetportal";
};

export type RetryPaymentRunExportError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 409;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 501;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type RetryPaymentRunExportResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The payment run queued for export
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The export format for the payment run
     */
    target?: "xlsx" | "evertime" | "timesheetportal";
    /**
     * The processing status of the queued export
     */
    status?:
      | ("pending" | "processing" | "complete" | "failed")
      | (
          | "pending"
          | "consultant_discovery"
          | "consultant_confirmation"
          | "consultant_processing"
          | "candidate_discovery"
          | "candidate_confirmation"
          | "candidate_processing"
          | "client_discovery"
          | "client_confirmation"
          | "client_processing"
          | "placement_discovery"
          | "placement_confirmation"
          | "placement_processing"
          | "timesheet_processing"
          | "complete"
          | "failed"
        )
      | (
          | "pending"
          | "admin_discovery"
          | "admin_confirmation"
          | "admin_processing"
          | "rate_discovery"
          | "rate_confirmation"
          | "rate_processing"
          | "client_discovery"
          | "client_confirmation"
          | "client_processing"
          | "contractor_discovery"
          | "contractor_confirmation"
          | "contractor_processing"
          | "approver_discovery"
          | "approver_confirmation"
          | "approver_processing"
          | "job_discovery"
          | "job_confirmation"
          | "job_processing"
          | "timesheet_processing"
          | "complete"
          | "failed"
        );
  };
};

export type RetryPaymentRunExportVariables = {
  pathParams: RetryPaymentRunExportPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchRetryPaymentRunExport = (
  variables: RetryPaymentRunExportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    RetryPaymentRunExportResponse,
    RetryPaymentRunExportError,
    undefined,
    {},
    {},
    RetryPaymentRunExportPathParams
  >({
    url: "/payment-runs/{paymentRunId}/exports/{target}/retries",
    method: "post",
    ...variables,
    signal,
  });

export const useRetryPaymentRunExport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      RetryPaymentRunExportResponse,
      RetryPaymentRunExportError,
      RetryPaymentRunExportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    RetryPaymentRunExportResponse,
    RetryPaymentRunExportError,
    RetryPaymentRunExportVariables
  >({
    mutationFn: (variables: RetryPaymentRunExportVariables) =>
      fetchRetryPaymentRunExport(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetPaymentRunExportConfirmationPathParams = {
  /**
   * The payment run identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  paymentRunId: string;
  /**
   * The payment run export target being confirmed
   */
  target: "evertime" | "timesheetportal";
};

export type GetPaymentRunExportConfirmationError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 501;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetPaymentRunExportConfirmationResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data:
    | {
        /**
         * The payment run export target being confirmed
         */
        target?: "evertime";
        /**
         * The stage currently awaiting confirmation, or null if none
         */
        stage?:
          | "consultant"
          | "candidate"
          | "client"
          | "placement"
          | any
          | null;
        /**
         * The entities awaiting confirmation for the current stage
         *
         * @maxItems 1000
         */
        entities?: {
          /**
           * Stable identifier for the entity (consultant custom id, candidate id, client id, or booking id)
           *
           * @maxLength 256
           */
          key?: string;
          /**
           * Display name for the entity
           *
           * @maxLength 512
           */
          name?: string;
          /**
           * The discovered or confirmed Evertime ID, if any
           *
           * @maxLength 256
           */
          evertimeId?: string;
          /**
           * Whether the matched Evertime candidate's NI number also matches Edun (candidates only)
           */
          niNumberMatch?: boolean;
          /**
           * Whether the matched Evertime client's name also matches Edun (clients only)
           */
          nameMatch?: boolean;
          /**
           * The client's custom id, used as the base for generated contact/location ids (clients only)
           *
           * @maxLength 256
           */
          customClientId?: string;
          /**
           * Discovered Evertime contact IDs (clients only)
           *
           * @maxItems 50
           */
          evertimeContactIds?: string[];
          /**
           * Discovered Evertime location IDs (clients only)
           *
           * @maxItems 50
           */
          evertimeLocationIds?: string[];
          /**
           * The client's contacts, each creatable as an Evertime contact when the client is created (clients only)
           *
           * @maxItems 50
           */
          clientContacts?: {
            /**
             * The Edun client contact id
             *
             * @maxLength 256
             */
            id?: string;
            /**
             * The client contact's display name
             *
             * @maxLength 512
             */
            name?: string;
            /**
             * The client contact's role, if set
             *
             * @maxLength 256
             */
            role?: string;
          }[];
        }[];
      }
    | {
        /**
         * The payment run export target being confirmed
         */
        target?: "timesheetportal";
        /**
         * The stage currently awaiting confirmation, or null if none
         */
        stage?:
          | "admin"
          | "rate"
          | "client"
          | "contractor"
          | "approver"
          | "job"
          | any
          | null;
        /**
         * The entities awaiting confirmation for the current stage
         *
         * @maxItems 1000
         */
        entities?: {
          /**
           * Stable Edun identifier for the entity
           *
           * @maxLength 256
           */
          key?: string;
          /**
           * Display name for the entity
           *
           * @maxLength 512
           */
          name?: string;
          /**
           * The discovered or confirmed Timesheet Portal GUID
           *
           * @format uuid
           */
          timesheetPortalId?: string;
          /**
           * The discovered or confirmed Timesheet Portal code
           *
           * @maxLength 256
           */
          timesheetPortalCode?: string;
          /**
           * The field used to discover the Timesheet Portal record
           */
          matchedBy?: "email" | "code" | "name";
        }[];
      };
};

export type GetPaymentRunExportConfirmationVariables = {
  pathParams: GetPaymentRunExportConfirmationPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetPaymentRunExportConfirmation = (
  variables: GetPaymentRunExportConfirmationVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetPaymentRunExportConfirmationResponse,
    GetPaymentRunExportConfirmationError,
    undefined,
    {},
    {},
    GetPaymentRunExportConfirmationPathParams
  >({
    url: "/payment-runs/{paymentRunId}/exports/{target}/confirmations",
    method: "get",
    ...variables,
    signal,
  });

export function getPaymentRunExportConfirmationQuery(
  variables: GetPaymentRunExportConfirmationVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (
    options: QueryFnOptions,
  ) => Promise<GetPaymentRunExportConfirmationResponse>;
};

export function getPaymentRunExportConfirmationQuery(
  variables: GetPaymentRunExportConfirmationVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((
        options: QueryFnOptions,
      ) => Promise<GetPaymentRunExportConfirmationResponse>)
    | reactQuery.SkipToken;
};

export function getPaymentRunExportConfirmationQuery(
  variables: GetPaymentRunExportConfirmationVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/payment-runs/{paymentRunId}/exports/{target}/confirmations",
      operationId: "getPaymentRunExportConfirmation",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetPaymentRunExportConfirmation(variables, signal),
  };
}

export const useSuspenseGetPaymentRunExportConfirmation = <
  TData = GetPaymentRunExportConfirmationResponse,
>(
  variables: GetPaymentRunExportConfirmationVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetPaymentRunExportConfirmationResponse,
      GetPaymentRunExportConfirmationError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetPaymentRunExportConfirmationResponse,
    GetPaymentRunExportConfirmationError,
    TData
  >({
    ...getPaymentRunExportConfirmationQuery(
      deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export const useGetPaymentRunExportConfirmation = <
  TData = GetPaymentRunExportConfirmationResponse,
>(
  variables: GetPaymentRunExportConfirmationVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetPaymentRunExportConfirmationResponse,
      GetPaymentRunExportConfirmationError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetPaymentRunExportConfirmationResponse,
    GetPaymentRunExportConfirmationError,
    TData
  >({
    ...getPaymentRunExportConfirmationQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type ConfirmPaymentRunExportStagePathParams = {
  /**
   * The payment run identifier
   *
   * @pattern ^[0-9a-f]{24}$
   */
  paymentRunId: string;
  /**
   * The payment run export target being confirmed
   */
  target: "evertime" | "timesheetportal";
};

export type ConfirmPaymentRunExportStageError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 409;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 501;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type ConfirmPaymentRunExportStageResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data:
    | {
        /**
         * The confirmed payment run export target
         */
        target?: "evertime";
        /**
         * The payment run that was advanced
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The stage that was confirmed
         */
        stage?: "consultant" | "candidate" | "client" | "placement";
        /**
         * The new Evertime export status after confirmation
         */
        status?:
          | "pending"
          | "consultant_discovery"
          | "consultant_confirmation"
          | "consultant_processing"
          | "candidate_discovery"
          | "candidate_confirmation"
          | "candidate_processing"
          | "client_discovery"
          | "client_confirmation"
          | "client_processing"
          | "placement_discovery"
          | "placement_confirmation"
          | "placement_processing"
          | "timesheet_processing"
          | "complete"
          | "failed";
      }
    | {
        /**
         * The confirmed payment run export target
         */
        target?: "timesheetportal";
        /**
         * The payment run that was advanced
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * The stage that was confirmed
         */
        stage?: "admin" | "rate" | "client" | "contractor" | "approver" | "job";
        /**
         * The new Timesheet Portal export status after confirmation
         */
        status?:
          | "pending"
          | "admin_discovery"
          | "admin_confirmation"
          | "admin_processing"
          | "rate_discovery"
          | "rate_confirmation"
          | "rate_processing"
          | "client_discovery"
          | "client_confirmation"
          | "client_processing"
          | "contractor_discovery"
          | "contractor_confirmation"
          | "contractor_processing"
          | "approver_discovery"
          | "approver_confirmation"
          | "approver_processing"
          | "job_discovery"
          | "job_confirmation"
          | "job_processing"
          | "timesheet_processing"
          | "complete"
          | "failed";
      };
};

export type ConfirmPaymentRunExportStageRequestBody = {
  stage:
    | ("consultant" | "candidate" | "client" | "placement")
    | ("admin" | "rate" | "client" | "contractor" | "approver" | "job");
  /**
   * @maxItems 1000
   */
  entries: {
    /**
     * The entity identifier being confirmed
     *
     * @maxLength 256
     */
    key: string;
    /**
     * Whether to create a new Evertime record instead of using an existing ID
     */
    createNew: boolean;
    /**
     * The Evertime ID to use when not creating a new record
     *
     * @maxLength 256
     */
    evertimeId?: string;
    /**
     * The client's Evertime contacts to use or create (clients only)
     *
     * @maxItems 50
     */
    evertimeContacts?: {
      /**
       * An existing Evertime contact ID to use
       *
       * @maxLength 256
       */
      evertimeId?: string;
      /**
       * Create the contact from the linked client contact; when false the Evertime contact is only referenced
       */
      createNew: boolean;
      /**
       * The Edun client contact to create this Evertime contact from (required when createNew)
       *
       * @maxLength 256
       */
      clientContactId?: string;
    }[];
    /**
     * The client's Evertime locations to use or create (clients only)
     *
     * @maxItems 50
     */
    evertimeLocations?: {
      /**
       * An existing Evertime location ID to use
       *
       * @maxLength 256
       */
      evertimeId?: string;
      /**
       * Create the location from the client's address; when false the Evertime location is only referenced
       */
      createNew: boolean;
    }[];
    /**
     * The Timesheet Portal GUID to use when not creating a new record
     *
     * @format uuid
     */
    timesheetPortalId?: string;
    /**
     * The Timesheet Portal code to use when linking or creating the record
     *
     * @maxLength 256
     */
    timesheetPortalCode?: string;
  }[];
};

export type ConfirmPaymentRunExportStageVariables = {
  body: ConfirmPaymentRunExportStageRequestBody;
  pathParams: ConfirmPaymentRunExportStagePathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchConfirmPaymentRunExportStage = (
  variables: ConfirmPaymentRunExportStageVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    ConfirmPaymentRunExportStageResponse,
    ConfirmPaymentRunExportStageError,
    ConfirmPaymentRunExportStageRequestBody,
    {},
    {},
    ConfirmPaymentRunExportStagePathParams
  >({
    url: "/payment-runs/{paymentRunId}/exports/{target}/confirmations",
    method: "post",
    ...variables,
    signal,
  });

export const useConfirmPaymentRunExportStage = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      ConfirmPaymentRunExportStageResponse,
      ConfirmPaymentRunExportStageError,
      ConfirmPaymentRunExportStageVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    ConfirmPaymentRunExportStageResponse,
    ConfirmPaymentRunExportStageError,
    ConfirmPaymentRunExportStageVariables
  >({
    mutationFn: (variables: ConfirmPaymentRunExportStageVariables) =>
      fetchConfirmPaymentRunExportStage(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetFormsQueryParams = {
  /**
   * The fields to include in each returned form.
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "title"
    | "description"
    | "confirmationMessage"
    | "status"
    | "questions"
    | "requestCount"
    | "submissionCount"
    | "createdBy"
    | "createdBy.id"
    | "createdBy.email"
    | "createdBy.firstName"
    | "createdBy.lastName"
    | "createdBy.username"
    | "createdBy.lea"
    | "createdBy.clients"
    | "createdBy.position"
    | "createdBy.permissions"
    | "createdBy.activationCode"
    | "createdBy.customer"
    | "createdBy.disabled"
    | "createdBy.commissionThreshold"
    | "createdBy.branches"
    | "createdBy.displayOnboarding"
    | "createdBy._customId"
    | "createdBy.signature"
    | "createdBy.phone"
    | "createdBy.defaultCandidateWatcher"
    | "createdBy.defaultTaskWorkspace"
    | "createdBy.teamAdmin"
    | "createdBy.contact"
    | "createdBy.contact.id"
    | "createdBy.contact.forename"
    | "createdBy.contact.surname"
    | "createdBy.contact.email"
    | "createdBy.contact.telephone"
    | "createdBy.contact.mobile"
    | "createdBy.contact.extension"
    | "createdBy.createdAt"
    | "createdBy.evertime.id"
    | "createdBy.evertime"
    | "createdBy.timesheetPortal.id"
    | "createdBy.timesheetPortal.code"
    | "createdBy.timesheetPortal"
    | "updatedBy"
    | "updatedBy.id"
    | "updatedBy.email"
    | "updatedBy.firstName"
    | "updatedBy.lastName"
    | "updatedBy.username"
    | "updatedBy.lea"
    | "updatedBy.clients"
    | "updatedBy.position"
    | "updatedBy.permissions"
    | "updatedBy.activationCode"
    | "updatedBy.customer"
    | "updatedBy.disabled"
    | "updatedBy.commissionThreshold"
    | "updatedBy.branches"
    | "updatedBy.displayOnboarding"
    | "updatedBy._customId"
    | "updatedBy.signature"
    | "updatedBy.phone"
    | "updatedBy.defaultCandidateWatcher"
    | "updatedBy.defaultTaskWorkspace"
    | "updatedBy.teamAdmin"
    | "updatedBy.contact"
    | "updatedBy.contact.id"
    | "updatedBy.contact.forename"
    | "updatedBy.contact.surname"
    | "updatedBy.contact.email"
    | "updatedBy.contact.telephone"
    | "updatedBy.contact.mobile"
    | "updatedBy.contact.extension"
    | "updatedBy.createdAt"
    | "updatedBy.evertime.id"
    | "updatedBy.evertime"
    | "updatedBy.timesheetPortal.id"
    | "updatedBy.timesheetPortal.code"
    | "updatedBy.timesheetPortal"
    | "publishedAt"
    | "createdAt"
    | "updatedAt"
    | ""
  )[];
  /**
   * Related resources to return as objects.
   *
   * @maxItems 2
   */
  expand?: ("createdBy" | "updatedBy")[];
  /**
   * Filters applied to the form collection.
   */
  filter?: {
    /**
     * Return forms whose title contains this text.
     *
     * @minLength 1
     * @maxLength 200
     */
    title?: string;
    /**
     * Return forms in any of the supplied lifecycle states.
     *
     * @maxItems 3
     */
    status?: ("draft" | "published" | "archived")[];
    /**
     * Return forms published within this absolute or relative date range.
     */
    publishedAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Return forms updated within this absolute or relative date range.
     */
    updatedAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
  };
  /**
   * The field and direction used to order forms.
   */
  sort?:
    | "title"
    | "-title"
    | "createdAt"
    | "-createdAt"
    | "updatedAt"
    | "-updatedAt";
  /**
   * Page number
   *
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
};

export type GetFormsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetFormsResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the form.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The internal and recipient-facing title of the form.
     *
     * @minLength 1
     * @maxLength 256
     */
    title?: string;
    /**
     * Optional introductory text shown before the questions.
     *
     * @maxLength 5000
     */
    description?: string | null;
    /**
     * Optional message shown after a response is submitted.
     *
     * @maxLength 2000
     */
    confirmationMessage?: string | null;
    /**
     * The lifecycle state of the form definition.
     */
    status?: "draft" | "published" | "archived";
    /**
     * The ordered questions included in the form.
     *
     * @maxItems 100
     */
    questions?: {
      /**
       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * A stable key used to associate the question with current and historical answers.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label?: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type?:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required?: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled?: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order?: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode?: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules?: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator?: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value?: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value?: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label?: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
      /**
       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
       */
      audience?: "candidate" | "staff";
      /**
       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
       */
      fixedRole?: "fullName" | "signature";
    }[];
    /**
     * The number of requests issued for this form.
     *
     * @minimum 0
     */
    requestCount?: number;
    /**
     * The number of responses submitted for this form.
     *
     * @minimum 0
     */
    submissionCount?: number;
    /**
     * The user who created the form. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The user who last updated the form. If expanded, a user object; otherwise, a user ID.
     */
    updatedBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * When the form was most recently published.
     *
     * @format date-time
     */
    publishedAt?: string | null;
    /**
     * When the form was created.
     *
     * @format date-time
     */
    createdAt?: string;
    /**
     * When the form was last updated.
     *
     * @format date-time
     */
    updatedAt?: string;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetFormsVariables = {
  queryParams?: GetFormsQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetForms = (
  variables: GetFormsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetFormsResponse,
    GetFormsError,
    undefined,
    {},
    GetFormsQueryParams,
    {}
  >({ url: "/forms", method: "get", ...variables, signal });

export function getFormsQuery(variables: GetFormsVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetFormsResponse>;
};

export function getFormsQuery(
  variables: GetFormsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetFormsResponse>)
    | reactQuery.SkipToken;
};

export function getFormsQuery(
  variables: GetFormsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/forms",
      operationId: "getForms",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetForms(variables, signal),
  };
}

export const useSuspenseGetForms = <TData = GetFormsResponse,>(
  variables: GetFormsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetFormsResponse, GetFormsError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<GetFormsResponse, GetFormsError, TData>({
    ...getFormsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetForms = <TData = GetFormsResponse,>(
  variables: GetFormsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetFormsResponse, GetFormsError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetFormsResponse, GetFormsError, TData>({
    ...getFormsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateFormError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateFormResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the form.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The internal and recipient-facing title of the form.
     *
     * @minLength 1
     * @maxLength 256
     */
    title?: string;
    /**
     * Optional introductory text shown before the questions.
     *
     * @maxLength 5000
     */
    description?: string | null;
    /**
     * Optional message shown after a response is submitted.
     *
     * @maxLength 2000
     */
    confirmationMessage?: string | null;
    /**
     * The lifecycle state of the form definition.
     */
    status?: "draft" | "published" | "archived";
    /**
     * The ordered questions included in the form.
     *
     * @maxItems 100
     */
    questions?: {
      /**
       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * A stable key used to associate the question with current and historical answers.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label?: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type?:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required?: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled?: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order?: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode?: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules?: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator?: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value?: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value?: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label?: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
      /**
       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
       */
      audience?: "candidate" | "staff";
      /**
       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
       */
      fixedRole?: "fullName" | "signature";
    }[];
    /**
     * The number of requests issued for this form.
     *
     * @minimum 0
     */
    requestCount?: number;
    /**
     * The number of responses submitted for this form.
     *
     * @minimum 0
     */
    submissionCount?: number;
    /**
     * The user who created the form. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The user who last updated the form. If expanded, a user object; otherwise, a user ID.
     */
    updatedBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * When the form was most recently published.
     *
     * @format date-time
     */
    publishedAt?: string | null;
    /**
     * When the form was created.
     *
     * @format date-time
     */
    createdAt?: string;
    /**
     * When the form was last updated.
     *
     * @format date-time
     */
    updatedAt?: string;
  };
};

export type CreateFormRequestBody = {
  /**
   * The internal and recipient-facing title of the form.
   *
   * @minLength 1
   * @maxLength 256
   */
  title: string;
  /**
   * Optional introductory text shown before the questions.
   *
   * @maxLength 5000
   */
  description?: string | null;
  /**
   * Optional message shown after a response is submitted.
   *
   * @maxLength 2000
   */
  confirmationMessage?: string | null;
  /**
   * The initial ordered questions for the form.
   *
   * @maxItems 100
   */
  questions?: {
    /**
     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The stable question key. Omit it for a new question and one will be assigned automatically.
     *
     * @minLength 1
     * @maxLength 128
     */
    key?: string;
    /**
     * The question shown to the person completing the form.
     *
     * @minLength 1
     * @maxLength 500
     */
    label: string;
    /**
     * Optional supporting text displayed with the question.
     *
     * @maxLength 2000
     */
    description?: string;
    /**
     * The type of answer collected for the question.
     */
    type:
      | "checkbox"
      | "date"
      | "daterange"
      | "dropdown"
      | "number"
      | "signature"
      | "text"
      | "textarea";
    /**
     * Whether an answer is required while the question is visible.
     */
    required: boolean;
    /**
     * Whether the question is included in new forms. Historical responses retain disabled questions.
     */
    enabled: boolean;
    /**
     * The display order of the question.
     *
     * @minimum 0
     * @maximum 1000
     */
    order: number;
    /**
     * Optional rules that determine when the question is shown.
     */
    condition?: {
      /**
       * Whether any rule or every rule must match before the question is shown.
       */
      mode: "any" | "all";
      /**
       * The comparisons used to determine question visibility.
       *
       * @minItems 1
       * @maxItems 20
       */
      rules: {
        /**
         * The stable key of the question that controls visibility.
         *
         * @minLength 1
         * @maxLength 128
         */
        questionKey: string;
        /**
         * How the controlling answer is compared with the value.
         */
        operator: "equals" | "notEquals";
        /**
         * The answer value used to determine visibility.
         */
        value: string | boolean | number | (void | null);
      }[];
    };
    /**
     * The available answers for a dropdown question.
     *
     * @minItems 1
     * @maxItems 100
     */
    options?: {
      /**
       * The value stored when this option is selected.
       *
       * @minLength 1
       * @maxLength 200
       */
      value: string;
      /**
       * The option displayed to the person answering the question.
       *
       * @minLength 1
       * @maxLength 200
       */
      label: string;
    }[];
    /**
     * Whether this question belongs to the standard template.
     */
    ["default"]?: boolean;
  }[];
};

export type CreateFormVariables = {
  body: CreateFormRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateForm = (
  variables: CreateFormVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateFormResponse,
    CreateFormError,
    CreateFormRequestBody,
    {},
    {},
    {}
  >({ url: "/forms", method: "post", ...variables, signal });

export const useCreateForm = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateFormResponse,
      CreateFormError,
      CreateFormVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateFormResponse,
    CreateFormError,
    CreateFormVariables
  >({
    mutationFn: (variables: CreateFormVariables) =>
      fetchCreateForm(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetFormPathParams = {
  /**
   * The form identifier.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  formId: string;
};

export type GetFormQueryParams = {
  /**
   * The fields to include in each returned form.
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "title"
    | "description"
    | "confirmationMessage"
    | "status"
    | "questions"
    | "createdBy"
    | "createdBy.id"
    | "createdBy.email"
    | "createdBy.firstName"
    | "createdBy.lastName"
    | "createdBy.username"
    | "createdBy.lea"
    | "createdBy.clients"
    | "createdBy.position"
    | "createdBy.permissions"
    | "createdBy.activationCode"
    | "createdBy.customer"
    | "createdBy.disabled"
    | "createdBy.commissionThreshold"
    | "createdBy.branches"
    | "createdBy.displayOnboarding"
    | "createdBy._customId"
    | "createdBy.signature"
    | "createdBy.phone"
    | "createdBy.defaultCandidateWatcher"
    | "createdBy.defaultTaskWorkspace"
    | "createdBy.teamAdmin"
    | "createdBy.contact"
    | "createdBy.contact.id"
    | "createdBy.contact.forename"
    | "createdBy.contact.surname"
    | "createdBy.contact.email"
    | "createdBy.contact.telephone"
    | "createdBy.contact.mobile"
    | "createdBy.contact.extension"
    | "createdBy.createdAt"
    | "createdBy.evertime.id"
    | "createdBy.evertime"
    | "createdBy.timesheetPortal.id"
    | "createdBy.timesheetPortal.code"
    | "createdBy.timesheetPortal"
    | "updatedBy"
    | "updatedBy.id"
    | "updatedBy.email"
    | "updatedBy.firstName"
    | "updatedBy.lastName"
    | "updatedBy.username"
    | "updatedBy.lea"
    | "updatedBy.clients"
    | "updatedBy.position"
    | "updatedBy.permissions"
    | "updatedBy.activationCode"
    | "updatedBy.customer"
    | "updatedBy.disabled"
    | "updatedBy.commissionThreshold"
    | "updatedBy.branches"
    | "updatedBy.displayOnboarding"
    | "updatedBy._customId"
    | "updatedBy.signature"
    | "updatedBy.phone"
    | "updatedBy.defaultCandidateWatcher"
    | "updatedBy.defaultTaskWorkspace"
    | "updatedBy.teamAdmin"
    | "updatedBy.contact"
    | "updatedBy.contact.id"
    | "updatedBy.contact.forename"
    | "updatedBy.contact.surname"
    | "updatedBy.contact.email"
    | "updatedBy.contact.telephone"
    | "updatedBy.contact.mobile"
    | "updatedBy.contact.extension"
    | "updatedBy.createdAt"
    | "updatedBy.evertime.id"
    | "updatedBy.evertime"
    | "updatedBy.timesheetPortal.id"
    | "updatedBy.timesheetPortal.code"
    | "updatedBy.timesheetPortal"
    | "publishedAt"
    | "createdAt"
    | "updatedAt"
    | ""
  )[];
  /**
   * Related resources to return as objects.
   *
   * @maxItems 2
   */
  expand?: ("createdBy" | "updatedBy")[];
};

export type GetFormError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetFormResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the form.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The internal and recipient-facing title of the form.
     *
     * @minLength 1
     * @maxLength 256
     */
    title?: string;
    /**
     * Optional introductory text shown before the questions.
     *
     * @maxLength 5000
     */
    description?: string | null;
    /**
     * Optional message shown after a response is submitted.
     *
     * @maxLength 2000
     */
    confirmationMessage?: string | null;
    /**
     * The lifecycle state of the form definition.
     */
    status?: "draft" | "published" | "archived";
    /**
     * The ordered questions included in the form.
     *
     * @maxItems 100
     */
    questions?: {
      /**
       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * A stable key used to associate the question with current and historical answers.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label?: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type?:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required?: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled?: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order?: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode?: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules?: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator?: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value?: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value?: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label?: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
      /**
       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
       */
      audience?: "candidate" | "staff";
      /**
       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
       */
      fixedRole?: "fullName" | "signature";
    }[];
    /**
     * The number of requests issued for this form.
     *
     * @minimum 0
     */
    requestCount?: number;
    /**
     * The number of responses submitted for this form.
     *
     * @minimum 0
     */
    submissionCount?: number;
    /**
     * The user who created the form. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The user who last updated the form. If expanded, a user object; otherwise, a user ID.
     */
    updatedBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * When the form was most recently published.
     *
     * @format date-time
     */
    publishedAt?: string | null;
    /**
     * When the form was created.
     *
     * @format date-time
     */
    createdAt?: string;
    /**
     * When the form was last updated.
     *
     * @format date-time
     */
    updatedAt?: string;
  };
};

export type GetFormVariables = {
  pathParams: GetFormPathParams;
  queryParams?: GetFormQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetForm = (
  variables: GetFormVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetFormResponse,
    GetFormError,
    undefined,
    {},
    GetFormQueryParams,
    GetFormPathParams
  >({ url: "/forms/{formId}", method: "get", ...variables, signal });

export function getFormQuery(variables: GetFormVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetFormResponse>;
};

export function getFormQuery(
  variables: GetFormVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetFormResponse>)
    | reactQuery.SkipToken;
};

export function getFormQuery(
  variables: GetFormVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/forms/{formId}",
      operationId: "getForm",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetForm(variables, signal),
  };
}

export const useSuspenseGetForm = <TData = GetFormResponse,>(
  variables: GetFormVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetFormResponse, GetFormError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<GetFormResponse, GetFormError, TData>({
    ...getFormQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetForm = <TData = GetFormResponse,>(
  variables: GetFormVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<GetFormResponse, GetFormError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetFormResponse, GetFormError, TData>({
    ...getFormQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateFormPathParams = {
  /**
   * The form identifier.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  formId: string;
};

export type UpdateFormError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 409;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Resource limit reached
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateFormResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the form.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The internal and recipient-facing title of the form.
     *
     * @minLength 1
     * @maxLength 256
     */
    title?: string;
    /**
     * Optional introductory text shown before the questions.
     *
     * @maxLength 5000
     */
    description?: string | null;
    /**
     * Optional message shown after a response is submitted.
     *
     * @maxLength 2000
     */
    confirmationMessage?: string | null;
    /**
     * The lifecycle state of the form definition.
     */
    status?: "draft" | "published" | "archived";
    /**
     * The ordered questions included in the form.
     *
     * @maxItems 100
     */
    questions?: {
      /**
       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * A stable key used to associate the question with current and historical answers.
       *
       * @minLength 1
       * @maxLength 128
       */
      key?: string;
      /**
       * The question shown to the person completing the form.
       *
       * @minLength 1
       * @maxLength 500
       */
      label?: string;
      /**
       * Optional supporting text displayed with the question.
       *
       * @maxLength 2000
       */
      description?: string;
      /**
       * The type of answer collected for the question.
       */
      type?:
        | "checkbox"
        | "date"
        | "daterange"
        | "dropdown"
        | "number"
        | "signature"
        | "text"
        | "textarea";
      /**
       * Whether an answer is required while the question is visible.
       */
      required?: boolean;
      /**
       * Whether the question is included in new forms. Historical responses retain disabled questions.
       */
      enabled?: boolean;
      /**
       * The display order of the question.
       *
       * @minimum 0
       * @maximum 1000
       */
      order?: number;
      /**
       * Optional rules that determine when the question is shown.
       */
      condition?: {
        /**
         * Whether any rule or every rule must match before the question is shown.
         */
        mode?: "any" | "all";
        /**
         * The comparisons used to determine question visibility.
         *
         * @minItems 1
         * @maxItems 20
         */
        rules?: {
          /**
           * The stable key of the question that controls visibility.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * How the controlling answer is compared with the value.
           */
          operator?: "equals" | "notEquals";
          /**
           * The answer value used to determine visibility.
           */
          value?: string | boolean | number | (void | null);
        }[];
      };
      /**
       * The available answers for a dropdown question.
       *
       * @minItems 1
       * @maxItems 100
       */
      options?: {
        /**
         * The value stored when this option is selected.
         *
         * @minLength 1
         * @maxLength 200
         */
        value?: string;
        /**
         * The option displayed to the person answering the question.
         *
         * @minLength 1
         * @maxLength 200
         */
        label?: string;
      }[];
      /**
       * Whether this question belongs to the standard template.
       */
      ["default"]?: boolean;
      /**
       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
       */
      audience?: "candidate" | "staff";
      /**
       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
       */
      fixedRole?: "fullName" | "signature";
    }[];
    /**
     * The number of requests issued for this form.
     *
     * @minimum 0
     */
    requestCount?: number;
    /**
     * The number of responses submitted for this form.
     *
     * @minimum 0
     */
    submissionCount?: number;
    /**
     * The user who created the form. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * The user who last updated the form. If expanded, a user object; otherwise, a user ID.
     */
    updatedBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * When the form was most recently published.
     *
     * @format date-time
     */
    publishedAt?: string | null;
    /**
     * When the form was created.
     *
     * @format date-time
     */
    createdAt?: string;
    /**
     * When the form was last updated.
     *
     * @format date-time
     */
    updatedAt?: string;
  };
};

export type UpdateFormRequestBody = {
  /**
   * The internal and recipient-facing title of the form.
   *
   * @minLength 1
   * @maxLength 256
   */
  title?: string;
  /**
   * Optional introductory text shown before the questions.
   *
   * @maxLength 5000
   */
  description?: string | null;
  /**
   * Optional message shown after a response is submitted.
   *
   * @maxLength 2000
   */
  confirmationMessage?: string | null;
  /**
   * The lifecycle state of the form definition.
   */
  status?: "draft" | "published" | "archived";
  /**
   * The complete ordered question configuration to save.
   *
   * @maxItems 100
   */
  questions?: {
    /**
     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The stable question key. Omit it for a new question and one will be assigned automatically.
     *
     * @minLength 1
     * @maxLength 128
     */
    key?: string;
    /**
     * The question shown to the person completing the form.
     *
     * @minLength 1
     * @maxLength 500
     */
    label: string;
    /**
     * Optional supporting text displayed with the question.
     *
     * @maxLength 2000
     */
    description?: string;
    /**
     * The type of answer collected for the question.
     */
    type:
      | "checkbox"
      | "date"
      | "daterange"
      | "dropdown"
      | "number"
      | "signature"
      | "text"
      | "textarea";
    /**
     * Whether an answer is required while the question is visible.
     */
    required: boolean;
    /**
     * Whether the question is included in new forms. Historical responses retain disabled questions.
     */
    enabled: boolean;
    /**
     * The display order of the question.
     *
     * @minimum 0
     * @maximum 1000
     */
    order: number;
    /**
     * Optional rules that determine when the question is shown.
     */
    condition?: {
      /**
       * Whether any rule or every rule must match before the question is shown.
       */
      mode: "any" | "all";
      /**
       * The comparisons used to determine question visibility.
       *
       * @minItems 1
       * @maxItems 20
       */
      rules: {
        /**
         * The stable key of the question that controls visibility.
         *
         * @minLength 1
         * @maxLength 128
         */
        questionKey: string;
        /**
         * How the controlling answer is compared with the value.
         */
        operator: "equals" | "notEquals";
        /**
         * The answer value used to determine visibility.
         */
        value: string | boolean | number | (void | null);
      }[];
    };
    /**
     * The available answers for a dropdown question.
     *
     * @minItems 1
     * @maxItems 100
     */
    options?: {
      /**
       * The value stored when this option is selected.
       *
       * @minLength 1
       * @maxLength 200
       */
      value: string;
      /**
       * The option displayed to the person answering the question.
       *
       * @minLength 1
       * @maxLength 200
       */
      label: string;
    }[];
    /**
     * Whether this question belongs to the standard template.
     */
    ["default"]?: boolean;
  }[];
};

export type UpdateFormVariables = {
  body?: UpdateFormRequestBody;
  pathParams: UpdateFormPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateForm = (
  variables: UpdateFormVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateFormResponse,
    UpdateFormError,
    UpdateFormRequestBody,
    {},
    {},
    UpdateFormPathParams
  >({ url: "/forms/{formId}", method: "patch", ...variables, signal });

export const useUpdateForm = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateFormResponse,
      UpdateFormError,
      UpdateFormVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateFormResponse,
    UpdateFormError,
    UpdateFormVariables
  >({
    mutationFn: (variables: UpdateFormVariables) =>
      fetchUpdateForm(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetFormRequestsQueryParams = {
  /**
   * The fields to include in each returned request.
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "formId"
    | "responseId"
    | "candidate"
    | "candidate.id"
    | "candidate.forename"
    | "candidate.surname"
    | "candidate.previousNames"
    | "candidate.email"
    | "candidate.telephone"
    | "candidate.mobile"
    | "candidate.occupation"
    | "candidate.subject"
    | "candidate.address.addressLine1"
    | "candidate.address.addressLine2"
    | "candidate.address.addressLine3"
    | "candidate.address.city"
    | "candidate.address.county"
    | "candidate.address.borough"
    | "candidate.address.postcode"
    | "candidate.address.country"
    | "candidate.address.lat"
    | "candidate.address.lng"
    | "candidate.address"
    | "candidate.qualifications"
    | "candidate.employment"
    | "candidate.references"
    | "candidate.referenceCheckConsent"
    | "candidate.referenceCheckConsentName"
    | "candidate.referenceCheckConsentDate"
    | "candidate.dob"
    | "candidate.sex"
    | "candidate.status"
    | "candidate.onboardingStatus"
    | "candidate.onboardingSent"
    | "candidate.nextOfKin.nextOfKinName"
    | "candidate.nextOfKin.nextOfKinRelationship"
    | "candidate.nextOfKin.nextOfKinTelephone"
    | "candidate.nextOfKin.nextOfKinEmail"
    | "candidate.nextOfKin.nextOfKinAddress.addressLine1"
    | "candidate.nextOfKin.nextOfKinAddress.addressLine2"
    | "candidate.nextOfKin.nextOfKinAddress.addressLine3"
    | "candidate.nextOfKin.nextOfKinAddress.city"
    | "candidate.nextOfKin.nextOfKinAddress.county"
    | "candidate.nextOfKin.nextOfKinAddress.borough"
    | "candidate.nextOfKin.nextOfKinAddress.postcode"
    | "candidate.nextOfKin.nextOfKinAddress.country"
    | "candidate.nextOfKin.nextOfKinAddress.lat"
    | "candidate.nextOfKin.nextOfKinAddress.lng"
    | "candidate.nextOfKin.nextOfKinAddress"
    | "candidate.nextOfKin"
    | "candidate.criminalRecord.dbsHeld"
    | "candidate.criminalRecord.dbsNumber"
    | "candidate.criminalRecord.dbsType"
    | "candidate.criminalRecord.dbsLastChecked"
    | "candidate.criminalRecord.dbsUpdateServiceNumber"
    | "candidate.criminalRecord.prohibitionCheck"
    | "candidate.criminalRecord.overseasCheck"
    | "candidate.criminalRecord.overseasCheckNote"
    | "candidate.criminalRecord.convictedOffence"
    | "candidate.criminalRecord.convictedOffenceDescription"
    | "candidate.criminalRecord.convictions"
    | "candidate.criminalRecord.convictionsDescription"
    | "candidate.criminalRecord.registeredToUpdateService"
    | "candidate.criminalRecord.consentUpdateServiceChecks"
    | "candidate.criminalRecord.note"
    | "candidate.criminalRecord.updateServiceCheckDate"
    | "candidate.criminalRecord.abroad"
    | "candidate.criminalRecord.overseasDeclarationExtended"
    | "candidate.criminalRecord.educationalDisciplinaryProceedings"
    | "candidate.criminalRecord.dbsComments"
    | "candidate.criminalRecord.dbsDeclarationRead"
    | "candidate.criminalRecord.overseasOffence"
    | "candidate.criminalRecord.overseasOffenceDescription"
    | "candidate.criminalRecord.currentCriminalOffence"
    | "candidate.criminalRecord.currentCriminalOffenceDescription"
    | "candidate.criminalRecord.dbsIssuedByBusiness"
    | "candidate.criminalRecord.dbsIssuedByCurrentEmployer"
    | "candidate.criminalRecord.section128Required"
    | "candidate.criminalRecord.section128Checked"
    | "candidate.criminalRecord"
    | "candidate.criminalRecordDeclaration.questions"
    | "candidate.criminalRecordDeclaration.answers"
    | "candidate.criminalRecordDeclaration.submittedAt"
    | "candidate.criminalRecordDeclaration.submittedIp"
    | "candidate.criminalRecordDeclaration.configurationUpdatedAt"
    | "candidate.criminalRecordDeclaration"
    | "candidate.criminalRecordCheckConsent"
    | "candidate.criminalRecordCheckConsentName"
    | "candidate.criminalRecordCheckConsentDate"
    | "candidate.safeInEducation.barred"
    | "candidate.safeInEducation.barredCheckDate"
    | "candidate.safeInEducation.waiver"
    | "candidate.safeInEducation.ordersYou"
    | "candidate.safeInEducation.ordersChild"
    | "candidate.safeInEducation.previousSafeguardingAllegations"
    | "candidate.safeInEducation.cautioned"
    | "candidate.safeInEducation.violentOffence"
    | "candidate.safeInEducation.anotherCountry"
    | "candidate.safeInEducation.readKCSIE"
    | "candidate.safeInEducation.readChildcareAct"
    | "candidate.safeInEducation.prohibitedFromTeaching"
    | "candidate.safeInEducation.knownPolice"
    | "candidate.safeInEducation.prohibitedFromManagement"
    | "candidate.safeInEducation.criminalOffencesLaw"
    | "candidate.safeInEducation.safeInEducationDetails"
    | "candidate.safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
    | "candidate.safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
    | "candidate.safeInEducation.safeguardingQuestionaire.signOfAbuse"
    | "candidate.safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
    | "candidate.safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
    | "candidate.safeInEducation.safeguardingQuestionaire.fgmDefinition"
    | "candidate.safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
    | "candidate.safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
    | "candidate.safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
    | "candidate.safeInEducation.safeguardingQuestionaire.studentConfides"
    | "candidate.safeInEducation.safeguardingQuestionaire"
    | "candidate.safeInEducation"
    | "candidate.socialMedia.facebook"
    | "candidate.socialMedia.instagram"
    | "candidate.socialMedia.twitter"
    | "candidate.socialMedia.linkedIn"
    | "candidate.socialMedia.tiktok"
    | "candidate.socialMedia.others"
    | "candidate.socialMedia.socialMediaChecked"
    | "candidate.socialMedia.googleCheckedDate"
    | "candidate.socialMedia"
    | "candidate.safeInEducationCheckConsent"
    | "candidate.safeInEducationCheckConsentName"
    | "candidate.safeInEducationCheckConsentDate"
    | "candidate.healthRecord.physicalAbilityCondition"
    | "candidate.healthRecord.confused"
    | "candidate.healthRecord.hearing"
    | "candidate.healthRecord.eyesight"
    | "candidate.healthRecord.depression"
    | "candidate.healthRecord.severePain"
    | "candidate.healthRecord.excessiveDrowsiness"
    | "candidate.healthRecord.epilepsy"
    | "candidate.healthRecord.heart"
    | "candidate.healthRecord.asthma"
    | "candidate.healthRecord.mentalHealth"
    | "candidate.healthRecord.expectantMother"
    | "candidate.healthRecord.allergies"
    | "candidate.healthRecord.medication"
    | "candidate.healthRecord.healthIssues"
    | "candidate.healthRecord.healthIssuesDescription"
    | "candidate.healthRecord.disabilityNeeds"
    | "candidate.healthRecord.contagiousDiseases"
    | "candidate.healthRecord.affectAbility"
    | "candidate.healthRecord.notificationAgree"
    | "candidate.healthRecord.sasOptIn"
    | "candidate.healthRecord.signature"
    | "candidate.healthRecord.drawnSignature"
    | "candidate.healthRecord.signatureDate"
    | "candidate.healthRecord.vaccinationMeasles"
    | "candidate.healthRecord.vaccinationMumps"
    | "candidate.healthRecord.vaccinationRubella"
    | "candidate.healthRecord.vaccinationVaricella"
    | "candidate.healthRecord.vaccinationTuberculosis"
    | "candidate.healthRecord.vaccinationHepatitisB"
    | "candidate.healthRecord.vaccinationHepatitisC"
    | "candidate.healthRecord.vaccinationHIV"
    | "candidate.healthRecord.vaccinationCovid"
    | "candidate.healthRecord.vaccinationCovid1"
    | "candidate.healthRecord.vaccinationCovid2"
    | "candidate.healthRecord.vaccinationCovidBooster"
    | "candidate.healthRecord.vaccinationCovidInfluenza"
    | "candidate.healthRecord.fitToWorkOverride"
    | "candidate.healthRecord"
    | "candidate.healthDeclaration.questions"
    | "candidate.healthDeclaration.answers"
    | "candidate.healthDeclaration.submittedAt"
    | "candidate.healthDeclaration.submittedIp"
    | "candidate.healthDeclaration.configurationUpdatedAt"
    | "candidate.healthDeclaration"
    | "candidate.healthRecordCheckConsent"
    | "candidate.healthRecordCheckConsentName"
    | "candidate.healthRecordCheckConsentDate"
    | "candidate.bankDetails.bankAccountName"
    | "candidate.bankDetails.bankName"
    | "candidate.bankDetails.accountNumber"
    | "candidate.bankDetails.sortCode"
    | "candidate.bankDetails.niNumber"
    | "candidate.bankDetails.jobOp1"
    | "candidate.bankDetails.jobOp2"
    | "candidate.bankDetails.jobOp3"
    | "candidate.bankDetails.studentLoan.studentLoan"
    | "candidate.bankDetails.studentLoan.plan1"
    | "candidate.bankDetails.studentLoan.plan2"
    | "candidate.bankDetails.studentLoan.plan4"
    | "candidate.bankDetails.studentLoan.postGradLoan"
    | "candidate.bankDetails.studentLoan"
    | "candidate.bankDetails"
    | "candidate.declarationCheckConsent"
    | "candidate.declarationCheckConsentName"
    | "candidate.declarationCheckConsentDate"
    | "candidate.contractForServiceConsent"
    | "candidate.contractForServiceName"
    | "candidate.contractForServiceDate"
    | "candidate.confirmed"
    | "candidate.createdBy"
    | "candidate.customerId"
    | "candidate.migrationId"
    | "candidate.migrationApproval"
    | "candidate.assignee"
    | "candidate.assignee.id"
    | "candidate.assignee.email"
    | "candidate.assignee.firstName"
    | "candidate.assignee.lastName"
    | "candidate.assignee.username"
    | "candidate.assignee.lea"
    | "candidate.assignee.clients"
    | "candidate.assignee.position"
    | "candidate.assignee.permissions"
    | "candidate.assignee.activationCode"
    | "candidate.assignee.customer"
    | "candidate.assignee.disabled"
    | "candidate.assignee.commissionThreshold"
    | "candidate.assignee.branches"
    | "candidate.assignee.displayOnboarding"
    | "candidate.assignee._customId"
    | "candidate.assignee.signature"
    | "candidate.assignee.phone"
    | "candidate.assignee.defaultCandidateWatcher"
    | "candidate.assignee.defaultTaskWorkspace"
    | "candidate.assignee.teamAdmin"
    | "candidate.assignee.contact"
    | "candidate.assignee.contact.id"
    | "candidate.assignee.contact.forename"
    | "candidate.assignee.contact.surname"
    | "candidate.assignee.contact.email"
    | "candidate.assignee.contact.telephone"
    | "candidate.assignee.contact.mobile"
    | "candidate.assignee.contact.extension"
    | "candidate.assignee.createdAt"
    | "candidate.assignee.evertime.id"
    | "candidate.assignee.evertime"
    | "candidate.assignee.timesheetPortal.id"
    | "candidate.assignee.timesheetPortal.code"
    | "candidate.assignee.timesheetPortal"
    | "candidate.currentBooking"
    | "candidate.profilePhoto"
    | "candidate.payType"
    | "candidate.payInterval"
    | "candidate.payRate"
    | "candidate.customId"
    | "candidate._internalId"
    | "candidate.statusChangeDate"
    | "candidate.optOut"
    | "candidate.trnNumber"
    | "candidate.trnNumberCheckedDate"
    | "candidate.trnNumberNotes"
    | "candidate.employmentStartDate"
    | "candidate.ewcConfirmation"
    | "candidate.homeOfficeShareCode"
    | "candidate.archived"
    | "candidate.umbrella"
    | "candidate.payeUmbrella"
    | "candidate.umbrellaCompanyName"
    | "candidate.umbrellaCompanyCode"
    | "candidate.transport"
    | "candidate.importantNotes"
    | "candidate.candidateLastUpdated"
    | "candidate.source"
    | "candidate.sourceOther"
    | "candidate.onlineCheckConsent"
    | "candidate.title"
    | "candidate.pronoun"
    | "candidate.updatedAt"
    | "candidate.lastContactedDate"
    | "candidate.highlight"
    | "candidate.teacherPayScale"
    | "candidate.p45"
    | "candidate.roleType"
    | "candidate.rtwVerification"
    | "candidate.rtwVerificationNote"
    | "candidate.rtwVerificationStatus"
    | "candidate.desiredLocation"
    | "candidate.complianceUser"
    | "candidate.complianceUser.id"
    | "candidate.complianceUser.email"
    | "candidate.complianceUser.firstName"
    | "candidate.complianceUser.lastName"
    | "candidate.complianceUser.username"
    | "candidate.complianceUser.lea"
    | "candidate.complianceUser.clients"
    | "candidate.complianceUser.position"
    | "candidate.complianceUser.permissions"
    | "candidate.complianceUser.activationCode"
    | "candidate.complianceUser.customer"
    | "candidate.complianceUser.disabled"
    | "candidate.complianceUser.commissionThreshold"
    | "candidate.complianceUser.branches"
    | "candidate.complianceUser.displayOnboarding"
    | "candidate.complianceUser._customId"
    | "candidate.complianceUser.signature"
    | "candidate.complianceUser.phone"
    | "candidate.complianceUser.defaultCandidateWatcher"
    | "candidate.complianceUser.defaultTaskWorkspace"
    | "candidate.complianceUser.teamAdmin"
    | "candidate.complianceUser.contact"
    | "candidate.complianceUser.contact.id"
    | "candidate.complianceUser.contact.forename"
    | "candidate.complianceUser.contact.surname"
    | "candidate.complianceUser.contact.email"
    | "candidate.complianceUser.contact.telephone"
    | "candidate.complianceUser.contact.mobile"
    | "candidate.complianceUser.contact.extension"
    | "candidate.complianceUser.createdAt"
    | "candidate.complianceUser.evertime.id"
    | "candidate.complianceUser.evertime"
    | "candidate.complianceUser.timesheetPortal.id"
    | "candidate.complianceUser.timesheetPortal.code"
    | "candidate.complianceUser.timesheetPortal"
    | "candidate.watchers"
    | "candidate.tags"
    | "candidate.documents"
    | "candidate.documentsCount"
    | "candidate.desiredLocationMultiple"
    | "candidate.availabilityDays"
    | "candidate.availabilityLastChecked"
    | "candidate.subjectToNotice"
    | "candidate.subsidiarySubject"
    | "candidate.employmentPreference"
    | "candidate.otherNames"
    | "candidate.ltdCompany"
    | "candidate.employmentVerified"
    | "candidate.candidateDBSVerification"
    | "candidate.candidateDBSVerificationNote"
    | "candidate.candidateDBSVerificationStatus"
    | "candidate.candidateDBSCheckVerification"
    | "candidate.candidateDBSCheckVerificationNote"
    | "candidate.candidateDBSCheckVerificationStatus"
    | "candidate.candidateDBSUpdateServiceVerificationNote"
    | "candidate.candidateDBSUpdateServiceVerificationStatus"
    | "candidate.candidateTRNVerificationNote"
    | "candidate.candidateTRNVerificationStatus"
    | "candidate.candidateHealthSafetyVerification"
    | "candidate.candidateHealthSafetyVerificationNote"
    | "candidate.candidateHealthSafetyVerificationStatus"
    | "candidate.onboardingSession"
    | "candidate.onboardingSession.id"
    | "candidate.onboardingSession.url"
    | "candidate.onboardingSession.healthDeclarationConfig.questions"
    | "candidate.onboardingSession.healthDeclarationConfig.updatedAt"
    | "candidate.onboardingSession.healthDeclarationConfig"
    | "candidate.onboardingSession.criminalRecordConfig.questions"
    | "candidate.onboardingSession.criminalRecordConfig.updatedAt"
    | "candidate.onboardingSession.criminalRecordConfig"
    | "candidate.contact"
    | "candidate.contact.id"
    | "candidate.contact.forename"
    | "candidate.contact.surname"
    | "candidate.contact.email"
    | "candidate.contact.telephone"
    | "candidate.contact.mobile"
    | "candidate.contact.extension"
    | "candidate.optOut48Hours"
    | "candidate.recAuditConsent"
    | "candidate.recAuditConsentDate"
    | "candidate.recAuditConsentMethod"
    | "candidate.createdAt"
    | "candidate.candidateMobileAppStatus"
    | "candidate.evertime.id"
    | "candidate.evertime.niNumberMatch"
    | "candidate.evertime"
    | "candidate.relevanceScore"
    | "candidate.matchReasons"
    | "candidate.distance"
    | "candidate.timesheetPortal.id"
    | "candidate.timesheetPortal.code"
    | "candidate.timesheetPortal"
    | "candidate.mobileAppLinkInvite.isUsed"
    | "candidate.mobileAppLinkInvite.createdAt"
    | "candidate.mobileAppLinkInvite.usedBy"
    | "candidate.mobileAppLinkInvite.usedAt"
    | "candidate.mobileAppLinkInvite.unlinkedBy"
    | "candidate.mobileAppLinkInvite.unlinkedAt"
    | "candidate.mobileAppLinkInvite.deletedBy"
    | "candidate.mobileAppLinkInvite.deletedAt"
    | "candidate.mobileAppLinkInvite.status"
    | "candidate.mobileAppLinkInvite"
    | "clientContact"
    | "clientId"
    | "recipientName"
    | "recipientEmail"
    | "snapshot.title"
    | "snapshot.description"
    | "snapshot.confirmationMessage"
    | "snapshot.questions"
    | "snapshot.formId"
    | "snapshot.configurationUpdatedAt"
    | "snapshot"
    | "status"
    | "expiresAt"
    | "startedAt"
    | "submittedAt"
    | "revokedAt"
    | "createdBy"
    | "createdBy.id"
    | "createdBy.email"
    | "createdBy.firstName"
    | "createdBy.lastName"
    | "createdBy.username"
    | "createdBy.lea"
    | "createdBy.clients"
    | "createdBy.position"
    | "createdBy.permissions"
    | "createdBy.activationCode"
    | "createdBy.customer"
    | "createdBy.disabled"
    | "createdBy.commissionThreshold"
    | "createdBy.branches"
    | "createdBy.displayOnboarding"
    | "createdBy._customId"
    | "createdBy.signature"
    | "createdBy.phone"
    | "createdBy.defaultCandidateWatcher"
    | "createdBy.defaultTaskWorkspace"
    | "createdBy.teamAdmin"
    | "createdBy.contact"
    | "createdBy.contact.id"
    | "createdBy.contact.forename"
    | "createdBy.contact.surname"
    | "createdBy.contact.email"
    | "createdBy.contact.telephone"
    | "createdBy.contact.mobile"
    | "createdBy.contact.extension"
    | "createdBy.createdAt"
    | "createdBy.evertime.id"
    | "createdBy.evertime"
    | "createdBy.timesheetPortal.id"
    | "createdBy.timesheetPortal.code"
    | "createdBy.timesheetPortal"
    | "delivery.emailRequestedAt"
    | "delivery.emailSentAt"
    | "delivery.emailFailedAt"
    | "delivery"
    | "createdAt"
    | "updatedAt"
    | ""
  )[];
  /**
   * Related resources to return as objects.
   *
   * @maxItems 2
   */
  expand?: ("candidate" | "createdBy")[];
  /**
   * Filters applied to issued form requests.
   */
  filter?: {
    /**
     * Return requests issued from this form.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    formId?: string;
    /**
     * Return requests linked to this candidate.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    candidateId?: string;
    /**
     * Return requests linked to this Client contact.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientContactId?: string;
    /**
     * Return requests linked to this Client.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientId?: string;
    /**
     * Return requests whose recipient name or email contains this text.
     *
     * @minLength 1
     * @maxLength 256
     */
    recipient?: string;
    /**
     * Return requests in any of the supplied states.
     *
     * @maxItems 5
     */
    status?: ("issued" | "inProgress" | "submitted" | "revoked" | "expired")[];
    /**
     * Return requests expiring within this date range.
     */
    expiresAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Return requests submitted within this date range.
     */
    submittedAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
    /**
     * Return requests in any of the supplied delivery states.
     *
     * @maxItems 4
     */
    deliveryStatus?: ("linkOnly" | "queued" | "sent" | "failed")[];
  };
  /**
   * The field and direction used to order requests.
   */
  sort?:
    | "createdAt"
    | "-createdAt"
    | "expiresAt"
    | "-expiresAt"
    | "submittedAt"
    | "-submittedAt";
  /**
   * Page number
   *
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
};

export type GetFormRequestsError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetFormRequestsResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the issued form request.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The form definition used to create this request.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    formId?: string;
    /**
     * The submitted response for this request, when one is available.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    responseId?: string | null;
    /**
     * The candidate linked to this request. If expanded, a candidate object; otherwise, a candidate ID.
     */
    candidate?:
      | string
      | {
          /**
           * The unique identifier of the candidate
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the candidate
           *
           * @minLength 1
           * @maxLength 256
           */
          forename?: string;
          /**
           * The surname of the candidate
           *
           * @minLength 1
           * @maxLength 256
           */
          surname?: string;
          /**
           * Any previous names of the candidate
           *
           * @maxLength 256
           */
          previousNames?: string;
          /**
           * The email of the candidate. This will be used for communication.
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the candidate
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the candidate
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The occupation of the candidate
           */
          occupation?:
            | "Teacher"
            | "Cover Supervisor"
            | "Teaching Assistant"
            | "School Support Staff"
            | "Primary Teacher"
            | "Secondary Teacher"
            | "Head Teacher"
            | "Senior Leader"
            | "Tutor"
            | "Mentor"
            | "Learning Support Assistant"
            | "Finance Officer"
            | "HR Administrator"
            | "HR Manager"
            | "Office Manager"
            | "Administrator"
            | "Personal Assistant"
            | "Pastoral Manager"
            | "Receptionist"
            | "Instructor"
            | "Caretaker"
            | "Nursery Assistant"
            | "Nursery Nurse"
            | "Deputy Nursery Manager"
            | "Lunchtime Supervisor"
            | "Classroom Graduate"
            | "Primary Cover Supervisor"
            | "HLTA"
            | "Nurture Teacher"
            | "Nurture Mentor"
            | "SENDCo"
            | "Registered Manager"
            | "Regional Manager"
            | "Education Director"
            | "Residential Director"
            | "Deputy Manager"
            | "Residential Support Worker"
            | "Sports Coach"
            | "Executive Headteacher"
            | "General Practitioner"
            | "Advance Nurse Practitioner (ANP)"
            | "Emergency Nurse Practitioner (ENP)"
            | "Advanced Clinical Practitioner (ACP)"
            | "Emergency Clinical Practitioner (ECP)"
            | "Consultant Psychiatrist"
            | "Psychiatrist \u2013 SPR"
            | "IT Technician"
            | "Art Technician"
            | "DT Technician"
            | "Science Technician"
            | "Healthcare Assistant/ Residential Support Worker"
            | "Healthcare Assistant"
            | "Senior Healthcare Assistant"
            | "Team Leader"
            | "Registered General Nurse"
            | "Registered Mental Nurse"
            | "Registered Nurse Learning Disabilities"
            | "Overseas Trained Teacher";
          /**
           * The subject the candidate specializes in
           *
           * @maxLength 256
           */
          subject?: string;
          /**
           * The address of the resource
           */
          address?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The qualifications of the candidate
           *
           * @maxItems 32
           */
          qualifications?: {
            /**
             * The type of the qualification
             *
             * @maxLength 256
             */
            type?: string;
            /**
             * The place where the qualification was obtained
             *
             * @maxLength 256
             */
            place?: string;
            /**
             * The name of the qualification
             *
             * @maxLength 256
             */
            qualificationName?: string;
            /**
             * The date the qualification was obtained
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date?: string;
            /**
             * The unique identifier of the qualification
             *
             * @maxLength 256
             */
            qualificationId?: string;
            /**
             * The order index of the qualification
             *
             * @minimum 0
             */
            orderIndex?: number;
            /**
             * The expiry date of the qualification
             *
             * @maxLength 256
             */
            qualificationExpiry?: string;
            /**
             * Whether to exclude this qualification from vetting
             */
            excludeFromVetting?: boolean;
            /**
             * The date the qualification was created
             *
             * @format date-time
             */
            createdAt?: string | null;
          }[];
          /**
           * The employment history of the candidate
           *
           * @maxItems 32
           */
          employment?: {
            /**
             * The name of the employer
             *
             * @maxLength 256
             */
            employerName?: string;
            /**
             * The date the candidate started employment
             *
             * @maxLength 256
             */
            dateStarted?: string;
            /**
             * The date the candidate ended employment
             *
             * @maxLength 256
             */
            dateEnded?: string;
            /**
             * The job title of the candidate
             *
             * @maxLength 256
             */
            jobTitle?: string;
            /**
             * The reason the candidate left this employment
             *
             * @maxLength 1024
             */
            reasonForLeaving?: string;
            /**
             * The duties of the candidate in this role. Consultant only, visible on Edun CV
             *
             * @maxLength 1024
             */
            duties?: string;
            /**
             * The salary of the candidate in this role
             *
             * @maxLength 256
             */
            salary?: string;
            /**
             * The address of the candidate's reference for this employment
             */
            referenceAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The unique identifier for this employment record
             *
             * @maxLength 256
             */
            employmentId?: string;
            /**
             * The order index of this employment record
             *
             * @minimum 0
             */
            orderIndex?: number;
          }[];
          /**
           * The references for the candidate
           *
           * @maxItems 32
           */
          references?: {
            /**
             * The title of the reference
             *
             * @maxLength 16
             */
            referenceTitle?: string;
            /**
             * The forename of the reference
             *
             * @maxLength 256
             */
            referenceForename?: string;
            /**
             * The surname of the reference
             *
             * @maxLength 256
             */
            referenceSurname?: string;
            /**
             * The company of the reference
             *
             * @maxLength 256
             */
            referenceCompany?: string;
            /**
             * The position of the reference
             *
             * @maxLength 256
             */
            referencePosition?: string;
            /**
             * The email of the reference
             *
             * @format email
             */
            referenceEmail?: string;
            /**
             * The telephone number of the reference
             *
             * @maxLength 32
             */
            referenceTelephone?: string;
            /**
             * The type of the reference
             */
            referenceType?:
              | "Education"
              | "Professional"
              | "Character"
              | "Factual"
              | "Academic"
              | "Volunteer";
            /**
             * The duration of the reference
             *
             * @maxLength 128
             */
            referenceDuration?: string;
            /**
             * The ID of the reference
             *
             * @maxLength 256
             */
            referenceId?: string;
            /**
             * This field only applies if the reference immediate consent question is enabled
             * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
             */
            referenceApply?: boolean;
            /**
             * Any additional notes about the reference
             *
             * @maxLength 1024
             */
            referenceNote?: string;
            /**
             * The start date of the reference
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            dateFrom?: string;
            /**
             * The end date of the reference
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            dateTo?: string;
          }[];
          /**
           * Whether the candidate has consented to a reference check
           */
          referenceCheckConsent?: boolean;
          /**
           * The name of the person who provided consent for the reference check
           *
           * @maxLength 256
           */
          referenceCheckConsentName?: string;
          /**
           * The date when consent for the reference check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          referenceCheckConsentDate?: string;
          /**
           * The date of birth of the candidate
           *
           * @format date-time
           */
          dob?: string | null;
          /**
           * The sex of the candidate
           */
          sex?: "Male" | "Female" | "Other" | "Unknown";
          /**
           * The status of the candidate
           */
          status?:
            | "Pending"
            | "Approved"
            | "Rejected"
            | "Anonymised"
            | "Archived"
            | "Enquired"
            | "Hold"
            | "Future"
            | "DNC"
            | "Pre";
          /**
           * The current status of the onboarding process for the candidate
           */
          onboardingStatus?: "Not Started" | "In Progress" | "Completed";
          /**
           * Whether the onboarding information has been sent to the candidate
           */
          onboardingSent?: boolean;
          nextOfKin?: {
            /**
             * The name of the next of kin
             *
             * @maxLength 256
             */
            nextOfKinName?: string;
            /**
             * The relationship of the next of kin to the candidate
             *
             * @maxLength 256
             */
            nextOfKinRelationship?: string;
            /**
             * The telephone number of the next of kin
             *
             * @maxLength 32
             */
            nextOfKinTelephone?: string;
            /**
             * The email address of the next of kin
             *
             * @format email
             */
            nextOfKinEmail?: string;
            nextOfKinAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
          };
          criminalRecord?: {
            /**
             * Whether the candidate has a DBS certificate
             */
            dbsHeld?: boolean;
            /**
             * The unique identifier for the DBS certificate
             *
             * @maxLength 256
             */
            dbsNumber?: string;
            /**
             * The type of DBS check
             */
            dbsType?:
              | "Basic"
              | "Standard"
              | "Enhanced"
              | "Enhanced with Barred List"
              | "Child workforce"
              | "Child and adult workforce"
              | "Adult workforce";
            /**
             * The date when the DBS check was last performed
             *
             * @maxLength 256
             */
            dbsLastChecked?: string;
            /**
             * The DBS update service number for the candidate
             *
             * @maxLength 256
             */
            dbsUpdateServiceNumber?: string;
            /**
             * The result of the prohibition check
             *
             * @maxLength 256
             */
            prohibitionCheck?: string;
            /**
             * The result of the overseas check
             *
             * @maxLength 256
             */
            overseasCheck?: string;
            /**
             * Any additional notes regarding the overseas check
             *
             * @maxLength 256
             */
            overseasCheckNote?: string;
            /**
             * Whether the candidate has a convicted offence
             */
            convictedOffence?: boolean;
            /**
             * The description of the convicted offence
             *
             * @maxLength 1024
             */
            convictedOffenceDescription?: string;
            /**
             * Whether the candidate has any convictions
             */
            convictions?: boolean;
            /**
             * The description of the convictions
             *
             * @maxLength 1024
             */
            convictionsDescription?: string;
            /**
             * Whether the candidate is registered to the DBS update service
             */
            registeredToUpdateService?: boolean;
            /**
             * Whether the candidate consents to update service checks
             */
            consentUpdateServiceChecks?: boolean;
            /**
             * Any additional notes regarding the candidate's criminal record
             *
             * @maxLength 1024
             */
            note?: string;
            /**
             * The date when the update service check was performed
             *
             * @maxLength 64
             */
            updateServiceCheckDate?: string;
            /**
             * Whether the candidate has lived abroad
             */
            abroad?: boolean;
            /**
             * Any additional notes regarding the overseas declaration
             *
             * @maxLength 1024
             */
            overseasDeclarationExtended?: string;
            /**
             * Whether the candidate has been subject to educational disciplinary proceedings
             */
            educationalDisciplinaryProceedings?: boolean;
            /**
             * Any comments regarding the DBS check
             *
             * @maxLength 1024
             */
            dbsComments?: string;
            /**
             * Whether the candidate has read the DBS declaration
             */
            dbsDeclarationRead?: boolean;
            /**
             * Whether the candidate has any overseas offences
             */
            overseasOffence?: boolean;
            /**
             * The description of the overseas offence
             *
             * @maxLength 1024
             */
            overseasOffenceDescription?: string;
            /**
             * Whether the candidate has a current criminal offence
             */
            currentCriminalOffence?: boolean;
            /**
             * The description of the current criminal offence
             *
             * @maxLength 1024
             */
            currentCriminalOffenceDescription?: string;
            /**
             * Whether the DBS was issued by a business
             */
            dbsIssuedByBusiness?: boolean;
            /**
             * Whether the DBS was issued by the current employer
             */
            dbsIssuedByCurrentEmployer?: boolean;
            /**
             * Whether a Section 128 check is required for the candidate.
             */
            section128Required?: boolean;
            /**
             * The date when the Section 128 check was completed.
             */
            section128Checked?: string;
          };
          /**
           * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
           */
          criminalRecordDeclaration?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * The answers recorded for the point-in-time question set.
             *
             * @maxItems 100
             */
            answers?: {
              /**
               * The identifier of the question being answered.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              questionId?: string;
              /**
               * The stable key of the question being answered.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * The submitted answer to the question.
               */
              value?: string | string | boolean | number | (void | null);
            }[];
            /**
             * When the question set was submitted.
             *
             * @format date-time
             */
            submittedAt?: string;
            /**
             * The network address recorded when the response was submitted.
             *
             * @maxLength 64
             */
            submittedIp?: string;
            /**
             * When the question configuration captured by this response was last updated.
             *
             * @format date-time
             */
            configurationUpdatedAt?: string;
          };
          /**
           * Whether the candidate confirmed their criminal record declaration.
           */
          criminalRecordCheckConsent?: boolean;
          /**
           * The name used to confirm the criminal record declaration.
           *
           * @maxLength 256
           */
          criminalRecordCheckConsentName?: string;
          /**
           * The date the criminal record declaration was confirmed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          criminalRecordCheckConsentDate?: string;
          safeInEducation?: {
            /**
             * Whether the candidate is barred from working in education
             */
            barred?: boolean;
            /**
             * The date when the barred status was checked
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            barredCheckDate?: string;
            /**
             * Whether the candidate has a waiver for the barred status
             */
            waiver?: boolean;
            /**
             * Any orders related to the candidate's barred status
             */
            ordersYou?: boolean;
            /**
             * Any orders related to the candidate's barred status for children
             */
            ordersChild?: boolean;
            /**
             * Whether the candidate has any previous safeguarding allegations
             */
            previousSafeguardingAllegations?: boolean;
            /**
             * Whether the candidate has been cautioned
             */
            cautioned?: boolean;
            /**
             * Whether the candidate has a violent offence
             */
            violentOffence?: boolean;
            /**
             * Whether the candidate has lived or worked in another country
             */
            anotherCountry?: boolean;
            /**
             * Whether the candidate has read the Keeping Children Safe in Education document
             */
            readKCSIE?: boolean;
            /**
             * Whether the candidate has read the Disqualification under the Childcare Act guidance
             */
            readChildcareAct?: boolean;
            /**
             * Whether the candidate is prohibited from teaching
             */
            prohibitedFromTeaching?: boolean;
            /**
             * Whether the candidate is known to the police
             */
            knownPolice?: boolean;
            /**
             * Whether the candidate is prohibited from management roles
             */
            prohibitedFromManagement?: boolean;
            /**
             * Whether the candidate has any criminal offences under law
             */
            criminalOffencesLaw?: boolean;
            /**
             * Any additional details regarding the candidate's safe in education status
             */
            safeInEducationDetails?: string;
            safeguardingQuestionaire?: {
              /**
               * What is the primary purpose of safeguarding in a school environment?
               */
              primaryPurposeSafeguarding?:
                | "enhance-academic-performance"
                | "ensure-safe-learning-environment"
                | "promote-healthy-relationships"
                | "facilitate-extracurricular-activities";
              /**
               * Who is typically responsible for coordinating safeguarding efforts within a school?
               */
              typicallyResponsibleCoordinatingSafeguarding?:
                | "janitorial-staff"
                | "parents-association"
                | "school-nurse"
                | "designated-safeguarding-lead";
              /**
               * Which of the following is considered a potential sign of child abuse or neglect?
               */
              signOfAbuse?:
                | "student-excelling"
                | "frequent-extracurricular"
                | "unexplained-injuries"
                | "not-listening";
              /**
               * How should a teacher respond if a student discloses information about potential abuse or harm?
               */
              studentDisclosesInformation?:
                | "ignore-disclosure"
                | "report-disclosure"
                | "share-information"
                | "advise-independent";
              /**
               * What is the purpose of a school's Child Protection Policy?
               */
              schoolsChildProtectionPolicy?:
                | "enforcing-strict-disciplinary-measures"
                | "identifying-troublemakers"
                | "outlining-procedures-for-protecting-students-from-harm"
                | "establishing-academic-achievement-standards";
              /**
               * What does the term 'FGM' stand for in the context of safeguarding?
               */
              fgmDefinition?:
                | "female-general-medicine"
                | "forced-genetic-modification"
                | "female-genital-mutilation"
                | "foundation-gender-equality";
              /**
               * Which of the following is a key principle of online safety for students?
               */
              keyPrincipleOnlineSafetyStudents?:
                | "unlimited-screen-time"
                | "anon-online-interaction"
                | "responsible-digital-citizen"
                | "restrict-edu-sites";
              /**
               * When planning a school trip, what should teachers consider regarding safeguarding?
               */
              teachersConsiderRegardingSafeguarding?:
                | "minimise-supervision"
                | "appropriate-staff-ratios"
                | "exclude-medical-conditions"
                | "allow-roaming";
              /**
               * What is the purpose of a criminal background check for teachers?
               */
              criminalBackgroundCheckTeachers?:
                | "evaluating-teaching-proficiency"
                | "verifying-immigration-status"
                | "assessing-financial-stability"
                | "identifying-criminal-history";
              /**
               * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
               */
              studentConfides?:
                | "assure-secret-safe"
                | "encourage-share-friends"
                | "explain-no-guarantee"
                | "disclose-to-others";
            };
          };
          socialMedia?: {
            /**
             * The Facebook profile URL of the candidate
             *
             * @maxLength 256
             */
            facebook?: string;
            /**
             * The Instagram profile URL of the candidate
             *
             * @maxLength 256
             */
            instagram?: string;
            /**
             * The Twitter profile URL of the candidate
             *
             * @maxLength 256
             */
            twitter?: string;
            /**
             * The LinkedIn profile URL of the candidate
             *
             * @maxLength 256
             */
            linkedIn?: string;
            /**
             * The TikTok profile URL of the candidate
             *
             * @maxLength 256
             */
            tiktok?: string;
            /**
             * Any other social media profile URLs of the candidate
             *
             * @maxLength 256
             */
            others?: string;
            /**
             * Whether the social media profiles have been checked
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            socialMediaChecked?: string;
            /**
             * The date the candidate's Google search check was completed
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            googleCheckedDate?: string;
          };
          /**
           * Whether the candidate has consented to a safe in education check
           */
          safeInEducationCheckConsent?: boolean;
          /**
           * The name of the person who provided consent for the safe in education check
           *
           * @maxLength 256
           */
          safeInEducationCheckConsentName?: string;
          /**
           * The date when consent for the safe in education check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          safeInEducationCheckConsentDate?: string;
          /**
           * The health record of the individual
           */
          healthRecord?: {
            /**
             * Whether the individual has a physical ability condition
             */
            physicalAbilityCondition?: boolean;
            /**
             * Whether the individual is confused
             */
            confused?: boolean;
            /**
             * Whether the individual has hearing issues
             */
            hearing?: boolean;
            /**
             * Whether the individual has eyesight issues
             */
            eyesight?: boolean;
            /**
             * Whether the individual has depression
             */
            depression?: boolean;
            /**
             * Whether the individual has severe pain
             */
            severePain?: boolean;
            /**
             * Whether the individual has excessive drowsiness
             */
            excessiveDrowsiness?: boolean;
            /**
             * Whether the individual has epilepsy
             */
            epilepsy?: boolean;
            /**
             * Whether the individual has heart issues
             */
            heart?: boolean;
            /**
             * Whether the individual has asthma
             */
            asthma?: boolean;
            /**
             * Whether the individual has mental health issues
             */
            mentalHealth?: boolean;
            /**
             * Whether the individual is an expectant mother
             */
            expectantMother?: boolean;
            /**
             * Whether the individual has allergies
             */
            allergies?: boolean;
            /**
             * Whether the individual is taking medication
             */
            medication?: boolean;
            /**
             * Whether the individual has any health issues
             */
            healthIssues?: boolean;
            /**
             * A description of the individual's health issues
             *
             * @maxLength 2048
             */
            healthIssuesDescription?: string;
            /**
             * Any disability needs of the individual
             *
             * @maxLength 2048
             */
            disabilityNeeds?: string;
            /**
             * Whether the individual has any contagious diseases
             */
            contagiousDiseases?: boolean;
            /**
             * Whether the individual's health affects their ability
             */
            affectAbility?: boolean;
            /**
             * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
             */
            notificationAgree?: boolean;
            /**
             * Whether the individual has opted into the SAS Wellbeing App
             */
            sasOptIn?: boolean;
            /**
             * The individual's signature
             *
             * @maxLength 256
             */
            signature?: string;
            /**
             * The individual's drawn signature in base64url format
             *
             * @maxLength 4096
             */
            drawnSignature?: string;
            /**
             * The date the individual signed the form
             *
             * @format date-time
             */
            signatureDate?: string | null;
            /**
             * Whether the individual has been vaccinated against measles
             */
            vaccinationMeasles?: boolean;
            /**
             * Whether the individual has been vaccinated against mumps
             */
            vaccinationMumps?: boolean;
            /**
             * Whether the individual has been vaccinated against rubella
             */
            vaccinationRubella?: boolean;
            /**
             * Whether the individual has been vaccinated against varicella
             */
            vaccinationVaricella?: boolean;
            /**
             * Whether the individual has been vaccinated against tuberculosis
             */
            vaccinationTuberculosis?: boolean;
            /**
             * Whether the individual has been vaccinated against hepatitis B
             */
            vaccinationHepatitisB?: boolean;
            /**
             * Whether the individual has been vaccinated against hepatitis C
             */
            vaccinationHepatitisC?: boolean;
            /**
             * Whether the individual has been vaccinated against HIV
             */
            vaccinationHIV?: boolean;
            /**
             * Whether the individual has been vaccinated against COVID-19
             */
            vaccinationCovid?: boolean;
            /**
             * Whether the individual has received the 1st dose of the COVID-19 vaccine
             */
            vaccinationCovid1?: boolean;
            /**
             * Whether the individual has received the 2nd dose of the COVID-19 vaccine
             */
            vaccinationCovid2?: boolean;
            /**
             * Whether the individual has received the COVID-19 booster vaccine
             */
            vaccinationCovidBooster?: boolean;
            /**
             * Whether the individual has received the COVID-19 influenza vaccine
             */
            vaccinationCovidInfluenza?: boolean;
            /**
             * Whether the individual has an override for fit to work requirements
             */
            fitToWorkOverride?: boolean;
          };
          /**
           * The candidate's latest submitted health declaration, including the exact questions presented.
           */
          healthDeclaration?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * The answers recorded for the point-in-time question set.
             *
             * @maxItems 100
             */
            answers?: {
              /**
               * The identifier of the question being answered.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              questionId?: string;
              /**
               * The stable key of the question being answered.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * The submitted answer to the question.
               */
              value?: string | string | boolean | number | (void | null);
            }[];
            /**
             * When the question set was submitted.
             *
             * @format date-time
             */
            submittedAt?: string;
            /**
             * The network address recorded when the response was submitted.
             *
             * @maxLength 64
             */
            submittedIp?: string;
            /**
             * When the question configuration captured by this response was last updated.
             *
             * @format date-time
             */
            configurationUpdatedAt?: string;
          };
          /**
           * Whether the candidate confirmed their health declaration.
           */
          healthRecordCheckConsent?: boolean;
          /**
           * The name used to confirm the health declaration.
           *
           * @maxLength 256
           */
          healthRecordCheckConsentName?: string;
          /**
           * The date the health declaration was confirmed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          healthRecordCheckConsentDate?: string;
          /**
           * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
           */
          bankDetails?: {
            /**
             * The name on the bank account
             */
            bankAccountName?: string;
            /**
             * The name of the bank
             */
            bankName?: string;
            /**
             * The account number
             */
            accountNumber?: string;
            /**
             * The sort code
             */
            sortCode?: string;
            /**
             * The National Insurance number
             */
            niNumber?: string;
            /**
             * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
             */
            jobOp1?: boolean;
            /**
             * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
             */
            jobOp2?: boolean;
            /**
             * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
             */
            jobOp3?: boolean;
            studentLoan?: {
              /**
               * Boolean flag for if candidate has student loan
               */
              studentLoan?: boolean;
              /**
               * Boolean flag for if candidate is on plan 1
               */
              plan1?: boolean;
              /**
               * Boolean flag for if candidate is on plan 2
               */
              plan2?: boolean;
              /**
               * Boolean flag for if candidate is on plan 4
               */
              plan4?: boolean;
              /**
               * Boolean flag for if candidate has postgrad loan
               */
              postGradLoan?: boolean;
            };
          };
          /**
           * Whether the candidate has confirmed their details are correct.
           */
          declarationCheckConsent?: boolean;
          /**
           * The name of the person who confirmed their details are correct.
           *
           * @maxLength 256
           */
          declarationCheckConsentName?: string;
          /**
           * The date when consent for the declaration check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          declarationCheckConsentDate?: string;
          /**
           * Whether the candidate has consented to a contract for service
           */
          contractForServiceConsent?: boolean;
          /**
           * The name of the person who provided consent for the contract for service
           *
           * @maxLength 256
           */
          contractForServiceName?: string;
          /**
           * The date when consent for the contract for service was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          contractForServiceDate?: string;
          /**
           * The candidate has completed their onboarding form.
           */
          confirmed?: boolean;
          /**
           * The ID of the user who created the candidate record.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          createdBy?: string;
          /**
           * The ID of the customer associated with the candidate.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customerId?: string;
          /**
           * The migration that created this candidate
           *
           * @pattern ^[0-9a-f]{24}$
           */
          migrationId?: string;
          /**
           * Whether this migrated candidate has been approved
           */
          migrationApproval?: "pending" | "approved" | "rejected";
          /**
           * The user assigned to the candidate. If expanded, a user object else the user ID.
           */
          assignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * The ID of the current booking for the candidate.
           */
          currentBooking?: string[];
          /**
           * The URL of the candidate's profile photo.
           *
           * @maxLength 256
           */
          profilePhoto?: string;
          /**
           * The pay type for the candidate.
           */
          payType?: "PAYE" | "Umbrella" | "Limited Company" | "Not Applicable";
          /**
           * The pay interval for the candidate.
           */
          payInterval?:
            | "Monthly"
            | "Weekly"
            | "Weekly Pay in Arrears"
            | "Bi-Weekly"
            | any
            | null;
          /**
           * The pay rate for the candidate.
           */
          payRate?: string;
          /**
           * A custom ID for the candidate.
           *
           * @maxLength 256
           */
          customId?: string;
          /**
           * A company-defined identifier used to match the candidate with external records.
           *
           * @maxLength 256
           */
          _internalId?: string;
          /**
           * The date when the candidate's status was last changed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          statusChangeDate?: string;
          /**
           * Whether the candidate has opted out of communications.
           */
          optOut?: boolean;
          /**
           * The TRN number for the candidate.
           *
           * @maxLength 256
           */
          trnNumber?: string;
          /**
           * The date when the TRN number was last checked.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          trnNumberCheckedDate?: string;
          /**
           * Any notes related to the TRN number.
           *
           * @maxLength 1024
           */
          trnNumberNotes?: string;
          /**
           * The date when the candidate's employment is set to start.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          employmentStartDate?: string;
          /**
           * Whether the candidate has confirmed their Early Years Workforce status.
           */
          ewcConfirmation?: boolean;
          /**
           * The share code for the candidate's Home Office application.
           *
           * @maxLength 256
           */
          homeOfficeShareCode?: string;
          /**
           * Whether the candidate has been archived.
           */
          archived?: boolean;
          /**
           * Whether the candidate is using an umbrella company.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          umbrella?: string;
          /**
           * ID of the PAYE umbrella company.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          payeUmbrella?: string;
          /**
           * The name of the umbrella company the candidate is using.
           *
           * @maxLength 256
           */
          umbrellaCompanyName?: string;
          /**
           * The code of the umbrella company the candidate is using.
           *
           * @maxLength 256
           */
          umbrellaCompanyCode?: string;
          /**
           * The transport method for the candidate.
           */
          transport?:
            | "Drives Own Vehicle"
            | "Use Public Transport"
            | "Access to Another Vehicle"
            | "Bicycle"
            | "No Access"
            | "Other";
          /**
           * Any important notes related to the candidate.
           *
           * @maxLength 1024
           */
          importantNotes?: string;
          /**
           * The date when the candidate was last updated.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          candidateLastUpdated?: string;
          /**
           * The source from which the candidate was obtained.
           */
          source?:
            | (
                | "Company Website"
                | "iCan Teach UK Website"
                | "Apply to Education"
                | "CV Library - CV Search"
                | "CV Library Advert"
                | "CV Library - Watchdog"
                | "Guardian- CV Search"
                | "Guardian Advert"
                | "Indeed - CV Search"
                | "Indeed Advert"
                | "Reed - CV Search"
                | "Reed Advert"
                | "Reed \u2013 Watchdog"
                | "SENploy - Advert"
                | "Total Jobs"
                | "Other Job Board"
                | "Google"
                | "LinkedIn"
                | "Linkedin Recruiter"
                | "LinkedIn Advert"
                | "Referral - School"
                | "Referral - Candidate"
                | "UK University"
                | "Irish University"
                | "Canadian University"
                | "Marketing Campaign"
                | "Woo"
                | "Job Show"
                | "Recruitment Fair"
                | "Career Fair"
                | "Volcanic"
                | "Broadbean"
                | "Headhunt"
                | "Referral"
                | "Direct"
                | "Other"
              )
            | (
                | "Company Website"
                | "Direct Call"
                | "CV Library Watchdog"
                | "CV Library Advert"
                | "CV Library CV Search"
                | "Reed Watchdog"
                | "Reed Advert"
                | "Reed CV Search"
                | "Indeed Watchdog"
                | "Indeed Advert"
                | "Indeed CV Search"
                | "Job Centre"
                | "Returning Candidate"
                | "Referral Candidate"
                | "Referral Client"
                | "Training Provider"
                | "Recruitment Fair"
                | "University/College"
              );
          /**
           * Any other source from which the candidate was obtained.
           *
           * @maxLength 256
           */
          sourceOther?: string;
          /**
           * Whether the candidate has given consent for online checks.
           */
          onlineCheckConsent?: boolean;
          /**
           * The title of the candidate.
           */
          title?:
            | "Mr"
            | "Mrs"
            | "Ms"
            | "Miss"
            | "Dr"
            | "Mx"
            | "Lady"
            | "Lord"
            | "Other";
          /**
           * The pronoun used by the candidate.
           */
          pronoun?:
            | "She/Her/Hers"
            | "He/Him/His"
            | "They/Them/Theirs"
            | "Ze/Hir/Hirs"
            | "Ze/Zir/Zirs"
            | "Other"
            | "Prefer not to say";
          /**
           * The date when the candidate was last updated.
           *
           * @format date-time
           */
          updatedAt?: string;
          /**
           * The date when the candidate was last contacted.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          lastContactedDate?: string;
          /**
           * A short, prominent note shown with the candidate to communicate important context.
           */
          highlight?: string;
          /**
           * The pay scale for the candidate's teaching position.
           *
           * @maxLength 256
           */
          teacherPayScale?: string;
          /**
           * Whether the candidate has a P45.
           */
          p45?: boolean;
          /**
           * The type of role the candidate is applying for.
           */
          roleType?:
            | "Senior Leadership Positions"
            | "Teaching Roles"
            | "School Support Positions";
          /**
           * The Right to Work verification status for the candidate.
           *
           * @maxLength 256
           */
          rtwVerification?: string;
          /**
           * Any notes related to the Right to Work verification status for the candidate.
           *
           * @maxLength 1024
           */
          rtwVerificationNote?: string;
          /**
           * The Right to Work verification status for the candidate.
           *
           * @maxLength 256
           */
          rtwVerificationStatus?: string;
          /**
           * The location where the candidate desires to work.
           *
           * @maxLength 256
           */
          desiredLocation?: string;
          /**
           * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
           */
          complianceUser?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
           */
          watchers?:
            | string[]
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              }[];
          /**
           * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
           */
          tags?:
            | string[]
            | {
                /**
                 * The unique identifier of the tag
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The label displayed wherever this tag is applied.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                name?: string;
                /**
                 * The tag display colour as a hexadecimal value, for example #2563EB.
                 *
                 * @maxLength 32
                 */
                color?: string | null;
              }[];
          /**
           * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
           *
           * @maxItems 5
           */
          documents?: {
            /**
             * The unique identifier of the document
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The name of the document
             *
             * @maxLength 512
             */
            name?: string;
            /**
             * The file reference used to retrieve the document
             *
             * @maxLength 2048
             */
            reference?: string;
            /**
             * The file's media type
             *
             * @maxLength 256
             */
            fileType?: string;
            /**
             * The area of the platform the document belongs to
             *
             * @maxLength 256
             */
            type?: string;
            /**
             * The configured category of the document
             *
             * @maxLength 256
             */
            docType?: string;
            /**
             * The document's expiry date, when applicable
             *
             * @maxLength 64
             */
            expiry?: string;
            /**
             * The document's issued date, when applicable
             *
             * @maxLength 64
             */
            issued?: string;
            /**
             * Whether the document has been archived
             */
            documentArchived?: boolean;
            /**
             * The date the document was uploaded
             *
             * @format date-time
             */
            createdAt?: string;
          }[];
          /**
           * The total number of documents attached to the candidate.
           *
           * @minimum 0
           */
          documentsCount?: number;
          /**
           * The locations where the candidate desires to work.
           */
          desiredLocationMultiple?: string[];
          /**
           * Daily availability for the requested period. Returned by the availability view only.
           *
           * @maxItems 31
           */
          availabilityDays?: {
            /**
             * Calendar date in YYYY-MM-DD format.
             *
             * @maxLength 10
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date?: string;
            /**
             * Availability or booking state for this date.
             */
            status?:
              | "available"
              | "unavailable"
              | "booked"
              | "halfDay"
              | "awaiting"
              | "unknown";
            /**
             * Recorded availability, or null when unrecorded.
             */
            available?: boolean | null;
            /**
             * Morning or afternoon availability.
             */
            partial?: "AM" | "PM" | any | null;
            /**
             * Additional availability information.
             *
             * @maxLength 4096
             */
            note?: string | null;
            /**
             * Active bookings on this date.
             *
             * @maxItems 100
             */
            bookings?: {
              /**
               * The unique identifier of the resource
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * @maxLength 512
               */
              clientName?: string;
              /**
               * @maxLength 512
               */
              role?: string;
              /**
               * @maxLength 32
               */
              timeDesignation?: string;
            }[];
          }[];
          /**
           * The date when the candidate's availability was last checked.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          availabilityLastChecked?: string;
          /**
           * Whether the candidate is subject to notice period.
           */
          subjectToNotice?: "Unsure" | "Yes" | "No";
          /**
           * The subsidiary subjects the candidate is qualified to teach.
           */
          subsidiarySubject?: string[];
          /**
           * The candidate's preferred employment type.
           */
          employmentPreference?: (
            | "Day To Day"
            | "Long Term"
            | "Fixed Term"
            | "Perm"
          )[];
          /**
           * Any other names the candidate is known by.
           *
           * @maxLength 256
           */
          otherNames?: string;
          /**
           * The name of the candidate's limited company, if applicable.
           *
           * @maxLength 256
           */
          ltdCompany?: string;
          /**
           * Whether the candidate's employment has been verified.
           */
          employmentVerified?: boolean;
          /**
           * The DBS verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSVerification?: string;
          /**
           * Any notes related to the DBS verification status for the candidate.
           *
           * @maxLength 1024
           */
          candidateDBSVerificationNote?: string;
          /**
           * The DBS verification status for the candidate.
           */
          candidateDBSVerificationStatus?: "Cleared" | "Alerts";
          /**
           * The DBS check verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSCheckVerification?: string;
          /**
           * Any notes related to the DBS check verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSCheckVerificationNote?: string;
          /**
           * The DBS check verification status for the candidate.
           */
          candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
          /**
           * Any notes related to the DBS update service verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSUpdateServiceVerificationNote?: string;
          /**
           * The DBS update service verification status for the candidate.
           */
          candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
          /**
           * Any notes related to the TRN verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateTRNVerificationNote?: string;
          /**
           * The TRN verification status for the candidate.
           */
          candidateTRNVerificationStatus?:
            | "Completed Induction"
            | "Not Yet Completed Induction"
            | "Induction Exempt"
            | "ALERTS - Failed Induction"
            | "Induction Not Required";
          /**
           * The health and safety verification status for the candidate.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          candidateHealthSafetyVerification?: string;
          /**
           * Any notes related to the health and safety verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateHealthSafetyVerificationNote?: string;
          /**
           * The health and safety verification status for the candidate.
           */
          candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
          /**
           * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
           */
          onboardingSession?:
            | string
            | {
                /**
                 * Unique identifier for the onboarding session
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The URL for the onboarding session
                 *
                 * @format uri
                 */
                url?: string;
                /**
                 * The frozen health declaration configuration for this onboarding session.
                 */
                healthDeclarationConfig?: {
                  /**
                   * The ordered questions included in the question set.
                   *
                   * @maxItems 100
                   */
                  questions?: {
                    /**
                     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id?: string;
                    /**
                     * A stable key used to associate the question with current and historical answers.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    key?: string;
                    /**
                     * The question shown to the person completing the form.
                     *
                     * @minLength 1
                     * @maxLength 500
                     */
                    label?: string;
                    /**
                     * Optional supporting text displayed with the question.
                     *
                     * @maxLength 2000
                     */
                    description?: string;
                    /**
                     * The type of answer collected for the question.
                     */
                    type?:
                      | "checkbox"
                      | "date"
                      | "daterange"
                      | "dropdown"
                      | "number"
                      | "signature"
                      | "text"
                      | "textarea";
                    /**
                     * Whether an answer is required while the question is visible.
                     */
                    required?: boolean;
                    /**
                     * Whether the question is included in new forms. Historical responses retain disabled questions.
                     */
                    enabled?: boolean;
                    /**
                     * The display order of the question.
                     *
                     * @minimum 0
                     * @maximum 1000
                     */
                    order?: number;
                    /**
                     * Optional rules that determine when the question is shown.
                     */
                    condition?: {
                      /**
                       * Whether any rule or every rule must match before the question is shown.
                       */
                      mode?: "any" | "all";
                      /**
                       * The comparisons used to determine question visibility.
                       *
                       * @minItems 1
                       * @maxItems 20
                       */
                      rules?: {
                        /**
                         * The stable key of the question that controls visibility.
                         *
                         * @minLength 1
                         * @maxLength 128
                         */
                        questionKey?: string;
                        /**
                         * How the controlling answer is compared with the value.
                         */
                        operator?: "equals" | "notEquals";
                        /**
                         * The answer value used to determine visibility.
                         */
                        value?: string | boolean | number | (void | null);
                      }[];
                    };
                    /**
                     * The available answers for a dropdown question.
                     *
                     * @minItems 1
                     * @maxItems 100
                     */
                    options?: {
                      /**
                       * The value stored when this option is selected.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      value?: string;
                      /**
                       * The option displayed to the person answering the question.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      label?: string;
                    }[];
                    /**
                     * Whether this question belongs to the standard template.
                     */
                    ["default"]?: boolean;
                    /**
                     * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                     */
                    audience?: "candidate" | "staff";
                    /**
                     * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                     */
                    fixedRole?: "fullName" | "signature";
                  }[];
                  /**
                   * When this question configuration was last updated.
                   *
                   * @format date-time
                   */
                  updatedAt?: string;
                };
                /**
                 * The frozen Criminal Record & Security Checks configuration for this onboarding session.
                 */
                criminalRecordConfig?: {
                  /**
                   * The ordered questions included in the question set.
                   *
                   * @maxItems 100
                   */
                  questions?: {
                    /**
                     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id?: string;
                    /**
                     * A stable key used to associate the question with current and historical answers.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    key?: string;
                    /**
                     * The question shown to the person completing the form.
                     *
                     * @minLength 1
                     * @maxLength 500
                     */
                    label?: string;
                    /**
                     * Optional supporting text displayed with the question.
                     *
                     * @maxLength 2000
                     */
                    description?: string;
                    /**
                     * The type of answer collected for the question.
                     */
                    type?:
                      | "checkbox"
                      | "date"
                      | "daterange"
                      | "dropdown"
                      | "number"
                      | "signature"
                      | "text"
                      | "textarea";
                    /**
                     * Whether an answer is required while the question is visible.
                     */
                    required?: boolean;
                    /**
                     * Whether the question is included in new forms. Historical responses retain disabled questions.
                     */
                    enabled?: boolean;
                    /**
                     * The display order of the question.
                     *
                     * @minimum 0
                     * @maximum 1000
                     */
                    order?: number;
                    /**
                     * Optional rules that determine when the question is shown.
                     */
                    condition?: {
                      /**
                       * Whether any rule or every rule must match before the question is shown.
                       */
                      mode?: "any" | "all";
                      /**
                       * The comparisons used to determine question visibility.
                       *
                       * @minItems 1
                       * @maxItems 20
                       */
                      rules?: {
                        /**
                         * The stable key of the question that controls visibility.
                         *
                         * @minLength 1
                         * @maxLength 128
                         */
                        questionKey?: string;
                        /**
                         * How the controlling answer is compared with the value.
                         */
                        operator?: "equals" | "notEquals";
                        /**
                         * The answer value used to determine visibility.
                         */
                        value?: string | boolean | number | (void | null);
                      }[];
                    };
                    /**
                     * The available answers for a dropdown question.
                     *
                     * @minItems 1
                     * @maxItems 100
                     */
                    options?: {
                      /**
                       * The value stored when this option is selected.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      value?: string;
                      /**
                       * The option displayed to the person answering the question.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      label?: string;
                    }[];
                    /**
                     * Whether this question belongs to the standard template.
                     */
                    ["default"]?: boolean;
                    /**
                     * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                     */
                    audience?: "candidate" | "staff";
                    /**
                     * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                     */
                    fixedRole?: "fullName" | "signature";
                  }[];
                  /**
                   * When this question configuration was last updated.
                   *
                   * @format date-time
                   */
                  updatedAt?: string;
                };
              };
          /**
           * The contact record for this candidate. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * Whether the candidate has opted out of 48 hour communications.
           */
          optOut48Hours?: boolean;
          /**
           * Whether the candidate has consented to REC audit checks.
           */
          recAuditConsent?: boolean | null;
          /**
           * The date the candidate's REC audit consent was recorded.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          recAuditConsentDate?: string;
          /**
           * The method through which REC audit consent was captured.
           *
           * @maxLength 256
           */
          recAuditConsentMethod?: string;
          /**
           * The date when the candidate record was created.
           *
           * @format date-time
           */
          createdAt?: string;
          /**
           * The mobile app link status for the candidate.
           */
          candidateMobileAppStatus?:
            | "Unlinked"
            | "Scheduled Unlink"
            | "Linked"
            | "Invited"
            | "Not Invited";
          /**
           * Evertime details for this candidate
           */
          evertime?: {
            /**
             * The Evertime CandidateId used for this candidate when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
            /**
             * Whether the matched Evertime candidate's NI number also matches the one held in Edun
             */
            niNumberMatch?: boolean;
          };
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Candidate fields or attached document excerpts that explain why the candidate matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * Whether the matching information came from the candidate record or an attached document.
             */
            source?: "candidate" | "document";
            /**
             * The candidate or document information that matched the search.
             *
             * @maxLength 256
             */
            field?: string;
            /**
             * A short excerpt showing why the result matched.
             *
             * @maxLength 1000
             */
            text?: string;
            /**
             * The exact terms within the content that matched the search, including synonym matches.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
            /**
             * The name of the attached document when the match came from a document.
             *
             * @maxLength 512
             */
            documentName?: string | null;
            /**
             * The unique identifier of the attached document when the match came from a document.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            documentId?: string | null;
          }[];
          /**
           * The candidate's distance in miles from the requested location. Present only when location filtering is used.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * Timesheet Portal details for this candidate
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal contractor GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal contractor code
             *
             * @maxLength 256
             */
            code?: string;
          };
          /**
           * Invite to candidate to link their mobile app.
           */
          mobileAppLinkInvite?: {
            /**
             * Boolean status of whether the link invite is being used
             *
             * @default false
             */
            isUsed?: boolean;
            /**
             * Creation date of the invite
             *
             * @format date-time
             * @default 2026-10-30T13:39:09.027Z
             */
            createdAt?: string;
            /**
             * ID of candidate using this link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            usedBy?: string;
            /**
             * Date of when link used
             *
             * @format date-time
             */
            usedAt?: string;
            /**
             * If unlinked, ID of user that unlinked this candidate's link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            unlinkedBy?: string;
            /**
             * If unlinked, date of when this link was unlinked
             *
             * @format date-time
             */
            unlinkedAt?: string;
            /**
             * If deleted, ID of user that deleted this candidate's link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            deletedBy?: string;
            /**
             * If deleted, date of when this link was deleted
             *
             * @format date-time
             */
            deletedAt?: string;
            /**
             * Current status of this mobile app link
             */
            status?:
              | "Sent"
              | "Linked"
              | "Unlinked"
              | "ScheduledUnlink"
              | "Not Sent"
              | "Rescinded";
          };
        }
      | (void | null);
    /**
     * The Client contact linked to this request.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientContact?: string | null;
    /**
     * The Client linked to the request's Client contact.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientId?: string | null;
    /**
     * The recipient name captured when the request was issued.
     *
     * @minLength 1
     * @maxLength 256
     */
    recipientName?: string | null;
    /**
     * The recipient email captured when the request was issued.
     *
     * @maxLength 320
     * @format email
     */
    recipientEmail?: string | null;
    /**
     * The point-in-time form definition presented to the recipient.
     */
    snapshot?: {
      /**
       * The internal and recipient-facing title of the form.
       *
       * @minLength 1
       * @maxLength 256
       */
      title?: string;
      /**
       * Optional introductory text shown before the questions.
       *
       * @maxLength 5000
       */
      description?: string | null;
      /**
       * Optional message shown after a response is submitted.
       *
       * @maxLength 2000
       */
      confirmationMessage?: string | null;
      /**
       * The ordered questions included in the form.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * The form definition from which this snapshot was captured.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      formId?: string;
      /**
       * When the captured form definition was last updated.
       *
       * @format date-time
       */
      configurationUpdatedAt?: string;
    };
    /**
     * The current lifecycle state of this request.
     */
    status?: "issued" | "inProgress" | "submitted" | "revoked" | "expired";
    /**
     * When the signed request link stops being valid.
     *
     * @format date-time
     */
    expiresAt?: string;
    /**
     * When the recipient first saved answers.
     *
     * @format date-time
     */
    startedAt?: string | null;
    /**
     * When the recipient submitted the response.
     *
     * @format date-time
     */
    submittedAt?: string | null;
    /**
     * When access to the request was revoked.
     *
     * @format date-time
     */
    revokedAt?: string | null;
    /**
     * The user who issued the request. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * Email delivery information for this request.
     */
    delivery?: {
      /**
       * When email delivery was most recently requested.
       *
       * @format date-time
       */
      emailRequestedAt?: string;
      /**
       * When the request email was most recently sent.
       *
       * @format date-time
       */
      emailSentAt?: string;
      /**
       * When the most recent request email failed to send.
       *
       * @format date-time
       */
      emailFailedAt?: string;
    };
    /**
     * When the request was issued.
     *
     * @format date-time
     */
    createdAt?: string;
    /**
     * When the request was last updated.
     *
     * @format date-time
     */
    updatedAt?: string;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetFormRequestsVariables = {
  queryParams?: GetFormRequestsQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetFormRequests = (
  variables: GetFormRequestsVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetFormRequestsResponse,
    GetFormRequestsError,
    undefined,
    {},
    GetFormRequestsQueryParams,
    {}
  >({ url: "/form-requests", method: "get", ...variables, signal });

export function getFormRequestsQuery(variables: GetFormRequestsVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetFormRequestsResponse>;
};

export function getFormRequestsQuery(
  variables: GetFormRequestsVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetFormRequestsResponse>)
    | reactQuery.SkipToken;
};

export function getFormRequestsQuery(
  variables: GetFormRequestsVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/form-requests",
      operationId: "getFormRequests",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetFormRequests(variables, signal),
  };
}

export const useSuspenseGetFormRequests = <TData = GetFormRequestsResponse,>(
  variables: GetFormRequestsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetFormRequestsResponse,
      GetFormRequestsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetFormRequestsResponse,
    GetFormRequestsError,
    TData
  >({
    ...getFormRequestsQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetFormRequests = <TData = GetFormRequestsResponse,>(
  variables: GetFormRequestsVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetFormRequestsResponse,
      GetFormRequestsError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetFormRequestsResponse,
    GetFormRequestsError,
    TData
  >({
    ...getFormRequestsQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type CreateFormRequestError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateFormRequestResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The issued request metadata.
     */
    request?: {
      /**
       * The unique identifier of the issued form request.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The form definition used to create this request.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      formId?: string;
      /**
       * The submitted response for this request, when one is available.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      responseId?: string | null;
      /**
       * The candidate linked to this request. If expanded, a candidate object; otherwise, a candidate ID.
       */
      candidate?:
        | string
        | {
            /**
             * The unique identifier of the candidate
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The forename of the candidate
             *
             * @minLength 1
             * @maxLength 256
             */
            forename?: string;
            /**
             * The surname of the candidate
             *
             * @minLength 1
             * @maxLength 256
             */
            surname?: string;
            /**
             * Any previous names of the candidate
             *
             * @maxLength 256
             */
            previousNames?: string;
            /**
             * The email of the candidate. This will be used for communication.
             *
             * @format email
             */
            email?: string;
            /**
             * The telephone number of the candidate
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * The mobile number of the candidate
             *
             * @maxLength 32
             */
            mobile?: string;
            /**
             * The occupation of the candidate
             */
            occupation?:
              | "Teacher"
              | "Cover Supervisor"
              | "Teaching Assistant"
              | "School Support Staff"
              | "Primary Teacher"
              | "Secondary Teacher"
              | "Head Teacher"
              | "Senior Leader"
              | "Tutor"
              | "Mentor"
              | "Learning Support Assistant"
              | "Finance Officer"
              | "HR Administrator"
              | "HR Manager"
              | "Office Manager"
              | "Administrator"
              | "Personal Assistant"
              | "Pastoral Manager"
              | "Receptionist"
              | "Instructor"
              | "Caretaker"
              | "Nursery Assistant"
              | "Nursery Nurse"
              | "Deputy Nursery Manager"
              | "Lunchtime Supervisor"
              | "Classroom Graduate"
              | "Primary Cover Supervisor"
              | "HLTA"
              | "Nurture Teacher"
              | "Nurture Mentor"
              | "SENDCo"
              | "Registered Manager"
              | "Regional Manager"
              | "Education Director"
              | "Residential Director"
              | "Deputy Manager"
              | "Residential Support Worker"
              | "Sports Coach"
              | "Executive Headteacher"
              | "General Practitioner"
              | "Advance Nurse Practitioner (ANP)"
              | "Emergency Nurse Practitioner (ENP)"
              | "Advanced Clinical Practitioner (ACP)"
              | "Emergency Clinical Practitioner (ECP)"
              | "Consultant Psychiatrist"
              | "Psychiatrist \u2013 SPR"
              | "IT Technician"
              | "Art Technician"
              | "DT Technician"
              | "Science Technician"
              | "Healthcare Assistant/ Residential Support Worker"
              | "Healthcare Assistant"
              | "Senior Healthcare Assistant"
              | "Team Leader"
              | "Registered General Nurse"
              | "Registered Mental Nurse"
              | "Registered Nurse Learning Disabilities"
              | "Overseas Trained Teacher";
            /**
             * The subject the candidate specializes in
             *
             * @maxLength 256
             */
            subject?: string;
            /**
             * The address of the resource
             */
            address?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The qualifications of the candidate
             *
             * @maxItems 32
             */
            qualifications?: {
              /**
               * The type of the qualification
               *
               * @maxLength 256
               */
              type?: string;
              /**
               * The place where the qualification was obtained
               *
               * @maxLength 256
               */
              place?: string;
              /**
               * The name of the qualification
               *
               * @maxLength 256
               */
              qualificationName?: string;
              /**
               * The date the qualification was obtained
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              date?: string;
              /**
               * The unique identifier of the qualification
               *
               * @maxLength 256
               */
              qualificationId?: string;
              /**
               * The order index of the qualification
               *
               * @minimum 0
               */
              orderIndex?: number;
              /**
               * The expiry date of the qualification
               *
               * @maxLength 256
               */
              qualificationExpiry?: string;
              /**
               * Whether to exclude this qualification from vetting
               */
              excludeFromVetting?: boolean;
              /**
               * The date the qualification was created
               *
               * @format date-time
               */
              createdAt?: string | null;
            }[];
            /**
             * The employment history of the candidate
             *
             * @maxItems 32
             */
            employment?: {
              /**
               * The name of the employer
               *
               * @maxLength 256
               */
              employerName?: string;
              /**
               * The date the candidate started employment
               *
               * @maxLength 256
               */
              dateStarted?: string;
              /**
               * The date the candidate ended employment
               *
               * @maxLength 256
               */
              dateEnded?: string;
              /**
               * The job title of the candidate
               *
               * @maxLength 256
               */
              jobTitle?: string;
              /**
               * The reason the candidate left this employment
               *
               * @maxLength 1024
               */
              reasonForLeaving?: string;
              /**
               * The duties of the candidate in this role. Consultant only, visible on Edun CV
               *
               * @maxLength 1024
               */
              duties?: string;
              /**
               * The salary of the candidate in this role
               *
               * @maxLength 256
               */
              salary?: string;
              /**
               * The address of the candidate's reference for this employment
               */
              referenceAddress?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
              /**
               * The unique identifier for this employment record
               *
               * @maxLength 256
               */
              employmentId?: string;
              /**
               * The order index of this employment record
               *
               * @minimum 0
               */
              orderIndex?: number;
            }[];
            /**
             * The references for the candidate
             *
             * @maxItems 32
             */
            references?: {
              /**
               * The title of the reference
               *
               * @maxLength 16
               */
              referenceTitle?: string;
              /**
               * The forename of the reference
               *
               * @maxLength 256
               */
              referenceForename?: string;
              /**
               * The surname of the reference
               *
               * @maxLength 256
               */
              referenceSurname?: string;
              /**
               * The company of the reference
               *
               * @maxLength 256
               */
              referenceCompany?: string;
              /**
               * The position of the reference
               *
               * @maxLength 256
               */
              referencePosition?: string;
              /**
               * The email of the reference
               *
               * @format email
               */
              referenceEmail?: string;
              /**
               * The telephone number of the reference
               *
               * @maxLength 32
               */
              referenceTelephone?: string;
              /**
               * The type of the reference
               */
              referenceType?:
                | "Education"
                | "Professional"
                | "Character"
                | "Factual"
                | "Academic"
                | "Volunteer";
              /**
               * The duration of the reference
               *
               * @maxLength 128
               */
              referenceDuration?: string;
              /**
               * The ID of the reference
               *
               * @maxLength 256
               */
              referenceId?: string;
              /**
               * This field only applies if the reference immediate consent question is enabled
               * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
               */
              referenceApply?: boolean;
              /**
               * Any additional notes about the reference
               *
               * @maxLength 1024
               */
              referenceNote?: string;
              /**
               * The start date of the reference
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              dateFrom?: string;
              /**
               * The end date of the reference
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              dateTo?: string;
            }[];
            /**
             * Whether the candidate has consented to a reference check
             */
            referenceCheckConsent?: boolean;
            /**
             * The name of the person who provided consent for the reference check
             *
             * @maxLength 256
             */
            referenceCheckConsentName?: string;
            /**
             * The date when consent for the reference check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            referenceCheckConsentDate?: string;
            /**
             * The date of birth of the candidate
             *
             * @format date-time
             */
            dob?: string | null;
            /**
             * The sex of the candidate
             */
            sex?: "Male" | "Female" | "Other" | "Unknown";
            /**
             * The status of the candidate
             */
            status?:
              | "Pending"
              | "Approved"
              | "Rejected"
              | "Anonymised"
              | "Archived"
              | "Enquired"
              | "Hold"
              | "Future"
              | "DNC"
              | "Pre";
            /**
             * The current status of the onboarding process for the candidate
             */
            onboardingStatus?: "Not Started" | "In Progress" | "Completed";
            /**
             * Whether the onboarding information has been sent to the candidate
             */
            onboardingSent?: boolean;
            nextOfKin?: {
              /**
               * The name of the next of kin
               *
               * @maxLength 256
               */
              nextOfKinName?: string;
              /**
               * The relationship of the next of kin to the candidate
               *
               * @maxLength 256
               */
              nextOfKinRelationship?: string;
              /**
               * The telephone number of the next of kin
               *
               * @maxLength 32
               */
              nextOfKinTelephone?: string;
              /**
               * The email address of the next of kin
               *
               * @format email
               */
              nextOfKinEmail?: string;
              nextOfKinAddress?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
            };
            criminalRecord?: {
              /**
               * Whether the candidate has a DBS certificate
               */
              dbsHeld?: boolean;
              /**
               * The unique identifier for the DBS certificate
               *
               * @maxLength 256
               */
              dbsNumber?: string;
              /**
               * The type of DBS check
               */
              dbsType?:
                | "Basic"
                | "Standard"
                | "Enhanced"
                | "Enhanced with Barred List"
                | "Child workforce"
                | "Child and adult workforce"
                | "Adult workforce";
              /**
               * The date when the DBS check was last performed
               *
               * @maxLength 256
               */
              dbsLastChecked?: string;
              /**
               * The DBS update service number for the candidate
               *
               * @maxLength 256
               */
              dbsUpdateServiceNumber?: string;
              /**
               * The result of the prohibition check
               *
               * @maxLength 256
               */
              prohibitionCheck?: string;
              /**
               * The result of the overseas check
               *
               * @maxLength 256
               */
              overseasCheck?: string;
              /**
               * Any additional notes regarding the overseas check
               *
               * @maxLength 256
               */
              overseasCheckNote?: string;
              /**
               * Whether the candidate has a convicted offence
               */
              convictedOffence?: boolean;
              /**
               * The description of the convicted offence
               *
               * @maxLength 1024
               */
              convictedOffenceDescription?: string;
              /**
               * Whether the candidate has any convictions
               */
              convictions?: boolean;
              /**
               * The description of the convictions
               *
               * @maxLength 1024
               */
              convictionsDescription?: string;
              /**
               * Whether the candidate is registered to the DBS update service
               */
              registeredToUpdateService?: boolean;
              /**
               * Whether the candidate consents to update service checks
               */
              consentUpdateServiceChecks?: boolean;
              /**
               * Any additional notes regarding the candidate's criminal record
               *
               * @maxLength 1024
               */
              note?: string;
              /**
               * The date when the update service check was performed
               *
               * @maxLength 64
               */
              updateServiceCheckDate?: string;
              /**
               * Whether the candidate has lived abroad
               */
              abroad?: boolean;
              /**
               * Any additional notes regarding the overseas declaration
               *
               * @maxLength 1024
               */
              overseasDeclarationExtended?: string;
              /**
               * Whether the candidate has been subject to educational disciplinary proceedings
               */
              educationalDisciplinaryProceedings?: boolean;
              /**
               * Any comments regarding the DBS check
               *
               * @maxLength 1024
               */
              dbsComments?: string;
              /**
               * Whether the candidate has read the DBS declaration
               */
              dbsDeclarationRead?: boolean;
              /**
               * Whether the candidate has any overseas offences
               */
              overseasOffence?: boolean;
              /**
               * The description of the overseas offence
               *
               * @maxLength 1024
               */
              overseasOffenceDescription?: string;
              /**
               * Whether the candidate has a current criminal offence
               */
              currentCriminalOffence?: boolean;
              /**
               * The description of the current criminal offence
               *
               * @maxLength 1024
               */
              currentCriminalOffenceDescription?: string;
              /**
               * Whether the DBS was issued by a business
               */
              dbsIssuedByBusiness?: boolean;
              /**
               * Whether the DBS was issued by the current employer
               */
              dbsIssuedByCurrentEmployer?: boolean;
              /**
               * Whether a Section 128 check is required for the candidate.
               */
              section128Required?: boolean;
              /**
               * The date when the Section 128 check was completed.
               */
              section128Checked?: string;
            };
            /**
             * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
             */
            criminalRecordDeclaration?: {
              /**
               * The ordered questions included in the question set.
               *
               * @maxItems 100
               */
              questions?: {
                /**
                 * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * A stable key used to associate the question with current and historical answers.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                key?: string;
                /**
                 * The question shown to the person completing the form.
                 *
                 * @minLength 1
                 * @maxLength 500
                 */
                label?: string;
                /**
                 * Optional supporting text displayed with the question.
                 *
                 * @maxLength 2000
                 */
                description?: string;
                /**
                 * The type of answer collected for the question.
                 */
                type?:
                  | "checkbox"
                  | "date"
                  | "daterange"
                  | "dropdown"
                  | "number"
                  | "signature"
                  | "text"
                  | "textarea";
                /**
                 * Whether an answer is required while the question is visible.
                 */
                required?: boolean;
                /**
                 * Whether the question is included in new forms. Historical responses retain disabled questions.
                 */
                enabled?: boolean;
                /**
                 * The display order of the question.
                 *
                 * @minimum 0
                 * @maximum 1000
                 */
                order?: number;
                /**
                 * Optional rules that determine when the question is shown.
                 */
                condition?: {
                  /**
                   * Whether any rule or every rule must match before the question is shown.
                   */
                  mode?: "any" | "all";
                  /**
                   * The comparisons used to determine question visibility.
                   *
                   * @minItems 1
                   * @maxItems 20
                   */
                  rules?: {
                    /**
                     * The stable key of the question that controls visibility.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    questionKey?: string;
                    /**
                     * How the controlling answer is compared with the value.
                     */
                    operator?: "equals" | "notEquals";
                    /**
                     * The answer value used to determine visibility.
                     */
                    value?: string | boolean | number | (void | null);
                  }[];
                };
                /**
                 * The available answers for a dropdown question.
                 *
                 * @minItems 1
                 * @maxItems 100
                 */
                options?: {
                  /**
                   * The value stored when this option is selected.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  value?: string;
                  /**
                   * The option displayed to the person answering the question.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  label?: string;
                }[];
                /**
                 * Whether this question belongs to the standard template.
                 */
                ["default"]?: boolean;
                /**
                 * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                 */
                audience?: "candidate" | "staff";
                /**
                 * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                 */
                fixedRole?: "fullName" | "signature";
              }[];
              /**
               * The answers recorded for the point-in-time question set.
               *
               * @maxItems 100
               */
              answers?: {
                /**
                 * The identifier of the question being answered.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                questionId?: string;
                /**
                 * The stable key of the question being answered.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey?: string;
                /**
                 * The submitted answer to the question.
                 */
                value?: string | string | boolean | number | (void | null);
              }[];
              /**
               * When the question set was submitted.
               *
               * @format date-time
               */
              submittedAt?: string;
              /**
               * The network address recorded when the response was submitted.
               *
               * @maxLength 64
               */
              submittedIp?: string;
              /**
               * When the question configuration captured by this response was last updated.
               *
               * @format date-time
               */
              configurationUpdatedAt?: string;
            };
            /**
             * Whether the candidate confirmed their criminal record declaration.
             */
            criminalRecordCheckConsent?: boolean;
            /**
             * The name used to confirm the criminal record declaration.
             *
             * @maxLength 256
             */
            criminalRecordCheckConsentName?: string;
            /**
             * The date the criminal record declaration was confirmed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            criminalRecordCheckConsentDate?: string;
            safeInEducation?: {
              /**
               * Whether the candidate is barred from working in education
               */
              barred?: boolean;
              /**
               * The date when the barred status was checked
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              barredCheckDate?: string;
              /**
               * Whether the candidate has a waiver for the barred status
               */
              waiver?: boolean;
              /**
               * Any orders related to the candidate's barred status
               */
              ordersYou?: boolean;
              /**
               * Any orders related to the candidate's barred status for children
               */
              ordersChild?: boolean;
              /**
               * Whether the candidate has any previous safeguarding allegations
               */
              previousSafeguardingAllegations?: boolean;
              /**
               * Whether the candidate has been cautioned
               */
              cautioned?: boolean;
              /**
               * Whether the candidate has a violent offence
               */
              violentOffence?: boolean;
              /**
               * Whether the candidate has lived or worked in another country
               */
              anotherCountry?: boolean;
              /**
               * Whether the candidate has read the Keeping Children Safe in Education document
               */
              readKCSIE?: boolean;
              /**
               * Whether the candidate has read the Disqualification under the Childcare Act guidance
               */
              readChildcareAct?: boolean;
              /**
               * Whether the candidate is prohibited from teaching
               */
              prohibitedFromTeaching?: boolean;
              /**
               * Whether the candidate is known to the police
               */
              knownPolice?: boolean;
              /**
               * Whether the candidate is prohibited from management roles
               */
              prohibitedFromManagement?: boolean;
              /**
               * Whether the candidate has any criminal offences under law
               */
              criminalOffencesLaw?: boolean;
              /**
               * Any additional details regarding the candidate's safe in education status
               */
              safeInEducationDetails?: string;
              safeguardingQuestionaire?: {
                /**
                 * What is the primary purpose of safeguarding in a school environment?
                 */
                primaryPurposeSafeguarding?:
                  | "enhance-academic-performance"
                  | "ensure-safe-learning-environment"
                  | "promote-healthy-relationships"
                  | "facilitate-extracurricular-activities";
                /**
                 * Who is typically responsible for coordinating safeguarding efforts within a school?
                 */
                typicallyResponsibleCoordinatingSafeguarding?:
                  | "janitorial-staff"
                  | "parents-association"
                  | "school-nurse"
                  | "designated-safeguarding-lead";
                /**
                 * Which of the following is considered a potential sign of child abuse or neglect?
                 */
                signOfAbuse?:
                  | "student-excelling"
                  | "frequent-extracurricular"
                  | "unexplained-injuries"
                  | "not-listening";
                /**
                 * How should a teacher respond if a student discloses information about potential abuse or harm?
                 */
                studentDisclosesInformation?:
                  | "ignore-disclosure"
                  | "report-disclosure"
                  | "share-information"
                  | "advise-independent";
                /**
                 * What is the purpose of a school's Child Protection Policy?
                 */
                schoolsChildProtectionPolicy?:
                  | "enforcing-strict-disciplinary-measures"
                  | "identifying-troublemakers"
                  | "outlining-procedures-for-protecting-students-from-harm"
                  | "establishing-academic-achievement-standards";
                /**
                 * What does the term 'FGM' stand for in the context of safeguarding?
                 */
                fgmDefinition?:
                  | "female-general-medicine"
                  | "forced-genetic-modification"
                  | "female-genital-mutilation"
                  | "foundation-gender-equality";
                /**
                 * Which of the following is a key principle of online safety for students?
                 */
                keyPrincipleOnlineSafetyStudents?:
                  | "unlimited-screen-time"
                  | "anon-online-interaction"
                  | "responsible-digital-citizen"
                  | "restrict-edu-sites";
                /**
                 * When planning a school trip, what should teachers consider regarding safeguarding?
                 */
                teachersConsiderRegardingSafeguarding?:
                  | "minimise-supervision"
                  | "appropriate-staff-ratios"
                  | "exclude-medical-conditions"
                  | "allow-roaming";
                /**
                 * What is the purpose of a criminal background check for teachers?
                 */
                criminalBackgroundCheckTeachers?:
                  | "evaluating-teaching-proficiency"
                  | "verifying-immigration-status"
                  | "assessing-financial-stability"
                  | "identifying-criminal-history";
                /**
                 * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
                 */
                studentConfides?:
                  | "assure-secret-safe"
                  | "encourage-share-friends"
                  | "explain-no-guarantee"
                  | "disclose-to-others";
              };
            };
            socialMedia?: {
              /**
               * The Facebook profile URL of the candidate
               *
               * @maxLength 256
               */
              facebook?: string;
              /**
               * The Instagram profile URL of the candidate
               *
               * @maxLength 256
               */
              instagram?: string;
              /**
               * The Twitter profile URL of the candidate
               *
               * @maxLength 256
               */
              twitter?: string;
              /**
               * The LinkedIn profile URL of the candidate
               *
               * @maxLength 256
               */
              linkedIn?: string;
              /**
               * The TikTok profile URL of the candidate
               *
               * @maxLength 256
               */
              tiktok?: string;
              /**
               * Any other social media profile URLs of the candidate
               *
               * @maxLength 256
               */
              others?: string;
              /**
               * Whether the social media profiles have been checked
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              socialMediaChecked?: string;
              /**
               * The date the candidate's Google search check was completed
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              googleCheckedDate?: string;
            };
            /**
             * Whether the candidate has consented to a safe in education check
             */
            safeInEducationCheckConsent?: boolean;
            /**
             * The name of the person who provided consent for the safe in education check
             *
             * @maxLength 256
             */
            safeInEducationCheckConsentName?: string;
            /**
             * The date when consent for the safe in education check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            safeInEducationCheckConsentDate?: string;
            /**
             * The health record of the individual
             */
            healthRecord?: {
              /**
               * Whether the individual has a physical ability condition
               */
              physicalAbilityCondition?: boolean;
              /**
               * Whether the individual is confused
               */
              confused?: boolean;
              /**
               * Whether the individual has hearing issues
               */
              hearing?: boolean;
              /**
               * Whether the individual has eyesight issues
               */
              eyesight?: boolean;
              /**
               * Whether the individual has depression
               */
              depression?: boolean;
              /**
               * Whether the individual has severe pain
               */
              severePain?: boolean;
              /**
               * Whether the individual has excessive drowsiness
               */
              excessiveDrowsiness?: boolean;
              /**
               * Whether the individual has epilepsy
               */
              epilepsy?: boolean;
              /**
               * Whether the individual has heart issues
               */
              heart?: boolean;
              /**
               * Whether the individual has asthma
               */
              asthma?: boolean;
              /**
               * Whether the individual has mental health issues
               */
              mentalHealth?: boolean;
              /**
               * Whether the individual is an expectant mother
               */
              expectantMother?: boolean;
              /**
               * Whether the individual has allergies
               */
              allergies?: boolean;
              /**
               * Whether the individual is taking medication
               */
              medication?: boolean;
              /**
               * Whether the individual has any health issues
               */
              healthIssues?: boolean;
              /**
               * A description of the individual's health issues
               *
               * @maxLength 2048
               */
              healthIssuesDescription?: string;
              /**
               * Any disability needs of the individual
               *
               * @maxLength 2048
               */
              disabilityNeeds?: string;
              /**
               * Whether the individual has any contagious diseases
               */
              contagiousDiseases?: boolean;
              /**
               * Whether the individual's health affects their ability
               */
              affectAbility?: boolean;
              /**
               * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
               */
              notificationAgree?: boolean;
              /**
               * Whether the individual has opted into the SAS Wellbeing App
               */
              sasOptIn?: boolean;
              /**
               * The individual's signature
               *
               * @maxLength 256
               */
              signature?: string;
              /**
               * The individual's drawn signature in base64url format
               *
               * @maxLength 4096
               */
              drawnSignature?: string;
              /**
               * The date the individual signed the form
               *
               * @format date-time
               */
              signatureDate?: string | null;
              /**
               * Whether the individual has been vaccinated against measles
               */
              vaccinationMeasles?: boolean;
              /**
               * Whether the individual has been vaccinated against mumps
               */
              vaccinationMumps?: boolean;
              /**
               * Whether the individual has been vaccinated against rubella
               */
              vaccinationRubella?: boolean;
              /**
               * Whether the individual has been vaccinated against varicella
               */
              vaccinationVaricella?: boolean;
              /**
               * Whether the individual has been vaccinated against tuberculosis
               */
              vaccinationTuberculosis?: boolean;
              /**
               * Whether the individual has been vaccinated against hepatitis B
               */
              vaccinationHepatitisB?: boolean;
              /**
               * Whether the individual has been vaccinated against hepatitis C
               */
              vaccinationHepatitisC?: boolean;
              /**
               * Whether the individual has been vaccinated against HIV
               */
              vaccinationHIV?: boolean;
              /**
               * Whether the individual has been vaccinated against COVID-19
               */
              vaccinationCovid?: boolean;
              /**
               * Whether the individual has received the 1st dose of the COVID-19 vaccine
               */
              vaccinationCovid1?: boolean;
              /**
               * Whether the individual has received the 2nd dose of the COVID-19 vaccine
               */
              vaccinationCovid2?: boolean;
              /**
               * Whether the individual has received the COVID-19 booster vaccine
               */
              vaccinationCovidBooster?: boolean;
              /**
               * Whether the individual has received the COVID-19 influenza vaccine
               */
              vaccinationCovidInfluenza?: boolean;
              /**
               * Whether the individual has an override for fit to work requirements
               */
              fitToWorkOverride?: boolean;
            };
            /**
             * The candidate's latest submitted health declaration, including the exact questions presented.
             */
            healthDeclaration?: {
              /**
               * The ordered questions included in the question set.
               *
               * @maxItems 100
               */
              questions?: {
                /**
                 * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * A stable key used to associate the question with current and historical answers.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                key?: string;
                /**
                 * The question shown to the person completing the form.
                 *
                 * @minLength 1
                 * @maxLength 500
                 */
                label?: string;
                /**
                 * Optional supporting text displayed with the question.
                 *
                 * @maxLength 2000
                 */
                description?: string;
                /**
                 * The type of answer collected for the question.
                 */
                type?:
                  | "checkbox"
                  | "date"
                  | "daterange"
                  | "dropdown"
                  | "number"
                  | "signature"
                  | "text"
                  | "textarea";
                /**
                 * Whether an answer is required while the question is visible.
                 */
                required?: boolean;
                /**
                 * Whether the question is included in new forms. Historical responses retain disabled questions.
                 */
                enabled?: boolean;
                /**
                 * The display order of the question.
                 *
                 * @minimum 0
                 * @maximum 1000
                 */
                order?: number;
                /**
                 * Optional rules that determine when the question is shown.
                 */
                condition?: {
                  /**
                   * Whether any rule or every rule must match before the question is shown.
                   */
                  mode?: "any" | "all";
                  /**
                   * The comparisons used to determine question visibility.
                   *
                   * @minItems 1
                   * @maxItems 20
                   */
                  rules?: {
                    /**
                     * The stable key of the question that controls visibility.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    questionKey?: string;
                    /**
                     * How the controlling answer is compared with the value.
                     */
                    operator?: "equals" | "notEquals";
                    /**
                     * The answer value used to determine visibility.
                     */
                    value?: string | boolean | number | (void | null);
                  }[];
                };
                /**
                 * The available answers for a dropdown question.
                 *
                 * @minItems 1
                 * @maxItems 100
                 */
                options?: {
                  /**
                   * The value stored when this option is selected.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  value?: string;
                  /**
                   * The option displayed to the person answering the question.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  label?: string;
                }[];
                /**
                 * Whether this question belongs to the standard template.
                 */
                ["default"]?: boolean;
                /**
                 * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                 */
                audience?: "candidate" | "staff";
                /**
                 * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                 */
                fixedRole?: "fullName" | "signature";
              }[];
              /**
               * The answers recorded for the point-in-time question set.
               *
               * @maxItems 100
               */
              answers?: {
                /**
                 * The identifier of the question being answered.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                questionId?: string;
                /**
                 * The stable key of the question being answered.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey?: string;
                /**
                 * The submitted answer to the question.
                 */
                value?: string | string | boolean | number | (void | null);
              }[];
              /**
               * When the question set was submitted.
               *
               * @format date-time
               */
              submittedAt?: string;
              /**
               * The network address recorded when the response was submitted.
               *
               * @maxLength 64
               */
              submittedIp?: string;
              /**
               * When the question configuration captured by this response was last updated.
               *
               * @format date-time
               */
              configurationUpdatedAt?: string;
            };
            /**
             * Whether the candidate confirmed their health declaration.
             */
            healthRecordCheckConsent?: boolean;
            /**
             * The name used to confirm the health declaration.
             *
             * @maxLength 256
             */
            healthRecordCheckConsentName?: string;
            /**
             * The date the health declaration was confirmed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            healthRecordCheckConsentDate?: string;
            /**
             * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
             */
            bankDetails?: {
              /**
               * The name on the bank account
               */
              bankAccountName?: string;
              /**
               * The name of the bank
               */
              bankName?: string;
              /**
               * The account number
               */
              accountNumber?: string;
              /**
               * The sort code
               */
              sortCode?: string;
              /**
               * The National Insurance number
               */
              niNumber?: string;
              /**
               * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
               */
              jobOp1?: boolean;
              /**
               * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
               */
              jobOp2?: boolean;
              /**
               * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
               */
              jobOp3?: boolean;
              studentLoan?: {
                /**
                 * Boolean flag for if candidate has student loan
                 */
                studentLoan?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 1
                 */
                plan1?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 2
                 */
                plan2?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 4
                 */
                plan4?: boolean;
                /**
                 * Boolean flag for if candidate has postgrad loan
                 */
                postGradLoan?: boolean;
              };
            };
            /**
             * Whether the candidate has confirmed their details are correct.
             */
            declarationCheckConsent?: boolean;
            /**
             * The name of the person who confirmed their details are correct.
             *
             * @maxLength 256
             */
            declarationCheckConsentName?: string;
            /**
             * The date when consent for the declaration check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            declarationCheckConsentDate?: string;
            /**
             * Whether the candidate has consented to a contract for service
             */
            contractForServiceConsent?: boolean;
            /**
             * The name of the person who provided consent for the contract for service
             *
             * @maxLength 256
             */
            contractForServiceName?: string;
            /**
             * The date when consent for the contract for service was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            contractForServiceDate?: string;
            /**
             * The candidate has completed their onboarding form.
             */
            confirmed?: boolean;
            /**
             * The ID of the user who created the candidate record.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            createdBy?: string;
            /**
             * The ID of the customer associated with the candidate.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customerId?: string;
            /**
             * The migration that created this candidate
             *
             * @pattern ^[0-9a-f]{24}$
             */
            migrationId?: string;
            /**
             * Whether this migrated candidate has been approved
             */
            migrationApproval?: "pending" | "approved" | "rejected";
            /**
             * The user assigned to the candidate. If expanded, a user object else the user ID.
             */
            assignee?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * The ID of the current booking for the candidate.
             */
            currentBooking?: string[];
            /**
             * The URL of the candidate's profile photo.
             *
             * @maxLength 256
             */
            profilePhoto?: string;
            /**
             * The pay type for the candidate.
             */
            payType?:
              | "PAYE"
              | "Umbrella"
              | "Limited Company"
              | "Not Applicable";
            /**
             * The pay interval for the candidate.
             */
            payInterval?:
              | "Monthly"
              | "Weekly"
              | "Weekly Pay in Arrears"
              | "Bi-Weekly"
              | any
              | null;
            /**
             * The pay rate for the candidate.
             */
            payRate?: string;
            /**
             * A custom ID for the candidate.
             *
             * @maxLength 256
             */
            customId?: string;
            /**
             * A company-defined identifier used to match the candidate with external records.
             *
             * @maxLength 256
             */
            _internalId?: string;
            /**
             * The date when the candidate's status was last changed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            statusChangeDate?: string;
            /**
             * Whether the candidate has opted out of communications.
             */
            optOut?: boolean;
            /**
             * The TRN number for the candidate.
             *
             * @maxLength 256
             */
            trnNumber?: string;
            /**
             * The date when the TRN number was last checked.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            trnNumberCheckedDate?: string;
            /**
             * Any notes related to the TRN number.
             *
             * @maxLength 1024
             */
            trnNumberNotes?: string;
            /**
             * The date when the candidate's employment is set to start.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            employmentStartDate?: string;
            /**
             * Whether the candidate has confirmed their Early Years Workforce status.
             */
            ewcConfirmation?: boolean;
            /**
             * The share code for the candidate's Home Office application.
             *
             * @maxLength 256
             */
            homeOfficeShareCode?: string;
            /**
             * Whether the candidate has been archived.
             */
            archived?: boolean;
            /**
             * Whether the candidate is using an umbrella company.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            umbrella?: string;
            /**
             * ID of the PAYE umbrella company.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            payeUmbrella?: string;
            /**
             * The name of the umbrella company the candidate is using.
             *
             * @maxLength 256
             */
            umbrellaCompanyName?: string;
            /**
             * The code of the umbrella company the candidate is using.
             *
             * @maxLength 256
             */
            umbrellaCompanyCode?: string;
            /**
             * The transport method for the candidate.
             */
            transport?:
              | "Drives Own Vehicle"
              | "Use Public Transport"
              | "Access to Another Vehicle"
              | "Bicycle"
              | "No Access"
              | "Other";
            /**
             * Any important notes related to the candidate.
             *
             * @maxLength 1024
             */
            importantNotes?: string;
            /**
             * The date when the candidate was last updated.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            candidateLastUpdated?: string;
            /**
             * The source from which the candidate was obtained.
             */
            source?:
              | (
                  | "Company Website"
                  | "iCan Teach UK Website"
                  | "Apply to Education"
                  | "CV Library - CV Search"
                  | "CV Library Advert"
                  | "CV Library - Watchdog"
                  | "Guardian- CV Search"
                  | "Guardian Advert"
                  | "Indeed - CV Search"
                  | "Indeed Advert"
                  | "Reed - CV Search"
                  | "Reed Advert"
                  | "Reed \u2013 Watchdog"
                  | "SENploy - Advert"
                  | "Total Jobs"
                  | "Other Job Board"
                  | "Google"
                  | "LinkedIn"
                  | "Linkedin Recruiter"
                  | "LinkedIn Advert"
                  | "Referral - School"
                  | "Referral - Candidate"
                  | "UK University"
                  | "Irish University"
                  | "Canadian University"
                  | "Marketing Campaign"
                  | "Woo"
                  | "Job Show"
                  | "Recruitment Fair"
                  | "Career Fair"
                  | "Volcanic"
                  | "Broadbean"
                  | "Headhunt"
                  | "Referral"
                  | "Direct"
                  | "Other"
                )
              | (
                  | "Company Website"
                  | "Direct Call"
                  | "CV Library Watchdog"
                  | "CV Library Advert"
                  | "CV Library CV Search"
                  | "Reed Watchdog"
                  | "Reed Advert"
                  | "Reed CV Search"
                  | "Indeed Watchdog"
                  | "Indeed Advert"
                  | "Indeed CV Search"
                  | "Job Centre"
                  | "Returning Candidate"
                  | "Referral Candidate"
                  | "Referral Client"
                  | "Training Provider"
                  | "Recruitment Fair"
                  | "University/College"
                );
            /**
             * Any other source from which the candidate was obtained.
             *
             * @maxLength 256
             */
            sourceOther?: string;
            /**
             * Whether the candidate has given consent for online checks.
             */
            onlineCheckConsent?: boolean;
            /**
             * The title of the candidate.
             */
            title?:
              | "Mr"
              | "Mrs"
              | "Ms"
              | "Miss"
              | "Dr"
              | "Mx"
              | "Lady"
              | "Lord"
              | "Other";
            /**
             * The pronoun used by the candidate.
             */
            pronoun?:
              | "She/Her/Hers"
              | "He/Him/His"
              | "They/Them/Theirs"
              | "Ze/Hir/Hirs"
              | "Ze/Zir/Zirs"
              | "Other"
              | "Prefer not to say";
            /**
             * The date when the candidate was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
            /**
             * The date when the candidate was last contacted.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            lastContactedDate?: string;
            /**
             * A short, prominent note shown with the candidate to communicate important context.
             */
            highlight?: string;
            /**
             * The pay scale for the candidate's teaching position.
             *
             * @maxLength 256
             */
            teacherPayScale?: string;
            /**
             * Whether the candidate has a P45.
             */
            p45?: boolean;
            /**
             * The type of role the candidate is applying for.
             */
            roleType?:
              | "Senior Leadership Positions"
              | "Teaching Roles"
              | "School Support Positions";
            /**
             * The Right to Work verification status for the candidate.
             *
             * @maxLength 256
             */
            rtwVerification?: string;
            /**
             * Any notes related to the Right to Work verification status for the candidate.
             *
             * @maxLength 1024
             */
            rtwVerificationNote?: string;
            /**
             * The Right to Work verification status for the candidate.
             *
             * @maxLength 256
             */
            rtwVerificationStatus?: string;
            /**
             * The location where the candidate desires to work.
             *
             * @maxLength 256
             */
            desiredLocation?: string;
            /**
             * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
             */
            complianceUser?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
             */
            watchers?:
              | string[]
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                }[];
            /**
             * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
             */
            tags?:
              | string[]
              | {
                  /**
                   * The unique identifier of the tag
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The label displayed wherever this tag is applied.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  name?: string;
                  /**
                   * The tag display colour as a hexadecimal value, for example #2563EB.
                   *
                   * @maxLength 32
                   */
                  color?: string | null;
                }[];
            /**
             * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
             *
             * @maxItems 5
             */
            documents?: {
              /**
               * The unique identifier of the document
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The name of the document
               *
               * @maxLength 512
               */
              name?: string;
              /**
               * The file reference used to retrieve the document
               *
               * @maxLength 2048
               */
              reference?: string;
              /**
               * The file's media type
               *
               * @maxLength 256
               */
              fileType?: string;
              /**
               * The area of the platform the document belongs to
               *
               * @maxLength 256
               */
              type?: string;
              /**
               * The configured category of the document
               *
               * @maxLength 256
               */
              docType?: string;
              /**
               * The document's expiry date, when applicable
               *
               * @maxLength 64
               */
              expiry?: string;
              /**
               * The document's issued date, when applicable
               *
               * @maxLength 64
               */
              issued?: string;
              /**
               * Whether the document has been archived
               */
              documentArchived?: boolean;
              /**
               * The date the document was uploaded
               *
               * @format date-time
               */
              createdAt?: string;
            }[];
            /**
             * The total number of documents attached to the candidate.
             *
             * @minimum 0
             */
            documentsCount?: number;
            /**
             * The locations where the candidate desires to work.
             */
            desiredLocationMultiple?: string[];
            /**
             * Daily availability for the requested period. Returned by the availability view only.
             *
             * @maxItems 31
             */
            availabilityDays?: {
              /**
               * Calendar date in YYYY-MM-DD format.
               *
               * @maxLength 10
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              date?: string;
              /**
               * Availability or booking state for this date.
               */
              status?:
                | "available"
                | "unavailable"
                | "booked"
                | "halfDay"
                | "awaiting"
                | "unknown";
              /**
               * Recorded availability, or null when unrecorded.
               */
              available?: boolean | null;
              /**
               * Morning or afternoon availability.
               */
              partial?: "AM" | "PM" | any | null;
              /**
               * Additional availability information.
               *
               * @maxLength 4096
               */
              note?: string | null;
              /**
               * Active bookings on this date.
               *
               * @maxItems 100
               */
              bookings?: {
                /**
                 * The unique identifier of the resource
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * @maxLength 512
                 */
                clientName?: string;
                /**
                 * @maxLength 512
                 */
                role?: string;
                /**
                 * @maxLength 32
                 */
                timeDesignation?: string;
              }[];
            }[];
            /**
             * The date when the candidate's availability was last checked.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            availabilityLastChecked?: string;
            /**
             * Whether the candidate is subject to notice period.
             */
            subjectToNotice?: "Unsure" | "Yes" | "No";
            /**
             * The subsidiary subjects the candidate is qualified to teach.
             */
            subsidiarySubject?: string[];
            /**
             * The candidate's preferred employment type.
             */
            employmentPreference?: (
              | "Day To Day"
              | "Long Term"
              | "Fixed Term"
              | "Perm"
            )[];
            /**
             * Any other names the candidate is known by.
             *
             * @maxLength 256
             */
            otherNames?: string;
            /**
             * The name of the candidate's limited company, if applicable.
             *
             * @maxLength 256
             */
            ltdCompany?: string;
            /**
             * Whether the candidate's employment has been verified.
             */
            employmentVerified?: boolean;
            /**
             * The DBS verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSVerification?: string;
            /**
             * Any notes related to the DBS verification status for the candidate.
             *
             * @maxLength 1024
             */
            candidateDBSVerificationNote?: string;
            /**
             * The DBS verification status for the candidate.
             */
            candidateDBSVerificationStatus?: "Cleared" | "Alerts";
            /**
             * The DBS check verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSCheckVerification?: string;
            /**
             * Any notes related to the DBS check verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSCheckVerificationNote?: string;
            /**
             * The DBS check verification status for the candidate.
             */
            candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
            /**
             * Any notes related to the DBS update service verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSUpdateServiceVerificationNote?: string;
            /**
             * The DBS update service verification status for the candidate.
             */
            candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
            /**
             * Any notes related to the TRN verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateTRNVerificationNote?: string;
            /**
             * The TRN verification status for the candidate.
             */
            candidateTRNVerificationStatus?:
              | "Completed Induction"
              | "Not Yet Completed Induction"
              | "Induction Exempt"
              | "ALERTS - Failed Induction"
              | "Induction Not Required";
            /**
             * The health and safety verification status for the candidate.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            candidateHealthSafetyVerification?: string;
            /**
             * Any notes related to the health and safety verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateHealthSafetyVerificationNote?: string;
            /**
             * The health and safety verification status for the candidate.
             */
            candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
            /**
             * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
             */
            onboardingSession?:
              | string
              | {
                  /**
                   * Unique identifier for the onboarding session
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The URL for the onboarding session
                   *
                   * @format uri
                   */
                  url?: string;
                  /**
                   * The frozen health declaration configuration for this onboarding session.
                   */
                  healthDeclarationConfig?: {
                    /**
                     * The ordered questions included in the question set.
                     *
                     * @maxItems 100
                     */
                    questions?: {
                      /**
                       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * A stable key used to associate the question with current and historical answers.
                       *
                       * @minLength 1
                       * @maxLength 128
                       */
                      key?: string;
                      /**
                       * The question shown to the person completing the form.
                       *
                       * @minLength 1
                       * @maxLength 500
                       */
                      label?: string;
                      /**
                       * Optional supporting text displayed with the question.
                       *
                       * @maxLength 2000
                       */
                      description?: string;
                      /**
                       * The type of answer collected for the question.
                       */
                      type?:
                        | "checkbox"
                        | "date"
                        | "daterange"
                        | "dropdown"
                        | "number"
                        | "signature"
                        | "text"
                        | "textarea";
                      /**
                       * Whether an answer is required while the question is visible.
                       */
                      required?: boolean;
                      /**
                       * Whether the question is included in new forms. Historical responses retain disabled questions.
                       */
                      enabled?: boolean;
                      /**
                       * The display order of the question.
                       *
                       * @minimum 0
                       * @maximum 1000
                       */
                      order?: number;
                      /**
                       * Optional rules that determine when the question is shown.
                       */
                      condition?: {
                        /**
                         * Whether any rule or every rule must match before the question is shown.
                         */
                        mode?: "any" | "all";
                        /**
                         * The comparisons used to determine question visibility.
                         *
                         * @minItems 1
                         * @maxItems 20
                         */
                        rules?: {
                          /**
                           * The stable key of the question that controls visibility.
                           *
                           * @minLength 1
                           * @maxLength 128
                           */
                          questionKey?: string;
                          /**
                           * How the controlling answer is compared with the value.
                           */
                          operator?: "equals" | "notEquals";
                          /**
                           * The answer value used to determine visibility.
                           */
                          value?: string | boolean | number | (void | null);
                        }[];
                      };
                      /**
                       * The available answers for a dropdown question.
                       *
                       * @minItems 1
                       * @maxItems 100
                       */
                      options?: {
                        /**
                         * The value stored when this option is selected.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        value?: string;
                        /**
                         * The option displayed to the person answering the question.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        label?: string;
                      }[];
                      /**
                       * Whether this question belongs to the standard template.
                       */
                      ["default"]?: boolean;
                      /**
                       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                       */
                      audience?: "candidate" | "staff";
                      /**
                       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                       */
                      fixedRole?: "fullName" | "signature";
                    }[];
                    /**
                     * When this question configuration was last updated.
                     *
                     * @format date-time
                     */
                    updatedAt?: string;
                  };
                  /**
                   * The frozen Criminal Record & Security Checks configuration for this onboarding session.
                   */
                  criminalRecordConfig?: {
                    /**
                     * The ordered questions included in the question set.
                     *
                     * @maxItems 100
                     */
                    questions?: {
                      /**
                       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * A stable key used to associate the question with current and historical answers.
                       *
                       * @minLength 1
                       * @maxLength 128
                       */
                      key?: string;
                      /**
                       * The question shown to the person completing the form.
                       *
                       * @minLength 1
                       * @maxLength 500
                       */
                      label?: string;
                      /**
                       * Optional supporting text displayed with the question.
                       *
                       * @maxLength 2000
                       */
                      description?: string;
                      /**
                       * The type of answer collected for the question.
                       */
                      type?:
                        | "checkbox"
                        | "date"
                        | "daterange"
                        | "dropdown"
                        | "number"
                        | "signature"
                        | "text"
                        | "textarea";
                      /**
                       * Whether an answer is required while the question is visible.
                       */
                      required?: boolean;
                      /**
                       * Whether the question is included in new forms. Historical responses retain disabled questions.
                       */
                      enabled?: boolean;
                      /**
                       * The display order of the question.
                       *
                       * @minimum 0
                       * @maximum 1000
                       */
                      order?: number;
                      /**
                       * Optional rules that determine when the question is shown.
                       */
                      condition?: {
                        /**
                         * Whether any rule or every rule must match before the question is shown.
                         */
                        mode?: "any" | "all";
                        /**
                         * The comparisons used to determine question visibility.
                         *
                         * @minItems 1
                         * @maxItems 20
                         */
                        rules?: {
                          /**
                           * The stable key of the question that controls visibility.
                           *
                           * @minLength 1
                           * @maxLength 128
                           */
                          questionKey?: string;
                          /**
                           * How the controlling answer is compared with the value.
                           */
                          operator?: "equals" | "notEquals";
                          /**
                           * The answer value used to determine visibility.
                           */
                          value?: string | boolean | number | (void | null);
                        }[];
                      };
                      /**
                       * The available answers for a dropdown question.
                       *
                       * @minItems 1
                       * @maxItems 100
                       */
                      options?: {
                        /**
                         * The value stored when this option is selected.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        value?: string;
                        /**
                         * The option displayed to the person answering the question.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        label?: string;
                      }[];
                      /**
                       * Whether this question belongs to the standard template.
                       */
                      ["default"]?: boolean;
                      /**
                       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                       */
                      audience?: "candidate" | "staff";
                      /**
                       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                       */
                      fixedRole?: "fullName" | "signature";
                    }[];
                    /**
                     * When this question configuration was last updated.
                     *
                     * @format date-time
                     */
                    updatedAt?: string;
                  };
                };
            /**
             * The contact record for this candidate. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * Whether the candidate has opted out of 48 hour communications.
             */
            optOut48Hours?: boolean;
            /**
             * Whether the candidate has consented to REC audit checks.
             */
            recAuditConsent?: boolean | null;
            /**
             * The date the candidate's REC audit consent was recorded.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            recAuditConsentDate?: string;
            /**
             * The method through which REC audit consent was captured.
             *
             * @maxLength 256
             */
            recAuditConsentMethod?: string;
            /**
             * The date when the candidate record was created.
             *
             * @format date-time
             */
            createdAt?: string;
            /**
             * The mobile app link status for the candidate.
             */
            candidateMobileAppStatus?:
              | "Unlinked"
              | "Scheduled Unlink"
              | "Linked"
              | "Invited"
              | "Not Invited";
            /**
             * Evertime details for this candidate
             */
            evertime?: {
              /**
               * The Evertime CandidateId used for this candidate when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
              /**
               * Whether the matched Evertime candidate's NI number also matches the one held in Edun
               */
              niNumberMatch?: boolean;
            };
            /**
             * Search relevance relative to the strongest result, where 100 is the strongest match.
             *
             * @minimum 0
             * @maximum 100
             */
            relevanceScore?: number;
            /**
             * Candidate fields or attached document excerpts that explain why the candidate matched.
             *
             * @maxItems 5
             */
            matchReasons?: {
              /**
               * Whether the matching information came from the candidate record or an attached document.
               */
              source?: "candidate" | "document";
              /**
               * The candidate or document information that matched the search.
               *
               * @maxLength 256
               */
              field?: string;
              /**
               * A short excerpt showing why the result matched.
               *
               * @maxLength 1000
               */
              text?: string;
              /**
               * The exact terms within the content that matched the search, including synonym matches.
               *
               * @maxItems 25
               */
              matchedTerms?: string[];
              /**
               * The name of the attached document when the match came from a document.
               *
               * @maxLength 512
               */
              documentName?: string | null;
              /**
               * The unique identifier of the attached document when the match came from a document.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              documentId?: string | null;
            }[];
            /**
             * The candidate's distance in miles from the requested location. Present only when location filtering is used.
             *
             * @minimum 0
             */
            distance?: number;
            /**
             * Timesheet Portal details for this candidate
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal contractor GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal contractor code
               *
               * @maxLength 256
               */
              code?: string;
            };
            /**
             * Invite to candidate to link their mobile app.
             */
            mobileAppLinkInvite?: {
              /**
               * Boolean status of whether the link invite is being used
               *
               * @default false
               */
              isUsed?: boolean;
              /**
               * Creation date of the invite
               *
               * @format date-time
               * @default 2026-10-30T13:39:09.027Z
               */
              createdAt?: string;
              /**
               * ID of candidate using this link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              usedBy?: string;
              /**
               * Date of when link used
               *
               * @format date-time
               */
              usedAt?: string;
              /**
               * If unlinked, ID of user that unlinked this candidate's link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              unlinkedBy?: string;
              /**
               * If unlinked, date of when this link was unlinked
               *
               * @format date-time
               */
              unlinkedAt?: string;
              /**
               * If deleted, ID of user that deleted this candidate's link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              deletedBy?: string;
              /**
               * If deleted, date of when this link was deleted
               *
               * @format date-time
               */
              deletedAt?: string;
              /**
               * Current status of this mobile app link
               */
              status?:
                | "Sent"
                | "Linked"
                | "Unlinked"
                | "ScheduledUnlink"
                | "Not Sent"
                | "Rescinded";
            };
          }
        | (void | null);
      /**
       * The Client contact linked to this request.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      clientContact?: string | null;
      /**
       * The Client linked to the request's Client contact.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      clientId?: string | null;
      /**
       * The recipient name captured when the request was issued.
       *
       * @minLength 1
       * @maxLength 256
       */
      recipientName?: string | null;
      /**
       * The recipient email captured when the request was issued.
       *
       * @maxLength 320
       * @format email
       */
      recipientEmail?: string | null;
      /**
       * The point-in-time form definition presented to the recipient.
       */
      snapshot?: {
        /**
         * The internal and recipient-facing title of the form.
         *
         * @minLength 1
         * @maxLength 256
         */
        title?: string;
        /**
         * Optional introductory text shown before the questions.
         *
         * @maxLength 5000
         */
        description?: string | null;
        /**
         * Optional message shown after a response is submitted.
         *
         * @maxLength 2000
         */
        confirmationMessage?: string | null;
        /**
         * The ordered questions included in the form.
         *
         * @maxItems 100
         */
        questions?: {
          /**
           * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * A stable key used to associate the question with current and historical answers.
           *
           * @minLength 1
           * @maxLength 128
           */
          key?: string;
          /**
           * The question shown to the person completing the form.
           *
           * @minLength 1
           * @maxLength 500
           */
          label?: string;
          /**
           * Optional supporting text displayed with the question.
           *
           * @maxLength 2000
           */
          description?: string;
          /**
           * The type of answer collected for the question.
           */
          type?:
            | "checkbox"
            | "date"
            | "daterange"
            | "dropdown"
            | "number"
            | "signature"
            | "text"
            | "textarea";
          /**
           * Whether an answer is required while the question is visible.
           */
          required?: boolean;
          /**
           * Whether the question is included in new forms. Historical responses retain disabled questions.
           */
          enabled?: boolean;
          /**
           * The display order of the question.
           *
           * @minimum 0
           * @maximum 1000
           */
          order?: number;
          /**
           * Optional rules that determine when the question is shown.
           */
          condition?: {
            /**
             * Whether any rule or every rule must match before the question is shown.
             */
            mode?: "any" | "all";
            /**
             * The comparisons used to determine question visibility.
             *
             * @minItems 1
             * @maxItems 20
             */
            rules?: {
              /**
               * The stable key of the question that controls visibility.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * How the controlling answer is compared with the value.
               */
              operator?: "equals" | "notEquals";
              /**
               * The answer value used to determine visibility.
               */
              value?: string | boolean | number | (void | null);
            }[];
          };
          /**
           * The available answers for a dropdown question.
           *
           * @minItems 1
           * @maxItems 100
           */
          options?: {
            /**
             * The value stored when this option is selected.
             *
             * @minLength 1
             * @maxLength 200
             */
            value?: string;
            /**
             * The option displayed to the person answering the question.
             *
             * @minLength 1
             * @maxLength 200
             */
            label?: string;
          }[];
          /**
           * Whether this question belongs to the standard template.
           */
          ["default"]?: boolean;
          /**
           * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
           */
          audience?: "candidate" | "staff";
          /**
           * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
           */
          fixedRole?: "fullName" | "signature";
        }[];
        /**
         * The form definition from which this snapshot was captured.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        formId?: string;
        /**
         * When the captured form definition was last updated.
         *
         * @format date-time
         */
        configurationUpdatedAt?: string;
      };
      /**
       * The current lifecycle state of this request.
       */
      status?: "issued" | "inProgress" | "submitted" | "revoked" | "expired";
      /**
       * When the signed request link stops being valid.
       *
       * @format date-time
       */
      expiresAt?: string;
      /**
       * When the recipient first saved answers.
       *
       * @format date-time
       */
      startedAt?: string | null;
      /**
       * When the recipient submitted the response.
       *
       * @format date-time
       */
      submittedAt?: string | null;
      /**
       * When access to the request was revoked.
       *
       * @format date-time
       */
      revokedAt?: string | null;
      /**
       * The user who issued the request. If expanded, a user object; otherwise, a user ID.
       */
      createdBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * Email delivery information for this request.
       */
      delivery?: {
        /**
         * When email delivery was most recently requested.
         *
         * @format date-time
         */
        emailRequestedAt?: string;
        /**
         * When the request email was most recently sent.
         *
         * @format date-time
         */
        emailSentAt?: string;
        /**
         * When the most recent request email failed to send.
         *
         * @format date-time
         */
        emailFailedAt?: string;
      };
      /**
       * When the request was issued.
       *
       * @format date-time
       */
      createdAt?: string;
      /**
       * When the request was last updated.
       *
       * @format date-time
       */
      updatedAt?: string;
    };
    /**
     * The newly issued recipient link. It is returned only when the link is created or rotated.
     *
     * @maxLength 4096
     * @format uri
     */
    signedUrl?: string;
  };
};

export type CreateFormRequestRequestBody = {
  /**
   * The published form to issue.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  formId: string;
  /**
   * An optional candidate to link to the request.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  candidateId?: string | null;
  /**
   * An optional Client contact to link to the request.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  clientContactId?: string | null;
  /**
   * A recipient name to use when no recipient record is linked.
   *
   * @minLength 1
   * @maxLength 256
   */
  recipientName?: string;
  /**
   * An email address to use when no linked recipient email is available.
   *
   * @maxLength 320
   * @format email
   */
  recipientEmail?: string;
  /**
   * How many days the request link remains valid.
   *
   * @minimum 1
   * @maximum 30
   */
  expiresInDays?: number;
  /**
   * Whether to email the request link to the recipient.
   */
  sendEmail?: boolean;
};

export type CreateFormRequestVariables = {
  body: CreateFormRequestRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateFormRequest = (
  variables: CreateFormRequestVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    CreateFormRequestResponse,
    CreateFormRequestError,
    CreateFormRequestRequestBody,
    {},
    {},
    {}
  >({ url: "/form-requests", method: "post", ...variables, signal });

export const useCreateFormRequest = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      CreateFormRequestResponse,
      CreateFormRequestError,
      CreateFormRequestVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    CreateFormRequestResponse,
    CreateFormRequestError,
    CreateFormRequestVariables
  >({
    mutationFn: (variables: CreateFormRequestVariables) =>
      fetchCreateFormRequest(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetFormRequestPathParams = {
  /**
   * The issued form request identifier.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  formRequestId: string;
};

export type GetFormRequestError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetFormRequestResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the issued form request.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The form definition used to create this request.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    formId?: string;
    /**
     * The submitted response for this request, when one is available.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    responseId?: string | null;
    /**
     * The candidate linked to this request. If expanded, a candidate object; otherwise, a candidate ID.
     */
    candidate?:
      | string
      | {
          /**
           * The unique identifier of the candidate
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the candidate
           *
           * @minLength 1
           * @maxLength 256
           */
          forename?: string;
          /**
           * The surname of the candidate
           *
           * @minLength 1
           * @maxLength 256
           */
          surname?: string;
          /**
           * Any previous names of the candidate
           *
           * @maxLength 256
           */
          previousNames?: string;
          /**
           * The email of the candidate. This will be used for communication.
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the candidate
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the candidate
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The occupation of the candidate
           */
          occupation?:
            | "Teacher"
            | "Cover Supervisor"
            | "Teaching Assistant"
            | "School Support Staff"
            | "Primary Teacher"
            | "Secondary Teacher"
            | "Head Teacher"
            | "Senior Leader"
            | "Tutor"
            | "Mentor"
            | "Learning Support Assistant"
            | "Finance Officer"
            | "HR Administrator"
            | "HR Manager"
            | "Office Manager"
            | "Administrator"
            | "Personal Assistant"
            | "Pastoral Manager"
            | "Receptionist"
            | "Instructor"
            | "Caretaker"
            | "Nursery Assistant"
            | "Nursery Nurse"
            | "Deputy Nursery Manager"
            | "Lunchtime Supervisor"
            | "Classroom Graduate"
            | "Primary Cover Supervisor"
            | "HLTA"
            | "Nurture Teacher"
            | "Nurture Mentor"
            | "SENDCo"
            | "Registered Manager"
            | "Regional Manager"
            | "Education Director"
            | "Residential Director"
            | "Deputy Manager"
            | "Residential Support Worker"
            | "Sports Coach"
            | "Executive Headteacher"
            | "General Practitioner"
            | "Advance Nurse Practitioner (ANP)"
            | "Emergency Nurse Practitioner (ENP)"
            | "Advanced Clinical Practitioner (ACP)"
            | "Emergency Clinical Practitioner (ECP)"
            | "Consultant Psychiatrist"
            | "Psychiatrist \u2013 SPR"
            | "IT Technician"
            | "Art Technician"
            | "DT Technician"
            | "Science Technician"
            | "Healthcare Assistant/ Residential Support Worker"
            | "Healthcare Assistant"
            | "Senior Healthcare Assistant"
            | "Team Leader"
            | "Registered General Nurse"
            | "Registered Mental Nurse"
            | "Registered Nurse Learning Disabilities"
            | "Overseas Trained Teacher";
          /**
           * The subject the candidate specializes in
           *
           * @maxLength 256
           */
          subject?: string;
          /**
           * The address of the resource
           */
          address?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The qualifications of the candidate
           *
           * @maxItems 32
           */
          qualifications?: {
            /**
             * The type of the qualification
             *
             * @maxLength 256
             */
            type?: string;
            /**
             * The place where the qualification was obtained
             *
             * @maxLength 256
             */
            place?: string;
            /**
             * The name of the qualification
             *
             * @maxLength 256
             */
            qualificationName?: string;
            /**
             * The date the qualification was obtained
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date?: string;
            /**
             * The unique identifier of the qualification
             *
             * @maxLength 256
             */
            qualificationId?: string;
            /**
             * The order index of the qualification
             *
             * @minimum 0
             */
            orderIndex?: number;
            /**
             * The expiry date of the qualification
             *
             * @maxLength 256
             */
            qualificationExpiry?: string;
            /**
             * Whether to exclude this qualification from vetting
             */
            excludeFromVetting?: boolean;
            /**
             * The date the qualification was created
             *
             * @format date-time
             */
            createdAt?: string | null;
          }[];
          /**
           * The employment history of the candidate
           *
           * @maxItems 32
           */
          employment?: {
            /**
             * The name of the employer
             *
             * @maxLength 256
             */
            employerName?: string;
            /**
             * The date the candidate started employment
             *
             * @maxLength 256
             */
            dateStarted?: string;
            /**
             * The date the candidate ended employment
             *
             * @maxLength 256
             */
            dateEnded?: string;
            /**
             * The job title of the candidate
             *
             * @maxLength 256
             */
            jobTitle?: string;
            /**
             * The reason the candidate left this employment
             *
             * @maxLength 1024
             */
            reasonForLeaving?: string;
            /**
             * The duties of the candidate in this role. Consultant only, visible on Edun CV
             *
             * @maxLength 1024
             */
            duties?: string;
            /**
             * The salary of the candidate in this role
             *
             * @maxLength 256
             */
            salary?: string;
            /**
             * The address of the candidate's reference for this employment
             */
            referenceAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The unique identifier for this employment record
             *
             * @maxLength 256
             */
            employmentId?: string;
            /**
             * The order index of this employment record
             *
             * @minimum 0
             */
            orderIndex?: number;
          }[];
          /**
           * The references for the candidate
           *
           * @maxItems 32
           */
          references?: {
            /**
             * The title of the reference
             *
             * @maxLength 16
             */
            referenceTitle?: string;
            /**
             * The forename of the reference
             *
             * @maxLength 256
             */
            referenceForename?: string;
            /**
             * The surname of the reference
             *
             * @maxLength 256
             */
            referenceSurname?: string;
            /**
             * The company of the reference
             *
             * @maxLength 256
             */
            referenceCompany?: string;
            /**
             * The position of the reference
             *
             * @maxLength 256
             */
            referencePosition?: string;
            /**
             * The email of the reference
             *
             * @format email
             */
            referenceEmail?: string;
            /**
             * The telephone number of the reference
             *
             * @maxLength 32
             */
            referenceTelephone?: string;
            /**
             * The type of the reference
             */
            referenceType?:
              | "Education"
              | "Professional"
              | "Character"
              | "Factual"
              | "Academic"
              | "Volunteer";
            /**
             * The duration of the reference
             *
             * @maxLength 128
             */
            referenceDuration?: string;
            /**
             * The ID of the reference
             *
             * @maxLength 256
             */
            referenceId?: string;
            /**
             * This field only applies if the reference immediate consent question is enabled
             * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
             */
            referenceApply?: boolean;
            /**
             * Any additional notes about the reference
             *
             * @maxLength 1024
             */
            referenceNote?: string;
            /**
             * The start date of the reference
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            dateFrom?: string;
            /**
             * The end date of the reference
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            dateTo?: string;
          }[];
          /**
           * Whether the candidate has consented to a reference check
           */
          referenceCheckConsent?: boolean;
          /**
           * The name of the person who provided consent for the reference check
           *
           * @maxLength 256
           */
          referenceCheckConsentName?: string;
          /**
           * The date when consent for the reference check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          referenceCheckConsentDate?: string;
          /**
           * The date of birth of the candidate
           *
           * @format date-time
           */
          dob?: string | null;
          /**
           * The sex of the candidate
           */
          sex?: "Male" | "Female" | "Other" | "Unknown";
          /**
           * The status of the candidate
           */
          status?:
            | "Pending"
            | "Approved"
            | "Rejected"
            | "Anonymised"
            | "Archived"
            | "Enquired"
            | "Hold"
            | "Future"
            | "DNC"
            | "Pre";
          /**
           * The current status of the onboarding process for the candidate
           */
          onboardingStatus?: "Not Started" | "In Progress" | "Completed";
          /**
           * Whether the onboarding information has been sent to the candidate
           */
          onboardingSent?: boolean;
          nextOfKin?: {
            /**
             * The name of the next of kin
             *
             * @maxLength 256
             */
            nextOfKinName?: string;
            /**
             * The relationship of the next of kin to the candidate
             *
             * @maxLength 256
             */
            nextOfKinRelationship?: string;
            /**
             * The telephone number of the next of kin
             *
             * @maxLength 32
             */
            nextOfKinTelephone?: string;
            /**
             * The email address of the next of kin
             *
             * @format email
             */
            nextOfKinEmail?: string;
            nextOfKinAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
          };
          criminalRecord?: {
            /**
             * Whether the candidate has a DBS certificate
             */
            dbsHeld?: boolean;
            /**
             * The unique identifier for the DBS certificate
             *
             * @maxLength 256
             */
            dbsNumber?: string;
            /**
             * The type of DBS check
             */
            dbsType?:
              | "Basic"
              | "Standard"
              | "Enhanced"
              | "Enhanced with Barred List"
              | "Child workforce"
              | "Child and adult workforce"
              | "Adult workforce";
            /**
             * The date when the DBS check was last performed
             *
             * @maxLength 256
             */
            dbsLastChecked?: string;
            /**
             * The DBS update service number for the candidate
             *
             * @maxLength 256
             */
            dbsUpdateServiceNumber?: string;
            /**
             * The result of the prohibition check
             *
             * @maxLength 256
             */
            prohibitionCheck?: string;
            /**
             * The result of the overseas check
             *
             * @maxLength 256
             */
            overseasCheck?: string;
            /**
             * Any additional notes regarding the overseas check
             *
             * @maxLength 256
             */
            overseasCheckNote?: string;
            /**
             * Whether the candidate has a convicted offence
             */
            convictedOffence?: boolean;
            /**
             * The description of the convicted offence
             *
             * @maxLength 1024
             */
            convictedOffenceDescription?: string;
            /**
             * Whether the candidate has any convictions
             */
            convictions?: boolean;
            /**
             * The description of the convictions
             *
             * @maxLength 1024
             */
            convictionsDescription?: string;
            /**
             * Whether the candidate is registered to the DBS update service
             */
            registeredToUpdateService?: boolean;
            /**
             * Whether the candidate consents to update service checks
             */
            consentUpdateServiceChecks?: boolean;
            /**
             * Any additional notes regarding the candidate's criminal record
             *
             * @maxLength 1024
             */
            note?: string;
            /**
             * The date when the update service check was performed
             *
             * @maxLength 64
             */
            updateServiceCheckDate?: string;
            /**
             * Whether the candidate has lived abroad
             */
            abroad?: boolean;
            /**
             * Any additional notes regarding the overseas declaration
             *
             * @maxLength 1024
             */
            overseasDeclarationExtended?: string;
            /**
             * Whether the candidate has been subject to educational disciplinary proceedings
             */
            educationalDisciplinaryProceedings?: boolean;
            /**
             * Any comments regarding the DBS check
             *
             * @maxLength 1024
             */
            dbsComments?: string;
            /**
             * Whether the candidate has read the DBS declaration
             */
            dbsDeclarationRead?: boolean;
            /**
             * Whether the candidate has any overseas offences
             */
            overseasOffence?: boolean;
            /**
             * The description of the overseas offence
             *
             * @maxLength 1024
             */
            overseasOffenceDescription?: string;
            /**
             * Whether the candidate has a current criminal offence
             */
            currentCriminalOffence?: boolean;
            /**
             * The description of the current criminal offence
             *
             * @maxLength 1024
             */
            currentCriminalOffenceDescription?: string;
            /**
             * Whether the DBS was issued by a business
             */
            dbsIssuedByBusiness?: boolean;
            /**
             * Whether the DBS was issued by the current employer
             */
            dbsIssuedByCurrentEmployer?: boolean;
            /**
             * Whether a Section 128 check is required for the candidate.
             */
            section128Required?: boolean;
            /**
             * The date when the Section 128 check was completed.
             */
            section128Checked?: string;
          };
          /**
           * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
           */
          criminalRecordDeclaration?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * The answers recorded for the point-in-time question set.
             *
             * @maxItems 100
             */
            answers?: {
              /**
               * The identifier of the question being answered.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              questionId?: string;
              /**
               * The stable key of the question being answered.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * The submitted answer to the question.
               */
              value?: string | string | boolean | number | (void | null);
            }[];
            /**
             * When the question set was submitted.
             *
             * @format date-time
             */
            submittedAt?: string;
            /**
             * The network address recorded when the response was submitted.
             *
             * @maxLength 64
             */
            submittedIp?: string;
            /**
             * When the question configuration captured by this response was last updated.
             *
             * @format date-time
             */
            configurationUpdatedAt?: string;
          };
          /**
           * Whether the candidate confirmed their criminal record declaration.
           */
          criminalRecordCheckConsent?: boolean;
          /**
           * The name used to confirm the criminal record declaration.
           *
           * @maxLength 256
           */
          criminalRecordCheckConsentName?: string;
          /**
           * The date the criminal record declaration was confirmed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          criminalRecordCheckConsentDate?: string;
          safeInEducation?: {
            /**
             * Whether the candidate is barred from working in education
             */
            barred?: boolean;
            /**
             * The date when the barred status was checked
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            barredCheckDate?: string;
            /**
             * Whether the candidate has a waiver for the barred status
             */
            waiver?: boolean;
            /**
             * Any orders related to the candidate's barred status
             */
            ordersYou?: boolean;
            /**
             * Any orders related to the candidate's barred status for children
             */
            ordersChild?: boolean;
            /**
             * Whether the candidate has any previous safeguarding allegations
             */
            previousSafeguardingAllegations?: boolean;
            /**
             * Whether the candidate has been cautioned
             */
            cautioned?: boolean;
            /**
             * Whether the candidate has a violent offence
             */
            violentOffence?: boolean;
            /**
             * Whether the candidate has lived or worked in another country
             */
            anotherCountry?: boolean;
            /**
             * Whether the candidate has read the Keeping Children Safe in Education document
             */
            readKCSIE?: boolean;
            /**
             * Whether the candidate has read the Disqualification under the Childcare Act guidance
             */
            readChildcareAct?: boolean;
            /**
             * Whether the candidate is prohibited from teaching
             */
            prohibitedFromTeaching?: boolean;
            /**
             * Whether the candidate is known to the police
             */
            knownPolice?: boolean;
            /**
             * Whether the candidate is prohibited from management roles
             */
            prohibitedFromManagement?: boolean;
            /**
             * Whether the candidate has any criminal offences under law
             */
            criminalOffencesLaw?: boolean;
            /**
             * Any additional details regarding the candidate's safe in education status
             */
            safeInEducationDetails?: string;
            safeguardingQuestionaire?: {
              /**
               * What is the primary purpose of safeguarding in a school environment?
               */
              primaryPurposeSafeguarding?:
                | "enhance-academic-performance"
                | "ensure-safe-learning-environment"
                | "promote-healthy-relationships"
                | "facilitate-extracurricular-activities";
              /**
               * Who is typically responsible for coordinating safeguarding efforts within a school?
               */
              typicallyResponsibleCoordinatingSafeguarding?:
                | "janitorial-staff"
                | "parents-association"
                | "school-nurse"
                | "designated-safeguarding-lead";
              /**
               * Which of the following is considered a potential sign of child abuse or neglect?
               */
              signOfAbuse?:
                | "student-excelling"
                | "frequent-extracurricular"
                | "unexplained-injuries"
                | "not-listening";
              /**
               * How should a teacher respond if a student discloses information about potential abuse or harm?
               */
              studentDisclosesInformation?:
                | "ignore-disclosure"
                | "report-disclosure"
                | "share-information"
                | "advise-independent";
              /**
               * What is the purpose of a school's Child Protection Policy?
               */
              schoolsChildProtectionPolicy?:
                | "enforcing-strict-disciplinary-measures"
                | "identifying-troublemakers"
                | "outlining-procedures-for-protecting-students-from-harm"
                | "establishing-academic-achievement-standards";
              /**
               * What does the term 'FGM' stand for in the context of safeguarding?
               */
              fgmDefinition?:
                | "female-general-medicine"
                | "forced-genetic-modification"
                | "female-genital-mutilation"
                | "foundation-gender-equality";
              /**
               * Which of the following is a key principle of online safety for students?
               */
              keyPrincipleOnlineSafetyStudents?:
                | "unlimited-screen-time"
                | "anon-online-interaction"
                | "responsible-digital-citizen"
                | "restrict-edu-sites";
              /**
               * When planning a school trip, what should teachers consider regarding safeguarding?
               */
              teachersConsiderRegardingSafeguarding?:
                | "minimise-supervision"
                | "appropriate-staff-ratios"
                | "exclude-medical-conditions"
                | "allow-roaming";
              /**
               * What is the purpose of a criminal background check for teachers?
               */
              criminalBackgroundCheckTeachers?:
                | "evaluating-teaching-proficiency"
                | "verifying-immigration-status"
                | "assessing-financial-stability"
                | "identifying-criminal-history";
              /**
               * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
               */
              studentConfides?:
                | "assure-secret-safe"
                | "encourage-share-friends"
                | "explain-no-guarantee"
                | "disclose-to-others";
            };
          };
          socialMedia?: {
            /**
             * The Facebook profile URL of the candidate
             *
             * @maxLength 256
             */
            facebook?: string;
            /**
             * The Instagram profile URL of the candidate
             *
             * @maxLength 256
             */
            instagram?: string;
            /**
             * The Twitter profile URL of the candidate
             *
             * @maxLength 256
             */
            twitter?: string;
            /**
             * The LinkedIn profile URL of the candidate
             *
             * @maxLength 256
             */
            linkedIn?: string;
            /**
             * The TikTok profile URL of the candidate
             *
             * @maxLength 256
             */
            tiktok?: string;
            /**
             * Any other social media profile URLs of the candidate
             *
             * @maxLength 256
             */
            others?: string;
            /**
             * Whether the social media profiles have been checked
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            socialMediaChecked?: string;
            /**
             * The date the candidate's Google search check was completed
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            googleCheckedDate?: string;
          };
          /**
           * Whether the candidate has consented to a safe in education check
           */
          safeInEducationCheckConsent?: boolean;
          /**
           * The name of the person who provided consent for the safe in education check
           *
           * @maxLength 256
           */
          safeInEducationCheckConsentName?: string;
          /**
           * The date when consent for the safe in education check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          safeInEducationCheckConsentDate?: string;
          /**
           * The health record of the individual
           */
          healthRecord?: {
            /**
             * Whether the individual has a physical ability condition
             */
            physicalAbilityCondition?: boolean;
            /**
             * Whether the individual is confused
             */
            confused?: boolean;
            /**
             * Whether the individual has hearing issues
             */
            hearing?: boolean;
            /**
             * Whether the individual has eyesight issues
             */
            eyesight?: boolean;
            /**
             * Whether the individual has depression
             */
            depression?: boolean;
            /**
             * Whether the individual has severe pain
             */
            severePain?: boolean;
            /**
             * Whether the individual has excessive drowsiness
             */
            excessiveDrowsiness?: boolean;
            /**
             * Whether the individual has epilepsy
             */
            epilepsy?: boolean;
            /**
             * Whether the individual has heart issues
             */
            heart?: boolean;
            /**
             * Whether the individual has asthma
             */
            asthma?: boolean;
            /**
             * Whether the individual has mental health issues
             */
            mentalHealth?: boolean;
            /**
             * Whether the individual is an expectant mother
             */
            expectantMother?: boolean;
            /**
             * Whether the individual has allergies
             */
            allergies?: boolean;
            /**
             * Whether the individual is taking medication
             */
            medication?: boolean;
            /**
             * Whether the individual has any health issues
             */
            healthIssues?: boolean;
            /**
             * A description of the individual's health issues
             *
             * @maxLength 2048
             */
            healthIssuesDescription?: string;
            /**
             * Any disability needs of the individual
             *
             * @maxLength 2048
             */
            disabilityNeeds?: string;
            /**
             * Whether the individual has any contagious diseases
             */
            contagiousDiseases?: boolean;
            /**
             * Whether the individual's health affects their ability
             */
            affectAbility?: boolean;
            /**
             * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
             */
            notificationAgree?: boolean;
            /**
             * Whether the individual has opted into the SAS Wellbeing App
             */
            sasOptIn?: boolean;
            /**
             * The individual's signature
             *
             * @maxLength 256
             */
            signature?: string;
            /**
             * The individual's drawn signature in base64url format
             *
             * @maxLength 4096
             */
            drawnSignature?: string;
            /**
             * The date the individual signed the form
             *
             * @format date-time
             */
            signatureDate?: string | null;
            /**
             * Whether the individual has been vaccinated against measles
             */
            vaccinationMeasles?: boolean;
            /**
             * Whether the individual has been vaccinated against mumps
             */
            vaccinationMumps?: boolean;
            /**
             * Whether the individual has been vaccinated against rubella
             */
            vaccinationRubella?: boolean;
            /**
             * Whether the individual has been vaccinated against varicella
             */
            vaccinationVaricella?: boolean;
            /**
             * Whether the individual has been vaccinated against tuberculosis
             */
            vaccinationTuberculosis?: boolean;
            /**
             * Whether the individual has been vaccinated against hepatitis B
             */
            vaccinationHepatitisB?: boolean;
            /**
             * Whether the individual has been vaccinated against hepatitis C
             */
            vaccinationHepatitisC?: boolean;
            /**
             * Whether the individual has been vaccinated against HIV
             */
            vaccinationHIV?: boolean;
            /**
             * Whether the individual has been vaccinated against COVID-19
             */
            vaccinationCovid?: boolean;
            /**
             * Whether the individual has received the 1st dose of the COVID-19 vaccine
             */
            vaccinationCovid1?: boolean;
            /**
             * Whether the individual has received the 2nd dose of the COVID-19 vaccine
             */
            vaccinationCovid2?: boolean;
            /**
             * Whether the individual has received the COVID-19 booster vaccine
             */
            vaccinationCovidBooster?: boolean;
            /**
             * Whether the individual has received the COVID-19 influenza vaccine
             */
            vaccinationCovidInfluenza?: boolean;
            /**
             * Whether the individual has an override for fit to work requirements
             */
            fitToWorkOverride?: boolean;
          };
          /**
           * The candidate's latest submitted health declaration, including the exact questions presented.
           */
          healthDeclaration?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * The answers recorded for the point-in-time question set.
             *
             * @maxItems 100
             */
            answers?: {
              /**
               * The identifier of the question being answered.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              questionId?: string;
              /**
               * The stable key of the question being answered.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * The submitted answer to the question.
               */
              value?: string | string | boolean | number | (void | null);
            }[];
            /**
             * When the question set was submitted.
             *
             * @format date-time
             */
            submittedAt?: string;
            /**
             * The network address recorded when the response was submitted.
             *
             * @maxLength 64
             */
            submittedIp?: string;
            /**
             * When the question configuration captured by this response was last updated.
             *
             * @format date-time
             */
            configurationUpdatedAt?: string;
          };
          /**
           * Whether the candidate confirmed their health declaration.
           */
          healthRecordCheckConsent?: boolean;
          /**
           * The name used to confirm the health declaration.
           *
           * @maxLength 256
           */
          healthRecordCheckConsentName?: string;
          /**
           * The date the health declaration was confirmed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          healthRecordCheckConsentDate?: string;
          /**
           * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
           */
          bankDetails?: {
            /**
             * The name on the bank account
             */
            bankAccountName?: string;
            /**
             * The name of the bank
             */
            bankName?: string;
            /**
             * The account number
             */
            accountNumber?: string;
            /**
             * The sort code
             */
            sortCode?: string;
            /**
             * The National Insurance number
             */
            niNumber?: string;
            /**
             * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
             */
            jobOp1?: boolean;
            /**
             * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
             */
            jobOp2?: boolean;
            /**
             * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
             */
            jobOp3?: boolean;
            studentLoan?: {
              /**
               * Boolean flag for if candidate has student loan
               */
              studentLoan?: boolean;
              /**
               * Boolean flag for if candidate is on plan 1
               */
              plan1?: boolean;
              /**
               * Boolean flag for if candidate is on plan 2
               */
              plan2?: boolean;
              /**
               * Boolean flag for if candidate is on plan 4
               */
              plan4?: boolean;
              /**
               * Boolean flag for if candidate has postgrad loan
               */
              postGradLoan?: boolean;
            };
          };
          /**
           * Whether the candidate has confirmed their details are correct.
           */
          declarationCheckConsent?: boolean;
          /**
           * The name of the person who confirmed their details are correct.
           *
           * @maxLength 256
           */
          declarationCheckConsentName?: string;
          /**
           * The date when consent for the declaration check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          declarationCheckConsentDate?: string;
          /**
           * Whether the candidate has consented to a contract for service
           */
          contractForServiceConsent?: boolean;
          /**
           * The name of the person who provided consent for the contract for service
           *
           * @maxLength 256
           */
          contractForServiceName?: string;
          /**
           * The date when consent for the contract for service was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          contractForServiceDate?: string;
          /**
           * The candidate has completed their onboarding form.
           */
          confirmed?: boolean;
          /**
           * The ID of the user who created the candidate record.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          createdBy?: string;
          /**
           * The ID of the customer associated with the candidate.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customerId?: string;
          /**
           * The migration that created this candidate
           *
           * @pattern ^[0-9a-f]{24}$
           */
          migrationId?: string;
          /**
           * Whether this migrated candidate has been approved
           */
          migrationApproval?: "pending" | "approved" | "rejected";
          /**
           * The user assigned to the candidate. If expanded, a user object else the user ID.
           */
          assignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * The ID of the current booking for the candidate.
           */
          currentBooking?: string[];
          /**
           * The URL of the candidate's profile photo.
           *
           * @maxLength 256
           */
          profilePhoto?: string;
          /**
           * The pay type for the candidate.
           */
          payType?: "PAYE" | "Umbrella" | "Limited Company" | "Not Applicable";
          /**
           * The pay interval for the candidate.
           */
          payInterval?:
            | "Monthly"
            | "Weekly"
            | "Weekly Pay in Arrears"
            | "Bi-Weekly"
            | any
            | null;
          /**
           * The pay rate for the candidate.
           */
          payRate?: string;
          /**
           * A custom ID for the candidate.
           *
           * @maxLength 256
           */
          customId?: string;
          /**
           * A company-defined identifier used to match the candidate with external records.
           *
           * @maxLength 256
           */
          _internalId?: string;
          /**
           * The date when the candidate's status was last changed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          statusChangeDate?: string;
          /**
           * Whether the candidate has opted out of communications.
           */
          optOut?: boolean;
          /**
           * The TRN number for the candidate.
           *
           * @maxLength 256
           */
          trnNumber?: string;
          /**
           * The date when the TRN number was last checked.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          trnNumberCheckedDate?: string;
          /**
           * Any notes related to the TRN number.
           *
           * @maxLength 1024
           */
          trnNumberNotes?: string;
          /**
           * The date when the candidate's employment is set to start.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          employmentStartDate?: string;
          /**
           * Whether the candidate has confirmed their Early Years Workforce status.
           */
          ewcConfirmation?: boolean;
          /**
           * The share code for the candidate's Home Office application.
           *
           * @maxLength 256
           */
          homeOfficeShareCode?: string;
          /**
           * Whether the candidate has been archived.
           */
          archived?: boolean;
          /**
           * Whether the candidate is using an umbrella company.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          umbrella?: string;
          /**
           * ID of the PAYE umbrella company.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          payeUmbrella?: string;
          /**
           * The name of the umbrella company the candidate is using.
           *
           * @maxLength 256
           */
          umbrellaCompanyName?: string;
          /**
           * The code of the umbrella company the candidate is using.
           *
           * @maxLength 256
           */
          umbrellaCompanyCode?: string;
          /**
           * The transport method for the candidate.
           */
          transport?:
            | "Drives Own Vehicle"
            | "Use Public Transport"
            | "Access to Another Vehicle"
            | "Bicycle"
            | "No Access"
            | "Other";
          /**
           * Any important notes related to the candidate.
           *
           * @maxLength 1024
           */
          importantNotes?: string;
          /**
           * The date when the candidate was last updated.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          candidateLastUpdated?: string;
          /**
           * The source from which the candidate was obtained.
           */
          source?:
            | (
                | "Company Website"
                | "iCan Teach UK Website"
                | "Apply to Education"
                | "CV Library - CV Search"
                | "CV Library Advert"
                | "CV Library - Watchdog"
                | "Guardian- CV Search"
                | "Guardian Advert"
                | "Indeed - CV Search"
                | "Indeed Advert"
                | "Reed - CV Search"
                | "Reed Advert"
                | "Reed \u2013 Watchdog"
                | "SENploy - Advert"
                | "Total Jobs"
                | "Other Job Board"
                | "Google"
                | "LinkedIn"
                | "Linkedin Recruiter"
                | "LinkedIn Advert"
                | "Referral - School"
                | "Referral - Candidate"
                | "UK University"
                | "Irish University"
                | "Canadian University"
                | "Marketing Campaign"
                | "Woo"
                | "Job Show"
                | "Recruitment Fair"
                | "Career Fair"
                | "Volcanic"
                | "Broadbean"
                | "Headhunt"
                | "Referral"
                | "Direct"
                | "Other"
              )
            | (
                | "Company Website"
                | "Direct Call"
                | "CV Library Watchdog"
                | "CV Library Advert"
                | "CV Library CV Search"
                | "Reed Watchdog"
                | "Reed Advert"
                | "Reed CV Search"
                | "Indeed Watchdog"
                | "Indeed Advert"
                | "Indeed CV Search"
                | "Job Centre"
                | "Returning Candidate"
                | "Referral Candidate"
                | "Referral Client"
                | "Training Provider"
                | "Recruitment Fair"
                | "University/College"
              );
          /**
           * Any other source from which the candidate was obtained.
           *
           * @maxLength 256
           */
          sourceOther?: string;
          /**
           * Whether the candidate has given consent for online checks.
           */
          onlineCheckConsent?: boolean;
          /**
           * The title of the candidate.
           */
          title?:
            | "Mr"
            | "Mrs"
            | "Ms"
            | "Miss"
            | "Dr"
            | "Mx"
            | "Lady"
            | "Lord"
            | "Other";
          /**
           * The pronoun used by the candidate.
           */
          pronoun?:
            | "She/Her/Hers"
            | "He/Him/His"
            | "They/Them/Theirs"
            | "Ze/Hir/Hirs"
            | "Ze/Zir/Zirs"
            | "Other"
            | "Prefer not to say";
          /**
           * The date when the candidate was last updated.
           *
           * @format date-time
           */
          updatedAt?: string;
          /**
           * The date when the candidate was last contacted.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          lastContactedDate?: string;
          /**
           * A short, prominent note shown with the candidate to communicate important context.
           */
          highlight?: string;
          /**
           * The pay scale for the candidate's teaching position.
           *
           * @maxLength 256
           */
          teacherPayScale?: string;
          /**
           * Whether the candidate has a P45.
           */
          p45?: boolean;
          /**
           * The type of role the candidate is applying for.
           */
          roleType?:
            | "Senior Leadership Positions"
            | "Teaching Roles"
            | "School Support Positions";
          /**
           * The Right to Work verification status for the candidate.
           *
           * @maxLength 256
           */
          rtwVerification?: string;
          /**
           * Any notes related to the Right to Work verification status for the candidate.
           *
           * @maxLength 1024
           */
          rtwVerificationNote?: string;
          /**
           * The Right to Work verification status for the candidate.
           *
           * @maxLength 256
           */
          rtwVerificationStatus?: string;
          /**
           * The location where the candidate desires to work.
           *
           * @maxLength 256
           */
          desiredLocation?: string;
          /**
           * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
           */
          complianceUser?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
           */
          watchers?:
            | string[]
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              }[];
          /**
           * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
           */
          tags?:
            | string[]
            | {
                /**
                 * The unique identifier of the tag
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The label displayed wherever this tag is applied.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                name?: string;
                /**
                 * The tag display colour as a hexadecimal value, for example #2563EB.
                 *
                 * @maxLength 32
                 */
                color?: string | null;
              }[];
          /**
           * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
           *
           * @maxItems 5
           */
          documents?: {
            /**
             * The unique identifier of the document
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The name of the document
             *
             * @maxLength 512
             */
            name?: string;
            /**
             * The file reference used to retrieve the document
             *
             * @maxLength 2048
             */
            reference?: string;
            /**
             * The file's media type
             *
             * @maxLength 256
             */
            fileType?: string;
            /**
             * The area of the platform the document belongs to
             *
             * @maxLength 256
             */
            type?: string;
            /**
             * The configured category of the document
             *
             * @maxLength 256
             */
            docType?: string;
            /**
             * The document's expiry date, when applicable
             *
             * @maxLength 64
             */
            expiry?: string;
            /**
             * The document's issued date, when applicable
             *
             * @maxLength 64
             */
            issued?: string;
            /**
             * Whether the document has been archived
             */
            documentArchived?: boolean;
            /**
             * The date the document was uploaded
             *
             * @format date-time
             */
            createdAt?: string;
          }[];
          /**
           * The total number of documents attached to the candidate.
           *
           * @minimum 0
           */
          documentsCount?: number;
          /**
           * The locations where the candidate desires to work.
           */
          desiredLocationMultiple?: string[];
          /**
           * Daily availability for the requested period. Returned by the availability view only.
           *
           * @maxItems 31
           */
          availabilityDays?: {
            /**
             * Calendar date in YYYY-MM-DD format.
             *
             * @maxLength 10
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date?: string;
            /**
             * Availability or booking state for this date.
             */
            status?:
              | "available"
              | "unavailable"
              | "booked"
              | "halfDay"
              | "awaiting"
              | "unknown";
            /**
             * Recorded availability, or null when unrecorded.
             */
            available?: boolean | null;
            /**
             * Morning or afternoon availability.
             */
            partial?: "AM" | "PM" | any | null;
            /**
             * Additional availability information.
             *
             * @maxLength 4096
             */
            note?: string | null;
            /**
             * Active bookings on this date.
             *
             * @maxItems 100
             */
            bookings?: {
              /**
               * The unique identifier of the resource
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * @maxLength 512
               */
              clientName?: string;
              /**
               * @maxLength 512
               */
              role?: string;
              /**
               * @maxLength 32
               */
              timeDesignation?: string;
            }[];
          }[];
          /**
           * The date when the candidate's availability was last checked.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          availabilityLastChecked?: string;
          /**
           * Whether the candidate is subject to notice period.
           */
          subjectToNotice?: "Unsure" | "Yes" | "No";
          /**
           * The subsidiary subjects the candidate is qualified to teach.
           */
          subsidiarySubject?: string[];
          /**
           * The candidate's preferred employment type.
           */
          employmentPreference?: (
            | "Day To Day"
            | "Long Term"
            | "Fixed Term"
            | "Perm"
          )[];
          /**
           * Any other names the candidate is known by.
           *
           * @maxLength 256
           */
          otherNames?: string;
          /**
           * The name of the candidate's limited company, if applicable.
           *
           * @maxLength 256
           */
          ltdCompany?: string;
          /**
           * Whether the candidate's employment has been verified.
           */
          employmentVerified?: boolean;
          /**
           * The DBS verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSVerification?: string;
          /**
           * Any notes related to the DBS verification status for the candidate.
           *
           * @maxLength 1024
           */
          candidateDBSVerificationNote?: string;
          /**
           * The DBS verification status for the candidate.
           */
          candidateDBSVerificationStatus?: "Cleared" | "Alerts";
          /**
           * The DBS check verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSCheckVerification?: string;
          /**
           * Any notes related to the DBS check verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSCheckVerificationNote?: string;
          /**
           * The DBS check verification status for the candidate.
           */
          candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
          /**
           * Any notes related to the DBS update service verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSUpdateServiceVerificationNote?: string;
          /**
           * The DBS update service verification status for the candidate.
           */
          candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
          /**
           * Any notes related to the TRN verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateTRNVerificationNote?: string;
          /**
           * The TRN verification status for the candidate.
           */
          candidateTRNVerificationStatus?:
            | "Completed Induction"
            | "Not Yet Completed Induction"
            | "Induction Exempt"
            | "ALERTS - Failed Induction"
            | "Induction Not Required";
          /**
           * The health and safety verification status for the candidate.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          candidateHealthSafetyVerification?: string;
          /**
           * Any notes related to the health and safety verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateHealthSafetyVerificationNote?: string;
          /**
           * The health and safety verification status for the candidate.
           */
          candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
          /**
           * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
           */
          onboardingSession?:
            | string
            | {
                /**
                 * Unique identifier for the onboarding session
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The URL for the onboarding session
                 *
                 * @format uri
                 */
                url?: string;
                /**
                 * The frozen health declaration configuration for this onboarding session.
                 */
                healthDeclarationConfig?: {
                  /**
                   * The ordered questions included in the question set.
                   *
                   * @maxItems 100
                   */
                  questions?: {
                    /**
                     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id?: string;
                    /**
                     * A stable key used to associate the question with current and historical answers.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    key?: string;
                    /**
                     * The question shown to the person completing the form.
                     *
                     * @minLength 1
                     * @maxLength 500
                     */
                    label?: string;
                    /**
                     * Optional supporting text displayed with the question.
                     *
                     * @maxLength 2000
                     */
                    description?: string;
                    /**
                     * The type of answer collected for the question.
                     */
                    type?:
                      | "checkbox"
                      | "date"
                      | "daterange"
                      | "dropdown"
                      | "number"
                      | "signature"
                      | "text"
                      | "textarea";
                    /**
                     * Whether an answer is required while the question is visible.
                     */
                    required?: boolean;
                    /**
                     * Whether the question is included in new forms. Historical responses retain disabled questions.
                     */
                    enabled?: boolean;
                    /**
                     * The display order of the question.
                     *
                     * @minimum 0
                     * @maximum 1000
                     */
                    order?: number;
                    /**
                     * Optional rules that determine when the question is shown.
                     */
                    condition?: {
                      /**
                       * Whether any rule or every rule must match before the question is shown.
                       */
                      mode?: "any" | "all";
                      /**
                       * The comparisons used to determine question visibility.
                       *
                       * @minItems 1
                       * @maxItems 20
                       */
                      rules?: {
                        /**
                         * The stable key of the question that controls visibility.
                         *
                         * @minLength 1
                         * @maxLength 128
                         */
                        questionKey?: string;
                        /**
                         * How the controlling answer is compared with the value.
                         */
                        operator?: "equals" | "notEquals";
                        /**
                         * The answer value used to determine visibility.
                         */
                        value?: string | boolean | number | (void | null);
                      }[];
                    };
                    /**
                     * The available answers for a dropdown question.
                     *
                     * @minItems 1
                     * @maxItems 100
                     */
                    options?: {
                      /**
                       * The value stored when this option is selected.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      value?: string;
                      /**
                       * The option displayed to the person answering the question.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      label?: string;
                    }[];
                    /**
                     * Whether this question belongs to the standard template.
                     */
                    ["default"]?: boolean;
                    /**
                     * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                     */
                    audience?: "candidate" | "staff";
                    /**
                     * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                     */
                    fixedRole?: "fullName" | "signature";
                  }[];
                  /**
                   * When this question configuration was last updated.
                   *
                   * @format date-time
                   */
                  updatedAt?: string;
                };
                /**
                 * The frozen Criminal Record & Security Checks configuration for this onboarding session.
                 */
                criminalRecordConfig?: {
                  /**
                   * The ordered questions included in the question set.
                   *
                   * @maxItems 100
                   */
                  questions?: {
                    /**
                     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id?: string;
                    /**
                     * A stable key used to associate the question with current and historical answers.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    key?: string;
                    /**
                     * The question shown to the person completing the form.
                     *
                     * @minLength 1
                     * @maxLength 500
                     */
                    label?: string;
                    /**
                     * Optional supporting text displayed with the question.
                     *
                     * @maxLength 2000
                     */
                    description?: string;
                    /**
                     * The type of answer collected for the question.
                     */
                    type?:
                      | "checkbox"
                      | "date"
                      | "daterange"
                      | "dropdown"
                      | "number"
                      | "signature"
                      | "text"
                      | "textarea";
                    /**
                     * Whether an answer is required while the question is visible.
                     */
                    required?: boolean;
                    /**
                     * Whether the question is included in new forms. Historical responses retain disabled questions.
                     */
                    enabled?: boolean;
                    /**
                     * The display order of the question.
                     *
                     * @minimum 0
                     * @maximum 1000
                     */
                    order?: number;
                    /**
                     * Optional rules that determine when the question is shown.
                     */
                    condition?: {
                      /**
                       * Whether any rule or every rule must match before the question is shown.
                       */
                      mode?: "any" | "all";
                      /**
                       * The comparisons used to determine question visibility.
                       *
                       * @minItems 1
                       * @maxItems 20
                       */
                      rules?: {
                        /**
                         * The stable key of the question that controls visibility.
                         *
                         * @minLength 1
                         * @maxLength 128
                         */
                        questionKey?: string;
                        /**
                         * How the controlling answer is compared with the value.
                         */
                        operator?: "equals" | "notEquals";
                        /**
                         * The answer value used to determine visibility.
                         */
                        value?: string | boolean | number | (void | null);
                      }[];
                    };
                    /**
                     * The available answers for a dropdown question.
                     *
                     * @minItems 1
                     * @maxItems 100
                     */
                    options?: {
                      /**
                       * The value stored when this option is selected.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      value?: string;
                      /**
                       * The option displayed to the person answering the question.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      label?: string;
                    }[];
                    /**
                     * Whether this question belongs to the standard template.
                     */
                    ["default"]?: boolean;
                    /**
                     * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                     */
                    audience?: "candidate" | "staff";
                    /**
                     * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                     */
                    fixedRole?: "fullName" | "signature";
                  }[];
                  /**
                   * When this question configuration was last updated.
                   *
                   * @format date-time
                   */
                  updatedAt?: string;
                };
              };
          /**
           * The contact record for this candidate. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * Whether the candidate has opted out of 48 hour communications.
           */
          optOut48Hours?: boolean;
          /**
           * Whether the candidate has consented to REC audit checks.
           */
          recAuditConsent?: boolean | null;
          /**
           * The date the candidate's REC audit consent was recorded.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          recAuditConsentDate?: string;
          /**
           * The method through which REC audit consent was captured.
           *
           * @maxLength 256
           */
          recAuditConsentMethod?: string;
          /**
           * The date when the candidate record was created.
           *
           * @format date-time
           */
          createdAt?: string;
          /**
           * The mobile app link status for the candidate.
           */
          candidateMobileAppStatus?:
            | "Unlinked"
            | "Scheduled Unlink"
            | "Linked"
            | "Invited"
            | "Not Invited";
          /**
           * Evertime details for this candidate
           */
          evertime?: {
            /**
             * The Evertime CandidateId used for this candidate when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
            /**
             * Whether the matched Evertime candidate's NI number also matches the one held in Edun
             */
            niNumberMatch?: boolean;
          };
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Candidate fields or attached document excerpts that explain why the candidate matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * Whether the matching information came from the candidate record or an attached document.
             */
            source?: "candidate" | "document";
            /**
             * The candidate or document information that matched the search.
             *
             * @maxLength 256
             */
            field?: string;
            /**
             * A short excerpt showing why the result matched.
             *
             * @maxLength 1000
             */
            text?: string;
            /**
             * The exact terms within the content that matched the search, including synonym matches.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
            /**
             * The name of the attached document when the match came from a document.
             *
             * @maxLength 512
             */
            documentName?: string | null;
            /**
             * The unique identifier of the attached document when the match came from a document.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            documentId?: string | null;
          }[];
          /**
           * The candidate's distance in miles from the requested location. Present only when location filtering is used.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * Timesheet Portal details for this candidate
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal contractor GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal contractor code
             *
             * @maxLength 256
             */
            code?: string;
          };
          /**
           * Invite to candidate to link their mobile app.
           */
          mobileAppLinkInvite?: {
            /**
             * Boolean status of whether the link invite is being used
             *
             * @default false
             */
            isUsed?: boolean;
            /**
             * Creation date of the invite
             *
             * @format date-time
             * @default 2026-10-30T13:39:09.027Z
             */
            createdAt?: string;
            /**
             * ID of candidate using this link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            usedBy?: string;
            /**
             * Date of when link used
             *
             * @format date-time
             */
            usedAt?: string;
            /**
             * If unlinked, ID of user that unlinked this candidate's link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            unlinkedBy?: string;
            /**
             * If unlinked, date of when this link was unlinked
             *
             * @format date-time
             */
            unlinkedAt?: string;
            /**
             * If deleted, ID of user that deleted this candidate's link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            deletedBy?: string;
            /**
             * If deleted, date of when this link was deleted
             *
             * @format date-time
             */
            deletedAt?: string;
            /**
             * Current status of this mobile app link
             */
            status?:
              | "Sent"
              | "Linked"
              | "Unlinked"
              | "ScheduledUnlink"
              | "Not Sent"
              | "Rescinded";
          };
        }
      | (void | null);
    /**
     * The Client contact linked to this request.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientContact?: string | null;
    /**
     * The Client linked to the request's Client contact.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    clientId?: string | null;
    /**
     * The recipient name captured when the request was issued.
     *
     * @minLength 1
     * @maxLength 256
     */
    recipientName?: string | null;
    /**
     * The recipient email captured when the request was issued.
     *
     * @maxLength 320
     * @format email
     */
    recipientEmail?: string | null;
    /**
     * The point-in-time form definition presented to the recipient.
     */
    snapshot?: {
      /**
       * The internal and recipient-facing title of the form.
       *
       * @minLength 1
       * @maxLength 256
       */
      title?: string;
      /**
       * Optional introductory text shown before the questions.
       *
       * @maxLength 5000
       */
      description?: string | null;
      /**
       * Optional message shown after a response is submitted.
       *
       * @maxLength 2000
       */
      confirmationMessage?: string | null;
      /**
       * The ordered questions included in the form.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * The form definition from which this snapshot was captured.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      formId?: string;
      /**
       * When the captured form definition was last updated.
       *
       * @format date-time
       */
      configurationUpdatedAt?: string;
    };
    /**
     * The current lifecycle state of this request.
     */
    status?: "issued" | "inProgress" | "submitted" | "revoked" | "expired";
    /**
     * When the signed request link stops being valid.
     *
     * @format date-time
     */
    expiresAt?: string;
    /**
     * When the recipient first saved answers.
     *
     * @format date-time
     */
    startedAt?: string | null;
    /**
     * When the recipient submitted the response.
     *
     * @format date-time
     */
    submittedAt?: string | null;
    /**
     * When access to the request was revoked.
     *
     * @format date-time
     */
    revokedAt?: string | null;
    /**
     * The user who issued the request. If expanded, a user object; otherwise, a user ID.
     */
    createdBy?:
      | string
      | {
          /**
           * The unique identifier for the user
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The email address of the user
           *
           * @format email
           */
          email?: string;
          /**
           * The first name of the user
           *
           * @maxLength 128
           */
          firstName?: string;
          /**
           * The last name of the user
           *
           * @maxLength 128
           */
          lastName?: string;
          /**
           * The username of the user
           *
           * @maxLength 128
           */
          username?: string;
          /**
           * The local education authority of the user
           *
           * @maxLength 128
           */
          lea?: string;
          /**
           * Array of client IDs associated with the user
           */
          clients?: string[];
          /**
           * The position or job title of the user
           *
           * @maxLength 128
           */
          position?: string;
          /**
           * Array of permissions assigned to the user
           */
          permissions?: number[];
          /**
           * The activation code for the user
           *
           * @maxLength 256
           */
          activationCode?: string;
          /**
           * The unique identifier of the company the user belongs to.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customer?: string;
          /**
           * Indicates whether the user account is disabled
           */
          disabled?: boolean;
          /**
           * Array of commission thresholds for the user
           */
          commissionThreshold?: {
            min?: number | string;
            commission?: number | string;
            type?: "Fixed" | "Variable";
            weekly?: boolean;
          }[];
          /**
           * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
           */
          branches?:
            | string[]
            | {
                /**
                 * The unique identifier for the branch
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The name of the branch
                 *
                 * @maxLength 256
                 */
                name?: string;
                /**
                 * The code of the branch
                 *
                 * @maxLength 32
                 */
                code?: string;
                /**
                 * The contact email of the branch
                 *
                 * @maxLength 256
                 * @format email
                 */
                contactEmail?: string;
                /**
                 * The contact phone number of the branch
                 *
                 * @maxLength 32
                 */
                contactTelephone?: string;
                /**
                 * The contact website of the branch
                 *
                 * @maxLength 512
                 */
                contactWebsite?: string;
                /**
                 * The accent color for the customer that the branch belongs to
                 *
                 * @maxLength 7
                 */
                customerAccentColor?: string;
                /**
                 * The address of the branch
                 */
                address?: {
                  /**
                   * The first line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine1?: string;
                  /**
                   * The second line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine2?: string;
                  /**
                   * The third line of the address
                   *
                   * @maxLength 256
                   */
                  addressLine3?: string;
                  /**
                   * The city of the address
                   *
                   * @maxLength 256
                   */
                  city?: string;
                  /**
                   * The county of the address
                   *
                   * @maxLength 256
                   */
                  county?: string;
                  /**
                   * The borough of the address
                   *
                   * @maxLength 256
                   */
                  borough?: string;
                  /**
                   * The postcode of the address
                   *
                   * @maxLength 10
                   */
                  postcode?: string;
                  /**
                   * The country of the address
                   *
                   * @maxLength 256
                   */
                  country?: string;
                  /**
                   * The latitude of the address
                   *
                   * @minimum -90
                   * @maximum 90
                   */
                  lat?: number;
                  /**
                   * The longitude of the address
                   *
                   * @minimum -180
                   * @maximum 180
                   */
                  lng?: number;
                };
                /**
                 * Indicates whether the branch is archived
                 */
                archived?: boolean;
                /**
                 * The number of users assigned to the branch. Only returned when explicitly selected.
                 *
                 * @minimum 0
                 */
                userCount?: number;
              }[];
          /**
           * Indicates whether to display the onboarding process for the user
           */
          displayOnboarding?: boolean;
          /**
           * A custom identifier for the user
           *
           * @maxLength 256
           */
          _customId?: string;
          /**
           * The signature of the user in base64 format
           *
           * @maxLength 2048
           */
          signature?: string | null;
          /**
           * The phone number of the user
           *
           * @maxLength 32
           */
          phone?: string;
          /**
           * Indicates whether the user is the default candidate watcher
           */
          defaultCandidateWatcher?: boolean;
          /**
           * The user's default task workspace
           *
           * @pattern ^[0-9a-f]{24}$
           */
          defaultTaskWorkspace?: string | null;
          /**
           * Indicates whether the user has team admin privileges
           */
          teamAdmin?: boolean;
          /**
           * The contact record for this user. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * The date and time the user account was created
           */
          createdAt?: string;
          /**
           * Evertime details for this user
           */
          evertime?: {
            /**
             * The Evertime ConsultantId used for this user when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
          };
          /**
           * Timesheet Portal details for this user
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal administrator GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal administrator code
             *
             * @maxLength 256
             */
            code?: string;
          };
        };
    /**
     * Email delivery information for this request.
     */
    delivery?: {
      /**
       * When email delivery was most recently requested.
       *
       * @format date-time
       */
      emailRequestedAt?: string;
      /**
       * When the request email was most recently sent.
       *
       * @format date-time
       */
      emailSentAt?: string;
      /**
       * When the most recent request email failed to send.
       *
       * @format date-time
       */
      emailFailedAt?: string;
    };
    /**
     * When the request was issued.
     *
     * @format date-time
     */
    createdAt?: string;
    /**
     * When the request was last updated.
     *
     * @format date-time
     */
    updatedAt?: string;
  };
};

export type GetFormRequestVariables = {
  pathParams: GetFormRequestPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetFormRequest = (
  variables: GetFormRequestVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetFormRequestResponse,
    GetFormRequestError,
    undefined,
    {},
    {},
    GetFormRequestPathParams
  >({
    url: "/form-requests/{formRequestId}",
    method: "get",
    ...variables,
    signal,
  });

export function getFormRequestQuery(variables: GetFormRequestVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetFormRequestResponse>;
};

export function getFormRequestQuery(
  variables: GetFormRequestVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetFormRequestResponse>)
    | reactQuery.SkipToken;
};

export function getFormRequestQuery(
  variables: GetFormRequestVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/form-requests/{formRequestId}",
      operationId: "getFormRequest",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetFormRequest(variables, signal),
  };
}

export const useSuspenseGetFormRequest = <TData = GetFormRequestResponse,>(
  variables: GetFormRequestVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetFormRequestResponse,
      GetFormRequestError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetFormRequestResponse,
    GetFormRequestError,
    TData
  >({
    ...getFormRequestQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetFormRequest = <TData = GetFormRequestResponse,>(
  variables: GetFormRequestVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetFormRequestResponse,
      GetFormRequestError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetFormRequestResponse,
    GetFormRequestError,
    TData
  >({
    ...getFormRequestQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateFormRequestPathParams = {
  /**
   * The issued form request identifier.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  formRequestId: string;
};

export type UpdateFormRequestError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 409;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Resource limit reached
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateFormRequestResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The issued request metadata.
     */
    request?: {
      /**
       * The unique identifier of the issued form request.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      id?: string;
      /**
       * The form definition used to create this request.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      formId?: string;
      /**
       * The submitted response for this request, when one is available.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      responseId?: string | null;
      /**
       * The candidate linked to this request. If expanded, a candidate object; otherwise, a candidate ID.
       */
      candidate?:
        | string
        | {
            /**
             * The unique identifier of the candidate
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The forename of the candidate
             *
             * @minLength 1
             * @maxLength 256
             */
            forename?: string;
            /**
             * The surname of the candidate
             *
             * @minLength 1
             * @maxLength 256
             */
            surname?: string;
            /**
             * Any previous names of the candidate
             *
             * @maxLength 256
             */
            previousNames?: string;
            /**
             * The email of the candidate. This will be used for communication.
             *
             * @format email
             */
            email?: string;
            /**
             * The telephone number of the candidate
             *
             * @maxLength 32
             */
            telephone?: string;
            /**
             * The mobile number of the candidate
             *
             * @maxLength 32
             */
            mobile?: string;
            /**
             * The occupation of the candidate
             */
            occupation?:
              | "Teacher"
              | "Cover Supervisor"
              | "Teaching Assistant"
              | "School Support Staff"
              | "Primary Teacher"
              | "Secondary Teacher"
              | "Head Teacher"
              | "Senior Leader"
              | "Tutor"
              | "Mentor"
              | "Learning Support Assistant"
              | "Finance Officer"
              | "HR Administrator"
              | "HR Manager"
              | "Office Manager"
              | "Administrator"
              | "Personal Assistant"
              | "Pastoral Manager"
              | "Receptionist"
              | "Instructor"
              | "Caretaker"
              | "Nursery Assistant"
              | "Nursery Nurse"
              | "Deputy Nursery Manager"
              | "Lunchtime Supervisor"
              | "Classroom Graduate"
              | "Primary Cover Supervisor"
              | "HLTA"
              | "Nurture Teacher"
              | "Nurture Mentor"
              | "SENDCo"
              | "Registered Manager"
              | "Regional Manager"
              | "Education Director"
              | "Residential Director"
              | "Deputy Manager"
              | "Residential Support Worker"
              | "Sports Coach"
              | "Executive Headteacher"
              | "General Practitioner"
              | "Advance Nurse Practitioner (ANP)"
              | "Emergency Nurse Practitioner (ENP)"
              | "Advanced Clinical Practitioner (ACP)"
              | "Emergency Clinical Practitioner (ECP)"
              | "Consultant Psychiatrist"
              | "Psychiatrist \u2013 SPR"
              | "IT Technician"
              | "Art Technician"
              | "DT Technician"
              | "Science Technician"
              | "Healthcare Assistant/ Residential Support Worker"
              | "Healthcare Assistant"
              | "Senior Healthcare Assistant"
              | "Team Leader"
              | "Registered General Nurse"
              | "Registered Mental Nurse"
              | "Registered Nurse Learning Disabilities"
              | "Overseas Trained Teacher";
            /**
             * The subject the candidate specializes in
             *
             * @maxLength 256
             */
            subject?: string;
            /**
             * The address of the resource
             */
            address?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The qualifications of the candidate
             *
             * @maxItems 32
             */
            qualifications?: {
              /**
               * The type of the qualification
               *
               * @maxLength 256
               */
              type?: string;
              /**
               * The place where the qualification was obtained
               *
               * @maxLength 256
               */
              place?: string;
              /**
               * The name of the qualification
               *
               * @maxLength 256
               */
              qualificationName?: string;
              /**
               * The date the qualification was obtained
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              date?: string;
              /**
               * The unique identifier of the qualification
               *
               * @maxLength 256
               */
              qualificationId?: string;
              /**
               * The order index of the qualification
               *
               * @minimum 0
               */
              orderIndex?: number;
              /**
               * The expiry date of the qualification
               *
               * @maxLength 256
               */
              qualificationExpiry?: string;
              /**
               * Whether to exclude this qualification from vetting
               */
              excludeFromVetting?: boolean;
              /**
               * The date the qualification was created
               *
               * @format date-time
               */
              createdAt?: string | null;
            }[];
            /**
             * The employment history of the candidate
             *
             * @maxItems 32
             */
            employment?: {
              /**
               * The name of the employer
               *
               * @maxLength 256
               */
              employerName?: string;
              /**
               * The date the candidate started employment
               *
               * @maxLength 256
               */
              dateStarted?: string;
              /**
               * The date the candidate ended employment
               *
               * @maxLength 256
               */
              dateEnded?: string;
              /**
               * The job title of the candidate
               *
               * @maxLength 256
               */
              jobTitle?: string;
              /**
               * The reason the candidate left this employment
               *
               * @maxLength 1024
               */
              reasonForLeaving?: string;
              /**
               * The duties of the candidate in this role. Consultant only, visible on Edun CV
               *
               * @maxLength 1024
               */
              duties?: string;
              /**
               * The salary of the candidate in this role
               *
               * @maxLength 256
               */
              salary?: string;
              /**
               * The address of the candidate's reference for this employment
               */
              referenceAddress?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
              /**
               * The unique identifier for this employment record
               *
               * @maxLength 256
               */
              employmentId?: string;
              /**
               * The order index of this employment record
               *
               * @minimum 0
               */
              orderIndex?: number;
            }[];
            /**
             * The references for the candidate
             *
             * @maxItems 32
             */
            references?: {
              /**
               * The title of the reference
               *
               * @maxLength 16
               */
              referenceTitle?: string;
              /**
               * The forename of the reference
               *
               * @maxLength 256
               */
              referenceForename?: string;
              /**
               * The surname of the reference
               *
               * @maxLength 256
               */
              referenceSurname?: string;
              /**
               * The company of the reference
               *
               * @maxLength 256
               */
              referenceCompany?: string;
              /**
               * The position of the reference
               *
               * @maxLength 256
               */
              referencePosition?: string;
              /**
               * The email of the reference
               *
               * @format email
               */
              referenceEmail?: string;
              /**
               * The telephone number of the reference
               *
               * @maxLength 32
               */
              referenceTelephone?: string;
              /**
               * The type of the reference
               */
              referenceType?:
                | "Education"
                | "Professional"
                | "Character"
                | "Factual"
                | "Academic"
                | "Volunteer";
              /**
               * The duration of the reference
               *
               * @maxLength 128
               */
              referenceDuration?: string;
              /**
               * The ID of the reference
               *
               * @maxLength 256
               */
              referenceId?: string;
              /**
               * This field only applies if the reference immediate consent question is enabled
               * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
               */
              referenceApply?: boolean;
              /**
               * Any additional notes about the reference
               *
               * @maxLength 1024
               */
              referenceNote?: string;
              /**
               * The start date of the reference
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              dateFrom?: string;
              /**
               * The end date of the reference
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              dateTo?: string;
            }[];
            /**
             * Whether the candidate has consented to a reference check
             */
            referenceCheckConsent?: boolean;
            /**
             * The name of the person who provided consent for the reference check
             *
             * @maxLength 256
             */
            referenceCheckConsentName?: string;
            /**
             * The date when consent for the reference check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            referenceCheckConsentDate?: string;
            /**
             * The date of birth of the candidate
             *
             * @format date-time
             */
            dob?: string | null;
            /**
             * The sex of the candidate
             */
            sex?: "Male" | "Female" | "Other" | "Unknown";
            /**
             * The status of the candidate
             */
            status?:
              | "Pending"
              | "Approved"
              | "Rejected"
              | "Anonymised"
              | "Archived"
              | "Enquired"
              | "Hold"
              | "Future"
              | "DNC"
              | "Pre";
            /**
             * The current status of the onboarding process for the candidate
             */
            onboardingStatus?: "Not Started" | "In Progress" | "Completed";
            /**
             * Whether the onboarding information has been sent to the candidate
             */
            onboardingSent?: boolean;
            nextOfKin?: {
              /**
               * The name of the next of kin
               *
               * @maxLength 256
               */
              nextOfKinName?: string;
              /**
               * The relationship of the next of kin to the candidate
               *
               * @maxLength 256
               */
              nextOfKinRelationship?: string;
              /**
               * The telephone number of the next of kin
               *
               * @maxLength 32
               */
              nextOfKinTelephone?: string;
              /**
               * The email address of the next of kin
               *
               * @format email
               */
              nextOfKinEmail?: string;
              nextOfKinAddress?: {
                /**
                 * The first line of the address
                 *
                 * @maxLength 256
                 */
                addressLine1?: string;
                /**
                 * The second line of the address
                 *
                 * @maxLength 256
                 */
                addressLine2?: string;
                /**
                 * The third line of the address
                 *
                 * @maxLength 256
                 */
                addressLine3?: string;
                /**
                 * The city of the address
                 *
                 * @maxLength 256
                 */
                city?: string;
                /**
                 * The county of the address
                 *
                 * @maxLength 256
                 */
                county?: string;
                /**
                 * The borough of the address
                 *
                 * @maxLength 256
                 */
                borough?: string;
                /**
                 * The postcode of the address
                 *
                 * @maxLength 10
                 */
                postcode?: string;
                /**
                 * The country of the address
                 *
                 * @maxLength 256
                 */
                country?: string;
                /**
                 * The latitude of the address
                 *
                 * @minimum -90
                 * @maximum 90
                 */
                lat?: number;
                /**
                 * The longitude of the address
                 *
                 * @minimum -180
                 * @maximum 180
                 */
                lng?: number;
              };
            };
            criminalRecord?: {
              /**
               * Whether the candidate has a DBS certificate
               */
              dbsHeld?: boolean;
              /**
               * The unique identifier for the DBS certificate
               *
               * @maxLength 256
               */
              dbsNumber?: string;
              /**
               * The type of DBS check
               */
              dbsType?:
                | "Basic"
                | "Standard"
                | "Enhanced"
                | "Enhanced with Barred List"
                | "Child workforce"
                | "Child and adult workforce"
                | "Adult workforce";
              /**
               * The date when the DBS check was last performed
               *
               * @maxLength 256
               */
              dbsLastChecked?: string;
              /**
               * The DBS update service number for the candidate
               *
               * @maxLength 256
               */
              dbsUpdateServiceNumber?: string;
              /**
               * The result of the prohibition check
               *
               * @maxLength 256
               */
              prohibitionCheck?: string;
              /**
               * The result of the overseas check
               *
               * @maxLength 256
               */
              overseasCheck?: string;
              /**
               * Any additional notes regarding the overseas check
               *
               * @maxLength 256
               */
              overseasCheckNote?: string;
              /**
               * Whether the candidate has a convicted offence
               */
              convictedOffence?: boolean;
              /**
               * The description of the convicted offence
               *
               * @maxLength 1024
               */
              convictedOffenceDescription?: string;
              /**
               * Whether the candidate has any convictions
               */
              convictions?: boolean;
              /**
               * The description of the convictions
               *
               * @maxLength 1024
               */
              convictionsDescription?: string;
              /**
               * Whether the candidate is registered to the DBS update service
               */
              registeredToUpdateService?: boolean;
              /**
               * Whether the candidate consents to update service checks
               */
              consentUpdateServiceChecks?: boolean;
              /**
               * Any additional notes regarding the candidate's criminal record
               *
               * @maxLength 1024
               */
              note?: string;
              /**
               * The date when the update service check was performed
               *
               * @maxLength 64
               */
              updateServiceCheckDate?: string;
              /**
               * Whether the candidate has lived abroad
               */
              abroad?: boolean;
              /**
               * Any additional notes regarding the overseas declaration
               *
               * @maxLength 1024
               */
              overseasDeclarationExtended?: string;
              /**
               * Whether the candidate has been subject to educational disciplinary proceedings
               */
              educationalDisciplinaryProceedings?: boolean;
              /**
               * Any comments regarding the DBS check
               *
               * @maxLength 1024
               */
              dbsComments?: string;
              /**
               * Whether the candidate has read the DBS declaration
               */
              dbsDeclarationRead?: boolean;
              /**
               * Whether the candidate has any overseas offences
               */
              overseasOffence?: boolean;
              /**
               * The description of the overseas offence
               *
               * @maxLength 1024
               */
              overseasOffenceDescription?: string;
              /**
               * Whether the candidate has a current criminal offence
               */
              currentCriminalOffence?: boolean;
              /**
               * The description of the current criminal offence
               *
               * @maxLength 1024
               */
              currentCriminalOffenceDescription?: string;
              /**
               * Whether the DBS was issued by a business
               */
              dbsIssuedByBusiness?: boolean;
              /**
               * Whether the DBS was issued by the current employer
               */
              dbsIssuedByCurrentEmployer?: boolean;
              /**
               * Whether a Section 128 check is required for the candidate.
               */
              section128Required?: boolean;
              /**
               * The date when the Section 128 check was completed.
               */
              section128Checked?: string;
            };
            /**
             * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
             */
            criminalRecordDeclaration?: {
              /**
               * The ordered questions included in the question set.
               *
               * @maxItems 100
               */
              questions?: {
                /**
                 * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * A stable key used to associate the question with current and historical answers.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                key?: string;
                /**
                 * The question shown to the person completing the form.
                 *
                 * @minLength 1
                 * @maxLength 500
                 */
                label?: string;
                /**
                 * Optional supporting text displayed with the question.
                 *
                 * @maxLength 2000
                 */
                description?: string;
                /**
                 * The type of answer collected for the question.
                 */
                type?:
                  | "checkbox"
                  | "date"
                  | "daterange"
                  | "dropdown"
                  | "number"
                  | "signature"
                  | "text"
                  | "textarea";
                /**
                 * Whether an answer is required while the question is visible.
                 */
                required?: boolean;
                /**
                 * Whether the question is included in new forms. Historical responses retain disabled questions.
                 */
                enabled?: boolean;
                /**
                 * The display order of the question.
                 *
                 * @minimum 0
                 * @maximum 1000
                 */
                order?: number;
                /**
                 * Optional rules that determine when the question is shown.
                 */
                condition?: {
                  /**
                   * Whether any rule or every rule must match before the question is shown.
                   */
                  mode?: "any" | "all";
                  /**
                   * The comparisons used to determine question visibility.
                   *
                   * @minItems 1
                   * @maxItems 20
                   */
                  rules?: {
                    /**
                     * The stable key of the question that controls visibility.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    questionKey?: string;
                    /**
                     * How the controlling answer is compared with the value.
                     */
                    operator?: "equals" | "notEquals";
                    /**
                     * The answer value used to determine visibility.
                     */
                    value?: string | boolean | number | (void | null);
                  }[];
                };
                /**
                 * The available answers for a dropdown question.
                 *
                 * @minItems 1
                 * @maxItems 100
                 */
                options?: {
                  /**
                   * The value stored when this option is selected.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  value?: string;
                  /**
                   * The option displayed to the person answering the question.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  label?: string;
                }[];
                /**
                 * Whether this question belongs to the standard template.
                 */
                ["default"]?: boolean;
                /**
                 * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                 */
                audience?: "candidate" | "staff";
                /**
                 * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                 */
                fixedRole?: "fullName" | "signature";
              }[];
              /**
               * The answers recorded for the point-in-time question set.
               *
               * @maxItems 100
               */
              answers?: {
                /**
                 * The identifier of the question being answered.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                questionId?: string;
                /**
                 * The stable key of the question being answered.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey?: string;
                /**
                 * The submitted answer to the question.
                 */
                value?: string | string | boolean | number | (void | null);
              }[];
              /**
               * When the question set was submitted.
               *
               * @format date-time
               */
              submittedAt?: string;
              /**
               * The network address recorded when the response was submitted.
               *
               * @maxLength 64
               */
              submittedIp?: string;
              /**
               * When the question configuration captured by this response was last updated.
               *
               * @format date-time
               */
              configurationUpdatedAt?: string;
            };
            /**
             * Whether the candidate confirmed their criminal record declaration.
             */
            criminalRecordCheckConsent?: boolean;
            /**
             * The name used to confirm the criminal record declaration.
             *
             * @maxLength 256
             */
            criminalRecordCheckConsentName?: string;
            /**
             * The date the criminal record declaration was confirmed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            criminalRecordCheckConsentDate?: string;
            safeInEducation?: {
              /**
               * Whether the candidate is barred from working in education
               */
              barred?: boolean;
              /**
               * The date when the barred status was checked
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              barredCheckDate?: string;
              /**
               * Whether the candidate has a waiver for the barred status
               */
              waiver?: boolean;
              /**
               * Any orders related to the candidate's barred status
               */
              ordersYou?: boolean;
              /**
               * Any orders related to the candidate's barred status for children
               */
              ordersChild?: boolean;
              /**
               * Whether the candidate has any previous safeguarding allegations
               */
              previousSafeguardingAllegations?: boolean;
              /**
               * Whether the candidate has been cautioned
               */
              cautioned?: boolean;
              /**
               * Whether the candidate has a violent offence
               */
              violentOffence?: boolean;
              /**
               * Whether the candidate has lived or worked in another country
               */
              anotherCountry?: boolean;
              /**
               * Whether the candidate has read the Keeping Children Safe in Education document
               */
              readKCSIE?: boolean;
              /**
               * Whether the candidate has read the Disqualification under the Childcare Act guidance
               */
              readChildcareAct?: boolean;
              /**
               * Whether the candidate is prohibited from teaching
               */
              prohibitedFromTeaching?: boolean;
              /**
               * Whether the candidate is known to the police
               */
              knownPolice?: boolean;
              /**
               * Whether the candidate is prohibited from management roles
               */
              prohibitedFromManagement?: boolean;
              /**
               * Whether the candidate has any criminal offences under law
               */
              criminalOffencesLaw?: boolean;
              /**
               * Any additional details regarding the candidate's safe in education status
               */
              safeInEducationDetails?: string;
              safeguardingQuestionaire?: {
                /**
                 * What is the primary purpose of safeguarding in a school environment?
                 */
                primaryPurposeSafeguarding?:
                  | "enhance-academic-performance"
                  | "ensure-safe-learning-environment"
                  | "promote-healthy-relationships"
                  | "facilitate-extracurricular-activities";
                /**
                 * Who is typically responsible for coordinating safeguarding efforts within a school?
                 */
                typicallyResponsibleCoordinatingSafeguarding?:
                  | "janitorial-staff"
                  | "parents-association"
                  | "school-nurse"
                  | "designated-safeguarding-lead";
                /**
                 * Which of the following is considered a potential sign of child abuse or neglect?
                 */
                signOfAbuse?:
                  | "student-excelling"
                  | "frequent-extracurricular"
                  | "unexplained-injuries"
                  | "not-listening";
                /**
                 * How should a teacher respond if a student discloses information about potential abuse or harm?
                 */
                studentDisclosesInformation?:
                  | "ignore-disclosure"
                  | "report-disclosure"
                  | "share-information"
                  | "advise-independent";
                /**
                 * What is the purpose of a school's Child Protection Policy?
                 */
                schoolsChildProtectionPolicy?:
                  | "enforcing-strict-disciplinary-measures"
                  | "identifying-troublemakers"
                  | "outlining-procedures-for-protecting-students-from-harm"
                  | "establishing-academic-achievement-standards";
                /**
                 * What does the term 'FGM' stand for in the context of safeguarding?
                 */
                fgmDefinition?:
                  | "female-general-medicine"
                  | "forced-genetic-modification"
                  | "female-genital-mutilation"
                  | "foundation-gender-equality";
                /**
                 * Which of the following is a key principle of online safety for students?
                 */
                keyPrincipleOnlineSafetyStudents?:
                  | "unlimited-screen-time"
                  | "anon-online-interaction"
                  | "responsible-digital-citizen"
                  | "restrict-edu-sites";
                /**
                 * When planning a school trip, what should teachers consider regarding safeguarding?
                 */
                teachersConsiderRegardingSafeguarding?:
                  | "minimise-supervision"
                  | "appropriate-staff-ratios"
                  | "exclude-medical-conditions"
                  | "allow-roaming";
                /**
                 * What is the purpose of a criminal background check for teachers?
                 */
                criminalBackgroundCheckTeachers?:
                  | "evaluating-teaching-proficiency"
                  | "verifying-immigration-status"
                  | "assessing-financial-stability"
                  | "identifying-criminal-history";
                /**
                 * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
                 */
                studentConfides?:
                  | "assure-secret-safe"
                  | "encourage-share-friends"
                  | "explain-no-guarantee"
                  | "disclose-to-others";
              };
            };
            socialMedia?: {
              /**
               * The Facebook profile URL of the candidate
               *
               * @maxLength 256
               */
              facebook?: string;
              /**
               * The Instagram profile URL of the candidate
               *
               * @maxLength 256
               */
              instagram?: string;
              /**
               * The Twitter profile URL of the candidate
               *
               * @maxLength 256
               */
              twitter?: string;
              /**
               * The LinkedIn profile URL of the candidate
               *
               * @maxLength 256
               */
              linkedIn?: string;
              /**
               * The TikTok profile URL of the candidate
               *
               * @maxLength 256
               */
              tiktok?: string;
              /**
               * Any other social media profile URLs of the candidate
               *
               * @maxLength 256
               */
              others?: string;
              /**
               * Whether the social media profiles have been checked
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              socialMediaChecked?: string;
              /**
               * The date the candidate's Google search check was completed
               *
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              googleCheckedDate?: string;
            };
            /**
             * Whether the candidate has consented to a safe in education check
             */
            safeInEducationCheckConsent?: boolean;
            /**
             * The name of the person who provided consent for the safe in education check
             *
             * @maxLength 256
             */
            safeInEducationCheckConsentName?: string;
            /**
             * The date when consent for the safe in education check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            safeInEducationCheckConsentDate?: string;
            /**
             * The health record of the individual
             */
            healthRecord?: {
              /**
               * Whether the individual has a physical ability condition
               */
              physicalAbilityCondition?: boolean;
              /**
               * Whether the individual is confused
               */
              confused?: boolean;
              /**
               * Whether the individual has hearing issues
               */
              hearing?: boolean;
              /**
               * Whether the individual has eyesight issues
               */
              eyesight?: boolean;
              /**
               * Whether the individual has depression
               */
              depression?: boolean;
              /**
               * Whether the individual has severe pain
               */
              severePain?: boolean;
              /**
               * Whether the individual has excessive drowsiness
               */
              excessiveDrowsiness?: boolean;
              /**
               * Whether the individual has epilepsy
               */
              epilepsy?: boolean;
              /**
               * Whether the individual has heart issues
               */
              heart?: boolean;
              /**
               * Whether the individual has asthma
               */
              asthma?: boolean;
              /**
               * Whether the individual has mental health issues
               */
              mentalHealth?: boolean;
              /**
               * Whether the individual is an expectant mother
               */
              expectantMother?: boolean;
              /**
               * Whether the individual has allergies
               */
              allergies?: boolean;
              /**
               * Whether the individual is taking medication
               */
              medication?: boolean;
              /**
               * Whether the individual has any health issues
               */
              healthIssues?: boolean;
              /**
               * A description of the individual's health issues
               *
               * @maxLength 2048
               */
              healthIssuesDescription?: string;
              /**
               * Any disability needs of the individual
               *
               * @maxLength 2048
               */
              disabilityNeeds?: string;
              /**
               * Whether the individual has any contagious diseases
               */
              contagiousDiseases?: boolean;
              /**
               * Whether the individual's health affects their ability
               */
              affectAbility?: boolean;
              /**
               * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
               */
              notificationAgree?: boolean;
              /**
               * Whether the individual has opted into the SAS Wellbeing App
               */
              sasOptIn?: boolean;
              /**
               * The individual's signature
               *
               * @maxLength 256
               */
              signature?: string;
              /**
               * The individual's drawn signature in base64url format
               *
               * @maxLength 4096
               */
              drawnSignature?: string;
              /**
               * The date the individual signed the form
               *
               * @format date-time
               */
              signatureDate?: string | null;
              /**
               * Whether the individual has been vaccinated against measles
               */
              vaccinationMeasles?: boolean;
              /**
               * Whether the individual has been vaccinated against mumps
               */
              vaccinationMumps?: boolean;
              /**
               * Whether the individual has been vaccinated against rubella
               */
              vaccinationRubella?: boolean;
              /**
               * Whether the individual has been vaccinated against varicella
               */
              vaccinationVaricella?: boolean;
              /**
               * Whether the individual has been vaccinated against tuberculosis
               */
              vaccinationTuberculosis?: boolean;
              /**
               * Whether the individual has been vaccinated against hepatitis B
               */
              vaccinationHepatitisB?: boolean;
              /**
               * Whether the individual has been vaccinated against hepatitis C
               */
              vaccinationHepatitisC?: boolean;
              /**
               * Whether the individual has been vaccinated against HIV
               */
              vaccinationHIV?: boolean;
              /**
               * Whether the individual has been vaccinated against COVID-19
               */
              vaccinationCovid?: boolean;
              /**
               * Whether the individual has received the 1st dose of the COVID-19 vaccine
               */
              vaccinationCovid1?: boolean;
              /**
               * Whether the individual has received the 2nd dose of the COVID-19 vaccine
               */
              vaccinationCovid2?: boolean;
              /**
               * Whether the individual has received the COVID-19 booster vaccine
               */
              vaccinationCovidBooster?: boolean;
              /**
               * Whether the individual has received the COVID-19 influenza vaccine
               */
              vaccinationCovidInfluenza?: boolean;
              /**
               * Whether the individual has an override for fit to work requirements
               */
              fitToWorkOverride?: boolean;
            };
            /**
             * The candidate's latest submitted health declaration, including the exact questions presented.
             */
            healthDeclaration?: {
              /**
               * The ordered questions included in the question set.
               *
               * @maxItems 100
               */
              questions?: {
                /**
                 * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * A stable key used to associate the question with current and historical answers.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                key?: string;
                /**
                 * The question shown to the person completing the form.
                 *
                 * @minLength 1
                 * @maxLength 500
                 */
                label?: string;
                /**
                 * Optional supporting text displayed with the question.
                 *
                 * @maxLength 2000
                 */
                description?: string;
                /**
                 * The type of answer collected for the question.
                 */
                type?:
                  | "checkbox"
                  | "date"
                  | "daterange"
                  | "dropdown"
                  | "number"
                  | "signature"
                  | "text"
                  | "textarea";
                /**
                 * Whether an answer is required while the question is visible.
                 */
                required?: boolean;
                /**
                 * Whether the question is included in new forms. Historical responses retain disabled questions.
                 */
                enabled?: boolean;
                /**
                 * The display order of the question.
                 *
                 * @minimum 0
                 * @maximum 1000
                 */
                order?: number;
                /**
                 * Optional rules that determine when the question is shown.
                 */
                condition?: {
                  /**
                   * Whether any rule or every rule must match before the question is shown.
                   */
                  mode?: "any" | "all";
                  /**
                   * The comparisons used to determine question visibility.
                   *
                   * @minItems 1
                   * @maxItems 20
                   */
                  rules?: {
                    /**
                     * The stable key of the question that controls visibility.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    questionKey?: string;
                    /**
                     * How the controlling answer is compared with the value.
                     */
                    operator?: "equals" | "notEquals";
                    /**
                     * The answer value used to determine visibility.
                     */
                    value?: string | boolean | number | (void | null);
                  }[];
                };
                /**
                 * The available answers for a dropdown question.
                 *
                 * @minItems 1
                 * @maxItems 100
                 */
                options?: {
                  /**
                   * The value stored when this option is selected.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  value?: string;
                  /**
                   * The option displayed to the person answering the question.
                   *
                   * @minLength 1
                   * @maxLength 200
                   */
                  label?: string;
                }[];
                /**
                 * Whether this question belongs to the standard template.
                 */
                ["default"]?: boolean;
                /**
                 * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                 */
                audience?: "candidate" | "staff";
                /**
                 * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                 */
                fixedRole?: "fullName" | "signature";
              }[];
              /**
               * The answers recorded for the point-in-time question set.
               *
               * @maxItems 100
               */
              answers?: {
                /**
                 * The identifier of the question being answered.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                questionId?: string;
                /**
                 * The stable key of the question being answered.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                questionKey?: string;
                /**
                 * The submitted answer to the question.
                 */
                value?: string | string | boolean | number | (void | null);
              }[];
              /**
               * When the question set was submitted.
               *
               * @format date-time
               */
              submittedAt?: string;
              /**
               * The network address recorded when the response was submitted.
               *
               * @maxLength 64
               */
              submittedIp?: string;
              /**
               * When the question configuration captured by this response was last updated.
               *
               * @format date-time
               */
              configurationUpdatedAt?: string;
            };
            /**
             * Whether the candidate confirmed their health declaration.
             */
            healthRecordCheckConsent?: boolean;
            /**
             * The name used to confirm the health declaration.
             *
             * @maxLength 256
             */
            healthRecordCheckConsentName?: string;
            /**
             * The date the health declaration was confirmed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            healthRecordCheckConsentDate?: string;
            /**
             * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
             */
            bankDetails?: {
              /**
               * The name on the bank account
               */
              bankAccountName?: string;
              /**
               * The name of the bank
               */
              bankName?: string;
              /**
               * The account number
               */
              accountNumber?: string;
              /**
               * The sort code
               */
              sortCode?: string;
              /**
               * The National Insurance number
               */
              niNumber?: string;
              /**
               * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
               */
              jobOp1?: boolean;
              /**
               * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
               */
              jobOp2?: boolean;
              /**
               * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
               */
              jobOp3?: boolean;
              studentLoan?: {
                /**
                 * Boolean flag for if candidate has student loan
                 */
                studentLoan?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 1
                 */
                plan1?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 2
                 */
                plan2?: boolean;
                /**
                 * Boolean flag for if candidate is on plan 4
                 */
                plan4?: boolean;
                /**
                 * Boolean flag for if candidate has postgrad loan
                 */
                postGradLoan?: boolean;
              };
            };
            /**
             * Whether the candidate has confirmed their details are correct.
             */
            declarationCheckConsent?: boolean;
            /**
             * The name of the person who confirmed their details are correct.
             *
             * @maxLength 256
             */
            declarationCheckConsentName?: string;
            /**
             * The date when consent for the declaration check was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            declarationCheckConsentDate?: string;
            /**
             * Whether the candidate has consented to a contract for service
             */
            contractForServiceConsent?: boolean;
            /**
             * The name of the person who provided consent for the contract for service
             *
             * @maxLength 256
             */
            contractForServiceName?: string;
            /**
             * The date when consent for the contract for service was provided
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            contractForServiceDate?: string;
            /**
             * The candidate has completed their onboarding form.
             */
            confirmed?: boolean;
            /**
             * The ID of the user who created the candidate record.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            createdBy?: string;
            /**
             * The ID of the customer associated with the candidate.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customerId?: string;
            /**
             * The migration that created this candidate
             *
             * @pattern ^[0-9a-f]{24}$
             */
            migrationId?: string;
            /**
             * Whether this migrated candidate has been approved
             */
            migrationApproval?: "pending" | "approved" | "rejected";
            /**
             * The user assigned to the candidate. If expanded, a user object else the user ID.
             */
            assignee?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * The ID of the current booking for the candidate.
             */
            currentBooking?: string[];
            /**
             * The URL of the candidate's profile photo.
             *
             * @maxLength 256
             */
            profilePhoto?: string;
            /**
             * The pay type for the candidate.
             */
            payType?:
              | "PAYE"
              | "Umbrella"
              | "Limited Company"
              | "Not Applicable";
            /**
             * The pay interval for the candidate.
             */
            payInterval?:
              | "Monthly"
              | "Weekly"
              | "Weekly Pay in Arrears"
              | "Bi-Weekly"
              | any
              | null;
            /**
             * The pay rate for the candidate.
             */
            payRate?: string;
            /**
             * A custom ID for the candidate.
             *
             * @maxLength 256
             */
            customId?: string;
            /**
             * A company-defined identifier used to match the candidate with external records.
             *
             * @maxLength 256
             */
            _internalId?: string;
            /**
             * The date when the candidate's status was last changed.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            statusChangeDate?: string;
            /**
             * Whether the candidate has opted out of communications.
             */
            optOut?: boolean;
            /**
             * The TRN number for the candidate.
             *
             * @maxLength 256
             */
            trnNumber?: string;
            /**
             * The date when the TRN number was last checked.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            trnNumberCheckedDate?: string;
            /**
             * Any notes related to the TRN number.
             *
             * @maxLength 1024
             */
            trnNumberNotes?: string;
            /**
             * The date when the candidate's employment is set to start.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            employmentStartDate?: string;
            /**
             * Whether the candidate has confirmed their Early Years Workforce status.
             */
            ewcConfirmation?: boolean;
            /**
             * The share code for the candidate's Home Office application.
             *
             * @maxLength 256
             */
            homeOfficeShareCode?: string;
            /**
             * Whether the candidate has been archived.
             */
            archived?: boolean;
            /**
             * Whether the candidate is using an umbrella company.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            umbrella?: string;
            /**
             * ID of the PAYE umbrella company.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            payeUmbrella?: string;
            /**
             * The name of the umbrella company the candidate is using.
             *
             * @maxLength 256
             */
            umbrellaCompanyName?: string;
            /**
             * The code of the umbrella company the candidate is using.
             *
             * @maxLength 256
             */
            umbrellaCompanyCode?: string;
            /**
             * The transport method for the candidate.
             */
            transport?:
              | "Drives Own Vehicle"
              | "Use Public Transport"
              | "Access to Another Vehicle"
              | "Bicycle"
              | "No Access"
              | "Other";
            /**
             * Any important notes related to the candidate.
             *
             * @maxLength 1024
             */
            importantNotes?: string;
            /**
             * The date when the candidate was last updated.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            candidateLastUpdated?: string;
            /**
             * The source from which the candidate was obtained.
             */
            source?:
              | (
                  | "Company Website"
                  | "iCan Teach UK Website"
                  | "Apply to Education"
                  | "CV Library - CV Search"
                  | "CV Library Advert"
                  | "CV Library - Watchdog"
                  | "Guardian- CV Search"
                  | "Guardian Advert"
                  | "Indeed - CV Search"
                  | "Indeed Advert"
                  | "Reed - CV Search"
                  | "Reed Advert"
                  | "Reed \u2013 Watchdog"
                  | "SENploy - Advert"
                  | "Total Jobs"
                  | "Other Job Board"
                  | "Google"
                  | "LinkedIn"
                  | "Linkedin Recruiter"
                  | "LinkedIn Advert"
                  | "Referral - School"
                  | "Referral - Candidate"
                  | "UK University"
                  | "Irish University"
                  | "Canadian University"
                  | "Marketing Campaign"
                  | "Woo"
                  | "Job Show"
                  | "Recruitment Fair"
                  | "Career Fair"
                  | "Volcanic"
                  | "Broadbean"
                  | "Headhunt"
                  | "Referral"
                  | "Direct"
                  | "Other"
                )
              | (
                  | "Company Website"
                  | "Direct Call"
                  | "CV Library Watchdog"
                  | "CV Library Advert"
                  | "CV Library CV Search"
                  | "Reed Watchdog"
                  | "Reed Advert"
                  | "Reed CV Search"
                  | "Indeed Watchdog"
                  | "Indeed Advert"
                  | "Indeed CV Search"
                  | "Job Centre"
                  | "Returning Candidate"
                  | "Referral Candidate"
                  | "Referral Client"
                  | "Training Provider"
                  | "Recruitment Fair"
                  | "University/College"
                );
            /**
             * Any other source from which the candidate was obtained.
             *
             * @maxLength 256
             */
            sourceOther?: string;
            /**
             * Whether the candidate has given consent for online checks.
             */
            onlineCheckConsent?: boolean;
            /**
             * The title of the candidate.
             */
            title?:
              | "Mr"
              | "Mrs"
              | "Ms"
              | "Miss"
              | "Dr"
              | "Mx"
              | "Lady"
              | "Lord"
              | "Other";
            /**
             * The pronoun used by the candidate.
             */
            pronoun?:
              | "She/Her/Hers"
              | "He/Him/His"
              | "They/Them/Theirs"
              | "Ze/Hir/Hirs"
              | "Ze/Zir/Zirs"
              | "Other"
              | "Prefer not to say";
            /**
             * The date when the candidate was last updated.
             *
             * @format date-time
             */
            updatedAt?: string;
            /**
             * The date when the candidate was last contacted.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            lastContactedDate?: string;
            /**
             * A short, prominent note shown with the candidate to communicate important context.
             */
            highlight?: string;
            /**
             * The pay scale for the candidate's teaching position.
             *
             * @maxLength 256
             */
            teacherPayScale?: string;
            /**
             * Whether the candidate has a P45.
             */
            p45?: boolean;
            /**
             * The type of role the candidate is applying for.
             */
            roleType?:
              | "Senior Leadership Positions"
              | "Teaching Roles"
              | "School Support Positions";
            /**
             * The Right to Work verification status for the candidate.
             *
             * @maxLength 256
             */
            rtwVerification?: string;
            /**
             * Any notes related to the Right to Work verification status for the candidate.
             *
             * @maxLength 1024
             */
            rtwVerificationNote?: string;
            /**
             * The Right to Work verification status for the candidate.
             *
             * @maxLength 256
             */
            rtwVerificationStatus?: string;
            /**
             * The location where the candidate desires to work.
             *
             * @maxLength 256
             */
            desiredLocation?: string;
            /**
             * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
             */
            complianceUser?:
              | string
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                };
            /**
             * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
             */
            watchers?:
              | string[]
              | {
                  /**
                   * The unique identifier for the user
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The email address of the user
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The first name of the user
                   *
                   * @maxLength 128
                   */
                  firstName?: string;
                  /**
                   * The last name of the user
                   *
                   * @maxLength 128
                   */
                  lastName?: string;
                  /**
                   * The username of the user
                   *
                   * @maxLength 128
                   */
                  username?: string;
                  /**
                   * The local education authority of the user
                   *
                   * @maxLength 128
                   */
                  lea?: string;
                  /**
                   * Array of client IDs associated with the user
                   */
                  clients?: string[];
                  /**
                   * The position or job title of the user
                   *
                   * @maxLength 128
                   */
                  position?: string;
                  /**
                   * Array of permissions assigned to the user
                   */
                  permissions?: number[];
                  /**
                   * The activation code for the user
                   *
                   * @maxLength 256
                   */
                  activationCode?: string;
                  /**
                   * The unique identifier of the company the user belongs to.
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  customer?: string;
                  /**
                   * Indicates whether the user account is disabled
                   */
                  disabled?: boolean;
                  /**
                   * Array of commission thresholds for the user
                   */
                  commissionThreshold?: {
                    min?: number | string;
                    commission?: number | string;
                    type?: "Fixed" | "Variable";
                    weekly?: boolean;
                  }[];
                  /**
                   * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                   */
                  branches?:
                    | string[]
                    | {
                        /**
                         * The unique identifier for the branch
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The name of the branch
                         *
                         * @maxLength 256
                         */
                        name?: string;
                        /**
                         * The code of the branch
                         *
                         * @maxLength 32
                         */
                        code?: string;
                        /**
                         * The contact email of the branch
                         *
                         * @maxLength 256
                         * @format email
                         */
                        contactEmail?: string;
                        /**
                         * The contact phone number of the branch
                         *
                         * @maxLength 32
                         */
                        contactTelephone?: string;
                        /**
                         * The contact website of the branch
                         *
                         * @maxLength 512
                         */
                        contactWebsite?: string;
                        /**
                         * The accent color for the customer that the branch belongs to
                         *
                         * @maxLength 7
                         */
                        customerAccentColor?: string;
                        /**
                         * The address of the branch
                         */
                        address?: {
                          /**
                           * The first line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine1?: string;
                          /**
                           * The second line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine2?: string;
                          /**
                           * The third line of the address
                           *
                           * @maxLength 256
                           */
                          addressLine3?: string;
                          /**
                           * The city of the address
                           *
                           * @maxLength 256
                           */
                          city?: string;
                          /**
                           * The county of the address
                           *
                           * @maxLength 256
                           */
                          county?: string;
                          /**
                           * The borough of the address
                           *
                           * @maxLength 256
                           */
                          borough?: string;
                          /**
                           * The postcode of the address
                           *
                           * @maxLength 10
                           */
                          postcode?: string;
                          /**
                           * The country of the address
                           *
                           * @maxLength 256
                           */
                          country?: string;
                          /**
                           * The latitude of the address
                           *
                           * @minimum -90
                           * @maximum 90
                           */
                          lat?: number;
                          /**
                           * The longitude of the address
                           *
                           * @minimum -180
                           * @maximum 180
                           */
                          lng?: number;
                        };
                        /**
                         * Indicates whether the branch is archived
                         */
                        archived?: boolean;
                        /**
                         * The number of users assigned to the branch. Only returned when explicitly selected.
                         *
                         * @minimum 0
                         */
                        userCount?: number;
                      }[];
                  /**
                   * Indicates whether to display the onboarding process for the user
                   */
                  displayOnboarding?: boolean;
                  /**
                   * A custom identifier for the user
                   *
                   * @maxLength 256
                   */
                  _customId?: string;
                  /**
                   * The signature of the user in base64 format
                   *
                   * @maxLength 2048
                   */
                  signature?: string | null;
                  /**
                   * The phone number of the user
                   *
                   * @maxLength 32
                   */
                  phone?: string;
                  /**
                   * Indicates whether the user is the default candidate watcher
                   */
                  defaultCandidateWatcher?: boolean;
                  /**
                   * The user's default task workspace
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  defaultTaskWorkspace?: string | null;
                  /**
                   * Indicates whether the user has team admin privileges
                   */
                  teamAdmin?: boolean;
                  /**
                   * The contact record for this user. If expanded, a contact object else the contact record ID.
                   */
                  contact?:
                    | string
                    | {
                        /**
                         * The unique identifier of the contact
                         *
                         * @pattern ^[0-9a-f]{24}$
                         */
                        id?: string;
                        /**
                         * The forename of the contact
                         *
                         * @maxLength 128
                         */
                        forename?: string;
                        /**
                         * The surname of the contact
                         *
                         * @maxLength 128
                         */
                        surname?: string;
                        /**
                         * The email address of the contact
                         *
                         * @format email
                         */
                        email?: string;
                        /**
                         * The telephone number of the contact
                         *
                         * @maxLength 32
                         */
                        telephone?: string;
                        /**
                         * The mobile number of the contact
                         *
                         * @maxLength 32
                         */
                        mobile?: string;
                        /**
                         * The extension number of the contact
                         *
                         * @maxLength 8
                         */
                        extension?: string;
                      };
                  /**
                   * The date and time the user account was created
                   */
                  createdAt?: string;
                  /**
                   * Evertime details for this user
                   */
                  evertime?: {
                    /**
                     * The Evertime ConsultantId used for this user when exporting payment runs
                     *
                     * @maxLength 256
                     */
                    id?: string;
                  };
                  /**
                   * Timesheet Portal details for this user
                   */
                  timesheetPortal?: {
                    /**
                     * The Timesheet Portal administrator GUID used when exporting payment runs
                     *
                     * @format uuid
                     */
                    id?: string;
                    /**
                     * The Timesheet Portal administrator code
                     *
                     * @maxLength 256
                     */
                    code?: string;
                  };
                }[];
            /**
             * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
             */
            tags?:
              | string[]
              | {
                  /**
                   * The unique identifier of the tag
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The label displayed wherever this tag is applied.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  name?: string;
                  /**
                   * The tag display colour as a hexadecimal value, for example #2563EB.
                   *
                   * @maxLength 32
                   */
                  color?: string | null;
                }[];
            /**
             * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
             *
             * @maxItems 5
             */
            documents?: {
              /**
               * The unique identifier of the document
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * The name of the document
               *
               * @maxLength 512
               */
              name?: string;
              /**
               * The file reference used to retrieve the document
               *
               * @maxLength 2048
               */
              reference?: string;
              /**
               * The file's media type
               *
               * @maxLength 256
               */
              fileType?: string;
              /**
               * The area of the platform the document belongs to
               *
               * @maxLength 256
               */
              type?: string;
              /**
               * The configured category of the document
               *
               * @maxLength 256
               */
              docType?: string;
              /**
               * The document's expiry date, when applicable
               *
               * @maxLength 64
               */
              expiry?: string;
              /**
               * The document's issued date, when applicable
               *
               * @maxLength 64
               */
              issued?: string;
              /**
               * Whether the document has been archived
               */
              documentArchived?: boolean;
              /**
               * The date the document was uploaded
               *
               * @format date-time
               */
              createdAt?: string;
            }[];
            /**
             * The total number of documents attached to the candidate.
             *
             * @minimum 0
             */
            documentsCount?: number;
            /**
             * The locations where the candidate desires to work.
             */
            desiredLocationMultiple?: string[];
            /**
             * Daily availability for the requested period. Returned by the availability view only.
             *
             * @maxItems 31
             */
            availabilityDays?: {
              /**
               * Calendar date in YYYY-MM-DD format.
               *
               * @maxLength 10
               * @pattern ^\d{4}-\d{2}-\d{2}$
               */
              date?: string;
              /**
               * Availability or booking state for this date.
               */
              status?:
                | "available"
                | "unavailable"
                | "booked"
                | "halfDay"
                | "awaiting"
                | "unknown";
              /**
               * Recorded availability, or null when unrecorded.
               */
              available?: boolean | null;
              /**
               * Morning or afternoon availability.
               */
              partial?: "AM" | "PM" | any | null;
              /**
               * Additional availability information.
               *
               * @maxLength 4096
               */
              note?: string | null;
              /**
               * Active bookings on this date.
               *
               * @maxItems 100
               */
              bookings?: {
                /**
                 * The unique identifier of the resource
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * @maxLength 512
                 */
                clientName?: string;
                /**
                 * @maxLength 512
                 */
                role?: string;
                /**
                 * @maxLength 32
                 */
                timeDesignation?: string;
              }[];
            }[];
            /**
             * The date when the candidate's availability was last checked.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            availabilityLastChecked?: string;
            /**
             * Whether the candidate is subject to notice period.
             */
            subjectToNotice?: "Unsure" | "Yes" | "No";
            /**
             * The subsidiary subjects the candidate is qualified to teach.
             */
            subsidiarySubject?: string[];
            /**
             * The candidate's preferred employment type.
             */
            employmentPreference?: (
              | "Day To Day"
              | "Long Term"
              | "Fixed Term"
              | "Perm"
            )[];
            /**
             * Any other names the candidate is known by.
             *
             * @maxLength 256
             */
            otherNames?: string;
            /**
             * The name of the candidate's limited company, if applicable.
             *
             * @maxLength 256
             */
            ltdCompany?: string;
            /**
             * Whether the candidate's employment has been verified.
             */
            employmentVerified?: boolean;
            /**
             * The DBS verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSVerification?: string;
            /**
             * Any notes related to the DBS verification status for the candidate.
             *
             * @maxLength 1024
             */
            candidateDBSVerificationNote?: string;
            /**
             * The DBS verification status for the candidate.
             */
            candidateDBSVerificationStatus?: "Cleared" | "Alerts";
            /**
             * The DBS check verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSCheckVerification?: string;
            /**
             * Any notes related to the DBS check verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSCheckVerificationNote?: string;
            /**
             * The DBS check verification status for the candidate.
             */
            candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
            /**
             * Any notes related to the DBS update service verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateDBSUpdateServiceVerificationNote?: string;
            /**
             * The DBS update service verification status for the candidate.
             */
            candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
            /**
             * Any notes related to the TRN verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateTRNVerificationNote?: string;
            /**
             * The TRN verification status for the candidate.
             */
            candidateTRNVerificationStatus?:
              | "Completed Induction"
              | "Not Yet Completed Induction"
              | "Induction Exempt"
              | "ALERTS - Failed Induction"
              | "Induction Not Required";
            /**
             * The health and safety verification status for the candidate.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            candidateHealthSafetyVerification?: string;
            /**
             * Any notes related to the health and safety verification status for the candidate.
             *
             * @maxLength 150
             */
            candidateHealthSafetyVerificationNote?: string;
            /**
             * The health and safety verification status for the candidate.
             */
            candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
            /**
             * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
             */
            onboardingSession?:
              | string
              | {
                  /**
                   * Unique identifier for the onboarding session
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The URL for the onboarding session
                   *
                   * @format uri
                   */
                  url?: string;
                  /**
                   * The frozen health declaration configuration for this onboarding session.
                   */
                  healthDeclarationConfig?: {
                    /**
                     * The ordered questions included in the question set.
                     *
                     * @maxItems 100
                     */
                    questions?: {
                      /**
                       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * A stable key used to associate the question with current and historical answers.
                       *
                       * @minLength 1
                       * @maxLength 128
                       */
                      key?: string;
                      /**
                       * The question shown to the person completing the form.
                       *
                       * @minLength 1
                       * @maxLength 500
                       */
                      label?: string;
                      /**
                       * Optional supporting text displayed with the question.
                       *
                       * @maxLength 2000
                       */
                      description?: string;
                      /**
                       * The type of answer collected for the question.
                       */
                      type?:
                        | "checkbox"
                        | "date"
                        | "daterange"
                        | "dropdown"
                        | "number"
                        | "signature"
                        | "text"
                        | "textarea";
                      /**
                       * Whether an answer is required while the question is visible.
                       */
                      required?: boolean;
                      /**
                       * Whether the question is included in new forms. Historical responses retain disabled questions.
                       */
                      enabled?: boolean;
                      /**
                       * The display order of the question.
                       *
                       * @minimum 0
                       * @maximum 1000
                       */
                      order?: number;
                      /**
                       * Optional rules that determine when the question is shown.
                       */
                      condition?: {
                        /**
                         * Whether any rule or every rule must match before the question is shown.
                         */
                        mode?: "any" | "all";
                        /**
                         * The comparisons used to determine question visibility.
                         *
                         * @minItems 1
                         * @maxItems 20
                         */
                        rules?: {
                          /**
                           * The stable key of the question that controls visibility.
                           *
                           * @minLength 1
                           * @maxLength 128
                           */
                          questionKey?: string;
                          /**
                           * How the controlling answer is compared with the value.
                           */
                          operator?: "equals" | "notEquals";
                          /**
                           * The answer value used to determine visibility.
                           */
                          value?: string | boolean | number | (void | null);
                        }[];
                      };
                      /**
                       * The available answers for a dropdown question.
                       *
                       * @minItems 1
                       * @maxItems 100
                       */
                      options?: {
                        /**
                         * The value stored when this option is selected.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        value?: string;
                        /**
                         * The option displayed to the person answering the question.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        label?: string;
                      }[];
                      /**
                       * Whether this question belongs to the standard template.
                       */
                      ["default"]?: boolean;
                      /**
                       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                       */
                      audience?: "candidate" | "staff";
                      /**
                       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                       */
                      fixedRole?: "fullName" | "signature";
                    }[];
                    /**
                     * When this question configuration was last updated.
                     *
                     * @format date-time
                     */
                    updatedAt?: string;
                  };
                  /**
                   * The frozen Criminal Record & Security Checks configuration for this onboarding session.
                   */
                  criminalRecordConfig?: {
                    /**
                     * The ordered questions included in the question set.
                     *
                     * @maxItems 100
                     */
                    questions?: {
                      /**
                       * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * A stable key used to associate the question with current and historical answers.
                       *
                       * @minLength 1
                       * @maxLength 128
                       */
                      key?: string;
                      /**
                       * The question shown to the person completing the form.
                       *
                       * @minLength 1
                       * @maxLength 500
                       */
                      label?: string;
                      /**
                       * Optional supporting text displayed with the question.
                       *
                       * @maxLength 2000
                       */
                      description?: string;
                      /**
                       * The type of answer collected for the question.
                       */
                      type?:
                        | "checkbox"
                        | "date"
                        | "daterange"
                        | "dropdown"
                        | "number"
                        | "signature"
                        | "text"
                        | "textarea";
                      /**
                       * Whether an answer is required while the question is visible.
                       */
                      required?: boolean;
                      /**
                       * Whether the question is included in new forms. Historical responses retain disabled questions.
                       */
                      enabled?: boolean;
                      /**
                       * The display order of the question.
                       *
                       * @minimum 0
                       * @maximum 1000
                       */
                      order?: number;
                      /**
                       * Optional rules that determine when the question is shown.
                       */
                      condition?: {
                        /**
                         * Whether any rule or every rule must match before the question is shown.
                         */
                        mode?: "any" | "all";
                        /**
                         * The comparisons used to determine question visibility.
                         *
                         * @minItems 1
                         * @maxItems 20
                         */
                        rules?: {
                          /**
                           * The stable key of the question that controls visibility.
                           *
                           * @minLength 1
                           * @maxLength 128
                           */
                          questionKey?: string;
                          /**
                           * How the controlling answer is compared with the value.
                           */
                          operator?: "equals" | "notEquals";
                          /**
                           * The answer value used to determine visibility.
                           */
                          value?: string | boolean | number | (void | null);
                        }[];
                      };
                      /**
                       * The available answers for a dropdown question.
                       *
                       * @minItems 1
                       * @maxItems 100
                       */
                      options?: {
                        /**
                         * The value stored when this option is selected.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        value?: string;
                        /**
                         * The option displayed to the person answering the question.
                         *
                         * @minLength 1
                         * @maxLength 200
                         */
                        label?: string;
                      }[];
                      /**
                       * Whether this question belongs to the standard template.
                       */
                      ["default"]?: boolean;
                      /**
                       * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                       */
                      audience?: "candidate" | "staff";
                      /**
                       * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                       */
                      fixedRole?: "fullName" | "signature";
                    }[];
                    /**
                     * When this question configuration was last updated.
                     *
                     * @format date-time
                     */
                    updatedAt?: string;
                  };
                };
            /**
             * The contact record for this candidate. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * Whether the candidate has opted out of 48 hour communications.
             */
            optOut48Hours?: boolean;
            /**
             * Whether the candidate has consented to REC audit checks.
             */
            recAuditConsent?: boolean | null;
            /**
             * The date the candidate's REC audit consent was recorded.
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            recAuditConsentDate?: string;
            /**
             * The method through which REC audit consent was captured.
             *
             * @maxLength 256
             */
            recAuditConsentMethod?: string;
            /**
             * The date when the candidate record was created.
             *
             * @format date-time
             */
            createdAt?: string;
            /**
             * The mobile app link status for the candidate.
             */
            candidateMobileAppStatus?:
              | "Unlinked"
              | "Scheduled Unlink"
              | "Linked"
              | "Invited"
              | "Not Invited";
            /**
             * Evertime details for this candidate
             */
            evertime?: {
              /**
               * The Evertime CandidateId used for this candidate when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
              /**
               * Whether the matched Evertime candidate's NI number also matches the one held in Edun
               */
              niNumberMatch?: boolean;
            };
            /**
             * Search relevance relative to the strongest result, where 100 is the strongest match.
             *
             * @minimum 0
             * @maximum 100
             */
            relevanceScore?: number;
            /**
             * Candidate fields or attached document excerpts that explain why the candidate matched.
             *
             * @maxItems 5
             */
            matchReasons?: {
              /**
               * Whether the matching information came from the candidate record or an attached document.
               */
              source?: "candidate" | "document";
              /**
               * The candidate or document information that matched the search.
               *
               * @maxLength 256
               */
              field?: string;
              /**
               * A short excerpt showing why the result matched.
               *
               * @maxLength 1000
               */
              text?: string;
              /**
               * The exact terms within the content that matched the search, including synonym matches.
               *
               * @maxItems 25
               */
              matchedTerms?: string[];
              /**
               * The name of the attached document when the match came from a document.
               *
               * @maxLength 512
               */
              documentName?: string | null;
              /**
               * The unique identifier of the attached document when the match came from a document.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              documentId?: string | null;
            }[];
            /**
             * The candidate's distance in miles from the requested location. Present only when location filtering is used.
             *
             * @minimum 0
             */
            distance?: number;
            /**
             * Timesheet Portal details for this candidate
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal contractor GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal contractor code
               *
               * @maxLength 256
               */
              code?: string;
            };
            /**
             * Invite to candidate to link their mobile app.
             */
            mobileAppLinkInvite?: {
              /**
               * Boolean status of whether the link invite is being used
               *
               * @default false
               */
              isUsed?: boolean;
              /**
               * Creation date of the invite
               *
               * @format date-time
               * @default 2026-10-30T13:39:09.027Z
               */
              createdAt?: string;
              /**
               * ID of candidate using this link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              usedBy?: string;
              /**
               * Date of when link used
               *
               * @format date-time
               */
              usedAt?: string;
              /**
               * If unlinked, ID of user that unlinked this candidate's link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              unlinkedBy?: string;
              /**
               * If unlinked, date of when this link was unlinked
               *
               * @format date-time
               */
              unlinkedAt?: string;
              /**
               * If deleted, ID of user that deleted this candidate's link
               *
               * @pattern ^[0-9a-f]{24}$
               */
              deletedBy?: string;
              /**
               * If deleted, date of when this link was deleted
               *
               * @format date-time
               */
              deletedAt?: string;
              /**
               * Current status of this mobile app link
               */
              status?:
                | "Sent"
                | "Linked"
                | "Unlinked"
                | "ScheduledUnlink"
                | "Not Sent"
                | "Rescinded";
            };
          }
        | (void | null);
      /**
       * The Client contact linked to this request.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      clientContact?: string | null;
      /**
       * The Client linked to the request's Client contact.
       *
       * @pattern ^[0-9a-f]{24}$
       */
      clientId?: string | null;
      /**
       * The recipient name captured when the request was issued.
       *
       * @minLength 1
       * @maxLength 256
       */
      recipientName?: string | null;
      /**
       * The recipient email captured when the request was issued.
       *
       * @maxLength 320
       * @format email
       */
      recipientEmail?: string | null;
      /**
       * The point-in-time form definition presented to the recipient.
       */
      snapshot?: {
        /**
         * The internal and recipient-facing title of the form.
         *
         * @minLength 1
         * @maxLength 256
         */
        title?: string;
        /**
         * Optional introductory text shown before the questions.
         *
         * @maxLength 5000
         */
        description?: string | null;
        /**
         * Optional message shown after a response is submitted.
         *
         * @maxLength 2000
         */
        confirmationMessage?: string | null;
        /**
         * The ordered questions included in the form.
         *
         * @maxItems 100
         */
        questions?: {
          /**
           * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * A stable key used to associate the question with current and historical answers.
           *
           * @minLength 1
           * @maxLength 128
           */
          key?: string;
          /**
           * The question shown to the person completing the form.
           *
           * @minLength 1
           * @maxLength 500
           */
          label?: string;
          /**
           * Optional supporting text displayed with the question.
           *
           * @maxLength 2000
           */
          description?: string;
          /**
           * The type of answer collected for the question.
           */
          type?:
            | "checkbox"
            | "date"
            | "daterange"
            | "dropdown"
            | "number"
            | "signature"
            | "text"
            | "textarea";
          /**
           * Whether an answer is required while the question is visible.
           */
          required?: boolean;
          /**
           * Whether the question is included in new forms. Historical responses retain disabled questions.
           */
          enabled?: boolean;
          /**
           * The display order of the question.
           *
           * @minimum 0
           * @maximum 1000
           */
          order?: number;
          /**
           * Optional rules that determine when the question is shown.
           */
          condition?: {
            /**
             * Whether any rule or every rule must match before the question is shown.
             */
            mode?: "any" | "all";
            /**
             * The comparisons used to determine question visibility.
             *
             * @minItems 1
             * @maxItems 20
             */
            rules?: {
              /**
               * The stable key of the question that controls visibility.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * How the controlling answer is compared with the value.
               */
              operator?: "equals" | "notEquals";
              /**
               * The answer value used to determine visibility.
               */
              value?: string | boolean | number | (void | null);
            }[];
          };
          /**
           * The available answers for a dropdown question.
           *
           * @minItems 1
           * @maxItems 100
           */
          options?: {
            /**
             * The value stored when this option is selected.
             *
             * @minLength 1
             * @maxLength 200
             */
            value?: string;
            /**
             * The option displayed to the person answering the question.
             *
             * @minLength 1
             * @maxLength 200
             */
            label?: string;
          }[];
          /**
           * Whether this question belongs to the standard template.
           */
          ["default"]?: boolean;
          /**
           * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
           */
          audience?: "candidate" | "staff";
          /**
           * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
           */
          fixedRole?: "fullName" | "signature";
        }[];
        /**
         * The form definition from which this snapshot was captured.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        formId?: string;
        /**
         * When the captured form definition was last updated.
         *
         * @format date-time
         */
        configurationUpdatedAt?: string;
      };
      /**
       * The current lifecycle state of this request.
       */
      status?: "issued" | "inProgress" | "submitted" | "revoked" | "expired";
      /**
       * When the signed request link stops being valid.
       *
       * @format date-time
       */
      expiresAt?: string;
      /**
       * When the recipient first saved answers.
       *
       * @format date-time
       */
      startedAt?: string | null;
      /**
       * When the recipient submitted the response.
       *
       * @format date-time
       */
      submittedAt?: string | null;
      /**
       * When access to the request was revoked.
       *
       * @format date-time
       */
      revokedAt?: string | null;
      /**
       * The user who issued the request. If expanded, a user object; otherwise, a user ID.
       */
      createdBy?:
        | string
        | {
            /**
             * The unique identifier for the user
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The email address of the user
             *
             * @format email
             */
            email?: string;
            /**
             * The first name of the user
             *
             * @maxLength 128
             */
            firstName?: string;
            /**
             * The last name of the user
             *
             * @maxLength 128
             */
            lastName?: string;
            /**
             * The username of the user
             *
             * @maxLength 128
             */
            username?: string;
            /**
             * The local education authority of the user
             *
             * @maxLength 128
             */
            lea?: string;
            /**
             * Array of client IDs associated with the user
             */
            clients?: string[];
            /**
             * The position or job title of the user
             *
             * @maxLength 128
             */
            position?: string;
            /**
             * Array of permissions assigned to the user
             */
            permissions?: number[];
            /**
             * The activation code for the user
             *
             * @maxLength 256
             */
            activationCode?: string;
            /**
             * The unique identifier of the company the user belongs to.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            customer?: string;
            /**
             * Indicates whether the user account is disabled
             */
            disabled?: boolean;
            /**
             * Array of commission thresholds for the user
             */
            commissionThreshold?: {
              min?: number | string;
              commission?: number | string;
              type?: "Fixed" | "Variable";
              weekly?: boolean;
            }[];
            /**
             * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
             */
            branches?:
              | string[]
              | {
                  /**
                   * The unique identifier for the branch
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The name of the branch
                   *
                   * @maxLength 256
                   */
                  name?: string;
                  /**
                   * The code of the branch
                   *
                   * @maxLength 32
                   */
                  code?: string;
                  /**
                   * The contact email of the branch
                   *
                   * @maxLength 256
                   * @format email
                   */
                  contactEmail?: string;
                  /**
                   * The contact phone number of the branch
                   *
                   * @maxLength 32
                   */
                  contactTelephone?: string;
                  /**
                   * The contact website of the branch
                   *
                   * @maxLength 512
                   */
                  contactWebsite?: string;
                  /**
                   * The accent color for the customer that the branch belongs to
                   *
                   * @maxLength 7
                   */
                  customerAccentColor?: string;
                  /**
                   * The address of the branch
                   */
                  address?: {
                    /**
                     * The first line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine1?: string;
                    /**
                     * The second line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine2?: string;
                    /**
                     * The third line of the address
                     *
                     * @maxLength 256
                     */
                    addressLine3?: string;
                    /**
                     * The city of the address
                     *
                     * @maxLength 256
                     */
                    city?: string;
                    /**
                     * The county of the address
                     *
                     * @maxLength 256
                     */
                    county?: string;
                    /**
                     * The borough of the address
                     *
                     * @maxLength 256
                     */
                    borough?: string;
                    /**
                     * The postcode of the address
                     *
                     * @maxLength 10
                     */
                    postcode?: string;
                    /**
                     * The country of the address
                     *
                     * @maxLength 256
                     */
                    country?: string;
                    /**
                     * The latitude of the address
                     *
                     * @minimum -90
                     * @maximum 90
                     */
                    lat?: number;
                    /**
                     * The longitude of the address
                     *
                     * @minimum -180
                     * @maximum 180
                     */
                    lng?: number;
                  };
                  /**
                   * Indicates whether the branch is archived
                   */
                  archived?: boolean;
                  /**
                   * The number of users assigned to the branch. Only returned when explicitly selected.
                   *
                   * @minimum 0
                   */
                  userCount?: number;
                }[];
            /**
             * Indicates whether to display the onboarding process for the user
             */
            displayOnboarding?: boolean;
            /**
             * A custom identifier for the user
             *
             * @maxLength 256
             */
            _customId?: string;
            /**
             * The signature of the user in base64 format
             *
             * @maxLength 2048
             */
            signature?: string | null;
            /**
             * The phone number of the user
             *
             * @maxLength 32
             */
            phone?: string;
            /**
             * Indicates whether the user is the default candidate watcher
             */
            defaultCandidateWatcher?: boolean;
            /**
             * The user's default task workspace
             *
             * @pattern ^[0-9a-f]{24}$
             */
            defaultTaskWorkspace?: string | null;
            /**
             * Indicates whether the user has team admin privileges
             */
            teamAdmin?: boolean;
            /**
             * The contact record for this user. If expanded, a contact object else the contact record ID.
             */
            contact?:
              | string
              | {
                  /**
                   * The unique identifier of the contact
                   *
                   * @pattern ^[0-9a-f]{24}$
                   */
                  id?: string;
                  /**
                   * The forename of the contact
                   *
                   * @maxLength 128
                   */
                  forename?: string;
                  /**
                   * The surname of the contact
                   *
                   * @maxLength 128
                   */
                  surname?: string;
                  /**
                   * The email address of the contact
                   *
                   * @format email
                   */
                  email?: string;
                  /**
                   * The telephone number of the contact
                   *
                   * @maxLength 32
                   */
                  telephone?: string;
                  /**
                   * The mobile number of the contact
                   *
                   * @maxLength 32
                   */
                  mobile?: string;
                  /**
                   * The extension number of the contact
                   *
                   * @maxLength 8
                   */
                  extension?: string;
                };
            /**
             * The date and time the user account was created
             */
            createdAt?: string;
            /**
             * Evertime details for this user
             */
            evertime?: {
              /**
               * The Evertime ConsultantId used for this user when exporting payment runs
               *
               * @maxLength 256
               */
              id?: string;
            };
            /**
             * Timesheet Portal details for this user
             */
            timesheetPortal?: {
              /**
               * The Timesheet Portal administrator GUID used when exporting payment runs
               *
               * @format uuid
               */
              id?: string;
              /**
               * The Timesheet Portal administrator code
               *
               * @maxLength 256
               */
              code?: string;
            };
          };
      /**
       * Email delivery information for this request.
       */
      delivery?: {
        /**
         * When email delivery was most recently requested.
         *
         * @format date-time
         */
        emailRequestedAt?: string;
        /**
         * When the request email was most recently sent.
         *
         * @format date-time
         */
        emailSentAt?: string;
        /**
         * When the most recent request email failed to send.
         *
         * @format date-time
         */
        emailFailedAt?: string;
      };
      /**
       * When the request was issued.
       *
       * @format date-time
       */
      createdAt?: string;
      /**
       * When the request was last updated.
       *
       * @format date-time
       */
      updatedAt?: string;
    };
    /**
     * @maxLength 4096
     * @format uri
     */
    signedUrl?: string;
  };
};

export type UpdateFormRequestRequestBody = {
  /**
   * The lifecycle action to apply to the request.
   */
  action: "revoke" | "rotate";
  /**
   * A replacement validity period when rotating the link.
   *
   * @minimum 1
   * @maximum 30
   */
  expiresInDays?: number;
  /**
   * Whether to email the newly rotated link.
   */
  sendEmail?: boolean;
};

export type UpdateFormRequestVariables = {
  body: UpdateFormRequestRequestBody;
  pathParams: UpdateFormRequestPathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateFormRequest = (
  variables: UpdateFormRequestVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateFormRequestResponse,
    UpdateFormRequestError,
    UpdateFormRequestRequestBody,
    {},
    {},
    UpdateFormRequestPathParams
  >({
    url: "/form-requests/{formRequestId}",
    method: "patch",
    ...variables,
    signal,
  });

export const useUpdateFormRequest = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateFormRequestResponse,
      UpdateFormRequestError,
      UpdateFormRequestVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateFormRequestResponse,
    UpdateFormRequestError,
    UpdateFormRequestVariables
  >({
    mutationFn: (variables: UpdateFormRequestVariables) =>
      fetchUpdateFormRequest(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type GetFormResponsesQueryParams = {
  /**
   * The fields to include in each returned response.
   *
   * @maxItems 250
   */
  select?: (
    | "id"
    | "formId"
    | "formRequestId"
    | "candidate"
    | "candidate.id"
    | "candidate.forename"
    | "candidate.surname"
    | "candidate.previousNames"
    | "candidate.email"
    | "candidate.telephone"
    | "candidate.mobile"
    | "candidate.occupation"
    | "candidate.subject"
    | "candidate.address.addressLine1"
    | "candidate.address.addressLine2"
    | "candidate.address.addressLine3"
    | "candidate.address.city"
    | "candidate.address.county"
    | "candidate.address.borough"
    | "candidate.address.postcode"
    | "candidate.address.country"
    | "candidate.address.lat"
    | "candidate.address.lng"
    | "candidate.address"
    | "candidate.qualifications"
    | "candidate.employment"
    | "candidate.references"
    | "candidate.referenceCheckConsent"
    | "candidate.referenceCheckConsentName"
    | "candidate.referenceCheckConsentDate"
    | "candidate.dob"
    | "candidate.sex"
    | "candidate.status"
    | "candidate.onboardingStatus"
    | "candidate.onboardingSent"
    | "candidate.nextOfKin.nextOfKinName"
    | "candidate.nextOfKin.nextOfKinRelationship"
    | "candidate.nextOfKin.nextOfKinTelephone"
    | "candidate.nextOfKin.nextOfKinEmail"
    | "candidate.nextOfKin.nextOfKinAddress.addressLine1"
    | "candidate.nextOfKin.nextOfKinAddress.addressLine2"
    | "candidate.nextOfKin.nextOfKinAddress.addressLine3"
    | "candidate.nextOfKin.nextOfKinAddress.city"
    | "candidate.nextOfKin.nextOfKinAddress.county"
    | "candidate.nextOfKin.nextOfKinAddress.borough"
    | "candidate.nextOfKin.nextOfKinAddress.postcode"
    | "candidate.nextOfKin.nextOfKinAddress.country"
    | "candidate.nextOfKin.nextOfKinAddress.lat"
    | "candidate.nextOfKin.nextOfKinAddress.lng"
    | "candidate.nextOfKin.nextOfKinAddress"
    | "candidate.nextOfKin"
    | "candidate.criminalRecord.dbsHeld"
    | "candidate.criminalRecord.dbsNumber"
    | "candidate.criminalRecord.dbsType"
    | "candidate.criminalRecord.dbsLastChecked"
    | "candidate.criminalRecord.dbsUpdateServiceNumber"
    | "candidate.criminalRecord.prohibitionCheck"
    | "candidate.criminalRecord.overseasCheck"
    | "candidate.criminalRecord.overseasCheckNote"
    | "candidate.criminalRecord.convictedOffence"
    | "candidate.criminalRecord.convictedOffenceDescription"
    | "candidate.criminalRecord.convictions"
    | "candidate.criminalRecord.convictionsDescription"
    | "candidate.criminalRecord.registeredToUpdateService"
    | "candidate.criminalRecord.consentUpdateServiceChecks"
    | "candidate.criminalRecord.note"
    | "candidate.criminalRecord.updateServiceCheckDate"
    | "candidate.criminalRecord.abroad"
    | "candidate.criminalRecord.overseasDeclarationExtended"
    | "candidate.criminalRecord.educationalDisciplinaryProceedings"
    | "candidate.criminalRecord.dbsComments"
    | "candidate.criminalRecord.dbsDeclarationRead"
    | "candidate.criminalRecord.overseasOffence"
    | "candidate.criminalRecord.overseasOffenceDescription"
    | "candidate.criminalRecord.currentCriminalOffence"
    | "candidate.criminalRecord.currentCriminalOffenceDescription"
    | "candidate.criminalRecord.dbsIssuedByBusiness"
    | "candidate.criminalRecord.dbsIssuedByCurrentEmployer"
    | "candidate.criminalRecord.section128Required"
    | "candidate.criminalRecord.section128Checked"
    | "candidate.criminalRecord"
    | "candidate.criminalRecordDeclaration.questions"
    | "candidate.criminalRecordDeclaration.answers"
    | "candidate.criminalRecordDeclaration.submittedAt"
    | "candidate.criminalRecordDeclaration.submittedIp"
    | "candidate.criminalRecordDeclaration.configurationUpdatedAt"
    | "candidate.criminalRecordDeclaration"
    | "candidate.criminalRecordCheckConsent"
    | "candidate.criminalRecordCheckConsentName"
    | "candidate.criminalRecordCheckConsentDate"
    | "candidate.safeInEducation.barred"
    | "candidate.safeInEducation.barredCheckDate"
    | "candidate.safeInEducation.waiver"
    | "candidate.safeInEducation.ordersYou"
    | "candidate.safeInEducation.ordersChild"
    | "candidate.safeInEducation.previousSafeguardingAllegations"
    | "candidate.safeInEducation.cautioned"
    | "candidate.safeInEducation.violentOffence"
    | "candidate.safeInEducation.anotherCountry"
    | "candidate.safeInEducation.readKCSIE"
    | "candidate.safeInEducation.readChildcareAct"
    | "candidate.safeInEducation.prohibitedFromTeaching"
    | "candidate.safeInEducation.knownPolice"
    | "candidate.safeInEducation.prohibitedFromManagement"
    | "candidate.safeInEducation.criminalOffencesLaw"
    | "candidate.safeInEducation.safeInEducationDetails"
    | "candidate.safeInEducation.safeguardingQuestionaire.primaryPurposeSafeguarding"
    | "candidate.safeInEducation.safeguardingQuestionaire.typicallyResponsibleCoordinatingSafeguarding"
    | "candidate.safeInEducation.safeguardingQuestionaire.signOfAbuse"
    | "candidate.safeInEducation.safeguardingQuestionaire.studentDisclosesInformation"
    | "candidate.safeInEducation.safeguardingQuestionaire.schoolsChildProtectionPolicy"
    | "candidate.safeInEducation.safeguardingQuestionaire.fgmDefinition"
    | "candidate.safeInEducation.safeguardingQuestionaire.keyPrincipleOnlineSafetyStudents"
    | "candidate.safeInEducation.safeguardingQuestionaire.teachersConsiderRegardingSafeguarding"
    | "candidate.safeInEducation.safeguardingQuestionaire.criminalBackgroundCheckTeachers"
    | "candidate.safeInEducation.safeguardingQuestionaire.studentConfides"
    | "candidate.safeInEducation.safeguardingQuestionaire"
    | "candidate.safeInEducation"
    | "candidate.socialMedia.facebook"
    | "candidate.socialMedia.instagram"
    | "candidate.socialMedia.twitter"
    | "candidate.socialMedia.linkedIn"
    | "candidate.socialMedia.tiktok"
    | "candidate.socialMedia.others"
    | "candidate.socialMedia.socialMediaChecked"
    | "candidate.socialMedia.googleCheckedDate"
    | "candidate.socialMedia"
    | "candidate.safeInEducationCheckConsent"
    | "candidate.safeInEducationCheckConsentName"
    | "candidate.safeInEducationCheckConsentDate"
    | "candidate.healthRecord.physicalAbilityCondition"
    | "candidate.healthRecord.confused"
    | "candidate.healthRecord.hearing"
    | "candidate.healthRecord.eyesight"
    | "candidate.healthRecord.depression"
    | "candidate.healthRecord.severePain"
    | "candidate.healthRecord.excessiveDrowsiness"
    | "candidate.healthRecord.epilepsy"
    | "candidate.healthRecord.heart"
    | "candidate.healthRecord.asthma"
    | "candidate.healthRecord.mentalHealth"
    | "candidate.healthRecord.expectantMother"
    | "candidate.healthRecord.allergies"
    | "candidate.healthRecord.medication"
    | "candidate.healthRecord.healthIssues"
    | "candidate.healthRecord.healthIssuesDescription"
    | "candidate.healthRecord.disabilityNeeds"
    | "candidate.healthRecord.contagiousDiseases"
    | "candidate.healthRecord.affectAbility"
    | "candidate.healthRecord.notificationAgree"
    | "candidate.healthRecord.sasOptIn"
    | "candidate.healthRecord.signature"
    | "candidate.healthRecord.drawnSignature"
    | "candidate.healthRecord.signatureDate"
    | "candidate.healthRecord.vaccinationMeasles"
    | "candidate.healthRecord.vaccinationMumps"
    | "candidate.healthRecord.vaccinationRubella"
    | "candidate.healthRecord.vaccinationVaricella"
    | "candidate.healthRecord.vaccinationTuberculosis"
    | "candidate.healthRecord.vaccinationHepatitisB"
    | "candidate.healthRecord.vaccinationHepatitisC"
    | "candidate.healthRecord.vaccinationHIV"
    | "candidate.healthRecord.vaccinationCovid"
    | "candidate.healthRecord.vaccinationCovid1"
    | "candidate.healthRecord.vaccinationCovid2"
    | "candidate.healthRecord.vaccinationCovidBooster"
    | "candidate.healthRecord.vaccinationCovidInfluenza"
    | "candidate.healthRecord.fitToWorkOverride"
    | "candidate.healthRecord"
    | "candidate.healthDeclaration.questions"
    | "candidate.healthDeclaration.answers"
    | "candidate.healthDeclaration.submittedAt"
    | "candidate.healthDeclaration.submittedIp"
    | "candidate.healthDeclaration.configurationUpdatedAt"
    | "candidate.healthDeclaration"
    | "candidate.healthRecordCheckConsent"
    | "candidate.healthRecordCheckConsentName"
    | "candidate.healthRecordCheckConsentDate"
    | "candidate.bankDetails.bankAccountName"
    | "candidate.bankDetails.bankName"
    | "candidate.bankDetails.accountNumber"
    | "candidate.bankDetails.sortCode"
    | "candidate.bankDetails.niNumber"
    | "candidate.bankDetails.jobOp1"
    | "candidate.bankDetails.jobOp2"
    | "candidate.bankDetails.jobOp3"
    | "candidate.bankDetails.studentLoan.studentLoan"
    | "candidate.bankDetails.studentLoan.plan1"
    | "candidate.bankDetails.studentLoan.plan2"
    | "candidate.bankDetails.studentLoan.plan4"
    | "candidate.bankDetails.studentLoan.postGradLoan"
    | "candidate.bankDetails.studentLoan"
    | "candidate.bankDetails"
    | "candidate.declarationCheckConsent"
    | "candidate.declarationCheckConsentName"
    | "candidate.declarationCheckConsentDate"
    | "candidate.contractForServiceConsent"
    | "candidate.contractForServiceName"
    | "candidate.contractForServiceDate"
    | "candidate.confirmed"
    | "candidate.createdBy"
    | "candidate.customerId"
    | "candidate.migrationId"
    | "candidate.migrationApproval"
    | "candidate.assignee"
    | "candidate.assignee.id"
    | "candidate.assignee.email"
    | "candidate.assignee.firstName"
    | "candidate.assignee.lastName"
    | "candidate.assignee.username"
    | "candidate.assignee.lea"
    | "candidate.assignee.clients"
    | "candidate.assignee.position"
    | "candidate.assignee.permissions"
    | "candidate.assignee.activationCode"
    | "candidate.assignee.customer"
    | "candidate.assignee.disabled"
    | "candidate.assignee.commissionThreshold"
    | "candidate.assignee.branches"
    | "candidate.assignee.displayOnboarding"
    | "candidate.assignee._customId"
    | "candidate.assignee.signature"
    | "candidate.assignee.phone"
    | "candidate.assignee.defaultCandidateWatcher"
    | "candidate.assignee.defaultTaskWorkspace"
    | "candidate.assignee.teamAdmin"
    | "candidate.assignee.contact"
    | "candidate.assignee.contact.id"
    | "candidate.assignee.contact.forename"
    | "candidate.assignee.contact.surname"
    | "candidate.assignee.contact.email"
    | "candidate.assignee.contact.telephone"
    | "candidate.assignee.contact.mobile"
    | "candidate.assignee.contact.extension"
    | "candidate.assignee.createdAt"
    | "candidate.assignee.evertime.id"
    | "candidate.assignee.evertime"
    | "candidate.assignee.timesheetPortal.id"
    | "candidate.assignee.timesheetPortal.code"
    | "candidate.assignee.timesheetPortal"
    | "candidate.currentBooking"
    | "candidate.profilePhoto"
    | "candidate.payType"
    | "candidate.payInterval"
    | "candidate.payRate"
    | "candidate.customId"
    | "candidate._internalId"
    | "candidate.statusChangeDate"
    | "candidate.optOut"
    | "candidate.trnNumber"
    | "candidate.trnNumberCheckedDate"
    | "candidate.trnNumberNotes"
    | "candidate.employmentStartDate"
    | "candidate.ewcConfirmation"
    | "candidate.homeOfficeShareCode"
    | "candidate.archived"
    | "candidate.umbrella"
    | "candidate.payeUmbrella"
    | "candidate.umbrellaCompanyName"
    | "candidate.umbrellaCompanyCode"
    | "candidate.transport"
    | "candidate.importantNotes"
    | "candidate.candidateLastUpdated"
    | "candidate.source"
    | "candidate.sourceOther"
    | "candidate.onlineCheckConsent"
    | "candidate.title"
    | "candidate.pronoun"
    | "candidate.updatedAt"
    | "candidate.lastContactedDate"
    | "candidate.highlight"
    | "candidate.teacherPayScale"
    | "candidate.p45"
    | "candidate.roleType"
    | "candidate.rtwVerification"
    | "candidate.rtwVerificationNote"
    | "candidate.rtwVerificationStatus"
    | "candidate.desiredLocation"
    | "candidate.complianceUser"
    | "candidate.complianceUser.id"
    | "candidate.complianceUser.email"
    | "candidate.complianceUser.firstName"
    | "candidate.complianceUser.lastName"
    | "candidate.complianceUser.username"
    | "candidate.complianceUser.lea"
    | "candidate.complianceUser.clients"
    | "candidate.complianceUser.position"
    | "candidate.complianceUser.permissions"
    | "candidate.complianceUser.activationCode"
    | "candidate.complianceUser.customer"
    | "candidate.complianceUser.disabled"
    | "candidate.complianceUser.commissionThreshold"
    | "candidate.complianceUser.branches"
    | "candidate.complianceUser.displayOnboarding"
    | "candidate.complianceUser._customId"
    | "candidate.complianceUser.signature"
    | "candidate.complianceUser.phone"
    | "candidate.complianceUser.defaultCandidateWatcher"
    | "candidate.complianceUser.defaultTaskWorkspace"
    | "candidate.complianceUser.teamAdmin"
    | "candidate.complianceUser.contact"
    | "candidate.complianceUser.contact.id"
    | "candidate.complianceUser.contact.forename"
    | "candidate.complianceUser.contact.surname"
    | "candidate.complianceUser.contact.email"
    | "candidate.complianceUser.contact.telephone"
    | "candidate.complianceUser.contact.mobile"
    | "candidate.complianceUser.contact.extension"
    | "candidate.complianceUser.createdAt"
    | "candidate.complianceUser.evertime.id"
    | "candidate.complianceUser.evertime"
    | "candidate.complianceUser.timesheetPortal.id"
    | "candidate.complianceUser.timesheetPortal.code"
    | "candidate.complianceUser.timesheetPortal"
    | "candidate.watchers"
    | "candidate.tags"
    | "candidate.documents"
    | "candidate.documentsCount"
    | "candidate.desiredLocationMultiple"
    | "candidate.availabilityDays"
    | "candidate.availabilityLastChecked"
    | "candidate.subjectToNotice"
    | "candidate.subsidiarySubject"
    | "candidate.employmentPreference"
    | "candidate.otherNames"
    | "candidate.ltdCompany"
    | "candidate.employmentVerified"
    | "candidate.candidateDBSVerification"
    | "candidate.candidateDBSVerificationNote"
    | "candidate.candidateDBSVerificationStatus"
    | "candidate.candidateDBSCheckVerification"
    | "candidate.candidateDBSCheckVerificationNote"
    | "candidate.candidateDBSCheckVerificationStatus"
    | "candidate.candidateDBSUpdateServiceVerificationNote"
    | "candidate.candidateDBSUpdateServiceVerificationStatus"
    | "candidate.candidateTRNVerificationNote"
    | "candidate.candidateTRNVerificationStatus"
    | "candidate.candidateHealthSafetyVerification"
    | "candidate.candidateHealthSafetyVerificationNote"
    | "candidate.candidateHealthSafetyVerificationStatus"
    | "candidate.onboardingSession"
    | "candidate.onboardingSession.id"
    | "candidate.onboardingSession.url"
    | "candidate.onboardingSession.healthDeclarationConfig.questions"
    | "candidate.onboardingSession.healthDeclarationConfig.updatedAt"
    | "candidate.onboardingSession.healthDeclarationConfig"
    | "candidate.onboardingSession.criminalRecordConfig.questions"
    | "candidate.onboardingSession.criminalRecordConfig.updatedAt"
    | "candidate.onboardingSession.criminalRecordConfig"
    | "candidate.contact"
    | "candidate.contact.id"
    | "candidate.contact.forename"
    | "candidate.contact.surname"
    | "candidate.contact.email"
    | "candidate.contact.telephone"
    | "candidate.contact.mobile"
    | "candidate.contact.extension"
    | "candidate.optOut48Hours"
    | "candidate.recAuditConsent"
    | "candidate.recAuditConsentDate"
    | "candidate.recAuditConsentMethod"
    | "candidate.createdAt"
    | "candidate.candidateMobileAppStatus"
    | "candidate.evertime.id"
    | "candidate.evertime.niNumberMatch"
    | "candidate.evertime"
    | "candidate.relevanceScore"
    | "candidate.matchReasons"
    | "candidate.distance"
    | "candidate.timesheetPortal.id"
    | "candidate.timesheetPortal.code"
    | "candidate.timesheetPortal"
    | "candidate.mobileAppLinkInvite.isUsed"
    | "candidate.mobileAppLinkInvite.createdAt"
    | "candidate.mobileAppLinkInvite.usedBy"
    | "candidate.mobileAppLinkInvite.usedAt"
    | "candidate.mobileAppLinkInvite.unlinkedBy"
    | "candidate.mobileAppLinkInvite.unlinkedAt"
    | "candidate.mobileAppLinkInvite.deletedBy"
    | "candidate.mobileAppLinkInvite.deletedAt"
    | "candidate.mobileAppLinkInvite.status"
    | "candidate.mobileAppLinkInvite"
    | "recipientName"
    | "recipientEmail"
    | "response.questions"
    | "response.answers"
    | "response.submittedAt"
    | "response.submittedIp"
    | "response.configurationUpdatedAt"
    | "response"
    | "submittedAt"
    | "createdAt"
    | ""
  )[];
  /**
   * Related resources to return as objects.
   *
   * @maxItems 1
   */
  expand?: "candidate"[];
  /**
   * Filters applied to submitted responses.
   */
  filter?: {
    /**
     * Return responses submitted for this form.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    formId?: string;
    /**
     * Return responses linked to this candidate.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    candidateId?: string;
  };
  /**
   * The direction used to order submitted responses.
   */
  sort?: "submittedAt" | "-submittedAt";
  /**
   * Page number
   *
   * @minimum 1
   */
  page?: number;
  /**
   * Items per page
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize?: number;
};

export type GetFormResponsesError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetFormResponsesResponse = {
  /**
   * Resources for the current page. Each resource contains the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the submitted response.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The form definition from which this response originated.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    formId?: string;
    /**
     * The request through which the response was submitted.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    formRequestId?: string;
    /**
     * The candidate linked to the response. If expanded, a candidate object; otherwise, a candidate ID.
     */
    candidate?:
      | string
      | {
          /**
           * The unique identifier of the candidate
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the candidate
           *
           * @minLength 1
           * @maxLength 256
           */
          forename?: string;
          /**
           * The surname of the candidate
           *
           * @minLength 1
           * @maxLength 256
           */
          surname?: string;
          /**
           * Any previous names of the candidate
           *
           * @maxLength 256
           */
          previousNames?: string;
          /**
           * The email of the candidate. This will be used for communication.
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the candidate
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the candidate
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The occupation of the candidate
           */
          occupation?:
            | "Teacher"
            | "Cover Supervisor"
            | "Teaching Assistant"
            | "School Support Staff"
            | "Primary Teacher"
            | "Secondary Teacher"
            | "Head Teacher"
            | "Senior Leader"
            | "Tutor"
            | "Mentor"
            | "Learning Support Assistant"
            | "Finance Officer"
            | "HR Administrator"
            | "HR Manager"
            | "Office Manager"
            | "Administrator"
            | "Personal Assistant"
            | "Pastoral Manager"
            | "Receptionist"
            | "Instructor"
            | "Caretaker"
            | "Nursery Assistant"
            | "Nursery Nurse"
            | "Deputy Nursery Manager"
            | "Lunchtime Supervisor"
            | "Classroom Graduate"
            | "Primary Cover Supervisor"
            | "HLTA"
            | "Nurture Teacher"
            | "Nurture Mentor"
            | "SENDCo"
            | "Registered Manager"
            | "Regional Manager"
            | "Education Director"
            | "Residential Director"
            | "Deputy Manager"
            | "Residential Support Worker"
            | "Sports Coach"
            | "Executive Headteacher"
            | "General Practitioner"
            | "Advance Nurse Practitioner (ANP)"
            | "Emergency Nurse Practitioner (ENP)"
            | "Advanced Clinical Practitioner (ACP)"
            | "Emergency Clinical Practitioner (ECP)"
            | "Consultant Psychiatrist"
            | "Psychiatrist \u2013 SPR"
            | "IT Technician"
            | "Art Technician"
            | "DT Technician"
            | "Science Technician"
            | "Healthcare Assistant/ Residential Support Worker"
            | "Healthcare Assistant"
            | "Senior Healthcare Assistant"
            | "Team Leader"
            | "Registered General Nurse"
            | "Registered Mental Nurse"
            | "Registered Nurse Learning Disabilities"
            | "Overseas Trained Teacher";
          /**
           * The subject the candidate specializes in
           *
           * @maxLength 256
           */
          subject?: string;
          /**
           * The address of the resource
           */
          address?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The qualifications of the candidate
           *
           * @maxItems 32
           */
          qualifications?: {
            /**
             * The type of the qualification
             *
             * @maxLength 256
             */
            type?: string;
            /**
             * The place where the qualification was obtained
             *
             * @maxLength 256
             */
            place?: string;
            /**
             * The name of the qualification
             *
             * @maxLength 256
             */
            qualificationName?: string;
            /**
             * The date the qualification was obtained
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date?: string;
            /**
             * The unique identifier of the qualification
             *
             * @maxLength 256
             */
            qualificationId?: string;
            /**
             * The order index of the qualification
             *
             * @minimum 0
             */
            orderIndex?: number;
            /**
             * The expiry date of the qualification
             *
             * @maxLength 256
             */
            qualificationExpiry?: string;
            /**
             * Whether to exclude this qualification from vetting
             */
            excludeFromVetting?: boolean;
            /**
             * The date the qualification was created
             *
             * @format date-time
             */
            createdAt?: string | null;
          }[];
          /**
           * The employment history of the candidate
           *
           * @maxItems 32
           */
          employment?: {
            /**
             * The name of the employer
             *
             * @maxLength 256
             */
            employerName?: string;
            /**
             * The date the candidate started employment
             *
             * @maxLength 256
             */
            dateStarted?: string;
            /**
             * The date the candidate ended employment
             *
             * @maxLength 256
             */
            dateEnded?: string;
            /**
             * The job title of the candidate
             *
             * @maxLength 256
             */
            jobTitle?: string;
            /**
             * The reason the candidate left this employment
             *
             * @maxLength 1024
             */
            reasonForLeaving?: string;
            /**
             * The duties of the candidate in this role. Consultant only, visible on Edun CV
             *
             * @maxLength 1024
             */
            duties?: string;
            /**
             * The salary of the candidate in this role
             *
             * @maxLength 256
             */
            salary?: string;
            /**
             * The address of the candidate's reference for this employment
             */
            referenceAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The unique identifier for this employment record
             *
             * @maxLength 256
             */
            employmentId?: string;
            /**
             * The order index of this employment record
             *
             * @minimum 0
             */
            orderIndex?: number;
          }[];
          /**
           * The references for the candidate
           *
           * @maxItems 32
           */
          references?: {
            /**
             * The title of the reference
             *
             * @maxLength 16
             */
            referenceTitle?: string;
            /**
             * The forename of the reference
             *
             * @maxLength 256
             */
            referenceForename?: string;
            /**
             * The surname of the reference
             *
             * @maxLength 256
             */
            referenceSurname?: string;
            /**
             * The company of the reference
             *
             * @maxLength 256
             */
            referenceCompany?: string;
            /**
             * The position of the reference
             *
             * @maxLength 256
             */
            referencePosition?: string;
            /**
             * The email of the reference
             *
             * @format email
             */
            referenceEmail?: string;
            /**
             * The telephone number of the reference
             *
             * @maxLength 32
             */
            referenceTelephone?: string;
            /**
             * The type of the reference
             */
            referenceType?:
              | "Education"
              | "Professional"
              | "Character"
              | "Factual"
              | "Academic"
              | "Volunteer";
            /**
             * The duration of the reference
             *
             * @maxLength 128
             */
            referenceDuration?: string;
            /**
             * The ID of the reference
             *
             * @maxLength 256
             */
            referenceId?: string;
            /**
             * This field only applies if the reference immediate consent question is enabled
             * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
             */
            referenceApply?: boolean;
            /**
             * Any additional notes about the reference
             *
             * @maxLength 1024
             */
            referenceNote?: string;
            /**
             * The start date of the reference
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            dateFrom?: string;
            /**
             * The end date of the reference
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            dateTo?: string;
          }[];
          /**
           * Whether the candidate has consented to a reference check
           */
          referenceCheckConsent?: boolean;
          /**
           * The name of the person who provided consent for the reference check
           *
           * @maxLength 256
           */
          referenceCheckConsentName?: string;
          /**
           * The date when consent for the reference check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          referenceCheckConsentDate?: string;
          /**
           * The date of birth of the candidate
           *
           * @format date-time
           */
          dob?: string | null;
          /**
           * The sex of the candidate
           */
          sex?: "Male" | "Female" | "Other" | "Unknown";
          /**
           * The status of the candidate
           */
          status?:
            | "Pending"
            | "Approved"
            | "Rejected"
            | "Anonymised"
            | "Archived"
            | "Enquired"
            | "Hold"
            | "Future"
            | "DNC"
            | "Pre";
          /**
           * The current status of the onboarding process for the candidate
           */
          onboardingStatus?: "Not Started" | "In Progress" | "Completed";
          /**
           * Whether the onboarding information has been sent to the candidate
           */
          onboardingSent?: boolean;
          nextOfKin?: {
            /**
             * The name of the next of kin
             *
             * @maxLength 256
             */
            nextOfKinName?: string;
            /**
             * The relationship of the next of kin to the candidate
             *
             * @maxLength 256
             */
            nextOfKinRelationship?: string;
            /**
             * The telephone number of the next of kin
             *
             * @maxLength 32
             */
            nextOfKinTelephone?: string;
            /**
             * The email address of the next of kin
             *
             * @format email
             */
            nextOfKinEmail?: string;
            nextOfKinAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
          };
          criminalRecord?: {
            /**
             * Whether the candidate has a DBS certificate
             */
            dbsHeld?: boolean;
            /**
             * The unique identifier for the DBS certificate
             *
             * @maxLength 256
             */
            dbsNumber?: string;
            /**
             * The type of DBS check
             */
            dbsType?:
              | "Basic"
              | "Standard"
              | "Enhanced"
              | "Enhanced with Barred List"
              | "Child workforce"
              | "Child and adult workforce"
              | "Adult workforce";
            /**
             * The date when the DBS check was last performed
             *
             * @maxLength 256
             */
            dbsLastChecked?: string;
            /**
             * The DBS update service number for the candidate
             *
             * @maxLength 256
             */
            dbsUpdateServiceNumber?: string;
            /**
             * The result of the prohibition check
             *
             * @maxLength 256
             */
            prohibitionCheck?: string;
            /**
             * The result of the overseas check
             *
             * @maxLength 256
             */
            overseasCheck?: string;
            /**
             * Any additional notes regarding the overseas check
             *
             * @maxLength 256
             */
            overseasCheckNote?: string;
            /**
             * Whether the candidate has a convicted offence
             */
            convictedOffence?: boolean;
            /**
             * The description of the convicted offence
             *
             * @maxLength 1024
             */
            convictedOffenceDescription?: string;
            /**
             * Whether the candidate has any convictions
             */
            convictions?: boolean;
            /**
             * The description of the convictions
             *
             * @maxLength 1024
             */
            convictionsDescription?: string;
            /**
             * Whether the candidate is registered to the DBS update service
             */
            registeredToUpdateService?: boolean;
            /**
             * Whether the candidate consents to update service checks
             */
            consentUpdateServiceChecks?: boolean;
            /**
             * Any additional notes regarding the candidate's criminal record
             *
             * @maxLength 1024
             */
            note?: string;
            /**
             * The date when the update service check was performed
             *
             * @maxLength 64
             */
            updateServiceCheckDate?: string;
            /**
             * Whether the candidate has lived abroad
             */
            abroad?: boolean;
            /**
             * Any additional notes regarding the overseas declaration
             *
             * @maxLength 1024
             */
            overseasDeclarationExtended?: string;
            /**
             * Whether the candidate has been subject to educational disciplinary proceedings
             */
            educationalDisciplinaryProceedings?: boolean;
            /**
             * Any comments regarding the DBS check
             *
             * @maxLength 1024
             */
            dbsComments?: string;
            /**
             * Whether the candidate has read the DBS declaration
             */
            dbsDeclarationRead?: boolean;
            /**
             * Whether the candidate has any overseas offences
             */
            overseasOffence?: boolean;
            /**
             * The description of the overseas offence
             *
             * @maxLength 1024
             */
            overseasOffenceDescription?: string;
            /**
             * Whether the candidate has a current criminal offence
             */
            currentCriminalOffence?: boolean;
            /**
             * The description of the current criminal offence
             *
             * @maxLength 1024
             */
            currentCriminalOffenceDescription?: string;
            /**
             * Whether the DBS was issued by a business
             */
            dbsIssuedByBusiness?: boolean;
            /**
             * Whether the DBS was issued by the current employer
             */
            dbsIssuedByCurrentEmployer?: boolean;
            /**
             * Whether a Section 128 check is required for the candidate.
             */
            section128Required?: boolean;
            /**
             * The date when the Section 128 check was completed.
             */
            section128Checked?: string;
          };
          /**
           * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
           */
          criminalRecordDeclaration?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * The answers recorded for the point-in-time question set.
             *
             * @maxItems 100
             */
            answers?: {
              /**
               * The identifier of the question being answered.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              questionId?: string;
              /**
               * The stable key of the question being answered.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * The submitted answer to the question.
               */
              value?: string | string | boolean | number | (void | null);
            }[];
            /**
             * When the question set was submitted.
             *
             * @format date-time
             */
            submittedAt?: string;
            /**
             * The network address recorded when the response was submitted.
             *
             * @maxLength 64
             */
            submittedIp?: string;
            /**
             * When the question configuration captured by this response was last updated.
             *
             * @format date-time
             */
            configurationUpdatedAt?: string;
          };
          /**
           * Whether the candidate confirmed their criminal record declaration.
           */
          criminalRecordCheckConsent?: boolean;
          /**
           * The name used to confirm the criminal record declaration.
           *
           * @maxLength 256
           */
          criminalRecordCheckConsentName?: string;
          /**
           * The date the criminal record declaration was confirmed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          criminalRecordCheckConsentDate?: string;
          safeInEducation?: {
            /**
             * Whether the candidate is barred from working in education
             */
            barred?: boolean;
            /**
             * The date when the barred status was checked
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            barredCheckDate?: string;
            /**
             * Whether the candidate has a waiver for the barred status
             */
            waiver?: boolean;
            /**
             * Any orders related to the candidate's barred status
             */
            ordersYou?: boolean;
            /**
             * Any orders related to the candidate's barred status for children
             */
            ordersChild?: boolean;
            /**
             * Whether the candidate has any previous safeguarding allegations
             */
            previousSafeguardingAllegations?: boolean;
            /**
             * Whether the candidate has been cautioned
             */
            cautioned?: boolean;
            /**
             * Whether the candidate has a violent offence
             */
            violentOffence?: boolean;
            /**
             * Whether the candidate has lived or worked in another country
             */
            anotherCountry?: boolean;
            /**
             * Whether the candidate has read the Keeping Children Safe in Education document
             */
            readKCSIE?: boolean;
            /**
             * Whether the candidate has read the Disqualification under the Childcare Act guidance
             */
            readChildcareAct?: boolean;
            /**
             * Whether the candidate is prohibited from teaching
             */
            prohibitedFromTeaching?: boolean;
            /**
             * Whether the candidate is known to the police
             */
            knownPolice?: boolean;
            /**
             * Whether the candidate is prohibited from management roles
             */
            prohibitedFromManagement?: boolean;
            /**
             * Whether the candidate has any criminal offences under law
             */
            criminalOffencesLaw?: boolean;
            /**
             * Any additional details regarding the candidate's safe in education status
             */
            safeInEducationDetails?: string;
            safeguardingQuestionaire?: {
              /**
               * What is the primary purpose of safeguarding in a school environment?
               */
              primaryPurposeSafeguarding?:
                | "enhance-academic-performance"
                | "ensure-safe-learning-environment"
                | "promote-healthy-relationships"
                | "facilitate-extracurricular-activities";
              /**
               * Who is typically responsible for coordinating safeguarding efforts within a school?
               */
              typicallyResponsibleCoordinatingSafeguarding?:
                | "janitorial-staff"
                | "parents-association"
                | "school-nurse"
                | "designated-safeguarding-lead";
              /**
               * Which of the following is considered a potential sign of child abuse or neglect?
               */
              signOfAbuse?:
                | "student-excelling"
                | "frequent-extracurricular"
                | "unexplained-injuries"
                | "not-listening";
              /**
               * How should a teacher respond if a student discloses information about potential abuse or harm?
               */
              studentDisclosesInformation?:
                | "ignore-disclosure"
                | "report-disclosure"
                | "share-information"
                | "advise-independent";
              /**
               * What is the purpose of a school's Child Protection Policy?
               */
              schoolsChildProtectionPolicy?:
                | "enforcing-strict-disciplinary-measures"
                | "identifying-troublemakers"
                | "outlining-procedures-for-protecting-students-from-harm"
                | "establishing-academic-achievement-standards";
              /**
               * What does the term 'FGM' stand for in the context of safeguarding?
               */
              fgmDefinition?:
                | "female-general-medicine"
                | "forced-genetic-modification"
                | "female-genital-mutilation"
                | "foundation-gender-equality";
              /**
               * Which of the following is a key principle of online safety for students?
               */
              keyPrincipleOnlineSafetyStudents?:
                | "unlimited-screen-time"
                | "anon-online-interaction"
                | "responsible-digital-citizen"
                | "restrict-edu-sites";
              /**
               * When planning a school trip, what should teachers consider regarding safeguarding?
               */
              teachersConsiderRegardingSafeguarding?:
                | "minimise-supervision"
                | "appropriate-staff-ratios"
                | "exclude-medical-conditions"
                | "allow-roaming";
              /**
               * What is the purpose of a criminal background check for teachers?
               */
              criminalBackgroundCheckTeachers?:
                | "evaluating-teaching-proficiency"
                | "verifying-immigration-status"
                | "assessing-financial-stability"
                | "identifying-criminal-history";
              /**
               * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
               */
              studentConfides?:
                | "assure-secret-safe"
                | "encourage-share-friends"
                | "explain-no-guarantee"
                | "disclose-to-others";
            };
          };
          socialMedia?: {
            /**
             * The Facebook profile URL of the candidate
             *
             * @maxLength 256
             */
            facebook?: string;
            /**
             * The Instagram profile URL of the candidate
             *
             * @maxLength 256
             */
            instagram?: string;
            /**
             * The Twitter profile URL of the candidate
             *
             * @maxLength 256
             */
            twitter?: string;
            /**
             * The LinkedIn profile URL of the candidate
             *
             * @maxLength 256
             */
            linkedIn?: string;
            /**
             * The TikTok profile URL of the candidate
             *
             * @maxLength 256
             */
            tiktok?: string;
            /**
             * Any other social media profile URLs of the candidate
             *
             * @maxLength 256
             */
            others?: string;
            /**
             * Whether the social media profiles have been checked
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            socialMediaChecked?: string;
            /**
             * The date the candidate's Google search check was completed
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            googleCheckedDate?: string;
          };
          /**
           * Whether the candidate has consented to a safe in education check
           */
          safeInEducationCheckConsent?: boolean;
          /**
           * The name of the person who provided consent for the safe in education check
           *
           * @maxLength 256
           */
          safeInEducationCheckConsentName?: string;
          /**
           * The date when consent for the safe in education check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          safeInEducationCheckConsentDate?: string;
          /**
           * The health record of the individual
           */
          healthRecord?: {
            /**
             * Whether the individual has a physical ability condition
             */
            physicalAbilityCondition?: boolean;
            /**
             * Whether the individual is confused
             */
            confused?: boolean;
            /**
             * Whether the individual has hearing issues
             */
            hearing?: boolean;
            /**
             * Whether the individual has eyesight issues
             */
            eyesight?: boolean;
            /**
             * Whether the individual has depression
             */
            depression?: boolean;
            /**
             * Whether the individual has severe pain
             */
            severePain?: boolean;
            /**
             * Whether the individual has excessive drowsiness
             */
            excessiveDrowsiness?: boolean;
            /**
             * Whether the individual has epilepsy
             */
            epilepsy?: boolean;
            /**
             * Whether the individual has heart issues
             */
            heart?: boolean;
            /**
             * Whether the individual has asthma
             */
            asthma?: boolean;
            /**
             * Whether the individual has mental health issues
             */
            mentalHealth?: boolean;
            /**
             * Whether the individual is an expectant mother
             */
            expectantMother?: boolean;
            /**
             * Whether the individual has allergies
             */
            allergies?: boolean;
            /**
             * Whether the individual is taking medication
             */
            medication?: boolean;
            /**
             * Whether the individual has any health issues
             */
            healthIssues?: boolean;
            /**
             * A description of the individual's health issues
             *
             * @maxLength 2048
             */
            healthIssuesDescription?: string;
            /**
             * Any disability needs of the individual
             *
             * @maxLength 2048
             */
            disabilityNeeds?: string;
            /**
             * Whether the individual has any contagious diseases
             */
            contagiousDiseases?: boolean;
            /**
             * Whether the individual's health affects their ability
             */
            affectAbility?: boolean;
            /**
             * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
             */
            notificationAgree?: boolean;
            /**
             * Whether the individual has opted into the SAS Wellbeing App
             */
            sasOptIn?: boolean;
            /**
             * The individual's signature
             *
             * @maxLength 256
             */
            signature?: string;
            /**
             * The individual's drawn signature in base64url format
             *
             * @maxLength 4096
             */
            drawnSignature?: string;
            /**
             * The date the individual signed the form
             *
             * @format date-time
             */
            signatureDate?: string | null;
            /**
             * Whether the individual has been vaccinated against measles
             */
            vaccinationMeasles?: boolean;
            /**
             * Whether the individual has been vaccinated against mumps
             */
            vaccinationMumps?: boolean;
            /**
             * Whether the individual has been vaccinated against rubella
             */
            vaccinationRubella?: boolean;
            /**
             * Whether the individual has been vaccinated against varicella
             */
            vaccinationVaricella?: boolean;
            /**
             * Whether the individual has been vaccinated against tuberculosis
             */
            vaccinationTuberculosis?: boolean;
            /**
             * Whether the individual has been vaccinated against hepatitis B
             */
            vaccinationHepatitisB?: boolean;
            /**
             * Whether the individual has been vaccinated against hepatitis C
             */
            vaccinationHepatitisC?: boolean;
            /**
             * Whether the individual has been vaccinated against HIV
             */
            vaccinationHIV?: boolean;
            /**
             * Whether the individual has been vaccinated against COVID-19
             */
            vaccinationCovid?: boolean;
            /**
             * Whether the individual has received the 1st dose of the COVID-19 vaccine
             */
            vaccinationCovid1?: boolean;
            /**
             * Whether the individual has received the 2nd dose of the COVID-19 vaccine
             */
            vaccinationCovid2?: boolean;
            /**
             * Whether the individual has received the COVID-19 booster vaccine
             */
            vaccinationCovidBooster?: boolean;
            /**
             * Whether the individual has received the COVID-19 influenza vaccine
             */
            vaccinationCovidInfluenza?: boolean;
            /**
             * Whether the individual has an override for fit to work requirements
             */
            fitToWorkOverride?: boolean;
          };
          /**
           * The candidate's latest submitted health declaration, including the exact questions presented.
           */
          healthDeclaration?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * The answers recorded for the point-in-time question set.
             *
             * @maxItems 100
             */
            answers?: {
              /**
               * The identifier of the question being answered.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              questionId?: string;
              /**
               * The stable key of the question being answered.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * The submitted answer to the question.
               */
              value?: string | string | boolean | number | (void | null);
            }[];
            /**
             * When the question set was submitted.
             *
             * @format date-time
             */
            submittedAt?: string;
            /**
             * The network address recorded when the response was submitted.
             *
             * @maxLength 64
             */
            submittedIp?: string;
            /**
             * When the question configuration captured by this response was last updated.
             *
             * @format date-time
             */
            configurationUpdatedAt?: string;
          };
          /**
           * Whether the candidate confirmed their health declaration.
           */
          healthRecordCheckConsent?: boolean;
          /**
           * The name used to confirm the health declaration.
           *
           * @maxLength 256
           */
          healthRecordCheckConsentName?: string;
          /**
           * The date the health declaration was confirmed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          healthRecordCheckConsentDate?: string;
          /**
           * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
           */
          bankDetails?: {
            /**
             * The name on the bank account
             */
            bankAccountName?: string;
            /**
             * The name of the bank
             */
            bankName?: string;
            /**
             * The account number
             */
            accountNumber?: string;
            /**
             * The sort code
             */
            sortCode?: string;
            /**
             * The National Insurance number
             */
            niNumber?: string;
            /**
             * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
             */
            jobOp1?: boolean;
            /**
             * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
             */
            jobOp2?: boolean;
            /**
             * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
             */
            jobOp3?: boolean;
            studentLoan?: {
              /**
               * Boolean flag for if candidate has student loan
               */
              studentLoan?: boolean;
              /**
               * Boolean flag for if candidate is on plan 1
               */
              plan1?: boolean;
              /**
               * Boolean flag for if candidate is on plan 2
               */
              plan2?: boolean;
              /**
               * Boolean flag for if candidate is on plan 4
               */
              plan4?: boolean;
              /**
               * Boolean flag for if candidate has postgrad loan
               */
              postGradLoan?: boolean;
            };
          };
          /**
           * Whether the candidate has confirmed their details are correct.
           */
          declarationCheckConsent?: boolean;
          /**
           * The name of the person who confirmed their details are correct.
           *
           * @maxLength 256
           */
          declarationCheckConsentName?: string;
          /**
           * The date when consent for the declaration check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          declarationCheckConsentDate?: string;
          /**
           * Whether the candidate has consented to a contract for service
           */
          contractForServiceConsent?: boolean;
          /**
           * The name of the person who provided consent for the contract for service
           *
           * @maxLength 256
           */
          contractForServiceName?: string;
          /**
           * The date when consent for the contract for service was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          contractForServiceDate?: string;
          /**
           * The candidate has completed their onboarding form.
           */
          confirmed?: boolean;
          /**
           * The ID of the user who created the candidate record.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          createdBy?: string;
          /**
           * The ID of the customer associated with the candidate.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customerId?: string;
          /**
           * The migration that created this candidate
           *
           * @pattern ^[0-9a-f]{24}$
           */
          migrationId?: string;
          /**
           * Whether this migrated candidate has been approved
           */
          migrationApproval?: "pending" | "approved" | "rejected";
          /**
           * The user assigned to the candidate. If expanded, a user object else the user ID.
           */
          assignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * The ID of the current booking for the candidate.
           */
          currentBooking?: string[];
          /**
           * The URL of the candidate's profile photo.
           *
           * @maxLength 256
           */
          profilePhoto?: string;
          /**
           * The pay type for the candidate.
           */
          payType?: "PAYE" | "Umbrella" | "Limited Company" | "Not Applicable";
          /**
           * The pay interval for the candidate.
           */
          payInterval?:
            | "Monthly"
            | "Weekly"
            | "Weekly Pay in Arrears"
            | "Bi-Weekly"
            | any
            | null;
          /**
           * The pay rate for the candidate.
           */
          payRate?: string;
          /**
           * A custom ID for the candidate.
           *
           * @maxLength 256
           */
          customId?: string;
          /**
           * A company-defined identifier used to match the candidate with external records.
           *
           * @maxLength 256
           */
          _internalId?: string;
          /**
           * The date when the candidate's status was last changed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          statusChangeDate?: string;
          /**
           * Whether the candidate has opted out of communications.
           */
          optOut?: boolean;
          /**
           * The TRN number for the candidate.
           *
           * @maxLength 256
           */
          trnNumber?: string;
          /**
           * The date when the TRN number was last checked.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          trnNumberCheckedDate?: string;
          /**
           * Any notes related to the TRN number.
           *
           * @maxLength 1024
           */
          trnNumberNotes?: string;
          /**
           * The date when the candidate's employment is set to start.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          employmentStartDate?: string;
          /**
           * Whether the candidate has confirmed their Early Years Workforce status.
           */
          ewcConfirmation?: boolean;
          /**
           * The share code for the candidate's Home Office application.
           *
           * @maxLength 256
           */
          homeOfficeShareCode?: string;
          /**
           * Whether the candidate has been archived.
           */
          archived?: boolean;
          /**
           * Whether the candidate is using an umbrella company.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          umbrella?: string;
          /**
           * ID of the PAYE umbrella company.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          payeUmbrella?: string;
          /**
           * The name of the umbrella company the candidate is using.
           *
           * @maxLength 256
           */
          umbrellaCompanyName?: string;
          /**
           * The code of the umbrella company the candidate is using.
           *
           * @maxLength 256
           */
          umbrellaCompanyCode?: string;
          /**
           * The transport method for the candidate.
           */
          transport?:
            | "Drives Own Vehicle"
            | "Use Public Transport"
            | "Access to Another Vehicle"
            | "Bicycle"
            | "No Access"
            | "Other";
          /**
           * Any important notes related to the candidate.
           *
           * @maxLength 1024
           */
          importantNotes?: string;
          /**
           * The date when the candidate was last updated.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          candidateLastUpdated?: string;
          /**
           * The source from which the candidate was obtained.
           */
          source?:
            | (
                | "Company Website"
                | "iCan Teach UK Website"
                | "Apply to Education"
                | "CV Library - CV Search"
                | "CV Library Advert"
                | "CV Library - Watchdog"
                | "Guardian- CV Search"
                | "Guardian Advert"
                | "Indeed - CV Search"
                | "Indeed Advert"
                | "Reed - CV Search"
                | "Reed Advert"
                | "Reed \u2013 Watchdog"
                | "SENploy - Advert"
                | "Total Jobs"
                | "Other Job Board"
                | "Google"
                | "LinkedIn"
                | "Linkedin Recruiter"
                | "LinkedIn Advert"
                | "Referral - School"
                | "Referral - Candidate"
                | "UK University"
                | "Irish University"
                | "Canadian University"
                | "Marketing Campaign"
                | "Woo"
                | "Job Show"
                | "Recruitment Fair"
                | "Career Fair"
                | "Volcanic"
                | "Broadbean"
                | "Headhunt"
                | "Referral"
                | "Direct"
                | "Other"
              )
            | (
                | "Company Website"
                | "Direct Call"
                | "CV Library Watchdog"
                | "CV Library Advert"
                | "CV Library CV Search"
                | "Reed Watchdog"
                | "Reed Advert"
                | "Reed CV Search"
                | "Indeed Watchdog"
                | "Indeed Advert"
                | "Indeed CV Search"
                | "Job Centre"
                | "Returning Candidate"
                | "Referral Candidate"
                | "Referral Client"
                | "Training Provider"
                | "Recruitment Fair"
                | "University/College"
              );
          /**
           * Any other source from which the candidate was obtained.
           *
           * @maxLength 256
           */
          sourceOther?: string;
          /**
           * Whether the candidate has given consent for online checks.
           */
          onlineCheckConsent?: boolean;
          /**
           * The title of the candidate.
           */
          title?:
            | "Mr"
            | "Mrs"
            | "Ms"
            | "Miss"
            | "Dr"
            | "Mx"
            | "Lady"
            | "Lord"
            | "Other";
          /**
           * The pronoun used by the candidate.
           */
          pronoun?:
            | "She/Her/Hers"
            | "He/Him/His"
            | "They/Them/Theirs"
            | "Ze/Hir/Hirs"
            | "Ze/Zir/Zirs"
            | "Other"
            | "Prefer not to say";
          /**
           * The date when the candidate was last updated.
           *
           * @format date-time
           */
          updatedAt?: string;
          /**
           * The date when the candidate was last contacted.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          lastContactedDate?: string;
          /**
           * A short, prominent note shown with the candidate to communicate important context.
           */
          highlight?: string;
          /**
           * The pay scale for the candidate's teaching position.
           *
           * @maxLength 256
           */
          teacherPayScale?: string;
          /**
           * Whether the candidate has a P45.
           */
          p45?: boolean;
          /**
           * The type of role the candidate is applying for.
           */
          roleType?:
            | "Senior Leadership Positions"
            | "Teaching Roles"
            | "School Support Positions";
          /**
           * The Right to Work verification status for the candidate.
           *
           * @maxLength 256
           */
          rtwVerification?: string;
          /**
           * Any notes related to the Right to Work verification status for the candidate.
           *
           * @maxLength 1024
           */
          rtwVerificationNote?: string;
          /**
           * The Right to Work verification status for the candidate.
           *
           * @maxLength 256
           */
          rtwVerificationStatus?: string;
          /**
           * The location where the candidate desires to work.
           *
           * @maxLength 256
           */
          desiredLocation?: string;
          /**
           * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
           */
          complianceUser?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
           */
          watchers?:
            | string[]
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              }[];
          /**
           * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
           */
          tags?:
            | string[]
            | {
                /**
                 * The unique identifier of the tag
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The label displayed wherever this tag is applied.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                name?: string;
                /**
                 * The tag display colour as a hexadecimal value, for example #2563EB.
                 *
                 * @maxLength 32
                 */
                color?: string | null;
              }[];
          /**
           * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
           *
           * @maxItems 5
           */
          documents?: {
            /**
             * The unique identifier of the document
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The name of the document
             *
             * @maxLength 512
             */
            name?: string;
            /**
             * The file reference used to retrieve the document
             *
             * @maxLength 2048
             */
            reference?: string;
            /**
             * The file's media type
             *
             * @maxLength 256
             */
            fileType?: string;
            /**
             * The area of the platform the document belongs to
             *
             * @maxLength 256
             */
            type?: string;
            /**
             * The configured category of the document
             *
             * @maxLength 256
             */
            docType?: string;
            /**
             * The document's expiry date, when applicable
             *
             * @maxLength 64
             */
            expiry?: string;
            /**
             * The document's issued date, when applicable
             *
             * @maxLength 64
             */
            issued?: string;
            /**
             * Whether the document has been archived
             */
            documentArchived?: boolean;
            /**
             * The date the document was uploaded
             *
             * @format date-time
             */
            createdAt?: string;
          }[];
          /**
           * The total number of documents attached to the candidate.
           *
           * @minimum 0
           */
          documentsCount?: number;
          /**
           * The locations where the candidate desires to work.
           */
          desiredLocationMultiple?: string[];
          /**
           * Daily availability for the requested period. Returned by the availability view only.
           *
           * @maxItems 31
           */
          availabilityDays?: {
            /**
             * Calendar date in YYYY-MM-DD format.
             *
             * @maxLength 10
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date?: string;
            /**
             * Availability or booking state for this date.
             */
            status?:
              | "available"
              | "unavailable"
              | "booked"
              | "halfDay"
              | "awaiting"
              | "unknown";
            /**
             * Recorded availability, or null when unrecorded.
             */
            available?: boolean | null;
            /**
             * Morning or afternoon availability.
             */
            partial?: "AM" | "PM" | any | null;
            /**
             * Additional availability information.
             *
             * @maxLength 4096
             */
            note?: string | null;
            /**
             * Active bookings on this date.
             *
             * @maxItems 100
             */
            bookings?: {
              /**
               * The unique identifier of the resource
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * @maxLength 512
               */
              clientName?: string;
              /**
               * @maxLength 512
               */
              role?: string;
              /**
               * @maxLength 32
               */
              timeDesignation?: string;
            }[];
          }[];
          /**
           * The date when the candidate's availability was last checked.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          availabilityLastChecked?: string;
          /**
           * Whether the candidate is subject to notice period.
           */
          subjectToNotice?: "Unsure" | "Yes" | "No";
          /**
           * The subsidiary subjects the candidate is qualified to teach.
           */
          subsidiarySubject?: string[];
          /**
           * The candidate's preferred employment type.
           */
          employmentPreference?: (
            | "Day To Day"
            | "Long Term"
            | "Fixed Term"
            | "Perm"
          )[];
          /**
           * Any other names the candidate is known by.
           *
           * @maxLength 256
           */
          otherNames?: string;
          /**
           * The name of the candidate's limited company, if applicable.
           *
           * @maxLength 256
           */
          ltdCompany?: string;
          /**
           * Whether the candidate's employment has been verified.
           */
          employmentVerified?: boolean;
          /**
           * The DBS verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSVerification?: string;
          /**
           * Any notes related to the DBS verification status for the candidate.
           *
           * @maxLength 1024
           */
          candidateDBSVerificationNote?: string;
          /**
           * The DBS verification status for the candidate.
           */
          candidateDBSVerificationStatus?: "Cleared" | "Alerts";
          /**
           * The DBS check verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSCheckVerification?: string;
          /**
           * Any notes related to the DBS check verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSCheckVerificationNote?: string;
          /**
           * The DBS check verification status for the candidate.
           */
          candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
          /**
           * Any notes related to the DBS update service verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSUpdateServiceVerificationNote?: string;
          /**
           * The DBS update service verification status for the candidate.
           */
          candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
          /**
           * Any notes related to the TRN verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateTRNVerificationNote?: string;
          /**
           * The TRN verification status for the candidate.
           */
          candidateTRNVerificationStatus?:
            | "Completed Induction"
            | "Not Yet Completed Induction"
            | "Induction Exempt"
            | "ALERTS - Failed Induction"
            | "Induction Not Required";
          /**
           * The health and safety verification status for the candidate.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          candidateHealthSafetyVerification?: string;
          /**
           * Any notes related to the health and safety verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateHealthSafetyVerificationNote?: string;
          /**
           * The health and safety verification status for the candidate.
           */
          candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
          /**
           * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
           */
          onboardingSession?:
            | string
            | {
                /**
                 * Unique identifier for the onboarding session
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The URL for the onboarding session
                 *
                 * @format uri
                 */
                url?: string;
                /**
                 * The frozen health declaration configuration for this onboarding session.
                 */
                healthDeclarationConfig?: {
                  /**
                   * The ordered questions included in the question set.
                   *
                   * @maxItems 100
                   */
                  questions?: {
                    /**
                     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id?: string;
                    /**
                     * A stable key used to associate the question with current and historical answers.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    key?: string;
                    /**
                     * The question shown to the person completing the form.
                     *
                     * @minLength 1
                     * @maxLength 500
                     */
                    label?: string;
                    /**
                     * Optional supporting text displayed with the question.
                     *
                     * @maxLength 2000
                     */
                    description?: string;
                    /**
                     * The type of answer collected for the question.
                     */
                    type?:
                      | "checkbox"
                      | "date"
                      | "daterange"
                      | "dropdown"
                      | "number"
                      | "signature"
                      | "text"
                      | "textarea";
                    /**
                     * Whether an answer is required while the question is visible.
                     */
                    required?: boolean;
                    /**
                     * Whether the question is included in new forms. Historical responses retain disabled questions.
                     */
                    enabled?: boolean;
                    /**
                     * The display order of the question.
                     *
                     * @minimum 0
                     * @maximum 1000
                     */
                    order?: number;
                    /**
                     * Optional rules that determine when the question is shown.
                     */
                    condition?: {
                      /**
                       * Whether any rule or every rule must match before the question is shown.
                       */
                      mode?: "any" | "all";
                      /**
                       * The comparisons used to determine question visibility.
                       *
                       * @minItems 1
                       * @maxItems 20
                       */
                      rules?: {
                        /**
                         * The stable key of the question that controls visibility.
                         *
                         * @minLength 1
                         * @maxLength 128
                         */
                        questionKey?: string;
                        /**
                         * How the controlling answer is compared with the value.
                         */
                        operator?: "equals" | "notEquals";
                        /**
                         * The answer value used to determine visibility.
                         */
                        value?: string | boolean | number | (void | null);
                      }[];
                    };
                    /**
                     * The available answers for a dropdown question.
                     *
                     * @minItems 1
                     * @maxItems 100
                     */
                    options?: {
                      /**
                       * The value stored when this option is selected.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      value?: string;
                      /**
                       * The option displayed to the person answering the question.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      label?: string;
                    }[];
                    /**
                     * Whether this question belongs to the standard template.
                     */
                    ["default"]?: boolean;
                    /**
                     * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                     */
                    audience?: "candidate" | "staff";
                    /**
                     * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                     */
                    fixedRole?: "fullName" | "signature";
                  }[];
                  /**
                   * When this question configuration was last updated.
                   *
                   * @format date-time
                   */
                  updatedAt?: string;
                };
                /**
                 * The frozen Criminal Record & Security Checks configuration for this onboarding session.
                 */
                criminalRecordConfig?: {
                  /**
                   * The ordered questions included in the question set.
                   *
                   * @maxItems 100
                   */
                  questions?: {
                    /**
                     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id?: string;
                    /**
                     * A stable key used to associate the question with current and historical answers.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    key?: string;
                    /**
                     * The question shown to the person completing the form.
                     *
                     * @minLength 1
                     * @maxLength 500
                     */
                    label?: string;
                    /**
                     * Optional supporting text displayed with the question.
                     *
                     * @maxLength 2000
                     */
                    description?: string;
                    /**
                     * The type of answer collected for the question.
                     */
                    type?:
                      | "checkbox"
                      | "date"
                      | "daterange"
                      | "dropdown"
                      | "number"
                      | "signature"
                      | "text"
                      | "textarea";
                    /**
                     * Whether an answer is required while the question is visible.
                     */
                    required?: boolean;
                    /**
                     * Whether the question is included in new forms. Historical responses retain disabled questions.
                     */
                    enabled?: boolean;
                    /**
                     * The display order of the question.
                     *
                     * @minimum 0
                     * @maximum 1000
                     */
                    order?: number;
                    /**
                     * Optional rules that determine when the question is shown.
                     */
                    condition?: {
                      /**
                       * Whether any rule or every rule must match before the question is shown.
                       */
                      mode?: "any" | "all";
                      /**
                       * The comparisons used to determine question visibility.
                       *
                       * @minItems 1
                       * @maxItems 20
                       */
                      rules?: {
                        /**
                         * The stable key of the question that controls visibility.
                         *
                         * @minLength 1
                         * @maxLength 128
                         */
                        questionKey?: string;
                        /**
                         * How the controlling answer is compared with the value.
                         */
                        operator?: "equals" | "notEquals";
                        /**
                         * The answer value used to determine visibility.
                         */
                        value?: string | boolean | number | (void | null);
                      }[];
                    };
                    /**
                     * The available answers for a dropdown question.
                     *
                     * @minItems 1
                     * @maxItems 100
                     */
                    options?: {
                      /**
                       * The value stored when this option is selected.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      value?: string;
                      /**
                       * The option displayed to the person answering the question.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      label?: string;
                    }[];
                    /**
                     * Whether this question belongs to the standard template.
                     */
                    ["default"]?: boolean;
                    /**
                     * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                     */
                    audience?: "candidate" | "staff";
                    /**
                     * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                     */
                    fixedRole?: "fullName" | "signature";
                  }[];
                  /**
                   * When this question configuration was last updated.
                   *
                   * @format date-time
                   */
                  updatedAt?: string;
                };
              };
          /**
           * The contact record for this candidate. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * Whether the candidate has opted out of 48 hour communications.
           */
          optOut48Hours?: boolean;
          /**
           * Whether the candidate has consented to REC audit checks.
           */
          recAuditConsent?: boolean | null;
          /**
           * The date the candidate's REC audit consent was recorded.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          recAuditConsentDate?: string;
          /**
           * The method through which REC audit consent was captured.
           *
           * @maxLength 256
           */
          recAuditConsentMethod?: string;
          /**
           * The date when the candidate record was created.
           *
           * @format date-time
           */
          createdAt?: string;
          /**
           * The mobile app link status for the candidate.
           */
          candidateMobileAppStatus?:
            | "Unlinked"
            | "Scheduled Unlink"
            | "Linked"
            | "Invited"
            | "Not Invited";
          /**
           * Evertime details for this candidate
           */
          evertime?: {
            /**
             * The Evertime CandidateId used for this candidate when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
            /**
             * Whether the matched Evertime candidate's NI number also matches the one held in Edun
             */
            niNumberMatch?: boolean;
          };
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Candidate fields or attached document excerpts that explain why the candidate matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * Whether the matching information came from the candidate record or an attached document.
             */
            source?: "candidate" | "document";
            /**
             * The candidate or document information that matched the search.
             *
             * @maxLength 256
             */
            field?: string;
            /**
             * A short excerpt showing why the result matched.
             *
             * @maxLength 1000
             */
            text?: string;
            /**
             * The exact terms within the content that matched the search, including synonym matches.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
            /**
             * The name of the attached document when the match came from a document.
             *
             * @maxLength 512
             */
            documentName?: string | null;
            /**
             * The unique identifier of the attached document when the match came from a document.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            documentId?: string | null;
          }[];
          /**
           * The candidate's distance in miles from the requested location. Present only when location filtering is used.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * Timesheet Portal details for this candidate
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal contractor GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal contractor code
             *
             * @maxLength 256
             */
            code?: string;
          };
          /**
           * Invite to candidate to link their mobile app.
           */
          mobileAppLinkInvite?: {
            /**
             * Boolean status of whether the link invite is being used
             *
             * @default false
             */
            isUsed?: boolean;
            /**
             * Creation date of the invite
             *
             * @format date-time
             * @default 2026-10-30T13:39:09.027Z
             */
            createdAt?: string;
            /**
             * ID of candidate using this link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            usedBy?: string;
            /**
             * Date of when link used
             *
             * @format date-time
             */
            usedAt?: string;
            /**
             * If unlinked, ID of user that unlinked this candidate's link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            unlinkedBy?: string;
            /**
             * If unlinked, date of when this link was unlinked
             *
             * @format date-time
             */
            unlinkedAt?: string;
            /**
             * If deleted, ID of user that deleted this candidate's link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            deletedBy?: string;
            /**
             * If deleted, date of when this link was deleted
             *
             * @format date-time
             */
            deletedAt?: string;
            /**
             * Current status of this mobile app link
             */
            status?:
              | "Sent"
              | "Linked"
              | "Unlinked"
              | "ScheduledUnlink"
              | "Not Sent"
              | "Rescinded";
          };
        }
      | (void | null);
    /**
     * The recipient name captured when the request was issued.
     *
     * @minLength 1
     * @maxLength 256
     */
    recipientName?: string | null;
    /**
     * The recipient email captured when the request was issued.
     *
     * @maxLength 320
     * @format email
     */
    recipientEmail?: string | null;
    /**
     * The frozen questions and canonical submitted answers.
     */
    response?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * The answers recorded for the point-in-time question set.
       *
       * @maxItems 100
       */
      answers?: {
        /**
         * The identifier of the question being answered.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        questionId?: string;
        /**
         * The stable key of the question being answered.
         *
         * @minLength 1
         * @maxLength 128
         */
        questionKey?: string;
        /**
         * The submitted answer to the question.
         */
        value?: string | string | boolean | number | (void | null);
      }[];
      /**
       * When the question set was submitted.
       *
       * @format date-time
       */
      submittedAt?: string;
      /**
       * The network address recorded when the response was submitted.
       *
       * @maxLength 64
       */
      submittedIp?: string;
      /**
       * When the question configuration captured by this response was last updated.
       *
       * @format date-time
       */
      configurationUpdatedAt?: string;
    };
    /**
     * When the response was submitted.
     *
     * @format date-time
     */
    submittedAt?: string;
    /**
     * When the response record was created.
     *
     * @format date-time
     */
    createdAt?: string;
  }[];
  /**
   * Total number of resources matching the request before pagination.
   *
   * @minimum 0
   */
  total: number;
  /**
   * One-based page number returned by this request.
   *
   * @minimum 1
   */
  page: number;
  /**
   * Maximum number of resources requested for this page.
   *
   * @minimum 1
   * @maximum 100
   */
  pageSize: number;
};

export type GetFormResponsesVariables = {
  queryParams?: GetFormResponsesQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetFormResponses = (
  variables: GetFormResponsesVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetFormResponsesResponse,
    GetFormResponsesError,
    undefined,
    {},
    GetFormResponsesQueryParams,
    {}
  >({ url: "/form-responses", method: "get", ...variables, signal });

export function getFormResponsesQuery(variables: GetFormResponsesVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetFormResponsesResponse>;
};

export function getFormResponsesQuery(
  variables: GetFormResponsesVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetFormResponsesResponse>)
    | reactQuery.SkipToken;
};

export function getFormResponsesQuery(
  variables: GetFormResponsesVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/form-responses",
      operationId: "getFormResponses",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetFormResponses(variables, signal),
  };
}

export const useSuspenseGetFormResponses = <TData = GetFormResponsesResponse,>(
  variables: GetFormResponsesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetFormResponsesResponse,
      GetFormResponsesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetFormResponsesResponse,
    GetFormResponsesError,
    TData
  >({
    ...getFormResponsesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetFormResponses = <TData = GetFormResponsesResponse,>(
  variables: GetFormResponsesVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetFormResponsesResponse,
      GetFormResponsesError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetFormResponsesResponse,
    GetFormResponsesError,
    TData
  >({
    ...getFormResponsesQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetFormResponsePathParams = {
  /**
   * The submitted response identifier.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  formResponseId: string;
};

export type GetFormResponseError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetFormResponseResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The unique identifier of the submitted response.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    id?: string;
    /**
     * The form definition from which this response originated.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    formId?: string;
    /**
     * The request through which the response was submitted.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    formRequestId?: string;
    /**
     * The candidate linked to the response. If expanded, a candidate object; otherwise, a candidate ID.
     */
    candidate?:
      | string
      | {
          /**
           * The unique identifier of the candidate
           *
           * @pattern ^[0-9a-f]{24}$
           */
          id?: string;
          /**
           * The forename of the candidate
           *
           * @minLength 1
           * @maxLength 256
           */
          forename?: string;
          /**
           * The surname of the candidate
           *
           * @minLength 1
           * @maxLength 256
           */
          surname?: string;
          /**
           * Any previous names of the candidate
           *
           * @maxLength 256
           */
          previousNames?: string;
          /**
           * The email of the candidate. This will be used for communication.
           *
           * @format email
           */
          email?: string;
          /**
           * The telephone number of the candidate
           *
           * @maxLength 32
           */
          telephone?: string;
          /**
           * The mobile number of the candidate
           *
           * @maxLength 32
           */
          mobile?: string;
          /**
           * The occupation of the candidate
           */
          occupation?:
            | "Teacher"
            | "Cover Supervisor"
            | "Teaching Assistant"
            | "School Support Staff"
            | "Primary Teacher"
            | "Secondary Teacher"
            | "Head Teacher"
            | "Senior Leader"
            | "Tutor"
            | "Mentor"
            | "Learning Support Assistant"
            | "Finance Officer"
            | "HR Administrator"
            | "HR Manager"
            | "Office Manager"
            | "Administrator"
            | "Personal Assistant"
            | "Pastoral Manager"
            | "Receptionist"
            | "Instructor"
            | "Caretaker"
            | "Nursery Assistant"
            | "Nursery Nurse"
            | "Deputy Nursery Manager"
            | "Lunchtime Supervisor"
            | "Classroom Graduate"
            | "Primary Cover Supervisor"
            | "HLTA"
            | "Nurture Teacher"
            | "Nurture Mentor"
            | "SENDCo"
            | "Registered Manager"
            | "Regional Manager"
            | "Education Director"
            | "Residential Director"
            | "Deputy Manager"
            | "Residential Support Worker"
            | "Sports Coach"
            | "Executive Headteacher"
            | "General Practitioner"
            | "Advance Nurse Practitioner (ANP)"
            | "Emergency Nurse Practitioner (ENP)"
            | "Advanced Clinical Practitioner (ACP)"
            | "Emergency Clinical Practitioner (ECP)"
            | "Consultant Psychiatrist"
            | "Psychiatrist \u2013 SPR"
            | "IT Technician"
            | "Art Technician"
            | "DT Technician"
            | "Science Technician"
            | "Healthcare Assistant/ Residential Support Worker"
            | "Healthcare Assistant"
            | "Senior Healthcare Assistant"
            | "Team Leader"
            | "Registered General Nurse"
            | "Registered Mental Nurse"
            | "Registered Nurse Learning Disabilities"
            | "Overseas Trained Teacher";
          /**
           * The subject the candidate specializes in
           *
           * @maxLength 256
           */
          subject?: string;
          /**
           * The address of the resource
           */
          address?: {
            /**
             * The first line of the address
             *
             * @maxLength 256
             */
            addressLine1?: string;
            /**
             * The second line of the address
             *
             * @maxLength 256
             */
            addressLine2?: string;
            /**
             * The third line of the address
             *
             * @maxLength 256
             */
            addressLine3?: string;
            /**
             * The city of the address
             *
             * @maxLength 256
             */
            city?: string;
            /**
             * The county of the address
             *
             * @maxLength 256
             */
            county?: string;
            /**
             * The borough of the address
             *
             * @maxLength 256
             */
            borough?: string;
            /**
             * The postcode of the address
             *
             * @maxLength 10
             */
            postcode?: string;
            /**
             * The country of the address
             *
             * @maxLength 256
             */
            country?: string;
            /**
             * The latitude of the address
             *
             * @minimum -90
             * @maximum 90
             */
            lat?: number;
            /**
             * The longitude of the address
             *
             * @minimum -180
             * @maximum 180
             */
            lng?: number;
          };
          /**
           * The qualifications of the candidate
           *
           * @maxItems 32
           */
          qualifications?: {
            /**
             * The type of the qualification
             *
             * @maxLength 256
             */
            type?: string;
            /**
             * The place where the qualification was obtained
             *
             * @maxLength 256
             */
            place?: string;
            /**
             * The name of the qualification
             *
             * @maxLength 256
             */
            qualificationName?: string;
            /**
             * The date the qualification was obtained
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date?: string;
            /**
             * The unique identifier of the qualification
             *
             * @maxLength 256
             */
            qualificationId?: string;
            /**
             * The order index of the qualification
             *
             * @minimum 0
             */
            orderIndex?: number;
            /**
             * The expiry date of the qualification
             *
             * @maxLength 256
             */
            qualificationExpiry?: string;
            /**
             * Whether to exclude this qualification from vetting
             */
            excludeFromVetting?: boolean;
            /**
             * The date the qualification was created
             *
             * @format date-time
             */
            createdAt?: string | null;
          }[];
          /**
           * The employment history of the candidate
           *
           * @maxItems 32
           */
          employment?: {
            /**
             * The name of the employer
             *
             * @maxLength 256
             */
            employerName?: string;
            /**
             * The date the candidate started employment
             *
             * @maxLength 256
             */
            dateStarted?: string;
            /**
             * The date the candidate ended employment
             *
             * @maxLength 256
             */
            dateEnded?: string;
            /**
             * The job title of the candidate
             *
             * @maxLength 256
             */
            jobTitle?: string;
            /**
             * The reason the candidate left this employment
             *
             * @maxLength 1024
             */
            reasonForLeaving?: string;
            /**
             * The duties of the candidate in this role. Consultant only, visible on Edun CV
             *
             * @maxLength 1024
             */
            duties?: string;
            /**
             * The salary of the candidate in this role
             *
             * @maxLength 256
             */
            salary?: string;
            /**
             * The address of the candidate's reference for this employment
             */
            referenceAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
            /**
             * The unique identifier for this employment record
             *
             * @maxLength 256
             */
            employmentId?: string;
            /**
             * The order index of this employment record
             *
             * @minimum 0
             */
            orderIndex?: number;
          }[];
          /**
           * The references for the candidate
           *
           * @maxItems 32
           */
          references?: {
            /**
             * The title of the reference
             *
             * @maxLength 16
             */
            referenceTitle?: string;
            /**
             * The forename of the reference
             *
             * @maxLength 256
             */
            referenceForename?: string;
            /**
             * The surname of the reference
             *
             * @maxLength 256
             */
            referenceSurname?: string;
            /**
             * The company of the reference
             *
             * @maxLength 256
             */
            referenceCompany?: string;
            /**
             * The position of the reference
             *
             * @maxLength 256
             */
            referencePosition?: string;
            /**
             * The email of the reference
             *
             * @format email
             */
            referenceEmail?: string;
            /**
             * The telephone number of the reference
             *
             * @maxLength 32
             */
            referenceTelephone?: string;
            /**
             * The type of the reference
             */
            referenceType?:
              | "Education"
              | "Professional"
              | "Character"
              | "Factual"
              | "Academic"
              | "Volunteer";
            /**
             * The duration of the reference
             *
             * @maxLength 128
             */
            referenceDuration?: string;
            /**
             * The ID of the reference
             *
             * @maxLength 256
             */
            referenceId?: string;
            /**
             * This field only applies if the reference immediate consent question is enabled
             * . If true the candidate has consented to a consultant requesting this reference immediately upon onboarding completion.
             */
            referenceApply?: boolean;
            /**
             * Any additional notes about the reference
             *
             * @maxLength 1024
             */
            referenceNote?: string;
            /**
             * The start date of the reference
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            dateFrom?: string;
            /**
             * The end date of the reference
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            dateTo?: string;
          }[];
          /**
           * Whether the candidate has consented to a reference check
           */
          referenceCheckConsent?: boolean;
          /**
           * The name of the person who provided consent for the reference check
           *
           * @maxLength 256
           */
          referenceCheckConsentName?: string;
          /**
           * The date when consent for the reference check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          referenceCheckConsentDate?: string;
          /**
           * The date of birth of the candidate
           *
           * @format date-time
           */
          dob?: string | null;
          /**
           * The sex of the candidate
           */
          sex?: "Male" | "Female" | "Other" | "Unknown";
          /**
           * The status of the candidate
           */
          status?:
            | "Pending"
            | "Approved"
            | "Rejected"
            | "Anonymised"
            | "Archived"
            | "Enquired"
            | "Hold"
            | "Future"
            | "DNC"
            | "Pre";
          /**
           * The current status of the onboarding process for the candidate
           */
          onboardingStatus?: "Not Started" | "In Progress" | "Completed";
          /**
           * Whether the onboarding information has been sent to the candidate
           */
          onboardingSent?: boolean;
          nextOfKin?: {
            /**
             * The name of the next of kin
             *
             * @maxLength 256
             */
            nextOfKinName?: string;
            /**
             * The relationship of the next of kin to the candidate
             *
             * @maxLength 256
             */
            nextOfKinRelationship?: string;
            /**
             * The telephone number of the next of kin
             *
             * @maxLength 32
             */
            nextOfKinTelephone?: string;
            /**
             * The email address of the next of kin
             *
             * @format email
             */
            nextOfKinEmail?: string;
            nextOfKinAddress?: {
              /**
               * The first line of the address
               *
               * @maxLength 256
               */
              addressLine1?: string;
              /**
               * The second line of the address
               *
               * @maxLength 256
               */
              addressLine2?: string;
              /**
               * The third line of the address
               *
               * @maxLength 256
               */
              addressLine3?: string;
              /**
               * The city of the address
               *
               * @maxLength 256
               */
              city?: string;
              /**
               * The county of the address
               *
               * @maxLength 256
               */
              county?: string;
              /**
               * The borough of the address
               *
               * @maxLength 256
               */
              borough?: string;
              /**
               * The postcode of the address
               *
               * @maxLength 10
               */
              postcode?: string;
              /**
               * The country of the address
               *
               * @maxLength 256
               */
              country?: string;
              /**
               * The latitude of the address
               *
               * @minimum -90
               * @maximum 90
               */
              lat?: number;
              /**
               * The longitude of the address
               *
               * @minimum -180
               * @maximum 180
               */
              lng?: number;
            };
          };
          criminalRecord?: {
            /**
             * Whether the candidate has a DBS certificate
             */
            dbsHeld?: boolean;
            /**
             * The unique identifier for the DBS certificate
             *
             * @maxLength 256
             */
            dbsNumber?: string;
            /**
             * The type of DBS check
             */
            dbsType?:
              | "Basic"
              | "Standard"
              | "Enhanced"
              | "Enhanced with Barred List"
              | "Child workforce"
              | "Child and adult workforce"
              | "Adult workforce";
            /**
             * The date when the DBS check was last performed
             *
             * @maxLength 256
             */
            dbsLastChecked?: string;
            /**
             * The DBS update service number for the candidate
             *
             * @maxLength 256
             */
            dbsUpdateServiceNumber?: string;
            /**
             * The result of the prohibition check
             *
             * @maxLength 256
             */
            prohibitionCheck?: string;
            /**
             * The result of the overseas check
             *
             * @maxLength 256
             */
            overseasCheck?: string;
            /**
             * Any additional notes regarding the overseas check
             *
             * @maxLength 256
             */
            overseasCheckNote?: string;
            /**
             * Whether the candidate has a convicted offence
             */
            convictedOffence?: boolean;
            /**
             * The description of the convicted offence
             *
             * @maxLength 1024
             */
            convictedOffenceDescription?: string;
            /**
             * Whether the candidate has any convictions
             */
            convictions?: boolean;
            /**
             * The description of the convictions
             *
             * @maxLength 1024
             */
            convictionsDescription?: string;
            /**
             * Whether the candidate is registered to the DBS update service
             */
            registeredToUpdateService?: boolean;
            /**
             * Whether the candidate consents to update service checks
             */
            consentUpdateServiceChecks?: boolean;
            /**
             * Any additional notes regarding the candidate's criminal record
             *
             * @maxLength 1024
             */
            note?: string;
            /**
             * The date when the update service check was performed
             *
             * @maxLength 64
             */
            updateServiceCheckDate?: string;
            /**
             * Whether the candidate has lived abroad
             */
            abroad?: boolean;
            /**
             * Any additional notes regarding the overseas declaration
             *
             * @maxLength 1024
             */
            overseasDeclarationExtended?: string;
            /**
             * Whether the candidate has been subject to educational disciplinary proceedings
             */
            educationalDisciplinaryProceedings?: boolean;
            /**
             * Any comments regarding the DBS check
             *
             * @maxLength 1024
             */
            dbsComments?: string;
            /**
             * Whether the candidate has read the DBS declaration
             */
            dbsDeclarationRead?: boolean;
            /**
             * Whether the candidate has any overseas offences
             */
            overseasOffence?: boolean;
            /**
             * The description of the overseas offence
             *
             * @maxLength 1024
             */
            overseasOffenceDescription?: string;
            /**
             * Whether the candidate has a current criminal offence
             */
            currentCriminalOffence?: boolean;
            /**
             * The description of the current criminal offence
             *
             * @maxLength 1024
             */
            currentCriminalOffenceDescription?: string;
            /**
             * Whether the DBS was issued by a business
             */
            dbsIssuedByBusiness?: boolean;
            /**
             * Whether the DBS was issued by the current employer
             */
            dbsIssuedByCurrentEmployer?: boolean;
            /**
             * Whether a Section 128 check is required for the candidate.
             */
            section128Required?: boolean;
            /**
             * The date when the Section 128 check was completed.
             */
            section128Checked?: string;
          };
          /**
           * The candidate's latest submitted Criminal Record & Security Checks declaration, including the exact questions presented.
           */
          criminalRecordDeclaration?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * The answers recorded for the point-in-time question set.
             *
             * @maxItems 100
             */
            answers?: {
              /**
               * The identifier of the question being answered.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              questionId?: string;
              /**
               * The stable key of the question being answered.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * The submitted answer to the question.
               */
              value?: string | string | boolean | number | (void | null);
            }[];
            /**
             * When the question set was submitted.
             *
             * @format date-time
             */
            submittedAt?: string;
            /**
             * The network address recorded when the response was submitted.
             *
             * @maxLength 64
             */
            submittedIp?: string;
            /**
             * When the question configuration captured by this response was last updated.
             *
             * @format date-time
             */
            configurationUpdatedAt?: string;
          };
          /**
           * Whether the candidate confirmed their criminal record declaration.
           */
          criminalRecordCheckConsent?: boolean;
          /**
           * The name used to confirm the criminal record declaration.
           *
           * @maxLength 256
           */
          criminalRecordCheckConsentName?: string;
          /**
           * The date the criminal record declaration was confirmed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          criminalRecordCheckConsentDate?: string;
          safeInEducation?: {
            /**
             * Whether the candidate is barred from working in education
             */
            barred?: boolean;
            /**
             * The date when the barred status was checked
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            barredCheckDate?: string;
            /**
             * Whether the candidate has a waiver for the barred status
             */
            waiver?: boolean;
            /**
             * Any orders related to the candidate's barred status
             */
            ordersYou?: boolean;
            /**
             * Any orders related to the candidate's barred status for children
             */
            ordersChild?: boolean;
            /**
             * Whether the candidate has any previous safeguarding allegations
             */
            previousSafeguardingAllegations?: boolean;
            /**
             * Whether the candidate has been cautioned
             */
            cautioned?: boolean;
            /**
             * Whether the candidate has a violent offence
             */
            violentOffence?: boolean;
            /**
             * Whether the candidate has lived or worked in another country
             */
            anotherCountry?: boolean;
            /**
             * Whether the candidate has read the Keeping Children Safe in Education document
             */
            readKCSIE?: boolean;
            /**
             * Whether the candidate has read the Disqualification under the Childcare Act guidance
             */
            readChildcareAct?: boolean;
            /**
             * Whether the candidate is prohibited from teaching
             */
            prohibitedFromTeaching?: boolean;
            /**
             * Whether the candidate is known to the police
             */
            knownPolice?: boolean;
            /**
             * Whether the candidate is prohibited from management roles
             */
            prohibitedFromManagement?: boolean;
            /**
             * Whether the candidate has any criminal offences under law
             */
            criminalOffencesLaw?: boolean;
            /**
             * Any additional details regarding the candidate's safe in education status
             */
            safeInEducationDetails?: string;
            safeguardingQuestionaire?: {
              /**
               * What is the primary purpose of safeguarding in a school environment?
               */
              primaryPurposeSafeguarding?:
                | "enhance-academic-performance"
                | "ensure-safe-learning-environment"
                | "promote-healthy-relationships"
                | "facilitate-extracurricular-activities";
              /**
               * Who is typically responsible for coordinating safeguarding efforts within a school?
               */
              typicallyResponsibleCoordinatingSafeguarding?:
                | "janitorial-staff"
                | "parents-association"
                | "school-nurse"
                | "designated-safeguarding-lead";
              /**
               * Which of the following is considered a potential sign of child abuse or neglect?
               */
              signOfAbuse?:
                | "student-excelling"
                | "frequent-extracurricular"
                | "unexplained-injuries"
                | "not-listening";
              /**
               * How should a teacher respond if a student discloses information about potential abuse or harm?
               */
              studentDisclosesInformation?:
                | "ignore-disclosure"
                | "report-disclosure"
                | "share-information"
                | "advise-independent";
              /**
               * What is the purpose of a school's Child Protection Policy?
               */
              schoolsChildProtectionPolicy?:
                | "enforcing-strict-disciplinary-measures"
                | "identifying-troublemakers"
                | "outlining-procedures-for-protecting-students-from-harm"
                | "establishing-academic-achievement-standards";
              /**
               * What does the term 'FGM' stand for in the context of safeguarding?
               */
              fgmDefinition?:
                | "female-general-medicine"
                | "forced-genetic-modification"
                | "female-genital-mutilation"
                | "foundation-gender-equality";
              /**
               * Which of the following is a key principle of online safety for students?
               */
              keyPrincipleOnlineSafetyStudents?:
                | "unlimited-screen-time"
                | "anon-online-interaction"
                | "responsible-digital-citizen"
                | "restrict-edu-sites";
              /**
               * When planning a school trip, what should teachers consider regarding safeguarding?
               */
              teachersConsiderRegardingSafeguarding?:
                | "minimise-supervision"
                | "appropriate-staff-ratios"
                | "exclude-medical-conditions"
                | "allow-roaming";
              /**
               * What is the purpose of a criminal background check for teachers?
               */
              criminalBackgroundCheckTeachers?:
                | "evaluating-teaching-proficiency"
                | "verifying-immigration-status"
                | "assessing-financial-stability"
                | "identifying-criminal-history";
              /**
               * A student confides in you, asking you to keep a secret. What should be your immediate response in the context of safeguarding?
               */
              studentConfides?:
                | "assure-secret-safe"
                | "encourage-share-friends"
                | "explain-no-guarantee"
                | "disclose-to-others";
            };
          };
          socialMedia?: {
            /**
             * The Facebook profile URL of the candidate
             *
             * @maxLength 256
             */
            facebook?: string;
            /**
             * The Instagram profile URL of the candidate
             *
             * @maxLength 256
             */
            instagram?: string;
            /**
             * The Twitter profile URL of the candidate
             *
             * @maxLength 256
             */
            twitter?: string;
            /**
             * The LinkedIn profile URL of the candidate
             *
             * @maxLength 256
             */
            linkedIn?: string;
            /**
             * The TikTok profile URL of the candidate
             *
             * @maxLength 256
             */
            tiktok?: string;
            /**
             * Any other social media profile URLs of the candidate
             *
             * @maxLength 256
             */
            others?: string;
            /**
             * Whether the social media profiles have been checked
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            socialMediaChecked?: string;
            /**
             * The date the candidate's Google search check was completed
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            googleCheckedDate?: string;
          };
          /**
           * Whether the candidate has consented to a safe in education check
           */
          safeInEducationCheckConsent?: boolean;
          /**
           * The name of the person who provided consent for the safe in education check
           *
           * @maxLength 256
           */
          safeInEducationCheckConsentName?: string;
          /**
           * The date when consent for the safe in education check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          safeInEducationCheckConsentDate?: string;
          /**
           * The health record of the individual
           */
          healthRecord?: {
            /**
             * Whether the individual has a physical ability condition
             */
            physicalAbilityCondition?: boolean;
            /**
             * Whether the individual is confused
             */
            confused?: boolean;
            /**
             * Whether the individual has hearing issues
             */
            hearing?: boolean;
            /**
             * Whether the individual has eyesight issues
             */
            eyesight?: boolean;
            /**
             * Whether the individual has depression
             */
            depression?: boolean;
            /**
             * Whether the individual has severe pain
             */
            severePain?: boolean;
            /**
             * Whether the individual has excessive drowsiness
             */
            excessiveDrowsiness?: boolean;
            /**
             * Whether the individual has epilepsy
             */
            epilepsy?: boolean;
            /**
             * Whether the individual has heart issues
             */
            heart?: boolean;
            /**
             * Whether the individual has asthma
             */
            asthma?: boolean;
            /**
             * Whether the individual has mental health issues
             */
            mentalHealth?: boolean;
            /**
             * Whether the individual is an expectant mother
             */
            expectantMother?: boolean;
            /**
             * Whether the individual has allergies
             */
            allergies?: boolean;
            /**
             * Whether the individual is taking medication
             */
            medication?: boolean;
            /**
             * Whether the individual has any health issues
             */
            healthIssues?: boolean;
            /**
             * A description of the individual's health issues
             *
             * @maxLength 2048
             */
            healthIssuesDescription?: string;
            /**
             * Any disability needs of the individual
             *
             * @maxLength 2048
             */
            disabilityNeeds?: string;
            /**
             * Whether the individual has any contagious diseases
             */
            contagiousDiseases?: boolean;
            /**
             * Whether the individual's health affects their ability
             */
            affectAbility?: boolean;
            /**
             * Whether the individual agrees to notify their employers of any changes that would mean they are no longer fit and able to work with children.
             */
            notificationAgree?: boolean;
            /**
             * Whether the individual has opted into the SAS Wellbeing App
             */
            sasOptIn?: boolean;
            /**
             * The individual's signature
             *
             * @maxLength 256
             */
            signature?: string;
            /**
             * The individual's drawn signature in base64url format
             *
             * @maxLength 4096
             */
            drawnSignature?: string;
            /**
             * The date the individual signed the form
             *
             * @format date-time
             */
            signatureDate?: string | null;
            /**
             * Whether the individual has been vaccinated against measles
             */
            vaccinationMeasles?: boolean;
            /**
             * Whether the individual has been vaccinated against mumps
             */
            vaccinationMumps?: boolean;
            /**
             * Whether the individual has been vaccinated against rubella
             */
            vaccinationRubella?: boolean;
            /**
             * Whether the individual has been vaccinated against varicella
             */
            vaccinationVaricella?: boolean;
            /**
             * Whether the individual has been vaccinated against tuberculosis
             */
            vaccinationTuberculosis?: boolean;
            /**
             * Whether the individual has been vaccinated against hepatitis B
             */
            vaccinationHepatitisB?: boolean;
            /**
             * Whether the individual has been vaccinated against hepatitis C
             */
            vaccinationHepatitisC?: boolean;
            /**
             * Whether the individual has been vaccinated against HIV
             */
            vaccinationHIV?: boolean;
            /**
             * Whether the individual has been vaccinated against COVID-19
             */
            vaccinationCovid?: boolean;
            /**
             * Whether the individual has received the 1st dose of the COVID-19 vaccine
             */
            vaccinationCovid1?: boolean;
            /**
             * Whether the individual has received the 2nd dose of the COVID-19 vaccine
             */
            vaccinationCovid2?: boolean;
            /**
             * Whether the individual has received the COVID-19 booster vaccine
             */
            vaccinationCovidBooster?: boolean;
            /**
             * Whether the individual has received the COVID-19 influenza vaccine
             */
            vaccinationCovidInfluenza?: boolean;
            /**
             * Whether the individual has an override for fit to work requirements
             */
            fitToWorkOverride?: boolean;
          };
          /**
           * The candidate's latest submitted health declaration, including the exact questions presented.
           */
          healthDeclaration?: {
            /**
             * The ordered questions included in the question set.
             *
             * @maxItems 100
             */
            questions?: {
              /**
               * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * A stable key used to associate the question with current and historical answers.
               *
               * @minLength 1
               * @maxLength 128
               */
              key?: string;
              /**
               * The question shown to the person completing the form.
               *
               * @minLength 1
               * @maxLength 500
               */
              label?: string;
              /**
               * Optional supporting text displayed with the question.
               *
               * @maxLength 2000
               */
              description?: string;
              /**
               * The type of answer collected for the question.
               */
              type?:
                | "checkbox"
                | "date"
                | "daterange"
                | "dropdown"
                | "number"
                | "signature"
                | "text"
                | "textarea";
              /**
               * Whether an answer is required while the question is visible.
               */
              required?: boolean;
              /**
               * Whether the question is included in new forms. Historical responses retain disabled questions.
               */
              enabled?: boolean;
              /**
               * The display order of the question.
               *
               * @minimum 0
               * @maximum 1000
               */
              order?: number;
              /**
               * Optional rules that determine when the question is shown.
               */
              condition?: {
                /**
                 * Whether any rule or every rule must match before the question is shown.
                 */
                mode?: "any" | "all";
                /**
                 * The comparisons used to determine question visibility.
                 *
                 * @minItems 1
                 * @maxItems 20
                 */
                rules?: {
                  /**
                   * The stable key of the question that controls visibility.
                   *
                   * @minLength 1
                   * @maxLength 128
                   */
                  questionKey?: string;
                  /**
                   * How the controlling answer is compared with the value.
                   */
                  operator?: "equals" | "notEquals";
                  /**
                   * The answer value used to determine visibility.
                   */
                  value?: string | boolean | number | (void | null);
                }[];
              };
              /**
               * The available answers for a dropdown question.
               *
               * @minItems 1
               * @maxItems 100
               */
              options?: {
                /**
                 * The value stored when this option is selected.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                value?: string;
                /**
                 * The option displayed to the person answering the question.
                 *
                 * @minLength 1
                 * @maxLength 200
                 */
                label?: string;
              }[];
              /**
               * Whether this question belongs to the standard template.
               */
              ["default"]?: boolean;
              /**
               * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
               */
              audience?: "candidate" | "staff";
              /**
               * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
               */
              fixedRole?: "fullName" | "signature";
            }[];
            /**
             * The answers recorded for the point-in-time question set.
             *
             * @maxItems 100
             */
            answers?: {
              /**
               * The identifier of the question being answered.
               *
               * @pattern ^[0-9a-f]{24}$
               */
              questionId?: string;
              /**
               * The stable key of the question being answered.
               *
               * @minLength 1
               * @maxLength 128
               */
              questionKey?: string;
              /**
               * The submitted answer to the question.
               */
              value?: string | string | boolean | number | (void | null);
            }[];
            /**
             * When the question set was submitted.
             *
             * @format date-time
             */
            submittedAt?: string;
            /**
             * The network address recorded when the response was submitted.
             *
             * @maxLength 64
             */
            submittedIp?: string;
            /**
             * When the question configuration captured by this response was last updated.
             *
             * @format date-time
             */
            configurationUpdatedAt?: string;
          };
          /**
           * Whether the candidate confirmed their health declaration.
           */
          healthRecordCheckConsent?: boolean;
          /**
           * The name used to confirm the health declaration.
           *
           * @maxLength 256
           */
          healthRecordCheckConsentName?: string;
          /**
           * The date the health declaration was confirmed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          healthRecordCheckConsentDate?: string;
          /**
           * Payment, tax declaration, and student-loan details supplied for a candidate. Access is restricted by candidate and payroll permissions.
           */
          bankDetails?: {
            /**
             * The name on the bank account
             */
            bankAccountName?: string;
            /**
             * The name of the bank
             */
            bankName?: string;
            /**
             * The account number
             */
            accountNumber?: string;
            /**
             * The sort code
             */
            sortCode?: string;
            /**
             * The National Insurance number
             */
            niNumber?: string;
            /**
             * This is my first job since 6 April and since the 6 April I’ve not received payments from any of the following:  Jobseeker’s Allowance -  Employment and Support Allowance  - Incapacity Benefit
             */
            jobOp1?: boolean;
            /**
             * Since 6 April I have had another job. And/or since the 6 April I have received payments from any of the following:  Jobseeker’s Allowance - Employment and Support Allowance -  Incapacity Benefit
             */
            jobOp2?: boolean;
            /**
             * Choose this statement if:  you have another job and/or  -  you’re in receipt of a State, Works or Private Pension
             */
            jobOp3?: boolean;
            studentLoan?: {
              /**
               * Boolean flag for if candidate has student loan
               */
              studentLoan?: boolean;
              /**
               * Boolean flag for if candidate is on plan 1
               */
              plan1?: boolean;
              /**
               * Boolean flag for if candidate is on plan 2
               */
              plan2?: boolean;
              /**
               * Boolean flag for if candidate is on plan 4
               */
              plan4?: boolean;
              /**
               * Boolean flag for if candidate has postgrad loan
               */
              postGradLoan?: boolean;
            };
          };
          /**
           * Whether the candidate has confirmed their details are correct.
           */
          declarationCheckConsent?: boolean;
          /**
           * The name of the person who confirmed their details are correct.
           *
           * @maxLength 256
           */
          declarationCheckConsentName?: string;
          /**
           * The date when consent for the declaration check was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          declarationCheckConsentDate?: string;
          /**
           * Whether the candidate has consented to a contract for service
           */
          contractForServiceConsent?: boolean;
          /**
           * The name of the person who provided consent for the contract for service
           *
           * @maxLength 256
           */
          contractForServiceName?: string;
          /**
           * The date when consent for the contract for service was provided
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          contractForServiceDate?: string;
          /**
           * The candidate has completed their onboarding form.
           */
          confirmed?: boolean;
          /**
           * The ID of the user who created the candidate record.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          createdBy?: string;
          /**
           * The ID of the customer associated with the candidate.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          customerId?: string;
          /**
           * The migration that created this candidate
           *
           * @pattern ^[0-9a-f]{24}$
           */
          migrationId?: string;
          /**
           * Whether this migrated candidate has been approved
           */
          migrationApproval?: "pending" | "approved" | "rejected";
          /**
           * The user assigned to the candidate. If expanded, a user object else the user ID.
           */
          assignee?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * The ID of the current booking for the candidate.
           */
          currentBooking?: string[];
          /**
           * The URL of the candidate's profile photo.
           *
           * @maxLength 256
           */
          profilePhoto?: string;
          /**
           * The pay type for the candidate.
           */
          payType?: "PAYE" | "Umbrella" | "Limited Company" | "Not Applicable";
          /**
           * The pay interval for the candidate.
           */
          payInterval?:
            | "Monthly"
            | "Weekly"
            | "Weekly Pay in Arrears"
            | "Bi-Weekly"
            | any
            | null;
          /**
           * The pay rate for the candidate.
           */
          payRate?: string;
          /**
           * A custom ID for the candidate.
           *
           * @maxLength 256
           */
          customId?: string;
          /**
           * A company-defined identifier used to match the candidate with external records.
           *
           * @maxLength 256
           */
          _internalId?: string;
          /**
           * The date when the candidate's status was last changed.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          statusChangeDate?: string;
          /**
           * Whether the candidate has opted out of communications.
           */
          optOut?: boolean;
          /**
           * The TRN number for the candidate.
           *
           * @maxLength 256
           */
          trnNumber?: string;
          /**
           * The date when the TRN number was last checked.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          trnNumberCheckedDate?: string;
          /**
           * Any notes related to the TRN number.
           *
           * @maxLength 1024
           */
          trnNumberNotes?: string;
          /**
           * The date when the candidate's employment is set to start.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          employmentStartDate?: string;
          /**
           * Whether the candidate has confirmed their Early Years Workforce status.
           */
          ewcConfirmation?: boolean;
          /**
           * The share code for the candidate's Home Office application.
           *
           * @maxLength 256
           */
          homeOfficeShareCode?: string;
          /**
           * Whether the candidate has been archived.
           */
          archived?: boolean;
          /**
           * Whether the candidate is using an umbrella company.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          umbrella?: string;
          /**
           * ID of the PAYE umbrella company.
           *
           * @pattern ^[0-9a-f]{24}$
           */
          payeUmbrella?: string;
          /**
           * The name of the umbrella company the candidate is using.
           *
           * @maxLength 256
           */
          umbrellaCompanyName?: string;
          /**
           * The code of the umbrella company the candidate is using.
           *
           * @maxLength 256
           */
          umbrellaCompanyCode?: string;
          /**
           * The transport method for the candidate.
           */
          transport?:
            | "Drives Own Vehicle"
            | "Use Public Transport"
            | "Access to Another Vehicle"
            | "Bicycle"
            | "No Access"
            | "Other";
          /**
           * Any important notes related to the candidate.
           *
           * @maxLength 1024
           */
          importantNotes?: string;
          /**
           * The date when the candidate was last updated.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          candidateLastUpdated?: string;
          /**
           * The source from which the candidate was obtained.
           */
          source?:
            | (
                | "Company Website"
                | "iCan Teach UK Website"
                | "Apply to Education"
                | "CV Library - CV Search"
                | "CV Library Advert"
                | "CV Library - Watchdog"
                | "Guardian- CV Search"
                | "Guardian Advert"
                | "Indeed - CV Search"
                | "Indeed Advert"
                | "Reed - CV Search"
                | "Reed Advert"
                | "Reed \u2013 Watchdog"
                | "SENploy - Advert"
                | "Total Jobs"
                | "Other Job Board"
                | "Google"
                | "LinkedIn"
                | "Linkedin Recruiter"
                | "LinkedIn Advert"
                | "Referral - School"
                | "Referral - Candidate"
                | "UK University"
                | "Irish University"
                | "Canadian University"
                | "Marketing Campaign"
                | "Woo"
                | "Job Show"
                | "Recruitment Fair"
                | "Career Fair"
                | "Volcanic"
                | "Broadbean"
                | "Headhunt"
                | "Referral"
                | "Direct"
                | "Other"
              )
            | (
                | "Company Website"
                | "Direct Call"
                | "CV Library Watchdog"
                | "CV Library Advert"
                | "CV Library CV Search"
                | "Reed Watchdog"
                | "Reed Advert"
                | "Reed CV Search"
                | "Indeed Watchdog"
                | "Indeed Advert"
                | "Indeed CV Search"
                | "Job Centre"
                | "Returning Candidate"
                | "Referral Candidate"
                | "Referral Client"
                | "Training Provider"
                | "Recruitment Fair"
                | "University/College"
              );
          /**
           * Any other source from which the candidate was obtained.
           *
           * @maxLength 256
           */
          sourceOther?: string;
          /**
           * Whether the candidate has given consent for online checks.
           */
          onlineCheckConsent?: boolean;
          /**
           * The title of the candidate.
           */
          title?:
            | "Mr"
            | "Mrs"
            | "Ms"
            | "Miss"
            | "Dr"
            | "Mx"
            | "Lady"
            | "Lord"
            | "Other";
          /**
           * The pronoun used by the candidate.
           */
          pronoun?:
            | "She/Her/Hers"
            | "He/Him/His"
            | "They/Them/Theirs"
            | "Ze/Hir/Hirs"
            | "Ze/Zir/Zirs"
            | "Other"
            | "Prefer not to say";
          /**
           * The date when the candidate was last updated.
           *
           * @format date-time
           */
          updatedAt?: string;
          /**
           * The date when the candidate was last contacted.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          lastContactedDate?: string;
          /**
           * A short, prominent note shown with the candidate to communicate important context.
           */
          highlight?: string;
          /**
           * The pay scale for the candidate's teaching position.
           *
           * @maxLength 256
           */
          teacherPayScale?: string;
          /**
           * Whether the candidate has a P45.
           */
          p45?: boolean;
          /**
           * The type of role the candidate is applying for.
           */
          roleType?:
            | "Senior Leadership Positions"
            | "Teaching Roles"
            | "School Support Positions";
          /**
           * The Right to Work verification status for the candidate.
           *
           * @maxLength 256
           */
          rtwVerification?: string;
          /**
           * Any notes related to the Right to Work verification status for the candidate.
           *
           * @maxLength 1024
           */
          rtwVerificationNote?: string;
          /**
           * The Right to Work verification status for the candidate.
           *
           * @maxLength 256
           */
          rtwVerificationStatus?: string;
          /**
           * The location where the candidate desires to work.
           *
           * @maxLength 256
           */
          desiredLocation?: string;
          /**
           * The user responsible for compliance checks on the candidate. If expanded, a user object else the user ID.
           */
          complianceUser?:
            | string
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              };
          /**
           * Users watching this candidate. If expanded, an array of user objects, else an array of user IDs.
           */
          watchers?:
            | string[]
            | {
                /**
                 * The unique identifier for the user
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The email address of the user
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The first name of the user
                 *
                 * @maxLength 128
                 */
                firstName?: string;
                /**
                 * The last name of the user
                 *
                 * @maxLength 128
                 */
                lastName?: string;
                /**
                 * The username of the user
                 *
                 * @maxLength 128
                 */
                username?: string;
                /**
                 * The local education authority of the user
                 *
                 * @maxLength 128
                 */
                lea?: string;
                /**
                 * Array of client IDs associated with the user
                 */
                clients?: string[];
                /**
                 * The position or job title of the user
                 *
                 * @maxLength 128
                 */
                position?: string;
                /**
                 * Array of permissions assigned to the user
                 */
                permissions?: number[];
                /**
                 * The activation code for the user
                 *
                 * @maxLength 256
                 */
                activationCode?: string;
                /**
                 * The unique identifier of the company the user belongs to.
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                customer?: string;
                /**
                 * Indicates whether the user account is disabled
                 */
                disabled?: boolean;
                /**
                 * Array of commission thresholds for the user
                 */
                commissionThreshold?: {
                  min?: number | string;
                  commission?: number | string;
                  type?: "Fixed" | "Variable";
                  weekly?: boolean;
                }[];
                /**
                 * Branches the user can access. If expanded, branch objects are returned; otherwise, branch IDs are returned.
                 */
                branches?:
                  | string[]
                  | {
                      /**
                       * The unique identifier for the branch
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The name of the branch
                       *
                       * @maxLength 256
                       */
                      name?: string;
                      /**
                       * The code of the branch
                       *
                       * @maxLength 32
                       */
                      code?: string;
                      /**
                       * The contact email of the branch
                       *
                       * @maxLength 256
                       * @format email
                       */
                      contactEmail?: string;
                      /**
                       * The contact phone number of the branch
                       *
                       * @maxLength 32
                       */
                      contactTelephone?: string;
                      /**
                       * The contact website of the branch
                       *
                       * @maxLength 512
                       */
                      contactWebsite?: string;
                      /**
                       * The accent color for the customer that the branch belongs to
                       *
                       * @maxLength 7
                       */
                      customerAccentColor?: string;
                      /**
                       * The address of the branch
                       */
                      address?: {
                        /**
                         * The first line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine1?: string;
                        /**
                         * The second line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine2?: string;
                        /**
                         * The third line of the address
                         *
                         * @maxLength 256
                         */
                        addressLine3?: string;
                        /**
                         * The city of the address
                         *
                         * @maxLength 256
                         */
                        city?: string;
                        /**
                         * The county of the address
                         *
                         * @maxLength 256
                         */
                        county?: string;
                        /**
                         * The borough of the address
                         *
                         * @maxLength 256
                         */
                        borough?: string;
                        /**
                         * The postcode of the address
                         *
                         * @maxLength 10
                         */
                        postcode?: string;
                        /**
                         * The country of the address
                         *
                         * @maxLength 256
                         */
                        country?: string;
                        /**
                         * The latitude of the address
                         *
                         * @minimum -90
                         * @maximum 90
                         */
                        lat?: number;
                        /**
                         * The longitude of the address
                         *
                         * @minimum -180
                         * @maximum 180
                         */
                        lng?: number;
                      };
                      /**
                       * Indicates whether the branch is archived
                       */
                      archived?: boolean;
                      /**
                       * The number of users assigned to the branch. Only returned when explicitly selected.
                       *
                       * @minimum 0
                       */
                      userCount?: number;
                    }[];
                /**
                 * Indicates whether to display the onboarding process for the user
                 */
                displayOnboarding?: boolean;
                /**
                 * A custom identifier for the user
                 *
                 * @maxLength 256
                 */
                _customId?: string;
                /**
                 * The signature of the user in base64 format
                 *
                 * @maxLength 2048
                 */
                signature?: string | null;
                /**
                 * The phone number of the user
                 *
                 * @maxLength 32
                 */
                phone?: string;
                /**
                 * Indicates whether the user is the default candidate watcher
                 */
                defaultCandidateWatcher?: boolean;
                /**
                 * The user's default task workspace
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                defaultTaskWorkspace?: string | null;
                /**
                 * Indicates whether the user has team admin privileges
                 */
                teamAdmin?: boolean;
                /**
                 * The contact record for this user. If expanded, a contact object else the contact record ID.
                 */
                contact?:
                  | string
                  | {
                      /**
                       * The unique identifier of the contact
                       *
                       * @pattern ^[0-9a-f]{24}$
                       */
                      id?: string;
                      /**
                       * The forename of the contact
                       *
                       * @maxLength 128
                       */
                      forename?: string;
                      /**
                       * The surname of the contact
                       *
                       * @maxLength 128
                       */
                      surname?: string;
                      /**
                       * The email address of the contact
                       *
                       * @format email
                       */
                      email?: string;
                      /**
                       * The telephone number of the contact
                       *
                       * @maxLength 32
                       */
                      telephone?: string;
                      /**
                       * The mobile number of the contact
                       *
                       * @maxLength 32
                       */
                      mobile?: string;
                      /**
                       * The extension number of the contact
                       *
                       * @maxLength 8
                       */
                      extension?: string;
                    };
                /**
                 * The date and time the user account was created
                 */
                createdAt?: string;
                /**
                 * Evertime details for this user
                 */
                evertime?: {
                  /**
                   * The Evertime ConsultantId used for this user when exporting payment runs
                   *
                   * @maxLength 256
                   */
                  id?: string;
                };
                /**
                 * Timesheet Portal details for this user
                 */
                timesheetPortal?: {
                  /**
                   * The Timesheet Portal administrator GUID used when exporting payment runs
                   *
                   * @format uuid
                   */
                  id?: string;
                  /**
                   * The Timesheet Portal administrator code
                   *
                   * @maxLength 256
                   */
                  code?: string;
                };
              }[];
          /**
           * Tags associated with this candidate. If expanded, an array of tag reference objects, else an array of IDs.
           */
          tags?:
            | string[]
            | {
                /**
                 * The unique identifier of the tag
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The label displayed wherever this tag is applied.
                 *
                 * @minLength 1
                 * @maxLength 128
                 */
                name?: string;
                /**
                 * The tag display colour as a hexadecimal value, for example #2563EB.
                 *
                 * @maxLength 32
                 */
                color?: string | null;
              }[];
          /**
           * Up to five documents attached to the candidate when expanded. Use the candidate documents endpoint to retrieve the complete paginated list.
           *
           * @maxItems 5
           */
          documents?: {
            /**
             * The unique identifier of the document
             *
             * @pattern ^[0-9a-f]{24}$
             */
            id?: string;
            /**
             * The name of the document
             *
             * @maxLength 512
             */
            name?: string;
            /**
             * The file reference used to retrieve the document
             *
             * @maxLength 2048
             */
            reference?: string;
            /**
             * The file's media type
             *
             * @maxLength 256
             */
            fileType?: string;
            /**
             * The area of the platform the document belongs to
             *
             * @maxLength 256
             */
            type?: string;
            /**
             * The configured category of the document
             *
             * @maxLength 256
             */
            docType?: string;
            /**
             * The document's expiry date, when applicable
             *
             * @maxLength 64
             */
            expiry?: string;
            /**
             * The document's issued date, when applicable
             *
             * @maxLength 64
             */
            issued?: string;
            /**
             * Whether the document has been archived
             */
            documentArchived?: boolean;
            /**
             * The date the document was uploaded
             *
             * @format date-time
             */
            createdAt?: string;
          }[];
          /**
           * The total number of documents attached to the candidate.
           *
           * @minimum 0
           */
          documentsCount?: number;
          /**
           * The locations where the candidate desires to work.
           */
          desiredLocationMultiple?: string[];
          /**
           * Daily availability for the requested period. Returned by the availability view only.
           *
           * @maxItems 31
           */
          availabilityDays?: {
            /**
             * Calendar date in YYYY-MM-DD format.
             *
             * @maxLength 10
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date?: string;
            /**
             * Availability or booking state for this date.
             */
            status?:
              | "available"
              | "unavailable"
              | "booked"
              | "halfDay"
              | "awaiting"
              | "unknown";
            /**
             * Recorded availability, or null when unrecorded.
             */
            available?: boolean | null;
            /**
             * Morning or afternoon availability.
             */
            partial?: "AM" | "PM" | any | null;
            /**
             * Additional availability information.
             *
             * @maxLength 4096
             */
            note?: string | null;
            /**
             * Active bookings on this date.
             *
             * @maxItems 100
             */
            bookings?: {
              /**
               * The unique identifier of the resource
               *
               * @pattern ^[0-9a-f]{24}$
               */
              id?: string;
              /**
               * @maxLength 512
               */
              clientName?: string;
              /**
               * @maxLength 512
               */
              role?: string;
              /**
               * @maxLength 32
               */
              timeDesignation?: string;
            }[];
          }[];
          /**
           * The date when the candidate's availability was last checked.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          availabilityLastChecked?: string;
          /**
           * Whether the candidate is subject to notice period.
           */
          subjectToNotice?: "Unsure" | "Yes" | "No";
          /**
           * The subsidiary subjects the candidate is qualified to teach.
           */
          subsidiarySubject?: string[];
          /**
           * The candidate's preferred employment type.
           */
          employmentPreference?: (
            | "Day To Day"
            | "Long Term"
            | "Fixed Term"
            | "Perm"
          )[];
          /**
           * Any other names the candidate is known by.
           *
           * @maxLength 256
           */
          otherNames?: string;
          /**
           * The name of the candidate's limited company, if applicable.
           *
           * @maxLength 256
           */
          ltdCompany?: string;
          /**
           * Whether the candidate's employment has been verified.
           */
          employmentVerified?: boolean;
          /**
           * The DBS verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSVerification?: string;
          /**
           * Any notes related to the DBS verification status for the candidate.
           *
           * @maxLength 1024
           */
          candidateDBSVerificationNote?: string;
          /**
           * The DBS verification status for the candidate.
           */
          candidateDBSVerificationStatus?: "Cleared" | "Alerts";
          /**
           * The DBS check verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSCheckVerification?: string;
          /**
           * Any notes related to the DBS check verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSCheckVerificationNote?: string;
          /**
           * The DBS check verification status for the candidate.
           */
          candidateDBSCheckVerificationStatus?: "Cleared" | "Alerts";
          /**
           * Any notes related to the DBS update service verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateDBSUpdateServiceVerificationNote?: string;
          /**
           * The DBS update service verification status for the candidate.
           */
          candidateDBSUpdateServiceVerificationStatus?: "Cleared" | "Alerts";
          /**
           * Any notes related to the TRN verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateTRNVerificationNote?: string;
          /**
           * The TRN verification status for the candidate.
           */
          candidateTRNVerificationStatus?:
            | "Completed Induction"
            | "Not Yet Completed Induction"
            | "Induction Exempt"
            | "ALERTS - Failed Induction"
            | "Induction Not Required";
          /**
           * The health and safety verification status for the candidate.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          candidateHealthSafetyVerification?: string;
          /**
           * Any notes related to the health and safety verification status for the candidate.
           *
           * @maxLength 150
           */
          candidateHealthSafetyVerificationNote?: string;
          /**
           * The health and safety verification status for the candidate.
           */
          candidateHealthSafetyVerificationStatus?: "Cleared" | "Alerts";
          /**
           * The onboarding session for the candidate. If expanded, an onboarding session object else the onboarding session ID.
           */
          onboardingSession?:
            | string
            | {
                /**
                 * Unique identifier for the onboarding session
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The URL for the onboarding session
                 *
                 * @format uri
                 */
                url?: string;
                /**
                 * The frozen health declaration configuration for this onboarding session.
                 */
                healthDeclarationConfig?: {
                  /**
                   * The ordered questions included in the question set.
                   *
                   * @maxItems 100
                   */
                  questions?: {
                    /**
                     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id?: string;
                    /**
                     * A stable key used to associate the question with current and historical answers.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    key?: string;
                    /**
                     * The question shown to the person completing the form.
                     *
                     * @minLength 1
                     * @maxLength 500
                     */
                    label?: string;
                    /**
                     * Optional supporting text displayed with the question.
                     *
                     * @maxLength 2000
                     */
                    description?: string;
                    /**
                     * The type of answer collected for the question.
                     */
                    type?:
                      | "checkbox"
                      | "date"
                      | "daterange"
                      | "dropdown"
                      | "number"
                      | "signature"
                      | "text"
                      | "textarea";
                    /**
                     * Whether an answer is required while the question is visible.
                     */
                    required?: boolean;
                    /**
                     * Whether the question is included in new forms. Historical responses retain disabled questions.
                     */
                    enabled?: boolean;
                    /**
                     * The display order of the question.
                     *
                     * @minimum 0
                     * @maximum 1000
                     */
                    order?: number;
                    /**
                     * Optional rules that determine when the question is shown.
                     */
                    condition?: {
                      /**
                       * Whether any rule or every rule must match before the question is shown.
                       */
                      mode?: "any" | "all";
                      /**
                       * The comparisons used to determine question visibility.
                       *
                       * @minItems 1
                       * @maxItems 20
                       */
                      rules?: {
                        /**
                         * The stable key of the question that controls visibility.
                         *
                         * @minLength 1
                         * @maxLength 128
                         */
                        questionKey?: string;
                        /**
                         * How the controlling answer is compared with the value.
                         */
                        operator?: "equals" | "notEquals";
                        /**
                         * The answer value used to determine visibility.
                         */
                        value?: string | boolean | number | (void | null);
                      }[];
                    };
                    /**
                     * The available answers for a dropdown question.
                     *
                     * @minItems 1
                     * @maxItems 100
                     */
                    options?: {
                      /**
                       * The value stored when this option is selected.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      value?: string;
                      /**
                       * The option displayed to the person answering the question.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      label?: string;
                    }[];
                    /**
                     * Whether this question belongs to the standard template.
                     */
                    ["default"]?: boolean;
                    /**
                     * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                     */
                    audience?: "candidate" | "staff";
                    /**
                     * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                     */
                    fixedRole?: "fullName" | "signature";
                  }[];
                  /**
                   * When this question configuration was last updated.
                   *
                   * @format date-time
                   */
                  updatedAt?: string;
                };
                /**
                 * The frozen Criminal Record & Security Checks configuration for this onboarding session.
                 */
                criminalRecordConfig?: {
                  /**
                   * The ordered questions included in the question set.
                   *
                   * @maxItems 100
                   */
                  questions?: {
                    /**
                     * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
                     *
                     * @pattern ^[0-9a-f]{24}$
                     */
                    id?: string;
                    /**
                     * A stable key used to associate the question with current and historical answers.
                     *
                     * @minLength 1
                     * @maxLength 128
                     */
                    key?: string;
                    /**
                     * The question shown to the person completing the form.
                     *
                     * @minLength 1
                     * @maxLength 500
                     */
                    label?: string;
                    /**
                     * Optional supporting text displayed with the question.
                     *
                     * @maxLength 2000
                     */
                    description?: string;
                    /**
                     * The type of answer collected for the question.
                     */
                    type?:
                      | "checkbox"
                      | "date"
                      | "daterange"
                      | "dropdown"
                      | "number"
                      | "signature"
                      | "text"
                      | "textarea";
                    /**
                     * Whether an answer is required while the question is visible.
                     */
                    required?: boolean;
                    /**
                     * Whether the question is included in new forms. Historical responses retain disabled questions.
                     */
                    enabled?: boolean;
                    /**
                     * The display order of the question.
                     *
                     * @minimum 0
                     * @maximum 1000
                     */
                    order?: number;
                    /**
                     * Optional rules that determine when the question is shown.
                     */
                    condition?: {
                      /**
                       * Whether any rule or every rule must match before the question is shown.
                       */
                      mode?: "any" | "all";
                      /**
                       * The comparisons used to determine question visibility.
                       *
                       * @minItems 1
                       * @maxItems 20
                       */
                      rules?: {
                        /**
                         * The stable key of the question that controls visibility.
                         *
                         * @minLength 1
                         * @maxLength 128
                         */
                        questionKey?: string;
                        /**
                         * How the controlling answer is compared with the value.
                         */
                        operator?: "equals" | "notEquals";
                        /**
                         * The answer value used to determine visibility.
                         */
                        value?: string | boolean | number | (void | null);
                      }[];
                    };
                    /**
                     * The available answers for a dropdown question.
                     *
                     * @minItems 1
                     * @maxItems 100
                     */
                    options?: {
                      /**
                       * The value stored when this option is selected.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      value?: string;
                      /**
                       * The option displayed to the person answering the question.
                       *
                       * @minLength 1
                       * @maxLength 200
                       */
                      label?: string;
                    }[];
                    /**
                     * Whether this question belongs to the standard template.
                     */
                    ["default"]?: boolean;
                    /**
                     * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
                     */
                    audience?: "candidate" | "staff";
                    /**
                     * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
                     */
                    fixedRole?: "fullName" | "signature";
                  }[];
                  /**
                   * When this question configuration was last updated.
                   *
                   * @format date-time
                   */
                  updatedAt?: string;
                };
              };
          /**
           * The contact record for this candidate. If expanded, a contact object else the contact record ID.
           */
          contact?:
            | string
            | {
                /**
                 * The unique identifier of the contact
                 *
                 * @pattern ^[0-9a-f]{24}$
                 */
                id?: string;
                /**
                 * The forename of the contact
                 *
                 * @maxLength 128
                 */
                forename?: string;
                /**
                 * The surname of the contact
                 *
                 * @maxLength 128
                 */
                surname?: string;
                /**
                 * The email address of the contact
                 *
                 * @format email
                 */
                email?: string;
                /**
                 * The telephone number of the contact
                 *
                 * @maxLength 32
                 */
                telephone?: string;
                /**
                 * The mobile number of the contact
                 *
                 * @maxLength 32
                 */
                mobile?: string;
                /**
                 * The extension number of the contact
                 *
                 * @maxLength 8
                 */
                extension?: string;
              };
          /**
           * Whether the candidate has opted out of 48 hour communications.
           */
          optOut48Hours?: boolean;
          /**
           * Whether the candidate has consented to REC audit checks.
           */
          recAuditConsent?: boolean | null;
          /**
           * The date the candidate's REC audit consent was recorded.
           *
           * @pattern ^\d{4}-\d{2}-\d{2}$
           */
          recAuditConsentDate?: string;
          /**
           * The method through which REC audit consent was captured.
           *
           * @maxLength 256
           */
          recAuditConsentMethod?: string;
          /**
           * The date when the candidate record was created.
           *
           * @format date-time
           */
          createdAt?: string;
          /**
           * The mobile app link status for the candidate.
           */
          candidateMobileAppStatus?:
            | "Unlinked"
            | "Scheduled Unlink"
            | "Linked"
            | "Invited"
            | "Not Invited";
          /**
           * Evertime details for this candidate
           */
          evertime?: {
            /**
             * The Evertime CandidateId used for this candidate when exporting payment runs
             *
             * @maxLength 256
             */
            id?: string;
            /**
             * Whether the matched Evertime candidate's NI number also matches the one held in Edun
             */
            niNumberMatch?: boolean;
          };
          /**
           * Search relevance relative to the strongest result, where 100 is the strongest match.
           *
           * @minimum 0
           * @maximum 100
           */
          relevanceScore?: number;
          /**
           * Candidate fields or attached document excerpts that explain why the candidate matched.
           *
           * @maxItems 5
           */
          matchReasons?: {
            /**
             * Whether the matching information came from the candidate record or an attached document.
             */
            source?: "candidate" | "document";
            /**
             * The candidate or document information that matched the search.
             *
             * @maxLength 256
             */
            field?: string;
            /**
             * A short excerpt showing why the result matched.
             *
             * @maxLength 1000
             */
            text?: string;
            /**
             * The exact terms within the content that matched the search, including synonym matches.
             *
             * @maxItems 25
             */
            matchedTerms?: string[];
            /**
             * The name of the attached document when the match came from a document.
             *
             * @maxLength 512
             */
            documentName?: string | null;
            /**
             * The unique identifier of the attached document when the match came from a document.
             *
             * @pattern ^[0-9a-f]{24}$
             */
            documentId?: string | null;
          }[];
          /**
           * The candidate's distance in miles from the requested location. Present only when location filtering is used.
           *
           * @minimum 0
           */
          distance?: number;
          /**
           * Timesheet Portal details for this candidate
           */
          timesheetPortal?: {
            /**
             * The Timesheet Portal contractor GUID used when exporting payment runs
             *
             * @format uuid
             */
            id?: string;
            /**
             * The Timesheet Portal contractor code
             *
             * @maxLength 256
             */
            code?: string;
          };
          /**
           * Invite to candidate to link their mobile app.
           */
          mobileAppLinkInvite?: {
            /**
             * Boolean status of whether the link invite is being used
             *
             * @default false
             */
            isUsed?: boolean;
            /**
             * Creation date of the invite
             *
             * @format date-time
             * @default 2026-10-30T13:39:09.027Z
             */
            createdAt?: string;
            /**
             * ID of candidate using this link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            usedBy?: string;
            /**
             * Date of when link used
             *
             * @format date-time
             */
            usedAt?: string;
            /**
             * If unlinked, ID of user that unlinked this candidate's link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            unlinkedBy?: string;
            /**
             * If unlinked, date of when this link was unlinked
             *
             * @format date-time
             */
            unlinkedAt?: string;
            /**
             * If deleted, ID of user that deleted this candidate's link
             *
             * @pattern ^[0-9a-f]{24}$
             */
            deletedBy?: string;
            /**
             * If deleted, date of when this link was deleted
             *
             * @format date-time
             */
            deletedAt?: string;
            /**
             * Current status of this mobile app link
             */
            status?:
              | "Sent"
              | "Linked"
              | "Unlinked"
              | "ScheduledUnlink"
              | "Not Sent"
              | "Rescinded";
          };
        }
      | (void | null);
    /**
     * The recipient name captured when the request was issued.
     *
     * @minLength 1
     * @maxLength 256
     */
    recipientName?: string | null;
    /**
     * The recipient email captured when the request was issued.
     *
     * @maxLength 320
     * @format email
     */
    recipientEmail?: string | null;
    /**
     * The frozen questions and canonical submitted answers.
     */
    response?: {
      /**
       * The ordered questions included in the question set.
       *
       * @maxItems 100
       */
      questions?: {
        /**
         * The unique question identifier. Omit it when adding a question; an identifier will be assigned automatically.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        id?: string;
        /**
         * A stable key used to associate the question with current and historical answers.
         *
         * @minLength 1
         * @maxLength 128
         */
        key?: string;
        /**
         * The question shown to the person completing the form.
         *
         * @minLength 1
         * @maxLength 500
         */
        label?: string;
        /**
         * Optional supporting text displayed with the question.
         *
         * @maxLength 2000
         */
        description?: string;
        /**
         * The type of answer collected for the question.
         */
        type?:
          | "checkbox"
          | "date"
          | "daterange"
          | "dropdown"
          | "number"
          | "signature"
          | "text"
          | "textarea";
        /**
         * Whether an answer is required while the question is visible.
         */
        required?: boolean;
        /**
         * Whether the question is included in new forms. Historical responses retain disabled questions.
         */
        enabled?: boolean;
        /**
         * The display order of the question.
         *
         * @minimum 0
         * @maximum 1000
         */
        order?: number;
        /**
         * Optional rules that determine when the question is shown.
         */
        condition?: {
          /**
           * Whether any rule or every rule must match before the question is shown.
           */
          mode?: "any" | "all";
          /**
           * The comparisons used to determine question visibility.
           *
           * @minItems 1
           * @maxItems 20
           */
          rules?: {
            /**
             * The stable key of the question that controls visibility.
             *
             * @minLength 1
             * @maxLength 128
             */
            questionKey?: string;
            /**
             * How the controlling answer is compared with the value.
             */
            operator?: "equals" | "notEquals";
            /**
             * The answer value used to determine visibility.
             */
            value?: string | boolean | number | (void | null);
          }[];
        };
        /**
         * The available answers for a dropdown question.
         *
         * @minItems 1
         * @maxItems 100
         */
        options?: {
          /**
           * The value stored when this option is selected.
           *
           * @minLength 1
           * @maxLength 200
           */
          value?: string;
          /**
           * The option displayed to the person answering the question.
           *
           * @minLength 1
           * @maxLength 200
           */
          label?: string;
        }[];
        /**
         * Whether this question belongs to the standard template.
         */
        ["default"]?: boolean;
        /**
         * Who is expected to answer the question. When omitted, the question may be answered through both candidate and staff workflows.
         */
        audience?: "candidate" | "staff";
        /**
         * The candidate attestation value represented by this protected question. Staff submissions cannot replace these answers.
         */
        fixedRole?: "fullName" | "signature";
      }[];
      /**
       * The answers recorded for the point-in-time question set.
       *
       * @maxItems 100
       */
      answers?: {
        /**
         * The identifier of the question being answered.
         *
         * @pattern ^[0-9a-f]{24}$
         */
        questionId?: string;
        /**
         * The stable key of the question being answered.
         *
         * @minLength 1
         * @maxLength 128
         */
        questionKey?: string;
        /**
         * The submitted answer to the question.
         */
        value?: string | string | boolean | number | (void | null);
      }[];
      /**
       * When the question set was submitted.
       *
       * @format date-time
       */
      submittedAt?: string;
      /**
       * The network address recorded when the response was submitted.
       *
       * @maxLength 64
       */
      submittedIp?: string;
      /**
       * When the question configuration captured by this response was last updated.
       *
       * @format date-time
       */
      configurationUpdatedAt?: string;
    };
    /**
     * When the response was submitted.
     *
     * @format date-time
     */
    submittedAt?: string;
    /**
     * When the response record was created.
     *
     * @format date-time
     */
    createdAt?: string;
  };
};

export type GetFormResponseVariables = {
  pathParams: GetFormResponsePathParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetFormResponse = (
  variables: GetFormResponseVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetFormResponseResponse,
    GetFormResponseError,
    undefined,
    {},
    {},
    GetFormResponsePathParams
  >({
    url: "/form-responses/{formResponseId}",
    method: "get",
    ...variables,
    signal,
  });

export function getFormResponseQuery(variables: GetFormResponseVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetFormResponseResponse>;
};

export function getFormResponseQuery(
  variables: GetFormResponseVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetFormResponseResponse>)
    | reactQuery.SkipToken;
};

export function getFormResponseQuery(
  variables: GetFormResponseVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/form-responses/{formResponseId}",
      operationId: "getFormResponse",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetFormResponse(variables, signal),
  };
}

export const useSuspenseGetFormResponse = <TData = GetFormResponseResponse,>(
  variables: GetFormResponseVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetFormResponseResponse,
      GetFormResponseError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetFormResponseResponse,
    GetFormResponseError,
    TData
  >({
    ...getFormResponseQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetFormResponse = <TData = GetFormResponseResponse,>(
  variables: GetFormResponseVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetFormResponseResponse,
      GetFormResponseError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetFormResponseResponse,
    GetFormResponseError,
    TData
  >({
    ...getFormResponseQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetFormReportPathParams = {
  /**
   * The form identifier.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  formId: string;
};

export type GetFormReportQueryParams = {
  /**
   * Filters applied to report requests and responses.
   */
  filter?: {
    /**
     * Include requests issued within this absolute or relative date range.
     */
    createdAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
  };
};

export type GetFormReportError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetFormReportResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * The form definition represented by this report.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    formId?: string;
    /**
     * When the report was generated.
     *
     * @format date-time
     */
    generatedAt?: string;
    funnel?: {
      /**
       * @minimum 0
       */
      issued?: number;
      /**
       * @minimum 0
       */
      inProgress?: number;
      /**
       * @minimum 0
       */
      submitted?: number;
      /**
       * @minimum 0
       */
      revoked?: number;
      /**
       * @minimum 0
       */
      expired?: number;
      /**
       * @minimum 0
       * @maximum 100
       */
      completionRate?: number;
      /**
       * @minimum 0
       */
      medianCompletionSeconds?: number | null;
    };
    /**
     * @maxItems 100
     */
    questions?: (
      | {
          /**
           * The stable key used to group historical question responses.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * The question wording shown in the report.
           *
           * @minLength 1
           * @maxLength 512
           */
          label?: string;
          /**
           * The number of submitted responses containing an answer.
           *
           * @minimum 0
           */
          answered?: number;
          /**
           * The number of submitted responses without an answer.
           *
           * @minimum 0
           */
          missing?: number;
          type?: "checkbox";
          /**
           * @minimum 0
           */
          yes?: number;
          /**
           * @minimum 0
           */
          no?: number;
          /**
           * @minimum 0
           * @maximum 100
           */
          yesPercentage?: number;
          /**
           * @minimum 0
           * @maximum 100
           */
          noPercentage?: number;
        }
      | {
          /**
           * The stable key used to group historical question responses.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * The question wording shown in the report.
           *
           * @minLength 1
           * @maxLength 512
           */
          label?: string;
          /**
           * The number of submitted responses containing an answer.
           *
           * @minimum 0
           */
          answered?: number;
          /**
           * The number of submitted responses without an answer.
           *
           * @minimum 0
           */
          missing?: number;
          type?: "dropdown";
          /**
           * @maxItems 100
           */
          options?: {
            /**
             * @maxLength 512
             */
            value?: string;
            /**
             * @maxLength 512
             */
            label?: string;
            /**
             * @minimum 0
             */
            count?: number;
            /**
             * @minimum 0
             * @maximum 100
             */
            percentage?: number;
          }[];
        }
      | {
          /**
           * The stable key used to group historical question responses.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * The question wording shown in the report.
           *
           * @minLength 1
           * @maxLength 512
           */
          label?: string;
          /**
           * The number of submitted responses containing an answer.
           *
           * @minimum 0
           */
          answered?: number;
          /**
           * The number of submitted responses without an answer.
           *
           * @minimum 0
           */
          missing?: number;
          type?: "number";
          minimum?: number | null;
          maximum?: number | null;
          average?: number | null;
          median?: number | null;
        }
      | {
          /**
           * The stable key used to group historical question responses.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * The question wording shown in the report.
           *
           * @minLength 1
           * @maxLength 512
           */
          label?: string;
          /**
           * The number of submitted responses containing an answer.
           *
           * @minimum 0
           */
          answered?: number;
          /**
           * The number of submitted responses without an answer.
           *
           * @minimum 0
           */
          missing?: number;
          type?: "date";
          /**
           * @maxItems 366
           */
          buckets?: {
            /**
             * A date string in the format YYYY-MM-DD
             *
             * @pattern ^\d{4}-\d{2}-\d{2}$
             */
            date?: string;
            /**
             * @minimum 0
             */
            count?: number;
          }[];
        }
      | {
          /**
           * The stable key used to group historical question responses.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * The question wording shown in the report.
           *
           * @minLength 1
           * @maxLength 512
           */
          label?: string;
          /**
           * The number of submitted responses containing an answer.
           *
           * @minimum 0
           */
          answered?: number;
          /**
           * The number of submitted responses without an answer.
           *
           * @minimum 0
           */
          missing?: number;
          type?: "daterange";
          /**
           * @minimum 0
           */
          averageDurationDays?: number | null;
          /**
           * @minimum 0
           */
          medianDurationDays?: number | null;
        }
      | {
          /**
           * The stable key used to group historical question responses.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * The question wording shown in the report.
           *
           * @minLength 1
           * @maxLength 512
           */
          label?: string;
          /**
           * The number of submitted responses containing an answer.
           *
           * @minimum 0
           */
          answered?: number;
          /**
           * The number of submitted responses without an answer.
           *
           * @minimum 0
           */
          missing?: number;
          type?: "text";
        }
      | {
          /**
           * The stable key used to group historical question responses.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * The question wording shown in the report.
           *
           * @minLength 1
           * @maxLength 512
           */
          label?: string;
          /**
           * The number of submitted responses containing an answer.
           *
           * @minimum 0
           */
          answered?: number;
          /**
           * The number of submitted responses without an answer.
           *
           * @minimum 0
           */
          missing?: number;
          type?: "textarea";
        }
      | {
          /**
           * The stable key used to group historical question responses.
           *
           * @minLength 1
           * @maxLength 128
           */
          questionKey?: string;
          /**
           * The question wording shown in the report.
           *
           * @minLength 1
           * @maxLength 512
           */
          label?: string;
          /**
           * The number of submitted responses containing an answer.
           *
           * @minimum 0
           */
          answered?: number;
          /**
           * The number of submitted responses without an answer.
           *
           * @minimum 0
           */
          missing?: number;
          type?: "signature";
        }
    )[];
  };
};

export type GetFormReportVariables = {
  pathParams: GetFormReportPathParams;
  queryParams?: GetFormReportQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchGetFormReport = (
  variables: GetFormReportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetFormReportResponse,
    GetFormReportError,
    undefined,
    {},
    GetFormReportQueryParams,
    GetFormReportPathParams
  >({ url: "/forms/{formId}/report", method: "get", ...variables, signal });

export function getFormReportQuery(variables: GetFormReportVariables): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<GetFormReportResponse>;
};

export function getFormReportQuery(
  variables: GetFormReportVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<GetFormReportResponse>)
    | reactQuery.SkipToken;
};

export function getFormReportQuery(
  variables: GetFormReportVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/forms/{formId}/report",
      operationId: "getFormReport",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) => fetchGetFormReport(variables, signal),
  };
}

export const useSuspenseGetFormReport = <TData = GetFormReportResponse,>(
  variables: GetFormReportVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetFormReportResponse,
      GetFormReportError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetFormReportResponse,
    GetFormReportError,
    TData
  >({
    ...getFormReportQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetFormReport = <TData = GetFormReportResponse,>(
  variables: GetFormReportVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetFormReportResponse,
      GetFormReportError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<GetFormReportResponse, GetFormReportError, TData>({
    ...getFormReportQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type ExportFormResponsesPathParams = {
  /**
   * The form identifier.
   *
   * @pattern ^[0-9a-f]{24}$
   */
  formId: string;
};

export type ExportFormResponsesQueryParams = {
  /**
   * Filters applied to the exported responses.
   */
  filter?: {
    /**
     * Export responses linked to this candidate.
     *
     * @pattern ^[0-9a-f]{24}$
     */
    candidateId?: string;
    /**
     * Export responses submitted within this absolute or relative date range.
     */
    submittedAt?: {
      /**
       * The inclusive absolute start of the date range.
       *
       * @format date-time
       */
      from?: string | null;
      /**
       * The inclusive absolute end of the date range.
       *
       * @format date-time
       */
      to?: string | null;
      /**
       * Relative boundaries to use instead of the absolute boundaries. These are recalculated whenever the query runs.
       */
      relative?: {
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        from: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
        /**
         * A date boundary calculated from the current day, week, or month when the query runs.
         */
        to: {
          /**
           * The current calendar point from which a relative date boundary is calculated.
           */
          anchor: "today" | "startOfWeek" | "startOfMonth";
          /**
           * The number of units before or after the selected calendar anchor.
           *
           * @minimum -120
           * @maximum 120
           */
          offset: number | null;
          /**
           * The unit used to offset a relative date boundary.
           */
          unit: "days" | "weeks" | "months";
        };
      };
    };
  };
};

export type ExportFormResponsesError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type ExportFormResponsesVariables = {
  pathParams: ExportFormResponsesPathParams;
  queryParams?: ExportFormResponsesQueryParams;
} & Apiv2Context["fetcherOptions"];

export const fetchExportFormResponses = (
  variables: ExportFormResponsesVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    ExportFormResponsesError,
    undefined,
    {},
    ExportFormResponsesQueryParams,
    ExportFormResponsesPathParams
  >({
    url: "/forms/{formId}/responses/export",
    method: "get",
    ...variables,
    signal,
  });

export function exportFormResponsesQuery(
  variables: ExportFormResponsesVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (options: QueryFnOptions) => Promise<undefined>;
};

export function exportFormResponsesQuery(
  variables: ExportFormResponsesVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((options: QueryFnOptions) => Promise<undefined>)
    | reactQuery.SkipToken;
};

export function exportFormResponsesQuery(
  variables: ExportFormResponsesVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/forms/{formId}/responses/export",
      operationId: "exportFormResponses",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchExportFormResponses(variables, signal),
  };
}

export const useSuspenseExportFormResponses = <TData = undefined,>(
  variables: ExportFormResponsesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<undefined, ExportFormResponsesError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    undefined,
    ExportFormResponsesError,
    TData
  >({
    ...exportFormResponsesQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useExportFormResponses = <TData = undefined,>(
  variables: ExportFormResponsesVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<undefined, ExportFormResponsesError, TData>,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<undefined, ExportFormResponsesError, TData>({
    ...exportFormResponsesQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type GetBookingConfirmationConfigError = Fetcher.ErrorWrapper<
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type GetBookingConfirmationConfigResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * Variant-specific display settings for candidate booking confirmations.
     */
    candidate?: {
      standard?: {
        /**
         * Rows available in standard candidate booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "role"
            | "teacherRegistrationNumber"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "clientName"
            | "address"
            | "payRate"
            | "paymentType"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in standard candidate confirmations.
         */
        options?: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage?: boolean;
          /**
           * Whether standard booking confirmations include an attendance button.
           */
          showStandardAttendanceButton?: boolean;
          /**
           * Whether booking confirmations include an acknowledgement button.
           */
          showAcknowledgementButton?: boolean;
        };
      };
      tutor?: {
        /**
         * Rows available in tutor candidate booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "studentName"
            | "role"
            | "teacherRegistrationNumber"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "clientName"
            | "address"
            | "payRate"
            | "paymentType"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in tutor candidate confirmations.
         */
        options?: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage?: boolean;
          /**
           * Whether booking confirmations include an acknowledgement button.
           */
          showAcknowledgementButton?: boolean;
        };
      };
      interview?: {
        /**
         * Rows available in interview candidate booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "start"
            | "address"
            | "jobDescription"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
      };
      trial?: {
        /**
         * Rows available in trial candidate booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "start"
            | "end"
            | "payRate"
            | "address"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in trial candidate confirmations.
         */
        options?: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage?: boolean;
        };
      };
      permanent?: {
        /**
         * Rows available in permanent candidate booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "role"
            | "start"
            | "clientName"
            | "address"
            | "salary"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
      };
    };
    /**
     * Variant-specific display settings for client booking confirmations.
     */
    client?: {
      standard?: {
        /**
         * Rows available in standard client booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "awrInformation"
            | "chargeRate"
            | "agencyFee"
            | "bookedBy"
            | "approvedBy"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options?: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates?: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate?: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount?: boolean;
        };
      };
      tutor?: {
        /**
         * Rows available in tutor client booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "studentName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "awrInformation"
            | "chargeRate"
            | "agencyFee"
            | "bookedBy"
            | "approvedBy"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options?: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates?: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate?: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount?: boolean;
        };
      };
      interview?: {
        /**
         * Rows available in interview client booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "anaphylaxisTraining"
            | "start"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions"
            | "address";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
      };
      trial?: {
        /**
         * Rows available in trial client booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options?: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates?: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate?: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount?: boolean;
        };
      };
      permanent?: {
        /**
         * Rows available in permanent client booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options?: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates?: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate?: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount?: boolean;
        };
      };
    };
  };
};

export type GetBookingConfirmationConfigVariables =
  Apiv2Context["fetcherOptions"];

export const fetchGetBookingConfirmationConfig = (
  variables: GetBookingConfirmationConfigVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    GetBookingConfirmationConfigResponse,
    GetBookingConfirmationConfigError,
    undefined,
    {},
    {},
    {}
  >({
    url: "/company/booking-confirmation-config",
    method: "get",
    ...variables,
    signal,
  });

export function getBookingConfirmationConfigQuery(
  variables: GetBookingConfirmationConfigVariables,
): {
  queryKey: reactQuery.QueryKey;
  queryFn: (
    options: QueryFnOptions,
  ) => Promise<GetBookingConfirmationConfigResponse>;
};

export function getBookingConfirmationConfigQuery(
  variables: GetBookingConfirmationConfigVariables | reactQuery.SkipToken,
): {
  queryKey: reactQuery.QueryKey;
  queryFn:
    | ((
        options: QueryFnOptions,
      ) => Promise<GetBookingConfirmationConfigResponse>)
    | reactQuery.SkipToken;
};

export function getBookingConfirmationConfigQuery(
  variables: GetBookingConfirmationConfigVariables | reactQuery.SkipToken,
) {
  return {
    queryKey: queryKeyFn({
      path: "/company/booking-confirmation-config",
      operationId: "getBookingConfirmationConfig",
      variables,
    }),
    queryFn:
      variables === reactQuery.skipToken
        ? reactQuery.skipToken
        : ({ signal }: QueryFnOptions) =>
            fetchGetBookingConfirmationConfig(variables, signal),
  };
}

export const useSuspenseGetBookingConfirmationConfig = <
  TData = GetBookingConfirmationConfigResponse,
>(
  variables: GetBookingConfirmationConfigVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetBookingConfirmationConfigResponse,
      GetBookingConfirmationConfigError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useSuspenseQuery<
    GetBookingConfirmationConfigResponse,
    GetBookingConfirmationConfigError,
    TData
  >({
    ...getBookingConfirmationConfigQuery(deepMerge(fetcherOptions, variables)),
    ...options,
    ...queryOptions,
  });
};

export const useGetBookingConfirmationConfig = <
  TData = GetBookingConfirmationConfigResponse,
>(
  variables: GetBookingConfirmationConfigVariables | reactQuery.SkipToken,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetBookingConfirmationConfigResponse,
      GetBookingConfirmationConfigError,
      TData
    >,
    "queryKey" | "queryFn" | "initialData"
  >,
) => {
  const { queryOptions, fetcherOptions } = useApiv2Context(options);
  return reactQuery.useQuery<
    GetBookingConfirmationConfigResponse,
    GetBookingConfirmationConfigError,
    TData
  >({
    ...getBookingConfirmationConfigQuery(
      variables === reactQuery.skipToken
        ? variables
        : deepMerge(fetcherOptions, variables),
    ),
    ...options,
    ...queryOptions,
  });
};

export type UpdateBookingConfirmationConfigError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type UpdateBookingConfirmationConfigResponse = {
  /**
   * The requested resource containing the fields requested with select.
   */
  data: {
    /**
     * Variant-specific display settings for candidate booking confirmations.
     */
    candidate?: {
      standard?: {
        /**
         * Rows available in standard candidate booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "role"
            | "teacherRegistrationNumber"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "clientName"
            | "address"
            | "payRate"
            | "paymentType"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in standard candidate confirmations.
         */
        options?: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage?: boolean;
          /**
           * Whether standard booking confirmations include an attendance button.
           */
          showStandardAttendanceButton?: boolean;
          /**
           * Whether booking confirmations include an acknowledgement button.
           */
          showAcknowledgementButton?: boolean;
        };
      };
      tutor?: {
        /**
         * Rows available in tutor candidate booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "studentName"
            | "role"
            | "teacherRegistrationNumber"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "clientName"
            | "address"
            | "payRate"
            | "paymentType"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in tutor candidate confirmations.
         */
        options?: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage?: boolean;
          /**
           * Whether booking confirmations include an acknowledgement button.
           */
          showAcknowledgementButton?: boolean;
        };
      };
      interview?: {
        /**
         * Rows available in interview candidate booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "start"
            | "address"
            | "jobDescription"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
      };
      trial?: {
        /**
         * Rows available in trial candidate booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "start"
            | "end"
            | "payRate"
            | "address"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
        /**
         * Options available in trial candidate confirmations.
         */
        options?: {
          /**
           * Whether candidate pay details include the holiday pay percentage.
           */
          showHolidayPayPercentage?: boolean;
        };
      };
      permanent?: {
        /**
         * Rows available in permanent candidate booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "role"
            | "start"
            | "clientName"
            | "address"
            | "salary"
            | "arrivalTime"
            | "schoolContact"
            | "healthAndSafetyRisks"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
         */
        includeWrittenStatement?: boolean;
        /**
         * Whether the attached written statement of particulars includes a signature for this booking type.
         */
        includeWrittenStatementSignature?: boolean;
        /**
         * Written statement of particulars content attached to candidate confirmations for this booking type.
         */
        writtenStatement?: {
          /**
           * Written statement entries describing working hours.
           *
           * @maxItems 100
           */
          hoursWork?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing remuneration.
           *
           * @maxItems 100
           */
          renumeration?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing holiday entitlement.
           *
           * @maxItems 100
           */
          holidays?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing sickness arrangements.
           *
           * @maxItems 100
           */
          sickness?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing benefits.
           *
           * @maxItems 100
           */
          benefits?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing termination terms.
           *
           * @maxItems 100
           */
          termination?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
          /**
           * Written statement entries describing applicable collective agreements.
           *
           * @maxItems 100
           */
          collectiveAgreements?:
            | {
                /**
                 * Content included in this written statement section.
                 *
                 * @maxLength 20000
                 */
                value?: string;
              }[]
            | null;
        };
      };
    };
    /**
     * Variant-specific display settings for client booking confirmations.
     */
    client?: {
      standard?: {
        /**
         * Rows available in standard client booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "awrInformation"
            | "chargeRate"
            | "agencyFee"
            | "bookedBy"
            | "approvedBy"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options?: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates?: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate?: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount?: boolean;
        };
      };
      tutor?: {
        /**
         * Rows available in tutor client booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "studentName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "duration"
            | "durationDetail"
            | "awrInformation"
            | "chargeRate"
            | "agencyFee"
            | "bookedBy"
            | "approvedBy"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options?: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates?: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate?: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount?: boolean;
        };
      };
      interview?: {
        /**
         * Rows available in interview client booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "anaphylaxisTraining"
            | "start"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions"
            | "address";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
      };
      trial?: {
        /**
         * Rows available in trial client booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "idCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "end"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options?: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates?: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate?: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount?: boolean;
        };
      };
      permanent?: {
        /**
         * Rows available in permanent client booking confirmations.
         */
        rows?: {
          /**
           * Whether this row is included when it has a value.
           */
          isEnabled?: boolean;
          /**
           * A custom title for the first column. When omitted, the standard title is used.
           *
           * @maxLength 256
           */
          title?: string;
          /**
           * The row represented by this configuration.
           */
          key?:
            | "candidateName"
            | "dateOfBirth"
            | "clientName"
            | "invoiceContact"
            | "role"
            | "qualification"
            | "occupation"
            | "address"
            | "dbsNumber"
            | "dbsType"
            | "dbsCertificateIssueDate"
            | "dbsUpdateServiceNumber"
            | "dbsUpdateServiceChecked"
            | "barredListChecked"
            | "dbsComments"
            | "barredListCheckDate"
            | "prohibitionCheck"
            | "overseasCheck"
            | "section128Check"
            | "rightToWorkVerification"
            | "rightToWorkCheck"
            | "teacherRegistrationNumber"
            | "ewcConfirmation"
            | "nationalInsuranceNumber"
            | "referenceCheck"
            | "safeguardingQualification"
            | "childcareDisqualificationDeclaration"
            | "fitnessToWork"
            | "readKcsie"
            | "safeInEducationCheck"
            | "anaphylaxisTraining"
            | "socialMediaCheck"
            | "googleCheckDate"
            | "start"
            | "chargeRate"
            | "agencyFee"
            | "buyersName"
            | "buyersReference"
            | "relevantKnowledgeAndAppropriateSkills"
            | "additionalInstructions";
        }[];
        /**
         * Custom content shown after the booking confirmation details for this audience and booking type.
         *
         * @maxLength 20000
         */
        confirmationText?: string;
        /**
         * Options available in standard, tutor, trial, and permanent client confirmations.
         */
        options?: {
          /**
           * Whether qualification details include their uploaded dates.
           */
          showQualificationDates?: boolean;
          /**
           * Whether fitness-to-work details include the declaration date.
           */
          showFitnessToWorkDate?: boolean;
          /**
           * Whether reference details include the number of references received.
           */
          showReferenceCount?: boolean;
        };
      };
    };
  };
};

export type UpdateBookingConfirmationConfigRequestBody = {
  /**
   * Variant-specific display settings for candidate booking confirmations.
   */
  candidate: {
    standard: {
      /**
       * Rows available in standard candidate booking confirmations.
       */
      rows: {
        /**
         * Whether this row is included when it has a value.
         */
        isEnabled: boolean;
        /**
         * A custom title for the first column. When omitted, the standard title is used.
         *
         * @maxLength 256
         */
        title?: string;
        /**
         * The row represented by this configuration.
         */
        key:
          | "candidateName"
          | "role"
          | "teacherRegistrationNumber"
          | "start"
          | "end"
          | "duration"
          | "durationDetail"
          | "clientName"
          | "address"
          | "payRate"
          | "paymentType"
          | "arrivalTime"
          | "schoolContact"
          | "healthAndSafetyRisks"
          | "additionalInstructions";
      }[];
      /**
       * Custom content shown after the booking confirmation details for this audience and booking type.
       *
       * @maxLength 20000
       */
      confirmationText?: string;
      /**
       * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
       */
      includeWrittenStatement?: boolean;
      /**
       * Whether the attached written statement of particulars includes a signature for this booking type.
       */
      includeWrittenStatementSignature?: boolean;
      /**
       * Written statement of particulars content attached to candidate confirmations for this booking type.
       */
      writtenStatement?: {
        /**
         * Written statement entries describing working hours.
         *
         * @maxItems 100
         */
        hoursWork?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing remuneration.
         *
         * @maxItems 100
         */
        renumeration?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing holiday entitlement.
         *
         * @maxItems 100
         */
        holidays?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing sickness arrangements.
         *
         * @maxItems 100
         */
        sickness?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing benefits.
         *
         * @maxItems 100
         */
        benefits?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing termination terms.
         *
         * @maxItems 100
         */
        termination?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing applicable collective agreements.
         *
         * @maxItems 100
         */
        collectiveAgreements?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
      };
      /**
       * Options available in standard candidate confirmations.
       */
      options: {
        /**
         * Whether candidate pay details include the holiday pay percentage.
         */
        showHolidayPayPercentage: boolean;
        /**
         * Whether standard booking confirmations include an attendance button.
         */
        showStandardAttendanceButton: boolean;
        /**
         * Whether booking confirmations include an acknowledgement button.
         */
        showAcknowledgementButton: boolean;
      };
    };
    tutor: {
      /**
       * Rows available in tutor candidate booking confirmations.
       */
      rows: {
        /**
         * Whether this row is included when it has a value.
         */
        isEnabled: boolean;
        /**
         * A custom title for the first column. When omitted, the standard title is used.
         *
         * @maxLength 256
         */
        title?: string;
        /**
         * The row represented by this configuration.
         */
        key:
          | "candidateName"
          | "studentName"
          | "role"
          | "teacherRegistrationNumber"
          | "start"
          | "end"
          | "duration"
          | "durationDetail"
          | "clientName"
          | "address"
          | "payRate"
          | "paymentType"
          | "arrivalTime"
          | "schoolContact"
          | "healthAndSafetyRisks"
          | "additionalInstructions";
      }[];
      /**
       * Custom content shown after the booking confirmation details for this audience and booking type.
       *
       * @maxLength 20000
       */
      confirmationText?: string;
      /**
       * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
       */
      includeWrittenStatement?: boolean;
      /**
       * Whether the attached written statement of particulars includes a signature for this booking type.
       */
      includeWrittenStatementSignature?: boolean;
      /**
       * Written statement of particulars content attached to candidate confirmations for this booking type.
       */
      writtenStatement?: {
        /**
         * Written statement entries describing working hours.
         *
         * @maxItems 100
         */
        hoursWork?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing remuneration.
         *
         * @maxItems 100
         */
        renumeration?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing holiday entitlement.
         *
         * @maxItems 100
         */
        holidays?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing sickness arrangements.
         *
         * @maxItems 100
         */
        sickness?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing benefits.
         *
         * @maxItems 100
         */
        benefits?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing termination terms.
         *
         * @maxItems 100
         */
        termination?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing applicable collective agreements.
         *
         * @maxItems 100
         */
        collectiveAgreements?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
      };
      /**
       * Options available in tutor candidate confirmations.
       */
      options: {
        /**
         * Whether candidate pay details include the holiday pay percentage.
         */
        showHolidayPayPercentage: boolean;
        /**
         * Whether booking confirmations include an acknowledgement button.
         */
        showAcknowledgementButton: boolean;
      };
    };
    interview: {
      /**
       * Rows available in interview candidate booking confirmations.
       */
      rows: {
        /**
         * Whether this row is included when it has a value.
         */
        isEnabled: boolean;
        /**
         * A custom title for the first column. When omitted, the standard title is used.
         *
         * @maxLength 256
         */
        title?: string;
        /**
         * The row represented by this configuration.
         */
        key:
          | "start"
          | "address"
          | "jobDescription"
          | "healthAndSafetyRisks"
          | "additionalInstructions";
      }[];
      /**
       * Custom content shown after the booking confirmation details for this audience and booking type.
       *
       * @maxLength 20000
       */
      confirmationText?: string;
      /**
       * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
       */
      includeWrittenStatement?: boolean;
      /**
       * Whether the attached written statement of particulars includes a signature for this booking type.
       */
      includeWrittenStatementSignature?: boolean;
      /**
       * Written statement of particulars content attached to candidate confirmations for this booking type.
       */
      writtenStatement?: {
        /**
         * Written statement entries describing working hours.
         *
         * @maxItems 100
         */
        hoursWork?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing remuneration.
         *
         * @maxItems 100
         */
        renumeration?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing holiday entitlement.
         *
         * @maxItems 100
         */
        holidays?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing sickness arrangements.
         *
         * @maxItems 100
         */
        sickness?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing benefits.
         *
         * @maxItems 100
         */
        benefits?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing termination terms.
         *
         * @maxItems 100
         */
        termination?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing applicable collective agreements.
         *
         * @maxItems 100
         */
        collectiveAgreements?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
      };
    };
    trial: {
      /**
       * Rows available in trial candidate booking confirmations.
       */
      rows: {
        /**
         * Whether this row is included when it has a value.
         */
        isEnabled: boolean;
        /**
         * A custom title for the first column. When omitted, the standard title is used.
         *
         * @maxLength 256
         */
        title?: string;
        /**
         * The row represented by this configuration.
         */
        key:
          | "start"
          | "end"
          | "payRate"
          | "address"
          | "healthAndSafetyRisks"
          | "additionalInstructions";
      }[];
      /**
       * Custom content shown after the booking confirmation details for this audience and booking type.
       *
       * @maxLength 20000
       */
      confirmationText?: string;
      /**
       * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
       */
      includeWrittenStatement?: boolean;
      /**
       * Whether the attached written statement of particulars includes a signature for this booking type.
       */
      includeWrittenStatementSignature?: boolean;
      /**
       * Written statement of particulars content attached to candidate confirmations for this booking type.
       */
      writtenStatement?: {
        /**
         * Written statement entries describing working hours.
         *
         * @maxItems 100
         */
        hoursWork?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing remuneration.
         *
         * @maxItems 100
         */
        renumeration?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing holiday entitlement.
         *
         * @maxItems 100
         */
        holidays?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing sickness arrangements.
         *
         * @maxItems 100
         */
        sickness?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing benefits.
         *
         * @maxItems 100
         */
        benefits?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing termination terms.
         *
         * @maxItems 100
         */
        termination?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing applicable collective agreements.
         *
         * @maxItems 100
         */
        collectiveAgreements?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
      };
      /**
       * Options available in trial candidate confirmations.
       */
      options: {
        /**
         * Whether candidate pay details include the holiday pay percentage.
         */
        showHolidayPayPercentage: boolean;
      };
    };
    permanent: {
      /**
       * Rows available in permanent candidate booking confirmations.
       */
      rows: {
        /**
         * Whether this row is included when it has a value.
         */
        isEnabled: boolean;
        /**
         * A custom title for the first column. When omitted, the standard title is used.
         *
         * @maxLength 256
         */
        title?: string;
        /**
         * The row represented by this configuration.
         */
        key:
          | "candidateName"
          | "role"
          | "start"
          | "clientName"
          | "address"
          | "salary"
          | "arrivalTime"
          | "schoolContact"
          | "healthAndSafetyRisks"
          | "additionalInstructions";
      }[];
      /**
       * Custom content shown after the booking confirmation details for this audience and booking type.
       *
       * @maxLength 20000
       */
      confirmationText?: string;
      /**
       * Whether candidate booking confirmations attach a written statement of particulars for this booking type.
       */
      includeWrittenStatement?: boolean;
      /**
       * Whether the attached written statement of particulars includes a signature for this booking type.
       */
      includeWrittenStatementSignature?: boolean;
      /**
       * Written statement of particulars content attached to candidate confirmations for this booking type.
       */
      writtenStatement?: {
        /**
         * Written statement entries describing working hours.
         *
         * @maxItems 100
         */
        hoursWork?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing remuneration.
         *
         * @maxItems 100
         */
        renumeration?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing holiday entitlement.
         *
         * @maxItems 100
         */
        holidays?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing sickness arrangements.
         *
         * @maxItems 100
         */
        sickness?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing benefits.
         *
         * @maxItems 100
         */
        benefits?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing termination terms.
         *
         * @maxItems 100
         */
        termination?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
        /**
         * Written statement entries describing applicable collective agreements.
         *
         * @maxItems 100
         */
        collectiveAgreements?:
          | {
              /**
               * Content included in this written statement section.
               *
               * @maxLength 20000
               */
              value?: string;
            }[]
          | null;
      };
    };
  };
  /**
   * Variant-specific display settings for client booking confirmations.
   */
  client: {
    standard: {
      /**
       * Rows available in standard client booking confirmations.
       */
      rows: {
        /**
         * Whether this row is included when it has a value.
         */
        isEnabled: boolean;
        /**
         * A custom title for the first column. When omitted, the standard title is used.
         *
         * @maxLength 256
         */
        title?: string;
        /**
         * The row represented by this configuration.
         */
        key:
          | "candidateName"
          | "dateOfBirth"
          | "clientName"
          | "invoiceContact"
          | "role"
          | "qualification"
          | "occupation"
          | "address"
          | "dbsNumber"
          | "dbsType"
          | "dbsCertificateIssueDate"
          | "dbsUpdateServiceNumber"
          | "dbsUpdateServiceChecked"
          | "barredListChecked"
          | "dbsComments"
          | "barredListCheckDate"
          | "prohibitionCheck"
          | "overseasCheck"
          | "section128Check"
          | "rightToWorkVerification"
          | "rightToWorkCheck"
          | "idCheck"
          | "teacherRegistrationNumber"
          | "ewcConfirmation"
          | "nationalInsuranceNumber"
          | "referenceCheck"
          | "safeguardingQualification"
          | "childcareDisqualificationDeclaration"
          | "fitnessToWork"
          | "readKcsie"
          | "safeInEducationCheck"
          | "anaphylaxisTraining"
          | "socialMediaCheck"
          | "googleCheckDate"
          | "start"
          | "end"
          | "duration"
          | "durationDetail"
          | "awrInformation"
          | "chargeRate"
          | "agencyFee"
          | "bookedBy"
          | "approvedBy"
          | "buyersName"
          | "buyersReference"
          | "relevantKnowledgeAndAppropriateSkills"
          | "additionalInstructions";
      }[];
      /**
       * Custom content shown after the booking confirmation details for this audience and booking type.
       *
       * @maxLength 20000
       */
      confirmationText?: string;
      /**
       * Options available in standard, tutor, trial, and permanent client confirmations.
       */
      options: {
        /**
         * Whether qualification details include their uploaded dates.
         */
        showQualificationDates: boolean;
        /**
         * Whether fitness-to-work details include the declaration date.
         */
        showFitnessToWorkDate: boolean;
        /**
         * Whether reference details include the number of references received.
         */
        showReferenceCount: boolean;
      };
    };
    tutor: {
      /**
       * Rows available in tutor client booking confirmations.
       */
      rows: {
        /**
         * Whether this row is included when it has a value.
         */
        isEnabled: boolean;
        /**
         * A custom title for the first column. When omitted, the standard title is used.
         *
         * @maxLength 256
         */
        title?: string;
        /**
         * The row represented by this configuration.
         */
        key:
          | "candidateName"
          | "studentName"
          | "dateOfBirth"
          | "clientName"
          | "invoiceContact"
          | "role"
          | "qualification"
          | "occupation"
          | "address"
          | "dbsNumber"
          | "dbsType"
          | "dbsCertificateIssueDate"
          | "dbsUpdateServiceNumber"
          | "dbsUpdateServiceChecked"
          | "barredListChecked"
          | "dbsComments"
          | "barredListCheckDate"
          | "prohibitionCheck"
          | "overseasCheck"
          | "section128Check"
          | "rightToWorkVerification"
          | "rightToWorkCheck"
          | "idCheck"
          | "teacherRegistrationNumber"
          | "ewcConfirmation"
          | "nationalInsuranceNumber"
          | "referenceCheck"
          | "safeguardingQualification"
          | "childcareDisqualificationDeclaration"
          | "fitnessToWork"
          | "readKcsie"
          | "safeInEducationCheck"
          | "socialMediaCheck"
          | "googleCheckDate"
          | "start"
          | "end"
          | "duration"
          | "durationDetail"
          | "awrInformation"
          | "chargeRate"
          | "agencyFee"
          | "bookedBy"
          | "approvedBy"
          | "buyersName"
          | "buyersReference"
          | "relevantKnowledgeAndAppropriateSkills"
          | "additionalInstructions";
      }[];
      /**
       * Custom content shown after the booking confirmation details for this audience and booking type.
       *
       * @maxLength 20000
       */
      confirmationText?: string;
      /**
       * Options available in standard, tutor, trial, and permanent client confirmations.
       */
      options: {
        /**
         * Whether qualification details include their uploaded dates.
         */
        showQualificationDates: boolean;
        /**
         * Whether fitness-to-work details include the declaration date.
         */
        showFitnessToWorkDate: boolean;
        /**
         * Whether reference details include the number of references received.
         */
        showReferenceCount: boolean;
      };
    };
    interview: {
      /**
       * Rows available in interview client booking confirmations.
       */
      rows: {
        /**
         * Whether this row is included when it has a value.
         */
        isEnabled: boolean;
        /**
         * A custom title for the first column. When omitted, the standard title is used.
         *
         * @maxLength 256
         */
        title?: string;
        /**
         * The row represented by this configuration.
         */
        key:
          | "candidateName"
          | "clientName"
          | "invoiceContact"
          | "role"
          | "anaphylaxisTraining"
          | "start"
          | "chargeRate"
          | "agencyFee"
          | "buyersName"
          | "buyersReference"
          | "relevantKnowledgeAndAppropriateSkills"
          | "additionalInstructions"
          | "address";
      }[];
      /**
       * Custom content shown after the booking confirmation details for this audience and booking type.
       *
       * @maxLength 20000
       */
      confirmationText?: string;
    };
    trial: {
      /**
       * Rows available in trial client booking confirmations.
       */
      rows: {
        /**
         * Whether this row is included when it has a value.
         */
        isEnabled: boolean;
        /**
         * A custom title for the first column. When omitted, the standard title is used.
         *
         * @maxLength 256
         */
        title?: string;
        /**
         * The row represented by this configuration.
         */
        key:
          | "candidateName"
          | "dateOfBirth"
          | "clientName"
          | "invoiceContact"
          | "role"
          | "qualification"
          | "occupation"
          | "address"
          | "dbsNumber"
          | "dbsType"
          | "dbsCertificateIssueDate"
          | "dbsUpdateServiceNumber"
          | "dbsUpdateServiceChecked"
          | "barredListChecked"
          | "dbsComments"
          | "barredListCheckDate"
          | "prohibitionCheck"
          | "overseasCheck"
          | "section128Check"
          | "rightToWorkVerification"
          | "rightToWorkCheck"
          | "idCheck"
          | "teacherRegistrationNumber"
          | "ewcConfirmation"
          | "nationalInsuranceNumber"
          | "referenceCheck"
          | "safeguardingQualification"
          | "childcareDisqualificationDeclaration"
          | "fitnessToWork"
          | "readKcsie"
          | "safeInEducationCheck"
          | "anaphylaxisTraining"
          | "socialMediaCheck"
          | "googleCheckDate"
          | "start"
          | "end"
          | "chargeRate"
          | "agencyFee"
          | "buyersName"
          | "buyersReference"
          | "relevantKnowledgeAndAppropriateSkills"
          | "additionalInstructions";
      }[];
      /**
       * Custom content shown after the booking confirmation details for this audience and booking type.
       *
       * @maxLength 20000
       */
      confirmationText?: string;
      /**
       * Options available in standard, tutor, trial, and permanent client confirmations.
       */
      options: {
        /**
         * Whether qualification details include their uploaded dates.
         */
        showQualificationDates: boolean;
        /**
         * Whether fitness-to-work details include the declaration date.
         */
        showFitnessToWorkDate: boolean;
        /**
         * Whether reference details include the number of references received.
         */
        showReferenceCount: boolean;
      };
    };
    permanent: {
      /**
       * Rows available in permanent client booking confirmations.
       */
      rows: {
        /**
         * Whether this row is included when it has a value.
         */
        isEnabled: boolean;
        /**
         * A custom title for the first column. When omitted, the standard title is used.
         *
         * @maxLength 256
         */
        title?: string;
        /**
         * The row represented by this configuration.
         */
        key:
          | "candidateName"
          | "dateOfBirth"
          | "clientName"
          | "invoiceContact"
          | "role"
          | "qualification"
          | "occupation"
          | "address"
          | "dbsNumber"
          | "dbsType"
          | "dbsCertificateIssueDate"
          | "dbsUpdateServiceNumber"
          | "dbsUpdateServiceChecked"
          | "barredListChecked"
          | "dbsComments"
          | "barredListCheckDate"
          | "prohibitionCheck"
          | "overseasCheck"
          | "section128Check"
          | "rightToWorkVerification"
          | "rightToWorkCheck"
          | "teacherRegistrationNumber"
          | "ewcConfirmation"
          | "nationalInsuranceNumber"
          | "referenceCheck"
          | "safeguardingQualification"
          | "childcareDisqualificationDeclaration"
          | "fitnessToWork"
          | "readKcsie"
          | "safeInEducationCheck"
          | "anaphylaxisTraining"
          | "socialMediaCheck"
          | "googleCheckDate"
          | "start"
          | "chargeRate"
          | "agencyFee"
          | "buyersName"
          | "buyersReference"
          | "relevantKnowledgeAndAppropriateSkills"
          | "additionalInstructions";
      }[];
      /**
       * Custom content shown after the booking confirmation details for this audience and booking type.
       *
       * @maxLength 20000
       */
      confirmationText?: string;
      /**
       * Options available in standard, tutor, trial, and permanent client confirmations.
       */
      options: {
        /**
         * Whether qualification details include their uploaded dates.
         */
        showQualificationDates: boolean;
        /**
         * Whether fitness-to-work details include the declaration date.
         */
        showFitnessToWorkDate: boolean;
        /**
         * Whether reference details include the number of references received.
         */
        showReferenceCount: boolean;
      };
    };
  };
};

export type UpdateBookingConfirmationConfigVariables = {
  body: UpdateBookingConfirmationConfigRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchUpdateBookingConfirmationConfig = (
  variables: UpdateBookingConfirmationConfigVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    UpdateBookingConfirmationConfigResponse,
    UpdateBookingConfirmationConfigError,
    UpdateBookingConfirmationConfigRequestBody,
    {},
    {},
    {}
  >({
    url: "/company/booking-confirmation-config",
    method: "put",
    ...variables,
    signal,
  });

export const useUpdateBookingConfirmationConfig = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      UpdateBookingConfirmationConfigResponse,
      UpdateBookingConfirmationConfigError,
      UpdateBookingConfirmationConfigVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    UpdateBookingConfirmationConfigResponse,
    UpdateBookingConfirmationConfigError,
    UpdateBookingConfirmationConfigVariables
  >({
    mutationFn: (variables: UpdateBookingConfirmationConfigVariables) =>
      fetchUpdateBookingConfirmationConfig(
        deepMerge(fetcherOptions, variables),
      ),
    ...options,
  });
};

export type CreateWrittenStatementExportError = Fetcher.ErrorWrapper<
  | {
      status: 400;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Bad Request
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 401;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Unauthorized
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 403;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Forbidden
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 404;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Not Found
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
  | {
      status: 500;
      payload: {
        /**
         * A human-readable summary of the error.
         *
         * @example Internal Server Error
         */
        message?: string;
        /**
         * The ISO 8601 date and time when the error was produced.
         *
         * @example 2024-01-01T00:00:00.000Z
         */
        timestamp?: string;
      };
    }
>;

export type CreateWrittenStatementExportRequestBody = {
  /**
   * The booking type whose written statement of particulars should be rendered.
   */
  variant: "standard" | "tutor" | "interview" | "trial" | "permanent";
  /**
   * Unsaved written statement content to render instead of the saved configuration.
   */
  writtenStatement?: {
    /**
     * Written statement entries describing working hours.
     *
     * @maxItems 100
     */
    hoursWork?:
      | {
          /**
           * Content included in this written statement section.
           *
           * @maxLength 20000
           */
          value?: string;
        }[]
      | null;
    /**
     * Written statement entries describing remuneration.
     *
     * @maxItems 100
     */
    renumeration?:
      | {
          /**
           * Content included in this written statement section.
           *
           * @maxLength 20000
           */
          value?: string;
        }[]
      | null;
    /**
     * Written statement entries describing holiday entitlement.
     *
     * @maxItems 100
     */
    holidays?:
      | {
          /**
           * Content included in this written statement section.
           *
           * @maxLength 20000
           */
          value?: string;
        }[]
      | null;
    /**
     * Written statement entries describing sickness arrangements.
     *
     * @maxItems 100
     */
    sickness?:
      | {
          /**
           * Content included in this written statement section.
           *
           * @maxLength 20000
           */
          value?: string;
        }[]
      | null;
    /**
     * Written statement entries describing benefits.
     *
     * @maxItems 100
     */
    benefits?:
      | {
          /**
           * Content included in this written statement section.
           *
           * @maxLength 20000
           */
          value?: string;
        }[]
      | null;
    /**
     * Written statement entries describing termination terms.
     *
     * @maxItems 100
     */
    termination?:
      | {
          /**
           * Content included in this written statement section.
           *
           * @maxLength 20000
           */
          value?: string;
        }[]
      | null;
    /**
     * Written statement entries describing applicable collective agreements.
     *
     * @maxItems 100
     */
    collectiveAgreements?:
      | {
          /**
           * Content included in this written statement section.
           *
           * @maxLength 20000
           */
          value?: string;
        }[]
      | null;
  };
};

export type CreateWrittenStatementExportVariables = {
  body: CreateWrittenStatementExportRequestBody;
} & Apiv2Context["fetcherOptions"];

export const fetchCreateWrittenStatementExport = (
  variables: CreateWrittenStatementExportVariables,
  signal?: AbortSignal,
) =>
  apiv2Fetch<
    undefined,
    CreateWrittenStatementExportError,
    CreateWrittenStatementExportRequestBody,
    {},
    {},
    {}
  >({
    url: "/company/booking-confirmation-config/written-statement-exports",
    method: "post",
    ...variables,
    signal,
  });

export const useCreateWrittenStatementExport = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      undefined,
      CreateWrittenStatementExportError,
      CreateWrittenStatementExportVariables
    >,
    "mutationFn"
  >,
) => {
  const { fetcherOptions } = useApiv2Context();
  return reactQuery.useMutation<
    undefined,
    CreateWrittenStatementExportError,
    CreateWrittenStatementExportVariables
  >({
    mutationFn: (variables: CreateWrittenStatementExportVariables) =>
      fetchCreateWrittenStatementExport(deepMerge(fetcherOptions, variables)),
    ...options,
  });
};

export type QueryOperation =
  | {
      path: "/candidates";
      operationId: "getCandidates";
      variables: GetCandidatesVariables | reactQuery.SkipToken;
    }
  | {
      path: "/candidates/{candidateId}";
      operationId: "getCandidate";
      variables: GetCandidateVariables | reactQuery.SkipToken;
    }
  | {
      path: "/candidates/{candidateId}/documents";
      operationId: "getCandidateDocuments";
      variables: GetCandidateDocumentsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/candidates/{candidateId}/documents/{documentId}/content";
      operationId: "getCandidateDocumentContent";
      variables: GetCandidateDocumentContentVariables | reactQuery.SkipToken;
    }
  | {
      path: "/candidates/{candidateId}/health-declaration";
      operationId: "getCandidateHealthDeclaration";
      variables: GetCandidateHealthDeclarationVariables | reactQuery.SkipToken;
    }
  | {
      path: "/candidates/{candidateId}/criminal-record-declaration";
      operationId: "getCandidateCriminalRecordDeclaration";
      variables:
        | GetCandidateCriminalRecordDeclarationVariables
        | reactQuery.SkipToken;
    }
  | {
      path: "/clients";
      operationId: "getClients";
      variables: GetClientsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/clients/contacts";
      operationId: "getClientContacts";
      variables: GetClientContactsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/clients/{clientId}";
      operationId: "getClient";
      variables: GetClientVariables | reactQuery.SkipToken;
    }
  | {
      path: "/vacancies";
      operationId: "getVacancies";
      variables: GetVacanciesVariables | reactQuery.SkipToken;
    }
  | {
      path: "/vacancies/{vacancyId}";
      operationId: "getVacancy";
      variables: GetVacancyVariables | reactQuery.SkipToken;
    }
  | {
      path: "/messages";
      operationId: "getMessages";
      variables: GetMessagesVariables | reactQuery.SkipToken;
    }
  | {
      path: "/messages/{messageId}";
      operationId: "getMessage";
      variables: GetMessageVariables | reactQuery.SkipToken;
    }
  | {
      path: "/users";
      operationId: "getUsers";
      variables: GetUsersVariables | reactQuery.SkipToken;
    }
  | {
      path: "/me";
      operationId: "getUserInfo";
      variables: GetUserInfoVariables | reactQuery.SkipToken;
    }
  | {
      path: "/migrations";
      operationId: "listMigrations";
      variables: ListMigrationsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/migrations/{migrationId}";
      operationId: "getMigration";
      variables: GetMigrationVariables | reactQuery.SkipToken;
    }
  | {
      path: "/migrations/{migrationId}/errors";
      operationId: "listMigrationErrors";
      variables: ListMigrationErrorsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/migrations/{migrationId}/errors/{migrationErrorId}";
      operationId: "getMigrationError";
      variables: GetMigrationErrorVariables | reactQuery.SkipToken;
    }
  | {
      path: "/candidates/{candidateId}/remarks";
      operationId: "getCandidateRemarks";
      variables: GetCandidateRemarksVariables | reactQuery.SkipToken;
    }
  | {
      path: "/company";
      operationId: "getCompany";
      variables: GetCompanyVariables | reactQuery.SkipToken;
    }
  | {
      path: "/timesheets/{timesheetId}/tuition-report";
      operationId: "getTimesheetTuitionReport";
      variables: GetTimesheetTuitionReportVariables | reactQuery.SkipToken;
    }
  | {
      path: "/task-workspaces";
      operationId: "getTaskWorkspaces";
      variables: GetTaskWorkspacesVariables | reactQuery.SkipToken;
    }
  | {
      path: "/task-workspaces/{taskWorkspaceId}";
      operationId: "getTaskWorkspace";
      variables: GetTaskWorkspaceVariables | reactQuery.SkipToken;
    }
  | {
      path: "/task-workspaces/{taskWorkspaceId}/tasks";
      operationId: "getTasks";
      variables: GetTasksVariables | reactQuery.SkipToken;
    }
  | {
      path: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}";
      operationId: "getTask";
      variables: GetTaskVariables | reactQuery.SkipToken;
    }
  | {
      path: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}/activities";
      operationId: "getTaskActivities";
      variables: GetTaskActivitiesVariables | reactQuery.SkipToken;
    }
  | {
      path: "/task-workspaces/{taskWorkspaceId}/tasks/{taskId}/attachments/{attachmentId}/download";
      operationId: "downloadTaskAttachment";
      variables: DownloadTaskAttachmentVariables | reactQuery.SkipToken;
    }
  | {
      path: "/tags";
      operationId: "getTags";
      variables: GetTagsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/bookings";
      operationId: "getBookings";
      variables: GetBookingsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/bookings/{bookingId}";
      operationId: "getBooking";
      variables: GetBookingVariables | reactQuery.SkipToken;
    }
  | {
      path: "/company/auth-clients";
      operationId: "getCompanyAuthClients";
      variables: GetCompanyAuthClientsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/company/occupations";
      operationId: "getCompanyOccupations";
      variables: GetCompanyOccupationsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/saved-searches";
      operationId: "getSavedSearches";
      variables: GetSavedSearchesVariables | reactQuery.SkipToken;
    }
  | {
      path: "/saved-searches/{savedSearchId}";
      operationId: "getSavedSearch";
      variables: GetSavedSearchVariables | reactQuery.SkipToken;
    }
  | {
      path: "/branches";
      operationId: "getBranches";
      variables: GetBranchesVariables | reactQuery.SkipToken;
    }
  | {
      path: "/branches/{branchId}";
      operationId: "getBranch";
      variables: GetBranchVariables | reactQuery.SkipToken;
    }
  | {
      path: "/company/export-configs/{type}";
      operationId: "getExportConfig";
      variables: GetExportConfigVariables | reactQuery.SkipToken;
    }
  | {
      path: "/payment-runs/{paymentRunId}";
      operationId: "getPaymentRun";
      variables: GetPaymentRunVariables | reactQuery.SkipToken;
    }
  | {
      path: "/payment-runs/{paymentRunId}/exports/{target}/confirmations";
      operationId: "getPaymentRunExportConfirmation";
      variables:
        | GetPaymentRunExportConfirmationVariables
        | reactQuery.SkipToken;
    }
  | {
      path: "/forms";
      operationId: "getForms";
      variables: GetFormsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/forms/{formId}";
      operationId: "getForm";
      variables: GetFormVariables | reactQuery.SkipToken;
    }
  | {
      path: "/form-requests";
      operationId: "getFormRequests";
      variables: GetFormRequestsVariables | reactQuery.SkipToken;
    }
  | {
      path: "/form-requests/{formRequestId}";
      operationId: "getFormRequest";
      variables: GetFormRequestVariables | reactQuery.SkipToken;
    }
  | {
      path: "/form-responses";
      operationId: "getFormResponses";
      variables: GetFormResponsesVariables | reactQuery.SkipToken;
    }
  | {
      path: "/form-responses/{formResponseId}";
      operationId: "getFormResponse";
      variables: GetFormResponseVariables | reactQuery.SkipToken;
    }
  | {
      path: "/forms/{formId}/report";
      operationId: "getFormReport";
      variables: GetFormReportVariables | reactQuery.SkipToken;
    }
  | {
      path: "/forms/{formId}/responses/export";
      operationId: "exportFormResponses";
      variables: ExportFormResponsesVariables | reactQuery.SkipToken;
    }
  | {
      path: "/company/booking-confirmation-config";
      operationId: "getBookingConfirmationConfig";
      variables: GetBookingConfirmationConfigVariables | reactQuery.SkipToken;
    };
